/*!
  * bigbank-interface-components v11.282.8
  * (c) 2025 WEB team <web.team@bigbank.ee>
  * @license MIT
 */
import DOMPurify from 'dompurify';
import Vue from 'vue';
import { tabbable } from 'tabbable';
import ClientOnly from 'vue-client-only';
import PortalVue from 'portal-vue';
import VueScreen from 'vue-screen';
import { flip, shift, limitShift, offset, arrow, computePosition, autoUpdate } from '@floating-ui/dom';
import { merge, omit, inRange, omitBy, isNil } from 'lodash';
import { FocusTrap } from 'focus-trap-vue';
import { ref, inject, onBeforeMount, onBeforeUnmount, defineComponent, computed, watch } from '@vue/composition-api';
import { validationMixin } from 'vuelidate';
import { useCurrencyInput } from 'vue-currency-input';
import isInteger from 'lodash/isInteger';
import 'core-js/modules/es.error.cause.js';
import throttle from 'lodash/throttle';
import { mergeData } from 'vue-functional-data-merge';
import { CollapseTransition } from 'vue2-transitions';
import resize from 'vue-resize-directive';
import pickBy from 'lodash/pickBy';
import InputMask from 'inputmask-core';
import 'core-js/modules/es.array.reduce.js';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import { createPopper } from '@popperjs/core';
import Clipboard from 'vue-clipboard2';
import castArray from 'lodash/castArray';
import _mergeJSXProps from '@vue/babel-helper-vue-jsx-merge-props';
import { __decorate } from 'tslib';
import { Prop, Component, Vue as Vue$1, Model, Watch } from 'vue-property-decorator';
import clamp$1 from 'lodash/clamp';
import requiredUnless from 'vuelidate/lib/validators/requiredUnless';
import { getMonth, getYear, isSameDay, format, startOfMonth, getDaysInMonth, parseISO, isBefore, isAfter, setYear, setMonth, isToday, getHours, getMinutes, getSeconds, subSeconds, subDays, addDays, lastDayOfMonth, subMonths, addMonths, clamp as clamp$2 } from 'date-fns';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isEqual from 'lodash/isEqual';
import last$1 from 'lodash/last';
import VueMultiselect from 'vue-multiselect';
import toString from 'lodash/toString';
import isObject$1 from 'lodash/isObject';
import 'core-js/modules/es.object.has-own.js';
import { TouchSwipe } from 'quasar';
import round from 'lodash/round';
import clone from 'lodash/clone';
import isBetween from 'dayjs/plugin/isBetween';
import { useIntersectionObserver } from '@vueuse/core';

const toTitleCase = str => str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
const slugify = str => {
  if (str) {
    str = str.replace(/^\s+|\s+$/g, '');
    str = str.replace(/[A-Z]/g, s => `-${s}`);
    str = str.toLowerCase();
    const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;',
      to = 'aaaaeeeeiiiioooouuuunc------';
    for (let i = 0, l = from.length; i < l; i++) {
      str = str.replace(from[i], to[i]);
    }
    str = str.replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-/g, '');
  }
  return str;
};
const padWithLeadingZero = (val, length = 2) => `${val}`.padStart(length, '0');
const containsCaseInsensitive = (string, search) => string.toLowerCase().includes(search.toLowerCase());
const equalAsStrings = (lhs, rhs) => `${lhs}` === `${rhs}`;

var FieldExtensions = {
  props: {
    framed: Boolean,
    small: Boolean,
    large: Boolean,
    placeholder: String
  }
};

const isBoolean = val => typeof val === 'boolean';
const isString = val => typeof val === 'string';
const isObject = obj => obj !== null && typeof obj === 'object';
const isFunction$1 = val => typeof val === 'function';
const isArray = a => Array.isArray(a);
const isDate = val => Object.prototype.toString.call(val) === '[object Date]';
const isNumber = val => !isNaN(parseFloat(val)) && isFinite(val);
const isNullish = val => (val !== null && val !== void 0 ? val : null) === null;
const isDefined = val => !isNullish(val);
const isNonEmptyStringOrArray = val => !!(val !== null && val !== void 0 && val.length);
const isNonEmptyObject = val => isDefined(val) && !!Object.keys(val).length;
const valueIsFilled = v => isDefined(v) && (isFunction$1(v) || isBoolean(v) || isNonEmptyStringOrArray(v) || isNonEmptyObject(v) || isDate(v) || isNumber(v));
const isEmpty = val => {
  let attr;
  if (isNullish(val)) {
    return true;
  }
  if (isFunction$1(val)) {
    return false;
  }
  if (isString(val)) {
    return /^\s*$/.test(val);
  }
  if (isArray(val)) {
    return val.length === 0;
  }
  if (isDate(val)) {
    return false;
  }
  if (isObject(val)) {
    for (attr in val) {
      return false;
    }
    return true;
  }
  return false;
};
const notEmpty = val => !isEmpty(val);

var Accessibility = {
  props: {
    tabindex: [String, Number]
  }
};

var _process, _process$env;
const inBrowser = typeof window !== 'undefined';
const isTestEnvironment = typeof process !== 'undefined' && ((_process = process) === null || _process === void 0 ? void 0 : (_process$env = _process.env) === null || _process$env === void 0 ? void 0 : _process$env.NODE_ENV) === 'test';

var uuid = ((length = 32) => {
  let i, random;
  let uuid = '';
  for (i = 0; i < length; i++) {
    random = Math.random() * 16 | 0;
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      uuid += '-';
    }
    uuid += (i === 12 ? 4 : i === 16 ? random & 3 | 8 : random).toString(16);
  }
  return uuid;
});

var script$2D = {
  name: 'bb-cdn-icon',
  props: {
    cdnPath: String,
    svgName: String
  },
  data() {
    return {
      isTestEnvironment
    };
  },
  computed: {
    svgPath() {
      return `${this.cdnPath}${this.svgName}.svg`;
    }
  },
  mounted() {
    this.loadIcon();
  },
  updated() {
    this.loadIcon();
  },
  methods: {
    async fetchSVG() {
      try {
        const response = await fetch(this.svgPath);
        if (response.ok) {
          const svgString = await response.text();
          DOMPurify.addHook('afterSanitizeAttributes', node => {
            const href = node.getAttribute('xlink:href') || node.getAttribute('href');
            if (href && !href.startsWith('#')) {
              node.removeAttribute('xlink:href');
              node.removeAttribute('href');
            }
          });
          const svgStringSafe = DOMPurify.sanitize(svgString, {
            USE_PROFILES: {
              svg: true
            },
            ADD_TAGS: ['use']
          });
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgStringSafe, 'image/svg+xml');
          return svgDoc.documentElement;
        } else {
          console.error('Failed to fetch SVG:', response.statusText);
          return null;
        }
      } catch (error) {
        console.error('Error fetching SVG:', error);
        return null;
      }
    },
    async replaceSVG() {
      if (!this.$refs.svgPlaceholder) {
        return;
      }
      const svgNode = await this.fetchSVG();
      if (!svgNode) {
        return;
      }
      Array.from(this.$refs.svgPlaceholder.attributes).forEach(attr => svgNode.setAttribute(attr.name, attr.value));
      for (const attr in this.$attrs) {
        if (!attr.startsWith('on')) continue;
        svgNode.addEventListener(attr.slice(2).toLowerCase(), this.$attrs[attr]);
      }
      svgNode.classList.remove('hidden');
      this.prefixIdsInSVG(svgNode);
      this.$refs.svgPlaceholder.replaceWith(svgNode);
    },
    async prefixIdsInSVG(svgNode) {
      const idMap = new Map();
      const uuid$1 = uuid();
      for (const el of svgNode.querySelectorAll('[id]')) {
        const {
          id: oldId
        } = el;
        const newId = `${oldId}_${uuid$1}`;
        el.id = newId;
        idMap.set(oldId, newId);
      }
      for (const el of svgNode.querySelectorAll('*')) {
        for (const {
          name,
          value
        } of [...el.attributes]) {
          for (const [oldId, newId] of idMap) {
            if (value.includes(`#${oldId}`)) {
              el.setAttribute(name, value.replace(`#${oldId}`, `#${newId}`));
            }
          }
        }
      }
    },
    async loadIcon() {
      if (isTestEnvironment) {
        return;
      }
      await this.$nextTick();
      await this.replaceSVG();
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier , shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    const options = typeof script === 'function' ? script.options : script;
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        hook = function (context) {
            context =
                context ||
                    (this.$vnode && this.$vnode.ssrContext) ||
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext);
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__$2D = script$2D;

/* template */
var __vue_render__$2v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return !_vm.isTestEnvironment ? _c('svg', {
    ref: "svgPlaceholder",
    staticClass: "hidden"
  }) : _c('svg', [_c('text', [_vm._v(_vm._s(_vm.svgPath))])]);
};
var __vue_staticRenderFns__$2v = [];

/* style */
const __vue_inject_styles__$2D = undefined;
/* scoped */
const __vue_scope_id__$2D = undefined;
/* module identifier */
const __vue_module_identifier__$2D = undefined;
/* functional template */
const __vue_is_functional_template__$2D = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2D = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2v,
  staticRenderFns: __vue_staticRenderFns__$2v
}, __vue_inject_styles__$2D, __vue_script__$2D, __vue_scope_id__$2D, __vue_is_functional_template__$2D, __vue_module_identifier__$2D, false, undefined, undefined, undefined);

var ALLOWED_ICON_SIZES = [18, 24, 36, 48, 64, 128];

const defaultFontSize = 16;
const xsBreakpoint = 0;
const smBreakpoint = 576;
const mdBreakpoint = 768;
const lgBreakpoint = 992;
const xlBreakpoint = 1200;
const gridBreakpoints = {
  'xs': xsBreakpoint,
  'sm': smBreakpoint,
  'md': mdBreakpoint,
  'lg': lgBreakpoint,
  'xl': xlBreakpoint
};
const defaultFormKey = 'form';
const defaultRepeaterFormKey = 'form-repeater';
const defaultFormKeys = [defaultFormKey, defaultRepeaterFormKey];
const ICON_CDN_PATH = 'ICON_CDN_PATH';

var script$2C = {
  name: 'bb-icon',
  components: {
    BbCdnIcon: __vue_component__$2D
  },
  inject: {
    iconCdnPath: {
      from: ICON_CDN_PATH,
      default: ''
    }
  },
  props: {
    name: {
      type: String,
      required: true
    },
    size: {
      type: [String, Number],
      validator: size => ['auto', 'fluid', 'font', ...ALLOWED_ICON_SIZES].includes(isNaN(size) ? size : +size),
      default: 'font'
    },
    rotate: {
      type: String,
      validator: rotate => ['up', 'right', 'down', 'left'].includes(rotate),
      default: 'up'
    },
    fill: {
      type: String,
      default: 'current'
    },
    animated: {
      type: Boolean,
      default: false
    },
    cdnPath: {
      type: String,
      default: ''
    }
  },
  emits: ['click'],
  computed: {
    computedCdnPath() {
      return this.cdnPath || this.iconCdnPath;
    },
    classes() {
      return {
        [`bb-icon--size-${this.size}`]: this.size,
        [`bb-icon--rotated-${this.rotate}`]: this.rotate,
        [`bb-icon--fill-${this.fill}`]: this.fill && this.fill !== 'original',
        'bb-icon--animated': this.animated
      };
    }
  },
  methods: {
    clickHandler(evt) {
      this.$emit('click', evt);
    }
  }
};

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$2C = script$2C;

/* template */
var __vue_render__$2u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.computedCdnPath ? _c('bb-cdn-icon', {
    staticClass: "bb-icon",
    class: _vm.classes,
    attrs: {
      "cdn-path": _vm.computedCdnPath,
      "svg-name": _vm.name,
      "role": "img",
      "aria-hidden": "true"
    },
    on: {
      "click": _vm.clickHandler
    }
  }) : _c("icon-" + _vm.name, {
    tag: "component",
    staticClass: "bb-icon",
    class: _vm.classes,
    attrs: {
      "role": "img",
      "aria-hidden": "true"
    },
    nativeOn: {
      "click": function ($event) {
        return _vm.clickHandler.apply(null, arguments);
      }
    }
  });
};
var __vue_staticRenderFns__$2u = [];

/* style */
const __vue_inject_styles__$2C = function (inject) {
  if (!inject) return;
  inject("data-v-8a0872e6_0", {
    source: ".bb-icon{display:inline-block;transition:transform .5s cubic-bezier(.2,.25,.3,1.5);vertical-align:middle}.bb-icon--size-font{width:1em;min-width:1em;height:1em;vertical-align:text-top}.bb-icon--size-18{width:1.125rem;min-width:1.125rem;height:1.125rem}.bb-icon--size-24{width:1.5rem;min-width:1.5rem;height:1.5rem}.bb-icon--size-36{width:2.25rem;min-width:2.25rem;height:2.25rem}.bb-icon--size-48{width:3rem;min-width:3rem;height:3rem}.bb-icon--size-64{width:4rem;min-width:4rem;height:4rem}.bb-icon--size-128{width:8rem;min-width:8rem;height:8rem}.bb-icon--size-auto{width:auto;height:auto}.bb-icon--size-fluid{width:100%;height:100%}.bb-icon--rotated-up{transform:rotate(0)}.bb-icon--rotated-right{transform:rotate(90deg)}.bb-icon--rotated-down{transform:rotate(180deg)}.bb-icon--rotated-left{transform:rotate(-90deg)}.bb-icon--fill-current,.bb-icon--fill-current .bb-icon__dynamic-fill,.bb-icon--fill-current [fill]{fill:currentColor}.bb-icon--fill-white .bb-icon__dynamic-fill{fill:#fff}.bb-icon--fill-black .bb-icon__dynamic-fill{fill:#000}.bb-icon--fill-navy .bb-icon__dynamic-fill{fill:#00285a}.bb-icon--fill-navy-90 .bb-icon__dynamic-fill{fill:#004c73}.bb-icon--fill-navy-80 .bb-icon__dynamic-fill{fill:#005d8c}.bb-icon--fill-navy-70 .bb-icon__dynamic-fill{fill:#003373}.bb-icon--fill-navy-50 .bb-icon__dynamic-fill{fill:#003e8c}.bb-icon--fill-mint .bb-icon__dynamic-fill{fill:#0ccca2}.bb-icon--fill-mint-90 .bb-icon__dynamic-fill{fill:#3dd6b5}.bb-icon--fill-mint-70 .bb-icon__dynamic-fill{fill:#3dd6b5}.bb-icon--fill-mint-50 .bb-icon__dynamic-fill{fill:#69e4ca}.bb-icon--fill-mint-30 .bb-icon__dynamic-fill{fill:#b6f0e3}.bb-icon--fill-mint-10 .bb-icon__dynamic-fill{fill:#e7faf6}.bb-icon--fill-green .bb-icon__dynamic-fill{fill:#0ccca2}.bb-icon--fill-green-90 .bb-icon__dynamic-fill{fill:#3dd6b5}.bb-icon--fill-green-70 .bb-icon__dynamic-fill{fill:#3dd6b5}.bb-icon--fill-green-50 .bb-icon__dynamic-fill{fill:#69e4ca}.bb-icon--fill-green-30 .bb-icon__dynamic-fill{fill:#b6f0e3}.bb-icon--fill-green-10 .bb-icon__dynamic-fill{fill:#e7faf6}.bb-icon--fill-blue .bb-icon__dynamic-fill{fill:#2485e5}.bb-icon--fill-blue-90 .bb-icon__dynamic-fill{fill:#5ea6ed}.bb-icon--fill-blue-80 .bb-icon__dynamic-fill{fill:#5ea6ed}.bb-icon--fill-blue-70 .bb-icon__dynamic-fill{fill:#5ea6ed}.bb-icon--fill-blue-50 .bb-icon__dynamic-fill{fill:#6eb3f7}.bb-icon--fill-blue-40 .bb-icon__dynamic-fill{fill:#6eb3f7}.bb-icon--fill-blue-30 .bb-icon__dynamic-fill{fill:#add9ff}.bb-icon--fill-blue-20 .bb-icon__dynamic-fill{fill:#add9ff}.bb-icon--fill-blue-10 .bb-icon__dynamic-fill{fill:#e6f3ff}.bb-icon--fill-gray .bb-icon__dynamic-fill{fill:#38414d}.bb-icon--fill-gray-90 .bb-icon__dynamic-fill{fill:#57697a}.bb-icon--fill-gray-80 .bb-icon__dynamic-fill{fill:#687d92}.bb-icon--fill-gray-70 .bb-icon__dynamic-fill{fill:#89a}.bb-icon--fill-gray-50 .bb-icon__dynamic-fill{fill:#9dabb9}.bb-icon--fill-gray-40 .bb-icon__dynamic-fill{fill:#bdc7d0}.bb-icon--fill-gray-30 .bb-icon__dynamic-fill{fill:#dee3e8}.bb-icon--fill-gray-20 .bb-icon__dynamic-fill{fill:#eeeff2}.bb-icon--fill-gray-10 .bb-icon__dynamic-fill{fill:#f7f7f8}.bb-icon--fill-yellow .bb-icon__dynamic-fill{fill:#fc0}.bb-icon--fill-yellow-90 .bb-icon__dynamic-fill{fill:#fc0}.bb-icon--fill-yellow-70 .bb-icon__dynamic-fill{fill:#ffeea6}.bb-icon--fill-yellow-50 .bb-icon__dynamic-fill{fill:#ffeea6}.bb-icon--fill-yellow-30 .bb-icon__dynamic-fill{fill:#fbf5cb}.bb-icon--fill-yellow-10 .bb-icon__dynamic-fill{fill:#fbf5cb}.bb-icon--fill-red .bb-icon__dynamic-fill{fill:#ff7155}.bb-icon--fill-red-90 .bb-icon__dynamic-fill{fill:#fa9}.bb-icon--fill-red-70 .bb-icon__dynamic-fill{fill:#fa9}.bb-icon--fill-red-50 .bb-icon__dynamic-fill{fill:#ffbfb3}.bb-icon--fill-red-30 .bb-icon__dynamic-fill{fill:#ffd5cc}.bb-icon--fill-red-10 .bb-icon__dynamic-fill{fill:#ffeae6}.bb-icon__white-fill{fill:#fff}@media screen and (prefers-reduced-motion:reduce){.bb-icon *{animation-play-state:paused!important}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2C = undefined;
/* module identifier */
const __vue_module_identifier__$2C = undefined;
/* functional template */
const __vue_is_functional_template__$2C = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2C = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2u,
  staticRenderFns: __vue_staticRenderFns__$2u
}, __vue_inject_styles__$2C, __vue_script__$2C, __vue_scope_id__$2C, __vue_is_functional_template__$2C, __vue_module_identifier__$2C, false, createInjector, undefined, undefined);

var Icon$_ = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg198118579__a bb-icon__dynamic-fill",attrs:{"d":"M15.07,11.25L14.17,12.17C13.45,12.89 13,13.5 13,15H11V14.5C11,13.39 11.45,12.39 12.17,11.67L13.41,10.41C13.78,10.05 14,9.55 14,9C14,7.89 13.1,7 12,7A2,2 0 0,0 10,9H8A4,4 0 0,1 12,5A4,4 0 0,1 16,9C16,9.88 15.64,10.67 15.07,11.25M13,19H11V17H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2Z"}})]) } };

Vue.component('icon-ui-help-circle', Icon$_);

function isFunction(value) {
  if (typeof value !== 'function') {
    console.warn(`[Vue-click-outside:] provided expression ${value} is not a function.`);
    return false;
  }
  return true;
}
function isServer(vNode) {
  return typeof vNode.componentInstance !== 'undefined' && vNode.componentInstance.$isServer;
}
var clickOutside = {
  bind(el, binding, vNode) {
    if (!isFunction(binding.value)) {
      return;
    }
    const argumentString = binding.arg;
    const argumentValue = vNode.context[argumentString] || vNode.context.$refs[argumentString];
    const relatedEl = argumentValue && argumentValue.$el || argumentValue;
    const insiders = [el, relatedEl].filter(v => v);
    function handler(clickEvent) {
      if (!vNode.context) {
        return;
      }
      const notOutside = insiders.some(el => el.contains(clickEvent.target));
      if (notOutside) {
        return;
      }
      el.__vueClickOutside__.callback(clickEvent);
    }
    el.__vueClickOutside__ = {
      handler,
      callback: binding.value
    };
    const clickHandler = 'ontouchstart' in document.documentElement ? 'touchstart' : 'click';
    if (!isServer(vNode)) {
      document.addEventListener(clickHandler, handler);
    }
  },
  update(el, {
    value
  }) {
    if (isFunction(value)) {
      el.__vueClickOutside__.callback = value;
    }
  },
  unbind(el, binding, vNode) {
    const clickHandler = 'ontouchstart' in document.documentElement ? 'touchstart' : 'click';
    if (!isServer(vNode) && el.__vueClickOutside__) {
      document.removeEventListener(clickHandler, el.__vueClickOutside__.handler);
    }
    delete el.__vueClickOutside__;
  }
};

const isElement = el => el && el.nodeType === Node.ELEMENT_NODE;
const isVisible = el => isElement(el) && document.body.contains(el) && el.getBoundingClientRect().height > 0 && el.getBoundingClientRect().width > 0;
const selectAll = (selector, root) => {
  if (!isElement(root)) {
    root = document;
  }
  return Array.from(root.querySelectorAll(selector));
};
const matches = (el, selector) => {
  if (!isElement(el)) {
    return false;
  }
  const proto = Element.prototype;
  const Matches = proto.matches || proto.matchesSelector || proto.mozMatchesSelector || proto.msMatchesSelector || proto.oMatchesSelector || proto.webkitMatchesSelector ||
  function (sel) {
    const element = this;
    const m = selectAll(sel, element.document || element.ownerDocument);
    let i = m.length;
    while (--i >= 0 && m.item(i) !== element) {}
    return i > -1;
  };
  return Matches.call(el, selector);
};
const closest$1 = (selector, root) => {
  if (!isElement(root)) {
    return null;
  }
  const Closest = Element.prototype.closest ||
  function (sel) {
    let element = this;
    if (!document.documentElement.contains(element)) {
      return null;
    }
    do {
      if (matches(element, sel)) {
        return element;
      }
      element = element.parentElement;
    } while (element !== null);
    return null;
  };
  const el = Closest.call(root, selector);
  return el === root ? null : el;
};
const getAttr = (el, attr) => {
  if (attr && isElement(el)) {
    return el.getAttribute(attr);
  }
  return null;
};
const eventOn = (el, evtName, handler) => {
  if (el && el.addEventListener) {
    el.addEventListener(evtName, handler);
  }
};
const eventOff = (el, evtName, handler) => {
  if (el && el.removeEventListener) {
    el.removeEventListener(evtName, handler);
  }
};
const isHoveringElement = target => {
  const elementsMouseIsOver = document.querySelectorAll(':hover');
  const elementMouseIsOver = elementsMouseIsOver[elementsMouseIsOver.length - 1];
  return target && target.contains(elementMouseIsOver);
};
const getTabbableChildren = container => {
  tabbable(container);
};

const warning = (component, message) => {
  const componentName = !!component && component.$options ? component.$options.name : component;
  console.warn(`<${componentName}>: ${message}`);
  return true;
};
const deprecated = (oldNameOrMessage, newNameOrValidator, component, validator = () => true) => {
  const message = typeof newNameOrValidator === 'string' ? `${oldNameOrMessage} is deprecated${component && ` in ${component}`}! Use ${newNameOrValidator} instead.` : oldNameOrMessage;
  const validatorFunction = typeof newNameOrValidator === 'function' ? newNameOrValidator : validator;
  return value => {
    if (value) {
      console.warn(message);
      return validatorFunction(value);
    }
    return true;
  };
};
const validateWithDeprecationWarning = (componentName, propName, value, allowed) => {
  if (!allowed.hasOwnProperty(value)) {
    return false;
  }
  if (allowed[value] !== value) {
    const suggestion = allowed[value] ? ` Use "${allowed[value]}" instead".` : '';
    console.warn(`"${value}" for ${componentName}'s \`${propName}\` prop is deprecated.${suggestion}`);
  }
  return true;
};

Vue.use(PortalVue);
Vue.use(VueScreen, gridBreakpoints);
var script$2B = {
  name: 'bb-tooltip',
  components: {
    BbIcon: __vue_component__$2C,
    ClientOnly,
    FocusTrap
  },
  directives: {
    clickOutside
  },
  props: {
    bubble: {
      type: [Boolean, String],
      validator: val => [true, false, 'error'].includes(val)
    },
    isInInput: {
      type: Boolean,
      default: false
    },
    reference: null,
    popperOpts: {
      type: Object,
      validator: deprecated('popperOpts', 'customFloatingUiOptions', 'BbTooltip')
    },
    customFloatingUiOptions: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['hide'],
  data() {
    return {
      targetID: uuid(),
      portalTargetElementID: '',
      tooltipContentVisible: this.bubble,
      cleanupFloatingUI: undefined,
      bubblePlacement: 'bottom',
      bubblePositionStyles: {},
      bubbleArrowPositionStyles: {}
    };
  },
  computed: {
    wrapperClasses() {
      return {
        'bb-tooltip--inline': !this.isInInput && !this.bubble,
        'bb-tooltip--in-input': this.isInInput,
        'bb-tooltip--bubble': this.bubble,
        'bb-tooltip--bubble-error': this.bubble === 'error'
      };
    },
    bubbleContentClasses() {
      return this.bubble === 'error' ? 'bb-tooltip__content--error' : '';
    },
    referenceElement() {
      var _this$reference;
      const referenceEl = ((_this$reference = this.reference) === null || _this$reference === void 0 ? void 0 : _this$reference.$el) || this.reference;
      return referenceEl || this.$refs.iconReference;
    },
    tooltipOffset() {
      return this.bubble ? 7 : 14;
    },
    tooltipPlacement() {
      return this.$screen.md ? 'right' : 'bottom';
    },
    floatingUIOptions() {
      const floatingUIOptions = {
        placement: this.tooltipPlacement,
        middleware: [flip(), shift({
          limiter: limitShift()
        }), offset(this.tooltipOffset), arrow({
          element: this.$refs.bubbleArrow,
          padding: 6
        })]
      };
      return merge(floatingUIOptions, this.customFloatingUiOptions);
    }
  },
  watch: {
    tooltipContentVisible: 'handleFloatingUi'
  },
  mounted() {
    this.$nextTick(() => {
      if (this.tooltipContentVisible) {
        this.handleFloatingUi();
      }
    });
  },
  beforeMount() {
    if (!this.portalTargetElementID) {
      this.createPortalTargetElement();
    }
  },
  beforeDestroy() {
    var _this$cleanupFloating;
    (_this$cleanupFloating = this.cleanupFloatingUI) === null || _this$cleanupFloating === void 0 ? void 0 : _this$cleanupFloating.call(this);
    this.destroyPortalTargetElement();
  },
  methods: {
    handleFloatingUi() {
      var _this$cleanupFloating2;
      (_this$cleanupFloating2 = this.cleanupFloatingUI) === null || _this$cleanupFloating2 === void 0 ? void 0 : _this$cleanupFloating2.call(this);
      this.cleanupFloatingUI = this.tooltipContentVisible ? this.autoPositionFloatingElement() : undefined;
    },
    onMouseenter() {
      this.showContent();
    },
    onMouseleave() {
      this.hideContent();
    },
    onKeyEsc() {
      this.hideContent();
    },
    onKeyEnter() {
      this.showContent();
      this.$nextTick(() => {
        this.$refs.bubbleContent.focus();
      });
    },
    onTouchStart(event) {
      event.preventDefault();
      this.tooltipContentVisible ? this.hideContent() : this.showContent();
    },
    showContent() {
      this.tooltipContentVisible = true;
    },
    hideContent() {
      setTimeout(() => {
        if (!isHoveringElement(this.$refs.bubble)) {
          this.tooltipContentVisible = false;
        }
      }, 100);
    },
    onHide() {
      this.tooltipContentVisible = !!this.bubble;
      this.$emit('hide');
    },
    async updatePosition() {
      const computedPosition = await computePosition(this.referenceElement, this.$refs.bubble, this.floatingUIOptions);
      const {
        x,
        y,
        placement,
        middlewareData: {
          arrow: {
            x: arrowX,
            y: arrowY
          }
        }
      } = computedPosition;
      this.bubblePositionStyles = {
        left: `${x}px`,
        top: `${y}px`
      };
      this.bubbleArrowPositionStyles = {
        left: isDefined(arrowX) ? `${arrowX}px` : '',
        top: isDefined(arrowY) ? `${arrowY}px` : ''
      };
      this.bubblePlacement = placement;
    },
    autoPositionFloatingElement() {
      const cleanup = autoUpdate(this.referenceElement, this.$refs.bubble, this.updatePosition);
      return cleanup;
    },
    createPortalTargetElement() {
      const portalTargetEl = document.createElement('div');
      portalTargetEl.setAttribute('id', `tooltip-${this.targetID}`);
      document.body.appendChild(portalTargetEl);
      this.portalTargetElementID = `tooltip-${this.targetID}`;
    },
    destroyPortalTargetElement() {
      const portalTargetEl = document.querySelector(`#${this.portalTargetElementID}`);
      if (!portalTargetEl) return;
      const isPortalTargetElInsidePortal = portalTargetEl.parentElement.className === 'vue-portal-target';
      isPortalTargetElInsidePortal ? portalTargetEl.parentElement.remove() : portalTargetEl.remove();
    }
  }
};

/* script */
const __vue_script__$2B = script$2B;

/* template */
var __vue_render__$2t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: ['bb-tooltip', _vm.wrapperClasses],
    attrs: {
      "data-testid": "bb-tooltip",
      "aria-labelledby": _vm.portalTargetElementID,
      "role": "tooltip",
      "tabindex": "0"
    },
    on: {
      "mouseenter": _vm.onMouseenter,
      "mouseleave": _vm.onMouseleave,
      "touchstart": _vm.onTouchStart,
      "keyup": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        $event.preventDefault();
        return _vm.onKeyEnter.apply(null, arguments);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }
        return _vm.onKeyEsc.apply(null, arguments);
      }]
    }
  }, [_c('div', {
    ref: "iconReference",
    staticClass: "bb-tooltip__reference"
  }, [!_vm.bubble ? _vm._t("trigger", function () {
    return [_c('bb-icon', {
      staticClass: "bb-tooltip__icon",
      attrs: {
        "size": "24",
        "name": "ui-help-circle",
        "fill": "blue"
      }
    })];
  }) : _vm._e()], 2), _vm._v(" "), _c('client-only', [_c('mounting-portal', {
    attrs: {
      "mount-to": "#" + _vm.portalTargetElementID,
      "name": _vm.portalTargetElementID
    }
  }, [_c('transition', {
    attrs: {
      "name": "fade"
    }
  }, [_c('focus-trap', {
    attrs: {
      "active": !!_vm.tooltipContentVisible,
      "escape-deactivates": true
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.tooltipContentVisible,
      expression: "tooltipContentVisible"
    }, {
      name: "click-outside",
      rawName: "v-click-outside:referenceElement",
      value: _vm.onHide,
      expression: "onHide",
      arg: "referenceElement"
    }],
    ref: "bubble",
    staticClass: "bb-tooltip__content",
    class: _vm.bubbleContentClasses,
    style: _vm.bubblePositionStyles,
    attrs: {
      "id": _vm.portalTargetElementID,
      "data-testid": "bb-tooltip__content",
      "data-floating-placement": _vm.bubblePlacement
    },
    on: {
      "click": _vm.onHide
    }
  }, [_c('div', {
    ref: "bubbleArrow",
    staticClass: "bb-tooltip__arrow",
    style: _vm.bubbleArrowPositionStyles
  }), _vm._v(" "), _c('div', {
    ref: "bubbleContent",
    staticClass: "bb-tooltip__bubble",
    attrs: {
      "data-testid": "bb-tooltip__bubble",
      "tabindex": "0"
    }
  }, [_vm._t("default")], 2)])])], 1)], 1)], 1)], 1);
};
var __vue_staticRenderFns__$2t = [];

/* style */
const __vue_inject_styles__$2B = function (inject) {
  if (!inject) return;
  inject("data-v-727ba1ef_0", {
    source: ".bb-tooltip--inline{display:inline-block}.bb-tooltip__reference{position:absolute}.bb-tooltip--inline .bb-tooltip__reference{position:relative}.bb-tooltip--bubble .bb-tooltip__reference{bottom:0;left:0;width:100%;height:100%;pointer-events:none}.bb-tooltip--in-input .bb-tooltip__reference{top:.75rem;right:-1.875rem;display:inline-flex;transform:translateY(-50%)}.framed-input .bb-tooltip--in-input .bb-tooltip__reference{top:1.25rem}.framed-input--small .bb-tooltip--in-input .bb-tooltip__reference{top:.9375rem}.bb-tooltip__bubble{padding:1.25rem;border-radius:.375rem;background:#f7f7f8;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px;font-weight:400;line-height:1.3}.bb-tooltip__arrow{position:absolute;border-style:solid;border-color:#f7f7f8}.bb-tooltip__content--error .bb-tooltip__arrow{border-color:#ff7155}[data-floating-placement=top]>.bb-tooltip__arrow{bottom:-.4375rem;border-width:.4375rem .4375rem 0 .4375rem;border-right-color:transparent;border-left-color:transparent}[data-floating-placement=bottom]>.bb-tooltip__arrow{top:-.4375rem;border-width:0 .4375rem .4375rem .4375rem;border-right-color:transparent;border-left-color:transparent}[data-floating-placement=right]>.bb-tooltip__arrow{left:-.4375rem;border-width:.4375rem .4375rem .4375rem 0;border-top-color:transparent;border-bottom-color:transparent}[data-floating-placement=left]>.bb-tooltip__arrow{right:-.4375rem;border-width:.4375rem 0 .4375rem .4375rem;border-top-color:transparent;border-bottom-color:transparent}.bb-tooltip__content{position:absolute;z-index:1600;top:0;left:0;max-width:97.5vw;color:#687d92;filter:drop-shadow(0 2px 2px rgba(104, 104, 104, .5));text-align:left}@media (min-width:768px){.bb-tooltip__content{max-width:18.75rem;padding:0}}@media (min-width:768px){.bb-tooltip__content--error{max-width:11.3125rem}}.bb-tooltip__content--error .bb-tooltip__bubble{background:#ff7155;color:#fff}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2B = undefined;
/* module identifier */
const __vue_module_identifier__$2B = undefined;
/* functional template */
const __vue_is_functional_template__$2B = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2B = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2t,
  staticRenderFns: __vue_staticRenderFns__$2t
}, __vue_inject_styles__$2B, __vue_script__$2B, __vue_scope_id__$2B, __vue_is_functional_template__$2B, __vue_module_identifier__$2B, false, createInjector, undefined, undefined);

var Field = {
  components: {
    BbTooltip: __vue_component__$2B
  },
  data() {
    return {
      val: valueIsFilled(this.value) ? this.value : '',
      focus: false,
      previousVal: '',
      decimalSeparators: ['.', ',']
    };
  },
  mixins: [Accessibility],
  props: {
    inputAttrs: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: [String, Number, Boolean, Object, Date],
      default: ''
    },
    id: [String, Number],
    name: {
      type: String,
      default: '',
      required: true
    },
    type: {
      type: String,
      default: 'text'
    },
    min: Number,
    max: {
      type: Number
    },
    label: String,
    multilineLabel: Boolean
  },
  emits: ['input', 'change', 'focus', 'blur'],
  watch: {
    value(newVal) {
      this.val = newVal;
    },
    focus() {
      if (this.validationTooltipCondition) {
        this.focus ? this.showErrorBubble() : this.hideErrorBubble();
      }
    }
  },
  computed: {
    filled() {
      return valueIsFilled(this.val);
    },
    fieldGroupClasses() {
      var _this$addOn, _this$addOn2;
      return {
        'has-label': this.label,
        'has-placeholder': this.placeholder,
        'is-filled': this.filled,
        'is-focused': this.focus,
        'contains-addon': this.addOn,
        'contains-tooltip': this.helpActive,
        'multiline-label': this.multilineLabel,
        'framed-input': this.isFramed,
        'framed-input--small': this.small,
        'framed-input--large': this.large,
        'framed-input--center': this.center,
        'input--readonly': this.readonly,
        'loading': this.loading,
        'addon-1em': ((_this$addOn = this.addOn) === null || _this$addOn === void 0 ? void 0 : _this$addOn.length) === 1,
        'addon-3em': ((_this$addOn2 = this.addOn) === null || _this$addOn2 === void 0 ? void 0 : _this$addOn2.length) > 1
      };
    },
    isFramed() {
      return this.framed || this.small || this.large;
    }
  },
  methods: {
    isSeparatorEntered(event) {
      if (event.target.value !== '') {
        return false;
      }
      const lastEnteredCharacter = event.data;
      return this.type === 'number' && event.inputType === 'insertText' && this.decimalSeparators.includes(lastEnteredCharacter);
    },
    endsWithSeparatorOnBackspace(event) {
      var _this$previousVal;
      const lastChar = (_this$previousVal = this.previousVal) === null || _this$previousVal === void 0 ? void 0 : _this$previousVal.charAt(this.previousVal.length - 2);
      return this.type === 'number' && event.inputType === 'deleteContentBackward' && lastChar && this.decimalSeparators.includes(lastChar);
    },
    onInput(event) {
      if (this.isSeparatorEntered(event)) {
        return;
      }
      this.val = this.endsWithSeparatorOnBackspace(event) ? this.previousVal.slice(0, -2) : event.target.value;
      this.previousVal = this.val;
      this.$emit('input', this.val);
    },
    onChange(event) {
      this.$emit('change', event.target.value);
    },
    onFocus() {
      this.focus = true;
      this.$emit('focus');
    },
    onBlur() {
      this.focus = false;
      this.$emit('blur');
    }
  }
};

var ErrorBubble = {
  data() {
    return {
      isHoveringInput: true
    };
  },
  props: {
    errorMessageAsBubble: Boolean,
    errorBubbleOnHoverOnly: Boolean
  },
  computed: {
    validationTooltipCondition() {
      const errorMessageAsBubble = this.small || this.errorMessageAsBubble;
      const errorBubbleVisible = this.errorBubbleOnHoverOnly ? this.isHoveringInput : true;
      return errorMessageAsBubble && errorBubbleVisible;
    }
  },
  methods: {
    showErrorBubble() {
      this.isHoveringInput = true;
    },
    hideErrorBubble() {
      this.isHoveringInput = false;
    }
  }
};

var Icon$Z = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3179361345__a bb-icon__dynamic-fill",attrs:{"d":"M11 15h2v2h-2v-2m0-8h2v6h-2V7m1-5C6.47 2 2 6.5 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2m0 18a8 8 0 0 1-8-8 8 8 0 0 1 8-8 8 8 0 0 1 8 8 8 8 0 0 1-8 8z"}})]) } };

Vue.component('icon-ui-alert-circle-outline', Icon$Z);

var vuelidateValidator = {
  mixins: [validationMixin],
  validations() {
    return {
      val: this.rules || {}
    };
  },
  props: {
    messages: Object,
    formKeys: {
      type: Array,
      default: () => [...defaultFormKeys]
    }
  },
  setup(props) {
    const root = ref(null);
    const fieldData = ref({
      name: props.name,
      $el: root,
      hasCustomValidation: false,
      isInvalid: false,
      hasError: false,
      validate: () => {}
    });
    function getForms(formKeys) {
      return formKeys.map(formKey => inject(formKey, {})).filter(notEmpty);
    }
    const forms = getForms(props.formKeys);
    for (const form of forms) {
      onBeforeMount(() => form.addField(fieldData.value));
      onBeforeUnmount(() => form.removeField(props.name));
    }
    return {
      root,
      fieldData
    };
  },
  created() {
    this.updateFieldData();
  },
  computed: {
    hasCustomValidation() {
      return this.rules && this.messages;
    },
    isInvalid() {
      return this.$v.$invalid;
    },
    hasError() {
      return this.$v.$anyError;
    }
  },
  watch: {
    isInvalid() {
      this.updateFieldData();
    },
    hasError() {
      this.updateFieldData();
    },
    hasCustomValidation() {
      this.updateFieldData();
    }
  },
  methods: {
    updateFieldData() {
      if (this.fieldData) {
        this.fieldData.hasCustomValidation = this.hasCustomValidation;
        this.fieldData.isInvalid = this.isInvalid;
        this.fieldData.hasError = this.hasError;
        this.fieldData.validate = this.validate;
      }
    },
    validate() {
      if (this.hasCustomValidation) {
        this.$v.$touch();
      }
    },
    forceValidate() {
      this.$v.$touch();
    },
    getFirstError() {
      const fields = Object.keys(this.$v.$params);
      for (let i = 0; i < fields.length; i++) {
        const rules = Object.keys(this.$v[fields[i]].$params);
        for (let j = 0; j < rules.length; j++) {
          if (!this.$v[fields[i]][rules[j]]) {
            var _this$messages$error$, _this$messages, _this$messages$error;
            return (_this$messages$error$ = (_this$messages = this.messages) === null || _this$messages === void 0 ? void 0 : (_this$messages$error = _this$messages.error) === null || _this$messages$error === void 0 ? void 0 : _this$messages$error[rules[j]]) !== null && _this$messages$error$ !== void 0 ? _this$messages$error$ : '';
          }
        }
      }
      return '';
    }
  }
};

var validator = {
  inject: {
    '$validator': {
      from: '$validator',
      default: () => ({
        mocked: true
      })
    }
  }
};

var veeValidationMixin = {
  mixins: [validator],
  props: {
    dataValid: String
  },
  emits: ['validate'],
  watch: {
    value(newVal) {
      this.val = newVal;
    },
    validity(newVal) {
      this.onValidate(newVal);
    }
  },
  computed: {
    validity() {
      if (this.dataValid) {
        return this.dataValid;
      }
      const isTestEnvironment = this.$validator.mocked;
      if (isTestEnvironment) {
        return null;
      }
      if (this.veeValidateErrors || this.vuelidateErrors) {
        return 'false';
      }
      if (this.filled) {
        return 'true';
      }
      return null;
    },
    filled() {
      return valueIsFilled(this.val);
    },
    firstError() {
      return this.veeValidateErrors && this.errors.items.find(error => error.field === this.name).msg;
    },
    veeValidateErrors() {
      return !this.$validator.mocked && this.errors && this.errors.items && this.errors.items.some(error => error.field === this.name);
    },
    vuelidateErrors() {
      return this.rules && this.hasError;
    }
  },
  methods: {
    onValidate(validity) {
      this.$emit('validate', validity);
    }
  }
};

var script$2A = {
  name: 'bb-big-input',
  mixins: [FieldExtensions, ErrorBubble, Field, veeValidationMixin, vuelidateValidator],
  props: {
    suffix: String,
    placeholder: String,
    emphasizeLabel: Boolean,
    background: Boolean,
    disabled: Boolean,
    formatter: Function,
    maxlength: [Number, String],
    pattern: {
      type: String
    },
    autocomplete: {
      type: String,
      default: null,
      validator: val => ['off', 'on'].includes(val)
    },
    readonly: Boolean,
    rules: Object,
    mandatory: Boolean
  },
  emits: ['input'],
  data() {
    return {
      val: this.formatValue(this.value) || isNumber(this.formatValue(this.value)) ? this.formatValue(this.value) : '',
      inputWidth: '.75rem'
    };
  },
  computed: {
    classes() {
      return {
        'has-value': this.val,
        'has-error': this.validity === 'false',
        'shows-placeholder': this.placeholder && !this.val,
        'bb-big-input--background': this.background,
        'bb-big-input--disabled': this.disabled,
        'bb-big-input--readonly': this.readonly,
        'bb-big-input--emphasize-label': this.emphasizeLabel && this.validity === 'false',
        'bb-big-input--has-suffix': this.suffix
      };
    },
    computedInputmode() {
      const inputModes = {
        number: 'decimal',
        tel: 'tel'
      };
      return inputModes[this.type] || 'text';
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `error-message-${slugify(this.name)}`;
    }
  },
  watch: {
    val() {
      this.$nextTick(() => {
        this.inputWidth = `${this.$refs.suffix.offsetWidth}px`;
      });
    },
    value(newVal) {
      this.val = this.formatValue(newVal);
      if (this.val !== newVal) {
        this.$emit('input', this.val);
      }
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    formatValue(val) {
      return this.formatter ? this.formatter(val) : val;
    }
  }
};

/* script */
const __vue_script__$2A = script$2A;

/* template */
var __vue_render__$2s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('label', {
    ref: "root",
    staticClass: "bb-big-input",
    class: _vm.classes,
    attrs: {
      "data-valid": _vm.validity,
      "for": _vm.id,
      "data-vv-value-path": "val",
      "data-testid": "bb-big-input"
    }
  }, [_c('div', {
    staticClass: "bb-big-input__input-group",
    style: {
      width: _vm.inputWidth
    }
  }, [_c('div', {
    ref: "suffix",
    staticClass: "bb-big-input__suffix"
  }, [_c('span', {
    staticClass: "bb-big-input__suffix-size-keeper",
    attrs: {
      "aria-hidden": "true"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.val || _vm.placeholder) + "\n      ")]), _vm._v("\n      " + _vm._s(_vm.suffix) + "\n    ")]), _vm._v(" "), _vm.type === 'checkbox' ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "input",
    staticClass: "bb-big-input__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "inputmode": _vm.computedInputmode,
      "pattern": _vm.pattern,
      "maxlength": _vm.maxlength,
      "autocomplete": _vm.autocomplete,
      "readonly": _vm.readonly,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "data-testid": "bb-big-input__input",
      "type": "checkbox"
    },
    domProps: {
      "checked": Array.isArray(_vm.val) ? _vm._i(_vm.val, null) > -1 : _vm.val
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": function ($event) {
        $event.stopPropagation();
        return _vm.onInput.apply(null, arguments);
      },
      "change": [function ($event) {
        var $$a = _vm.val,
          $$el = $event.target,
          $$c = $$el.checked ? true : false;
        if (Array.isArray($$a)) {
          var $$v = null,
            $$i = _vm._i($$a, $$v);
          if ($$el.checked) {
            $$i < 0 && (_vm.val = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.val = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.val = $$c;
        }
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }]
    }
  }, 'input', _vm.inputAttrs, false)) : _vm.type === 'radio' ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "input",
    staticClass: "bb-big-input__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "inputmode": _vm.computedInputmode,
      "pattern": _vm.pattern,
      "maxlength": _vm.maxlength,
      "autocomplete": _vm.autocomplete,
      "readonly": _vm.readonly,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "data-testid": "bb-big-input__input",
      "type": "radio"
    },
    domProps: {
      "checked": _vm._q(_vm.val, null)
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": function ($event) {
        $event.stopPropagation();
        return _vm.onInput.apply(null, arguments);
      },
      "change": [function ($event) {
        _vm.val = null;
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }]
    }
  }, 'input', _vm.inputAttrs, false)) : _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "input",
    staticClass: "bb-big-input__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "inputmode": _vm.computedInputmode,
      "pattern": _vm.pattern,
      "maxlength": _vm.maxlength,
      "autocomplete": _vm.autocomplete,
      "readonly": _vm.readonly,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "data-testid": "bb-big-input__input",
      "type": _vm.type
    },
    domProps: {
      "value": _vm.val
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": [function ($event) {
        if ($event.target.composing) {
          return;
        }
        _vm.val = $event.target.value;
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onInput.apply(null, arguments);
      }],
      "change": function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }, 'input', _vm.inputAttrs, false))]), _vm._v(" "), _vm.label || _vm.$slots.default ? _c('span', {
    staticClass: "bb-big-input__label",
    attrs: {
      "data-testid": "bb-big-input__label"
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-big-input__mandatory-indicator",
      attrs: {
        "data-testid": "bb-big-input__mandatory-indicator"
      }
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasValidationError ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n  ")]) : _vm._e()]);
};
var __vue_staticRenderFns__$2s = [];

/* style */
const __vue_inject_styles__$2A = function (inject) {
  if (!inject) return;
  inject("data-v-15e651e4_0", {
    source: ".bb-big-input{display:flex;overflow:hidden;flex-direction:column;align-items:center;padding:.9375rem;cursor:text}.bb-big-input.bb-big-input--readonly{cursor:default}.bb-big-input.bb-big-input--disabled{cursor:not-allowed}.bb-big-input--background{border-radius:.3125rem;background-color:#f7f7f8}.bb-big-input__input-group{position:relative;min-width:7.5rem;margin-bottom:.125rem}.bb-big-input__input{padding-right:0;padding-bottom:0;border-bottom:.0625rem solid transparent}.bb-big-input--has-suffix .bb-big-input__input{padding-right:1.25rem}.bb-big-input:not(.has-value) .bb-big-input__input{border-bottom:.0625rem solid #89a}.bb-big-input__input:focus,.bb-big-input__input:hover{border-color:#00285a;transition:color .2s,background-color .2s,border-color .2s}.bb-big-input--disabled .bb-big-input__input:focus,.bb-big-input--disabled .bb-big-input__input:hover,.bb-big-input--readonly .bb-big-input__input:focus,.bb-big-input--readonly .bb-big-input__input:hover{border-color:transparent}.bb-big-input__input:focus::placeholder{color:transparent}@-webkit-keyframes autofill{to{background:0 0;color:#2485e5}}.bb-big-input__input,.bb-big-input__suffix{color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.5rem;line-height:1.1;text-align:center}.bb-big-input--disabled>*>.bb-big-input__input,.bb-big-input--disabled>*>.bb-big-input__suffix{color:#89a;opacity:.6}.shows-placeholder .bb-big-input__input,.shows-placeholder .bb-big-input__suffix{color:#89a}.bb-big-input__input::-webkit-input-placeholder,.bb-big-input__suffix::-webkit-input-placeholder{color:#89a;opacity:1}.bb-big-input__input:-moz-placeholder,.bb-big-input__suffix:-moz-placeholder{color:#89a;opacity:1}.bb-big-input__input::-moz-placeholder,.bb-big-input__suffix::-moz-placeholder{color:#89a;opacity:1}.bb-big-input__input:-ms-input-placeholder,.bb-big-input__suffix:-ms-input-placeholder{color:#89a;opacity:1}.bb-big-input__input::placeholder,.bb-big-input__suffix::placeholder{color:#89a;opacity:1}.bb-big-input__suffix{position:absolute;top:0;left:50%;pointer-events:none;transform:translateX(-50%);user-select:none;white-space:nowrap}.bb-big-input__suffix-size-keeper{opacity:0;speak:none}.bb-big-input__label{margin-bottom:.0625rem;color:#89a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-big-input--emphasize-label .bb-big-input__label{color:#00285a}.bb-big-input--disabled>.bb-big-input__label{color:#89a;opacity:.6}.bb-big-input__mandatory-indicator{color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2A = undefined;
/* module identifier */
const __vue_module_identifier__$2A = undefined;
/* functional template */
const __vue_is_functional_template__$2A = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2A = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2s,
  staticRenderFns: __vue_staticRenderFns__$2s
}, __vue_inject_styles__$2A, __vue_script__$2A, __vue_scope_id__$2A, __vue_is_functional_template__$2A, __vue_module_identifier__$2A, false, createInjector, undefined, undefined);

const aNumberWithFourDigits$1 = 1111;
const aNumberWithDecimalPlace$1 = 1.1;
const toLocaleStringWithoutNumbers$1 = (val, locale) => val.toLocaleString(locale).replace(/1/g, '');
const escapedThousandsSeparator = locale => `${thousandsSeparator(locale) && '\\'}${thousandsSeparator(locale)}`;
const escapedDecimalSeparator = locale => `${decimalSeparator(locale) && '\\'}${decimalSeparator(locale)}`;
const thousandsSeparator = locale => toLocaleStringWithoutNumbers$1(aNumberWithFourDigits$1, locale);
const decimalSeparator = locale => toLocaleStringWithoutNumbers$1(aNumberWithDecimalPlace$1, locale);
const localizeNumber = (val, {
  style = 'decimal',
  useGrouping = true,
  currency = 'EUR',
  minimumFractionDigits,
  maximumFractionDigits,
  locale
} = {}) => {
  const numberVal = +val;
  return locale && isNumber(numberVal) ? numberVal.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
    style,
    currency,
    useGrouping
  }) : val;
};
const unlocalizeNumber = (_val, {
  locale
}) => {
  const val = typeof _val === 'number' ? _val : String(_val).replace(new RegExp(escapedThousandsSeparator(locale), 'g'), '').replace(new RegExp(escapedDecimalSeparator(locale), 'g'), '.').replace(/\s+/g, '');
  return Number.isNaN(val) ? 0 : +val;
};

/**
 * A functional input field that is used for currencies.
 * Not to be used on its own.
 */
var script$2z = defineComponent({
  name: 'bb-functional-input-currency',
  props: {
    value: {
      type: [String, Number],
      default: null
    },
    /**
     * Minimum allowed value
     */
    min: Number,
    /**
     * Maximum allowed value
     */
    max: Number,
    /**
     * A BCP 47 language tag (for example "en" or "de-DE").
     * `undefined` uses the default locale of the Browser.
     */
    locale: {
      type: String,
      default: 'et'
    },
    /**
     * Fixed decimal places. `locale` has to be specified.
     * @values 0 - 15
     */
    decimals: {
      type: [Number, String]
    },
    /**
     * Minimum decimal places. `locale` has to be specified.
     * @values 0 - 15
     */
    minDecimals: {
      type: [Number, String]
    },
    /**
     * Maximum decimal places. `locale` has to be specified.
     * @values 0 - 15
     */
    maxDecimals: {
      type: [Number, String]
    },
    /**
     * An ISO 4217 currency code.
     */
    currency: {
      type: String,
      default: 'EUR'
    },
    /**
     * How to display the currency in currency formatting.
     *
     * `symbol`: localized currency symbol such as "€" (default value);
     * `narrowSymbol`: narrow format symbol ("$100" rather than "US$100");
     * `code`: the ISO currency code;
     * `name`: localized currency name such as "dollar";
     * `hidden`: hide the currency.
     * @values symbol, narrowSymbol, code, name, hidden
     */
    currencyDisplay: {
      type: String,
      default: 'symbol'
    },
    /**
     * For non-editable pre-filled inputs
     * @ignore
     */
    readonly: Boolean
  },
  emits: ['blur', 'focus', 'input', 'change', 'format'],
  setup(props, {
    emit
  }) {
    const currencyOptions = computed(() => {
      var _props$decimals, _props$decimals2, _props$max, _props$min;
      // WEB-3417: `bg` locale temp fix, TODO: remove in IC v12
      const locale = String(props.locale).startsWith('bg') ? 'fi' : props.locale;
      const precision = {
        min: (_props$decimals = props.decimals) !== null && _props$decimals !== void 0 ? _props$decimals : props.minDecimals,
        max: (_props$decimals2 = props.decimals) !== null && _props$decimals2 !== void 0 ? _props$decimals2 : props.maxDecimals
      };
      const isUnlocalizedOnFocus = true;
      const formatOnFocus = {
        hideCurrencySymbolOnFocus: isUnlocalizedOnFocus,
        hideGroupingSeparatorOnFocus: isUnlocalizedOnFocus,
        hideNegligibleDecimalDigitsOnFocus: isUnlocalizedOnFocus
      };
      return {
        locale,
        currency: props.currency,
        currencyDisplay: props.currencyDisplay,
        ...formatOnFocus,
        precision: Object.values(precision).some(isInteger) ? precision : undefined,
        valueRange: {
          max: (_props$max = props.max) !== null && _props$max !== void 0 ? _props$max : Number.MAX_SAFE_INTEGER,
          min: (_props$min = props.min) !== null && _props$min !== void 0 ? _props$min : 0
        }
      };
    });
    const {
      inputRef,
      formattedValue,
      setOptions,
      setValue
    } = useCurrencyInput(currencyOptions.value);
    const setCurrencyValue = value => setValue(value !== null && value !== void 0 ? value : null);
    watch(() => [props.currency, props.currencyDisplay, props.locale, props.max, props.min, props.readonly], () => {
      setOptions(currencyOptions.value);
    });
    watch(() => formattedValue.value, () => {
      emit('format', formattedValue.value);
    });
    watch(() => props.value, () => {
      setCurrencyValue(props.value);
    });
    return {
      inputRef,
      setCurrencyValue
    };
  },
  computed: {
    thousandsSeparator() {
      return thousandsSeparator(this.locale);
    },
    decimalSeparator() {
      return decimalSeparator(this.locale);
    }
  },
  methods: {
    onPaste(event) {
      var _pastedText$match;
      const regexDecimalSeparators = /[,.٫]/;
      if (this.thousandsSeparator.match(regexDecimalSeparators)) {
        return;
      }
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const usedDecimalSeparator = (_pastedText$match = pastedText.match(regexDecimalSeparators)) === null || _pastedText$match === void 0 ? void 0 : _pastedText$match[0];
      if (!usedDecimalSeparator || usedDecimalSeparator === this.decimalSeparator) {
        return;
      }
      event.preventDefault();
      const pastedValueWithNormalizedDecimalSeparator = pastedText.replace(/\s/g, '').replace(usedDecimalSeparator, '.');
      this.setCurrencyValue(pastedValueWithNormalizedDecimalSeparator);
    },
    onBlur() {
      this.emitBlur();
    },
    onFocus() {
      this.emitFocus();
    },
    onInput(event) {
      if (event instanceof CustomEvent) {
        this.emitInput(event.detail.number);
      }
    },
    emitBlur() {
      /**
       * Blur event.
       *
       * @event blur
       */
      this.$emit('blur');
    },
    emitFocus() {
      /**
       * Focus event.
       *
       * @event focus
       */
      this.$emit('focus');
    },
    emitInput(value) {
      /**
       * Input event.
       *
       * @event input
       * @type {number}
       */
      this.$emit('input', value);
    },
    emitChange(value) {
      /**
       * Change event.
       *
       * @event change
       * @type {number}
       */
      this.$emit('change', value);
    }
  }
});

/* script */
const __vue_script__$2z = script$2z;

/* template */
var __vue_render__$2r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('input', _vm._b({
    ref: "inputRef",
    staticClass: "bb-functional-input-currency",
    attrs: {
      "data-testid": "bb-functional-input-currency",
      "readonly": _vm.readonly
    },
    on: {
      "paste": _vm.onPaste,
      "blur": _vm.onBlur,
      "focus": _vm.onFocus,
      "input": _vm.onInput
    }
  }, 'input', _vm.$attrs, false));
};
var __vue_staticRenderFns__$2r = [];

/* style */
const __vue_inject_styles__$2z = undefined;
/* scoped */
const __vue_scope_id__$2z = undefined;
/* module identifier */
const __vue_module_identifier__$2z = undefined;
/* functional template */
const __vue_is_functional_template__$2z = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2z = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2r,
  staticRenderFns: __vue_staticRenderFns__$2r
}, __vue_inject_styles__$2z, __vue_script__$2z, __vue_scope_id__$2z, __vue_is_functional_template__$2z, __vue_module_identifier__$2z, false, undefined, undefined, undefined);

var script$2y = defineComponent({
  name: 'bb-big-input-currency',
  components: {
    BbFunctionalInputCurrency: __vue_component__$2z
  },
  mixins: [Field, FieldExtensions, veeValidationMixin, vuelidateValidator],
  props: {
    value: {
      type: [String, Number],
      default: null
    },
    /**
     * Object with props for [bb-functional-input-currency](/#/Atoms/bb-functional-input-currency).
     */
    currencyProps: Object,
    /**
     * @deprecated Use `currencyProps`.
     */
    min: {
      type: Number,
      validator: deprecated('min', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    max: {
      type: Number,
      validator: deprecated('max', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    locale: {
      type: String,
      validator: deprecated('locale', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    decimals: {
      type: [Number, String],
      validator: deprecated('decimals', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    minDecimals: {
      type: [Number, String],
      validator: deprecated('minDecimals', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    maxDecimals: {
      type: [Number, String],
      validator: deprecated('maxDecimals', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    currency: {
      type: String,
      validator: deprecated('currency', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * @deprecated Use `currencyProps`.
     */
    currencyDisplay: {
      type: String,
      validator: deprecated('currencyDisplay', 'currencyProps', 'BbBigInputCurrency')
    },
    /**
     * Placeholder for the input
     */
    placeholder: String,
    /**
     * Emphasizes the label.
     */
    emphasizeLabel: Boolean,
    /**
     * Toggles the background, which behaves as a label.
     */
    background: Boolean,
    /**
     * Disable the text field
     */
    disabled: Boolean,
    /**
     * For non-editable pre-filled inputs
     */
    readonly: Boolean,
    /**
     * ID for the input
     */
    id: [String, Number],
    /**
     * HTML tabindex Attribute
     */
    tabindex: [String, Number],
    /**
     * Manually set HTML5 autocomplete attribute.
     * @values off, on
     */
    autocomplete: {
      type: String,
      default: null,
      validator: val => ['off', 'on'].includes(val)
    },
    /**
     * Adds an asterisk (*) before the input label to indicate mandatory field
     */
    mandatory: Boolean,
    /**
     * Validation rules
     */
    rules: Object,
    /**
     * Error messages
     */
    messages: Object
  },
  emits: ['blur', 'focus', 'input', 'change'],
  data() {
    return {
      val: this.value || isNumber(this.value) ? this.value : null,
      inputWidth: '120px',
      formattedValue: ''
    };
  },
  computed: {
    classes() {
      return {
        'has-value': this.val,
        'has-error': this.validity === 'false',
        'shows-placeholder': this.placeholder && !this.val,
        'bb-big-input-currency--background': this.background,
        'bb-big-input-currency--disabled': this.disabled,
        'bb-big-input-currency--readonly': this.readonly,
        'bb-big-input-currency--emphasize-label': this.emphasizeLabel && this.validity === 'false'
      };
    },
    computedCurrencyProps() {
      return {
        min: this.min,
        max: this.max,
        locale: this.locale,
        decimals: this.decimals,
        minDecimals: this.minDecimals,
        maxDecimals: this.maxDecimals,
        currency: this.currency,
        currencyDisplay: this.currencyDisplay,
        ...this.currencyProps
      };
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `errorMessage-${slugify(this.name)}`;
    }
  },
  watch: {
    val: 'setInputWidth',
    formattedValue: 'setInputWidth',
    value(newVal) {
      this.val = newVal;
      if (this.val !== newVal) {
        this.$emit('input', this.val);
      }
    }
  },
  mounted() {
    this.setInputWidth();
  },
  methods: {
    setInputWidth() {
      this.$nextTick(() => {
        if (this.$refs.sizeKeeper) {
          this.inputWidth = `${this.$refs.sizeKeeper.offsetWidth}px`;
        }
      });
    },
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    onInput(value) {
      /**
       * Input event.
       *
       * @event input
       * @type {number}
       */
      this.$emit('input', value);
    },
    onChange(value) {
      /**
       * Change event.
       *
       * @event change
       * @type {number}
       */
      this.$emit('change', value);
    },
    onFormat(event) {
      this.formattedValue = event;
      setTimeout(() => {
        this.setInputWidth();
      }, 10);
    },
    onFocus() {
      /**
       * Focus event.
       *
       * @event focus
       * @type {string}
       */
      this.$emit('focus');
    },
    onBlur() {
      /**
       * Blur event.
       *
       * @event blur
       * @type {string}
       */
      this.$emit('blur');
    }
  }
});

/* script */
const __vue_script__$2y = script$2y;

/* template */
var __vue_render__$2q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('label', {
    ref: "root",
    staticClass: "bb-big-input-currency",
    class: _vm.classes,
    attrs: {
      "data-valid": _vm.validity,
      "for": _vm.id,
      "data-vv-value-path": "val",
      "data-testid": "bb-big-input-currency"
    }
  }, [_c('div', {
    staticClass: "bb-big-input-currency__input-group",
    style: {
      width: _vm.inputWidth
    }
  }, [_c('span', {
    ref: "sizeKeeper",
    staticClass: "bb-big-input-currency__size-keeper",
    attrs: {
      "aria-hidden": "true"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.formattedValue || _vm.placeholder) + "\n    ")]), _vm._v(" "), _c('bb-functional-input-currency', _vm._b({
    ref: "input",
    staticClass: "bb-big-input-currency__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "type": _vm.type,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "autocomplete": _vm.autocomplete,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "readonly": _vm.readonly
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": _vm.onInput,
      "change": _vm.onChange,
      "format": _vm.onFormat
    },
    model: {
      value: _vm.val,
      callback: function ($$v) {
        _vm.val = $$v;
      },
      expression: "val"
    }
  }, 'bb-functional-input-currency', _vm.computedCurrencyProps, false))], 1), _vm._v(" "), _vm.label || _vm.$slots.default ? _c('span', {
    staticClass: "bb-big-input-currency__label",
    attrs: {
      "data-testid": "bb-big-input-currency__label"
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-big-input-currency__mandatory-indicator",
      attrs: {
        "data-testid": "bb-big-input-currency__mandatory-indicator"
      }
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasValidationError ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n  ")]) : _vm._e()]);
};
var __vue_staticRenderFns__$2q = [];

/* style */
const __vue_inject_styles__$2y = function (inject) {
  if (!inject) return;
  inject("data-v-bd81d6c8_0", {
    source: ".bb-big-input-currency{display:flex;overflow:hidden;flex-direction:column;align-items:center;padding:.9375rem;cursor:text}.bb-big-input-currency.bb-big-input-currency--readonly{cursor:default}.bb-big-input-currency.bb-big-input-currency--disabled{cursor:not-allowed}.bb-big-input-currency--background{border-radius:.3125rem;background-color:#f7f7f8}.bb-big-input-currency__input-group{position:relative;min-width:7.5rem;margin-bottom:.125rem}.bb-big-input-currency__input{padding-right:0;padding-bottom:0;border-bottom:.0625rem solid transparent;text-overflow:unset}.bb-big-input-currency:not(.has-value) .bb-big-input-currency__input{border-bottom:.0625rem solid #89a}.bb-big-input-currency__input:focus,.bb-big-input-currency__input:hover{border-color:#00285a;transition:color .2s,background-color .2s,border-color .2s}.bb-big-input-currency--disabled .bb-big-input-currency__input:focus,.bb-big-input-currency--disabled .bb-big-input-currency__input:hover,.bb-big-input-currency--readonly .bb-big-input-currency__input:focus,.bb-big-input-currency--readonly .bb-big-input-currency__input:hover{border-color:transparent}.bb-big-input-currency__input:focus::placeholder{color:transparent}@-webkit-keyframes autofill{to{background:0 0;color:#2485e5}}.bb-big-input-currency__input,.bb-big-input-currency__size-keeper{color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.5rem;line-height:1.1;text-align:center}.bb-big-input-currency--disabled>*>.bb-big-input-currency__input,.bb-big-input-currency--disabled>*>.bb-big-input-currency__size-keeper{color:#89a;opacity:.6}.shows-placeholder .bb-big-input-currency__input,.shows-placeholder .bb-big-input-currency__size-keeper{color:#89a}.bb-big-input-currency__input::-webkit-input-placeholder,.bb-big-input-currency__size-keeper::-webkit-input-placeholder{color:#89a;opacity:1}.bb-big-input-currency__input:-moz-placeholder,.bb-big-input-currency__size-keeper:-moz-placeholder{color:#89a;opacity:1}.bb-big-input-currency__input::-moz-placeholder,.bb-big-input-currency__size-keeper::-moz-placeholder{color:#89a;opacity:1}.bb-big-input-currency__input:-ms-input-placeholder,.bb-big-input-currency__size-keeper:-ms-input-placeholder{color:#89a;opacity:1}.bb-big-input-currency__input::placeholder,.bb-big-input-currency__size-keeper::placeholder{color:#89a;opacity:1}.bb-big-input-currency__size-keeper{position:absolute;top:0;left:50%;opacity:0;pointer-events:none;speak:none;transform:translateX(-50%);user-select:none;white-space:nowrap}.bb-big-input-currency__label{margin-bottom:.0625rem;color:#89a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-big-input-currency--emphasize-label .bb-big-input-currency__label{color:#00285a}.bb-big-input-currency--disabled>.bb-big-input-currency__label{color:#89a;opacity:.6}.bb-big-input-currency__mandatory-indicator{color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2y = undefined;
/* module identifier */
const __vue_module_identifier__$2y = undefined;
/* functional template */
const __vue_is_functional_template__$2y = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2q,
  staticRenderFns: __vue_staticRenderFns__$2q
}, __vue_inject_styles__$2y, __vue_script__$2y, __vue_scope_id__$2y, __vue_is_functional_template__$2y, __vue_module_identifier__$2y, false, createInjector, undefined, undefined);

var script$2x = {
  name: 'bb-box',
  props: {
    thin: Boolean,
    dashed: Boolean
  },
  computed: {
    classes() {
      return ['bb-box', {
        'bb-box--thin': this.thin,
        'bb-box--dashed': this.dashed
      }];
    }
  }
};

/* script */
const __vue_script__$2x = script$2x;

/* template */
var __vue_render__$2p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$2p = [];

/* style */
const __vue_inject_styles__$2x = function (inject) {
  if (!inject) return;
  inject("data-v-436d03a9_0", {
    source: ".bb-box{padding:.9375rem;border:2px solid #dee3e8;border-radius:.625rem;transition:border-color .2s}.bb-box--thin{border:1px solid #dee3e8;border-radius:.375rem}.bb-box--dashed{border:1px dashed #dee3e8}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2x = undefined;
/* module identifier */
const __vue_module_identifier__$2x = undefined;
/* functional template */
const __vue_is_functional_template__$2x = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2x = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2p,
  staticRenderFns: __vue_staticRenderFns__$2p
}, __vue_inject_styles__$2x, __vue_script__$2x, __vue_scope_id__$2x, __vue_is_functional_template__$2x, __vue_module_identifier__$2x, false, createInjector, undefined, undefined);

var HrefHandler = {
  props: {
    to: {
      type: [String, Object]
    },
    target: {
      type: String,
      validator: target => ['_self', '_blank', '_parent', '_top'].includes(target)
    }
  },
  computed: {
    isRouterLink() {
      const protocols = ['//', 'http:', 'https:', 'mailto:', 'tel:'];
      return this.$router && this.to && (typeof this.to === 'object' || !protocols.some(protocol => this.to.startsWith(protocol)));
    }
  },
  methods: {
    resolveHref() {
      if (this.isRouterLink) {
        return this.$router.resolve(this.to).href;
      }
      return this.to;
    }
  }
};

const randomDuration = () => ({
  animationDuration: `${Math.random() * 100 / 100 + 0.6}s`
});
const randomDelay = () => ({
  animationDelay: `${Math.random() * 100 / 100 - 0.2}s`
});
var script$2w = {
  name: 'bb-spinner',
  functional: true,
  props: {
    size: Number,
    color: {
      type: String,
      default: 'mint'
    },
    small: Boolean
  },
  render(h, context) {
    const options = {
      staticClass: 'bb-spinner',
      class: {
        'bb-spinner--small': context.props.small,
        [`b-${context.props.color}`]: context.props.small && context.props.color
      },
      style: !context.props.small ? {
        width: `${context.props.size}px`,
        height: `${context.props.size}px`
      } : null,
      attrs: {
        'title': 'Loading',
        'role': 'progressbar',
        'aria-live': 'polite'
      }
    };
    const spinnerItem = !context.props.small ? Array.from({
      length: 9
    }, (_, i) => h('div', {
      key: i,
      staticClass: 'bb-spinner__item'
    }, [h('div', {
      staticClass: 'bb-spinner__bubble',
      class: `bg-${context.props.color}`,
      style: [randomDuration(), randomDelay()]
    })])) : null;
    return h('div', options, spinnerItem);
  }
};

/* script */
const __vue_script__$2w = script$2w;

/* template */

/* style */
const __vue_inject_styles__$2w = function (inject) {
  if (!inject) return;
  inject("data-v-1d64550a_0", {
    source: ".bb-spinner{position:relative;display:flex;width:3.5625rem;height:3.5625rem;box-sizing:border-box;flex-flow:row wrap;flex-shrink:0;line-height:0}.bb-spinner__item{display:flex;height:33%;flex:1 0 33%;padding:2px}.bb-spinner__bubble{display:block;width:100%;height:100%;border-radius:50%;margin:auto;animation:bubble ease infinite alternate;opacity:.7}.bb-spinner--small{width:1.5rem;height:1.5rem}.bb-spinner--small::after,.bb-spinner--small::before{position:absolute;display:block;width:100%;height:100%;border-width:.1875rem;border-style:solid;border-radius:50%;margin:auto 0;content:\"\"}.bb-spinner--small::before{border-color:inherit;opacity:.2}.bb-spinner--small::after{border-color:transparent;border-top-color:inherit;animation:rotate-full .7s linear infinite}@keyframes bubble{from{transform:scale(.5)}to{opacity:1}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2w = undefined;
/* module identifier */
const __vue_module_identifier__$2w = undefined;
/* functional template */
const __vue_is_functional_template__$2w = undefined;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2w = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$2w, __vue_script__$2w, __vue_scope_id__$2w, __vue_is_functional_template__$2w, __vue_module_identifier__$2w, false, createInjector, undefined, undefined);

var EmitClickEvent = {
  emits: ['click'],
  methods: {
    emitClickEvent(event) {
      this.$emit('click', event);
    }
  }
};

var script$2v = {
  name: 'bb-button',
  mixins: [HrefHandler, Accessibility, EmitClickEvent],
  props: {
    color: {
      type: String,
      default: 'mint',
      validator: value => validateWithDeprecationWarning('BbButton', 'color', value, {
        'mint': 'mint',
        'green': 'mint',
        'blue': 'blue',
        'navy': 'navy',
        'red': 'red',
        'gray': 'gray',
        'yellow': 'gray',
        'gray-90': 'gray',
        'black': 'black',
        'white': 'white',
        'contrast': 'white'
      })
    },
    size: {
      type: String,
      validator: value => validateWithDeprecationWarning('BbButton', 'size', value, {
        'sm': 'sm',
        'mini': 'sm',
        'small': 'sm',
        'md': 'md',
        'regular': 'md',
        'lg': 'lg',
        'big': 'lg'
      }),
      default: 'md'
    },
    display: {
      type: String,
      validator: type => ['narrow', 'block', 'blockMobileOnly', 'circle', 'circleMobileOnly', 'image', 'link', 'square', 'squareMobileOnly', 'ticket'].includes(type)
    },
    inverted: {
      type: [Boolean, String],
      validator: value => [true, false, 'filled'].includes(value)
    },
    icon: [Boolean, String],
    iconAfter: [Boolean, String],
    iconFixed: Boolean,
    iconAfterFixed: Boolean,
    uppercase: Boolean,
    capitalize: Boolean,
    corner: {
      type: String,
      validator: value => validateWithDeprecationWarning('BbButton', 'corner', value, {
        'sharp': 'sharp',
        'sharpMobileOnly': 'sharpMobileOnly',
        'rounded': ''
      })
    },
    type: {
      type: String,
      default: 'button',
      validator: type => ['button', 'reset', 'submit'].includes(type)
    },
    title: String,
    disabled: Boolean,
    readOnly: Boolean,
    loading: Boolean,
    label: String,
    tag: String,
    noUnderline: Boolean,
    noBackground: Boolean,
    noPaddings: Boolean,
    noSidePaddings: Boolean,
    iconColor: String,
    iconAfterColor: String,
    iconRotate: String,
    iconAfterRotate: String,
    iconSize: {
      type: [String, Number],
      validator: size => ALLOWED_ICON_SIZES.includes(+size)
    },
    iconAfterSize: {
      type: [String, Number],
      validator: size => ALLOWED_ICON_SIZES.includes(+size)
    }
  },
  emits: ['focus', 'blur'],
  data() {
    return {
      isHandlingRouterLink: false
    };
  },
  computed: {
    hasText() {
      const hasIcons = this.icon || this.iconAfter;
      const hasTextInSlot = hasIcons && this.$slots.default;
      return this.label || hasTextInSlot;
    },
    computedIconSize() {
      const buttonSizesWithSmallIcons = ['sm', 'mini',
      'small'
      ];
      const buttonSizesWithBigIcons = ['lg', 'big'
      ];
      const buttonDisplayWithBigIcons = ['circle', 'square'];
      const isNotDisplayLink = this.display !== 'link';
      return buttonSizesWithSmallIcons.includes(this.size) && isNotDisplayLink ? 18 : buttonSizesWithBigIcons.includes(this.size) && buttonDisplayWithBigIcons.includes(this.display) && isNotDisplayLink ? 36 : 24;
    },
    classList() {
      const staticClasses = ['bb-button'];
      const modifiersFromPropName = ['disabled', 'loading', 'readOnly', 'icon', 'iconAfter', 'iconFixed', 'iconAfterFixed', 'label', 'noUnderline', 'noBackground', 'noPaddings', 'noSidePaddings', 'uppercase', 'capitalize'].filter(propName => this[propName]);
      const modifiersFromPropValue = ['color', 'size', 'display', 'corner'].filter(propName => this[propName]).map(propName => this[propName]);
      const modifiersFromPropNameAndValue = ['inverted'].filter(propName => this[propName]).map(propName => typeof this[propName] === 'string' ? `${propName}-${this[propName]}` : propName);
      const BEMifiedModifierClasses = [...modifiersFromPropName, ...modifiersFromPropValue, ...modifiersFromPropNameAndValue].map(modifier => `bb-button--${slugify(modifier)}`);
      return [...staticClasses, ...BEMifiedModifierClasses];
    },
    isButton() {
      return !this.to;
    }
  },
  render(h) {
    const tag = this.tag ? this.tag : this.isButton ? 'button' : 'a';
    const options = {
      attrs: {
        href: this.resolveHref(),
        type: this.isButton ? this.type : null,
        target: this.target ? this.target : null,
        rel: this.target === '_blank' ? 'noopener noreferrer' : null,
        title: this.title,
        disabled: this.disabled,
        tabindex: this.readOnly ? -1 : this.tabindex,
        'data-testid': 'bb-button'
      },
      class: this.classList,
      on: {
        click: event => {
          this.emitClickEvent(event);
          if (this.isRouterLink) {
            event.preventDefault();
            if (this.isHandlingRouterLink) {
              return;
            }
            this.isHandlingRouterLink = true;
            this.$router.push(this.to, () => this.isHandlingRouterLink = false, () => this.isHandlingRouterLink = false);
          }
        },
        focus: event => {
          this.$emit('focus', event);
        },
        blur: event => {
          this.$emit('blur', event);
        }
      }
    };
    const loadingCircleOrSquare = this.loading && (this.display === 'circle' || this.display === 'square');
    const icon = this.icon && !loadingCircleOrSquare ? h(__vue_component__$2C, {
      props: {
        name: this.icon,
        size: this.iconSize || this.computedIconSize,
        fill: this.iconColor,
        rotate: this.iconRotate
      },
      class: {
        'bb-button__icon': true,
        'bb-button__icon--has-text': this.hasText
      }
    }) : null;
    const label = this.label ? h('span', {
      class: 'bb-button__label'
    }, this.label.trim()) : null;
    const iconAfter = this.iconAfter && !loadingCircleOrSquare ? h(__vue_component__$2C, {
      props: {
        name: this.iconAfter,
        size: this.iconAfterSize || this.computedIconSize,
        fill: this.iconAfterColor,
        rotate: this.iconAfterRotate
      },
      class: {
        'bb-button__icon-after': true,
        'bb-button__icon-after--has-text': this.hasText
      }
    }) : null;
    const spinner = this.loading ? h(__vue_component__$2w, {
      props: {
        small: true,
        color: 'white'
      },
      class: {
        'bb-button__spinner': true
      }
    }) : null;
    const children = [icon, this.$slots.default, label, iconAfter, spinner];
    return h(tag, options, children);
  }
};

/* script */
const __vue_script__$2v = script$2v;

/* template */

/* style */
const __vue_inject_styles__$2v = function (inject) {
  if (!inject) return;
  inject("data-v-5cd9c56e_0", {
    source: ".bb-button{position:relative;display:inline-flex;min-height:2.5rem;align-items:center;justify-content:center;padding:0;padding-right:1.5625rem;padding-left:1.5625rem;border:none;border-radius:.375rem;margin:0;box-shadow:none;cursor:pointer;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;outline:0;text-decoration:none;transition:background-color .2s,box-shadow .2s}.bb-button.bb-button--mint{background-color:#0ccca2;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--mint:hover{background-color:#0bb48f}.bb-button.bb-button--mint:active,.bb-button.bb-button--mint:focus{background-color:#099c7c}.bb-button.bb-button--mint.bb-button--green:focus,.bb-button.bb-button--mint.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--mint.bb-button--blue:focus,.bb-button.bb-button--mint.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--mint.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--mint.bb-button--black:focus,.bb-button.bb-button--mint.bb-button--gray-90:focus,.bb-button.bb-button--mint.bb-button--gray:focus,.bb-button.bb-button--mint.bb-button--white:focus,.bb-button.bb-button--mint.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--mint.bb-button--disabled,.bb-button.bb-button--mint[disabled]{background-color:#c1cad2}.bb-button.bb-button--mint.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--mint.bb-button--white:active,.bb-button.bb-button--mint.bb-button--white:focus,.bb-button.bb-button--mint.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--mint.bb-button--white:focus,.bb-button.bb-button--mint.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--mint.bb-button--white:active{color:#099c7c}.bb-button.bb-button--mint.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--mint.bb-button--white.bb-button--disabled,.bb-button.bb-button--mint.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--mint.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--mint.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--mint.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--mint.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--mint.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--mint.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--mint.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--mint.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--mint.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--mint.bb-button--read-only{box-shadow:none}.bb-button.bb-button--mint.bb-button--read-only.bb-button--green,.bb-button.bb-button--mint.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--mint.bb-button--read-only.bb-button--blue,.bb-button.bb-button--mint.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--mint.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--mint.bb-button--read-only.bb-button--black,.bb-button.bb-button--mint.bb-button--read-only.bb-button--gray,.bb-button.bb-button--mint.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--mint.bb-button--read-only.bb-button--white,.bb-button.bb-button--mint.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--mint.bb-button--inverted{background-color:transparent;color:#0ccca2}.bb-button.bb-button--mint.bb-button--inverted::after{border-color:#0ccca2}.bb-button.bb-button--mint.bb-button--inverted:active,.bb-button.bb-button--mint.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--mint.bb-button--inverted:hover{color:#0bb48f}.bb-button.bb-button--mint.bb-button--inverted:hover::after{border-color:#0bb48f}.bb-button.bb-button--mint.bb-button--inverted:active,.bb-button.bb-button--mint.bb-button--inverted:focus{color:#099c7c}.bb-button.bb-button--mint.bb-button--inverted:active::after,.bb-button.bb-button--mint.bb-button--inverted:focus::after{border-color:#099c7c}.bb-button.bb-button--mint.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--mint.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--mint.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--mint.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--mint.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--mint.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--mint.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--mint.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--mint.bb-button--inverted-filled{background-color:#fff;color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--mint.bb-button--inverted-filled:active,.bb-button.bb-button--mint.bb-button--inverted-filled:focus,.bb-button.bb-button--mint.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--mint.bb-button--inverted-filled:hover{color:#0bb48f}.bb-button.bb-button--mint.bb-button--inverted-filled:active,.bb-button.bb-button--mint.bb-button--inverted-filled:focus{color:#099c7c}.bb-button.bb-button--mint.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--mint.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--mint.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--mint.bb-button--image{background-color:transparent}.bb-button.bb-button--mint.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--mint.bb-button--image:active,.bb-button.bb-button--mint.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--mint.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--mint.bb-button--image:active::after,.bb-button.bb-button--mint.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--mint.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--mint.bb-button--image.bb-button--disabled,.bb-button.bb-button--mint.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--mint.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--mint.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--mint.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--mint.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--mint.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--mint.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--mint.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--mint.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--mint.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--mint.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--green{background-color:#0ccca2;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--green:hover{background-color:#0bb48f}.bb-button.bb-button--green:active,.bb-button.bb-button--green:focus{background-color:#099c7c}.bb-button.bb-button--green.bb-button--green:focus,.bb-button.bb-button--green.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--green.bb-button--blue:focus,.bb-button.bb-button--green.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--green.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--green.bb-button--black:focus,.bb-button.bb-button--green.bb-button--gray-90:focus,.bb-button.bb-button--green.bb-button--gray:focus,.bb-button.bb-button--green.bb-button--white:focus,.bb-button.bb-button--green.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--green.bb-button--disabled,.bb-button.bb-button--green[disabled]{background-color:#c1cad2}.bb-button.bb-button--green.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--green.bb-button--white:active,.bb-button.bb-button--green.bb-button--white:focus,.bb-button.bb-button--green.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--green.bb-button--white:focus,.bb-button.bb-button--green.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--green.bb-button--white:active{color:#099c7c}.bb-button.bb-button--green.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--green.bb-button--white.bb-button--disabled,.bb-button.bb-button--green.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--green.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--green.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--green.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--green.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--green.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--green.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--green.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--green.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--green.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--green.bb-button--read-only{box-shadow:none}.bb-button.bb-button--green.bb-button--read-only.bb-button--green,.bb-button.bb-button--green.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--green.bb-button--read-only.bb-button--blue,.bb-button.bb-button--green.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--green.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--green.bb-button--read-only.bb-button--black,.bb-button.bb-button--green.bb-button--read-only.bb-button--gray,.bb-button.bb-button--green.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--green.bb-button--read-only.bb-button--white,.bb-button.bb-button--green.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--green.bb-button--inverted{background-color:transparent;color:#0ccca2}.bb-button.bb-button--green.bb-button--inverted::after{border-color:#0ccca2}.bb-button.bb-button--green.bb-button--inverted:active,.bb-button.bb-button--green.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--green.bb-button--inverted:hover{color:#0bb48f}.bb-button.bb-button--green.bb-button--inverted:hover::after{border-color:#0bb48f}.bb-button.bb-button--green.bb-button--inverted:active,.bb-button.bb-button--green.bb-button--inverted:focus{color:#099c7c}.bb-button.bb-button--green.bb-button--inverted:active::after,.bb-button.bb-button--green.bb-button--inverted:focus::after{border-color:#099c7c}.bb-button.bb-button--green.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--green.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--green.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--green.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--green.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--green.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--green.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--green.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--green.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--green.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--green.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--green.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--green.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--green.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--green.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--green.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--green.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--green.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--green.bb-button--inverted-filled{background-color:#fff;color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--green.bb-button--inverted-filled:active,.bb-button.bb-button--green.bb-button--inverted-filled:focus,.bb-button.bb-button--green.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--green.bb-button--inverted-filled:hover{color:#0bb48f}.bb-button.bb-button--green.bb-button--inverted-filled:active,.bb-button.bb-button--green.bb-button--inverted-filled:focus{color:#099c7c}.bb-button.bb-button--green.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--green.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--green.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--green.bb-button--image{background-color:transparent}.bb-button.bb-button--green.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--green.bb-button--image:active,.bb-button.bb-button--green.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--green.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--green.bb-button--image:active::after,.bb-button.bb-button--green.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--green.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--green.bb-button--image.bb-button--disabled,.bb-button.bb-button--green.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--green.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--green.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--green.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--green.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--green.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--green.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--green.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--green.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--green.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--green.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--blue{background-color:#2485e5;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--blue:hover{background-color:#1978d6}.bb-button.bb-button--blue:active,.bb-button.bb-button--blue:focus{background-color:#176bbf}.bb-button.bb-button--blue.bb-button--green:focus,.bb-button.bb-button--blue.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--blue.bb-button--blue:focus,.bb-button.bb-button--blue.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--blue.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--blue.bb-button--black:focus,.bb-button.bb-button--blue.bb-button--gray-90:focus,.bb-button.bb-button--blue.bb-button--gray:focus,.bb-button.bb-button--blue.bb-button--white:focus,.bb-button.bb-button--blue.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--blue.bb-button--disabled,.bb-button.bb-button--blue[disabled]{background-color:#c1cad2}.bb-button.bb-button--blue.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--blue.bb-button--white:active,.bb-button.bb-button--blue.bb-button--white:focus,.bb-button.bb-button--blue.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--blue.bb-button--white:focus,.bb-button.bb-button--blue.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--blue.bb-button--white:active{color:#099c7c}.bb-button.bb-button--blue.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--blue.bb-button--white.bb-button--disabled,.bb-button.bb-button--blue.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--blue.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--blue.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--blue.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--blue.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--blue.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--blue.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--blue.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--blue.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--blue.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--blue.bb-button--read-only{box-shadow:none}.bb-button.bb-button--blue.bb-button--read-only.bb-button--green,.bb-button.bb-button--blue.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--blue.bb-button--read-only.bb-button--blue,.bb-button.bb-button--blue.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--blue.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--blue.bb-button--read-only.bb-button--black,.bb-button.bb-button--blue.bb-button--read-only.bb-button--gray,.bb-button.bb-button--blue.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--blue.bb-button--read-only.bb-button--white,.bb-button.bb-button--blue.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--blue.bb-button--inverted{background-color:transparent;color:#2485e5}.bb-button.bb-button--blue.bb-button--inverted::after{border-color:#2485e5}.bb-button.bb-button--blue.bb-button--inverted:active,.bb-button.bb-button--blue.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--blue.bb-button--inverted:hover{color:#1978d6}.bb-button.bb-button--blue.bb-button--inverted:hover::after{border-color:#1978d6}.bb-button.bb-button--blue.bb-button--inverted:active,.bb-button.bb-button--blue.bb-button--inverted:focus{color:#176bbf}.bb-button.bb-button--blue.bb-button--inverted:active::after,.bb-button.bb-button--blue.bb-button--inverted:focus::after{border-color:#176bbf}.bb-button.bb-button--blue.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--blue.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--blue.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--blue.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--blue.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--blue.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--blue.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--blue.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--blue.bb-button--inverted-filled{background-color:#fff;color:#2485e5;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--blue.bb-button--inverted-filled:active,.bb-button.bb-button--blue.bb-button--inverted-filled:focus,.bb-button.bb-button--blue.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--blue.bb-button--inverted-filled:hover{color:#1978d6}.bb-button.bb-button--blue.bb-button--inverted-filled:active,.bb-button.bb-button--blue.bb-button--inverted-filled:focus{color:#176bbf}.bb-button.bb-button--blue.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--blue.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#2485e5}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:hover{color:#1978d6}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:focus{color:#176bbf}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--blue.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--blue.bb-button--image{background-color:transparent}.bb-button.bb-button--blue.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--blue.bb-button--image:active,.bb-button.bb-button--blue.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--blue.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--blue.bb-button--image:active::after,.bb-button.bb-button--blue.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--blue.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--blue.bb-button--image.bb-button--disabled,.bb-button.bb-button--blue.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--blue.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--blue.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--blue.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--blue.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--blue.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--blue.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--blue.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--blue.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--blue.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--blue.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--navy{background-color:#00285a;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--navy:hover{background-color:#001d41}.bb-button.bb-button--navy:active,.bb-button.bb-button--navy:focus{background-color:#001127}.bb-button.bb-button--navy.bb-button--green:focus,.bb-button.bb-button--navy.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--navy.bb-button--blue:focus,.bb-button.bb-button--navy.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--navy.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--navy.bb-button--black:focus,.bb-button.bb-button--navy.bb-button--gray-90:focus,.bb-button.bb-button--navy.bb-button--gray:focus,.bb-button.bb-button--navy.bb-button--white:focus,.bb-button.bb-button--navy.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--navy.bb-button--disabled,.bb-button.bb-button--navy[disabled]{background-color:#c1cad2}.bb-button.bb-button--navy.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--navy.bb-button--white:active,.bb-button.bb-button--navy.bb-button--white:focus,.bb-button.bb-button--navy.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--navy.bb-button--white:focus,.bb-button.bb-button--navy.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--navy.bb-button--white:active{color:#099c7c}.bb-button.bb-button--navy.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--navy.bb-button--white.bb-button--disabled,.bb-button.bb-button--navy.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--navy.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--navy.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--navy.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--navy.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--navy.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--navy.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--navy.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--navy.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--navy.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--navy.bb-button--read-only{box-shadow:none}.bb-button.bb-button--navy.bb-button--read-only.bb-button--green,.bb-button.bb-button--navy.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--navy.bb-button--read-only.bb-button--blue,.bb-button.bb-button--navy.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--navy.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--navy.bb-button--read-only.bb-button--black,.bb-button.bb-button--navy.bb-button--read-only.bb-button--gray,.bb-button.bb-button--navy.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--navy.bb-button--read-only.bb-button--white,.bb-button.bb-button--navy.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--navy.bb-button--inverted{background-color:transparent;color:#00285a}.bb-button.bb-button--navy.bb-button--inverted::after{border-color:#00285a}.bb-button.bb-button--navy.bb-button--inverted:active,.bb-button.bb-button--navy.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--navy.bb-button--inverted:hover{color:#001d41}.bb-button.bb-button--navy.bb-button--inverted:hover::after{border-color:#001d41}.bb-button.bb-button--navy.bb-button--inverted:active,.bb-button.bb-button--navy.bb-button--inverted:focus{color:#001127}.bb-button.bb-button--navy.bb-button--inverted:active::after,.bb-button.bb-button--navy.bb-button--inverted:focus::after{border-color:#001127}.bb-button.bb-button--navy.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--navy.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--navy.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--navy.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--navy.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--navy.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--navy.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--navy.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--navy.bb-button--inverted-filled{background-color:#fff;color:#00285a;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--navy.bb-button--inverted-filled:active,.bb-button.bb-button--navy.bb-button--inverted-filled:focus,.bb-button.bb-button--navy.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--navy.bb-button--inverted-filled:hover{color:#001d41}.bb-button.bb-button--navy.bb-button--inverted-filled:active,.bb-button.bb-button--navy.bb-button--inverted-filled:focus{color:#001127}.bb-button.bb-button--navy.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--navy.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#00285a}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:hover{color:#001d41}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:focus{color:#001127}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--navy.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--navy.bb-button--image{background-color:transparent}.bb-button.bb-button--navy.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--navy.bb-button--image:active,.bb-button.bb-button--navy.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--navy.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--navy.bb-button--image:active::after,.bb-button.bb-button--navy.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--navy.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--navy.bb-button--image.bb-button--disabled,.bb-button.bb-button--navy.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--navy.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--navy.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--navy.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--navy.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--navy.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--navy.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--navy.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--navy.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--navy.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--navy.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--red{background-color:#ff7155;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--red:hover{background-color:#ff5c3c}.bb-button.bb-button--red:active,.bb-button.bb-button--red:focus{background-color:#ff4622}.bb-button.bb-button--red.bb-button--green:focus,.bb-button.bb-button--red.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--red.bb-button--blue:focus,.bb-button.bb-button--red.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--red.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--red.bb-button--black:focus,.bb-button.bb-button--red.bb-button--gray-90:focus,.bb-button.bb-button--red.bb-button--gray:focus,.bb-button.bb-button--red.bb-button--white:focus,.bb-button.bb-button--red.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--red.bb-button--disabled,.bb-button.bb-button--red[disabled]{background-color:#c1cad2}.bb-button.bb-button--red.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--red.bb-button--white:active,.bb-button.bb-button--red.bb-button--white:focus,.bb-button.bb-button--red.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--red.bb-button--white:focus,.bb-button.bb-button--red.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--red.bb-button--white:active{color:#099c7c}.bb-button.bb-button--red.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--red.bb-button--white.bb-button--disabled,.bb-button.bb-button--red.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--red.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--red.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--red.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--red.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--red.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--red.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--red.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--red.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--red.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--red.bb-button--read-only{box-shadow:none}.bb-button.bb-button--red.bb-button--read-only.bb-button--green,.bb-button.bb-button--red.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--red.bb-button--read-only.bb-button--blue,.bb-button.bb-button--red.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--red.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--red.bb-button--read-only.bb-button--black,.bb-button.bb-button--red.bb-button--read-only.bb-button--gray,.bb-button.bb-button--red.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--red.bb-button--read-only.bb-button--white,.bb-button.bb-button--red.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--red.bb-button--inverted{background-color:transparent;color:#ff7155}.bb-button.bb-button--red.bb-button--inverted::after{border-color:#ff7155}.bb-button.bb-button--red.bb-button--inverted:active,.bb-button.bb-button--red.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--red.bb-button--inverted:hover{color:#ff5c3c}.bb-button.bb-button--red.bb-button--inverted:hover::after{border-color:#ff5c3c}.bb-button.bb-button--red.bb-button--inverted:active,.bb-button.bb-button--red.bb-button--inverted:focus{color:#ff4622}.bb-button.bb-button--red.bb-button--inverted:active::after,.bb-button.bb-button--red.bb-button--inverted:focus::after{border-color:#ff4622}.bb-button.bb-button--red.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--red.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--red.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--red.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--red.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--red.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--red.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--red.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--red.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--red.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--red.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--red.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--red.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--red.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--red.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--red.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--red.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--red.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--red.bb-button--inverted-filled{background-color:#fff;color:#ff7155;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--red.bb-button--inverted-filled:active,.bb-button.bb-button--red.bb-button--inverted-filled:focus,.bb-button.bb-button--red.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--red.bb-button--inverted-filled:hover{color:#ff5c3c}.bb-button.bb-button--red.bb-button--inverted-filled:active,.bb-button.bb-button--red.bb-button--inverted-filled:focus{color:#ff4622}.bb-button.bb-button--red.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--red.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#ff7155}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:hover{color:#ff5c3c}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:focus{color:#ff4622}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--red.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--red.bb-button--image{background-color:transparent}.bb-button.bb-button--red.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--red.bb-button--image:active,.bb-button.bb-button--red.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--red.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--red.bb-button--image:active::after,.bb-button.bb-button--red.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--red.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--red.bb-button--image.bb-button--disabled,.bb-button.bb-button--red.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--red.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--red.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--red.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--red.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--red.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--red.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--red.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--red.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--red.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--red.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--gray{background-color:#687d92;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--gray:hover{background-color:#5d7083}.bb-button.bb-button--gray:active,.bb-button.bb-button--gray:focus{background-color:#536474}.bb-button.bb-button--gray.bb-button--green:focus,.bb-button.bb-button--gray.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--gray.bb-button--blue:focus,.bb-button.bb-button--gray.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--gray.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--gray.bb-button--black:focus,.bb-button.bb-button--gray.bb-button--gray-90:focus,.bb-button.bb-button--gray.bb-button--gray:focus,.bb-button.bb-button--gray.bb-button--white:focus,.bb-button.bb-button--gray.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--gray.bb-button--disabled,.bb-button.bb-button--gray[disabled]{background-color:#c1cad2}.bb-button.bb-button--gray.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--gray.bb-button--white:active,.bb-button.bb-button--gray.bb-button--white:focus,.bb-button.bb-button--gray.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--gray.bb-button--white:focus,.bb-button.bb-button--gray.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--gray.bb-button--white:active{color:#099c7c}.bb-button.bb-button--gray.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--gray.bb-button--white.bb-button--disabled,.bb-button.bb-button--gray.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--gray.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--gray.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--gray.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--gray.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--gray.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--gray.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--gray.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--gray.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--gray.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--gray.bb-button--read-only{box-shadow:none}.bb-button.bb-button--gray.bb-button--read-only.bb-button--green,.bb-button.bb-button--gray.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--gray.bb-button--read-only.bb-button--blue,.bb-button.bb-button--gray.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--gray.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--gray.bb-button--read-only.bb-button--black,.bb-button.bb-button--gray.bb-button--read-only.bb-button--gray,.bb-button.bb-button--gray.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--gray.bb-button--read-only.bb-button--white,.bb-button.bb-button--gray.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--gray.bb-button--inverted{background-color:transparent;color:#687d92}.bb-button.bb-button--gray.bb-button--inverted::after{border-color:#687d92}.bb-button.bb-button--gray.bb-button--inverted:active,.bb-button.bb-button--gray.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--gray.bb-button--inverted:hover{color:#5d7083}.bb-button.bb-button--gray.bb-button--inverted:hover::after{border-color:#5d7083}.bb-button.bb-button--gray.bb-button--inverted:active,.bb-button.bb-button--gray.bb-button--inverted:focus{color:#536474}.bb-button.bb-button--gray.bb-button--inverted:active::after,.bb-button.bb-button--gray.bb-button--inverted:focus::after{border-color:#536474}.bb-button.bb-button--gray.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--gray.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--gray.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--gray.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--gray.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--gray.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--gray.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--gray.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--gray.bb-button--inverted-filled{background-color:#fff;color:#687d92;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--gray.bb-button--inverted-filled:active,.bb-button.bb-button--gray.bb-button--inverted-filled:focus,.bb-button.bb-button--gray.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--gray.bb-button--inverted-filled:hover{color:#5d7083}.bb-button.bb-button--gray.bb-button--inverted-filled:active,.bb-button.bb-button--gray.bb-button--inverted-filled:focus{color:#536474}.bb-button.bb-button--gray.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--gray.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#687d92}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:hover{color:#5d7083}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:focus{color:#536474}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--gray.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--gray.bb-button--image{background-color:transparent}.bb-button.bb-button--gray.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--gray.bb-button--image:active,.bb-button.bb-button--gray.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--gray.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--gray.bb-button--image:active::after,.bb-button.bb-button--gray.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--gray.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--gray.bb-button--image.bb-button--disabled,.bb-button.bb-button--gray.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--gray.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--gray.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--gray.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--gray.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--gray.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--gray.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--gray.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--gray.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--gray.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--gray.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--gray-90{background-color:#687d92;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--gray-90:hover{background-color:#5d7083}.bb-button.bb-button--gray-90:active,.bb-button.bb-button--gray-90:focus{background-color:#536474}.bb-button.bb-button--gray-90.bb-button--green:focus,.bb-button.bb-button--gray-90.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--gray-90.bb-button--blue:focus,.bb-button.bb-button--gray-90.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--gray-90.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--gray-90.bb-button--black:focus,.bb-button.bb-button--gray-90.bb-button--gray-90:focus,.bb-button.bb-button--gray-90.bb-button--gray:focus,.bb-button.bb-button--gray-90.bb-button--white:focus,.bb-button.bb-button--gray-90.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--gray-90.bb-button--disabled,.bb-button.bb-button--gray-90[disabled]{background-color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--gray-90.bb-button--white:active,.bb-button.bb-button--gray-90.bb-button--white:focus,.bb-button.bb-button--gray-90.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--gray-90.bb-button--white:focus,.bb-button.bb-button--gray-90.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--gray-90.bb-button--white:active{color:#099c7c}.bb-button.bb-button--gray-90.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--gray-90.bb-button--white.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--read-only{box-shadow:none}.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--green,.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--blue,.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--black,.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--gray,.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--white,.bb-button.bb-button--gray-90.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--inverted{background-color:transparent;color:#687d92}.bb-button.bb-button--gray-90.bb-button--inverted::after{border-color:#687d92}.bb-button.bb-button--gray-90.bb-button--inverted:active,.bb-button.bb-button--gray-90.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--gray-90.bb-button--inverted:hover{color:#5d7083}.bb-button.bb-button--gray-90.bb-button--inverted:hover::after{border-color:#5d7083}.bb-button.bb-button--gray-90.bb-button--inverted:active,.bb-button.bb-button--gray-90.bb-button--inverted:focus{color:#536474}.bb-button.bb-button--gray-90.bb-button--inverted:active::after,.bb-button.bb-button--gray-90.bb-button--inverted:focus::after{border-color:#536474}.bb-button.bb-button--gray-90.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--gray-90.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--gray-90.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--inverted-filled{background-color:#fff;color:#687d92;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--gray-90.bb-button--inverted-filled:active,.bb-button.bb-button--gray-90.bb-button--inverted-filled:focus,.bb-button.bb-button--gray-90.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--gray-90.bb-button--inverted-filled:hover{color:#5d7083}.bb-button.bb-button--gray-90.bb-button--inverted-filled:active,.bb-button.bb-button--gray-90.bb-button--inverted-filled:focus{color:#536474}.bb-button.bb-button--gray-90.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#687d92}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:hover{color:#5d7083}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:focus{color:#536474}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--gray-90.bb-button--image{background-color:transparent}.bb-button.bb-button--gray-90.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--gray-90.bb-button--image:active,.bb-button.bb-button--gray-90.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--gray-90.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--gray-90.bb-button--image:active::after,.bb-button.bb-button--gray-90.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--gray-90.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--gray-90.bb-button--image.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--gray-90.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--gray-90.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--yellow{background-color:#687d92;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--yellow:hover{background-color:#5d7083}.bb-button.bb-button--yellow:active,.bb-button.bb-button--yellow:focus{background-color:#536474}.bb-button.bb-button--yellow.bb-button--green:focus,.bb-button.bb-button--yellow.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--yellow.bb-button--blue:focus,.bb-button.bb-button--yellow.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--yellow.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--yellow.bb-button--black:focus,.bb-button.bb-button--yellow.bb-button--gray-90:focus,.bb-button.bb-button--yellow.bb-button--gray:focus,.bb-button.bb-button--yellow.bb-button--white:focus,.bb-button.bb-button--yellow.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--yellow.bb-button--disabled,.bb-button.bb-button--yellow[disabled]{background-color:#c1cad2}.bb-button.bb-button--yellow.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--yellow.bb-button--white:active,.bb-button.bb-button--yellow.bb-button--white:focus,.bb-button.bb-button--yellow.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--yellow.bb-button--white:focus,.bb-button.bb-button--yellow.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--yellow.bb-button--white:active{color:#099c7c}.bb-button.bb-button--yellow.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--yellow.bb-button--white.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--yellow.bb-button--read-only{box-shadow:none}.bb-button.bb-button--yellow.bb-button--read-only.bb-button--green,.bb-button.bb-button--yellow.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--yellow.bb-button--read-only.bb-button--blue,.bb-button.bb-button--yellow.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--yellow.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--yellow.bb-button--read-only.bb-button--black,.bb-button.bb-button--yellow.bb-button--read-only.bb-button--gray,.bb-button.bb-button--yellow.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--yellow.bb-button--read-only.bb-button--white,.bb-button.bb-button--yellow.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--yellow.bb-button--inverted{background-color:transparent;color:#687d92}.bb-button.bb-button--yellow.bb-button--inverted::after{border-color:#687d92}.bb-button.bb-button--yellow.bb-button--inverted:active,.bb-button.bb-button--yellow.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--yellow.bb-button--inverted:hover{color:#5d7083}.bb-button.bb-button--yellow.bb-button--inverted:hover::after{border-color:#5d7083}.bb-button.bb-button--yellow.bb-button--inverted:active,.bb-button.bb-button--yellow.bb-button--inverted:focus{color:#536474}.bb-button.bb-button--yellow.bb-button--inverted:active::after,.bb-button.bb-button--yellow.bb-button--inverted:focus::after{border-color:#536474}.bb-button.bb-button--yellow.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--yellow.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--yellow.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--yellow.bb-button--inverted-filled{background-color:#fff;color:#687d92;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--yellow.bb-button--inverted-filled:active,.bb-button.bb-button--yellow.bb-button--inverted-filled:focus,.bb-button.bb-button--yellow.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--yellow.bb-button--inverted-filled:hover{color:#5d7083}.bb-button.bb-button--yellow.bb-button--inverted-filled:active,.bb-button.bb-button--yellow.bb-button--inverted-filled:focus{color:#536474}.bb-button.bb-button--yellow.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#687d92}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:hover{color:#5d7083}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:focus{color:#536474}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--yellow.bb-button--image{background-color:transparent}.bb-button.bb-button--yellow.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--yellow.bb-button--image:active,.bb-button.bb-button--yellow.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--yellow.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--yellow.bb-button--image:active::after,.bb-button.bb-button--yellow.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--yellow.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--yellow.bb-button--image.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--yellow.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--yellow.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--black{background-color:#38414d;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--black:hover{background-color:#2d353e}.bb-button.bb-button--black:active,.bb-button.bb-button--black:focus{background-color:#23282f}.bb-button.bb-button--black.bb-button--green:focus,.bb-button.bb-button--black.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--black.bb-button--blue:focus,.bb-button.bb-button--black.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--black.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--black.bb-button--black:focus,.bb-button.bb-button--black.bb-button--gray-90:focus,.bb-button.bb-button--black.bb-button--gray:focus,.bb-button.bb-button--black.bb-button--white:focus,.bb-button.bb-button--black.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--black.bb-button--disabled,.bb-button.bb-button--black[disabled]{background-color:#c1cad2}.bb-button.bb-button--black.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--black.bb-button--white:active,.bb-button.bb-button--black.bb-button--white:focus,.bb-button.bb-button--black.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--black.bb-button--white:focus,.bb-button.bb-button--black.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--black.bb-button--white:active{color:#099c7c}.bb-button.bb-button--black.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--black.bb-button--white.bb-button--disabled,.bb-button.bb-button--black.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--black.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--black.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--black.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--black.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--black.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--black.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--black.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--black.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--black.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--black.bb-button--read-only{box-shadow:none}.bb-button.bb-button--black.bb-button--read-only.bb-button--green,.bb-button.bb-button--black.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--black.bb-button--read-only.bb-button--blue,.bb-button.bb-button--black.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--black.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--black.bb-button--read-only.bb-button--black,.bb-button.bb-button--black.bb-button--read-only.bb-button--gray,.bb-button.bb-button--black.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--black.bb-button--read-only.bb-button--white,.bb-button.bb-button--black.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--black.bb-button--inverted{background-color:transparent;color:#38414d}.bb-button.bb-button--black.bb-button--inverted::after{border-color:#38414d}.bb-button.bb-button--black.bb-button--inverted:active,.bb-button.bb-button--black.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--black.bb-button--inverted:hover{color:#2d353e}.bb-button.bb-button--black.bb-button--inverted:hover::after{border-color:#2d353e}.bb-button.bb-button--black.bb-button--inverted:active,.bb-button.bb-button--black.bb-button--inverted:focus{color:#23282f}.bb-button.bb-button--black.bb-button--inverted:active::after,.bb-button.bb-button--black.bb-button--inverted:focus::after{border-color:#23282f}.bb-button.bb-button--black.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--black.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--black.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--black.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--black.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--black.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--black.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--black.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--black.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--black.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--black.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--black.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--black.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--black.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--black.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--black.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--black.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--black.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--black.bb-button--inverted-filled{background-color:#fff;color:#38414d;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--black.bb-button--inverted-filled:active,.bb-button.bb-button--black.bb-button--inverted-filled:focus,.bb-button.bb-button--black.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--black.bb-button--inverted-filled:hover{color:#2d353e}.bb-button.bb-button--black.bb-button--inverted-filled:active,.bb-button.bb-button--black.bb-button--inverted-filled:focus{color:#23282f}.bb-button.bb-button--black.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--black.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#38414d}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:hover{color:#2d353e}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:focus{color:#23282f}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--black.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--black.bb-button--image{background-color:transparent}.bb-button.bb-button--black.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--black.bb-button--image:active,.bb-button.bb-button--black.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--black.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--black.bb-button--image:active::after,.bb-button.bb-button--black.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--black.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--black.bb-button--image.bb-button--disabled,.bb-button.bb-button--black.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--black.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--black.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--black.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--black.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--black.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--black.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--black.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--black.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--black.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--black.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--white{background-color:#fff;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--white:hover{background-color:#f2f2f2}.bb-button.bb-button--white:active,.bb-button.bb-button--white:focus{background-color:#e6e6e6}.bb-button.bb-button--white.bb-button--green:focus,.bb-button.bb-button--white.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--white.bb-button--blue:focus,.bb-button.bb-button--white.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--white.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--white.bb-button--black:focus,.bb-button.bb-button--white.bb-button--gray-90:focus,.bb-button.bb-button--white.bb-button--gray:focus,.bb-button.bb-button--white.bb-button--white:focus,.bb-button.bb-button--white.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--white.bb-button--disabled,.bb-button.bb-button--white[disabled]{background-color:#c1cad2}.bb-button.bb-button--white.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--white.bb-button--white:active,.bb-button.bb-button--white.bb-button--white:focus,.bb-button.bb-button--white.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--white.bb-button--white:focus,.bb-button.bb-button--white.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--white.bb-button--white:active{color:#099c7c}.bb-button.bb-button--white.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--white.bb-button--white.bb-button--disabled,.bb-button.bb-button--white.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--white.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--white.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--white.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--white.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--white.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--white.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--white.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--white.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--white.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--white.bb-button--read-only{box-shadow:none}.bb-button.bb-button--white.bb-button--read-only.bb-button--green,.bb-button.bb-button--white.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--white.bb-button--read-only.bb-button--blue,.bb-button.bb-button--white.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--white.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--white.bb-button--read-only.bb-button--black,.bb-button.bb-button--white.bb-button--read-only.bb-button--gray,.bb-button.bb-button--white.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--white.bb-button--read-only.bb-button--white,.bb-button.bb-button--white.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--white.bb-button--inverted{background-color:transparent;color:#fff}.bb-button.bb-button--white.bb-button--inverted::after{border-color:#fff}.bb-button.bb-button--white.bb-button--inverted:active,.bb-button.bb-button--white.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--white.bb-button--inverted:hover{color:#f2f2f2}.bb-button.bb-button--white.bb-button--inverted:hover::after{border-color:#f2f2f2}.bb-button.bb-button--white.bb-button--inverted:active,.bb-button.bb-button--white.bb-button--inverted:focus{color:#e6e6e6}.bb-button.bb-button--white.bb-button--inverted:active::after,.bb-button.bb-button--white.bb-button--inverted:focus::after{border-color:#e6e6e6}.bb-button.bb-button--white.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--white.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--white.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--white.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--white.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--white.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--white.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--white.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--white.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--white.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--white.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--white.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--white.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--white.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--white.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--white.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--white.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--white.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--white.bb-button--inverted-filled{background-color:#fff;color:#fff;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--white.bb-button--inverted-filled:active,.bb-button.bb-button--white.bb-button--inverted-filled:focus,.bb-button.bb-button--white.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--white.bb-button--inverted-filled:hover{color:#f2f2f2}.bb-button.bb-button--white.bb-button--inverted-filled:active,.bb-button.bb-button--white.bb-button--inverted-filled:focus{color:#e6e6e6}.bb-button.bb-button--white.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--white.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#fff}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:hover{color:#f2f2f2}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:focus{color:#e6e6e6}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--white.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--white.bb-button--image{background-color:transparent}.bb-button.bb-button--white.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--white.bb-button--image:active,.bb-button.bb-button--white.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--white.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--white.bb-button--image:active::after,.bb-button.bb-button--white.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--white.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--white.bb-button--image.bb-button--disabled,.bb-button.bb-button--white.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--white.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--white.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--white.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--white.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--white.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--white.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--white.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--white.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--white.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--white.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--disabled{background-color:#687d92;box-shadow:0 0 0 0 transparent;color:#fff}.bb-button.bb-button--disabled:hover{background-color:#5d7083}.bb-button.bb-button--disabled:active,.bb-button.bb-button--disabled:focus{background-color:#536474}.bb-button.bb-button--disabled.bb-button--green:focus,.bb-button.bb-button--disabled.bb-button--mint:focus{box-shadow:0 0 0 .25rem #a0eada}.bb-button.bb-button--disabled.bb-button--blue:focus,.bb-button.bb-button--disabled.bb-button--navy:focus{box-shadow:0 0 0 .25rem #b4d1ee}.bb-button.bb-button--disabled.bb-button--red:focus{box-shadow:0 0 0 .25rem #f8cbc2}.bb-button.bb-button--disabled.bb-button--black:focus,.bb-button.bb-button--disabled.bb-button--gray-90:focus,.bb-button.bb-button--disabled.bb-button--gray:focus,.bb-button.bb-button--disabled.bb-button--white:focus,.bb-button.bb-button--disabled.bb-button--yellow:focus{box-shadow:0 0 0 .25rem #c1cad2}.bb-button.bb-button--disabled.bb-button--disabled,.bb-button.bb-button--disabled[disabled]{background-color:#c1cad2}.bb-button.bb-button--disabled.bb-button--white{color:#0ccca2;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--disabled.bb-button--white:active,.bb-button.bb-button--disabled.bb-button--white:focus,.bb-button.bb-button--disabled.bb-button--white:hover{background-color:#fff}.bb-button.bb-button--disabled.bb-button--white:focus,.bb-button.bb-button--disabled.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--disabled.bb-button--white:active{color:#099c7c}.bb-button.bb-button--disabled.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--disabled.bb-button--white.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--white[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket{background-color:transparent;color:#0ccca2}.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket:focus,.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket:hover{color:#0bb48f}.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket:active,.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket:focus{color:#099c7c}.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--white.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--disabled.bb-button--read-only{box-shadow:none}.bb-button.bb-button--disabled.bb-button--read-only.bb-button--green,.bb-button.bb-button--disabled.bb-button--read-only.bb-button--mint{background-color:#a0eada}.bb-button.bb-button--disabled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--disabled.bb-button--read-only.bb-button--navy{background-color:#b4d1ee}.bb-button.bb-button--disabled.bb-button--read-only.bb-button--red{background-color:#f8cbc2}.bb-button.bb-button--disabled.bb-button--read-only.bb-button--black,.bb-button.bb-button--disabled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--disabled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--disabled.bb-button--read-only.bb-button--white,.bb-button.bb-button--disabled.bb-button--read-only.bb-button--yellow{background-color:#c1cad2}.bb-button.bb-button--disabled.bb-button--inverted{background-color:transparent;color:#687d92}.bb-button.bb-button--disabled.bb-button--inverted::after{border-color:#687d92}.bb-button.bb-button--disabled.bb-button--inverted:active,.bb-button.bb-button--disabled.bb-button--inverted:hover{background-color:#f7f7f8}.bb-button.bb-button--disabled.bb-button--inverted:hover{color:#5d7083}.bb-button.bb-button--disabled.bb-button--inverted:hover::after{border-color:#5d7083}.bb-button.bb-button--disabled.bb-button--inverted:active,.bb-button.bb-button--disabled.bb-button--inverted:focus{color:#536474}.bb-button.bb-button--disabled.bb-button--inverted:active::after,.bb-button.bb-button--disabled.bb-button--inverted:focus::after{border-color:#536474}.bb-button.bb-button--disabled.bb-button--inverted:focus{background-color:transparent}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--inverted[disabled]{background-color:#f7f7f8;color:#c1cad2}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--disabled::after,.bb-button.bb-button--disabled.bb-button--inverted[disabled]::after{border-color:#c1cad2}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:hover{color:rgba(255,255,255,.7)}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:hover::after{border-color:rgba(255,255,255,.7)}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:focus,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:hover{background-color:transparent}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:active,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:focus{color:#fff}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:active::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:focus::after{border-color:#fff}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white.bb-button--disabled::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--white[disabled]::after{border-color:rgba(255,255,255,.5)}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--green,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--mint{background:0 0;color:#a0eada}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--green::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--mint::after{border-color:#a0eada}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--blue,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--navy{background:0 0;color:#b4d1ee}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--blue::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--navy::after{border-color:#b4d1ee}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--red{background:0 0;color:#f8cbc2}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--red::after{border-color:#f8cbc2}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--black,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--gray,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--white,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--yellow{background:0 0;color:#c1cad2}.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--black::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--gray-90::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--gray::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--white::after,.bb-button.bb-button--disabled.bb-button--inverted.bb-button--read-only.bb-button--yellow::after{border-color:#c1cad2}.bb-button.bb-button--disabled.bb-button--inverted-filled{background-color:#fff;color:#687d92;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button.bb-button--disabled.bb-button--inverted-filled:active,.bb-button.bb-button--disabled.bb-button--inverted-filled:focus,.bb-button.bb-button--disabled.bb-button--inverted-filled:hover{background-color:#fff}.bb-button.bb-button--disabled.bb-button--inverted-filled:hover{color:#5d7083}.bb-button.bb-button--disabled.bb-button--inverted-filled:active,.bb-button.bb-button--disabled.bb-button--inverted-filled:focus{color:#536474}.bb-button.bb-button--disabled.bb-button--inverted-filled:focus{box-shadow:0 0 0 .25rem rgba(255,255,255,.2)}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--inverted-filled[disabled]{background-color:rgba(255,255,255,.3);color:#fff}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only:active,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only:focus:hover,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only:hover{background-color:rgba(255,255,255,.3)}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--green,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--blue,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--black,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--gray,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--gray-90,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--white,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket{background-color:transparent;color:#687d92}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:focus,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:hover{background-color:#f7f7f8}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:hover{color:#5d7083}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:active,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:focus{color:#536474}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--inverted-filled.bb-button--ticket[disabled]{background-color:transparent;color:#c1cad2}.bb-button.bb-button--disabled.bb-button--image{background-color:transparent}.bb-button.bb-button--disabled.bb-button--image::after{border-color:#687d92}.bb-button.bb-button--disabled.bb-button--image:active,.bb-button.bb-button--disabled.bb-button--image:hover{background-color:#f7f7f8}.bb-button.bb-button--disabled.bb-button--image:hover::after{border-color:#5d7083}.bb-button.bb-button--disabled.bb-button--image:active::after,.bb-button.bb-button--disabled.bb-button--image:focus::after{border-color:#536474}.bb-button.bb-button--disabled.bb-button--image:focus{box-shadow:0 0 0 .25rem rgba(104,125,146,.3)}.bb-button.bb-button--disabled.bb-button--image.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--image[disabled]{background-color:#f7f7f8}.bb-button.bb-button--disabled.bb-button--ticket:focus{box-shadow:none}.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white{background-color:transparent;color:#0ccca2}.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white:focus,.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white:hover{background-color:#f7f7f8}.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white:hover{color:#0bb48f}.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white:active,.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white:focus{color:#099c7c}.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white.bb-button--disabled,.bb-button.bb-button--disabled.bb-button--ticket.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button:active,.bb-button:focus{outline:0}.bb-button--disabled,.bb-button--read-only,.bb-button[disabled]{cursor:not-allowed}.bb-button--image::after,.bb-button--inverted::after,.bb-button--no-background::after{position:absolute;top:0;left:0;width:100%;height:100%;border:.0625rem solid transparent;border-radius:.375rem;content:\"\";pointer-events:none;transition:border .2s}.bb-button--link,.bb-button--no-background{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-button--sm,.bb-button--small{min-height:1.875rem;padding-right:1.25rem;padding-left:1.25rem;font-size:.6875rem}.bb-button--sm.bb-button--sharp,.bb-button--small.bb-button--sharp{padding-right:1.375rem;padding-left:1.375rem}.bb-button--big,.bb-button--lg{min-height:3.75rem;padding-right:2.1875rem;padding-left:2.1875rem;border-radius:.5rem;font-size:1rem}.bb-button--big::after,.bb-button--lg::after{border-radius:.5rem}.bb-button--icon{padding-right:1rem;padding-left:1rem}.bb-button--icon.bb-button--label{padding-right:1.5625rem;padding-left:1rem}.bb-button--icon.bb-button--sm,.bb-button--icon.bb-button--small{padding-right:.875rem;padding-left:.875rem}.bb-button--icon.bb-button--sm.bb-button--label,.bb-button--icon.bb-button--small.bb-button--label{padding-right:1.25rem;padding-left:.875rem}.bb-button--icon.bb-button--big,.bb-button--icon.bb-button--lg{padding-right:1.625rem;padding-left:1.625rem}.bb-button--icon.bb-button--big.bb-button--label,.bb-button--icon.bb-button--lg.bb-button--label{padding-right:2.1875rem;padding-left:1.625rem}.bb-button--icon.bb-button--narrow{padding-right:.625rem;padding-left:.625rem}.bb-button--icon.bb-button--narrow.bb-button--label{padding-right:.9375rem;padding-left:.625rem}.bb-button--icon-after{padding-right:1rem;padding-left:1rem}.bb-button--icon-after.bb-button--label{padding-right:1rem;padding-left:1.5625rem}.bb-button--icon-after.bb-button--label.bb-button--icon{padding-left:1rem}.bb-button--icon-after.bb-button--sm,.bb-button--icon-after.bb-button--small{padding-right:.875rem;padding-left:.875rem}.bb-button--icon-after.bb-button--sm.bb-button--label,.bb-button--icon-after.bb-button--small.bb-button--label{padding-right:.875rem;padding-left:1.25rem}.bb-button--icon-after.bb-button--sm.bb-button--label.bb-button--icon,.bb-button--icon-after.bb-button--small.bb-button--label.bb-button--icon{padding-left:.875rem}.bb-button--icon-after.bb-button--big,.bb-button--icon-after.bb-button--lg{padding-right:1.625rem;padding-left:1.625rem}.bb-button--icon-after.bb-button--big.bb-button--label,.bb-button--icon-after.bb-button--lg.bb-button--label{padding-right:1.625rem;padding-left:2.1875rem}.bb-button--icon-after.bb-button--big.bb-button--label.bb-button--icon,.bb-button--icon-after.bb-button--lg.bb-button--label.bb-button--icon{padding-left:1.625rem}.bb-button--icon-after.bb-button--narrow{padding-right:.625rem;padding-left:.625rem}.bb-button--icon-after.bb-button--narrow.bb-button--label{padding-right:.625rem;padding-left:.9375rem}.bb-button--icon-after.bb-button--narrow.bb-button--label.bb-button--icon{padding-left:.625rem}@media (max-width:768px){.bb-button--icon-after.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon-after.bb-button--label.bb-button--square-mobile-only,.bb-button--icon.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon.bb-button--label.bb-button--square-mobile-only{padding:0}}@media (max-width:768px){.bb-button--icon-after.bb-button--big.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon-after.bb-button--big.bb-button--label.bb-button--square-mobile-only,.bb-button--icon-after.bb-button--lg.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon-after.bb-button--lg.bb-button--label.bb-button--square-mobile-only,.bb-button--icon-after.bb-button--narrow.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon-after.bb-button--narrow.bb-button--label.bb-button--square-mobile-only,.bb-button--icon-after.bb-button--sm.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon-after.bb-button--sm.bb-button--label.bb-button--square-mobile-only,.bb-button--icon-after.bb-button--small.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon-after.bb-button--small.bb-button--label.bb-button--square-mobile-only,.bb-button--icon.bb-button--big.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon.bb-button--big.bb-button--label.bb-button--square-mobile-only,.bb-button--icon.bb-button--lg.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon.bb-button--lg.bb-button--label.bb-button--square-mobile-only,.bb-button--icon.bb-button--narrow.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon.bb-button--narrow.bb-button--label.bb-button--square-mobile-only,.bb-button--icon.bb-button--sm.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon.bb-button--sm.bb-button--label.bb-button--square-mobile-only,.bb-button--icon.bb-button--small.bb-button--label.bb-button--circle-mobile-only,.bb-button--icon.bb-button--small.bb-button--label.bb-button--square-mobile-only{padding:0}}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--block,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--block{padding-right:3rem;padding-left:3rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--block.bb-button--sm,.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--block.bb-button--small,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--block.bb-button--sm,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--block.bb-button--small{padding-right:2rem;padding-left:2rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--block.bb-button--big,.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--block.bb-button--lg,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--block.bb-button--big,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--block.bb-button--lg{padding-right:3.5rem;padding-left:3.5rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--block.bb-button--narrow,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--block.bb-button--narrow{padding-right:1rem}.bb-button--icon-fixed.bb-button--icon.bb-button--label{padding-right:3rem;padding-left:3rem}.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--sm,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--small{padding-right:2rem;padding-left:2rem}.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--big,.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--lg{padding-right:3.5rem;padding-left:3.5rem}.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--narrow{padding-right:1rem}.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--link.bb-button--link{padding-left:1.875rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label{padding-right:3rem;padding-left:3rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--sm,.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--small{padding-right:2rem;padding-left:2rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--big,.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--lg{padding-right:3.5rem;padding-left:3.5rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--narrow{padding-left:1rem}.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--link.bb-button--link{padding-right:1.875rem}.bb-button--loading{padding-right:1rem}.bb-button--loading.bb-button--sm,.bb-button--loading.bb-button--small{padding-right:.875rem}.bb-button--loading.bb-button--big,.bb-button--loading.bb-button--lg{padding-right:1.625rem}.bb-button--loading.bb-button--narrow{padding-right:.625rem}.bb-button--loading.bb-button--circle,.bb-button--loading.bb-button--square{padding:0}.bb-button--block{display:flex;width:100%}@media (max-width:768px){.bb-button--block-mobile-only{display:flex;width:100%}}.bb-button--narrow{padding-right:.9375rem;padding-left:.9375rem}.bb-button--sharp{padding-right:2rem;padding-left:2rem;border-radius:0}.bb-button--sharp::after{border-radius:0}.bb-button--sharp.bb-button--sm,.bb-button--sharp.bb-button--small{padding-right:1.375rem;padding-left:1.375rem}.bb-button--sharp.bb-button--big,.bb-button--sharp.bb-button--lg{padding-right:3.875rem;padding-left:3.875rem}@media (max-width:768px){.bb-button--sharp-mobile-only{border-radius:0}.bb-button--sharp-mobile-only::after{border-radius:0}.bb-button--sharp-mobile-only.bb-button--sm,.bb-button--sharp-mobile-only.bb-button--small{padding-right:1.375rem;padding-left:1.375rem}.bb-button--sharp-mobile-only.bb-button--big,.bb-button--sharp-mobile-only.bb-button--lg{padding-right:3.875rem;padding-left:3.875rem}}.bb-button--circle,.bb-button--square{width:2.5rem;height:2.5rem;min-height:0;flex-shrink:0;padding:0}.bb-button--circle.bb-button--mini,.bb-button--circle.bb-button--sm,.bb-button--circle.bb-button--small,.bb-button--square.bb-button--mini,.bb-button--square.bb-button--sm,.bb-button--square.bb-button--small{width:1.875rem;height:1.875rem;padding:0}.bb-button--circle.bb-button--big,.bb-button--circle.bb-button--lg,.bb-button--square.bb-button--big,.bb-button--square.bb-button--lg{width:3.75rem;height:3.75rem;padding:0}@media (max-width:768px){.bb-button--circle-mobile-only,.bb-button--square-mobile-only{width:2.5rem;height:2.5rem;min-height:0;flex-shrink:0;padding:0}}@media (max-width:768px){.bb-button--circle-mobile-only.bb-button--mini,.bb-button--circle-mobile-only.bb-button--sm,.bb-button--circle-mobile-only.bb-button--small,.bb-button--square-mobile-only.bb-button--mini,.bb-button--square-mobile-only.bb-button--sm,.bb-button--square-mobile-only.bb-button--small{width:1.875rem;height:1.875rem;padding:0}}@media (max-width:768px){.bb-button--circle-mobile-only.bb-button--big,.bb-button--circle-mobile-only.bb-button--lg,.bb-button--square-mobile-only.bb-button--big,.bb-button--square-mobile-only.bb-button--lg{width:3.75rem;height:3.75rem;padding:0}}.bb-button--circle{border-radius:50%}.bb-button--circle::after{border-radius:50%}@media (max-width:768px){.bb-button--circle-mobile-only{border-radius:50%}}@media (max-width:768px){.bb-button--circle-mobile-only::after{border-radius:50%}}.bb-button--image.bb-button{height:2.5rem;min-height:2.5rem;padding-right:1.25rem;padding-left:1.25rem;transition:background-color .2s,color .2s,box-shadow .2s}.bb-button--image.bb-button.bb-button--loading{padding-right:2.5rem;padding-left:2.5rem}.bb-button--image.bb-button img{height:1.125rem}.bb-button--disabled.bb-button--image.bb-button img,.bb-button[disabled].bb-button--image.bb-button img{opacity:.5}.bb-button--inverted{transition:background-color .2s,color .2s,box-shadow .2s}.bb-button--no-background{transition:background-color .2s,color .2s}.bb-button--no-background.bb-button--mint.bb-button--mint{background-color:transparent;color:#0ccca2}.bb-button--no-background.bb-button--mint.bb-button--mint::after{border-color:transparent}.bb-button--no-background.bb-button--mint.bb-button--mint:active,.bb-button--no-background.bb-button--mint.bb-button--mint:focus:hover,.bb-button--no-background.bb-button--mint.bb-button--mint:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--mint.bb-button--mint:focus,.bb-button--no-background.bb-button--mint.bb-button--mint:hover{color:#0bb48f}.bb-button--no-background.bb-button--mint.bb-button--mint:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--mint.bb-button--mint:focus::after{border-color:#0bb48f}.bb-button--no-background.bb-button--mint.bb-button--mint:active{color:#099c7c}.bb-button--no-background.bb-button--mint.bb-button--mint:active::after{border-color:#099c7c}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--disabled,.bb-button--no-background.bb-button--mint.bb-button--mint[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--disabled::after,.bb-button--no-background.bb-button--mint.bb-button--mint[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only:active,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white{color:#fff}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:active,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:focus,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:hover{color:#0ccca2}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:active,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:hover{background-color:rgba(12,204,162,.4)}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:active::after,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:focus::after{border-color:#0ccca2}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white[disabled]{background-color:transparent;color:rgba(12,204,162,.5)}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white[disabled]:active{color:rgba(12,204,162,.5)}.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--mint.bb-button--mint.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--green.bb-button--green{background-color:transparent;color:#0ccca2}.bb-button--no-background.bb-button--green.bb-button--green::after{border-color:transparent}.bb-button--no-background.bb-button--green.bb-button--green:active,.bb-button--no-background.bb-button--green.bb-button--green:focus:hover,.bb-button--no-background.bb-button--green.bb-button--green:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--green.bb-button--green:focus,.bb-button--no-background.bb-button--green.bb-button--green:hover{color:#0bb48f}.bb-button--no-background.bb-button--green.bb-button--green:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--green.bb-button--green:focus::after{border-color:#0bb48f}.bb-button--no-background.bb-button--green.bb-button--green:active{color:#099c7c}.bb-button--no-background.bb-button--green.bb-button--green:active::after{border-color:#099c7c}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--disabled,.bb-button--no-background.bb-button--green.bb-button--green[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--disabled::after,.bb-button--no-background.bb-button--green.bb-button--green[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only:active,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white{color:#fff}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:active,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:focus,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:hover{color:#0ccca2}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:active,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:hover{background-color:rgba(12,204,162,.4)}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:active::after,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:focus::after{border-color:#0ccca2}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white[disabled]{background-color:transparent;color:rgba(12,204,162,.5)}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white[disabled]:active{color:rgba(12,204,162,.5)}.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--green.bb-button--green.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--blue.bb-button--blue{background-color:transparent;color:#2485e5}.bb-button--no-background.bb-button--blue.bb-button--blue::after{border-color:transparent}.bb-button--no-background.bb-button--blue.bb-button--blue:active,.bb-button--no-background.bb-button--blue.bb-button--blue:focus:hover,.bb-button--no-background.bb-button--blue.bb-button--blue:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--blue.bb-button--blue:focus,.bb-button--no-background.bb-button--blue.bb-button--blue:hover{color:#1978d6}.bb-button--no-background.bb-button--blue.bb-button--blue:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--blue.bb-button--blue:focus::after{border-color:#1978d6}.bb-button--no-background.bb-button--blue.bb-button--blue:active{color:#176bbf}.bb-button--no-background.bb-button--blue.bb-button--blue:active::after{border-color:#176bbf}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--disabled,.bb-button--no-background.bb-button--blue.bb-button--blue[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--disabled::after,.bb-button--no-background.bb-button--blue.bb-button--blue[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only:active,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white{color:#fff}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:active,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:focus,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:hover{color:#2485e5}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:active,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:hover{background-color:rgba(36,133,229,.4)}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:active::after,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:focus::after{border-color:#2485e5}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white[disabled]{background-color:transparent;color:rgba(36,133,229,.5)}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white[disabled]:active{color:rgba(36,133,229,.5)}.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--blue.bb-button--blue.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--navy.bb-button--navy{background-color:transparent;color:#00285a}.bb-button--no-background.bb-button--navy.bb-button--navy::after{border-color:transparent}.bb-button--no-background.bb-button--navy.bb-button--navy:active,.bb-button--no-background.bb-button--navy.bb-button--navy:focus:hover,.bb-button--no-background.bb-button--navy.bb-button--navy:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--navy.bb-button--navy:focus,.bb-button--no-background.bb-button--navy.bb-button--navy:hover{color:#001d41}.bb-button--no-background.bb-button--navy.bb-button--navy:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--navy.bb-button--navy:focus::after{border-color:#001d41}.bb-button--no-background.bb-button--navy.bb-button--navy:active{color:#001127}.bb-button--no-background.bb-button--navy.bb-button--navy:active::after{border-color:#001127}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--disabled,.bb-button--no-background.bb-button--navy.bb-button--navy[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--disabled::after,.bb-button--no-background.bb-button--navy.bb-button--navy[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only:active,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white{color:#fff}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:active,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:focus,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:hover{color:#00285a}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:active,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:hover{background-color:rgba(0,40,90,.4)}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:active::after,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:focus::after{border-color:#00285a}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white[disabled]{background-color:transparent;color:rgba(0,40,90,.5)}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white[disabled]:active{color:rgba(0,40,90,.5)}.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--navy.bb-button--navy.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--red.bb-button--red{background-color:transparent;color:#ff7155}.bb-button--no-background.bb-button--red.bb-button--red::after{border-color:transparent}.bb-button--no-background.bb-button--red.bb-button--red:active,.bb-button--no-background.bb-button--red.bb-button--red:focus:hover,.bb-button--no-background.bb-button--red.bb-button--red:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--red.bb-button--red:focus,.bb-button--no-background.bb-button--red.bb-button--red:hover{color:#ff5c3c}.bb-button--no-background.bb-button--red.bb-button--red:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--red.bb-button--red:focus::after{border-color:#ff5c3c}.bb-button--no-background.bb-button--red.bb-button--red:active{color:#ff4622}.bb-button--no-background.bb-button--red.bb-button--red:active::after{border-color:#ff4622}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--disabled,.bb-button--no-background.bb-button--red.bb-button--red[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--disabled::after,.bb-button--no-background.bb-button--red.bb-button--red[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only:active,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white{color:#fff}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:active,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:focus,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:hover{color:#ff7155}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:active,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:hover{background-color:rgba(255,113,85,.4)}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:active::after,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:focus::after{border-color:#ff7155}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white[disabled]{background-color:transparent;color:rgba(255,113,85,.5)}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white[disabled]:active{color:rgba(255,113,85,.5)}.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--red.bb-button--red.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--gray.bb-button--gray{background-color:transparent;color:#687d92}.bb-button--no-background.bb-button--gray.bb-button--gray::after{border-color:transparent}.bb-button--no-background.bb-button--gray.bb-button--gray:active,.bb-button--no-background.bb-button--gray.bb-button--gray:focus:hover,.bb-button--no-background.bb-button--gray.bb-button--gray:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--gray.bb-button--gray:focus,.bb-button--no-background.bb-button--gray.bb-button--gray:hover{color:#5d7083}.bb-button--no-background.bb-button--gray.bb-button--gray:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--gray.bb-button--gray:focus::after{border-color:#5d7083}.bb-button--no-background.bb-button--gray.bb-button--gray:active{color:#536474}.bb-button--no-background.bb-button--gray.bb-button--gray:active::after{border-color:#536474}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--disabled,.bb-button--no-background.bb-button--gray.bb-button--gray[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--disabled::after,.bb-button--no-background.bb-button--gray.bb-button--gray[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only:active,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white{color:#fff}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:active,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:focus,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:hover{color:#687d92}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:active,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:hover{background-color:rgba(104,125,146,.4)}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:active::after,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:focus::after{border-color:#687d92}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white[disabled]{background-color:transparent;color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white[disabled]:active{color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--gray.bb-button--gray.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90{background-color:transparent;color:#687d92}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90::after{border-color:transparent}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:active,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:focus:hover,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:focus,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:hover{color:#5d7083}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:focus::after{border-color:#5d7083}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:active{color:#536474}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90:active::after{border-color:#536474}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--disabled,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--disabled::after,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only:active,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white{color:#fff}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:active,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:focus,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:hover{color:#687d92}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:active,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:hover{background-color:rgba(104,125,146,.4)}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:active::after,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:focus::after{border-color:#687d92}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white[disabled]{background-color:transparent;color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white[disabled]:active{color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--gray-90.bb-button--gray-90.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--yellow.bb-button--yellow{background-color:transparent;color:#687d92}.bb-button--no-background.bb-button--yellow.bb-button--yellow::after{border-color:transparent}.bb-button--no-background.bb-button--yellow.bb-button--yellow:active,.bb-button--no-background.bb-button--yellow.bb-button--yellow:focus:hover,.bb-button--no-background.bb-button--yellow.bb-button--yellow:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--yellow.bb-button--yellow:focus,.bb-button--no-background.bb-button--yellow.bb-button--yellow:hover{color:#5d7083}.bb-button--no-background.bb-button--yellow.bb-button--yellow:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--yellow.bb-button--yellow:focus::after{border-color:#5d7083}.bb-button--no-background.bb-button--yellow.bb-button--yellow:active{color:#536474}.bb-button--no-background.bb-button--yellow.bb-button--yellow:active::after{border-color:#536474}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--disabled,.bb-button--no-background.bb-button--yellow.bb-button--yellow[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--disabled::after,.bb-button--no-background.bb-button--yellow.bb-button--yellow[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only:active,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white{color:#fff}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:active,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:focus,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:hover{color:#687d92}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:active,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:hover{background-color:rgba(104,125,146,.4)}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:active::after,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:focus::after{border-color:#687d92}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white[disabled]{background-color:transparent;color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white[disabled]:active{color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--yellow.bb-button--yellow.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--black.bb-button--black{background-color:transparent;color:#38414d}.bb-button--no-background.bb-button--black.bb-button--black::after{border-color:transparent}.bb-button--no-background.bb-button--black.bb-button--black:active,.bb-button--no-background.bb-button--black.bb-button--black:focus:hover,.bb-button--no-background.bb-button--black.bb-button--black:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--black.bb-button--black:focus,.bb-button--no-background.bb-button--black.bb-button--black:hover{color:#2d353e}.bb-button--no-background.bb-button--black.bb-button--black:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--black.bb-button--black:focus::after{border-color:#2d353e}.bb-button--no-background.bb-button--black.bb-button--black:active{color:#23282f}.bb-button--no-background.bb-button--black.bb-button--black:active::after{border-color:#23282f}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--disabled,.bb-button--no-background.bb-button--black.bb-button--black[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--disabled::after,.bb-button--no-background.bb-button--black.bb-button--black[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only:active,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white{color:#fff}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:active,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:focus,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:hover{color:#38414d}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:active,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:hover{background-color:rgba(56,65,77,.4)}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:active::after,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:focus::after{border-color:#38414d}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white[disabled]{background-color:transparent;color:rgba(56,65,77,.5)}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white[disabled]:active{color:rgba(56,65,77,.5)}.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--black.bb-button--black.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--white.bb-button--white{background-color:transparent;color:#fff}.bb-button--no-background.bb-button--white.bb-button--white::after{border-color:transparent}.bb-button--no-background.bb-button--white.bb-button--white:active,.bb-button--no-background.bb-button--white.bb-button--white:focus:hover,.bb-button--no-background.bb-button--white.bb-button--white:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--white.bb-button--white:focus,.bb-button--no-background.bb-button--white.bb-button--white:hover{color:#f2f2f2}.bb-button--no-background.bb-button--white.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--white.bb-button--white:focus::after{border-color:#f2f2f2}.bb-button--no-background.bb-button--white.bb-button--white:active{color:#e6e6e6}.bb-button--no-background.bb-button--white.bb-button--white:active::after{border-color:#e6e6e6}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--white.bb-button--white[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--disabled::after,.bb-button--no-background.bb-button--white.bb-button--white[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only:active,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white{color:#fff}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:active,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:focus,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:hover{color:#fff}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:active,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:hover{background-color:rgba(255,255,255,.4)}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:active::after,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:focus::after{border-color:#fff}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white[disabled]{background-color:transparent;color:rgba(255,255,255,.5)}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white[disabled]:active{color:rgba(255,255,255,.5)}.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--white.bb-button--white.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background.bb-button--disabled.bb-button--disabled{background-color:transparent;color:#687d92}.bb-button--no-background.bb-button--disabled.bb-button--disabled::after{border-color:transparent}.bb-button--no-background.bb-button--disabled.bb-button--disabled:active,.bb-button--no-background.bb-button--disabled.bb-button--disabled:focus:hover,.bb-button--no-background.bb-button--disabled.bb-button--disabled:hover{background-color:#f7f7f8}.bb-button--no-background.bb-button--disabled.bb-button--disabled:focus,.bb-button--no-background.bb-button--disabled.bb-button--disabled:hover{color:#5d7083}.bb-button--no-background.bb-button--disabled.bb-button--disabled:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--disabled.bb-button--disabled:focus::after{border-color:#5d7083}.bb-button--no-background.bb-button--disabled.bb-button--disabled:active{color:#536474}.bb-button--no-background.bb-button--disabled.bb-button--disabled:active::after{border-color:#536474}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--disabled,.bb-button--no-background.bb-button--disabled.bb-button--disabled[disabled]{background-color:transparent;color:#c1cad2}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--disabled::after,.bb-button--no-background.bb-button--disabled.bb-button--disabled[disabled]::after{border-color:transparent}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only{background-color:transparent}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only:active,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only:focus:hover,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only:hover{background-color:transparent}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--green,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--blue,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--black,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--gray,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--gray-90,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--white,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--read-only::after{border-color:transparent}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white{color:#fff}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:active,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:focus,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:hover{color:#687d92}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:active,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:hover{background-color:rgba(104,125,146,.4)}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:active::after,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:focus::after{border-color:#687d92}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white:focus{background-color:transparent;box-shadow:none}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--disabled,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white[disabled]{background-color:transparent;color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--disabled:active,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white[disabled]:active{color:rgba(104,125,146,.5)}.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--disabled:active::after,.bb-button--no-background.bb-button--disabled.bb-button--disabled.bb-button--white[disabled]:active::after{border-color:transparent}.bb-button--no-background::after{border-style:dashed}.bb-button--link{display:inline;height:auto;min-height:0;border-radius:0;font-size:inherit;transition:color .2s}.bb-button--link.bb-button--mint.bb-button--mint{background-color:transparent;box-shadow:none;color:#0ccca2}.bb-button--link.bb-button--mint.bb-button--mint:active,.bb-button--link.bb-button--mint.bb-button--mint:focus,.bb-button--link.bb-button--mint.bb-button--mint:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint:active .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint:focus .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--mint.bb-button--mint:hover{box-shadow:none;color:#0bb48f}.bb-button--link.bb-button--mint.bb-button--mint:focus-visible::before{border-color:#0ccca2}.bb-button--link.bb-button--mint.bb-button--mint:active{color:#099c7c}.bb-button--link.bb-button--mint.bb-button--mint .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--black:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--gray-70:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--gray-80:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--gray-90:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--gray:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only{background:0 0}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--disabled,.bb-button--link.bb-button--mint.bb-button--mint[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--disabled:active,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--disabled:focus,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--disabled:hover,.bb-button--link.bb-button--mint.bb-button--mint[disabled]:active,.bb-button--link.bb-button--mint.bb-button--mint[disabled]:focus,.bb-button--link.bb-button--mint.bb-button--mint[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline:active,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline:focus,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #0ccca2}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast:hover::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast[disabled],.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--mint.bb-button--mint.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--green.bb-button--green{background-color:transparent;box-shadow:none;color:#0ccca2}.bb-button--link.bb-button--green.bb-button--green:active,.bb-button--link.bb-button--green.bb-button--green:focus,.bb-button--link.bb-button--green.bb-button--green:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--green.bb-button--green:active .bb-button__label,.bb-button--link.bb-button--green.bb-button--green:focus .bb-button__label,.bb-button--link.bb-button--green.bb-button--green:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--green.bb-button--green:hover{box-shadow:none;color:#0bb48f}.bb-button--link.bb-button--green.bb-button--green:focus-visible::before{border-color:#0ccca2}.bb-button--link.bb-button--green.bb-button--green:active{color:#099c7c}.bb-button--link.bb-button--green.bb-button--green .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--green.bb-button--green.bb-button--black:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--gray-70:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--gray-80:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--gray-90:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--gray:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only{background:0 0}.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--green.bb-button--green.bb-button--disabled,.bb-button--link.bb-button--green.bb-button--green[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--green.bb-button--green.bb-button--disabled:active,.bb-button--link.bb-button--green.bb-button--green.bb-button--disabled:focus,.bb-button--link.bb-button--green.bb-button--green.bb-button--disabled:hover,.bb-button--link.bb-button--green.bb-button--green[disabled]:active,.bb-button--link.bb-button--green.bb-button--green[disabled]:focus,.bb-button--link.bb-button--green.bb-button--green[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--green.bb-button--green.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--green.bb-button--green[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline:active,.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline:focus,.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #0ccca2}.bb-button--link.bb-button--green.bb-button--green.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast,.bb-button--link.bb-button--green.bb-button--green.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast:hover::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast[disabled],.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--green.bb-button--green.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--green.bb-button--green.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--green.bb-button--green.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--blue.bb-button--blue{background-color:transparent;box-shadow:none;color:#2485e5}.bb-button--link.bb-button--blue.bb-button--blue:active,.bb-button--link.bb-button--blue.bb-button--blue:focus,.bb-button--link.bb-button--blue.bb-button--blue:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue:active .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue:focus .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--blue.bb-button--blue:hover{box-shadow:none;color:#1978d6}.bb-button--link.bb-button--blue.bb-button--blue:focus-visible::before{border-color:#2485e5}.bb-button--link.bb-button--blue.bb-button--blue:active{color:#176bbf}.bb-button--link.bb-button--blue.bb-button--blue .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--black:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--gray-70:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--gray-80:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--gray-90:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--gray:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only{background:0 0}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--disabled,.bb-button--link.bb-button--blue.bb-button--blue[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--disabled:active,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--disabled:focus,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--disabled:hover,.bb-button--link.bb-button--blue.bb-button--blue[disabled]:active,.bb-button--link.bb-button--blue.bb-button--blue[disabled]:focus,.bb-button--link.bb-button--blue.bb-button--blue[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline:active,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline:focus,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #2485e5}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast:hover::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast[disabled],.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--blue.bb-button--blue.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--navy.bb-button--navy{background-color:transparent;box-shadow:none;color:#00285a}.bb-button--link.bb-button--navy.bb-button--navy:active,.bb-button--link.bb-button--navy.bb-button--navy:focus,.bb-button--link.bb-button--navy.bb-button--navy:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy:active .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy:focus .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--navy.bb-button--navy:hover{box-shadow:none;color:#001d41}.bb-button--link.bb-button--navy.bb-button--navy:focus-visible::before{border-color:#00285a}.bb-button--link.bb-button--navy.bb-button--navy:active{color:#001127}.bb-button--link.bb-button--navy.bb-button--navy .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--black:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--gray-70:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--gray-80:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--gray-90:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--gray:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only{background:0 0}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--disabled,.bb-button--link.bb-button--navy.bb-button--navy[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--disabled:active,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--disabled:focus,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--disabled:hover,.bb-button--link.bb-button--navy.bb-button--navy[disabled]:active,.bb-button--link.bb-button--navy.bb-button--navy[disabled]:focus,.bb-button--link.bb-button--navy.bb-button--navy[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline:active,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline:focus,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #00285a}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast:hover::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast[disabled],.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--navy.bb-button--navy.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--red.bb-button--red{background-color:transparent;box-shadow:none;color:#ff7155}.bb-button--link.bb-button--red.bb-button--red:active,.bb-button--link.bb-button--red.bb-button--red:focus,.bb-button--link.bb-button--red.bb-button--red:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--red.bb-button--red:active .bb-button__label,.bb-button--link.bb-button--red.bb-button--red:focus .bb-button__label,.bb-button--link.bb-button--red.bb-button--red:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--red.bb-button--red:hover{box-shadow:none;color:#ff5c3c}.bb-button--link.bb-button--red.bb-button--red:focus-visible::before{border-color:#ff7155}.bb-button--link.bb-button--red.bb-button--red:active{color:#ff4622}.bb-button--link.bb-button--red.bb-button--red .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--red.bb-button--red.bb-button--black:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--gray-70:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--gray-80:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--gray-90:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--gray:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only{background:0 0}.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--red.bb-button--red.bb-button--disabled,.bb-button--link.bb-button--red.bb-button--red[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--red.bb-button--red.bb-button--disabled:active,.bb-button--link.bb-button--red.bb-button--red.bb-button--disabled:focus,.bb-button--link.bb-button--red.bb-button--red.bb-button--disabled:hover,.bb-button--link.bb-button--red.bb-button--red[disabled]:active,.bb-button--link.bb-button--red.bb-button--red[disabled]:focus,.bb-button--link.bb-button--red.bb-button--red[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--red.bb-button--red.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--red.bb-button--red[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline:active,.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline:focus,.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #ff7155}.bb-button--link.bb-button--red.bb-button--red.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast,.bb-button--link.bb-button--red.bb-button--red.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast:hover::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast[disabled],.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--red.bb-button--red.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--red.bb-button--red.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--red.bb-button--red.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--gray.bb-button--gray{background-color:transparent;box-shadow:none;color:#687d92}.bb-button--link.bb-button--gray.bb-button--gray:active,.bb-button--link.bb-button--gray.bb-button--gray:focus,.bb-button--link.bb-button--gray.bb-button--gray:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray:active .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray:focus .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--gray.bb-button--gray:hover{box-shadow:none;color:#5d7083}.bb-button--link.bb-button--gray.bb-button--gray:focus-visible::before{border-color:#687d92}.bb-button--link.bb-button--gray.bb-button--gray:active{color:#536474}.bb-button--link.bb-button--gray.bb-button--gray .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--black:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--gray-70:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--gray-80:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--gray-90:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--gray:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only{background:0 0}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--disabled,.bb-button--link.bb-button--gray.bb-button--gray[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--disabled:active,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--disabled:focus,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--disabled:hover,.bb-button--link.bb-button--gray.bb-button--gray[disabled]:active,.bb-button--link.bb-button--gray.bb-button--gray[disabled]:focus,.bb-button--link.bb-button--gray.bb-button--gray[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline:active,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline:focus,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast:hover::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast[disabled],.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--gray.bb-button--gray.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--yellow.bb-button--yellow{background-color:transparent;box-shadow:none;color:#687d92}.bb-button--link.bb-button--yellow.bb-button--yellow:active,.bb-button--link.bb-button--yellow.bb-button--yellow:focus,.bb-button--link.bb-button--yellow.bb-button--yellow:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow:active .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow:focus .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--yellow.bb-button--yellow:hover{box-shadow:none;color:#5d7083}.bb-button--link.bb-button--yellow.bb-button--yellow:focus-visible::before{border-color:#687d92}.bb-button--link.bb-button--yellow.bb-button--yellow:active{color:#536474}.bb-button--link.bb-button--yellow.bb-button--yellow .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--black:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--gray-70:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--gray-80:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--gray-90:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--gray:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only{background:0 0}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--disabled,.bb-button--link.bb-button--yellow.bb-button--yellow[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--disabled:active,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--disabled:focus,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--disabled:hover,.bb-button--link.bb-button--yellow.bb-button--yellow[disabled]:active,.bb-button--link.bb-button--yellow.bb-button--yellow[disabled]:focus,.bb-button--link.bb-button--yellow.bb-button--yellow[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline:active,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline:focus,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast:hover::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast[disabled],.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--yellow.bb-button--yellow.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--contrast.bb-button--contrast{background-color:transparent;box-shadow:none;color:#fff}.bb-button--link.bb-button--contrast.bb-button--contrast:active,.bb-button--link.bb-button--contrast.bb-button--contrast:focus,.bb-button--link.bb-button--contrast.bb-button--contrast:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast:active .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast:focus .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--contrast.bb-button--contrast:hover{box-shadow:none;color:#f2f2f2}.bb-button--link.bb-button--contrast.bb-button--contrast:focus-visible::before{border-color:#fff}.bb-button--link.bb-button--contrast.bb-button--contrast:active{color:#e6e6e6}.bb-button--link.bb-button--contrast.bb-button--contrast .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--black:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--gray-70:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--gray-80:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--gray-90:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--gray:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only{background:0 0}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--contrast.bb-button--contrast[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--disabled:active,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--disabled:focus,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--disabled:hover,.bb-button--link.bb-button--contrast.bb-button--contrast[disabled]:active,.bb-button--link.bb-button--contrast.bb-button--contrast[disabled]:focus,.bb-button--link.bb-button--contrast.bb-button--contrast[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #fff}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast:hover::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast[disabled],.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--contrast.bb-button--contrast.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--black.bb-button--black{background-color:transparent;box-shadow:none;color:#38414d}.bb-button--link.bb-button--black.bb-button--black:active,.bb-button--link.bb-button--black.bb-button--black:focus,.bb-button--link.bb-button--black.bb-button--black:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--black.bb-button--black:active .bb-button__label,.bb-button--link.bb-button--black.bb-button--black:focus .bb-button__label,.bb-button--link.bb-button--black.bb-button--black:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--black.bb-button--black:hover{box-shadow:none;color:#2d353e}.bb-button--link.bb-button--black.bb-button--black:focus-visible::before{border-color:#38414d}.bb-button--link.bb-button--black.bb-button--black:active{color:#23282f}.bb-button--link.bb-button--black.bb-button--black .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--black.bb-button--black.bb-button--black:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--gray-70:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--gray-80:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--gray-90:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--gray:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only{background:0 0}.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--black.bb-button--black.bb-button--disabled,.bb-button--link.bb-button--black.bb-button--black[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--black.bb-button--black.bb-button--disabled:active,.bb-button--link.bb-button--black.bb-button--black.bb-button--disabled:focus,.bb-button--link.bb-button--black.bb-button--black.bb-button--disabled:hover,.bb-button--link.bb-button--black.bb-button--black[disabled]:active,.bb-button--link.bb-button--black.bb-button--black[disabled]:focus,.bb-button--link.bb-button--black.bb-button--black[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--black.bb-button--black.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--black.bb-button--black[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline:active,.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline:focus,.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #38414d}.bb-button--link.bb-button--black.bb-button--black.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast,.bb-button--link.bb-button--black.bb-button--black.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast:hover::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast[disabled],.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--black.bb-button--black.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--black.bb-button--black.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--black.bb-button--black.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--white.bb-button--white{background-color:transparent;box-shadow:none;color:#fff}.bb-button--link.bb-button--white.bb-button--white:active,.bb-button--link.bb-button--white.bb-button--white:focus,.bb-button--link.bb-button--white.bb-button--white:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--white.bb-button--white:active .bb-button__label,.bb-button--link.bb-button--white.bb-button--white:focus .bb-button__label,.bb-button--link.bb-button--white.bb-button--white:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--white.bb-button--white:hover{box-shadow:none;color:#f2f2f2}.bb-button--link.bb-button--white.bb-button--white:focus-visible::before{border-color:#fff}.bb-button--link.bb-button--white.bb-button--white:active{color:#e6e6e6}.bb-button--link.bb-button--white.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--white.bb-button--white.bb-button--black:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--gray-70:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--gray-80:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--gray-90:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--gray:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only{background:0 0}.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--white.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--white.bb-button--white[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--white.bb-button--white.bb-button--disabled:active,.bb-button--link.bb-button--white.bb-button--white.bb-button--disabled:focus,.bb-button--link.bb-button--white.bb-button--white.bb-button--disabled:hover,.bb-button--link.bb-button--white.bb-button--white[disabled]:active,.bb-button--link.bb-button--white.bb-button--white[disabled]:focus,.bb-button--link.bb-button--white.bb-button--white[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--white.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--white.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #fff}.bb-button--link.bb-button--white.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast,.bb-button--link.bb-button--white.bb-button--white.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast:hover::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast[disabled],.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--white.bb-button--white.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--white.bb-button--white.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--white.bb-button--white.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link.bb-button--disabled.bb-button--disabled{background-color:transparent;box-shadow:none;color:#687d92}.bb-button--link.bb-button--disabled.bb-button--disabled:active,.bb-button--link.bb-button--disabled.bb-button--disabled:focus,.bb-button--link.bb-button--disabled.bb-button--disabled:hover{background-color:transparent;box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled:active .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled:focus .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled:hover .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--disabled.bb-button--disabled:hover{box-shadow:none;color:#5d7083}.bb-button--link.bb-button--disabled.bb-button--disabled:focus-visible::before{border-color:#687d92}.bb-button--link.bb-button--disabled.bb-button--disabled:active{color:#536474}.bb-button--link.bb-button--disabled.bb-button--disabled .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--black:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--gray-70:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--gray-80:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--gray-90:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--gray:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--yellow:hover{color:#0ccca2}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only{background:0 0}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--green,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--mint{color:#a0eada}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--blue,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--navy{color:#b4d1ee}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--red{color:#f8cbc2}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--black,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--gray,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--gray-90,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--white,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--yellow{color:#c1cad2}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--black::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--blue::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--gray-90::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--gray::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--green::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--mint::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--navy::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--red::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--white::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--read-only.bb-button--yellow::after{border-color:transparent}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--disabled,.bb-button--link.bb-button--disabled.bb-button--disabled[disabled]{background-color:transparent;box-shadow:none;color:#c1cad2}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--disabled:active,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--disabled:focus,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--disabled:hover,.bb-button--link.bb-button--disabled.bb-button--disabled[disabled]:active,.bb-button--link.bb-button--disabled.bb-button--disabled[disabled]:focus,.bb-button--link.bb-button--disabled.bb-button--disabled[disabled]:hover{background-color:transparent;color:#c1cad2}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 #89a}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline{border:none;box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline:active,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline:focus,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline:focus-visible,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white{box-shadow:none;color:#fff}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white:hover{background:0 0;color:rgba(255,255,255,.7)}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast:hover::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white:hover::after{border-color:transparent}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white .bb-button__label{box-shadow:0 .0625rem 0 0 #fff}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline{box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline:active,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline:focus,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline[disabled],.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline.bb-button--disabled,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline:active,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline:focus,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline:hover,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline[disabled]{box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline[disabled] .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline:active .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline:focus .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline:hover .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline[disabled] .bb-button__label{box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--no-underline .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--no-underline .bb-button__label{box-shadow:none}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--disabled,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast[disabled],.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--disabled,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white[disabled]{box-shadow:none;color:rgba(255,255,255,.5)}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--disabled::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast[disabled]::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--disabled::after,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white[disabled]::after{border-color:transparent}.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--contrast[disabled] .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white.bb-button--disabled .bb-button__label,.bb-button--link.bb-button--disabled.bb-button--disabled.bb-button--white[disabled] .bb-button__label{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5)}.bb-button--link::before{position:absolute;top:0;left:0;width:100%;height:100%;border:.0625rem dashed transparent;border-radius:.1875rem;content:\"\";pointer-events:none}.bb-button--link .bb-button__label{position:relative;transition:box-shadow .2s}.bb-button--link.bb-button--link.bb-button--big.bb-button--big,.bb-button--link.bb-button--link.bb-button--lg.bb-button--lg,.bb-button--link.bb-button--link.bb-button--link.bb-button--link,.bb-button--link.bb-button--link.bb-button--mini.bb-button--mini,.bb-button--link.bb-button--link.bb-button--sm.bb-button--sm,.bb-button--link.bb-button--link.bb-button--small.bb-button--small{padding:.125rem 0 0;text-align:left}.bb-button--link.bb-button--link.bb-button--big.bb-button--big::after,.bb-button--link.bb-button--link.bb-button--lg.bb-button--lg::after,.bb-button--link.bb-button--link.bb-button--link.bb-button--link::after,.bb-button--link.bb-button--link.bb-button--mini.bb-button--mini::after,.bb-button--link.bb-button--link.bb-button--sm.bb-button--sm::after,.bb-button--link.bb-button--link.bb-button--small.bb-button--small::after{border-color:transparent}.bb-button--link.bb-button--link.bb-button--big.bb-button--big.bb-button--no-paddings,.bb-button--link.bb-button--link.bb-button--lg.bb-button--lg.bb-button--no-paddings,.bb-button--link.bb-button--link.bb-button--link.bb-button--link.bb-button--no-paddings,.bb-button--link.bb-button--link.bb-button--mini.bb-button--mini.bb-button--no-paddings,.bb-button--link.bb-button--link.bb-button--sm.bb-button--sm.bb-button--no-paddings,.bb-button--link.bb-button--link.bb-button--small.bb-button--small.bb-button--no-paddings{padding:0}.bb-button--link.bb-button--no-underline.bb-button--no-underline.bb-button--no-underline{padding-top:.0625rem;padding-bottom:.0625rem}.bb-button--ticket{flex:1;margin:0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;transition:background-color .2s,color .2s}.bb-button--ticket.bb-button--big,.bb-button--ticket.bb-button--lg,.bb-button--ticket.bb-button--loading,.bb-button--ticket.bb-button--sm,.bb-button--ticket.bb-button--small{min-height:unset;padding:0}.bb-button--uppercase{text-transform:uppercase}.bb-button--capitalize{text-transform:capitalize}.bb-button--no-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label,.bb-button--no-side-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label{padding-right:0;padding-left:1.5625rem}.bb-button--no-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--sm,.bb-button--no-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--small,.bb-button--no-side-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--sm,.bb-button--no-side-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--small{padding-right:0;padding-left:1.25rem}.bb-button--no-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--big,.bb-button--no-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--lg,.bb-button--no-side-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--big,.bb-button--no-side-paddings.bb-button--icon-fixed.bb-button--icon.bb-button--label.bb-button--lg{padding-right:0;padding-left:2.1875rem}.bb-button--no-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label,.bb-button--no-side-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label{padding-right:1.5625rem;padding-left:0}.bb-button--no-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--sm,.bb-button--no-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--small,.bb-button--no-side-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--sm,.bb-button--no-side-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--small{padding-right:1.25rem;padding-left:0}.bb-button--no-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--big,.bb-button--no-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--lg,.bb-button--no-side-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--big,.bb-button--no-side-paddings.bb-button--icon-after-fixed.bb-button--icon-after.bb-button--label.bb-button--lg{padding-right:2.1875rem;padding-left:0}.bb-button--no-paddings{min-height:unset;padding:0}.bb-button--no-paddings.bb-button--label.bb-button--label{padding:0}.bb-button--no-side-paddings{padding-right:0;padding-left:0}.bb-button--no-side-paddings.bb-button--label.bb-button--label{padding-right:0;padding-left:0}.bb-button__label{padding-top:.6875rem;padding-bottom:.6875rem;pointer-events:none;text-align:center;user-select:none}.bb-button--sm .bb-button__label,.bb-button--small .bb-button__label{padding-top:.4375rem;padding-bottom:.4375rem}.bb-button--big .bb-button__label,.bb-button--lg .bb-button__label{padding-top:1.125rem;padding-bottom:1.125rem}.bb-button--link .bb-button__label,.bb-button--no-paddings .bb-button__label{padding:0}@media (max-width:768px){.bb-button--circle-mobile-only .bb-button__label,.bb-button--square-mobile-only .bb-button__label{display:none}}.bb-button__icon,.bb-button__icon-after{min-width:1.5rem}.bb-button--mini .bb-button__icon,.bb-button--mini .bb-button__icon-after,.bb-button--sm .bb-button__icon,.bb-button--sm .bb-button__icon-after,.bb-button--small .bb-button__icon,.bb-button--small .bb-button__icon-after{min-width:1.125rem}.bb-button__icon svg path,.bb-button__icon-after svg path{transition:fill .2s}@media (max-width:768px){.bb-button--circle-mobile-only .bb-button__icon,.bb-button--square-mobile-only .bb-button__icon{margin:0}}@media (max-width:768px){.bb-button--lg.bb-button--circle-mobile-only .bb-button__icon,.bb-button--lg.bb-button--square-mobile-only .bb-button__icon{width:2.25rem;min-width:2.25rem;height:2.25rem}}@media (max-width:768px){.bb-button--loading.bb-button--circle-mobile-only .bb-button__icon,.bb-button--loading.bb-button--square-mobile-only .bb-button__icon{display:none}}.bb-button__icon--has-text{margin-right:.375rem}.bb-button--icon-fixed .bb-button__icon--has-text{position:absolute;top:50%;left:.625rem;margin:0;transform:translateY(-50%)}.bb-button--icon-fixed.bb-button--link .bb-button__icon--has-text,.bb-button--icon-fixed.bb-button--no-paddings .bb-button__icon--has-text{left:0}.bb-button--no-background>.bb-button__icon--has-text{margin-right:.625rem}@media (max-width:768px){.bb-button--circle-mobile-only .bb-button__icon--has-text,.bb-button--square-mobile-only .bb-button__icon--has-text{margin:0}}@media (max-width:768px){.bb-button--circle-mobile-only .bb-button__icon-after,.bb-button--square-mobile-only .bb-button__icon-after{display:none}}.bb-button__icon-after--has-text{margin-left:.375rem}.bb-button--icon-after-fixed .bb-button__icon-after--has-text{position:absolute;top:50%;right:.625rem;margin:0;transform:translateY(-50%)}.bb-button--icon-after-fixed.bb-button--link .bb-button__icon-after--has-text,.bb-button--icon-after-fixed.bb-button--no-paddings .bb-button__icon-after--has-text{right:0}.bb-button--no-background>.bb-button__icon-after--has-text{margin-left:.625rem}.bb-button .bb-spinner{margin-left:.375rem}.bb-button--sm.bb-button .bb-spinner,.bb-button--small.bb-button .bb-spinner{width:1.125rem;height:1.125rem}.bb-button--circle.bb-button .bb-spinner,.bb-button--square.bb-button .bb-spinner{margin:0}.bb-button--ticket.bb-button .bb-spinner{position:absolute;right:1.5625rem;margin:0}.bb-button--image.bb-button .bb-spinner{position:absolute;right:.625rem;margin:0;color:#687d92}@media (max-width:768px){.bb-button--circle-mobile-only.bb-button .bb-spinner,.bb-button--square-mobile-only.bb-button .bb-spinner{margin:0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2v = undefined;
/* module identifier */
const __vue_module_identifier__$2v = undefined;
/* functional template */
const __vue_is_functional_template__$2v = undefined;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2v = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$2v, __vue_script__$2v, __vue_scope_id__$2v, __vue_is_functional_template__$2v, __vue_module_identifier__$2v, false, createInjector, undefined, undefined);

var script$2u = {
  name: 'bb-overlay',
  mixins: [validator],
  props: {
    role: {
      type: String,
      default: 'dialog',
      validator: role => ['dialog', 'alertdialog'].includes(role)
    },
    visible: Boolean,
    fluid: Boolean,
    classes: String,
    fixedMobileSize: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'light-blue',
      validator: color => ['light-blue', 'dark', 'gray', 'white'].includes(color)
    },
    centerVertically: Boolean,
    constrained: Boolean,
    dialogAriaLabel: String
  },
  emits: ['close'],
  data() {
    return {
      uuid: uuid(8)
    };
  },
  computed: {
    instanceNoScrollClassName() {
      return `no-scroll-${this.uuid}`;
    },
    overlayClasses() {
      return {
        [`bb-overlay--${this.color}`]: this.color,
        'bb-overlay--centered': this.centerVertically,
        'bb-overlay--constrained': this.constrained
      };
    },
    overlayContentClasses() {
      return [this.classes, {
        'bb-overlay__content--fluid': this.fluid,
        'bb-overlay__content--fixed-mobile-size': this.fixedMobileSize
      }];
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler() {
        if (this.constrained || !inBrowser) {
          return;
        }
        if (this.visible) {
          this.addNoScrollClass();
          this.focusDialogOnNextTick();
        } else {
          this.removeNoScrollClass();
        }
      }
    }
  },
  beforeDestroy() {
    inBrowser && this.removeNoScrollClass();
  },
  methods: {
    focusDialogOnNextTick() {
      this.$nextTick(() => {
        var _this$$refs$dialog;
        (_this$$refs$dialog = this.$refs.dialog) === null || _this$$refs$dialog === void 0 ? void 0 : _this$$refs$dialog.focus();
      });
    },
    addNoScrollClass() {
      document.body.classList.add(this.instanceNoScrollClassName);
    },
    removeNoScrollClass() {
      document.body.classList.remove(this.instanceNoScrollClassName);
    },
    hideOverlay(event) {
      if (this.constrained) {
        return;
      }
      const overlayElements = Object.values(this.$refs);
      const isFocusOnOverlayElements = overlayElements.includes(event.target);
      if (isFocusOnOverlayElements) {
        this.$emit('close');
      }
    }
  }
};

/* script */
const __vue_script__$2u = script$2u;

/* template */
var __vue_render__$2o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('transition', {
    attrs: {
      "name": "overlay"
    }
  }, [_vm.visible ? _c('div', {
    ref: "overlay",
    staticClass: "bb-overlay",
    class: _vm.overlayClasses,
    attrs: {
      "data-testid": "bb-overlay"
    },
    on: {
      "mousedown": _vm.hideOverlay
    }
  }, [_c('dialog', {
    ref: "dialog",
    staticClass: "bb-overlay__content",
    class: _vm.overlayContentClasses,
    attrs: {
      "open": "",
      "tabindex": "-1",
      "aria-label": _vm.dialogAriaLabel,
      "role": _vm.role,
      "aria-modal": "true"
    },
    on: {
      "keyup": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }
        return _vm.hideOverlay.apply(null, arguments);
      }
    }
  }, [_vm._t("default")], 2)]) : _vm._e()]);
};
var __vue_staticRenderFns__$2o = [];

/* style */
const __vue_inject_styles__$2u = function (inject) {
  if (!inject) return;
  inject("data-v-025884bc_0", {
    source: ".bb-overlay{position:fixed;z-index:1500;width:100vw;height:100vh;height:-webkit-fill-available;background-color:rgba(230,243,255,.95);inset:0;overflow-y:auto;transition:opacity .2s ease-in}.bb-overlay--dark{background-color:rgba(56,65,77,.95)}.bb-overlay--gray{background-color:rgba(56,65,77,.3)}.bb-overlay--white{background-color:rgba(255,255,255,.9)}.bb-overlay--constrained{position:absolute;width:100%;height:100%}.bb-overlay__content{position:absolute;right:0;left:0;display:block;width:-webkit-fit-content;max-width:100%;height:-webkit-fit-content;padding:0;border:none;margin:1.25rem auto 6.25rem;background-color:transparent;transition:transform .2s ease-in}@media (min-width:576px){.bb-overlay__content{margin-top:3.75rem}}.bb-overlay--centered .bb-overlay__content{display:flex;min-height:100%;align-items:center;margin-top:0;margin-bottom:0}.bb-overlay__content:focus{outline:0}.bb-overlay__content--fluid{height:100%}@media (min-width:768px){.bb-overlay__content--fluid{height:auto}}.bb-overlay__content:not(.bb-overlay__content--fluid){max-width:90%}@media (min-width:768px){.bb-overlay__content:not(.bb-overlay__content--fluid){max-width:none}}@media (max-width:768px){.bb-overlay__content.bb-overlay__content--fixed-mobile-size{width:100%;margin:1.25rem auto 0}}@media (max-width:576px){.bb-overlay__content.bb-overlay__content--fixed-mobile-size{max-width:100%}}.overlay-enter,.overlay-leave-active{opacity:0}.overlay-enter .bb-overlay__content,.overlay-leave-active .bb-overlay__content{transform:scale(1.1)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2u = undefined;
/* module identifier */
const __vue_module_identifier__$2u = undefined;
/* functional template */
const __vue_is_functional_template__$2u = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2u = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2o,
  staticRenderFns: __vue_staticRenderFns__$2o
}, __vue_inject_styles__$2u, __vue_script__$2u, __vue_scope_id__$2u, __vue_is_functional_template__$2u, __vue_module_identifier__$2u, false, createInjector, undefined, undefined);

function jump(target, options) {
  target = target === '#' ? options.offset || 0 : target;
  const opt = {
    duration: options.duration || 300,
    offset: options.offset || 0,
    callback: options.callback,
    easing: options.easing || easeInOutQuad,
    scope: options.scope || window,
    start: isNumber(options.start) ? options.start : window.pageYOffset,
    direction: options.direction || 'vertical'
  };
  const distance = typeof target === 'string' ? opt.offset + document.querySelector(target).getBoundingClientRect().top : target;
  const duration = typeof opt.duration === 'function' ? opt.duration(distance) : opt.duration;
  let timeStart;
  if (opt.direction && !['horizontal', 'vertical'].includes(opt.direction)) {
    throw new TypeError('jump.js – `options.direction` can only equal `horizontal` or `vertical`.');
  }
  window.requestAnimationFrame(time => {
    timeStart = time;
    loop(time);
  });
  function scrollTo(pos) {
    opt.direction === 'horizontal' ? opt.scope.scrollTo(pos, 0) : opt.scope.scrollTo(0, pos);
  }
  function loop(time) {
    const timeElapsed = time - timeStart;
    scrollTo(opt.easing(timeElapsed, opt.start, distance, duration));
    if (timeElapsed < duration) window.requestAnimationFrame(loop);else end();
  }
  function end() {
    scrollTo(opt.start + parseInt(distance, 10));
    if (typeof opt.callback === 'function') opt.callback();
  }
  function easeInOutQuad(t, b, c, d) {
    t /= d / 2;
    if (t < 1) return c / 2 * t * t + b;
    t--;
    return -c / 2 * (t * (t - 2) - 1) + b;
  }
}

var Icon$Y = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg4018434002__a bb-icon__dynamic-fill",attrs:{"d":"M7,14.94L13.06,8.88L15.12,10.94L9.06,17H7V14.94M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M16.7,9.35L15.7,10.35L13.65,8.3L14.65,7.3C14.86,7.08 15.21,7.08 15.42,7.3L16.7,8.58C16.92,8.79 16.92,9.14 16.7,9.35M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2"}})]) } };

Vue.component('icon-ui-pencil-circle-outline', Icon$Y);

var Icon$X = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1093964597__a bb-icon__dynamic-fill",attrs:{"d":"M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m0-18A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2m1 5h-2v4H7v2h4v4h2v-4h4v-2h-4V7z"}})]) } };

Vue.component('icon-ui-plus-circle-outline', Icon$X);

var Icon$W = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg500303801__a bb-icon__dynamic-fill",attrs:{"d":"M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22C17.53,22 22,17.53 22,12C22,6.47 17.53,2 12,2M14.59,8L12,10.59L9.41,8L8,9.41L10.59,12L8,14.59L9.41,16L12,13.41L14.59,16L16,14.59L13.41,12L16,9.41L14.59,8Z"}})]) } };

Vue.component('icon-ui-close-circle-outline', Icon$W);

var Icon$V = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2492519115__a bb-icon__dynamic-fill",attrs:{"d":"M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"}})]) } };

Vue.component('icon-ui-close', Icon$V);

var Icon$U = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3268546011__a bb-icon__dynamic-fill",attrs:{"d":"M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"}})]) } };

Vue.component('icon-ui-check', Icon$U);

var Icon$T = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 40 40"}},[_c('circle',{attrs:{"cx":"19.9","cy":"19.9","r":"19.9","fill":"#f7f7f7"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M10.2,28.77l4.25-10.58a1.08,1.08,0,0,1,.16-.3,3.47,3.47,0,0,1,2.74-1.28c.22,0,.44,0,.66.06a7.71,7.71,0,0,1,6,5.69,3.19,3.19,0,0,1-2.47,4.07,2,2,0,0,0-.23.06L11.1,29.66a.73.73,0,0,1-.8-.12.63.63,0,0,1-.1-.77Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M22.85,23.12c-.1-2.44-3-5.21-5.47-5.23a1.89,1.89,0,0,0-2,2.45,6.4,6.4,0,0,0,5.1,4.85,1.89,1.89,0,0,0,2.34-2.07Z"}}),_c('path',{attrs:{"fill":"#8ac4ff","d":"M18.63,26a8.51,8.51,0,0,1-4.21-4.29L11.86,28.1Z"}}),_c('path',{attrs:{"fill":"#3cc88c","d":"M21.19,13.7c-.15.13-.31.24-.44.37a.91.91,0,0,0-.2,1.24.63.63,0,0,1-.22.89.66.66,0,0,1-.92-.28,2.18,2.18,0,0,1-.29-1.32,2.46,2.46,0,0,1,1.07-1.73,1.44,1.44,0,0,0,.13-2.33,3.9,3.9,0,0,0-.51-.4.59.59,0,0,1-.23-.71A.63.63,0,0,1,20.25,9a1.13,1.13,0,0,1,.24.08,2.79,2.79,0,0,1,.7,4.62Zm6.2-1.43a.86.86,0,0,0-.37,1,8.36,8.36,0,0,1,.27,1.65,2.42,2.42,0,0,1-.53,1.59A4.63,4.63,0,0,1,24.65,18a.66.66,0,0,1-.9-.34c-.14-.37,0-.68.48-.85A4,4,0,0,0,25.54,16,1.33,1.33,0,0,0,26,14.89a10.85,10.85,0,0,0-.21-1.27,2,2,0,0,1,.85-2.36.66.66,0,0,1,.93.12.58.58,0,0,1-.17.89Zm-.17,7.39a1.59,1.59,0,0,1,1.93.19,1.27,1.27,0,0,0,1.46.22,4,4,0,0,0,.91-.5.67.67,0,0,1,1,.07.66.66,0,0,1-.21,1,4.59,4.59,0,0,1-1.31.7,2.36,2.36,0,0,1-2.59-.45.42.42,0,0,0-.62,0,2.84,2.84,0,0,0-.64.7.62.62,0,0,1-.75.25.66.66,0,0,1-.44-.62,1.22,1.22,0,0,1,.12-.31,3.11,3.11,0,0,1,1.18-1.18Z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M22.81 15.35a.77.77 0 0 1-.52-.2.72.72 0 0 1-.22-.51.7.7 0 0 1 .21-.53.72.72 0 0 1 .54-.21.75.75 0 0 1 .73.73.73.73 0 0 1-.74.72zm6.4 8.1a.69.69 0 0 1 .67.38.67.67 0 0 1-.12.75.69.69 0 0 1-.75.16.65.65 0 0 1-.43-.61.66.66 0 0 1 .63-.68zm0-9a.71.71 0 0 1 .67.38.67.67 0 0 1-.12.75.7.7 0 0 1-.75.17.67.67 0 0 1 .2-1.3zM29.73 16.92l-.14.28-.32.05.23.22-.06.3.29-.14.28.14-.05-.3.23-.22-.32-.05zm-5.58-5l-.14.29-.33 0 .24.23-.06.31.29-.14.29.14-.05-.31.23-.23-.32 0zM30.9 22.33l-.09.18-.21 0 .15.15 0 .2.18-.1.19.1 0-.2.15-.15-.2 0zM18.63 12.09l-.09.19-.22 0 .16.15 0 .21.19-.1.19.1 0-.21.15-.15-.21 0zm11.56-.5l-.08.15-.17 0 .12.12L30 12l.15-.07.15.07 0-.16.12-.12-.17 0z"}})]) } };

Vue.component('icon-client-success', Icon$T);

var Icon$S = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 40 40"}},[_c('circle',{attrs:{"cx":"19.9","cy":"19.9","r":"19.9","fill":"#f7f7f7"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M20,31.5A9.5,9.5,0,1,1,29.5,22,9.5,9.5,0,0,1,20,31.5Z"}}),_c('path',{attrs:{"fill":"#8ac4ff","d":"M20,30.5A8.5,8.5,0,1,0,11.5,22,8.51,8.51,0,0,0,20,30.5Z"}}),_c('path',{attrs:{"fill":"#3cc88c","d":"M27.6,13a.46.46,0,0,0-.68,0,.56.56,0,0,0,0,.73l.65.7-.3.32a.54.54,0,0,0,0,.73.45.45,0,0,0,.34.15.44.44,0,0,0,.33-.15l.31-.32.64.69a.48.48,0,0,0,.34.15.45.45,0,0,0,.34-.15.54.54,0,0,0,0-.73Zm3.26.74-2-2.12a.46.46,0,0,0-.68,0,.54.54,0,0,0,0,.73l2,2.11a.45.45,0,0,0,.34.16.44.44,0,0,0,.34-.16.53.53,0,0,0,0-.72Zm-13.48-.53L18,13l.29-.06a6.37,6.37,0,0,1,.63-.11l.27,0c.3,0,.6-.05.9-.05a9.08,9.08,0,0,1,.91.05l.26,0c.22,0,.43.06.64.11l.28.06.61.17.26.08.12,0v-2.9a11.31,11.31,0,0,0-6.16,0v2.9l.13,0,.25-.08Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M17.66,20.36a.49.49,0,1,1,.7-.7l.57.57a.5.5,0,0,1,0,.7.49.49,0,0,1-.35.15.5.5,0,0,1-.35-.15Zm4.58-1.3a.5.5,0,0,1,.7,0,.48.48,0,0,1,0,.7l-2,2a.93.93,0,0,1-.23.88,1,1,0,1,1-.47-1.58Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M20,28.37a6.35,6.35,0,0,1-1.07-.09.48.48,0,0,1-.4-.57.49.49,0,0,1,.57-.4A5.41,5.41,0,0,0,25.38,22c0-.1,0-.2,0-.31a.51.51,0,0,1,.47-.52.5.5,0,0,1,.52.46c0,.13,0,.25,0,.37A6.38,6.38,0,0,1,20,28.37Zm-2.41-1a.5.5,0,0,1-.43.25.53.53,0,0,1-.24-.06A6.61,6.61,0,0,1,16,27a.49.49,0,1,1,.62-.77,4.66,4.66,0,0,0,.75.5A.51.51,0,0,1,17.59,27.39Zm-2.3-1.84a.54.54,0,0,1-.27.07.48.48,0,0,1-.41-.23,6.61,6.61,0,0,1-.5-.95.5.5,0,0,1,.92-.38,5.13,5.13,0,0,0,.41.81A.49.49,0,0,1,15.29,25.55Zm-1.65-3.19c0-.12,0-.24,0-.36h0A6.38,6.38,0,0,1,20,15.63a7.59,7.59,0,0,1,1.07.08.5.5,0,0,1-.09,1H20.9A5.36,5.36,0,0,0,14.62,22c0,.1,0,.21,0,.31a.51.51,0,0,1-.47.52h0A.49.49,0,0,1,13.64,22.36Zm8.77-5.75a.5.5,0,0,1,.67-.19A5.79,5.79,0,0,1,24,17a.49.49,0,0,1,.08.69.51.51,0,0,1-.39.19.53.53,0,0,1-.31-.11,4.66,4.66,0,0,0-.75-.5A.51.51,0,0,1,22.41,16.61Zm2.3,1.84a.48.48,0,0,1,.68.16,6.61,6.61,0,0,1,.5.95.49.49,0,0,1-.27.64.43.43,0,0,1-.19,0,.51.51,0,0,1-.46-.31,5.5,5.5,0,0,0-.41-.8A.49.49,0,0,1,24.71,18.45Z"}})]) } };

Vue.component('icon-client-expired', Icon$S);

const states = {
  success: {
    icon: 'ui-check',
    size: 48,
    color: 'mint'
  },
  alert: {
    icon: 'ui-close',
    size: 48,
    color: 'red'
  },
  victory: {
    icon: 'client-success',
    size: 64,
    color: 'original'
  },
  waiting: {
    icon: 'client-expired',
    size: 64,
    color: 'original'
  }
};
var script$2t = {
  name: 'bb-card',
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbOverlay: __vue_component__$2u,
    FocusTrap
  },
  mixins: [validator],
  props: {
    openDialogLabel: String,
    closeDialogLabel: String,
    dialogTitle: String,
    flipText: String,
    status: {
      type: String,
      validator: val => ['success', 'alert', 'victory', 'waiting'].includes(val)
    },
    statusColor: {
      type: String,
      default: 'blue'
    },
    secondary: {
      type: Boolean
    },
    highlight: Boolean,
    size: {
      type: String,
      default: 'default',
      validator: v => ['condensed', 'wide', 'full', 'fluid', 'default'].includes(v)
    },
    animation: String,
    flipTriggerAnimation: Boolean
  },
  emits: ['flip', 'close'],
  data() {
    return {
      flipped: false,
      resizeObserver: null
    };
  },
  computed: {
    flipTriggerAttributes() {
      return {
        'aria-label': this.flipText || this.openDialogLabel,
        role: 'button',
        tabindex: 0
      };
    },
    state() {
      return states[this.status];
    },
    frontClasses() {
      return {
        'bb-card--status': this.status,
        'bb-card--secondary': this.secondary,
        'bb-card--highlighted': this.highlight,
        [`bb-card--${this.size}`]: this.size
      };
    }
  },
  watch: {
    flipped() {
      if (this.flipped) {
        this.addResizeListener();
        this.$refs.cardBackFace.focus();
        this.$emit('flip');
      } else {
        this.removeResizeListener();
        this.$emit('close');
      }
    }
  },
  beforeDestroy() {
    this.removeResizeListener();
  },
  methods: {
    addResizeListener() {
      this.resizeObserver = new ResizeObserver(this.centerBackside);
      this.resizeObserver.observe(document.body);
    },
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(document.body);
      }
    },
    flip() {
      const _ = this;
      const front = _.$refs.cardFront.getBoundingClientRect();
      const offset = front.top + front.height / 2 - window.innerHeight / 2;
      jump('#', {
        offset,
        duration: 250,
        callback() {
          _.centerBackside();
          _.flipped = true;
          _.$nextTick(() => {
            var _getTabbableChildren$;
            (_getTabbableChildren$ = getTabbableChildren(_.$refs.cardBackFace)[0]) === null || _getTabbableChildren$ === void 0 ? void 0 : _getTabbableChildren$.focus();
          });
        }
      });
    },
    centerBackside: throttle(function () {
      const front = this.$refs.cardFront.getBoundingClientRect();
      const back = this.$refs.cardBack.getBoundingClientRect();
      const heightDiff = (front.height - back.height) / 2;
      const widthDiff = (front.width - back.width) / 2;
      const centeredInWindow = (window.innerWidth - back.width) / 2;
      const centeredOnFrontside = front.left + widthDiff;
      const backTop = front.top + heightDiff > 0 ? front.top + heightDiff : front.top;
      const backLeft = front.left + back.width < window.innerWidth ? centeredOnFrontside : centeredInWindow;
      this.$refs.cardBack.style.top = `${backTop}px`;
      this.$refs.cardBack.style.left = `${backLeft}px`;
    }, 100)
  }
};

/* script */
const __vue_script__$2t = script$2t;

/* template */
var __vue_render__$2n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.$slots['back-side'] || _vm.secondary ? _c('div', {
    staticClass: "bb-card-wrapper",
    class: {
      'bb-card-wrapper--flipped': _vm.flipped,
      'bb-card--animated': _vm.animation
    }
  }, [_vm.animation ? _c('bb-card', {
    staticClass: "bb-card__animation-background",
    class: _vm.animation
  }) : _vm._e(), _vm._v(" "), _c('div', _vm._b({
    ref: "cardFront",
    staticClass: "bb-card bb-card__front-side",
    class: _vm.frontClasses,
    on: {
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        _vm.secondary && _vm.flip();
      },
      "click": function ($event) {
        _vm.secondary && _vm.flip();
      }
    }
  }, 'div', _vm.secondary ? _vm.flipTriggerAttributes : {}, false), [_c('div', _vm._b({
    class: ['bb-card__flip-trigger', {
      'animation-fade-in animation-delay-2s': _vm.flipTriggerAnimation
    }],
    on: {
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        !_vm.secondary && _vm.flip();
      },
      "click": function ($event) {
        !_vm.secondary && _vm.flip();
      }
    }
  }, 'div', !_vm.secondary ? _vm.flipTriggerAttributes : {}, false), [_vm.flipText ? _c('span', {
    staticClass: "bb-card__flip-text",
    class: _vm.animation
  }, [_vm._v("\n        " + _vm._s(_vm.flipText) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm._t("flip-icon", function () {
    return [_c('span', {
      class: {
        'animation-edit-bubble': _vm.flipTriggerAnimation
      }
    }, [_vm.secondary ? _c('bb-icon', {
      attrs: {
        "name": "ui-plus-circle-outline",
        "size": "24",
        "fill": "mint"
      }
    }) : _c('bb-icon', {
      attrs: {
        "name": "ui-pencil-circle-outline",
        "size": "24",
        "fill": "blue"
      }
    })], 1)];
  })], 2), _vm._v(" "), _vm.status ? _c('div', {
    staticClass: "bb-card__circle-status",
    class: "border-" + _vm.statusColor
  }, [_c('bb-icon', {
    attrs: {
      "name": _vm.state.icon,
      "size": _vm.state.size,
      "fill": _vm.state.color
    }
  })], 1) : _vm._e(), _vm._v(" "), _vm._t("default")], 2), _vm._v(" "), _c('bb-overlay', {
    attrs: {
      "visible": _vm.flipped,
      "color": "dark"
    },
    on: {
      "close": function ($event) {
        _vm.flipped = false;
      }
    }
  }), _vm._v(" "), _c('focus-trap', {
    attrs: {
      "active": _vm.flipped,
      "escape-deactivates": false
    }
  }, [_c('div', {
    ref: "cardBack",
    staticClass: "bb-card__flip-stage",
    attrs: {
      "role": "dialog",
      "aria-modal": "true",
      "inert": !_vm.flipped,
      "aria-label": _vm.dialogTitle
    },
    on: {
      "keyup": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }
        _vm.flipped = false;
      }
    }
  }, [_c('transition', {
    attrs: {
      "name": "disable-nested-transitions"
    }
  }, [_c('div', {
    ref: "cardBackFace",
    staticClass: "bb-card bb-card__back-side"
  }, [_vm._t("back-side"), _vm._v(" "), _c('bb-button', {
    staticClass: "bb-card__flip-trigger",
    attrs: {
      "aria-label": _vm.closeDialogLabel,
      "icon": "ui-close-circle-outline",
      "display": "circle",
      "no-background": ""
    },
    on: {
      "click": function ($event) {
        _vm.flipped = false;
      }
    }
  })], 2)])], 1)])], 1) : _c('div', {
    staticClass: "bb-card",
    class: _vm.frontClasses
  }, [_vm.status ? _c('div', {
    staticClass: "bb-card__circle-status",
    class: "border-" + _vm.statusColor
  }, [_c('bb-icon', {
    attrs: {
      "name": _vm.state.icon,
      "size": _vm.state.size,
      "fill": _vm.state.color
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-card__title",
    attrs: {
      "data-testid": "bb-card__title"
    }
  }, [_vm._t("title")], 2), _vm._v(" "), _vm._t("default")], 2);
};
var __vue_staticRenderFns__$2n = [];

/* style */
const __vue_inject_styles__$2t = function (inject) {
  if (!inject) return;
  inject("data-v-7e31311d_0", {
    source: ".bb-card{position:relative;width:26.5625rem;max-width:90%;padding:1.875rem;border-radius:.625rem;margin:0 auto;background:#fff;box-shadow:0 .0625rem .9375rem 0 rgba(74,74,74,.06)}@media (min-width:768px){.bb-card{max-width:none}}.bb-card .clear-side-gutter{margin-right:-1.875rem;margin-left:-1.875rem}.bb-card .clear-top-gutter{margin-top:-1.875rem}.bb-card .clear-bottom-gutter{margin-bottom:-1.875rem}.bb-card__animation-background{position:absolute;inset:0}.bb-card__title{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-align:center}.bb-card__front-side{position:relative;transition:opacity .2s}.bb-card--animated .bb-card__front-side{box-shadow:none}.bb-card__back-side{opacity:0;transform:rotateY(-100deg);transform-style:preserve-3d;transition:transform .5s,opacity .2s}.bb-card__back-side:focus{outline:0}.bb-card__flip-stage{position:fixed;z-index:1500;max-width:90%;perspective:25rem;pointer-events:none}.bb-card__flip-trigger{position:absolute;top:.625rem;right:.625rem;display:flex;cursor:pointer}.bb-card__flip-text{padding-top:.125rem;margin-right:.625rem;color:#9dabb9;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1rem}.bb-card--status{padding-top:3.75rem;margin-top:2.375rem}.bb-card--secondary{padding:.9375rem;cursor:pointer;transition:box-shadow .2s ease-out}.bb-card--secondary:hover{box-shadow:0 .1875rem .625rem 0 rgba(74,74,74,.16)}.bb-card--condensed{width:21.25rem}.bb-card--wide{width:53.125rem;max-width:90%}.bb-card--full{width:100%}.bb-card--fluid{width:auto}.bb-card--highlighted{border:.0625rem solid #0ccca2}.bb-card__circle-status{position:absolute;top:0;left:50%;display:flex;width:4.75rem;height:4.75rem;border-width:.3125rem;border-style:solid;border-radius:50%;margin-left:-2.375rem;background:#fff;transform:translateY(-2.375rem)}.bb-card__circle-status svg{margin:auto}.bb-card__circle-status svg.green path{fill:#0ccca2}.bb-card__circle-status svg.red path{fill:#ff7155}.bb-card__circle-status svg.blue path{fill:#2485e5}.bb-card__circle-status svg.gray path{fill:#38414d}.bb-card__circle-status svg circle{display:none}.bb-card>.bb-list-group:first-child .bb-list-item:first-child .bb-separator:first-child{border-bottom:0}.bb-card>.bb-list-group:last-child .bb-list-item:last-child .bb-separator:last-child{border-bottom:0}.bb-card-wrapper{margin:0 auto;transition:height .3s}.bb-card-wrapper--flipped .bb-card__front-side{opacity:0}.bb-card-wrapper--flipped .bb-card__back-side{max-width:100%;opacity:1;transform:rotate(0)}.bb-card-wrapper--flipped .bb-card__flip-stage{pointer-events:all}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2t = undefined;
/* module identifier */
const __vue_module_identifier__$2t = undefined;
/* functional template */
const __vue_is_functional_template__$2t = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2t = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2n,
  staticRenderFns: __vue_staticRenderFns__$2n
}, __vue_inject_styles__$2t, __vue_script__$2t, __vue_scope_id__$2t, __vue_is_functional_template__$2t, __vue_module_identifier__$2t, false, createInjector, undefined, undefined);

var script$2s = {
  name: 'bb-checkbox',
  components: {
    BbIcon: __vue_component__$2C
  },
  mixins: [Field, veeValidationMixin, vuelidateValidator],
  props: {
    checked: Boolean,
    background: Boolean,
    disabled: Boolean,
    size: {
      type: String,
      validator: value => validateWithDeprecationWarning('BbCheckbox', 'size', value, {
        small: 'small',
        tiny: 'tiny',
        tiniest: 'tiny'
      })
    },
    centerCheckbox: Boolean,
    noPadding: Boolean,
    rules: Object,
    mandatory: Boolean
  },
  data() {
    return {
      val: this.value || this.checked || null
    };
  },
  computed: {
    showConditional() {
      return Boolean(this.val) && Boolean(this.$slots.conditional);
    },
    classes() {
      return [{
        'bb-checkbox--background': this.background,
        'bb-checkbox--small': this.small,
        [`bb-checkbox--${this.computedSize}`]: this.size,
        'bb-checkbox--no-padding': this.noPadding
      }];
    },
    computedSize() {
      if (this.size === 'tiniest') {
        return 'tiny';
      }
      return this.size;
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `errorMessage-${slugify(this.name)}`;
    }
  },
  methods: {
    onChecked(event) {
      this.val = event.target.checked ? true : null;
      this.validate();
      this.$emit('input', this.val);
      this.$emit('change', this.val);
    }
  }
};

/* script */
const __vue_script__$2s = script$2s;

/* template */
var __vue_render__$2m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-checkbox",
    class: _vm.classes,
    attrs: {
      "data-testid": "bb-checkbox",
      "data-vv-value-path": "val"
    }
  }, [_c('div', {
    staticClass: "bb-checkbox__content"
  }, [_c('label', {
    staticClass: "bb-checkbox__label",
    attrs: {
      "for": _vm.id,
      "data-valid": _vm.validity,
      "tabindex": _vm.tabindex,
      "data-testid": "bb-checkbox__label"
    }
  }, [_c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    staticClass: "bb-checkbox__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "type": "checkbox",
      "data-testid": "bb-checkbox__input"
    },
    domProps: {
      "checked": Array.isArray(_vm.val) ? _vm._i(_vm.val, null) > -1 : _vm.val
    },
    on: {
      "change": [function ($event) {
        var $$a = _vm.val,
          $$el = $event.target,
          $$c = $$el.checked ? true : false;
        if (Array.isArray($$a)) {
          var $$v = null,
            $$i = _vm._i($$a, $$v);
          if ($$el.checked) {
            $$i < 0 && (_vm.val = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.val = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.val = $$c;
        }
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChecked.apply(null, arguments);
      }]
    }
  }, 'input', _vm.inputAttrs, false)), _vm._v(" "), _c('span', {
    staticClass: "bb-checkbox__box",
    class: {
      'bb-checkbox__box--centered': _vm.centerCheckbox
    }
  }, [_c('span', {
    staticClass: "bb-checkbox__icon-wrapper"
  }, [_c('bb-icon', {
    staticClass: "bb-checkbox__icon",
    attrs: {
      "name": "ui-check",
      "size": "18",
      "fill": "mint"
    }
  })], 1)]), _vm._v(" "), _vm.$slots.default || _vm.label || _vm.mandatory ? _c('div', {
    staticClass: "bb-checkbox__text"
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-checkbox__mandatory-indicator",
      attrs: {
        "data-testid": "bb-checkbox__mandatory-indicator"
      }
    }, [_vm._v("\n            *\n          ")]) : _vm._e(), _vm._v("\n          " + _vm._s(_vm.label) + "\n        ")];
  })], 2) : _vm._e()]), _vm._v(" "), _c('transition', {
    attrs: {
      "name": "slide-down-up"
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showConditional,
      expression: "showConditional"
    }],
    staticClass: "bb-checkbox__conditional"
  }, [_vm._t("conditional")], 2)])], 1), _vm._v(" "), _vm.hasValidationError ? _c('p', {
    staticClass: "error-message error-message--pushed",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_c('bb-icon', {
    staticClass: "bb-checkbox__error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  }), _vm._v("\n    " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n  ")], 1) : _vm._e()]);
};
var __vue_staticRenderFns__$2m = [];

/* style */
const __vue_inject_styles__$2s = function (inject) {
  if (!inject) return;
  inject("data-v-682a405a_0", {
    source: ".bb-checkbox__content{position:relative;padding-top:.9375rem;padding-right:.9375rem}.bb-checkbox--background .bb-checkbox__content{padding:.9375rem .9375rem;background:#f7f7f8}.bb-checkbox--tiny .bb-checkbox__content{padding:.5rem 0}.bb-checkbox--no-padding .bb-checkbox__content{padding:0}.bb-checkbox__label{display:flex;cursor:pointer}.bb-checkbox--small .bb-checkbox__label,.bb-checkbox--tiny .bb-checkbox__label{position:relative}.bb-checkbox__input{position:absolute;top:0;left:0;width:unset;padding:0;border:none;opacity:0!important}.bb-checkbox__text{margin-top:.25rem;margin-left:1.25rem;color:#00285a;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5}.bb-checkbox--small .bb-checkbox__text,.bb-checkbox--tiny .bb-checkbox__text{margin-top:0}.bb-checkbox--tiny .bb-checkbox__text{margin-left:.625rem;font-size:11px}.bb-checkbox__box{position:relative;display:inline-block;width:1.75rem;height:1.75rem;flex:0 0 1.75rem;border:.0625rem solid #bdc7d0;border-radius:.375rem;background:#fff;transition:border 150ms ease-in;vertical-align:middle}.bb-checkbox__box--centered{align-self:center}.bb-checkbox__input:focus~.bb-checkbox__box,.bb-checkbox__label:hover .bb-checkbox__box{border-color:#687d92;transition:color .2s,background-color .2s,border-color .2s}.bb-checkbox__input[disabled]~.bb-checkbox__box{border:.0625rem solid #bdc7d0;background:#eeeff2;cursor:not-allowed}.bb-checkbox--tiny .bb-checkbox__box{margin-top:0}.bb-checkbox--small .bb-checkbox__box{margin-top:.125rem}.bb-checkbox--small .bb-checkbox__box,.bb-checkbox--tiny .bb-checkbox__box{width:.9375rem;height:.9375rem;flex:0 0 .9375rem;border-radius:.1875rem;margin-left:.625rem}.bb-checkbox__label[data-valid=false] .bb-checkbox__box{border-color:#ff7155}.bb-checkbox__icon-wrapper{position:absolute;top:50%;left:50%;display:none;transform:translate(-50%,-50%)}.bb-checkbox__input:checked~.bb-checkbox__box .bb-checkbox__icon-wrapper{display:flex}.bb-checkbox--small .bb-checkbox__icon,.bb-checkbox--tiny .bb-checkbox__icon{width:.8125rem;min-width:.8125rem;height:.8125rem}.bb-checkbox__input[disabled]~.bb-checkbox__box .bb-checkbox__icon path{fill:#bdc7d0}.bb-checkbox__error-icon{margin-right:.3125rem}.bb-checkbox__conditional{padding:.625rem 0 .3125rem 0}.bb-checkbox__mandatory-indicator{color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2s = undefined;
/* module identifier */
const __vue_module_identifier__$2s = undefined;
/* functional template */
const __vue_is_functional_template__$2s = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2s = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2m,
  staticRenderFns: __vue_staticRenderFns__$2m
}, __vue_inject_styles__$2s, __vue_script__$2s, __vue_scope_id__$2s, __vue_is_functional_template__$2s, __vue_module_identifier__$2s, false, createInjector, undefined, undefined);

function memoize(fn) {
  const cache = {};
  return function memoizedFn() {
    const args = JSON.stringify(arguments);
    return cache[args] = cache[args] || fn.apply(null, arguments);
  };
}
function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
function BooleanStringNumber() {
  return {
    type: [Boolean, String, Number],
    default: false
  };
}
function StringNumber() {
  return {
    type: [String, Number],
    default: null
  };
}
const TYPES = ['col', 'offset', 'order'];
const BREAKPOINTS = ['sm', 'md', 'lg', 'xl'];
const computeBreakpointClass = memoize((type, breakpoint, val) => {
  let className = type;
  if (val === false || val === null || val === undefined) {
    return undefined;
  }
  if (breakpoint) {
    className += `-${breakpoint}`;
  }
  if (type === 'col' && (val === '' || val === true)) {
    return className.toLowerCase();
  }
  className += `-${val}`;
  return className.toLowerCase();
});
const props$2 = {
  tag: {
    type: String,
    default: 'div'
  },
  col: BooleanStringNumber(),
  sm: BooleanStringNumber(),
  md: BooleanStringNumber(),
  lg: BooleanStringNumber(),
  xl: BooleanStringNumber(),
  offset: StringNumber(),
  offsetSm: StringNumber(),
  offsetMd: StringNumber(),
  offsetLg: StringNumber(),
  offsetXl: StringNumber(),
  order: StringNumber(),
  orderSm: StringNumber(),
  orderMd: StringNumber(),
  orderLg: StringNumber(),
  orderXl: StringNumber(),
  alignSelf: {
    type: String,
    default: null,
    validator: str => ['auto', 'start', 'end', 'center', 'baseline', 'stretch'].includes(str)
  }
};
var script$2r = {
  name: 'bb-col',
  functional: true,
  props: props$2,
  render(h, {
    props,
    data,
    children
  }) {
    const classList = [];
    TYPES.forEach(type => {
      BREAKPOINTS.forEach(breakpoint => {
        const prop = type === 'col' ? breakpoint : type + capitalize(breakpoint);
        const clazz = computeBreakpointClass(type, breakpoint, props[prop]);
        if (clazz) {
          classList.push(clazz);
        }
      });
    });
    if (typeof props.col === 'boolean' && props.col || classList.length === 0 && !props.col) {
      classList.push('col');
    }
    if (typeof props.col !== 'boolean') {
      classList.push(`col-${props.col}`);
    }
    if (props.offset) {
      classList.push(`offset-${props.offset}`);
    }
    if (props.order) {
      classList.push(`order-${props.order}`);
    }
    if (props.alignSelf) {
      classList.push(`align-self-${props.alignSelf}`);
    }
    classList.push('bb-col');
    return h(props.tag, mergeData(data, {
      'class': classList
    }), children);
  }
};

/* script */
const __vue_script__$2r = script$2r;

/* template */

/* style */
const __vue_inject_styles__$2r = undefined;
/* scoped */
const __vue_scope_id__$2r = undefined;
/* module identifier */
const __vue_module_identifier__$2r = undefined;
/* functional template */
const __vue_is_functional_template__$2r = undefined;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2r = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$2r, __vue_script__$2r, __vue_scope_id__$2r, __vue_is_functional_template__$2r, __vue_module_identifier__$2r, false, undefined, undefined, undefined);

const props$1 = {
  tag: {
    type: String,
    default: 'div'
  },
  fluid: {
    type: Boolean,
    default: false
  }
};
var script$2q = {
  name: 'bb-container',
  functional: true,
  props: props$1,
  render(h, {
    props,
    data,
    children
  }) {
    return h(props.tag, mergeData(data, {
      class: ['bb-container', {
        'container': !props.fluid,
        'container-fluid': props.fluid
      }]
    }), children);
  }
};

/* script */
const __vue_script__$2q = script$2q;

/* template */

/* style */
const __vue_inject_styles__$2q = undefined;
/* scoped */
const __vue_scope_id__$2q = undefined;
/* module identifier */
const __vue_module_identifier__$2q = undefined;
/* functional template */
const __vue_is_functional_template__$2q = undefined;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2q = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$2q, __vue_script__$2q, __vue_scope_id__$2q, __vue_is_functional_template__$2q, __vue_module_identifier__$2q, false, undefined, undefined, undefined);

var script$2p = {
  name: 'bb-link',
  mixins: [HrefHandler, Accessibility],
  props: {
    label: String,
    title: String,
    back: Boolean,
    secondary: {
      type: Boolean,
      validator: deprecated('secondary', 'noUnderline', 'BbLink')
    },
    noUnderline: Boolean,
    disabled: Boolean,
    color: {
      type: String,
      default: 'mint',
      validator: value => validateWithDeprecationWarning('BbLink', 'color', value, {
        'mint': 'mint',
        'green': 'mint',
        'blue': 'blue',
        'navy': 'blue',
        'red': 'red',
        'gray': 'gray',
        'gray-90': 'gray',
        'gray-80': 'gray',
        'gray-70': 'gray',
        'white': 'white',
        'contrast': 'white',
        'none': 'none'
      })
    },
    hoverBlue: {
      type: Boolean,
      validator: deprecated('`hoverBlue` will be removed from BbLink in the future.')
    }
  },
  emits: ['click', 'mouseover', 'mouseleave'],
  data() {
    return {
      isHandlingRouterLink: false
    };
  },
  computed: {
    frontClasses() {
      return ['bb-link', {
        'bb-link--no-underline': this.noUnderline || this.secondary,
        'bb-link--disabled': this.disabled,
        [`bb-link--${this.color}`]: this.color
      }];
    }
  },
  render(h) {
    const tag = 'a';
    const options = {
      attrs: {
        href: this.resolveHref(),
        title: this.title,
        target: this.target,
        rel: this.target === '_blank' ? 'noopener noreferrer' : null,
        tabindex: this.disabled ? -1 : this.tabindex
      },
      'data-testid': 'bb-link',
      on: {
        click: event => {
          if (this.disabled) {
            event.preventDefault();
            return;
          }
          this.$emit('click', event);
          if (this.isRouterLink) {
            event.preventDefault();
            if (this.isHandlingRouterLink) {
              return;
            }
            this.isHandlingRouterLink = true;
            if (this.back) {
              this.isHandlingRouterLink = false;
              this.$router.go(-1);
            } else if (this.target === '_blank') {
              const {
                href
              } = this.$router.resolve(this.to);
              window.open(href, this.target);
              this.isHandlingRouterLink = false;
            } else {
              this.$router.push(this.to, () => this.isHandlingRouterLink = false, () => this.isHandlingRouterLink = false);
            }
          } else {
            this.back && history.go(-1);
          }
        },
        mouseover: event => {
          this.$emit('mouseover', event);
        },
        mouseleave: event => {
          this.$emit('mouseleave', event);
        }
      }
    };
    options.class = this.frontClasses;
    const label = this.label ? h('span', {
      class: 'bb-link__label'
    }, this.label.trim()) : null;
    const children = [this.$slots.default, label];
    return h(tag, options, children);
  }
};

/* script */
const __vue_script__$2p = script$2p;

/* template */

/* style */
const __vue_inject_styles__$2p = function (inject) {
  if (!inject) return;
  inject("data-v-15ff77a0_0", {
    source: ".bb-link{position:relative;padding-top:.125rem;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-decoration:none;transition:color .1s,box-shadow .1s}.bb-link::before{position:absolute;top:0;left:0;width:100%;height:100%;border:.0625rem dashed transparent;border-radius:.1875rem .1875rem 0 0;content:\"\";pointer-events:none}.bb-link:focus{outline:0}.bb-link:focus-visible.bb-link--none::before,.bb-link:focus-visible::before{border-color:#687d92}.bb-link:focus-visible:not(.bb-link--none)::before{border-bottom:none}.bb-link--no-underline{padding-top:.0625rem;padding-bottom:.0625rem}.bb-link--no-underline::before{border-bottom-left-radius:.1875rem;border-bottom-right-radius:.1875rem}.bb-link--disabled{cursor:not-allowed}.bb-link.bb-link--mint{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#0ccca2}.bb-link.bb-link--mint:active,.bb-link.bb-link--mint:focus,.bb-link.bb-link--mint:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--mint:hover{color:#0bb48f}.bb-link.bb-link--mint:active{color:#099c7c}.bb-link.bb-link--mint:focus-visible::before{border-color:#0ccca2;border-bottom:none}.bb-link.bb-link--mint.bb-link--gray-70:hover,.bb-link.bb-link--mint.bb-link--gray-80:hover,.bb-link.bb-link--mint.bb-link--gray-90:hover,.bb-link.bb-link--mint.bb-link--gray:hover,.bb-link.bb-link--mint.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--mint.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--mint.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--mint.bb-link--no-underline:active,.bb-link.bb-link--mint.bb-link--no-underline:focus,.bb-link.bb-link--mint.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--mint.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #0ccca2}.bb-link.bb-link--mint.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--mint.bb-link--disabled:active,.bb-link.bb-link--mint.bb-link--disabled:focus,.bb-link.bb-link--mint.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--mint.bb-link--contrast,.bb-link.bb-link--mint.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--mint.bb-link--contrast:hover,.bb-link.bb-link--mint.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--mint.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--mint.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--mint.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--mint.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--mint.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--mint.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--mint.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--mint.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--mint.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--mint.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--mint.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--mint.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--green{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#0ccca2}.bb-link.bb-link--green:active,.bb-link.bb-link--green:focus,.bb-link.bb-link--green:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--green:hover{color:#0bb48f}.bb-link.bb-link--green:active{color:#099c7c}.bb-link.bb-link--green:focus-visible::before{border-color:#0ccca2;border-bottom:none}.bb-link.bb-link--green.bb-link--gray-70:hover,.bb-link.bb-link--green.bb-link--gray-80:hover,.bb-link.bb-link--green.bb-link--gray-90:hover,.bb-link.bb-link--green.bb-link--gray:hover,.bb-link.bb-link--green.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--green.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--green.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--green.bb-link--no-underline:active,.bb-link.bb-link--green.bb-link--no-underline:focus,.bb-link.bb-link--green.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--green.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #0ccca2}.bb-link.bb-link--green.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--green.bb-link--disabled:active,.bb-link.bb-link--green.bb-link--disabled:focus,.bb-link.bb-link--green.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--green.bb-link--contrast,.bb-link.bb-link--green.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--green.bb-link--contrast:hover,.bb-link.bb-link--green.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--green.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--green.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--green.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--green.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--green.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--green.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--green.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--green.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--green.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--green.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--green.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--green.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--blue{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#2485e5}.bb-link.bb-link--blue:active,.bb-link.bb-link--blue:focus,.bb-link.bb-link--blue:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--blue:hover{color:#1978d6}.bb-link.bb-link--blue:active{color:#176bbf}.bb-link.bb-link--blue:focus-visible::before{border-color:#2485e5;border-bottom:none}.bb-link.bb-link--blue.bb-link--gray-70:hover,.bb-link.bb-link--blue.bb-link--gray-80:hover,.bb-link.bb-link--blue.bb-link--gray-90:hover,.bb-link.bb-link--blue.bb-link--gray:hover,.bb-link.bb-link--blue.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--blue.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--blue.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--blue.bb-link--no-underline:active,.bb-link.bb-link--blue.bb-link--no-underline:focus,.bb-link.bb-link--blue.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--blue.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #2485e5}.bb-link.bb-link--blue.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--blue.bb-link--disabled:active,.bb-link.bb-link--blue.bb-link--disabled:focus,.bb-link.bb-link--blue.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--blue.bb-link--contrast,.bb-link.bb-link--blue.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--blue.bb-link--contrast:hover,.bb-link.bb-link--blue.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--blue.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--blue.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--blue.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--blue.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--blue.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--blue.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--blue.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--blue.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--blue.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--blue.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--blue.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--blue.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--navy{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#2485e5}.bb-link.bb-link--navy:active,.bb-link.bb-link--navy:focus,.bb-link.bb-link--navy:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--navy:hover{color:#1978d6}.bb-link.bb-link--navy:active{color:#176bbf}.bb-link.bb-link--navy:focus-visible::before{border-color:#2485e5;border-bottom:none}.bb-link.bb-link--navy.bb-link--gray-70:hover,.bb-link.bb-link--navy.bb-link--gray-80:hover,.bb-link.bb-link--navy.bb-link--gray-90:hover,.bb-link.bb-link--navy.bb-link--gray:hover,.bb-link.bb-link--navy.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--navy.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--navy.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--navy.bb-link--no-underline:active,.bb-link.bb-link--navy.bb-link--no-underline:focus,.bb-link.bb-link--navy.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--navy.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #2485e5}.bb-link.bb-link--navy.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--navy.bb-link--disabled:active,.bb-link.bb-link--navy.bb-link--disabled:focus,.bb-link.bb-link--navy.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--navy.bb-link--contrast,.bb-link.bb-link--navy.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--navy.bb-link--contrast:hover,.bb-link.bb-link--navy.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--navy.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--navy.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--navy.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--navy.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--navy.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--navy.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--navy.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--navy.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--navy.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--navy.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--navy.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--navy.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--red{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#ff7155}.bb-link.bb-link--red:active,.bb-link.bb-link--red:focus,.bb-link.bb-link--red:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--red:hover{color:#ff5c3c}.bb-link.bb-link--red:active{color:#ff4622}.bb-link.bb-link--red:focus-visible::before{border-color:#ff7155;border-bottom:none}.bb-link.bb-link--red.bb-link--gray-70:hover,.bb-link.bb-link--red.bb-link--gray-80:hover,.bb-link.bb-link--red.bb-link--gray-90:hover,.bb-link.bb-link--red.bb-link--gray:hover,.bb-link.bb-link--red.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--red.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--red.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--red.bb-link--no-underline:active,.bb-link.bb-link--red.bb-link--no-underline:focus,.bb-link.bb-link--red.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--red.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #ff7155}.bb-link.bb-link--red.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--red.bb-link--disabled:active,.bb-link.bb-link--red.bb-link--disabled:focus,.bb-link.bb-link--red.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--red.bb-link--contrast,.bb-link.bb-link--red.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--red.bb-link--contrast:hover,.bb-link.bb-link--red.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--red.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--red.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--red.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--red.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--red.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--red.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--red.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--red.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--red.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--red.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--red.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--red.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--gray{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#687d92}.bb-link.bb-link--gray:active,.bb-link.bb-link--gray:focus,.bb-link.bb-link--gray:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--gray:hover{color:#5d7083}.bb-link.bb-link--gray:active{color:#536474}.bb-link.bb-link--gray:focus-visible::before{border-color:#687d92;border-bottom:none}.bb-link.bb-link--gray.bb-link--gray-70:hover,.bb-link.bb-link--gray.bb-link--gray-80:hover,.bb-link.bb-link--gray.bb-link--gray-90:hover,.bb-link.bb-link--gray.bb-link--gray:hover,.bb-link.bb-link--gray.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--gray.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray.bb-link--no-underline:active,.bb-link.bb-link--gray.bb-link--no-underline:focus,.bb-link.bb-link--gray.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-link.bb-link--gray.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray.bb-link--disabled:active,.bb-link.bb-link--gray.bb-link--disabled:focus,.bb-link.bb-link--gray.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray.bb-link--contrast,.bb-link.bb-link--gray.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--gray.bb-link--contrast:hover,.bb-link.bb-link--gray.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--gray.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--gray.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--gray.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--gray.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--gray.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--gray.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--gray.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--gray.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--gray-90{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#687d92}.bb-link.bb-link--gray-90:active,.bb-link.bb-link--gray-90:focus,.bb-link.bb-link--gray-90:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--gray-90:hover{color:#5d7083}.bb-link.bb-link--gray-90:active{color:#536474}.bb-link.bb-link--gray-90:focus-visible::before{border-color:#687d92;border-bottom:none}.bb-link.bb-link--gray-90.bb-link--gray-70:hover,.bb-link.bb-link--gray-90.bb-link--gray-80:hover,.bb-link.bb-link--gray-90.bb-link--gray-90:hover,.bb-link.bb-link--gray-90.bb-link--gray:hover,.bb-link.bb-link--gray-90.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--gray-90.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray-90.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-90.bb-link--no-underline:active,.bb-link.bb-link--gray-90.bb-link--no-underline:focus,.bb-link.bb-link--gray-90.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray-90.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-link.bb-link--gray-90.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray-90.bb-link--disabled:active,.bb-link.bb-link--gray-90.bb-link--disabled:focus,.bb-link.bb-link--gray-90.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray-90.bb-link--contrast,.bb-link.bb-link--gray-90.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--gray-90.bb-link--contrast:hover,.bb-link.bb-link--gray-90.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--gray-90.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--gray-90.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray-90.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-90.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--gray-90.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--gray-90.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--gray-90.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-90.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--gray-90.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--gray-90.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray-90.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--gray-90.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--gray-80{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#687d92}.bb-link.bb-link--gray-80:active,.bb-link.bb-link--gray-80:focus,.bb-link.bb-link--gray-80:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--gray-80:hover{color:#5d7083}.bb-link.bb-link--gray-80:active{color:#536474}.bb-link.bb-link--gray-80:focus-visible::before{border-color:#687d92;border-bottom:none}.bb-link.bb-link--gray-80.bb-link--gray-70:hover,.bb-link.bb-link--gray-80.bb-link--gray-80:hover,.bb-link.bb-link--gray-80.bb-link--gray-90:hover,.bb-link.bb-link--gray-80.bb-link--gray:hover,.bb-link.bb-link--gray-80.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--gray-80.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray-80.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-80.bb-link--no-underline:active,.bb-link.bb-link--gray-80.bb-link--no-underline:focus,.bb-link.bb-link--gray-80.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray-80.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-link.bb-link--gray-80.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray-80.bb-link--disabled:active,.bb-link.bb-link--gray-80.bb-link--disabled:focus,.bb-link.bb-link--gray-80.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray-80.bb-link--contrast,.bb-link.bb-link--gray-80.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--gray-80.bb-link--contrast:hover,.bb-link.bb-link--gray-80.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--gray-80.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--gray-80.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray-80.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-80.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--gray-80.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--gray-80.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--gray-80.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-80.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--gray-80.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--gray-80.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray-80.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--gray-80.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--gray-70{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#687d92}.bb-link.bb-link--gray-70:active,.bb-link.bb-link--gray-70:focus,.bb-link.bb-link--gray-70:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--gray-70:hover{color:#5d7083}.bb-link.bb-link--gray-70:active{color:#536474}.bb-link.bb-link--gray-70:focus-visible::before{border-color:#687d92;border-bottom:none}.bb-link.bb-link--gray-70.bb-link--gray-70:hover,.bb-link.bb-link--gray-70.bb-link--gray-80:hover,.bb-link.bb-link--gray-70.bb-link--gray-90:hover,.bb-link.bb-link--gray-70.bb-link--gray:hover,.bb-link.bb-link--gray-70.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--gray-70.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray-70.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-70.bb-link--no-underline:active,.bb-link.bb-link--gray-70.bb-link--no-underline:focus,.bb-link.bb-link--gray-70.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray-70.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-link.bb-link--gray-70.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray-70.bb-link--disabled:active,.bb-link.bb-link--gray-70.bb-link--disabled:focus,.bb-link.bb-link--gray-70.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--gray-70.bb-link--contrast,.bb-link.bb-link--gray-70.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--gray-70.bb-link--contrast:hover,.bb-link.bb-link--gray-70.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--gray-70.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--gray-70.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--gray-70.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-70.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--gray-70.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--gray-70.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--gray-70.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--gray-70.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--gray-70.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--gray-70.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--gray-70.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--gray-70.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--yellow{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#687d92}.bb-link.bb-link--yellow:active,.bb-link.bb-link--yellow:focus,.bb-link.bb-link--yellow:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--yellow:hover{color:#5d7083}.bb-link.bb-link--yellow:active{color:#536474}.bb-link.bb-link--yellow:focus-visible::before{border-color:#687d92;border-bottom:none}.bb-link.bb-link--yellow.bb-link--gray-70:hover,.bb-link.bb-link--yellow.bb-link--gray-80:hover,.bb-link.bb-link--yellow.bb-link--gray-90:hover,.bb-link.bb-link--yellow.bb-link--gray:hover,.bb-link.bb-link--yellow.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--yellow.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--yellow.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--yellow.bb-link--no-underline:active,.bb-link.bb-link--yellow.bb-link--no-underline:focus,.bb-link.bb-link--yellow.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--yellow.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-link.bb-link--yellow.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--yellow.bb-link--disabled:active,.bb-link.bb-link--yellow.bb-link--disabled:focus,.bb-link.bb-link--yellow.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--yellow.bb-link--contrast,.bb-link.bb-link--yellow.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--yellow.bb-link--contrast:hover,.bb-link.bb-link--yellow.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--yellow.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--yellow.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--yellow.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--yellow.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--yellow.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--yellow.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--yellow.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--yellow.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--yellow.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--yellow.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--yellow.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--yellow.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--contrast{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#fff}.bb-link.bb-link--contrast:active,.bb-link.bb-link--contrast:focus,.bb-link.bb-link--contrast:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--contrast:hover{color:#f2f2f2}.bb-link.bb-link--contrast:active{color:#e6e6e6}.bb-link.bb-link--contrast:focus-visible::before{border-color:#fff;border-bottom:none}.bb-link.bb-link--contrast.bb-link--gray-70:hover,.bb-link.bb-link--contrast.bb-link--gray-80:hover,.bb-link.bb-link--contrast.bb-link--gray-90:hover,.bb-link.bb-link--contrast.bb-link--gray:hover,.bb-link.bb-link--contrast.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--contrast.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--contrast.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--contrast.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #fff}.bb-link.bb-link--contrast.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--contrast.bb-link--disabled:active,.bb-link.bb-link--contrast.bb-link--disabled:focus,.bb-link.bb-link--contrast.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--contrast.bb-link--contrast,.bb-link.bb-link--contrast.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--contrast.bb-link--contrast:hover,.bb-link.bb-link--contrast.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--contrast.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--contrast.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--contrast.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--contrast.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--contrast.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--contrast.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--contrast.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--contrast.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--contrast.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--contrast.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--contrast.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--contrast.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--white{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#fff}.bb-link.bb-link--white:active,.bb-link.bb-link--white:focus,.bb-link.bb-link--white:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--white:hover{color:#f2f2f2}.bb-link.bb-link--white:active{color:#e6e6e6}.bb-link.bb-link--white:focus-visible::before{border-color:#fff;border-bottom:none}.bb-link.bb-link--white.bb-link--gray-70:hover,.bb-link.bb-link--white.bb-link--gray-80:hover,.bb-link.bb-link--white.bb-link--gray-90:hover,.bb-link.bb-link--white.bb-link--gray:hover,.bb-link.bb-link--white.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--white.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #fff}.bb-link.bb-link--white.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--white.bb-link--disabled:active,.bb-link.bb-link--white.bb-link--disabled:focus,.bb-link.bb-link--white.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--white.bb-link--contrast,.bb-link.bb-link--white.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--white.bb-link--contrast:hover,.bb-link.bb-link--white.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--white.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--white.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--white.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--white.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--white.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--white.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--white.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--white.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--white.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--white.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--white.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--white.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}.bb-link.bb-link--disabled{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a;color:#687d92}.bb-link.bb-link--disabled:active,.bb-link.bb-link--disabled:focus,.bb-link.bb-link--disabled:hover{background-color:transparent;box-shadow:0 .0625rem 0 0 #89a}.bb-link.bb-link--disabled:hover{color:#5d7083}.bb-link.bb-link--disabled:active{color:#536474}.bb-link.bb-link--disabled:focus-visible::before{border-color:#687d92;border-bottom:none}.bb-link.bb-link--disabled.bb-link--gray-70:hover,.bb-link.bb-link--disabled.bb-link--gray-80:hover,.bb-link.bb-link--disabled.bb-link--gray-90:hover,.bb-link.bb-link--disabled.bb-link--gray:hover,.bb-link.bb-link--disabled.bb-link--yellow:hover{color:#0ccca2}.bb-link.bb-link--disabled.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--disabled.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--disabled.bb-link--no-underline:active,.bb-link.bb-link--disabled.bb-link--no-underline:focus,.bb-link.bb-link--disabled.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--disabled.bb-link--no-underline:focus-visible::before{border:.0625rem dashed #687d92}.bb-link.bb-link--disabled.bb-link--disabled{background-color:transparent;color:#c1cad2}.bb-link.bb-link--disabled.bb-link--disabled:active,.bb-link.bb-link--disabled.bb-link--disabled:focus,.bb-link.bb-link--disabled.bb-link--disabled:hover{background-color:transparent;color:#c1cad2}.bb-link.bb-link--disabled.bb-link--contrast,.bb-link.bb-link--disabled.bb-link--white{box-shadow:0 .0625rem 0 0 #fff;color:#fff}.bb-link.bb-link--disabled.bb-link--contrast:hover,.bb-link.bb-link--disabled.bb-link--white:hover{color:rgba(255,255,255,.7)}.bb-link.bb-link--disabled.bb-link--contrast.bb-link--no-underline,.bb-link.bb-link--disabled.bb-link--white.bb-link--no-underline{box-shadow:none}.bb-link.bb-link--disabled.bb-link--contrast.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--disabled.bb-link--contrast.bb-link--no-underline:active,.bb-link.bb-link--disabled.bb-link--contrast.bb-link--no-underline:focus,.bb-link.bb-link--disabled.bb-link--contrast.bb-link--no-underline:hover,.bb-link.bb-link--disabled.bb-link--white.bb-link--no-underline.bb-link--disabled,.bb-link.bb-link--disabled.bb-link--white.bb-link--no-underline:active,.bb-link.bb-link--disabled.bb-link--white.bb-link--no-underline:focus,.bb-link.bb-link--disabled.bb-link--white.bb-link--no-underline:hover{box-shadow:none}.bb-link.bb-link--disabled.bb-link--contrast.bb-link--disabled,.bb-link.bb-link--disabled.bb-link--white.bb-link--disabled{box-shadow:0 .0625rem 0 0 rgba(255,255,255,.5);color:rgba(255,255,255,.5)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2p = undefined;
/* module identifier */
const __vue_module_identifier__$2p = undefined;
/* functional template */
const __vue_is_functional_template__$2p = undefined;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2p = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$2p, __vue_script__$2p, __vue_scope_id__$2p, __vue_is_functional_template__$2p, __vue_module_identifier__$2p, false, createInjector, undefined, undefined);

const BBRL = '__BB_root_listeners__';
var ListenOnRootMixin = {
  methods: {
    listenOnRoot(event, callback) {
      if (!this[BBRL] || !isArray(this[BBRL])) {
        this[BBRL] = [];
      }
      this[BBRL].push({
        event,
        callback
      });
      this.$root.$on(event, callback);
      return this;
    },
    emitOnRoot(event, ...args) {
      this.$root.$emit(event, ...args);
      return this;
    }
  },
  beforeDestroy() {
    if (this[BBRL] && isArray(this[BBRL])) {
      while (this[BBRL].length > 0) {
        const {
          event,
          callback
        } = this[BBRL].shift();
        this.$root.$off(event, callback);
      }
    }
  }
};

var script$2o = {
  name: 'bb-dropdown-item',
  components: {
    BbButton: __vue_component__$2v,
    BbLink: __vue_component__$2p
  },
  mixins: [ListenOnRootMixin],
  props: {
    target: {
      type: String,
      default: '_self'
    },
    to: {
      type: [String, Object]
    },
    condensed: Boolean,
    label: String,
    disabled: Boolean,
    buttonProps: Object,
    linkProps: Object
  },
  emits: ['click', 'bb::dropdown-item::clicked'],
  computed: {
    isStatic() {
      return !this.to;
    },
    dropdownItemClasses() {
      return ['bb-dropdown-item', {
        'bb-dropdown-item--condensed': this.condensed,
        'bb-dropdown-item--disabled': this.disabled
      }];
    },
    isButtonDisabled() {
      var _this$buttonProps$dis, _this$buttonProps;
      return (_this$buttonProps$dis = (_this$buttonProps = this.buttonProps) === null || _this$buttonProps === void 0 ? void 0 : _this$buttonProps.disabled) !== null && _this$buttonProps$dis !== void 0 ? _this$buttonProps$dis : this.disabled;
    },
    isLinkDisabled() {
      var _this$linkProps$disab, _this$linkProps;
      return (_this$linkProps$disab = (_this$linkProps = this.linkProps) === null || _this$linkProps === void 0 ? void 0 : _this$linkProps.disabled) !== null && _this$linkProps$disab !== void 0 ? _this$linkProps$disab : this.disabled;
    },
    linkTarget() {
      var _this$linkProps$targe, _this$linkProps2;
      return (_this$linkProps$targe = (_this$linkProps2 = this.linkProps) === null || _this$linkProps2 === void 0 ? void 0 : _this$linkProps2.target) !== null && _this$linkProps$targe !== void 0 ? _this$linkProps$targe : this.target;
    }
  },
  methods: {
    click(event) {
      this.$emit('click');
      this.emitOnRoot('bb::dropdown-item::clicked', event.target);
    }
  }
};

/* script */
const __vue_script__$2o = script$2o;

/* template */
var __vue_render__$2l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.isStatic ? _c('bb-button', _vm._b({
    class: _vm.dropdownItemClasses,
    attrs: {
      "disabled": _vm.isButtonDisabled,
      "role": "menuitem",
      "data-testid": "bb-dropdown-item",
      "aria-disabled": _vm.isButtonDisabled
    },
    on: {
      "click": _vm.click
    }
  }, 'bb-button', Object.assign({}, {
    label: _vm.label,
    color: 'gray',
    display: 'block',
    corner: 'sharp',
    noBackground: true
  }, _vm.buttonProps), false), [_vm._t("default")], 2) : _c('bb-link', _vm._b({
    class: _vm.dropdownItemClasses,
    attrs: {
      "to": _vm.to,
      "target": _vm.linkTarget,
      "role": "menuitem",
      "data-testid": "bb-dropdown-item",
      "disabled": _vm.isLinkDisabled,
      "aria-disabled": _vm.isLinkDisabled
    },
    nativeOn: {
      "click": function ($event) {
        return _vm.click.apply(null, arguments);
      }
    }
  }, 'bb-link', Object.assign({}, {
    color: 'none',
    noUnderline: true
  }, _vm.linkProps), false), [_vm._t("default", function () {
    return [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")];
  }, {
    "dataTestid": "bb-dropdown-item__label"
  })], 2);
};
var __vue_staticRenderFns__$2l = [];

/* style */
const __vue_inject_styles__$2o = function (inject) {
  if (!inject) return;
  inject("data-v-c778c2a2_0", {
    source: ".bb-button.bb-dropdown-item,.bb-dropdown-item,.bb-link.bb-dropdown-item{display:block;overflow:hidden;min-height:0;padding:.8rem 1.25rem;border-top:.0625rem solid #eeeff2;color:#687d92;font-size:14px;font-weight:400;line-height:inherit;text-align:left;white-space:normal}.bb-button.bb-dropdown-item:first-child,.bb-dropdown-item:first-child,.bb-link.bb-dropdown-item:first-child{border-top:none}.bb-button.bb-dropdown-item:focus,.bb-button.bb-dropdown-item:hover,.bb-dropdown-item:focus,.bb-dropdown-item:hover,.bb-link.bb-dropdown-item:focus,.bb-link.bb-dropdown-item:hover{background-color:#eeeff2;color:#57697a;outline:0;text-decoration:none}.bb-button.bb-dropdown-item.active,.bb-button.bb-dropdown-item:active,.bb-dropdown-item.active,.bb-dropdown-item:active,.bb-link.bb-dropdown-item.active,.bb-link.bb-dropdown-item:active{background-color:#eeeff2;color:#2485e5;text-decoration:none}.bb-button.bb-dropdown-item--disabled,.bb-button.bb-dropdown-item--disabled:active,.bb-button.bb-dropdown-item--disabled:focus,.bb-button.bb-dropdown-item--disabled:hover,.bb-dropdown-item--disabled,.bb-dropdown-item--disabled:active,.bb-dropdown-item--disabled:focus,.bb-dropdown-item--disabled:hover,.bb-link.bb-dropdown-item--disabled,.bb-link.bb-dropdown-item--disabled:active,.bb-link.bb-dropdown-item--disabled:focus,.bb-link.bb-dropdown-item--disabled:hover{background-color:transparent;color:#c1cad2}.bb-button.bb-dropdown-item--condensed,.bb-dropdown-item--condensed,.bb-link.bb-dropdown-item--condensed{padding:.5rem .625rem}.bb-dropdown--autosize .bb-button.bb-dropdown-item,.bb-dropdown--autosize .bb-dropdown-item,.bb-dropdown--autosize .bb-link.bb-dropdown-item{white-space:nowrap}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2o = undefined;
/* module identifier */
const __vue_module_identifier__$2o = undefined;
/* functional template */
const __vue_is_functional_template__$2o = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2o = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2l,
  staticRenderFns: __vue_staticRenderFns__$2l
}, __vue_inject_styles__$2o, __vue_script__$2o, __vue_scope_id__$2o, __vue_is_functional_template__$2o, __vue_module_identifier__$2o, false, createInjector, undefined, undefined);

const logicalOperators = [{
  text: '=',
  value: 'EQ'
}, {
  text: '≠',
  value: 'NEQ'
}, {
  text: '~',
  value: 'LIKE'
}, {
  text: '>',
  value: 'GT'
}, {
  text: '<',
  value: 'LT'
}, {
  text: '<>',
  value: 'BETWEEN'
}];

var Icon$R = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg94918225__a bb-icon__dynamic-fill",attrs:{"d":"M7,10L12,15L17,10H7Z"}})]) } };

Vue.component('icon-ui-menu-down', Icon$R);

var script$2n = {
  name: 'bb-field-prefix',
  components: {
    BbIcon: __vue_component__$2C
  },
  mixins: [Field, FieldExtensions, veeValidationMixin],
  props: {
    options: {
      type: Array,
      default: () => logicalOperators
    },
    prefixAriaLabel: {
      type: String,
      default: 'Prefix'
    }
  },
  emits: ['blur', 'focus']
};

/* script */
const __vue_script__$2n = script$2n;

/* template */
var __vue_render__$2k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-field-prefix",
    class: {
      'bb-field-prefix--small': _vm.small
    }
  }, [_c('select', {
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    staticClass: "bb-field-prefix__select",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "aria-label": _vm.prefixAriaLabel,
      "data-testid": "bb-field-prefix__select"
    },
    on: {
      "input": _vm.onInput,
      "change": [function ($event) {
        var $$selectedVal = Array.prototype.filter.call($event.target.options, function (o) {
          return o.selected;
        }).map(function (o) {
          var val = "_value" in o ? o._value : o.value;
          return val;
        });
        _vm.val = $event.target.multiple ? $$selectedVal : $$selectedVal[0];
      }, _vm.onChange],
      "blur": function ($event) {
        return _vm.$emit('blur');
      },
      "focus": function ($event) {
        return _vm.$emit('focus');
      }
    }
  }, _vm._l(_vm.options, function (opt, index) {
    return _c('option', {
      key: index,
      attrs: {
        "disabled": opt.disabled ? true : null
      },
      domProps: {
        "value": opt.value
      }
    }, [_vm._v("\n      " + _vm._s(opt.text) + "\n    ")]);
  }), 0), _vm._v(" "), _c('bb-icon', {
    staticClass: "bb-field-prefix__arrow-icon",
    attrs: {
      "name": "ui-menu-down",
      "size": "24",
      "fill": "gray-70"
    }
  })], 1);
};
var __vue_staticRenderFns__$2k = [];

/* style */
const __vue_inject_styles__$2n = function (inject) {
  if (!inject) return;
  inject("data-v-2faee678_0", {
    source: ".bb-field-prefix{position:relative;display:inline-block}.framed-input .bb-field-prefix{height:2.5rem}.framed-input .bb-field-prefix--small{height:1.875rem}.bb-field-prefix__select{display:inline-block;width:auto;min-width:2.8125rem;padding-right:1.5rem;margin-left:0;background-position-x:88%}.framed-input .bb-field-prefix__select{height:100%;padding:.3125rem 1.5rem .25rem .375rem;border-bottom-right-radius:0;border-top-right-radius:0}.bb-field-prefix--small .bb-field-prefix__select{padding-top:.25rem;padding-right:1.5rem;background-position:right 5px top 56%;background-size:10px}.bb-field-prefix__arrow-icon{position:absolute;top:50%;right:0;pointer-events:none;transform:translateY(-50%)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2n = undefined;
/* module identifier */
const __vue_module_identifier__$2n = undefined;
/* functional template */
const __vue_is_functional_template__$2n = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2n = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2k,
  staticRenderFns: __vue_staticRenderFns__$2k
}, __vue_inject_styles__$2n, __vue_script__$2n, __vue_scope_id__$2n, __vue_is_functional_template__$2n, __vue_module_identifier__$2n, false, createInjector, undefined, undefined);

/**
 * A short description of the component.
 */
var script$2m = defineComponent({
  name: 'bb-functional-accordion',
  components: {
    CollapseTransition
  },
  props: {
    /** Open when mounted */
    open: Boolean,
    /** ID suffix for header & content */
    id: String
  },
  emits: ['open', 'close'],
  data() {
    return {
      expanded: this.open,
      uuid: uuid()
    };
  },
  computed: {
    computedId() {
      return this.id || this.uuid;
    },
    headerId() {
      return `header-${this.computedId}`;
    },
    contentId() {
      return `content-${this.computedId}`;
    }
  },
  watch: {
    open(newVal) {
      this.expanded = newVal;
    },
    expanded(newVal) {
      if (newVal) {
        /**
         * Accordion opened
         *
         * @event open
         */
        this.$emit('open');
      } else {
        /**
         * Accordion closed
         *
         * @event close
         */
        this.$emit('close');
      }
    }
  },
  methods: {
    toggleContent() {
      this.expanded = !this.expanded;
    }
  }
});

/* script */
const __vue_script__$2m = script$2m;

/* template */
var __vue_render__$2j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-functional-accordion"
  }, [_c('div', {
    staticClass: "bb-functional-accordion__header",
    attrs: {
      "id": _vm.headerId,
      "tabindex": "0",
      "role": "button",
      "aria-expanded": _vm.expanded,
      "aria-controls": _vm.contentId
    },
    on: {
      "click": _vm.toggleContent,
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.toggleContent.apply(null, arguments);
      }
    }
  }, [_vm._t("header", null, null, {
    expanded: _vm.expanded
  })], 2), _vm._v(" "), _c('collapse-transition', [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.expanded,
      expression: "expanded"
    }],
    attrs: {
      "id": _vm.contentId,
      "role": "region",
      "aria-hidden": !_vm.expanded,
      "aria-labelledby": _vm.headerId
    }
  }, [_vm._t("default")], 2)])], 1);
};
var __vue_staticRenderFns__$2j = [];

/* style */
const __vue_inject_styles__$2m = function (inject) {
  if (!inject) return;
  inject("data-v-7f82af05_0", {
    source: ".bb-functional-accordion__header{cursor:pointer}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2m = undefined;
/* module identifier */
const __vue_module_identifier__$2m = undefined;
/* functional template */
const __vue_is_functional_template__$2m = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2m = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2j,
  staticRenderFns: __vue_staticRenderFns__$2j
}, __vue_inject_styles__$2m, __vue_script__$2m, __vue_scope_id__$2m, __vue_is_functional_template__$2m, __vue_module_identifier__$2m, false, createInjector, undefined, undefined);

var ClickoutMixin = {
  mounted() {
    if (typeof document !== 'undefined') {
      document.documentElement.addEventListener('click', this._clickOutListener);
    }
  },
  beforeDestroy() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('click', this._clickOutListener);
    }
  },
  methods: {
    _clickOutListener(e) {
      if (!this.$el.contains(e.target)) {
        if (this.clickOutListener) {
          this.clickOutListener();
        }
      }
    }
  }
};

const KeyCodes = {
  SPACE: 32,
  ENTER: 13,
  ESC: 27,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  PAGEUP: 33,
  PAGEDOWN: 34,
  HOME: 36,
  END: 35
};

/**
 * Dropdown without styling.
 */
var script$2l = defineComponent({
  name: 'bb-functional-dropdown',
  mixins: [ClickoutMixin, ListenOnRootMixin],
  props: {
    /**
     * Classes for the menu wrapper element
     */
    menuClasses: [Array, String, Object],
    /**
     * Custom options for Floating UI used to position the menu
     */
    customFloatingUiOptions: {
      type: Object,
      default: () => ({})
    },
    /**
     * Custom reference element the menu should align to.
     * If omitted, the trigger is used
     */
    menuPositionRef: null
  },
  emits: ['show', 'bb::dropdown::shown', 'hide', 'bb::dropdown::hidden'],
  data() {
    return {
      uuid: uuid(),
      isMenuVisible: false,
      cleanupFloatingUi: undefined,
      menuPositionStyles: {}
    };
  },
  computed: {
    toggleButton() {
      var _this$$refs$toggle;
      return (_this$$refs$toggle = this.$refs.toggle) === null || _this$$refs$toggle === void 0 ? void 0 : _this$$refs$toggle.firstElementChild;
    },
    referenceElement() {
      var _this$menuPositionRef;
      const menuPositionEl = ((_this$menuPositionRef = this.menuPositionRef) === null || _this$menuPositionRef === void 0 ? void 0 : _this$menuPositionRef.$el) || this.menuPositionRef;
      return menuPositionEl || this.toggleButton;
    },
    floatingUIOptions() {
      const floatingUIOptions = {
        placement: 'bottom-start'
      };
      return merge(floatingUIOptions, this.customFloatingUiOptions);
    }
  },
  watch: {
    isMenuVisible(state) {
      this.handleFloatingUi();
      if (state) {
        /**
         * Emitted when menu is opened
         * @event show
         */
        this.$emit('show');
        this.emitOnRoot('bb::dropdown::shown', this);
      } else {
        /**
         * Emitted when menu is closed
         * @event hide
         */
        this.$emit('hide');
        this.emitOnRoot('bb::dropdown::hidden', this);
      }
    }
  },
  mounted() {
    // Keep only one menu opened on page
    this.listenOnRoot('bb::dropdown::shown', this.rootCloseHandler);
  },
  deactivated() {
    var _this$cleanupFloating;
    // In case we are inside a `<keep-alive>`
    this.isMenuVisible = false;
    (_this$cleanupFloating = this.cleanupFloatingUi) === null || _this$cleanupFloating === void 0 ? void 0 : _this$cleanupFloating.call(this);
  },
  beforeDestroy() {
    var _this$cleanupFloating2;
    (_this$cleanupFloating2 = this.cleanupFloatingUi) === null || _this$cleanupFloating2 === void 0 ? void 0 : _this$cleanupFloating2.call(this);
  },
  methods: {
    rootCloseHandler(vm) {
      if (vm !== this) {
        this.isMenuVisible = false;
      }
    },
    toggleMenu() {
      this.isMenuVisible = !this.isMenuVisible;
    },
    onClick(event) {
      event === null || event === void 0 ? void 0 : event.stopPropagation();
      this.toggleMenu();
    },
    onKeydown(event) {
      const keys = [KeyCodes.ENTER, KeyCodes.SPACE];
      if (!keys.includes(event.keyCode)) {
        return;
      }
      event === null || event === void 0 ? void 0 : event.stopPropagation();
      this.toggleMenu();
    },
    onEsc(event) {
      if (this.isMenuVisible) {
        var _this$toggleButton;
        this.isMenuVisible = false;
        event.stopPropagation();
        (_this$toggleButton = this.toggleButton) === null || _this$toggleButton === void 0 ? void 0 : _this$toggleButton.focus();
      }
    },
    clickOutListener() {
      this.isMenuVisible = false;
    },
    handleFloatingUi() {
      var _this$cleanupFloating3;
      (_this$cleanupFloating3 = this.cleanupFloatingUi) === null || _this$cleanupFloating3 === void 0 ? void 0 : _this$cleanupFloating3.call(this);
      this.cleanupFloatingUi = this.isMenuVisible ? this.autoPositionFloatingElement() : undefined;
    },
    autoPositionFloatingElement() {
      const cleanup = autoUpdate(this.referenceElement, this.$refs.menu, this.updatePosition);
      return cleanup;
    },
    async updatePosition() {
      const {
        x,
        y
      } = await computePosition(this.referenceElement, this.$refs.menu, this.floatingUIOptions);
      this.menuPositionStyles = {
        left: `${x}px`,
        top: `${y}px`
      };
    }
  }
});

/* script */
const __vue_script__$2l = script$2l;

/* template */
var __vue_render__$2i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-functional-dropdown"
  }, [_c('div', {
    ref: "toggle"
  }, [_vm._t("trigger", null, {
    "isOpen": _vm.isMenuVisible,
    "attrs": {
      'aria-haspopup': 'menu',
      'aria-expanded': _vm.isMenuVisible,
      'aria-controls': _vm.uuid,
      role: 'button',
      tabindex: 0
    },
    "eventHandlers": {
      keydown: _vm.onKeydown,
      click: _vm.onClick
    }
  })], 2), _vm._v(" "), _c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.isMenuVisible,
      expression: "isMenuVisible"
    }],
    ref: "menu",
    staticClass: "bb-functional-dropdown__menu",
    class: _vm.menuClasses,
    style: _vm.menuPositionStyles,
    attrs: {
      "id": _vm.uuid,
      "role": "menu"
    },
    on: {
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }
        return _vm.onEsc.apply(null, arguments);
      }
    }
  }, [_vm._t("default", null, {
    "eventHandlers": {
      keydown: _vm.onKeydown,
      click: _vm.onClick
    }
  })], 2)]);
};
var __vue_staticRenderFns__$2i = [];

/* style */
const __vue_inject_styles__$2l = function (inject) {
  if (!inject) return;
  inject("data-v-1773b681_0", {
    source: ".bb-functional-dropdown{display:inline-block}.bb-functional-dropdown__menu{position:absolute;z-index:1400;width:max-content}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2l = undefined;
/* module identifier */
const __vue_module_identifier__$2l = undefined;
/* functional template */
const __vue_is_functional_template__$2l = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2l = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2i,
  staticRenderFns: __vue_staticRenderFns__$2i
}, __vue_inject_styles__$2l, __vue_script__$2l, __vue_scope_id__$2l, __vue_is_functional_template__$2l, __vue_module_identifier__$2l, false, createInjector, undefined, undefined);

var script$2k = {
  name: 'bb-functional-sticky',
  directives: {
    resize
  },
  props: {
    position: {
      type: String,
      default: 'bottom',
      validator: position => ['bottom', 'top'].includes(position)
    },
    fitParent: Boolean,
    scrollContent: null,
    viewport: typeof HTMLElement === 'undefined' ? null : HTMLElement,
    stickyAreaDistance: String
  },
  emits: ['stuck'],
  data() {
    return {
      stickyWrapperHeight: 0,
      areaStyles: null,
      resizeObserver: null,
      intersectionObservers: [],
      refs: {
        ...this.$refs
      },
      isScrollContentInView: false,
      isStickyWrapperAboveHorizon: false
    };
  },
  computed: {
    classes() {
      return ['bb-functional-sticky', `bb-functional-sticky--position-${this.position}`, {
        'is-stuck': this.isStuck
      }];
    },
    styles() {
      return this.isStuck && {
        'min-height': `${this.stickyWrapperHeight}px`,
        'max-height': `${this.stickyWrapperHeight}px`
      };
    },
    isStuckToTop() {
      return this.position === 'top';
    },
    computedAreaStyles() {
      return this.fitParent && this.isStuck && this.areaStyles;
    },
    computedTopOrBottom() {
      return this.stickyAreaDistance ? {
        [this.position]: this.stickyAreaDistance
      } : {};
    },
    observables() {
      var _this$refs$stickyArea, _this$scrollContent;
      const ratioOfStickyAreaToScrollContent = 1.5 * ((_this$refs$stickyArea = this.refs.stickyArea) === null || _this$refs$stickyArea === void 0 ? void 0 : _this$refs$stickyArea.offsetHeight) / ((_this$scrollContent = this.scrollContent) === null || _this$scrollContent === void 0 ? void 0 : _this$scrollContent.offsetHeight);
      return [{
        element: this.$el,
        threshold: 1,
        handler: this.intersectStickyWrapper
      }, {
        element: this.scrollContent,
        threshold: Math.min(ratioOfStickyAreaToScrollContent, 1) || 0,
        handler: this.intersectScrollContent
      }].filter(v => isDefined(v.element));
    },
    isStuck() {
      const isScrollContentInView = !this.scrollContent || this.isScrollContentInView;
      return isScrollContentInView && this.isStickyWrapperAboveHorizon;
    }
  },
  watch: {
    isStuck(stickyState) {
      this.$emit('stuck', stickyState);
    },
    viewport: {
      immediate: true,
      handler() {
        this.initializeIntersectionObservers();
      }
    },
    scrollContent() {
      this.initializeIntersectionObservers();
    },
    computedTopOrBottom() {
      if (this.fitParent) {
        this.updateAreaStyles();
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateRefs();
      this.updateStickyWrapperHeight();
      this.addResizeListener();
      this.initializeIntersectionObservers();
    });
  },
  updated() {
    this.updateRefs();
  },
  destroyed() {
    this.removeIntersectionObservers();
    this.removeResizeListener();
  },
  methods: {
    initializeIntersectionObservers() {
      this.removeIntersectionObservers();
      this.addIntersectionObservers();
    },
    addIntersectionObservers() {
      this.intersectionObservers = this.observables.map(this.addIntersectionObserver);
    },
    removeIntersectionObservers() {
      this.intersectionObservers.forEach((observer, index) => observer.unobserve(this.observables[index].element));
      this.intersectionObservers.length = 0;
    },
    addIntersectionObserver({
      element,
      threshold,
      handler
    }) {
      const options = {
        threshold,
        root: this.viewport
      };
      const intersectionObserver = new IntersectionObserver(handler, options);
      intersectionObserver.observe(element);
      return intersectionObserver;
    },
    addResizeListener() {
      if (this.fitParent) {
        this.resizeObserver = new ResizeObserver(this.updateAreaStyles);
        this.resizeObserver.observe(document.body);
      }
    },
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(document.body);
      }
    },
    updateAreaStyles() {
      const parent = this.$parent.$el.getBoundingClientRect();
      this.areaStyles = {
        left: `${parent.left}px`,
        width: `${parent.width}px`,
        ...this.computedTopOrBottom
      };
    },
    onScroll() {
      this.updateStickyWrapperHeightIfNone();
      if (this.fitParent) {
        this.updateAreaStyles();
      }
    },
    intersectStickyWrapper([{
      boundingClientRect,
      rootBounds
    }]) {
      this.updateStickyWrapperHeightIfNone();
      this.isStickyWrapperAboveHorizon = this.isStuckToTop ? boundingClientRect.y < rootBounds.y : boundingClientRect.y + boundingClientRect.height > rootBounds.y + rootBounds.height;
    },
    intersectScrollContent([{
      isIntersecting
    }]) {
      this.updateStickyWrapperHeightIfNone();
      this.isScrollContentInView = isIntersecting;
    },
    onKeepHeightSlotResize() {
      this.updateStickyWrapperHeight();
    },
    updateStickyWrapperHeightIfNone() {
      if (!this.stickyWrapperHeight) {
        this.updateStickyWrapperHeight();
      }
    },
    updateStickyWrapperHeight() {
      var _this$$refs$keepHeigh, _this$$refs$keepHeigh2;
      this.stickyWrapperHeight = (_this$$refs$keepHeigh = (_this$$refs$keepHeigh2 = this.$refs.keepHeightWrapper) === null || _this$$refs$keepHeigh2 === void 0 ? void 0 : _this$$refs$keepHeigh2.clientHeight) !== null && _this$$refs$keepHeigh !== void 0 ? _this$$refs$keepHeigh : this.stickyWrapperHeight;
    },
    updateRefs() {
      Object.keys({
        ...this.$refs,
        ...this.refs
      }).forEach(key => this.$set(this.refs, key, this.$refs[key]));
    }
  }
};

/* script */
const __vue_script__$2k = script$2k;

/* template */
var __vue_render__$2h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes,
    style: _vm.styles
  }, [_c('div', {
    ref: "stickyArea",
    staticClass: "bb-functional-sticky__sticky-area",
    style: _vm.computedAreaStyles
  }, [_vm._t("noHeight"), _vm._v(" "), _vm.$slots.keepHeight ? _c('div', {
    directives: [{
      name: "resize",
      rawName: "v-resize:throttle.100",
      value: _vm.onKeepHeightSlotResize,
      expression: "onKeepHeightSlotResize",
      arg: "throttle",
      modifiers: {
        "100": true
      }
    }],
    ref: "keepHeightWrapper"
  }, [_vm._t("keepHeight")], 2) : _vm._e()], 2)]);
};
var __vue_staticRenderFns__$2h = [];

/* style */
const __vue_inject_styles__$2k = function (inject) {
  if (!inject) return;
  inject("data-v-0a2886e0_0", {
    source: ".is-stuck .bb-functional-sticky__sticky-area{position:fixed;z-index:1300;left:0;width:100%;text-align:center}.is-stuck.bb-functional-sticky--position-top .bb-functional-sticky__sticky-area{top:0}.is-stuck.bb-functional-sticky--position-bottom .bb-functional-sticky__sticky-area{bottom:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2k = undefined;
/* module identifier */
const __vue_module_identifier__$2k = undefined;
/* functional template */
const __vue_is_functional_template__$2k = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2k = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2h,
  staticRenderFns: __vue_staticRenderFns__$2h
}, __vue_inject_styles__$2k, __vue_script__$2k, __vue_scope_id__$2k, __vue_is_functional_template__$2k, __vue_module_identifier__$2k, false, createInjector, undefined, undefined);

var script$2j = {
  name: 'bb-heading',
  props: {
    title: String,
    topic: String,
    center: Boolean,
    tagName: {
      type: String,
      default: 'h6',
      validator: tagName => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)
    },
    size: {
      type: String,
      default: 'responsive',
      validator: size => ['responsive', 'sm', 'md', 'lg'].includes(size)
    },
    white: Boolean
  },
  computed: {
    classes() {
      return ['bb-heading', `bb-heading--${this.size}`, `bb-heading--${this.center ? 'center' : 'left'}`, {
        'bb-heading--white': this.white
      }];
    }
  }
};

/* script */
const __vue_script__$2j = script$2j;

/* template */
var __vue_render__$2g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(_vm.tagName, {
    tag: "component",
    class: _vm.classes
  }, [_vm.topic ? _c('strong', {
    staticClass: "bb-heading__topic"
  }, [_vm._v("\n    " + _vm._s(_vm.topic) + "\n  ")]) : _vm._e(), _vm._v("\n  " + _vm._s(_vm.title) + "\n")]);
};
var __vue_staticRenderFns__$2g = [];

/* style */
const __vue_inject_styles__$2j = function (inject) {
  if (!inject) return;
  inject("data-v-71a56d51_0", {
    source: ".bb-heading{position:relative;padding:1.25rem 1.875rem;margin-bottom:0;color:#00285a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-heading--white{color:#fff}.bb-heading--responsive{font-size:24px;line-height:1.17}@media (min-width:768px){.bb-heading--responsive{font-size:32px;line-height:1.11}}@media (min-width:992px){.bb-heading--responsive{font-size:42px;line-height:1.08}}.bb-heading--lg{font-size:42px;line-height:1.08}.bb-heading--md{font-size:32px;line-height:1.11}.bb-heading--sm{font-size:24px;line-height:1.17}.bb-heading--center{display:flex;flex-direction:column;align-items:center;text-align:center}.bb-heading--left{padding-left:2.125rem}.bb-heading--left.bb-heading::after{position:absolute;top:0;left:0;display:block;width:.25rem;height:100%;border-radius:.125rem;background:#0ccca2;content:\"\"}.bb-heading--white.bb-heading--left.bb-heading::after{background:#fff}.bb-heading--center.bb-heading::after{display:block;width:6.25rem;border-radius:.125rem;border-bottom:.25rem solid #0ccca2;margin-top:.875rem;content:\"\"}.bb-heading--lg.bb-heading--center.bb-heading::after{margin-top:1.4375rem}.bb-heading--white.bb-heading--center.bb-heading::after{border-color:#fff}.bb-heading__topic{display:block;margin-bottom:1.125rem;color:#9dabb9;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;text-transform:uppercase}.bb-heading--white .bb-heading__topic{color:#fff}.bb-heading--lg .bb-heading__topic{margin-bottom:1.125rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2j = undefined;
/* module identifier */
const __vue_module_identifier__$2j = undefined;
/* functional template */
const __vue_is_functional_template__$2j = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2j = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2g,
  staticRenderFns: __vue_staticRenderFns__$2g
}, __vue_inject_styles__$2j, __vue_script__$2j, __vue_scope_id__$2j, __vue_is_functional_template__$2j, __vue_module_identifier__$2j, false, createInjector, undefined, undefined);

var script$2i = {
  name: 'bb-hero',
  props: {
    title: String,
    titleLineTwo: String,
    color: {
      type: String,
      default: 'blue'
    },
    bleed: {
      type: String,
      default: 'card',
      validator: val => ['card', 'all', 'none'].includes(val)
    },
    animation: String
  },
  computed: {
    heroSectionClasses() {
      return ['bb-hero', this.bleed !== 'none' ? 'bb-hero--bleed' : null, this.bleed === 'all' ? 'bb-hero--bleed-all' : null, `bg-${this.color}`];
    }
  }
};

/* script */
const __vue_script__$2i = script$2i;

/* template */
var __vue_render__$2f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('section', {
    class: _vm.heroSectionClasses
  }, [_vm.title ? _c('div', {
    staticClass: "bb-hero__title-wrapper"
  }, [_c('h1', {
    class: ['bb-hero__title', _vm.animation]
  }, [_vm._v("\n      " + _vm._s(_vm.title)), _vm.titleLineTwo ? _c('br') : _vm._e(), _vm._v(_vm._s(_vm.titleLineTwo) + "\n    ")])]) : _vm._e(), _vm._v(" "), _vm._t("default")], 2);
};
var __vue_staticRenderFns__$2f = [];

/* style */
const __vue_inject_styles__$2i = function (inject) {
  if (!inject) return;
  inject("data-v-2906da7c_0", {
    source: ".bb-hero{padding-top:0;padding-bottom:0}.bb-hero--bleed{padding-bottom:5rem}.bb-hero--bleed-all+*{position:relative;min-height:6.875rem;margin-top:-5rem}.bb-hero--bleed+.bb-card,.bb-hero--bleed+.bb-card-slider,.bb-hero--bleed+.bb-card-wrapper{position:relative;min-height:6.875rem;margin-top:-5rem}.bb-hero--bleed+.bb-card--status{margin-top:24px;margin-bottom:-5rem;transform:translateY(-5rem)}.bb-hero__title-wrapper{display:flex;min-height:4.5rem;align-items:center;justify-content:center}@media (min-width:768px){.bb-hero__title-wrapper{min-height:6.25rem}}@media (-ms-high-contrast:none),(-ms-high-contrast:active){.bb-hero__title-wrapper{height:4.5rem}}.bb-hero__title{padding:.25rem 1rem 0 1rem;margin-bottom:0;color:#fff;font-size:18px;line-height:1.5;text-align:center}@media (min-width:768px){.bb-hero__title{padding:0;font-size:24px}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2i = undefined;
/* module identifier */
const __vue_module_identifier__$2i = undefined;
/* functional template */
const __vue_is_functional_template__$2i = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2i = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2f,
  staticRenderFns: __vue_staticRenderFns__$2f
}, __vue_inject_styles__$2i, __vue_script__$2i, __vue_scope_id__$2i, __vue_is_functional_template__$2i, __vue_module_identifier__$2i, false, createInjector, undefined, undefined);

var script$2h = {
  name: 'bb-highlight',
  components: {
    BbIcon: __vue_component__$2C,
    BbTooltip: __vue_component__$2B
  },
  props: {
    label: String,
    icon: String,
    iconPosition: String,
    tooltipLabel: String
  },
  data() {
    return {
      isTooltipIconHovered: false
    };
  },
  computed: {
    iconWrapperClasses() {
      return {
        [`bb-highlight__icon-wrapper--${this.iconPosition}`]: this.iconPosition
      };
    },
    tooltipExists() {
      return this.isTooltipIconHovered && (this.tooltipLabel || !!this.$slots.tooltipLabel);
    }
  }
};

/* script */
const __vue_script__$2h = script$2h;

/* template */
var __vue_render__$2e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-highlight",
    attrs: {
      "data-testid": "bb-highlight"
    }
  }, [_vm.icon ? _c('div', {
    class: _vm.iconWrapperClasses,
    on: {
      "mouseover": function ($event) {
        _vm.isTooltipIconHovered = true;
      },
      "mouseleave": function ($event) {
        _vm.isTooltipIconHovered = false;
      }
    }
  }, [_c('bb-icon', {
    ref: "icon",
    attrs: {
      "name": _vm.icon,
      "size": "18",
      "data-testid": "bb-highlight__icon"
    }
  }), _vm._v(" "), _vm.tooltipExists ? _c('bb-tooltip', {
    attrs: {
      "bubble": "",
      "reference": _vm.$refs.icon
    }
  }, [_vm._t("tooltipLabel", function () {
    return [_vm._v("\n        " + _vm._s(_vm.tooltipLabel) + "\n      ")];
  })], 2) : _vm._e()], 1) : _vm._e(), _vm._v(" "), _vm._t("label", function () {
    return [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")];
  })], 2);
};
var __vue_staticRenderFns__$2e = [];

/* style */
const __vue_inject_styles__$2h = function (inject) {
  if (!inject) return;
  inject("data-v-7218f5ff_0", {
    source: ".bb-highlight{display:inline-flex;height:1.875rem;align-items:center;padding-right:1.25rem;padding-left:1.25rem;border-radius:.375rem;background:#fc0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}.bb-highlight__icon-wrapper--after,.bb-highlight__icon-wrapper--before{display:flex}.bb-highlight__icon-wrapper--before{order:0;margin-right:.5rem}.bb-highlight__icon-wrapper--after{order:1;margin-left:.5rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2h = undefined;
/* module identifier */
const __vue_module_identifier__$2h = undefined;
/* functional template */
const __vue_is_functional_template__$2h = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2h = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2e,
  staticRenderFns: __vue_staticRenderFns__$2e
}, __vue_inject_styles__$2h, __vue_script__$2h, __vue_scope_id__$2h, __vue_is_functional_template__$2h, __vue_module_identifier__$2h, false, createInjector, undefined, undefined);

var script$2g = {
  name: 'bb-icon-frame',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    filled: Boolean,
    coloredFrame: Boolean,
    shape: {
      type: String,
      validator: shape => ['square', 'circle'].includes(shape),
      default: 'square'
    },
    size: {
      type: String,
      validator: size => ['default', 'small', 'medium', 'big'].includes(size),
      default: 'default'
    },
    icon: {
      type: String,
      required: true
    },
    iconColor: {
      type: String,
      default: 'gray-80'
    },
    backgroundColor: {
      type: String,
      validator: color => ['gray-40', 'gray-30', 'gray-20', 'gray-10'].includes(color)
    },
    iconSize: {
      type: [String, Number],
      validator: size => ['fluid', '36', 36, '24', 24, '18', 18].includes(size),
      default: 24
    }
  },
  computed: {
    classes() {
      return {
        [`bb-icon-frame--shape-${this.shape}`]: this.shape,
        [`bb-icon-frame--size-${this.size}`]: this.size,
        [`bb-icon-frame--color-${this.iconColor}`]: this.iconColor,
        [`bb-icon-frame--background-${this.backgroundColor}`]: this.backgroundColor,
        'bb-icon-frame--filled': this.filled,
        'bb-icon-frame--colored-frame': this.coloredFrame
      };
    }
  }
};

/* script */
const __vue_script__$2g = script$2g;

/* template */
var __vue_render__$2d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-icon-frame",
    class: _vm.classes
  }, [_c('bb-icon', {
    attrs: {
      "name": _vm.icon && _vm.icon,
      "fill": _vm.filled ? 'white' : _vm.iconColor,
      "size": _vm.iconSize
    }
  })], 1);
};
var __vue_staticRenderFns__$2d = [];

/* style */
const __vue_inject_styles__$2g = function (inject) {
  if (!inject) return;
  inject("data-v-5a0ba9c8_0", {
    source: ".bb-icon-frame{display:flex;flex-shrink:0;align-items:center;justify-content:center;border:.125rem solid #dee3e8;transition:background .1s,border-color .1s}.bb-icon-frame--size-default{width:2.5rem;min-width:2.5rem;height:2.5rem}.bb-icon-frame--size-small{width:1.875rem;min-width:1.875rem;height:1.875rem}.bb-icon-frame--size-medium{width:3.125rem;min-width:3.125rem;height:3.125rem}.bb-icon-frame--size-big{width:3.75rem;min-width:3.75rem;height:3.75rem}.bb-icon-frame--shape-square{border-radius:.625rem}.bb-icon-frame--shape-circle{border-radius:99rem}.bb-icon-frame[class*=\"--background\"]{border:none}.bb-icon-frame--color-white.bb-icon-frame--colored-frame{border-color:#fff}.bb-icon-frame--color-white.bb-icon-frame--filled{border-color:#fff;background:#fff}.bb-icon-frame--color-black.bb-icon-frame--colored-frame{border-color:#000}.bb-icon-frame--color-black.bb-icon-frame--filled{border-color:#000;background:#000}.bb-icon-frame--color-navy.bb-icon-frame--colored-frame{border-color:#00285a}.bb-icon-frame--color-navy.bb-icon-frame--filled{border-color:#00285a;background:#00285a}.bb-icon-frame--color-navy-90.bb-icon-frame--colored-frame{border-color:#004c73}.bb-icon-frame--color-navy-90.bb-icon-frame--filled{border-color:#004c73;background:#004c73}.bb-icon-frame--color-navy-80.bb-icon-frame--colored-frame{border-color:#005d8c}.bb-icon-frame--color-navy-80.bb-icon-frame--filled{border-color:#005d8c;background:#005d8c}.bb-icon-frame--color-navy-70.bb-icon-frame--colored-frame{border-color:#003373}.bb-icon-frame--color-navy-70.bb-icon-frame--filled{border-color:#003373;background:#003373}.bb-icon-frame--color-navy-50.bb-icon-frame--colored-frame{border-color:#003e8c}.bb-icon-frame--color-navy-50.bb-icon-frame--filled{border-color:#003e8c;background:#003e8c}.bb-icon-frame--color-mint.bb-icon-frame--colored-frame{border-color:#0ccca2}.bb-icon-frame--color-mint.bb-icon-frame--filled{border-color:#0ccca2;background:#0ccca2}.bb-icon-frame--color-mint-90.bb-icon-frame--colored-frame{border-color:#3dd6b5}.bb-icon-frame--color-mint-90.bb-icon-frame--filled{border-color:#3dd6b5;background:#3dd6b5}.bb-icon-frame--color-mint-70.bb-icon-frame--colored-frame{border-color:#3dd6b5}.bb-icon-frame--color-mint-70.bb-icon-frame--filled{border-color:#3dd6b5;background:#3dd6b5}.bb-icon-frame--color-mint-50.bb-icon-frame--colored-frame{border-color:#69e4ca}.bb-icon-frame--color-mint-50.bb-icon-frame--filled{border-color:#69e4ca;background:#69e4ca}.bb-icon-frame--color-mint-30.bb-icon-frame--colored-frame{border-color:#b6f0e3}.bb-icon-frame--color-mint-30.bb-icon-frame--filled{border-color:#b6f0e3;background:#b6f0e3}.bb-icon-frame--color-mint-10.bb-icon-frame--colored-frame{border-color:#e7faf6}.bb-icon-frame--color-mint-10.bb-icon-frame--filled{border-color:#e7faf6;background:#e7faf6}.bb-icon-frame--color-green.bb-icon-frame--colored-frame{border-color:#0ccca2}.bb-icon-frame--color-green.bb-icon-frame--filled{border-color:#0ccca2;background:#0ccca2}.bb-icon-frame--color-green-90.bb-icon-frame--colored-frame{border-color:#3dd6b5}.bb-icon-frame--color-green-90.bb-icon-frame--filled{border-color:#3dd6b5;background:#3dd6b5}.bb-icon-frame--color-green-70.bb-icon-frame--colored-frame{border-color:#3dd6b5}.bb-icon-frame--color-green-70.bb-icon-frame--filled{border-color:#3dd6b5;background:#3dd6b5}.bb-icon-frame--color-green-50.bb-icon-frame--colored-frame{border-color:#69e4ca}.bb-icon-frame--color-green-50.bb-icon-frame--filled{border-color:#69e4ca;background:#69e4ca}.bb-icon-frame--color-green-30.bb-icon-frame--colored-frame{border-color:#b6f0e3}.bb-icon-frame--color-green-30.bb-icon-frame--filled{border-color:#b6f0e3;background:#b6f0e3}.bb-icon-frame--color-green-10.bb-icon-frame--colored-frame{border-color:#e7faf6}.bb-icon-frame--color-green-10.bb-icon-frame--filled{border-color:#e7faf6;background:#e7faf6}.bb-icon-frame--color-blue.bb-icon-frame--colored-frame{border-color:#2485e5}.bb-icon-frame--color-blue.bb-icon-frame--filled{border-color:#2485e5;background:#2485e5}.bb-icon-frame--color-blue-90.bb-icon-frame--colored-frame{border-color:#5ea6ed}.bb-icon-frame--color-blue-90.bb-icon-frame--filled{border-color:#5ea6ed;background:#5ea6ed}.bb-icon-frame--color-blue-80.bb-icon-frame--colored-frame{border-color:#5ea6ed}.bb-icon-frame--color-blue-80.bb-icon-frame--filled{border-color:#5ea6ed;background:#5ea6ed}.bb-icon-frame--color-blue-70.bb-icon-frame--colored-frame{border-color:#5ea6ed}.bb-icon-frame--color-blue-70.bb-icon-frame--filled{border-color:#5ea6ed;background:#5ea6ed}.bb-icon-frame--color-blue-50.bb-icon-frame--colored-frame{border-color:#6eb3f7}.bb-icon-frame--color-blue-50.bb-icon-frame--filled{border-color:#6eb3f7;background:#6eb3f7}.bb-icon-frame--color-blue-40.bb-icon-frame--colored-frame{border-color:#6eb3f7}.bb-icon-frame--color-blue-40.bb-icon-frame--filled{border-color:#6eb3f7;background:#6eb3f7}.bb-icon-frame--color-blue-30.bb-icon-frame--colored-frame{border-color:#add9ff}.bb-icon-frame--color-blue-30.bb-icon-frame--filled{border-color:#add9ff;background:#add9ff}.bb-icon-frame--color-blue-20.bb-icon-frame--colored-frame{border-color:#add9ff}.bb-icon-frame--color-blue-20.bb-icon-frame--filled{border-color:#add9ff;background:#add9ff}.bb-icon-frame--color-blue-10.bb-icon-frame--colored-frame{border-color:#e6f3ff}.bb-icon-frame--color-blue-10.bb-icon-frame--filled{border-color:#e6f3ff;background:#e6f3ff}.bb-icon-frame--color-gray.bb-icon-frame--colored-frame{border-color:#38414d}.bb-icon-frame--color-gray.bb-icon-frame--filled{border-color:#38414d;background:#38414d}.bb-icon-frame--color-gray-90.bb-icon-frame--colored-frame{border-color:#57697a}.bb-icon-frame--color-gray-90.bb-icon-frame--filled{border-color:#57697a;background:#57697a}.bb-icon-frame--color-gray-80.bb-icon-frame--colored-frame{border-color:#687d92}.bb-icon-frame--color-gray-80.bb-icon-frame--filled{border-color:#687d92;background:#687d92}.bb-icon-frame--color-gray-70.bb-icon-frame--colored-frame{border-color:#89a}.bb-icon-frame--color-gray-70.bb-icon-frame--filled{border-color:#89a;background:#89a}.bb-icon-frame--color-gray-50.bb-icon-frame--colored-frame{border-color:#9dabb9}.bb-icon-frame--color-gray-50.bb-icon-frame--filled{border-color:#9dabb9;background:#9dabb9}.bb-icon-frame--color-gray-40.bb-icon-frame--colored-frame{border-color:#bdc7d0}.bb-icon-frame--color-gray-40.bb-icon-frame--filled{border-color:#bdc7d0;background:#bdc7d0}.bb-icon-frame--color-gray-30.bb-icon-frame--colored-frame{border-color:#dee3e8}.bb-icon-frame--color-gray-30.bb-icon-frame--filled{border-color:#dee3e8;background:#dee3e8}.bb-icon-frame--color-gray-20.bb-icon-frame--colored-frame{border-color:#eeeff2}.bb-icon-frame--color-gray-20.bb-icon-frame--filled{border-color:#eeeff2;background:#eeeff2}.bb-icon-frame--color-gray-10.bb-icon-frame--colored-frame{border-color:#f7f7f8}.bb-icon-frame--color-gray-10.bb-icon-frame--filled{border-color:#f7f7f8;background:#f7f7f8}.bb-icon-frame--color-yellow.bb-icon-frame--colored-frame{border-color:#fc0}.bb-icon-frame--color-yellow.bb-icon-frame--filled{border-color:#fc0;background:#fc0}.bb-icon-frame--color-yellow-90.bb-icon-frame--colored-frame{border-color:#fc0}.bb-icon-frame--color-yellow-90.bb-icon-frame--filled{border-color:#fc0;background:#fc0}.bb-icon-frame--color-yellow-70.bb-icon-frame--colored-frame{border-color:#ffeea6}.bb-icon-frame--color-yellow-70.bb-icon-frame--filled{border-color:#ffeea6;background:#ffeea6}.bb-icon-frame--color-yellow-50.bb-icon-frame--colored-frame{border-color:#ffeea6}.bb-icon-frame--color-yellow-50.bb-icon-frame--filled{border-color:#ffeea6;background:#ffeea6}.bb-icon-frame--color-yellow-30.bb-icon-frame--colored-frame{border-color:#fbf5cb}.bb-icon-frame--color-yellow-30.bb-icon-frame--filled{border-color:#fbf5cb;background:#fbf5cb}.bb-icon-frame--color-yellow-10.bb-icon-frame--colored-frame{border-color:#fbf5cb}.bb-icon-frame--color-yellow-10.bb-icon-frame--filled{border-color:#fbf5cb;background:#fbf5cb}.bb-icon-frame--color-red.bb-icon-frame--colored-frame{border-color:#ff7155}.bb-icon-frame--color-red.bb-icon-frame--filled{border-color:#ff7155;background:#ff7155}.bb-icon-frame--color-red-90.bb-icon-frame--colored-frame{border-color:#fa9}.bb-icon-frame--color-red-90.bb-icon-frame--filled{border-color:#fa9;background:#fa9}.bb-icon-frame--color-red-70.bb-icon-frame--colored-frame{border-color:#fa9}.bb-icon-frame--color-red-70.bb-icon-frame--filled{border-color:#fa9;background:#fa9}.bb-icon-frame--color-red-50.bb-icon-frame--colored-frame{border-color:#ffbfb3}.bb-icon-frame--color-red-50.bb-icon-frame--filled{border-color:#ffbfb3;background:#ffbfb3}.bb-icon-frame--color-red-30.bb-icon-frame--colored-frame{border-color:#ffd5cc}.bb-icon-frame--color-red-30.bb-icon-frame--filled{border-color:#ffd5cc;background:#ffd5cc}.bb-icon-frame--color-red-10.bb-icon-frame--colored-frame{border-color:#ffeae6}.bb-icon-frame--color-red-10.bb-icon-frame--filled{border-color:#ffeae6;background:#ffeae6}.bb-icon-frame--background-gray-40{background:#bdc7d0}.bb-icon-frame--background-gray-30{background:#dee3e8}.bb-icon-frame--background-gray-20{background:#eeeff2}.bb-icon-frame--background-gray-10{background:#f7f7f8}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2g = undefined;
/* module identifier */
const __vue_module_identifier__$2g = undefined;
/* functional template */
const __vue_is_functional_template__$2g = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2g = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2d,
  staticRenderFns: __vue_staticRenderFns__$2d
}, __vue_inject_styles__$2g, __vue_script__$2g, __vue_scope_id__$2g, __vue_is_functional_template__$2g, __vue_module_identifier__$2g, false, createInjector, undefined, undefined);

const OS_LOCALE = false;
const aNumberWithFourDigits = 1111;
const aNumberWithDecimalPlace = 1.1;
const toLocaleStringWithoutNumbers = (val, locale = 'et') => val.toLocaleString(locale).replace(/1/g, '');
var LocalizeNumber = {
  props: {
    locale: String,
    decimals: {
      type: [Number, String]
    },
    minDecimals: {
      type: [Number, String]
    },
    maxDecimals: {
      type: [Number, String]
    }
  },
  computed: {
    computedLocale() {
      return this.locale === 'default' ? OS_LOCALE : this.locale || this.numberFormat;
    },
    thousandsSeparator() {
      return toLocaleStringWithoutNumbers(aNumberWithFourDigits, this.computedLocale);
    },
    decimalSeparator() {
      return toLocaleStringWithoutNumbers(aNumberWithDecimalPlace, this.computedLocale);
    },
    escapedThousandsSeparator() {
      return `${this.thousandsSeparator && '\\'}${this.thousandsSeparator}`;
    },
    escapedDecimalSeparator() {
      return `${this.decimalSeparator && '\\'}${this.decimalSeparator}`;
    }
  },
  methods: {
    localizeNumber(val, {
      style = 'decimal',
      useGrouping = true,
      currency = 'EUR',
      minimumFractionDigits,
      maximumFractionDigits
    } = {}) {
      const numberVal = +val;
      return this.locale && isNumber(numberVal) ? numberVal.toLocaleString(this.computedLocale, {
        minimumFractionDigits,
        maximumFractionDigits,
        style,
        currency,
        useGrouping
      }) : val;
    },
    unlocalizeNumber(_val) {
      const val = typeof _val === 'number' ? _val : String(_val).replace(new RegExp(this.escapedThousandsSeparator, 'g'), '').replace(new RegExp(this.escapedDecimalSeparator, 'g'), '.').replace(/\s+/g, '');
      return Number.isNaN(val) ? 0 : +val;
    }
  }
};

var script$2f = defineComponent({
  name: 'bb-input',
  components: {
    BbFunctionalInputCurrency: __vue_component__$2z,
    BbIcon: __vue_component__$2C,
    BbTooltip: __vue_component__$2B,
    BbSpinner: __vue_component__$2w
  },
  mixins: [FieldExtensions, ErrorBubble, Field, LocalizeNumber, veeValidationMixin, vuelidateValidator],
  props: {
    loading: Boolean,
    addOn: String,
    disabled: Boolean,
    center: Boolean,
    formatter: Function,
    maxlength: [Number, String],
    pattern: String,
    step: {
      type: [Number, String]
    },
    autocomplete: {
      type: String,
      default: null,
      validator: val => ['off', 'on'].includes(val)
    },
    readonly: Boolean,
    currency: {
      type: [Boolean,
      String
      ]
    },
    rules: Object,
    currencyDisplay: {
      type: String,
      default: 'hidden'
    },
    value: {
      type: [String, Number],
      default: null
    },
    helpText: String,
    helpActive: Boolean,
    mandatory: Boolean
  },
  emits: ['input', 'change'],
  data() {
    return {
      val: this.getFormattedAndNormalizedValue()
    };
  },
  computed: {
    computedStep() {
      return this.step ? this.step : this.isTypeNumber ? 'any' : null;
    },
    isTypeNumber() {
      return this.type === 'number';
    },
    supportsSetSelectionRange() {
      return ['text', 'search', 'URL', 'tel', 'password'].includes(this.type);
    },
    showAddon() {
      return this.addOn && this.addOn.trim();
    },
    computedInputmode() {
      const inputModes = {
        number: 'decimal',
        tel: 'tel'
      };
      return inputModes[this.type] || 'text';
    },
    hasLabel() {
      return notEmpty(this.label);
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    slugifiedName() {
      return slugify(this.name);
    },
    errorMessageId() {
      return `errorMessage-${this.slugifiedName}`;
    },
    labelId() {
      return `label-${this.slugifiedName}`;
    }
  },
  watch: {
    value(newVal) {
      if (!this.currency) {
        this.val = this.formatValue(newVal);
        if (this.val !== newVal) {
          this.emitInput(this.val);
        }
      }
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    getFormattedAndNormalizedValue() {
      return this.normalizeEmptyValue(this.formatValue(this.value));
    },
    formatValue(val) {
      return this.formatter ? this.formatter(val) : val;
    },
    normalizeEmptyValue(val) {
      var _this$min;
      if (notEmpty(val)) {
        return val;
      }
      return this.currency ? (_this$min = this.min) !== null && _this$min !== void 0 ? _this$min : null : '';
    },
    onCurrencyInput(value) {
      this.emitInput(value);
    },
    onCurrencyChange(value) {
      this.emitChange(value);
    },
    onChange(event) {
      this.emitChange(this.formatValue(event.target.value));
    },
    onKeydownEnter(event) {
      this.emitChange(this.formatValue(event.target.value));
    },
    onInput(event) {
      const {
        value,
        selectionStart,
        selectionEnd
      } = event.target;
      if (this.isSeparatorEntered(event)) {
        return;
      }
      this.val = this.endsWithSeparatorOnBackspace(event) ? this.previousVal.slice(0, -2) : this.formatValue(value);
      this.previousVal = this.val;
      if (this.$refs.input.value !== this.val) {
        this.$refs.input.value = this.val;
      }
      if (this.supportsSetSelectionRange) {
        this.$nextTick(() => {
          this.$refs.input.setSelectionRange(selectionStart, selectionEnd);
        });
      }
      this.emitInput(this.val);
    },
    emitInput(value) {
      this.$emit('input', value);
    },
    emitChange(value) {
      this.$emit('change', value);
    }
  }
});

/* script */
const __vue_script__$2f = script$2f;

/* template */
var __vue_render__$2c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-input field-group",
    class: _vm.fieldGroupClasses,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "val",
      "data-testid": "bb-input"
    }
  }, [_vm.hasLabel || _vm.$slots.default ? _c('label', {
    attrs: {
      "id": _vm.labelId,
      "for": _vm.id,
      "data-testid": "bb-input__label"
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-input__mandatory-indicator"
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "input-group"
  }, [_vm.currency ? _c('bb-functional-input-currency', _vm._b({
    ref: "inputRef",
    staticClass: "bb-input__input",
    attrs: {
      "id": _vm.id,
      "min": _vm.min,
      "max": _vm.max,
      "decimals": _vm.decimals,
      "max-decimals": _vm.maxDecimals,
      "min-decimals": _vm.minDecimals,
      "locale": _vm.locale,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "readonly": _vm.readonly,
      "currency-display": "hidden",
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-labelledby": _vm.labelId,
      "aria-required": _vm.mandatory,
      "data-testid": "bb-input__input"
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "change": _vm.onCurrencyChange,
      "focus": _vm.onFocus,
      "input": _vm.onCurrencyInput,
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    },
    model: {
      value: _vm.val,
      callback: function ($$v) {
        _vm.val = $$v;
      },
      expression: "val"
    }
  }, 'bb-functional-input-currency', _vm.inputAttrs, false)) : _vm.type === 'checkbox' ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "input",
    staticClass: "bb-input__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "min": _vm.min,
      "max": _vm.max,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "inputmode": _vm.computedInputmode,
      "pattern": _vm.pattern,
      "maxlength": _vm.maxlength,
      "autocomplete": _vm.autocomplete,
      "readonly": _vm.readonly,
      "step": _vm.computedStep,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-labelledby": _vm.labelId,
      "aria-required": _vm.mandatory,
      "data-testid": "bb-input__input",
      "type": "checkbox"
    },
    domProps: {
      "checked": Array.isArray(_vm.val) ? _vm._i(_vm.val, null) > -1 : _vm.val
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": function ($event) {
        $event.stopPropagation();
        return _vm.onInput.apply(null, arguments);
      },
      "change": [function ($event) {
        var $$a = _vm.val,
          $$el = $event.target,
          $$c = $$el.checked ? true : false;
        if (Array.isArray($$a)) {
          var $$v = null,
            $$i = _vm._i($$a, $$v);
          if ($$el.checked) {
            $$i < 0 && (_vm.val = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.val = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.val = $$c;
        }
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }],
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.onKeydownEnter.apply(null, arguments);
      },
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, 'input', _vm.inputAttrs, false)) : _vm.type === 'radio' ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "input",
    staticClass: "bb-input__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "min": _vm.min,
      "max": _vm.max,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "inputmode": _vm.computedInputmode,
      "pattern": _vm.pattern,
      "maxlength": _vm.maxlength,
      "autocomplete": _vm.autocomplete,
      "readonly": _vm.readonly,
      "step": _vm.computedStep,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-labelledby": _vm.labelId,
      "aria-required": _vm.mandatory,
      "data-testid": "bb-input__input",
      "type": "radio"
    },
    domProps: {
      "checked": _vm._q(_vm.val, null)
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": function ($event) {
        $event.stopPropagation();
        return _vm.onInput.apply(null, arguments);
      },
      "change": [function ($event) {
        _vm.val = null;
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }],
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.onKeydownEnter.apply(null, arguments);
      },
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, 'input', _vm.inputAttrs, false)) : _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "input",
    staticClass: "bb-input__input",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "min": _vm.min,
      "max": _vm.max,
      "disabled": _vm.disabled,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "inputmode": _vm.computedInputmode,
      "pattern": _vm.pattern,
      "maxlength": _vm.maxlength,
      "autocomplete": _vm.autocomplete,
      "readonly": _vm.readonly,
      "step": _vm.computedStep,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-labelledby": _vm.labelId,
      "aria-required": _vm.mandatory,
      "data-testid": "bb-input__input",
      "type": _vm.type
    },
    domProps: {
      "value": _vm.val
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": [function ($event) {
        if ($event.target.composing) {
          return;
        }
        _vm.val = $event.target.value;
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onInput.apply(null, arguments);
      }],
      "change": function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      },
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.onKeydownEnter.apply(null, arguments);
      },
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, 'input', _vm.inputAttrs, false)), _vm._v(" "), _c('div', {
    staticClass: "input-addon-wrapper"
  }, [_vm.loading ? _c('bb-spinner', {
    staticClass: "input-loading",
    attrs: {
      "small": ""
    }
  }) : _vm._e(), _vm._v(" "), _vm.showAddon ? _c('div', {
    staticClass: "input-addon"
  }, [_vm._v("\n        " + _vm._s(_vm.addOn) + "\n      ")]) : _vm._e(), _vm._v(" "), _c('bb-icon', {
    staticClass: "validation-error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  })], 1), _vm._v(" "), _vm.helpActive ? _c('bb-tooltip', {
    attrs: {
      "is-in-input": ""
    }
  }, [_c('p', [_vm._t("tooltipText", function () {
    return [_vm._v("\n          " + _vm._s(_vm.helpText) + "\n        ")];
  })], 2)]) : _vm._e()], 1), _vm._v(" "), _vm.hasValidationError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.input || _vm.inputRef,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', [_vm._v(_vm._s(_vm.firstError || _vm.getFirstError()))])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n    ")]) : _vm._e()] : _vm._e()], 2);
};
var __vue_staticRenderFns__$2c = [];

/* style */
const __vue_inject_styles__$2f = undefined;
/* scoped */
const __vue_scope_id__$2f = undefined;
/* module identifier */
const __vue_module_identifier__$2f = undefined;
/* functional template */
const __vue_is_functional_template__$2f = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2f = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2c,
  staticRenderFns: __vue_staticRenderFns__$2c
}, __vue_inject_styles__$2f, __vue_script__$2f, __vue_scope_id__$2f, __vue_is_functional_template__$2f, __vue_module_identifier__$2f, false, undefined, undefined, undefined);

var script$2e = {
  name: 'bb-label',
  props: {
    value: {
      type: String
    },
    size: {
      type: [String, Number],
      validator: size => [...ALLOWED_ICON_SIZES, 'avatar', 'auto', 'fluid', 'font'].includes(isNaN(size) ? size : +size),
      default: 'font'
    }
  },
  computed: {
    labelClasses() {
      const slotTextLength = this.$slots.default && this.$slots.default[0].text.trim().length;
      return {
        [`bb-label--${this.size}`]: this.size,
        'bb-label--full-word': this.value && this.value.length > 1 || slotTextLength > 1
      };
    }
  }
};

/* script */
const __vue_script__$2e = script$2e;

/* template */
var __vue_render__$2b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('span', {
    staticClass: "bb-label",
    class: _vm.labelClasses,
    attrs: {
      "data-testid": "bb-label"
    }
  }, [_c('span', [_vm._t("default", function () {
    return [_vm._v(_vm._s(_vm.value))];
  })], 2)]);
};
var __vue_staticRenderFns__$2b = [];

/* style */
const __vue_inject_styles__$2e = function (inject) {
  if (!inject) return;
  inject("data-v-512fbab4_0", {
    source: ".bb-label{display:inline-flex;align-items:center;justify-content:center;color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-label--18{min-width:1.125rem;height:1.125rem;border-radius:.5625rem;font-size:11px}.bb-label--24{min-width:1.5rem;height:1.5rem;border-radius:.75rem;font-size:11px}.bb-label--36{min-width:2.25rem;height:2.25rem;border-radius:1.125rem;font-size:11px}.bb-label--48{min-width:3rem;height:3rem;border-radius:1.5rem;font-size:11px}.bb-label--64{min-width:4rem;height:4rem;border-radius:2rem;font-size:11px}.bb-label--128{min-width:8rem;height:8rem;border-radius:4rem;font-size:11px}.bb-label--font{min-width:1.5em;height:1.5em;border-radius:.75em;vertical-align:middle}.bb-label--avatar{min-width:2.5rem;height:2.5rem;border-radius:1.25rem;font-size:16px}.bb-label--auto{width:auto;height:auto}.bb-label--fluid{width:100%;height:100%}.bb-label--full-word{padding:0 .625rem;text-transform:uppercase}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2e = undefined;
/* module identifier */
const __vue_module_identifier__$2e = undefined;
/* functional template */
const __vue_is_functional_template__$2e = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2b,
  staticRenderFns: __vue_staticRenderFns__$2b
}, __vue_inject_styles__$2e, __vue_script__$2e, __vue_scope_id__$2e, __vue_is_functional_template__$2e, __vue_module_identifier__$2e, false, createInjector, undefined, undefined);

const rem = size => {
  const cleanedSize = size.replace(/\D/g, '');
  const remSize = cleanedSize / defaultFontSize;
  return `${remSize}rem`;
};
const formatCSSUnit = value => {
  const values = value.split(' ');
  const mappedValues = values.map(iterableValue => /[^\d]/.test(iterableValue) ? iterableValue.endsWith('%') || iterableValue.endsWith('rem') ? iterableValue : rem(iterableValue) : rem(iterableValue));
  return mappedValues.join(' ');
};

var script$2d = {
  name: 'bb-skeleton',
  props: {
    color: {
      type: String,
      default: 'gray-20',
      validator: type => ['gray-40', 'gray-30', 'gray-20'].includes(type)
    },
    width: {
      type: String,
      default: '150'
    },
    height: {
      type: String,
      default: '14'
    },
    radius: {
      type: String,
      default: '99rem'
    }
  },
  computed: {
    classes() {
      return {
        [`bg-${this.color}`]: this.color
      };
    },
    computedStyles() {
      return {
        'width': formatCSSUnit(this.width),
        'height': formatCSSUnit(this.height),
        'border-radius': formatCSSUnit(this.radius)
      };
    }
  }
};

/* script */
const __vue_script__$2d = script$2d;

/* template */
var __vue_render__$2a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-skeleton",
    class: _vm.classes,
    style: _vm.computedStyles
  });
};
var __vue_staticRenderFns__$2a = [];

/* style */
const __vue_inject_styles__$2d = function (inject) {
  if (!inject) return;
  inject("data-v-198f8be8_0", {
    source: ".bb-skeleton{position:relative;overflow:hidden}.bb-skeleton::before{position:absolute;width:500%;margin-left:-250%;animation:placeholderAnimation .8s linear infinite;background:linear-gradient(to right,rgba(255,255,255,0) 40%,rgba(255,255,255,.35) 50%,rgba(255,255,255,0) 60%) 50% 50%;content:\"\";inset:0 0 0 50%;pointer-events:none}@keyframes placeholderAnimation{0%{transform:translate3d(-30%,0,0)}100%{transform:translate3d(30%,0,0)}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2d = undefined;
/* module identifier */
const __vue_module_identifier__$2d = undefined;
/* functional template */
const __vue_is_functional_template__$2d = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2d = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2a,
  staticRenderFns: __vue_staticRenderFns__$2a
}, __vue_inject_styles__$2d, __vue_script__$2d, __vue_scope_id__$2d, __vue_is_functional_template__$2d, __vue_module_identifier__$2d, false, createInjector, undefined, undefined);

/**
 * Collection of utility methods to work with objects.
 */
function ifElse(conditions) {
  for (const key in conditions) {
    if (conditions[key]) {
      return key;
    }
  }
  return '';
}
function mutateValues(object, value) {
  Object.keys(object).forEach(key => object[key] = value);
}

/**
 * A value with a label.
 */
var script$2c = Vue.extend({
  name: 'bb-labeled-value',
  components: {
    BbSkeleton: __vue_component__$2d
  },
  props: {
    /**
     * Value color
     */
    color: {
      type: String,
      default: 'gray'
    },
    /**
     * Extra leading between label and value
     */
    extraLeading: {
      type: String,
      validator: extraLeading => ['7'].includes(extraLeading)
    },
    /**
     * Font weight of the value
     */
    fontWeight: {
      type: String,
      default: 'medium',
      validator: fontWeight => ['medium', 'bold'].includes(fontWeight)
    },
    /**
     * Display labeled value with a background
     */
    inverted: {
      type: Boolean
    },
    /**
     * Label text displayed on the value
     */
    label: {
      type: String
    },
    /**
     * Make label uppercase
     */
    labelUppercase: {
      type: Boolean
    },
    /**
     * Label color
     */
    labelColor: {
      type: String,
      default: 'gray-70'
    },
    /**
     * Font weight for the label.
     */
    labelWeight: {
      type: String,
      validator: labelWeight => ['medium', 'bold'].includes(labelWeight)
    },
    /**
     * Label position
     */
    labelPosition: {
      type: String,
      default: 'top-left',
      validator: labelPosition => ['top-left', 'top-center', 'top-right', 'bottom-left', 'bottom-center', 'bottom-right'].includes(labelPosition)
    },
    /**
     * Value size
     */
    size: {
      type: String,
      default: '24',
      validator: size => ['12', '14', '16', '18', '24', '32'].includes(size)
    },
    /**
     * Value
     */
    value: {
      type: [String, Number]
    },
    /**
     * Make value to be strike-through
     */
    strikeThroughValue: {
      type: [String, Number]
    },
    /**
     * Render a skeleton version of the component
     */
    loading: Boolean,
    /**
     * Aria label for the strike-through text
     */
    strikeThroughAriaLabelPartial: {
      type: String,
      default: 'Crossed through'
    }
  },
  data() {
    return {
      uuid: uuid()
    };
  },
  computed: {
    classes() {
      return {
        'bb-labeled-value--loading': this.loading,
        'bb-labeled-value--inverted': this.inverted,
        'bb-labeled-value--extra-leading-7': this.extraLeading === '7',
        'bb-labeled-value--label-uppercase': !!this.labelUppercase,
        'bb-labeled-value--label-center': this.labelPosition.includes('center'),
        'bb-labeled-value--label-right': this.labelPosition.includes('right'),
        'bb-labeled-value--label-bottom': this.labelPosition.includes('bottom'),
        [`bb-labeled-value--label-${this.labelColor}`]: !!this.labelColor,
        [`bb-labeled-value--label-${this.labelWeight}`]: !!this.labelWeight,
        [`bb-labeled-value--font-${this.fontWeight}`]: !!this.fontWeight,
        [`bb-labeled-value--size-${this.size}`]: !!this.size,
        [`bb-labeled-value--${this.color}`]: !!this.color
      };
    },
    computedMargin() {
      const sizes = ['12', '14'];
      const side = this.labelPosition.startsWith('top') ? 't' : 'b';
      return sizes.includes(this.size) ? `m-${side}-5` : `m-${side}-10`;
    },
    computedLabelHeight() {
      return ifElse({
        '14': this.size === '32',
        '10': this.size === '14',
        '12': true
      });
    },
    computedValueHeight() {
      const skeletonHeights = {
        32: '26',
        24: '22',
        18: '18',
        16: '18',
        14: '15',
        12: '11'
      };
      return skeletonHeights[this.size];
    },
    computedSkeletonWidth() {
      return {
        two: this.getMultipliedSize(2),
        five: this.getMultipliedSize(5)
      };
    },
    labelId() {
      return `label-${this.uuid}`;
    },
    strikeThroughAriaLabel() {
      return `${this.strikeThroughValue}, ${this.strikeThroughAriaLabelPartial}`;
    }
  },
  methods: {
    getMultipliedSize(multiplier) {
      return `${parseFloat(this.size) * multiplier}`;
    }
  }
});

/* script */
const __vue_script__$2c = script$2c;

/* template */
var __vue_render__$29 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-labeled-value",
    class: _vm.classes,
    attrs: {
      "aria-busy": _vm.loading,
      "data-testid": "bb-labeled-value"
    }
  }, [_c('div', {
    staticClass: "bb-labeled-value__label",
    attrs: {
      "id": _vm.labelId,
      "aria-hidden": "true",
      "data-testid": "bb-labeled-value__label"
    }
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": _vm.computedSkeletonWidth.two,
      "height": _vm.computedLabelHeight
    }
  }) : [_vm._t("label", function () {
    return [_vm._v("\n        " + _vm._s(_vm.label) + "\n      ")];
  })]], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-labeled-value__value",
    attrs: {
      "data-testid": "bb-labeled-value__value",
      "aria-live": "polite",
      "aria-labelledby": _vm.labelId
    }
  }, [_vm.loading ? _c('bb-skeleton', {
    class: _vm.computedMargin,
    attrs: {
      "width": _vm.computedSkeletonWidth.five,
      "height": _vm.computedValueHeight
    }
  }) : [_vm.strikeThroughValue ? _c('span', {
    staticClass: "bb-labeled-value__strikethrough-value",
    attrs: {
      "aria-label": _vm.strikeThroughAriaLabel
    }
  }, [_vm._v("\n        " + _vm._s(_vm.strikeThroughValue) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm._t("value", function () {
    return [_vm._v("\n        " + _vm._s(_vm.value) + "\n      ")];
  })]], 2)]);
};
var __vue_staticRenderFns__$29 = [];

/* style */
const __vue_inject_styles__$2c = function (inject) {
  if (!inject) return;
  inject("data-v-ad9efa8e_0", {
    source: ".bb-labeled-value{display:inline-flex;flex-direction:column;align-items:flex-start;vertical-align:top}.bb-labeled-value--label-bottom{flex-direction:column-reverse}.bb-labeled-value--label-center{align-items:center}.bb-labeled-value--label-right{align-items:flex-end}.bb-labeled-value__label{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;line-height:1rem}.bb-labeled-value--size-12 .bb-labeled-value__label,.bb-labeled-value--size-14 .bb-labeled-value__label{font-size:.5625rem;line-height:.75rem}.bb-labeled-value--size-32 .bb-labeled-value__label{font-size:.875rem;line-height:1.125rem}.bb-labeled-value--label-uppercase .bb-labeled-value__label{text-transform:uppercase}.bb-labeled-value--label-left .bb-labeled-value__label{text-align:left}.bb-labeled-value--label-center .bb-labeled-value__label{text-align:center}.bb-labeled-value--label-right .bb-labeled-value__label{text-align:right}.bb-labeled-value--label-gray-70 .bb-labeled-value__label{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-labeled-value--label-white .bb-labeled-value__label{color:#fff}.bb-labeled-value--label-black .bb-labeled-value__label{color:#000}.bb-labeled-value--label-navy .bb-labeled-value__label{color:#00285a}.bb-labeled-value--label-navy-90 .bb-labeled-value__label{color:#004c73}.bb-labeled-value--label-navy-80 .bb-labeled-value__label{color:#005d8c}.bb-labeled-value--label-navy-70 .bb-labeled-value__label{color:#003373}.bb-labeled-value--label-navy-50 .bb-labeled-value__label{color:#003e8c}.bb-labeled-value--label-mint .bb-labeled-value__label{color:#0ccca2}.bb-labeled-value--label-mint-90 .bb-labeled-value__label{color:#3dd6b5}.bb-labeled-value--label-mint-70 .bb-labeled-value__label{color:#3dd6b5}.bb-labeled-value--label-mint-50 .bb-labeled-value__label{color:#69e4ca}.bb-labeled-value--label-mint-30 .bb-labeled-value__label{color:#b6f0e3}.bb-labeled-value--label-mint-10 .bb-labeled-value__label{color:#e7faf6}.bb-labeled-value--label-green .bb-labeled-value__label{color:#0ccca2}.bb-labeled-value--label-green-90 .bb-labeled-value__label{color:#3dd6b5}.bb-labeled-value--label-green-70 .bb-labeled-value__label{color:#3dd6b5}.bb-labeled-value--label-green-50 .bb-labeled-value__label{color:#69e4ca}.bb-labeled-value--label-green-30 .bb-labeled-value__label{color:#b6f0e3}.bb-labeled-value--label-green-10 .bb-labeled-value__label{color:#e7faf6}.bb-labeled-value--label-blue .bb-labeled-value__label{color:#2485e5}.bb-labeled-value--label-blue-90 .bb-labeled-value__label{color:#5ea6ed}.bb-labeled-value--label-blue-80 .bb-labeled-value__label{color:#5ea6ed}.bb-labeled-value--label-blue-70 .bb-labeled-value__label{color:#5ea6ed}.bb-labeled-value--label-blue-50 .bb-labeled-value__label{color:#6eb3f7}.bb-labeled-value--label-blue-40 .bb-labeled-value__label{color:#6eb3f7}.bb-labeled-value--label-blue-30 .bb-labeled-value__label{color:#add9ff}.bb-labeled-value--label-blue-20 .bb-labeled-value__label{color:#add9ff}.bb-labeled-value--label-blue-10 .bb-labeled-value__label{color:#e6f3ff}.bb-labeled-value--label-gray .bb-labeled-value__label{color:#38414d}.bb-labeled-value--label-gray-90 .bb-labeled-value__label{color:#57697a}.bb-labeled-value--label-gray-80 .bb-labeled-value__label{color:#687d92}.bb-labeled-value--label-gray-70 .bb-labeled-value__label{color:#89a}.bb-labeled-value--label-gray-50 .bb-labeled-value__label{color:#9dabb9}.bb-labeled-value--label-gray-40 .bb-labeled-value__label{color:#bdc7d0}.bb-labeled-value--label-gray-30 .bb-labeled-value__label{color:#dee3e8}.bb-labeled-value--label-gray-20 .bb-labeled-value__label{color:#eeeff2}.bb-labeled-value--label-gray-10 .bb-labeled-value__label{color:#f7f7f8}.bb-labeled-value--label-yellow .bb-labeled-value__label{color:#fc0}.bb-labeled-value--label-yellow-90 .bb-labeled-value__label{color:#fc0}.bb-labeled-value--label-yellow-70 .bb-labeled-value__label{color:#ffeea6}.bb-labeled-value--label-yellow-50 .bb-labeled-value__label{color:#ffeea6}.bb-labeled-value--label-yellow-30 .bb-labeled-value__label{color:#fbf5cb}.bb-labeled-value--label-yellow-10 .bb-labeled-value__label{color:#fbf5cb}.bb-labeled-value--label-red .bb-labeled-value__label{color:#ff7155}.bb-labeled-value--label-red-90 .bb-labeled-value__label{color:#fa9}.bb-labeled-value--label-red-70 .bb-labeled-value__label{color:#fa9}.bb-labeled-value--label-red-50 .bb-labeled-value__label{color:#ffbfb3}.bb-labeled-value--label-red-30 .bb-labeled-value__label{color:#ffd5cc}.bb-labeled-value--label-red-10 .bb-labeled-value__label{color:#ffeae6}.bb-labeled-value--label-medium .bb-labeled-value__label{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-labeled-value--label-bold .bb-labeled-value__label{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-labeled-value__value{white-space:nowrap}.bb-labeled-value--extra-leading-7 .bb-labeled-value__value{margin-top:.4375rem}.bb-labeled-value--extra-leading-7.bb-labeled-value--label-bottom .bb-labeled-value__value{margin-top:0;margin-bottom:.4375rem}.bb-labeled-value--size-12 .bb-labeled-value__value{font-size:.75rem;line-height:1rem}.bb-labeled-value--size-14 .bb-labeled-value__value{font-size:.875rem;line-height:1.125rem}.bb-labeled-value--size-16 .bb-labeled-value__value{font-size:1rem;line-height:1.5rem}.bb-labeled-value--size-18 .bb-labeled-value__value{font-size:1.125rem;line-height:1.5rem}.bb-labeled-value--size-24 .bb-labeled-value__value{font-size:1.5rem;line-height:1.75rem}.bb-labeled-value--size-32 .bb-labeled-value__value{font-size:2rem;line-height:2rem}.bb-labeled-value--white .bb-labeled-value__value{color:#fff}.bb-labeled-value--black .bb-labeled-value__value{color:#000}.bb-labeled-value--navy .bb-labeled-value__value{color:#00285a}.bb-labeled-value--navy-90 .bb-labeled-value__value{color:#004c73}.bb-labeled-value--navy-80 .bb-labeled-value__value{color:#005d8c}.bb-labeled-value--navy-70 .bb-labeled-value__value{color:#003373}.bb-labeled-value--navy-50 .bb-labeled-value__value{color:#003e8c}.bb-labeled-value--mint .bb-labeled-value__value{color:#0ccca2}.bb-labeled-value--mint-90 .bb-labeled-value__value{color:#3dd6b5}.bb-labeled-value--mint-70 .bb-labeled-value__value{color:#3dd6b5}.bb-labeled-value--mint-50 .bb-labeled-value__value{color:#69e4ca}.bb-labeled-value--mint-30 .bb-labeled-value__value{color:#b6f0e3}.bb-labeled-value--mint-10 .bb-labeled-value__value{color:#e7faf6}.bb-labeled-value--green .bb-labeled-value__value{color:#0ccca2}.bb-labeled-value--green-90 .bb-labeled-value__value{color:#3dd6b5}.bb-labeled-value--green-70 .bb-labeled-value__value{color:#3dd6b5}.bb-labeled-value--green-50 .bb-labeled-value__value{color:#69e4ca}.bb-labeled-value--green-30 .bb-labeled-value__value{color:#b6f0e3}.bb-labeled-value--green-10 .bb-labeled-value__value{color:#e7faf6}.bb-labeled-value--blue .bb-labeled-value__value{color:#2485e5}.bb-labeled-value--blue-90 .bb-labeled-value__value{color:#5ea6ed}.bb-labeled-value--blue-80 .bb-labeled-value__value{color:#5ea6ed}.bb-labeled-value--blue-70 .bb-labeled-value__value{color:#5ea6ed}.bb-labeled-value--blue-50 .bb-labeled-value__value{color:#6eb3f7}.bb-labeled-value--blue-40 .bb-labeled-value__value{color:#6eb3f7}.bb-labeled-value--blue-30 .bb-labeled-value__value{color:#add9ff}.bb-labeled-value--blue-20 .bb-labeled-value__value{color:#add9ff}.bb-labeled-value--blue-10 .bb-labeled-value__value{color:#e6f3ff}.bb-labeled-value--gray .bb-labeled-value__value{color:#38414d}.bb-labeled-value--gray-90 .bb-labeled-value__value{color:#57697a}.bb-labeled-value--gray-80 .bb-labeled-value__value{color:#687d92}.bb-labeled-value--gray-70 .bb-labeled-value__value{color:#89a}.bb-labeled-value--gray-50 .bb-labeled-value__value{color:#9dabb9}.bb-labeled-value--gray-40 .bb-labeled-value__value{color:#bdc7d0}.bb-labeled-value--gray-30 .bb-labeled-value__value{color:#dee3e8}.bb-labeled-value--gray-20 .bb-labeled-value__value{color:#eeeff2}.bb-labeled-value--gray-10 .bb-labeled-value__value{color:#f7f7f8}.bb-labeled-value--yellow .bb-labeled-value__value{color:#fc0}.bb-labeled-value--yellow-90 .bb-labeled-value__value{color:#fc0}.bb-labeled-value--yellow-70 .bb-labeled-value__value{color:#ffeea6}.bb-labeled-value--yellow-50 .bb-labeled-value__value{color:#ffeea6}.bb-labeled-value--yellow-30 .bb-labeled-value__value{color:#fbf5cb}.bb-labeled-value--yellow-10 .bb-labeled-value__value{color:#fbf5cb}.bb-labeled-value--red .bb-labeled-value__value{color:#ff7155}.bb-labeled-value--red-90 .bb-labeled-value__value{color:#fa9}.bb-labeled-value--red-70 .bb-labeled-value__value{color:#fa9}.bb-labeled-value--red-50 .bb-labeled-value__value{color:#ffbfb3}.bb-labeled-value--red-30 .bb-labeled-value__value{color:#ffd5cc}.bb-labeled-value--red-10 .bb-labeled-value__value{color:#ffeae6}.bb-labeled-value--font-medium .bb-labeled-value__value{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-labeled-value--font-bold .bb-labeled-value__value{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-labeled-value__strikethrough-value{margin-right:.5rem;color:#89a;text-decoration:line-through}.bb-labeled-value--inverted .bb-labeled-value__label,.bb-labeled-value--inverted .bb-labeled-value__strikethrough-value,.bb-labeled-value--inverted .bb-labeled-value__value{color:#fff}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2c = undefined;
/* module identifier */
const __vue_module_identifier__$2c = undefined;
/* functional template */
const __vue_is_functional_template__$2c = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$29,
  staticRenderFns: __vue_staticRenderFns__$29
}, __vue_inject_styles__$2c, __vue_script__$2c, __vue_scope_id__$2c, __vue_is_functional_template__$2c, __vue_module_identifier__$2c, false, createInjector, undefined, undefined);

var script$2b = {
  name: 'bb-list-group',
  props: {
    flavor: {
      type: String,
      validator: val => ['upload'].includes(val)
    },
    title: String
  }
};

/* script */
const __vue_script__$2b = script$2b;

/* template */
var __vue_render__$28 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ul', {
    staticClass: "bb-list-group",
    class: _vm.flavor && "bb-list-group--" + _vm.flavor,
    attrs: {
      "data-testid": "bb-list-group"
    }
  }, [_vm.title ? _c('h6', {
    staticClass: "bb-list-group__title"
  }, [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]) : _vm._e(), _vm._v(" "), _vm._t("default")], 2);
};
var __vue_staticRenderFns__$28 = [];

/* style */
const __vue_inject_styles__$2b = function (inject) {
  if (!inject) return;
  inject("data-v-48146825_0", {
    source: ".bb-list-group{display:flex;flex-direction:column;padding-left:0;margin-bottom:0}.bb-list-group--upload .bb-list-group__title{padding:10px 20px;border-top:1px solid #dee3e8;background:#f7f7f8;color:#89a;font-size:10px;text-transform:uppercase}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2b = undefined;
/* module identifier */
const __vue_module_identifier__$2b = undefined;
/* functional template */
const __vue_is_functional_template__$2b = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2b = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$28,
  staticRenderFns: __vue_staticRenderFns__$28
}, __vue_inject_styles__$2b, __vue_script__$2b, __vue_scope_id__$2b, __vue_is_functional_template__$2b, __vue_module_identifier__$2b, false, createInjector, undefined, undefined);

var script$2a = {
  name: 'bb-separator',
  props: {
    direction: {
      type: String,
      default: 'horizontal',
      validator: val => ['vertical', 'horizontal'].includes(val)
    },
    dashed: {
      type: Boolean,
      default: false
    },
    width: {
      type: [String, Number],
      default: 1,
      validator: val => [1, 2, 4].includes(+val)
    }
  },
  computed: {
    separatorClasses() {
      return [`bb-separator--${this.direction}`, `bb-separator--width-${this.width}`, this.dashed && 'bb-separator--dashed'];
    }
  }
};

/* script */
const __vue_script__$2a = script$2a;

/* template */
var __vue_render__$27 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('span', {
    staticClass: "bb-separator",
    class: _vm.separatorClasses,
    attrs: {
      "aria-hidden": "true"
    }
  });
};
var __vue_staticRenderFns__$27 = [];

/* style */
const __vue_inject_styles__$2a = function (inject) {
  if (!inject) return;
  inject("data-v-af4c2980_0", {
    source: ".bb-separator{border-style:solid;border-color:#bdc7d0}.bb-separator--horizontal{display:block;width:100%}.bb-separator--horizontal.bb-separator--width-1{border-bottom-width:1px}.bb-separator--horizontal.bb-separator--width-2{border-bottom-width:2px}.bb-separator--horizontal.bb-separator--width-4{border-bottom-width:4px}.bb-separator--vertical{display:inline-block;height:auto;align-self:stretch}.bb-separator--vertical.bb-separator--width-1{border-left-width:1px}.bb-separator--vertical.bb-separator--width-2{border-left-width:2px}.bb-separator--vertical.bb-separator--width-4{border-left-width:4px}.bb-separator--dashed{border-style:dashed}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2a = undefined;
/* module identifier */
const __vue_module_identifier__$2a = undefined;
/* functional template */
const __vue_is_functional_template__$2a = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$27,
  staticRenderFns: __vue_staticRenderFns__$27
}, __vue_inject_styles__$2a, __vue_script__$2a, __vue_scope_id__$2a, __vue_is_functional_template__$2a, __vue_module_identifier__$2a, false, createInjector, undefined, undefined);

var Icon$Q = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3881977225__a bb-icon__dynamic-fill",attrs:{"d":"M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"}})]) } };

Vue.component('icon-ui-arrow-right', Icon$Q);

var script$29 = {
  name: 'bb-list-item',
  components: {
    BbIcon: __vue_component__$2C,
    BbButton: __vue_component__$2v,
    BbSeparator: __vue_component__$2a
  },
  props: {
    type: {
      type: String,
      default: 'text',
      validator: type => ['text', 'button'].includes(type)
    },
    to: {
      type: [String, Object]
    },
    title: String,
    buttonIcon: String,
    condensed: Boolean,
    extraCondensed: Boolean,
    noTopBorder: Boolean,
    noBottomBorder: Boolean,
    titleUppercase: {
      type: Boolean,
      default: true
    },
    titleNowrap: Boolean,
    slotNowrap: {
      type: Boolean,
      default: true
    },
    bullet: Boolean,
    contentClasses: {
      type: [Array, String, Object]
    }
  },
  emits: ['click'],
  computed: {
    classes() {
      return ['bb-list-item', {
        [`bb-list-item--type-${this.type}`]: this.type,
        'bb-list-item--has-slot-content': this.hasContentInSlot,
        'bb-list-item--title-uppercase': this.titleUppercase,
        'bb-list-item--title-nowrap': this.titleNowrap,
        'bb-list-item--slot-nowrap': this.slotNowrap,
        'bb-list-item--condensed': this.condensed,
        'bb-list-item--extracondensed': this.extraCondensed,
        'bb-list-item--bullet': this.bullet
      }];
    },
    computedButtonSize() {
      return this.condensed || this.extraCondensed ? 'small' : null;
    },
    hasContentInSlot() {
      return this.$slots.default;
    }
  },
  methods: {
    onButtonClick() {
      this.$emit('click');
    }
  }
};

/* script */
const __vue_script__$29 = script$29;

/* template */
var __vue_render__$26 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('li', {
    class: _vm.classes,
    attrs: {
      "data-testid": "bb-list-item"
    }
  }, [!_vm.noTopBorder && !_vm.bullet ? _c('bb-separator', {
    attrs: {
      "dashed": ""
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-list-item__content",
    class: _vm.contentClasses
  }, [_vm.title ? [_vm.type === 'text' ? [_vm.bullet ? _c('bb-icon', {
    attrs: {
      "name": "ui-arrow-right",
      "size": "24",
      "fill": "mint"
    }
  }) : _vm._e(), _vm._v(" "), _c('span', {
    staticClass: "bb-list-item__title",
    attrs: {
      "data-testid": "bb-list-item__title"
    }
  }, [_vm._v("\n          " + _vm._s(_vm.title) + "\n        ")]), _vm._v(" "), _vm.hasContentInSlot ? _c('div', {
    staticClass: "bb-list-item__default-slot",
    attrs: {
      "data-testid": "bb-list-item__default-slot"
    }
  }, [_vm._t("default")], 2) : _vm._e()] : _vm.type === 'button' ? [_c('bb-button', {
    staticClass: "bb-list-item__button",
    attrs: {
      "uppercase": _vm.titleUppercase,
      "to": _vm.to,
      "label": _vm.title,
      "icon-after": _vm.buttonIcon,
      "size": _vm.computedButtonSize,
      "icon-after-fixed": "",
      "no-background": "",
      "no-side-paddings": "",
      "color": "blue",
      "display": "block",
      "aria-label": _vm.title + " button"
    },
    on: {
      "click": _vm.onButtonClick
    }
  })] : _vm._e()] : [_vm._t("default")]], 2), _vm._v(" "), !_vm.noBottomBorder && !_vm.bullet ? _c('bb-separator', {
    attrs: {
      "dashed": ""
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$26 = [];

/* style */
const __vue_inject_styles__$29 = function (inject) {
  if (!inject) return;
  inject("data-v-595dfcb9_0", {
    source: ".bb-list-item{position:relative;display:block;margin-bottom:-.0625rem;color:#57697a}.bb-list-item--condensed,.bb-list-item--extracondensed{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-list-item:last-child{margin-bottom:0}.bb-list-item__content{display:flex;align-items:center;justify-content:space-between}.bb-list-item--type-text .bb-list-item__content{padding:.75rem 1.25rem}.bb-list-item--bullet .bb-list-item__content{align-items:flex-start;justify-content:flex-start}.bb-list-item--type-text.bb-list-item--condensed .bb-list-item__content{padding:.5625rem 0}.bb-list-item--type-text.bb-list-item--extracondensed .bb-list-item__content{padding:.125rem 0}.bb-list-item--bullet .bb-list-item__content .bb-list-item__title{padding-left:1.5625rem;margin-top:.1875rem}.bb-list-item__title{font-size:.75rem;text-align:left}.bb-list-item--has-slot-content .bb-list-item__title{margin-top:.125rem;margin-right:1rem}.bb-list-item--title-nowrap .bb-list-item__title{white-space:nowrap}.bb-list-item--title-uppercase .bb-list-item__title{text-transform:uppercase}.bb-list-item--extracondensed .bb-list-item__title{font-size:.6875rem}.bb-list-item__default-slot{color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-align:right}.bb-list-item--slot-nowrap .bb-list-item__default-slot{white-space:nowrap}.bb-list-item__button{justify-content:flex-start;padding:0 1.25rem}.bb-list-item--condensed .bb-list-item__button{padding:.25rem 0}.bb-list-item--extracondensed .bb-list-item__button{padding:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$29 = undefined;
/* module identifier */
const __vue_module_identifier__$29 = undefined;
/* functional template */
const __vue_is_functional_template__$29 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$29 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$26,
  staticRenderFns: __vue_staticRenderFns__$26
}, __vue_inject_styles__$29, __vue_script__$29, __vue_scope_id__$29, __vue_is_functional_template__$29, __vue_module_identifier__$29, false, createInjector, undefined, undefined);

var Icon$P = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","xmlns:xlink":"http://www.w3.org/1999/xlink","viewBox":"0 0 361.65 104.5"}},[_c('defs',[_c('path',{attrs:{"id":"SVGID_1_","d":"M79.76 73.9c0 4.69-.85 8.85-2.54 12.49-1.7 3.64-3.99 6.73-6.88 9.27-2.89 2.54-6.2 4.46-9.94 5.76-3.74 1.29-7.65 1.94-11.74 1.94H8.13c-1.39 0-2.32-.3-2.77-.9-.45-.6-.67-1.4-.67-2.39V7.36c0-1 .22-1.79.67-2.39.45-.6 1.32-.9 2.62-.9l37.98-.15c10.27 0 18.02 2.47 23.25 7.4 5.24 4.93 7.85 11.69 7.85 20.26 0 1.19-.15 2.64-.45 4.33-.3 1.7-.82 3.44-1.57 5.24-.75 1.79-1.85 3.54-3.29 5.23-1.45 1.7-3.26 3.09-5.46 4.19l.15.6c2.89 1.1 5.21 2.59 6.95 4.48 1.74 1.9 3.09 3.94 4.04 6.13.95 2.19 1.57 4.36 1.87 6.51.31 2.15.46 4.02.46 5.61z"}}),_c('clipPath',{attrs:{"id":"SVGID_2_"}},[_c('use',{attrs:{"xlink:href":"#SVGID_1_","overflow":"visible"}})]),_c('radialGradient',{attrs:{"id":"SVGID_3_","cx":"89.3921","cy":"61.663","r":"73.2962","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"offset":".3805","stop-color":"#3cc88c"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#005876"}})],1),_c('radialGradient',{attrs:{"id":"SVGID_4_","cx":"-8.7241","cy":"10.8662","r":"73.2962","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"offset":".4734","stop-color":"#00c8b4"}}),_c('stop',{attrs:{"offset":".5679","stop-color":"#00c4b2"}}),_c('stop',{attrs:{"offset":".669","stop-color":"#00b7aa"}}),_c('stop',{attrs:{"offset":".7732","stop-color":"#00a19f"}}),_c('stop',{attrs:{"offset":".8795","stop-color":"#00838e"}}),_c('stop',{attrs:{"offset":".9865","stop-color":"#005d79"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#005876"}})],1)],1),_c('path',{attrs:{"d":"M79.76 73.9c0 4.69-.85 8.85-2.54 12.49-1.7 3.64-3.99 6.73-6.88 9.27-2.89 2.54-6.2 4.46-9.94 5.76-3.74 1.29-7.65 1.94-11.74 1.94H8.13c-1.39 0-2.32-.3-2.77-.9-.45-.6-.67-1.4-.67-2.39V7.36c0-1 .22-1.79.67-2.39.45-.6 1.32-.9 2.62-.9l37.98-.15c10.27 0 18.02 2.47 23.25 7.4 5.24 4.93 7.85 11.69 7.85 20.26 0 1.19-.15 2.64-.45 4.33-.3 1.7-.82 3.44-1.57 5.24-.75 1.79-1.85 3.54-3.29 5.23-1.45 1.7-3.26 3.09-5.46 4.19l.15.6c2.89 1.1 5.21 2.59 6.95 4.48 1.74 1.9 3.09 3.94 4.04 6.13.95 2.19 1.57 4.36 1.87 6.51.31 2.15.46 4.02.46 5.61z","fill":"#66ccc6"}}),_c('path',{attrs:{"d":"M162.69 61.66c0 40.48-32.82 73.3-73.29 73.3-40.48 0-73.3-32.82-73.3-73.3s32.82-73.29 73.3-73.29c40.47 0 73.29 32.81 73.29 73.29z","clip-path":"url(#SVGID_2_)","fill":"url(#SVGID_3_)"}}),_c('path',{attrs:{"d":"M64.57 10.86c0 40.48-32.82 73.3-73.3 73.3s-73.3-32.82-73.3-73.3 32.82-73.29 73.3-73.29c40.49 0 73.3 32.81 73.3 73.29z","opacity":".6","clip-path":"url(#SVGID_2_)","fill":"url(#SVGID_4_)"}}),_c('path',{staticClass:"bb-icon__logo-type",attrs:{"d":"M135.13 53.65v-2.17c5.49-.98 9.87-3.63 9.87-10.6 0-8.03-4.27-11.92-14.8-11.92h-22.83c-.59 0-1.11.48-1.11 1.07v46.51c0 .59.51 1.09 1.11 1.09h21.91c11.18 0 17.36-3.9 17.36-12.71.01-7.76-4.51-10.61-11.51-11.27zm-19.38-17.9h12.68c4.87 0 7.11 1.98 7.11 6.58 0 5-2.56 6.78-7.56 6.98l-12.23.53V35.75zm12.36 35.1h-12.35V55.83l12.36.39c7.04.2 8.81 2.32 8.81 7.71-.01 4.02-2.18 6.92-8.82 6.92zM159.16 28.1h-5.53c-1.05 0-1.59.48-1.59 1.54v7.44c0 1.05.54 1.47 1.59 1.47h5.53c1.05 0 1.61-.42 1.61-1.47v-7.44c-.01-1.05-.56-1.54-1.61-1.54zM159.75 43.61h-6.71c-.59 0-1.1.44-1.1 1.03v31.97c0 .59.51 1.02 1.1 1.02h6.71c.59 0 1.01-.43 1.01-1.02V44.64c0-.59-.42-1.03-1.01-1.03zM221.66 42.21c-3.34 0-7.04.88-8.71 1.33l-.11-14.36c0-.66-.46-1.07-1.05-1.07h-.26l-6.45 1.13c-.79.13-1.12.61-1.12 1.39v40.72c0 2.83.2 4.41 3.76 5.53 2.43.85 6.91 1.58 11.91 1.58 13.22 0 16.19-5.92 16.19-18.55-.02-12.9-3.83-17.7-14.16-17.7zm-2.63 30.13c-3.55 0-6.25-.66-6.25-.66V49.44s4.01-1.05 7.04-1.05c5.66 0 7.04 1.97 7.04 11.71 0 10-1.91 12.24-7.83 12.24zM267.37 44.05c-2.11-1.18-5.25-1.84-9.92-1.84-6.71 0-12.78 1.65-14.36 2.24-.33.13-.81.4-.81.99v.27l.94 4.08c.07.46.41.59.74.59.13 0 .27-.06.47-.06 1.98-.53 6.85-1.84 11.32-1.84 2.37 0 3.97.2 5.02.66 1.65.72 1.86 2.04 1.86 3.88v2.11l-11.73 1.91c-5.26.85-10.6 1.78-10.6 10.26 0 10.46 8.74 11.18 15.85 11.18 3.68 0 6.81-.39 9.11-.92 4.48-1.05 6.09-1.91 6.09-6.84V52.54c.01-3.56-.62-6.65-3.98-8.49zm-4.74 27.76c-1.4.46-3.51.92-6.07.92-3.16 0-7.31-.39-7.31-5.53 0-4.28 2.51-4.67 5.21-5.07l8.17-1.32v11zM294.65 42.21c-4.21 0-9.73.79-13.09 1.71-3.68.99-4.93 1.84-4.93 4.34v28.35c0 .59.44 1.02 1.09 1.02h6.71c.59 0 1.03-.43 1.03-1.02V49.38s4.27-1.12 8.21-1.12c3.49 0 4.92 1.31 4.92 5.13v23.22c0 .59.44 1.02 1.03 1.02h6.78c.59 0 1.02-.43 1.02-1.02v-24.4c.01-7.9-4.54-10-12.77-10zM344.17 76.68L329.4 59.84l14.05-15.66c.07-.13.13-.2.13-.33 0-.26-.2-.59-.66-.59l-8.02-.2c-.33 0-.59.06-.86.46L322.03 57.4h-.6V29.25c0-.73-.42-1.14-1.01-1.14h-.27l-6.44 1.13c-.79.2-1.12.61-1.12 1.39v45.99c0 .59.45 1.02 1.05 1.02h6.78c.65 0 1.01-.43 1.01-1.02v-13.3h.6l11.81 13.9c.33.4.66.42.99.42h8.81c.39 0 .66-.35.66-.61 0-.13-.06-.29-.13-.35zM195.58 43.48c-1.77-.58-3.9-1.03-6.4-1.37-2.5-.34-4.89-.5-7.19-.5-3.08 0-5.63.43-7.67 1.29-2.04.86-3.66 2.1-4.86 3.71-1.2 1.61-2.04 3.55-2.52 5.83-.48 2.28-.72 4.83-.72 7.67 0 2.69.21 5.12.61 7.31.41 2.18 1.15 4.06 2.23 5.62 1.08 1.56 2.55 2.77 4.43 3.64 1.87.86 4.27 1.29 7.2 1.29 1.15 0 2.46-.12 3.92-.36 1.29-.21 2.72-.6 4.28-1.14.04-.02.07-.03.11-.05l.13 3.32c0 1.1.1 2.06-.12 2.88-.22.81-.51 1.47-1.08 1.98-.57.5-1.3.88-2.29 1.12-.98.24-2.22.36-3.75.36-1.39 0-2.98-.06-4.77-.18-1.8-.12-3.51-.3-5.14-.54-.67-.1-1.16-.1-1.47 0-.31.1-.52.43-.61 1.01l-.58 2.66c-.09.38-.07.76.07 1.12.14.36.6.68 1.37.97 1.49.62 3.32 1.06 5.51 1.3 2.18.24 4.21.36 6.08.36 3.46 0 6.18-.36 8.17-1.08 1.99-.72 3.52-1.73 4.57-3.03 1.05-1.29 1.7-2.83 1.99-4.61.29-1.78.41-3.7.41-5.76V46.71c0-.67-.08-1.31-.3-1.91-.22-.59-.75-1.04-1.61-1.32zm-6.49 26.34c-.75.43-2.78.75-4.2.94-1.42.19-2.54.29-3.59.29-1.25 0-2.26-.19-3.14-.57-.89-.38-1.58-1.02-2.13-1.91-.55-.89-.95-2.08-1.21-3.56-.26-1.49-.39-3.34-.39-5.54 0-2.35.21-4.25.62-5.69.41-1.44.97-2.53 1.69-3.28.72-.74 1.56-1.23 2.52-1.48.96-.24 1.99-.36 3.1-.36 1.15 0 2.13.06 3.41.18 1.27.12 2.58.28 3.33.47v20.51z","fill":"#003B5E"}})]) } };

Vue.component('icon-logo-bigbank', Icon$P);

var Icon$O = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","xmlns:xlink":"http://www.w3.org/1999/xlink","viewBox":"0 0 81.6 104.5"}},[_c('defs',[_c('path',{attrs:{"id":"SVGID_1_mark_","d":"M79.76 73.9c0 4.69-.85 8.85-2.54 12.49-1.7 3.64-3.99 6.73-6.88 9.27-2.89 2.54-6.2 4.46-9.94 5.76-3.74 1.29-7.65 1.94-11.74 1.94H8.13c-1.39 0-2.32-.3-2.77-.9-.45-.6-.67-1.4-.67-2.39V7.36c0-1 .22-1.79.67-2.39.45-.6 1.32-.9 2.62-.9l37.98-.15c10.27 0 18.02 2.47 23.25 7.4 5.24 4.93 7.85 11.69 7.85 20.26 0 1.19-.15 2.64-.45 4.33-.3 1.7-.82 3.44-1.57 5.24-.75 1.79-1.85 3.54-3.29 5.23-1.45 1.7-3.26 3.09-5.46 4.19l.15.6c2.89 1.1 5.21 2.59 6.95 4.48 1.74 1.9 3.09 3.94 4.04 6.13.95 2.19 1.57 4.36 1.87 6.51.31 2.15.46 4.02.46 5.61z"}}),_c('clipPath',{attrs:{"id":"SVGID_2_mark_"}},[_c('use',{attrs:{"xlink:href":"#SVGID_1_mark_","overflow":"visible"}})]),_c('radialGradient',{attrs:{"id":"SVGID_3_mark_","cx":"89.3921","cy":"61.663","r":"73.2962","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"offset":".3805","stop-color":"#3cc88c"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#005876"}})],1),_c('radialGradient',{attrs:{"id":"SVGID_4_mark_","cx":"-8.7241","cy":"10.8662","r":"73.2962","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"offset":".4734","stop-color":"#00c8b4"}}),_c('stop',{attrs:{"offset":".5679","stop-color":"#00c4b2"}}),_c('stop',{attrs:{"offset":".669","stop-color":"#00b7aa"}}),_c('stop',{attrs:{"offset":".7732","stop-color":"#00a19f"}}),_c('stop',{attrs:{"offset":".8795","stop-color":"#00838e"}}),_c('stop',{attrs:{"offset":".9865","stop-color":"#005d79"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#005876"}})],1)],1),_c('path',{attrs:{"d":"M79.76 73.9c0 4.69-.85 8.85-2.54 12.49-1.7 3.64-3.99 6.73-6.88 9.27-2.89 2.54-6.2 4.46-9.94 5.76-3.74 1.29-7.65 1.94-11.74 1.94H8.13c-1.39 0-2.32-.3-2.77-.9-.45-.6-.67-1.4-.67-2.39V7.36c0-1 .22-1.79.67-2.39.45-.6 1.32-.9 2.62-.9l37.98-.15c10.27 0 18.02 2.47 23.25 7.4 5.24 4.93 7.85 11.69 7.85 20.26 0 1.19-.15 2.64-.45 4.33-.3 1.7-.82 3.44-1.57 5.24-.75 1.79-1.85 3.54-3.29 5.23-1.45 1.7-3.26 3.09-5.46 4.19l.15.6c2.89 1.1 5.21 2.59 6.95 4.48 1.74 1.9 3.09 3.94 4.04 6.13.95 2.19 1.57 4.36 1.87 6.51.31 2.15.46 4.02.46 5.61z","fill":"#66ccc6"}}),_c('path',{attrs:{"d":"M162.69 61.66c0 40.48-32.82 73.3-73.29 73.3-40.48 0-73.3-32.82-73.3-73.3s32.82-73.29 73.3-73.29c40.47 0 73.29 32.81 73.29 73.29z","clip-path":"url(#SVGID_2_mark_)","fill":"url(#SVGID_3_mark_)"}}),_c('path',{attrs:{"d":"M64.57 10.86c0 40.48-32.82 73.3-73.3 73.3s-73.3-32.82-73.3-73.3 32.82-73.29 73.3-73.29c40.49 0 73.3 32.81 73.3 73.29z","opacity":".6","clip-path":"url(#SVGID_2_mark_)","fill":"url(#SVGID_4_mark_)"}})]) } };

Vue.component('icon-logo-bigbank-mark', Icon$O);

Vue.use(VueScreen, gridBreakpoints);
var script$28 = {
  name: 'bb-logo',
  components: {
    BbLink: __vue_component__$2p,
    BbIcon: __vue_component__$2C
  },
  props: {
    logo: {
      type: [String, Object]
    },
    fluid: Boolean,
    inverted: Boolean,
    mark: Boolean,
    markBeforeBreakpoint: {
      type: String,
      validator: breakpoint => ['sm', 'md', 'lg', 'xl'].includes(breakpoint)
    },
    to: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: 'Bigbank logo'
    }
  },
  computed: {
    renderLogoMark() {
      return this.mark || this.markBeforeBreakpoint && !this.$screen[this.markBeforeBreakpoint];
    },
    logoName() {
      return this.renderLogoMark ? 'logo-bigbank-mark' : 'logo-bigbank';
    },
    classes() {
      return ['bb-logo', {
        'bb-logo--fluid': this.fluid,
        'bb-logo--inverted': this.inverted
      }];
    }
  }
};

/* script */
const __vue_script__$28 = script$28;

/* template */
var __vue_render__$25 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-link', {
    class: _vm.classes,
    attrs: {
      "to": _vm.to,
      "color": _vm.inverted ? 'white' : 'gray',
      "no-underline": ""
    }
  }, [_vm.logo ? _c('img', {
    staticClass: "bb-logo__img",
    attrs: {
      "src": _vm.logo,
      "alt": _vm.alt
    }
  }) : _c('bb-icon', {
    staticClass: "bb-logo__svg",
    attrs: {
      "name": _vm.logoName,
      "size": "fluid",
      "fill": "original"
    }
  })], 1);
};
var __vue_staticRenderFns__$25 = [];

/* style */
const __vue_inject_styles__$28 = function (inject) {
  if (!inject) return;
  inject("data-v-327cd791_0", {
    source: ".bb-logo{display:inline-block;height:2.1875rem;padding:0}.bb-logo--fluid{height:100%}.bb-logo--inverted .bb-icon__logo-type{fill:#fff}.bb-logo .bb-logo__img{height:100%}.bb-logo .bb-logo__svg{width:auto}.bb-logo .bb-logo__svg .bb-icon__logo-type{transition:fill .2s}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$28 = undefined;
/* module identifier */
const __vue_module_identifier__$28 = undefined;
/* functional template */
const __vue_is_functional_template__$28 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$28 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$25,
  staticRenderFns: __vue_staticRenderFns__$25
}, __vue_inject_styles__$28, __vue_script__$28, __vue_scope_id__$28, __vue_is_functional_template__$28, __vue_module_identifier__$28, false, createInjector, undefined, undefined);

function ffpoly () {
  if (typeof window === 'undefined') return;
  const w = window;
  const d = w.document;
  if (w.onfocusin === undefined) {
    d.addEventListener('focus', addPolyfill, true);
    d.addEventListener('blur', addPolyfill, true);
    d.addEventListener('focusin', removePolyfill, true);
    d.addEventListener('focusout', removePolyfill, true);
  }
  function addPolyfill(e) {
    const type = e.type === 'focus' ? 'focusin' : 'focusout';
    const event = new CustomEvent(type, {
      bubbles: true,
      cancelable: false
    });
    event.c1Generated = true;
    e.target.dispatchEvent(event);
  }
  function removePolyfill(e) {
    if (!e.c1Generated) {
      d.removeEventListener('focus', addPolyfill, true);
      d.removeEventListener('blur', addPolyfill, true);
      d.removeEventListener('focusin', removePolyfill, true);
      d.removeEventListener('focusout', removePolyfill, true);
    }
    setTimeout(() => {
      d.removeEventListener('focusin', removePolyfill, true);
      d.removeEventListener('focusout', removePolyfill, true);
    });
  }
}

ffpoly();
const modifiers = [9];
var MaskedInput = {
  name: 'MaskedInput',
  render(h) {
    return h('input', {
      ref: 'input',
      attrs: {
        disabled: this.maskCore === null || this.disabled,
        placeholder: this.placeholder ? this.placeholder : this.maskCore && this.isFramed ? this.maskCore.getValue() : '',
        autocomplete: 'off',
        'data-testid': 'bb-masked-input__input'
      },
      domProps: {
        value: this.value
      },
      on: {
        keydown: this.keyDown,
        keypress: this.keyPress,
        keyup: this.keyUp,
        textInput: this.textInput,
        mouseup: this.mouseUp,
        focusout: this.focusOut,
        cut: this.cut,
        copy: this.copy,
        paste: this.paste
      }
    });
  },
  data: () => ({
    marginLeft: 0,
    maskCore: null,
    updateAfterAll: false
  }),
  props: {
    value: {
      type: String
    },
    mask: {
      required: true,
      validator: value => !!(value && value.length >= 1 || value instanceof Object)
    },
    placeholderChar: {
      type: String,
      default: '_',
      validator: value => !!(value && value.length === 1)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    framed: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    mask(newValue, oldValue) {
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        this.initMask();
      }
    },
    value(newValue) {
      if (this.maskCore) this.maskCore.setValue(newValue);
    }
  },
  mounted() {
    this.initMask();
  },
  methods: {
    initMask() {
      try {
        if (this.mask instanceof Object) {
          this.maskCore = new InputMask(this.mask);
        } else {
          this.maskCore = new InputMask({
            pattern: this.mask,
            value: '',
            placeholderChar: this.placeholderChar,
            formatCharacters: {
              'a': {
                validate: char => /^[A-Za-zА-Яа-я]$/.test(char)
              },
              'A': {
                validate: char => /^[A-Za-zА-Яа-я]$/.test(char),
                transform: char => char.toUpperCase()
              },
              '*': {
                validate: char => /^[\dA-Za-zА-Яа-я]$/.test(char)
              },
              '#': {
                validate: char => /^[\dA-Za-zА-Яа-я]$/.test(char),
                transform: char => char.toUpperCase()
              },
              '+': {
                validate: () => true
              }
            }
          });
        }
        this.maskCore.setValue(this.$refs.input.value);
        this.maskCore.setSelection({
          start: this.$refs.input.selectionStart,
          end: this.$refs.input.selectionStart
        });
        if (this.$refs.input.value === '') {
          this.$emit('input', '', '');
        } else {
          this.updateToCoreState();
        }
      } catch (e) {
        this.maskCore = null;
        this.$refs.input.value = 'Error';
        this.$emit('input', this.$refs.input.value, '');
      }
    },
    getValue() {
      return this.maskCore ? this.maskCore.getValue() : '';
    },
    keyDown(e) {
      if (this.maskCore === null) {
        e.preventDefault();
        return;
      }
      this.setNativeSelection();
      switch (e.keyCode) {
        case 8:
          e.preventDefault();
          if (this.maskCore.selection.start > this.marginLeft || this.maskCore.selection.start !== this.maskCore.selection.end) {
            this.maskCore.backspace();
            this.updateToCoreState();
          }
          break;
        case 37:
          e.preventDefault();
          if (this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
            this.$refs.input.selectionStart -= 1;
          }
          this.maskCore.selection = {
            start: this.$refs.input.selectionStart,
            end: this.$refs.input.selectionStart
          };
          this.updateToCoreState();
          break;
        case 39:
          e.preventDefault();
          if (this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
            this.$refs.input.selectionEnd += 1;
          }
          this.maskCore.selection = {
            start: this.$refs.input.selectionEnd,
            end: this.$refs.input.selectionEnd
          };
          this.updateToCoreState();
          break;
        case 35:
          e.preventDefault();
          this.$refs.input.selectionStart = this.$refs.input.value.length;
          this.$refs.input.selectionEnd = this.$refs.input.value.length;
          this.maskCore.selection = {
            start: this.$refs.input.selectionEnd,
            end: this.$refs.input.selectionEnd
          };
          this.updateToCoreState();
          break;
        case 36:
          e.preventDefault();
          this.$refs.input.selectionStart = 0;
          this.$refs.input.selectionEnd = 0;
          this.maskCore.selection = {
            start: this.$refs.input.selectionStart,
            end: this.$refs.input.selectionStart
          };
          this.updateToCoreState();
          break;
        case 46:
          e.preventDefault();
          if (this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
            this.maskCore.setValue('');
            this.maskCore.setSelection({
              start: 0,
              end: 0
            });
            this.$refs.input.selectionStart = this.maskCore.selection.start;
            this.$refs.input.selectionEnd = this.maskCore.selection.start;
          } else {
            this.maskCore.backspace();
          }
          this.updateToCoreState();
          break;
      }
    },
    keyPress(e) {
      if (e.ctrlKey) return;
      const isIE = !!document.documentMode;
      const isFirefox = typeof InstallTrigger !== 'undefined';
      if (!modifiers.includes(e.keyCode) && (isIE || isFirefox)) {
        e.preventDefault();
        e.data = e.key;
        this.textInput(e);
      }
    },
    textInput(e) {
      if (e.preventDefault) e.preventDefault();
      if (this.maskCore.input(e.data)) {
        this.updateAfterAll = true;
      }
      this.updateToCoreState();
    },
    keyUp() {
      this.maskCore.setValue(this.$refs.input.value);
      this.updateToCoreState();
      this.updateAfterAll = false;
    },
    cut(e) {
      e.preventDefault();
      if (this.$refs.input.selectionStart !== this.$refs.input.selectionEnd) {
        try {
          document.execCommand('copy');
        } catch (err) {}
        this.maskCore.backspace();
        this.updateToCoreState();
      }
    },
    copy() {},
    paste(e) {
      e.preventDefault();
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedData = clipboardData.getData('Text');
      this.maskCore.paste(pastedData);
      this.updateToCoreState();
    },
    updateToCoreState() {
      if (this.maskCore === null) return;
      if (this.$refs.input.value !== this.maskCore.getValue()) {
        this.$refs.input.value = this.maskCore.getValue();
        this.$emit('input', this.$refs.input.value, this.maskCore.getRawValue());
      }
      this.$refs.input.selectionStart = this.maskCore.selection.start;
      this.$refs.input.selectionEnd = this.maskCore.selection.end;
    },
    isEmpty() {
      if (this.maskCore === null) return true;
      return this.maskCore.getValue() === this.maskCore.emptyValue;
    },
    focusOut() {
      if (this.isEmpty()) {
        this.$refs.input.value = '';
        this.maskCore.setSelection({
          start: 0,
          end: 0
        });
        this.$emit('input', '', '');
      }
    },
    setNativeSelection() {
      this.maskCore.selection = {
        start: this.$refs.input.selectionStart,
        end: this.$refs.input.selectionEnd
      };
    },
    mouseUp() {
      if (this.isEmpty() && this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
        this.maskCore.setSelection({
          start: 0,
          end: 0
        });
        this.$refs.input.selectionStart = this.maskCore.selection.start;
        this.$refs.input.selectionEnd = this.maskCore.selection.start;
        this.marginLeft = this.maskCore.selection.start;
        this.updateToCoreState();
      } else {
        this.setNativeSelection();
      }
    }
  }
};

var script$27 = {
  name: 'bb-masked-input',
  components: {
    MaskedInput,
    BbIcon: __vue_component__$2C
  },
  mixins: [FieldExtensions, ErrorBubble, Field, veeValidationMixin, vuelidateValidator],
  props: {
    helpText: String,
    helpActive: Boolean,
    mask: {
      type: String,
      required: true,
      validator: mask => !!mask.length
    },
    placeholderChar: {
      type: String,
      validator: type => ['#', '_', ''].includes(type),
      default: '_'
    },
    center: Boolean,
    disabled: Boolean,
    readonly: Boolean,
    placeholder: {
      type: String,
      default: ''
    },
    pattern: String,
    inputmode: String,
    rules: Object,
    mandatory: Boolean
  },
  data() {
    return {
      intermediateValue: ''
    };
  },
  computed: {
    hasLabel() {
      return notEmpty(this.label);
    },
    maskOptions() {
      return {
        pattern: this.mask,
        formatCharacters: {
          'V': {
            validate: char => /[a-zA-Z]/.test(char),
            transform: char => char.toUpperCase()
          }
        }
      };
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `error-message-${slugify(this.name)}`;
    },
    computedId() {
      var _this$id;
      return (_this$id = this.id) !== null && _this$id !== void 0 ? _this$id : slugify(this.name);
    }
  },
  watch: {
    val(newVal) {
      this.onInput({
        target: {
          value: newVal
        }
      });
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    onFocus() {
      this.focus = true;
      this.$refs.input.mouseUp();
      this.intermediateValue = this.value;
      this.$emit('focus');
    },
    onBlur() {
      this.focus = false;
      this.$emit('blur');
      this.intermediateValue !== this.value && this.onChange();
    },
    onChange() {
      this.$emit('change', this.value);
    }
  }
};

/* script */
const __vue_script__$27 = script$27;

/* template */
var __vue_render__$24 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-masked-input field-group",
    class: _vm.fieldGroupClasses,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "val"
    },
    on: {
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, [_vm.hasLabel || _vm.$slots.default ? _c('label', {
    attrs: {
      "for": _vm.computedId
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-masked-input__mandatory-indicator"
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _c('div', {
    ref: "inputWrapper",
    staticClass: "input-group"
  }, [_c('masked-input', _vm._b({
    ref: "input",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "type": _vm.type,
      "guide": true,
      "mask": _vm.maskOptions,
      "disabled": _vm.disabled,
      "readonly": _vm.readonly,
      "framed": _vm.isFramed,
      "inputmode": _vm.inputmode,
      "pattern": _vm.pattern,
      "placeholder": _vm.placeholder,
      "placeholder-char": _vm.placeholderChar,
      "aria-live": "assertive",
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory
    },
    nativeOn: {
      "blur": function ($event) {
        return _vm.onBlurAndValidate.apply(null, arguments);
      },
      "input": function ($event) {
        return _vm.onInput.apply(null, arguments);
      },
      "change": function ($event) {
        return _vm.onChange.apply(null, arguments);
      },
      "focus": function ($event) {
        return _vm.onFocus.apply(null, arguments);
      }
    },
    model: {
      value: _vm.val,
      callback: function ($$v) {
        _vm.val = $$v;
      },
      expression: "val"
    }
  }, 'masked-input', _vm.inputAttrs, false)), _vm._v(" "), _c('div', {
    staticClass: "input-addon-wrapper"
  }, [_c('bb-icon', {
    staticClass: "validation-error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  })], 1), _vm._v(" "), _vm.helpActive ? _c('bb-tooltip', {
    attrs: {
      "is-in-input": ""
    }
  }, [_c('p', [_vm._t("tooltipText", function () {
    return [_vm._v("\n          " + _vm._s(_vm.helpText) + "\n        ")];
  })], 2)]) : _vm._e()], 1), _vm._v(" "), _vm.hasValidationError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.inputWrapper,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', [_vm._v("\n        " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n      ")])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "aria-live": "assertive",
      "role": "alert"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n    ")]) : _vm._e()] : _vm._e()], 2);
};
var __vue_staticRenderFns__$24 = [];

/* style */
const __vue_inject_styles__$27 = undefined;
/* scoped */
const __vue_scope_id__$27 = undefined;
/* module identifier */
const __vue_module_identifier__$27 = undefined;
/* functional template */
const __vue_is_functional_template__$27 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$27 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$24,
  staticRenderFns: __vue_staticRenderFns__$24
}, __vue_inject_styles__$27, __vue_script__$27, __vue_scope_id__$27, __vue_is_functional_template__$27, __vue_module_identifier__$27, false, undefined, undefined, undefined);

var script$26 = Vue.extend({
  name: 'bb-progress-bar',
  props: {
    /**
     * Maximum value for the progress
     */
    max: {
      type: Number,
      required: true
    },
    /**
     * Progress initial value
     */
    value: {
      type: Number,
      required: true
    },
    /**
     * Progress color
     */
    color: {
      type: String,
      default: 'blue'
    },
    /**
     * Transparent background for progress
     */
    transparent: Boolean,
    /**
     * Progress corner style | [`round`, `sharp`]
     */
    corner: {
      type: String,
      default: 'sharp'
    },
    /**
     * Progress bar label for accessibility
     */
    ariaLabel: {
      type: String,
      default: 'Progress bar'
    }
  },
  computed: {
    classes() {
      return ['bb-progress-bar', {
        'bb-progress-bar--transparent': this.transparent,
        [`bb-progress-bar--corner-${this.corner}`]: this.corner
      }];
    },
    progressClasses() {
      return ['bb-progress-bar__progress', `bg-${this.color}`];
    },
    /**
     * Progress bar's progress is visually recalculated each time `max` and `value` change.
     * Updated percentage value is then applied directly to the progress style.
     */
    progress() {
      if (this.value <= 0 || this.max <= 0) {
        return 0;
      }
      if (this.value >= this.max) {
        return 100;
      }
      return Math.round(this.value / this.max * 100);
    }
  }
});

/* script */
const __vue_script__$26 = script$26;

/* template */
var __vue_render__$23 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes,
    attrs: {
      "data-testid": "bb-progress-bar"
    }
  }, [_c('progress', {
    class: _vm.progressClasses,
    style: {
      '--width': _vm.progress
    },
    attrs: {
      "max": _vm.max,
      "data-testid": "bb-progress-bar__progress",
      "aria-valuenow": _vm.value,
      "aria-valuemin": 0,
      "aria-valuemax": _vm.max,
      "tabindex": "0",
      "aria-label": _vm.ariaLabel
    },
    domProps: {
      "value": _vm.value
    }
  })]);
};
var __vue_staticRenderFns__$23 = [];

/* style */
const __vue_inject_styles__$26 = function (inject) {
  if (!inject) return;
  inject("data-v-785cf79e_0", {
    source: ".bb-progress-bar{width:100%;height:.25rem;background:#eeeff2}@media (min-width:768px){.bb-progress-bar{height:.375rem}}.bb-progress-bar--transparent{background:0 0}.bb-progress-bar__progress{display:block;width:calc(var(--width,0) * 1%);height:.25rem;transition:width .4s cubic-bezier(.645,.045,.355,1)}@media (min-width:768px){.bb-progress-bar__progress{height:.375rem}}.bb-progress-bar--corner-round{border-radius:.25rem}.bb-progress-bar--corner-round .bb-progress-bar__progress{display:block;width:calc(var(--width,0) * 1%);height:.25rem;border-radius:.25rem;transition:width .4s cubic-bezier(.645,.045,.355,1)}@media (min-width:768px){.bb-progress-bar--corner-round .bb-progress-bar__progress{height:.375rem;border-radius:.375rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$26 = undefined;
/* module identifier */
const __vue_module_identifier__$26 = undefined;
/* functional template */
const __vue_is_functional_template__$26 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$26 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$23,
  staticRenderFns: __vue_staticRenderFns__$23
}, __vue_inject_styles__$26, __vue_script__$26, __vue_scope_id__$26, __vue_is_functional_template__$26, __vue_module_identifier__$26, false, createInjector, undefined, undefined);

var script$25 = {
  name: 'bb-progress-dots',
  props: {
    count: {
      type: Number,
      required: true
    },
    activeIndex: {
      type: Number,
      default: 0
    },
    isClickable: Boolean,
    shape: {
      type: String,
      default: 'circle',
      validator: value => ['circle', 'rectangle'].includes(value)
    },
    ariaLabel: {
      type: String,
      default: 'Page number indicator'
    }
  },
  emits: ['activeIndex'],
  computed: {
    classes() {
      return [`bb-progress-dots--shape-${this.shape}`, {
        'bb-progress-dots--clickable': this.isClickable
      }];
    },
    completeAriaLabel() {
      return `${this.ariaLabel}, ${this.activeIndex + 1}/${this.count}`;
    }
  },
  methods: {
    handleClick(index) {
      this.$emit('activeIndex', index);
    },
    getDotClasses(activeIndex, index) {
      return ['bb-progress-dots__dot', {
        'is-active': activeIndex === index
      }];
    }
  }
};

/* script */
const __vue_script__$25 = script$25;

/* template */
var __vue_render__$22 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-progress-dots",
    class: _vm.classes,
    attrs: {
      "data-testid": "bb-progress-dots",
      "aria-label": _vm.completeAriaLabel
    }
  }, [_c('ol', {
    staticClass: "bb-progress-dots__list"
  }, _vm._l(_vm.count, function (item, i) {
    return _c('li', {
      key: i,
      staticClass: "bb-progress-dots__list-item",
      attrs: {
        "data-testid": "bb-progress-dots__list-item"
      }
    }, [_vm.isClickable ? _c('a', {
      staticClass: "bb-progress-dots__link",
      attrs: {
        "to": "#",
        "tabindex": "0"
      },
      on: {
        "click": function ($event) {
          $event.preventDefault();
          return _vm.handleClick(i);
        },
        "keydown": [function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
            return null;
          }
          $event.preventDefault();
          return _vm.handleClick(i);
        }, function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
            return null;
          }
          $event.preventDefault();
          return _vm.handleClick(i);
        }]
      }
    }, [_c('span', {
      class: _vm.getDotClasses(_vm.activeIndex, i)
    })]) : _c('span', {
      class: _vm.getDotClasses(_vm.activeIndex, i)
    })]);
  }), 0)]);
};
var __vue_staticRenderFns__$22 = [];

/* style */
const __vue_inject_styles__$25 = function (inject) {
  if (!inject) return;
  inject("data-v-5dfd3423_0", {
    source: ".bb-progress-dots{display:flex;justify-content:center}.bb-progress-dots .bb-progress-dots__list{display:flex;flex-direction:row}.bb-progress-dots--shape-rectangle.bb-progress-dots .bb-progress-dots__list{width:100%;justify-content:space-between}.bb-progress-dots--shape-rectangle.bb-progress-dots .bb-progress-dots__list-item{display:inline-block;width:100%;min-width:1.875rem;height:1.25rem;margin-right:.3125rem}.bb-progress-dots .bb-progress-dots__link{cursor:pointer}.bb-progress-dots .bb-progress-dots__link:hover .bb-progress-dots__dot{background:#0ccca2}.bb-progress-dots--shape-rectangle.bb-progress-dots .bb-progress-dots__link{display:flex;width:100%;height:100%;align-items:center}.bb-progress-dots .bb-progress-dots__dot{display:inline-block;background:#dee3e8;transition:background .2s}.bb-progress-dots--clickable.bb-progress-dots .bb-progress-dots__dot{cursor:pointer}.bb-progress-dots--shape-circle.bb-progress-dots .bb-progress-dots__dot{width:.5rem;height:.5rem;border-radius:99em;margin:.46875rem}.bb-progress-dots--shape-rectangle.bb-progress-dots .bb-progress-dots__dot{width:100%;height:.3125rem}.bb-progress-dots .bb-progress-dots__dot.is-active{background:#0ccca2}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$25 = undefined;
/* module identifier */
const __vue_module_identifier__$25 = undefined;
/* functional template */
const __vue_is_functional_template__$25 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$25 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$22,
  staticRenderFns: __vue_staticRenderFns__$22
}, __vue_inject_styles__$25, __vue_script__$25, __vue_scope_id__$25, __vue_is_functional_template__$25, __vue_module_identifier__$25, false, createInjector, undefined, undefined);

/**
 * A table for use in client-facing applications.
 */
var script$24 = defineComponent({
  name: 'bb-public-table',
  props: {
    /**
     * The column template, `key` refers to any single key in `rows`. `[{ key: string, label: string, class: string }]`
     */
    columns: {
      type: Array,
      required: true
    },
    /**
     * Table data, array of objects
     */
    rows: {
      type: Array,
      required: true
    },
    /**
     * Footer column template, `key` refers to any single key in `footerRows`. `[{ key: string, class: string }]`
     */
    footerColumns: Array,
    /**
     * Table footer, array of objects
     */
    footerRows: Array,
    /**
     * @deprecated Use the `cell` slot instead. Will be removed from BbPublicTable in IC 12.
     */
    hasActions: {
      type: Boolean,
      validator: deprecated('`hasActions` will be removed from BbPublicTable in IC 12. Use the `cell` slot instead.')
    },
    /**
     * @deprecated Use the `columns` slot instead. Will be removed from BbPublicTable in IC 12.
     */
    actionsLabel: {
      type: String,
      validator: deprecated('`actionsLabel` will be removed from BbPublicTable in IC 12. Use the `columns` slot instead.')
    },
    /**
     * Classes applied on every cell in the row. Key: row index, value: CSS classes
     */
    rowClasses: {
      type: Object,
      default: () => ({})
    },
    /**
     * Sub-row column template, `key` refers to any single key in `rows`. `[{ key: string, class: string }]`
     */
    subrowColumns: Array,
    /**
     * Accessible label for clickable row (sub-row toggle)
     */
    subrowToggleLabel: String,
    /**
     * The state of sub-rows
     */
    rowsState: Object
  },
  computed: {
    classes() {
      return {
        'bb-public-table--has-actions': this.hasActions
      };
    },
    hasSubrows() {
      return notEmpty(this.subrowColumns);
    }
  },
  methods: {
    injectOffsets(columns) {
      return columns.flatMap(column => column.offset ? [{
        key: `offset-${column.key}`,
        td: {
          colspan: column.offset,
          style: 'visibility: collapse;'
        }
      }, column] : [column]);
    },
    omit,
    notEmpty,
    toggleSubrow(rowIndex) {
      const newState = {
        ...this.rowsState,
        [rowIndex]: !this.rowsState[rowIndex]
      };
      this.$emit('update:rowsState', newState);
    }
  }
});

/* script */
const __vue_script__$24 = script$24;

/* template */
var __vue_render__$21 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-public-table",
    class: _vm.classes
  }, [_c('table', [_c('thead', [_c('tr', [_vm._l(_vm.columns, function (column) {
    return _c('th', _vm._b({
      key: column.key,
      class: [column.class, column.th && column.th.class]
    }, 'th', column.th, false), [_vm._t("header", function () {
      return [_vm._v("\n            " + _vm._s(column.label) + "\n          ")];
    }, null, {
      column: column
    })], 2);
  }), _vm._v(" "), _vm.hasActions ? _c('th', {
    attrs: {
      "aria-label": _vm.actionsLabel
    }
  }) : _vm._e()], 2)]), _vm._v(" "), _c('tbody', [_vm._l(_vm.rows, function (row, rowIndex) {
    return [_c('tr', {
      key: "row-" + rowIndex,
      class: {
        'has-subrow': _vm.hasSubrows
      },
      attrs: {
        "tabindex": _vm.hasSubrows ? '0' : '-1',
        "aria-label": _vm.hasSubrows && _vm.subrowToggleLabel
      },
      on: {
        "click": function ($event) {
          _vm.hasSubrows && _vm.toggleSubrow(rowIndex);
        },
        "keyup": function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
            return null;
          }
          _vm.hasSubrows && _vm.toggleSubrow(rowIndex);
        }
      }
    }, [_vm._l(_vm.injectOffsets(_vm.columns), function (column, columnIndex) {
      return _c(column.type || 'td', _vm._b({
        key: "column-" + columnIndex,
        tag: "component",
        class: [_vm.rowClasses[rowIndex], column.class, column.td && column.td.class]
      }, 'component', column.td, false), [_vm._t("cell", function () {
        return [_vm._v("\n              " + _vm._s(row[column.key]) + "\n            ")];
      }, null, {
        column: column,
        columnIndex: columnIndex,
        row: row,
        rowIndex: rowIndex,
        value: row[column.key]
      })], 2);
    }), _vm._v(" "), _vm.hasActions ? _c('td', {
      class: [_vm.rowClasses[rowIndex]]
    }, [_vm._t("actions", null, null, {
      row: row,
      rowIndex: rowIndex
    })], 2) : _vm._e()], 2), _vm._v(" "), _vm.hasSubrows ? _c('tr', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.rowsState[rowIndex],
        expression: "rowsState[rowIndex]"
      }],
      key: "subrow-" + rowIndex
    }, _vm._l(_vm.injectOffsets(_vm.subrowColumns), function (column, columnIndex) {
      return _c(column.type || 'td', _vm._b({
        key: "subrowColumn-" + columnIndex,
        tag: "component",
        class: [column.class, column.td && column.td.class]
      }, 'component', column.td, false), [_vm._t("subrowCell", function () {
        return [_vm._v("\n              " + _vm._s(row[column.key]) + "\n            ")];
      }, null, {
        column: column,
        columnIndex: columnIndex,
        row: row,
        rowIndex: rowIndex,
        value: row[column.key]
      })], 2);
    }), 1) : _vm._e()];
  })], 2), _vm._v(" "), _vm.notEmpty(_vm.footerRows) && _vm.notEmpty(_vm.footerColumns) ? _c('tfoot', _vm._l(_vm.footerRows, function (row, rowIndex) {
    return _c('tr', {
      key: rowIndex
    }, _vm._l(_vm.injectOffsets(_vm.footerColumns), function (column, columnIndex) {
      return _c(column.type || 'td', _vm._b({
        key: columnIndex,
        tag: "component",
        class: [column.class, column.td && column.td.class]
      }, 'component', column.td, false), [_vm._t("footerCell", function () {
        return [_vm._v("\n            " + _vm._s(row[column.key]) + "\n          ")];
      }, null, {
        column: column,
        columnIndex: columnIndex,
        row: row,
        rowIndex: rowIndex,
        value: row[column.key]
      })], 2);
    }), 1);
  }), 0) : _vm._e()])]);
};
var __vue_staticRenderFns__$21 = [];

/* style */
const __vue_inject_styles__$24 = function (inject) {
  if (!inject) return;
  inject("data-v-4a4d21d8_0", {
    source: ".bb-public-table{overflow:hidden}.bb-public-table table{width:100%;margin-top:-.625rem;margin-bottom:-.625rem;border-collapse:separate;border-spacing:0 .625rem}.bb-public-table thead{display:none}@media (min-width:768px){.bb-public-table thead{display:table-header-group}}.bb-public-table thead::after{display:table-row;content:\"\"}.bb-public-table tbody{display:flex;min-height:auto;flex-direction:column;align-items:stretch}@media (min-width:768px){.bb-public-table tbody{display:table-row-group}}.bb-public-table tbody tr.has-subrow{cursor:pointer}.bb-public-table tbody tr.has-subrow:hover td{background:#eeeff2}.bb-public-table tr{padding:.9375rem;border:.0625rem solid #eeeff2;border-radius:.625rem;background:#fff}@media (min-width:768px){.bb-public-table tr{background:0 0}}.bb-public-table tr:not(:last-of-type){margin-bottom:.5rem}.bb-public-table th{padding:.5rem .9375rem;border-top:solid .0625rem transparent;border-bottom:solid .0625rem transparent;background:#fff;text-align:left;vertical-align:middle}.bb-public-table th:first-of-type{border-left:solid .0625rem transparent}.bb-public-table th:last-of-type{border-right:solid .0625rem transparent}.bb-public-table thead th{background:#dee3e8;color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;text-transform:uppercase}@media (min-width:768px){.bb-public-table thead th{padding:.5rem .9375rem}}.bb-public-table thead th:first-of-type{border-bottom-left-radius:.375rem;border-top-left-radius:.375rem}.bb-public-table thead th:last-of-type{border-bottom-right-radius:.375rem;border-top-right-radius:.375rem}.bb-public-table--has-actions.bb-public-table thead th:last-of-type{padding-left:.3125rem}.bb-public-table td{display:block;border-top:solid .0625rem transparent;border-bottom:solid .0625rem transparent;background:#fff;transition:background .2s}.bb-public-table td:not(:first-of-type,:last-of-type){margin:.5rem 0}.bb-public-table td:last-of-type{border-right:solid .0625rem transparent;margin-top:1rem}.bb-public-table td:first-of-type{border-left:solid .0625rem transparent}@media (min-width:768px){.bb-public-table td{display:table-cell;padding:.5rem .9375rem;vertical-align:middle}.bb-public-table td:first-of-type{border-bottom-left-radius:.375rem;border-top-left-radius:.375rem}.bb-public-table td:last-of-type{border-bottom-right-radius:.375rem;border-top-right-radius:.375rem}.bb-public-table--has-actions.bb-public-table td:last-of-type{padding-left:.3125rem}}.bb-public-table td>*{vertical-align:middle}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$24 = undefined;
/* module identifier */
const __vue_module_identifier__$24 = undefined;
/* functional template */
const __vue_is_functional_template__$24 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$24 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$21,
  staticRenderFns: __vue_staticRenderFns__$21
}, __vue_inject_styles__$24, __vue_script__$24, __vue_scope_id__$24, __vue_is_functional_template__$24, __vue_module_identifier__$24, false, createInjector, undefined, undefined);

/**
 * A ribbon to indicate which environment is currently in use.
 */
var script$23 = Vue.extend({
  name: 'bb-ribbon',
  props: {
    label: String
  }
});

/* script */
const __vue_script__$23 = script$23;

/* template */
var __vue_render__$20 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-ribbon"
  }, [_c('span', {
    staticClass: "bb-ribbon__label"
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")])]);
};
var __vue_staticRenderFns__$20 = [];

/* style */
const __vue_inject_styles__$23 = function (inject) {
  if (!inject) return;
  inject("data-v-aa9d57ee_0", {
    source: ".bb-ribbon{display:flex;width:100%;height:1.625rem;align-items:center;justify-content:center;background:#ff7155}.bb-ribbon__label{color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.5625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$23 = undefined;
/* module identifier */
const __vue_module_identifier__$23 = undefined;
/* functional template */
const __vue_is_functional_template__$23 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$23 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$20,
  staticRenderFns: __vue_staticRenderFns__$20
}, __vue_inject_styles__$23, __vue_script__$23, __vue_scope_id__$23, __vue_is_functional_template__$23, __vue_module_identifier__$23, false, createInjector, undefined, undefined);

const COMMON_ALIGNMENT = ['start', 'end', 'center'];
const props = {
  tag: {
    type: String,
    default: 'div'
  },
  noGutters: Boolean,
  gutter: {
    type: [String, Number],
    validator: val => [0, 4, 10, 20, 30, 40, 60].includes(+val)
  },
  align: {
    type: String,
    default: null,
    validator: str => COMMON_ALIGNMENT.concat(['baseline', 'stretch']).includes(str)
  },
  justify: {
    type: String,
    default: null,
    validator: str => COMMON_ALIGNMENT.concat(['between', 'around']).includes(str)
  },
  alignContent: {
    type: String,
    default: null,
    validator: str => COMMON_ALIGNMENT.concat(['between', 'around', 'stretch']).includes(str)
  }
};
var script$22 = {
  name: 'bb-row',
  functional: true,
  props,
  render(h, {
    props,
    data,
    children
  }) {
    const classMap = {
      'gutter-0': props.noGutters,
      [`gutter-${props.gutter}`]: isNumber(props.gutter),
      [`align-items-${props.align}`]: props.align,
      [`justify-content-${props.justify}`]: props.justify,
      [`align-content-${props.alignContent}`]: props.alignContent
    };
    return h(props.tag, mergeData(data, {
      staticClass: 'bb-row row',
      class: classMap
    }), children);
  }
};

/* script */
const __vue_script__$22 = script$22;

/* template */

/* style */
const __vue_inject_styles__$22 = undefined;
/* scoped */
const __vue_scope_id__$22 = undefined;
/* module identifier */
const __vue_module_identifier__$22 = undefined;
/* functional template */
const __vue_is_functional_template__$22 = undefined;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$22 = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$22, __vue_script__$22, __vue_scope_id__$22, __vue_is_functional_template__$22, __vue_module_identifier__$22, false, undefined, undefined, undefined);

var script$21 = {
  name: 'bb-scroll-wrapper',
  mixins: [validator],
  props: {
    shadow: {
      type: [Boolean, String],
      validator: v => ['top', 'bottom', 'y', true, false].includes(v)
    },
    gradient: {
      type: String,
      validator: v => ['gray-10'].includes(v)
    },
    fullHeight: Boolean,
    fullHeightMobile: Boolean,
    viewportClass: String
  },
  data() {
    return {
      hasOverflow: {
        top: false,
        bottom: false
      },
      intersectionObservers: []
    };
  },
  computed: {
    classes() {
      return {
        'bb-scroll-wrapper--gradient': this.gradient,
        'bb-scroll-wrapper--shadow-top': ['top', 'y'].includes(this.shadow),
        'bb-scroll-wrapper--shadow-bottom': [true, 'bottom', 'y'].includes(this.shadow),
        'bb-scroll-wrapper--full-height': this.fullHeight,
        'bb-scroll-wrapper--full-height-mobile': this.fullHeightMobile,
        'has-overflow-top': this.hasOverflow.top,
        'has-overflow-bottom': this.hasOverflow.bottom
      };
    },
    observables() {
      return [{
        element: this.$refs.scrollTopMarker,
        handler: this.onTopIntersection
      }, {
        element: this.$refs.scrollBottomMarker,
        handler: this.onBottomIntersection
      }];
    }
  },
  mounted() {
    if (this.shadow || this.gradient) {
      this.initializeIntersectionObservers();
    }
  },
  destroyed() {
    this.removeIntersectionObservers();
  },
  methods: {
    initializeIntersectionObservers() {
      this.removeIntersectionObservers();
      this.addIntersectionObservers();
    },
    addIntersectionObservers() {
      this.intersectionObservers = this.observables.map(this.addIntersectionObserver);
    },
    removeIntersectionObservers() {
      Object.keys(this.hasOverflow).forEach(direction => this.hasOverflow[direction] = false);
      this.intersectionObservers.forEach((observer, index) => {
        var _this$observables$ind;
        return ((_this$observables$ind = this.observables[index]) === null || _this$observables$ind === void 0 ? void 0 : _this$observables$ind.element) && observer.unobserve(this.observables[index].element);
      });
      this.intersectionObservers.length = 0;
    },
    addIntersectionObserver({
      element,
      handler
    }) {
      const options = {
        threshold: 1,
        root: this.$refs.viewport
      };
      const intersectionObserver = new IntersectionObserver(handler, options);
      intersectionObserver.observe(element);
      return intersectionObserver;
    },
    onTopIntersection([{
      isIntersecting
    }]) {
      this.hasOverflow.top = !isIntersecting;
    },
    onBottomIntersection([{
      isIntersecting
    }]) {
      this.hasOverflow.bottom = !isIntersecting;
    }
  }
};

/* script */
const __vue_script__$21 = script$21;

/* template */
var __vue_render__$1$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-scroll-wrapper",
    class: _vm.classes
  }, [_c('div', {
    ref: "viewport",
    staticClass: "bb-scroll-wrapper__viewport",
    class: _vm.viewportClass,
    attrs: {
      "data-testid": "bb-scroll-wrapper__viewport"
    }
  }, [_c('div', {
    ref: "scrollTopMarker",
    attrs: {
      "data-testid": "bb-scroll-wrapper__scroll-top-marker"
    }
  }), _vm._v(" "), _vm._t("default"), _vm._v(" "), _c('div', {
    ref: "scrollBottomMarker",
    attrs: {
      "data-testid": "bb-scroll-wrapper__scroll-top-marker"
    }
  })], 2)]);
};
var __vue_staticRenderFns__$1$ = [];

/* style */
const __vue_inject_styles__$21 = function (inject) {
  if (!inject) return;
  inject("data-v-425d1f4c_0", {
    source: ".bb-scroll-wrapper{position:relative;display:flex;max-height:55vh}.bb-scroll-wrapper--full-height{max-height:100%}@media (max-width:768px){.bb-scroll-wrapper--full-height-mobile{max-height:100%}}.bb-scroll-wrapper__viewport{flex-grow:1;overflow-y:auto}.bb-scroll-wrapper::after,.bb-scroll-wrapper::before{position:absolute;width:100%;opacity:0;transition:opacity .2s}.bb-scroll-wrapper::before{top:0}.has-overflow-top.bb-scroll-wrapper::before{opacity:1}.bb-scroll-wrapper--gradient.bb-scroll-wrapper::before,.bb-scroll-wrapper--shadow-top.bb-scroll-wrapper::before{content:\"\"}.bb-scroll-wrapper--shadow-top.bb-scroll-wrapper::before{height:.4375rem;background:linear-gradient(rgba(0,0,0,.09),rgba(0,0,0,0))}.bb-scroll-wrapper--gradient.bb-scroll-wrapper::before{height:1.875rem;background:linear-gradient(#f7f7f8,rgba(247,247,248,0))}.bb-scroll-wrapper::after{bottom:0}.has-overflow-bottom.bb-scroll-wrapper::after{opacity:1}.bb-scroll-wrapper--gradient.bb-scroll-wrapper::after,.bb-scroll-wrapper--shadow-bottom.bb-scroll-wrapper::after{content:\"\"}.bb-scroll-wrapper--shadow-bottom.bb-scroll-wrapper::after{height:.4375rem;background:linear-gradient(rgba(0,0,0,0),rgba(0,0,0,.09))}.bb-scroll-wrapper--gradient.bb-scroll-wrapper::after{height:1.875rem;background:linear-gradient(rgba(247,247,248,0),#f7f7f8)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$21 = undefined;
/* module identifier */
const __vue_module_identifier__$21 = undefined;
/* functional template */
const __vue_is_functional_template__$21 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$21 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1$,
  staticRenderFns: __vue_staticRenderFns__$1$
}, __vue_inject_styles__$21, __vue_script__$21, __vue_scope_id__$21, __vue_is_functional_template__$21, __vue_module_identifier__$21, false, createInjector, undefined, undefined);

var script$20 = {
  name: 'bb-select',
  components: {
    BbIcon: __vue_component__$2C,
    BbTooltip: __vue_component__$2B
  },
  mixins: [FieldExtensions, Field, ErrorBubble, veeValidationMixin, vuelidateValidator],
  props: {
    helpText: String,
    helpActive: Boolean,
    inputOther: Boolean,
    inputOtherLabel: {
      type: String,
      default: 'Other'
    },
    inputOtherPlaceholder: String,
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    disabled: Boolean,
    readonly: Boolean,
    center: Boolean,
    noempty: {
      type: Boolean,
      default: false
    },
    medium: Boolean,
    color: {
      type: String,
      validator: color => ['mint', 'green',
      'yellow', 'red', 'gray'].includes(color)
    },
    rules: Object,
    mandatory: Boolean
  },
  data() {
    return {
      inputOtherVal: null,
      showInputOther: this.inputOther && this.inputOtherLabel === this.value,
      domSelectedOption: undefined
    };
  },
  computed: {
    selectedOptionText() {
      var _this$domSelectedOpti, _this$domSelectedOpti2;
      return (_this$domSelectedOpti = (_this$domSelectedOpti2 = this.domSelectedOption) === null || _this$domSelectedOpti2 === void 0 ? void 0 : _this$domSelectedOpti2.text) !== null && _this$domSelectedOpti !== void 0 ? _this$domSelectedOpti : this.val;
    },
    isFramed() {
      return this.framed || this.medium || this.small || this.large;
    },
    hasPlaceholder() {
      return this.showInputOther ? this.inputOtherPlaceholder : this.placeholder;
    },
    computedOptionsWithOther() {
      return [...this.options, this.createOption(this.inputOtherLabel)];
    },
    computedOptionsWithOtherValue() {
      return this.inputOtherVal ? [this.createOption(this.inputOtherVal), ...this.computedOptionsWithOther] : this.computedOptionsWithOther;
    },
    computedOptions() {
      return this.inputOther ? this.computedOptionsWithOtherValue : this.options;
    },
    hasSimpleOptions() {
      var _this$options;
      return ((_this$options = this.options) === null || _this$options === void 0 ? void 0 : _this$options.length) && typeof this.options[0] !== 'object';
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    slugifiedName() {
      return slugify(this.name);
    },
    errorMessageId() {
      return `errorMessage-${this.slugifiedName}`;
    },
    computedId() {
      var _this$id;
      return (_this$id = this.id) !== null && _this$id !== void 0 ? _this$id : this.slugifiedName;
    }
  },
  watch: {
    val() {
      this.$nextTick(() => {
        this.updateSelectedOption();
      });
      if (this.inputOther && !this.showInputOther && this.val === this.inputOtherLabel) {
        const ua = window.navigator.userAgent;
        const iOSiPadOS = !!ua.match(/iPad|iPhone/i) && !!ua.match(/WebKit/i);
        this.val = '';
        this.onChange();
        this.showInputOther = true;
        this.inputOtherVal = null;
        !iOSiPadOS && this.$nextTick(() => {
          var _this$$refs$input;
          (_this$$refs$input = this.$refs.input) === null || _this$$refs$input === void 0 ? void 0 : _this$$refs$input.focus();
        });
      }
    },
    color() {
      this.$emit('colorChange', this.color);
    }
  },
  mounted() {
    this.updateSelectedOption();
    this.$emit('colorInit', this.color);
  },
  methods: {
    updateSelectedOption() {
      const selectElement = this.$refs.select;
      this.domSelectedOption = selectElement === null || selectElement === void 0 ? void 0 : selectElement.options[selectElement.selectedIndex];
    },
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    onInputOther(event) {
      this.val = event.target.value;
      this.onChange();
    },
    onChange() {
      this.$emit('input', this.val);
      this.$emit('change', this.val);
    },
    onInputOtherEnter() {
      if (this.inputOtherVal) {
        this.showInputOther = false;
      }
    },
    onInputOtherBlur() {
      this.showInputOther = false;
      this.onBlur();
    },
    createOption(value) {
      return this.hasSimpleOptions ? value : {
        value,
        text: value
      };
    }
  }
};

/* script */
const __vue_script__$20 = script$20;

/* template */
var __vue_render__$1_ = function () {
  var _obj;
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-select field-group",
    class: Object.assign({}, _vm.fieldGroupClasses, {
      'field-group--readonly': _vm.readonly,
      'has-placeholder': _vm.hasPlaceholder
    }),
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "val",
      "data-testid": "bb-select"
    },
    on: {
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, [_vm.label ? _c('label', {
    attrs: {
      "for": _vm.computedId
    }
  }, [_vm.mandatory ? _c('span', {
    staticClass: "bb-select__mandatory-indicator"
  }, [_vm._v("\n      *\n    ")]) : _vm._e(), _vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "input-group",
    class: (_obj = {}, _obj["input-group--" + _vm.color] = _vm.color, _obj['input-group--medium'] = _vm.medium, _obj['input-group--disabled'] = _vm.disabled, _obj)
  }, [_vm.inputAttrs.type === 'checkbox' && _vm.showInputOther ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.inputOtherVal,
      expression: "inputOtherVal"
    }],
    ref: "input",
    staticClass: "bb-select__input-other",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "placeholder": _vm.inputOtherPlaceholder,
      "data-testid": "bb-select__input-other",
      "type": "checkbox"
    },
    domProps: {
      "checked": Array.isArray(_vm.inputOtherVal) ? _vm._i(_vm.inputOtherVal, null) > -1 : _vm.inputOtherVal
    },
    on: {
      "focus": _vm.onFocus,
      "blur": _vm.onInputOtherBlur,
      "input": _vm.onInputOther,
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        $event.preventDefault();
        return _vm.onInputOtherEnter.apply(null, arguments);
      },
      "change": function ($event) {
        var $$a = _vm.inputOtherVal,
          $$el = $event.target,
          $$c = $$el.checked ? true : false;
        if (Array.isArray($$a)) {
          var $$v = null,
            $$i = _vm._i($$a, $$v);
          if ($$el.checked) {
            $$i < 0 && (_vm.inputOtherVal = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.inputOtherVal = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.inputOtherVal = $$c;
        }
      }
    }
  }, 'input', _vm.inputAttrs, false)) : _vm.inputAttrs.type === 'radio' && _vm.showInputOther ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.inputOtherVal,
      expression: "inputOtherVal"
    }],
    ref: "input",
    staticClass: "bb-select__input-other",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "placeholder": _vm.inputOtherPlaceholder,
      "data-testid": "bb-select__input-other",
      "type": "radio"
    },
    domProps: {
      "checked": _vm._q(_vm.inputOtherVal, null)
    },
    on: {
      "focus": _vm.onFocus,
      "blur": _vm.onInputOtherBlur,
      "input": _vm.onInputOther,
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        $event.preventDefault();
        return _vm.onInputOtherEnter.apply(null, arguments);
      },
      "change": function ($event) {
        _vm.inputOtherVal = null;
      }
    }
  }, 'input', _vm.inputAttrs, false)) : _vm.showInputOther ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.inputOtherVal,
      expression: "inputOtherVal"
    }],
    ref: "input",
    staticClass: "bb-select__input-other",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "placeholder": _vm.inputOtherPlaceholder,
      "data-testid": "bb-select__input-other",
      "type": _vm.inputAttrs.type
    },
    domProps: {
      "value": _vm.inputOtherVal
    },
    on: {
      "focus": _vm.onFocus,
      "blur": _vm.onInputOtherBlur,
      "input": [function ($event) {
        if ($event.target.composing) {
          return;
        }
        _vm.inputOtherVal = $event.target.value;
      }, _vm.onInputOther],
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        $event.preventDefault();
        return _vm.onInputOtherEnter.apply(null, arguments);
      }
    }
  }, 'input', _vm.inputAttrs, false)) : [_c('select', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "select",
    staticClass: "bb-select__select",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "tabindex": _vm.tabindex,
      "disabled": _vm.disabled,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "data-testid": "bb-select__select"
    },
    on: {
      "change": [function ($event) {
        var $$selectedVal = Array.prototype.filter.call($event.target.options, function (o) {
          return o.selected;
        }).map(function (o) {
          var val = "_value" in o ? o._value : o.value;
          return val;
        });
        _vm.val = $event.target.multiple ? $$selectedVal : $$selectedVal[0];
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }],
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus
    }
  }, 'select', _vm.inputAttrs, false), [!_vm.noempty || _vm.placeholder ? _c('option', {
    attrs: {
      "value": "",
      "data-testid": "bb-select__option-placeholder"
    }
  }, [_vm._v("\n          " + _vm._s(_vm.placeholder) + "\n        ")]) : _vm._e(), _vm._v(" "), _vm._t("first"), _vm._v(" "), _vm._l(_vm.computedOptions, function (opt, index) {
    return _c('option', {
      key: index,
      attrs: {
        "disabled": opt.disabled ? true : null,
        "data-testid": "bb-select__option-" + index
      },
      domProps: {
        "value": opt.value
      }
    }, [_vm._v("\n          " + _vm._s(opt.text) + "\n        ")]);
  }), _vm._v(" "), _vm._t("default")], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-select__display-overlay",
    attrs: {
      "aria-hidden": "true"
    }
  }, [_vm._t("selectedOption", function () {
    return [_c('span', {
      staticClass: "overflow-ellipses"
    }, [_vm._v("\n            " + _vm._s(_vm.selectedOptionText || _vm.placeholder || _vm.label) + "\n          ")])];
  }, null, {
    value: _vm.val,
    text: _vm.selectedOptionText
  }), _vm._v(" "), _vm._t("suffix", null, null, {
    value: _vm.val
  })], 2), _vm._v(" "), _c('bb-icon', {
    staticClass: "select-input-arrow-icon",
    attrs: {
      "fill": _vm.color ? 'white' : 'gray-50',
      "name": "ui-menu-down",
      "size": "24"
    }
  })], _vm._v(" "), _c('div', {
    staticClass: "input-addon-wrapper"
  }, [_c('bb-icon', {
    staticClass: "validation-error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  })], 1), _vm._v(" "), _vm.helpActive ? _c('bb-tooltip', {
    attrs: {
      "is-in-input": ""
    }
  }, [_c('p', [_vm._t("tooltipText", function () {
    return [_vm._v("\n          " + _vm._s(_vm.helpText) + "\n        ")];
  })], 2)]) : _vm._e()], 2), _vm._v(" "), _vm.hasValidationError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.select,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', [_vm._v(_vm._s(_vm.firstError || _vm.getFirstError()))])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n    ")]) : _vm._e()] : _vm._e()], 2);
};
var __vue_staticRenderFns__$1_ = [];

/* style */
const __vue_inject_styles__$20 = function (inject) {
  if (!inject) return;
  inject("data-v-4ba630b6_0", {
    source: ".bb-select__input-other{padding-left:.25rem}.bb-select__select{position:absolute;top:0;left:0;height:100%;opacity:0!important}.bb-select__display-overlay{display:flex;align-items:center}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$20 = undefined;
/* module identifier */
const __vue_module_identifier__$20 = undefined;
/* functional template */
const __vue_is_functional_template__$20 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$20 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1_,
  staticRenderFns: __vue_staticRenderFns__$1_
}, __vue_inject_styles__$20, __vue_script__$20, __vue_scope_id__$20, __vue_is_functional_template__$20, __vue_module_identifier__$20, false, createInjector, undefined, undefined);

/**
 * A list of vertical buttons meant to be used as tabs in ABO Drawers.
 */
var script$1$ = Vue.extend({
  name: 'bb-side-tabs',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    /**
     * Array of tab objects | `[ { id: (String, Number), label: String, icon: String } ]`
     */
    tabs: {
      type: Array,
      required: true
    },
    /**
     * Active tab id
     */
    value: {
      type: [String, Number]
    }
  },
  data() {
    return {
      activeTab: this.value
    };
  },
  watch: {
    value() {
      this.activeTab = this.value;
    }
  },
  methods: {
    setActiveTab(tabId) {
      this.activeTab = tabId;
    },
    onClick(tabId) {
      this.setActiveTab(tabId);
      /**
       * A tab button is clicked, payload is `id`
       * @event input
       */
      this.$emit('input', tabId);
    }
  }
});

/* script */
const __vue_script__$1$ = script$1$;

/* template */
var __vue_render__$1Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('nav', {
    staticClass: "bb-side-tabs",
    attrs: {
      "data-testid": "bb-side-tabs"
    }
  }, _vm._l(_vm.tabs, function (tab) {
    return _c('button', {
      key: JSON.stringify(tab),
      class: ['bb-side-tabs__button', {
        'is-active': _vm.activeTab === tab.id
      }],
      attrs: {
        "data-testid": "bb-side-tabs__button",
        "type": "button"
      },
      on: {
        "click": function ($event) {
          return _vm.onClick(tab.id);
        }
      }
    }, [_c('bb-icon', {
      staticClass: "bb-side-tabs__button-icon",
      attrs: {
        "name": tab.icon,
        "size": "24"
      }
    }), _vm._v("\n    " + _vm._s(tab.label) + "\n  ")], 1);
  }), 0);
};
var __vue_staticRenderFns__$1Z = [];

/* style */
const __vue_inject_styles__$1$ = function (inject) {
  if (!inject) return;
  inject("data-v-35db120b_0", {
    source: ".bb-side-tabs{display:flex;max-width:6.875rem;flex-direction:column;justify-content:center;padding:.625rem;border:solid .0625rem #eeeff2;border-radius:.625rem 0 0 .625rem;background:#fff}.bb-side-tabs .bb-side-tabs__button{all:unset;display:flex;height:5rem;box-sizing:inherit;flex-direction:column;align-items:center;justify-content:center;border:solid .0625rem transparent;border-radius:.375rem;background:0 0;box-shadow:0 0 0 0 #eeeff2;color:#89a;cursor:pointer;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;line-height:1rem;text-align:center;transition:color .1s ease-out,background .1s ease-out,border .1s ease-out,box-shadow .2s ease-in-out;user-select:none}.bb-side-tabs .bb-side-tabs__button:hover{color:#2485e5}.bb-side-tabs .bb-side-tabs__button:focus{box-shadow:0 0 0 .125rem #eeeff2}.bb-side-tabs .bb-side-tabs__button.is-active{border-color:#add9ff;background:#e6f3ff;color:#2485e5}.bb-side-tabs .bb-side-tabs__button-icon{margin-bottom:.25rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1$ = undefined;
/* module identifier */
const __vue_module_identifier__$1$ = undefined;
/* functional template */
const __vue_is_functional_template__$1$ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1$ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1Z,
  staticRenderFns: __vue_staticRenderFns__$1Z
}, __vue_inject_styles__$1$, __vue_script__$1$, __vue_scope_id__$1$, __vue_is_functional_template__$1$, __vue_module_identifier__$1$, false, createInjector, undefined, undefined);

var script$1_ = {
  name: 'bb-slotted-paragraph',
  props: {
    templateMarker: {
      type: String,
      default: '##'
    },
    template: {
      type: String,
      required: true
    }
  },
  computed: {
    textElements() {
      return this.template.split(this.templateMarker);
    }
  }
};

/* script */
const __vue_script__$1_ = script$1_;

/* template */
var __vue_render__$1Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('p', {
    staticClass: "bb-slotted-paragraph"
  }, [_vm._l(_vm.textElements, function (text, indexElement) {
    return [indexElement % 2 === 0 ? [_vm._v(_vm._s(text))] : _vm._t("default", null, {
      "text": text,
      "index": Math.floor(indexElement / 2)
    })];
  })], 2);
};
var __vue_staticRenderFns__$1Y = [];

/* style */
const __vue_inject_styles__$1_ = undefined;
/* scoped */
const __vue_scope_id__$1_ = undefined;
/* module identifier */
const __vue_module_identifier__$1_ = undefined;
/* functional template */
const __vue_is_functional_template__$1_ = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1_ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1Y,
  staticRenderFns: __vue_staticRenderFns__$1Y
}, __vue_inject_styles__$1_, __vue_script__$1_, __vue_scope_id__$1_, __vue_is_functional_template__$1_, __vue_module_identifier__$1_, false, undefined, undefined, undefined);

var script$1Z = {
  name: 'bb-social-links',
  components: {
    BbLink: __vue_component__$2p,
    BbIcon: __vue_component__$2C,
    BbSeparator: __vue_component__$2a
  },
  props: {
    links: {
      type: Array,
      default: () => []
    },
    iconColor: {
      type: String,
      default: 'gray-70',
      validator: value => ['gray-70', 'gray-80'].includes(value)
    },
    noSeparators: Boolean
  },
  computed: {
    classes() {
      return ['bb-social-links', `bb-social-links--color-${this.iconColor}`, {
        'bb-social-links--no-separators': this.noSeparators
      }];
    }
  }
};

/* script */
const __vue_script__$1Z = script$1Z;

/* template */
var __vue_render__$1X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm._l(_vm.links, function (link, index) {
    return [!_vm.noSeparators && index > 0 ? _c('bb-separator', {
      key: "separator-" + index,
      staticClass: "bb-social-links__separator",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e(), _vm._v(" "), _c('bb-link', _vm._b({
      key: "link-" + index,
      staticClass: "bb-social-links__link",
      attrs: {
        "to": link.to,
        "target": "_blank",
        "color": "gray",
        "no-underline": ""
      }
    }, 'bb-link', link, false), [_c('bb-icon', {
      staticClass: "bb-social-links__icon",
      attrs: {
        "name": link.icon,
        "size": "24",
        "aria-hidden": "true"
      }
    })], 1)];
  })], 2);
};
var __vue_staticRenderFns__$1X = [];

/* style */
const __vue_inject_styles__$1Z = function (inject) {
  if (!inject) return;
  inject("data-v-3576b430_0", {
    source: ".bb-social-links{display:flex}.bb-social-links--color-gray-70.bb-social-links .bb-social-links__link{color:#89a}.bb-social-links--color-gray-80.bb-social-links .bb-social-links__link{color:#687d92}.bb-social-links .bb-social-links__link:not(:first-child){margin-left:.9375rem}.bb-social-links--no-separators.bb-social-links .bb-social-links__link:not(:first-child){margin-left:.625rem}.bb-social-links .bb-social-links__link:not(:last-child){margin-right:.9375rem}.bb-social-links--no-separators.bb-social-links .bb-social-links__link:not(:last-child){margin-right:.625rem}.bb-social-links .bb-social-links__separator{border-color:#dee3e8}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1Z = undefined;
/* module identifier */
const __vue_module_identifier__$1Z = undefined;
/* functional template */
const __vue_is_functional_template__$1Z = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1Z = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1X,
  staticRenderFns: __vue_staticRenderFns__$1X
}, __vue_inject_styles__$1Z, __vue_script__$1Z, __vue_scope_id__$1Z, __vue_is_functional_template__$1Z, __vue_module_identifier__$1Z, false, createInjector, undefined, undefined);

var script$1Y = {
  name: 'bb-step-list',
  props: {
    steps: {
      type: Array,
      required: true
    },
    active: {
      type: Number,
      required: true
    },
    ariaLabelPartials: {
      type: Object,
      default: () => ({
        completed: 'Completed',
        incomplete: 'Incomplete'
      })
    }
  },
  data() {
    return {};
  },
  methods: {
    stepClassNames(index) {
      return ['bb-step-list__step', index === this.active ? 'is-active' : index > this.active ? 'is-inactive' : ''];
    },
    completeAriaLabel(step, index) {
      const completionInfo = index <= this.active ? this.ariaLabelPartials.completed : this.ariaLabelPartials.incomplete;
      const contentText = typeof step === 'string' ? step : `${step.title}: ${step.description}`;
      return `${completionInfo}, ${contentText}`;
    }
  }
};

/* script */
const __vue_script__$1Y = script$1Y;

/* template */
var __vue_render__$1W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ul', {
    staticClass: "bb-step-list"
  }, _vm._l(_vm.steps, function (step, index) {
    return _c('li', {
      key: index,
      class: _vm.stepClassNames(index),
      attrs: {
        "aria-current": _vm.active === index ? 'step' : undefined,
        "role": "progressbar",
        "aria-label": _vm.completeAriaLabel(step, index)
      }
    }, [_c('div', {
      staticClass: "bb-step-list__dot"
    }), _vm._v(" "), _c('div', {
      staticClass: "bb-step-list__stripe"
    }), _vm._v(" "), _c('div', {
      staticClass: "bb-step-list__text"
    }, [typeof step === 'string' ? _c('div', {
      staticClass: "bb-step-list__plaintext"
    }, [_vm._v("\n        " + _vm._s(step) + "\n      ")]) : [_c('div', {
      staticClass: "bb-step-list__title"
    }, [_vm._v("\n          " + _vm._s(step.title) + "\n        ")]), _vm._v(" "), _c('div', {
      class: ['bb-step-list__description', {
        'is-ok': step.done
      }]
    }, [_vm._v("\n          " + _vm._s(step.description) + "\n        ")])]], 2)]);
  }), 0);
};
var __vue_staticRenderFns__$1W = [];

/* style */
const __vue_inject_styles__$1Y = function (inject) {
  if (!inject) return;
  inject("data-v-b09ab694_0", {
    source: ".bb-step-list{margin:1.25rem 0}.bb-step-list__step{display:flex}.bb-step-list__dot{z-index:1100;display:block;width:.625rem;height:.625rem;box-sizing:border-box;border:.125rem solid #2485e5;border-radius:50%;margin-right:1.25rem;background-color:#fff;transform:translate(0,.1875rem)}.bb-step-list__stripe{border-left:.125rem solid #2485e5;transform:translate(-1.625rem,.3125rem)}.bb-step-list__text{display:flex;min-height:2.1875rem;flex:1 0 0;flex-direction:column;padding-bottom:.3125rem;color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}.bb-step-list__text .bb-step-list__description{color:#9dabb9;font-size:.6875rem}.bb-step-list__text .bb-step-list__description.is-ok{color:#0ccca2}.bb-step-list .is-inactive .bb-step-list__dot{border-color:#dee3e8}.bb-step-list .is-inactive .bb-step-list__text{color:#9dabb9}.bb-step-list .is-inactive .bb-step-list__stripe{border-style:dashed;border-color:#dee3e8}.bb-step-list .is-active .bb-step-list__dot{box-shadow:0 0 .1875rem .1875rem #add9ff}.bb-step-list .is-active .bb-step-list__stripe{border-style:dashed;border-color:#dee3e8}.bb-step-list__step:last-child .bb-step-list__stripe{border:none}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1Y = undefined;
/* module identifier */
const __vue_module_identifier__$1Y = undefined;
/* functional template */
const __vue_is_functional_template__$1Y = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1Y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1W,
  staticRenderFns: __vue_staticRenderFns__$1W
}, __vue_inject_styles__$1Y, __vue_script__$1Y, __vue_scope_id__$1Y, __vue_is_functional_template__$1Y, __vue_module_identifier__$1Y, false, createInjector, undefined, undefined);

var script$1X = {
  name: 'bb-steps-overview'
};

/* script */
const __vue_script__$1X = script$1X;

/* template */
var __vue_render__$1V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-steps-overview",
    attrs: {
      "role": "list"
    }
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$1V = [];

/* style */
const __vue_inject_styles__$1X = function (inject) {
  if (!inject) return;
  inject("data-v-7ff03ac2_0", {
    source: ".bb-steps-overview{display:flex;max-width:28.75rem;flex-direction:row;justify-content:space-around;padding-right:1rem;padding-left:1rem;margin:0 auto}.bb-steps-overview .bb-step-icon{margin:.9375rem 0}@media (min-width:768px){.bb-steps-overview .bb-step-icon{margin:1.25rem 0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1X = undefined;
/* module identifier */
const __vue_module_identifier__$1X = undefined;
/* functional template */
const __vue_is_functional_template__$1X = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1X = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1V,
  staticRenderFns: __vue_staticRenderFns__$1V
}, __vue_inject_styles__$1X, __vue_script__$1X, __vue_scope_id__$1X, __vue_is_functional_template__$1X, __vue_module_identifier__$1X, false, createInjector, undefined, undefined);

var script$1W = {
  name: 'bb-sticky',
  directives: {
    resize
  },
  props: {
    contained: Boolean,
    bleedMobileOnly: Boolean,
    mobileOnly: Boolean,
    topElement: {
      type: null,
      validator: deprecated('Prop `topElement` will be removed from BbSticky in the next major version of IC.')
    }
  },
  emits: ['stuck'],
  data() {
    return {
      isStuck: false,
      stickyWrapperHeight: '',
      intersectionObserver: null
    };
  },
  computed: {
    classes() {
      return {
        'bb-sticky--sticky': this.isStuck,
        'bb-sticky--contained': this.contained,
        'bb-sticky--mobile-only': this.mobileOnly,
        'bb-sticky--bleed-mobile-only': this.bleedMobileOnly
      };
    }
  },
  watch: {
    isStuck(stickyState) {
      this.$emit('stuck', stickyState);
    }
  },
  mounted() {
    this.updateStickyWrapperHeight();
    this.addIntersectionListener();
  },
  destroyed() {
    this.removeIntersectionListener();
  },
  methods: {
    addIntersectionListener() {
      const options = {
        threshold: 1
      };
      this.intersectionObserver = new IntersectionObserver(this.decideStickiness, options);
      this.intersectionObserver.observe(this.$el);
    },
    removeIntersectionListener() {
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(this.$el);
      }
    },
    decideStickiness([{
      boundingClientRect,
      isIntersecting
    }]) {
      if (!this.stickyWrapperHeight) this.updateStickyWrapperHeight();
      if (boundingClientRect.top > 0) {
        this.isStuck = !isIntersecting;
      }
    },
    onStickyAreaResize: throttle(function () {
      this.updateStickyWrapperHeight();
    }, 100),
    updateStickyWrapperHeight() {
      var _this$$refs$stickyAre;
      this.stickyWrapperHeight = ((_this$$refs$stickyAre = this.$refs.stickyArea) === null || _this$$refs$stickyAre === void 0 ? void 0 : _this$$refs$stickyAre.clientHeight) || this.stickyWrapperHeight;
    }
  }
};

/* script */
const __vue_script__$1W = script$1W;

/* template */
var __vue_render__$1U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "sticky",
    staticClass: "bb-sticky",
    class: _vm.classes,
    style: {
      height: _vm.stickyWrapperHeight + "px"
    }
  }, [_c('div', {
    directives: [{
      name: "resize",
      rawName: "v-resize",
      value: _vm.onStickyAreaResize,
      expression: "onStickyAreaResize"
    }],
    ref: "stickyArea",
    staticClass: "bb-sticky__container"
  }, [_vm._t("default")], 2)]);
};
var __vue_staticRenderFns__$1U = [];

/* style */
const __vue_inject_styles__$1W = function (inject) {
  if (!inject) return;
  inject("data-v-0f4c120c_0", {
    source: ".bb-sticky--bleed-mobile-only{margin:1.875rem 0}@media (min-width:768px){.bb-sticky--bleed-mobile-only{margin:0}}.bb-sticky__container{position:absolute;right:0;left:0;padding:1.875rem 0;transition:background-color .2s,box-shadow .2s}.bb-sticky--sticky .bb-sticky__container{position:fixed;z-index:1300;bottom:0;padding:1.875rem;background-color:#fff;box-shadow:0 -.4375rem .875rem 0 rgba(56,65,77,.07)}.bb-sticky--bleed-mobile-only .bb-sticky__container{padding:0}@media (min-width:768px){.bb-sticky--bleed-mobile-only .bb-sticky__container{padding:1.875rem 0}.bb-modal .bb-sticky--bleed-mobile-only .bb-sticky__container{padding:0}}@media (min-width:768px){.bb-sticky--sticky.bb-sticky--bleed-mobile-only .bb-sticky__container{padding:1.875rem}}@media (min-width:768px){.bb-sticky--mobile-only .bb-sticky__container{position:absolute;z-index:initial;bottom:initial;background-color:initial;box-shadow:initial}}.bb-sticky--contained .bb-sticky__container{bottom:0;background-color:#fff}.bb-sticky--sticky.bb-sticky--contained .bb-sticky__container{position:absolute;padding:1.875rem 0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1W = undefined;
/* module identifier */
const __vue_module_identifier__$1W = undefined;
/* functional template */
const __vue_is_functional_template__$1W = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1W = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1U,
  staticRenderFns: __vue_staticRenderFns__$1U
}, __vue_inject_styles__$1W, __vue_script__$1W, __vue_scope_id__$1W, __vue_is_functional_template__$1W, __vue_module_identifier__$1W, false, createInjector, undefined, undefined);

var Icon$N = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('rect',{attrs:{"width":"16","height":"16","x":"4","y":"4","fill":"#fff"}}),_c('path',{staticClass:"svg3073746098__a bb-icon__dynamic-fill",attrs:{"d":"M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-8-2h2v-4h4v-2h-4V7h-2v4H7v2h4z"}})]) } };

Vue.component('icon-ui-plus-box-outline', Icon$N);

var Icon$M = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('rect',{attrs:{"width":"16","height":"16","x":"4","y":"4","fill":"#fff"}}),_c('path',{staticClass:"svg3990858788__a bb-icon__dynamic-fill",attrs:{"d":"M19,19V5H5V19H19M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5C3,3.89 3.9,3 5,3H19M17,11V13H7V11H17Z"}})]) } };

Vue.component('icon-ui-minus-box-outline', Icon$M);

const DEFAULT_WRAPPED_ROW_NAME = 'DEFAULT_WRAPPED_ROW';
const unique = (v, i, a) => a.indexOf(v) === i;
const SortingDirection = Object.freeze({
  asc: 'asc',
  desc: 'desc'
});
var script$1V = {
  name: 'bb-table',
  components: {
    BbIcon: __vue_component__$2C,
    BbProgressDots: __vue_component__$25
  },
  mixins: [validator],
  props: {
    data: Array,
    columns: Array,
    subtableProps: Object,
    subtableRowCount: Boolean,
    subtableSlots: Array,
    rowPropName: String,
    indexColumn: Boolean,
    startIndex: {
      type: Number,
      default: 1
    },
    striped: Boolean,
    sortingType: {
      type: String,
      validator: type => ['frontend', 'backend'].includes(type),
      default: 'frontend'
    },
    allSortable: Boolean,
    backendSortingDirection: {
      type: String,
      validator: type => ['', 'asc', 'desc'].includes(type)
    },
    backendSortableColumnKey: {
      type: String
    },
    condensed: Boolean,
    hoverable: Boolean,
    color: String,
    customSort: Function,
    fixed: {
      type: Object,
      default: () => ({
        head: false,
        footer: false,
        firstColumn: false,
        lastColumn: false,
        lastRow: false
      })
    },
    headUnderline: Boolean,
    trimmed: Boolean,
    dashed: Boolean,
    rowActionsNoBackground: Boolean,
    narrowCellPadding: Boolean,
    disableOverflow: Boolean,
    disableTableHead: Boolean,
    progressDotsProps: Object
  },
  emits: ['sortClick'],
  data() {
    return {
      table: this.data || [],
      sortBy: false,
      indexSortMap: {},
      overflow: {
        left: false,
        right: false,
        top: false,
        bottom: false
      },
      headerHeight: 0,
      footerHeight: 0,
      scrollIndicator: 0,
      isSafari: false,
      rowTemplates: {},
      validDirections: Object.values(SortingDirection)
    };
  },
  computed: {
    headWrappedRows() {
      return this.wrappedRowNames.map(wrappedRowName => this.parsedLabels.filter(label => label.row === wrappedRowName && !label.hideLabel)).filter(notEmpty);
    },
    parsedLabelsByRow() {
      return Object.fromEntries(this.wrappedRowNames.map(wrappedRowName => [wrappedRowName, this.parsedLabels.filter(label => label.row === wrappedRowName)]));
    },
    parsedLabelsKeysByRow() {
      return mapValues(this.parsedLabelsByRow, labels => labels.map(({
        key
      }) => key));
    },
    wrappedRows() {
      return this.table.map(row => this.wrappedRowNames.filter(wrappedRowName => this.parsedLabelsKeysByRow[wrappedRowName].some(key => notEmpty(row[key]))).map(wrappedRowName => ({
        wrappedRowName,
        data: pick(row, this.parsedLabelsKeysByRow[wrappedRowName])
      })));
    },
    hasSubtable() {
      return this.subtableProps !== undefined;
    },
    initiallySortedColumn() {
      return this.parsedLabels.find(col => this.validDirections.includes(col.sortingDirection));
    },
    isInitiallySorted() {
      return this.initiallySortedColumn !== undefined;
    },
    firstRowKeys() {
      return Object.keys(this.table[0] || {});
    },
    parsedLabels() {
      if (this.columns) {
        return this.columns.filter(col => (col === null || col === void 0 ? void 0 : col.key) && !col.hidden).map(col => ({
          label: col.label || col.key,
          key: col.key,
          sortable: col.sortable || false,
          sortingDirection: col.sortingDirection,
          hideLabel: col.hideLabel || false,
          colspan: col.colspan,
          row: col[this.rowPropName] || DEFAULT_WRAPPED_ROW_NAME,
          width: col.width || null,
          class: col.class || null,
          color: col.color || null
        }));
      } else {
        return this.firstRowKeys.map(key => ({
          label: key,
          key,
          row: DEFAULT_WRAPPED_ROW_NAME
        }));
      }
    },
    tableClasses() {
      const obj = {
        'bb-table--striped': this.striped,
        'bb-table--wrapped-rows': this.wrappedRowNames.length > 1,
        'bb-table--condensed': this.condensed,
        'bb-table--hoverable': this.hoverable,
        'bb-table--has-index': this.indexColumn,
        'bb-table--underline': this.headUnderline,
        'bb-table--has-subtable': this.hasSubtable,
        'bb-table--trimmed': this.trimmed,
        'bb-table--dashed': this.dashed,
        'bb-table--narrow-cell-padding': this.narrowCellPadding,
        'bb-table--disable-overflow': this.disableOverflow,
        'bb-table--disable-table-head': this.disableTableHead
      };
      if (this.color) {
        obj[`bb-table--${this.color}`] = true;
      }
      const overflowClasses = Object.keys(this.overflow).filter(item => this.overflow[item] === true).reduce((acc, cur) => {
        acc[`bb-table--overflow-${cur}`] = true;
        return acc;
      }, {});
      const fixedClasses = Object.keys(this.fixed).filter(item => this.fixed[item] === true).reduce((acc, cur) => {
        acc[`bb-table--fixed-${cur}`] = true;
        return acc;
      }, {});
      return {
        ...obj,
        ...fixedClasses,
        ...overflowClasses
      };
    },
    wrappedRowNames() {
      var _this$columns;
      const uniqueRowNames = (_this$columns = this.columns) === null || _this$columns === void 0 ? void 0 : _this$columns.filter(label => label.hasOwnProperty(this.rowPropName)).map(label => label[this.rowPropName]).filter(unique);
      return uniqueRowNames !== null && uniqueRowNames !== void 0 && uniqueRowNames.length ? uniqueRowNames : [DEFAULT_WRAPPED_ROW_NAME];
    },
    fixedLastRowStyles() {
      const bottom = this.fixed.footer * this.footerHeight;
      return bottom && this.fixed.lastRow ? {
        bottom: `${bottom}px`
      } : null;
    },
    hasWrappedRows() {
      return this.wrappedRowNames.length > 1;
    },
    rowActions() {
      return !!this.$scopedSlots.rowActions;
    },
    sortingOrder() {
      return this.sortingType === 'backend' ? this.backendSortingDirection : this.sortBy.order;
    },
    sortingKey() {
      return this.sortingType === 'backend' ? this.backendSortableColumnKey : this.sortBy.key;
    },
    lastIndexDigitCount() {
      return this.data.length.toString().length;
    }
  },
  watch: {
    data: {
      deep: true,
      immediate: true,
      handler() {
        this.table = this.data || [];
        this.setSubtablesInitiallyOpened();
      }
    }
  },
  mounted() {
    this.isSafari = ~navigator.userAgent.indexOf('Safari') && !~navigator.userAgent.indexOf('Chrome');
    this.updateHeaderHeight();
    this.updateFooterHeight();
    this.updateSafariFooterBottom();
    this.updateOverflowState(this.$refs.table);
    if (this.isInitiallySorted) {
      this.frontendSort(this.initiallySortedColumn, this.initiallySortedColumn.sortingDirection);
    }
  },
  updated() {
    this.updateHeaderHeight();
    this.updateFooterHeight();
    this.updateSafariFooterBottom();
    this.updateOverflowState(this.$refs.table);
  },
  methods: {
    setSubtablesInitiallyOpened() {
      this.table.forEach((row, rowIndex) => {
        if (row.expanded) {
          this.toggleSubtable(rowIndex);
        }
      });
    },
    isSubtableExpanded(rowIndex) {
      var _this$rowTemplates$ro;
      return (_this$rowTemplates$ro = this.rowTemplates[rowIndex]) === null || _this$rowTemplates$ro === void 0 ? void 0 : _this$rowTemplates$ro.subtableExpanded;
    },
    updateIndexSortMap(labelBeingSorted) {
      this.indexSortMap = this.sort(this.table.map((row, oldIndex) => (
      {
        [labelBeingSorted.key]: row[labelBeingSorted.key],
        oldIndex
      })), labelBeingSorted.key, this.sortBy.order).reduce((indexSortMap, row, newIndex) => {
        indexSortMap[row.oldIndex] = newIndex;
        return indexSortMap;
      }, {});
    },
    reorderRowTemplates() {
      this.rowTemplates = Object.keys(this.rowTemplates).reduce((result, oldIndex) => {
        const newIndex = this.indexSortMap[oldIndex];
        result[newIndex] = this.rowTemplates[oldIndex];
        return result;
      }, {});
    },
    isLastRow(rowIndex) {
      return this.table.length - 1 === rowIndex;
    },
    isLastWrappedRow(rowIndex) {
      return this.wrappedRows.length - 1 === rowIndex;
    },
    getStylesForFixedLastRow(rowIndex) {
      const isLastRow = this.hasWrappedRows ? this.isLastWrappedRow(rowIndex) : this.isLastRow(rowIndex);
      return isLastRow ? this.fixedLastRowStyles : null;
    },
    updateHeaderHeight() {
      this.headerHeight = this.$refs.thead && this.$refs.thead.clientHeight ? this.$refs.thead.clientHeight : 0;
    },
    updateFooterHeight() {
      this.footerHeight = this.$refs.tfoot && this.$refs.tfoot.clientHeight ? this.$refs.tfoot.clientHeight : 0;
    },
    updateSafariFooterBottom() {
      if (this.isSafari && this.fixed.footer && this.$slots.footer) {
        this.$slots.footer.forEach(vnode => vnode.elm.style.bottom = `${this.headerHeight}px`);
      }
    },
    wrappedHeadRowTop(wrappedRowIndex) {
      if (!this.fixed.head || !wrappedRowIndex) {
        return;
      }
      return {
        top: `${this.headerHeight / this.wrappedRowNames.length * wrappedRowIndex}px`
      };
    },
    sort(data, key, order = 'asc') {
      if (this.customSort) {
        return this.customSort(data, key, order);
      } else {
        return order === 'desc' ? sortBy(data, [key]).reverse() : sortBy(data, [key]);
      }
    },
    onSortClick(label) {
      if (!this.isColumnSortable(label)) {
        return;
      }
      if (this.sortingType === 'backend') {
        this.$emit('sortClick', label);
      } else {
        this.frontendSort(label);
      }
    },
    columnSpecifiedFrontendSort(label) {
      return sortDirection => this.frontendSort(label, sortDirection);
    },
    frontendSort(column, sortingDirection) {
      this.updateSortBy(column, sortingDirection);
      this.handleRowTemplates(column);
      this.sortTable(column);
      this.handleRowTemplateOrder();
    },
    updateSortBy(column, sortingDirection) {
      this.sortBy = {
        key: column.key,
        order: sortingDirection ? sortingDirection : this.isColumnSortedAsc(column) ? 'desc' : 'asc'
      };
    },
    handleRowTemplates(column) {
      if (isNonEmptyObject(this.rowTemplates)) {
        this.updateIndexSortMap(column);
      }
    },
    sortTable(column) {
      this.table = this.sort(this.table, column.key, this.sortBy.order);
    },
    handleRowTemplateOrder() {
      if (isNonEmptyObject(this.rowTemplates)) {
        this.reorderRowTemplates();
      }
    },
    colHeaderClasses(label) {
      return ['col-header', {
        'col-header--sortable': this.isColumnSortable(label),
        'col-header--sort-up': this.isColumnSortedAsc(label),
        'col-header--sort-down': this.isColumnSortedDesc(label)
      }];
    },
    isColumnSortable(label) {
      return this.sortingType && (label.sortable || this.allSortable);
    },
    isColumnSortedAsc(label) {
      return this.sortingKey === label.key && this.sortingOrder === 'asc';
    },
    isColumnSortedDesc(label) {
      return this.sortingKey === label.key && this.sortingOrder === 'desc';
    },
    updateOverflowState(target) {
      this.overflow.left = target.scrollLeft > 0;
      this.overflow.top = target.scrollTop > 0;
      this.overflow.right = target.scrollLeft < target.scrollWidth - target.offsetWidth;
      this.overflow.bottom = target.scrollTop < target.scrollHeight - target.offsetHeight;
    },
    listenScroll: throttle(function (event) {
      const tableActualWidth = event.target.scrollWidth;
      const tableVisibleWidth = event.target.clientWidth;
      const tableScrolledWidth = event.target.scrollLeft;
      if (tableScrolledWidth >= tableActualWidth - tableVisibleWidth) {
        this.scrollIndicator = 2;
      } else if (tableScrolledWidth >= (tableActualWidth - tableVisibleWidth) / 3) {
        this.scrollIndicator = 1;
      } else {
        this.scrollIndicator = 0;
      }
      this.updateOverflowState(event.target);
    }, 100),
    cellColorClass(rowIndex, columnColor) {
      const className = 'cell-background--';
      const rowTemplate = this.rowTemplates[rowIndex];
      const rowColorsWhitelist = ['red', 'mint', 'green',
      'yellow'];
      const columnColorsWhitelist = ['red', 'mint', 'green',
      'blue', 'gray'];
      if (rowTemplate && rowTemplate.color && rowColorsWhitelist.includes(rowTemplate.color)) {
        return `${className}${rowTemplate.color}`;
      } else if (columnColorsWhitelist.includes(columnColor)) {
        return `${className}${columnColor.replace(/blue/, 'lightblue')}`;
      }
    },
    formatRowIndex(rowIndex) {
      let displayIndex = (rowIndex + this.startIndex).toString();
      while (displayIndex.length < this.lastIndexDigitCount) {
        displayIndex = `0${displayIndex}`;
      }
      return displayIndex;
    },
    setRowColor(rowIndex, rowColor) {
      this.setRowTemplate(rowIndex, 'color', rowColor);
    },
    toggleSubtable(rowIndex) {
      this.setRowTemplate(rowIndex, 'subtableExpanded', !this.isSubtableExpanded(rowIndex));
    },
    setRowTemplate(rowIndex, prop, val) {
      if (this.rowTemplates[rowIndex]) {
        this.$set(this.rowTemplates[rowIndex], prop, val);
      } else {
        this.$set(this.rowTemplates, rowIndex, {
          [prop]: val
        });
      }
    }
  }
};

/* script */
const __vue_script__$1V = script$1V;

/* template */
var __vue_render__$1T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-table",
    class: _vm.tableClasses,
    attrs: {
      "data-testid": "bb-table"
    }
  }, [!_vm.disableOverflow ? _c('bb-progress-dots', _vm._b({
    staticClass: "bb-table__scrolling-indicator",
    attrs: {
      "count": 3,
      "active-index": _vm.scrollIndicator
    }
  }, 'bb-progress-dots', _vm.progressDotsProps, false)) : _vm._e(), _vm._v(" "), _c('div', {
    class: {
      'bb-table__overflow-shadows': !_vm.disableOverflow
    }
  }, [_vm.$slots.settings ? _c('span', {
    staticClass: "bb-table__settings",
    attrs: {
      "data-testid": "bb-table__settings"
    }
  }, [_vm._t("settings")], 2) : _vm._e(), _vm._v(" "), _c('div', {
    ref: "table",
    staticClass: "bb-table__scroll-wrapper",
    on: {
      "scroll": _vm.listenScroll
    }
  }, [_c('table', {
    staticClass: "bb-table__content"
  }, [!_vm.disableTableHead && _vm.headWrappedRows.length ? _c('thead', {
    ref: "thead"
  }, _vm._l(_vm.headWrappedRows, function (labels, headWrappedRowIndex) {
    return _c('tr', {
      key: "head-row-" + headWrappedRowIndex
    }, [_vm.indexColumn ? _c('th', {
      staticClass: "col-header--index",
      style: _vm.wrappedHeadRowTop(headWrappedRowIndex)
    }) : _vm._e(), _vm._v(" "), _vm.hasSubtable ? _c('th', {
      staticClass: "col-header--groupedRows-meta"
    }) : _vm._e(), _vm._v(" "), _vm._l(labels, function (label) {
      return _c('th', {
        key: "head-row-" + headWrappedRowIndex + "_" + label.key,
        class: [_vm.colHeaderClasses(label), label.class],
        style: [{
          width: label.width,
          minWidth: label.width
        }, _vm.wrappedHeadRowTop(headWrappedRowIndex)],
        attrs: {
          "data-testid": "bb-table__content--head-row-" + headWrappedRowIndex + "_" + label.key
        },
        on: {
          "click": function ($event) {
            return _vm.onSortClick(label);
          }
        }
      }, [_vm.isColumnSortable(label) ? _c('span', {
        staticClass: "col-header__sorting-icons"
      }, [_c('bb-icon', {
        staticClass: "col-header__icon-sort-up",
        attrs: {
          "name": "ui-menu-down",
          "size": "18"
        }
      }), _vm._v(" "), _c('bb-icon', {
        staticClass: "col-header__icon-sort-down",
        attrs: {
          "name": "ui-menu-down",
          "size": "18"
        }
      })], 1) : _vm._e(), _vm._v(" "), _vm._t("headerCell", function () {
        return [_vm._v("\n                " + _vm._s(label.label) + "\n              ")];
      }, {
        "sortingMethod": _vm.columnSpecifiedFrontendSort(label),
        "sortingType": _vm.sortingType,
        "column": label
      })], 2);
    }), _vm._v(" "), _vm.rowActions && !_vm.rowActionsNoBackground ? _c('th', {
      staticClass: "col-header--row-actions",
      style: _vm.wrappedHeadRowTop(headWrappedRowIndex)
    }) : _vm._e()], 2);
  }), 0) : _vm._e(), _vm._v(" "), _vm.hasWrappedRows ? [_vm._l(_vm.wrappedRows, function (rows, wrappedRowIndex) {
    return _c('tbody', {
      key: "wrapped-row-" + wrappedRowIndex,
      class: {
        'bb-table__last-row': _vm.isLastWrappedRow(wrappedRowIndex)
      },
      style: _vm.getStylesForFixedLastRow(wrappedRowIndex)
    }, _vm._l(rows, function (row, rowIndex) {
      return _c('tr', {
        key: "wrapped-row-" + wrappedRowIndex + "_" + rowIndex
      }, [_vm.indexColumn ? _c('td', {
        staticClass: "bb-table__row-index",
        attrs: {
          "data-testid": "bb-table__row-index"
        }
      }, [_vm._v("\n                " + _vm._s(_vm.formatRowIndex(wrappedRowIndex)) + "\n              ")]) : _vm._e(), _vm._v(" "), _vm._l(_vm.parsedLabelsByRow[row.wrappedRowName], function (label) {
        return _c('td', {
          key: "wrapped-row-" + wrappedRowIndex + "_" + rowIndex + "_" + label.key,
          class: [_vm.cellColorClass(wrappedRowIndex, label.color), label.class],
          attrs: {
            "data-testid": "bb-table__row--wrapped-row-" + wrappedRowIndex + "_" + rowIndex + "_" + label.key,
            "colspan": label.colspan
          }
        }, [_vm._t(label.key + "Column", function () {
          return [_vm._v("\n                  " + _vm._s(row.data[label.key]) + "\n                ")];
        }, {
          "label": label.label,
          "labelKey": label.key,
          "value": row.data[label.key],
          "row": row,
          "rowIndex": wrappedRowIndex,
          "setRowColor": _vm.setRowColor
        })], 2);
      }), _vm._v(" "), _vm.rowActions ? _c('td', {
        staticClass: "bb-table__row-actions",
        class: {
          'bb-table__row-actions--no-background': _vm.rowActionsNoBackground
        },
        attrs: {
          "data-testid": "bb-table__row-actions"
        }
      }, [rowIndex === 0 ? _vm._t("rowActions", null, {
        "rowData": row,
        "rowIndex": rowIndex
      }) : _vm._e()], 2) : _vm._e()], 2);
    }), 0);
  }), _vm._v(" "), _c('tbody', [_c('tr', {
    ref: "tfoot",
    staticClass: "tfoot"
  }, [_vm._t("footer")], 2)])] : _vm.hasSubtable ? [_vm._l(_vm.table, function (row, rowIndex) {
    return _c('tbody', {
      key: "tbody-" + rowIndex,
      staticClass: "bb-table__groupedRows",
      class: [{
        'bb-table__last-row': _vm.isLastRow(rowIndex),
        'is-expanded': _vm.isSubtableExpanded(rowIndex)
      }]
    }, [_c('tr', {
      class: {
        'table-group__head': row.subtable
      }
    }, [_vm.indexColumn ? _c('td', {
      staticClass: "bb-table__row-index",
      style: _vm.getStylesForFixedLastRow(rowIndex)
    }, [_vm._v("\n                " + _vm._s(_vm.formatRowIndex(rowIndex)) + "\n              ")]) : _vm._e(), _vm._v(" "), _c('td', {
      staticClass: "bb-table__groupedRows-meta-cell",
      style: _vm.getStylesForFixedLastRow(rowIndex)
    }, [row.subtable ? [_c('span', {
      staticClass: "bb-table__groupedRows-toggle",
      attrs: {
        "data-testid": "bb-table__groupedRows-toggle"
      },
      on: {
        "click": function ($event) {
          return _vm.toggleSubtable(rowIndex);
        }
      }
    }, [_c('bb-icon', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.isSubtableExpanded(rowIndex),
        expression: "isSubtableExpanded(rowIndex)"
      }],
      attrs: {
        "name": "ui-minus-box-outline",
        "fill": "gray-70",
        "size": "24"
      }
    }), _vm._v(" "), _c('bb-icon', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: !_vm.isSubtableExpanded(rowIndex),
        expression: "!isSubtableExpanded(rowIndex)"
      }],
      attrs: {
        "name": "ui-plus-box-outline",
        "fill": "gray-70",
        "size": "24"
      }
    })], 1), _vm._v(" "), _vm.subtableRowCount ? _c('span', {
      staticClass: "bb-table__groupedRows-subtableRowCount"
    }, [_vm._v("\n                    (" + _vm._s(row.subtable.length) + ")\n                  ")]) : _vm._e()] : _vm._e()], 2), _vm._v(" "), _vm._l(_vm.parsedLabels, function (label) {
      return _c('td', {
        key: "tbody-" + rowIndex + "_" + label.key,
        class: [_vm.cellColorClass(rowIndex, label.color), label.class],
        style: _vm.getStylesForFixedLastRow(rowIndex)
      }, [_vm._t(label.key + "Column", function () {
        return [_vm._v("\n                  " + _vm._s(row[label.key]) + "\n                ")];
      }, {
        "label": label.label,
        "labelKey": label.key,
        "value": row[label.key],
        "row": row,
        "rowIndex": rowIndex,
        "setRowColor": _vm.setRowColor
      })], 2);
    }), _vm._v(" "), _vm.rowActions ? _c('td', {
      staticClass: "bb-table__row-actions",
      class: {
        'bb-table__row-actions--no-background': _vm.rowActionsNoBackground
      },
      style: _vm.getStylesForFixedLastRow(rowIndex)
    }, [_vm._t("rowActions", null, {
      "rowData": row,
      "rowIndex": rowIndex
    })], 2) : _vm._e()], 2), _vm._v(" "), row.subtable ? _c('tr', {
      staticClass: "bb-table__groupedRows-subrow"
    }, [_c('td', {
      attrs: {
        "colspan": _vm.parsedLabels.length + 1
      }
    }, [_c('bb-table', _vm._b({
      staticClass: "bb-table--subtable",
      attrs: {
        "data": row.subtable,
        "color": _vm.subtableProps.color || 'mint'
      },
      scopedSlots: _vm._u([_vm._l(_vm.subtableSlots, function (slotName) {
        return {
          key: slotName,
          fn: function (subtableScope) {
            return [_vm._t(slotName, null, {
              "label": subtableScope.label,
              "labelKey": subtableScope.labelKey,
              "value": subtableScope.row[subtableScope.labelKey],
              "row": subtableScope.row,
              "rowIndex": subtableScope.rowIndex,
              "setRowColor": subtableScope.setRowColor
            })];
          }
        };
      })], null, true)
    }, 'bb-table', _vm.subtableProps, false))], 1)]) : _vm._e()]);
  }), _vm._v(" "), _c('tbody', [_c('tr', {
    ref: "tfoot",
    staticClass: "tfoot"
  }, [_vm._t("footer")], 2)])] : _c('tbody', [_vm._l(_vm.table, function (row, rowIndex) {
    return _c('tr', {
      key: "row-" + rowIndex,
      class: {
        'bb-table__last-row': _vm.isLastRow(rowIndex)
      }
    }, [_vm.indexColumn ? _c('td', {
      staticClass: "bb-table__row-index",
      style: _vm.getStylesForFixedLastRow(rowIndex)
    }, [_vm._v("\n              " + _vm._s(_vm.formatRowIndex(rowIndex)) + "\n            ")]) : _vm._e(), _vm._v(" "), _vm._l(_vm.parsedLabels, function (label) {
      return _c('td', {
        key: "row-" + rowIndex + "_" + label.key,
        class: [_vm.cellColorClass(rowIndex, label.color), label.class],
        style: _vm.getStylesForFixedLastRow(rowIndex)
      }, [_vm._t(label.key + "Column", function () {
        return [_vm._v("\n                " + _vm._s(row[label.key]) + "\n              ")];
      }, {
        "label": label.label,
        "labelKey": label.key,
        "value": row[label.key],
        "row": row,
        "rowIndex": rowIndex,
        "setRowColor": _vm.setRowColor
      })], 2);
    }), _vm._v(" "), _vm.rowActions ? _c('td', {
      staticClass: "bb-table__row-actions",
      class: {
        'bb-table__row-actions--no-background': _vm.rowActionsNoBackground
      },
      style: _vm.getStylesForFixedLastRow(rowIndex)
    }, [_vm._t("rowActions", null, {
      "rowData": row,
      "rowIndex": rowIndex
    })], 2) : _vm._e()], 2);
  }), _vm._v(" "), _c('tr', {
    ref: "tfoot",
    staticClass: "tfoot"
  }, [_vm._t("footer")], 2)], 2)], 2)])])], 1);
};
var __vue_staticRenderFns__$1T = [];

/* style */
const __vue_inject_styles__$1V = function (inject) {
  if (!inject) return;
  inject("data-v-ba8e2844_0", {
    source: ".bb-table{display:flex;min-height:0;flex-direction:column}.bb-table__overflow-shadows{position:relative;display:flex;min-height:0;flex:1 1 0%}.bb-table__overflow-shadows::after{position:absolute;z-index:0;content:\"\";inset:0;pointer-events:none}.table--overflow-left:not(.table--fixed-firstColumn):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset 15px 0 15px -15px rgba(0,0,0,.2)}.table--overflow-right:not(.table--fixed-lastColumn):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset -15px 0 15px -15px rgba(0,0,0,.2)}.table--overflow-left:not(.table--fixed-firstColumn):not(.table--subtable).table--overflow-right:not(.table--fixed-lastColumn):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset 15px 0 15px -15px rgba(0,0,0,.2),inset -15px 0 15px -15px rgba(0,0,0,.2)}.table--overflow-bottom:not(.table--fixed-footer):not(.table--fixed-lastRow):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset 0 -15px 15px -15px rgba(0,0,0,.2)}.table--overflow-bottom:not(.table--fixed-footer):not(.table--fixed-lastRow):not(.table--subtable).table--overflow-left:not(.table--fixed-firstColumn):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset 15px -15px 15px -15px rgba(0,0,0,.2)}.table--overflow-bottom:not(.table--fixed-footer):not(.table--fixed-lastRow):not(.table--subtable).table--overflow-right:not(.table--fixed-lastColumn):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset -15px -15px 15px -15px rgba(0,0,0,.2)}.table--overflow-bottom:not(.table--fixed-footer):not(.table--fixed-lastRow):not(.table--subtable).table--overflow-left:not(.table--fixed-firstColumn):not(.table--subtable).table--overflow-right:not(.table--fixed-lastColumn):not(.table--subtable)>.bb-table__overflow-shadows::after{box-shadow:inset -15px -15px 15px -15px rgba(0,0,0,.2),inset 15px 0 15px -15px rgba(0,0,0,.2)}.bb-table__settings{position:absolute;z-index:1201;top:0;right:0}.bb-table__scroll-wrapper{display:block;width:100%;flex:1 1 0%;border-radius:3px 3px 0 0;-webkit-overflow-scrolling:touch;-ms-overflow-style:-ms-autohiding-scrollbar}:not(.bb-table--disable-overflow)>*>.bb-table__scroll-wrapper{overflow:auto}.bb-table--has-subtable>*>.bb-table__scroll-wrapper{padding-left:calc(1.25rem * .5)}.bb-table--subtable .bb-table__scroll-wrapper{border-radius:0}.bb-table__content{width:100%;color:#00285a;font-size:12px}.bb-table__content thead{color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-align:left}.bb-table--has-index .bb-table__content thead tr:first-child th:nth-child(2){border-radius:3px 0 0}.bb-table--subtable .bb-table--has-index .bb-table__content thead tr:first-child th:nth-child(2){border-radius:0}.bb-table__content thead tr:first-child th:first-child{border-radius:3px 0 0}.bb-table--subtable .bb-table__content thead tr:first-child th:first-child{border-radius:0}.bb-table__content thead tr:first-child th:last-child{border-radius:0 3px 0 0}.bb-table--subtable .bb-table__content thead tr:first-child th:last-child{border-radius:0}.bb-table__content thead tr:nth-child(2) th{background:#5ea6ed}.bb-table__content thead th{position:relative;padding:.6875rem 1.5625rem .6875rem;background:#2485e5;vertical-align:middle}.bb-table--narrow-cell-padding .bb-table__content thead th{padding:.5rem .1875rem}.bb-table--narrow-cell-padding .bb-table__content thead th:first-child{padding-left:.6875rem}.bb-table__content thead th.col-header--sortable{cursor:pointer;user-select:none}.bb-table--narrow-cell-padding .bb-table__content thead th.col-header--sortable{padding:.5rem .1875rem .5rem .875rem}.bb-table__content thead th.col-header--index{width:1.875rem;min-width:1.875rem;padding:0;background:#fff}.bb-table__content thead th.col-header--index::after{background:0 0}.bb-table__content thead th.col-header--groupedRows-meta{width:2.1875rem;padding-left:0}.bb-table__content thead th::after{position:absolute;right:0;bottom:0;left:0;height:2px;background:0 0;content:\"\"}.bb-table--trimmed .bb-table__content thead th:not(.col-header--sortable){padding-right:.3125rem;padding-left:.3125rem}.bb-table__content tr td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#e6f3ff}.bb-table__content tr td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#e6f3ff}.bb-table__content tr td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#f7f7f8}.bb-table__content tr td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#ffeae6}.bb-table__content tr td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#e7faf6}.bb-table__content tr td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#e7faf6}.bb-table__content tr td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#fbf5cb}.bb-table__content td{position:relative;background:#fff;vertical-align:middle}.bb-table__content td:not(.disable-cell-padding){padding:.4375rem 1.5625rem .5rem}.bb-table--narrow-cell-padding .bb-table__content td:not(.disable-cell-padding){padding:.1875rem .125rem}.bb-table__content td:not(.bb-table__row-index,.bb-table__row-actions--no-background)::after{position:absolute;z-index:0;content:\"\";inset:0;top:unset;height:1px;background:#bdc7d0}.bb-table--dashed .bb-table__content td:not(.bb-table__row-index,.bb-table__row-actions--no-background)::after{height:0;border-bottom:1px dashed #bdc7d0;background:0 0}.bb-table__content td.bb-table__row-actions{padding:0}.bb-table__content td.bb-table__row-index.bb-table__row-index.bb-table__row-index{padding:0;background:#fff;color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-table__content td.bb-table__row-index.bb-table__row-index.bb-table__row-index::after{background:#fff}.bb-table--trimmed .bb-table__content td{padding-right:.3125rem;padding-left:.3125rem}.bb-table--condensed{font-size:11px}.bb-table--condensed .bb-table__content td,.bb-table--condensed .bb-table__content th{padding-top:.25rem;padding-bottom:.3125rem}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#add9ff}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#eeeff2}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#ffd5cc}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#b6f0e3}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#b6f0e3}.bb-table--striped:not(.bb-table--wrapped-rows) tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--striped:not(.bb-table--wrapped-rows).bb-table--subtable tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background),.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#add9ff}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#eeeff2}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--red.cell-background--red.cell-background--red.cell-background--red,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#ffd5cc}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#b6f0e3}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--green.cell-background--green.cell-background--green.cell-background--green,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#b6f0e3}.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow,.bb-table--striped.bb-table--wrapped-rows>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background).cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--wrapped-rows td.row-name,.bb-table--wrapped-rows th.row-name{text-align:center}.bb-table--wrapped-rows td.row-name .row-name__bubble{display:inline-block;width:1rem;height:1rem;padding:.0625rem;border-radius:50%;background:#2485e5;color:#fff;font-size:90%;font-weight:700}.bb-table--wrapped-rows tbody tr:first-child td:not(.bb-table__row-index,.bb-table__row-actions--no-background)::after{background:#dee3e8}.bb-table--lightblue>*>*>.bb-table__content>thead{color:#38414d}.bb-table--lightblue>*>*>.bb-table__content>thead th{background:#fff}.bb-table--lightblue>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#6eb3f7}.bb-table--lightblue>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--lightblue.bb-table--fixed-firstColumn thead th:first-child,.bb-table--lightblue.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--lightblue>*>*>.bb-table__content>thead{color:#fff}.bb-table--lightblue>*>*>.bb-table__content>thead th{background:#6eb3f7}.bb-table--lightblue>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#add9ff}.bb-table--lightblue>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#add9ff}.bb-table--lightblue.bb-table--fixed-firstColumn thead th:first-child,.bb-table--lightblue.bb-table--fixed-lastColumn thead th:last-child{background:#6eb3f7}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#6eb3f7}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--lightblue.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--lightblue.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead th{background:#6eb3f7}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#add9ff}.bb-table--lightblue.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#add9ff}.bb-table--lightblue.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--lightblue.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#6eb3f7}.bb-table--lightblue.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--lightblue.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--lightblue.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--lightblue.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--lightblue.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--lightblue.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--lightblue.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#add9ff}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--lightblue.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#add9ff}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--lightblue.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--navy>*>*>.bb-table__content>thead{color:#38414d}.bb-table--navy>*>*>.bb-table__content>thead th{background:#fff}.bb-table--navy>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#00285a}.bb-table--navy>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--navy.bb-table--fixed-firstColumn thead th:first-child,.bb-table--navy.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--navy>*>*>.bb-table__content>thead{color:#fff}.bb-table--navy>*>*>.bb-table__content>thead th{background:#00285a}.bb-table--navy>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#003e8c}.bb-table--navy>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#003e8c}.bb-table--navy.bb-table--fixed-firstColumn thead th:first-child,.bb-table--navy.bb-table--fixed-lastColumn thead th:last-child{background:#00285a}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#00285a}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--navy.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--navy.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead th{background:#00285a}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#003e8c}.bb-table--navy.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#003e8c}.bb-table--navy.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--navy.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#00285a}.bb-table--navy.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--navy.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--navy.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--navy.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--navy.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--navy.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#add9ff}.bb-table--navy.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e6f3ff}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#add9ff}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--navy.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#add9ff}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--navy.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--gray>*>*>.bb-table__content>thead{color:#38414d}.bb-table--gray>*>*>.bb-table__content>thead th{background:#fff}.bb-table--gray>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#9dabb9}.bb-table--gray>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--gray.bb-table--fixed-firstColumn thead th:first-child,.bb-table--gray.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--gray>*>*>.bb-table__content>thead{color:#fff}.bb-table--gray>*>*>.bb-table__content>thead th{background:#9dabb9}.bb-table--gray>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#bdc7d0}.bb-table--gray>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#bdc7d0}.bb-table--gray.bb-table--fixed-firstColumn thead th:first-child,.bb-table--gray.bb-table--fixed-lastColumn thead th:last-child{background:#9dabb9}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#9dabb9}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--gray.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--gray.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead th{background:#9dabb9}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#bdc7d0}.bb-table--gray.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#bdc7d0}.bb-table--gray.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--gray.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#9dabb9}.bb-table--gray.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#f7f7f8}.bb-table--gray.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#f7f7f8}.bb-table--gray.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#f7f7f8}.bb-table--gray.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#eeeff2}.bb-table--gray.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#eeeff2}.bb-table--gray.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#eeeff2}.bb-table--gray.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#f7f7f8}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#eeeff2}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--gray.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#eeeff2}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--gray.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--red>*>*>.bb-table__content>thead{color:#38414d}.bb-table--red>*>*>.bb-table__content>thead th{background:#fff}.bb-table--red>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#ff7155}.bb-table--red>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--red.bb-table--fixed-firstColumn thead th:first-child,.bb-table--red.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--red>*>*>.bb-table__content>thead{color:#fff}.bb-table--red>*>*>.bb-table__content>thead th{background:#ff7155}.bb-table--red>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fa9}.bb-table--red>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#fa9}.bb-table--red.bb-table--fixed-firstColumn thead th:first-child,.bb-table--red.bb-table--fixed-lastColumn thead th:last-child{background:#ff7155}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#ff7155}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--red.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--red.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead th{background:#ff7155}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fa9}.bb-table--red.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#fa9}.bb-table--red.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--red.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#ff7155}.bb-table--red.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeae6}.bb-table--red.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeae6}.bb-table--red.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeae6}.bb-table--red.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffd5cc}.bb-table--red.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffd5cc}.bb-table--red.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffd5cc}.bb-table--red.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeae6}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#ffd5cc}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--red.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#ffd5cc}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--red.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--mint>*>*>.bb-table__content>thead{color:#38414d}.bb-table--mint>*>*>.bb-table__content>thead th{background:#fff}.bb-table--mint>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#0ccca2}.bb-table--mint>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--mint.bb-table--fixed-firstColumn thead th:first-child,.bb-table--mint.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--mint>*>*>.bb-table__content>thead{color:#fff}.bb-table--mint>*>*>.bb-table__content>thead th{background:#0ccca2}.bb-table--mint>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#3dd6b5}.bb-table--mint>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#3dd6b5}.bb-table--mint.bb-table--fixed-firstColumn thead th:first-child,.bb-table--mint.bb-table--fixed-lastColumn thead th:last-child{background:#0ccca2}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#0ccca2}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--mint.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--mint.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead th{background:#0ccca2}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#3dd6b5}.bb-table--mint.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#3dd6b5}.bb-table--mint.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--mint.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#0ccca2}.bb-table--mint.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--mint.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--mint.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--mint.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#b6f0e3}.bb-table--mint.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#b6f0e3}.bb-table--mint.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#b6f0e3}.bb-table--mint.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#b6f0e3}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--mint.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#b6f0e3}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--mint.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--green>*>*>.bb-table__content>thead{color:#38414d}.bb-table--green>*>*>.bb-table__content>thead th{background:#fff}.bb-table--green>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#0ccca2}.bb-table--green>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--green.bb-table--fixed-firstColumn thead th:first-child,.bb-table--green.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--green>*>*>.bb-table__content>thead{color:#fff}.bb-table--green>*>*>.bb-table__content>thead th{background:#0ccca2}.bb-table--green>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#3dd6b5}.bb-table--green>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#3dd6b5}.bb-table--green.bb-table--fixed-firstColumn thead th:first-child,.bb-table--green.bb-table--fixed-lastColumn thead th:last-child{background:#0ccca2}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#0ccca2}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--green.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--green.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead th{background:#0ccca2}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#3dd6b5}.bb-table--green.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#3dd6b5}.bb-table--green.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--green.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#0ccca2}.bb-table--green.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--green.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--green.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--green.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#b6f0e3}.bb-table--green.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#b6f0e3}.bb-table--green.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#b6f0e3}.bb-table--green.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#e7faf6}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#b6f0e3}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--green.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#b6f0e3}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--green.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--yellow>*>*>.bb-table__content>thead{color:#38414d}.bb-table--yellow>*>*>.bb-table__content>thead th{background:#fff}.bb-table--yellow>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#fc0}.bb-table--yellow>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--yellow.bb-table--fixed-firstColumn thead th:first-child,.bb-table--yellow.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--yellow>*>*>.bb-table__content>thead{color:#fff}.bb-table--yellow>*>*>.bb-table__content>thead th{background:#fc0}.bb-table--yellow>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#ffeea6}.bb-table--yellow>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#ffeea6}.bb-table--yellow.bb-table--fixed-firstColumn thead th:first-child,.bb-table--yellow.bb-table--fixed-lastColumn thead th:last-child{background:#fc0}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead{color:#38414d}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fff}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#fc0}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--yellow.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--yellow.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead{color:#fff}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead th{background:#fc0}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#ffeea6}.bb-table--yellow.bb-table--subtable>*>*>.bb-table__content>thead tr:nth-child(2) th::after{background:#ffeea6}.bb-table--yellow.bb-table--subtable.bb-table--fixed-firstColumn thead th:first-child,.bb-table--yellow.bb-table--subtable.bb-table--fixed-lastColumn thead th:last-child{background:#fc0}.bb-table--yellow.bb-table--striped:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#fbf5cb}.bb-table--yellow.bb-table--striped.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#fbf5cb}.bb-table--yellow.bb-table--striped.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#fbf5cb}.bb-table--yellow.bb-table--striped.bb-table--subtable:not(.bb-table--wrapped-rows)>*>*>.bb-table__content>tbody>tr:not(.bb-table__groupedRows-subrow):nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeea6}.bb-table--yellow.bb-table--striped.bb-table--subtable.bb-table--wrapped-rows tbody:nth-child(even) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeea6}.bb-table--yellow.bb-table--striped.bb-table--subtable.bb-table--has-subtable>*>*>.bb-table__content>tbody:nth-child(even)>tr:not(.bb-table__groupedRows-subrow)>td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#ffeea6}.bb-table--yellow.bb-table--striped.bb-table--subtable tbody:not(.bb-table__groupedRows) tr:nth-child(odd) td:not(.bb-table__row-index,.bb-table__row-actions--no-background){background:#fbf5cb}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#ffeea6}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--yellow.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#ffeea6}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--yellow.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td{background:#add9ff}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--hoverable tbody tr:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td{background:#add9ff}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue.cell-background--lightblue{background:#add9ff}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--navy.cell-background--navy.cell-background--navy.cell-background--navy{background:#003e8c}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--gray.cell-background--gray.cell-background--gray.cell-background--gray{background:#bdc7d0}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--red.cell-background--red.cell-background--red.cell-background--red{background:#fa9}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--mint.cell-background--mint.cell-background--mint.cell-background--mint{background:#3dd6b5}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--green.cell-background--green.cell-background--green.cell-background--green{background:#3dd6b5}.bb-table--hoverable.bb-table--wrapped-rows tbody:hover:hover:hover:hover:hover:hover td.cell-background--yellow.cell-background--yellow.cell-background--yellow.cell-background--yellow{background:#ffeea6}.bb-table--fixed-head thead th{position:-webkit-sticky;position:sticky;z-index:1100;top:0}.bb-table--fixed-head.bb-table--wrapped-rows thead tr:nth-child(2) th{top:39px}.bb-table--fixed-head.bb-table--overflow-top thead th{box-shadow:3px 4px 8px rgba(0,0,0,.1)}.bb-table--fixed-head.bb-table--overflow-top thead th:first-child{box-shadow:-3px 4px 8px rgba(0,0,0,.1)}.bb-table--fixed-head.bb-table--overflow-top thead th:first-child.col-header--index{box-shadow:none}.bb-table--fixed-footer .tfoot td{position:-webkit-sticky;position:sticky;bottom:0}.bb-table--fixed-footer.bb-table--overflow-bottom:not(.bb-table--fixed-lastRow) .tfoot td,.bb-table--fixed-footer.bb-table--overflow-bottom:not(.bb-table--fixed-lastRow).bb-table--fixed-firstColumn .tfoot td:first-child{box-shadow:6px -2px 8px rgba(0,0,0,.1)}.bb-table--fixed-footer.bb-table--overflow-bottom:not(.bb-table--fixed-lastRow).bb-table--fixed-firstColumn .tfoot td:last-child{box-shadow:6px 0 8px 2px rgba(0,0,0,.1)}.bb-table--fixed-footer.bb-table--overflow-bottom:not(.bb-table--fixed-lastRow).bb-table--fixed-firstColumn:not(.bb-table--overflow-left) .tfoot td:first-child{box-shadow:-2px -4px 8px -2px rgba(0,0,0,.1)}.bb-table--fixed-footer.bb-table--overflow-bottom:not(.bb-table--fixed-lastRow).bb-table--fixed-lastColumn.bb-table--overflow-right .tfoot td:last-child{box-shadow:-4px -2px 8px rgba(0,0,0,.1)}.bb-table--fixed-firstColumn td:first-child,.bb-table--fixed-firstColumn th:first-child{position:-webkit-sticky;position:sticky;left:0;z-index:1100}.bb-table--fixed-firstColumn thead td:first-child,.bb-table--fixed-firstColumn thead th:first-child{z-index:1200;background:#2485e5}.bb-table--fixed-firstColumn thead td:first-child.col-header--index,.bb-table--fixed-firstColumn thead th:first-child.col-header--index{background:#fff}.bb-table--fixed-firstColumn .tfoot td:first-child,.bb-table--fixed-firstColumn .tfoot th:first-child{z-index:1100}.bb-table--fixed-firstColumn.bb-table--overflow-left thead th:first-child::before{position:absolute;z-index:-1;content:\"\";inset:0;height:100%;box-shadow:4px -4px 4px rgba(0,0,0,.1)}.bb-table--fixed-firstColumn.bb-table--overflow-left tbody td:first-child,.bb-table--fixed-firstColumn.bb-table--overflow-left tfoot td:first-child{box-shadow:3px 4px 8px rgba(0,0,0,.1)}.bb-table--fixed-lastColumn td:last-child,.bb-table--fixed-lastColumn th:last-child{position:-webkit-sticky;position:sticky;right:0;z-index:1100}.bb-table--fixed-lastColumn thead td:last-child,.bb-table--fixed-lastColumn thead th:last-child{z-index:1200;background:#2485e5}.bb-table--fixed-lastColumn thead td:last-child.col-header--index,.bb-table--fixed-lastColumn thead th:last-child.col-header--index{background:#fff}.bb-table--fixed-lastColumn .tfoot td:last-child,.bb-table--fixed-lastColumn .tfoot th:last-child{z-index:1100}.bb-table--fixed-lastColumn.bb-table--overflow-right thead th:last-child::before{position:absolute;z-index:-1;content:\"\";inset:0;height:100%;box-shadow:-4px -4px 4px rgba(0,0,0,.1)}.bb-table--fixed-lastColumn.bb-table--overflow-right tbody td:last-child,.bb-table--fixed-lastColumn.bb-table--overflow-right tfoot td:last-child{box-shadow:-3px 4px 8px rgba(0,0,0,.1)}.bb-table--fixed-lastRow .bb-table__last-row td{position:-webkit-sticky;position:sticky;bottom:0}.bb-table--fixed-lastRow.bb-table--overflow-bottom .bb-table__last-row td{box-shadow:6px -2px 8px rgba(0,0,0,.1)}.bb-table--fixed-lastRow.bb-table--overflow-bottom .bb-table__last-row td:first-child{box-shadow:1px 1px 8px 3px rgba(0,0,0,.1)}.bb-table--fixed-lastRow.bb-table--overflow-bottom .bb-table__last-row td:last-child{box-shadow:4px -2px 8px rgba(0,0,0,.1)}.bb-table--fixed-lastRow.bb-table--overflow-bottom.bb-table--fixed-firstColumn:not(.bb-table--overflow-left) .bb-table__last-row td:first-child{box-shadow:-2px -4px 8px -2px rgba(0,0,0,.1)}.bb-table--fixed-lastRow.bb-table--overflow-bottom.bb-table--fixed-lastColumn.bb-table--overflow-right .bb-table__last-row td:last-child{box-shadow:-1px 0 8px 2px rgba(0,0,0,.1)}.bb-table--underline>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#2485e5}.bb-table--underline>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--lightblue>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--lightblue>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--lightblue>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#6eb3f7}.bb-table--underline.bb-table--lightblue>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--lightblue.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--lightblue.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--navy>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--navy>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--navy>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#00285a}.bb-table--underline.bb-table--navy>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--navy.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--navy.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--gray>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--gray>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--gray>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#9dabb9}.bb-table--underline.bb-table--gray>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--gray.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--gray.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--red>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--red>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--red>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#ff7155}.bb-table--underline.bb-table--red>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--red.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--red.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--mint>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--mint>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--mint>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#0ccca2}.bb-table--underline.bb-table--mint>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--mint.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--mint.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--green>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--green>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--green>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#0ccca2}.bb-table--underline.bb-table--green>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--green.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--green.bb-table--fixed-lastColumn thead th:last-child{background:#fff}.bb-table--underline.bb-table--yellow>*>*>.bb-table__content>thead{color:#38414d}.bb-table--underline.bb-table--yellow>*>*>.bb-table__content>thead th{background:#fff}.bb-table--underline.bb-table--yellow>*>*>.bb-table__content>thead th:not(.col-header--index)::after{background:#fc0}.bb-table--underline.bb-table--yellow>*>*>.bb-table__content>thead tr:nth-child(2) th{background:#fff}.bb-table--underline.bb-table--yellow.bb-table--fixed-firstColumn thead th:first-child,.bb-table--underline.bb-table--yellow.bb-table--fixed-lastColumn thead th:last-child{background:#fff}@media screen and (min-width:768px){.bb-table__row-actions:not(.bb-table__row-actions--no-background)>*{opacity:0}.bb-table--wrapped-rows tbody:hover .bb-table__row-actions:not(.bb-table__row-actions--no-background)>*,tbody>tr:hover>.bb-table__row-actions:not(.bb-table__row-actions--no-background)>*{opacity:1}}.bb-table__row-actions--no-background{background:0 0}.bb-table__row-actions--no-background::after{content:none}.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow>td{padding:0;padding-left:2rem}.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow>td>*{overflow:hidden;max-height:0;transition:max-height .3s cubic-bezier(.03,.71,.23,.98)}.is-expanded>.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow>td>*{max-height:3000px;transition:max-height .3s cubic-bezier(.7,.02,.99,.32)}.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow>td::before{position:absolute;top:-10px;left:0;width:2rem;height:30px;border-bottom:1px solid #89a;border-left:1px solid #89a;content:\"\";opacity:0;transform:scaleY(0);transform-origin:top;transition:opacity .2s cubic-bezier(.7,.02,.99,.32),transform .2s cubic-bezier(.7,.02,.99,.32)}.is-expanded>.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow.bb-table__groupedRows-subrow>td::before{opacity:1;transform:scaleY(1);transition:opacity .2s cubic-bezier(.7,.02,.99,.32),transform .2s cubic-bezier(.7,.02,.99,.32)}.bb-table__groupedRows-subtableRowCount{position:relative;z-index:1100;display:inline-block;margin-right:-5px;color:#ff7155}.bb-table__content .bb-table__groupedRows-meta-cell{padding-right:0;padding-left:20px}.bb-table__groupedRows-toggle{position:absolute;z-index:1100;top:calc(50% - 1.25rem * .5);left:calc(-1.25rem * .5 - 1px);width:1.25rem;height:1.25rem;color:#89a;cursor:pointer;line-height:12px;text-align:center;user-select:none}.bb-table__groupedRows-toggle svg{width:1.25rem;height:1.25rem}.bb-table--subtable .bb-table__scrolling-indicator{display:none}.bb-table--subtable tr td:first-child,.bb-table--subtable tr th:first-child{padding-left:3.4375rem}.bb-table--subtable tr th:first-child .col-header__sorting-icons{left:2.5rem}.bb-table--disable-table-head tr:first-child td:not(.bb-table__row-index,.bb-table__row-actions--no-background)::before{position:absolute;z-index:0;content:\"\";inset:0;top:0;bottom:unset;height:1px;background:#bdc7d0}.bb-table--disable-table-head.bb-table--dashed tr:first-child td:not(.bb-table__row-index,.bb-table__row-actions--no-background)::before{height:0;border-top:1px dashed #bdc7d0;background:0 0}.col-header__sorting-icons{position:absolute;bottom:0;left:.25rem;display:inline-flex;height:100%;flex-direction:column;justify-content:center}.bb-table--narrow-cell-padding .col-header__sorting-icons{left:-.125rem}.col-header__icon-sort-down,.col-header__icon-sort-up{position:relative;opacity:.2;transition:opacity 150ms}.col-header__icon-sort-up{top:.3125rem;transform:rotate(180deg)}th.col-header--sort-up .col-header__icon-sort-up{opacity:1}.col-header__icon-sort-down{bottom:.3125rem}th.col-header--sort-down .col-header__icon-sort-down{opacity:1}.bb-table__scrolling-indicator.bb-table__scrolling-indicator{justify-content:flex-end}@media screen and (min-width:768px){.bb-table__scrolling-indicator.bb-table__scrolling-indicator{display:none}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1V = undefined;
/* module identifier */
const __vue_module_identifier__$1V = undefined;
/* functional template */
const __vue_is_functional_template__$1V = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1V = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1T,
  staticRenderFns: __vue_staticRenderFns__$1T
}, __vue_inject_styles__$1V, __vue_script__$1V, __vue_scope_id__$1V, __vue_is_functional_template__$1V, __vue_module_identifier__$1V, false, createInjector, undefined, undefined);

var script$1U = {
  name: 'bb-tag-group'
};

/* script */
const __vue_script__$1U = script$1U;

/* template */
var __vue_render__$1S = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-tag-group"
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$1S = [];

/* style */
const __vue_inject_styles__$1U = function (inject) {
  if (!inject) return;
  inject("data-v-4c49fb03_0", {
    source: ".bb-tag-group{display:flex;flex-wrap:wrap}.bb-tag-group .bb-tag{margin:.3125rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1U = undefined;
/* module identifier */
const __vue_module_identifier__$1U = undefined;
/* functional template */
const __vue_is_functional_template__$1U = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1U = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1S,
  staticRenderFns: __vue_staticRenderFns__$1S
}, __vue_inject_styles__$1U, __vue_script__$1U, __vue_scope_id__$1U, __vue_is_functional_template__$1U, __vue_module_identifier__$1U, false, createInjector, undefined, undefined);

var script$1T = {
  name: 'bb-task-area',
  components: {
    BbIcon: __vue_component__$2C,
    CollapseTransition
  },
  mixins: [validator],
  props: {
    title: {
      type: String
    },
    flavor: {
      type: String,
      default: 'admin',
      validator: f => ['admin', 'user'].includes(f)
    },
    expandable: Boolean,
    expanded: Boolean
  },
  emits: ['expanded', 'collapsed'],
  data() {
    return {
      contentExpanded: this.expanded
    };
  },
  computed: {
    classes() {
      return ['bb-task-area', {
        'bb-task-area--public': this.flavor === 'user',
        'bb-task-area--expandable': this.expandable,
        'bb-task-area--expanded': this.contentExpanded
      }];
    }
  },
  methods: {
    toggleContent() {
      if (this.expandable) {
        this.contentExpanded = !this.contentExpanded;
        if (this.contentExpanded) {
          this.$emit('expanded');
        } else {
          this.$emit('collapsed');
        }
      }
    }
  }
};

/* script */
const __vue_script__$1T = script$1T;

/* template */
var __vue_render__$1R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.title ? _c('div', {
    staticClass: "bb-task-area__title",
    on: {
      "click": _vm.toggleContent,
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.toggleContent.apply(null, arguments);
      }
    }
  }, [_c('h2', [_vm._v(_vm._s(_vm.title))]), _vm._v(" "), _c('div', {
    staticClass: "bb-task-area__header",
    attrs: {
      "data-testid": "bb-task-area__header"
    }
  }, [_vm._t("header"), _vm._v(" "), _vm.expandable ? _c('bb-icon', {
    staticClass: "bb-task-area__chevron",
    attrs: {
      "name": "ui-chevron-down",
      "fill": "gray-80",
      "size": "24"
    }
  }) : _vm._e()], 2)]) : _vm._e(), _vm._v(" "), _c('collapse-transition', [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: !_vm.expandable || _vm.contentExpanded,
      expression: "!expandable || contentExpanded"
    }]
  }, [_c('div', {
    staticClass: "bb-task-area__content"
  }, [_vm._t("default")], 2)])])], 1);
};
var __vue_staticRenderFns__$1R = [];

/* style */
const __vue_inject_styles__$1T = function (inject) {
  if (!inject) return;
  inject("data-v-ea0a97e6_0", {
    source: ".bb-task-area{overflow:hidden;border:.0625rem dashed #9dabb9;border-radius:.125rem}.bb-task-area__title{display:flex;align-items:center;justify-content:space-between;padding:.9375rem 1.875rem;background-color:#e6f3ff;color:#2485e5}.bb-task-area--expandable .bb-task-area__title{cursor:pointer}.bb-task-area--expandable .bb-task-area__title:hover{color:#003e8c}.bb-task-area__title h2{margin:0;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1rem}.bb-task-area__content{padding:1.25rem 1.875rem;color:#38414d;font-size:.75rem}.bb-upload__wrapper--task-area .bb-task-area__content{padding:0 1.875rem}@media (min-width:768px){.bb-upload__wrapper--task-area .bb-task-area__content{padding:1.25rem 1.875rem}}.bb-task-area--public{border-color:#dee3e8;border-radius:.3125rem}.bb-task-area__header{display:flex;align-items:center}.bb-task-area__chevron{margin-left:1rem;transform:rotate(0);transition:transform .5s cubic-bezier(.2,.25,.3,1.5);will-change:transform}.bb-task-area--expanded .bb-task-area__chevron{transform:rotate(180deg)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1T = undefined;
/* module identifier */
const __vue_module_identifier__$1T = undefined;
/* functional template */
const __vue_is_functional_template__$1T = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1T = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1R,
  staticRenderFns: __vue_staticRenderFns__$1R
}, __vue_inject_styles__$1T, __vue_script__$1T, __vue_scope_id__$1T, __vue_is_functional_template__$1T, __vue_module_identifier__$1T, false, createInjector, undefined, undefined);

var script$1S = defineComponent({
  name: 'bb-textarea',
  components: {
    BbTooltip: __vue_component__$2B
  },
  mixins: [veeValidationMixin, Field, ErrorBubble, vuelidateValidator],
  props: {
    /**
     * ID for the text area element
     */
    id: [String, Number],
    /**
     * Name for the text area input
     */
    name: {
      type: String,
      default: '',
      required: true
    },
    /**
     * Input type | [`text`, `number`, `tel`]
     */
    type: {
      type: String,
      default: 'text'
    },
    /**
     * Initial value for a text area field
     */
    value: {
      type: String,
      default: ''
    },
    /**
     * HTML tabindex Attribute
     */
    tabindex: [String, Number],
    /**
     * Text area field label
     */
    label: String,
    /**
     * Secondary inputs aka the framed inputs
     */
    framed: Boolean,
    /**
     * Condensed size for text area
     */
    small: Boolean,
    /**
     * Placeholder for the text area
     */
    placeholder: String,
    /**
     * The maximum number of characters (Unicode code points) that the user can enter.
     * If this value isn't specified, the user can enter an unlimited number of characters.
     */
    maxlength: {
      type: [Number, String],
      default: null
    },
    /**
     * For non-editable pre-filled inputs
     */
    readonly: Boolean,
    /**
     * The number of visible text lines for the field.
     */
    rows: {
      type: [Number, String],
      default: null
    },
    /**
     * Controls the resizability of the field using corresponding CSS property | [`none`, `both`, `horizontal`, `vertical`, `block`, `inline`]
     */
    resize: {
      type: [String],
      default: null,
      validator: resize => ['none', 'both', 'horizontal', 'vertical', 'block', 'inline'].includes(resize)
    },
    /**
     * Validation rules
     */
    rules: Object,
    /**
     * Show input error in a tooltip bubble
     */
    errorMessageAsBubble: Boolean,
    /**
     * Show error bubble only on hover.
     */
    errorBubbleOnHoverOnly: Boolean,
    /**
     * Adds an asterisk (*) before the textarea label to indicate mandatory field
     */
    mandatory: Boolean
  },
  computed: {
    attrs() {
      return notEmpty(this.inputAttrs) ? this.inputAttrs : this.$attrs;
    },
    computedId() {
      var _this$id;
      return (_this$id = this.id) !== null && _this$id !== void 0 ? _this$id : slugify(this.name);
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `error-message-${slugify(this.name)}`;
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    }
  }
});

/* script */
const __vue_script__$1S = script$1S;

/* template */
var __vue_render__$1Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-textarea field-group",
    class: _vm.fieldGroupClasses,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "val",
      "data-testid": "bb-textarea"
    },
    on: {
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, [_vm.label ? _c('label', {
    staticClass: "bb-textarea__label",
    attrs: {
      "for": _vm.computedId
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-textarea__mandatory-indicator"
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "input-group"
  }, [_c('textarea', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    ref: "textarea",
    staticClass: "bb-textarea__textarea",
    style: {
      resize: _vm.resize
    },
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "type": _vm.type,
      "tabindex": _vm.tabindex,
      "maxlength": _vm.maxlength,
      "placeholder": _vm.placeholder,
      "rows": _vm.rows,
      "readonly": _vm.readonly,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "aria-required": _vm.mandatory,
      "data-testid": "bb-textarea__textarea"
    },
    domProps: {
      "value": _vm.val
    },
    on: {
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus,
      "input": [function ($event) {
        if ($event.target.composing) {
          return;
        }
        _vm.val = $event.target.value;
      }, _vm.onInput],
      "change": _vm.onChange
    }
  }, 'textarea', _vm.attrs, false)), _vm._v(" "), _vm.hasValidationError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.textarea,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', [_vm._v(_vm._s(_vm.firstError || _vm.getFirstError()))])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n      ")]) : _vm._e()] : _vm._e()], 2)]);
};
var __vue_staticRenderFns__$1Q = [];

/* style */
const __vue_inject_styles__$1S = function (inject) {
  if (!inject) return;
  inject("data-v-03b4fcce_0", {
    source: ".bb-textarea__textarea{width:100%;padding:.625rem .875rem;border:.0625rem solid #bdc7d0;border-radius:.375rem;-webkit-appearance:none;-moz-appearance:none;background-color:transparent;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:inherit;line-height:1.5em;resize:none;transition:color .2s,background-color .2s,border-color .2s}.bb-textarea__textarea:focus,.bb-textarea__textarea:hover{border-color:#003e8c}.bb-textarea__textarea[disabled]{border-color:#9dabb9;background-color:#dee3e8;cursor:not-allowed;opacity:.6}.bb-textarea__textarea[disabled]:focus,.bb-textarea__textarea[disabled]:hover{border-color:#9dabb9}.bb-textarea__textarea::placeholder{color:#89a}.bb-textarea__textarea:focus{outline:0}.bb-textarea[data-valid=false] .bb-textarea__textarea{border-color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1S = undefined;
/* module identifier */
const __vue_module_identifier__$1S = undefined;
/* functional template */
const __vue_is_functional_template__$1S = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1S = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1Q,
  staticRenderFns: __vue_staticRenderFns__$1Q
}, __vue_inject_styles__$1S, __vue_script__$1S, __vue_scope_id__$1S, __vue_is_functional_template__$1S, __vue_module_identifier__$1S, false, createInjector, undefined, undefined);

var script$1R = {
  name: 'bb-ticket-stub'
};

/* script */
const __vue_script__$1R = script$1R;

/* template */
var __vue_render__$1P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-ticket-stub"
  }, [_vm.$slots.left ? _c('div', {
    staticClass: "bb-ticket-stub__segment bb-ticket-stub__segment--left"
  }, [_vm._t("left")], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-ticket-stub__segment",
    class: {
      'bb-ticket-stub__segment--right': _vm.$slots.left
    }
  }, [_vm._t("default")], 2)]);
};
var __vue_staticRenderFns__$1P = [];

/* style */
const __vue_inject_styles__$1R = function (inject) {
  if (!inject) return;
  inject("data-v-342271d8_0", {
    source: ".bb-ticket-stub{display:flex;flex-wrap:nowrap}.bb-ticket-stub__segment{display:flex;min-height:3.4375rem;flex:1;align-items:center;justify-content:center;border-top:.0625rem dashed #9dabb9}.bb-ticket-stub__segment>.bb-button--ticket{height:100%;border-radius:0 0 .625rem .625rem}.bb-ticket-stub__segment>.bb-button--ticket:not(.bb-button--disabled,.bb-button--inverted-filled,.bb-button--white){height:calc(100% + .0625rem);margin-top:-.0625rem}.bb-ticket-stub__segment--left{border-right:.0625rem dashed #9dabb9}.bb-ticket-stub__segment--left .bb-button--ticket{border-bottom-right-radius:0}.bb-ticket-stub__segment--right>.bb-button--ticket{border-bottom-left-radius:0}.bb-ticket-stub__segment--right>.bb-button--ticket:not(.bb-button--disabled,.bb-button--inverted-filled,.bb-button--white){width:calc(100% + .0625rem);margin-left:-.0625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1R = undefined;
/* module identifier */
const __vue_module_identifier__$1R = undefined;
/* functional template */
const __vue_is_functional_template__$1R = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1R = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1P,
  staticRenderFns: __vue_staticRenderFns__$1P
}, __vue_inject_styles__$1R, __vue_script__$1R, __vue_scope_id__$1R, __vue_is_functional_template__$1R, __vue_module_identifier__$1R, false, createInjector, undefined, undefined);

var Icon$L = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3746022340__a bb-icon__dynamic-fill",attrs:{"d":"M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"}})]) } };

Vue.component('icon-ui-check-circle', Icon$L);

var Icon$K = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg227081940__a bb-icon__dynamic-fill",attrs:{"d":"M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"}})]) } };

Vue.component('icon-ui-close-circle', Icon$K);

var script$1Q = {
  name: 'bb-toggle-switch',
  components: {
    BbIcon: __vue_component__$2C
  },
  mixins: [Field, veeValidationMixin],
  props: {
    checked: {
      type: Boolean,
      validator: deprecated('checked', 'value', 'ToggleSwitch')
    },
    value: Boolean,
    label: String,
    disabled: Boolean,
    showStatus: Boolean,
    onStatusLabel: {
      type: String,
      default: 'Yes'
    },
    offStatusLabel: {
      type: String,
      default: 'No'
    },
    size: {
      type: String,
      default: 'md',
      validator: size => ['sm', 'md'].includes(size)
    },
    labelAlign: {
      type: String,
      default: 'auto',
      validator: alignment => ['auto', 'right'].includes(alignment)
    }
  },
  emits: ['input', 'change'],
  data() {
    return {
      val: this.value || this.checked || null,
      isFocused: false
    };
  },
  computed: {
    classes() {
      return ['bb-toggle', `bb-toggle--size-${this.size}`, `bb-toggle--label-align-${this.labelAlign}`, {
        'bb-toggle--disabled': this.disabled,
        'bb-toggle--checked': this.val,
        'bb-toggle--is-focused': this.isFocused
      }];
    },
    addonClasses() {
      return ['bb-toggle__addon', {
        'bb-toggle__addon--multiline': this.label && this.showStatus
      }];
    },
    status() {
      return this.val ? this.onStatusLabel : this.offStatusLabel;
    },
    computedDotIconName() {
      return this.val ? 'ui-check-circle' : 'ui-close-circle';
    },
    computedId() {
      var _this$id;
      return (_this$id = this.id) !== null && _this$id !== void 0 ? _this$id : slugify(this.name);
    },
    statusId() {
      return `status-${this.computedId}`;
    }
  },
  watch: {
    checked() {
      this.val = this.checked;
    },
    value() {
      this.val = this.value;
    }
  },
  methods: {
    isToggled(event) {
      this.val = event.target.checked;
      this.$emit('input', this.val);
      this.$emit('change', this.val);
    },
    onFocus() {
      this.isFocused = true;
    },
    onBlur() {
      this.isFocused = false;
    }
  }
};

/* script */
const __vue_script__$1Q = script$1Q;

/* template */
var __vue_render__$1O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('label', {
    class: _vm.classes,
    attrs: {
      "for": _vm.computedId,
      "data-valid": _vm.validity,
      "tabindex": _vm.tabindex,
      "data-testid": "bb-toggle"
    }
  }, [_vm.label || _vm.showStatus ? _c('div', {
    class: _vm.addonClasses
  }, [_vm.label ? _c('div', {
    staticClass: "bb-toggle__label",
    attrs: {
      "data-testid": "bb-toggle__label"
    }
  }, [_vm._t("default", function () {
    return [_vm._v(_vm._s(_vm.label))];
  })], 2) : _vm._e(), _vm._v(" "), _vm.showStatus ? _c('div', {
    staticClass: "bb-toggle__status",
    attrs: {
      "id": _vm.statusId
    }
  }, [_vm._v("\n      " + _vm._s(_vm.status) + "\n    ")]) : _vm._e()]) : _vm._e(), _vm._v(" "), _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.val,
      expression: "val"
    }],
    staticClass: "bb-toggle__input visually-hidden",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "aria-describedby": _vm.showStatus ? _vm.statusId : undefined,
      "type": "checkbox",
      "role": "switch",
      "data-testid": "bb-toggle__input"
    },
    domProps: {
      "checked": Array.isArray(_vm.val) ? _vm._i(_vm.val, null) > -1 : _vm.val
    },
    on: {
      "change": [function ($event) {
        var $$a = _vm.val,
          $$el = $event.target,
          $$c = $$el.checked ? true : false;
        if (Array.isArray($$a)) {
          var $$v = null,
            $$i = _vm._i($$a, $$v);
          if ($$el.checked) {
            $$i < 0 && (_vm.val = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.val = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.val = $$c;
        }
      }, function ($event) {
        $event.stopPropagation();
        return _vm.isToggled.apply(null, arguments);
      }],
      "focus": _vm.onFocus,
      "blur": _vm.onBlur
    }
  }, 'input', _vm.inputAttrs, false)), _vm._v(" "), _c('div', {
    staticClass: "bb-toggle__slider"
  }, [_c('div', {
    staticClass: "bb-toggle__dot"
  }, [_c('bb-icon', {
    staticClass: "bb-toggle__dot-icon",
    attrs: {
      "name": _vm.computedDotIconName,
      "size": "24"
    }
  })], 1)])]);
};
var __vue_staticRenderFns__$1O = [];

/* style */
const __vue_inject_styles__$1Q = function (inject) {
  if (!inject) return;
  inject("data-v-48dfe283_0", {
    source: ".bb-toggle{position:relative;display:flex;align-items:center;cursor:pointer}.bb-toggle--label-align-auto{justify-content:space-between}.bb-toggle--label-align-right{justify-content:flex-end}.bb-toggle--disabled{cursor:not-allowed}.bb-toggle__slider{position:relative;display:flex;flex:none;align-items:center;justify-content:flex-start;border-style:solid;border-color:#dee3e8;border-radius:99rem;background:#fff;transition:color .2s,background-color .2s,border-color .2s}.bb-toggle--disabled .bb-toggle__slider{background:#f7f7f8}.bb-toggle__slider:hover{border-color:#003e8c}.bb-toggle--disabled .bb-toggle__slider:hover{border-color:#dee3e8;cursor:not-allowed}.bb-toggle--is-focused .bb-toggle__slider{border-color:#003e8c}.bb-toggle--size-md .bb-toggle__slider{width:3.5rem;height:1.875rem;padding:0 .125rem;border-width:.125rem}.bb-toggle--size-sm .bb-toggle__slider{width:2.1875rem;height:1.25rem;padding:0 .0625rem;border-width:.09375rem}.bb-toggle__dot{display:flex;align-items:center;justify-content:center;color:#bdc7d0;transition:transform .1s ease-out,color .1s}.bb-toggle:hover .bb-toggle__dot{color:#9dabb9}.bb-toggle:active .bb-toggle__dot{color:#89a}.bb-toggle--checked .bb-toggle__dot{color:#0ccca2}.bb-toggle--checked:hover .bb-toggle__dot{color:#0bb48f}.bb-toggle--checked:active .bb-toggle__dot{color:#099c7c}.bb-toggle--disabled .bb-toggle__dot{color:#bdc7d0}.bb-toggle--disabled:hover .bb-toggle__dot{color:#bdc7d0}.bb-toggle--size-md .bb-toggle__dot{width:1.5rem;height:1.5rem}.bb-toggle--checked.bb-toggle--size-md .bb-toggle__dot{transform:translateX(1.5rem)}.bb-toggle--size-sm .bb-toggle__dot{width:.9375rem;height:.9375rem}.bb-toggle--checked.bb-toggle--size-sm .bb-toggle__dot{transform:translateX(.9375rem)}.bb-toggle--size-md .bb-toggle__dot-icon{width:1.5rem;min-width:1.5rem;height:1.5rem}.bb-toggle--size-sm .bb-toggle__dot-icon{width:.9375rem;min-width:.9375rem;height:.9375rem}.bb-toggle__addon{display:inline-flex;flex-direction:column;justify-content:space-between;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;user-select:none}.bb-toggle__addon--multiline{height:100%;justify-content:center}.bb-toggle--label-align-right .bb-toggle__addon{align-items:flex-end;margin-right:1.75rem}.bb-toggle__label,.bb-toggle__status{font-size:.875rem;line-height:1.125rem}.bb-toggle--label-align-right .bb-toggle__label,.bb-toggle--label-align-right .bb-toggle__status{text-align:right}.bb-toggle__label{color:#57697a;text-transform:uppercase;white-space:nowrap}.bb-toggle__status{color:#57697a}.bb-toggle--checked .bb-toggle__status{color:#0ccca2}.bb-toggle--disabled .bb-toggle__status{color:#9dabb9}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1Q = undefined;
/* module identifier */
const __vue_module_identifier__$1Q = undefined;
/* functional template */
const __vue_is_functional_template__$1Q = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1Q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1O,
  staticRenderFns: __vue_staticRenderFns__$1O
}, __vue_inject_styles__$1Q, __vue_script__$1Q, __vue_scope_id__$1Q, __vue_is_functional_template__$1Q, __vue_module_identifier__$1Q, false, createInjector, undefined, undefined);

var script$1P = {
  name: 'bb-toolbar',
  mixins: [validator],
  props: {
    color: {
      type: String,
      default: 'white'
    }
  }
};

/* script */
const __vue_script__$1P = script$1P;

/* template */
var __vue_render__$1N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-toolbar",
    class: "bg-" + _vm.color
  }, [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$1N = [];

/* style */
const __vue_inject_styles__$1P = function (inject) {
  if (!inject) return;
  inject("data-v-314fb5c9_0", {
    source: ".bb-toolbar{display:flex;width:100%;min-height:50px;align-items:center;justify-content:center;padding:0 30px 0 35px}.bb-toolbar>:first-child{margin-right:auto}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1P = undefined;
/* module identifier */
const __vue_module_identifier__$1P = undefined;
/* functional template */
const __vue_is_functional_template__$1P = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1P = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1N,
  staticRenderFns: __vue_staticRenderFns__$1N
}, __vue_inject_styles__$1P, __vue_script__$1P, __vue_scope_id__$1P, __vue_is_functional_template__$1P, __vue_module_identifier__$1P, false, createInjector, undefined, undefined);

var Icon$J = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1087763849__a bb-icon__dynamic-fill",attrs:{"d":"M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"}})]) } };

Vue.component('icon-ui-chevron-down', Icon$J);

var script$1O = {
  name: 'bb-accordion-item',
  components: {
    BbFunctionalAccordion: __vue_component__$2m,
    BbLink: __vue_component__$2p,
    BbIcon: __vue_component__$2C
  },
  props: {
    titleTagName: {
      type: String,
      default: 'h4',
      validator: titleTagName => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(titleTagName)
    },
    open: Boolean,
    card: Boolean,
    flat: Boolean,
    link: {
      type: Object,
      default: null
    }
  },
  emits: ['open', 'close'],
  data() {
    return {
      expanded: this.open
    };
  },
  computed: {
    classes() {
      return ['bb-accordion-item', {
        'bb-accordion-item--card': this.card,
        'bb-accordion-item--flat': this.flat,
        'bb-accordion-item--link': this.link,
        'is-open': this.expanded
      }];
    }
  },
  watch: {
    expanded(newVal) {
      if (newVal) {
        this.$emit('open');
      } else {
        this.$emit('close');
      }
    },
    open(newVal) {
      this.expanded = newVal;
    }
  },
  methods: {
    onOpen() {
      this.expanded = true;
    },
    onClose() {
      this.expanded = false;
    }
  }
};

/* script */
const __vue_script__$1O = script$1O;

/* template */
var __vue_render__$1M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-functional-accordion', {
    class: _vm.classes,
    attrs: {
      "open": _vm.open
    },
    on: {
      "open": _vm.onOpen,
      "close": _vm.onClose
    },
    scopedSlots: _vm._u([{
      key: "header",
      fn: function (ref) {
        var accordionExpanded = ref.expanded;
        return [_c('div', {
          staticClass: "bb-accordion-item__header"
        }, [_c('div', {
          staticClass: "bb-accordion-item__header-content",
          attrs: {
            "tabindex": "-1"
          }
        }, [_c('div', {
          staticClass: "bb-accordion-item__header-icon"
        }, [_vm._t("icon")], 2), _vm._v(" "), _c(_vm.titleTagName, {
          tag: "component",
          staticClass: "bb-accordion-item__header-title"
        }, [_vm._t("title")], 2), _vm._v(" "), _c('div', {
          staticClass: "bb-accordion-item__header-after"
        }, [_vm._t("after")], 2), _vm._v(" "), _c('bb-icon', {
          attrs: {
            "name": "ui-chevron-down",
            "fill": "navy",
            "size": "24",
            "rotate": accordionExpanded ? 'down' : 'up'
          }
        })], 1)])];
      }
    }], null, true)
  }, [_vm._v(" "), _c('div', {
    staticClass: "bb-accordion-item__content"
  }, [_c('div', {
    staticClass: "bb-accordion-item__content-wrapper"
  }, [_vm._t("default")], 2), _vm._v(" "), _vm.link ? _c('bb-link', {
    staticClass: "bb-accordion-item__link",
    attrs: {
      "to": _vm.link.to ? _vm.link.to : null,
      "color": "blue",
      "no-underline": ""
    }
  }, [_c('span', {
    staticClass: "bb-accordion-item__link-label"
  }, [_vm._v("\n        " + _vm._s(_vm.link.label) + "\n      ")]), _vm._v(" "), _c('bb-icon', {
    staticClass: "bb-accordion-item__link-icon",
    attrs: {
      "name": "ui-chevron-down",
      "size": "18",
      "rotate": "left"
    }
  })], 1) : _vm._e()], 1)]);
};
var __vue_staticRenderFns__$1M = [];

/* style */
const __vue_inject_styles__$1O = function (inject) {
  if (!inject) return;
  inject("data-v-7481d0bb_0", {
    source: ".bb-accordion-item{width:100%;padding:.5rem}.bb-accordion-item__header{position:relative;border:none;-moz-appearance:none;-webkit-appearance:none;background:0 0;cursor:pointer;font-size:inherit;outline:0}.bb-accordion-item__header-content{position:relative;display:flex;height:2.25rem;align-items:center}.bb-accordion-item__header-icon:not(:empty){margin-right:1rem}.bb-accordion-item__header-title{margin-bottom:0;color:#00285a;font-size:1rem;transition:color .2s;user-select:none}.bb-accordion-item__header-after{margin-right:20px;margin-left:auto}.bb-accordion-item__header:hover .bb-accordion-item__header-title{color:#2485e5}.bb-accordion-item__header:focus .bb-accordion-item__header-content::after{position:absolute;top:0;left:0;width:100%;height:100%;border:1px dashed rgba(136,153,170,.5);border-radius:.1875rem;content:\"\"}.bb-accordion-item__header-content:focus,.bb-accordion-item__header:focus{outline:0}.bb-accordion-item__content{overflow:hidden;height:auto;color:#38414d;font-size:.875rem}.bb-accordion-item__content-wrapper{padding-top:.9375rem}.bb-accordion-item__link{margin-top:.9375rem}.bb-accordion-item__link-label{font-size:12px;text-transform:uppercase}.bb-accordion-item--card{padding:1.25rem 1.25rem 1.25rem 1.5625rem;border-radius:.625rem;transition:padding-bottom .5s cubic-bezier(.2,.25,.3,5) 0s;will-change:padding-bottom;box-shadow:0 .0625rem 1.0625rem 0 rgba(0,0,0,.06)}.bb-accordion-item--card.bb-accordion-item--link .bb-accordion-item__content-wrapper{padding-bottom:.9375rem}.bb-accordion-item--card.is-open{padding-bottom:1.25rem}.bb-accordion-item--flat{padding:1.25rem 1.25rem 1.25rem 1.5625rem;border-radius:.625rem;transition:padding-bottom .5s cubic-bezier(.2,.25,.3,5) 0s;will-change:padding-bottom;box-shadow:0 0 0 .0625rem #dee3e8}.bb-accordion-item--flat.bb-accordion-item--link .bb-accordion-item__content-wrapper{padding-bottom:.9375rem}.bb-accordion-item--flat.is-open{padding-bottom:1.25rem}.bb-accordion-item--flat:hover{background:#f7f7f8}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1O = undefined;
/* module identifier */
const __vue_module_identifier__$1O = undefined;
/* functional template */
const __vue_is_functional_template__$1O = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1O = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1M,
  staticRenderFns: __vue_staticRenderFns__$1M
}, __vue_inject_styles__$1O, __vue_script__$1O, __vue_scope_id__$1O, __vue_is_functional_template__$1O, __vue_module_identifier__$1O, false, createInjector, undefined, undefined);

var script$1N = {
  name: 'bb-accordion',
  components: {
    BbAccordionItem: __vue_component__$1O
  },
  props: {
    card: Boolean,
    flat: Boolean,
    value: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      activeIndex: null
    };
  },
  computed: {
    classes() {
      return ['bb-accordion', {
        'bb-accordion--group': this.isGroup
      }];
    },
    isGroup() {
      return this.value && this.value.length > 1;
    }
  },
  mounted() {
    this.value.forEach((element, i) => {
      if (element.open) this.activeIndex = i;
    });
  }
};

/* script */
const __vue_script__$1N = script$1N;

/* template */
var __vue_render__$1L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, _vm._l(_vm.value, function (item, itemIndex) {
    return _c('bb-accordion-item', {
      key: item.id,
      class: "bb-accordion__item bb-accordion__item-" + itemIndex,
      attrs: {
        "open": itemIndex === _vm.activeIndex,
        "card": _vm.card,
        "flat": _vm.flat,
        "link": item.link
      },
      on: {
        "open": function ($event) {
          _vm.activeIndex = itemIndex;
        }
      },
      scopedSlots: _vm._u([{
        key: "icon",
        fn: function () {
          return [_vm._t(item.id + "-icon")];
        },
        proxy: true
      }, {
        key: "title",
        fn: function () {
          return [_vm._t(item.id + "-title", function () {
            return [_vm._v("\n        " + _vm._s(item.title) + "\n      ")];
          })];
        },
        proxy: true
      }, {
        key: "after",
        fn: function () {
          return [_vm._t(item.id + "-after")];
        },
        proxy: true
      }], null, true)
    }, [_vm._v(" "), _vm._v(" "), _vm._t(item.id)], 2);
  }), 1);
};
var __vue_staticRenderFns__$1L = [];

/* style */
const __vue_inject_styles__$1N = function (inject) {
  if (!inject) return;
  inject("data-v-fb9bed6e_0", {
    source: ".bb-accordion--group .bb-accordion__item:not(:last-child){margin-bottom:.9375rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1N = undefined;
/* module identifier */
const __vue_module_identifier__$1N = undefined;
/* functional template */
const __vue_is_functional_template__$1N = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1N = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1L,
  staticRenderFns: __vue_staticRenderFns__$1L
}, __vue_inject_styles__$1N, __vue_script__$1N, __vue_scope_id__$1N, __vue_is_functional_template__$1N, __vue_module_identifier__$1N, false, createInjector, undefined, undefined);

function filterVisible(els) {
  return (els || []).filter(isVisible);
}
const ITEM_SELECTOR = '.dropdown-item:not(.disabled):not([disabled])';
const AttachmentMap = {
  BOTTOM: 'bottom-start',
  BOTTOMEND: 'bottom-end'
};
var script$1M = {
  name: 'bb-dropdown',
  mixins: [ClickoutMixin, ListenOnRootMixin],
  props: {
    autosize: {
      type: Boolean,
      default: false
    },
    noMinWidth: {
      type: Boolean,
      default: false
    },
    boundary: {
      type: [String, Object],
      default: 'scrollParent'
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    display: {
      type: String
    },
    condensed: {
      type: Boolean
    },
    gap: {
      type: [Number, String]
    },
    disabled: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: ''
    },
    right: {
      type: Boolean,
      default: false
    },
    offset: {
      type: [Number, String],
      default: 0
    },
    noFlip: {
      type: Boolean,
      default: false
    },
    popperOpts: {
      type: Object,
      default: () => {}
    },
    contentClasses: {
      type: [Array, String, Object]
    },
    remainOpenAfterItemClick: {
      type: Boolean,
      default: false
    }
  },
  emits: ['show', 'shown', 'bb::dropdown::shown', 'hide', 'hidden', 'bb::dropdown::hidden', 'click'],
  data() {
    return {
      visible: false,
      inNavbar: null
    };
  },
  computed: {
    toggler() {
      return this.$refs.toggle.$el || this.$refs.toggle;
    },
    dropdownClasses() {
      return ['bb-dropdown', {
        'is-shown': this.visible,
        'bb-dropdown--position-static': this.boundary !== 'scrollParent' || !this.boundary,
        'bb-dropdown--block': this.display === 'block',
        'bb-dropdown--autosize': this.autosize,
        'bb-dropdown--condensed': this.condensed,
        'bb-dropdown--no-min-width': this.noMinWidth
      }];
    },
    menuClasses() {
      return ['bb-dropdown__menu', this.gap ? `p-t-${this.gap}` : '', this.gap ? `p-b-${this.gap}` : ''];
    }
  },
  watch: {
    visible(state, old) {
      if (state === old) {
        return;
      }
      if (state) {
        this.showMenu();
      } else {
        this.hideMenu();
      }
    },
    disabled(state, old) {
      if (state !== old && state && this.visible) {
        this.visible = false;
      }
    }
  },
  created() {
    this._popper = null;
  },
  mounted() {
    this.listenOnRoot('bb::dropdown::shown', this.rootCloseListener);
    if (!this.remainOpenAfterItemClick) {
      this.listenOnRoot('bb::dropdown-item::clicked', this.rootCloseListener);
    }
  },
  deactivated() {
    this.visible = false;
    this.setTouchStart(false);
    this.removePopper();
  },
  beforeUnmount() {
    this.visible = false;
    this.setTouchStart(false);
    this.removePopper();
  },
  methods: {
    showMenu() {
      if (this.disabled) {
        return;
      }
      this.$emit('show');
      this.emitOnRoot('bb::dropdown::shown', this);
      if (this.inNavbar === null) {
        this.inNavbar = Boolean(closest$1('.navbar', this.$el));
      }
      if (!this.inNavbar) {
        let element = this.right ? this.$el : this.$refs.toggle;
        element = element.$el || element;
        this.createPopper(element);
      }
      this.setTouchStart(true);
      this.$emit('shown');
      if (!this.inNavbar) {
        this.$nextTick(this.focusFirstItem);
      }
    },
    hideMenu() {
      this.$emit('hide');
      this.setTouchStart(false);
      this.emitOnRoot('bb::dropdown::hidden', this);
      this.$emit('hidden');
      this.removePopper();
    },
    createPopper(element) {
      this.removePopper();
      this._popper = createPopper(element, this.$refs.menu, this.getPopperConfig());
    },
    removePopper() {
      if (this._popper) {
        this._popper.destroy();
      }
      this._popper = null;
    },
    getPopperConfig() {
      let placement = AttachmentMap.BOTTOM;
      if (this.right) {
        placement = AttachmentMap.BOTTOMEND;
      }
      const popperConfig = {
        placement,
        modifiers: [{
          name: 'offset',
          enabled: true,
          options: {
            offset: this.offset || 0
          }
        }, {
          name: 'flip',
          enabled: !this.noFlip
        }]
      };
      if (this.boundary) {
        popperConfig.modifiers.preventOverflow = {
          boundariesElement: this.boundary
        };
      }
      return Object.assign(popperConfig, this.popperOpts || {});
    },
    setTouchStart(on) {
      if ('ontouchstart' in document.documentElement) {
        const children = Array.from(document.body.children);
        children.forEach(() => {
          if (on) {
            eventOn('mouseover', this._noop);
          } else {
            eventOff('mouseover', this._noop);
          }
        });
      }
    },
    _noop() {
    },
    rootCloseListener(vm) {
      if (vm !== this) {
        this.visible = false;
      }
    },
    clickOutListener() {
      this.visible = false;
    },
    show() {
      if (this.disabled) {
        return;
      }
      this.visible = true;
    },
    hide() {
      if (this.disabled) {
        return;
      }
      this.visible = false;
    },
    toggle(evt) {
      evt = evt || {};
      const type = evt.type;
      const key = evt.keyCode;
      if (type !== 'click' && !(type === 'keydown' && (key === KeyCodes.ENTER || key === KeyCodes.SPACE || key === KeyCodes.DOWN))) {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      if (this.disabled) {
        this.visible = false;
        return;
      }
      this.visible = !this.visible;
    },
    click(evt) {
      if (this.disabled) {
        this.visible = false;
        return;
      }
      this.$emit('click', evt);
    },
    onKeyDown(evt) {
      const key = evt.keyCode;
      if (key === KeyCodes.ESC) {
        this.onEsc(evt);
      } else if (key === KeyCodes.TAB) {
        this.onTab(evt);
      } else if (key === KeyCodes.DOWN) {
        this.focusNext(evt, false);
      } else if (key === KeyCodes.UP) {
        this.focusNext(evt, true);
      }
    },
    onEsc(evt) {
      if (this.visible) {
        this.visible = false;
        evt.preventDefault();
        evt.stopPropagation();
        this.$nextTick(this.focusToggler);
      }
    },
    onTab() {
      if (this.visible) {
        this.visible = false;
      }
    },
    onMouseOver(evt) {
      const item = evt.target;
      if (item.classList.contains('dropdown-item') && !item.disabled && !item.classList.contains('disabled') && item.focus) {
        item.focus();
      }
    },
    focusNext(evt, up) {
      if (!this.visible) {
        return;
      }
      evt.preventDefault();
      evt.stopPropagation();
      this.$nextTick(() => {
        const items = this.getItems();
        if (items.length < 1) {
          return;
        }
        let index = items.indexOf(evt.target);
        if (up && index > 0) {
          index--;
        } else if (!up && index < items.length - 1) {
          index++;
        }
        if (index < 0) {
          index = 0;
        }
        this.focusItem(index, items);
      });
    },
    focusItem(idx, items) {
      const el = items.find((__el, i) => i === idx);
      if (el && getAttr(el, 'tabindex') !== '-1') {
        el.focus();
      }
    },
    getItems() {
      return filterVisible(selectAll(ITEM_SELECTOR, this.$refs.menu));
    },
    getFirstItem() {
      const item = this.getItems()[0];
      return item || null;
    },
    focusFirstItem() {
      const item = this.getFirstItem();
      if (item) {
        this.focusItem(0, [item]);
      }
    },
    focusToggler() {
      const toggler = this.toggler;
      if (toggler && toggler.focus) {
        toggler.focus();
      }
    },
    toggleDropdown(e) {
      if (!this.hoverable) this.toggle(e);
    },
    hideDropdown() {
      if (this.hoverable) this.hide();
    },
    showDropdown() {
      if (this.hoverable) this.show();
    }
  }
};

/* script */
const __vue_script__$1M = script$1M;

/* template */
var __vue_render__$1K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.dropdownClasses,
    on: {
      "mouseover": _vm.showDropdown,
      "mouseout": _vm.hideDropdown
    }
  }, [_c('div', {
    ref: "toggle",
    staticClass: "bb-dropdown__toggle",
    attrs: {
      "aria-haspopup": "menu",
      "aria-expanded": _vm.visible
    },
    on: {
      "keydown": _vm.toggle,
      "click": _vm.toggleDropdown
    }
  }, [_vm._t("trigger")], 2), _vm._v(" "), _c('div', {
    ref: "menu",
    class: _vm.menuClasses,
    on: {
      "mouseover": _vm.onMouseOver,
      "keydown": _vm.onKeyDown
    }
  }, [_c('div', {
    staticClass: "bb-dropdown__content",
    class: _vm.contentClasses,
    attrs: {
      "role": "menu"
    }
  }, [_vm._t("default")], 2)])]);
};
var __vue_staticRenderFns__$1K = [];

/* style */
const __vue_inject_styles__$1M = function (inject) {
  if (!inject) return;
  inject("data-v-399c692c_0", {
    source: ".bb-dropdown{position:relative;display:inline-block}.bb-dropdown--block{display:block;width:100%}.bb-dropdown--position-static{position:static}.bb-dropdown__toggle{min-width:12rem}.bb-dropdown--autosize .bb-dropdown__toggle,.bb-dropdown--condensed .bb-dropdown__toggle,.bb-dropdown--no-min-width .bb-dropdown__toggle{min-width:unset}.bb-dropdown__menu{position:absolute;z-index:1400;top:100%;left:0;display:none;width:100%;padding:.125rem 0}.bb-dropdown--autosize .bb-dropdown__menu{width:auto}.bb-dropdown--condensed .bb-dropdown__menu{width:21.25rem;max-width:calc(100vw - 10px)}.is-shown .bb-dropdown__menu{display:block}.bb-dropdown__menu[x-placement^=top]{padding-top:0}.bb-dropdown__menu[x-placement^=bottom]{padding-bottom:0}.bb-dropdown__content{overflow:hidden;width:100%;min-width:12rem;padding:0;border-radius:.375rem;background-clip:padding-box;background-color:#fff;box-shadow:0 .0625rem .25rem 0 rgba(0,0,0,.2);float:left;list-style:none;text-align:left}.bb-dropdown--no-min-width .bb-dropdown__content{min-width:unset}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1M = undefined;
/* module identifier */
const __vue_module_identifier__$1M = undefined;
/* functional template */
const __vue_is_functional_template__$1M = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1M = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1K,
  staticRenderFns: __vue_staticRenderFns__$1K
}, __vue_inject_styles__$1M, __vue_script__$1M, __vue_scope_id__$1M, __vue_is_functional_template__$1M, __vue_module_identifier__$1M, false, createInjector, undefined, undefined);

var Icon$I = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1288314108__a bb-icon__dynamic-fill",attrs:{"d":"M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z"}})]) } };

Vue.component('icon-ui-content-copy', Icon$I);

Vue.use(Clipboard);
var script$1L = {
  name: 'bb-account-card',
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbIconFrame: __vue_component__$2g,
    BbSeparator: __vue_component__$2a,
    BbLabeledValue: __vue_component__$2c,
    BbSkeleton: __vue_component__$2d,
    BbTooltip: __vue_component__$2B,
    BbDropdown: __vue_component__$1M,
    BbDropdownItem: __vue_component__$2o
  },
  props: {
    titleOptions: {
      type: Array,
      default() {
        return [];
      }
    },
    titleLabel: String,
    tooltipContent: String,
    copyButton: {
      type: Object,
      default() {
        return {};
      }
    },
    sections: {
      type: Array,
      default() {
        return [];
      }
    },
    loading: Boolean,
    copyButtonProps: Object
  },
  emits: ['titleClick', 'copyButtonClick'],
  data() {
    return {
      internalTitles: JSON.parse(JSON.stringify(this.titleOptions) || []),
      inactiveTitles: [],
      showCopyTooltip: false,
      copyFeedbackMsg: null
    };
  },
  computed: {
    classes() {
      return {
        'bb-account-card--tooltip': this.hasTooltip
      };
    },
    hasMultipleTitleOptions() {
      return this.titleOptions.length > 1;
    },
    hasTooltip() {
      return notEmpty(this.tooltipContent);
    },
    activeTitle() {
      return this.internalTitles && this.internalTitles.find(val => val.active) || {};
    }
  },
  watch: {
    titleOptions: {
      handler(e) {
        this.internalTitles = JSON.parse(JSON.stringify(e));
        this.setFirstTitleActive();
      },
      deep: true
    },
    internalTitles: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal) this.inactiveTitles = newVal.filter(val => !val.active);
      }
    }
  },
  created() {
    this.setFirstTitleActive();
  },
  methods: {
    setFirstTitleActive() {
      if (this.internalTitles && !this.internalTitles.find(val => val.active)) {
        this.internalTitles[0].active = true;
      }
    },
    onTitleClick(title) {
      const clickedTitleIndex = this.titleOptions.findIndex(obj => obj.label === title.label);
      this.internalTitles.forEach((internalTitle, internalTitleIndex) => {
        this.$set(this.internalTitles[internalTitleIndex], 'active', internalTitleIndex === clickedTitleIndex);
      });
      this.$emit('titleClick', title);
    },
    onCopyButtonClick() {
      this.showCopyTooltip = !this.showCopyTooltip;
      this.$emit('copyButtonClick');
    },
    onCopy(status) {
      this.showCopyTooltip = true;
      this.copyFeedbackMsg = status ? this.copyButton.successMsg : this.copyButton.errorMsg;
    }
  }
};

/* script */
const __vue_script__$1L = script$1L;

/* template */
var __vue_render__$1J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-account-card",
    class: _vm.classes
  }, [_c('div', {
    staticClass: "bb-account-card__heading"
  }, [_c('div', {
    staticClass: "bb-account-card__title-wrapper"
  }, [_vm.loading ? _c('bb-skeleton', {
    staticClass: "bb-account-card__copy-button"
  }) : _c('bb-button', _vm._b({
    directives: [{
      name: "clipboard",
      rawName: "v-clipboard:copy",
      value: _vm.activeTitle.label,
      expression: "activeTitle.label",
      arg: "copy"
    }, {
      name: "clipboard",
      rawName: "v-clipboard:success",
      value: _vm.onCopy,
      expression: "onCopy",
      arg: "success"
    }, {
      name: "clipboard",
      rawName: "v-clipboard:error",
      value: _vm.onCopy,
      expression: "onCopy",
      arg: "error"
    }],
    staticClass: "bb-account-card__copy-button",
    attrs: {
      "color": "gray",
      "icon": "ui-content-copy",
      "inverted": "",
      "display": "circle"
    },
    on: {
      "click": _vm.onCopyButtonClick
    }
  }, 'bb-button', _vm.copyButtonProps, false), [_vm.showCopyTooltip ? _c('bb-tooltip', {
    attrs: {
      "bubble": ""
    }
  }, [_vm._v("\n          " + _vm._s(_vm.copyFeedbackMsg) + "\n        ")]) : _vm._e()], 1), _vm._v(" "), _vm.hasMultipleTitleOptions && !_vm.loading ? _c('bb-dropdown', {
    staticClass: "bb-account-card__title-dropdown",
    attrs: {
      "autosize": ""
    },
    scopedSlots: _vm._u([{
      key: "trigger",
      fn: function () {
        return [_c('bb-button', {
          staticClass: "bb-account-card__title",
          attrs: {
            "color": "gray",
            "display": "link",
            "no-underline": "",
            "no-paddings": "",
            "icon-after": "ui-chevron-down",
            "icon-after-color": "gray-70"
          }
        }, [_c('bb-labeled-value', {
          attrs: {
            "label": _vm.titleLabel,
            "value": _vm.activeTitle.label,
            "label-color": "gray-70",
            "label-weight": "medium",
            "label-uppercase": "",
            "color": "gray-90",
            "size": "18"
          }
        })], 1)];
      },
      proxy: true
    }], null, false, 738300479)
  }, [_vm._v(" "), _vm._l(_vm.inactiveTitles, function (inactiveTitle, index) {
    return _c('bb-dropdown-item', {
      key: index,
      attrs: {
        "label": inactiveTitle.label
      },
      on: {
        "click": function ($event) {
          return _vm.onTitleClick(inactiveTitle);
        }
      }
    });
  })], 2) : _c('div', {
    staticClass: "bb-account-card__title"
  }, [_c('bb-labeled-value', {
    attrs: {
      "label": _vm.titleLabel,
      "value": _vm.activeTitle.label,
      "loading": _vm.loading,
      "label-color": "gray-70",
      "label-weight": "medium",
      "label-uppercase": "",
      "color": "gray-90",
      "size": "18"
    }
  })], 1)], 1), _vm._v(" "), _vm.hasTooltip ? _c('div', {
    staticClass: "bb-account-card__tooltip"
  }, [_vm.loading ? [_c('bb-skeleton', {
    attrs: {
      "width": "18",
      "height": "18"
    }
  })] : [_c('bb-tooltip', {
    scopedSlots: _vm._u([{
      key: "trigger",
      fn: function () {
        return [_c('bb-icon', {
          attrs: {
            "name": "ui-help-circle",
            "fill": "blue",
            "size": "18",
            "tabindex": "0"
          }
        })];
      },
      proxy: true
    }], null, false, 3692176240)
  }, [_vm._v("\n          " + _vm._s(_vm.tooltipContent) + "\n        ")])]], 2) : _vm._e()]), _vm._v(" "), _c('bb-separator', {
    staticClass: "bb-account-card__title-separator"
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-account-card__sections"
  }, _vm._l(_vm.sections, function (section, index) {
    return _c('div', {
      key: index,
      staticClass: "bb-account-card__section"
    }, [_vm.loading ? _c('bb-skeleton', {
      staticClass: "bb-account-card__section-icon",
      attrs: {
        "width": "40",
        "height": "40",
        "radius": "10"
      }
    }) : _c('bb-icon-frame', {
      key: index,
      staticClass: "bb-account-card__section-icon",
      attrs: {
        "icon": section.icon,
        "icon-color": section.iconColor,
        "background-color": "gray-10"
      }
    }), _vm._v(" "), _c('bb-labeled-value', {
      attrs: {
        "label": section.label,
        "value": section.content,
        "loading": _vm.loading,
        "label-color": "gray-70",
        "label-weight": "medium",
        "label-uppercase": "",
        "color": "gray",
        "size": "18"
      }
    })], 1);
  }), 0)], 1);
};
var __vue_staticRenderFns__$1J = [];

/* style */
const __vue_inject_styles__$1L = function (inject) {
  if (!inject) return;
  inject("data-v-ff903364_0", {
    source: ".bb-account-card{position:relative;display:flex;flex-direction:column;justify-content:space-between;border-radius:.625rem;background:#fff;box-shadow:0 .0625rem .9375rem 0 rgba(74,74,74,.06);transition:transform .2s ease-out,box-shadow .2s ease-out;white-space:nowrap}@media (min-width:992px){.bb-account-card{flex-direction:row}}.bb-account-card__heading{display:flex;align-items:center;justify-content:space-between;padding:.46875rem;padding-left:.9375rem}@media (min-width:992px){.bb-account-card__heading{justify-content:initial;padding:1.875rem;padding-right:0}}.bb-account-card__title-wrapper{display:flex;align-items:center}.bb-account-card__tooltip{position:relative;display:flex}@media (min-width:992px){.bb-account-card__tooltip{position:absolute;top:.625rem;right:.625rem}}.bb-account-card__copy-button{margin-right:1.5625rem}@media (min-width:992px){.bb-account-card__copy-button{margin-right:.9375rem}}.bb-account-card__copy-button.bb-button{width:1.875rem;height:1.875rem}@media (min-width:992px){.bb-account-card__copy-button.bb-button{width:2.5rem;height:2.5rem}}.bb-account-card__copy-button.bb-button__icon{width:1.125rem;height:1.125rem}@media (min-width:992px){.bb-account-card__copy-button.bb-button__icon{width:1.5rem;height:1.5rem}}.bb-account-card__copy-button.bb-skeleton{width:1.875rem!important;height:1.875rem!important}@media (min-width:992px){.bb-account-card__copy-button.bb-skeleton{width:2.5rem!important;height:2.5rem!important}}.bb-account-card__title{display:flex;align-items:flex-end}.bb-account-card__label-skeleton{margin-bottom:.3125rem}.bb-account-card__title-separator.bb-separator{border-color:#eeeff2}@media (min-width:992px){.bb-account-card__title-separator.bb-separator{display:none}}.bb-account-card__sections{display:flex;flex-direction:column;padding:.9375rem}@media (min-width:992px){.bb-account-card__sections{flex-direction:row;padding:1.875rem 2.8125rem 1.875rem 0}}.bb-account-card__section{display:flex;align-items:center}.bb-account-card__section:not(:last-of-type){margin-bottom:.9375rem}@media (min-width:992px){.bb-account-card__section{margin-bottom:0}.bb-account-card__section:last-of-type{margin-left:3.125rem}.bb-account-card__section:not(:last-of-type){margin-bottom:0}}.bb-account-card__section-icon{margin-right:.9375rem}.bb-account-card__section-text{display:flex;flex-direction:column;justify-content:center}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1L = undefined;
/* module identifier */
const __vue_module_identifier__$1L = undefined;
/* functional template */
const __vue_is_functional_template__$1L = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1L = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1J,
  staticRenderFns: __vue_staticRenderFns__$1J
}, __vue_inject_styles__$1L, __vue_script__$1L, __vue_scope_id__$1L, __vue_is_functional_template__$1L, __vue_module_identifier__$1L, false, createInjector, undefined, undefined);

var script$1K = {
  name: 'bb-article-footer',
  components: {
    BbSeparator: __vue_component__$2a
  },
  props: {
    name: String,
    avatar: String,
    date: String,
    datePrefix: {
      type: String,
      default: 'on'
    },
    dateSuffix: {
      type: String,
      default: ''
    },
    ariaLabels: {
      type: Object,
      default: () => ({
        container: 'Article metadata',
        published: 'Published'
      })
    }
  },
  computed: {
    computedDate() {
      return this.date ? this.date : '';
    },
    computedDatePrefix() {
      return this.datePrefix ? `${this.datePrefix.trim()} ` : '';
    },
    computedDateSuffix() {
      return this.dateSuffix ? ` ${this.dateSuffix.trim()}` : '';
    },
    computedDateString() {
      return `${this.computedDatePrefix}${this.computedDate}${this.computedDateSuffix}`;
    },
    containerAriaLabel() {
      return this.ariaLabels.container;
    },
    avatarAriaLabel() {
      var _this$name;
      return (_this$name = this.name) !== null && _this$name !== void 0 ? _this$name : undefined;
    },
    publishedAriaLabel() {
      return this.computedDate ? `${this.ariaLabels.published} ${this.computedDateString}` : undefined;
    }
  }
};

/* script */
const __vue_script__$1K = script$1K;

/* template */
var __vue_render__$1I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('footer', {
    staticClass: "bb-article-footer"
  }, [_c('bb-separator', {
    staticClass: "bb-article-footer__separator"
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-article-footer__content",
    attrs: {
      "role": "complementary",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-article-footer__avatar",
    style: {
      backgroundImage: "url(" + _vm.avatar + ")"
    },
    attrs: {
      "role": "img",
      "aria-label": _vm.avatarAriaLabel
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-article-footer__text"
  }, [_c('div', {
    staticClass: "bb-article-footer__name"
  }, [_vm._v("\n        " + _vm._s(_vm.name) + "\n      ")]), _vm._v(" "), _c('time', {
    staticClass: "bb-article-footer__date",
    attrs: {
      "datetime": _vm.computedDate || undefined,
      "aria-label": _vm.publishedAriaLabel
    }
  }, [_vm._v("\n        " + _vm._s(_vm.computedDateString) + "\n      ")])])]), _vm._v(" "), _c('bb-separator', {
    staticClass: "bb-article-footer__separator"
  })], 1);
};
var __vue_staticRenderFns__$1I = [];

/* style */
const __vue_inject_styles__$1K = function (inject) {
  if (!inject) return;
  inject("data-v-54c2bbe0_0", {
    source: ".bb-article-footer .bb-article-footer__separator{border-color:#eeeff2}.bb-article-footer .bb-article-footer__content{display:flex;padding:1.25rem 0}.bb-article-footer .bb-article-footer__avatar{width:2.5rem;height:2.5rem;flex-shrink:0;border-radius:50%;margin-right:.9375rem;background-color:#eeeff2;background-position:center;background-repeat:no-repeat;background-size:cover}.bb-article-footer .bb-article-footer__text{display:flex;flex-direction:column;flex-grow:1}@media (min-width:576px){.bb-article-footer .bb-article-footer__text{flex-direction:row;align-items:center;justify-content:space-between}}.bb-article-footer .bb-article-footer__name{color:#00285a;font-size:.875rem}@media (min-width:576px){.bb-article-footer .bb-article-footer__name{font-size:1rem}}.bb-article-footer .bb-article-footer__date{color:#57697a;font-size:.875rem}@media (min-width:576px){.bb-article-footer .bb-article-footer__date{text-align:right}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1K = undefined;
/* module identifier */
const __vue_module_identifier__$1K = undefined;
/* functional template */
const __vue_is_functional_template__$1K = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1K = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1I,
  staticRenderFns: __vue_staticRenderFns__$1I
}, __vue_inject_styles__$1K, __vue_script__$1K, __vue_scope_id__$1K, __vue_is_functional_template__$1K, __vue_module_identifier__$1K, false, createInjector, undefined, undefined);

var script$1J = {
  name: 'bb-article-meta',
  components: {
    BbContainer: __vue_component__$2q,
    BbSocialLinks: __vue_component__$1Z
  },
  props: {
    category: String,
    categoryLabel: {
      type: String,
      default: 'Category'
    },
    date: String,
    dateLabel: {
      type: String,
      default: 'Published on'
    },
    readingTime: String,
    readingTimeLabel: {
      type: String,
      default: 'Reading time'
    },
    sharing: Array,
    sharingLabel: {
      type: String,
      default: 'Share'
    },
    ariaLabel: {
      type: String,
      default: 'Article metadata'
    }
  },
  data() {
    return {
      uuid: uuid()
    };
  },
  computed: {
    ids() {
      return {
        categoryLabelId: `category-label-${this.uuid}`,
        dateLabelId: `date-label-${this.uuid}`,
        readingTimeLabelId: `reading-time-label-${this.uuid}`,
        sharingLabelId: `sharing-label-${this.uuid}`,
        sharingMobileLabelId: `sharing-mobile-label-${this.uuid}`
      };
    }
  }
};

/* script */
const __vue_script__$1J = script$1J;

/* template */
var __vue_render__$1H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('aside', {
    staticClass: "bb-article-meta",
    attrs: {
      "aria-label": _vm.ariaLabel
    }
  }, [_vm.category ? _c('section', {
    staticClass: "bb-article-meta__section",
    attrs: {
      "aria-labelledby": _vm.ids.categoryLabelId
    }
  }, [_c('span', {
    attrs: {
      "id": _vm.ids.categoryLabelId
    }
  }, [_vm._v(_vm._s(_vm.categoryLabel))]), _vm._v(" "), _c('div', {
    staticClass: "bb-article-meta__section-content"
  }, [_vm._v("\n      " + _vm._s(_vm.category) + "\n    ")])]) : _vm._e(), _vm._v(" "), _vm.date ? _c('section', {
    staticClass: "bb-article-meta__section",
    attrs: {
      "aria-labelledby": _vm.ids.dateLabelId
    }
  }, [_c('span', {
    attrs: {
      "id": _vm.ids.dateLabelId
    }
  }, [_vm._v(_vm._s(_vm.dateLabel))]), _vm._v(" "), _c('div', {
    staticClass: "bb-article-meta__section-content"
  }, [_vm._v("\n      " + _vm._s(_vm.date) + "\n    ")])]) : _vm._e(), _vm._v(" "), _vm.readingTime ? _c('section', {
    staticClass: "bb-article-meta__section",
    attrs: {
      "aria-labelledby": _vm.ids.readingTimeLabelId
    }
  }, [_c('span', {
    attrs: {
      "id": _vm.ids.readingTimeLabelId
    }
  }, [_vm._v(_vm._s(_vm.readingTimeLabel))]), _vm._v(" "), _c('div', {
    staticClass: "bb-article-meta__section-content"
  }, [_vm._v("\n      " + _vm._s(_vm.readingTime) + "\n    ")])]) : _vm._e(), _vm._v(" "), _vm.sharing ? _c('section', {
    staticClass: "bb-article-meta__section bb-article-meta__sharing",
    attrs: {
      "aria-labelledby": _vm.ids.sharingLabelId
    }
  }, [_c('span', {
    attrs: {
      "id": _vm.ids.sharingLabelId
    }
  }, [_vm._v(_vm._s(_vm.sharingLabel))]), _vm._v(" "), _c('bb-social-links', {
    staticClass: "bb-article-meta__social-links",
    attrs: {
      "links": _vm.sharing,
      "icon-color": "gray-80",
      "no-separators": ""
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('bb-container', {
    staticClass: "bb-article-meta__sharing-mobile",
    attrs: {
      "aria-labelledby": _vm.ids.sharingMobileLabelId
    }
  }, [_c('div', {
    staticClass: "bb-article-meta__sharing-mobile-wrapper"
  }, [_c('span', {
    staticClass: "bb-article-meta__sharing-mobile-label",
    attrs: {
      "id": _vm.ids.sharingMobileLabelId
    }
  }, [_vm._v("\n        " + _vm._s(_vm.sharingLabel) + "\n      ")]), _vm._v(" "), _c('bb-social-links', {
    staticClass: "bb-article-meta__social-links",
    attrs: {
      "links": _vm.sharing,
      "no-separators": ""
    }
  })], 1)])], 1);
};
var __vue_staticRenderFns__$1H = [];

/* style */
const __vue_inject_styles__$1J = function (inject) {
  if (!inject) return;
  inject("data-v-2ae2fe08_0", {
    source: ".bb-article-meta{position:fixed;bottom:0;left:0;display:flex;width:100%;height:3.125rem;align-items:center;background:#fff;box-shadow:0 -.0625rem 1.0625rem rgba(0,0,0,.07)}@media (min-width:576px){.bb-article-meta{position:static;display:block;height:auto;background:0 0;box-shadow:none}}.bb-article-meta .bb-article-meta__section,.bb-article-meta .bb-article-meta__sharing-mobile{color:#89a;font-size:.75rem}.bb-article-meta .bb-article-meta__section{display:none;margin-bottom:1.5625rem}@media (min-width:576px){.bb-article-meta .bb-article-meta__section{display:block}}.bb-article-meta .bb-article-meta__section-content{color:#57697a;font-size:.875rem;line-height:1.8}.bb-article-meta .bb-article-meta__social-links{margin-top:.75rem}.bb-article-meta .bb-article-meta__sharing-mobile{display:flex;align-items:center}@media (min-width:576px){.bb-article-meta .bb-article-meta__sharing-mobile{display:none}}.bb-article-meta .bb-article-meta__sharing-mobile-wrapper{position:relative;display:flex;flex-grow:1;align-items:center;justify-content:center}.bb-article-meta .bb-article-meta__sharing-mobile-label{position:absolute;left:0}.bb-article-meta .bb-article-meta__sharing-mobile .bb-article-meta__social-links{margin-top:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1J = undefined;
/* module identifier */
const __vue_module_identifier__$1J = undefined;
/* functional template */
const __vue_is_functional_template__$1J = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1J = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1H,
  staticRenderFns: __vue_staticRenderFns__$1H
}, __vue_inject_styles__$1J, __vue_script__$1J, __vue_scope_id__$1J, __vue_is_functional_template__$1J, __vue_module_identifier__$1J, false, createInjector, undefined, undefined);

var script$1I = {
  name: 'bb-article-photo',
  components: {
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r
  },
  props: {
    src: {
      required: true,
      type: [String, Object, Array]
    },
    alt: String,
    caption: String,
    height: String
  },
  data() {
    return {
      gutter: null,
      resizeObserver: null
    };
  },
  computed: {
    classes() {
      return ['bb-article-photo', {
        'bb-article-photo--multiple-photos': this.multiplePhotos
      }];
    },
    multiplePhotos() {
      return Array.isArray(this.src) && this.src.length > 1;
    },
    images() {
      return castArray(this.src).map(srcObjectOrString => {
        const {
          src: srcProperty,
          ...otherProperties
        } = srcObjectOrString;
        const srcString = srcProperty || srcObjectOrString;
        return {
          ...(srcProperty && otherProperties),
          'background-image': `url('${srcString}')`
        };
      });
    },
    columns() {
      const [firstImage, ...imagesAfterTheFirst] = this.images;
      const columns = [[firstImage], imagesAfterTheFirst];
      return columns.filter(images => images.length);
    },
    computedHeight() {
      return this.height ? {
        'height': this.height
      } : null;
    }
  },
  mounted() {
    this.addResizeListener();
  },
  destroyed() {
    this.removeResizeListener();
  },
  methods: {
    addResizeListener() {
      this.resizeObserver = new ResizeObserver(this.setGutter);
      this.resizeObserver.observe(document.body);
    },
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(document.body);
      }
    },
    setGutter: throttle(function () {
      var _window, _window$matchMedia;
      const mediaQuery = (_window = window) === null || _window === void 0 ? void 0 : (_window$matchMedia = _window.matchMedia) === null || _window$matchMedia === void 0 ? void 0 : _window$matchMedia.call(_window, '(min-width: 768px)');
      const isMobileWidth = !(mediaQuery !== null && mediaQuery !== void 0 && mediaQuery.matches);
      this.gutter = isMobileWidth ? '10' : null;
    }, 100)
  }
};

/* script */
const __vue_script__$1I = script$1I;

/* template */
var __vue_render__$1G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('figure', {
    class: _vm.classes
  }, [_c('bb-row', {
    staticClass: "bb-article-photo__images",
    style: _vm.computedHeight,
    attrs: {
      "gutter": _vm.gutter,
      "aria-role": "img",
      "aria-label": _vm.alt || _vm.caption,
      "alt": _vm.alt || _vm.caption
    }
  }, _vm._l(_vm.columns, function (column, indexColumn) {
    return _c('bb-col', {
      key: indexColumn,
      staticClass: "bb-article-photo__column"
    }, _vm._l(column, function (image, indexImage) {
      return _c('div', {
        key: indexImage,
        staticClass: "bb-article-photo__img",
        style: image
      });
    }), 0);
  }), 1), _vm._v(" "), _vm.caption ? _c('figcaption', {
    staticClass: "bb-article-photo__caption"
  }, [_vm._v("\n    " + _vm._s(_vm.caption) + "\n  ")]) : _vm._e()], 1);
};
var __vue_staticRenderFns__$1G = [];

/* style */
const __vue_inject_styles__$1I = function (inject) {
  if (!inject) return;
  inject("data-v-6a289520_0", {
    source: ".bb-article-photo__images{height:21rem;max-height:100vh}.bb-article-photo--multiple-photos .bb-article-photo__images{height:30rem}.bb-article-photo__img{width:100%;height:100%;border-radius:.625rem;background-position:center center;background-repeat:no-repeat;background-size:cover}.bb-article-photo__column{display:flex;flex-direction:column}.bb-article-photo__column>*{margin-bottom:1.875rem}.bb-article-photo__column>:last-child{margin-bottom:0}.bb-article-photo__caption{margin-top:.625rem;color:#57697a;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;text-align:center}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1I = undefined;
/* module identifier */
const __vue_module_identifier__$1I = undefined;
/* functional template */
const __vue_is_functional_template__$1I = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1I = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1G,
  staticRenderFns: __vue_staticRenderFns__$1G
}, __vue_inject_styles__$1I, __vue_script__$1I, __vue_scope_id__$1I, __vue_is_functional_template__$1I, __vue_module_identifier__$1I, false, createInjector, undefined, undefined);

var Icon$H = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2935423824__a bb-icon__dynamic-fill",attrs:{"d":"M10,7L8,11H11V17H5V11L7,7H10M18,7L16,11H19V17H13V11L15,7H18Z"}})]) } };

Vue.component('icon-ui-format-quote-open', Icon$H);

var script$1H = {
  name: 'bb-article-pullquote',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    quote: String,
    name: String,
    avatar: String,
    ariaLabel: {
      type: String,
      default: 'Pull quote'
    }
  },
  computed: {
    classes() {
      return ['bb-article-pullquote', {
        'bb-article-pullquote--has-author': this.name
      }];
    },
    avatarAriaLabel() {
      var _this$name;
      return (_this$name = this.name) !== null && _this$name !== void 0 ? _this$name : undefined;
    }
  }
};

/* script */
const __vue_script__$1H = script$1H;

/* template */
var __vue_render__$1F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.ariaLabel
    }
  }, [_c('bb-icon', {
    staticClass: "bb-article-pullquote__icon",
    attrs: {
      "name": "ui-format-quote-open",
      "fill": "white",
      "size": "48",
      "aria-hidden": "true"
    }
  }), _vm._v(" "), _c('blockquote', {
    staticClass: "bb-article-pullquote__quote"
  }, [_vm._v("\n    " + _vm._s(_vm.quote) + "\n  ")]), _vm._v(" "), _vm.name ? _c('div', {
    staticClass: "bb-article-pullquote__author"
  }, [_vm.avatar ? _c('div', {
    staticClass: "bb-article-pullquote__author-avatar",
    style: {
      backgroundImage: "url(" + _vm.avatar + ")"
    },
    attrs: {
      "role": "img",
      "aria-label": _vm.avatarAriaLabel
    }
  }) : _vm._e(), _vm._v("\n    " + _vm._s(_vm.name) + "\n  ")]) : _vm._e()], 1);
};
var __vue_staticRenderFns__$1F = [];

/* style */
const __vue_inject_styles__$1H = function (inject) {
  if (!inject) return;
  inject("data-v-a430afb0_0", {
    source: ".bb-article-pullquote{position:relative;padding:4rem;border-radius:.625rem;background:#2485e5}.bb-article-pullquote--has-author{padding:4.1875rem 1.625rem 1.875rem}@media (min-width:576px){.bb-article-pullquote--has-author{padding:4.1875rem 4rem 2.375rem}}.bb-article-pullquote .bb-article-pullquote__icon{position:absolute;top:.4375rem;left:.75rem}@media (min-width:576px){.bb-article-pullquote .bb-article-pullquote__icon{top:1.1875rem;left:1rem}}.bb-article-pullquote .bb-article-pullquote__quote{margin-bottom:0;color:#fff;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;line-height:1.5rem;text-align:center}@media (min-width:576px){.bb-article-pullquote .bb-article-pullquote__quote{font-size:2rem;line-height:3.125rem}}.bb-article-pullquote--has-author.bb-article-pullquote .bb-article-pullquote__quote{margin-bottom:1.6875rem}@media (min-width:576px){.bb-article-pullquote--has-author.bb-article-pullquote .bb-article-pullquote__quote{margin-bottom:2.5625rem}}.bb-article-pullquote .bb-article-pullquote__author{display:flex;align-items:center;color:#fff;font-size:.875rem}@media (min-width:576px){.bb-article-pullquote .bb-article-pullquote__author{font-size:1rem}}.bb-article-pullquote .bb-article-pullquote__author-avatar{width:2.5rem;height:2.5rem;flex-shrink:0;border-radius:50%;margin-right:.9375rem;background-color:#fff;background-position:center;background-repeat:no-repeat;background-size:cover}@media (min-width:576px){.bb-article-pullquote .bb-article-pullquote__author-avatar{width:3.75rem;height:3.75rem;margin-right:1.9375rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1H = undefined;
/* module identifier */
const __vue_module_identifier__$1H = undefined;
/* functional template */
const __vue_is_functional_template__$1H = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1H = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1F,
  staticRenderFns: __vue_staticRenderFns__$1F
}, __vue_inject_styles__$1H, __vue_script__$1H, __vue_scope_id__$1H, __vue_is_functional_template__$1H, __vue_module_identifier__$1H, false, createInjector, undefined, undefined);

var Icon$G = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1428399785__a bb-icon__dynamic-fill",attrs:{"d":"M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"}})]) } };

Vue.component('icon-ui-check-circle-outline', Icon$G);

var Icon$F = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg244579070__a bb-icon__dynamic-fill",attrs:{"d":"M12,2L1,21H23M12,6L19.53,19H4.47M11,10V14H13V10M11,16V18H13V16"}})]) } };

Vue.component('icon-ui-alert-outline', Icon$F);

var Icon$E = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg4078957788__a bb-icon__dynamic-fill",attrs:{"d":"M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z"}})]) } };

Vue.component('icon-ui-information-circle-outline', Icon$E);

var script$1G = {
  name: 'bb-banner',
  components: {
    BbIcon: __vue_component__$2C,
    BbButton: __vue_component__$2v
  },
  props: {
    visible: Boolean,
    permanent: Boolean,
    title: String,
    error: {
      type: Boolean,
      validator: deprecated('error', 'type', 'BbBanner')
    },
    type: {
      type: String,
      default: 'success',
      validator: type => ['success', 'warning', 'error', 'info'].includes(type)
    },
    ariaLabels: {
      type: Object,
      default: () => ({
        container: 'Banner',
        button: 'Close'
      })
    }
  },
  emits: ['close'],
  computed: {
    classes() {
      return [`bb-banner--type-${this.computedType}`];
    },
    computedType() {
      return this.error ? 'error' : this.type;
    },
    displayedIcon() {
      const typeIcons = {
        info: {
          icon: 'ui-information-circle-outline',
          fill: 'blue'
        },
        error: {
          icon: 'ui-alert-circle-outline',
          fill: 'red'
        },
        success: {
          icon: 'ui-check-circle-outline',
          fill: 'mint'
        },
        warning: {
          icon: 'ui-alert-outline',
          fill: 'yellow'
        }
      };
      return typeIcons[this.computedType];
    },
    computedRole() {
      return ['error', 'warning'].includes(this.computedType) ? 'alert' : 'status';
    },
    computedTitleId() {
      return this.title ? slugify(this.title) : undefined;
    }
  },
  methods: {
    hideBanner(event) {
      this.$emit('close', event);
    }
  }
};

/* script */
const __vue_script__$1G = script$1G;

/* template */
var __vue_render__$1E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.visible,
      expression: "visible"
    }],
    staticClass: "bb-banner",
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.ariaLabels.container,
      "role": _vm.computedRole,
      "aria-live": "polite"
    }
  }, [_c('div', {
    staticClass: "bb-banner__icon-wrapper"
  }, [_c('bb-icon', {
    attrs: {
      "name": _vm.displayedIcon.icon,
      "fill": _vm.displayedIcon.fill,
      "size": "24"
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-banner__content",
    attrs: {
      "aria-labelledby": _vm.computedTitleId
    }
  }, [_c('div', [_vm.title ? _c('p', {
    staticClass: "bb-banner__title",
    attrs: {
      "id": _vm.computedTitleId
    }
  }, [_vm._v("\n        " + _vm._s(_vm.title) + "\n      ")]) : _vm._e(), _vm._v(" "), _c('p', {
    staticClass: "bb-banner__body"
  }, [_vm._t("default")], 2)])]), _vm._v(" "), !_vm.permanent ? _c('div', {
    staticClass: "bb-banner__close"
  }, [_c('bb-button', {
    attrs: {
      "color": "gray",
      "no-background": "",
      "display": "square",
      "icon": "ui-close",
      "icon-color": "gray-40",
      "aria-label": _vm.ariaLabels.button
    },
    on: {
      "click": _vm.hideBanner
    }
  })], 1) : _vm._e()]);
};
var __vue_staticRenderFns__$1E = [];

/* style */
const __vue_inject_styles__$1G = function (inject) {
  if (!inject) return;
  inject("data-v-6afc7915_0", {
    source: ".bb-banner{display:flex;overflow:hidden;min-height:4.5rem;border-radius:8px;background:#fff;box-shadow:0 -.0625rem 1.0625rem rgba(0,0,0,.07);word-break:break-word}.bb-banner--type-success{border:.0625rem solid #0ccca2}.bb-banner--type-warning{border:.0625rem solid #fc0}.bb-banner--type-info{border:.0625rem solid #2485e5}.bb-banner--type-error{border:.0625rem solid #ff7155}.bb-banner__close,.bb-banner__icon-wrapper{display:flex;min-width:3.125rem;align-items:center;justify-content:center}.bb-banner--type-success .bb-banner__icon-wrapper{background:#e7faf6}.bb-banner--type-warning .bb-banner__icon-wrapper{background:#fbf5cb}.bb-banner--type-info .bb-banner__icon-wrapper{background:#e6f3ff}.bb-banner--type-error .bb-banner__icon-wrapper{background:#ffeae6}.bb-banner__close{min-width:auto;padding-right:1.25rem}.bb-banner__content{display:flex;flex:auto;align-items:center;margin:1.25rem}.bb-banner__title{margin-bottom:.3125rem;color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;text-transform:uppercase}.bb-banner__body{font-size:.75rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1G = undefined;
/* module identifier */
const __vue_module_identifier__$1G = undefined;
/* functional template */
const __vue_is_functional_template__$1G = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1G = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1E,
  staticRenderFns: __vue_staticRenderFns__$1E
}, __vue_inject_styles__$1G, __vue_script__$1G, __vue_scope_id__$1G, __vue_is_functional_template__$1G, __vue_module_identifier__$1G, false, createInjector, undefined, undefined);

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;
  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn) {
  var module = { exports: {} };
	return fn(module, module.exports), module.exports;
}

var _typeof_1 = createCommonjsModule(function (module) {
function _typeof(obj) {
  "@babel/helpers - typeof";
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    module.exports = _typeof = function _typeof(obj) {
      return typeof obj;
    };
    module.exports["default"] = module.exports, module.exports.__esModule = true;
  } else {
    module.exports = _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
    module.exports["default"] = module.exports, module.exports.__esModule = true;
  }
  return _typeof(obj);
}
module.exports = _typeof;
module.exports["default"] = module.exports, module.exports.__esModule = true;
});
var _typeof = getDefaultExportFromCjs(_typeof_1);

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }
  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();
  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;
    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;
      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }
    return _possibleConstructorReturn(this, result);
  };
}

let VueSliderDot = /*#__PURE__*/function (_Vue) {
  _inherits(VueSliderDot, _Vue);
  var _super = _createSuper(VueSliderDot);
  function VueSliderDot() {
    _classCallCheck(this, VueSliderDot);
    return _super.apply(this, arguments);
  }
  _createClass(VueSliderDot, [{
    key: "dotClasses",
    get: function () {
      return ['vue-slider-dot', {
        'vue-slider-dot-hover': this.tooltip === 'hover' || this.tooltip === 'active',
        'vue-slider-dot-disabled': this.disabled,
        'vue-slider-dot-focus': this.focus
      }];
    }
  }, {
    key: "handleClasses",
    get: function () {
      return ['vue-slider-dot-handle', {
        'vue-slider-dot-handle-disabled': this.disabled,
        'vue-slider-dot-handle-focus': this.focus
      }];
    }
  }, {
    key: "tooltipClasses",
    get: function () {
      return ['vue-slider-dot-tooltip', [`vue-slider-dot-tooltip-${this.tooltipPlacement}`], {
        'vue-slider-dot-tooltip-show': this.showTooltip
      }];
    }
  }, {
    key: "tooltipInnerClasses",
    get: function () {
      return ['vue-slider-dot-tooltip-inner', [`vue-slider-dot-tooltip-inner-${this.tooltipPlacement}`], {
        'vue-slider-dot-tooltip-inner-disabled': this.disabled,
        'vue-slider-dot-tooltip-inner-focus': this.focus
      }];
    }
  }, {
    key: "showTooltip",
    get: function () {
      switch (this.tooltip) {
        case 'always':
          return true;
        case 'none':
          return false;
        case 'focus':
        case 'active':
          return !!this.focus;
        default:
          return false;
      }
    }
  }, {
    key: "tooltipValue",
    get: function () {
      if (this.tooltipFormatter) {
        return typeof this.tooltipFormatter === 'string' ? this.tooltipFormatter.replace(/\{value\}/, String(this.value)) : this.tooltipFormatter(this.value);
      } else {
        return this.value;
      }
    }
  }, {
    key: "dragStart",
    value: function dragStart(e) {
      if (this.disabled) {
        return false;
      }
      this.$emit('drag-start');
    }
  }, {
    key: "render",
    value: function render() {
      const h = arguments[0];
      return h("div", {
        "ref": "dot",
        "class": this.dotClasses,
        "attrs": {
          "aria-valuetext": this.tooltipValue ? this.tooltipValue.toString() : ''
        },
        "on": {
          "mousedown": this.dragStart,
          "touchstart": this.dragStart
        }
      }, [this.$slots.dot || h("div", {
        "class": this.handleClasses,
        "style": this.dotStyle
      }), this.tooltip !== 'none' ? h("div", {
        "class": this.tooltipClasses
      }, [this.$slots.tooltip || h("div", {
        "class": this.tooltipInnerClasses,
        "style": this.tooltipStyle
      }, [h("span", {
        "class": 'vue-slider-dot-tooltip-text'
      }, [this.tooltipValue])])]) : null]);
    }
  }]);
  return VueSliderDot;
}(Vue$1);
__decorate([Prop({
  default: 0
})], VueSliderDot.prototype, "value", void 0);
__decorate([Prop()], VueSliderDot.prototype, "tooltip", void 0);
__decorate([Prop()], VueSliderDot.prototype, "dotStyle", void 0);
__decorate([Prop()], VueSliderDot.prototype, "tooltipStyle", void 0);
__decorate([Prop({
  type: String,
  validator: val => ['top', 'right', 'bottom', 'left'].indexOf(val) > -1,
  required: true
})], VueSliderDot.prototype, "tooltipPlacement", void 0);
__decorate([Prop({
  type: [String, Function]
})], VueSliderDot.prototype, "tooltipFormatter", void 0);
__decorate([Prop({
  type: Boolean,
  default: false
})], VueSliderDot.prototype, "focus", void 0);
__decorate([Prop({
  default: false
})], VueSliderDot.prototype, "disabled", void 0);
VueSliderDot = __decorate([Component({
  name: 'VueSliderDot'
})], VueSliderDot);
var VueSliderDot$1 = VueSliderDot;

let VueSlideMark = /*#__PURE__*/function (_Vue) {
  _inherits(VueSlideMark, _Vue);
  var _super = _createSuper(VueSlideMark);
  function VueSlideMark() {
    _classCallCheck(this, VueSlideMark);
    return _super.apply(this, arguments);
  }
  _createClass(VueSlideMark, [{
    key: "marksClasses",
    get: function () {
      return ['vue-slider-mark', {
        'vue-slider-mark-active': this.mark.active
      }];
    }
  }, {
    key: "stepClasses",
    get: function () {
      return ['vue-slider-mark-step', {
        'vue-slider-mark-step-active': this.mark.active
      }];
    }
  }, {
    key: "labelClasses",
    get: function () {
      return ['vue-slider-mark-label', {
        'vue-slider-mark-label-active': this.mark.active
      }];
    }
  }, {
    key: "labelClickHandle",
    value: function labelClickHandle(e) {
      e.stopPropagation();
      this.$emit('pressLabel', this.mark.pos);
    }
  }, {
    key: "render",
    value: function render() {
      const h = arguments[0];
      const mark = this.mark;
      return h("div", {
        "class": this.marksClasses
      }, [this.$slots.step || h("div", {
        "class": this.stepClasses,
        "style": [this.stepStyle || {}, mark.style || {}, mark.active ? this.stepActiveStyle || {} : {}, mark.active ? mark.activeStyle || {} : {}]
      }), !this.hideLabel ? this.$slots.label || h("div", {
        "class": this.labelClasses,
        "style": [this.labelStyle || {}, mark.labelStyle || {}, mark.active ? this.labelActiveStyle || {} : {}, mark.active ? mark.labelActiveStyle || {} : {}],
        "on": {
          "click": this.labelClickHandle
        }
      }, [mark.label]) : null]);
    }
  }]);
  return VueSlideMark;
}(Vue$1);
__decorate([Prop({
  required: true
})], VueSlideMark.prototype, "mark", void 0);
__decorate([Prop(Boolean)], VueSlideMark.prototype, "hideLabel", void 0);
__decorate([Prop()], VueSlideMark.prototype, "stepStyle", void 0);
__decorate([Prop()], VueSlideMark.prototype, "stepActiveStyle", void 0);
__decorate([Prop()], VueSlideMark.prototype, "labelStyle", void 0);
__decorate([Prop()], VueSlideMark.prototype, "labelActiveStyle", void 0);
VueSlideMark = __decorate([Component({
  name: 'VueSlideMark'
})], VueSlideMark);
var VueSliderMark = VueSlideMark;

const getSize = value => typeof value === 'number' ? `${value}px` : value;
/** Get the distance of the element from the top/left of the page */
const getOffset = elem => {
  const doc = document.documentElement;
  const body = document.body;
  const rect = elem.getBoundingClientRect();
  const offset = {
    y: rect.top + (window.pageYOffset || doc.scrollTop) - (doc.clientTop || body.clientTop || 0),
    x: rect.left + (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || body.clientLeft || 0)
  };
  return offset;
};
/**
 * Get the position of the mouse/finger in the element
 * @param e Trigger event
 * @param elem Container element
 * @param isReverse From the right/bottom
 */
const getPos = (e, elem, isReverse, zoom = 1) => {
  const event = 'targetTouches' in e ? e.targetTouches[0] : e;
  const offset = getOffset(elem);
  const posObj = {
    x: event.pageX - offset.x,
    y: event.pageY - offset.y
  };
  return {
    x: isReverse ? elem.offsetWidth * zoom - posObj.x : posObj.x,
    y: isReverse ? elem.offsetHeight * zoom - posObj.y : posObj.y
  };
};
var KEY_CODE;
(function (KEY_CODE) {
  KEY_CODE[KEY_CODE["PAGE_UP"] = 33] = "PAGE_UP";
  KEY_CODE[KEY_CODE["PAGE_DOWN"] = 34] = "PAGE_DOWN";
  KEY_CODE[KEY_CODE["END"] = 35] = "END";
  KEY_CODE[KEY_CODE["HOME"] = 36] = "HOME";
  KEY_CODE[KEY_CODE["LEFT"] = 37] = "LEFT";
  KEY_CODE[KEY_CODE["UP"] = 38] = "UP";
  KEY_CODE[KEY_CODE["RIGHT"] = 39] = "RIGHT";
  KEY_CODE[KEY_CODE["DOWN"] = 40] = "DOWN";
})(KEY_CODE || (KEY_CODE = {}));
const getKeyboardHandleFunc = (e, params) => {
  if (params.hook) {
    const result = params.hook(e);
    if (typeof result === 'function') return result;
    if (!result) return null;
  }
  switch (e.keyCode) {
    case KEY_CODE.UP:
      return i => params.direction === 'ttb' ? i - 1 : i + 1;
    case KEY_CODE.RIGHT:
      return i => params.direction === 'rtl' ? i - 1 : i + 1;
    case KEY_CODE.DOWN:
      return i => params.direction === 'ttb' ? i + 1 : i - 1;
    case KEY_CODE.LEFT:
      return i => params.direction === 'rtl' ? i + 1 : i - 1;
    case KEY_CODE.END:
      return () => params.max;
    case KEY_CODE.HOME:
      return () => params.min;
    case KEY_CODE.PAGE_UP:
      return i => i + 10;
    case KEY_CODE.PAGE_DOWN:
      return i => i - 10;
    default:
      return null;
  }
};

/**
 * For any precise floating point calculation
 *
 * @export
 * @class Decimal
 */
let Decimal = /*#__PURE__*/function () {
  function Decimal(num) {
    _classCallCheck(this, Decimal);
    this.num = num;
  }
  _createClass(Decimal, [{
    key: "decimal",
    value: function decimal(num2, operator) {
      const num1 = this.num;
      const len1 = this.getDecimalLen(num1);
      const len2 = this.getDecimalLen(num2);
      let base = 0;
      switch (operator) {
        case '+':
          base = this.getExponent(len1, len2);
          this.num = (this.safeRoundUp(num1, base) + this.safeRoundUp(num2, base)) / base;
          break;
        case '-':
          base = this.getExponent(len1, len2);
          this.num = (this.safeRoundUp(num1, base) - this.safeRoundUp(num2, base)) / base;
          break;
        case '*':
          this.num = this.safeRoundUp(this.safeRoundUp(num1, this.getExponent(len1)), this.safeRoundUp(num2, this.getExponent(len2))) / this.getExponent(len1 + len2);
          break;
        case '/':
          base = this.getExponent(len1, len2);
          this.num = this.safeRoundUp(num1, base) / this.safeRoundUp(num2, base);
          break;
        case '%':
          base = this.getExponent(len1, len2);
          this.num = this.safeRoundUp(num1, base) % this.safeRoundUp(num2, base) / base;
          break;
      }
      return this;
    }
  }, {
    key: "plus",
    value: function plus(num2) {
      return this.decimal(num2, '+');
    }
  }, {
    key: "minus",
    value: function minus(num2) {
      return this.decimal(num2, '-');
    }
  }, {
    key: "multiply",
    value: function multiply(num2) {
      return this.decimal(num2, '*');
    }
  }, {
    key: "divide",
    value: function divide(num2) {
      return this.decimal(num2, '/');
    }
  }, {
    key: "remainder",
    value: function remainder(num2) {
      return this.decimal(num2, '%');
    }
  }, {
    key: "toNumber",
    value: function toNumber() {
      return this.num;
    }
  }, {
    key: "getDecimalLen",
    value: function getDecimalLen(num) {
      const strArr = `${num}`.split('e');
      return (`${strArr[0]}`.split('.')[1] || '').length - (strArr[1] ? +strArr[1] : 0);
    }
  }, {
    key: "getExponent",
    value: function getExponent(num1, num2) {
      return Math.pow(10, num2 !== void 0 ? Math.max(num1, num2) : num1);
    }
    // fix: 9999999.99995 * 100000 = 999999999994.9999
  }, {
    key: "safeRoundUp",
    value: function safeRoundUp(num, exponent) {
      return Math.round(num * exponent);
    }
  }]);
  return Decimal;
}();

var ERROR_TYPE;
(function (ERROR_TYPE) {
  ERROR_TYPE[ERROR_TYPE["VALUE"] = 1] = "VALUE";
  ERROR_TYPE[ERROR_TYPE["INTERVAL"] = 2] = "INTERVAL";
  ERROR_TYPE[ERROR_TYPE["MIN"] = 3] = "MIN";
  ERROR_TYPE[ERROR_TYPE["MAX"] = 4] = "MAX";
  ERROR_TYPE[ERROR_TYPE["ORDER"] = 5] = "ORDER";
})(ERROR_TYPE || (ERROR_TYPE = {}));
const ERROR_MSG = {
  [ERROR_TYPE.VALUE]: 'The type of the "value" is illegal',
  [ERROR_TYPE.INTERVAL]: 'The prop "interval" is invalid, "(max - min)" must be divisible by "interval"',
  [ERROR_TYPE.MIN]: 'The "value" must be greater than or equal to the "min".',
  [ERROR_TYPE.MAX]: 'The "value" must be less than or equal to the "max".',
  [ERROR_TYPE.ORDER]: 'When "order" is false, the parameters "minRange", "maxRange", "fixed", "enabled" are invalid.'
};
/**
 * Slider logic control center
 *
 * @export
 * @class Control
 */
let Control = /*#__PURE__*/function () {
  function Control(options) {
    _classCallCheck(this, Control);
    this.dotsPos = []; // The position of each slider
    this.dotsValue = []; // The value of each slider
    this.cacheRangeDir = {};
    this.data = options.data;
    this.max = options.max;
    this.min = options.min;
    this.interval = options.interval;
    this.order = options.order;
    this.marks = options.marks;
    this.included = options.included;
    this.process = options.process;
    this.adsorb = options.adsorb;
    this.dotOptions = options.dotOptions;
    this.onError = options.onError;
    if (this.order) {
      this.minRange = options.minRange || 0;
      this.maxRange = options.maxRange || 0;
      this.enableCross = options.enableCross;
      this.fixed = options.fixed;
    } else {
      if (options.minRange || options.maxRange || !options.enableCross || options.fixed) {
        this.emitError(ERROR_TYPE.ORDER);
      }
      this.minRange = 0;
      this.maxRange = 0;
      this.enableCross = true;
      this.fixed = false;
    }
    this.setValue(options.value);
  }
  _createClass(Control, [{
    key: "setValue",
    value: function setValue(value) {
      this.setDotsValue(Array.isArray(value) ? this.order ? [...value].sort((a, b) => this.getIndexByValue(a) - this.getIndexByValue(b)) : [...value] : [value], true);
    }
  }, {
    key: "setDotsValue",
    value: function setDotsValue(value, syncPos) {
      this.dotsValue = value;
      if (syncPos) {
        this.syncDotsPos();
      }
    }
    // Set the slider position
  }, {
    key: "setDotsPos",
    value: function setDotsPos(dotsPos) {
      const list = this.order ? [...dotsPos].sort((a, b) => a - b) : dotsPos;
      this.dotsPos = list;
      this.setDotsValue(list.map(dotPos => this.getValueByPos(dotPos)), this.adsorb);
    }
    // Get value by position
  }, {
    key: "getValueByPos",
    value: function getValueByPos(pos) {
      let value = this.parsePos(pos);
      // When included is true, the return value is the value of the nearest mark
      if (this.included) {
        let dir = 100;
        this.markList.forEach(mark => {
          const curDir = Math.abs(mark.pos - pos);
          if (curDir < dir) {
            dir = curDir;
            value = mark.value;
          }
        });
      }
      return value;
    }
    // Sync slider position
  }, {
    key: "syncDotsPos",
    value: function syncDotsPos() {
      this.dotsPos = this.dotsValue.map(v => this.parseValue(v));
    }
    /**
     * Get all the marks
     *
     * @readonly
     * @type {Mark[]}
     * @memberof Control
     */
  }, {
    key: "markList",
    get: function () {
      if (!this.marks) {
        return [];
      }
      const getMarkByValue = (value, mark) => {
        const pos = this.parseValue(value);
        return {
          pos,
          value,
          label: value,
          active: this.isActiveByPos(pos),
          ...mark
        };
      };
      if (this.marks === true) {
        return this.getValues().map(value => getMarkByValue(value));
      } else if (Object.prototype.toString.call(this.marks) === '[object Object]') {
        return Object.keys(this.marks).sort((a, b) => +a - +b).map(value => {
          const item = this.marks[value];
          return getMarkByValue(value, typeof item !== 'string' ? item : {
            label: item
          });
        });
      } else if (Array.isArray(this.marks)) {
        return this.marks.map(value => getMarkByValue(value));
      } else if (typeof this.marks === 'function') {
        return this.getValues().map(value => ({
          value,
          result: this.marks(value)
        })).filter(({
          result
        }) => !!result).map(({
          value,
          result
        }) => getMarkByValue(value, result));
      } else {
        return [];
      }
    }
    /**
     * Get the most recent slider index by position
     *
     * @param {number} pos
     * @returns {number}
     * @memberof Control
     */
  }, {
    key: "getRecentDot",
    value: function getRecentDot(pos) {
      const arr = this.dotsPos.filter((dotPos, index) => !(this.getDotOption(index) && this.getDotOption(index).disabled)).map(dotPos => Math.abs(dotPos - pos));
      return arr.indexOf(Math.min(...arr));
    }
    /**
     * Get index by value
     *
     * @param {Value} value
     * @returns {number}
     * @memberof Control
     */
  }, {
    key: "getIndexByValue",
    value: function getIndexByValue(value) {
      if (this.data) {
        return this.data.indexOf(value);
      }
      return new Decimal(+value).minus(this.min).divide(this.interval).toNumber();
    }
    /**
     * Get value by index
     *
     * @param {index} number
     * @returns {Value}
     * @memberof Control
     */
  }, {
    key: "getValueByIndex",
    value: function getValueByIndex(index) {
      if (index < 0) {
        index = 0;
      } else if (index > this.total) {
        index = this.total;
      }
      return this.data ? this.data[index] : new Decimal(index).multiply(this.interval).plus(this.min).toNumber();
    }
    /**
     * Set the position of a single slider
     *
     * @param {number} pos
     * @param {number} index
     */
  }, {
    key: "setDotPos",
    value: function setDotPos(pos, index) {
      pos = this.getValidPos(pos, index).pos;
      const changePos = pos - this.dotsPos[index];
      if (!changePos) {
        return;
      }
      let changePosArr = new Array(this.dotsPos.length);
      if (this.fixed) {
        changePosArr = this.getFixedChangePosArr(changePos, index);
      } else if (this.minRange || this.maxRange) {
        changePosArr = this.getLimitRangeChangePosArr(pos, changePos, index);
      } else {
        changePosArr[index] = changePos;
      }
      this.setDotsPos(this.dotsPos.map((curPos, i) => curPos + (changePosArr[i] || 0)));
    }
    /**
     * In fixed mode, get the position of all slider changes
     *
     * @param {number} changePos Change distance of a single slider
     * @param {number} index slider index
     * @returns {DotsPosChangeArray}
     * @memberof Control
     */
  }, {
    key: "getFixedChangePosArr",
    value: function getFixedChangePosArr(changePos, index) {
      this.dotsPos.forEach((originPos, i) => {
        if (i !== index) {
          const {
            pos: lastPos,
            inRange
          } = this.getValidPos(originPos + changePos, i);
          if (!inRange) {
            changePos = Math.min(Math.abs(lastPos - originPos), Math.abs(changePos)) * (changePos < 0 ? -1 : 1);
          }
        }
      });
      return this.dotsPos.map(_ => changePos);
    }
    /**
     * In minRange/maxRange mode, get the position of all slider changes
     *
     * @param {number} pos position of a single slider
     * @param {number} changePos Change distance of a single slider
     * @param {number} index slider index
     * @returns {DotsPosChangeArray}
     * @memberof Control
     */
  }, {
    key: "getLimitRangeChangePosArr",
    value: function getLimitRangeChangePosArr(pos, changePos, index) {
      const changeDots = [{
        index,
        changePos
      }];
      const newChangePos = changePos;
      [this.minRange, this.maxRange].forEach((isLimitRange, rangeIndex) => {
        if (!isLimitRange) {
          return false;
        }
        const isMinRange = rangeIndex === 0;
        const isForward = changePos > 0;
        let next = 0;
        if (isMinRange) {
          next = isForward ? 1 : -1;
        } else {
          next = isForward ? -1 : 1;
        }
        // Determine if the two positions are within the legal interval
        const inLimitRange = (pos2, pos1) => {
          const diff = Math.abs(pos2 - pos1);
          return isMinRange ? diff < this.minRangeDir : diff > this.maxRangeDir;
        };
        let i = index + next;
        let nextPos = this.dotsPos[i];
        let curPos = pos;
        while (this.isPos(nextPos) && inLimitRange(nextPos, curPos)) {
          const {
            pos: lastPos
          } = this.getValidPos(nextPos + newChangePos, i);
          changeDots.push({
            index: i,
            changePos: lastPos - nextPos
          });
          i = i + next;
          curPos = lastPos;
          nextPos = this.dotsPos[i];
        }
      });
      return this.dotsPos.map((_, i) => {
        const changeDot = changeDots.filter(dot => dot.index === i);
        return changeDot.length ? changeDot[0].changePos : 0;
      });
    }
  }, {
    key: "isPos",
    value: function isPos(pos) {
      return typeof pos === 'number';
    }
    /**
     * Get a valid position by pos
     *
     * @param {number} pos
     * @param {number} index
     * @returns {{ pos: number, inRange: boolean }}
     */
  }, {
    key: "getValidPos",
    value: function getValidPos(pos, index) {
      const range = this.valuePosRange[index];
      let inRange = true;
      if (pos < range[0]) {
        pos = range[0];
        inRange = false;
      } else if (pos > range[1]) {
        pos = range[1];
        inRange = false;
      }
      return {
        pos,
        inRange
      };
    }
    /**
     * Calculate the position of the slider by value
     *
     * @param {Value} val
     * @returns {number}
     */
  }, {
    key: "parseValue",
    value: function parseValue(val) {
      if (this.data) {
        val = this.data.indexOf(val);
      } else if (typeof val === 'number' || typeof val === 'string') {
        val = +val;
        if (val < this.min) {
          this.emitError(ERROR_TYPE.MIN);
          return 0;
        }
        if (val > this.max) {
          this.emitError(ERROR_TYPE.MAX);
          return 0;
        }
        if (typeof val !== 'number' || val !== val) {
          this.emitError(ERROR_TYPE.VALUE);
          return 0;
        }
        val = new Decimal(val).minus(this.min).divide(this.interval).toNumber();
      }
      const pos = new Decimal(val).multiply(this.gap).toNumber();
      return pos < 0 ? 0 : pos > 100 ? 100 : pos;
    }
    /**
     * Calculate the value by position
     *
     * @param {number} pos
     * @returns {Value}
     * @memberof Control
     */
  }, {
    key: "parsePos",
    value: function parsePos(pos) {
      const index = Math.round(pos / this.gap);
      return this.getValueByIndex(index);
    }
    /**
     * Determine if the location is active
     *
     * @param {number} pos
     * @returns {boolean}
     * @memberof Control
     */
  }, {
    key: "isActiveByPos",
    value: function isActiveByPos(pos) {
      return this.processArray.some(([start, end]) => pos >= start && pos <= end);
    }
    /**
     * Get each value
     *
     * @returns {Value[]}
     * @memberof Control
     */
  }, {
    key: "getValues",
    value: function getValues() {
      if (this.data) {
        return this.data;
      } else {
        const values = [];
        for (let i = 0; i <= this.total; i++) {
          values.push(new Decimal(i).multiply(this.interval).plus(this.min).toNumber());
        }
        return values;
      }
    }
    /**
     * Calculate the distance of the range
     * @param range number
     */
  }, {
    key: "getRangeDir",
    value: function getRangeDir(range) {
      return range ? new Decimal(range).divide(new Decimal(this.data ? this.data.length - 1 : this.max).minus(this.data ? 0 : this.min).toNumber()).multiply(100).toNumber() : 100;
    }
  }, {
    key: "emitError",
    value: function emitError(type) {
      if (this.onError) {
        this.onError(type, ERROR_MSG[type]);
      }
    }
  }, {
    key: "processArray",
    get: function () {
      if (this.process) {
        if (typeof this.process === 'function') {
          return this.process(this.dotsPos);
        } else if (this.dotsPos.length === 1) {
          return [[0, this.dotsPos[0]]];
        } else if (this.dotsPos.length > 1) {
          return [[Math.min(...this.dotsPos), Math.max(...this.dotsPos)]];
        }
      }
      return [];
    }
    /**
     * The total number of values
     *
     * @type {number}
     * @memberof Control
     */
  }, {
    key: "total",
    get: function () {
      let total = 0;
      if (this.data) {
        total = this.data.length - 1;
      } else {
        total = new Decimal(this.max).minus(this.min).divide(this.interval).toNumber();
      }
      if (total - Math.floor(total) !== 0) {
        this.emitError(ERROR_TYPE.INTERVAL);
        return 0;
      }
      return total;
    }
    // Distance between each value
  }, {
    key: "gap",
    get: function () {
      return 100 / this.total;
    }
    // The minimum distance between the two sliders
  }, {
    key: "minRangeDir",
    get: function () {
      if (this.cacheRangeDir[this.minRange]) {
        return this.cacheRangeDir[this.minRange];
      }
      return this.cacheRangeDir[this.minRange] = this.getRangeDir(this.minRange);
    }
    // Maximum distance between the two sliders
  }, {
    key: "maxRangeDir",
    get: function () {
      if (this.cacheRangeDir[this.maxRange]) return this.cacheRangeDir[this.maxRange];
      return this.cacheRangeDir[this.maxRange] = this.getRangeDir(this.maxRange);
    }
  }, {
    key: "getDotOption",
    value: function getDotOption(index) {
      return Array.isArray(this.dotOptions) ? this.dotOptions[index] : this.dotOptions;
    }
  }, {
    key: "getDotRange",
    value: function getDotRange(index, key, defaultValue) {
      if (!this.dotOptions) {
        return defaultValue;
      }
      const option = this.getDotOption(index);
      return option && option[key] !== void 0 ? this.parseValue(option[key]) : defaultValue;
    }
    /**
     * Sliding range of each slider
     *
     * @type {Array<[number, number]>}
     * @memberof Control
     */
  }, {
    key: "valuePosRange",
    get: function () {
      const dotsPos = this.dotsPos;
      const valuePosRange = [];
      dotsPos.forEach((pos, i) => {
        valuePosRange.push([Math.max(this.minRange ? this.minRangeDir * i : 0, !this.enableCross ? dotsPos[i - 1] || 0 : 0, this.getDotRange(i, 'min', 0)), Math.min(this.minRange ? 100 - this.minRangeDir * (dotsPos.length - 1 - i) : 100, !this.enableCross ? dotsPos[i + 1] || 100 : 100, this.getDotRange(i, 'max', 100))]);
      });
      return valuePosRange;
    }
  }, {
    key: "dotsIndex",
    get: function () {
      return this.dotsValue.map(val => this.getIndexByValue(val));
    }
  }]);
  return Control;
}();

let State = /*#__PURE__*/function () {
  function State(map) {
    _classCallCheck(this, State);
    this.states = 0;
    this.map = map;
  }
  _createClass(State, [{
    key: "add",
    value: function add(state) {
      this.states |= state;
    }
  }, {
    key: "delete",
    value: function _delete(state) {
      this.states &= ~state;
    }
  }, {
    key: "toggle",
    value: function toggle(state) {
      if (this.has(state)) {
        this.delete(state);
      } else {
        this.add(state);
      }
    }
  }, {
    key: "has",
    value: function has(state) {
      return !!(this.states & state);
    }
  }]);
  return State;
}();

const SliderState = {
  None: 0,
  Drag: 1 << 1,
  Focus: 1 << 2
};
const DEFAULT_SLIDER_SIZE = 4;
let VueSlider = /*#__PURE__*/function (_Vue) {
  _inherits(VueSlider, _Vue);
  var _super = _createSuper(VueSlider);
  function VueSlider() {
    var _this;
    _classCallCheck(this, VueSlider);
    _this = _super.apply(this, arguments);
    _this.states = new State(SliderState);
    // The width of the component is divided into one hundred, the width of each one.
    _this.scale = 1;
    // Currently dragged slider index
    _this.focusDotIndex = 0;
    return _this;
  }
  _createClass(VueSlider, [{
    key: "tailSize",
    get: function () {
      return getSize((this.isHorizontal ? this.height : this.width) || DEFAULT_SLIDER_SIZE);
    }
  }, {
    key: "containerClasses",
    get: function () {
      return ['vue-slider', [`vue-slider-${this.direction}`], {
        'vue-slider-disabled': this.disabled
      }];
    }
  }, {
    key: "containerStyles",
    get: function () {
      const [dotWidth, dotHeight] = Array.isArray(this.dotSize) ? this.dotSize : [this.dotSize, this.dotSize];
      const containerWidth = this.width ? getSize(this.width) : this.isHorizontal ? 'auto' : getSize(DEFAULT_SLIDER_SIZE);
      const containerHeight = this.height ? getSize(this.height) : this.isHorizontal ? getSize(DEFAULT_SLIDER_SIZE) : 'auto';
      return {
        padding: this.contained ? `${dotHeight / 2}px ${dotWidth / 2}px` : this.isHorizontal ? `${dotHeight / 2}px 0` : `0 ${dotWidth / 2}px`,
        width: containerWidth,
        height: containerHeight
      };
    }
  }, {
    key: "processArray",
    get: function () {
      return this.control.processArray.map(([start, end, style], index) => {
        if (start > end) {
          [start, end] = [end, start];
        }
        const sizeStyleKey = this.isHorizontal ? 'width' : 'height';
        return {
          start,
          end,
          index,
          style: {
            [this.isHorizontal ? 'height' : 'width']: '100%',
            [this.isHorizontal ? 'top' : 'left']: 0,
            [this.mainDirection]: `${start}%`,
            [sizeStyleKey]: `${end - start}%`,
            transitionProperty: `${sizeStyleKey},${this.mainDirection}`,
            transitionDuration: `${this.animateTime}s`,
            ...this.processStyle,
            ...style
          }
        };
      });
    }
  }, {
    key: "dotBaseStyle",
    get: function () {
      const [dotWidth, dotHeight] = Array.isArray(this.dotSize) ? this.dotSize : [this.dotSize, this.dotSize];
      let dotPos;
      if (this.isHorizontal) {
        dotPos = {
          transform: `translate(${this.isReverse ? '50%' : '-50%'}, -50%)`,
          '-WebkitTransform': `translate(${this.isReverse ? '50%' : '-50%'}, -50%)`,
          top: '50%',
          [this.direction === 'ltr' ? 'left' : 'right']: '0'
        };
      } else {
        dotPos = {
          transform: `translate(-50%, ${this.isReverse ? '50%' : '-50%'})`,
          '-WebkitTransform': `translate(-50%, ${this.isReverse ? '50%' : '-50%'})`,
          left: '50%',
          [this.direction === 'btt' ? 'bottom' : 'top']: '0'
        };
      }
      return {
        width: `${dotWidth}px`,
        height: `${dotHeight}px`,
        ...dotPos
      };
    }
  }, {
    key: "mainDirection",
    get: function () {
      switch (this.direction) {
        case 'ltr':
          return 'left';
        case 'rtl':
          return 'right';
        case 'btt':
          return 'bottom';
        case 'ttb':
          return 'top';
      }
    }
  }, {
    key: "isHorizontal",
    get: function () {
      return this.direction === 'ltr' || this.direction === 'rtl';
    }
  }, {
    key: "isReverse",
    get: function () {
      return this.direction === 'rtl' || this.direction === 'btt';
    }
  }, {
    key: "tooltipDirections",
    get: function () {
      const dir = this.tooltipPlacement || (this.isHorizontal ? 'top' : 'left');
      if (Array.isArray(dir)) {
        return dir;
      } else {
        return this.dots.map(() => dir);
      }
    }
  }, {
    key: "dots",
    get: function () {
      return this.control.dotsPos.map((pos, index) => ({
        pos,
        index,
        value: this.control.dotsValue[index],
        focus: this.states.has(SliderState.Focus) && this.focusDotIndex === index,
        disabled: this.disabled,
        style: this.dotStyle,
        ...((Array.isArray(this.dotOptions) ? this.dotOptions[index] : this.dotOptions) || {})
      }));
    }
  }, {
    key: "animateTime",
    get: function () {
      if (this.states.has(SliderState.Drag)) {
        return 0;
      }
      return this.duration;
    }
  }, {
    key: "canSort",
    get: function () {
      return this.order && !this.minRange && !this.maxRange && !this.fixed && this.enableCross;
    }
  }, {
    key: "isObjectData",
    value: function isObjectData(data) {
      return !!data && Object.prototype.toString.call(data) === '[object Object]';
    }
  }, {
    key: "isObjectArrayData",
    value: function isObjectArrayData(data) {
      return !!data && Array.isArray(data) && data.length > 0 && typeof data[0] === 'object';
    }
  }, {
    key: "sliderData",
    get: function () {
      if (this.isObjectArrayData(this.data)) {
        return this.data.map(obj => obj[this.dataValue]);
      } else if (this.isObjectData(this.data)) {
        return Object.keys(this.data);
      } else {
        return this.data;
      }
    }
  }, {
    key: "sliderMarks",
    get: function () {
      if (this.marks) {
        return this.marks;
      } else if (this.isObjectArrayData(this.data)) {
        return val => {
          const mark = {
            label: val
          };
          this.data.some(obj => {
            if (obj[this.dataValue] === val) {
              mark.label = obj[this.dataLabel];
              return true;
            }
            return false;
          });
          return mark;
        };
      } else if (this.isObjectData(this.data)) {
        return this.data;
      }
    }
  }, {
    key: "sliderTooltipFormatter",
    get: function () {
      if (this.tooltipFormatter) {
        return this.tooltipFormatter;
      } else if (this.isObjectArrayData(this.data)) {
        return val => {
          let tooltipText = `${val}`;
          this.data.some(obj => {
            if (obj[this.dataValue] === val) {
              tooltipText = obj[this.dataLabel];
              return true;
            }
            return false;
          });
          return tooltipText;
        };
      } else if (this.isObjectData(this.data)) {
        const data = this.data;
        return val => data[val];
      }
    }
  }, {
    key: "onValueChanged",
    value: function onValueChanged() {
      if (this.control && !this.states.has(SliderState.Drag) && this.isNotSync) {
        this.control.setValue(this.value);
        this.syncValueByPos();
      }
    }
  }, {
    key: "created",
    value: function created() {
      this.initControl();
    }
  }, {
    key: "mounted",
    value: function mounted() {
      this.bindEvent();
    }
  }, {
    key: "beforeDestroy",
    value: function beforeDestroy() {
      this.unbindEvent();
    }
  }, {
    key: "bindEvent",
    value: function bindEvent() {
      document.addEventListener('touchmove', this.dragMove, {
        passive: false
      });
      document.addEventListener('touchend', this.dragEnd, {
        passive: false
      });
      document.addEventListener('mousedown', this.blurHandle);
      document.addEventListener('mousemove', this.dragMove, {
        passive: false
      });
      document.addEventListener('mouseup', this.dragEnd);
      document.addEventListener('mouseleave', this.dragEnd);
      document.addEventListener('keydown', this.keydownHandle);
    }
  }, {
    key: "unbindEvent",
    value: function unbindEvent() {
      document.removeEventListener('touchmove', this.dragMove);
      document.removeEventListener('touchend', this.dragEnd);
      document.removeEventListener('mousedown', this.blurHandle);
      document.removeEventListener('mousemove', this.dragMove);
      document.removeEventListener('mouseup', this.dragEnd);
      document.removeEventListener('mouseleave', this.dragEnd);
      document.removeEventListener('keydown', this.keydownHandle);
    }
  }, {
    key: "setScale",
    value: function setScale() {
      const decimal = new Decimal(Math.floor(this.isHorizontal ? this.$refs.rail.offsetWidth : this.$refs.rail.offsetHeight));
      if (this.zoom !== void 0) {
        decimal.multiply(this.zoom);
      }
      decimal.divide(100);
      this.scale = decimal.toNumber();
    }
  }, {
    key: "initControl",
    value: function initControl() {
      this.control = new Control({
        value: this.value,
        data: this.sliderData,
        enableCross: this.enableCross,
        fixed: this.fixed,
        max: this.max,
        min: this.min,
        interval: this.interval,
        minRange: this.minRange,
        maxRange: this.maxRange,
        order: this.order,
        marks: this.sliderMarks,
        included: this.included,
        process: this.process,
        adsorb: this.adsorb,
        dotOptions: this.dotOptions,
        onError: this.emitError
      });
      this.syncValueByPos();
      ['data', 'enableCross', 'fixed', 'max', 'min', 'interval', 'minRange', 'maxRange', 'order', 'marks', 'process', 'adsorb', 'included', 'dotOptions'].forEach(name => {
        this.$watch(name, val => {
          if (name === 'data' && Array.isArray(this.control.data) && Array.isArray(val) && this.control.data.length === val.length && val.every((item, index) => item === this.control.data[index])) {
            return false;
          }
          switch (name) {
            case 'data':
            case 'dataLabel':
            case 'dataValue':
              this.control.data = this.sliderData;
              break;
            case 'mark':
              this.control.marks = this.sliderMarks;
              break;
            default:
              this.control[name] = val;
          }
          if (['data', 'max', 'min', 'interval'].indexOf(name) > -1) {
            this.control.syncDotsPos();
          }
        });
      });
    }
  }, {
    key: "syncValueByPos",
    value: function syncValueByPos() {
      const values = this.control.dotsValue;
      if (this.isDiff(values, Array.isArray(this.value) ? this.value : [this.value])) {
        this.$emit('change', values.length === 1 ? values[0] : [...values], this.focusDotIndex);
      }
    }
    // Slider value and component internal value are inconsistent
  }, {
    key: "isNotSync",
    get: function () {
      const values = this.control.dotsValue;
      return Array.isArray(this.value) ? this.value.length !== values.length || this.value.some((val, index) => val !== values[index]) : this.value !== values[0];
    }
  }, {
    key: "isDiff",
    value: function isDiff(value1, value2) {
      return value1.length !== value2.length || value1.some((val, index) => val !== value2[index]);
    }
  }, {
    key: "emitError",
    value: function emitError(type, message) {
      if (!this.silent) {
        console.error(`[VueSlider error]: ${message}`);
      }
      this.$emit('error', type, message);
    }
    /**
     * Get the drag range of the slider
     *
     * @private
     * @param {number} index slider index
     * @returns {[number, number]} range [start, end]
     * @memberof VueSlider
     */
  }, {
    key: "dragRange",
    get: function () {
      const prevDot = this.dots[this.focusDotIndex - 1];
      const nextDot = this.dots[this.focusDotIndex + 1];
      return [prevDot ? prevDot.pos : -Infinity, nextDot ? nextDot.pos : Infinity];
    }
  }, {
    key: "dragStartOnProcess",
    value: function dragStartOnProcess(e) {
      if (this.dragOnClick) {
        this.setScale();
        const pos = this.getPosByEvent(e);
        const index = this.control.getRecentDot(pos);
        if (this.dots[index].disabled) {
          return;
        }
        this.dragStart(index);
        this.control.setDotPos(pos, this.focusDotIndex);
        if (!this.lazy) {
          this.syncValueByPos();
        }
      }
    }
  }, {
    key: "dragStart",
    value: function dragStart(index) {
      this.focusDotIndex = index;
      this.setScale();
      this.states.add(SliderState.Drag);
      this.states.add(SliderState.Focus);
      this.$emit('drag-start', this.focusDotIndex);
    }
  }, {
    key: "dragMove",
    value: function dragMove(e) {
      if (!this.states.has(SliderState.Drag)) {
        return false;
      }
      e.preventDefault();
      const pos = this.getPosByEvent(e);
      this.isCrossDot(pos);
      this.control.setDotPos(pos, this.focusDotIndex);
      if (!this.lazy) {
        this.syncValueByPos();
      }
      const value = this.control.dotsValue;
      this.$emit('dragging', value.length === 1 ? value[0] : [...value], this.focusDotIndex);
    }
    // If the component is sorted, then when the slider crosses, toggle the currently selected slider index
  }, {
    key: "isCrossDot",
    value: function isCrossDot(pos) {
      if (this.canSort) {
        const curIndex = this.focusDotIndex;
        let curPos = pos;
        if (curPos > this.dragRange[1]) {
          curPos = this.dragRange[1];
          this.focusDotIndex++;
        } else if (curPos < this.dragRange[0]) {
          curPos = this.dragRange[0];
          this.focusDotIndex--;
        }
        if (curIndex !== this.focusDotIndex) {
          const dotVm = this.$refs[`dot-${this.focusDotIndex}`];
          if (dotVm && dotVm.$el) {
            dotVm.$el.focus();
          }
          this.control.setDotPos(curPos, curIndex);
        }
      }
    }
  }, {
    key: "dragEnd",
    value: function dragEnd(e) {
      if (!this.states.has(SliderState.Drag)) {
        return false;
      }
      setTimeout(() => {
        if (this.lazy) {
          this.syncValueByPos();
        }
        if (this.included && this.isNotSync) {
          this.control.setValue(this.value);
        } else {
          // Sync slider position
          this.control.syncDotsPos();
        }
        this.states.delete(SliderState.Drag);
        // If useKeyboard is true, keep focus status after dragging
        if (!this.useKeyboard || 'targetTouches' in e) {
          this.states.delete(SliderState.Focus);
        }
        this.$emit('drag-end', this.focusDotIndex);
      });
    }
  }, {
    key: "blurHandle",
    value: function blurHandle(e) {
      if (!this.states.has(SliderState.Focus) || !this.$refs.container || this.$refs.container.contains(e.target)) {
        return false;
      }
      this.states.delete(SliderState.Focus);
    }
  }, {
    key: "clickHandle",
    value: function clickHandle(e) {
      if (!this.clickable || this.disabled) {
        return false;
      }
      if (this.states.has(SliderState.Drag)) {
        return;
      }
      this.setScale();
      const pos = this.getPosByEvent(e);
      this.setValueByPos(pos);
    }
  }, {
    key: "focus",
    value: function focus(index = 0) {
      this.states.add(SliderState.Focus);
      this.focusDotIndex = index;
    }
  }, {
    key: "blur",
    value: function blur() {
      this.states.delete(SliderState.Focus);
    }
  }, {
    key: "getValue",
    value: function getValue() {
      const values = this.control.dotsValue;
      return values.length === 1 ? values[0] : values;
    }
  }, {
    key: "getIndex",
    value: function getIndex() {
      const indexes = this.control.dotsIndex;
      return indexes.length === 1 ? indexes[0] : indexes;
    }
  }, {
    key: "setValue",
    value: function setValue(value) {
      this.control.setValue(Array.isArray(value) ? [...value] : [value]);
      this.syncValueByPos();
    }
  }, {
    key: "setIndex",
    value: function setIndex(index) {
      const value = Array.isArray(index) ? index.map(n => this.control.getValueByIndex(n)) : this.control.getValueByIndex(index);
      this.setValue(value);
    }
  }, {
    key: "setValueByPos",
    value: function setValueByPos(pos) {
      const index = this.control.getRecentDot(pos);
      if (this.disabled || this.dots[index].disabled) {
        return false;
      }
      this.focusDotIndex = index;
      this.control.setDotPos(pos, index);
      this.syncValueByPos();
      if (this.useKeyboard) {
        this.states.add(SliderState.Focus);
      }
      setTimeout(() => {
        if (this.included && this.isNotSync) {
          this.control.setValue(this.value);
        } else {
          this.control.syncDotsPos();
        }
      });
    }
  }, {
    key: "keydownHandle",
    value: function keydownHandle(e) {
      if (!this.useKeyboard || !this.states.has(SliderState.Focus)) {
        return false;
      }
      const isInclude = this.included && this.marks;
      const handleFunc = getKeyboardHandleFunc(e, {
        direction: this.direction,
        max: isInclude ? this.control.markList.length - 1 : this.control.total,
        min: 0,
        hook: this.keydownHook
      });
      if (handleFunc) {
        e.preventDefault();
        let newIndex = -1;
        let pos = 0;
        if (isInclude) {
          this.control.markList.some((mark, index) => {
            if (mark.value === this.control.dotsValue[this.focusDotIndex]) {
              newIndex = handleFunc(index);
              return true;
            }
            return false;
          });
          if (newIndex < 0) {
            newIndex = 0;
          } else if (newIndex > this.control.markList.length - 1) {
            newIndex = this.control.markList.length - 1;
          }
          pos = this.control.markList[newIndex].pos;
        } else {
          newIndex = handleFunc(this.control.getIndexByValue(this.control.dotsValue[this.focusDotIndex]));
          pos = this.control.parseValue(this.control.getValueByIndex(newIndex));
        }
        this.isCrossDot(pos);
        this.control.setDotPos(pos, this.focusDotIndex);
        this.syncValueByPos();
      }
    }
  }, {
    key: "getPosByEvent",
    value: function getPosByEvent(e) {
      return getPos(e, this.$refs.rail, this.isReverse, this.zoom)[this.isHorizontal ? 'x' : 'y'] / this.scale;
    }
  }, {
    key: "renderSlot",
    value: function renderSlot(name, data, defaultSlot, isDefault) {
      const h = this.$createElement;
      const scopedSlot = this.$scopedSlots[name];
      return scopedSlot ? isDefault ? scopedSlot(data) : h("template", {
        "slot": name
      }, [scopedSlot(data)]) : defaultSlot;
    }
  }, {
    key: "render",
    value: function render() {
      const h = arguments[0];
      return h("div", _mergeJSXProps([{
        "ref": "container",
        "class": this.containerClasses,
        "style": this.containerStyles,
        "on": {
          "click": this.clickHandle,
          "touchstart": this.dragStartOnProcess,
          "mousedown": this.dragStartOnProcess
        }
      }, this.$attrs]), [h("div", {
        "ref": "rail",
        "class": "vue-slider-rail",
        "style": this.railStyle
      }, [this.processArray.map((item, index) => this.renderSlot('process', item, h("div", {
        "class": "vue-slider-process",
        "key": `process-${index}`,
        "style": item.style
      }), true)), this.sliderMarks ? h("div", {
        "class": "vue-slider-marks"
      }, [this.control.markList.map((mark, index) => this.renderSlot('mark', mark, h("vue-slider-mark", {
        "key": `mark-${index}`,
        "attrs": {
          "mark": mark,
          "hideLabel": this.hideLabel,
          "stepStyle": this.stepStyle,
          "stepActiveStyle": this.stepActiveStyle,
          "labelStyle": this.labelStyle,
          "labelActiveStyle": this.labelActiveStyle
        },
        "style": {
          [this.isHorizontal ? 'height' : 'width']: '100%',
          [this.isHorizontal ? 'width' : 'height']: this.tailSize,
          [this.mainDirection]: `${mark.pos}%`
        },
        "on": {
          "pressLabel": pos => this.clickable && this.setValueByPos(pos)
        }
      }, [this.renderSlot('step', mark, null), this.renderSlot('label', mark, null)]), true))]) : null, this.dots.map((dot, index) => h("vue-slider-dot", {
        "ref": `dot-${index}`,
        "key": `dot-${index}`,
        "attrs": {
          "value": dot.value,
          "disabled": dot.disabled,
          "focus": dot.focus,
          "dot-style": [dot.style, dot.disabled ? dot.disabledStyle : null, dot.focus ? dot.focusStyle : null],
          "tooltip": dot.tooltip || this.tooltip,
          "tooltip-style": [this.tooltipStyle, dot.tooltipStyle, dot.disabled ? dot.tooltipDisabledStyle : null, dot.focus ? dot.tooltipFocusStyle : null],
          "tooltip-formatter": Array.isArray(this.sliderTooltipFormatter) ? this.sliderTooltipFormatter[index] : this.sliderTooltipFormatter,
          "tooltip-placement": this.tooltipDirections[index],
          "role": "slider",
          "aria-valuenow": dot.value,
          "aria-valuemin": this.min,
          "aria-valuemax": this.max,
          "aria-orientation": this.isHorizontal ? 'horizontal' : 'vertical',
          "tabindex": "0",
          ...this.dotAttrs
        },
        "style": [this.dotBaseStyle, {
          [this.mainDirection]: `${dot.pos}%`,
          transition: `${this.mainDirection} ${this.animateTime}s`
        }],
        "on": {
          "drag-start": () => this.dragStart(index)
        },
        "nativeOn": {
          "focus": () => !dot.disabled && this.focus(index),
          "blur": () => this.blur()
        }
      }, [this.renderSlot('dot', dot, null), this.renderSlot('tooltip', dot, null)])), this.renderSlot('default', {
        value: this.getValue()
      }, null, true)])]);
    }
  }]);
  return VueSlider;
}(Vue$1);
__decorate([Model('change', {
  default: 0
})], VueSlider.prototype, "value", void 0);
__decorate([Prop({
  type: Boolean,
  default: false
})], VueSlider.prototype, "silent", void 0);
__decorate([Prop({
  default: 'ltr',
  validator: dir => ['ltr', 'rtl', 'ttb', 'btt'].indexOf(dir) > -1
})], VueSlider.prototype, "direction", void 0);
__decorate([Prop({
  type: [Number, String]
})], VueSlider.prototype, "width", void 0);
__decorate([Prop({
  type: [Number, String]
})], VueSlider.prototype, "height", void 0);
__decorate([Prop({
  default: 14
})], VueSlider.prototype, "dotSize", void 0);
__decorate([Prop({
  default: false
})], VueSlider.prototype, "contained", void 0);
__decorate([Prop({
  type: Number,
  default: 0
})], VueSlider.prototype, "min", void 0);
__decorate([Prop({
  type: Number,
  default: 100
})], VueSlider.prototype, "max", void 0);
__decorate([Prop({
  type: Number,
  default: 1
})], VueSlider.prototype, "interval", void 0);
__decorate([Prop({
  type: Boolean,
  default: false
})], VueSlider.prototype, "disabled", void 0);
__decorate([Prop({
  type: Boolean,
  default: true
})], VueSlider.prototype, "clickable", void 0);
__decorate([Prop({
  type: Boolean,
  default: false
})], VueSlider.prototype, "dragOnClick", void 0);
__decorate([Prop({
  type: Number,
  default: 0.5
})], VueSlider.prototype, "duration", void 0);
__decorate([Prop({
  type: [Object, Array]
})], VueSlider.prototype, "data", void 0);
__decorate([Prop({
  type: String,
  default: 'value'
})], VueSlider.prototype, "dataValue", void 0);
__decorate([Prop({
  type: String,
  default: 'label'
})], VueSlider.prototype, "dataLabel", void 0);
__decorate([Prop({
  type: Boolean,
  default: false
})], VueSlider.prototype, "lazy", void 0);
__decorate([Prop({
  type: String,
  validator: val => ['none', 'always', 'focus', 'hover', 'active'].indexOf(val) > -1,
  default: 'active'
})], VueSlider.prototype, "tooltip", void 0);
__decorate([Prop({
  type: [String, Array],
  validator: data => (Array.isArray(data) ? data : [data]).every(val => ['top', 'right', 'bottom', 'left'].indexOf(val) > -1)
})], VueSlider.prototype, "tooltipPlacement", void 0);
__decorate([Prop({
  type: [String, Array, Function]
})], VueSlider.prototype, "tooltipFormatter", void 0);
__decorate([Prop({
  type: Boolean,
  default: true
})], VueSlider.prototype, "useKeyboard", void 0);
__decorate([Prop(Function)], VueSlider.prototype, "keydownHook", void 0);
__decorate([Prop({
  type: Boolean,
  default: true
})], VueSlider.prototype, "enableCross", void 0);
__decorate([Prop({
  type: Boolean,
  default: false
})], VueSlider.prototype, "fixed", void 0);
__decorate([Prop({
  type: Boolean,
  default: true
})], VueSlider.prototype, "order", void 0);
__decorate([Prop(Number)], VueSlider.prototype, "minRange", void 0);
__decorate([Prop(Number)], VueSlider.prototype, "maxRange", void 0);
__decorate([Prop({
  type: [Boolean, Object, Array, Function],
  default: false
})], VueSlider.prototype, "marks", void 0);
__decorate([Prop({
  type: [Boolean, Function],
  default: true
})], VueSlider.prototype, "process", void 0);
__decorate([Prop({
  type: [Number]
})], VueSlider.prototype, "zoom", void 0);
__decorate([Prop(Boolean)], VueSlider.prototype, "included", void 0);
__decorate([Prop(Boolean)], VueSlider.prototype, "adsorb", void 0);
__decorate([Prop(Boolean)], VueSlider.prototype, "hideLabel", void 0);
__decorate([Prop()], VueSlider.prototype, "dotOptions", void 0);
__decorate([Prop()], VueSlider.prototype, "dotAttrs", void 0);
__decorate([Prop()], VueSlider.prototype, "railStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "processStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "dotStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "tooltipStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "stepStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "stepActiveStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "labelStyle", void 0);
__decorate([Prop()], VueSlider.prototype, "labelActiveStyle", void 0);
__decorate([Watch('value')], VueSlider.prototype, "onValueChanged", null);
VueSlider = __decorate([Component({
  name: 'VueSlider',
  data() {
    return {
      control: null
    };
  },
  components: {
    VueSliderDot: VueSliderDot$1,
    VueSliderMark
  }
})], VueSlider);
var VueSlider$1 = VueSlider;

VueSlider$1.VueSliderMark = VueSliderMark;
VueSlider$1.VueSliderDot = VueSliderDot$1;

const countDecimalPlaces = val => (val.toString().split(/[.,]/)[1] || []).length;
const clamp = (number, lower, upper) => {
  number = +number;
  lower = +lower;
  upper = +upper;
  lower = lower === lower ? lower : 0;
  upper = upper === upper ? upper : 0;
  if (number === number) {
    number = number <= upper ? number : upper;
    number = number >= lower ? number : lower;
  }
  return number;
};

var Icon$D = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3599908832__a bb-icon__dynamic-fill",attrs:{"d":"M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"}})]) } };

Vue.component('icon-ui-pencil', Icon$D);

var script$1F = {
  name: 'bb-big-slider',
  components: {
    BbInput: __vue_component__$2f,
    ClientOnly,
    VueSlider: VueSlider$1
  },
  mixins: [LocalizeNumber],
  props: {
    unit: [String, Number],
    unitMin: [String, Number],
    unitMax: [String, Number],
    title: String,
    name: String,
    min: Number,
    max: Number,
    value: Number,
    step: {
      type: Number,
      default: 1
    },
    disabled: Boolean,
    allowedKeys: {
      type: RegExp,
      default: () => /^[\d ,.]$/
    },
    avoidOverflow: Boolean,
    editButton: Boolean
  },
  emits: ['input', 'blur'],
  data() {
    return {
      val: null,
      vueSliderVal: null,
      sliderInputHasFocus: false
    };
  },
  computed: {
    classes() {
      return ['bb-big-slider', {
        'is-disabled': this.disabled,
        'bb-big-slider--avoid-overflow': this.avoidOverflow
      }];
    },
    lastFullStep() {
      return this.computedInternalMin + Math.floor(this.rangeDividedByIncrement) * this.step;
    },
    stepDecimals() {
      return countDecimalPlaces(this.step);
    },
    vueSliderMax() {
      var _this$decimals;
      const vueSliderMax = this.computedInternalMin + Math.ceil(this.rangeDividedByIncrement) * this.step;
      return +vueSliderMax.toFixed((_this$decimals = this.decimals) !== null && _this$decimals !== void 0 ? _this$decimals : this.stepDecimals);
    },
    rangeDividedByIncrement() {
      return (this.computedInternalMax - this.computedInternalMin) / this.step;
    },
    localizedMin() {
      return this.localizeNumber(this.computedInternalMin);
    },
    localizedMax() {
      return this.localizeNumber(this.computedInternalMax);
    },
    computedVueSliderVal() {
      return this.vueSliderVal > this.computedInternalMax ? this.computedInternalMax : this.vueSliderVal < this.computedInternalMin ? this.computedInternalMin : this.vueSliderVal;
    },
    computedInternalMin() {
      return isNumber(this.min) && (!isNumber(this.max) || this.min <= this.max) ? this.min : this.min > this.max ? this.max : 0;
    },
    computedInternalMax() {
      return isNumber(this.max) ? this.max : isNumber(this.min) ? this.min : 1;
    },
    sanitizedName() {
      return this.name ? slugify(this.name) : uuid();
    }
  },
  watch: {
    value() {
      if (this.val !== this.value) {
        this.updateVal();
        this.$emit('input', this.val);
      }
    }
  },
  mounted() {
    this.updateVal();
  },
  methods: {
    updateVal() {
      const val = isNumber(this.value) ? this.calculateValFromValue() : this.calculateValWhenValueNotSet();
      this.val = val;
      this.vueSliderVal = this.calculateLastStep(val);
    },
    calculateValFromValue() {
      return this.valueSmallerThanMaxNoMin() ? this.value : this.valueLargerThanMinNoMax() ? this.min + 1 : this.valueLargerThanMax() ? this.max : this.valueSmallerThanMin() ? this.min : this.onlyValueSet ? this.value : 0;
    },
    calculateValWhenValueNotSet() {
      return isNumber(this.min) ? this.min : isNumber(this.max) ? this.max : 0;
    },
    onlyValueSet() {
      return isNumber(this.value) && !isNumber(this.max) && !isNumber(this.min);
    },
    valueSmallerThanMaxNoMin() {
      return isNumber(this.max) && !isNumber(this.min) && this.value <= this.max;
    },
    valueSmallerThanMin() {
      return isNumber(this.min) && this.value < this.min;
    },
    valueLargerThanMax() {
      return isNumber(this.max) && this.value > this.max;
    },
    valueLargerThanMinNoMax() {
      return isNumber(this.min) && !isNumber(this.max) && this.value > this.min;
    },
    calculateLastStep(newVal) {
      return newVal > this.lastFullStep ? this.vueSliderMax : newVal;
    },
    vueSliderChange(e) {
      const val = e > this.computedInternalMax ? this.computedInternalMax : e;
      this.val = val;
      this.vueSliderVal = this.calculateLastStep(val);
      this.emitNewValue(val);
    },
    onInputChange(value) {
      this.val = clamp$1(value, this.computedInternalMin, this.computedInternalMax);
      this.vueSliderVal = this.calculateLastStep(this.val);
      this.emitNewValue(this.val);
      this.sliderInputHasFocus = false;
    },
    onInputBlur() {
      this.$emit('blur');
    },
    emitNewValue(value) {
      this.$emit('input', value);
    },
    onInputFocus() {
      this.sliderInputHasFocus = true;
    }
  }
};

/* script */
const __vue_script__$1F = script$1F;

/* template */
var __vue_render__$1D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_c('div', {
    staticClass: "bb-big-slider__fields"
  }, [_c('bb-input', {
    staticClass: "bb-big-slider__input",
    attrs: {
      "value": _vm.val,
      "label": _vm.title,
      "add-on": _vm.unit,
      "disabled": _vm.disabled,
      "name": _vm.sanitizedName,
      "decimals": _vm.decimals,
      "min": _vm.min,
      "max": _vm.max,
      "currency": "",
      "framed": "",
      "large": ""
    },
    on: {
      "change": _vm.onInputChange,
      "focus": _vm.onInputFocus,
      "blur": _vm.onInputBlur
    }
  }), _vm._v(" "), _c('client-only', [_c('vue-slider', _vm._b({
    ref: "slider",
    staticClass: "bb-big-slider__vue-slider",
    attrs: {
      "value": _vm.computedVueSliderVal,
      "min": _vm.computedInternalMin,
      "max": _vm.vueSliderMax,
      "interval": _vm.step,
      "tooltip": "none",
      "height": "5",
      "use-keyboard": "",
      "silent": ""
    },
    on: {
      "change": _vm.vueSliderChange
    }
  }, 'vue-slider', _vm.disabled && {
    disabled: _vm.disabled
  }, false), [_c('div', {
    staticClass: "vue-slider-background"
  })])], 1)], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-big-slider__ranges"
  }, [_c('span', [_vm._v(_vm._s(_vm.localizedMin) + "\n      "), _vm.unitMin ? _c('span', [_vm._v(_vm._s(_vm.unitMin))]) : _vm.unit ? _c('span', [_vm._v(_vm._s(_vm.unit))]) : _vm._e()]), _vm._v(" "), _c('span', [_vm._v(_vm._s(_vm.localizedMax) + "\n      "), _vm.unitMax ? _c('span', [_vm._v(_vm._s(_vm.unitMax))]) : _vm.unit ? _c('span', [_vm._v(_vm._s(_vm.unit))]) : _vm._e()])])]);
};
var __vue_staticRenderFns__$1D = [];

/* style */
const __vue_inject_styles__$1F = function (inject) {
  if (!inject) return;
  inject("data-v-3e1a0092_0", {
    source: ".bb-big-slider{line-height:1}.bb-big-slider___fields{position:relative}.bb-big-slider--avoid-overflow{padding-right:1.125rem;padding-left:1.125rem}.bb-big-slider.is-disabled{cursor:not-allowed}.bb-big-slider .bb-input__input{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-big-slider .bb-big-slider__ranges{margin-bottom:.625rem;color:#57697a;font-size:.5625rem;text-transform:uppercase;user-select:none;display:flex;justify-content:space-between;margin-top:1.25rem;color:#9dabb9;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}.bb-big-slider .bb-big-slider__ranges>span:last-child{text-align:right}.bb-big-slider .bb-big-slider__input input:focus{border-color:#00285a}.bb-big-slider .bb-big-slider__vue-slider{position:absolute;bottom:1px;left:1px;width:calc(100% - 2px)!important;height:.3125rem}.bb-big-slider .vue-slider{padding:0!important}.bb-big-slider .vue-slider-disabled .vue-slider-process::before{background-color:#dee3e8}.bb-big-slider .vue-slider-disabled .vue-slider-rail{background-color:#dee3e8}.bb-big-slider .vue-slider-disabled .vue-slider-dot-handle{border-color:#dee3e8}.bb-big-slider .vue-slider-rail{position:relative!important;height:.3125rem}.bb-big-slider .vue-slider-background{position:relative;top:-.1875rem;display:flex;overflow:hidden;width:100%;height:.5rem;align-items:flex-end;border-radius:0 0 .5rem .5rem}.bb-big-slider .vue-slider-background::before{display:block;width:100%;height:.3125rem;background-color:#dee3e8;content:\"\"}.bb-big-slider .vue-slider-process{position:absolute;top:-.1875rem!important;display:flex;overflow:hidden;height:.5rem!important;align-items:flex-end;border-radius:0 0 0 .5rem}.bb-big-slider .vue-slider-process::before{display:block;height:.3125rem;flex:1;background-color:#0ccca2;content:\"\"}.bb-big-slider .vue-slider-dot{position:absolute;top:70%!important;left:-1.34375rem;width:auto!important;height:auto!important;padding:.625rem;outline:0}.bb-big-slider .vue-slider-dot:hover{cursor:ew-resize}.bb-big-slider .vue-slider-dot-handle{width:1.75rem;height:1.75rem;box-sizing:border-box;border:.375rem solid #0ccca2;border-radius:50%;background-color:#fff;box-shadow:0 0 0 0 rgba(12,204,162,.2);outline:0;transition:all .2s ease}.bb-big-slider .vue-slider-dot-handle-focus:not(:hover){box-shadow:0 0 0 .375rem rgba(12,204,162,.2)}.bb-big-slider .vue-slider-dot-handle-disabled{background-color:#fff;cursor:not-allowed}.bb-big-slider .vue-slider-dot-handle:hover{border-color:#3dd6b5}.bb-big-slider .vue-slider-dot-handle:active{width:1.375rem;height:1.375rem;border-color:#099c7c;box-shadow:0 0 0 0 rgba(12,204,162,.2)}.bb-big-slider .vue-slider-sr-only{display:none!important}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1F = undefined;
/* module identifier */
const __vue_module_identifier__$1F = undefined;
/* functional template */
const __vue_is_functional_template__$1F = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1F = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1D,
  staticRenderFns: __vue_staticRenderFns__$1D
}, __vue_inject_styles__$1F, __vue_script__$1F, __vue_scope_id__$1F, __vue_is_functional_template__$1F, __vue_module_identifier__$1F, false, createInjector, undefined, undefined);

var script$1E = Vue.extend({
  name: 'bb-blog-card-meta',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    /**
     * Category name
     */
    categoryName: {
      type: String
    },
    /**
     * Reading time
     */
    readingTime: {
      type: String
    },
    /**
     * Condensed, renders compact meta
     */
    condensed: {
      type: Boolean,
      default: false
    },
    /**
     * White color, renders meta with white reading time
     */
    white: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      return {
        'bb-blog-card-meta--condensed': this.condensed,
        'bb-blog-card-meta--white': this.white
      };
    }
  }
});

/* script */
const __vue_script__$1E = script$1E;

/* template */
var __vue_render__$1C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-blog-card-meta",
    class: _vm.classes
  }, [_vm.categoryName ? _c('div', {
    staticClass: "bb-blog-card-meta__category-name"
  }, [_vm._v("\n    " + _vm._s(_vm.categoryName) + "\n  ")]) : _vm._e(), _vm._v(" "), _vm.readingTime ? _c('div', {
    staticClass: "bb-blog-card-meta__reading-time"
  }, [!_vm.condensed ? _c('bb-icon', {
    staticClass: "bb-blog-card-meta__reading-time-icon",
    attrs: {
      "name": "ui-clock-outline",
      "size": "18"
    }
  }) : _vm._e(), _vm._v("\n    " + _vm._s(_vm.readingTime) + "\n  ")], 1) : _vm._e()]);
};
var __vue_staticRenderFns__$1C = [];

/* style */
const __vue_inject_styles__$1E = function (inject) {
  if (!inject) return;
  inject("data-v-d11ad09e_0", {
    source: ".bb-blog-card-meta{display:flex;justify-content:space-between}.bb-blog-card-meta--condensed{justify-content:flex-start}.bb-blog-card-meta__category-name,.bb-blog-card-meta__reading-time{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1.3}.bb-blog-card-meta__category-name{color:#0ccca2}.bb-blog-card-meta__reading-time-icon{margin-right:.625rem}.bb-blog-card-meta__reading-time{display:flex;align-items:center;color:#89a}.bb-blog-card-meta--white .bb-blog-card-meta__reading-time{color:#fff}.bb-blog-card-meta--condensed .bb-blog-card-meta__reading-time::before{display:block;width:1.25rem;height:.0625rem;margin:0 .625rem;background:#89a;content:\"\"}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1E = undefined;
/* module identifier */
const __vue_module_identifier__$1E = undefined;
/* functional template */
const __vue_is_functional_template__$1E = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1E = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1C,
  staticRenderFns: __vue_staticRenderFns__$1C
}, __vue_inject_styles__$1E, __vue_script__$1E, __vue_scope_id__$1E, __vue_is_functional_template__$1E, __vue_module_identifier__$1E, false, createInjector, undefined, undefined);

var script$1D = {
  name: 'bb-router-link',
  components: {
    BbLink: __vue_component__$2p
  },
  computed: {
    componentName() {
      return Vue.options.components['nuxt-link'] ? 'nuxt-link' : 'bb-link';
    }
  }
};

/* script */
const __vue_script__$1D = script$1D;

/* template */
var __vue_render__$1B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(_vm.componentName, _vm._g(_vm._b({
    tag: "component",
    staticClass: "bb-router-link"
  }, 'component', _vm.$attrs, false), _vm.$listeners), [_vm._t("default")], 2);
};
var __vue_staticRenderFns__$1B = [];

/* style */
const __vue_inject_styles__$1D = undefined;
/* scoped */
const __vue_scope_id__$1D = undefined;
/* module identifier */
const __vue_module_identifier__$1D = undefined;
/* functional template */
const __vue_is_functional_template__$1D = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1D = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1B,
  staticRenderFns: __vue_staticRenderFns__$1B
}, __vue_inject_styles__$1D, __vue_script__$1D, __vue_scope_id__$1D, __vue_is_functional_template__$1D, __vue_module_identifier__$1D, false, undefined, undefined, undefined);

/**
 * A card for a featured blog post.
 */
var script$1C = Vue.extend({
  name: 'bb-blog-featured-card',
  components: {
    BbBlogCardMeta: __vue_component__$1E,
    BbIcon: __vue_component__$2C,
    BbRouterLink: __vue_component__$1D,
    BbSkeleton: __vue_component__$2d
  },
  mixins: [HrefHandler],
  props: {
    /**
     * Card loading state
     */
    loading: {
      type: Boolean
    },
    /**
     * Card image absolute or relative url
     */
    src: {
      type: String
    },
    /**
     * Card title
     */
    title: {
      type: String
    },
    /**
     * Card related article category name
     */
    categoryName: {
      type: String
    },
    /**
     * Card related article reading time
     */
    readingTime: {
      type: String
    },
    /**
     * Card related article link label
     */
    linkLabel: {
      type: String
    },
    /**
     * Text for the container's aria label
     */
    ariaLabel: {
      type: String
    }
  },
  computed: {
    containerAriaLabel() {
      var _this$ariaLabel, _this$ariaLabel2;
      return (_this$ariaLabel = this.ariaLabel) !== null && _this$ariaLabel !== void 0 ? _this$ariaLabel : [this.readingTime, this.categoryName, (_this$ariaLabel2 = this.ariaLabel) !== null && _this$ariaLabel2 !== void 0 ? _this$ariaLabel2 : this.title].filter(Boolean).join(', ');
    }
  }
});

/* script */
const __vue_script__$1C = script$1C;

/* template */
var __vue_render__$1A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.loading ? _c('div', {
    staticClass: "bb-blog-featured-card",
    attrs: {
      "aria-busy": "true",
      "role": "link",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_c('bb-skeleton', {
    staticClass: "bb-blog-featured-card__skeleton",
    attrs: {
      "width": "85%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-card__skeleton",
    attrs: {
      "width": "100%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-card__skeleton",
    attrs: {
      "width": "85%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-card__skeleton",
    attrs: {
      "width": "100%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-card__skeleton",
    attrs: {
      "width": "60%",
      "height": "10px",
      "color": "gray-30"
    }
  })], 1) : _c('bb-router-link', {
    staticClass: "bb-blog-featured-card",
    attrs: {
      "to": _vm.to,
      "target": _vm.target,
      "color": "none",
      "no-underline": "",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_vm.src ? _c('div', {
    staticClass: "bb-blog-featured-card__image",
    style: {
      backgroundImage: "url(" + _vm.src + ")"
    },
    attrs: {
      "aria-hidden": "true"
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-featured-card__gradient",
    attrs: {
      "aria-hidden": "true"
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-featured-card__content"
  }, [_vm.title ? _c('div', {
    staticClass: "bb-blog-featured-card__title"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]) : _vm._e(), _vm._v(" "), _c('bb-blog-card-meta', {
    staticClass: "bb-blog-featured-card__meta",
    attrs: {
      "category-name": _vm.categoryName,
      "reading-time": _vm.readingTime,
      "white": ""
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-featured-card__link"
  }, [_c('span', {
    staticClass: "bb-blog-featured-card__link-label"
  }, [_vm._v("\n        " + _vm._s(_vm.linkLabel) + "\n      ")]), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "ui-arrow-right",
      "fill": "mint",
      "size": "24"
    }
  })], 1)], 1)]);
};
var __vue_staticRenderFns__$1A = [];

/* style */
const __vue_inject_styles__$1C = function (inject) {
  if (!inject) return;
  inject("data-v-9b232b8e_0", {
    source: ".bb-blog-featured-card{display:grid;overflow:hidden;align-items:end;border-radius:.625rem;cursor:pointer}.bb-blog-featured-card__image{height:100%;background-position:center;background-size:cover;grid-area:1/1/1/1}.bb-blog-featured-card__gradient{height:70%;background-image:linear-gradient(180deg,rgba(0,40,90,.00008) 2.6%,rgba(56,65,77,.608) 48.44%,rgba(56,65,77,.8) 89.69%);grid-area:1/1/1/1}.bb-blog-featured-card__content{display:flex;min-height:21.25rem;flex-direction:column;justify-content:flex-end;padding:1.875rem;background-size:cover;grid-area:1/1/1/1;transform:translateY(3.375rem);transition:transform .2s}@media (min-width:768px){.bb-blog-featured-card__content{min-height:30.3125rem}}.bb-blog-featured-card:hover .bb-blog-featured-card__content{transform:translateY(0)}.bb-blog-featured-card__skeleton:nth-child(1){margin-top:5.625rem}@media (min-width:768px){.bb-blog-featured-card__skeleton:nth-child(1){margin-top:14.6875rem}}.bb-blog-featured-card__skeleton:nth-child(2),.bb-blog-featured-card__skeleton:nth-child(4){margin-top:.625rem}.bb-blog-featured-card__skeleton:nth-child(3){margin-top:1.25rem}.bb-blog-featured-card__skeleton:nth-child(5){margin-top:3.75rem}.bb-blog-featured-card__title{color:#fff;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.5rem;line-height:1.1}@media (min-width:768px){.bb-blog-featured-card__title{font-size:2rem}}.bb-blog-featured-card__link{display:flex;width:fit-content;align-items:center;margin-top:1.875rem;opacity:0;transition:opacity .2s}.bb-blog-featured-card:hover .bb-blog-featured-card__link{opacity:1}.bb-blog-featured-card__link-label{padding-right:1.25rem;color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:16px;line-height:1.5}.bb-blog-featured-card__meta{margin-top:1.875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1C = undefined;
/* module identifier */
const __vue_module_identifier__$1C = undefined;
/* functional template */
const __vue_is_functional_template__$1C = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1C = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1A,
  staticRenderFns: __vue_staticRenderFns__$1A
}, __vue_inject_styles__$1C, __vue_script__$1C, __vue_scope_id__$1C, __vue_is_functional_template__$1C, __vue_module_identifier__$1C, false, createInjector, undefined, undefined);

/**
 * A featured list card for a featured blog post.
 */
var script$1B = Vue.extend({
  name: 'bb-blog-featured-list-card',
  components: {
    BbBlogCardMeta: __vue_component__$1E,
    BbIcon: __vue_component__$2C,
    BbRouterLink: __vue_component__$1D,
    BbSkeleton: __vue_component__$2d
  },
  mixins: [HrefHandler],
  props: {
    /**
     * Read more link label
     */
    linkLabel: {
      type: String
    },
    /**
     * Card loading state
     */
    loading: {
      type: Boolean
    },
    /**
     * Card image absolute or relative url
     */
    src: {
      type: String
    },
    /**
     * Card title
     */
    title: {
      type: String
    },
    /**
     * Card related article category name
     */
    categoryName: {
      type: String
    },
    /**
     * Card related article reading time
     */
    readingTime: {
      type: String
    },
    /**
     * Card related article localized date string
     */
    date: {
      type: String
    },
    /**
     * Text for the container's aria label
     */
    ariaLabel: {
      type: String
    }
  },
  computed: {
    containerAriaLabel() {
      var _this$ariaLabel, _this$ariaLabel2;
      return (_this$ariaLabel = this.ariaLabel) !== null && _this$ariaLabel !== void 0 ? _this$ariaLabel : [this.readingTime, this.categoryName, this.date, (_this$ariaLabel2 = this.ariaLabel) !== null && _this$ariaLabel2 !== void 0 ? _this$ariaLabel2 : this.title].filter(Boolean).join(', ');
    }
  }
});

/* script */
const __vue_script__$1B = script$1B;

/* template */
var __vue_render__$1z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.loading ? _c('div', {
    staticClass: "bb-blog-featured-list-card",
    attrs: {
      "aria-busy": "true",
      "role": "link",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-blog-featured-list-card__skeleton-picture"
  }, [_c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "100%",
      "height": "100%",
      "color": "gray-30",
      "radius": "0"
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-featured-list-card__skeleton-content"
  }, [_c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "40%",
      "height": "10px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "85%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "85%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "100%",
      "height": "10px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "100%",
      "height": "10px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-featured-list-card__skeleton",
    attrs: {
      "width": "45%",
      "height": "10px",
      "color": "gray-30"
    }
  })], 1)]) : _c('bb-router-link', {
    staticClass: "bb-blog-featured-list-card",
    attrs: {
      "target": _vm.target,
      "to": _vm.to,
      "no-underline": "",
      "color": "none",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-blog-featured-list-card__picture"
  }, [_vm.src ? _c('div', {
    staticClass: "bb-blog-featured-list-card__image",
    style: {
      backgroundImage: "url(" + _vm.src + ")"
    },
    attrs: {
      "aria-hidden": true
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-featured-list-card__link"
  }, [_c('span', {
    staticClass: "bb-blog-featured-list-card__link-label"
  }, [_vm._v("\n        " + _vm._s(_vm.linkLabel) + "\n      ")]), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "ui-arrow-right",
      "fill": "white",
      "size": "24"
    }
  })], 1)]), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-featured-list-card__content"
  }, [_c('bb-blog-card-meta', {
    staticClass: "bb-blog-featured-list-card__meta",
    attrs: {
      "category-name": _vm.categoryName,
      "reading-time": _vm.readingTime
    }
  }), _vm._v(" "), _vm.title ? _c('div', {
    staticClass: "bb-blog-featured-list-card__title"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]) : _vm._e(), _vm._v(" "), _vm.$slots.text ? _c('div', {
    staticClass: "bb-blog-featured-list-card__text"
  }, [_vm._t("text")], 2) : _vm._e(), _vm._v(" "), _vm.date ? _c('div', {
    staticClass: "bb-blog-featured-list-card__date"
  }, [_vm._v("\n      " + _vm._s(_vm.date) + "\n    ")]) : _vm._e()], 1)]);
};
var __vue_staticRenderFns__$1z = [];

/* style */
const __vue_inject_styles__$1B = function (inject) {
  if (!inject) return;
  inject("data-v-4605fc56_0", {
    source: ".bb-blog-featured-list-card{display:flex;overflow:hidden;min-height:23.125rem;flex-direction:column;border-radius:.625rem;background:#fff;box-shadow:0 .3125rem 1.875rem rgba(0,0,0,.05);cursor:pointer}@media (min-width:768px){.bb-blog-featured-list-card{flex-direction:row}}.bb-blog-featured-list-card__picture{display:grid;flex:50%}.bb-blog-featured-list-card__image{min-height:27.8125rem;background-position:center;background-size:cover;grid-area:1/1/1/1}.bb-blog-featured-list-card__date,.bb-blog-featured-list-card__link,.bb-blog-featured-list-card__title{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-blog-featured-list-card__link{display:flex;align-items:center;justify-content:center;background:rgba(12,204,162,.9);font-size:18px;grid-area:1/1/1/1;line-height:1.3;opacity:0;transition:opacity .2s}.bb-blog-featured-list-card:hover .bb-blog-featured-list-card__link{opacity:1}.bb-blog-featured-list-card__link-label{padding-right:.9375rem;color:#fff}.bb-blog-featured-list-card__content{flex:50%;padding:2.1875rem 3.125rem}.bb-blog-featured-list-card__title{margin-top:1.875rem;color:#38414d;font-size:1.5rem;line-height:1.1}@media (min-width:768px){.bb-blog-featured-list-card__title{font-size:2rem}}.bb-blog-featured-list-card__text{margin-top:1.25rem;color:#57697a;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5}@media (min-width:768px){.bb-blog-featured-list-card__text{font-size:16px}}.bb-blog-featured-list-card__date{margin-top:3.4375rem;color:#89a;font-size:.875rem;line-height:1.3}.bb-blog-featured-list-card__skeleton-content,.bb-blog-featured-list-card__skeleton-picture{flex:50%}.bb-blog-featured-list-card__skeleton-content{padding:2.1875rem 3.125rem}.bb-blog-featured-list-card__skeleton-content>:nth-child(2){margin-top:2.5rem}.bb-blog-featured-list-card__skeleton-content>:nth-child(3),.bb-blog-featured-list-card__skeleton-content>:nth-child(5){margin-top:.9375rem}.bb-blog-featured-list-card__skeleton-content>:nth-child(4){margin-top:1.25rem}.bb-blog-featured-list-card__skeleton-content>:nth-child(6){margin-top:12.1875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1B = undefined;
/* module identifier */
const __vue_module_identifier__$1B = undefined;
/* functional template */
const __vue_is_functional_template__$1B = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1B = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1z,
  staticRenderFns: __vue_staticRenderFns__$1z
}, __vue_inject_styles__$1B, __vue_script__$1B, __vue_scope_id__$1B, __vue_is_functional_template__$1B, __vue_module_identifier__$1B, false, createInjector, undefined, undefined);

/**
 * A list card for a featured blog post.
 */
var script$1A = Vue.extend({
  name: 'bb-blog-list-card',
  components: {
    BbBlogCardMeta: __vue_component__$1E,
    BbIcon: __vue_component__$2C,
    BbRouterLink: __vue_component__$1D,
    BbSkeleton: __vue_component__$2d
  },
  mixins: [HrefHandler],
  props: {
    /**
     * Card loading state
     */
    loading: {
      type: Boolean
    },
    /**
     * Card image absolute or relative url
     */
    src: {
      type: String
    },
    /**
     * Card title
     */
    title: {
      type: String
    },
    /**
     * Card related article category name
     */
    categoryName: {
      type: String
    },
    /**
     * Card related article reading time
     */
    readingTime: {
      type: String
    },
    /**
     * Card related article localized date string
     */
    date: {
      type: String
    },
    /**
     * Read more link label
     */
    linkLabel: {
      type: String
    },
    /**
     * Text for the container's aria label
     */
    ariaLabel: {
      type: String
    }
  },
  computed: {
    containerAriaLabel() {
      var _this$ariaLabel;
      return (_this$ariaLabel = this.ariaLabel) !== null && _this$ariaLabel !== void 0 ? _this$ariaLabel : [this.readingTime, this.categoryName, this.date, this.title].filter(Boolean).join(', ');
    }
  }
});

/* script */
const __vue_script__$1A = script$1A;

/* template */
var __vue_render__$1y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.loading ? _c('div', {
    staticClass: "bb-blog-list-card",
    attrs: {
      "aria-busy": "true",
      "role": "link",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-blog-list-card__meta-wrapper"
  }, [_c('bb-skeleton', {
    staticClass: "bb-blog-list-card__skeleton",
    attrs: {
      "width": "60%",
      "height": "10px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-list-card__skeleton",
    attrs: {
      "width": "85%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-list-card__skeleton",
    attrs: {
      "width": "100%",
      "height": "20px",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-list-card__skeleton",
    attrs: {
      "width": "45%",
      "height": "10px",
      "color": "gray-30"
    }
  })], 1), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-list-card__skeleton-picture-mobile",
    attrs: {
      "width": "100%",
      "height": "192px",
      "color": "gray-30",
      "radius": "0 0 8px 8px"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "bb-blog-list-card__skeleton-picture",
    attrs: {
      "width": "100%",
      "height": "252px",
      "color": "gray-30",
      "radius": "0 0 8px 8px"
    }
  })], 1) : _c('bb-router-link', {
    staticClass: "bb-blog-list-card",
    attrs: {
      "to": _vm.to,
      "target": _vm.target,
      "color": "none",
      "no-underline": "",
      "aria-label": _vm.containerAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-blog-list-card__meta-wrapper"
  }, [_c('bb-blog-card-meta', {
    staticClass: "bb-blog-list-card__meta",
    attrs: {
      "category-name": _vm.categoryName,
      "reading-time": _vm.readingTime
    }
  }), _vm._v(" "), _vm.title ? _c('div', {
    staticClass: "bb-blog-list-card__title"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]) : _vm._e(), _vm._v(" "), _vm.date ? _c('div', {
    staticClass: "bb-blog-list-card__date"
  }, [_vm._v("\n      " + _vm._s(_vm.date) + "\n    ")]) : _vm._e()], 1), _vm._v(" "), !_vm.$slots.text ? _c('div', {
    staticClass: "bb-blog-list-card__picture"
  }, [_vm.src ? _c('div', {
    staticClass: "bb-blog-list-card__image",
    style: {
      backgroundImage: "url(" + _vm.src + ")"
    },
    attrs: {
      "aria-hidden": true
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-list-card__link"
  }, [_c('span', {
    staticClass: "bb-blog-list-card__link-label"
  }, [_vm._v("\n        " + _vm._s(_vm.linkLabel) + "\n      ")]), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "ui-arrow-right",
      "fill": "white",
      "size": "24"
    }
  })], 1)]) : _c('div', {
    staticClass: "bb-blog-list-card__text"
  }, [_vm._t("text"), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-list-card__text-link"
  }, [_c('span', {
    staticClass: "bb-blog-list-card__text-link-label"
  }, [_vm._v("\n        " + _vm._s(_vm.linkLabel) + "\n      ")]), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "ui-arrow-right",
      "fill": "white",
      "size": "24"
    }
  })], 1)], 2)]);
};
var __vue_staticRenderFns__$1y = [];

/* style */
const __vue_inject_styles__$1A = function (inject) {
  if (!inject) return;
  inject("data-v-e28a7cb6_0", {
    source: ".bb-blog-list-card{display:flex;overflow:hidden;min-height:23.125rem;flex-direction:column;justify-content:space-between;padding:.8125rem;border-radius:.625rem;background:#fff;box-shadow:0 .3125rem 1.875rem rgba(0,0,0,.05);cursor:pointer}@media (min-width:768px){.bb-blog-list-card{min-height:30.3125rem;padding:.9375rem}}.bb-blog-list-card__meta-wrapper{padding:.1875rem}.bb-blog-list-card__date,.bb-blog-list-card__link,.bb-blog-list-card__text,.bb-blog-list-card__title{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-blog-list-card__title{margin:1.25rem 0;color:#38414d;font-size:1.125rem;line-height:1.1}@media (min-width:768px){.bb-blog-list-card__title{font-size:1.5rem}}.bb-blog-list-card__date{color:#89a;font-size:.875rem;line-height:1.3}.bb-blog-list-card__picture,.bb-blog-list-card__text{overflow:hidden;border-radius:0 0 .625rem .625rem}.bb-blog-list-card__picture{display:grid}.bb-blog-list-card__image{height:12rem;background-position:center;background-size:cover;grid-area:1/1/1/1}@media (min-width:768px){.bb-blog-list-card__image{height:15.75rem}}.bb-blog-list-card__link{display:flex;width:100%;height:12rem;align-items:center;justify-content:center;background:rgba(12,204,162,.9);font-size:18px;grid-area:1/1/1/1;line-height:1.3;opacity:0;transition:opacity .2s}@media (min-width:768px){.bb-blog-list-card__link{height:15.75rem}}.bb-blog-list-card:hover .bb-blog-list-card__link{opacity:1}.bb-blog-list-card__link-label{padding-right:.625rem;color:#fff}.bb-blog-list-card__text{display:flex;height:12rem;flex-direction:column;justify-content:space-between;padding:1.5625rem;background:#0ccca2;color:#fff;font-size:14px;line-height:1.1}@media (min-width:768px){.bb-blog-list-card__text{height:15.75rem;font-size:16px;line-height:1.5}}.bb-blog-list-card__text-link{display:none}@media (min-width:768px){.bb-blog-list-card__text-link{display:block;font-size:18px;line-height:1.3}}.bb-blog-list-card__skeleton-picture{display:none}@media (min-width:768px){.bb-blog-list-card__skeleton-picture{display:block}}@media (min-width:768px){.bb-blog-list-card__skeleton-picture-mobile{display:none}}.bb-blog-list-card__skeleton:nth-child(1){margin-top:.4375rem}.bb-blog-list-card__skeleton:nth-child(1),.bb-blog-list-card__skeleton:nth-child(3){margin-bottom:1.625rem}.bb-blog-list-card__skeleton:nth-child(2){margin-bottom:.625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1A = undefined;
/* module identifier */
const __vue_module_identifier__$1A = undefined;
/* functional template */
const __vue_is_functional_template__$1A = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1A = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1y,
  staticRenderFns: __vue_staticRenderFns__$1y
}, __vue_inject_styles__$1A, __vue_script__$1A, __vue_scope_id__$1A, __vue_is_functional_template__$1A, __vue_module_identifier__$1A, false, createInjector, undefined, undefined);

var script$1z = {
  name: 'bb-blog-topic-card',
  components: {
    BbBlogCardMeta: __vue_component__$1E,
    BbSkeleton: __vue_component__$2d,
    BbRouterLink: __vue_component__$1D
  },
  mixins: [HrefHandler],
  props: {
    categoryName: {
      type: String
    },
    readingTime: {
      type: String
    },
    title: String,
    date: String,
    loading: Boolean,
    ariaLabel: {
      type: String
    }
  },
  computed: {
    classes() {
      return ['bb-blog-topic-card', {
        'bb-blog-topic-card--loading': this.loading
      }];
    },
    computedAriaLabel() {
      var _this$ariaLabel;
      return (_this$ariaLabel = this.ariaLabel) !== null && _this$ariaLabel !== void 0 ? _this$ariaLabel : [this.title, this.categoryName, this.readingTime, this.date].filter(Boolean).join(', ');
    }
  }
};

/* script */
const __vue_script__$1z = script$1z;

/* template */
var __vue_render__$1x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-router-link', {
    class: _vm.classes,
    attrs: {
      "to": !_vm.loading && _vm.to,
      "target": _vm.target,
      "color": "none",
      "no-underline": "",
      "aria-label": _vm.computedAriaLabel,
      "aria-busy": _vm.loading
    }
  }, [_vm.loading ? [_c('bb-skeleton', {
    attrs: {
      "width": "70%",
      "height": "10",
      "color": "gray-30"
    }
  })] : [_c('bb-blog-card-meta', {
    attrs: {
      "category-name": _vm.categoryName,
      "reading-time": _vm.readingTime,
      "condensed": ""
    }
  })], _vm._v(" "), _vm.title ? _c('div', {
    staticClass: "bb-blog-topic-card__title"
  }, [_vm.loading ? [_c('bb-skeleton', {
    staticClass: "m-b-10",
    attrs: {
      "width": "100%",
      "height": "20",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    attrs: {
      "width": "100%",
      "height": "20",
      "color": "gray-30"
    }
  })] : [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]], 2) : _vm._e(), _vm._v(" "), _vm.date ? _c('div', {
    staticClass: "bb-blog-topic-card__date"
  }, [_vm.loading ? [_c('bb-skeleton', {
    attrs: {
      "width": "50%",
      "height": "8",
      "color": "gray-30"
    }
  })] : [_vm._v("\n      " + _vm._s(_vm.date) + "\n    ")]], 2) : _vm._e()], 2);
};
var __vue_staticRenderFns__$1x = [];

/* style */
const __vue_inject_styles__$1z = function (inject) {
  if (!inject) return;
  inject("data-v-cd9a543e_0", {
    source: ".bb-blog-topic-card .bb-blog-topic-card__date,.bb-blog-topic-card .bb-blog-topic-card__title{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-blog-topic-card .bb-blog-topic-card__title{margin:.9375rem 0;color:#38414d;font-size:1.125rem;line-height:1.1;transition:color .2s}@media (min-width:768px){.bb-blog-topic-card .bb-blog-topic-card__title{font-size:1.5rem}}.bb-blog-topic-card:hover.bb-blog-topic-card .bb-blog-topic-card__title{color:#5d7083}.bb-blog-topic-card--loading.bb-blog-topic-card .bb-blog-topic-card__title{margin:1.6875rem 0}.bb-blog-topic-card .bb-blog-topic-card__date{color:#89a;font-size:.875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1z = undefined;
/* module identifier */
const __vue_module_identifier__$1z = undefined;
/* functional template */
const __vue_is_functional_template__$1z = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1z = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1x,
  staticRenderFns: __vue_staticRenderFns__$1x
}, __vue_inject_styles__$1z, __vue_script__$1z, __vue_scope_id__$1z, __vue_is_functional_template__$1z, __vue_module_identifier__$1z, false, createInjector, undefined, undefined);

var Icon$C = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3049514511__a bb-icon__dynamic-fill",attrs:{"d":"M5.5,7A1.5,1.5 0 0,1 4,5.5A1.5,1.5 0 0,1 5.5,4A1.5,1.5 0 0,1 7,5.5A1.5,1.5 0 0,1 5.5,7M21.41,11.58L12.41,2.58C12.05,2.22 11.55,2 11,2H4C2.89,2 2,2.89 2,4V11C2,11.55 2.22,12.05 2.59,12.41L11.58,21.41C11.95,21.77 12.45,22 13,22C13.55,22 14.05,21.77 14.41,21.41L21.41,14.41C21.78,14.05 22,13.55 22,13C22,12.44 21.77,11.94 21.41,11.58Z"}})]) } };

Vue.component('icon-ui-tag', Icon$C);

var Icon$B = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1576266281__a bb-icon__dynamic-fill",attrs:{"d":"M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"}})]) } };

Vue.component('icon-ui-star', Icon$B);

const TAG_LABEL_MAX_LENGTH = 31;
/**
 * Campaign Card component.
 *
 * @deprecated bb-campaign-card is deprecated and will be removed in a future major version.
 */
var script$1y = Vue.extend({
  name: 'bb-campaign-card',
  components: {
    BbButton: __vue_component__$2v,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbIcon: __vue_component__$2C,
    BbCard: __vue_component__$2t
  },
  props: {
    /**
     * Display a "Overlay" type card
     */
    overlay: Boolean,
    /**
     * Image path
     */
    imagePath: String,
    /**
     * Card title
     */
    title: String,
    /**
     * Label to display in case of a campaign. Max length 31 characters.
     */
    labelCampaign: {
      type: String,
      validator: label => label.length <= TAG_LABEL_MAX_LENGTH
    },
    /**
     * Label to display in case of a loyalty program. Max length 31 characters.
     */
    labelLoyalty: {
      type: String,
      validator: label => label.length <= TAG_LABEL_MAX_LENGTH
    },
    /**
     * Button object with props for [bb-button](/#/Atoms/bb-button)
     */
    button: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Additional note in footer after main slot content
     */
    footerNote: String,
    /**
     * Consent text in footer
     */
    consent: String,
    /**
     * Full card size
     */
    full: Boolean
  },
  computed: {
    classes() {
      return [{
        'bb-campaign-card--overlay': this.overlay,
        'bb-campaign-card--loyalty': this.labelLoyalty,
        'bb-campaign-card--campaign': this.labelCampaign,
        'bb-campaign-card--has-footer': this.footerIsRendered
      }];
    },
    footerIsRendered() {
      return this.$slots.default || this.footerNote || this.consent;
    },
    titleClasses() {
      return this.overlay ? 'bb-campaign-card__title-overlay' : 'bb-campaign-card__title';
    },
    tagLabel() {
      return this.labelLoyalty || this.labelCampaign;
    },
    tagLabelExceedsMaxLength() {
      var _this$tagLabel;
      return ((_this$tagLabel = this.tagLabel) === null || _this$tagLabel === void 0 ? void 0 : _this$tagLabel.length) > TAG_LABEL_MAX_LENGTH;
    },
    tagLabelTruncated() {
      var _this$tagLabel2;
      return this.tagLabelExceedsMaxLength ? `${(_this$tagLabel2 = this.tagLabel) === null || _this$tagLabel2 === void 0 ? void 0 : _this$tagLabel2.substring(0, TAG_LABEL_MAX_LENGTH)}…` : this.tagLabel;
    },
    iconName() {
      if (this.labelLoyalty) return 'ui-star';
      if (this.labelCampaign) return 'ui-tag';
      return null;
    },
    imageStyles() {
      const image = {
        'background-image': `linear-gradient(90deg, rgba(199, 242, 236, 1) 40%, rgba(199, 242, 236, 0) 90%), url(${this.imagePath})`
      };
      const overlay = {
        'background-image': `linear-gradient(to right, rgba(12,204,162, 0.9), rgba(12,204,162, 0.95)), url(${this.imagePath})`
      };
      return this.overlay ? overlay : image;
    },
    size() {
      return this.full ? 'full' : 'wide';
    }
  },
  methods: {
    onButtonClick() {
      /**
       * Click event when Button is clicked.
       *
       * @event click
       */
      this.$emit('click');
    }
  }
});

/* script */
const __vue_script__$1y = script$1y;

/* template */
var __vue_render__$1w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-card', {
    staticClass: "bb-campaign-card",
    class: _vm.classes,
    attrs: {
      "tabindex": "0",
      "size": _vm.size
    }
  }, [_c('div', {
    staticClass: "bb-campaign-card__container"
  }, [_c('div', {
    staticClass: "bb-campaign-card__content",
    style: _vm.imageStyles,
    attrs: {
      "no-gutters": ""
    }
  }, [_vm.tagLabel ? _c('div', {
    staticClass: "bb-campaign-card__tag",
    attrs: {
      "title": _vm.tagLabel
    }
  }, [_c('bb-icon', {
    staticClass: "bb-campaign-card__tag-icon",
    attrs: {
      "name": _vm.iconName,
      "size": "18"
    }
  }), _vm._v("\n        " + _vm._s(_vm.tagLabelTruncated) + "\n      ")], 1) : _vm._e(), _vm._v(" "), _c('h3', {
    class: _vm.titleClasses
  }, [_vm._v("\n        " + _vm._s(_vm.title) + "\n      ")]), _vm._v(" "), _vm.button && _vm.button.label ? _c('bb-button', _vm._b({
    staticClass: "bb-campaign-card__button",
    attrs: {
      "color": _vm.overlay ? 'white' : 'mint'
    },
    on: {
      "click": _vm.onButtonClick
    }
  }, 'bb-button', _vm.button, false)) : _vm._e()], 1), _vm._v(" "), _vm.footerIsRendered ? _c('div', {
    staticClass: "bb-campaign-card__footer"
  }, [!_vm.overlay ? _c('bb-row', {
    staticClass: "bb-campaign-card__footer-text-row"
  }, [_c('bb-col', [_c('p', {
    staticClass: "bb-campaign-card__footer-text"
  }, [_vm._t("default")], 2)]), _vm._v(" "), _vm.footerNote ? _c('bb-col', {
    attrs: {
      "md": "auto",
      "sm": "24"
    }
  }, [_c('p', {
    staticClass: "bb-campaign-card__footer-note"
  }, [_vm._v("\n            " + _vm._s(_vm.footerNote) + "\n          ")])]) : _vm._e()], 1) : _vm._e(), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-campaign-card__footer-button-row"
  }, [_c('bb-col', {
    attrs: {
      "col": "24"
    }
  }, [_c('bb-button', _vm._b({
    attrs: {
      "display": "block"
    },
    on: {
      "click": _vm.onButtonClick
    }
  }, 'bb-button', _vm.button, false))], 1)], 1), _vm._v(" "), !_vm.overlay && _vm.consent ? _c('bb-row', {
    staticClass: "bb-campaign-card__footer-consent-row"
  }, [_c('bb-col', {
    attrs: {
      "col": "24"
    }
  }, [_c('p', {
    staticClass: "bb-campaign-card__footer-consent"
  }, [_vm._v("\n            " + _vm._s(_vm.consent) + "\n          ")])])], 1) : _vm._e()], 1) : _vm._e()])]);
};
var __vue_staticRenderFns__$1w = [];

/* style */
const __vue_inject_styles__$1y = function (inject) {
  if (!inject) return;
  inject("data-v-72952f33_0", {
    source: ".bb-campaign-card{height:100%;border-radius:8px;background:#fff;box-shadow:0 .0625rem .9375rem 0 rgba(74,74,74,.06);color:#00285a}.bb-campaign-card.bb-campaign-card{padding:0}.bb-campaign-card__container{position:relative;display:flex;overflow:hidden;height:100%;flex-direction:column;border-radius:.5rem .5rem}@media (min-width:768px){.bb-campaign-card__container{min-height:8.75rem;justify-content:center}}.bb-campaign-card__content{display:flex;flex-direction:column;align-items:flex-start;justify-content:flex-start;padding:.9375rem 1.5625rem;margin:auto 0;background-color:#eeeff2;background-position:center;background-repeat:no-repeat;background-size:cover;border-top-left-radius:8px;border-top-right-radius:8px;gap:.9375rem}.bb-campaign-card--has-footer .bb-campaign-card__content{height:8.5625rem;flex-direction:row;align-items:center;gap:unset}@media (min-width:768px){.bb-campaign-card__content{height:8.875rem;flex-direction:row;align-items:center;gap:unset}.bb-campaign-card--overlay .bb-campaign-card__content{min-height:8.75rem}}.bb-campaign-card__title,.bb-campaign-card__title-overlay{display:-webkit-box;overflow:hidden;max-width:70%;flex-grow:1;margin-bottom:0;-webkit-box-orient:vertical;-webkit-line-clamp:3;line-clamp:3;line-height:1.5rem;text-overflow:ellipsis}@media (min-width:768px){.bb-campaign-card__title,.bb-campaign-card__title-overlay{padding-top:0;padding-bottom:0;-webkit-line-clamp:2;line-clamp:2;line-height:1.75rem}}.bb-campaign-card--overlay .bb-campaign-card__title,.bb-campaign-card--overlay .bb-campaign-card__title-overlay{max-width:100%}.bb-campaign-card--campaign .bb-campaign-card__title,.bb-campaign-card--loyalty .bb-campaign-card__title{margin-top:1.375rem}.bb-campaign-card__title-overlay{padding-top:2rem;padding-bottom:2rem;color:#fff;text-align:center}@media (min-width:768px){.bb-campaign-card__title-overlay{text-align:left}}.bb-campaign-card__footer{z-index:1;bottom:0;width:100%;padding:.9375rem 1.5625rem;background-color:#fff;border-bottom-left-radius:8px;border-bottom-right-radius:8px}.bb-campaign-card__footer-note,.bb-campaign-card__footer-text{font-size:.75rem}@media (min-width:768px){.bb-campaign-card__footer-note,.bb-campaign-card__footer-text{padding-top:.25rem}}.bb-campaign-card__footer-text{flex-grow:1}.bb-campaign-card__footer-button-row{display:block;width:-moz-available}@media (min-width:768px){.bb-campaign-card__footer-button-row{display:none}}.bb-campaign-card__footer-button-row:only-child{padding-top:.25rem;padding-bottom:.25rem}@media (min-width:768px){.bb-campaign-card__footer-button-row:only-child{display:none}}.bb-campaign-card__footer-consent-row{padding-bottom:.25rem}.bb-campaign-card__footer-consent{padding-top:.9375rem;font-size:.6875rem}@media (min-width:768px){.bb-campaign-card__footer-consent{padding-top:0;padding-bottom:0}}.bb-campaign-card__footer-text-row{padding-bottom:.9375rem}@media (min-width:768px){.bb-campaign-card__footer-text-row{padding-bottom:.25rem}}.bb-campaign-card__footer>:last-child:not(:only-child):not(__footer-consent-row){padding-bottom:.9375rem}@media (min-width:768px){.bb-campaign-card--overlay .bb-campaign-card__footer{padding:0;border-bottom-left-radius:0;border-bottom-right-radius:0}}.bb-campaign-card__image{position:absolute;width:100%;height:100%}@media (min-width:768px){.bb-campaign-card__image{height:8.875rem}}.bb-campaign-card__tag{position:absolute;z-index:1;top:0;left:0;height:1.75rem;padding:.3125rem .9375rem;border-radius:0 .875rem 14px 0;color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-campaign-card--campaign .bb-campaign-card__tag{background:#0ccca2}.bb-campaign-card--loyalty .bb-campaign-card__tag{background:#fc0}.bb-campaign-card__tag-icon{margin-right:.625rem}.bb-campaign-card__button{width:100%}.bb-campaign-card--has-footer .bb-campaign-card__button{display:none;width:auto;margin-left:auto}@media (min-width:768px){.bb-campaign-card__button{width:auto;margin-left:auto}.bb-campaign-card--has-footer .bb-campaign-card__button{display:flex}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1y = undefined;
/* module identifier */
const __vue_module_identifier__$1y = undefined;
/* functional template */
const __vue_is_functional_template__$1y = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1w,
  staticRenderFns: __vue_staticRenderFns__$1w
}, __vue_inject_styles__$1y, __vue_script__$1y, __vue_scope_id__$1y, __vue_is_functional_template__$1y, __vue_module_identifier__$1y, false, createInjector, undefined, undefined);

var script$1x = Vue.extend({
  name: 'bb-campaign-cards',
  components: {
    BbCampaignCard: __vue_component__$1y,
    BbProgressDots: __vue_component__$25
  },
  props: {
    /**
     * Campaign cards
     */
    cards: {
      type: Array,
      required: true
    },
    /**
     * Visible card index
     */
    cardIndex: {
      type: Number,
      default: 0
    },
    /**
     * ProgressDots props
     */
    progressDotsProps: Object,
    /**
     * Text for carousel aria-roledescription
     */
    carouselAriaRoleDescription: {
      type: String,
      default: 'Carousel'
    },
    /**
     * Text for slide aria-roledescription
     */
    slideAriaRoleDescription: {
      type: String,
      default: 'Slide'
    }
  },
  data() {
    return {
      pageCount: 0,
      pageIndex: 0,
      mouseDown: null,
      resizeObserver: null,
      aboutToBeFocused: null
    };
  },
  computed: {
    classes() {
      return ['bb-campaign-cards', {
        'bb-campaign-cards--multiple': this.cards.length > 1
      }];
    },
    tabbableCards() {
      return [this.aboutToBeFocused, this.pageIndex];
    }
  },
  watch: {
    cardIndex(v) {
      this.onPagination(v);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setPageCount();
      this.addResizeListener();
    });
  },
  destroyed() {
    this.removeResizeListener();
  },
  methods: {
    addResizeListener() {
      this.resizeObserver = new ResizeObserver(this.setPageCount);
      this.resizeObserver.observe(document.body);
    },
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(document.body);
      }
    },
    getCarousel() {
      return this.$refs.carousel;
    },
    setPageCount() {
      if (!this.getCarousel()) {
        return;
      }
      const {
        clientWidth,
        scrollWidth
      } = this.getCarousel();
      this.pageCount = Math.ceil(scrollWidth / clientWidth);
    },
    scrollCards(distance) {
      if (!this.getCarousel()) {
        return;
      }
      this.getCarousel().scrollBy({
        left: distance,
        top: 0,
        behavior: 'smooth'
      });
    },
    onPagination(pageIndex) {
      if (!this.getCarousel()) {
        return;
      }
      const distance = this.getCarousel().clientWidth * (pageIndex - this.pageIndex) * 0.9;
      this.scrollCards(distance);
    },
    onMouseDown(e) {
      this.mouseDown = e;
    },
    onMouseUp(e) {
      if (!this.mouseDown) {
        return;
      }
      const distance = e.x - this.mouseDown.x;
      if (distance < -50) {
        this.onPagination(this.pageIndex + 1);
      } else if (distance > 50) {
        this.onPagination(this.pageIndex - 1);
      }
      this.mouseDown = null;
    },
    onScroll(e) {
      const {
        clientWidth,
        scrollLeft
      } = e.target;
      this.pageIndex = Math.ceil(scrollLeft / clientWidth);
    },
    onKeydownLeft() {
      this.keyboardScroll(this.pageIndex - 1);
    },
    onKeydownRight() {
      this.keyboardScroll(this.pageIndex + 1);
    },
    keyboardScroll(toIndex) {
      if (!inRange(toIndex, 0, this.pageCount)) {
        return;
      }
      this.aboutToBeFocused = toIndex;
      this.$nextTick(() => {
        this.$refs.cards[toIndex].$el.focus();
        setTimeout(() => {
          if (toIndex === this.aboutToBeFocused) {
            this.aboutToBeFocused = null;
          }
        }, 700);
        this.onPagination(toIndex);
      });
    }
  }
});

/* script */
const __vue_script__$1x = script$1x;

/* template */
var __vue_render__$1v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.cards.length > 1 ? _c('bb-progress-dots', _vm._b({
    staticClass: "bb-campaign-cards__pagination",
    attrs: {
      "count": _vm.pageCount,
      "active-index": _vm.pageIndex,
      "is-clickable": ""
    },
    on: {
      "activeIndex": _vm.onPagination
    }
  }, 'bb-progress-dots', _vm.progressDotsProps, false)) : _vm._e(), _vm._v(" "), _c('div', {
    ref: "carousel",
    staticClass: "bb-campaign-cards__carousel",
    attrs: {
      "role": "region",
      "aria-live": "polite",
      "aria-roledescription": _vm.carouselAriaRoleDescription
    },
    on: {
      "mousedown": _vm.onMouseDown,
      "mouseup": _vm.onMouseUp,
      "keydown": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "right", 39, $event.key, ["Right", "ArrowRight"])) {
          return null;
        }
        if ('button' in $event && $event.button !== 2) {
          return null;
        }
        $event.preventDefault();
        return _vm.onKeydownRight.apply(null, arguments);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "left", 37, $event.key, ["Left", "ArrowLeft"])) {
          return null;
        }
        if ('button' in $event && $event.button !== 0) {
          return null;
        }
        $event.preventDefault();
        return _vm.onKeydownLeft.apply(null, arguments);
      }],
      "scroll": _vm.onScroll
    }
  }, _vm._l(_vm.cards, function (card, index) {
    return _c('bb-campaign-card', _vm._b({
      key: index,
      ref: "cards",
      refInFor: true,
      staticClass: "bb-campaign-cards__card",
      attrs: {
        "aria-roledescription": _vm.slideAriaRoleDescription,
        "full": _vm.cards.length <= 1,
        "inert": !_vm.tabbableCards.includes(index)
      },
      on: {
        "click": function ($event) {
          return _vm.$emit('click', card.id);
        }
      }
    }, 'bb-campaign-card', card, false), [_vm._t(card.id)], 2);
  }), 1)], 1);
};
var __vue_staticRenderFns__$1v = [];

/* style */
const __vue_inject_styles__$1x = function (inject) {
  if (!inject) return;
  inject("data-v-bff5a15a_0", {
    source: ".bb-campaign-cards__carousel{display:flex;cursor:grab;overflow-x:auto;scroll-snap-type:x mandatory}.bb-campaign-cards__carousel::-webkit-scrollbar{display:none}.bb-campaign-cards__card{margin:0 .625rem;scroll-snap-align:center}.bb-campaign-cards--multiple .bb-campaign-cards__card{flex:0 0 90%}.bb-campaign-cards__pagination{justify-content:flex-end}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1x = undefined;
/* module identifier */
const __vue_module_identifier__$1x = undefined;
/* functional template */
const __vue_is_functional_template__$1x = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1x = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1v,
  staticRenderFns: __vue_staticRenderFns__$1v
}, __vue_inject_styles__$1x, __vue_script__$1x, __vue_scope_id__$1x, __vue_is_functional_template__$1x, __vue_module_identifier__$1x, false, createInjector, undefined, undefined);

var script$1w = {
  name: 'bb-card-button',
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C
  },
  props: {
    label: {
      type: String,
      required: true
    },
    icon: String,
    disabled: Boolean
  },
  computed: {
    classes() {
      return {
        'bb-card-button--disabled': this.disabled
      };
    }
  }
};

/* script */
const __vue_script__$1w = script$1w;

/* template */
var __vue_render__$1u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-button', _vm._g(_vm._b({
    staticClass: "bb-card-button",
    class: _vm.classes,
    attrs: {
      "disabled": _vm.disabled,
      "aria-disabled": _vm.disabled,
      "no-background": "",
      "color": "white"
    }
  }, 'bb-button', _vm.$attrs, false), _vm.$listeners), [_vm.icon ? _c('span', {
    staticClass: "bb-card-button__icon"
  }, [_c('bb-icon', {
    attrs: {
      "name": _vm.icon,
      "size": "24",
      "fill": "white"
    }
  })], 1) : _vm._e(), _vm._v(" "), _vm.label ? _c('span', {
    staticClass: "bb-card-button__label"
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]) : _vm._e(), _vm._v(" "), _vm._t("default")], 2);
};
var __vue_staticRenderFns__$1u = [];

/* style */
const __vue_inject_styles__$1w = function (inject) {
  if (!inject) return;
  inject("data-v-da93aee8_0", {
    source: ".bb-card-button{position:relative;display:inline-flex;overflow:hidden;min-width:6.6875rem;max-width:6.6875rem;flex-direction:column;align-items:center;padding:1rem .5rem .5rem;border:none;border-radius:.625rem;background:#fff!important;box-shadow:0 .0625rem 1.0625rem 0 rgba(74,74,74,.06)!important;cursor:pointer;transition:box-shadow .2s ease-out}@media (min-width:768px){.bb-card-button{min-width:13.8125rem;max-width:13.8125rem}}.bb-card-button::-moz-focus-inner{border:0}.bb-card-button:focus,.bb-card-button:hover{box-shadow:0 .0625rem 1.0625rem 0 rgba(74,74,74,.06),0 .0625rem 1.5625rem 0 rgba(74,74,74,.1)!important}.bb-card-button:focus{outline:0}.bb-card-button--disabled{pointer-events:none}.bb-card-button--disabled:hover{box-shadow:0 .0625rem 1.0625rem 0 rgba(74,74,74,.06)}.bb-card-button__icon{display:flex;width:2.5rem;height:2.5rem;align-items:center;justify-content:center;border-radius:99em;margin-bottom:.5rem;background:#0ccca2;transition:background .2s ease-out}.bb-card-button:hover .bb-card-button__icon{background:#3dd6b5}.bb-card-button--disabled .bb-card-button__icon,.bb-card-button--disabled:hover .bb-card-button__icon{background:rgba(12,204,162,.5)}.bb-card-button__label{height:1.625rem;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;line-height:.8125rem;text-align:center}@media (min-width:768px){.bb-card-button__label{height:2.25rem;font-size:.875rem;line-height:1.125rem}}.bb-card-button--disabled .bb-card-button__label{color:rgba(104,125,146,.7)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1w = undefined;
/* module identifier */
const __vue_module_identifier__$1w = undefined;
/* functional template */
const __vue_is_functional_template__$1w = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1w = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1u,
  staticRenderFns: __vue_staticRenderFns__$1u
}, __vue_inject_styles__$1w, __vue_script__$1w, __vue_scope_id__$1w, __vue_is_functional_template__$1w, __vue_module_identifier__$1w, false, createInjector, undefined, undefined);

var script$1v = {
  name: 'bb-card-button-group',
  components: {
    BbCardButton: __vue_component__$1w
  },
  props: {
    buttons: {
      type: Array,
      required: true
    }
  },
  emits: ['click'],
  methods: {
    onClick(event) {
      this.$emit('click', event);
    }
  }
};

/* script */
const __vue_script__$1v = script$1v;

/* template */
var __vue_render__$1t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-card-button-group"
  }, _vm._l(_vm.buttons, function (button, index) {
    return _c('bb-card-button', _vm._b({
      key: index,
      on: {
        "click": function ($event) {
          return _vm.onClick(button.id);
        }
      }
    }, 'bb-card-button', button, false));
  }), 1);
};
var __vue_staticRenderFns__$1t = [];

/* style */
const __vue_inject_styles__$1v = function (inject) {
  if (!inject) return;
  inject("data-v-6b685bd4_0", {
    source: ".bb-card-button-group{display:flex;justify-content:center}.bb-card-button-group>:not(:last-child){margin-right:.625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1v = undefined;
/* module identifier */
const __vue_module_identifier__$1v = undefined;
/* functional template */
const __vue_is_functional_template__$1v = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1v = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1t,
  staticRenderFns: __vue_staticRenderFns__$1t
}, __vue_inject_styles__$1v, __vue_script__$1v, __vue_scope_id__$1v, __vue_is_functional_template__$1v, __vue_module_identifier__$1v, false, createInjector, undefined, undefined);

var script$1u = {
  name: 'bb-card-slider',
  mixins: [validator],
  props: {
    index: {
      type: [String, Number]
    },
    direction: {
      type: String,
      validator: d => ['left', 'right'].includes(d)
    }
  },
  data() {
    return {
      slideHeight: '150px',
      defaultAriaLabel: 'Carousel'
    };
  },
  computed: {
    classes() {
      return {
        'goes-left': this.direction === 'left'
      };
    }
  },
  watch: {
    index() {
      this.$nextTick(() => this.getSlideHeight());
    }
  },
  mounted() {
    this.getSlideHeight();
  },
  methods: {
    getSlideHeight() {
      setTimeout(() => {
        const slot = this.$slots.default.find(slot => !!slot.tag);
        const slotStyles = slot.elm.currentStyle || window.getComputedStyle(slot.elm);
        const slotHeight = slot.elm.offsetHeight;
        const slotMarginY = parseFloat(slotStyles.marginTop) + parseFloat(slotStyles.marginBottom);
        const cardBoxShadowCompensation = 10;
        const slotComputedHeight = slotHeight + slotMarginY + cardBoxShadowCompensation;
        this.slideHeight = `${slotComputedHeight}px`;
      }, 200);
    }
  }
};

/* script */
const __vue_script__$1u = script$1u;

/* template */
var __vue_render__$1s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-card-slider",
    class: _vm.classes,
    style: {
      height: _vm.slideHeight
    },
    attrs: {
      "aria-label": _vm.defaultAriaLabel,
      "role": "region",
      "aria-live": "polite",
      "aria-atomic": "true"
    }
  }, [_c('transition', {
    attrs: {
      "name": "slide"
    }
  }, [_vm._t("default")], 2)], 1);
};
var __vue_staticRenderFns__$1s = [];

/* style */
const __vue_inject_styles__$1u = function (inject) {
  if (!inject) return;
  inject("data-v-91dca090_0", {
    source: ".bb-card-slider{position:relative;display:flex;overflow:hidden;flex-wrap:wrap;transition:all .5s}.bb-card-slider>*{position:absolute;right:0;left:0}.slide-enter-active,.slide-leave-active{transition:transform .5s,opacity .2s}.slide-enter,.slide-leave-active{opacity:0}.slide-enter{transform:translateX(40vw)}.slide-leave-active{transform:translateX(-40vw)}.slide.goes-left .slide-enter{transform:translateX(-40vw)}.slide.goes-left .slide-leave-active{transform:translateX(40vw)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1u = undefined;
/* module identifier */
const __vue_module_identifier__$1u = undefined;
/* functional template */
const __vue_is_functional_template__$1u = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1u = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1s,
  staticRenderFns: __vue_staticRenderFns__$1s
}, __vue_inject_styles__$1u, __vue_script__$1u, __vue_scope_id__$1u, __vue_is_functional_template__$1u, __vue_module_identifier__$1u, false, createInjector, undefined, undefined);

var script$1t = {
  name: 'bb-checkbox-group',
  components: {
    BbCheckbox: __vue_component__$2s,
    BbIcon: __vue_component__$2C
  },
  mixins: [vuelidateValidator, validator],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: String,
    errorMessage: String,
    single: Boolean,
    checkboxProps: {
      type: Object,
      default: () => {}
    },
    layout: {
      type: String,
      validator: type => ['horizontal', 'vertical'].includes(type),
      default: 'vertical'
    },
    disableValidation: Boolean,
    useCustomValidation: Boolean
  },
  emits: ['input'],
  data() {
    return {
      val: JSON.parse(JSON.stringify(this.value) || []),
      uuid: uuid()
    };
  },
  computed: {
    classes() {
      var _this$checkboxProps, _this$checkboxProps2;
      return [`bb-checkbox-group--layout-${this.layout}`, {
        'bb-checkbox-group--error': this.hasError,
        [`bb-checkbox-group--size-${(_this$checkboxProps = this.checkboxProps) === null || _this$checkboxProps === void 0 ? void 0 : _this$checkboxProps.size}`]: (_this$checkboxProps2 = this.checkboxProps) === null || _this$checkboxProps2 === void 0 ? void 0 : _this$checkboxProps2.size
      }];
    },
    skipValidation() {
      return this.disableValidation || this.single && this.val.some(cb => cb.checked);
    },
    veeValidateCheckboxRules() {
      return this.skipValidation ? null : 'required';
    },
    vuelidateCheckboxRules() {
      return {
        required: requiredUnless(this.isCheckboxNotRequired)
      };
    },
    rules() {
      return {
        $each: {
          checked: this.vuelidateCheckboxRules
        }
      };
    },
    hasLabel() {
      return notEmpty(this.label);
    },
    hasCustomValidation() {
      return this.useCustomValidation;
    },
    labelId() {
      return `label-${this.uuid}`;
    },
    errorMessageId() {
      return `errorMessage-${this.uuid}`;
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.val = JSON.parse(JSON.stringify(newVal) || []);
      },
      deep: true
    },
    val: {
      handler(newVal) {
        this.$emit('input', newVal);
      },
      deep: true
    }
  },
  methods: {
    isCheckboxNotRequired(checkbox) {
      return this.skipValidation || checkbox.disabled;
    },
    validateWithVuelidate() {
      this.validate();
      this.$refs.checkboxes.forEach(checkbox => checkbox.forceValidate());
    },
    validateWithVeeValidate() {
      this.val.forEach(checkbox => this.$validator.validate(checkbox.name, checkbox.checked));
    }
  }
};

/* script */
const __vue_script__$1t = script$1t;

/* template */
var __vue_render__$1r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('fieldset', {
    ref: "root",
    staticClass: "bb-checkbox-group",
    class: _vm.classes,
    attrs: {
      "aria-labelledby": _vm.hasLabel ? _vm.labelId : undefined,
      "aria-describedby": _vm.hasError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasError ? 'true' : 'false',
      "aria-required": !_vm.skipValidation
    }
  }, [_vm.hasLabel ? _c('legend', {
    staticClass: "bb-checkbox-group__label",
    attrs: {
      "id": _vm.labelId,
      "data-testid": "bb-checkbox-group__label"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]) : _vm._e(), _vm._v(" "), _vm._l(_vm.val, function (checkbox, index) {
    return _c('bb-checkbox', _vm._b({
      directives: [{
        name: "validate",
        rawName: "v-validate",
        value: _vm.veeValidateCheckboxRules,
        expression: "veeValidateCheckboxRules"
      }],
      key: index,
      ref: "checkboxes",
      refInFor: true,
      attrs: {
        "rules": _vm.vuelidateCheckboxRules,
        "data-testid": "bb-checkbox-group__checkbox"
      },
      on: {
        "change": _vm.validateWithVuelidate,
        "validate": _vm.validateWithVeeValidate
      },
      model: {
        value: checkbox.checked,
        callback: function ($$v) {
          _vm.$set(checkbox, "checked", $$v);
        },
        expression: "checkbox.checked"
      }
    }, 'bb-checkbox', Object.assign({}, _vm.checkboxProps, checkbox), false));
  }), _vm._v(" "), _vm.hasError ? _c('p', {
    staticClass: "error-message error-message--pushed",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_c('bb-icon', {
    staticClass: "bb-checkbox__error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  }), _vm._v("\n    " + _vm._s(_vm.errorMessage) + "\n  ")], 1) : _vm._e()], 2);
};
var __vue_staticRenderFns__$1r = [];

/* style */
const __vue_inject_styles__$1t = function (inject) {
  if (!inject) return;
  inject("data-v-68954c66_0", {
    source: ".bb-checkbox-group .bb-checkbox .error-message{display:none}.bb-checkbox-group.bb-checkbox-group--layout-horizontal{display:flex;flex-wrap:wrap}.bb-checkbox-group.bb-checkbox-group--layout-horizontal>:not(:last-child){margin-right:.625rem}.bb-checkbox-group .error-message{width:100%}.bb-checkbox-group__label{width:100%;margin-bottom:-.3125rem;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;line-height:1.3125rem}.bb-checkbox-group--size-small .bb-checkbox-group__label,.bb-checkbox-group--size-tiniest .bb-checkbox-group__label,.bb-checkbox-group--size-tiny .bb-checkbox-group__label{margin-bottom:0}.bb-checkbox-group--size-tiniest .bb-checkbox-group__label,.bb-checkbox-group--size-tiny .bb-checkbox-group__label{font-size:11px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1t = undefined;
/* module identifier */
const __vue_module_identifier__$1t = undefined;
/* functional template */
const __vue_is_functional_template__$1t = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1t = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1r,
  staticRenderFns: __vue_staticRenderFns__$1r
}, __vue_inject_styles__$1t, __vue_script__$1t, __vue_scope_id__$1t, __vue_is_functional_template__$1t, __vue_module_identifier__$1t, false, createInjector, undefined, undefined);

var Icon$A = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 352 222"}},[_c('mask',{staticStyle:{"mask-type":"alpha"},attrs:{"id":"mask0_2557_124893","width":"352","height":"222","x":"0","y":"0","maskUnits":"userSpaceOnUse"}},[_c('path',{attrs:{"fill":"#fff","d":"M336.524 0L15.4758 0C6.92873 0 0 6.92873 0 15.4758L0 153.229L0 183.905L0 206.524C0 215.071 6.92874 222 15.4758 222H40.6496L66.3399 222L336.524 222C345.071 222 352 215.071 352 206.524L352 15.4758C352 6.92873 345.071 0 336.524 0Z"}})]),_c('g',{attrs:{"stroke-width":"2","mask":"url(#mask0_2557_124893)"}},[_c('path',{attrs:{"stroke":"#0CCCA2","d":"M156.991 111.41C156.991 183.246 98.7974 241.48 27.0139 241.48C-44.7696 241.48 -102.963 183.246 -102.963 111.41C-102.963 39.5735 -44.7696 -18.6602 27.0139 -18.6602C98.7974 -18.6602 156.991 39.5735 156.991 111.41Z"}}),_c('path',{attrs:{"stroke":"url(#paint0_linear_2557_124893)","d":"M484.433 80.2803C484.433 163.427 417.077 230.83 333.991 230.83C250.905 230.83 183.549 163.427 183.549 80.2803C183.549 -2.86675 250.905 -70.2695 333.991 -70.2695C417.077 -70.2695 484.433 -2.86675 484.433 80.2803Z"}}),_c('path',{attrs:{"stroke":"url(#paint1_linear_2557_124893)","d":"M365.734 251.9C365.734 333.916 299.295 400.402 217.339 400.402C135.383 400.402 68.9434 333.916 68.9434 251.9C68.9434 169.884 135.383 103.398 217.339 103.398C299.295 103.398 365.734 169.884 365.734 251.9Z"}}),_c('path',{attrs:{"stroke":"#0BB48F","d":"M105.419 93.388C105.419 160.7 50.8911 215.266 -16.3714 215.266C-83.6339 215.266 -138.162 160.7 -138.162 93.388C-138.162 26.0758 -83.6339 -28.4902 -16.3714 -28.4902C50.8911 -28.4902 105.419 26.0758 105.419 93.388Z"}})]),_c('defs',[_c('linearGradient',{attrs:{"id":"paint0_linear_2557_124893","x1":"210.566","x2":"374.255","y1":"-8.376","y2":"178.293","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#003E8C"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#0BB48F"}})],1),_c('linearGradient',{attrs:{"id":"paint1_linear_2557_124893","x1":"312.098","x2":"104.652","y1":"172.024","y2":"177.569","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#0CCCA2"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#00768D"}})],1)],1)]) } };

Vue.component('icon-cc-bg-lines', Icon$A);

var Icon$z = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 352 222"}},[_c('path',{attrs:{"d":"M339 0.5L15 0.5C6.99187 0.5 0.5 6.99187 0.5 15L0.5 154.229L0.5 184.905L0.5 209C0.5 217.008 6.99188 223.5 15 223.5H41.6496L67.3399 223.5L339 223.5C347.008 223.5 353.5 217.008 353.5 209L353.5 15C353.5 6.99187 347.008 0.5 339 0.5Z"}}),_c('mask',{staticStyle:{"mask-type":"alpha"},attrs:{"id":"bg_disabled_mask0_2481_123288","width":"352","height":"222","x":"0","y":"0","maskUnits":"userSpaceOnUse"}},[_c('path',{attrs:{"fill":"#fff","d":"M336.524 0L15.4758 0C6.92873 0 0 6.92873 0 15.4758L0 153.229L0 183.905L0 206.524C0 215.071 6.92874 222 15.4758 222H40.6496L66.3399 222L336.524 222C345.071 222 352 215.071 352 206.524L352 15.4758C352 6.92873 345.071 0 336.524 0Z"}})]),_c('g',{attrs:{"stroke":"#89A","stroke-width":"2","mask":"url(#bg_disabled_mask0_2481_123288)"}},[_c('path',{attrs:{"d":"M156.991 111.41C156.991 183.246 98.7973 241.48 27.0139 241.48-44.7696 241.48-102.963 183.246-102.963 111.41-102.963 39.5735-44.7696-18.6602 27.0139-18.6602 98.7973-18.6602 156.991 39.5735 156.991 111.41ZM484.433 80.2803C484.433 163.427 417.077 230.83 333.991 230.83 250.905 230.83 183.549 163.427 183.549 80.2803 183.549-2.86675 250.905-70.2695 333.991-70.2695 417.077-70.2695 484.433-2.86675 484.433 80.2803Z"}}),_c('path',{attrs:{"d":"M365.736 251.9C365.736 333.916 299.296 400.402 217.341 400.402 135.385 400.402 68.9453 333.916 68.9453 251.9 68.9453 169.884 135.385 103.398 217.341 103.398 299.296 103.398 365.736 169.884 365.736 251.9ZM105.419 93.388C105.419 160.7 50.8911 215.266-16.3714 215.266-83.6339 215.266-138.162 160.7-138.162 93.388-138.162 26.0758-83.6339-28.4902-16.3714-28.4902 50.8911-28.4902 105.419 26.0758 105.419 93.388Z"}})])]) } };

Vue.component('icon-cc-bg-lines-disabled', Icon$z);

var Icon$y = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 352 222"}},[_c('path',{attrs:{"d":"M339 0.5L15 0.5C6.99187 0.5 0.5 6.99187 0.5 15L0.5 154.229L0.5 184.905L0.5 209C0.5 217.008 6.99188 223.5 15 223.5H41.6496L67.3399 223.5L339 223.5C347.008 223.5 353.5 217.008 353.5 209L353.5 15C353.5 6.99187 347.008 0.5 339 0.5Z"}}),_c('mask',{staticStyle:{"mask-type":"alpha"},attrs:{"id":"bg_disabled_mask0_2481_123288","width":"352","height":"222","x":"0","y":"0","maskUnits":"userSpaceOnUse"}},[_c('path',{attrs:{"fill":"#fff","d":"M336.524 0L15.4758 0C6.92873 0 0 6.92873 0 15.4758L0 153.229L0 183.905L0 206.524C0 215.071 6.92874 222 15.4758 222H40.6496L66.3399 222L336.524 222C345.071 222 352 215.071 352 206.524L352 15.4758C352 6.92873 345.071 0 336.524 0Z"}})]),_c('g',{attrs:{"stroke":"#38414D","stroke-width":"2","mask":"url(#bg_disabled_mask0_2481_123288)"}},[_c('path',{attrs:{"d":"M156.991 111.41C156.991 183.246 98.7973 241.48 27.0139 241.48-44.7696 241.48-102.963 183.246-102.963 111.41-102.963 39.5735-44.7696-18.6602 27.0139-18.6602 98.7973-18.6602 156.991 39.5735 156.991 111.41ZM484.433 80.2803C484.433 163.427 417.077 230.83 333.991 230.83 250.905 230.83 183.549 163.427 183.549 80.2803 183.549-2.86675 250.905-70.2695 333.991-70.2695 417.077-70.2695 484.433-2.86675 484.433 80.2803Z"}}),_c('path',{attrs:{"d":"M365.736 251.9C365.736 333.916 299.296 400.402 217.341 400.402 135.385 400.402 68.9453 333.916 68.9453 251.9 68.9453 169.884 135.385 103.398 217.341 103.398 299.296 103.398 365.736 169.884 365.736 251.9ZM105.419 93.388C105.419 160.7 50.8911 215.266-16.3714 215.266-83.6339 215.266-138.162 160.7-138.162 93.388-138.162 26.0758-83.6339-28.4902-16.3714-28.4902 50.8911-28.4902 105.419 26.0758 105.419 93.388Z"}})])]) } };

Vue.component('icon-cc-bg-lines-virtual', Icon$y);

var Icon$x = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 352 222"}},[_c('defs',[_c('linearGradient',{attrs:{"id":"chip0_linear_2481_123288","x1":"58.587","x2":"75.787","y1":"115.592","y2":"155.04","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#C8C5C6"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#BBB8BA"}})],1),_c('linearGradient',{attrs:{"id":"chip1_linear_2481_123288","x1":"24.781","x2":"33.483","y1":"103.678","y2":"125.438","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip2_linear_2481_123288","x1":"24.781","x2":"33.483","y1":"92.989","y2":"114.749","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip3_linear_2481_123288","x1":"24.781","x2":"33.483","y1":"82.301","y2":"104.06","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip4_linear_2481_123288","x1":"47.029","x2":"55.731","y1":"103.678","y2":"125.438","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip5_linear_2481_123288","x1":"47.029","x2":"55.731","y1":"92.989","y2":"114.749","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip6_linear_2481_123288","x1":"47.029","x2":"55.731","y1":"82.301","y2":"104.06","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1)],1),_c('mask',{staticStyle:{"mask-type":"alpha"},attrs:{"id":"chip_mask0_2481_123288","width":"352","height":"222","x":"0","y":"0","maskUnits":"userSpaceOnUse"}},[_c('path',{attrs:{"fill":"#fff","d":"M336.524 0L15.4758 0C6.92873 0 0 6.92873 0 15.4758L0 153.229L0 183.905L0 206.524C0 215.071 6.92874 222 15.4758 222H40.6496L66.3399 222L336.524 222C345.071 222 352 215.071 352 206.524L352 15.4758C352 6.92873 345.071 0 336.524 0Z"}})]),_c('g',{attrs:{"mask":"url(#chip_mask0_2481_123288)"}},[_c('path',{attrs:{"fill":"#38414D","d":"M78.2347 84.429C78.1411 84.3051 78.0734 84.1645 78.0354 84.0151C77.9974 83.8657 77.9899 83.7104 78.0133 83.5582C78.0607 83.2507 78.231 82.9742 78.487 82.7895C78.743 82.6048 79.0635 82.527 79.3782 82.5732C79.6929 82.6194 79.9759 82.7859 80.165 83.036C80.6083 83.6173 80.9548 84.2635 81.1917 84.9506C81.4379 85.6635 81.5628 86.411 81.5613 87.1633C81.5621 87.9137 81.4373 88.6592 81.1917 89.3703C80.9548 90.0574 80.6083 90.7035 80.165 91.2849C79.9759 91.535 79.6929 91.7015 79.3782 91.7477C79.0635 91.7939 78.743 91.7161 78.487 91.5314C78.231 91.3467 78.0607 91.0702 78.0133 90.7627C77.966 90.4552 78.0457 90.142 78.2347 89.8919C78.5277 89.511 78.7573 89.0873 78.9153 88.6365C79.0832 88.1626 79.1704 87.6649 79.1734 87.1633C79.1754 86.6646 79.0942 86.1689 78.9329 85.6958C78.7707 85.2399 78.535 84.8122 78.2347 84.429ZM82.3416 82.2449C83.2755 83.7228 83.7673 85.4265 83.7614 87.1633C83.7694 88.897 83.2817 90.5985 82.3533 92.076C82.2705 92.2069 82.2149 92.3526 82.1897 92.5046C82.1645 92.6565 82.1701 92.8119 82.2064 92.9617C82.2426 93.1116 82.3087 93.253 82.4008 93.3779C82.493 93.5028 82.6094 93.6087 82.7435 93.6896C82.8776 93.7705 83.0266 93.8249 83.1822 93.8495C83.3377 93.8742 83.4967 93.8686 83.6501 93.8332C83.8034 93.7978 83.9482 93.7333 84.076 93.6432C84.2038 93.5532 84.3122 93.4394 84.395 93.3084C85.5584 91.4612 86.1681 89.3322 86.1551 87.1633C86.1689 84.9925 85.5591 82.8615 84.395 81.0125C84.3122 80.8815 84.2038 80.7677 84.076 80.6777C83.9482 80.5876 83.8034 80.5231 83.6501 80.4877C83.4967 80.4523 83.3377 80.4467 83.1822 80.4714C83.0266 80.496 82.8776 80.5503 82.7435 80.6313C82.6094 80.7122 82.493 80.8181 82.4008 80.943C82.3087 81.0679 82.2426 81.2093 82.2064 81.3591C82.1701 81.509 82.1645 81.6643 82.1897 81.8163C82.2149 81.9683 82.2705 82.1139 82.3533 82.2449H82.3416ZM86.7125 80.038C88.0089 82.2072 88.6913 84.6743 88.6897 87.1862C88.6905 89.6963 88.0081 92.1614 86.7125 94.3288C86.6328 94.4616 86.5806 94.6086 86.559 94.7611C86.5373 94.9137 86.5467 95.0689 86.5864 95.218C86.6262 95.367 86.6956 95.507 86.7907 95.6298C86.8859 95.7526 87.0048 95.8559 87.1408 95.9338C87.2768 96.0118 87.4271 96.0627 87.5833 96.0839C87.7394 96.105 87.8983 96.0959 88.0509 96.057C88.2034 96.0182 88.3466 95.9503 88.4724 95.8574C88.5981 95.7645 88.7038 95.6482 88.7836 95.5154C90.2982 92.9915 91.0943 90.1177 91.0893 87.192C91.0993 84.2555 90.3052 81.3698 88.7894 78.8342C88.626 78.5651 88.3599 78.3704 88.0497 78.293C87.7394 78.2156 87.4103 78.2618 87.1349 78.4214C86.8595 78.5811 86.6603 78.8411 86.5811 79.1442C86.5018 79.4474 86.5491 79.7689 86.7125 80.038ZM91.0658 77.8023C92.7333 80.661 93.607 83.8959 93.6003 87.1862C93.6062 90.4747 92.7325 93.7075 91.0658 96.5644C90.9888 96.698 90.9394 96.8452 90.9206 96.9974C90.9017 97.1497 90.9137 97.3041 90.956 97.4518C90.9982 97.5995 91.0698 97.7376 91.1666 97.8583C91.2635 97.979 91.3837 98.0799 91.5205 98.1551C91.6573 98.2304 91.8079 98.2786 91.9637 98.2971C92.1195 98.3155 92.2775 98.3037 92.4287 98.2625C92.5799 98.2212 92.7213 98.1513 92.8448 98.0566C92.9683 97.962 93.0715 97.8445 93.1486 97.7109C95.021 94.499 96.0038 90.8655 95.9999 87.169C96.0086 83.4629 95.0257 79.819 93.1486 76.5985C92.993 76.3287 92.734 76.1303 92.4287 76.047C92.1234 75.9636 91.7967 76.0022 91.5205 76.1543C91.2443 76.3063 91.0412 76.5593 90.956 76.8576C90.8707 77.156 90.9102 77.4752 91.0658 77.745"}})]),_c('rect',{attrs:{"width":"44","height":"37","x":"68","y":"106","fill":"url(#chip0_linear_2481_123288)","rx":"6.464","transform":"rotate(-180 68 106)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"42.759","y":"102.206","stroke":"url(#chip1_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 42.7587 102.206)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"42.759","y":"91.517","stroke":"url(#chip2_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 42.7587 91.5167)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"42.759","y":"80.828","stroke":"url(#chip3_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 42.7587 80.828)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"65.007","y":"102.206","stroke":"url(#chip4_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 65.0067 102.206)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"65.007","y":"91.517","stroke":"url(#chip5_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 65.0067 91.5167)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"65.007","y":"80.828","stroke":"url(#chip6_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 65.0067 80.828)"}})]) } };

Vue.component('icon-cc-chip', Icon$x);

var Icon$w = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 352 222"}},[_c('defs',[_c('linearGradient',{attrs:{"id":"chip0_linear_2481_123288","x1":"58.587","x2":"75.787","y1":"115.592","y2":"155.04","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#C8C5C6"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#BBB8BA"}})],1),_c('linearGradient',{attrs:{"id":"chip1_linear_2481_123288","x1":"24.781","x2":"33.483","y1":"103.678","y2":"125.438","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip2_linear_2481_123288","x1":"24.781","x2":"33.483","y1":"92.989","y2":"114.749","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip3_linear_2481_123288","x1":"24.781","x2":"33.483","y1":"82.301","y2":"104.06","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip4_linear_2481_123288","x1":"47.029","x2":"55.731","y1":"103.678","y2":"125.438","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip5_linear_2481_123288","x1":"47.029","x2":"55.731","y1":"92.989","y2":"114.749","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1),_c('linearGradient',{attrs:{"id":"chip6_linear_2481_123288","x1":"47.029","x2":"55.731","y1":"82.301","y2":"104.06","gradientUnits":"userSpaceOnUse"}},[_c('stop',{attrs:{"stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".177","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":".386","stop-color":"#848484"}}),_c('stop',{attrs:{"offset":".756","stop-color":"#979797"}}),_c('stop',{attrs:{"offset":".902","stop-color":"#fff"}}),_c('stop',{attrs:{"offset":"1","stop-color":"#979797"}})],1)],1),_c('mask',{staticStyle:{"mask-type":"alpha"},attrs:{"id":"chip_mask0_2481_123288","width":"352","height":"222","x":"0","y":"0","maskUnits":"userSpaceOnUse"}},[_c('path',{attrs:{"fill":"#fff","d":"M336.524 0L15.4758 0C6.92873 0 0 6.92873 0 15.4758L0 153.229L0 183.905L0 206.524C0 215.071 6.92874 222 15.4758 222H40.6496L66.3399 222L336.524 222C345.071 222 352 215.071 352 206.524L352 15.4758C352 6.92873 345.071 0 336.524 0Z"}})]),_c('g',{attrs:{"mask":"url(#chip_mask0_2481_123288)"}},[_c('path',{attrs:{"fill":"#89A","d":"M78.2347 84.429C78.1411 84.3051 78.0734 84.1645 78.0354 84.0151C77.9974 83.8657 77.9899 83.7104 78.0133 83.5582C78.0607 83.2507 78.231 82.9742 78.487 82.7895C78.743 82.6048 79.0635 82.527 79.3782 82.5732C79.6929 82.6194 79.9759 82.7859 80.165 83.036C80.6083 83.6173 80.9548 84.2635 81.1917 84.9506C81.4379 85.6635 81.5628 86.411 81.5613 87.1633C81.5621 87.9137 81.4373 88.6592 81.1917 89.3703C80.9548 90.0574 80.6083 90.7035 80.165 91.2849C79.9759 91.535 79.6929 91.7015 79.3782 91.7477C79.0635 91.7939 78.743 91.7161 78.487 91.5314C78.231 91.3467 78.0607 91.0702 78.0133 90.7627C77.966 90.4552 78.0457 90.142 78.2347 89.8919C78.5277 89.511 78.7573 89.0873 78.9153 88.6365C79.0832 88.1626 79.1704 87.6649 79.1734 87.1633C79.1754 86.6646 79.0942 86.1689 78.9329 85.6958C78.7707 85.2399 78.535 84.8122 78.2347 84.429ZM82.3416 82.2449C83.2755 83.7228 83.7673 85.4265 83.7614 87.1633C83.7694 88.897 83.2817 90.5985 82.3533 92.076C82.2705 92.2069 82.2149 92.3526 82.1897 92.5046C82.1645 92.6565 82.1701 92.8119 82.2064 92.9617C82.2426 93.1116 82.3087 93.253 82.4008 93.3779C82.493 93.5028 82.6094 93.6087 82.7435 93.6896C82.8776 93.7705 83.0266 93.8249 83.1822 93.8495C83.3377 93.8742 83.4967 93.8686 83.6501 93.8332C83.8034 93.7978 83.9482 93.7333 84.076 93.6432C84.2038 93.5532 84.3122 93.4394 84.395 93.3084C85.5584 91.4612 86.1681 89.3322 86.1551 87.1633C86.1689 84.9925 85.5591 82.8615 84.395 81.0125C84.3122 80.8815 84.2038 80.7677 84.076 80.6777C83.9482 80.5876 83.8034 80.5231 83.6501 80.4877C83.4967 80.4523 83.3377 80.4467 83.1822 80.4714C83.0266 80.496 82.8776 80.5503 82.7435 80.6313C82.6094 80.7122 82.493 80.8181 82.4008 80.943C82.3087 81.0679 82.2426 81.2093 82.2064 81.3591C82.1701 81.509 82.1645 81.6643 82.1897 81.8163C82.2149 81.9683 82.2705 82.1139 82.3533 82.2449H82.3416ZM86.7125 80.038C88.0089 82.2072 88.6913 84.6743 88.6897 87.1862C88.6905 89.6963 88.0081 92.1614 86.7125 94.3288C86.6328 94.4616 86.5806 94.6086 86.559 94.7611C86.5373 94.9137 86.5467 95.0689 86.5864 95.218C86.6262 95.367 86.6956 95.507 86.7907 95.6298C86.8859 95.7526 87.0048 95.8559 87.1408 95.9338C87.2768 96.0118 87.4271 96.0627 87.5833 96.0839C87.7394 96.105 87.8983 96.0959 88.0509 96.057C88.2034 96.0182 88.3466 95.9503 88.4724 95.8574C88.5981 95.7645 88.7038 95.6482 88.7836 95.5154C90.2982 92.9915 91.0943 90.1177 91.0893 87.192C91.0993 84.2555 90.3052 81.3698 88.7894 78.8342C88.626 78.5651 88.3599 78.3704 88.0497 78.293C87.7394 78.2156 87.4103 78.2618 87.1349 78.4214C86.8595 78.5811 86.6603 78.8411 86.5811 79.1442C86.5018 79.4474 86.5491 79.7689 86.7125 80.038ZM91.0658 77.8023C92.7333 80.661 93.607 83.8959 93.6003 87.1862C93.6062 90.4747 92.7325 93.7075 91.0658 96.5644C90.9888 96.698 90.9394 96.8452 90.9206 96.9974C90.9017 97.1497 90.9137 97.3041 90.956 97.4518C90.9982 97.5995 91.0698 97.7376 91.1666 97.8583C91.2635 97.979 91.3837 98.0799 91.5205 98.1551C91.6573 98.2304 91.8079 98.2786 91.9637 98.2971C92.1195 98.3155 92.2775 98.3037 92.4287 98.2625C92.5799 98.2212 92.7213 98.1513 92.8448 98.0566C92.9683 97.962 93.0715 97.8445 93.1486 97.7109C95.021 94.499 96.0038 90.8655 95.9999 87.169C96.0086 83.4629 95.0257 79.819 93.1486 76.5985C92.993 76.3287 92.734 76.1303 92.4287 76.047C92.1234 75.9636 91.7967 76.0022 91.5205 76.1543C91.2443 76.3063 91.0412 76.5593 90.956 76.8576C90.8707 77.156 90.9102 77.4752 91.0658 77.745"}})]),_c('rect',{attrs:{"width":"44","height":"37","x":"68","y":"106","fill":"url(#chip0_linear_2481_123288)","rx":"6.464","transform":"rotate(-180 68 106)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"42.759","y":"102.206","stroke":"url(#chip1_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 42.7587 102.206)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"42.759","y":"91.517","stroke":"url(#chip2_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 42.7587 91.5167)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"42.759","y":"80.828","stroke":"url(#chip3_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 42.7587 80.828)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"65.007","y":"102.206","stroke":"url(#chip4_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 65.0067 102.206)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"65.007","y":"91.517","stroke":"url(#chip5_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 65.0067 91.5167)"}}),_c('rect',{attrs:{"width":"15.761","height":"8.034","x":"65.007","y":"80.828","stroke":"url(#chip6_linear_2481_123288)","stroke-width":"1.01","rx":"1.495","transform":"rotate(-180 65.0067 80.828)"}})]) } };

Vue.component('icon-cc-chip-disabled', Icon$w);

var Icon$v = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 362 105"}},[_c('mask',{staticStyle:{"mask-type":"alpha"},attrs:{"id":"mask0_2565_124553","width":"76","height":"101","x":"4","y":"3","maskUnits":"userSpaceOnUse"}},[_c('path',{attrs:{"fill":"#66CCC6","d":"M79.7599 73.9C79.7599 78.59 78.9099 82.75 77.2199 86.39C75.5199 90.03 73.2299 93.12 70.3399 95.66C67.4499 98.2 64.1399 100.12 60.3999 101.42C56.6599 102.71 52.7499 103.36 48.6599 103.36H8.12994C6.73994 103.36 5.80994 103.06 5.35994 102.46C4.90994 101.86 4.68994 101.06 4.68994 100.07V7.36004C4.68994 6.36004 4.90994 5.57004 5.35994 4.97004C5.80994 4.37004 6.67994 4.07004 7.97994 4.07004L45.9599 3.92004C56.2299 3.92004 63.9799 6.39004 69.2099 11.32C74.4499 16.25 77.0599 23.01 77.0599 31.58C77.0599 32.77 76.9099 34.22 76.6099 35.91C76.3099 37.61 75.7899 39.35 75.0399 41.15C74.2899 42.94 73.1899 44.69 71.7499 46.38C70.2999 48.08 68.4899 49.47 66.2899 50.57L66.4399 51.17C69.3299 52.27 71.6499 53.76 73.3899 55.65C75.1299 57.55 76.4799 59.59 77.4299 61.78C78.3799 63.97 78.9999 66.14 79.2999 68.29C79.6099 70.44 79.7599 72.31 79.7599 73.9V73.9Z"}})]),_c('g',{attrs:{"mask":"url(#mask0_2565_124553)"}},[_c('path',{attrs:{"fill":"#89A","d":"M79.7599 73.9C79.7599 78.59 78.9099 82.75 77.2199 86.39C75.5199 90.03 73.2299 93.12 70.3399 95.66C67.4499 98.2 64.1399 100.12 60.3999 101.42C56.6599 102.71 52.7499 103.36 48.6599 103.36H8.12994C6.73994 103.36 5.80994 103.06 5.35994 102.46C4.90994 101.86 4.68994 101.06 4.68994 100.07V7.36004C4.68994 6.36004 4.90994 5.57004 5.35994 4.97004C5.80994 4.37004 6.67994 4.07004 7.97994 4.07004L45.9599 3.92004C56.2299 3.92004 63.9799 6.39004 69.2099 11.32C74.4499 16.25 77.0599 23.01 77.0599 31.58C77.0599 32.77 76.9099 34.22 76.6099 35.91C76.3099 37.61 75.7899 39.35 75.0399 41.15C74.2899 42.94 73.1899 44.69 71.7499 46.38C70.2999 48.08 68.4899 49.47 66.2899 50.57L66.4399 51.17C69.3299 52.27 71.6499 53.76 73.3899 55.65C75.1299 57.55 76.4799 59.59 77.4299 61.78C78.3799 63.97 78.9999 66.14 79.2999 68.29C79.6099 70.44 79.7599 72.31 79.7599 73.9V73.9Z"}}),_c('path',{attrs:{"fill":"#BDC7D0","d":"M162.69 61.66C162.69 102.14 129.87 134.96 89.4001 134.96C48.9201 134.96 16.1001 102.14 16.1001 61.66C16.1001 21.18 48.9201 -11.63 89.4001 -11.63C129.87 -11.63 162.69 21.18 162.69 61.66V61.66Z"}}),_c('path',{attrs:{"fill":"#9DABB9","d":"M64.5702 10.8599C64.5702 51.3399 31.7502 84.1599 -8.72978 84.1599C-49.2098 84.1599 -82.0298 51.3399 -82.0298 10.8599C-82.0298 -29.6201 -49.2098 -62.4301 -8.72978 -62.4301C31.7602 -62.4301 64.5702 -29.6201 64.5702 10.8599Z","opacity":".6"}})]),_c('path',{attrs:{"fill":"#89A","d":"M135.13 53.65V51.48C140.62 50.5 145 47.85 145 40.88C145 32.85 140.73 28.96 130.2 28.96H107.37C106.78 28.96 106.26 29.44 106.26 30.03V76.54C106.26 77.13 106.77 77.63 107.37 77.63H129.28C140.46 77.63 146.64 73.73 146.64 64.92C146.65 57.16 142.13 54.31 135.13 53.65V53.65ZM115.75 35.75H128.43C133.3 35.75 135.54 37.73 135.54 42.33C135.54 47.33 132.98 49.11 127.98 49.31L115.75 49.84V35.75V35.75ZM128.11 70.85H115.76V55.83L128.12 56.22C135.16 56.42 136.93 58.54 136.93 63.93C136.92 67.95 134.75 70.85 128.11 70.85ZM159.16 28.1H153.63C152.58 28.1 152.04 28.58 152.04 29.64V37.08C152.04 38.13 152.58 38.55 153.63 38.55H159.16C160.21 38.55 160.77 38.13 160.77 37.08V29.64C160.76 28.59 160.21 28.1 159.16 28.1V28.1ZM159.75 43.61H153.04C152.45 43.61 151.94 44.05 151.94 44.64V76.61C151.94 77.2 152.45 77.63 153.04 77.63H159.75C160.34 77.63 160.76 77.2 160.76 76.61V44.64C160.76 44.05 160.34 43.61 159.75 43.61ZM221.66 42.21C218.32 42.21 214.62 43.09 212.95 43.54L212.84 29.18C212.84 28.52 212.38 28.11 211.79 28.11H211.53L205.08 29.24C204.29 29.37 203.96 29.85 203.96 30.63V71.35C203.96 74.18 204.16 75.76 207.72 76.88C210.15 77.73 214.63 78.46 219.63 78.46C232.85 78.46 235.82 72.54 235.82 59.91C235.8 47.01 231.99 42.21 221.66 42.21V42.21ZM219.03 72.34C215.48 72.34 212.78 71.68 212.78 71.68V49.44C212.78 49.44 216.79 48.39 219.82 48.39C225.48 48.39 226.86 50.36 226.86 60.1C226.86 70.1 224.95 72.34 219.03 72.34V72.34ZM267.37 44.05C265.26 42.87 262.12 42.21 257.45 42.21C250.74 42.21 244.67 43.86 243.09 44.45C242.76 44.58 242.28 44.85 242.28 45.44V45.71L243.22 49.79C243.29 50.25 243.63 50.38 243.96 50.38C244.09 50.38 244.23 50.32 244.43 50.32C246.41 49.79 251.28 48.48 255.75 48.48C258.12 48.48 259.72 48.68 260.77 49.14C262.42 49.86 262.63 51.18 262.63 53.02V55.13L250.9 57.04C245.64 57.89 240.3 58.82 240.3 67.3C240.3 77.76 249.04 78.48 256.15 78.48C259.83 78.48 262.96 78.09 265.26 77.56C269.74 76.51 271.35 75.65 271.35 70.72V52.54C271.36 48.98 270.73 45.89 267.37 44.05ZM262.63 71.81C261.23 72.27 259.12 72.73 256.56 72.73C253.4 72.73 249.25 72.34 249.25 67.2C249.25 62.92 251.76 62.53 254.46 62.13L262.63 60.81V71.81ZM294.65 42.21C290.44 42.21 284.92 43 281.56 43.92C277.88 44.91 276.63 45.76 276.63 48.26V76.61C276.63 77.2 277.07 77.63 277.72 77.63H284.43C285.02 77.63 285.46 77.2 285.46 76.61V49.38C285.46 49.38 289.73 48.26 293.67 48.26C297.16 48.26 298.59 49.57 298.59 53.39V76.61C298.59 77.2 299.03 77.63 299.62 77.63H306.4C306.99 77.63 307.42 77.2 307.42 76.61V52.21C307.43 44.31 302.88 42.21 294.65 42.21V42.21ZM344.17 76.68L329.4 59.84L343.45 44.18C343.52 44.05 343.58 43.98 343.58 43.85C343.58 43.59 343.38 43.26 342.92 43.26L334.9 43.06C334.57 43.06 334.31 43.12 334.04 43.52L322.03 57.4H321.43V29.25C321.43 28.52 321.01 28.11 320.42 28.11H320.15L313.71 29.24C312.92 29.44 312.59 29.85 312.59 30.63V76.62C312.59 77.21 313.04 77.64 313.64 77.64H320.42C321.07 77.64 321.43 77.21 321.43 76.62V63.32H322.03L333.84 77.22C334.17 77.62 334.5 77.64 334.83 77.64H343.64C344.03 77.64 344.3 77.29 344.3 77.03C344.3 76.9 344.24 76.74 344.17 76.68V76.68ZM195.58 43.48C193.81 42.9 191.68 42.45 189.18 42.11C186.68 41.77 184.29 41.61 181.99 41.61C178.91 41.61 176.36 42.04 174.32 42.9C172.28 43.76 170.66 45 169.46 46.61C168.26 48.22 167.42 50.16 166.94 52.44C166.46 54.72 166.22 57.27 166.22 60.11C166.22 62.8 166.43 65.23 166.83 67.42C167.24 69.6 167.98 71.48 169.06 73.04C170.14 74.6 171.61 75.81 173.49 76.68C175.36 77.54 177.76 77.97 180.69 77.97C181.84 77.97 183.15 77.85 184.61 77.61C185.9 77.4 187.33 77.01 188.89 76.47C188.93 76.45 188.96 76.44 189 76.42L189.13 79.74C189.13 80.84 189.23 81.8 189.01 82.62C188.79 83.43 188.5 84.09 187.93 84.6C187.36 85.1 186.63 85.48 185.64 85.72C184.66 85.96 183.42 86.08 181.89 86.08C180.5 86.08 178.91 86.02 177.12 85.9C175.32 85.78 173.61 85.6 171.98 85.36C171.31 85.26 170.82 85.26 170.51 85.36C170.2 85.46 169.99 85.79 169.9 86.37L169.32 89.03C169.23 89.41 169.25 89.79 169.39 90.15C169.53 90.51 169.99 90.83 170.76 91.12C172.25 91.74 174.08 92.18 176.27 92.42C178.45 92.66 180.48 92.78 182.35 92.78C185.81 92.78 188.53 92.42 190.52 91.7C192.51 90.98 194.04 89.97 195.09 88.67C196.14 87.38 196.79 85.84 197.08 84.06C197.37 82.28 197.49 80.36 197.49 78.3V46.71C197.49 46.04 197.41 45.4 197.19 44.8C196.97 44.21 196.44 43.76 195.58 43.48V43.48ZM189.09 69.82C188.34 70.25 186.31 70.57 184.89 70.76C183.47 70.95 182.35 71.05 181.3 71.05C180.05 71.05 179.04 70.86 178.16 70.48C177.27 70.1 176.58 69.46 176.03 68.57C175.48 67.68 175.08 66.49 174.82 65.01C174.56 63.52 174.43 61.67 174.43 59.47C174.43 57.12 174.64 55.22 175.05 53.78C175.46 52.34 176.02 51.25 176.74 50.5C177.46 49.76 178.3 49.27 179.26 49.02C180.22 48.78 181.25 48.66 182.36 48.66C183.51 48.66 184.49 48.72 185.77 48.84C187.04 48.96 188.35 49.12 189.1 49.31V69.82H189.09Z"}})]) } };

Vue.component('icon-logo-bigbank-grayscale', Icon$v);

var Icon$u = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","fill":"none","viewBox":"0 0 362 105"}},[_c('path',{staticClass:"svg2396634361__a bb-icon__dynamic-fill",attrs:{"fill":"#38414D","d":"M60.6542 101.039C64.3075 99.792 67.6807 97.8446 70.5837 95.3068 73.4982 92.7435 75.8399 89.5987 77.4571 86.0761 79.208 82.1707 80.0744 77.9291 79.995 73.6525 79.9733 71.783 79.8249 69.9172 79.5509 68.0677 79.2258 65.8361 78.596 63.6593 77.6792 61.5977 76.6973 59.3456 75.3345 57.2784 73.6503 55.486 71.7147 53.481 69.3356 51.9546 66.7029 51.0287L66.5548 50.4281C68.642 49.4069 70.4985 47.9727 72.0112 46.2131 73.3697 44.655 74.4757 42.8952 75.2893 40.9971 76.0083 39.3261 76.5368 37.58 76.8649 35.7916 77.135 34.3666 77.2836 32.9213 77.3091 31.4713 77.3091 22.936 74.6972 16.2131 69.4734 11.3027 68.5682 10.4596 67.5957 9.69136 66.5654 9.00554 66.5654 9.61671 66.5654 10.2279 66.5654 10.8496 66.5752 25.5794 62.2214 39.9844 54.0498 52.2588 45.8782 64.5332 34.2524 74.1308 20.6298 79.8485 22.8521 88.1579 26.5791 95.9929 31.6273 102.968H48.9483C52.931 102.981 56.8877 102.329 60.6542 101.039ZM41.6673 4 8.29416 4.13698C7.81894 4.07478 7.33573 4.12331 6.88251 4.27873 6.42929 4.43414 6.01844 4.69222 5.68226 5.03266 5.19805 5.7298 4.96 6.56749 5.0055 7.41411V80.8176C8.88585 80.0773 12.6985 79.02 16.4048 77.6564 13.4063 64.2515 14.1782 50.2819 18.6355 37.286 23.0927 24.2902 31.0625 12.772 41.6673 4ZM5.00515 84.4322V99.7009C4.96122 100.544 5.19924 101.378 5.68192 102.072 6.04298 102.421 6.47939 102.683 6.95775 102.838 7.4361 102.994 7.94374 103.038 8.44186 102.968H27.2433C22.7486 96.2855 19.377 88.9181 17.261 81.1551 13.2756 82.5895 9.17575 83.6857 5.00515 84.4322Z"}}),_c('path',{staticClass:"svg2396634361__a bb-icon__dynamic-fill",attrs:{"fill":"#38414D","d":"M47.4762 4.0108C36.3426 12.114 27.8012 23.2556 22.883 36.0903 17.9649 48.925 16.8803 62.904 19.7604 76.3395 32.6035 70.8416 43.548 61.7133 51.2403 50.0832 58.9327 38.453 63.0357 24.8313 63.0418 10.9023 63.0418 9.60617 63.0418 8.33113 62.9361 7.05611 58.0434 5.00662 52.7834 3.97051 47.4762 4.0108ZM135.13 53.6501V51.4801C140.62 50.5001 145 47.8501 145 40.8801 145 32.8501 140.73 28.9601 130.2 28.9601H107.37C106.78 28.9601 106.26 29.4401 106.26 30.0301V76.5401C106.26 77.1301 106.77 77.6301 107.37 77.6301H129.28C140.46 77.6301 146.64 73.7301 146.64 64.9201 146.65 57.1601 142.13 54.3101 135.13 53.6501V53.6501ZM115.75 35.7501H128.43C133.3 35.7501 135.54 37.7301 135.54 42.3301 135.54 47.3301 132.98 49.1101 127.98 49.3101L115.75 49.8401V35.7501 35.7501ZM128.11 70.8501H115.76V55.8301L128.12 56.2201C135.16 56.4201 136.93 58.5401 136.93 63.9301 136.92 67.9501 134.75 70.8501 128.11 70.8501ZM159.16 28.1001H153.63C152.58 28.1001 152.04 28.5801 152.04 29.6401V37.0801C152.04 38.1301 152.58 38.5501 153.63 38.5501H159.16C160.21 38.5501 160.77 38.1301 160.77 37.0801V29.6401C160.76 28.5901 160.21 28.1001 159.16 28.1001V28.1001ZM159.75 43.6101H153.04C152.45 43.6101 151.94 44.0501 151.94 44.6401V76.6101C151.94 77.2001 152.45 77.6301 153.04 77.6301H159.75C160.34 77.6301 160.76 77.2001 160.76 76.6101V44.6401C160.76 44.0501 160.34 43.6101 159.75 43.6101ZM221.66 42.2101C218.32 42.2101 214.62 43.0901 212.95 43.5401L212.84 29.1801C212.84 28.5201 212.38 28.1101 211.79 28.1101H211.53L205.08 29.2401C204.29 29.3701 203.96 29.8501 203.96 30.6301V71.3501C203.96 74.1801 204.16 75.7601 207.72 76.8801 210.15 77.7301 214.63 78.4601 219.63 78.4601 232.85 78.4601 235.82 72.5401 235.82 59.9101 235.8 47.0101 231.99 42.2101 221.66 42.2101V42.2101ZM219.03 72.3401C215.48 72.3401 212.78 71.6801 212.78 71.6801V49.4401C212.78 49.4401 216.79 48.3901 219.82 48.3901 225.48 48.3901 226.86 50.3601 226.86 60.1001 226.86 70.1001 224.95 72.3401 219.03 72.3401V72.3401ZM267.37 44.0501C265.26 42.8701 262.12 42.2101 257.45 42.2101 250.74 42.2101 244.67 43.8601 243.09 44.4501 242.76 44.5801 242.28 44.8501 242.28 45.4401V45.7101L243.22 49.7901C243.29 50.2501 243.63 50.3801 243.96 50.3801 244.09 50.3801 244.23 50.3201 244.43 50.3201 246.41 49.7901 251.28 48.4801 255.75 48.4801 258.12 48.4801 259.72 48.6801 260.77 49.1401 262.42 49.8601 262.63 51.1801 262.63 53.0201V55.1301L250.9 57.0401C245.64 57.8901 240.3 58.8201 240.3 67.3001 240.3 77.7601 249.04 78.4801 256.15 78.4801 259.83 78.4801 262.96 78.0901 265.26 77.5601 269.74 76.5101 271.35 75.6501 271.35 70.7201V52.5401C271.36 48.9801 270.73 45.8901 267.37 44.0501ZM262.63 71.8101C261.23 72.2701 259.12 72.7301 256.56 72.7301 253.4 72.7301 249.25 72.3401 249.25 67.2001 249.25 62.9201 251.76 62.5301 254.46 62.1301L262.63 60.8101V71.8101ZM294.65 42.2101C290.44 42.2101 284.92 43.0001 281.56 43.9201 277.88 44.9101 276.63 45.7601 276.63 48.2601V76.6101C276.63 77.2001 277.07 77.6301 277.72 77.6301H284.43C285.02 77.6301 285.46 77.2001 285.46 76.6101V49.3801C285.46 49.3801 289.73 48.2601 293.67 48.2601 297.16 48.2601 298.59 49.5701 298.59 53.3901V76.6101C298.59 77.2001 299.03 77.6301 299.62 77.6301H306.4C306.99 77.6301 307.42 77.2001 307.42 76.6101V52.2101C307.43 44.3101 302.88 42.2101 294.65 42.2101V42.2101ZM344.17 76.6801 329.4 59.8401 343.45 44.1801C343.52 44.0501 343.58 43.9801 343.58 43.8501 343.58 43.5901 343.38 43.2601 342.92 43.2601L334.9 43.0601C334.57 43.0601 334.31 43.1201 334.04 43.5201L322.03 57.4001H321.43V29.2501C321.43 28.5201 321.01 28.1101 320.42 28.1101H320.15L313.71 29.2401C312.92 29.4401 312.59 29.8501 312.59 30.6301V76.6201C312.59 77.2101 313.04 77.6401 313.64 77.6401H320.42C321.07 77.6401 321.43 77.2101 321.43 76.6201V63.3201H322.03L333.84 77.2201C334.17 77.6201 334.5 77.6401 334.83 77.6401H343.64C344.03 77.6401 344.3 77.2901 344.3 77.0301 344.3 76.9001 344.24 76.7401 344.17 76.6801V76.6801ZM195.58 43.4801C193.81 42.9001 191.68 42.4501 189.18 42.1101 186.68 41.7701 184.29 41.6101 181.99 41.6101 178.91 41.6101 176.36 42.0401 174.32 42.9001 172.28 43.7601 170.66 45.0001 169.46 46.6101 168.26 48.2201 167.42 50.1601 166.94 52.4401 166.46 54.7201 166.22 57.2701 166.22 60.1101 166.22 62.8001 166.43 65.2301 166.83 67.4201 167.24 69.6001 167.98 71.4801 169.06 73.0401 170.14 74.6001 171.61 75.8101 173.49 76.6801 175.36 77.5401 177.76 77.9701 180.69 77.9701 181.84 77.9701 183.15 77.8501 184.61 77.6101 185.9 77.4001 187.33 77.0101 188.89 76.4701 188.93 76.4501 188.96 76.4401 189 76.4201L189.13 79.7401C189.13 80.8401 189.23 81.8001 189.01 82.6201 188.79 83.4301 188.5 84.0901 187.93 84.6001 187.36 85.1001 186.63 85.4801 185.64 85.7201 184.66 85.9601 183.42 86.0801 181.89 86.0801 180.5 86.0801 178.91 86.0201 177.12 85.9001 175.32 85.7801 173.61 85.6001 171.98 85.3601 171.31 85.2601 170.82 85.2601 170.51 85.3601 170.2 85.4601 169.99 85.7901 169.9 86.3701L169.32 89.0301C169.23 89.4101 169.25 89.7901 169.39 90.1501 169.53 90.5101 169.99 90.8301 170.76 91.1201 172.25 91.7401 174.08 92.1801 176.27 92.4201 178.45 92.6601 180.48 92.7801 182.35 92.7801 185.81 92.7801 188.53 92.4201 190.52 91.7001 192.51 90.9801 194.04 89.9701 195.09 88.6701 196.14 87.3801 196.79 85.8401 197.08 84.0601 197.37 82.2801 197.49 80.3601 197.49 78.3001V46.7101C197.49 46.0401 197.41 45.4001 197.19 44.8001 196.97 44.2101 196.44 43.7601 195.58 43.4801V43.4801ZM189.09 69.8201C188.34 70.2501 186.31 70.5701 184.89 70.7601 183.47 70.9501 182.35 71.0501 181.3 71.0501 180.05 71.0501 179.04 70.8601 178.16 70.4801 177.27 70.1001 176.58 69.4601 176.03 68.5701 175.48 67.6801 175.08 66.4901 174.82 65.0101 174.56 63.5201 174.43 61.6701 174.43 59.4701 174.43 57.1201 174.64 55.2201 175.05 53.7801 175.46 52.3401 176.02 51.2501 176.74 50.5001 177.46 49.7601 178.3 49.2701 179.26 49.0201 180.22 48.7801 181.25 48.6601 182.36 48.6601 183.51 48.6601 184.49 48.7201 185.77 48.8401 187.04 48.9601 188.35 49.1201 189.1 49.3101V69.8201H189.09Z"}})]) } };

Vue.component('icon-logo-bigbank-mono', Icon$u);

var Icon$t = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 56 17"}},[_c('path',{staticClass:"svg2641185136__a bb-icon__dynamic-fill",attrs:{"fill":"#38414D","d":"M21.2582 0.300765L13.9079 16.7513H9.14442L5.56314 3.62225C5.52942 3.31237 5.41034 3.01611 5.21766 2.76271C5.02498 2.50931 4.76536 2.30753 4.46442 2.17727C3.04177 1.54324 1.54273 1.07342 0 0.778065L0.104309 0.300765H7.80231C8.30645 0.295642 8.79551 0.462354 9.17903 0.770065C9.56255 1.07778 9.81457 1.50566 9.88849 1.97458L11.8217 11.4944L16.5086 0.300765H21.2582ZM40.0338 11.416C40.0338 7.0745 33.6501 6.83912 33.6918 4.87762C33.6918 4.28916 34.3037 3.66148 35.6111 3.50456C37.1423 3.36352 38.6855 3.61665 40.0755 4.23686L40.8682 0.745373C39.5054 0.261838 38.0621 0.0094703 36.6055 0C32.1341 0 28.9562 2.23612 28.9562 5.43991C28.9562 7.8068 31.2023 9.12101 32.913 9.91215C34.6236 10.7033 35.2703 11.2198 35.2634 11.9587C35.2634 13.0636 33.8726 13.5475 32.5583 13.5671C30.9383 13.6022 29.3361 13.2441 27.9061 12.5275L27.1203 16.1301C28.7158 16.7151 30.4147 17.0102 32.1272 16.9997C36.8767 16.9997 39.9921 14.7963 40.006 11.3767L40.0338 11.416ZM51.8276 16.7447H56L52.3492 0.300765H48.4828C48.0765 0.307261 47.681 0.425193 47.345 0.640077C47.009 0.85496 46.7471 1.15744 46.5913 1.51036L39.7695 16.7513H44.5052L45.4509 14.2929H51.2574L51.8276 16.7447ZM46.7652 10.9191L49.1504 4.74685L50.5412 10.9191H46.7652ZM27.7253 0.300765L23.9841 16.7513H19.471L23.2053 0.300765H27.7253Z"}})]) } };

Vue.component('icon-logo-visa', Icon$t);

var Icon$s = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2802951611__a bb-icon__dynamic-fill",attrs:{"d":"M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12C4,13.85 4.63,15.55 5.68,16.91L16.91,5.68C15.55,4.63 13.85,4 12,4M12,20A8,8 0 0,0 20,12C20,10.15 19.37,8.45 18.32,7.09L7.09,18.32C8.45,19.37 10.15,20 12,20Z"}})]) } };

Vue.component('icon-ui-cancel', Icon$s);

var script$1s = Vue.extend({
  name: 'bb-credit-card',
  components: {
    BbIcon: __vue_component__$2C,
    BbButton: __vue_component__$2v
  },
  props: {
    buttonProps: {
      type: Object
    },
    type: {
      type: String,
      default: 'physical',
      validator: type => ['physical', 'virtual'].includes(type)
    },
    status: {
      type: String,
      default: 'empty',
      validator: status => ['empty', 'disabled', 'activated', 'blocked', 'closed'].includes(status)
    },
    digits: [Number, String],
    digitsPrefix: {
      type: String,
      default: '****'
    },
    statusLabel: {
      type: String,
      default: 'Blocked'
    },
    buttonLabel: {
      type: String,
      default: 'Activate'
    },
    virtualLabel: {
      type: String,
      default: 'Virtual card'
    },
    size: {
      type: String,
      default: 'md',
      validator: size => ['sm', 'md'].includes(size)
    },
    ariaLabels: {
      type: Object,
      default: () => ({
        cardNumber: 'Card number',
        cardStatus: 'Card status',
        virtualCard: 'Virtual credit card'
      })
    }
  },
  emits: ['click'],
  computed: {
    classes() {
      return ['bb-credit-card', `bb-credit-card--type-${this.type}`, `bb-credit-card--status-${this.status}`, `bb-credit-card--size-${this.size}`];
    },
    hasNumber() {
      return notEmpty(this.digits);
    },
    isPhysical() {
      return this.type === 'physical';
    },
    isVirtual() {
      return this.type === 'virtual';
    },
    isEmpty() {
      return this.status === 'empty';
    },
    isDisabled() {
      return this.status === 'disabled';
    },
    isActivated() {
      return this.status === 'activated';
    },
    isBlocked() {
      return this.status === 'blocked';
    },
    isClosed() {
      return this.status === 'closed';
    },
    numberVisible() {
      return this.hasNumber && this.isActivated || this.isClosed;
    },
    buttonVisible() {
      return this.isDisabled || this.isBlocked;
    },
    computedNumber() {
      return `${this.digitsPrefix} ${this.digits}`;
    },
    computedBackgroundImage() {
      return this.isDisabled || this.isClosed ? 'cc-bg-lines-disabled' : this.isPhysical ? 'cc-bg-lines' : 'cc-bg-lines-virtual';
    },
    computedChip() {
      return this.isDisabled || this.isClosed ? 'cc-chip-disabled' : 'cc-chip';
    },
    computedPhysicalLogo() {
      return this.isDisabled || this.isClosed ? 'logo-bigbank-grayscale' : 'logo-bigbank';
    },
    computedProcessorLogoFill() {
      return this.isPhysical ? this.isClosed ? 'gray-70' : 'gray' : this.isClosed ? 'gray-90' : 'gray';
    },
    computedStatusIcon() {
      return this.isBlocked ? 'ui-cancel' : 'ui-close-circle-outline';
    },
    cardAriaLabel() {
      var _this$digits;
      const status = toTitleCase(this.status);
      const type = this.isVirtual ? this.virtualLabel : 'Physical credit card';
      const number = this.numberVisible ? ((_this$digits = this.digits) === null || _this$digits === void 0 ? void 0 : _this$digits.length) > 4 ? this.digits : `ending in ${this.digits}` : '';
      return [type, status, number].filter(Boolean).join(', ');
    }
  },
  methods: {
    onClick() {
      this.$emit('click');
    }
  }
});

/* script */
const __vue_script__$1s = script$1s;

/* template */
var __vue_render__$1q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('article', {
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.cardAriaLabel
    }
  }, [_c('bb-icon', {
    staticClass: "bb-credit-card__background-image",
    attrs: {
      "name": _vm.computedBackgroundImage,
      "fill": "original",
      "size": "fluid"
    }
  }), _vm._v(" "), _vm.isPhysical ? _c('bb-icon', {
    staticClass: "bb-credit-card__chip",
    attrs: {
      "name": _vm.computedChip,
      "fill": "original",
      "size": "fluid"
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-credit-card__header"
  }, [_vm.isBlocked || _vm.isClosed ? _c('div', {
    staticClass: "bb-credit-card__status-label",
    attrs: {
      "role": "status",
      "aria-live": "polite",
      "aria-label": _vm.ariaLabels.cardStatus
    }
  }, [_c('bb-icon', {
    staticClass: "bb-credit-card__blocked-icon",
    attrs: {
      "name": _vm.computedStatusIcon,
      "fill": "gray",
      "size": "18"
    }
  }), _vm._v("\n      " + _vm._s(_vm.statusLabel) + "\n    ")], 1) : _vm._e(), _vm._v(" "), _vm.isPhysical ? _c('bb-icon', {
    staticClass: "bb-credit-card__logo-bigbank-physical",
    attrs: {
      "name": _vm.computedPhysicalLogo,
      "fill": "original",
      "size": "fluid"
    }
  }) : [!_vm.isBlocked && !_vm.isClosed ? _c('bb-icon', {
    staticClass: "bb-credit-card__logo-bigbank-virtual",
    attrs: {
      "name": "logo-bigbank-mono",
      "fill": "mint-10",
      "size": "fluid"
    }
  }) : _vm._e(), _vm._v(" "), _c('span', {
    staticClass: "bb-credit-card__virtual-label",
    attrs: {
      "aria-label": _vm.ariaLabels.virtualCard
    }
  }, [_vm._v(_vm._s(_vm.virtualLabel))])]], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-credit-card__footer"
  }, [_vm.numberVisible ? _c('span', {
    staticClass: "bb-credit-card__number",
    class: {
      'f-color-gray-70': _vm.isPhysical && _vm.isClosed,
      'f-color-gray-90': _vm.isVirtual && _vm.isClosed
    },
    attrs: {
      "aria-label": _vm.ariaLabels.cardNumber
    }
  }, [_vm._v(_vm._s(_vm.computedNumber))]) : _vm._e(), _vm._v(" "), _vm.buttonVisible ? _c('bb-button', _vm._b({
    attrs: {
      "label": _vm.buttonLabel,
      "display": "block"
    },
    on: {
      "click": _vm.onClick
    }
  }, 'bb-button', _vm.buttonProps, false)) : _c('bb-icon', {
    staticClass: "bb-credit-card__logo-processor",
    attrs: {
      "name": "logo-visa",
      "fill": _vm.computedProcessorLogoFill,
      "size": "fluid"
    }
  })], 1)], 1);
};
var __vue_staticRenderFns__$1q = [];

/* style */
const __vue_inject_styles__$1s = function (inject) {
  if (!inject) return;
  inject("data-v-684b3dee_0", {
    source: ".bb-credit-card{position:relative;overflow:hidden;width:19.0625rem;height:auto;border:solid .0625rem #dee3e8;border-radius:.875rem}@media (min-width:768px){.bb-credit-card{width:22rem;border-radius:1rem}}.bb-credit-card--type-physical{background:#fff}.bb-credit-card--type-physical.bb-credit-card--status-empty{box-shadow:0 .3125rem 1.875rem rgba(0,0,0,.05)}.bb-credit-card--size-sm{width:13.375rem;height:auto;border-radius:.625rem}.bb-credit-card--type-virtual{background:#e7faf6}.bb-credit-card--type-virtual.bb-credit-card--status-closed{background:#f7f7f8}.bb-credit-card .bb-credit-card__background-image,.bb-credit-card .bb-credit-card__chip{pointer-events:none}.bb-credit-card .bb-credit-card__chip{position:absolute;top:0;left:0}.bb-credit-card .bb-credit-card__footer,.bb-credit-card .bb-credit-card__header{position:absolute;left:0;display:flex;width:100%;justify-content:flex-end}.bb-credit-card .bb-credit-card__header{top:0;height:2.875rem;align-items:center;padding:.75rem}@media (min-width:768px){.bb-credit-card .bb-credit-card__header{height:3.375rem}}.bb-credit-card--type-virtual.bb-credit-card .bb-credit-card__header{padding:.5rem 1.25rem .5rem .75rem;background:#38414d}@media (min-width:768px){.bb-credit-card--type-virtual.bb-credit-card .bb-credit-card__header{padding:.75rem 1.25rem .75rem .75rem}}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__header{height:2.25rem}.bb-credit-card--type-virtual.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__header{padding:.75rem}.bb-credit-card .bb-credit-card__footer{bottom:0;height:5.9375rem;align-items:flex-end;padding:.75rem 1.25rem;background:linear-gradient(180deg,rgba(255,255,255,0) 0,rgba(255,255,255,.97) 51.04%)}.bb-credit-card--status-blocked.bb-credit-card .bb-credit-card__footer,.bb-credit-card--status-disabled.bb-credit-card .bb-credit-card__footer{padding:.75rem}.bb-credit-card--type-virtual.bb-credit-card .bb-credit-card__footer{background:linear-gradient(180deg,rgba(231,250,246,0) 0,#e7faf6 51.04%)}.bb-credit-card--status-closed.bb-credit-card--type-virtual.bb-credit-card .bb-credit-card__footer{background:linear-gradient(180deg,rgba(247,247,248,0) 0,#f7f7f8 51.04%)}.bb-credit-card--size-sm.bb-credit-card--type-virtual.bb-credit-card .bb-credit-card__footer{background:linear-gradient(180deg,rgba(231,250,246,0) 40%,#e7faf6 70%)}.bb-credit-card--status-empty.bb-credit-card .bb-credit-card__footer{background:0 0}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__footer{padding:.75rem;background:linear-gradient(180deg,rgba(255,255,255,0) 40%,rgba(255,255,255,.97) 70%)}.bb-credit-card .bb-credit-card__logo-bigbank-physical{width:4.5rem;height:auto;margin-right:.375rem;margin-left:auto}@media (min-width:768px){.bb-credit-card .bb-credit-card__logo-bigbank-physical{width:5.3125rem;margin-right:.25rem}}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__logo-bigbank-physical{width:3rem;margin-right:0;margin-left:0}.bb-credit-card .bb-credit-card__logo-bigbank-virtual{width:3.875rem;height:auto;flex-shrink:0;margin-left:.5rem}@media (min-width:768px){.bb-credit-card .bb-credit-card__logo-bigbank-virtual{width:4.5rem;margin-left:.75rem}}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__logo-bigbank-virtual{width:3rem;margin-right:0;margin-left:0}.bb-credit-card .bb-credit-card__blocked-icon{margin-right:.25rem}.bb-credit-card .bb-credit-card__status-label{display:flex;height:1.875rem;align-items:center;align-self:flex-start;padding:0 1.25rem;border-radius:.375rem;background:#fc0;color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-credit-card .bb-credit-card__virtual-label{margin-left:auto;color:#e7faf6;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}@media (min-width:768px){.bb-credit-card .bb-credit-card__virtual-label{font-size:.875rem}}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__virtual-label{font-size:.5625rem}.bb-credit-card .bb-credit-card__number{margin-right:auto;margin-bottom:.125rem;color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}@media (min-width:768px){.bb-credit-card .bb-credit-card__number{margin-bottom:.25rem;margin-left:.25rem;font-size:1rem}}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__number{margin-bottom:0;font-size:.5625rem}.bb-credit-card .bb-credit-card__logo-processor{width:3rem;height:auto;margin-bottom:.375rem}@media (min-width:768px){.bb-credit-card .bb-credit-card__logo-processor{width:3.5rem;margin-bottom:.5rem}}.bb-credit-card--size-sm.bb-credit-card .bb-credit-card__logo-processor{width:2.25rem;margin-bottom:.125rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1s = undefined;
/* module identifier */
const __vue_module_identifier__$1s = undefined;
/* functional template */
const __vue_is_functional_template__$1s = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1s = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1q,
  staticRenderFns: __vue_staticRenderFns__$1q
}, __vue_inject_styles__$1s, __vue_script__$1s, __vue_scope_id__$1s, __vue_is_functional_template__$1s, __vue_module_identifier__$1s, false, createInjector, undefined, undefined);

const languages = {
  en: {
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    pickers: ['Tomorrow', 'Day after tomorrow'],
    placeholder: {
      date: 'Select a date',
      dateRange: 'Select a date range'
    }
  },
  fr: {
    days: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'],
    months: ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'],
    pickers: ['Demain', 'Après demain'],
    placeholder: {
      date: 'Sélectionnez une date',
      dateRange: 'Sélectionnez une période'
    }
  },
  es: {
    days: ['Lun', 'mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'],
    months: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    pickers: ['Mañana', 'Pasado mañana'],
    placeholder: {
      date: 'Seleccionar fecha',
      dateRange: 'Seleccionar un rango de fechas'
    }
  },
  'pt-br': {
    days: ['Seg', 'Ter', 'Qua', 'Quin', 'Sex', 'Sáb', 'Dom'],
    months: ['Jan', 'Fev', 'Mar', 'Abr', 'Maio', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    pickers: ['Amanhã', 'Depois de amanhã'],
    placeholder: {
      date: 'Selecione uma data',
      dateRange: 'Selecione um período'
    }
  },
  ru: {
    days: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    months: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
    pickers: ['Завтра', 'Послезавтра'],
    placeholder: {
      date: 'Выберите дату',
      dateRange: 'Выберите период'
    }
  },
  de: {
    days: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    months: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    pickers: ['Morgen', 'Übermorgen'],
    placeholder: {
      date: 'Datum auswählen',
      dateRange: 'Zeitraum auswählen'
    }
  },
  it: {
    days: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'],
    months: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
    pickers: ['Domani', 'Dopodomani'],
    placeholder: {
      date: 'Seleziona una data',
      dateRange: 'Seleziona un intervallo date'
    }
  },
  nl: {
    days: ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'],
    months: ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'],
    pickers: ['Morgen', 'Overmorger'],
    placeholder: {
      date: 'Selecteer datum',
      dateRange: ' Selecteer periode'
    }
  },
  lt: {
    days: ['Pr', 'An', 'Tr', 'Ke', 'Pe', 'Še', 'Se'],
    months: ['Sau', 'Vas', 'Kov', 'Bal', 'Geg', 'Bir', 'Lie', 'Rgp', 'Rgs', 'Spa', 'Lap', 'Gru'],
    pickers: ['Rytoj', 'Poryt'],
    placeholder: {
      date: 'Pasirinkite datą',
      dateRange: 'Pasirinkite laikotarpį'
    }
  },
  lv: {
    days: ['Pr', 'Ot', 'Tr', 'Ce', 'Pk', 'Se', 'Sv'],
    months: ['Janv', 'Febr', 'Marts', 'Apr', 'Maijs', 'Jūn', 'Jūl', 'Aug', 'Sept', 'Okt', 'Nov', 'Dec'],
    pickers: ['Rīt', 'Parīt'],
    placeholder: {
      date: 'Izvēlēties datumu',
      dateRange: 'Izvēlēties periodu'
    }
  },
  se: {
    days: ['Tis', 'Ons', 'Tors', 'Fre', 'Lör', 'Sön', 'Mån'],
    months: ['Jan', 'Febr', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Aug', 'Sept', 'Okt', 'Nov', 'Dec'],
    pickers: ['Rytoj', 'Poryt'],
    placeholder: {
      date: 'Pasirinkite datą',
      dateRange: 'Pasirinkite laikotarpį'
    }
  },
  fi: {
    days: ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'],
    months: ['Tam', 'Hel', 'Maa', 'Huh', 'Tou', 'Kes', 'Hei', 'Elo', 'Syy', 'Lok', 'Mar', 'Jou'],
    pickers: ['Huomenna', 'Ylihuomenna'],
    placeholder: {
      date: 'Valitse Päivämäärä',
      dateRange: 'Valitse Ajanjakso'
    }
  },
  et: {
    days: ['E', 'T', 'K', 'N', 'R', 'L', 'P'],
    months: ['Jaan', 'Veebr', 'Mär', 'Apr', 'Mai', 'Juuni', 'Juuli', 'Aug', 'Sept', 'Okt', 'Nov', 'Dets'],
    pickers: ['Homme', 'Ülehomme'],
    placeholder: {
      date: 'Vali kuupäev',
      dateRange: 'Vali aeg'
    }
  }
};

var Icon$r = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2189745711__a bb-icon__dynamic-fill",attrs:{"d":"M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"}})]) } };

Vue.component('icon-ui-calendar', Icon$r);

var script$1r = {
  name: 'bb-month-year-picker',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    value: String,
    options: Array,
    hideIcon: {
      type: Boolean,
      default: false
    },
    disabled: Boolean,
    tabindex: Number
  },
  emits: ['input', 'change'],
  data() {
    return {
      localValue: this.value
    };
  },
  computed: {
    computedOptions() {
      return isNonEmptyStringOrArray(this.options) ? this.options : [];
    }
  },
  watch: {
    value(newVal) {
      this.localValue = newVal;
    },
    localValue() {
      this.$emit('input', this.localValue);
    }
  },
  methods: {
    onChange(event) {
      this.$emit('change', event.target.value);
    }
  }
};

/* script */
const __vue_script__$1r = script$1r;

/* template */
var __vue_render__$1p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-month-year-picker"
  }, [_c('div', {
    staticClass: "bb-month-year-picker__content-wrapper"
  }, [!_vm.hideIcon ? _c('bb-icon', {
    staticClass: "bb-month-year-picker__icon",
    attrs: {
      "name": "ui-calendar",
      "size": "18",
      "fill": "gray-90"
    }
  }) : _vm._e(), _vm._v(" "), _vm.computedOptions.length > 1 ? _c('select', {
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.localValue,
      expression: "localValue"
    }],
    staticClass: "bb-month-year-picker__options",
    attrs: {
      "tabindex": _vm.tabindex
    },
    on: {
      "change": [function ($event) {
        var $$selectedVal = Array.prototype.filter.call($event.target.options, function (o) {
          return o.selected;
        }).map(function (o) {
          var val = "_value" in o ? o._value : o.value;
          return val;
        });
        _vm.localValue = $event.target.multiple ? $$selectedVal : $$selectedVal[0];
      }, function ($event) {
        $event.stopPropagation();
        return _vm.onChange.apply(null, arguments);
      }]
    }
  }, _vm._l(_vm.computedOptions, function (option, index) {
    return _c('option', {
      key: index,
      attrs: {
        "disabled": _vm.disabled
      },
      domProps: {
        "value": option
      }
    }, [_vm._v("\n        " + _vm._s(option) + "\n      ")]);
  }), 0) : _vm.computedOptions.length === 1 ? _c('div', {
    staticClass: "bb-month-year-picker__option"
  }, [_vm._v("\n      " + _vm._s(_vm.computedOptions[0]) + "\n    ")]) : _vm._e()], 1)]);
};
var __vue_staticRenderFns__$1p = [];

/* style */
const __vue_inject_styles__$1r = function (inject) {
  if (!inject) return;
  inject("data-v-7a30b26f_0", {
    source: ".bb-month-year-picker{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;vertical-align:middle}.bb-month-year-picker__content-wrapper{display:flex}.bb-month-year-picker__icon{position:relative;top:.125rem}.bb-month-year-picker__option{margin-left:.25rem;background-color:inherit;color:#57697a;font-size:1rem;line-height:1.4}.bb-month-year-picker__options{position:relative;display:inline-block;width:auto!important;padding:0;border:none;-webkit-appearance:menulist-button;background-color:inherit;color:#57697a;cursor:pointer;font-size:1rem}.bb-month-year-picker__options::-ms-expand{display:none}.bb-month-year-picker__options:focus{outline:auto}.bb-month-year-picker__options:focus:not(:focus-visible){outline:0}.bb-month-year-picker__options:focus-visible{outline:auto}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1r = undefined;
/* module identifier */
const __vue_module_identifier__$1r = undefined;
/* functional template */
const __vue_is_functional_template__$1r = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1r = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1p,
  staticRenderFns: __vue_staticRenderFns__$1p
}, __vue_inject_styles__$1r, __vue_script__$1r, __vue_scope_id__$1r, __vue_is_functional_template__$1r, __vue_module_identifier__$1r, false, createInjector, undefined, undefined);

var Icon$q = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2291509632__a bb-icon__dynamic-fill",attrs:{"d":"M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"}})]) } };

Vue.component('icon-ui-chevron-left', Icon$q);

var Icon$p = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2046479579__a bb-icon__dynamic-fill",attrs:{"d":"M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"}})]) } };

Vue.component('icon-ui-chevron-right', Icon$p);

var script$1q = Vue.extend({
  name: 'bb-calendar',
  components: {
    BbButton: __vue_component__$2v,
    BbMonthYearPicker: __vue_component__$1r
  },
  props: {
    language: {
      type: String,
      default: 'en'
    },
    dateOne: {
      type: Date
    },
    dateTwo: {
      type: Date
    },
    minDate: {
      type: Date
    },
    maxDate: {
      type: Date
    },
    range: {
      type: Boolean
    },
    monthsToShow: {
      type: Number,
      default: 2
    },
    disabledDates: {
      type: Array,
      default: () => []
    },
    enabledDates: {
      type: Array,
      default: () => []
    },
    yearsSelectAmplitude: {
      type: Number,
      default: 10
    },
    /** Display as `monthpicker`, `yearpicker` or `monthyearpicker`, */
    display: {
      type: String
    },
    /** Hide calendar icon for `monthpicker`, `yearpicker` or `monthyearpicker` */
    hideIcon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dateFormat: 'yyyy-MM-dd',
      dateLabelFormat: 'dddd, MMMM d, yyyy',
      showMonths: 2,
      colors: {
        selected: '#00a699',
        inRange: '#66e2da',
        selectedText: '#fff',
        text: '#565a5c',
        inRangeBorder: '#33dacd',
        disabled: '#fff'
      },
      ariaLabels: {
        chooseDate: date => date,
        chooseStartDate: date => `Choose ${date} as your start date.`,
        chooseEndDate: date => `Choose ${date} as your end date.`,
        selectedDate: date => `Selected. ${date}`,
        unavailableDate: date => `Not available. ${date}`,
        previousMonth: 'Move backward to switch to the previous month.',
        nextMonth: 'Move forward to switch to the next month.'
      },
      monthNames: languages[this.language].months,
      days: languages[this.language].days,
      daysShort: languages[this.language].days,
      firstRenderedMonth: null,
      months: [],
      years: [],
      width: 300,
      selectedDate1: this.dateOne,
      selectedDate2: this.dateTwo,
      isSelectingDate1: true,
      focusedDate: null,
      invisibleRendered: false
    };
  },
  computed: {
    classes() {
      return ['bb-calendar', {
        'bb-calendar--all-dates-selected': this.allDatesSelected,
        'bb-calendar--month-year-picker': this.display,
        'bb-calendar--range': this.range
      }];
    },
    allDatesSelected() {
      return !!(this.selectedDate1 && this.selectedDate2);
    },
    firstMonthFromPicker() {
      const monthIndex = this.hasMinDate ? Number(getMonth(this.minDate)) : 0;
      return languages[this.language].months[monthIndex];
    },
    firstYearFromPicker() {
      return this.hasMinDate ? getYear(this.minDate).toString() : this.years[0];
    },
    hasMinDate() {
      return !!this.minDate;
    },
    hasMaxDate() {
      return !!this.maxDate;
    },
    visibleMonths() {
      const firstMonthArray = this.months.filter((m, index) => index > 0);
      const numberOfMonthsArray = [];
      for (let i = 0; i < this.showMonths; i++) {
        numberOfMonthsArray.push(i);
      }
      return numberOfMonthsArray.map((_, index) => firstMonthArray[index].firstDateOfMonth);
    }
  },
  watch: {
    dateOne(date) {
      if (this.selectedDate1 && isSameDay(date, this.selectedDate1)) {
        return;
      }
      this.initCalendar();
      this.selectDate(date);
    },
    selectedDate1(newValue) {
      const newDate = newValue ? format(newValue, this.dateFormat) : '';
      this.$emit('dateOneSelected', newDate);
    },
    selectedDate2(newValue) {
      const newDate = newValue ? format(newValue, this.dateFormat) : '';
      this.$emit('dateTwoSelected', newDate);
    },
    mode() {
      this.setStartDates();
    },
    minDate() {
      this.initCalendar();
    },
    maxDate() {
      this.generateYears();
    },
    yearsSelectAmplitude() {
      this.generateYears();
    },
    firstRenderedMonth() {
      if (!this.firstRenderedMonth) {
        return;
      }
      const currentYear = getYear(this.add1month(this.firstRenderedMonth));
      if (currentYear < parseInt(this.years[0])) {
        this.years.unshift(currentYear.toString());
      } else if (currentYear > parseInt(this.years[this.years.length - 1])) {
        this.years.push(currentYear.toString());
      }
    }
  },
  created() {
    this.setupDatepicker();
  },
  mounted() {
    this.initCalendar();
  },
  methods: {
    initCalendar() {
      this.setStartDates();
      this.generateMonths();
      this.generateYears();
    },
    onMonthChange(month) {
      /**
       * Change of month event.
       *
       * @event monthChange
       * @type {string}
       */
      this.$emit('monthChange', month);
    },
    onYearChange(year) {
      /**
       * Change of year event.
       *
       * @event yearChange
       * @type {string}
       */
      this.$emit('yearChange', year);
    },
    getDayStyles(date) {
      const isSelected = this.isSelected(date);
      const isInRange = this.isInRange(date);
      const isDisabled = this.isDisabled(date);
      const styles = {
        width: `${(this.width - 30) / 7}px`,
        background: isSelected ? this.colors.selected : isInRange ? this.colors.inRange : '',
        color: isSelected ? this.colors.selectedText : isInRange ? this.colors.selectedText : this.colors.text,
        border: isSelected ? `1px double ${this.colors.selected}` : isInRange && this.allDatesSelected ? `1px double ${this.colors.inRangeBorder}` : ''
      };
      if (isDisabled) {
        styles.background = this.colors.disabled;
      }
      return styles;
    },
    getAriaLabelForDate(date) {
      const dateLabel = format(date, this.dateLabelFormat);
      const isDisabled = this.isDisabled(date);
      if (isDisabled) {
        return this.ariaLabels.unavailableDate(dateLabel);
      }
      const isSelected = this.isSelected(date);
      if (isSelected) {
        return this.ariaLabels.selectedDate(dateLabel);
      }
      if (this.range) {
        if (this.isSelectingDate1) {
          return this.ariaLabels.chooseStartDate(dateLabel);
        } else {
          return this.ariaLabels.chooseEndDate(dateLabel);
        }
      } else {
        return this.ariaLabels.chooseDate(dateLabel);
      }
    },
    generateMonths() {
      if (!this.firstRenderedMonth) {
        return;
      }
      const months = [];
      let currentMonth = this.range && this.hasMaxDate ? this.sub1month(this.firstRenderedMonth) : this.firstRenderedMonth;
      for (let i = 0; i < this.showMonths + 2; i++) {
        months.push(this.getMonth(currentMonth));
        currentMonth = this.add1month(currentMonth);
      }
      this.months = months;
    },
    generateYears() {
      const years = [];
      if (!this.firstRenderedMonth) {
        return;
      }
      const currentYear = getYear(this.add1month(this.firstRenderedMonth));
      const startYear = this.hasMinDate ? getYear(this.minDate) : currentYear - this.yearsSelectAmplitude;
      const endYear = this.hasMaxDate ? getYear(this.maxDate) : currentYear + this.yearsSelectAmplitude;
      for (let year = startYear; year <= endYear; year++) {
        years.push(year.toString());
      }
      this.years = years;
    },
    setupDatepicker() {
      const options = this.$options;
      if (options.ariaLabels) {
        this.ariaLabels = this.copyObject(options.ariaLabels);
      }
      if (options.dateLabelFormat) {
        this.dateLabelFormat = this.copyObject(options.dateLabelFormat);
      }
      if (options.colors) {
        const colors = this.copyObject(options.colors);
        this.colors.selected = colors.selected || this.colors.selected;
        this.colors.inRange = colors.inRange || this.colors.inRange;
        this.colors.selectedText = colors.selectedText || this.colors.selectedText;
        this.colors.text = colors.text || this.colors.text;
        this.colors.inRangeBorder = colors.inRangeBorder || this.colors.inRangeBorder;
        this.colors.disabled = colors.disabled || this.colors.disabled;
      }
      if (options.monthNames && options.monthNames.length === 12) {
        this.monthNames = this.copyObject(options.monthNames);
      }
      if (options.days && options.days.length === 7) {
        this.days = this.copyObject(options.days);
      }
      if (options.daysShort && options.daysShort.length === 7) {
        this.daysShort = this.copyObject(options.daysShort);
      }
    },
    setStartDates() {
      const startDate = this.dateOne || new Date();
      const clampedStartDate = this.isBeforeMinDate(startDate) ? this.minDate : this.isAfterMaxDate(startDate) ? this.maxDate : startDate;
      this.firstRenderedMonth = this.sub1month(clampedStartDate);
      this.selectedDate1 = this.dateOne;
      this.selectedDate2 = this.dateTwo;
      this.focusedDate = clampedStartDate;
    },
    getMonth(date) {
      const firstDateOfMonth = format(date, 'yyyy-MM-01');
      const year = format(date, 'yyyy');
      const monthNumber = parseInt(format(date, 'M'));
      const monthName = this.monthNames[monthNumber - 1];
      return {
        year,
        firstDateOfMonth,
        monthName,
        monthNumber,
        weeks: this.getWeeks(startOfMonth(date))
      };
    },
    getWeeks(date) {
      const weekDayNotInMonth = {
        dayNumber: 0
      };
      const daysInMonth = getDaysInMonth(date);
      const year = format(date, 'yyyy');
      const month = format(date, 'MM');
      const firstDayInWeek = parseInt(format(date, 'i'));
      const weeks = [];
      let week = [];
      // add empty days to get first day in correct position
      for (let s = 1; s < firstDayInWeek; s++) {
        week.push(weekDayNotInMonth);
      }
      for (let d = 0; d < daysInMonth; d++) {
        const isLastDayInMonth = d >= daysInMonth - 1;
        const dayNumber = d + 1;
        const dayNumberFull = dayNumber.toString().padStart(2, '0');
        const fullDateString = `${year}-${month}-${dayNumberFull}`;
        week.push({
          dayNumber,
          dayNumberFull,
          fullDateString,
          fullDate: parseISO(fullDateString)
        });
        if (week.length === 7) {
          weeks.push(week);
          week = [];
        } else if (isLastDayInMonth) {
          for (let i = 0; i < 7 - week.length; i++) {
            week.push(weekDayNotInMonth);
          }
          weeks.push(week);
          week = [];
        }
      }
      return weeks;
    },
    selectDate(date) {
      if (this.isBeforeMinDate(date) || this.isAfterMaxDate(date) || this.isDateDisabled(date)) {
        return;
      }
      if (!this.range) {
        this.selectedDate1 = date;
        return;
      }
      if (this.isSelectingDate1 || this.selectedDate1 && isBefore(date, this.selectedDate1)) {
        this.selectedDate1 = date;
        this.isSelectingDate1 = false;
        if (this.selectedDate2 && isBefore(this.selectedDate2, date)) {
          this.selectedDate2 = null;
        }
      } else {
        this.selectedDate2 = date;
        this.isSelectingDate1 = true;
        if (this.selectedDate1 && isAfter(this.selectedDate1, date)) {
          this.selectedDate1 = null;
        }
      }
    },
    resetFocusedDate(setToFirst) {
      if (this.focusedDate && !this.isDateVisible(this.focusedDate)) {
        const visibleMonthIdx = setToFirst ? 0 : this.visibleMonths.length - 1;
        const targetMonth = this.visibleMonths[visibleMonthIdx];
        const monthIdx = getMonth(parseISO(targetMonth));
        const year = getYear(parseISO(targetMonth));
        this.focusedDate = setYear(setMonth(this.focusedDate, monthIdx), year);
      }
    },
    isToday(date) {
      return isToday(date);
    },
    isSameDate(date1, date2) {
      return isSameDay(date1, date2);
    },
    isSelected(date) {
      if (!date) {
        return false;
      }
      return this.selectedDate1 && this.isSameDate(date, this.selectedDate1) || this.selectedDate2 && this.isSameDate(date, this.selectedDate2);
    },
    isInRange(date) {
      if (!this.allDatesSelected || !this.range) {
        return false;
      }
      if (!this.selectedDate1 || !this.selectedDate2) {
        return false;
      }
      return isAfter(date, this.selectedDate1) && isBefore(date, this.selectedDate2) || isAfter(date, this.selectedDate1) && !this.allDatesSelected;
    },
    isBeforeMinDate(date) {
      if (!this.hasMinDate) {
        return false;
      }
      if (getHours(this.minDate) === 0 && getMinutes(this.minDate) === 0 && getSeconds(this.minDate) === 0) {
        return isBefore(date, subSeconds(this.minDate, 1));
      }
      return isBefore(date, subDays(this.minDate, 1));
    },
    isAfterMaxDate(date) {
      if (!this.hasMaxDate) {
        return false;
      }
      return isAfter(date, this.maxDate);
    },
    isDateVisible(date) {
      if (!date) {
        return false;
      }
      const start = subDays(parseISO(this.visibleMonths[0]), 1);
      const end = addDays(lastDayOfMonth(parseISO(this.visibleMonths[this.monthsToShow - 1])), 1);
      return isAfter(date, start) && isBefore(date, end);
    },
    isDateDisabled(date) {
      const equalsDate = containedDate => +containedDate === +date;
      return this.enabledDates.length ? !this.enabledDates.some(equalsDate) : this.disabledDates.some(equalsDate);
    },
    isDisabled(date) {
      return this.isDateDisabled(date) || this.isBeforeMinDate(date) || this.isAfterMaxDate(date);
    },
    async previousMonth() {
      this.invisibleRendered = true;
      await this.$nextTick();
      const date = parseISO(this.months[0].firstDateOfMonth);
      this.firstRenderedMonth = this.sub1month(date);
      this.months.unshift(this.getMonth(this.firstRenderedMonth));
      this.months.splice(this.months.length - 1, 1);
      this.$emit('previousMonth', this.visibleMonths);
      this.resetFocusedDate(false);
      await this.$nextTick();
      this.invisibleRendered = false;
    },
    async nextMonth() {
      this.invisibleRendered = true;
      await this.$nextTick();
      const date = parseISO(this.months[this.months.length - 1].firstDateOfMonth);
      this.firstRenderedMonth = this.add1month(date);
      this.months.push(this.getMonth(this.firstRenderedMonth));
      this.months.splice(0, 1);
      this.$emit('nextMonth', this.visibleMonths);
      this.resetFocusedDate(true);
      await this.$nextTick();
      this.invisibleRendered = false;
    },
    sub1month(date) {
      return subMonths(date, 1);
    },
    add1month(date) {
      return addMonths(date, 1);
    },
    updateMonth(offset, year, newMonth) {
      if (!this.firstRenderedMonth) {
        return;
      }
      const monthIdx = this.monthNames.indexOf(newMonth);
      const newDate = setYear(setMonth(this.firstRenderedMonth, monthIdx), year);
      this.firstRenderedMonth = subMonths(newDate, offset);
      this.generateMonths();
    },
    updateYear(offset, monthIdx, newYear) {
      if (!this.firstRenderedMonth) {
        return;
      }
      const newDate = setYear(setMonth(this.firstRenderedMonth, monthIdx), newYear);
      this.firstRenderedMonth = subMonths(newDate, offset);
      this.generateMonths();
    },
    copyObject(obj) {
      return JSON.parse(JSON.stringify(obj));
    }
  }
});

/* script */
const __vue_script__$1q = script$1q;

/* template */
var __vue_render__$1o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.display ? [_vm.display === 'monthpicker' || _vm.display === 'monthyearpicker' ? _c('bb-month-year-picker', {
    staticClass: "bb-calendar__monthyearpicker",
    attrs: {
      "value": _vm.firstMonthFromPicker,
      "options": _vm.monthNames,
      "hide-icon": _vm.hideIcon
    },
    on: {
      "change": _vm.onMonthChange
    }
  }) : _vm._e(), _vm._v(" "), _vm.display === 'yearpicker' || _vm.display === 'monthyearpicker' ? _c('bb-month-year-picker', {
    staticClass: "bb-calendar__monthyearpicker",
    attrs: {
      "value": _vm.firstYearFromPicker,
      "options": _vm.years,
      "hide-icon": _vm.display === 'monthyearpicker' || _vm.hideIcon
    },
    on: {
      "change": _vm.onYearChange
    }
  }) : _vm._e()] : [_c('div', {
    staticClass: "bb-calendar__datepicker-header"
  }, [_c('bb-button', {
    staticClass: "bb-calendar__change-month-button bb-calendar__change-month-button--previous",
    attrs: {
      "aria-label": _vm.ariaLabels.previousMonth,
      "no-background": "",
      "display": "square",
      "color": "gray",
      "icon": "ui-chevron-left"
    },
    on: {
      "click": _vm.previousMonth
    }
  }), _vm._v(" "), _c('bb-button', {
    staticClass: "bb-calendar__change-month-button bb-calendar__change-month-button--next",
    attrs: {
      "aria-label": _vm.ariaLabels.nextMonth,
      "no-background": "",
      "display": "square",
      "color": "gray",
      "icon": "ui-chevron-right"
    },
    on: {
      "click": _vm.nextMonth
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-calendar__inner-wrapper"
  }, [_c('transition-group', {
    attrs: {
      "name": "bb-calendar__list-complete",
      "tag": "div"
    }
  }, _vm._l(_vm.months, function (month, monthIndex) {
    return _c('div', {
      key: month.firstDateOfMonth,
      staticClass: "bb-calendar__month",
      class: {
        'bb-calendar__month--hidden': monthIndex === 0 || monthIndex > _vm.showMonths,
        'bb-calendar__month--invisible': !_vm.range && !_vm.invisibleRendered && monthIndex > 1 && monthIndex <= _vm.showMonths
      }
    }, [_c('div', {
      staticClass: "bb-calendar__month-year-name"
    }, [_c('bb-month-year-picker', {
      key: "month-" + monthIndex + "-" + month.monthName,
      attrs: {
        "value": month.monthName,
        "options": _vm.monthNames,
        "tabindex": monthIndex === 0 || monthIndex > _vm.showMonths ? -1 : 0,
        "hide-icon": ""
      },
      on: {
        "change": function ($event) {
          _vm.updateMonth(monthIndex, parseInt(month.year), $event);
        }
      }
    }), _vm._v(" "), _c('bb-month-year-picker', {
      key: "month-" + monthIndex + "-" + month.year,
      attrs: {
        "value": month.year,
        "options": _vm.years,
        "tabindex": monthIndex === 0 || monthIndex > _vm.showMonths ? -1 : 0,
        "hide-icon": ""
      },
      on: {
        "change": function ($event) {
          return _vm.updateYear(monthIndex, month.monthNumber - 1, $event);
        }
      }
    })], 1), _vm._v(" "), _c('div', {
      staticClass: "bb-calendar__days-legend"
    }, _vm._l(_vm.daysShort, function (day) {
      return _c('div', {
        key: day,
        staticClass: "bb-calendar__day-title"
      }, [_vm._v("\n              " + _vm._s(day) + "\n            ")]);
    }), 0), _vm._v(" "), _c('table', {
      staticClass: "bb-calendar__month-table",
      attrs: {
        "role": "presentation"
      }
    }, [_c('tbody', _vm._l(month.weeks, function (week, index) {
      return _c('tr', {
        key: index,
        staticClass: "bb-calendar__week"
      }, _vm._l(week, function (day, subindex) {
        return _c('td', {
          key: subindex + "_" + day.dayNumber,
          ref: "date-" + day.fullDateString,
          refInFor: true,
          staticClass: "bb-calendar__day",
          class: {
            'bb-calendar__day--enabled': day.dayNumber !== 0,
            'bb-calendar__day--empty': day.dayNumber === 0,
            'bb-calendar__day--disabled': _vm.isDisabled(day.fullDate),
            'bb-calendar__day--selected': day.fullDate && (_vm.isSameDate(day.fullDate, _vm.selectedDate1) || _vm.isSameDate(day.fullDate, _vm.selectedDate2)),
            'bb-calendar__day--in-range': _vm.isInRange(day.fullDate),
            'bb-calendar__day--today': day.fullDate && _vm.isToday(day.fullDate),
            'bb-calendar__selected-date-one': day.fullDate && _vm.isSameDate(day.fullDate, _vm.selectedDate1),
            'bb-calendar__selected-date-two': day.fullDate && _vm.isSameDate(day.fullDate, _vm.selectedDate2)
          },
          style: _vm.getDayStyles(day.fullDate),
          attrs: {
            "data-date": day.fullDateString
          }
        }, [day.dayNumber ? _c('button', {
          staticClass: "bb-calendar__day-button",
          attrs: {
            "date": day.fullDateString,
            "disabled": _vm.isDisabled(day.fullDate),
            "type": "button",
            "tabindex": "-1",
            "aria-label": _vm.isDateVisible(day.fullDate) ? _vm.getAriaLabelForDate(day.fullDate) : false
          },
          on: {
            "click": function () {
              _vm.selectDate(day.fullDate);
            }
          }
        }, [_vm._v("\n                    " + _vm._s(day.dayNumber) + "\n                  ")]) : _vm._e()]);
      }), 0);
    }), 0)])]);
  }), 0)], 1)]], 2);
};
var __vue_staticRenderFns__$1o = [];

/* style */
const __vue_inject_styles__$1q = function (inject) {
  if (!inject) return;
  inject("data-v-32d110e2_0", {
    source: ".bb-calendar__day--enabled::before,.bb-calendar__day--selected::before{position:absolute;z-index:-1100;top:50%;left:50%;display:block;width:24px;height:24px;content:\"\";transform:translate(-50%,-50%);transition:background-color .2s,border-radius .25s}.bb-calendar--range.bb-calendar--all-dates-selected .bb-calendar__day--selected.bb-calendar__selected-date-one::after,.bb-calendar--range.bb-calendar--all-dates-selected .bb-calendar__day--selected.bb-calendar__selected-date-two::after{position:absolute;z-index:-1200;top:0;width:calc(50% + 14px);height:100%;background-color:#e6f3ff;content:\"\"}.bb-calendar{z-index:0;display:block;overflow:hidden;width:100%;border:none;background:0 0;text-align:center;white-space:nowrap}@media (min-width:576px){.bb-calendar{height:16.5625rem}}.bb-calendar__list-complete-enter,.bb-calendar__list-complete-leave-to{opacity:0}.bb-calendar__list-complete-leave-active{position:absolute;visibility:hidden}.bb-calendar__inner-wrapper{position:relative;transition:all .3s ease}.bb-datepicker__popup--datetime .bb-calendar__inner-wrapper{border-right:1px solid #eeeff2}.bb-calendar__datepicker-header{position:relative}.bb-calendar__change-month-button{position:absolute;z-index:1100;top:.9375rem;width:1.875rem;height:1.875rem;min-height:1.875rem}.bb-calendar__change-month-button--previous{left:.9375rem}.bb-calendar__change-month-button--next{right:.9375rem}.bb-calendar__days-legend{width:100%!important;padding:0 .625rem;border-bottom:.0625rem solid #eeeff2;font-size:12px}.bb-calendar__day-title{display:inline-block;width:14.2857142857%;margin-bottom:.3125rem;margin-left:-.0625rem;color:#687d92;font-size:.6rem;text-align:center}.bb-calendar__month-table{width:100%;max-width:100%;border-collapse:separate;border-spacing:0 .125rem;font-size:12px;table-layout:fixed}.bb-calendar__month{display:inline-block;width:100%!important;padding:.9375rem .9375rem 1.875rem}.bb-calendar__month--hidden{display:none!important}.bb-calendar__month--invisible{visibility:hidden!important}.bb-calendar--range .bb-calendar__month{display:block;width:100%}@media (min-width:768px){.bb-calendar--range .bb-calendar__month{display:inline-block;max-width:50%}}.bb-calendar__month-year-name{display:flex;justify-content:center;margin:0 0 .9375rem;line-height:1.875rem;text-align:center}.bb-calendar__month-year-select{position:relative;display:inline-block;width:auto!important;height:1.875rem;padding:0;border:none;-webkit-appearance:menulist-button;background-color:inherit;color:#57697a;cursor:pointer;font-size:16px}.bb-calendar__month-year-select::-ms-expand{display:none}.bb-calendar__month-year-select:focus{outline:auto}.bb-calendar__month-year-select:focus:not(:focus-visible){outline:0}.bb-calendar__month-year-select:focus-visible{outline:auto}.bb-calendar__day{position:relative;overflow:hidden;height:1.75rem;padding:0;border:none!important;background:0 0!important;color:inherit!important;line-height:1.75rem}.bb-calendar__day--enabled::before{border-radius:0;background-color:#fff}.bb-calendar__day--enabled:hover{background:0 0}.bb-calendar__day--enabled:focus{outline:0;outline:auto .3125rem Highlight;outline:auto .3125rem -webkit-focus-ring-color}.bb-calendar__day--disabled,.bb-calendar__day--empty{opacity:.5}.bb-calendar__day--disabled button,.bb-calendar__day--empty button{cursor:not-allowed}.bb-calendar__day--disabled:focus,.bb-calendar__day--empty:focus{outline:0}.bb-calendar__day--empty{border:none}.bb-calendar__day--disabled:hover{background-color:transparent}.bb-calendar__day--today{color:#2485e5!important;font-family:GothamSSm-Black,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-calendar__day--selected{z-index:1100;color:#fff!important}.bb-calendar__day--selected::before{border-radius:50%;background-color:#2485e5}.bb-calendar--range.bb-calendar--all-dates-selected .bb-calendar__day--selected.bb-calendar__selected-date-one::after{right:0;border-bottom-left-radius:99em;border-top-left-radius:99em}.bb-calendar--range.bb-calendar--all-dates-selected .bb-calendar__day--selected.bb-calendar__selected-date-two::after{left:0;border-bottom-right-radius:99em;border-top-right-radius:99em}.bb-calendar--range.bb-calendar--all-dates-selected .bb-calendar__day--selected.bb-calendar__selected-date-one.bb-calendar__selected-date-two::after{right:auto;left:50%;width:28px;transform:translateX(-50%)}.bb-calendar__day--in-range{background:#e6f3ff!important}.bb-calendar__day-button{width:100%;height:100%;padding:0;border:none;background:0 0;color:inherit;cursor:pointer;font-family:inherit;font-size:12px;font-weight:inherit;text-align:center;user-select:none}.bb-calendar__day-button:focus{outline:0}.bb-calendar__day--selected .bb-calendar__day-button{color:#fff;font-family:GothamSSm-Black,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-calendar--month-year-picker{display:inline-block;width:unset;height:auto}.bb-calendar--month-year-picker .bb-calendar__monthyearpicker{display:inline-block}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1q = undefined;
/* module identifier */
const __vue_module_identifier__$1q = undefined;
/* functional template */
const __vue_is_functional_template__$1q = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1o,
  staticRenderFns: __vue_staticRenderFns__$1o
}, __vue_inject_styles__$1q, __vue_script__$1q, __vue_scope_id__$1q, __vue_is_functional_template__$1q, __vue_module_identifier__$1q, false, createInjector, undefined, undefined);

const internalTimeFormat = 'HH:mm';
const internalDateFormat = 'YYYY-MM-DD';
const zeroHour = {
  HH: '00',
  mm: '00'
};
const unixEpoch = () => dayjs.unix(0);
const unixEpochFormattedDate = () => unixEpoch().format(internalDateFormat);
const dateObjectToInternalDateFormat = date => dayjs(date).format(internalDateFormat);
const timepickerToDateObject = timepickerValue => {
  const timeString = `${timepickerValue.HH}:${timepickerValue.mm}`;
  return dayjs(`${unixEpochFormattedDate()} ${timeString}`).toDate();
};
const dateObjectToTimepicker = date => ({
  HH: dayjs(date).format('HH'),
  mm: dayjs(date).format('mm')
});
const extractTimeFromDate = date => {
  const copy = dayjs(date);
  const extractedHour = copy.hour();
  const extractedMinute = copy.minute();
  const extractedTimeOnUnixEpoch = unixEpoch().hour(extractedHour).minute(extractedMinute);
  const extractedTimeOnUnixEpochInMilliseconds = extractedTimeOnUnixEpoch.valueOf();
  return extractedTimeOnUnixEpochInMilliseconds;
};
const getHoursDoubleDigitFromDate = value => padWithLeadingZero(value.getHours());
const getMinutesDoubleDigitFromDate = value => padWithLeadingZero(value.getMinutes());
const addTimeToUnixEpoch = (hours, minutes) => unixEpoch().hour(+hours).minute(+minutes).second(0).toDate();
const ceilToNextMinuteStep = (date, minuteStep) => {
  const ms = 1000 * 60 * minuteStep;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
};
const timeIsBefore = (timeOne, timeTwo) => extractTimeFromDate(timeOne) < extractTimeFromDate(timeTwo);
const timeIsAfter = (timeOne, timeTwo) => extractTimeFromDate(timeOne) > extractTimeFromDate(timeTwo);
const timeIsSame = (timeOne, timeTwo) => extractTimeFromDate(timeOne) === extractTimeFromDate(timeTwo);

const DEFAULT_MIN_MINUTE = '00';
const DEFAULT_MAX_MINUTE = '59';
const DEFAULT_MIN_HOUR = '00';
const DEFAULT_MAX_HOUR = '23';
const DEFAULT_MIN_TIME = unixEpoch().hour(+DEFAULT_MIN_HOUR).minute(+DEFAULT_MIN_MINUTE).second(0).toDate();
const DEFAULT_MAX_TIME = unixEpoch().hour(+DEFAULT_MAX_HOUR).minute(+DEFAULT_MAX_MINUTE).second(0).toDate();

var Icon$o = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2898296600__a bb-icon__dynamic-fill",attrs:{"d":"M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"}})]) } };

Vue.component('icon-ui-clock-outline', Icon$o);

const CONFIG = {
  HOUR_TOKENS: ['HH', 'H'],
  MINUTE_TOKENS: ['mm', 'm']
};
var script$1p = {
  name: 'bb-timepicker',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        HH: DEFAULT_MIN_HOUR,
        mm: DEFAULT_MIN_MINUTE
      })
    },
    format: {
      type: String,
      default: 'HH:mm'
    },
    minuteStep: {
      type: Number,
      default: 1,
      validator: val => +val >= 0 && +val <= 30
    },
    minTime: {
      type: Date,
      default: () => DEFAULT_MIN_TIME
    },
    maxTime: {
      type: Date,
      default: () => DEFAULT_MAX_TIME
    },
    disabled: Boolean,
    hideIcon: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input'],
  data() {
    return {
      hour: '',
      minute: ''
    };
  },
  computed: {
    hours() {
      const hours = [];
      for (let hour = 0; hour <= DEFAULT_MAX_HOUR; hour++) {
        const hourIsExcluded = hour > this.maxTimeHours || hour < this.minTimeHours;
        if (!hourIsExcluded) {
          hours.push(this.formatValue(this.hourType, hour));
        }
      }
      return hours;
    },
    minutes() {
      const minutes = [];
      for (let minute = Number(this.minutesSince); minute <= Number(this.minutesUntil); minute += Number(this.minuteStep)) {
        minutes.push(this.formatValue(this.minuteType, minute));
      }
      return minutes;
    },
    minutesSince() {
      const minTimeStepRemainder = this.minTimeMinutes % this.minuteStep;
      return this.hour && this.hour === this.minTimeHours ? minTimeStepRemainder === 0 ? this.minTimeMinutes : this.minTimeMinutes - minTimeStepRemainder + this.minuteStep : DEFAULT_MIN_MINUTE;
    },
    minutesUntil() {
      return this.hour && this.hour === this.maxTimeHours ? this.maxTimeMinutes : DEFAULT_MAX_MINUTE;
    },
    hourType() {
      return this.checkAcceptingType(CONFIG.HOUR_TOKENS, this.format);
    },
    minuteType() {
      return this.checkAcceptingType(CONFIG.MINUTE_TOKENS, this.format);
    },
    valueHour() {
      return this.value[CONFIG.HOUR_TOKENS[0]];
    },
    valueMinute() {
      return this.value[CONFIG.MINUTE_TOKENS[0]];
    },
    minTimeHours() {
      return getHoursDoubleDigitFromDate(this.minTime);
    },
    minTimeMinutes() {
      return getMinutesDoubleDigitFromDate(this.minTime);
    },
    maxTimeHours() {
      return getHoursDoubleDigitFromDate(this.maxTime);
    },
    maxTimeMinutes() {
      return getMinutesDoubleDigitFromDate(this.maxTime);
    },
    unixEpochMinTime() {
      return addTimeToUnixEpoch(this.minTimeHours, this.minTimeMinutes);
    },
    unixEpochMaxTime() {
      return addTimeToUnixEpoch(this.maxTimeHours, this.maxTimeMinutes);
    },
    timeFromValue() {
      return addTimeToUnixEpoch(this.valueHour, this.valueMinute);
    },
    ceiledValue() {
      return ceilToNextMinuteStep(this.timeFromValue, this.minuteStep);
    },
    clampedCeiledValue() {
      return clamp$2(this.ceiledValue, {
        start: this.unixEpochMinTime,
        end: this.unixEpochMaxTime
      });
    }
  },
  watch: {
    'value': 'setModelsFromValue',
    'format': 'setModelsFromValue',
    'minuteStep': 'setModelsFromValue',
    'minTime': 'setModelsFromValue',
    'maxTime': 'setModelsFromValue'
  },
  mounted() {
    this.setModelsFromValue();
  },
  methods: {
    onHour(event) {
      const newValue = event.target.value;
      if (this.hour === newValue) return;
      this.hour = newValue;
      this.emitInput();
    },
    onMinute(event) {
      const newValue = event.target.value;
      if (this.minute === newValue) return;
      this.minute = newValue;
      this.emitInput();
    },
    formatValue(type, value) {
      switch (type) {
        case 'H':
        case 'm':
          return String(value);
        case 'HH':
        case 'mm':
          return padWithLeadingZero(value);
        case 'h':
          return String(value + 1);
        case 'hh':
          return value + 1 < 10 ? padWithLeadingZero(value + 1) : String(value + 1);
        default:
          return '';
      }
    },
    checkAcceptingType(validValues, formatString) {
      if (!validValues || !formatString || !formatString.length) return '';
      for (let i = 0; i < validValues.length; i++) {
        if (formatString.indexOf(validValues[i]) > -1) {
          return validValues[i];
        }
      }
    },
    setModelsFromValue() {
      const hour = this.formatValue(this.hourType, Number(getHoursDoubleDigitFromDate(this.clampedCeiledValue)));
      const minute = this.formatValue(this.minuteType, Number(getMinutesDoubleDigitFromDate(this.clampedCeiledValue)));
      this.hour = hour;
      this.minute = minute;
      if (this.valueHour !== hour || this.valueMinute !== minute) {
        this.emitInput();
      }
    },
    emitInput() {
      const val = {
        [this.hourType]: this.hour,
        [this.minuteType]: this.minute
      };
      this.$emit('input', val);
    }
  }
};

/* script */
const __vue_script__$1p = script$1p;

/* template */
var __vue_render__$1n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-timepicker"
  }, [_c('div', {
    staticClass: "bb-timepicker__content-wrapper"
  }, [!_vm.hideIcon ? _c('bb-icon', {
    staticClass: "bb-timepicker__icon",
    attrs: {
      "name": "ui-clock-outline",
      "size": "18",
      "fill": "gray-90"
    }
  }) : _vm._e(), _vm._v(" "), _c('select', {
    staticClass: "bb-timepicker__hours",
    domProps: {
      "value": _vm.hour
    },
    on: {
      "input": _vm.onHour
    }
  }, _vm._l(_vm.hours, function (hr) {
    return _c('option', {
      key: hr,
      attrs: {
        "disabled": _vm.disabled
      }
    }, [_vm._v("\n        " + _vm._s(hr) + "\n      ")]);
  }), 0), _vm._v(" "), _c('select', {
    staticClass: "bb-timepicker__minutes",
    domProps: {
      "value": _vm.minute
    },
    on: {
      "input": _vm.onMinute
    }
  }, _vm._l(_vm.minutes, function (m) {
    return _c('option', {
      key: m,
      attrs: {
        "disabled": _vm.disabled
      }
    }, [_vm._v("\n        " + _vm._s(m) + "\n      ")]);
  }), 0)], 1)]);
};
var __vue_staticRenderFns__$1n = [];

/* style */
const __vue_inject_styles__$1p = function (inject) {
  if (!inject) return;
  inject("data-v-454e9aa4_0", {
    source: ".bb-timepicker{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px;vertical-align:middle}.bb-timepicker__content-wrapper{display:flex}.bb-timepicker__icon{position:relative;top:.0625rem}.bb-timepicker__hours,.bb-timepicker__minutes{position:relative;display:inline-block;width:auto!important;padding:0;border:none;-webkit-appearance:menulist-button;background-color:inherit;color:#57697a;cursor:pointer;font-size:16px}.bb-timepicker__hours::-ms-expand,.bb-timepicker__minutes::-ms-expand{display:none}.bb-timepicker__hours:focus,.bb-timepicker__minutes:focus{outline:auto}.bb-timepicker__hours:focus:not(:focus-visible),.bb-timepicker__minutes:focus:not(:focus-visible){outline:0}.bb-timepicker__hours:focus-visible,.bb-timepicker__minutes:focus-visible{outline:auto}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1p = undefined;
/* module identifier */
const __vue_module_identifier__$1p = undefined;
/* functional template */
const __vue_is_functional_template__$1p = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1p = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1n,
  staticRenderFns: __vue_staticRenderFns__$1n
}, __vue_inject_styles__$1p, __vue_script__$1p, __vue_scope_id__$1p, __vue_is_functional_template__$1p, __vue_module_identifier__$1p, false, createInjector, undefined, undefined);

dayjs.extend(customParseFormat);
Vue.use(PortalVue);
Vue.use(VueScreen, gridBreakpoints);
const day = 3600 * 1000 * 24;
const defaultShortcuts = [{
  text: 'Tomorrow',
  start: new Date(),
  end: new Date(Date.now() + day)
}, {
  text: 'Day after tomorrow',
  start: new Date(),
  end: new Date(Date.now() + day * 2)
}];
var script$1o = {
  name: 'bb-datepicker',
  components: {
    BbButton: __vue_component__$2v,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbCalendar: __vue_component__$1q,
    BbTimepicker: __vue_component__$1p,
    BbInput: __vue_component__$2f,
    ClientOnly
  },
  directives: {
    clickOutside
  },
  mixins: [FieldExtensions, ErrorBubble, Field, veeValidationMixin, vuelidateValidator],
  props: {
    type: {
      type: String,
      default: 'date',
      validator: t => ['date', 'datetime', 'time', 'month', 'year', 'monthYear'].includes(t)
    },
    value: [Date, Array],
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    },
    range: Boolean,
    lang: {
      type: [String, Object],
      default: 'en'
    },
    confirmText: {
      type: String,
      default: 'OK'
    },
    shortcuts: {
      type: [Boolean, Array],
      default: () => defaultShortcuts
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    customFormatter: Function,
    yearsForSelect: {
      type: [Number, String],
      validator: deprecated('yearsForSelect', 'yearsSelectAmplitude', 'Datepicker')
    },
    yearsSelectAmplitude: {
      type: [Number, String],
      default: 10
    },
    enabledDates: {
      type: Array,
      default: () => []
    },
    disabledDates: {
      type: Array,
      default: () => []
    },
    minDate: Date,
    maxDate: Date,
    minTime: {
      type: Date,
      default: () => DEFAULT_MIN_TIME
    },
    maxTime: {
      type: Date,
      default: () => DEFAULT_MAX_TIME
    },
    minMaxDateIncludesTime: Boolean,
    minuteStep: {
      type: Number,
      default: 1,
      validator: val => +val >= 0 && +val <= 30
    },
    rules: Object,
    customFloatingUiOptions: {
      type: Object,
      default: () => ({})
    },
    mandatory: Boolean,
    popupButtonProps: {
      type: Object,
      default: () => ({
        'aria-label': 'Open datepicker'
      })
    }
  },
  data() {
    return {
      internalTimeFormat,
      popupVisible: false,
      inputFocus: false,
      buttonFocus: false,
      dateOne: this.initialDateOneValue(),
      dateTwo: this.initialDateTwoValue(),
      timeOne: this.initialTimeOneValue(),
      timeTwo: this.initialTimeTwoValue(),
      monthOne: null,
      monthTwo: null,
      yearOne: null,
      yearTwo: null,
      ranges: [],
      isPortalTargetElementMounted: false,
      datepickerTargetElement: null,
      cleanupFloatingUI: undefined,
      popupPlacement: 'right',
      popupPositionStyles: {},
      popupArrowPositionStyles: {},
      uuid: uuid()
    };
  },
  computed: {
    classes() {
      return {
        ...this.fieldGroupClasses,
        'bb-datepicker--disabled': this.disabled,
        'is-filled': this.hasValue,
        'bb-datepicker--no-calendar': this.type !== 'date' && this.type !== 'datetime'
      };
    },
    popupClasses() {
      return ['bb-datepicker__popup', {
        'bb-datepicker__popup--range': this.range,
        'bb-datepicker__popup--datetime': this.type === 'datetime',
        'has-both-dates-selected': this.dateOne && this.dateTwo
      }];
    },
    computedYearsSelectAmplitude() {
      return this.yearsForSelect ? this.yearsForSelect : this.yearsSelectAmplitude;
    },
    calendarColumnsXs() {
      return this.type === 'datetime' ? this.range ? 24 : 16 : 24;
    },
    datetimeColumnsXs() {
      const belowMd = !this.$screen.md;
      if (this.range) {
        return belowMd ? 24 : 5;
      }
      return 8;
    },
    calendarColumnsMd() {
      return this.type === 'datetime' ? this.range ? 19 : 16 : 24;
    },
    displayText() {
      if (!this.range && this.isValidDate(this.dateOne)) {
        return this.stringifyDate(this.computedVal);
      }
      if (this.range && this.isValidDate(this.dateOne) && this.isValidDate(this.dateTwo)) {
        return `${this.stringifyDate(this.computedVal[0])} – ${this.stringifyDate(this.computedVal[1])}`;
      }
      return '';
    },
    computedVal() {
      const timeFormatStringWithSpace = this.hasHoursMinutes(this.timeOne) || this.hasHoursMinutes(this.timeTwo) ? ` ${internalTimeFormat}` : '';
      const timeOneStringWithSpace = this.hasHoursMinutes(this.timeOne) ? ` ${this.timeOne.HH}:${this.timeOne.mm}` : '';
      const timeTwoStringWithSpace = this.hasHoursMinutes(this.timeTwo) ? ` ${this.timeTwo.HH}:${this.timeTwo.mm}` : '';
      const date1 = dayjs(`${this.dateOne}${timeOneStringWithSpace}`, `${internalDateFormat}${timeFormatStringWithSpace}`);
      const date2 = this.range ? dayjs(`${this.dateTwo}${timeTwoStringWithSpace}`, `${internalDateFormat}${timeFormatStringWithSpace}`) : null;
      const newVal = this.range ? date1.isValid() && date2.isValid() ? [date1.toDate(), date2.toDate()] : null : date1.isValid() ? date1.toDate() : null;
      return newVal;
    },
    minMaxDateIncludesTimeAndMaxTimeExceeded() {
      return this.minMaxDateIncludesTime && timeIsAfter(new Date(), this.maxTime);
    },
    minTimePlusOneDay() {
      return timeIsSame(this.minTime, DEFAULT_MIN_TIME) ? new Date(this.minDate.setHours(0, 0, 0, 0) + day) : new Date(this.minTime.getTime() + day);
    },
    maxTimePlusOneDay() {
      return new Date(this.maxTime.getTime() + day);
    },
    timeOneAsDateObject() {
      return timepickerToDateObject(this.timeOne);
    },
    timeTwoAsDateObject() {
      return timepickerToDateObject(this.timeTwo);
    },
    computedMinDate() {
      return this.minMaxDateIncludesTimeAndMaxTimeExceeded ? this.minTimePlusOneDay : this.minDate;
    },
    computedMinTimeOne() {
      return this.minMaxDateIncludesTimeAndMaxTimeExceeded ? this.getMinTime(dayjs(this.minTimePlusOneDay).format(internalDateFormat)) : this.getMinTime(this.dateOne);
    },
    computedMaxTimeOne() {
      return this.getMaxTime(this.dateOne);
    },
    computedMinTimeTwo() {
      return this.range && this.getMinTime(this.dateTwo);
    },
    computedMaxTimeTwo() {
      return this.range && this.getMaxTime(this.dateTwo);
    },
    translation() {
      if (isObject(this.lang)) {
        return Object.assign({}, languages.en, this.lang);
      }
      return languages[this.lang] || languages.en;
    },
    hasValue() {
      return !!this.displayText;
    },
    fieldPlaceholder() {
      return !this.isFramed && this.label ? '' : this.placeholder || (this.range ? this.translation.placeholder.dateRange : this.translation.placeholder.date);
    },
    yearSelectRange() {
      if (isString(this.computedYearsSelectAmplitude)) {
        return parseInt(this.computedYearsSelectAmplitude);
      }
      return this.computedYearsSelectAmplitude;
    },
    resetButtonVisible() {
      return this.hasValue;
    },
    childrenHaveFocus() {
      return this.inputFocus || this.buttonFocus || this.popupVisible;
    },
    dateOneObj() {
      return this.dateOne ? dayjs(this.dateOne).toDate() : null;
    },
    dateTwoObj() {
      return this.dateTwo ? dayjs(this.dateTwo).toDate() : null;
    },
    tooltipOffset() {
      return this.popupPlacement.includes('top') ? 0 : 8;
    },
    initialPlacement() {
      return this.$screen.md ? 'right' : 'bottom';
    },
    floatingUIOptions() {
      const floatingUIOptions = {
        placement: this.initialPlacement,
        middleware: [offset(this.tooltipOffset), flip({
          fallbackAxisSideDirection: 'end'
        }), shift({
          limiter: limitShift()
        }), arrow({
          element: this.$refs.popupArrow,
          padding: 6
        })]
      };
      return merge(floatingUIOptions, this.customFloatingUiOptions);
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `errorMessage-${this.computedId}`;
    },
    computedId() {
      return slugify(this.name) || this.uuid;
    },
    portalTargetElementId() {
      return `datepicker-portal-target-${this.computedId}`;
    }
  },
  watch: {
    computedVal(newVal) {
      if (!isEqual(newVal, this.value)) {
        this.emitValue(newVal);
      }
    },
    dateOne(newValue) {
      const date = dayjs(newValue);
      const minDate = dayjs(this.computedMinDate);
      const maxDate = dayjs(this.maxDate);
      if (!this.range && !this.minMaxDateIncludesTime) {
        if (this.minDate && date.isBefore(minDate)) {
          this.dateOne = minDate.format(internalDateFormat);
        }
        if (this.maxDate && date.isAfter(maxDate)) {
          this.dateOne = maxDate.format(internalDateFormat);
        }
      }
    },
    value() {
      this.updateModelFromValue();
    },
    popupVisible: 'handleFloatingUi',
    shortcuts() {
      this.initRanges();
    },
    lang() {
      this.initRanges();
    },
    focus() {
      if (this.focus) {
        this.showErrorBubble();
      } else {
        this.hideErrorBubble();
      }
    }
  },
  beforeDestroy() {
    var _this$cleanupFloating;
    (_this$cleanupFloating = this.cleanupFloatingUI) === null || _this$cleanupFloating === void 0 ? void 0 : _this$cleanupFloating.call(this);
    this.destroyPortalTargetElement();
  },
  beforeMount() {
    this.dateOne = this.minMaxDateIncludesTimeAndMaxTimeExceeded ? dayjs(this.maxTimePlusOneDay).format(internalDateFormat) : this.dateOne;
    this.initRanges();
    if ((this.type === 'date' || this.type === 'datetime') && !this.isPortalTargetElementMounted) {
      this.createPortalTargetElement();
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    emitValue(newVal) {
      this.$emit('input', newVal);
      this.$emit('change', newVal);
    },
    onDateOneClick(calendarDateString) {
      if (calendarDateString) {
        this.dateOne = dateObjectToInternalDateFormat(calendarDateString);
        const timeIsBeforeMinTime = timeIsBefore(this.timeOneAsDateObject, this.computedMinTimeOne);
        const timeIsAfterMaxTime = timeIsAfter(this.timeOneAsDateObject, this.computedMaxTimeOne);
        if (timeIsBeforeMinTime) {
          this.timeOne = dateObjectToTimepicker(this.computedMinTimeOne);
        } else if (timeIsAfterMaxTime) {
          this.timeOne = dateObjectToTimepicker(this.computedMaxTimeOne);
        }
      }
    },
    onDateTwoClick(calendarDateString) {
      if (this.range && calendarDateString) {
        this.dateTwo = dateObjectToInternalDateFormat(calendarDateString);
        const timeIsBeforeMinTime = timeIsBefore(this.timeTwoAsDateObject, this.computedMinTimeTwo);
        const timeIsAfterMaxTime = timeIsAfter(this.timeTwoAsDateObject, this.computedMaxTimeTwo);
        if (timeIsBeforeMinTime) {
          this.timeTwo = dateObjectToTimepicker(this.computedMinTimeTwo);
        } else if (timeIsAfterMaxTime) {
          this.timeTwo = dateObjectToTimepicker(this.computedMaxTimeTwo);
        }
      }
    },
    onMonthOne(monthString) {
      this.monthOne = languages[this.lang].months.indexOf(monthString);
      this.dateOne = dayjs(this.dateOne).set('month', this.monthOne).format(internalDateFormat);
    },
    onMonthTwo(monthString) {
      if (this.range && monthString) {
        this.monthTwo = languages[this.lang].months.indexOf(monthString);
        this.dateTwo = dayjs(this.dateTwo).set('month', this.monthTwo).format(internalDateFormat);
      }
    },
    onYearOne(yearString) {
      this.yearOne = +yearString;
      this.dateOne = dayjs(this.dateOne).set('year', this.yearOne).format(internalDateFormat);
    },
    onYearTwo(yearString) {
      if (this.range && yearString) {
        this.yearTwo = +yearString;
        this.dateTwo = dayjs(this.dateTwo).set('year', this.yearTwo).format(internalDateFormat);
      }
    },
    initialDateOneValue() {
      const pickersWithoutCalendars = ['time', 'month', 'year', 'monthYear'];
      return pickersWithoutCalendars.includes(this.type) ? unixEpochFormattedDate() : this.range ? this.value && this.value[0] ? dateObjectToInternalDateFormat(this.value[0]) : null : this.value ? dateObjectToInternalDateFormat(this.value) : null;
    },
    initialDateTwoValue() {
      const pickersWithoutCalendars = ['time', 'month', 'year', 'monthYear'];
      return this.range ? pickersWithoutCalendars.includes(this.type) ? unixEpochFormattedDate() : this.value && this.value[1] ? dateObjectToInternalDateFormat(this.value[1]) : null : null;
    },
    initialTimeOneValue() {
      return this.range ? this.value && this.value[0] ? dateObjectToTimepicker(this.value[0]) : zeroHour : this.value ? dateObjectToTimepicker(this.value) : zeroHour;
    },
    initialTimeTwoValue() {
      return this.range ? this.value && this.value[1] ? dateObjectToTimepicker(this.value[1]) : zeroHour : zeroHour;
    },
    hasHoursMinutes(timeObject) {
      return timeObject && timeObject.HH && timeObject.mm;
    },
    updateModelFromValue() {
      if (!this.range) {
        this.dateOne = this.value ? dateObjectToInternalDateFormat(this.value) : null;
        const time = timeIsBefore(this.value, this.computedMinTimeOne) ? this.computedMinTimeOne : timeIsAfter(this.value, this.computedMaxTimeOne) ? this.computedMaxTimeOne : this.value;
        this.timeOne = time ? dateObjectToTimepicker(time) : zeroHour;
      } else {
        this.dateOne = this.value && this.value[0] ? dateObjectToInternalDateFormat(this.value[0]) : null;
        this.timeOne = this.value && this.value[0] ? dateObjectToTimepicker(this.value[0]) : zeroHour;
        this.dateTwo = this.value && this.value[0] ? dateObjectToInternalDateFormat(this.value[1]) : null;
        this.timeTwo = this.value && this.value[0] ? dateObjectToTimepicker(this.value[1]) : zeroHour;
      }
    },
    parseDateFromDisplayText(inputString) {
      const inputDate = dayjs(inputString, this.format);
      if (!this.range && inputDate.isValid()) {
        this.dateOne = inputDate.format(internalDateFormat);
      }
    },
    onInternalInputEnterKey(event) {
      this.parseDateFromDisplayText(event.target.value);
    },
    onInternalInputFocus() {
      this.inputFocus = true;
      this.onFocus();
    },
    onInternalInputBlur(event) {
      this.inputFocus = false;
      this.parseDateFromDisplayText(event.target.value);
      this.onBlurAndValidate();
    },
    onInternalInputClick() {
      this.openPopup();
    },
    onInternalInputChange(event) {
      if (event.target.value === '') {
        this.resetValues();
      }
    },
    onButtonFocus() {
      this.buttonFocus = true;
      this.onFocus();
    },
    onButtonBlur() {
      this.buttonFocus = false;
      this.onBlur();
    },
    onButtonClick() {
      if (this.disabled) {
        return;
      }
      if (this.resetButtonVisible) {
        this.resetValues();
      } else {
        this.openPopup();
      }
    },
    onBlur() {
      setTimeout(() => {
        if (!this.childrenHaveFocus && this.focus) {
          this.focus = false;
          this.$emit('blur');
        }
      }, 0);
    },
    onFocus() {
      setTimeout(() => {
        if (this.childrenHaveFocus && !this.focus) {
          this.focus = true;
          this.$emit('focus');
        }
      }, 0);
    },
    confirmDate() {
      this.closePopup();
      this.$emit('confirm', this.computedVal);
    },
    resetValues() {
      this.dateOne = null;
      this.dateTwo = null;
      this.timeOne = zeroHour;
      this.timeTwo = zeroHour;
      this.$emit('reset');
      this.emitValue(null);
    },
    stringifyDate(date) {
      if (typeof this.customFormatter === 'function') {
        return this.customFormatter(new Date(date));
      }
      return dayjs(date).format(this.format);
    },
    isValidDate(date) {
      return dayjs(date).isValid();
    },
    selectShortcut(shortcut) {
      if (!this.range) {
        this.dateOne = dayjs(shortcut.end).format(internalDateFormat);
      } else {
        this.dateOne = dayjs(shortcut.start).format(internalDateFormat);
        this.dateTwo = dayjs(shortcut.end).format(internalDateFormat);
      }
    },
    initRanges() {
      if (isArray(this.shortcuts)) {
        this.ranges = this.shortcuts;
      } else if (this.shortcuts) {
        this.ranges = defaultShortcuts;
        this.ranges.forEach((v, i) => {
          v.text = this.translation.pickers[i];
        });
      }
    },
    handleFloatingUi() {
      var _this$cleanupFloating2;
      (_this$cleanupFloating2 = this.cleanupFloatingUI) === null || _this$cleanupFloating2 === void 0 ? void 0 : _this$cleanupFloating2.call(this);
      this.cleanupFloatingUI = this.popupVisible ? this.autoPositionFloatingElement() : undefined;
    },
    async updatePosition() {
      const computedPosition = await computePosition(this.$refs.root, this.$refs.calendar, this.floatingUIOptions);
      const {
        x,
        y,
        placement,
        middlewareData: {
          arrow: {
            x: arrowX,
            y: arrowY
          }
        }
      } = computedPosition;
      this.popupPositionStyles = {
        left: `${x}px`,
        top: `${y}px`
      };
      this.popupArrowPositionStyles = {
        left: isDefined(arrowX) ? `${arrowX}px` : '',
        top: isDefined(arrowY) ? `${arrowY}px` : ''
      };
      this.popupPlacement = placement;
    },
    autoPositionFloatingElement() {
      return autoUpdate(this.$refs.root, this.$refs.calendar, this.updatePosition);
    },
    createPortalTargetElement() {
      const portalTargetDiv = document.createElement('div');
      portalTargetDiv.setAttribute('id', this.portalTargetElementId);
      document.body.appendChild(portalTargetDiv);
      this.isPortalTargetElementMounted = true;
    },
    destroyPortalTargetElement() {
      const portalTargetEl = document.querySelector(`#${this.portalTargetElementId}`);
      if (!portalTargetEl) return;
      const isPortalTargetElInsidePortal = portalTargetEl.parentElement.className === 'vue-portal-target';
      isPortalTargetElInsidePortal ? portalTargetEl.parentElement.remove() : portalTargetEl.remove();
    },
    openPopup() {
      this.popupVisible = true;
      this.$refs.calendar.focus();
    },
    onKeydownEsc() {
      var _this$$refs$input;
      this.closePopup();
      (_this$$refs$input = this.$refs.input) === null || _this$$refs$input === void 0 ? void 0 : _this$$refs$input.focus();
    },
    closePopup() {
      this.popupVisible = false;
      this.onBlur();
    },
    onClickOutside() {
      this.inputFocus = false;
      this.buttonFocus = false;
      this.closePopup();
    },
    getMinTime(date) {
      const parsedDate = dayjs(date, internalDateFormat);
      return this.minMaxDateIncludesTime && this.minDate && dayjs(this.computedMinDate).isSame(parsedDate, 'day') && (timeIsSame(this.minTime, DEFAULT_MIN_TIME) || timeIsSame(this.minTime, this.computedMinDate) || timeIsBefore(this.minTime, this.computedMinDate)) ? this.computedMinDate : this.minTime;
    },
    getMaxTime(date) {
      const parsedDate = dayjs(date, internalDateFormat);
      return this.minMaxDateIncludesTime && this.maxDate && dayjs(this.maxDate).isSame(parsedDate, 'day') && (timeIsSame(this.maxTime, DEFAULT_MAX_TIME) || timeIsSame(this.maxTime, this.maxDate) || timeIsAfter(this.maxTime, this.maxDate)) ? this.maxDate : this.maxTime;
    }
  }
};

/* script */
const __vue_script__$1o = script$1o;

/* template */
var __vue_render__$1m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-datepicker field-group",
    class: _vm.classes,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "computedVal"
    }
  }, [_vm.label ? _c('label', {
    staticClass: "bb-datepicker__label",
    attrs: {
      "for": _vm.computedId
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-datepicker__mandatory-indicator",
      attrs: {
        "data-testid": "bb-datepicker__mandatory-indicator"
      }
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _vm.type === 'date' || _vm.type === 'datetime' ? [_c('div', {
    staticClass: "input-group"
  }, [_c('input', _vm._b({
    ref: "input",
    staticClass: "bb-datepicker__input",
    attrs: {
      "id": _vm.computedId,
      "name": _vm.name,
      "disabled": _vm.disabled,
      "readonly": !_vm.editable || _vm.range || _vm.readonly,
      "placeholder": _vm.fieldPlaceholder,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false'
    },
    domProps: {
      "value": _vm.displayText
    },
    on: {
      "click": _vm.onInternalInputClick,
      "focus": _vm.onInternalInputFocus,
      "change": _vm.onInternalInputChange,
      "blur": _vm.onInternalInputBlur,
      "keyup": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.onInternalInputEnterKey.apply(null, arguments);
      },
      "mousedown": function ($event) {
        $event.stopPropagation();
      },
      "mouseover": _vm.showErrorBubble,
      "mouseout": _vm.hideErrorBubble
    }
  }, 'input', _vm.inputAttrs, false)), _vm._v(" "), _vm.hasValidationError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.input,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', [_vm._v(_vm._s(_vm.firstError || _vm.getFirstError()))])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n          " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n        ")]) : _vm._e()] : _vm._e(), _vm._v(" "), !_vm.readonly && !_vm.disabled ? _c('bb-button', _vm._b({
    staticClass: "bb-datepicker__button",
    attrs: {
      "icon": _vm.resetButtonVisible ? 'ui-close-circle' : 'ui-calendar',
      "aria-haspopup": "dialog",
      "no-background": "",
      "color": "gray",
      "icon-size": "18",
      "icon-color": "gray-50",
      "size": "sm",
      "display": "square"
    },
    on: {
      "click": _vm.onButtonClick,
      "focus": _vm.onButtonFocus,
      "blur": _vm.onButtonBlur
    }
  }, 'bb-button', _vm.popupButtonProps, false)) : _vm._e()], 2), _vm._v(" "), _c('client-only', [_c('mounting-portal', {
    attrs: {
      "mount-to": "#" + _vm.portalTargetElementId,
      "name": _vm.portalTargetElementId,
      "to": _vm.portalTargetElementId
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.popupVisible,
      expression: "popupVisible"
    }, {
      name: "click-outside",
      rawName: "v-click-outside:root",
      value: _vm.onClickOutside,
      expression: "onClickOutside",
      arg: "root"
    }],
    ref: "calendar",
    class: _vm.popupClasses,
    style: _vm.popupPositionStyles,
    attrs: {
      "id": _vm.portalTargetElementId,
      "data-floating-placement": _vm.popupPlacement,
      "role": "dialog",
      "aria-modal": "true",
      "tabindex": "-1"
    },
    on: {
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }
        return _vm.onKeydownEsc.apply(null, arguments);
      }
    }
  }, [_c('div', {
    ref: "popupArrow",
    staticClass: "bb-datepicker__arrow",
    style: _vm.popupArrowPositionStyles
  }, [_c('div', {
    staticClass: "bb-datepicker__arrow-inner"
  })]), _vm._v(" "), _c('bb-row', {
    attrs: {
      "no-gutters": ""
    }
  }, [_c('bb-col', {
    attrs: {
      "col": _vm.calendarColumnsXs,
      "md": _vm.calendarColumnsMd
    }
  }, [_c('bb-calendar', {
    attrs: {
      "language": _vm.lang,
      "range": _vm.range,
      "min-date": _vm.computedMinDate,
      "max-date": _vm.maxDate,
      "enabled-dates": _vm.enabledDates,
      "disabled-dates": _vm.disabledDates,
      "years-select-amplitude": _vm.yearSelectRange,
      "date-one": _vm.dateOneObj,
      "date-two": _vm.range ? _vm.dateTwoObj : null
    },
    on: {
      "dateOneSelected": _vm.onDateOneClick,
      "dateTwoSelected": _vm.onDateTwoClick
    }
  })], 1), _vm._v(" "), _vm.type === 'datetime' ? _c('bb-col', {
    staticClass: "d-flex flex-column",
    attrs: {
      "col": _vm.datetimeColumnsXs
    }
  }, [_c('div', {
    staticClass: "p-10 p-s-20"
  }, [_c('bb-input', {
    attrs: {
      "value": _vm.dateOne || 'From',
      "name": _vm.name + "-time-from",
      "small": "",
      "readonly": "",
      "tabindex": "-1"
    }
  }), _vm._v(" "), _c('bb-timepicker', {
    attrs: {
      "format": _vm.internalTimeFormat,
      "minute-step": _vm.minuteStep,
      "min-time": _vm.computedMinTimeOne,
      "max-time": _vm.computedMaxTimeOne
    },
    model: {
      value: _vm.timeOne,
      callback: function ($$v) {
        _vm.timeOne = $$v;
      },
      expression: "timeOne"
    }
  }), _vm._v(" "), _vm.type === 'datetime' && _vm.range ? [_c('bb-input', {
    attrs: {
      "value": _vm.dateTwo || 'To',
      "name": _vm.name + "-time-to",
      "small": "",
      "readonly": "",
      "tabindex": "-1"
    }
  }), _vm._v(" "), _c('bb-timepicker', {
    attrs: {
      "format": _vm.internalTimeFormat,
      "minute-step": _vm.minuteStep,
      "min-time": _vm.computedMinTimeTwo,
      "max-time": _vm.computedMaxTimeTwo
    },
    model: {
      value: _vm.timeTwo,
      callback: function ($$v) {
        _vm.timeTwo = $$v;
      },
      expression: "timeTwo"
    }
  })] : _vm._e()], 2)]) : _vm._e()], 1), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-datepicker__footer",
    attrs: {
      "no-gutters": ""
    }
  }, [_vm.ranges.length ? _c('bb-col', {
    attrs: {
      "col": "18"
    }
  }, _vm._l(_vm.ranges, function (range2, i) {
    return _c('span', {
      key: i,
      staticClass: "bb-datepicker__shortcut",
      attrs: {
        "role": "button",
        "tabindex": "1"
      },
      on: {
        "click": function ($event) {
          return _vm.selectShortcut(range2);
        }
      }
    }, [_vm._v("\n                " + _vm._s(range2.text) + "\n              ")]);
  }), 0) : _vm._e(), _vm._v(" "), _c('bb-col', {
    staticClass: "d-flex justify-content-end"
  }, [_c('bb-button', {
    attrs: {
      "label": _vm.confirmText,
      "size": "sm",
      "color": "blue"
    },
    on: {
      "click": _vm.confirmDate
    }
  })], 1)], 1)], 1)])], 1)] : [_c('div', {
    staticClass: "bb-datepicker__single-picker-container"
  }, [_vm.type === 'time' ? [_c('bb-timepicker', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "format": _vm.internalTimeFormat,
      "minute-step": _vm.minuteStep,
      "min-time": _vm.computedMinTimeOne,
      "max-time": _vm.computedMaxTimeOne
    },
    model: {
      value: _vm.timeOne,
      callback: function ($$v) {
        _vm.timeOne = $$v;
      },
      expression: "timeOne"
    }
  }), _vm._v(" "), _vm.range ? [_c('span', {
    staticClass: "bb-datepicker__single-picker-range-separator"
  }, [_vm._v("–")]), _vm._v(" "), _c('bb-timepicker', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "format": _vm.internalTimeFormat,
      "minute-step": _vm.minuteStep,
      "min-time": _vm.computedMinTimeTwo,
      "max-time": _vm.computedMaxTimeTwo,
      "hide-icon": ""
    },
    model: {
      value: _vm.timeTwo,
      callback: function ($$v) {
        _vm.timeTwo = $$v;
      },
      expression: "timeTwo"
    }
  })] : _vm._e()] : _vm._e(), _vm._v(" "), _vm.type === 'month' ? [_c('bb-calendar', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "min-date": _vm.computedMinDate,
      "max-date": _vm.maxDate,
      "language": _vm.lang,
      "display": "monthpicker"
    },
    on: {
      "monthChange": _vm.onMonthOne
    }
  }), _vm._v(" "), _vm.range ? [_c('span', {
    staticClass: "bb-datepicker__single-picker-range-separator"
  }, [_vm._v("–")]), _vm._v(" "), _c('bb-calendar', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "language": _vm.lang,
      "hide-icon": "",
      "display": "monthpicker"
    },
    on: {
      "monthChange": _vm.onMonthTwo
    }
  })] : _vm._e()] : _vm._e(), _vm._v(" "), _vm.type === 'year' ? [_c('bb-calendar', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "min-date": _vm.computedMinDate,
      "max-date": _vm.maxDate,
      "display": "yearpicker"
    },
    on: {
      "yearChange": _vm.onYearOne
    }
  }), _vm._v(" "), _vm.range ? [_c('span', {
    staticClass: "bb-datepicker__single-picker-range-separator"
  }, [_vm._v("–")]), _vm._v(" "), _c('bb-calendar', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "min-date": _vm.computedMinDate,
      "max-date": _vm.maxDate,
      "hide-icon": "",
      "display": "yearpicker"
    },
    on: {
      "yearChange": _vm.onYearTwo
    }
  })] : _vm._e()] : _vm._e(), _vm._v(" "), _vm.type === 'monthYear' ? [_c('div', {
    staticClass: "bb-datepicker__month-year-picker"
  }, [_c('bb-calendar', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "min-date": _vm.computedMinDate,
      "max-date": _vm.maxDate,
      "language": _vm.lang,
      "display": "monthyearpicker"
    },
    on: {
      "monthChange": _vm.onMonthOne,
      "yearChange": _vm.onYearOne
    }
  }), _vm._v(" "), _vm.range ? [_c('span', {
    staticClass: "bb-datepicker__single-picker-range-separator"
  }, [_vm._v("–")]), _vm._v(" "), _c('bb-calendar', {
    staticClass: "bb-datepicker__single-picker",
    attrs: {
      "language": _vm.lang,
      "min-date": _vm.computedMinDate,
      "max-date": _vm.maxDate,
      "hide-icon": "",
      "display": "monthyearpicker"
    },
    on: {
      "monthChange": _vm.onMonthTwo,
      "yearChange": _vm.onYearTwo
    }
  })] : _vm._e()], 2)] : _vm._e()], 2)]], 2);
};
var __vue_staticRenderFns__$1m = [];

/* style */
const __vue_inject_styles__$1o = function (inject) {
  if (!inject) return;
  inject("data-v-e2cc3298_0", {
    source: ".bb-datepicker{position:relative}.bb-datepicker--disabled{cursor:not-allowed;opacity:.7}.bb-datepicker--no-calendar label.bb-datepicker__label{margin-top:.25rem;margin-bottom:0;cursor:default;transform:scale(.875)}.bb-datepicker--no-calendar.bb-datepicker{padding-top:.625rem}.bb-datepicker__button.bb-datepicker__button.bb-datepicker__button{position:absolute;top:0;right:0;outline:0}.bb-datepicker__button.bb-datepicker__button.bb-datepicker__button path{fill:#9dabb9}.bb-datepicker:not(.framed-input) .bb-datepicker__button.bb-datepicker__button.bb-datepicker__button{top:-.125rem;right:0}.framed-input:not(.framed-input--small) .bb-datepicker__button.bb-datepicker__button.bb-datepicker__button{top:.3125rem;right:.3125rem}.bb-datepicker__popup{position:absolute;z-index:1600;width:100%;max-width:18.75rem;border:.0625rem solid #eeeff2;border-radius:.1875rem;background:#fff;box-shadow:0 .0625rem .25rem 0 rgba(0,0,0,.2);color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}[data-floating-placement^=top] .bb-datepicker__popup{margin-bottom:.625rem}[data-floating-placement^=right] .bb-datepicker__popup{margin-left:.625rem}[data-floating-placement^=bottom] .bb-datepicker__popup{margin-top:.625rem}[data-floating-placement^=left] .bb-datepicker__popup{margin-right:.625rem}.bb-datepicker__popup a,.bb-datepicker__popup a:hover{box-shadow:none}.bb-datepicker__popup--datetime{display:inline-block;min-width:28.125rem}@media (min-width:768px){.bb-datepicker__popup--range{min-width:37.5rem}}.bb-datepicker__popup--range.bb-datepicker__popup--datetime{min-width:18.75rem}@media (min-width:768px){.bb-datepicker__popup--range.bb-datepicker__popup--datetime{min-width:46.875rem}}.bb-datepicker__arrow{position:absolute;width:0;height:0;border:.5625rem solid transparent}[data-floating-placement^=top] .bb-datepicker__arrow{bottom:-1.125rem;border-top-color:#eeeff2}[data-floating-placement^=right] .bb-datepicker__arrow{left:-1.125rem;border-right-color:#eeeff2}[data-floating-placement^=bottom] .bb-datepicker__arrow{top:-1.125rem;border-bottom-color:#eeeff2}[data-floating-placement^=left] .bb-datepicker__arrow{right:-1.125rem;border-left-color:#eeeff2}.bb-datepicker__arrow-inner{position:relative;width:0;height:0;border:.4375rem solid transparent}[data-floating-placement^=top] .bb-datepicker__arrow-inner{top:-.5625rem;left:-.4375rem;border-top-color:#fff}[data-floating-placement^=right] .bb-datepicker__arrow-inner{top:-.4375rem;left:-.3125rem;border-right-color:#fff}[data-floating-placement^=bottom] .bb-datepicker__arrow-inner{top:-.3125rem;left:-.4375rem;border-bottom-color:#fff}[data-floating-placement^=left] .bb-datepicker__arrow-inner{top:-.4375rem;left:-.5625rem;border-left-color:#fff}.bb-datepicker__single-picker-container{display:flex;align-items:center}.bb-datepicker__single-picker{display:inline-block}.bb-datepicker__single-picker-range-separator{margin:0 .375rem 0 .625rem;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;user-select:none}.bb-datepicker__month-year-picker{display:flex}.bb-datepicker__footer{padding:.625rem .9375rem;border-top:.0625rem solid #eeeff2}.bb-datepicker__shortcut{color:#2485e5;cursor:pointer;font-family:GothamSSm-Black,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:11px}.bb-datepicker__shortcut:hover{color:#5ea6ed}.bb-datepicker__shortcut+.bb-datepicker__shortcut{margin-left:.9375rem}.bb-datepicker__input::placeholder{color:#9dabb9;opacity:1}.bb-datepicker.input--readonly{pointer-events:none}.bb-datepicker__mandatory-indicator{color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1o = undefined;
/* module identifier */
const __vue_module_identifier__$1o = undefined;
/* functional template */
const __vue_is_functional_template__$1o = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1o = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1m,
  staticRenderFns: __vue_staticRenderFns__$1m
}, __vue_inject_styles__$1o, __vue_script__$1o, __vue_scope_id__$1o, __vue_is_functional_template__$1o, __vue_module_identifier__$1o, false, createInjector, undefined, undefined);

var script$1n = {
  name: 'bb-detailed-radio-item',
  components: {
    BbTicketStub: __vue_component__$1R,
    BbListGroup: __vue_component__$2b,
    BbListItem: __vue_component__$29,
    CollapseTransition
  },
  props: {
    inputAttrs: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: [Boolean, String, Number, Object],
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    description: String,
    special: {
      type: Object,
      default() {
        return {};
      }
    },
    label: {
      type: Object,
      default() {
        return {};
      }
    },
    leftColumn: {
      type: Object,
      default() {
        return {};
      }
    },
    rightColumn: {
      type: Object,
      default() {
        return {};
      }
    },
    extra: {
      type: Object,
      default() {
        return {};
      }
    },
    selected: Boolean,
    singleOption: Boolean,
    disabled: Boolean
  },
  emits: ['click'],
  computed: {
    classes() {
      return {
        'bb-detailed-radio-item--disabled': this.disabled,
        'bb-detailed-radio-item--special': this.special.enabled,
        'bb-detailed-radio-item--label': this.label.value,
        [`bb-detailed-radio-item--label-${this.computedLabelColor}`]: this.label.value,
        'bb-detailed-radio-item--single-row': this.hasNoSublabelsInLeftColumn,
        'bb-detailed-radio-item--single-option': this.singleOption
      };
    },
    hasNoSublabelsInLeftColumn() {
      return this.leftColumn && !this.leftColumn.sublabel && !this.leftColumn.sublabelSmall;
    },
    hasRightColumn() {
      return this.rightColumn.label || this.rightColumn.amount;
    },
    computedLabelColor() {
      return this.label.color || 'mint';
    },
    defaultAriaLabel() {
      var _this$special, _this$label, _this$leftColumn, _this$leftColumn2, _this$leftColumn3, _this$rightColumn, _this$rightColumn2, _this$extra, _this$extra2, _this$extra2$ticket, _this$extra2$ticket$l, _this$extra3, _this$extra3$ticket, _this$extra3$ticket$l, _this$extra4, _this$extra4$ticket, _this$extra4$ticket$r, _this$extra5, _this$extra5$ticket, _this$extra5$ticket$r;
      const labelParts = [this.description, (_this$special = this.special) === null || _this$special === void 0 ? void 0 : _this$special.label, (_this$label = this.label) === null || _this$label === void 0 ? void 0 : _this$label.value, (_this$leftColumn = this.leftColumn) === null || _this$leftColumn === void 0 ? void 0 : _this$leftColumn.label, (_this$leftColumn2 = this.leftColumn) === null || _this$leftColumn2 === void 0 ? void 0 : _this$leftColumn2.sublabel, (_this$leftColumn3 = this.leftColumn) === null || _this$leftColumn3 === void 0 ? void 0 : _this$leftColumn3.sublabelSmall, (_this$rightColumn = this.rightColumn) === null || _this$rightColumn === void 0 ? void 0 : _this$rightColumn.label, (_this$rightColumn2 = this.rightColumn) === null || _this$rightColumn2 === void 0 ? void 0 : _this$rightColumn2.amount, this.selected && ((_this$extra = this.extra) === null || _this$extra === void 0 ? void 0 : _this$extra.description), this.selected && ((_this$extra2 = this.extra) === null || _this$extra2 === void 0 ? void 0 : (_this$extra2$ticket = _this$extra2.ticket) === null || _this$extra2$ticket === void 0 ? void 0 : (_this$extra2$ticket$l = _this$extra2$ticket.left) === null || _this$extra2$ticket$l === void 0 ? void 0 : _this$extra2$ticket$l.label), this.selected && ((_this$extra3 = this.extra) === null || _this$extra3 === void 0 ? void 0 : (_this$extra3$ticket = _this$extra3.ticket) === null || _this$extra3$ticket === void 0 ? void 0 : (_this$extra3$ticket$l = _this$extra3$ticket.left) === null || _this$extra3$ticket$l === void 0 ? void 0 : _this$extra3$ticket$l.content), this.selected && ((_this$extra4 = this.extra) === null || _this$extra4 === void 0 ? void 0 : (_this$extra4$ticket = _this$extra4.ticket) === null || _this$extra4$ticket === void 0 ? void 0 : (_this$extra4$ticket$r = _this$extra4$ticket.right) === null || _this$extra4$ticket$r === void 0 ? void 0 : _this$extra4$ticket$r.label), this.selected && ((_this$extra5 = this.extra) === null || _this$extra5 === void 0 ? void 0 : (_this$extra5$ticket = _this$extra5.ticket) === null || _this$extra5$ticket === void 0 ? void 0 : (_this$extra5$ticket$r = _this$extra5$ticket.right) === null || _this$extra5$ticket$r === void 0 ? void 0 : _this$extra5$ticket$r.content)];
      return labelParts.filter(Boolean).join(', ');
    },
    isContentHidden() {
      var _this$extra6;
      return !((_this$extra6 = this.extra) !== null && _this$extra6 !== void 0 && _this$extra6.list);
    }
  },
  methods: {
    onClick() {
      this.$emit('click', this.value);
    },
    onFocus() {
      setTimeout(this.onClick, 50);
    }
  }
};

/* script */
const __vue_script__$1n = script$1n;

/* template */
var __vue_render__$1l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('label', {
    staticClass: "bb-detailed-radio-item",
    class: _vm.classes
  }, [_vm.description ? _c('p', {
    staticClass: "bb-detailed-radio-item__description",
    attrs: {
      "aria-hidden": _vm.isContentHidden
    }
  }, [_vm._v("\n    " + _vm._s(_vm.description) + "\n  ")]) : _vm._e(), _vm._v(" "), _c('input', _vm._b({
    staticClass: "bb-detailed-radio-item__input visually-hidden",
    attrs: {
      "name": _vm.name,
      "disabled": _vm.disabled,
      "type": "radio",
      "aria-label": _vm.defaultAriaLabel
    },
    domProps: {
      "value": _vm.value,
      "checked": _vm.selected
    },
    on: {
      "focus": _vm.onFocus,
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
          return null;
        }
        $event.preventDefault();
        return _vm.onClick.apply(null, arguments);
      },
      "click": _vm.onClick
    }
  }, 'input', _vm.inputAttrs, false)), _vm._v(" "), _c('div', {
    staticClass: "bb-detailed-radio-item__content-wrapper",
    attrs: {
      "aria-hidden": _vm.isContentHidden
    }
  }, [_c('div', {
    staticClass: "bb-detailed-radio-item__main-content"
  }, [_vm.special.enabled ? _c('div', {
    staticClass: "bb-detailed-radio-item__special-label"
  }, [_vm._v("\n        " + _vm._s(_vm.special.label) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.label.value ? _c('div', {
    staticClass: "bb-detailed-radio-item__label"
  }, [_vm._v("\n        " + _vm._s(_vm.label.value) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.singleOption ? [_c('div', {
    staticClass: "bb-detailed-radio-item__left-column"
  }, [_vm.leftColumn.label ? _c('div', {
    staticClass: "bb-detailed-radio-item__left-column-label"
  }, [_vm._v("\n            " + _vm._s(_vm.leftColumn.label) + "\n          ")]) : _vm._e(), _vm._v(" "), _vm.leftColumn.sublabel ? _c('div', {
    staticClass: "bb-detailed-radio-item__left-column-sublabel"
  }, [_vm._v("\n            " + _vm._s(_vm.leftColumn.sublabel) + "\n          ")]) : _vm._e()])] : [_c('div', {
    staticClass: "bb-detailed-radio-item__circle"
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-detailed-radio-item__columns"
  }, [_c('div', {
    staticClass: "bb-detailed-radio-item__left-column"
  }, [_vm.leftColumn.label ? _c('div', {
    staticClass: "bb-detailed-radio-item__left-column-label"
  }, [_vm._v("\n              " + _vm._s(_vm.leftColumn.label) + "\n            ")]) : _vm._e(), _vm._v(" "), _vm.leftColumn.sublabel ? _c('div', {
    staticClass: "bb-detailed-radio-item__left-column-sublabel"
  }, [_vm._v("\n              " + _vm._s(_vm.leftColumn.sublabel) + "\n            ")]) : _vm._e(), _vm._v(" "), _vm.leftColumn.sublabelSmall ? _c('div', {
    staticClass: "bb-detailed-radio-item__left-column-sublabel-small"
  }, [_vm._v("\n              " + _vm._s(_vm.leftColumn.sublabelSmall) + "\n            ")]) : _vm._e()]), _vm._v(" "), _vm.hasRightColumn ? _c('div', {
    staticClass: "bb-detailed-radio-item__right-column"
  }, [_vm.rightColumn.label ? _c('div', {
    staticClass: "bb-detailed-radio-item__right-column-label"
  }, [_vm._v("\n              " + _vm._s(_vm.rightColumn.label) + "\n            ")]) : _vm._e(), _vm._v(" "), _vm.rightColumn.amount ? _c('div', {
    staticClass: "bb-detailed-radio-item__right-column-amount"
  }, [_vm._v("\n              " + _vm._s(_vm.rightColumn.amount) + "\n            ")]) : _vm._e()]) : _vm._e()])]], 2), _vm._v(" "), _vm.extra && (_vm.extra.ticket || _vm.extra.description || _vm.extra.list) ? _c('collapse-transition', [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.selected,
      expression: "selected"
    }],
    staticClass: "bb-detailed-radio-item__extra-content"
  }, [_vm.extra.ticket ? _c('bb-ticket-stub', {
    scopedSlots: _vm._u([{
      key: "left",
      fn: function () {
        return [_c('div', {
          staticClass: "bb-detailed-radio-item__ticket-column"
        }, [_c('p', {
          staticClass: "bb-detailed-radio-item__ticket-label"
        }, [_vm._v("\n                " + _vm._s(_vm.extra && _vm.extra.ticket.left.label) + "\n              ")]), _vm._v(" "), _c('p', {
          staticClass: "bb-detailed-radio-item__ticket-content"
        }, [_vm._v("\n                " + _vm._s(_vm.extra && _vm.extra.ticket.left.content) + "\n              ")])])];
      },
      proxy: true
    }], null, false, 860350922)
  }, [_vm._v(" "), _c('div', {
    staticClass: "bb-detailed-radio-item__ticket-column"
  }, [_c('p', {
    staticClass: "bb-detailed-radio-item__ticket-label"
  }, [_vm._v("\n              " + _vm._s(_vm.extra && _vm.extra.ticket.right.label) + "\n            ")]), _vm._v(" "), _c('p', {
    staticClass: "bb-detailed-radio-item__ticket-content"
  }, [_vm._v("\n              " + _vm._s(_vm.extra && _vm.extra.ticket.right.content) + "\n            ")])])]) : _vm._e(), _vm._v(" "), _vm.extra.description ? _c('p', {
    staticClass: "bb-detailed-radio-item__extra-description"
  }, [_vm._t("extraDescription", function () {
    return [_vm._v("\n            " + _vm._s(_vm.extra.description) + "\n          ")];
  }, null, Object.assign({}, _vm.extra))], 2) : _vm._e(), _vm._v(" "), _vm.extra.list ? _c('bb-list-group', {
    staticClass: "bb-detailed-radio-item__extra-list"
  }, _vm._l(_vm.extra.list, function (item, index) {
    return _c('bb-list-item', _vm._b({
      key: index,
      attrs: {
        "title": item.title
      }
    }, 'bb-list-item', item.props, false), [item.content ? [_vm._v("\n              " + _vm._s(item.content) + "\n            ")] : _vm._e()], 2);
  }), 1) : _vm._e()], 1)]) : _vm._e()], 1)]);
};
var __vue_staticRenderFns__$1l = [];

/* style */
const __vue_inject_styles__$1n = function (inject) {
  if (!inject) return;
  inject("data-v-963ca850_0", {
    source: ".bb-detailed-radio-item{display:block;cursor:pointer}.bb-detailed-radio-item--disabled{cursor:not-allowed}.bb-detailed-radio-item:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option):hover .bb-detailed-radio-item__content-wrapper{border-color:#eeeff2}.bb-detailed-radio-item:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option):hover.bb-detailed-radio-item--special .bb-detailed-radio-item__content-wrapper{border-color:#3dd6b5}.bb-detailed-radio-item:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option):hover.bb-detailed-radio-item--special .bb-detailed-radio-item__special-label{background:#3dd6b5}.bb-detailed-radio-item--single-option{cursor:default}.bb-detailed-radio-item--single-option .bb-ticket-stub__segment{justify-content:flex-start}.bb-detailed-radio-item__input:focus+.bb-detailed-radio-item__content-wrapper{border-color:#eeeff2}.bb-detailed-radio-item--special .bb-detailed-radio-item__input:focus+.bb-detailed-radio-item__content-wrapper{border-color:#3dd6b5}.bb-detailed-radio-item--single-option .bb-detailed-radio-item__input:focus+.bb-detailed-radio-item__content-wrapper{border-color:#0ccca2}.bb-detailed-radio-item__input:focus+.bb-detailed-radio-item__content-wrapper .bb-detailed-radio-item__special-label{background:#3dd6b5}.bb-detailed-radio-item--single-option .bb-detailed-radio-item__input:focus+.bb-detailed-radio-item__content-wrapper .bb-detailed-radio-item__special-label{background:#0ccca2}.bb-detailed-radio-item:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option) .bb-detailed-radio-item__input:checked+.bb-detailed-radio-item__content-wrapper{border-color:#2485e5;background:rgba(230,243,255,.5)}.bb-detailed-radio-item:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option):hover .bb-detailed-radio-item__input:checked+.bb-detailed-radio-item__content-wrapper{border-color:#5ea6ed}.bb-detailed-radio-item--special:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option) .bb-detailed-radio-item__input:checked+.bb-detailed-radio-item__content-wrapper{border-color:#0ccca2;background:rgba(231,250,246,.4)}.bb-detailed-radio-item--special:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option):hover .bb-detailed-radio-item__input:checked+.bb-detailed-radio-item__content-wrapper{border-color:#3dd6b5}.bb-detailed-radio-item--disabled .bb-detailed-radio-item__input:checked+.bb-detailed-radio-item__content-wrapper{border-color:#dee3e8;background:#eeeff2}.bb-detailed-radio-item__input:checked+.bb-detailed-radio-item__content-wrapper .bb-detailed-radio-item__circle::after{transform:scale(1)}.bb-detailed-radio-item:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option) .bb-detailed-radio-item__input:checked:focus+.bb-detailed-radio-item__content-wrapper{border-color:#5ea6ed}.bb-detailed-radio-item--special:not(.bb-detailed-radio-item--disabled):not(.bb-detailed-radio-item--single-option) .bb-detailed-radio-item__input:checked:focus+.bb-detailed-radio-item__content-wrapper{border-color:#3dd6b5}.bb-detailed-radio-item__description{margin-bottom:.9375rem;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;line-height:.875rem;text-align:center}.bb-detailed-radio-item__content-wrapper,.bb-detailed-radio-item__extra-content{background:#fff}.bb-detailed-radio-item__content-wrapper{display:flex;flex-direction:column;border:.125rem solid #dee3e8;border-radius:.625rem;transition:background .2s,border-color .2s}.bb-detailed-radio-item--special .bb-detailed-radio-item__content-wrapper{border-color:#0ccca2}.bb-detailed-radio-item--disabled .bb-detailed-radio-item__content-wrapper{border-color:#dee3e8}.bb-detailed-radio-item__main-content{position:relative;display:grid;width:100%;min-height:3.75rem;align-items:center;padding:0 .8125rem;column-gap:.625rem;grid-column-gap:.625rem;grid-template-columns:auto 1fr}@media (min-width:768px){.bb-detailed-radio-item__main-content{padding:0 1.25rem;column-gap:.9375rem;grid-column-gap:.9375rem}}.bb-detailed-radio-item--single-option .bb-detailed-radio-item__main-content{grid-template-columns:1fr}.bb-detailed-radio-item__extra-content{border-bottom-left-radius:.625rem;border-bottom-right-radius:.625rem}.bb-detailed-radio-item--disabled .bb-detailed-radio-item__extra-content{background:#eeeff2}.bb-detailed-radio-item__label,.bb-detailed-radio-item__special-label{position:absolute;top:-.5rem;left:50%;height:1rem;padding:0 .5rem;border-radius:99rem;color:#fff;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;text-align:center;text-transform:uppercase;transform:translateX(-50%);transition:background .2s}.bb-detailed-radio-item--label-white .bb-detailed-radio-item__label{background:#fff}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-white .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-black .bb-detailed-radio-item__label{background:#000}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-black .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-navy .bb-detailed-radio-item__label{background:#00285a}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-navy .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-navy-90 .bb-detailed-radio-item__label{background:#004c73}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-navy-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-navy-80 .bb-detailed-radio-item__label{background:#005d8c}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-navy-80 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-navy-70 .bb-detailed-radio-item__label{background:#003373}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-navy-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-navy-50 .bb-detailed-radio-item__label{background:#003e8c}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-navy-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-mint .bb-detailed-radio-item__label{background:#0ccca2}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-mint .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-mint-90 .bb-detailed-radio-item__label{background:#3dd6b5}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-mint-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-mint-70 .bb-detailed-radio-item__label{background:#3dd6b5}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-mint-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-mint-50 .bb-detailed-radio-item__label{background:#69e4ca}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-mint-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-mint-30 .bb-detailed-radio-item__label{background:#b6f0e3}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-mint-30 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-mint-10 .bb-detailed-radio-item__label{background:#e7faf6}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-mint-10 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-green .bb-detailed-radio-item__label{background:#0ccca2}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-green .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-green-90 .bb-detailed-radio-item__label{background:#3dd6b5}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-green-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-green-70 .bb-detailed-radio-item__label{background:#3dd6b5}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-green-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-green-50 .bb-detailed-radio-item__label{background:#69e4ca}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-green-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-green-30 .bb-detailed-radio-item__label{background:#b6f0e3}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-green-30 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-green-10 .bb-detailed-radio-item__label{background:#e7faf6}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-green-10 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue .bb-detailed-radio-item__label{background:#2485e5}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-90 .bb-detailed-radio-item__label{background:#5ea6ed}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-80 .bb-detailed-radio-item__label{background:#5ea6ed}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-80 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-70 .bb-detailed-radio-item__label{background:#5ea6ed}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-50 .bb-detailed-radio-item__label{background:#6eb3f7}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-40 .bb-detailed-radio-item__label{background:#6eb3f7}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-40 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-30 .bb-detailed-radio-item__label{background:#add9ff}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-30 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-20 .bb-detailed-radio-item__label{background:#add9ff}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-20 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-blue-10 .bb-detailed-radio-item__label{background:#e6f3ff}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-blue-10 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray .bb-detailed-radio-item__label{background:#38414d}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-90 .bb-detailed-radio-item__label{background:#57697a}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-80 .bb-detailed-radio-item__label{background:#687d92}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-80 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-70 .bb-detailed-radio-item__label{background:#89a}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-50 .bb-detailed-radio-item__label{background:#9dabb9}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-40 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-40 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-30 .bb-detailed-radio-item__label{background:#dee3e8}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-30 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-20 .bb-detailed-radio-item__label{background:#eeeff2}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-20 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-gray-10 .bb-detailed-radio-item__label{background:#f7f7f8}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-gray-10 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-yellow .bb-detailed-radio-item__label{background:#fc0}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-yellow .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-yellow-90 .bb-detailed-radio-item__label{background:#fc0}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-yellow-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-yellow-70 .bb-detailed-radio-item__label{background:#ffeea6}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-yellow-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-yellow-50 .bb-detailed-radio-item__label{background:#ffeea6}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-yellow-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-yellow-30 .bb-detailed-radio-item__label{background:#fbf5cb}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-yellow-30 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-yellow-10 .bb-detailed-radio-item__label{background:#fbf5cb}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-yellow-10 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-red .bb-detailed-radio-item__label{background:#ff7155}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-red .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-red-90 .bb-detailed-radio-item__label{background:#fa9}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-red-90 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-red-70 .bb-detailed-radio-item__label{background:#fa9}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-red-70 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-red-50 .bb-detailed-radio-item__label{background:#ffbfb3}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-red-50 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-red-30 .bb-detailed-radio-item__label{background:#ffd5cc}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-red-30 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item--label-red-10 .bb-detailed-radio-item__label{background:#ffeae6}.bb-detailed-radio-item--disabled.bb-detailed-radio-item--label-red-10 .bb-detailed-radio-item__label{background:#bdc7d0}.bb-detailed-radio-item__special-label{display:none;background:#0ccca2}.bb-detailed-radio-item--special .bb-detailed-radio-item__special-label{display:block}.bb-detailed-radio-item--disabled .bb-detailed-radio-item__special-label{background:#bdc7d0}.bb-detailed-radio-item__columns{display:flex;flex-direction:column;align-items:flex-start;justify-content:center;padding:1rem 0}@media (min-width:420px){.bb-detailed-radio-item__columns{flex-direction:row;align-items:center;justify-content:space-between;padding:0}}.bb-detailed-radio-item__circle,.bb-detailed-radio-item__left-column,.bb-detailed-radio-item__right-column{z-index:1}.bb-detailed-radio-item__circle{position:relative;width:1.25rem;height:1.25rem;border:2px solid #bdc7d0;border-radius:100%;background:#fff}.bb-detailed-radio-item__circle::after{position:absolute;top:.25rem;left:.25rem;width:.5rem;height:.5rem;border-radius:99rem;background:#0ccca2;content:\"\";transform:scale(0);transition:transform .2s}.bb-detailed-radio-item--disabled .bb-detailed-radio-item__circle::after{background:#9dabb9}.bb-detailed-radio-item__left-column,.bb-detailed-radio-item__right-column,.bb-detailed-radio-item__ticket-column{display:flex;flex-direction:column}.bb-detailed-radio-item--disabled .bb-detailed-radio-item__left-column>*,.bb-detailed-radio-item--disabled .bb-detailed-radio-item__right-column>*,.bb-detailed-radio-item--disabled .bb-detailed-radio-item__ticket-column>*{color:#bdc7d0}.bb-detailed-radio-item__left-column,.bb-detailed-radio-item__right-column{justify-content:center}.bb-detailed-radio-item__left-column-label,.bb-detailed-radio-item__right-column-label{color:#38414d}.bb-detailed-radio-item__extra-description,.bb-detailed-radio-item__left-column-sublabel,.bb-detailed-radio-item__ticket-content{color:#57697a}.bb-detailed-radio-item__left-column-sublabel-small,.bb-detailed-radio-item__ticket-label{color:#687d92}.bb-detailed-radio-item__extra-description,.bb-detailed-radio-item__left-column-sublabel,.bb-detailed-radio-item__left-column-sublabel-small,.bb-detailed-radio-item__right-column-label{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-detailed-radio-item__left-column-label,.bb-detailed-radio-item__right-column-amount,.bb-detailed-radio-item__ticket-content,.bb-detailed-radio-item__ticket-label{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-detailed-radio-item__left-column-label,.bb-detailed-radio-item__right-column-amount,.bb-detailed-radio-item__ticket-content{font-size:1rem;line-height:1rem}.bb-detailed-radio-item__left-column-sublabel-small,.bb-detailed-radio-item__right-column-label{font-size:.625rem;line-height:.625rem}.bb-detailed-radio-item__left-column-label,.bb-detailed-radio-item__left-column-sublabel-small,.bb-detailed-radio-item__right-column-amount,.bb-detailed-radio-item__ticket-content{white-space:nowrap}.bb-detailed-radio-item--single-option .bb-detailed-radio-item__left-column{display:flex;align-items:center}.bb-detailed-radio-item--single-row .bb-detailed-radio-item__left-column-label{font-size:.875rem;line-height:.875rem;white-space:normal}.bb-detailed-radio-item__left-column-sublabel,.bb-detailed-radio-item__left-column-sublabel-small{margin-top:.1875rem}.bb-detailed-radio-item__left-column-sublabel{font-size:.6875rem;line-height:.6875rem}.bb-detailed-radio-item--single-option .bb-detailed-radio-item__left-column-sublabel{text-transform:uppercase}.bb-detailed-radio-item__right-column{align-items:flex-start;margin-top:.5rem}@media (min-width:420px){.bb-detailed-radio-item__right-column{align-items:flex-end;margin-top:0}}.bb-detailed-radio-item__right-column-amount,.bb-detailed-radio-item__right-column-label{text-align:left}@media (min-width:420px){.bb-detailed-radio-item__right-column-amount,.bb-detailed-radio-item__right-column-label{text-align:right}}.bb-detailed-radio-item__right-column-amount{margin-top:.25rem;color:#2485e5}@media (min-width:420px){.bb-detailed-radio-item__right-column-amount{font-size:1.125rem;line-height:1.125rem}}.bb-detailed-radio-item__ticket-column{align-items:center}.bb-detailed-radio-item__ticket-label{margin-bottom:.375rem;font-size:.625rem;line-height:.75rem;text-align:center;text-transform:uppercase}.bb-detailed-radio-item__extra-description,.bb-detailed-radio-item__extra-list{border-top:.0625rem dashed #9dabb9}.bb-detailed-radio-item__extra-description{padding:.5rem .8125rem .75rem;font-size:.75rem}@media (min-width:768px){.bb-detailed-radio-item__extra-description{padding:.5rem 1.25rem .75rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1n = undefined;
/* module identifier */
const __vue_module_identifier__$1n = undefined;
/* functional template */
const __vue_is_functional_template__$1n = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1n = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1l,
  staticRenderFns: __vue_staticRenderFns__$1l
}, __vue_inject_styles__$1n, __vue_script__$1n, __vue_scope_id__$1n, __vue_is_functional_template__$1n, __vue_module_identifier__$1n, false, createInjector, undefined, undefined);

var script$1m = {
  name: 'bb-detailed-radio',
  components: {
    BbDetailedRadioItem: __vue_component__$1n
  },
  mixins: [Accessibility],
  props: {
    inputAttrs: {
      type: Object,
      default: () => ({})
    },
    value: {
      type: [Boolean, String, Number, Object]
    },
    id: [String, Number],
    name: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    singleOption: Boolean,
    disabled: Boolean,
    layout: {
      type: String,
      validator: type => ['horizontal', 'vertical'].includes(type),
      default: 'vertical'
    }
  },
  emits: ['input', 'change'],
  data() {
    return {
      val: this.filterValidValue(this.value),
      defaultAriaLabel: 'Detailed radio group'
    };
  },
  computed: {
    classes() {
      return ['bb-detailed-radio', 'input-group', `bb-detailed-radio--layout-${this.layout}`];
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.updateVal();
      }
    },
    options: {
      deep: true,
      immediate: true,
      handler() {
        this.updateVal();
      }
    }
  },
  methods: {
    onClick(itemValue) {
      if (this.val !== itemValue) {
        this.val = itemValue;
        this.$emit('input', itemValue);
        this.$emit('change', itemValue);
      }
    },
    filterValidValue(value) {
      return this.valueExistsInOptions(value) ? value : null;
    },
    valueExistsInOptions(value) {
      return this.options.find(option => option.value === value);
    },
    updateVal() {
      this.val = this.filterValidValue(this.value);
    }
  }
};

/* script */
const __vue_script__$1m = script$1m;

/* template */
var __vue_render__$1k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes,
    attrs: {
      "id": _vm.id,
      "role": "radiogroup",
      "aria-label": _vm.defaultAriaLabel
    }
  }, _vm._l(_vm.options, function (item, index) {
    return _c('bb-detailed-radio-item', _vm._b({
      key: index,
      staticClass: "bb-detailed-radio__item",
      attrs: {
        "input-attrs": Object.assign({}, _vm.inputAttrs, {
          tabindex: item.value === _vm.val ? 0 : index === 0 ? 0 : -1
        }),
        "disabled": item.disabled || _vm.disabled,
        "selected": item.value === _vm.val
      },
      on: {
        "click": _vm.onClick
      },
      scopedSlots: _vm._u([{
        key: "extraDescription",
        fn: function (itemExtra) {
          return [_vm._t("extraDescription", function () {
            return [_vm._v("\n        " + _vm._s(itemExtra.description) + "\n      ")];
          }, null, itemExtra)];
        }
      }], null, true)
    }, 'bb-detailed-radio-item', Object.assign({}, item, {
      name: _vm.name,
      disabled: _vm.disabled,
      singleOption: _vm.singleOption
    }), false));
  }), 1);
};
var __vue_staticRenderFns__$1k = [];

/* style */
const __vue_inject_styles__$1m = function (inject) {
  if (!inject) return;
  inject("data-v-a9066164_0", {
    source: ".bb-detailed-radio.bb-detailed-radio--layout-vertical{display:block}.bb-detailed-radio.bb-detailed-radio--layout-horizontal{display:flex}.bb-detailed-radio--layout-vertical .bb-detailed-radio__item:not(:last-child){margin-bottom:.9375rem}.bb-detailed-radio--layout-horizontal .bb-detailed-radio__item{flex-grow:1}.bb-detailed-radio--layout-horizontal .bb-detailed-radio__item:not(:last-child){margin-right:.625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1m = undefined;
/* module identifier */
const __vue_module_identifier__$1m = undefined;
/* functional template */
const __vue_is_functional_template__$1m = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1m = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1k,
  staticRenderFns: __vue_staticRenderFns__$1k
}, __vue_inject_styles__$1m, __vue_script__$1m, __vue_scope_id__$1m, __vue_is_functional_template__$1m, __vue_module_identifier__$1m, false, createInjector, undefined, undefined);

const primaryColors = ['navy', 'blue', 'gray', 'mint', 'yellow', 'red', 'green'
];
const shadePercentiles = [90, 80, 70, 50, 40, 30, 20, 10];
const secondaryColors = primaryColors.reduce((acc, primaryColor) => acc.concat(shadePercentiles.filter(shadePercentile => primaryColor === 'blue' || primaryColor === 'gray' ? true : primaryColor === 'navy' ? shadePercentile > 70 : shadePercentile % 20 !== 0).map(shadePercentile => `${primaryColor}-${shadePercentile}`)), []);
const allowedColors = primaryColors.concat(secondaryColors);
var TaskColor = {
  props: {
    taskColor: {
      type: String,
      validator: taskColor => allowedColors.includes(taskColor)
    }
  }
};

var script$1l = {
  name: 'bb-drawer',
  components: {
    BbButton: __vue_component__$2v,
    BbOverlay: __vue_component__$2u,
    BbProgressBar: __vue_component__$26
  },
  mixins: [TaskColor, validator],
  props: {
    visible: Boolean,
    size: {
      type: String,
      default: 'm',
      validator: value => validateWithDeprecationWarning('BbDrawer', 'size', value, {
        'xs': 'xs',
        'm': 'm',
        'l': 'l',
        'xxl': 'xxl',
        'responsive': ''
      })
    },
    responsive: Boolean,
    static: Boolean,
    navigation: Boolean,
    hideOverlay: Boolean,
    left: Boolean,
    title: String,
    progressBar: Boolean,
    progressBarProps: Object
  },
  emits: ['open', 'close'],
  computed: {
    dialogClasses() {
      return {
        [`bb-drawer__dialog--size-${this.size}`]: !this.navigation,
        'bb-drawer__dialog--left': this.left,
        'bb-drawer__dialog--navigation': this.navigation,
        'bb-drawer__dialog--static': this.static,
        'bb-drawer__dialog--responsive': this.responsive
      };
    }
  },
  watch: {
    visible() {
      if (this.visible) {
        this.$el.focus({
          preventScroll: true
        });
        this.$emit('open');
      }
    }
  },
  methods: {
    hideDrawer() {
      this.$emit('close');
    }
  }
};

/* script */
const __vue_script__$1l = script$1l;

/* template */
var __vue_render__$1j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-drawer",
    attrs: {
      "tabindex": "-1"
    },
    on: {
      "keyup": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
          return null;
        }
        return _vm.hideDrawer.apply(null, arguments);
      }
    }
  }, [!_vm.hideOverlay ? _c('bb-overlay', {
    staticClass: "bb-drawer__overlay",
    attrs: {
      "visible": _vm.visible,
      "color": "gray"
    },
    on: {
      "close": _vm.hideDrawer
    }
  }) : _vm._e(), _vm._v(" "), _c('transition', {
    attrs: {
      "name": "bb-drawer"
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.visible,
      expression: "visible"
    }],
    staticClass: "bb-drawer__dialog wrapper",
    class: _vm.dialogClasses
  }, [_vm.title ? _c('header', {
    staticClass: "bb-drawer__header",
    class: "border-" + (_vm.taskColor || 'none')
  }, [_c('h3', {
    staticClass: "bb-drawer__title",
    attrs: {
      "title": _vm.title
    }
  }, [_vm._v("\n          " + _vm._s(_vm.title) + "\n        ")]), _vm._v(" "), _vm._t("titleBar"), _vm._v(" "), _c('bb-button', {
    staticClass: "bb-drawer__close",
    attrs: {
      "display": "square",
      "icon": "ui-close",
      "no-background": "",
      "color": "gray"
    },
    on: {
      "click": _vm.hideDrawer
    }
  })], 2) : _c('header', {
    staticClass: "bb-drawer__header bb-drawer__header--no-title"
  }, [_vm._t("header", null, {
    "close": _vm.hideDrawer
  })], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-drawer__body"
  }, [_vm._t("default")], 2), _vm._v(" "), _vm.$slots.navigation ? _c('div', {
    staticClass: "bb-drawer__navigation"
  }, [_vm._t("navigation")], 2) : _vm._e(), _vm._v(" "), _vm.progressBar ? _c('bb-progress-bar', _vm._b({
    staticClass: "bb-drawer__progress-bar"
  }, 'bb-progress-bar', _vm.progressBarProps, false)) : _vm._e()], 1)])], 1);
};
var __vue_staticRenderFns__$1j = [];

/* style */
const __vue_inject_styles__$1l = function (inject) {
  if (!inject) return;
  inject("data-v-73f3d65d_0", {
    source: ".bb-drawer__title{overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.bb-drawer{outline:0}.bb-drawer:focus{outline:0}.bb-drawer__dialog{position:fixed;z-index:1500;top:0;right:0;display:flex;height:100%;flex-direction:column;background-clip:padding-box;background-color:#fff;box-shadow:0 .125rem 1.25rem 0 rgba(0,0,0,.3);outline:0}@media (min-width:768px){.bb-drawer__dialog--size-xs{width:15.9375rem}.bb-drawer__dialog--size-xs.bb-drawer__dialog--responsive{width:unset;min-width:15.9375rem;max-width:90%}.bb-drawer__dialog--size-m{width:30rem}.bb-drawer__dialog--size-m.bb-drawer__dialog--responsive{width:unset;min-width:30rem;max-width:90%}.bb-drawer__dialog--size-l{width:49.375rem}.bb-drawer__dialog--size-l.bb-drawer__dialog--responsive{width:unset;min-width:49.375rem;max-width:90%}.bb-drawer__dialog--size-xxl{width:90rem}.bb-drawer__dialog--size-xxl.bb-drawer__dialog--responsive{width:unset;min-width:90rem;max-width:90%}}.bb-drawer__dialog--left{right:initial;left:0}.bb-drawer__dialog--navigation{width:89%}@media (min-width:1200px){.bb-drawer__dialog--static{position:static;z-index:0;display:flex!important;width:18.125rem;box-shadow:none}}@media (min-width:1200px){.bb-drawer__dialog--static .bb-drawer__overlay{display:none}}.bb-drawer__body{display:flex;overflow:auto;min-height:0;flex-direction:column;padding:2.5rem 3.125rem}.bb-drawer__dialog--navigation .bb-drawer__body{overflow:auto;flex:1 1 0%;padding:0}.bb-drawer__title{padding-right:1.625rem;margin-right:auto;margin-bottom:0;color:#00285a;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:18px;line-height:1.3}.bb-drawer__header{display:flex;min-height:3.125rem;align-items:center;padding-right:.9375rem;padding-left:1.875rem;border-left-width:.3125rem;border-left-style:solid;background:#f7f7f8}.bb-drawer__header--no-title{display:block;padding:0;border:0;background:0 0}.bb-drawer__navigation{position:absolute;top:3.125rem;left:-6.875rem;opacity:1}.bb-drawer__close{margin-right:0;margin-left:1.25rem}.bb-drawer__progress-bar{z-index:1301}.bb-drawer-enter-active,.bb-drawer-leave-active{transition:transform .4s}.bb-drawer-enter,.bb-drawer-leave-to{transform:translateX(100%)}.bb-drawer-enter.bb-drawer__dialog--left,.bb-drawer-leave-to.bb-drawer__dialog--left{transform:translateX(-100%)}.bb-drawer-enter-active .bb-drawer__navigation,.bb-drawer-leave-active .bb-drawer__navigation{transition:opacity .2s ease-out}.bb-drawer-enter .bb-drawer__navigation,.bb-drawer-leave-to .bb-drawer__navigation{opacity:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1l = undefined;
/* module identifier */
const __vue_module_identifier__$1l = undefined;
/* functional template */
const __vue_is_functional_template__$1l = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1l = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1j,
  staticRenderFns: __vue_staticRenderFns__$1j
}, __vue_inject_styles__$1l, __vue_script__$1l, __vue_scope_id__$1l, __vue_is_functional_template__$1l, __vue_module_identifier__$1l, false, createInjector, undefined, undefined);

var script$1k = {
  name: 'bb-edit-amount',
  components: {
    BbButton: __vue_component__$2v
  },
  props: {
    title: String,
    amount: [String, Number],
    loading: Boolean
  },
  emits: ['click'],
  data() {
    return {
      show: false
    };
  },
  computed: {
    iconAfter() {
      return this.loading ? false : 'ui-pencil';
    }
  },
  methods: {
    click() {
      if (this.loading) {
        return;
      }
      this.$emit('click');
    }
  }
};

/* script */
const __vue_script__$1k = script$1k;

/* template */
var __vue_render__$1i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-button', {
    staticClass: "bb-edit-amount",
    attrs: {
      "display": "narrow",
      "no-background": "",
      "color": "gray",
      "loading": _vm.loading,
      "icon-after": _vm.iconAfter,
      "icon-after-color": "mint"
    },
    on: {
      "click": _vm.click
    }
  }, [_c('span', {
    staticClass: "bb-edit-amount__content"
  }, [_c('div', {
    staticClass: "bb-edit-amount__title"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]), _vm._v(" "), _c('div', {
    staticClass: "bb-edit-amount__amount"
  }, [_vm._v("\n      " + _vm._s(_vm.amount) + "\n    ")])])]);
};
var __vue_staticRenderFns__$1i = [];

/* style */
const __vue_inject_styles__$1k = function (inject) {
  if (!inject) return;
  inject("data-v-58278200_0", {
    source: ".bb-edit-amount{display:flex;align-items:center}.bb-edit-amount__content{color:#00285a;text-align:right}.bb-edit-amount__title{font-size:9px}@media (min-width:768px){.bb-edit-amount__title{font-size:12px}}.bb-edit-amount__amount{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1k = undefined;
/* module identifier */
const __vue_module_identifier__$1k = undefined;
/* functional template */
const __vue_is_functional_template__$1k = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1k = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1i,
  staticRenderFns: __vue_staticRenderFns__$1i
}, __vue_inject_styles__$1k, __vue_script__$1k, __vue_scope_id__$1k, __vue_is_functional_template__$1k, __vue_module_identifier__$1k, false, createInjector, undefined, undefined);

var Icon$n = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3509816233__a bb-icon__dynamic-fill",attrs:{"d":"M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8,9H16V19H8V9M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z"}})]) } };

Vue.component('icon-ui-delete-outline', Icon$n);

var script$1j = {
  name: 'bb-feed-item',
  components: {
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbButton: __vue_component__$2v
  },
  props: {
    title: String,
    datetime: {
      type: String
    },
    user: {
      type: String
    },
    status: {
      type: String,
      validator: status => ['active', 'negative', 'done', 'positive'].includes(status)
    },
    deleteButton: {
      type: Boolean
    }
  },
  emits: ['click', 'delete'],
  computed: {
    classes() {
      return ['bb-feed-item', {
        [`bb-feed-item--${this.status}`]: this.status,
        'bb-feed-item--clickable': this.$listeners.click
      }];
    },
    computedUser() {
      var _this$user;
      const WORD_BREAK = '\u200B';
      return (_this$user = this.user) === null || _this$user === void 0 ? void 0 : _this$user.replace('.', `.${WORD_BREAK}`);
    }
  },
  methods: {
    onClick() {
      this.$emit('click');
    },
    onDelete() {
      this.$emit('delete');
    }
  }
};

/* script */
const __vue_script__$1j = script$1j;

/* template */
var __vue_render__$1h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes,
    on: {
      "click": _vm.onClick
    }
  }, [_c('bb-row', {
    staticClass: "bb-feed-item__row",
    attrs: {
      "align": "center",
      "no-gutters": ""
    }
  }, [_c('bb-col', {
    staticClass: "bb-feed-item__col-left"
  }, [_c('h2', {
    staticClass: "bb-feed-item__title"
  }, [_vm._v("\n        " + _vm._s(_vm.title) + "\n      ")]), _vm._v(" "), _vm.title || _vm.datetime ? _c('div', {
    staticClass: "bb-feed-item__datetime"
  }, [_vm._v("\n        " + _vm._s(_vm.datetime) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm._t("default")], 2), _vm._v(" "), _c('bb-col', {
    staticClass: "bb-feed-item__col-right",
    attrs: {
      "col": "auto"
    }
  }, [_c('div', {
    staticClass: "bb-feed-item__delete"
  }, [_vm._t("action-buttons", function () {
    return [_vm.deleteButton ? _c('bb-button', {
      attrs: {
        "display": "square",
        "size": "sm",
        "icon": "ui-delete-outline",
        "no-background": "",
        "color": "gray"
      },
      nativeOn: {
        "click": function ($event) {
          $event.stopPropagation();
          return _vm.onDelete.apply(null, arguments);
        }
      }
    }) : _vm._e()];
  })], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-feed-item__user"
  }, [_vm._v("\n        " + _vm._s(_vm.computedUser) + "\n      ")])])], 1)], 1);
};
var __vue_staticRenderFns__$1h = [];

/* style */
const __vue_inject_styles__$1j = function (inject) {
  if (!inject) return;
  inject("data-v-5f2bb08e_0", {
    source: "@charset \"UTF-8\";.bb-feed-item{position:relative;padding:1.25rem 1.5625rem;border-width:.125rem;border-style:solid;border-color:#eeeff2;border-radius:.125rem;color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;line-height:.9375rem}.bb-feed-item--active::after,.bb-feed-item--done::after,.bb-feed-item--negative::after,.bb-feed-item--positive::after{position:absolute;top:calc(50% - .4375rem);left:-.5rem;width:.875rem;height:.875rem;border-radius:.875rem;background-color:#687d92;content:\"\"}.bb-feed-item--active{border-color:#0ccca2}.bb-feed-item--active::after{background-color:#0ccca2}.bb-feed-item--negative::after{background-color:#ff7155}.bb-feed-item--positive::after{background-color:#0ccca2}.bb-feed-item--clickable{cursor:pointer}.bb-feed-item__row{flex-direction:column}@media (min-width:576px){.bb-feed-item__row{flex-direction:row}}.bb-feed-item__title{margin-bottom:0;color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1.3125rem;overflow-wrap:anywhere}.bb-feed-item__datetime{color:#89a}.bb-feed-item__datetime+*{margin-top:.9375rem}.bb-feed-item__delete{flex:1;text-align:right}.bb-feed-item__user{display:flex;flex-wrap:nowrap;align-items:center;color:#2485e5}.bb-feed-item__user::before{margin-right:.3125rem;color:#dee3e8;content:\"•\";font-size:1.5625rem}.bb-feed-item__col-left{flex:10}.bb-feed-item__col-right{display:flex;flex:1;flex-direction:row-reverse;align-self:stretch;justify-content:space-between}@media (min-width:576px){.bb-feed-item__col-right{flex-direction:column}.bb-feed-item__col-right::after{flex:1;content:\"\"}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1j = undefined;
/* module identifier */
const __vue_module_identifier__$1j = undefined;
/* functional template */
const __vue_is_functional_template__$1j = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1j = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1h,
  staticRenderFns: __vue_staticRenderFns__$1h
}, __vue_inject_styles__$1j, __vue_script__$1j, __vue_scope_id__$1j, __vue_is_functional_template__$1j, __vue_module_identifier__$1j, false, createInjector, undefined, undefined);

var script$1i = {
  name: 'bb-feed-task',
  components: {
    BbLink: __vue_component__$2p,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbIcon: __vue_component__$2C
  },
  mixins: [TaskColor],
  props: {
    title: {
      type: String
    },
    subtitle: {
      type: String
    },
    done: {
      type: Boolean,
      default: false
    },
    date: {
      type: String
    },
    info: {
      type: String
    },
    user: {
      type: String
    },
    userLink: {
      type: [String, Object]
    },
    notClickable: {
      type: Boolean,
      default: false
    }
  }
};

/* script */
const __vue_script__$1i = script$1i;

/* template */
var __vue_render__$1g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-row', {
    staticClass: "bb-feed-task",
    class: {
      'bb-feed-task--done': _vm.done,
      'bb-feed-task--not-clickable': _vm.notClickable
    },
    attrs: {
      "no-gutters": ""
    }
  }, [!_vm.done ? _c('div', {
    staticClass: "bb-feed-task__type-bar",
    class: "bg-" + _vm.taskColor
  }) : _vm._e(), _vm._v(" "), _vm.done ? _c('div', {
    staticClass: "bb-feed-task__status-icon"
  }, [_c('bb-icon', {
    attrs: {
      "name": "ui-check-circle",
      "size": "24",
      "fill": "mint"
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-feed-task__body"
  }, [_c('bb-col', {
    staticClass: "bb-feed-task__title",
    attrs: {
      "col": "24",
      "md": "14"
    }
  }, [_c('bb-row', [_c('bb-col', {
    staticClass: "bb-feed-task__main",
    attrs: {
      "col": "24"
    }
  }, [_vm._v("\n          " + _vm._s(_vm.title) + "\n        ")]), _vm._v(" "), _c('bb-col', {
    staticClass: "bb-feed-task__info",
    attrs: {
      "col": "24"
    }
  }, [_vm._v("\n          " + _vm._s(_vm.info) + "\n        ")]), _vm._v(" "), _vm.done ? _c('bb-col', {
    staticClass: "bb-feed-task__subtitle",
    attrs: {
      "col": "24"
    }
  }, [_vm._v("\n          " + _vm._s(_vm.subtitle) + "\n        ")]) : _vm._e()], 1)], 1), _vm._v(" "), _vm.done ? _c('bb-col', {
    staticClass: "bb-feed-task__user",
    attrs: {
      "col": "24",
      "md": "10"
    }
  }, [_c('bb-link', {
    attrs: {
      "to": _vm.userLink,
      "color": "blue",
      "no-underline": ""
    }
  }, [_c('span', {
    staticClass: "bb-feed-task__bullet"
  }), _vm._v(" " + _vm._s(_vm.user) + "\n      ")])], 1) : _vm._e(), _vm._v(" "), !_vm.done ? _c('bb-col', {
    staticClass: "bb-feed-task__date",
    attrs: {
      "col": "24",
      "md": "10"
    }
  }, [_c('span', {
    staticClass: "bb-feed-task__bullet"
  }), _vm._v(" " + _vm._s(_vm.date) + "\n    ")]) : _vm._e()], 1), _vm._v(" "), !_vm.done && !_vm.notClickable ? _c('bb-icon', {
    staticClass: "bb-feed-task__arrow",
    attrs: {
      "name": "ui-menu-down",
      "size": "24",
      "rotate": "left"
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$1g = [];

/* style */
const __vue_inject_styles__$1i = function (inject) {
  if (!inject) return;
  inject("data-v-23a6472e_0", {
    source: ".bb-feed-task{position:relative;display:flex;border:.125rem solid #eeeff2;border-radius:.125rem;margin-bottom:.4375rem;background:#fff;color:#38414d;font-size:.875rem}.bb-feed-task:hover:not(.bb-feed-task--not-clickable){border-color:#dee3e8;background-color:#eeeff2;box-shadow:none}.bb-feed-task__title{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-feed-task__info,.bb-feed-task__subtitle{color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}@media (min-width:768px){.bb-feed-task__info{order:-1}}.bb-feed-task__body{flex:1;align-items:flex-end;padding:.5625rem 1.25rem .625rem 1.5625rem}.bb-feed-task__main{color:#00285a}.bb-feed-task--done .bb-feed-task__main{color:#57697a;text-decoration:line-through}.bb-feed-task__type-bar{width:.5rem;margin:-.125rem}.bb-feed-task__status-icon{display:flex;align-items:center;margin-right:-.5625rem;margin-left:-.8125rem}.bb-feed-task__status-icon::before{position:absolute;left:-10px;display:block;width:18px;height:18px;border-radius:50%;background:#fff;content:\"\"}.bb-feed-task__arrow{align-self:center;margin-right:1.25rem}.bb-feed-task__date,.bb-feed-task__user{padding-top:1.25rem;padding-bottom:1.25rem;color:#38414d;font-size:.75rem;text-align:left}@media (min-width:768px){.bb-feed-task__date,.bb-feed-task__user{text-align:right}}.bb-feed-task__bullet{display:inline-block;width:.5rem;height:.5rem;border-radius:50%;margin-right:.625rem;background:#dee3e8}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1i = undefined;
/* module identifier */
const __vue_module_identifier__$1i = undefined;
/* functional template */
const __vue_is_functional_template__$1i = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1i = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1g,
  staticRenderFns: __vue_staticRenderFns__$1g
}, __vue_inject_styles__$1i, __vue_script__$1i, __vue_scope_id__$1i, __vue_is_functional_template__$1i, __vue_module_identifier__$1i, false, createInjector, undefined, undefined);

var script$1h = {
  name: 'bb-filter-bar',
  components: {
    BbTagGroup: __vue_component__$1U,
    BbButton: __vue_component__$2v
  },
  props: {
    clearButtonLabel: {
      type: String,
      default: 'Clear filters'
    }
  },
  emits: ['clearFilters'],
  methods: {
    onClick() {
      this.$emit('clearFilters');
    }
  }
};

/* script */
const __vue_script__$1h = script$1h;

/* template */
var __vue_render__$1f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-filter-bar"
  }, [_c('bb-tag-group', {
    staticClass: "bb-filter-bar__tag-group"
  }, [_vm._t("default")], 2), _vm._v(" "), _c('bb-button', {
    staticClass: "bb-filter-bar__clear-button",
    attrs: {
      "no-background": "",
      "color": "gray",
      "label": _vm.clearButtonLabel,
      "icon": "ui-close",
      "icon-color": "red"
    },
    on: {
      "click": _vm.onClick
    }
  })], 1);
};
var __vue_staticRenderFns__$1f = [];

/* style */
const __vue_inject_styles__$1h = function (inject) {
  if (!inject) return;
  inject("data-v-73f0f3d2_0", {
    source: ".bb-filter-bar{display:flex;min-height:3.125rem;align-items:center;justify-content:space-between;padding:0 2.1875rem 0 1.875rem;border-bottom:.0625rem solid #dee3e8;background:#fff;font-size:.75rem}.bb-filter-bar__clear-button{padding-right:.75rem;padding-left:.5rem;color:#89a;font-size:inherit;white-space:nowrap}.bb-filter-bar__clear-button:hover{color:#38414d}.bb-filter-bar__tag-group{padding-top:.1875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1h = undefined;
/* module identifier */
const __vue_module_identifier__$1h = undefined;
/* functional template */
const __vue_is_functional_template__$1h = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1h = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1f,
  staticRenderFns: __vue_staticRenderFns__$1f
}, __vue_inject_styles__$1h, __vue_script__$1h, __vue_scope_id__$1h, __vue_is_functional_template__$1h, __vue_module_identifier__$1h, false, createInjector, undefined, undefined);

var script$1g = {
  name: 'bb-contact-menu-item',
  components: {
    BbIcon: __vue_component__$2C,
    BbIconFrame: __vue_component__$2g,
    BbLink: __vue_component__$2p
  },
  props: {
    smallFont: Boolean,
    icon: String,
    iconColor: String,
    backgroundColor: String,
    label: String,
    link: Object
  },
  computed: {
    classes() {
      return ['bb-contact-menu-item', {
        'bb-contact-menu-item--small-font': this.smallFont
      }];
    }
  }
};

/* script */
const __vue_script__$1g = script$1g;

/* template */
var __vue_render__$1e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.backgroundColor ? _c('bb-icon-frame', {
    staticClass: "bb-contact-menu-item__icon",
    attrs: {
      "shape": "circle",
      "icon-size": "18",
      "icon": _vm.icon,
      "icon-color": _vm.iconColor,
      "background-color": _vm.backgroundColor
    }
  }) : _c('bb-icon', {
    staticClass: "bb-contact-menu-item__icon",
    attrs: {
      "name": _vm.icon,
      "fill": _vm.iconColor,
      "size": "18"
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-contact-menu-item__label"
  }, [_c('span', {
    staticClass: "bb-contact-menu-item__label-text"
  }, [_vm._v(_vm._s(_vm.label))]), _vm._v(" "), _c('bb-link', {
    attrs: {
      "to": _vm.link.to,
      "no-underline": "",
      "color": _vm.link.color ? _vm.link.color : 'mint'
    }
  }, [_vm._v("\n      " + _vm._s(_vm.link.label) + "\n    ")])], 1)], 1);
};
var __vue_staticRenderFns__$1e = [];

/* style */
const __vue_inject_styles__$1g = function (inject) {
  if (!inject) return;
  inject("data-v-78c1919c_0", {
    source: ".bb-contact-menu-item{display:flex;margin-bottom:1.875rem}.bb-contact-menu-item__icon{margin-right:.625rem}.bb-contact-menu-item__label{display:flex;flex-direction:column;color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;font-style:normal;line-height:1.125rem}.bb-contact-menu-item__label-text{margin-bottom:.1875rem}.bb-contact-menu-item--small-font .bb-contact-menu-item__label{font-size:.75rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1g = undefined;
/* module identifier */
const __vue_module_identifier__$1g = undefined;
/* functional template */
const __vue_is_functional_template__$1g = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1g = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1e,
  staticRenderFns: __vue_staticRenderFns__$1e
}, __vue_inject_styles__$1g, __vue_script__$1g, __vue_scope_id__$1g, __vue_is_functional_template__$1g, __vue_module_identifier__$1g, false, createInjector, undefined, undefined);

var script$1f = {
  name: 'bb-footer-menu',
  components: {
    BbContainer: __vue_component__$2q,
    BbLabel: __vue_component__$2e,
    BbRouterLink: __vue_component__$1D,
    BbContactMenuItem: __vue_component__$1g,
    BbAccordionItem: __vue_component__$1O
  },
  props: {
    links: {
      type: Array,
      required: true
    },
    contacts: {
      type: Array,
      required: true
    },
    baseHeadingLevel: {
      type: Number
    }
  },
  emits: ['linkClick'],
  computed: {
    sections() {
      const sections = [];
      this.links.forEach(item => {
        switch (item.type) {
          case 'title':
            sections.push({
              title: item.label,
              items: []
            });
            break;
          case 'group':
            last$1(sections).items.push({
              label: item.label,
              to: item.to,
              links: []
            });
            break;
          default:
            if (!sections.length) {
              sections.push({
                items: []
              });
            }
            const section = last$1(sections);
            const group = last$1(section.items);
            if (!group) {
              section.items.push({
                links: [item]
              });
            } else {
              group.links.push(item);
            }
        }
      });
      return sections;
    },
    subheadingLevel() {
      return Math.min(this.baseHeadingLevel + 1, 6);
    }
  }
};

/* script */
const __vue_script__$1f = script$1f;

/* template */
var __vue_render__$1d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-footer-menu"
  }, [_c('bb-container', [_c('div', {
    staticClass: "bb-footer-menu__mobile"
  }, [_vm._l(_vm.sections, function (section, index) {
    return _c('bb-accordion-item', {
      key: index,
      staticClass: "bb-footer-menu__section",
      scopedSlots: _vm._u([{
        key: "title",
        fn: function () {
          return [_vm._t(section.id + "-title", function () {
            return [_vm._v("\n            " + _vm._s(section.title) + "\n          ")];
          })];
        },
        proxy: true
      }], null, true)
    }, [_vm._v(" "), _c('ul', {
      staticClass: "bb-footer-menu__subsections"
    }, _vm._l(section.items, function (item, subIndex) {
      return _c('li', {
        key: subIndex,
        staticClass: "bb-footer-menu__subsection"
      }, [item.label ? _c('bb-router-link', {
        key: "link-" + subIndex,
        staticClass: "bb-footer-menu__subsection-title-link",
        attrs: {
          "to": item.to,
          "no-underline": ""
        }
      }, [_c("h" + _vm.subheadingLevel, {
        key: "title-" + subIndex,
        tag: "component",
        staticClass: "bb-footer-menu__subsection-title"
      }, [_vm._v("\n                " + _vm._s(item.label) + "\n              ")])], 1) : _vm._e(), _vm._v(" "), _vm._l(item.links, function (link, linkIndex) {
        return _c('bb-router-link', {
          key: linkIndex,
          staticClass: "bb-footer-menu__link",
          attrs: {
            "to": link.to,
            "color": "gray",
            "no-underline": "",
            "tabindex": "0",
            "aria-label": link.ariaLabel ? link.ariaLabel : link.labelExtra ? link.label + ", " + link.labelExtra : link.label
          },
          on: {
            "click": function ($event) {
              link.id ? _vm.$emit('linkClick', link.id) : null;
            }
          }
        }, [_vm._v("\n              " + _vm._s(link.label) + "\n              "), link.labelExtra ? _c('bb-label', {
          staticClass: "bb-footer-menu__link-label bg-mint",
          attrs: {
            "size": "18",
            "value": link.labelExtra
          }
        }) : _vm._e()], 1);
      })], 2);
    }), 0)]);
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-footer-menu__contact"
  }, _vm._l(_vm.contacts, function (item, index) {
    return _c('bb-contact-menu-item', _vm._b({
      key: index,
      staticClass: "bb-footer-menu__contact-menu-item",
      attrs: {
        "icon-color": "mint",
        "small-font": ""
      }
    }, 'bb-contact-menu-item', item, false));
  }), 1)], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-footer-menu__desktop"
  }, [_vm._l(_vm.sections, function (section, index) {
    return _c('section', {
      key: index,
      staticClass: "bb-footer-menu__section"
    }, [section.title ? _c("h" + _vm.baseHeadingLevel, {
      tag: "component",
      staticClass: "bb-footer-menu__section-title"
    }, [_vm._v("\n          " + _vm._s(section.title) + "\n        ")]) : _vm._e(), _vm._v(" "), section.items ? _c('ul', _vm._l(section.items, function (item, subIndex) {
      return _c('li', {
        key: subIndex,
        staticClass: "bb-footer-menu__subsection"
      }, [item.label ? _c("h" + _vm.subheadingLevel, {
        key: "title-" + subIndex,
        tag: "component",
        staticClass: "bb-footer-menu__subsection-title"
      }, [_vm._v("\n              " + _vm._s(item.label) + "\n            ")]) : _vm._e(), _vm._v(" "), _vm._l(item.links, function (link, linkIndex) {
        return _c('bb-router-link', {
          key: linkIndex,
          staticClass: "bb-footer-menu__link",
          attrs: {
            "to": link.to,
            "color": "gray",
            "no-underline": "",
            "tabindex": "0",
            "aria-label": link.ariaLabel ? link.labelExtra ? link.label + ", " + link.labelExtra : link.label : null
          },
          on: {
            "click": function ($event) {
              link.id ? _vm.$emit('linkClick', link.id) : null;
            }
          }
        }, [_vm._v("\n              " + _vm._s(link.label) + "\n              "), link.labelExtra ? _c('bb-label', {
          staticClass: "bb-footer-menu__link-label bg-mint",
          attrs: {
            "size": "18",
            "value": link.labelExtra
          }
        }) : _vm._e()], 1);
      })], 2);
    }), 0) : _vm._e()], 1);
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-footer-menu__contact"
  }, _vm._l(_vm.contacts, function (item, index) {
    return _c('bb-contact-menu-item', _vm._b({
      key: index,
      staticClass: "bb-footer-menu__contact-menu-item",
      attrs: {
        "icon-color": "gray",
        "background-color": "gray-20"
      }
    }, 'bb-contact-menu-item', item, false));
  }), 1)], 2)])], 1);
};
var __vue_staticRenderFns__$1d = [];

/* style */
const __vue_inject_styles__$1f = function (inject) {
  if (!inject) return;
  inject("data-v-18d3c1c6_0", {
    source: ".bb-footer-menu{background:#f7f7f8}.bb-footer-menu .bb-footer-menu__mobile{display:block;padding:1.875rem 0 .625rem}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__mobile{display:none}}.bb-footer-menu .bb-footer-menu__desktop{display:none;padding:4.375rem 0;column-gap:.9375rem;grid-template-columns:1fr 1fr}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__desktop{display:grid}}@media (min-width:768px){.bb-footer-menu .bb-footer-menu__desktop{grid-template-columns:1fr 1fr 1fr}}@media (min-width:992px){.bb-footer-menu .bb-footer-menu__desktop{grid-template-columns:1fr 1fr 1fr 1fr}}@media (min-width:1200px){.bb-footer-menu .bb-footer-menu__desktop{grid-template-columns:1fr 1fr 1fr 1fr 1fr}}.bb-footer-menu .bb-footer-menu__section{padding:0 0 1.875rem}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__section{padding:0 0 2.5rem}}.bb-footer-menu .bb-footer-menu__section-title{margin-bottom:1.875rem;color:#9dabb9;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1.5rem}.bb-footer-menu .bb-footer-menu__section .bb-accordion-item__header-title{font-size:1rem}.bb-footer-menu .bb-footer-menu__subsections{padding-top:.625rem}.bb-footer-menu .bb-footer-menu__subsection{display:flex;flex-direction:column;align-items:flex-start;padding-left:.1875rem}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__subsection{padding:0}}.bb-footer-menu .bb-footer-menu__subsection:not(:last-of-type){margin-bottom:1.25rem}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__subsection:not(:last-of-type){margin-bottom:1.875rem}}.bb-footer-menu .bb-footer-menu__subsection-title-link{margin-bottom:1.25rem}.bb-footer-menu .bb-footer-menu__subsection-title{margin-bottom:0;color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1.5rem}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__subsection-title{margin-bottom:1.25rem}}.bb-footer-menu .bb-footer-menu__link{display:flex;align-items:center;justify-content:flex-start;color:#38414d;cursor:pointer;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-footer-menu .bb-footer-menu__link:not(:last-of-type){margin-bottom:1.25rem}.bb-footer-menu .bb-footer-menu__link-label{margin-left:.9375rem;font-size:.5625rem}.bb-footer-menu .bb-footer-menu__contact{display:flex;flex-wrap:wrap;justify-content:flex-start}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__contact{flex-direction:column}}.bb-footer-menu .bb-footer-menu__contact-menu-item{margin-right:2.5rem;margin-bottom:1.25rem;white-space:nowrap}@media (min-width:576px){.bb-footer-menu .bb-footer-menu__contact-menu-item{margin-right:0;margin-bottom:0}.bb-footer-menu .bb-footer-menu__contact-menu-item:not(:last-child){margin-bottom:1.875rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1f = undefined;
/* module identifier */
const __vue_module_identifier__$1f = undefined;
/* functional template */
const __vue_is_functional_template__$1f = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1f = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1d,
  staticRenderFns: __vue_staticRenderFns__$1d
}, __vue_inject_styles__$1f, __vue_script__$1f, __vue_scope_id__$1f, __vue_is_functional_template__$1f, __vue_module_identifier__$1f, false, createInjector, undefined, undefined);

var script$1e = {
  name: 'bb-footer',
  components: {
    BbContactMenuItem: __vue_component__$1g,
    BbContainer: __vue_component__$2q,
    BbFooterMenu: __vue_component__$1f,
    BbRouterLink: __vue_component__$1D,
    BbLogo: __vue_component__$28,
    BbSocialLinks: __vue_component__$1Z,
    BbSeparator: __vue_component__$2a
  },
  props: {
    condensed: {
      type: Boolean,
      default: false
    },
    disclaimer: {
      type: String,
      default: ''
    },
    links: {
      type: Array,
      default: () => []
    },
    contactLinks: {
      type: Array,
      default: () => []
    },
    menuLinks: {
      type: Array,
      default: () => []
    },
    socialLinks: {
      type: Array,
      default: () => []
    },
    logoTo: String,
    baseHeadingLevel: {
      type: Number,
      default: 4,
      validator: val => val > 0 && val < 7
    }
  },
  computed: {
    classes() {
      return {
        'bb-footer--condensed': this.condensed,
        'bb-footer--no-links': !this.linksExist,
        'bb-footer--no-social-links': !this.socialLinksExist
      };
    },
    disclaimerExists() {
      return notEmpty(this.disclaimer) || !!this.$slots.disclaimer;
    },
    contactLinksExist() {
      return notEmpty(this.contactLinks);
    },
    linksExist() {
      return notEmpty(this.links);
    },
    menuLinksExist() {
      return notEmpty(this.menuLinks);
    },
    socialLinksExist() {
      return notEmpty(this.socialLinks);
    }
  }
};

/* script */
const __vue_script__$1e = script$1e;

/* template */
var __vue_render__$1c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-footer",
    class: _vm.classes
  }, [_vm.menuLinksExist ? _c('bb-footer-menu', _vm._g({
    staticClass: "bb-footer__menu",
    attrs: {
      "links": _vm.menuLinks,
      "contacts": _vm.contactLinks,
      "base-heading-level": _vm.baseHeadingLevel
    }
  }, _vm.$listeners)) : _vm._e(), _vm._v(" "), _c('bb-container', {
    staticClass: "bb-footer__container"
  }, [!_vm.condensed ? _c('bb-logo', {
    staticClass: "bb-footer__logo",
    attrs: {
      "to": _vm.logoTo
    }
  }) : _vm._e(), _vm._v(" "), _vm.linksExist ? _c('div', {
    staticClass: "bb-footer__links"
  }, [_vm._l(_vm.links, function (link, index) {
    return [_c('div', {
      key: "link-container-" + index,
      staticClass: "bb-footer__link-container"
    }, [_c('bb-router-link', _vm._b({
      key: "link-" + index,
      staticClass: "bb-footer__link",
      attrs: {
        "color": "gray",
        "no-underline": "",
        "target": "_blank",
        "tabindex": "0"
      },
      on: {
        "click": function ($event) {
          link.id ? _vm.$emit("linkClick", link.id) : null;
        }
      }
    }, 'bb-router-link', link, false)), _vm._v(" "), index < _vm.links.length - 1 ? _c('bb-separator', {
      key: "separator-" + index,
      staticClass: "bb-footer__link-separator",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e()], 1)];
  })], 2) : _vm._e(), _vm._v(" "), _vm.condensed && _vm.contactLinksExist ? _c('div', {
    staticClass: "bb-footer__contact-links"
  }, _vm._l(_vm.contactLinks, function (item, index) {
    return _c('bb-contact-menu-item', _vm._b({
      key: index,
      staticClass: "bb-footer__contact-link",
      attrs: {
        "icon-color": "mint",
        "small-font": ""
      }
    }, 'bb-contact-menu-item', item, false));
  }), 1) : _vm._e(), _vm._v(" "), _vm.socialLinksExist ? _c('bb-social-links', {
    staticClass: "bb-footer__social-links",
    attrs: {
      "links": _vm.socialLinks
    }
  }) : _vm._e(), _vm._v(" "), _vm.disclaimerExists ? _c('div', {
    staticClass: "bb-footer__disclaimer",
    class: {
      'bb-footer__disclaimer--top': _vm.condensed && !_vm.linksExist
    }
  }, [_vm._v("\n      " + _vm._s(_vm.disclaimer) + "\n      "), _vm._t("disclaimer")], 2) : _vm._e()], 1)], 1);
};
var __vue_staticRenderFns__$1c = [];

/* style */
const __vue_inject_styles__$1e = function (inject) {
  if (!inject) return;
  inject("data-v-42120d38_0", {
    source: ".bb-footer{width:100%;padding-bottom:1.25rem;background:#fff}.bb-footer--condensed{padding:1.25rem 0;border-top:solid .0625rem #dee3e8;background:0 0}.bb-footer .bb-footer__menu{margin-bottom:1.875rem}.bb-footer .bb-footer__container{display:flex;flex-flow:row wrap;align-items:flex-start;justify-content:space-between}@media (min-width:576px){.bb-footer .bb-footer__container{flex-direction:row;align-items:center}}.bb-footer .bb-footer__logo{display:none;order:1;margin-bottom:1.25rem}@media (min-width:576px){.bb-footer .bb-footer__logo{display:block;margin-bottom:1.875rem}}.bb-footer__links{display:flex;width:100%;height:max-content;flex-direction:column;order:2;margin-bottom:1.25rem}@media (min-width:576px){.bb-footer__links{width:auto;flex-basis:100%;flex-flow:row wrap;align-items:flex-start;order:3;margin-bottom:.625rem}}@media (min-width:992px){.bb-footer__links{flex-basis:50%;flex-grow:1;justify-content:center;order:2;padding-right:1.25rem}}.bb-footer--condensed .bb-footer__links{flex-basis:100%;margin-bottom:1.25rem}@media (min-width:576px){.bb-footer--condensed .bb-footer__links{margin-bottom:0}}@media (min-width:992px){.bb-footer--condensed .bb-footer__links{justify-content:flex-start;order:3;padding-right:0}}@media (min-width:1200px){.bb-footer--condensed .bb-footer__links{flex-basis:50%;order:0;padding-right:1.25rem}}.bb-footer__link{width:max-content;cursor:pointer;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1rem}.bb-footer__link-container{display:flex;flex-wrap:nowrap;align-items:center}@media (min-width:576px){.bb-footer__link-container{margin-bottom:1.25rem}}.bb-footer__link-container:not(:last-of-type){margin-bottom:.9375rem}@media (min-width:576px){.bb-footer__link-container:last-of-type{margin-bottom:1.25rem}}.bb-footer__link-separator{display:none;border-color:#89a;margin:0 .9375rem}@media (min-width:576px){.bb-footer__link-separator{display:inline-block}}.bb-footer__contact-links{display:flex;flex-wrap:wrap;justify-content:flex-start;order:3}@media (min-width:576px){.bb-footer__contact-links{justify-content:center}}.bb-footer--condensed .bb-footer__contact-links{order:1}@media (min-width:992px){.bb-footer--condensed.bb-footer--no-links .bb-footer__contact-links{order:2}}.bb-footer__contact-link{margin-right:2.5rem;margin-bottom:1.25rem;white-space:nowrap}@media (min-width:576px){.bb-footer--no-social-links .bb-footer__contact-link:last-child{margin-right:0}}@media (min-width:576px){.bb-footer--condensed .bb-footer__contact-link:last-child{margin-right:0}}@media (min-width:1200px){.bb-footer--condensed .bb-footer__contact-link:last-child{margin-right:2.5rem}}@media (min-width:576px){.bb-footer--condensed.bb-footer--no-links .bb-footer__contact-link{margin-right:2.5rem}}.bb-footer__social-links{height:max-content;justify-content:flex-start;order:3;margin-bottom:1.25rem}@media (min-width:576px){.bb-footer__social-links{justify-content:flex-end;order:2;margin-bottom:1.875rem}}@media (min-width:992px){.bb-footer__social-links{order:3}}.bb-footer--condensed .bb-footer__social-links{margin-bottom:1.25rem}@media (min-width:992px){.bb-footer--condensed .bb-footer__social-links{order:2}}.bb-footer--condensed.bb-footer--no-links .bb-footer__social-links{order:2}@media (min-width:992px){.bb-footer--condensed.bb-footer--no-links .bb-footer__social-links{order:3}}.bb-footer__disclaimer{width:100%;order:4;font-size:.75rem}@media (min-width:576px){.bb-footer__disclaimer{font-size:.875rem}}.bb-footer--condensed.bb-footer--no-links .bb-footer__disclaimer{order:3}@media (min-width:992px){.bb-footer--condensed.bb-footer--no-links .bb-footer__disclaimer{flex:1 1 0;order:1;margin-right:1.25rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1e = undefined;
/* module identifier */
const __vue_module_identifier__$1e = undefined;
/* functional template */
const __vue_is_functional_template__$1e = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1c,
  staticRenderFns: __vue_staticRenderFns__$1c
}, __vue_inject_styles__$1e, __vue_script__$1e, __vue_scope_id__$1e, __vue_is_functional_template__$1e, __vue_module_identifier__$1e, false, createInjector, undefined, undefined);

/**
 * Submit button component, usable in Loan Applications and Landings
 */
var script$1d = Vue.extend({
  name: 'bb-submit-button',
  components: {
    BbButton: __vue_component__$2v
  },
  props: {
    /**
     * 2 lines of meta info. Displayed next to the submit button
     */
    metaInfo: {
      type: Array
    },
    /**
     * Toggles the edit icon within the meta info
     */
    editIcon: {
      type: Boolean
    },
    /**
     * Inverted looks. To be used in mobile view while in sticky
     */
    mobileSticky: {
      type: Boolean
    },
    /**
     * Looks disabled to allow form invalidation
     */
    looksDisabled: {
      type: Boolean
    }
  },
  setup(props) {
    const hasMetaInfo = computed(() => notEmpty(props.metaInfo));
    const metaInfoLines = computed(() => {
      var _props$metaInfo;
      return (_props$metaInfo = props.metaInfo) === null || _props$metaInfo === void 0 ? void 0 : _props$metaInfo.slice(0, 2);
    });
    const classes = computed(() => ['bb-submit-button', {
      'bb-submit-button--mobile-sticky-has-meta-info': props.mobileSticky && hasMetaInfo.value
    }]);
    return {
      classes,
      hasMetaInfo,
      metaInfoLines
    };
  },
  methods: {
    onClick() {
      /**
       * Submit button click event
       *
       * @event click
       */
      this.$emit('click');
    },
    onEditIconClick() {
      /**
       * Edit icon click event
       *
       * @event edit
       */
      this.$emit('edit');
    }
  }
});

/* script */
const __vue_script__$1d = script$1d;

/* template */
var __vue_render__$1b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.hasMetaInfo ? _c('div', {
    staticClass: "bb-submit-button__meta"
  }, [_vm.editIcon ? _c('bb-button', {
    staticClass: "bb-submit-button__meta-edit",
    attrs: {
      "display": "square",
      "icon": "ui-pencil",
      "size": "md",
      "no-background": "",
      "color": _vm.mobileSticky ? 'white' : undefined
    },
    on: {
      "click": _vm.onEditIconClick
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-submit-button__meta-info-lines"
  }, _vm._l(_vm.metaInfoLines, function (line) {
    return _c('div', {
      key: line
    }, [_vm._v("\n        " + _vm._s(line) + "\n      ")]);
  }), 0)], 1) : _vm._e(), _vm._v(" "), _c('bb-button', _vm._b({
    staticClass: "bb-submit-button__button",
    class: {
      'bb-button--disabled': _vm.looksDisabled
    },
    attrs: {
      "type": "submit",
      "inverted": _vm.mobileSticky && _vm.hasMetaInfo ? true : _vm.$attrs.inverted,
      "color": _vm.mobileSticky && _vm.hasMetaInfo ? 'white' : _vm.$attrs.color,
      "corner": _vm.mobileSticky && !_vm.hasMetaInfo ? 'sharp' : _vm.$attrs.corner,
      "display": !_vm.hasMetaInfo ? 'block' : _vm.$attrs.display
    },
    on: {
      "click": _vm.onClick
    }
  }, 'bb-button', _vm.$attrs, false))], 1);
};
var __vue_staticRenderFns__$1b = [];

/* style */
const __vue_inject_styles__$1d = function (inject) {
  if (!inject) return;
  inject("data-v-190263f7_0", {
    source: ".bb-submit-button{display:flex;justify-content:space-between}.bb-submit-button--mobile-sticky-has-meta-info{padding:.5rem;background:#0ccca2}.bb-submit-button .bb-submit-button__button{flex-grow:.4}.bb-submit-button .bb-submit-button__meta{display:flex;align-items:center;margin-right:1.25rem}.bb-submit-button .bb-submit-button__meta-info-lines{margin-left:.625rem;color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;line-height:1.5;text-align:left;white-space:nowrap}.bb-submit-button--mobile-sticky-has-meta-info.bb-submit-button .bb-submit-button__meta-info-lines{color:#fff}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1d = undefined;
/* module identifier */
const __vue_module_identifier__$1d = undefined;
/* functional template */
const __vue_is_functional_template__$1d = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1d = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1b,
  staticRenderFns: __vue_staticRenderFns__$1b
}, __vue_inject_styles__$1d, __vue_script__$1d, __vue_scope_id__$1d, __vue_is_functional_template__$1d, __vue_module_identifier__$1d, false, createInjector, undefined, undefined);

Vue.use(VueScreen, gridBreakpoints);
const sortByClientTop = (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top;
var script$1c = defineComponent({
  name: 'bb-form',
  components: {
    BbTicketStub: __vue_component__$1R,
    BbButton: __vue_component__$2v,
    BbSubmitButton: __vue_component__$1d,
    BbSticky: __vue_component__$1W
  },
  directives: {
    resize
  },
  mixins: [validator],
  provide() {
    return {
      [this.formKey]: {
        addField: this.addField,
        removeField: this.removeField
      }
    };
  },
  props: {
    submitMetaInfo: Array,
    submitEditIcon: Boolean,
    restrictEnterSubmitting: Boolean,
    loading: Boolean,
    submitText: {
      type: String,
      default: 'Submit'
    },
    errorText: {
      type: String,
      default: 'Fix errors to continue'
    },
    id: String,
    formKey: {
      type: String,
      default: defaultFormKey
    },
    ticketButtonText: String,
    fieldSpecificError: Boolean,
    ticketButtons: Boolean,
    uppercaseButtons: Boolean,
    staticSubmit: Boolean,
    blockSubmit: {
      type: Boolean,
      default: true
    },
    disabled: Boolean,
    disclaimerText: String,
    disableInvalidFieldScroll: Boolean,
    invalidFieldScrollOffset: {
      type: String,
      default: '0'
    }
  },
  emits: ['progress', 'edit', 'submit', 'cancel'],
  data() {
    return {
      enterTriggeredSubmit: false,
      buttonWidth: null,
      stuck: null,
      fieldsWithCustomValidation: [],
      defaultAriaLabel: 'Form'
    };
  },
  computed: {
    computedSubmitButtonProps() {
      return {
        looksDisabled: this.progress < 1,
        id: this.id ? `${this.id}-submit` : undefined,
        mobileSticky: this.stuck && this.onMobile,
        metaInfo: this.stuck ? this.submitMetaInfo : undefined,
        ...(this.stuck && !this.onMobile && {
          style: {
            width: `${this.buttonWidth}px`
          }
        })
      };
    },
    onMobile() {
      return !this.$screen.md;
    },
    formHasCustomValidation() {
      return this.fieldsWithCustomValidation.length > 0;
    },
    fieldsWithCustomValidationWithErrors() {
      return this.fieldsWithCustomValidation.filter(field => field.hasError);
    },
    invalidFieldsWithCustomValidation() {
      return this.fieldsWithCustomValidation.filter(field => field.isInvalid);
    },
    validFieldsWithCustomValidation() {
      return this.fieldsWithCustomValidation.filter(field => !field.isInvalid);
    },
    fieldPairs() {
      return !this.$validator.mocked ? Object.entries(this.fields) : [];
    },
    allFields() {
      return !this.$validator.mocked ? Object.keys(this.fields).length : [];
    },
    invalidFields() {
      return this.fieldPairs.filter(field => !field[1].valid).map(field => field[0]);
    },
    invalidFieldsSelector() {
      return this.invalidFields.map(name => `[name="${name}"]`).join(', ');
    },
    dirtyFields() {
      return !this.$validator.mocked && this.errors.items.length ? this.errors.items : this.fieldPairs.filter(field => field[1].dirty && !field[1].valid);
    },
    buttonText() {
      if (this.formHasCustomValidation && this.fieldsWithCustomValidationWithErrors.length > 0) {
        return this.fieldSpecificError && this.fieldsWithCustomValidationWithErrors.length === 1 ? `Fix ${this.fieldsWithCustomValidationWithErrors[0].name} field` : this.errorText;
      }
      if (this.invalidFields.length && this.dirtyFields.length) {
        return this.fieldSpecificError && this.invalidFields.length === 1 ? `Fix ${this.invalidFields[0]} field` : this.errorText;
      }
      return this.submitText;
    },
    progress() {
      return this.isForcedToBeFilled() ? 1.00 : this.getValidFieldsRatio();
    }
  },
  watch: {
    progress(ratio) {
      this.$emit('progress', ratio);
    },
    restrictEnterSubmitting() {
      if (!this.restrictEnterSubmitting) {
        this.enterTriggeredSubmit = false;
      }
    }
  },
  mounted() {
    this.updateButtonWidth();
  },
  methods: {
    addField(field) {
      if (field.hasCustomValidation) {
        this.fieldsWithCustomValidation.push(field);
      }
    },
    removeField(fieldName) {
      const index = this.fieldsWithCustomValidation.findIndex(({
        name
      }) => name === fieldName);
      this.fieldsWithCustomValidation.splice(index, 1);
    },
    validateForm() {
      this.fieldsWithCustomValidation.forEach(field => field.validate());
    },
    getValidFieldsRatio() {
      if (this.formHasCustomValidation) {
        const ratio = this.validFieldsWithCustomValidation.length / this.fieldsWithCustomValidation.length;
        const roundedToFive = Math.round(ratio * 20) / 20;
        return parseFloat(roundedToFive || 0);
      }
      const validFields = this.fieldPairs.filter(field => field[1].valid).length;
      const ratio = validFields / this.allFields;
      const roundedToFive = Math.round(ratio * 20) / 20;
      return parseFloat(roundedToFive || 0);
    },
    isEmpty,
    onEdit() {
      this.$emit('edit');
    },
    isForcedToBeFilled() {
      return (!this.allFields || this.$validator.mocked) && this.fieldsWithCustomValidation.length === 0;
    },
    onSubmit() {
      if (this.restrictEnterSubmitting && this.enterTriggeredSubmit) {
        this.enterTriggeredSubmit = false;
        return;
      }
      if (this.$validator.mocked) {
        this.$emit('submit');
        return;
      }
      if (this.formHasCustomValidation) {
        this.validateForm();
        if (this.invalidFieldsWithCustomValidation.length === 0) {
          this.$emit('submit');
        } else if (!this.disableInvalidFieldScroll) {
          this.scrollToFirstInvalidField();
        }
        return;
      }
      this.$validator.validateAll().then(validity => validity ? this.$emit('submit') : this.disableInvalidFieldScroll ? null : this.scrollToFirstInvalidField());
    },
    scrollToFirstInvalidField() {
      const invalidFields = this.formHasCustomValidation ? this.invalidFieldsWithCustomValidation.map(vuelidateField => vuelidateField.$el) : Array.from(this.$refs.form.querySelectorAll(this.invalidFieldsSelector));
      const firstInvalidField = invalidFields.sort(sortByClientTop)[0];
      if (!firstInvalidField) {
        return;
      }
      const offset = firstInvalidField.getBoundingClientRect().top - parseInt(this.invalidFieldScrollOffset);
      jump('#', {
        offset,
        duration: 400
      });
    },
    cancel() {
      this.$emit('cancel');
    },
    updateButtonWidth() {
      if (this.$refs.form) {
        this.buttonWidth = this.$refs.form.clientWidth;
      }
    },
    throttleUpdateButtonWidth: throttle(function () {
      if (this.$refs.form) {
        this.buttonWidth = this.$refs.form.clientWidth;
      }
    }, 100)
  }
});

/* script */
const __vue_script__$1c = script$1c;

/* template */
var __vue_render__$1a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('form', {
    directives: [{
      name: "resize",
      rawName: "v-resize:throttle",
      value: _vm.throttleUpdateButtonWidth,
      expression: "throttleUpdateButtonWidth",
      arg: "throttle"
    }],
    ref: "form",
    staticClass: "bb-form",
    class: {
      'bb-form--mobile-sticky': _vm.onMobile && _vm.stuck
    },
    attrs: {
      "id": _vm.id,
      "aria-label": _vm.defaultAriaLabel
    },
    on: {
      "submit": function ($event) {
        $event.preventDefault();
        $event.stopPropagation();
        return _vm.onSubmit.apply(null, arguments);
      },
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        _vm.enterTriggeredSubmit = true;
      }
    }
  }, [_vm._t("default"), _vm._v(" "), _vm.ticketButtons ? [_c('bb-ticket-stub', {
    staticClass: "bb-form__ticket-submit-wrapper",
    scopedSlots: _vm._u([{
      key: "left",
      fn: function () {
        return [_vm.ticketButtonText ? _c('bb-button', {
          staticClass: "bb-form__ticket-button",
          attrs: {
            "id": _vm.id && _vm.id + "-ticket-button",
            "uppercase": _vm.uppercaseButtons,
            "label": _vm.ticketButtonText,
            "display": "ticket",
            "inverted": "filled"
          },
          on: {
            "click": _vm.cancel
          }
        }) : _vm._e()];
      },
      proxy: true
    }], null, false, 524846625)
  }, [_vm._v(" "), _c('bb-button', {
    staticClass: "bb-form__submit bb-form__submit--ticket",
    class: {
      'bb-button--disabled': _vm.progress < 1
    },
    attrs: {
      "id": _vm.id && _vm.id + "-submit",
      "loading": _vm.loading,
      "uppercase": _vm.uppercaseButtons,
      "disabled": _vm.disabled,
      "label": _vm.ticketButtonText ? _vm.submitText : _vm.buttonText,
      "type": "submit",
      "display": "ticket"
    }
  }), _vm._v(" "), _vm.disclaimerText ? _c('p', {
    staticClass: "bb-form__disclaimer-text",
    attrs: {
      "aria-live": "polite",
      "role": "status"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.disclaimerText) + "\n      ")]) : _vm._e()], 1)] : _vm.staticSubmit ? [_c('div', {
    staticClass: "bb-form__static-submit-wrapper"
  }, [_c('bb-button', {
    staticClass: "bb-form__submit bb-form__submit--static",
    class: {
      'bb-button--disabled': _vm.progress < 1
    },
    attrs: {
      "id": _vm.id && _vm.id + "-submit",
      "uppercase": _vm.uppercaseButtons,
      "loading": _vm.loading,
      "display": _vm.blockSubmit ? 'block' : null,
      "disabled": _vm.disabled,
      "label": _vm.buttonText,
      "type": "submit"
    }
  }), _vm._v(" "), _vm.disclaimerText ? _c('p', {
    staticClass: "bb-form__disclaimer-text",
    attrs: {
      "aria-live": "polite",
      "role": "status"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.disclaimerText) + "\n      ")]) : _vm._e()], 1)] : [_vm._t("submit", function () {
    return [_c('bb-sticky', {
      staticClass: "bb-form__sticky-submit-wrapper",
      attrs: {
        "bleed-mobile-only": ""
      },
      on: {
        "stuck": function ($event) {
          _vm.stuck = $event;
        }
      }
    }, [_c('bb-submit-button', _vm._b({
      staticClass: "bb-form__submit bb-form__submit--sticky",
      attrs: {
        "uppercase": _vm.uppercaseButtons,
        "loading": _vm.loading,
        "disabled": _vm.disabled,
        "label": _vm.buttonText,
        "type": "submit",
        "display": "block",
        "edit-icon": _vm.submitEditIcon
      },
      on: {
        "edit": _vm.onEdit
      }
    }, 'bb-submit-button', _vm.computedSubmitButtonProps, false)), _vm._v(" "), _vm.disclaimerText ? _c('p', {
      staticClass: "bb-form__disclaimer-text",
      attrs: {
        "aria-live": "polite",
        "role": "status"
      }
    }, [_vm._v("\n          " + _vm._s(_vm.disclaimerText) + "\n        ")]) : _vm._e()], 1)];
  }, {
    "disabled": _vm.disabled,
    "loading": _vm.loading,
    "progress": _vm.progress,
    "buttonText": _vm.buttonText
  })]], 2);
};
var __vue_staticRenderFns__$1a = [];

/* style */
const __vue_inject_styles__$1c = function (inject) {
  if (!inject) return;
  inject("data-v-652e96e2_0", {
    source: ".bb-form .bb-form__ticket-submit-wrapper{margin:1.875rem -1.875rem -1.875rem}.bb-form .bb-form__static-submit-wrapper{margin-top:1.875rem}.bb-form .bb-form__sticky-submit-wrapper.bb-sticky{margin:1.875rem 0 0}.bb-form .bb-form__submit--sticky{margin:0 auto}.bb-form .bb-form__disclaimer-text{margin-top:1.25rem;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px;text-align:center}.bb-form--mobile-sticky.bb-form .bb-form__disclaimer-text{margin:.625rem 1.25rem;background:#fff}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1c = undefined;
/* module identifier */
const __vue_module_identifier__$1c = undefined;
/* functional template */
const __vue_is_functional_template__$1c = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1a,
  staticRenderFns: __vue_staticRenderFns__$1a
}, __vue_inject_styles__$1c, __vue_script__$1c, __vue_scope_id__$1c, __vue_is_functional_template__$1c, __vue_module_identifier__$1c, false, createInjector, undefined, undefined);

var genPoints = ((arr, {
  minX,
  minY,
  maxX,
  maxY
}, max, min, maxAmount) => {
  arr = arr.map(item => typeof item === 'number' ? item : item.value);
  const minValue = min - 0.001;
  const gridX = (maxX - minX) / (maxAmount - 1);
  const gridY = (maxY - minY) / (max + 0.001 - minValue);
  return arr.map((value, index) => ({
    x: index * gridX + minX,
    y: maxY - (value - minValue) * gridY + +(index === maxAmount - 1) * 0.00001 - +(index === 0) * 0.00001
  }));
});

var genPath = ((pnts, smooth, {
  maxY
}) => {
  const points = [...pnts];
  const start = points.shift();
  const end = points[points.length - 1];
  const distance = points[0].x - start.x;
  const bezierX = distance / 2;
  let linePath = `M ${start.x},${start.y}`;
  points.forEach((point, index) => {
    if (!smooth) linePath += ` L${point.x},${point.y}`;else {
      const prev = points[index - 1] || start;
      linePath += ` C ${bezierX + prev.x},${prev.y} ${bezierX + prev.x},${point.y} ${point.x},${point.y}`;
    }
  });
  let fillPath = linePath;
  if (end.Y !== maxY) fillPath += ` L${end.x},${maxY}`;
  if (start.Y !== maxY) fillPath += ` L${start.x},${maxY}`;
  fillPath += ' Z';
  return {
    linePath,
    fillPath
  };
});

var script$1b = {
  name: 'bb-trend-chart-curve',
  props: {
    boundary: {
      required: true,
      type: Object
    },
    minValue: {
      required: true,
      type: Number
    },
    maxValue: {
      required: true,
      type: Number
    },
    maxAmount: {
      required: true,
      type: Number
    },
    activeLineParams: {
      type: Object
    },
    data: {
      required: true,
      type: Array
    },
    className: {
      type: String
    },
    smooth: {
      default: false,
      type: Boolean
    },
    stroke: {
      default: true,
      type: Boolean
    },
    fill: {
      default: false,
      type: Boolean
    },
    showPoints: {
      default: false,
      type: Boolean
    }
  },
  computed: {
    points() {
      return genPoints(this.data, this.boundary, this.maxValue, this.minValue, this.maxAmount);
    },
    paths() {
      return genPath(this.points, this.smooth, this.boundary);
    }
  },
  render(h) {
    const children = [];
    if (this.fill && this.paths && this.paths.fillPath) {
      children.push(h('path', {
        class: 'fill',
        attrs: {
          d: this.paths.fillPath,
          fill: 'rgba(0,0,0,0.15)'
        }
      }));
    }
    if (this.stroke && this.paths && this.paths.linePath) {
      children.push(h('path', {
        class: 'stroke',
        attrs: {
          d: this.paths.linePath,
          fill: 'none',
          stroke: 'black'
        }
      }));
    }
    if (this.showPoints && this.points) {
      children.push(h('g', {
        class: 'points'
      }, this.points.map((point, i) => h('circle', {
        class: {
          point: true,
          'is-active': this.activeLineParams && this.activeLineParams.index === i
        },
        attrs: {
          cx: point.x,
          cy: point.y,
          r: 2,
          stroke: '#000000',
          'stroke-width': 1
        }
      }))));
    }
    return h('g', {
      class: this.className
    }, children);
  }
};

/* script */
const __vue_script__$1b = script$1b;

/* template */

/* style */
const __vue_inject_styles__$1b = undefined;
/* scoped */
const __vue_scope_id__$1b = undefined;
/* module identifier */
const __vue_module_identifier__$1b = undefined;
/* functional template */
const __vue_is_functional_template__$1b = undefined;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1b = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$1b, __vue_script__$1b, __vue_scope_id__$1b, __vue_is_functional_template__$1b, __vue_module_identifier__$1b, false, undefined, undefined, undefined);

var script$1a = {
  name: 'bb-trend-chart',
  components: {
    BbTrendChartCurve: __vue_component__$1b
  },
  props: {
    datasets: {
      required: true,
      type: Array
    },
    min: {
      type: Number
    },
    interactive: {
      default: false,
      type: Boolean
    }
  },
  emits: ['mouseMove'],
  data() {
    return {
      padding: 5,
      width: null,
      height: null,
      labelsOverflowObject: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      },
      activeLine: null,
      activeLineParams: null,
      resizeObserver: null
    };
  },
  computed: {
    boundary() {
      const {
        width,
        height,
        labelsOverflowObject
      } = this;
      const boundary = {
        minX: this.padding + labelsOverflowObject.left,
        minY: this.padding + labelsOverflowObject.top,
        maxX: width - this.padding - labelsOverflowObject.right,
        maxY: height - this.padding - labelsOverflowObject.bottom
      };
      return boundary;
    },
    params() {
      let maxValue = -Infinity;
      let minValue = Infinity;
      let maxAmount = 0;
      this.datasets.forEach(dataset => {
        const dataArr = dataset.data.map(item => typeof item === 'number' ? item : item.value);
        const max = Math.max(...dataArr);
        if (max > maxValue) maxValue = max;
        const min = Math.min(...dataArr);
        if (min < minValue) minValue = min;
        if (dataArr.length > maxAmount) maxAmount = dataArr.length;
      });
      if (this.min !== undefined && this.min < minValue) minValue = this.min;
      return {
        maxValue,
        minValue,
        maxAmount
      };
    },
    chartOverlayParams() {
      const {
        boundary
      } = this;
      const width = boundary.maxX - boundary.minX;
      const height = boundary.maxY - boundary.minY;
      return {
        x: boundary.minX,
        y: boundary.minY,
        width: width > 0 ? width : 0,
        height: height > 0 ? height : 0,
        opacity: 0
      };
    },
    chartAxesXCoords() {
      const axes = [];
      const step = (this.boundary.maxX - this.boundary.minX) / (this.params.maxAmount - 1);
      for (let i = 0; i < this.params.maxAmount; i++) {
        axes.push(step * i + this.boundary.minX);
      }
      return axes;
    }
  },
  watch: {
    activeLine(val) {
      const data = [];
      if (val) {
        this.activeLineParams = {
          index: this.chartAxesXCoords.indexOf(this.activeLine)
        };
        this.datasets.forEach(dataset => {
          data.push(dataset.data[this.activeLineParams.index]);
        });
      }
      this.$emit('mouseMove', this.activeLineParams ? {
        ...this.activeLineParams,
        data
      } : null);
    }
  },
  mounted() {
    this.addResizeListener();
  },
  destroyed() {
    this.removeResizeListener();
  },
  methods: {
    addResizeListener() {
      this.resizeObserver = new ResizeObserver(this.setSize);
      this.resizeObserver.observe(document.body);
    },
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(document.body);
      }
    },
    setSize() {
      const params = this.$refs.chart.getBoundingClientRect();
      this.width = params.width;
      this.height = params.height;
    },
    getNearestCoordinate(val) {
      return this.chartAxesXCoords.reduce((p, n) => Math.abs(p) > Math.abs(n - val) ? n - val : p, Infinity) + val;
    },
    mouseMove(e) {
      if (this.$refs.chart !== undefined) {
        const rect = this.$refs.chart.getBoundingClientRect();
        this.activeLine = this.getNearestCoordinate(e.clientX - rect.left);
      }
    },
    mouseOut() {
      this.activeLine = null;
      this.activeLineParams = null;
    }
  },
  render(h) {
    const children = [];
    if (this.interactive && this.chartOverlayParams) {
      children.push(h('line', {
        class: 'active-line',
        ref: 'active-line',
        attrs: {
          x1: this.activeLine || this.boundary.minX,
          x2: this.activeLine || this.boundary.minX,
          y1: this.boundary.minY,
          y2: this.boundary.maxY,
          stroke: 'black',
          visibility: this.activeLine ? 'visible' : 'hidden'
        }
      }));
    }
    this.datasets.forEach(dataset => {
      children.push(h(__vue_component__$1b, {
        class: 'curve',
        attrs: {
          ...dataset,
          boundary: this.boundary,
          minValue: this.params.minValue,
          maxValue: this.params.maxValue,
          maxAmount: this.params.maxAmount,
          activeLineParams: this.activeLineParams
        }
      }));
    });
    if (this.interactive && this.chartOverlayParams) {
      children.push(h('rect', {
        ref: 'interactive-area',
        attrs: {
          ...this.chartOverlayParams
        },
        on: {
          mousemove: e => this.mouseMove(e),
          mouseout: () => this.mouseOut()
        }
      }));
    }
    return h('svg', {
      class: 'bb-trend-chart',
      ref: 'chart',
      attrs: {
        xmlns: 'http://www.w3.org/2000/svg',
        width: '100%',
        height: '100%'
      }
    }, children);
  }
};

/* script */
const __vue_script__$1a = script$1a;

/* template */

/* style */
const __vue_inject_styles__$1a = undefined;
/* scoped */
const __vue_scope_id__$1a = undefined;
/* module identifier */
const __vue_module_identifier__$1a = undefined;
/* functional template */
const __vue_is_functional_template__$1a = undefined;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1a = /*#__PURE__*/normalizeComponent({}, __vue_inject_styles__$1a, __vue_script__$1a, __vue_scope_id__$1a, __vue_is_functional_template__$1a, __vue_module_identifier__$1a, false, undefined, undefined, undefined);

var script$19 = {
  name: 'bb-graph',
  components: {
    BbTrendChart: __vue_component__$1a
  },
  props: {
    value: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  emits: ['hoverData'],
  data() {
    return {
      tooltipData: null,
      popper: null,
      popperIsActive: false,
      interactive: true
    };
  },
  computed: {
    datasets() {
      return [{
        data: this.value.data,
        fill: true,
        smooth: true,
        showPoints: true,
        className: 'curve-graph'
      }];
    }
  },
  mounted() {
    this.initPopper();
  },
  methods: {
    hover(params) {
      if (params) {
        this.$emit('hoverData', params);
      }
      this.popperIsActive = !!params;
      this.popper.update();
      this.tooltipData = params || null;
    },
    initPopper() {
      const chart = this.$refs.trendChart.$el;
      const ref = chart.querySelector('.points');
      const tooltip = this.$refs.tooltip;
      this.popper = createPopper(ref, tooltip, {
        placement: 'top',
        modifiers: [{
          name: 'offset',
          options: {
            offset: [0, 5]
          }
        }, {
          name: 'hide',
          enabled: true
        }, {
          name: 'arrow',
          enabled: true
        }]
      });
    }
  }
};

/* script */
const __vue_script__$19 = script$19;

/* template */
var __vue_render__$19 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-graph"
  }, [_c('svg', {
    staticClass: "bb-graph__graph",
    attrs: {
      "aria-hidden": "true",
      "focusable": "false"
    }
  }, [_c('defs', [_c('linearGradient', {
    attrs: {
      "id": "graphFill",
      "x1": "1",
      "x2": "1",
      "y1": "0",
      "y2": "1"
    }
  }, [_c('stop', {
    staticClass: "bb-graph__gradient-stop-mint",
    attrs: {
      "offset": "0%"
    }
  }), _vm._v(" "), _c('stop', {
    staticClass: "bb-graph__gradient-stop-white",
    attrs: {
      "offset": "90%"
    }
  })], 1)], 1)]), _vm._v(" "), _vm.value.data.length ? _c('bb-trend-chart', {
    ref: "trendChart",
    attrs: {
      "datasets": _vm.datasets,
      "min": 0,
      "interactive": true
    },
    on: {
      "mouseMove": _vm.hover
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    ref: "tooltip",
    staticClass: "bb-graph__tooltip",
    class: {
      'is-active': _vm.tooltipData
    },
    attrs: {
      "role": "tooltip"
    }
  }, [_vm.tooltipData ? _c('div', [_vm._v("\n      " + _vm._s(_vm.value.prefix) + _vm._s(_vm.tooltipData.data && _vm.tooltipData.data[0]) + _vm._s(_vm.value.suffix) + "\n    ")]) : _vm._e(), _vm._v(" "), _c('div', {
    ref: "arrow",
    staticClass: "bb-graph__arrow"
  })])], 1);
};
var __vue_staticRenderFns__$19 = [];

/* style */
const __vue_inject_styles__$19 = function (inject) {
  if (!inject) return;
  inject("data-v-0f9fa360_0", {
    source: ".bb-graph__graph{position:absolute;width:0;height:0}.bb-graph .curve-graph .stroke{stroke:#0ccca2;stroke-width:.125rem}.bb-graph .curve-graph .fill{width:100%;fill:url(#graphFill);fill-opacity:.5}.bb-graph .point{display:none;fill:#fff;stroke:#0ccca2;stroke-width:.125rem}.bb-graph .point.is-active{display:block}.bb-graph .active-line{stroke:#0ccca2}.bb-graph__gradient-stop-mint{stop-color:#0ccca2}.bb-graph__gradient-stop-white{stop-color:#fff}.bb-graph__tooltip{display:flex;align-items:center;justify-content:center;padding:.3125rem .625rem;border-radius:.3125rem;margin:.3125rem;background:#0ccca2;color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;pointer-events:none}.bb-graph__tooltip:not(.is-active){display:none}.bb-graph__arrow{position:absolute;width:.3125rem;height:.3125rem;background:#0ccca2;transform:rotate(45deg)!important}.bb-graph__tooltip[data-popper-placement=top] .bb-graph__arrow{bottom:-.1875rem}.bb-graph__tooltip[data-popper-placement=bottom] .bb-graph__arrow{top:-.1875rem}.bb-graph__tooltip[data-popper-placement=right] .bb-graph__arrow{left:-.1875rem}.bb-graph__tooltip[data-popper-placement=left] .bb-graph__arrow{right:-.1875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$19 = undefined;
/* module identifier */
const __vue_module_identifier__$19 = undefined;
/* functional template */
const __vue_is_functional_template__$19 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$19 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$19,
  staticRenderFns: __vue_staticRenderFns__$19
}, __vue_inject_styles__$19, __vue_script__$19, __vue_scope_id__$19, __vue_is_functional_template__$19, __vue_module_identifier__$19, false, createInjector, undefined, undefined);

var script$18 = {
  name: 'bb-graph-card',
  components: {
    BbIcon: __vue_component__$2C,
    BbGraph: __vue_component__$19,
    BbTooltip: __vue_component__$2B
  },
  props: {
    title: String,
    subtitle: String,
    graphLabel: String,
    tooltipMessage: String,
    pill: Boolean,
    graph: {
      type: Object,
      default() {
        return {};
      }
    },
    baseHeadingLevel: {
      type: Number,
      default: 3,
      validator: val => val > 0 && val < 5
    }
  },
  data() {
    return {
      showTooltip: false,
      hoverIndex: null
    };
  },
  methods: {
    onHoverData(event) {
      this.hoverIndex = event.index;
    },
    removeHoverIndex() {
      this.hoverIndex = null;
    }
  }
};

/* script */
const __vue_script__$18 = script$18;

/* template */
var __vue_render__$18 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-graph-card",
    class: {
      'bb-graph-card--pill': _vm.pill
    }
  }, [_c("h" + _vm.baseHeadingLevel, {
    tag: "component",
    staticClass: "bb-graph-card__title"
  }, [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]), _vm._v(" "), _vm.tooltipMessage ? _c('bb-tooltip', {
    staticClass: "bb-graph-card__tooltip",
    scopedSlots: _vm._u([{
      key: "trigger",
      fn: function () {
        return [_c('bb-icon', {
          attrs: {
            "name": "ui-help-circle-outline",
            "fill": "gray-50",
            "size": "18"
          }
        })];
      },
      proxy: true
    }], null, false, 3669756233)
  }, [_vm._v("\n    " + _vm._s(_vm.tooltipMessage) + "\n  ")]) : _vm._e(), _vm._v(" "), _c("h" + (_vm.baseHeadingLevel + 1), {
    tag: "component",
    staticClass: "bb-graph-card__subtitle"
  }, [_vm._v("\n    " + _vm._s(_vm.subtitle) + "\n  ")]), _vm._v(" "), !_vm.pill ? [_c('div', {
    staticClass: "bb-graph-card__divider"
  }, [_vm._v("\n       \n    ")]), _vm._v(" "), _c("h" + (_vm.baseHeadingLevel + 2), {
    tag: "component",
    staticClass: "bb-graph-card__graph-label"
  }, [_vm._v("\n      " + _vm._s(_vm.graphLabel) + "\n    ")]), _vm._v(" "), _c('div', {
    staticClass: "bb-graph-card__graph-wrapper",
    on: {
      "mouseleave": _vm.removeHoverIndex
    }
  }, [_c('bb-graph', {
    staticClass: "bb-graph-card__graph",
    attrs: {
      "value": _vm.graph,
      "aria-hidden": "true"
    },
    on: {
      "hoverData": _vm.onHoverData
    }
  }), _vm._v(" "), _c('ol', {
    staticClass: "bb-graph-card__footer-labels"
  }, _vm._l(_vm.graph.labels, function (item, i) {
    return _c('li', {
      key: i,
      staticClass: "bb-graph-card__footer-label",
      class: {
        'bb-graph-card__footer-label--hover': i === _vm.hoverIndex
      },
      attrs: {
        "aria-label": _vm.graph.ariaLabels && _vm.graph.ariaLabels[i] ? _vm.graph.ariaLabels[i] : item + " " + _vm.graph.prefix + _vm.graph.data[i] + _vm.graph.suffix
      }
    }, [_vm._v("\n          " + _vm._s(item) + "\n        ")]);
  }), 0)], 1)] : _vm._e()], 2);
};
var __vue_staticRenderFns__$18 = [];

/* style */
const __vue_inject_styles__$18 = function (inject) {
  if (!inject) return;
  inject("data-v-2a50604b_0", {
    source: ".bb-graph-card{position:relative;display:flex;width:100%;min-width:15rem;max-width:21.25rem;height:21.25rem;flex-direction:column;align-items:normal;padding:1.875rem 1.25rem 1.25rem;border:none;border-radius:.3125rem;background:#fff;box-shadow:0 .0625rem 1rem 0 rgba(0,0,0,.1);outline:0;text-align:left;transition:box-shadow .3s,border .3s}.bb-graph-card--pill{height:6.25rem;padding:1.5625rem 1.25rem 1.25rem}.bb-graph-card__title{justify-content:flex-start;margin-bottom:.25rem;color:#00285a;font-size:1.5rem;line-height:1.75rem}.bb-graph-card__subtitle{justify-content:flex-end;margin-bottom:0;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px;line-height:1.5rem}.bb-graph-card__tooltip{position:absolute;top:1.5rem;right:1.25rem;display:flex;cursor:pointer}.bb-graph-card__divider{width:1.875rem;height:.1875rem;margin-top:20px;background-color:#0ccca2}.bb-graph-card__graph-label{margin-top:20px;color:#00285a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:11px;text-transform:uppercase}.bb-graph-card__graph-wrapper{width:calc(100% + 40px);margin-top:2.8125rem;margin-left:-20px}.bb-graph-card__footer-labels{position:absolute;bottom:0;left:0;display:flex;width:100%;height:1.5625rem;align-items:center;justify-content:space-between;padding:0 .3125rem;background-color:#fff;pointer-events:none;table-layout:fixed}.bb-graph-card__footer-label{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:11px;text-transform:uppercase}.bb-graph-card__footer-label--hover{color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$18 = undefined;
/* module identifier */
const __vue_module_identifier__$18 = undefined;
/* functional template */
const __vue_is_functional_template__$18 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$18 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$18,
  staticRenderFns: __vue_staticRenderFns__$18
}, __vue_inject_styles__$18, __vue_script__$18, __vue_scope_id__$18, __vue_is_functional_template__$18, __vue_module_identifier__$18, false, createInjector, undefined, undefined);

const DEFAULT_BREAKPOINTS = {
  md: '22',
  lg: '21',
  xl: '18'
};
const BREAKPOINT_PROPS = ['col', 'sm', 'md', 'lg', 'xl'];
var script$17 = {
  name: 'bb-header',
  components: {
    BbCol: __vue_component__$2r,
    BbRow: __vue_component__$22,
    BbContainer: __vue_component__$2q,
    BbHeading: __vue_component__$2j
  },
  inheritAttrs: false,
  props: {
    title: String,
    headingLevel: {
      type: String,
      default: 'h1',
      validator: headingLevel => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(headingLevel)
    }
  },
  computed: {
    breakpoints() {
      const breakpoints = Object.entries(this.$attrs)
      .filter(([attributeName, _]) => BREAKPOINT_PROPS.some(breakpointName => attributeName === breakpointName));
      return breakpoints.length ? Object.fromEntries(breakpoints) : DEFAULT_BREAKPOINTS;
    },
    otherAttributes() {
      const otherAttributes = Object.entries(this.$attrs)
      .filter(([attributeName, _]) => !BREAKPOINT_PROPS.some(breakpointName => attributeName === breakpointName));
      return Object.fromEntries(otherAttributes);
    }
  }
};

/* script */
const __vue_script__$17 = script$17;

/* template */
var __vue_render__$17 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('header', _vm._b({
    staticClass: "bb-header"
  }, 'header', _vm.otherAttributes, false), [_c('bb-container', [_c('bb-row', [_c('bb-col', _vm._b({}, 'bb-col', _vm.breakpoints, false), [_c('bb-heading', {
    staticClass: "bb-header__heading",
    attrs: {
      "title": _vm.title,
      "tag-name": _vm.headingLevel,
      "white": ""
    }
  })], 1)], 1)], 1)], 1);
};
var __vue_staticRenderFns__$17 = [];

/* style */
const __vue_inject_styles__$17 = function (inject) {
  if (!inject) return;
  inject("data-v-396cb4f5_0", {
    source: ".bb-header{display:flex;align-items:center;padding:1.875rem 0;background:#0ccca2}@media (min-width:768px){.bb-header{padding:4.375rem 0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$17 = undefined;
/* module identifier */
const __vue_module_identifier__$17 = undefined;
/* functional template */
const __vue_is_functional_template__$17 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$17 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$17,
  staticRenderFns: __vue_staticRenderFns__$17
}, __vue_inject_styles__$17, __vue_script__$17, __vue_scope_id__$17, __vue_is_functional_template__$17, __vue_module_identifier__$17, false, createInjector, undefined, undefined);

var script$16 = {
  name: 'bb-horizontal-menu',
  components: {
    BbLink: __vue_component__$2p
  },
  props: {
    routes: {
      type: Array,
      required: true
    },
    showZero: Boolean,
    centered: Boolean,
    isStickyOnMobile: {
      type: Boolean,
      default: true
    },
    hideOnScroll: Boolean,
    linkColor: {
      type: String,
      validator: f => ['navy', 'mint', 'green'
      ].includes(f)
    },
    unitsAriaLabel: {
      type: String,
      default: 'Items'
    }
  },
  data() {
    return {
      hidden: false,
      lastScrollTop: 0
    };
  },
  computed: {
    menuClasses() {
      return [this.linkColor && `bb-horizontal-menu--links-${this.linkColor}`, {
        'bb-horizontal-menu--centered': this.centered,
        'bb-horizontal-menu--hidden': this.hidden,
        'is-sticky-mobile': this.isStickyOnMobile
      }];
    }
  },
  mounted() {
    if (this.isStickyOnMobile && this.hideOnScroll) {
      window.addEventListener('scroll', this.listenScroll);
    }
  },
  destroyed() {
    window.removeEventListener('scroll', this.listenScroll);
  },
  methods: {
    linkIsActive(route) {
      return {
        'is-active': route.active
      };
    },
    isCountDisplayed(amount) {
      if (amount === '0' || amount === 0) {
        return this.showZero;
      }
      return amount;
    },
    listenScroll: throttle(function () {
      const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
      if (scrollTop < this.lastScrollTop) {
        if (this.hidden) {
          this.hidden = false;
        }
      } else if (this.lastScrollTop > document.documentElement.clientHeight / 2) {
        this.hidden = true;
      }
      this.lastScrollTop = scrollTop;
    }, 100)
  }
};

/* script */
const __vue_script__$16 = script$16;

/* template */
var __vue_render__$16 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('nav', {
    staticClass: "bb-horizontal-menu",
    class: _vm.menuClasses
  }, [_c('div', {
    staticClass: "bb-horizontal-menu__view"
  }, [_c('ul', {
    staticClass: "bb-horizontal-menu__nav"
  }, _vm._l(_vm.routes, function (route) {
    return _c('li', {
      key: route.title,
      staticClass: "bb-horizontal-menu__item"
    }, [_c('bb-link', {
      staticClass: "bb-horizontal-menu__link",
      class: _vm.linkIsActive(route),
      attrs: {
        "to": route.link || route.path,
        "target": route.link ? '_blank' : '_self',
        "color": "gray",
        "aria-current": route.active ? 'page' : null,
        "no-underline": ""
      }
    }, [_c('span', {
      staticClass: "bb-horizontal-menu__link-content-wrapper"
    }, [_vm._t("link", function () {
      return [_vm._v("\n              " + _vm._s(route.title) + "\n              "), _vm.isCountDisplayed(route.count) ? _c('span', {
        staticClass: "bb-horizontal-menu__count"
      }, [_vm._v("\n                " + _vm._s(route.count) + "\n              ")]) : _vm._e(), _vm._v(" "), _c('span', {
        staticClass: "visually-hidden"
      }, [_vm._v("\n                " + _vm._s(_vm.unitsAriaLabel) + "\n              ")])];
    }, null, route)], 2)])], 1);
  }), 0)])]);
};
var __vue_staticRenderFns__$16 = [];

/* style */
const __vue_inject_styles__$16 = function (inject) {
  if (!inject) return;
  inject("data-v-03c53e65_0", {
    source: ".bb-horizontal-menu{overflow:hidden;height:2.8125rem;border-top:.0625rem solid #eeeff2;background:#fff;transition:transform .4s}@media (min-width:768px){.bb-horizontal-menu{overflow:visible;height:auto;border-top:0;background:0 0}}.bb-horizontal-menu.is-sticky-mobile{position:fixed;z-index:1200;right:0;bottom:0;left:0}@media (min-width:768px){.bb-horizontal-menu.is-sticky-mobile{position:static}}.bb-horizontal-menu__view{overflow:auto;width:100%;-webkit-overflow-scrolling:touch;white-space:nowrap}@media (min-width:768px){.bb-horizontal-menu__view{overflow:visible;white-space:normal}}.bb-horizontal-menu__nav{display:flex;width:100%;padding:0 0 1.25rem;user-select:none}@media (min-width:768px){.bb-horizontal-menu__nav{position:relative;padding-bottom:0;border-bottom:.125rem solid #eeeff2}}.bb-horizontal-menu--hidden{transform:translateY(100%)}@media (min-width:768px){.bb-horizontal-menu--hidden{transform:none}}.bb-horizontal-menu__item{display:flex;flex:1 0 auto;padding:0;border-left:none;margin:0;cursor:pointer}@media (min-width:768px){.bb-horizontal-menu__item{flex:unset;justify-content:flex-start}}@media (min-width:768px){.bb-horizontal-menu--centered .bb-horizontal-menu__item{flex:1 0 auto}}.bb-horizontal-menu__link.bb-link.bb-link{display:flex;width:100%;min-height:2.8125rem;align-items:center;padding:0 .625rem;margin:0 auto;color:#89a;font-size:11px;text-transform:uppercase;transition:color 150ms}@media (min-width:768px){.bb-horizontal-menu__link.bb-link.bb-link{min-height:unset;padding-right:1.875rem;padding-left:1.875rem;font-size:14px}}.bb-horizontal-menu__link.bb-link.bb-link.is-active,.bb-horizontal-menu__link.bb-link.bb-link:hover{color:#00285a}.bb-horizontal-menu__link.is-active{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}@media (min-width:768px){.bb-horizontal-menu--centered .bb-horizontal-menu__link{display:flex;width:100%;justify-content:center;padding:0}}@media (min-width:768px){.bb-horizontal-menu__item:first-child .bb-horizontal-menu__link{padding-left:0}}.bb-horizontal-menu--links-navy .bb-horizontal-menu__link.is-active,.bb-horizontal-menu--links-navy .bb-horizontal-menu__link:hover{color:#00285a}.bb-horizontal-menu--links-mint .bb-horizontal-menu__link.is-active,.bb-horizontal-menu--links-mint .bb-horizontal-menu__link:hover{color:#0ccca2}.bb-horizontal-menu--links-green .bb-horizontal-menu__link.is-active,.bb-horizontal-menu--links-green .bb-horizontal-menu__link:hover{color:#0ccca2}.bb-horizontal-menu__link-content-wrapper{display:inline-flex;width:100%;height:100%;align-items:center;justify-content:center}@media (min-width:768px){.bb-horizontal-menu__link-content-wrapper{position:relative;padding-bottom:1rem}}.is-active>.bb-horizontal-menu__link-content-wrapper::after{position:absolute;top:0;right:0;left:0;height:.125rem;background:#0ccca2;content:\"\"}@media (min-width:768px){.is-active>.bb-horizontal-menu__link-content-wrapper::after{top:auto;bottom:-.125rem}}.bb-horizontal-menu__count{display:flex;width:1.375rem;height:1.375rem;flex-shrink:0;align-items:center;justify-content:center;border-radius:100%;margin-left:1.125rem;background:#ffbfb3;color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:9px;transition:background-color 150ms}.bb-horizontal-menu__item:hover .bb-horizontal-menu__count,.is-active>*>.bb-horizontal-menu__count{background-color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$16 = undefined;
/* module identifier */
const __vue_module_identifier__$16 = undefined;
/* functional template */
const __vue_is_functional_template__$16 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$16 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$16,
  staticRenderFns: __vue_staticRenderFns__$16
}, __vue_inject_styles__$16, __vue_script__$16, __vue_scope_id__$16, __vue_is_functional_template__$16, __vue_module_identifier__$16, false, createInjector, undefined, undefined);

var script$15 = {
  name: 'bb-tag',
  components: {
    BbButton: __vue_component__$2v
  },
  props: {
    title: String,
    name: {
      type: String,
      required: true
    },
    value: String,
    color: String,
    unremovable: Boolean,
    condensed: Boolean,
    compressed: Boolean,
    removeButtonAriaLabel: {
      type: String,
      default: 'Remove'
    }
  },
  emits: ['remove'],
  computed: {
    val() {
      return this.value || this.name;
    },
    statusClass() {
      return this.color ? `color-${this.color}` : this.$slots.default ? 'bb-tag__status--symbol' : false;
    },
    tagClass() {
      return {
        'bb-tag--condensed': this.condensed,
        'bb-tag--compressed': this.compressed
      };
    },
    defaultAriaLabel() {
      return [this.title, this.name].filter(Boolean).join(', ');
    }
  },
  methods: {
    click() {
      this.$emit('remove', this.val);
    }
  }
};

/* script */
const __vue_script__$15 = script$15;

/* template */
var __vue_render__$15 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: ['bb-tag', _vm.tagClass],
    attrs: {
      "aria-label": _vm.defaultAriaLabel,
      "role": "group"
    }
  }, [_c('div', {
    staticClass: "bb-tag__contents"
  }, [_vm.statusClass ? _c('span', {
    class: ['bb-tag__status', _vm.statusClass],
    attrs: {
      "aria-hidden": "true"
    }
  }, [_vm._t("default", function () {
    return [_vm.color ? [_vm._v("\n          •\n        ")] : _vm._e()];
  })], 2) : _vm._e(), _vm._v(" "), _c('span', {
    staticClass: "bb-tag__name",
    attrs: {
      "aria-hidden": "true"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.name) + "\n    ")]), _vm._v(" "), !_vm.unremovable ? _c('bb-button', {
    staticClass: "bb-tag__close",
    attrs: {
      "no-background": _vm.compressed,
      "icon-color": _vm.compressed ? 'gray-70' : 'white',
      "type": "button",
      "size": "sm",
      "color": "gray",
      "icon": "ui-close",
      "display": "circle",
      "aria-label": _vm.removeButtonAriaLabel
    },
    on: {
      "click": _vm.click
    }
  }) : _vm._e()], 1), _vm._v(" "), _vm.title ? _c('span', {
    staticClass: "bb-tag__title",
    attrs: {
      "aria-hidden": "true"
    }
  }, [_c('span', {
    staticClass: "bb-tag__title-text"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")])]) : _vm._e()]);
};
var __vue_staticRenderFns__$15 = [];

/* style */
const __vue_inject_styles__$15 = function (inject) {
  if (!inject) return;
  inject("data-v-23c04233_0", {
    source: ".bb-tag__name,.bb-tag__title-text{overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.bb-tag{display:flex;max-width:100%;height:1.875rem;flex-direction:column-reverse;margin-right:.625rem;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}.bb-tag--condensed{height:1.625rem}.bb-tag--compressed{height:1.375rem}.bb-tag__contents{display:flex;max-width:100%;height:100%;flex:1 0 auto;align-items:center;padding:.1875rem .9375rem;border:.0625rem solid #dee3e8;border-radius:.9375rem;background:#fff;line-height:1}.bb-tag__name{flex-grow:1;color:#00285a}.bb-tag__status--symbol+.bb-tag__name{margin-left:.3125rem}.bb-tag__status{min-width:.9375rem;padding-left:.1875rem;margin-left:-.9375rem;font-size:1.375rem}.bb-tag__status--symbol{padding-left:.3125rem;font-size:.875rem}.bb-tag__title{position:relative;top:.75rem;padding-right:1.25rem;padding-left:.6875rem}.bb-tag--compressed .bb-tag__title,.bb-tag--condensed .bb-tag__title{top:.625rem}.bb-tag__title-text{display:inline-block;max-width:100%;padding-right:.3125rem;padding-left:.3125rem;background-color:#fff;color:#57697a;font-size:.5625rem}.bb-tag__close.bb-button.bb-button{width:1.25rem;height:1.25rem;margin-right:-.75rem}.bb-tag__name+.bb-tag__close{margin-left:.625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$15 = undefined;
/* module identifier */
const __vue_module_identifier__$15 = undefined;
/* functional template */
const __vue_is_functional_template__$15 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$15 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$15,
  staticRenderFns: __vue_staticRenderFns__$15
}, __vue_inject_styles__$15, __vue_script__$15, __vue_scope_id__$15, __vue_is_functional_template__$15, __vue_module_identifier__$15, false, createInjector, undefined, undefined);

var Icon$m = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg4061968078__a bb-icon__dynamic-fill",attrs:{"d":"M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"}})]) } };

Vue.component('icon-ui-magnify', Icon$m);

var script$14 = {
  name: 'bb-typeahead',
  components: {
    BbIcon: __vue_component__$2C,
    VueMultiselect,
    BbTag: __vue_component__$15,
    BbDropdownItem: __vue_component__$2o,
    BbButton: __vue_component__$2v
  },
  mixins: [FieldExtensions, Field, ErrorBubble, validator, veeValidationMixin, vuelidateValidator],
  props: {
    searchSuggestionsLabel: {
      type: String,
      default: 'Suggestions'
    },
    addOn: String,
    helpText: String,
    helpActive: Boolean,
    disabled: Boolean,
    disableSearch: Boolean,
    maxElementsLabel: String,
    optionAsSingleLabel: Boolean,
    labelPropName: String,
    value: {
      type: null,
      default() {
        return [];
      }
    },
    flavor: {
      validator: val => ['search', ''].includes(val)
    },
    closeOnSelect: Boolean,
    medium: Boolean,
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    noOptions: {
      type: String,
      default: 'Type to search'
    },
    noResult: {
      type: String,
      default: 'No results found for ##'
    },
    maxHeight: {
      type: Number,
      default: 310
    },
    vvName: String,
    vvValuePath: String,
    rules: Object,
    mandatory: Boolean,
    caretButtonAriaLabel: {
      type: String,
      default: 'Open options'
    }
  },
  data() {
    return {
      val: this.value || null,
      uuid: uuid(),
      searchQuery: '',
      localOptions: [],
      consideredValue: '',
      consideredValueIndex: 0,
      isOpen: false
    };
  },
  computed: {
    fieldId() {
      return this.id || this.uuid;
    },
    classes() {
      return {
        ...this.fieldGroupClasses,
        'bb-typeahead--search': this.flavor === 'search',
        'has-no-options': !this.localOptions || this.localOptions.length === 0,
        'has-value': this.val
      };
    },
    placeholderText() {
      return this.small && this.flavor === 'search' ? this.placeholder || 'Search …' : this.placeholder || '';
    },
    showSearchIcon() {
      return this.small ? this.focus || !this.val : true;
    },
    showClearButton() {
      return this.flavor === 'search' && this.val && this.val.length !== 0;
    },
    validationName() {
      return this.vvName || this.label;
    },
    validationPath() {
      return this.vvValuePath || this.label;
    },
    showAddon() {
      return this.addOn && this.addOn.trim();
    },
    computedLabel() {
      return this.labelPropName || 'label';
    },
    noResultString() {
      return this.noResult.replace('##', `‘${this.searchQuery}’`);
    },
    hasValidationError() {
      return this.veeValidateErrors || this.rules && this.hasError;
    },
    errorMessageId() {
      return `bb-typeahead-error-${this.uuid}`;
    }
  },
  watch: {
    options: {
      immediate: true,
      handler(newValue) {
        this.localOptions = JSON.parse(JSON.stringify(newValue));
      }
    },
    value() {
      if (this.value.length < 1) {
        this.$nextTick(() => {
          this.$refs.root.focus();
        });
      }
    },
    consideredValueIndex() {
      var _this$options$this$co;
      this.consideredValue = this.labelPropName ? this.options[this.consideredValueIndex][this.labelPropName] : (_this$options$this$co = this.options[this.consideredValueIndex].label) !== null && _this$options$this$co !== void 0 ? _this$options$this$co : this.options[this.consideredValueIndex];
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
      this.isOpen = false;
    },
    onSearchChange(searchQuery) {
      this.searchQuery = searchQuery;
      if (notEmpty(searchQuery)) {
        this.$emit('search-change', searchQuery);
      }
    },
    visualId({
      id
    }, prefix) {
      return id && `${prefix}-${id}`;
    },
    visualLabel(option) {
      return option[this.computedLabel] || option.isTag ? option[this.computedLabel] : option;
    },
    onInput(event) {
      this.val = event;
      this.$emit('input', event);
      this.$emit('change', event);
    },
    onOpen(event) {
      this.focus = true;
      this.isOpen = true;
      this.$emit('open', event);
    },
    addTag(label) {
      const newTag = isString(this.localOptions[0]) ? label : {
        label
      };
      this.localOptions.push(newTag);
      const val = 'multiple' in this.$attrs ? [...this.val, newTag] : newTag;
      this.onInput(val);
    },
    activate() {
      this.$refs.multiselect.activate();
    },
    onArrowKeyDown() {
      if (!this.isOpen) {
        return;
      }
      this.consideredValueIndex = Math.min(this.consideredValueIndex + 1, this.options.length - 1);
    },
    onArrowKeyUp() {
      if (!this.isOpen) {
        return;
      }
      this.consideredValueIndex = Math.max(this.consideredValueIndex - 1, 0);
    }
  }
};

/* script */
const __vue_script__$14 = script$14;

/* template */
var __vue_render__$14 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-typeahead field-group",
    class: _vm.classes,
    attrs: {
      "tabindex": "0",
      "data-valid": _vm.validity,
      "aria-required": _vm.mandatory
    },
    on: {
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble,
      "keydown": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "down", 40, $event.key, ["Down", "ArrowDown"])) {
          return null;
        }
        return _vm.onArrowKeyDown.apply(null, arguments);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "up", 38, $event.key, ["Up", "ArrowUp"])) {
          return null;
        }
        return _vm.onArrowKeyUp.apply(null, arguments);
      }]
    }
  }, [_vm.label ? _c('label', {
    staticClass: "multiselect__label",
    attrs: {
      "for": _vm.fieldId
    }
  }, [_vm._t("default", function () {
    return [_vm.mandatory ? _c('span', {
      staticClass: "bb-typeahead__mandatory-indicator"
    }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v("\n      " + _vm._s(_vm.label) + "\n    ")];
  })], 2) : _vm._e(), _vm._v(" "), _c('div', {
    ref: "inputGroup",
    staticClass: "bb-typeahead__input-group input-group",
    class: {
      'input-group--medium': _vm.medium
    }
  }, [_vm.flavor === 'search' ? _c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showSearchIcon,
      expression: "showSearchIcon"
    }],
    staticClass: "bb-typeahead__icon-search",
    attrs: {
      "size": _vm.small ? 18 : 24,
      "name": "ui-magnify",
      "fill": "gray-70"
    }
  }) : _vm._e(), _vm._v(" "), _vm.showClearButton ? _c('bb-button', {
    staticClass: "bb-typeahead__icon-reset",
    attrs: {
      "disabled": _vm.disabled,
      "no-background": "",
      "color": "gray",
      "size": "sm",
      "display": "square"
    },
    on: {
      "click": function ($event) {
        return _vm.onInput(null);
      }
    }
  }, [_c('bb-icon', {
    attrs: {
      "size": _vm.small ? 18 : 24,
      "name": "ui-close",
      "fill": "gray-80"
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('vue-multiselect', _vm._b({
    ref: "multiselect",
    attrs: {
      "id": _vm.fieldId,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false',
      "label": _vm.labelPropName,
      "disabled": _vm.disabled,
      "options": _vm.localOptions,
      "hide-selected": false,
      "close-on-select": _vm.closeOnSelect,
      "data-vv-name": _vm.validationName,
      "data-vv-value-path": _vm.validationPath,
      "searchable": !_vm.disableSearch,
      "tabindex": _vm.tabindex || 0,
      "name": _vm.name || _vm.fieldId,
      "max-height": _vm.maxHeight,
      "placeholder": _vm.placeholderText,
      "aria-expanded": _vm.isOpen
    },
    on: {
      "tag": _vm.addTag,
      "input": _vm.onInput,
      "close": _vm.onBlurAndValidate,
      "open": _vm.onOpen,
      "search-change": _vm.onSearchChange
    },
    scopedSlots: _vm._u([{
      key: "singleLabel",
      fn: function (ref) {
        var option = ref.option;
        return [_vm.optionAsSingleLabel ? _vm._t("option", function () {
          return [_c('span', {
            attrs: {
              "id": _vm.visualId(option, 'selected')
            }
          }, [_vm._v(_vm._s(_vm.visualLabel(option)))])];
        }, {
          "option": option
        }) : _vm._t("singleLabel", function () {
          return [_c('span', {
            attrs: {
              "id": _vm.visualId(option, 'selected')
            }
          }, [_vm._v(_vm._s(_vm.visualLabel(option)))])];
        }, {
          "option": option
        })];
      }
    }, {
      key: "tag",
      fn: function (ref) {
        var option = ref.option;
        var remove = ref.remove;
        return [_c('bb-tag', _vm._b({
          attrs: {
            "id": _vm.visualId(option, 'selected'),
            "name": _vm.visualLabel(option)
          },
          on: {
            "remove": function ($event) {
              return remove(option);
            }
          }
        }, 'bb-tag', _vm.small ? {
          compressed: true
        } : {
          condensed: true
        }, false))];
      }
    }, {
      key: "caret",
      fn: function (ref) {
        var toggle = ref.toggle;
        return [_vm.flavor !== 'search' ? _c('bb-button', {
          staticClass: "bb-typeahead__caret select-input-arrow-icon",
          attrs: {
            "disabled": _vm.disabled,
            "aria-label": _vm.caretButtonAriaLabel,
            "no-background": "",
            "no-paddings": "",
            "color": "gray",
            "size": "sm"
          },
          nativeOn: {
            "mousedown": function ($event) {
              $event.preventDefault();
              $event.stopPropagation();
              return toggle.apply(null, arguments);
            },
            "keydown": function ($event) {
              if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
                return null;
              }
              $event.preventDefault();
              $event.stopPropagation();
              return toggle.apply(null, arguments);
            }
          }
        }, [_c('bb-icon', {
          attrs: {
            "rotate": _vm.focus ? 'down' : 'up',
            "name": "ui-menu-down",
            "fill": "gray-50",
            "size": "24"
          }
        })], 1) : _vm._e()];
      }
    }, _vm.$slots.beforeList || _vm.flavor === 'search' && !_vm.small ? {
      key: "beforeList",
      fn: function () {
        return [_vm._t("beforeList", function () {
          return [_c('p', {
            staticClass: "bb-typeahead__before-list"
          }, [_vm._v("\n            " + _vm._s(_vm.searchSuggestionsLabel) + "\n          ")])];
        })];
      },
      proxy: true
    } : null, {
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_vm._t("option", function () {
          return [_c('div', [_c('bb-dropdown-item', {
            attrs: {
              "id": _vm.visualId(option, 'option'),
              "label": _vm.visualLabel(option)
            }
          })], 1)];
        }, {
          "option": option
        })];
      }
    }, {
      key: "noResult",
      fn: function () {
        return [_c('p', {
          staticClass: "bb-typeahead__no-result"
        }, [_vm._t("noResult", function () {
          return [_vm._v("\n            " + _vm._s(_vm.noResultString) + "\n          ")];
        }, {
          "searchQuery": _vm.searchQuery
        })], 2)];
      },
      proxy: true
    }, {
      key: "noOptions",
      fn: function () {
        return [_c('p', {
          staticClass: "bb-typeahead__no-options"
        }, [_vm._t("noOptions", function () {
          return [_vm._v("\n            " + _vm._s(_vm.noOptions) + "\n          ")];
        })], 2)];
      },
      proxy: true
    }, {
      key: "maxElements",
      fn: function () {
        return [_vm.maxElementsLabel ? _c('span', {
          staticClass: "bb-typeahead__max-elements-label"
        }, [_vm._v("\n          " + _vm._s(_vm.maxElementsLabel) + "\n        ")]) : _vm._e()];
      },
      proxy: true
    }], null, true),
    model: {
      value: _vm.val,
      callback: function ($$v) {
        _vm.val = $$v;
      },
      expression: "val"
    }
  }, 'vue-multiselect', _vm.$attrs, false)), _vm._v(" "), _c('div', {
    staticClass: "input-addon-wrapper"
  }, [_vm.showAddon ? _c('div', {
    staticClass: "input-addon"
  }, [_vm._v("\n        " + _vm._s(_vm.addOn) + "\n      ")]) : _vm._e(), _vm._v(" "), _c('bb-icon', {
    staticClass: "validation-error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  })], 1), _vm._v(" "), _vm.helpActive ? _c('bb-tooltip', {
    attrs: {
      "is-in-input": ""
    }
  }, [_c('p', [_vm._t("tooltipText", function () {
    return [_vm._v("\n          " + _vm._s(_vm.helpText) + "\n        ")];
  })], 2)]) : _vm._e()], 1), _vm._v(" "), _vm.veeValidateErrors || _vm.rules && _vm.hasError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.inputGroup,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', {
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n      ")])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n    ")]) : _vm._e()] : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "visually-hidden",
    attrs: {
      "aria-live": "polite"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.consideredValue) + "\n  ")])], 2);
};
var __vue_staticRenderFns__$14 = [];

/* style */
const __vue_inject_styles__$14 = function (inject) {
  if (!inject) return;
  inject("data-v-4834f89d_0", {
    source: ".bb-typeahead .multiselect__placeholder,.bb-typeahead .multiselect__single{overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.bb-typeahead__input-group{display:block}.bb-typeahead__before-list{margin-bottom:.9375rem;margin-left:1.5625rem;color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-typeahead__no-options,.bb-typeahead__no-result{margin:.9375rem;color:#bdc7d0;font-family:GothamSSm-MediumItalic,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}.bb-typeahead__icon-search.bb-icon{position:absolute;top:50%;left:2.1875rem;transform:translateY(-50%)}.framed-input--small>*>.bb-typeahead__icon-search.bb-icon{left:.75rem}.bb-typeahead__icon-reset.bb-button{position:absolute;z-index:1100;top:50%;right:0;transform:translateY(-50%)}.bb-typeahead__icon-reset.bb-button svg path{transition:fill .2s}.bb-typeahead__icon-reset.bb-button:hover svg path{fill:#ff7155}.is-focused .bb-typeahead__icon-reset.bb-button{display:none}.framed-input--small .bb-typeahead__icon-reset.bb-button{top:0;transform:unset}.bb-typeahead .bb-typeahead__caret{position:absolute;z-index:1100;right:0;pointer-events:initial}.bb-typeahead__max-elements-label{padding:.25rem .5rem;color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px}.bb-typeahead .multiselect{position:relative;cursor:pointer;outline:0}.bb-typeahead .multiselect__strong{margin-top:.3125rem;margin-left:.125rem;font-size:12px}.bb-typeahead .multiselect--disabled{cursor:not-allowed}.bb-typeahead .multiselect--disabled .multiselect__tags{border-color:#dee3e8;opacity:.6}.bb-typeahead .multiselect--disabled .bb-tag__contents{background-color:#eeeff2}.bb-typeahead .multiselect__tags{display:flex;min-height:1.875rem;flex-direction:column;justify-content:center;padding:0 1.5rem .125rem 0;border-bottom:.0625rem solid #bdc7d0;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;transition:border 150ms}.bb-typeahead .multiselect__input{height:auto;padding:0;border:none;margin-bottom:-.0625rem;vertical-align:middle}.bb-typeahead .multiselect__placeholder{width:100%}.bb-typeahead .multiselect__input::placeholder,.bb-typeahead .multiselect__placeholder{color:#9dabb9}.bb-typeahead .multiselect__tags-wrap{display:flex;flex-flow:row wrap;align-items:center;margin-top:-.125rem;margin-left:-.3125rem}.bb-typeahead .multiselect__content{width:100%}.bb-typeahead .multiselect__content-wrapper{position:absolute;z-index:1601;width:100%;background-color:#fff;box-shadow:0 .0625rem .25rem 0 rgba(0,0,0,.2);overflow-y:auto}.bb-typeahead .multiselect__element{border-top:.0625rem solid #eeeff2}.bb-typeahead .multiselect__element:first-child{border-top:none}.bb-typeahead:not([data-valid]) .multiselect:hover .multiselect__tags{border-color:#003e8c}.bb-typeahead:not([data-valid]).is-focused .multiselect__tags{border-color:#003e8c}.bb-typeahead.framed-input[data-valid=false] .multiselect__tags,.bb-typeahead[data-valid=false] .multiselect__tags{border-color:#ff7155}.bb-typeahead[data-valid=true] .multiselect__tags{border-color:#0ccca2}.bb-typeahead .multiselect--above .multiselect__content-wrapper{position:absolute;bottom:100%}.bb-typeahead .bb-tag{margin:.1875rem .3125rem 0}.bb-typeahead.framed-input .multiselect__tags-wrap{margin-top:-.0625rem}.bb-typeahead.framed-input .multiselect__tags{min-height:2.5rem;padding:.3125rem 2rem .3125rem .625rem;border:.0625rem solid #bdc7d0;border-radius:.375rem;margin-top:0}.bb-typeahead.framed-input .multiselect__single{display:inline-block;font-size:14px}.bb-typeahead.framed-input .bb-typeahead__caret{right:.3125rem}.bb-typeahead.framed-input .multiselect__label{transform:translate(0,.0625rem)}.bb-typeahead.framed-input .multiselect--disabled .multiselect__tags{border-color:#9dabb9;background-color:#dee3e8;opacity:.6}.bb-typeahead.framed-input .multiselect__placeholder{display:inline-block;padding-top:.3125rem;font-size:14px}.bb-typeahead.framed-input--small .multiselect__tags{min-height:1.875rem;padding:.1875rem 2rem .1875rem .625rem}.bb-typeahead.framed-input--small .multiselect__tags-wrap{margin:-.125rem}.bb-typeahead.framed-input--small .bb-tag{margin:.125rem}.bb-typeahead.framed-input--small .multiselect__single{font-size:12px;transform:translate(0,.0625rem)}.bb-typeahead.framed-input--small .bb-typeahead__caret{right:.5rem}.bb-typeahead.framed-input--small .multiselect__input{margin-top:-.0625rem}.bb-typeahead.framed-input--small .multiselect__placeholder{padding-top:0;font-size:12px}.bb-typeahead.framed-input--small .input-addon-wrapper{top:.375rem;right:.9375rem;font-size:12px}.bb-typeahead.framed-input--small .input-group-addon{top:.1875rem}.bb-typeahead.framed-input--small select{background-position:right 10px top 56%;background-size:10px}.bb-typeahead--search{padding-top:0;cursor:pointer}.bb-typeahead--search .multiselect__content{padding:1.25rem}.bb-typeahead--search .multiselect__single{width:100%}.bb-typeahead--search .multiselect__single .bb-dropdown-item{padding-left:0}.bb-typeahead--search .multiselect__single .bb-dropdown-item:hover{background:0 0}.bb-typeahead--search .multiselect__tags{display:flex;min-height:3.25rem;align-items:center;padding-bottom:0;padding-left:4.8125rem;border-bottom:none}.bb-typeahead--search .multiselect__placeholder{padding-top:.0625rem}.bb-typeahead--search.framed-input--small .multiselect__placeholder{padding-top:.0625rem}.bb-typeahead--search.framed-input--small .multiselect__tags{min-height:2.5rem}.bb-typeahead--search.framed-input--small .multiselect__content{padding:unset}.bb-typeahead--search.framed-input--small .multiselect__input{margin-top:0}.bb-typeahead--search.framed-input--small.has-value.is-focused .multiselect__tags,.bb-typeahead--search.framed-input--small:not(.has-value) .multiselect__tags{padding-left:2.6875rem}.bb-typeahead--search.framed-input--small .bb-dropdown-item{min-height:2.375rem;padding-top:.25rem;padding-bottom:.3125rem}.bb-typeahead--search:not(.framed-input--small) .multiselect__element{border-top:unset}.bb-typeahead .input-group--medium .multiselect__tags{min-height:2.1875rem;padding:0}.bb-typeahead .input-group--medium .bb-typeahead__caret{right:.25rem}.bb-typeahead .input-group--medium .multiselect__single{display:unset}.bb-typeahead .input-group--medium .bb-dropdown-item{min-height:2.0625rem;padding:.1875rem 1.875rem .1875rem .625rem;font-size:.625rem;line-height:1.3;word-break:break-word}.bb-typeahead .bb-dropdown-item{cursor:pointer}.bb-typeahead .multiselect__option--highlight .bb-dropdown-item,.bb-typeahead .multiselect__option--selected .bb-dropdown-item{box-shadow:none;color:#57697a;outline:0;text-decoration:none}.bb-typeahead .multiselect__option--selected .bb-dropdown-item{background-color:#eeeff2}.bb-typeahead .multiselect__option--highlight .bb-dropdown-item{background-color:#f7f7f8}.bb-typeahead .multiselect__spinner{position:absolute;top:50%;right:.9375rem;display:block;width:3rem;height:2.1875rem;transform:translateY(-50%)}.bb-typeahead .multiselect__spinner::after,.bb-typeahead .multiselect__spinner::before{position:absolute;top:50%;left:50%;width:1rem;height:1rem;border-width:.125rem;border-style:solid;border-color:#89a transparent transparent;border-radius:100%;margin:-.5rem 0 0 -.5rem;content:\"\"}.bb-typeahead .multiselect__spinner::before{animation:rotate-full 1.5s cubic-bezier(.41,.26,.2,.62) infinite}.bb-typeahead .multiselect__spinner::after{animation:rotate-full 1.5s cubic-bezier(.51,.09,.21,.8) infinite}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$14 = undefined;
/* module identifier */
const __vue_module_identifier__$14 = undefined;
/* functional template */
const __vue_is_functional_template__$14 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$14 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$14,
  staticRenderFns: __vue_staticRenderFns__$14
}, __vue_inject_styles__$14, __vue_script__$14, __vue_scope_id__$14, __vue_is_functional_template__$14, __vue_module_identifier__$14, false, createInjector, undefined, undefined);

var FieldRange = {
  components: {
    FieldPrefix: __vue_component__$2n
  },
  directives: {
    resize
  },
  data() {
    const val = {};
    val[this.prefixModel] = this.value && this.value[this.prefixModel] || null;
    val[this.primaryModel] = this.value && this.value[this.primaryModel] || null;
    val[this.secondaryModel] = this.value && this.value[this.secondaryModel] || null;
    return {
      splitDistance: '',
      prefixComponentWidth: '',
      val
    };
  },
  props: {
    filterOptions: Array,
    prefixModel: {
      type: String,
      default: 'prefix'
    },
    primaryModel: {
      type: String,
      default: 'primary'
    },
    secondaryModel: {
      type: String,
      default: 'secondary'
    },
    value: Object,
    placeholder: {
      type: String,
      default: ' '
    }
  },
  emits: ['input', 'change'],
  watch: {
    val: {
      deep: true,
      handler() {
        if (this.val[this.prefixModel] !== 'BETWEEN') {
          this.val[this.secondaryModel] = null;
        }
        this.$emit('input', this.val);
      }
    }
  },
  computed: {
    prefixOptions() {
      return !this.filterOptions ? logicalOperators : logicalOperators.filter(operator => this.filterOptions.includes(operator.value));
    },
    splitFieldsView() {
      return this.type !== 'suggestions' && this.val[this.prefixModel] === 'BETWEEN';
    }
  },
  mounted() {
    const prefix = this.$refs.fieldPrefix.$el;
    this.prefixComponentWidth = prefix ? prefix.clientWidth : '0';
    this.calculateMiddle();
  },
  methods: {
    calculateMiddle() {
      this.splitDistance = this.$el.clientWidth / 2 - this.prefixComponentWidth / 2;
    },
    onChange() {
      this.$emit('change', this.val);
    }
  }
};

var Options = {
  props: {
    options: Array
  }
};

var Icon$l = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg930160124__a bb-icon__dynamic-fill",attrs:{"d":"M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"}})]) } };

Vue.component('icon-ui-account', Icon$l);

var script$13 = {
  name: 'bb-input-range',
  components: {
    BbInput: __vue_component__$2f,
    BbFieldPrefix: __vue_component__$2n,
    BbTypeahead: __vue_component__$14,
    BbDatepicker: __vue_component__$1o,
    BbIcon: __vue_component__$2C
  },
  mixins: [FieldExtensions, Field, FieldRange, Options, veeValidationMixin],
  props: {
    type: {
      type: String,
      default: 'text',
      validator: val => ['text', 'number', 'date', 'suggestions'].includes(val)
    },
    format: String,
    typeaheadProps: Object
  },
  emits: ['search-change', 'focus', 'blur'],
  data() {
    return {
      typeMap: {
        suggestions: 'bb-typeahead',
        date: 'bb-datepicker'
      },
      fieldPrefixFocus: false
    };
  },
  computed: {
    classes() {
      return {
        'field-range--split': this.splitFieldsView,
        'field-range--focused': this.focus,
        'field-range--suggestions': this.type === 'suggestions',
        'has-label': this.label && this.type === 'suggestions'
      };
    },
    showIconUser() {
      return !this.val[this.primaryModel] && (!this.focus || this.fieldPrefixFocus);
    },
    inputComponent() {
      return this.typeMap[this.type] || 'bb-input';
    },
    computedType() {
      return ['bb-datepicker', 'bb-input'].includes(this.inputComponent) ? this.type : null;
    },
    mergedTypeaheadProps() {
      return this.type === 'suggestions' ? Object.assign({
        flavor: 'search',
        optionAsSingleLabel: true,
        closeOnSelect: true
      }, this.typeaheadProps) : null;
    }
  },
  methods: {
    onSearchChange(event) {
      this.$emit('search-change', event);
    },
    onFieldPrefixFocus() {
      this.fieldPrefixFocus = true;
      this.$emit('focus');
    },
    onFieldPrefixBlur() {
      this.fieldPrefixFocus = false;
      this.$emit('blur');
    }
  }
};

/* script */
const __vue_script__$13 = script$13;

/* template */
var __vue_render__$13 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    directives: [{
      name: "resize",
      rawName: "v-resize:throttle",
      value: _vm.calculateMiddle,
      expression: "calculateMiddle",
      arg: "throttle"
    }],
    staticClass: "bb-input-range field-group field-range framed-input is-filled",
    class: _vm.classes,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "val"
    }
  }, [_c('bb-field-prefix', {
    ref: "fieldPrefix",
    staticClass: "field-range__prefix",
    style: {
      transform: "translateX(" + (_vm.splitFieldsView ? _vm.splitDistance : 0) + "px)"
    },
    attrs: {
      "id": _vm.id + "-prefix",
      "options": _vm.prefixOptions,
      "name": _vm.name + "-prefix",
      "small": _vm.small !== false || _vm.type !== 'suggestions'
    },
    on: {
      "focus": _vm.onFieldPrefixFocus,
      "blur": _vm.onFieldPrefixBlur,
      "change": _vm.onChange
    },
    model: {
      value: _vm.val[_vm.prefixModel],
      callback: function ($$v) {
        _vm.$set(_vm.val, _vm.prefixModel, $$v);
      },
      expression: "val[prefixModel]"
    }
  }), _vm._v(" "), _c(_vm.inputComponent, _vm._b({
    tag: "component",
    staticClass: "field-range__primary-field",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "type": _vm.computedType,
      "label": _vm.label,
      "placeholder": _vm.placeholder,
      "format": _vm.format,
      "editable": "",
      "small": ""
    },
    on: {
      "search-change": _vm.onSearchChange,
      "focus": _vm.onFocus,
      "open": _vm.onFocus,
      "blur": _vm.onBlur,
      "change": _vm.onChange
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_vm._t("option", null, {
          "option": option
        })];
      }
    }], null, true),
    model: {
      value: _vm.val[_vm.primaryModel],
      callback: function ($$v) {
        _vm.$set(_vm.val, _vm.primaryModel, $$v);
      },
      expression: "val[primaryModel]"
    }
  }, 'component', _vm.mergedTypeaheadProps, false)), _vm._v(" "), _vm.splitFieldsView ? _c(_vm.inputComponent, {
    tag: "component",
    staticClass: "field-range__secondary-field",
    attrs: {
      "id": _vm.id + "-2",
      "name": _vm.name + "-2",
      "type": _vm.computedType,
      "placeholder": _vm.placeholder,
      "format": _vm.format,
      "editable": "",
      "small": ""
    },
    on: {
      "focus": _vm.onFocus,
      "blur": _vm.onBlur,
      "change": _vm.onChange
    },
    model: {
      value: _vm.val[_vm.secondaryModel],
      callback: function ($$v) {
        _vm.$set(_vm.val, _vm.secondaryModel, $$v);
      },
      expression: "val[secondaryModel]"
    }
  }) : _vm._e(), _vm._v(" "), _vm.type === 'suggestions' ? _c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showIconUser,
      expression: "showIconUser"
    }],
    staticClass: "bb-input-range__icon-user",
    attrs: {
      "name": "ui-account",
      "size": "24",
      "fill": "gray-30"
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$13 = [];

/* style */
const __vue_inject_styles__$13 = function (inject) {
  if (!inject) return;
  inject("data-v-2e9dd17e_0", {
    source: ".bb-input-range__icon-user{position:absolute;right:.5rem;bottom:.5rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$13 = undefined;
/* module identifier */
const __vue_module_identifier__$13 = undefined;
/* functional template */
const __vue_is_functional_template__$13 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$13 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$13,
  staticRenderFns: __vue_staticRenderFns__$13
}, __vue_inject_styles__$13, __vue_script__$13, __vue_scope_id__$13, __vue_is_functional_template__$13, __vue_module_identifier__$13, false, createInjector, undefined, undefined);

var script$12 = {
  name: 'bb-radio',
  components: {
    BbIcon: __vue_component__$2C,
    BbLink: __vue_component__$2p
  },
  mixins: [FieldExtensions, Field, veeValidationMixin, vuelidateValidator],
  props: {
    helpText: String,
    helpActive: Boolean,
    smallLabel: Boolean,
    square: Boolean,
    grouped: Boolean,
    equalWidth: Boolean,
    options: {
      required: true,
      type: Array
    },
    disabled: Boolean,
    block: Boolean,
    display: {
      type: String,
      default: 'button',
      validator: display => ['button', 'link'].includes(display)
    },
    labelCenter: Boolean,
    rules: Object,
    mandatory: Boolean
  },
  computed: {
    templateOptions() {
      return this.options.map(option => {
        var _option$disabled, _option$inputAttrs;
        const optionalProperties = {
          disabled: (_option$disabled = option.disabled) !== null && _option$disabled !== void 0 ? _option$disabled : this.disabled,
          inputAttrs: (_option$inputAttrs = option.inputAttrs) !== null && _option$inputAttrs !== void 0 ? _option$inputAttrs : this.inputAttrs
        };
        return {
          ...option,
          ...omitBy(optionalProperties, isNil)
        };
      });
    },
    showError() {
      return this.validity && this.veeValidateErrors;
    },
    computedId() {
      var _this$id;
      return (_this$id = this.id) !== null && _this$id !== void 0 ? _this$id : slugify(this.name);
    },
    classes() {
      return {
        [`bb-radio--display-${this.display}`]: this.display,
        'bb-radio--block': this.block,
        'bb-radio--fluid': this.block,
        'bb-radio--disabled': this.disabled,
        'bb-radio--grouped': this.grouped,
        'bb-radio--square': this.square,
        'bb-radio--small-label': this.smallLabel,
        'bb-radio--small': this.small,
        'bb-radio--equal': this.equalWidth,
        'bb-radio--label-centered': this.labelCenter,
        'has-label': this.label
      };
    },
    hasValidationError() {
      return this.showError || this.rules && this.hasError;
    },
    errorMessageId() {
      return `errorMessage-${slugify(this.name)}`;
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    equalAsStrings
  }
};

/* script */
const __vue_script__$12 = script$12;

/* template */
var __vue_render__$12 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-radio input-group",
    class: _vm.classes,
    attrs: {
      "id": _vm.id,
      "data-valid": _vm.validity,
      "data-vv-value-path": "val"
    }
  }, [_vm.$slots.default || _vm.label ? _c('legend', {
    staticClass: "bb-radio__legend"
  }, [_c('div', {
    staticClass: "bb-radio__tooltip-wrapper"
  }, [_vm.mandatory ? _c('span', {
    staticClass: "bb-radio__mandatory-indicator"
  }, [_vm._v("\n        *\n      ")]) : _vm._e(), _vm._v(" "), _vm._t("default", function () {
    return [_vm._v("\n        " + _vm._s(_vm.label) + "\n      ")];
  }), _vm._v(" "), _vm.helpActive ? _c('bb-tooltip', {
    attrs: {
      "is-in-input": ""
    }
  }, [_c('p', [_vm._t("tooltipText", function () {
    return [_vm._v("\n            " + _vm._s(_vm.helpText) + "\n          ")];
  })], 2)]) : _vm._e()], 2)]) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-radio__group",
    attrs: {
      "role": "radiogroup",
      "aria-required": _vm.mandatory,
      "aria-label": _vm.label,
      "aria-describedby": _vm.hasValidationError ? _vm.errorMessageId : undefined,
      "aria-invalid": _vm.hasValidationError ? 'true' : 'false'
    }
  }, _vm._l(_vm.templateOptions, function (option, index) {
    return _c('label', {
      key: index,
      staticClass: "bb-radio__button",
      class: {
        'is-disabled': option.disabled
      }
    }, [_c('input', _vm._b({
      staticClass: "bb-radio__input visually-hidden",
      attrs: {
        "id": _vm.computedId + "-" + index,
        "name": _vm.name,
        "disabled": option.disabled,
        "type": "radio",
        "tabindex": _vm.equalAsStrings(option.value, _vm.val) || !_vm.val && index === 0 ? 0 : -1,
        "aria-labelledby": _vm.display === 'button' ? "button-label-" + index : "link-label-" + index
      },
      domProps: {
        "value": option.value,
        "checked": _vm.equalAsStrings(option.value, _vm.val)
      },
      on: {
        "blur": _vm.onBlurAndValidate,
        "change": function ($event) {
          $event.stopPropagation();
          _vm.onInput($event);
          _vm.onChange($event);
        }
      }
    }, 'input', option.inputAttrs, false)), _vm._v(" "), _vm.display === 'button' ? _c('span', {
      staticClass: "bb-radio__text",
      attrs: {
        "id": "button-label-" + index,
        "aria-hidden": "true"
      }
    }, [_vm._v("\n        " + _vm._s(option.text) + "\n      ")]) : _vm.display === 'link' ? _c('bb-link', {
      staticClass: "bb-radio__link",
      attrs: {
        "id": "link-label-" + index,
        "disabled": option.disabled,
        "no-underline": !_vm.equalAsStrings(option.value, _vm.val),
        "color": _vm.equalAsStrings(option.value, _vm.val) ? 'mint' : 'gray',
        "aria-hidden": "true"
      }
    }, [_vm._v("\n        " + _vm._s(option.text) + "\n      ")]) : _vm._e()], 1);
  }), 0), _vm._v(" "), _vm.hasValidationError ? _c('div', {
    staticClass: "error-message error-message--pushed",
    attrs: {
      "id": _vm.errorMessageId,
      "role": "alert",
      "aria-live": "assertive"
    }
  }, [_c('bb-icon', {
    staticClass: "bb-radio__error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24"
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-radio__error"
  }, [_vm._v("\n      " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n    ")])], 1) : _vm._e()]);
};
var __vue_staticRenderFns__$12 = [];

/* style */
const __vue_inject_styles__$12 = function (inject) {
  if (!inject) return;
  inject("data-v-3c6fde68_0", {
    source: ".bb-radio{text-align:left}.bb-radio--fluid{width:100%}.bb-radio__legend{margin-top:.625rem;margin-bottom:0;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1.3125rem}.bb-radio--small .bb-radio__legend,.bb-radio--small-label .bb-radio__legend{font-size:.6875rem}.bb-radio--small-label .bb-radio__legend{margin-bottom:-.625rem}.bb-radio--small .bb-radio__legend{margin-bottom:-.3125rem}.bb-radio--label-centered .bb-radio__legend{text-align:center}.bb-radio__tooltip-wrapper{position:relative;display:inline-block}.bb-radio--equal .bb-radio__group{display:flex;flex-wrap:wrap}@supports (display:grid){.bb-radio--equal .bb-radio__group{display:grid;flex-wrap:initial;grid-gap:.3125rem;grid-template-columns:repeat(auto-fit,minmax(6.25rem,1fr))}}@supports (display:grid){.bb-radio--square.bb-radio--equal .bb-radio__group{grid-template-columns:repeat(auto-fit,minmax(2.5625rem,1fr))}}.bb-radio__button{display:inline-block;margin-top:.625rem;cursor:pointer;text-align:center}.bb-radio__button:hover:not(.is-disabled)>.bb-radio__text{border-color:#2485e5;background-color:#2485e5;color:#fff;transition:color .2s,background-color .2s,border-color .2s}.bb-radio__button:not(:last-of-type){margin-right:.625rem}.bb-radio--display-link .bb-radio__button:not(:last-of-type){margin-right:1.25rem}.bb-radio--display-button .bb-radio__button{min-width:3.75rem}.bb-radio--square .bb-radio__button{min-width:2.5625rem}.bb-radio--small .bb-radio__button{margin-top:.3125rem}.bb-radio--small .bb-radio__button:not(:last-of-type){margin-right:.3125rem}.bb-radio--block .bb-radio__button{display:block;margin-right:0}.bb-radio--equal .bb-radio__button{width:100%;flex:1}@supports (display:grid){.bb-radio--equal .bb-radio__button{flex:initial}}.bb-radio__link{font-size:.875rem}.bb-radio__text{display:flex;height:100%;align-items:center;justify-content:center;padding:.53125rem 1.5625rem;border:.0625rem solid #bdc7d0;border-radius:.375rem;background:#fff;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-radio--square .bb-radio__text{padding:.53125rem .625rem}.bb-radio--small .bb-radio__text{padding:.3125rem 1.25rem;font-size:.75rem}.bb-radio--equal .bb-radio__text{width:100%;text-align:center}.is-disabled .bb-radio__text{border:.0625rem solid #bdc7d0;background-color:#eeeff2;color:rgba(125,125,125,.4);cursor:not-allowed}.bb-radio[data-valid=false] .bb-radio__text{border-color:#ff7155}.bb-radio__input:focus+.bb-radio__text{border-color:#2485e5;background-color:#2485e5;color:#fff;transition:color .2s,background-color .2s,border-color .2s}.bb-radio__input:checked+.bb-radio__text{border-color:#2485e5;background:#2485e5;color:#fff;cursor:default}.bb-radio__error{display:inline-block;padding-top:.3125rem;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-radio__error-icon{margin-right:.3125rem}.bb-radio--grouped .bb-radio__text{border-radius:0}.bb-radio--grouped.bb-radio--small .bb-radio__text{padding:.3125rem 1.25rem;font-size:.75rem}.bb-radio--grouped .bb-radio__button{display:inline-block}.bb-radio--grouped .bb-radio__button:hover:not(.is-disabled) .bb-radio__text{border-color:#2485e5;background-color:#2485e5;color:#fff;transition:color .2s,background-color .2s,border-color .2s}.bb-radio--grouped .bb-radio__button:first-of-type .bb-radio__text{border-bottom-left-radius:.375rem;border-top-left-radius:.375rem}.bb-radio--grouped .bb-radio__button:last-of-type .bb-radio__text{border-bottom-right-radius:.375rem;border-top-right-radius:.375rem}.bb-radio--grouped .bb-radio__button:not(:last-of-type){margin-right:0}.bb-radio--grouped .bb-radio__button+.bb-radio__button .bb-radio__text{margin-left:-.0625rem}.bb-radio__mandatory-indicator{color:#ff7155}.bb-radio--small .bb-radio__mandatory-indicator{font-size:.75rem}.bb-radio .error-message{display:flex}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$12 = undefined;
/* module identifier */
const __vue_module_identifier__$12 = undefined;
/* functional template */
const __vue_is_functional_template__$12 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$12 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$12,
  staticRenderFns: __vue_staticRenderFns__$12
}, __vue_inject_styles__$12, __vue_script__$12, __vue_scope_id__$12, __vue_is_functional_template__$12, __vue_module_identifier__$12, false, createInjector, undefined, undefined);

const toOption = value => {
  var _value$toString;
  return {
    text: (_value$toString = value === null || value === void 0 ? void 0 : value.toString()) !== null && _value$toString !== void 0 ? _value$toString : value,
    value
  };
};
const defaultOptionValues = [25, 50, 100];
var script$11 = {
  name: 'bb-items-per-page',
  components: {
    BbRadio: __vue_component__$12
  },
  props: {
    /**
     * Name for the input
     */
    name: {
      type: String,
      default: '',
      required: true
    },
    value: {
      type: [String, Number, Boolean, null]
    },
    /**
     * Component label
     */
    label: {
      type: String,
      default: 'Items per page:'
    },
    /**
     * Page size options. E.g. `[{text: '25', value: 25}, …]`
     */
    perPageOptions: {
      type: Array,
      default() {
        return defaultOptionValues.map(toOption);
      }
    }
  },
  emits: ['input'],
  computed: {
    val() {
      var _this$value, _this$perPageOptions$;
      return (_this$value = this.value) !== null && _this$value !== void 0 ? _this$value : (_this$perPageOptions$ = this.perPageOptions[0]) === null || _this$perPageOptions$ === void 0 ? void 0 : _this$perPageOptions$.value;
    }
  },
  methods: {
    onInput(value) {
      /**
       * Input event.
       *
       * @event input
       */
      this.$emit('input', value);
    }
  }
};

/* script */
const __vue_script__$11 = script$11;

/* template */
var __vue_render__$11 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-items-per-page"
  }, [_c('label', {
    staticClass: "bb-items-per-page__page-size-label"
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]), _vm._v(" "), _c('bb-radio', {
    attrs: {
      "value": _vm.val,
      "options": _vm.perPageOptions,
      "name": _vm.name,
      "grouped": ""
    },
    on: {
      "input": _vm.onInput
    }
  })], 1);
};
var __vue_staticRenderFns__$11 = [];

/* style */
const __vue_inject_styles__$11 = function (inject) {
  if (!inject) return;
  inject("data-v-77ab7332_0", {
    source: ".bb-items-per-page{display:inline-flex;align-items:center}.bb-items-per-page__page-size-label{margin-right:.5rem;color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px}.bb-items-per-page .bb-radio__button:not(:last-of-type){margin:0}.bb-items-per-page .bb-radio__group{display:inline;white-space:nowrap}.bb-items-per-page .bb-radio__group .bb-radio__button{min-width:2.5rem;margin-top:0;user-select:none}.bb-items-per-page .bb-radio__group .bb-radio__button:hover .bb-radio__input:not(:checked)+.bb-radio__text,.bb-items-per-page .bb-radio__group .bb-radio__button:hover .bb-radio__text{background:0 0;color:#5ea6ed}.bb-items-per-page .bb-radio__group .bb-radio__button .bb-radio__text{padding:0;border:none;border-radius:0;margin:0 .625rem;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px}.bb-items-per-page .bb-radio__group .bb-radio__button .bb-radio__input:checked+.bb-radio__text,.bb-items-per-page .bb-radio__group .bb-radio__button:hover .bb-radio__input:checked+.bb-radio__text{border-bottom:.125rem solid #2485e5;background:0 0;border-bottom-left-radius:0;border-bottom-right-radius:0;color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$11 = undefined;
/* module identifier */
const __vue_module_identifier__$11 = undefined;
/* functional template */
const __vue_is_functional_template__$11 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$11 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$11,
  staticRenderFns: __vue_staticRenderFns__$11
}, __vue_inject_styles__$11, __vue_script__$11, __vue_scope_id__$11, __vue_is_functional_template__$11, __vue_module_identifier__$11, false, createInjector, undefined, undefined);

var script$10 = {
  name: 'bb-loading-overlay',
  components: {
    BbOverlay: __vue_component__$2u,
    BbSpinner: __vue_component__$2w
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true
    },
    loadingDelay: {
      type: Number,
      default: 100
    },
    color: {
      type: String,
      default: 'light-blue',
      validator: color => ['light-blue', 'dark', 'gray', 'white'].includes(color)
    },
    constrained: Boolean,
    text: String
  },
  data() {
    return {
      showLoadingOverlay: false
    };
  },
  watch: {
    isLoading(newVal, oldVal) {
      if (newVal && !oldVal) {
        setTimeout(() => {
          if (this.isLoading) {
            this.showLoadingOverlay = true;
          }
        }, this.loadingDelay);
      } else {
        this.showLoadingOverlay = false;
      }
    }
  },
  created() {
    if (this.isLoading) {
      this.showLoadingOverlay = true;
    }
  }
};

/* script */
const __vue_script__$10 = script$10;

/* template */
var __vue_render__$10 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-overlay', _vm._b({
    staticClass: "bb-loading-overlay",
    attrs: {
      "visible": _vm.showLoadingOverlay,
      "center-vertically": ""
    }
  }, 'bb-overlay', {
    color: _vm.color,
    constrained: _vm.constrained
  }, false), [_c('bb-spinner'), _vm._v(" "), _vm.text ? _c('p', [_vm._v("\n    " + _vm._s(_vm.text) + "\n  ")]) : _vm._e()], 1);
};
var __vue_staticRenderFns__$10 = [];

/* style */
const __vue_inject_styles__$10 = function (inject) {
  if (!inject) return;
  inject("data-v-9c56d26e_0", {
    source: ".bb-loading-overlay .bb-overlay__content{display:flex;flex-direction:column;justify-content:center}.bb-loading-overlay p{margin-top:2.1875rem;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$10 = undefined;
/* module identifier */
const __vue_module_identifier__$10 = undefined;
/* functional template */
const __vue_is_functional_template__$10 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$10 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$10,
  staticRenderFns: __vue_staticRenderFns__$10
}, __vue_inject_styles__$10, __vue_script__$10, __vue_scope_id__$10, __vue_is_functional_template__$10, __vue_module_identifier__$10, false, createInjector, undefined, undefined);

var Icon$k = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","data-name":"Layer 1","viewBox":"0 0 39.8 39.8"}},[_c('g',{attrs:{"data-name":"icons/application/iconHappy"}},[_c('circle',{attrs:{"cx":"19.9","cy":"19.9","r":"19.9","fill":"#ededed"}}),_c('path',{attrs:{"fill":"#4a4a4a","d":"M20.5 25a11.21 11.21 0 0 1-8.31-3.76.78.78 0 0 1 0-1 .62.62 0 0 1 .94 0 9.8 9.8 0 0 0 14.74 0 .62.62 0 0 1 .94 0 .78.78 0 0 1 0 1A11.21 11.21 0 0 1 20.5 25zm-4.16-9a.65.65 0 0 1-.66-.64 3.19 3.19 0 0 0-6.38 0 .66.66 0 0 1-1.31 0 4.5 4.5 0 0 1 9 0 .65.65 0 0 1-.65.64zm17 0a.65.65 0 0 1-.66-.64 3.19 3.19 0 0 0-6.38 0 .66.66 0 0 1-1.31 0 4.5 4.5 0 0 1 9 0 .65.65 0 0 1-.65.64z"}}),_c('path',{attrs:{"fill":"#e3e3e3","d":"M20 28a12.25 12.25 0 0 1-3.47-.5.77.77 0 0 1-.5-.94.72.72 0 0 1 .89-.53 10.85 10.85 0 0 0 6.16 0 .72.72 0 0 1 .89.53.77.77 0 0 1-.5.94A12.25 12.25 0 0 1 20 28zM9.13 30.35c6.92 6.92 18.56 8.1 26.11 2.27a17.56 17.56 0 0 1-1.6 1.72c-7.51 7.51-20.49 7-28-.5a19.54 19.54 0 0 1 0-27.46 20.79 20.79 0 0 1 1.83-1.76 19.75 19.75 0 0 0 1.66 25.73z"}}),_c('path',{attrs:{"fill":"#ffd5cc","d":"M9 22a2 2 0 1 1 2-2 2 2 0 0 1-2 2zm23 0a2 2 0 1 1 2-2 2 2 0 0 1-2 2z"}})])]) } };

Vue.component('icon-client-face-happy', Icon$k);

/**
 * A placeholder component to be used in empty list view etc.
 */
var script$$ = Vue.extend({
  name: 'bb-placeholder',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    /**
     * The message displayed as a placeholder
     */
    text: {
      type: String,
      default: 'Nothing to show yet'
    },
    /**
     * The icon displayed as a placeholder. It will not be automatically imported.
     */
    icon: {
      type: String,
      default: 'client-face-happy'
    }
  }
});

/* script */
const __vue_script__$$ = script$$;

/* template */
var __vue_render__$$ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-placeholder"
  }, [_c('bb-icon', {
    staticClass: "bb-placeholder__icon",
    attrs: {
      "name": _vm.icon,
      "size": "64",
      "fill": "original"
    }
  }), _vm._v(" "), _c('p', [_vm._v("\n    " + _vm._s(_vm.text) + "\n  ")])], 1);
};
var __vue_staticRenderFns__$$ = [];

/* style */
const __vue_inject_styles__$$ = function (inject) {
  if (!inject) return;
  inject("data-v-106a045e_0", {
    source: ".bb-placeholder{padding:.9375rem 0;color:#dee3e8;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-align:center}.bb-placeholder .bb-placeholder__icon{margin-bottom:1.25rem;opacity:.2}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$$ = undefined;
/* module identifier */
const __vue_module_identifier__$$ = undefined;
/* functional template */
const __vue_is_functional_template__$$ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$$ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$$,
  staticRenderFns: __vue_staticRenderFns__$$
}, __vue_inject_styles__$$, __vue_script__$$, __vue_scope_id__$$, __vue_is_functional_template__$$, __vue_module_identifier__$$, false, createInjector, undefined, undefined);

/**
 * A horizontal list with separators
 */
var script$_ = Vue.extend({
  name: 'bb-horizontal-list',
  components: {
    BbSeparator: __vue_component__$2a
  },
  props: {
    /**
     * Separated items | string[], unless you provide your own slot content.
     */
    value: {
      type: Array,
      default: () => []
    },
    /**
     * No wrapping
     */
    noWrap: Boolean
  }
});

/* script */
const __vue_script__$_ = script$_;

/* template */
var __vue_render__$_ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ul', {
    staticClass: "bb-horizontal-list",
    class: {
      'bb-horizontal-list--wrap': !_vm.noWrap
    }
  }, _vm._l(_vm.value, function (item, index) {
    return _c('li', {
      key: index,
      staticClass: "bb-horizontal-list__item"
    }, [_vm._t("default", function () {
      return [_vm._v("\n      " + _vm._s(item) + "\n    ")];
    }, {
      "item": item,
      "index": index
    }), _vm._v(" "), index < _vm.value.length - 1 ? _c('bb-separator', {
      staticClass: "bb-horizontal-list__separator",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e()], 2);
  }), 0);
};
var __vue_staticRenderFns__$_ = [];

/* style */
const __vue_inject_styles__$_ = function (inject) {
  if (!inject) return;
  inject("data-v-6e22f632_0", {
    source: ".bb-horizontal-list{display:flex}.bb-horizontal-list--wrap{flex-wrap:wrap}.bb-horizontal-list .bb-horizontal-list__item{display:flex}.bb-horizontal-list .bb-horizontal-list__separator{margin:0 .625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$_ = undefined;
/* module identifier */
const __vue_module_identifier__$_ = undefined;
/* functional template */
const __vue_is_functional_template__$_ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$_ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$_,
  staticRenderFns: __vue_staticRenderFns__$_
}, __vue_inject_styles__$_, __vue_script__$_, __vue_scope_id__$_, __vue_is_functional_template__$_, __vue_module_identifier__$_, false, createInjector, undefined, undefined);

var script$Z = {
  name: 'bb-tabs',
  components: {
    BbButton: __vue_component__$2v
  },
  directives: {
    resize
  },
  mixins: [validator],
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    sticky: {
      type: String,
      validator: v => ['top'].includes(v)
    },
    value: String,
    tabs: {
      type: Array,
      required: true
    },
    linkWidth: Number,
    extraHeight: Boolean,
    activeTabColor: {
      type: String,
      default: 'mint',
      validator: f => ['navy', 'mint', 'green'
      ].includes(f)
    },
    background: {
      type: String,
      validator: f => ['none', 'mint', 'green'
      ].includes(f)
    },
    centered: Boolean,
    vertical: Boolean,
    aligned: Boolean,
    isStickyOnMobile: Boolean,
    hideOnScroll: Boolean
  },
  emits: ['onTabClick', 'change'],
  data() {
    return {
      hidden: false,
      lastScrollTop: 0,
      val: this.value,
      overflow: {
        left: false,
        right: false
      },
      defaultAriaLabel: 'Tabs'
    };
  },
  computed: {
    classes() {
      return [this.activeTabColor && `bb-tabs--${this.activeTabColor}`, {
        [`bb-tabs--background-${this.background}`]: this.background,
        [`bb-tabs--sticky-${this.sticky}`]: this.sticky,
        'bb-tabs--hidden': this.hidden,
        'bb-tabs--fixed-width-links': this.linkWidth > 0,
        'bb-tabs--extra-height': this.extraHeight,
        'bb-tabs--vertical': this.vertical,
        'bb-tabs--aligned': this.aligned,
        'bb-tabs--centered': this.centered,
        'bb-tabs--sticky-on-mobile': this.isStickyOnMobile,
        'bb-tabs--overflow-left': this.overflow.left || null,
        'bb-tabs--overflow-right': this.overflow.right || null
      }];
    }
  },
  watch: {
    value(newVal) {
      this.val = newVal;
    }
  },
  created() {
    this.onScrollThrottled = throttle(this.onScroll, 100);
    this.onResizeThrottled = throttle(this.updateOverflowState, 100);
  },
  mounted() {
    if (this.isStickyOnMobile && this.hideOnScroll) {
      window.addEventListener('scroll', this.onScrollThrottled);
    }
    this.scrollToActiveTab();
  },
  updated() {
    this.updateOverflowState();
  },
  destroyed() {
    window.removeEventListener('scroll', this.onScrollThrottled);
  },
  methods: {
    isActiveTab(id) {
      return id === this.val;
    },
    onTabClick(value) {
      this.$emit('onTabClick', value);
      if (this.val !== value) {
        this.val = value;
        this.$emit('change', value);
      }
    },
    scrollToActiveTab() {
      if (!this.$refs.activeTab) {
        return;
      }
      const tabsScrollWrapper = this.$refs.tabs;
      const activeTab = this.$refs && this.$refs.activeTab[0];
      const tabsScrollWrapperWidth = tabsScrollWrapper.clientWidth;
      const activeTabRightEdgeOffsetLeft = activeTab.offsetLeft + activeTab.clientWidth;
      const activeTabOffsetWithinScrollWrapper = activeTabRightEdgeOffsetLeft - tabsScrollWrapperWidth;
      if (activeTabRightEdgeOffsetLeft > tabsScrollWrapperWidth) {
        tabsScrollWrapper.scrollTo({
          left: activeTabOffsetWithinScrollWrapper
        });
      }
      this.updateOverflowState();
    },
    updateOverflowState() {
      const target = this.$refs.tabs;
      this.overflow.left = isDefined(target) && target.scrollLeft > 0;
      this.overflow.right = isDefined(target) && target.scrollLeft < target.scrollWidth - target.offsetWidth;
    },
    onScroll() {
      const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
      if (scrollTop < this.lastScrollTop) {
        if (this.hidden) {
          this.hidden = false;
        }
      } else if (this.lastScrollTop > document.documentElement.clientHeight / 2) {
        this.hidden = true;
      }
      this.lastScrollTop = scrollTop;
    },
    onKeydown(event) {
      const keyDirectionMap = {
        ArrowRight: 1,
        ArrowDown: this.vertical ? 1 : 0,
        ArrowLeft: -1,
        ArrowUp: this.vertical ? -1 : 0
      };
      const keyDirection = keyDirectionMap[event.key];
      if (!keyDirection) return;
      event.preventDefault();
      const tabs = Array.from(event.target.closest('ul').querySelectorAll('[role="tab"]'));
      const currentIndex = tabs.indexOf(event.target);
      const newIndex = (currentIndex + keyDirection + tabs.length) % tabs.length;
      const newTab = tabs[newIndex];
      newTab.focus();
      this.updateActiveTab(newTab.getAttribute('aria-controls'));
    },
    updateActiveTab(panelId) {
      const tab = this.tabs.find(tab => `panel-${tab.id}` === panelId);
      if (tab) {
        this.onTabClick(tab.id);
      }
    }
  }
};

/* script */
const __vue_script__$Z = script$Z;

/* template */
var __vue_render__$Z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    directives: [{
      name: "resize",
      rawName: "v-resize",
      value: _vm.onResizeThrottled,
      expression: "onResizeThrottled"
    }],
    staticClass: "bb-tabs",
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.defaultAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-tabs__header"
  }, [_c('div', {
    ref: "tabs",
    staticClass: "bb-tabs__scroll-wrapper",
    on: {
      "scroll": _vm.onResizeThrottled
    }
  }, [_c('ul', {
    staticClass: "bb-tabs__titles-list",
    attrs: {
      "role": "tablist",
      "aria-orientation": _vm.vertical ? 'vertical' : 'horizontal'
    }
  }, _vm._l(_vm.tabs, function (tab) {
    return _c('li', {
      key: tab.id,
      ref: _vm.isActiveTab(tab.id) ? 'activeTab' : null,
      refInFor: true,
      staticClass: "bb-tabs__tab-title-wrapper",
      attrs: {
        "id": tab.id,
        "role": "tab",
        "aria-selected": _vm.isActiveTab(tab.id) ? 'true' : 'false',
        "aria-controls": "panel-" + tab.id,
        "tabindex": _vm.isActiveTab(tab.id) ? '0' : '-1'
      },
      on: {
        "keydown": [function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
            return null;
          }
          return _vm.onTabClick(tab.id);
        }, function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
            return null;
          }
          $event.preventDefault();
          return _vm.onTabClick(tab.id);
        }, _vm.onKeydown]
      }
    }, [_c('bb-button', {
      staticClass: "bb-tabs__tab-title",
      class: {
        'is-active': _vm.isActiveTab(tab.id)
      },
      style: {
        width: _vm.linkWidth + "px"
      },
      attrs: {
        "color": _vm.isActiveTab(tab.id) ? _vm.activeTabColor : 'gray',
        "no-underline": "",
        "display": "link",
        "tabindex": "-1"
      },
      on: {
        "click": function ($event) {
          return _vm.onTabClick(tab.id);
        }
      }
    }, [_c('div', {
      staticClass: "bb-tabs__tab-title-content"
    }, [_vm._t(tab.id + "-icon"), _vm._v(" "), _c('div', {
      staticClass: "bb-tabs__tab-title-text",
      attrs: {
        "data-title": tab.title
      }
    }, [_vm._t("menu-item", function () {
      return [_vm._v("\n                  " + _vm._s(tab.title) + "\n                ")];
    }, null, tab)], 2)], 2)])], 1);
  }), 0)])]), _vm._v(" "), _vm._l(_vm.tabs, function (tab) {
    return _c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.isActiveTab(tab.id),
        expression: "isActiveTab(tab.id)"
      }],
      key: tab.id,
      staticClass: "bb-tabs__tab-content",
      attrs: {
        "id": "panel-" + tab.id,
        "role": "tabpanel",
        "aria-labelledby": tab.id
      }
    }, [_vm._t(tab.id)], 2);
  })], 2);
};
var __vue_staticRenderFns__$Z = [];

/* style */
const __vue_inject_styles__$Z = function (inject) {
  if (!inject) return;
  inject("data-v-5f1620ac_0", {
    source: ".bb-tabs{overflow:hidden;background:#fff}.bb-tabs--sticky-top{overflow:unset}.bb-tabs--background-none{background:0 0}@media (min-width:768px){.bb-tabs--vertical{display:flex;font-size:.875rem}}.bb-tabs>.bb-tabs__header{transition:transform .4s,box-shadow .4s}.bb-tabs--sticky-top.bb-tabs>.bb-tabs__header{position:sticky;z-index:1200;top:0;padding-bottom:5rem;pointer-events:none}.bb-tabs--sticky-top.bb-tabs>.bb-tabs__header~*{margin-top:-5rem}.bb-tabs--overflow-left.bb-tabs>.bb-tabs__header{box-shadow:inset .9375rem 0 .9375rem -.9375rem rgba(0,0,0,.2)}.bb-tabs--overflow-right.bb-tabs>.bb-tabs__header{box-shadow:inset -.9375rem 0 .9375rem -.9375rem rgba(0,0,0,.2)}.bb-tabs--overflow-left.bb-tabs--overflow-right.bb-tabs>.bb-tabs__header{box-shadow:inset .9375rem 0 .9375rem -.9375rem rgba(0,0,0,.2),inset -.9375rem 0 .9375rem -.9375rem rgba(0,0,0,.2)}.bb-tabs--sticky-on-mobile.bb-tabs>.bb-tabs__header{position:fixed;z-index:1200;right:0;bottom:0;left:0;background:#fff;box-shadow:0 1.25rem 1.25rem 1.75rem rgba(0,0,0,.05)}@media (min-width:768px){.bb-tabs--sticky-on-mobile.bb-tabs>.bb-tabs__header{position:static;box-shadow:none}}.bb-tabs--hidden.bb-tabs>.bb-tabs__header{box-shadow:none;transform:translateY(100%)}@media (min-width:768px){.bb-tabs--hidden.bb-tabs>.bb-tabs__header{transform:none}}.bb-tabs>.bb-tabs__header .bb-tabs__scroll-wrapper{overflow:auto hidden;width:100%;-webkit-overflow-scrolling:touch;pointer-events:all;white-space:nowrap}@media (min-width:768px){.bb-tabs>.bb-tabs__header .bb-tabs__scroll-wrapper{white-space:normal}}.bb-tabs>.bb-tabs__header .bb-tabs__scroll-wrapper::-webkit-scrollbar{display:none}.bb-tabs--sticky-top.bb-tabs>.bb-tabs__header .bb-tabs__scroll-wrapper{background:#fff}.bb-tabs--aligned.bb-tabs>.bb-tabs__header .bb-tabs__scroll-wrapper{padding-left:1.875rem}@media (min-width:768px){.bb-tabs--vertical.bb-tabs>.bb-tabs__header .bb-tabs__scroll-wrapper{overflow:hidden auto;width:auto;height:100%}}.bb-tabs>.bb-tabs__header .bb-tabs__titles-list{display:flex;width:max-content;min-width:100%;user-select:none}@media (min-width:768px){.bb-tabs>.bb-tabs__header .bb-tabs__titles-list{position:relative;padding-bottom:0;border-bottom:.125rem solid #eeeff2}}.bb-tabs--background-mint.bb-tabs>.bb-tabs__header .bb-tabs__titles-list{padding:.25rem 0 .75rem;border-bottom:none;background:#0ccca2;color:#fff}.bb-tabs--centered.bb-tabs>.bb-tabs__header .bb-tabs__titles-list{justify-content:center}@media (min-width:768px){.bb-tabs--vertical.bb-tabs>.bb-tabs__header .bb-tabs__titles-list{display:block;height:100%;border-right:.125rem solid #eeeff2;border-bottom:0}}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-wrapper{display:flex;padding:0;border-left:none;margin:0;cursor:pointer}@media (min-width:768px){.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-wrapper{justify-content:flex-start}}.bb-tabs--aligned.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-wrapper:first-child{padding-left:0}@media (min-width:768px){.bb-tabs--vertical.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-wrapper{padding-top:.3125rem;padding-bottom:.3125rem}}@media (min-width:768px){.bb-tabs:not(.bb-tabs--vertical):not(.bb-tabs--aligned):not(.bb-tabs--centered).bb-tabs>.bb-tabs__header .bb-tabs__tab-title-wrapper:first-child .bb-tabs__tab-title.bb-button.bb-button.bb-button{padding-left:1.875rem}}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-wrapper svg{margin-right:.625rem;margin-bottom:0}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{width:100%;min-height:2.8125rem;align-items:center;padding:0 .625rem;margin:0 auto;color:#89a;font-size:.875rem;text-transform:none}@media (min-width:768px){.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{min-height:unset;padding-right:1.875rem;font-size:.875rem}}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button:focus{outline:0}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active .bb-tabs__tab-title-text,.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button:hover .bb-tabs__tab-title-text{color:#0ccca2}.bb-tabs--background-mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button:hover .bb-tabs__tab-title-text{color:#fff;opacity:1}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-tabs--background-mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active .bb-tabs__tab-title-text{color:#fff;opacity:1}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active .bb-tabs__tab-title-content::after{background:#0ccca2}.bb-tabs--background-mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active .bb-tabs__tab-title-content::after{background:#fff}.bb-tabs--sticky-on-mobile.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{font-size:.6875rem}@media (min-width:768px){.bb-tabs--sticky-on-mobile.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{font-size:.875rem}}.bb-tabs--aligned.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{padding-left:0}@media (min-width:768px){.bb-tabs--vertical.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{padding-right:0}}@media (min-width:768px){.bb-tabs--fixed-width-links.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button{padding:0}}.bb-tabs--navy.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active,.bb-tabs--navy.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button:hover{color:#00285a}.bb-tabs--mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active,.bb-tabs--mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button:hover{color:#0ccca2}.bb-tabs--green.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button.is-active,.bb-tabs--green.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button:hover{color:#0ccca2}.bb-tabs--background-mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title.bb-button.bb-button.bb-button .bb-tabs__tab-title-text{color:#fff;opacity:.7}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content{position:relative;display:flex;width:100%;height:100%;align-items:center;justify-content:center}@media (min-width:768px){.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content{position:relative;padding:.3125rem 0 .375rem}}@media (min-width:768px){.bb-tabs--background-mint.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content{padding:.3125rem 0 0}}@media (min-width:768px){.bb-tabs--tab-content.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content{padding-bottom:.375rem}}@media (min-width:768px){.bb-tabs--extra-height.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content{padding-top:.25rem;padding-bottom:.75rem}}@media (min-width:768px){.bb-tabs--vertical.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content{justify-content:left;padding-right:.9375rem}}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content::after{position:absolute;right:0;bottom:0;left:0;height:.125rem;content:\"\";transition:background .2s}@media (min-width:768px){.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content::after{bottom:-.125rem}}.bb-tabs--sticky-on-mobile.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content::after{top:0;bottom:initial}@media (min-width:768px){.bb-tabs--sticky-on-mobile.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-content::after{top:initial;bottom:-.125rem}}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-text{display:inline-flex;min-height:1.5rem;flex-direction:column;justify-content:center;transition:color .2s,opacity .2s}.bb-tabs>.bb-tabs__header .bb-tabs__tab-title-text::before{display:block;overflow:hidden;height:.0625rem;color:transparent;content:attr(data-title);font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;visibility:hidden}.bb-tabs__tab-content{padding:1.875rem}.bb-tabs__tab-content:empty{display:none}.bb-tabs--vertical>.bb-tabs__tab-content{min-width:0}@media (min-width:768px){.bb-tabs--vertical>.bb-tabs__tab-content{padding:0 1.875rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$Z = undefined;
/* module identifier */
const __vue_module_identifier__$Z = undefined;
/* functional template */
const __vue_is_functional_template__$Z = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$Z = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$Z,
  staticRenderFns: __vue_staticRenderFns__$Z
}, __vue_inject_styles__$Z, __vue_script__$Z, __vue_scope_id__$Z, __vue_is_functional_template__$Z, __vue_module_identifier__$Z, false, createInjector, undefined, undefined);

const FILTER_ALL = 'FILTER_ALL';
const feedItemProps = Object.keys(__vue_component__$1j.props);
const trimJoinedValues = obj => Object.values(obj).join('').trim();
const trim = v => toString(v).trim();
/**
 * A card for displaying and filtering memos in application back-office.
 */
var script$Y = Vue.extend({
  name: 'bb-memos',
  components: {
    BbFeedItem: __vue_component__$1j,
    BbIcon: __vue_component__$2C,
    BbCard: __vue_component__$2t,
    BbTabs: __vue_component__$Z,
    BbHorizontalList: __vue_component__$_,
    BbPlaceholder: __vue_component__$$
  },
  props: {
    /**
     * The name of the html element or Vue component for the Memos wrapper
     */
    wrapperComponent: {
      type: String,
      default: 'bb-card'
    },
    /**
     * Make the scroll wrapper overflow visible to show the entire status bullets
     */
    noScroll: Boolean,
    /**
     * Enable the delete button for Memos. Emits a `delete` event with memo `id` as payload
     */
    deleteButton: Boolean,
    /**
     * Label for the "All" filter shown as the first tab
     */
    labelFilterAll: {
      type: String,
      default: 'All'
    },
    /**
     * Label for type
     */
    labelType: {
      type: String,
      default: 'Type:'
    },
    /**
     * Label for memo creation date
     */
    labelRegistrationDate: {
      type: String,
      default: 'on:'
    },
    /**
     * Label for task
     */
    labelTask: {
      type: String,
      default: 'Task:'
    },
    /**
     * Key to be used for filtering the memos. Omit to hide the filtering Tabs
     */
    filteredKey: String,
    /**
     * Filters. Array of objects | {
     *   id: string|array of strings
     *   title: displayed string
     * }
     */
    filters: {
      type: Array,
      default() {
        return [];
      }
    },
    /**
     * Memos
     */
    memos: {
      type: Array,
      required: true
    },
    /**
     * Date display format.
     */
    dateDisplayFormat: {
      type: String,
      default: 'DD.MM.YYYY - HH:mm'
    }
  },
  data() {
    return {
      filterId: FILTER_ALL
    };
  },
  computed: {
    templateFilters() {
      if (this.filteredKey === '') {
        return [];
      }
      return [{
        title: this.labelFilterAll,
        id: FILTER_ALL
      }, ...this.filters].map(({
        title,
        id
      }) => ({
        title,
        id: toString(id),
        value: castArray(id)
      }));
    },
    filterValue() {
      var _this$templateFilters, _this$templateFilters2, _this$templateFilters3;
      return [...((_this$templateFilters = (_this$templateFilters2 = this.templateFilters) === null || _this$templateFilters2 === void 0 ? void 0 : (_this$templateFilters3 = _this$templateFilters2.find(filter => this.filterId === filter.id)) === null || _this$templateFilters3 === void 0 ? void 0 : _this$templateFilters3.value) !== null && _this$templateFilters !== void 0 ? _this$templateFilters : [])];
    },
    templateMemos() {
      return this.memos.map(memo => {
        var _memo$contactDirectio;
        return {
          memo,
          feedItemProps: pick(memo, feedItemProps),
          contentItems: [{
            hasIcon: memo.contactChannel || memo.contactDirection || memo.registrationDate,
            label: [memo.contactChannel && toTitleCase(memo.contactChannel), (_memo$contactDirectio = memo.contactDirection) === null || _memo$contactDirectio === void 0 ? void 0 : _memo$contactDirectio.toUpperCase(), memo.registrationDate && this.labelRegistrationDate].filter(trim).join(' '),
            highlight: memo.registrationDate && dayjs(memo.registrationDate).format(this.dateDisplayFormat)
          }, {
            label: memo.taskName && this.labelTask,
            highlight: memo.taskName
          }].filter(trimJoinedValues),
          footerItems: [memo.topic, memo.subTopic, `${toString(memo.commentType && this.labelType)} ${toString(memo.commentType)}`].filter(trim)
        };
      });
    },
    filteredTemplateMemos() {
      return FILTER_ALL === this.filterId ? this.templateMemos : this.templateMemos.filter(({
        memo
      }) => this.filterValue.includes(memo[this.filteredKey]));
    }
  },
  methods: {
    onClick(memoId) {
      /**
       * A memo is clicked, payload is `memo.id`.
       * @event click
       */
      this.$emit('click', memoId);
    },
    onDelete(memoId) {
      /**
       * A memo's delete button is clicked, payload is `memo.id`.
       * @event delete
       */
      this.$emit('delete', memoId);
    }
  }
});

/* script */
const __vue_script__$Y = script$Y;

/* template */
var __vue_render__$Y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(_vm.wrapperComponent, {
    tag: "component",
    staticClass: "bb-memos",
    class: {
      'bb-memos--has-tabs': _vm.filteredKey,
      'bb-memos--scrollable': !_vm.noScroll,
      'is-card': _vm.wrapperComponent === 'bb-card'
    },
    attrs: {
      "size": "wide"
    }
  }, [_vm.filteredKey ? _c('bb-tabs', {
    staticClass: "bb-memos__tabs",
    attrs: {
      "tabs": _vm.templateFilters,
      "aligned": _vm.wrapperComponent === 'bb-card'
    },
    model: {
      value: _vm.filterId,
      callback: function ($$v) {
        _vm.filterId = $$v;
      },
      expression: "filterId"
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-memos__scroll-wrapper"
  }, [0 === _vm.filteredTemplateMemos.length ? _c('bb-placeholder', {
    staticClass: "bb-memos__placeholder"
  }) : _vm._e(), _vm._v(" "), _vm._l(_vm.filteredTemplateMemos, function (ref) {
    var memo = ref.memo;
    var feedItemProps = ref.feedItemProps;
    var footerItems = ref.footerItems;
    var contentItems = ref.contentItems;
    return _c('bb-feed-item', _vm._b({
      key: memo.id,
      staticClass: "bb-memos__memo",
      attrs: {
        "delete-button": _vm.deleteButton
      },
      on: {
        "click": function ($event) {
          return _vm.onClick(memo.id);
        },
        "delete": function ($event) {
          return _vm.onDelete(memo.id);
        }
      },
      scopedSlots: _vm._u([{
        key: "action-buttons",
        fn: function () {
          return [_vm._t("action-buttons", null, {
            "memo": memo
          })];
        },
        proxy: true
      }], null, true)
    }, 'bb-feed-item', feedItemProps, false), [contentItems.length ? _c('bb-horizontal-list', {
      staticClass: "bb-memos__content",
      attrs: {
        "value": contentItems
      },
      scopedSlots: _vm._u([{
        key: "default",
        fn: function (ref) {
          var contentItem = ref.item;
          return _c('span', {}, [contentItem.hasIcon ? _c('bb-icon', {
            staticClass: "bb-memos__content-icon",
            attrs: {
              "size": "18",
              "fill": "blue",
              "name": "ui-comment-outline"
            }
          }) : _vm._e(), _vm._v("\n          " + _vm._s(contentItem.label) + "\n          "), _c('span', {
            staticClass: "bb-memos__content-highlight"
          }, [_vm._v(_vm._s(contentItem.highlight))])], 1);
        }
      }], null, true)
    }) : _vm._e(), _vm._v(" "), footerItems.length ? _c('bb-horizontal-list', {
      staticClass: "bb-memos__footer",
      attrs: {
        "value": footerItems
      }
    }) : _vm._e(), _vm._v(" "), _vm._v(" "), _vm._t(memo.id)], 2);
  })], 2)], 1);
};
var __vue_staticRenderFns__$Y = [];

/* style */
const __vue_inject_styles__$Y = function (inject) {
  if (!inject) return;
  inject("data-v-445342d0_0", {
    source: ".bb-memos{display:flex;height:100%;flex-direction:column}.bb-memos.is-card{padding-left:0}.bb-memos .bb-memos__tabs{overflow:visible;margin-bottom:.75rem}.is-card.bb-memos .bb-memos__tabs{margin-top:-1.1875rem}.bb-memos .bb-memos__tabs .bb-tabs__tab-title-text{text-transform:capitalize}.bb-memos--scrollable.bb-memos .bb-memos__scroll-wrapper{overflow-y:auto}.is-card.bb-memos .bb-memos__scroll-wrapper{padding-left:1.875rem}.bb-memos .bb-memos__memo .bb-feed-item__title{display:-webkit-box;overflow:hidden;max-height:3rem;-webkit-box-orient:vertical;box-orient:vertical;-webkit-line-clamp:3;line-height:1rem;text-overflow:ellipsis}.bb-memos .bb-memos__memo:not(:first-child){margin-top:.75rem}.bb-memos .bb-memos__content{color:#89a}.bb-memos .bb-memos__content-highlight{margin-left:.25rem;color:#2485e5;white-space:nowrap}.bb-memos .bb-memos__content-icon{margin:-.1875rem .25rem -.0625rem 0}.bb-memos .bb-memos__footer{margin:.25rem 0}.bb-memos--has-tabs.bb-memos .bb-memos__placeholder{margin-top:.5rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$Y = undefined;
/* module identifier */
const __vue_module_identifier__$Y = undefined;
/* functional template */
const __vue_is_functional_template__$Y = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$Y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$Y,
  staticRenderFns: __vue_staticRenderFns__$Y
}, __vue_inject_styles__$Y, __vue_script__$Y, __vue_scope_id__$Y, __vue_is_functional_template__$Y, __vue_module_identifier__$Y, false, createInjector, undefined, undefined);

var script$X = {
  name: 'bb-menu-item',
  components: {
    BbRouterLink: __vue_component__$1D,
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbIconFrame: __vue_component__$2g,
    BbLabel: __vue_component__$2e
  },
  mixins: [HrefHandler],
  props: {
    active: Boolean,
    hasChildren: Boolean,
    arrow: {
      type: String,
      validator: display => ['hidden', 'hover', 'visible'].includes(display),
      default: 'hover'
    },
    disabled: Boolean,
    label: String,
    tag: String,
    icon: {
      type: [Object, String]
    },
    type: {
      type: String,
      default: 'link',
      validator: type => ['link', 'button'].includes(type)
    }
  },
  computed: {
    classes() {
      return {
        'bb-menu-item--active': this.active,
        'bb-menu-item--disabled': this.disabled,
        [`bb-menu-item--arrow-${this.arrow}`]: this.arrow
      };
    },
    typeIsLink() {
      return this.type === 'link';
    },
    computedComponent() {
      return this.typeIsLink ? 'bb-router-link' : 'bb-button';
    },
    computedTo() {
      return this.typeIsLink && (!this.disabled || !this.hasChildren) ? this.to : null;
    },
    computedTabindex() {
      return this.disabled ? -1 : 0;
    },
    computedButtonProps() {
      return !this.typeIsLink && {
        noBackground: true,
        noPaddings: true,
        color: 'gray'
      };
    },
    computedIcon() {
      return isObject$1(this.icon) ? this.icon.name : this.icon;
    }
  },
  methods: {
    notEmpty
  }
};

/* script */
const __vue_script__$X = script$X;

/* template */
var __vue_render__$X = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(_vm.computedComponent, _vm._g(_vm._b({
    tag: "component",
    staticClass: "bb-menu-item",
    class: _vm.classes,
    attrs: {
      "to": _vm.computedTo,
      "target": _vm.target,
      "tabindex": _vm.computedTabindex,
      "no-underline": ""
    }
  }, 'component', _vm.computedButtonProps, false), _vm.$listeners), [_vm.icon && _vm.notEmpty(_vm.icon) ? _c('bb-icon-frame', {
    staticClass: "bb-menu-item__icon",
    attrs: {
      "icon": _vm.computedIcon,
      "icon-color": "gray",
      "background-color": "gray-20",
      "icon-size": "18",
      "size": "small",
      "shape": "circle"
    }
  }) : _vm._e(), _vm._v(" "), _c('span', {
    staticClass: "bb-menu-item__label",
    attrs: {
      "data-label": _vm.label
    }
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]), _vm._v(" "), _vm.tag ? _c('bb-label', {
    staticClass: "bb-menu-item__tag",
    attrs: {
      "size": "18",
      "value": _vm.tag
    }
  }) : _vm._e(), _vm._v(" "), _c('bb-icon', {
    staticClass: "bb-menu-item__indicator",
    attrs: {
      "name": "ui-chevron-right",
      "fill": "gray-70",
      "size": "24"
    }
  })], 1);
};
var __vue_staticRenderFns__$X = [];

/* style */
const __vue_inject_styles__$X = function (inject) {
  if (!inject) return;
  inject("data-v-5c6e7dc0_0", {
    source: ".bb-menu-item{display:flex;width:100%;min-height:2.5rem;align-items:center;padding:.4375rem .625rem;border:.0625rem dashed transparent;border-radius:.625rem;background:0 0;box-shadow:none;cursor:pointer;text-align:left;transition:background .1s}.bb-menu-item::before{display:none}@media (hover){.bb-menu-item.bb-menu-item:active,.bb-menu-item.bb-menu-item:focus,.bb-menu-item.bb-menu-item:hover{background:#f7f7f8}}.bb-menu-item--has-sub-items{cursor:pointer}.bb-menu-item--active{background:#f7f7f8}.bb-menu-item--active.bb-menu-item--disabled{background:0 0}.bb-menu-item--disabled{opacity:.5;pointer-events:none}.bb-menu-item__icon,.bb-menu-item__label{transform:translateX(0)}@media (hover){.bb-menu-item:not(.bb-menu-item--disabled):hover .bb-menu-item__icon,.bb-menu-item:not(.bb-menu-item--disabled):hover .bb-menu-item__label{transform:translateX(.9375rem)}}.bb-menu-item__icon{margin-right:.9375rem;transition:transform .2s ease-out,background .1s}.bb-menu-item--active:not(.bb-menu-item--disabled) .bb-menu-item__icon{background:#0ccca2}.bb-menu-item--active:not(.bb-menu-item--disabled) .bb-menu-item__icon .bb-icon__dynamic-fill{fill:#fff}.bb-menu-item:not(.bb-menu-item--disabled) .bb-menu-item__icon .bb-icon__dynamic-fill{transition:fill .1s}@media (hover){.bb-menu-item:not(.bb-menu-item--disabled):hover .bb-menu-item__icon{background:#0ccca2}.bb-menu-item:not(.bb-menu-item--disabled):hover .bb-menu-item__icon .bb-icon__dynamic-fill{fill:#fff}}.bb-menu-item__label{display:-webkit-box;overflow:hidden;flex:1 1 0;margin-right:.625rem;-webkit-box-orient:vertical;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;-webkit-line-clamp:2;transition:color .2s,transform .2s ease-out}.bb-menu-item__label::before{display:block;overflow:hidden;height:1px;color:transparent;content:attr(data-label);font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;visibility:hidden}.bb-menu-item--active:not(.bb-menu-item--disabled) .bb-menu-item__label{color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}@media (hover){.bb-menu-item:not(.bb-menu-item--disabled):active .bb-menu-item__label,.bb-menu-item:not(.bb-menu-item--disabled):focus .bb-menu-item__label,.bb-menu-item:not(.bb-menu-item--disabled):hover .bb-menu-item__label{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}}@media (hover){.bb-menu-item:not(.bb-menu-item--disabled):hover .bb-menu-item__label{color:#0ccca2}}.bb-menu-item__indicator,.bb-menu-item__tag{margin-left:.9375rem}.bb-menu-item__tag{background:#0ccca2;font-size:.5625rem}.bb-menu-item__indicator{opacity:0;transition:opacity .2s}@media (hover){.bb-menu-item:hover .bb-menu-item__indicator{opacity:1}}.bb-menu-item--arrow-visible .bb-menu-item__indicator{opacity:1}.bb-menu-item--arrow-hidden .bb-menu-item__indicator{display:none}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$X = undefined;
/* module identifier */
const __vue_module_identifier__$X = undefined;
/* functional template */
const __vue_is_functional_template__$X = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$X = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$X,
  staticRenderFns: __vue_staticRenderFns__$X
}, __vue_inject_styles__$X, __vue_script__$X, __vue_scope_id__$X, __vue_is_functional_template__$X, __vue_module_identifier__$X, false, createInjector, undefined, undefined);

var script$W = {
  name: 'bb-modal',
  components: {
    BbOverlay: __vue_component__$2u,
    BbButton: __vue_component__$2v
  },
  directives: {
    resize
  },
  mixins: [validator],
  props: {
    visible: Boolean,
    classes: String,
    size: {
      type: String,
      default: 'x1',
      validator: size => ['x1', 'x2', 'x3'].includes(size)
    },
    publicSize: {
      type: String,
      validator: size => ['s', 'm', 'l'].includes(size)
    },
    flavor: {
      type: String,
      default: 'admin',
      validator: f => ['admin', 'user', 'client', 'card'].includes(f)
    },
    centerVertically: Boolean,
    fullMobile: {
      type: Boolean,
      default: false
    },
    staticOverlay: Boolean,
    hideClose: {
      type: Boolean,
      default: false
    },
    unsetOverflow: {
      type: Boolean,
      default: false
    },
    scroll: {
      type: Boolean,
      default: false
    },
    closeAriaLabel: {
      type: String,
      default: 'Close modal'
    },
    dialogAriaLabel: String,
    titleTag: {
      type: String,
      default: 'h2',
      validator: tagName => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)
    }
  },
  emits: ['close'],
  computed: {
    modalClasses() {
      return [this.classes, this.publicSize ? `bb-modal--${this.publicSize}` : `wrapper--${this.size}`, `bb-modal--${this.flavor}`, {
        'bb-modal--card-full-mobile': this.flavor === 'card' && this.fullMobile,
        'bb-modal--unset-overflow': this.unsetOverflow,
        'bb-modal--scroll': this.scroll
      }];
    }
  },
  watch: {
    visible() {
      this.visible && this.$nextTick(() => {
        this.$el.focus();
      });
    }
  },
  mounted() {
    this.handleResize();
  },
  methods: {
    hideModal(eventSource) {
      this.$emit('close', {
        eventSource
      });
    },
    handleResize: throttle(() => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vhModal', `${vh}px`);
    }, 100)
  }
};

/* script */
const __vue_script__$W = script$W;

/* template */
var __vue_render__$W = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.visible ? _c('bb-overlay', {
    directives: [{
      name: "resize",
      rawName: "v-resize",
      value: _vm.handleResize,
      expression: "handleResize"
    }],
    attrs: {
      "visible": "",
      "fluid": _vm.flavor === 'client',
      "center-vertically": _vm.centerVertically,
      "fixed-mobile-size": _vm.flavor === 'card' && _vm.fullMobile,
      "color": "dark",
      "dialog-aria-label": _vm.dialogAriaLabel
    },
    on: {
      "close": function ($event) {
        !_vm.staticOverlay && _vm.hideModal('click-outside');
      }
    }
  }, [_c('div', {
    staticClass: "bb-modal wrapper",
    class: _vm.modalClasses
  }, [_c('div', {
    staticClass: "bb-modal__header"
  }, [['admin', 'card'].includes(_vm.flavor) ? _c(_vm.titleTag, {
    tag: "component",
    staticClass: "bb-modal__title"
  }, [_vm._t("heading")], 2) : _vm._e()], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-modal__body"
  }, [_vm._t("default"), _vm._v(" "), _vm.$slots.disclaimer ? _c('div', {
    staticClass: "bb-modal__disclaimer-mobile"
  }, [_vm._t("disclaimer")], 2) : _vm._e()], 2), _vm._v(" "), _vm.$slots.footer ? _c('div', {
    staticClass: "bb-modal__footer"
  }, [_vm._t("footer")], 2) : _vm._e(), _vm._v(" "), !_vm.hideClose ? _c('bb-button', {
    staticClass: "bb-modal__close",
    attrs: {
      "no-background": "",
      "display": "square",
      "icon": "ui-close",
      "color": "gray",
      "aria-label": _vm.closeAriaLabel
    },
    on: {
      "click": function ($event) {
        return _vm.hideModal('cross');
      }
    }
  }) : _vm._e()], 1), _vm._v(" "), _vm.$slots.disclaimer ? _c('div', {
    staticClass: "bb-modal__disclaimer-desktop"
  }, [_vm._t("disclaimer")], 2) : _vm._e()]) : _vm._e();
};
var __vue_staticRenderFns__$W = [];

/* style */
const __vue_inject_styles__$W = function (inject) {
  if (!inject) return;
  inject("data-v-1b15299a_0", {
    source: ".bb-modal{position:relative;display:flex;overflow:hidden;flex-direction:column;border-radius:.3125rem;margin:0 auto;background-clip:padding-box;background-color:#fff;box-shadow:0 .125rem 1.25rem 0 rgba(0,0,0,.32);outline:0;transition:all .2s ease-in}.bb-modal--unset-overflow{overflow:unset}.bb-modal--card,.bb-modal--user{border-radius:.625rem}.bb-modal--s{width:340px}.bb-modal--m{width:425px}.bb-modal--l{width:640px}@media (max-width:768px){.bb-modal--client{width:100%}}.bb-modal--card-full-mobile{border-bottom-left-radius:0;border-bottom-right-radius:0}@media (min-width:768px){.bb-modal--card-full-mobile{border-bottom-left-radius:.625rem;border-bottom-right-radius:.625rem}}@media (max-width:768px){.bb-modal--card{max-height:calc(100vh - 20px)}.bb-modal--card-full-mobile{width:100%;min-height:calc(var(--vhModal) * 100 - 20px);max-height:calc(var(--vhModal) * 100 - 20px)}}.bb-modal__header{padding:1.25rem}.bb-modal--admin .bb-modal__header{background-color:#f7f7f8}.bb-modal--user .bb-modal__header{padding:0}.bb-modal__title{margin-bottom:0;color:#00285a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-modal--card .bb-modal__title{color:#bdc7d0;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-align:center}.bb-modal__close.bb-button{position:absolute;z-index:1100;top:0;right:0}.bb-modal__body{padding:1.25rem;overflow-y:auto}.bb-modal--user .bb-modal__body{padding:1.875rem}.bb-modal--card .bb-modal__body{display:block;overflow:auto;max-height:65vh;flex:1 0 auto;padding:0 1.875rem;-webkit-overflow-scrolling:touch;-ms-overflow-style:-ms-autohiding-scrollbar}@media (max-width:768px){.bb-modal--card-full-mobile .bb-modal__body{min-height:100%;max-height:unset;flex:1;padding:0 1.875rem}}.bb-modal--scroll .bb-modal__body{overflow:auto;max-height:60vh}.bb-modal__footer{padding:1.25rem}.bb-modal--card .bb-modal__footer{padding:0}@media (min-width:768px){.bb-modal--card .bb-modal__footer{padding:1.875rem}}.bb-modal__disclaimer-mobile{margin-top:1.875rem;margin-bottom:1.875rem;color:#38414d;font-size:.75rem}@media (min-width:768px){.bb-modal__disclaimer-mobile{display:none}}.bb-modal__disclaimer-desktop{display:none;max-width:43.75rem;margin-top:2.8125rem;margin-bottom:2.5rem;color:#fff;font-size:.75rem;text-align:center}@media (min-width:768px){.bb-modal__disclaimer-desktop{display:block}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$W = undefined;
/* module identifier */
const __vue_module_identifier__$W = undefined;
/* functional template */
const __vue_is_functional_template__$W = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$W = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$W,
  staticRenderFns: __vue_staticRenderFns__$W
}, __vue_inject_styles__$W, __vue_script__$W, __vue_scope_id__$W, __vue_is_functional_template__$W, __vue_module_identifier__$W, false, createInjector, undefined, undefined);

const generateRange$1 = length => Array.apply(null, {
  length
}).map((n, i) => i + 1);
const closest = (nums, goal) => nums.length ? nums.reduce((prev, curr) => Math.abs(curr - goal) < Math.abs(prev - goal) ? curr : prev) : NaN;
const uniqueArray = val => [...new Set(val)];
const uniqueArrayBy = (val, by) => uniqueArray(val.map(by));
const first = array => array[0];
const last = array => array[array.length - 1];

const allowedVisiblePages = 7;
const paginate = (pages, activePage = 1) => {
  if (pages <= allowedVisiblePages) {
    if (pages === 0) return 1;
    return generateRange$1(pages);
  }
  const paginatedPages = generateRange$1(allowedVisiblePages);
  paginatedPages[allowedVisiblePages - 1] = pages;
  if (activePage > 3) {
    const upperRange = activePage >= pages - 2;
    paginatedPages[2] = upperRange ? pages - 4 : activePage - 1;
    paginatedPages[3] = upperRange ? pages - 3 : activePage;
    paginatedPages[4] = upperRange ? pages - 2 : activePage + 1;
  }
  paginatedPages[1] = activePage <= 4 ? 2 : '…';
  paginatedPages[allowedVisiblePages - 2] = activePage >= pages - 3 ? pages - 1 : '…';
  return paginatedPages;
};

function bound(value, length) {
  if (value < 1) return 1;
  if (value > length && length !== 0) return length;
  return value;
}
var script$V = {
  name: 'bb-pagination',
  components: {
    BbRow: __vue_component__$22,
    BbButton: __vue_component__$2v
  },
  props: {
    value: Number,
    length: Number,
    searchable: Boolean,
    single: Boolean,
    isNextPagePresent: {
      type: Boolean,
      default: true
    }
  },
  emits: ['input'],
  data() {
    return {
      name: uuid(),
      val: bound(this.value, this.length)
    };
  },
  computed: {
    totalPages() {
      return this.single ? [this.val] : paginate(this.length, this.val);
    },
    inputWidth() {
      const digitLength = String(this.length).length;
      return digitLength === 3 ? 'medium' : digitLength < 3 ? 'small' : 'large';
    }
  },
  watch: {
    value(newValue) {
      this.val = bound(newValue, this.length);
    }
  },
  methods: {
    navigate(page, input = false) {
      const pageNumber = parseInt(page, 10);
      if (page === false) {
        this.val--;
      } else if (page === true) {
        this.val++;
      } else if (pageNumber && pageNumber > 0 && pageNumber <= this.length) {
        this.val = pageNumber;
      } else if (input) {
        this.$refs.input[0].blur();
        this.$refs.input[0].value = this.val;
      }
      this.$emit('input', this.val);
    },
    getLabelClass(page) {
      return {
        'bb-pagination__label--selected': this.val === page,
        'bb-pagination__label--medium': this.inputWidth === 'medium',
        'bb-pagination__label--large': this.inputWidth === 'large',
        'bb-pagination__label--inactive': page === '…',
        'bb-pagination__label--single': this.single
      };
    }
  }
};

/* script */
const __vue_script__$V = script$V;

/* template */
var __vue_render__$V = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-row', {
    staticClass: "bb-pagination",
    attrs: {
      "align": "center",
      "no-gutters": ""
    }
  }, [_c('bb-button', {
    staticClass: "bb-pagination__button-back",
    attrs: {
      "disabled": _vm.length === 0 ? true : _vm.val <= 1,
      "size": "sm",
      "color": "blue",
      "icon": "ui-chevron-left",
      "display": "square"
    },
    on: {
      "click": function ($event) {
        return _vm.navigate(false);
      }
    }
  }), _vm._v(" "), _vm._l(_vm.totalPages, function (page, i) {
    return _c('label', {
      key: i,
      staticClass: "bb-pagination__label",
      class: _vm.getLabelClass(page),
      attrs: {
        "size": "sm"
      },
      on: {
        "click": function ($event) {
          $event.preventDefault();
          return _vm.navigate(page);
        }
      }
    }, [_vm.searchable && _vm.val === page && _vm.length > 7 ? _c('input', {
      ref: "input",
      refInFor: true,
      staticClass: "bb-pagination__input",
      class: "bb-pagination__input--" + _vm.inputWidth,
      attrs: {
        "name": _vm.name,
        "type": "number",
        "size": "medium"
      },
      domProps: {
        "value": page
      },
      on: {
        "keyup": function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
            return null;
          }
          return _vm.navigate($event.target.value, _vm.value);
        },
        "blur": function ($event) {
          return _vm.navigate($event.target.value, _vm.value);
        }
      }
    }) : [_c('input', {
      staticClass: "bb-pagination__radio visually-hidden",
      attrs: {
        "name": _vm.name,
        "disabled": _vm.val === '…',
        "type": "radio"
      },
      domProps: {
        "value": page
      }
    }), _vm._v("\n      " + _vm._s(page) + "\n    ")]], 2);
  }), _vm._v(" "), _c('bb-button', {
    staticClass: "bb-pagination__button-forward",
    attrs: {
      "disabled": _vm.single ? !_vm.isNextPagePresent : _vm.val >= _vm.length,
      "size": "sm",
      "color": "blue",
      "icon": "ui-chevron-right",
      "display": "square"
    },
    on: {
      "click": function ($event) {
        return _vm.navigate(true);
      }
    }
  })], 2);
};
var __vue_staticRenderFns__$V = [];

/* style */
const __vue_inject_styles__$V = function (inject) {
  if (!inject) return;
  inject("data-v-037247fe_0", {
    source: ".bb-pagination{flex-wrap:nowrap}.bb-pagination__input{width:1.875rem;padding:0;border:.0625rem solid #bdc7d0;color:#2485e5;text-align:center}.bb-pagination__button-back{margin-right:.625rem}.bb-pagination__button-forward{margin-left:.625rem}.bb-pagination__input,.bb-pagination__label{display:flex;min-width:1.875rem;height:1.875rem;align-items:center;justify-content:center;border-radius:.1875rem;font-size:12px}.bb-pagination__input--selected,.bb-pagination__label--selected{color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-pagination__input--medium,.bb-pagination__label--medium{width:2.5rem}.bb-pagination__input--large,.bb-pagination__label--large{width:3.4375rem}.bb-pagination__input:hover:not(.bb-pagination__label--inactive,.bb-pagination__label--single),.bb-pagination__label:hover:not(.bb-pagination__label--inactive,.bb-pagination__label--single){background-color:#dee3e8;color:#2485e5;cursor:pointer}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$V = undefined;
/* module identifier */
const __vue_module_identifier__$V = undefined;
/* functional template */
const __vue_is_functional_template__$V = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$V = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$V,
  staticRenderFns: __vue_staticRenderFns__$V
}, __vue_inject_styles__$V, __vue_script__$V, __vue_scope_id__$V, __vue_is_functional_template__$V, __vue_module_identifier__$V, false, createInjector, undefined, undefined);

var script$U = {
  name: 'bb-product-card',
  components: {
    BbCard: __vue_component__$2t,
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbIconFrame: __vue_component__$2g,
    BbLink: __vue_component__$2p,
    BbSeparator: __vue_component__$2a,
    BbSkeleton: __vue_component__$2d,
    BbListItem: __vue_component__$29,
    BbListGroup: __vue_component__$2b
  },
  props: {
    closeButton: Boolean,
    loading: Boolean,
    readMore: Boolean,
    imagePath: String,
    icon: String,
    title: String,
    description: String,
    campaignLabel: String,
    loyaltyLabel: String,
    cardLink: String,
    button: {
      type: Object,
      default() {
        return {};
      }
    },
    columns: {
      type: Object,
      default() {
        return {};
      }
    },
    bullets: Array,
    ariaLabels: {
      type: Object,
      default: () => ({
        closeButton: 'Close',
        leftColumnSpecialContent: 'Crossed through'
      })
    }
  },
  emits: ['click', 'close'],
  data() {
    return {
      limitBullets: 4,
      defaultAriaLabel: 'Card'
    };
  },
  computed: {
    classes() {
      return {
        'bb-product-card--bullets': this.bullets,
        'bb-product-card--loading': this.loading,
        'bb-product-card--read-more': this.readMore,
        'bb-product-card--loyalty': this.loyaltyLabel,
        'bb-product-card--campaign': this.campaignLabel,
        'bb-product-card--single-column': this.singleColumn,
        'bb-product-card--close-button': this.closeButton,
        'bb-product-card--card-link': this.cardLink
      };
    },
    singleColumn() {
      return this.columns && this.columns.left && isEmpty(this.columns.right);
    },
    computedBullets() {
      return this.bullets.slice(0, this.limitBullets);
    },
    computedSpecialTagLabel() {
      return this.loyaltyLabel || this.campaignLabel;
    },
    computedSpecialTagIcon() {
      return this.loyaltyLabel ? 'ui-star' : this.campaignLabel ? 'ui-tag' : null;
    },
    hasCardLink() {
      return notEmpty(this.cardLink);
    },
    computedUrl() {
      return this.hasCardLink ? this.cardLink : this.button.to;
    },
    computedButtonProps() {
      return {
        ...this.button,
        to: this.computedUrl
      };
    },
    contentAriaLabel() {
      return this.columns.left.specialContent && !this.singleColumn ? `${this.columns.left.content} ${this.ariaLabels.leftColumnSpecialContent}, ${this.columns.left.specialContent}` : undefined;
    }
  },
  methods: {
    onButtonClick() {
      this.$emit('click');
    },
    onCloseClick() {
      this.$emit('close');
    },
    notEmpty
  }
};

/* script */
const __vue_script__$U = script$U;

/* template */
var __vue_render__$U = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-card', {
    staticClass: "bb-product-card",
    class: _vm.classes,
    attrs: {
      "aria-label": !_vm.hasCardLink ? _vm.defaultAriaLabel : undefined,
      "aria-busy": _vm.loading
    }
  }, [_vm.hasCardLink ? _c('bb-link', {
    staticClass: "bb-product-card__overlay-link",
    attrs: {
      "to": _vm.cardLink,
      "no-underline": ""
    },
    on: {
      "click": _vm.onButtonClick
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-product-card__container"
  }, [_vm.imagePath && !_vm.loading ? _c('div', {
    staticClass: "bb-product-card__image",
    style: {
      'background-image': "url(" + _vm.imagePath + ")"
    }
  }, [_c('div', {
    staticClass: "bb-product-card__image-header"
  }, [_vm.computedSpecialTagLabel && !_vm.readMore ? _c('div', {
    staticClass: "bb-product-card__special-tag"
  }, [_c('bb-icon', {
    staticClass: "bb-product-card__special-tag-icon",
    attrs: {
      "name": _vm.computedSpecialTagIcon,
      "size": "18"
    }
  }), _vm._v("\n          " + _vm._s(_vm.computedSpecialTagLabel) + "\n        ")], 1) : _vm._e(), _vm._v(" "), _vm.closeButton ? _c('bb-button', {
    staticClass: "bb-product-card__close-button",
    attrs: {
      "color": "white",
      "icon": "ui-close",
      "display": "square",
      "no-background": "",
      "aria-label": _vm.ariaLabels.closeButton
    },
    on: {
      "click": _vm.onCloseClick
    }
  }) : _vm._e()], 1), _vm._v(" "), _vm.icon && !_vm.readMore ? _c('bb-icon-frame', {
    staticClass: "bb-product-card__icon",
    attrs: {
      "icon": _vm.icon,
      "icon-color": "navy",
      "shape": "circle",
      "size": "medium",
      "filled": ""
    }
  }) : _vm._e()], 1) : _vm.loading ? _c('bb-skeleton', {
    staticClass: "bb-product-card__image",
    attrs: {
      "width": "100%",
      "height": "115",
      "radius": "0",
      "color": "gray-30"
    }
  }) : _vm._e(), _vm._v(" "), _vm.readMore ? _c('div', {
    staticClass: "bb-product-card__image-overlay"
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-product-card__content"
  }, [_vm.title ? _c('h3', {
    staticClass: "bb-product-card__title"
  }, [!_vm.loading ? [_vm._v("\n          " + _vm._s(_vm.title) + "\n        ")] : [_c('bb-skeleton', {
    staticClass: "m-b-10",
    attrs: {
      "width": "170",
      "height": "20",
      "color": "gray-30"
    }
  })]], 2) : _vm._e(), _vm._v(" "), _vm.description ? _c('div', {
    staticClass: "bb-product-card__description"
  }, [!_vm.loading ? [_vm._v("\n          " + _vm._s(_vm.description) + "\n        ")] : [_c('bb-skeleton', {
    staticClass: "m-b-15",
    attrs: {
      "width": "90%",
      "height": "7",
      "color": "gray-30"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "m-b-10",
    attrs: {
      "width": "90%",
      "height": "7",
      "color": "gray-30"
    }
  })]], 2) : _vm._e(), _vm._v(" "), _vm.notEmpty(_vm.columns) && !_vm.readMore ? _c('div', {
    staticClass: "bb-product-card__columns"
  }, [_vm.columns.left ? _c('div', {
    staticClass: "bb-product-card__column",
    class: {
      'bb-product-card__column--special': _vm.columns.left.specialContent
    }
  }, [_vm.columns.left.label ? _c('p', {
    staticClass: "bb-product-card__column-label"
  }, [!_vm.loading ? [_vm._v("\n              " + _vm._s(_vm.columns.left.label) + "\n            ")] : [_c('bb-skeleton', {
    staticClass: "m-t-5 m-b-15",
    attrs: {
      "width": "60",
      "height": "7",
      "color": "gray-30"
    }
  })]], 2) : _vm._e(), _vm._v(" "), _vm.columns.left.content ? _c('p', {
    staticClass: "bb-product-card__column-content",
    attrs: {
      "aria-label": _vm.contentAriaLabel
    }
  }, [_c('span', {
    staticClass: "bb-product-card__column-content-regular"
  }, [!_vm.loading ? _c('span', {
    attrs: {
      "aria-hidden": _vm.columns.left.specialContent ? 'true' : undefined
    }
  }, [_vm._v("\n                " + _vm._s(_vm.columns.left.content) + "\n              ")]) : [_c('bb-skeleton', {
    staticClass: "m-b-5",
    attrs: {
      "width": "100",
      "height": "10",
      "color": "gray-30"
    }
  })]], 2), _vm._v(" "), _vm.columns.left.specialContent && !_vm.loading ? _c('span', {
    staticClass: "bb-product-card__column-content-special",
    attrs: {
      "aria-hidden": _vm.columns.left.specialContent ? 'true' : undefined
    }
  }, [_vm._v("\n              " + _vm._s(_vm.columns.left.specialContent) + "\n            ")]) : _vm._e()]) : _vm._e(), _vm._v(" "), _vm.singleColumn ? _c('p', {
    staticClass: "bb-product-card__column-sublabel"
  }, [!_vm.loading ? [_vm._v("\n              " + _vm._s(_vm.columns.left.subLabel) + "\n            ")] : [_c('bb-skeleton', {
    staticClass: "m-b-5",
    attrs: {
      "width": "60",
      "height": "7",
      "color": "gray-30"
    }
  })]], 2) : _vm._e()]) : _vm._e(), _vm._v(" "), _vm.columns.right ? _c('bb-separator', {
    staticClass: "border-gray-30",
    attrs: {
      "direction": "vertical"
    }
  }) : _vm._e(), _vm._v(" "), _vm.columns.right ? _c('div', {
    staticClass: "bb-product-card__column"
  }, [_vm.columns.right.label ? _c('p', {
    staticClass: "bb-product-card__column-label"
  }, [!_vm.loading ? [_vm._v("\n              " + _vm._s(_vm.columns.right.label) + "\n            ")] : [_c('bb-skeleton', {
    staticClass: "m-t-5 m-b-15",
    attrs: {
      "width": "60",
      "height": "7",
      "color": "gray-30"
    }
  })]], 2) : _vm._e(), _vm._v(" "), _vm.columns.right.content ? _c('p', {
    staticClass: "bb-product-card__column-content"
  }, [!_vm.loading ? [_vm._v("\n              " + _vm._s(_vm.columns.right.content) + "\n            ")] : [_c('bb-skeleton', {
    staticClass: "m-b-5",
    attrs: {
      "width": "100",
      "height": "10",
      "color": "gray-30"
    }
  })]], 2) : _vm._e()]) : _vm._e()], 1) : _vm._e(), _vm._v(" "), _vm.bullets && !_vm.readMore ? _c('div', {
    staticClass: "bb-product-card__bullets"
  }, [!_vm.loading ? [_c('bb-list-group', _vm._l(_vm.computedBullets, function (bullet, i) {
    return _c('bb-list-item', {
      key: i,
      attrs: {
        "title": bullet,
        "title-uppercase": false,
        "bullet": ""
      }
    });
  }), 1)] : _vm._l(_vm.computedBullets, function (bullet, i) {
    return _c('bb-skeleton', {
      key: i,
      staticClass: "m-b-20",
      attrs: {
        "width": "256",
        "height": "16",
        "color": "gray-30"
      }
    });
  })], 2) : _vm._e(), _vm._v(" "), _vm.notEmpty(_vm.button) && _vm.button.label && !_vm.loading ? _c('bb-button', _vm._b({
    staticClass: "bb-product-card__button",
    attrs: {
      "color": _vm.readMore ? 'white' : 'mint'
    },
    on: {
      "click": _vm.onButtonClick
    }
  }, 'bb-button', _vm.computedButtonProps, false)) : _vm.loading ? _c('bb-skeleton', {
    staticClass: "bb-product-card__button",
    attrs: {
      "width": "70%",
      "height": "40",
      "color": "gray-30"
    }
  }) : _vm._e()], 1)], 1)], 1);
};
var __vue_staticRenderFns__$U = [];

/* style */
const __vue_inject_styles__$U = function (inject) {
  if (!inject) return;
  inject("data-v-56b3820e_0", {
    source: ".bb-product-card{max-width:100%;padding:.9375rem;border:.0625rem dashed transparent;text-align:center;transition:transform .2s cubic-bezier(.79,.14,.15,.86),border-color .2s}@media (min-width:768px){.bb-product-card{min-width:21.5rem;max-width:21.5rem}}.bb-product-card:not(.bb-product-card--close-button):hover{transform:scale(1.05)}.bb-product-card:focus{border-color:#bdc7d0;outline:0;transform:scale(1.05)}.bb-product-card .bb-product-card__overlay-link{position:absolute;z-index:1;top:0;left:0;display:block;width:100%;height:100%}.bb-product-card__container{position:relative;display:flex;overflow:hidden;height:100%;flex-direction:column;border-radius:.5rem .5rem 0 0}.bb-product-card--read-more .bb-product-card__container{min-height:23.75rem;border-radius:.5rem}.bb-product-card__content{display:flex;flex-direction:column;flex-grow:1;align-items:center}.bb-product-card--read-more .bb-product-card__content{position:absolute;top:0;left:0;width:100%;height:100%;justify-content:center;padding:.9375rem}.bb-product-card--loading .bb-product-card__content{display:flex;flex-direction:column}.bb-product-card__image{display:flex;height:7.1875rem;flex-direction:column;align-items:center;justify-content:space-between;margin-bottom:2.8125rem;background-color:#eeeff2;background-position:center;background-repeat:no-repeat;background-size:cover}.bb-product-card--read-more .bb-product-card__image{height:auto;margin:0}.bb-product-card__image-header{display:flex;width:100%;justify-content:space-between}.bb-product-card__special-tag{height:1.75rem;padding:.3125rem .9375rem;border-radius:0 .875rem 14px 0;color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem}.bb-product-card--campaign .bb-product-card__special-tag{background:#0ccca2}.bb-product-card--loyalty .bb-product-card__special-tag{background:#fc0}.bb-product-card__special-tag-icon{margin-right:.625rem}.bb-product-card__close-button{margin-left:auto;border-bottom-right-radius:0;border-top-left-radius:0}.bb-product-card__close-button.bb-product-card__close-button.bb-product-card__close-button.bb-product-card__close-button{background-color:rgba(56,65,77,.5)}.bb-product-card__close-button.bb-product-card__close-button.bb-product-card__close-button.bb-product-card__close-button:hover{background-color:rgba(56,65,77,.7)}.bb-product-card__image-overlay{position:absolute;top:0;left:0;display:flex;width:100%;height:100%;flex-direction:column;background:rgba(12,204,162,.95)}.bb-product-card__icon{flex:none;box-shadow:0 0 0 2px #fff;transform:translateY(50%);transition:border-color .2s,background .2s}.bb-product-card:hover .bb-product-card__icon{border-color:#0ccca2;background:#0ccca2}.bb-product-card--loading .bb-product-card__icon path{display:none}.bb-product-card__description,.bb-product-card__title{margin-bottom:1.25rem;line-height:1.25rem}.bb-product-card--read-more .bb-product-card__description,.bb-product-card--read-more .bb-product-card__title{color:#fff}.bb-product-card--loading .bb-product-card__description,.bb-product-card--loading .bb-product-card__title{display:flex;flex-direction:column;align-items:center}.bb-product-card__title{display:-webkit-box;overflow:hidden;flex-grow:1;-webkit-box-orient:vertical;color:#38414d;font-size:1.5rem;-webkit-line-clamp:2;line-clamp:2;line-height:1.75rem;text-overflow:ellipsis}.bb-product-card--read-more .bb-product-card__title{flex-grow:unset}.bb-product-card__description{display:-webkit-box;overflow:hidden;flex-grow:1;-webkit-box-orient:vertical;color:#57697a;font-size:.75rem;-webkit-line-clamp:3;line-clamp:3;line-height:1.25rem;text-overflow:ellipsis}.bb-product-card--read-more .bb-product-card__description{flex-grow:0}.bb-product-card__columns{display:flex;width:100%;justify-content:space-between;margin-bottom:1.25rem}.bb-product-card__column{flex:1 1 0;padding:0 1.5625rem}.bb-product-card--loading .bb-product-card__column{display:flex;flex-direction:column;align-items:center}.bb-product-card__column-label,.bb-product-card__column-sublabel{font-size:.875rem;line-height:1.125rem}.bb-product-card__column-label{color:#89a}.bb-product-card__column-sublabel{color:#38414d}.bb-product-card__column-content{color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem}.bb-product-card--single-column .bb-product-card__column-content{margin:.3125rem 0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:2rem;line-height:2.1875rem}.bb-product-card__column--special .bb-product-card__column-content-regular{text-decoration:line-through}.bb-product-card__column-content-special{padding-left:.5rem;color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-decoration:none}.bb-product-card__bullets{margin-bottom:.625rem}.bb-product-card__bullets .bb-list-item__content{padding-left:.3125rem}.bb-product-card__button{margin-bottom:.9375rem}.bb-product-card--read-more .bb-product-card__button{margin:0}.bb-product-card--loading .bb-product-card__button{align-self:center}.bb-product-card--card-link:hover .bb-product-card__button{background-color:#0bb48f}.bb-product-card--bullets .bb-product-card__description{margin-bottom:.5rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$U = undefined;
/* module identifier */
const __vue_module_identifier__$U = undefined;
/* functional template */
const __vue_is_functional_template__$U = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$U = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$U,
  staticRenderFns: __vue_staticRenderFns__$U
}, __vue_inject_styles__$U, __vue_script__$U, __vue_scope_id__$U, __vue_is_functional_template__$U, __vue_module_identifier__$U, false, createInjector, undefined, undefined);

var script$T = {
  name: 'bb-product-cards',
  components: {
    BbProductCard: __vue_component__$U,
    BbProgressDots: __vue_component__$25,
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C
  },
  directives: {
    TouchSwipe
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: String,
    cards: {
      type: Array,
      required: true
    },
    title: String,
    justified: {
      type: Boolean,
      validator: deprecated('justified', 'size="justified"', 'BbProductCards')
    },
    big: {
      type: Boolean,
      validator: deprecated('big', 'size="lg"', 'BbProductCards')
    },
    size: {
      type: String,
      default: 'sm',
      validator: size => ['sm', 'md', 'lg', 'justified'].includes(size)
    },
    display: {
      type: String,
      default: 'carousel',
      validator: display => ['carousel', 'grid'].includes(display)
    },
    progressDotsProps: Object,
    ariaLabels: {
      type: Object,
      default: () => ({
        buttonLeft: 'Scroll left',
        buttonRight: 'Scroll right'
      })
    }
  },
  emits: ['change'],
  data() {
    return {
      selectedCard: this.value,
      activePage: 0,
      cardWidth: 1,
      pageCount: 1,
      pageCenterOffsets: [],
      hasOverflow: {
        left: false,
        right: false
      },
      resizeObserver: null
    };
  },
  computed: {
    classes() {
      return [`bb-product-cards--size-${this.size}`, `bb-product-cards--display-${this.display}`, {
        'bb-product-cards--size-justified': this.justified,
        'bb-product-cards--size-lg': this.big,
        'bb-product-cards--overflow-left': this.hasOverflow.left,
        'bb-product-cards--overflow-right': this.hasOverflow.right,
        'bb-product-cards--scroll-disabled': !this.hasLargeCardsInGrid && this.pageCount <= 1
      }];
    },
    hasLargeCardsInGrid() {
      return this.hasLargeCards && this.isDisplayGrid;
    },
    isDisplayGrid() {
      return this.display === 'grid';
    },
    hasLargeCards() {
      return this.big ||
      this.size === 'lg';
    },
    hasButtons() {
      return !this.isDisplayGrid;
    },
    hasRoundButtons() {
      return this.hasLargeCards;
    },
    hasProgressDots() {
      return this.hasLargeCards && !this.isDisplayGrid && this.pageCount > 1;
    }
  },
  watch: {
    value() {
      this.selectedCard = this.value;
    }
  },
  created() {
    this.onScrollThrottled = throttle(this.onScroll, 100);
    this.onResizeThrottled = throttle(this.onResize, 100);
  },
  mounted() {
    this.onResize();
    this.addResizeListener();
  },
  destroyed() {
    this.removeResizeListener();
  },
  methods: {
    addResizeListener() {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(document.body);
    },
    removeResizeListener() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(document.body);
      }
    },
    getCardDisabled(card) {
      return !!(card !== null && card !== void 0 && card.disabled);
    },
    getCardIconColor(card) {
      return card !== null && card !== void 0 && card.iconColor && !this.getCardDisabled(card) ? card.iconColor : 'gray-50';
    },
    getScrollButtonWidth() {
      const scrollButtonWidth = this.hasButtons && this.$refs.buttonLeft.$el.clientWidth;
      return scrollButtonWidth || 0;
    },
    getPageSize() {
      const scrollArea = this.$refs.cards;
      const viewportWidth = scrollArea.clientWidth - 2 * this.getScrollButtonWidth();
      const cardsPerView = Math.floor(viewportWidth / this.cardWidth);
      return Math.max(1, cardsPerView);
    },
    getPageCenterScrollLeft(pageIndex) {
      const pageSize = this.getPageSize();
      const indexFirstCardInPage = pageIndex * pageSize;
      const indexLastCardInPage = Math.min(indexFirstCardInPage + pageSize - 1, this.cards.length - 1);
      const firstCardInPage = this.$refs.cards.children[indexFirstCardInPage];
      const lastCardInPage = this.$refs.cards.children[indexLastCardInPage];
      const pageOffset = firstCardInPage.offsetLeft;
      const pageRightBoundaryOffset = lastCardInPage.offsetLeft + lastCardInPage.offsetWidth;
      const pageWidth = pageRightBoundaryOffset - pageOffset;
      return pageOffset + pageWidth / 2;
    },
    updateCardWidth() {
      const card = this.$refs.cards.children[0];
      const cardStyles = card.currentStyle || window.getComputedStyle(card);
      const cardWidth = card.offsetWidth;
      const cardMarginX = parseFloat(cardStyles.marginLeft) + parseFloat(cardStyles.marginRight);
      this.cardWidth = cardWidth + cardMarginX;
    },
    updatePageCount() {
      this.pageCount = Math.ceil(this.cards.length / this.getPageSize());
    },
    updatePagesOffsets() {
      this.pageCenterOffsets = Array.from({
        length: this.pageCount
      }, (_, index) => this.getPageCenterScrollLeft(index));
    },
    updateActivePage() {
      var _this$pageCenterOffse;
      const scrollArea = this.$refs.cards;
      const viewportWidth = scrollArea.clientWidth;
      const scrollLeft = scrollArea.scrollLeft;
      const viewportCenterOffset = scrollLeft + viewportWidth / 2;
      const closestToViewportCenter = closest(this.pageCenterOffsets, viewportCenterOffset);
      this.activePage = (_this$pageCenterOffse = this.pageCenterOffsets.indexOf(closestToViewportCenter)) !== null && _this$pageCenterOffse !== void 0 ? _this$pageCenterOffse : 0;
    },
    updateHasOverflow() {
      const scrollArea = this.$refs.cards;
      const contentWidth = scrollArea.scrollWidth;
      const scrollLeft = scrollArea.scrollLeft;
      const scrollAreaWidth = scrollArea.offsetWidth;
      const scrollEnd = contentWidth - scrollAreaWidth;
      const overflowMargin = this.cardWidth / 10;
      this.hasOverflow.left = scrollLeft > overflowMargin;
      this.hasOverflow.right = scrollLeft < scrollEnd - overflowMargin;
    },
    scrollCardsTo(scrollLeft) {
      this.$refs.cards.scroll({
        left: scrollLeft,
        behavior: 'smooth'
      });
    },
    scrollToPage(pageIndex) {
      const viewportWidth = this.$refs.cards.offsetWidth;
      const viewportCenter = viewportWidth / 2;
      const pageCenterScrollLeft = this.pageCenterOffsets[pageIndex];
      this.scrollCardsTo(pageCenterScrollLeft - viewportCenter);
    },
    scrollToPageRelative(direction) {
      const pageIndex = clamp$1(this.activePage + direction, 0, this.pageCount - 1);
      this.scrollToPage(pageIndex);
    },
    scrollToCard(cardIndex) {
      const pageIndex = Math.floor(cardIndex / this.getPageSize());
      this.scrollToPage(pageIndex);
    },
    onSwipe(swipeEvent) {
      const directions = {
        left: 1,
        right: -1
      };
      const direction = directions[swipeEvent.direction];
      direction && this.scrollToPageRelative(direction);
    },
    onLeftClick() {
      this.scrollToPageRelative(-1);
    },
    onRightClick() {
      this.scrollToPageRelative(1);
    },
    onProgressDotsClick(index) {
      this.activePage = index;
      this.scrollToPage(this.activePage);
    },
    onCardClick(value, index) {
      this.$emit('change', value);
      this.selectedCard = value;
      this.scrollToCard(index);
    },
    onResize() {
      this.updateCardWidth();
      this.updatePageCount();
      this.updatePagesOffsets();
      this.onScroll();
    },
    onScroll() {
      this.updateActivePage();
      this.updateHasOverflow();
    }
  }
};

const __vue_script__$T = script$T;

/* template */
var __vue_render__$T = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-product-cards",
    class: _vm.classes
  }, [_vm.title ? _c('div', {
    staticClass: "bb-product-cards__title"
  }, [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]) : _vm._e(), _vm._v(" "), _c('div', {
    class: {
      'bb-product-cards__scroll-area': !_vm.hasLargeCardsInGrid,
      'bb-product-cards__grid-area': _vm.hasLargeCardsInGrid
    }
  }, [_c('div', {
    directives: [{
      name: "touch-swipe",
      rawName: "v-touch-swipe.mouse.left.right",
      value: _vm.onSwipe,
      expression: "onSwipe",
      modifiers: {
        "mouse": true,
        "left": true,
        "right": true
      }
    }],
    ref: "cards",
    class: {
      'bb-product-cards__scroll-wrapper': !_vm.hasLargeCardsInGrid,
      'bb-product-cards__grid-wrapper': _vm.hasLargeCardsInGrid
    },
    attrs: {
      "role": "group",
      "aria-orientation": _vm.hasLargeCardsInGrid ? 'vertical' : 'horizontal'
    },
    on: {
      "scroll": _vm.onScrollThrottled
    }
  }, [_vm.hasLargeCards ? _vm._l(_vm.cards, function (card, index) {
    return _c('bb-product-card', _vm._b({
      key: index,
      staticClass: "bb-product-cards__card-large",
      attrs: {
        "title": card.label,
        "image-path": card.image
      },
      on: {
        "click": function ($event) {
          return _vm.onCardClick(card.id, index);
        }
      }
    }, 'bb-product-card', card, false));
  }) : _vm._l(_vm.cards, function (card, index) {
    return _c('label', {
      key: index,
      staticClass: "bb-product-cards__card",
      class: {
        'bb-product-cards__card--active': card.id === _vm.selectedCard,
        'bb-product-cards__card--disabled': _vm.getCardDisabled(card)
      },
      attrs: {
        "aria-current": card.id === _vm.selectedCard ? 'true' : undefined
      }
    }, [_c('input', {
      staticClass: "visually-hidden",
      attrs: {
        "disabled": _vm.getCardDisabled(card),
        "title": card.label,
        "type": "radio"
      },
      domProps: {
        "value": index,
        "checked": card.id === _vm.selectedCard
      },
      on: {
        "click": function ($event) {
          return _vm.onCardClick(card.id, index);
        }
      }
    }), _vm._v(" "), _c('bb-icon', {
      staticClass: "bb-product-cards__card-icon",
      class: {
        'bb-product-cards__card-icon--colored': Object.hasOwn(card, 'iconColor')
      },
      attrs: {
        "name": card.icon,
        "fill": _vm.getCardIconColor(card),
        "size": "24"
      }
    }), _vm._v(" "), Object.hasOwn(card, 'labelSecondary') ? _c('div', {
      staticClass: "bb-product-cards__card-label-secondary"
    }, [_vm._v("\n            " + _vm._s(card.labelSecondary) + "\n          ")]) : _vm._e(), _vm._v(" "), Object.hasOwn(card, 'label') ? _c('div', {
      staticClass: "bb-product-cards__card-label"
    }, [_vm._v("\n            " + _vm._s(card.label) + "\n          ")]) : _vm._e()], 1);
  })], 2), _vm._v(" "), _vm.hasProgressDots ? _c('bb-progress-dots', _vm._b({
    staticClass: "bb-product-cards__scrolling-indicator",
    attrs: {
      "is-clickable": "",
      "count": _vm.pageCount,
      "active-index": _vm.activePage
    },
    on: {
      "activeIndex": _vm.onProgressDotsClick
    }
  }, 'bb-progress-dots', _vm.progressDotsProps, false)) : _vm._e(), _vm._v(" "), _vm.hasButtons ? [_vm.hasRoundButtons ? [_c('div', {
    staticClass: "bb-product-cards__round-button-panel-left"
  }, [_c('bb-button', {
    ref: "buttonLeft",
    attrs: {
      "icon": "ui-chevron-left",
      "color": "gray",
      "display": "circle",
      "inverted": "",
      "data-testid": "bb-product-cards__round-button-left",
      "aria-label": _vm.ariaLabels.buttonLeft
    },
    on: {
      "click": _vm.onLeftClick
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-product-cards__round-button-panel-right"
  }, [_c('bb-button', {
    attrs: {
      "icon": "ui-chevron-right",
      "color": "gray",
      "display": "circle",
      "inverted": "",
      "data-testid": "bb-product-cards__round-button-right",
      "aria-label": _vm.ariaLabels.buttonRight
    },
    on: {
      "click": _vm.onRightClick
    }
  })], 1)] : [_c('div', {
    staticClass: "bb-product-cards__button-panel-left"
  }, [_c('bb-button', {
    ref: "buttonLeft",
    staticClass: "bb-product-cards__button-left",
    attrs: {
      "no-background": "",
      "icon": "ui-menu-down",
      "icon-rotate": "right",
      "color": "gray",
      "display": "square",
      "aria-label": _vm.ariaLabels.buttonLeft
    },
    on: {
      "click": _vm.onLeftClick
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-product-cards__button-panel-right"
  }, [_c('bb-button', {
    staticClass: "bb-product-cards__button-right",
    attrs: {
      "no-background": "",
      "icon": "ui-menu-down",
      "icon-rotate": "left",
      "color": "gray",
      "display": "square",
      "aria-label": _vm.ariaLabels.buttonRight
    },
    on: {
      "click": _vm.onRightClick
    }
  })], 1)]] : _vm._e()], 2), _vm._v(" "), !_vm.hasLargeCards ? _vm._l(_vm.cards, function (card) {
    return _c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: card.id === _vm.selectedCard,
        expression: "card.id === selectedCard"
      }],
      key: card.id,
      staticClass: "bb-product-cards__content"
    }, [_vm._t(card.id)], 2);
  }) : _vm._e()], 2);
};
var __vue_staticRenderFns__$T = [];

/* style */
const __vue_inject_styles__$T = function (inject) {
  if (!inject) return;
  inject("data-v-25b39661_0", {
    source: ".bb-product-cards{display:flex;flex-direction:column;margin-right:-.625rem;margin-left:-.625rem}.bb-product-cards__title{padding:0 .625rem;color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}.bb-product-cards__scroll-area{position:relative}.bb-product-cards__scroll-wrapper{position:relative;display:flex;overflow:auto;width:100%;padding:1rem .625rem;-webkit-overflow-scrolling:touch}.bb-product-cards--size-lg .bb-product-cards__scroll-wrapper{padding:2rem 1.25rem;cursor:grab}.bb-product-cards--size-lg .bb-product-cards__scroll-wrapper:active{cursor:grabbing}.bb-product-cards__scroll-wrapper::after{display:block;width:100%;min-width:.625rem;max-width:.625rem;height:7.75rem;content:\"\"}.bb-product-cards__scroll-wrapper::-webkit-scrollbar{display:none}.bb-product-cards--scroll-disabled .bb-product-cards__scroll-wrapper{cursor:auto}.bb-product-cards--display-carousel .bb-product-cards__card-large{flex-shrink:0;user-select:none}.bb-product-cards:not(.bb-product-cards--display-grid) .bb-product-cards__card-large:not(:last-of-type){margin-right:1.875rem}.bb-product-cards:not(.bb-product-cards--display-carousel) .bb-product-cards__card-large:not(:last-of-type){margin-bottom:1.25rem}@media (min-width:576px){.bb-product-cards:not(.bb-product-cards--display-carousel) .bb-product-cards__card-large:not(:last-of-type){margin-bottom:0}}.bb-product-cards__card-large:hover{cursor:pointer}.bb-product-cards__grid-wrapper{display:flex;flex-direction:column}@media (min-width:576px){.bb-product-cards__grid-wrapper{display:grid;justify-content:center;grid-gap:1.25rem;grid-template-columns:repeat(auto-fit,22.5rem)}}.bb-product-cards__card{display:flex;width:100%;min-width:6.4375rem;max-width:6.4375rem;height:7.75rem;flex-direction:column;align-items:normal;justify-content:space-between;padding:.9375rem .9375rem 1.25rem;border:solid .0625rem #dee3e8;border-radius:.3125rem;background:#fff;box-shadow:0 .1875rem .3125rem 0 rgba(0,0,0,.05);cursor:pointer;outline:0;text-align:left;transition:box-shadow .3s,border .3s;user-select:none}.bb-product-cards__card:not(:last-of-type){margin-right:.625rem}.bb-product-cards--size-md .bb-product-cards__card{min-width:11rem;max-width:11rem;height:7.0625rem}.bb-product-cards--size-justified .bb-product-cards__card{max-width:unset;flex:1 1 0%}.bb-product-cards__card-icon{opacity:1;transition:opacity .3s}.bb-product-cards__card-icon path{transition:fill .3s}.bb-product-cards__card-label,.bb-product-cards__card-label-secondary{overflow:hidden;max-height:3rem;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;opacity:1;text-overflow:ellipsis;transform:translateY(0);transition:opacity .3s,transform .3s}.bb-product-cards__card-label{color:#00285a;font-size:.75rem;line-height:.75rem}.bb-product-cards__card-label-secondary{margin-bottom:-.875rem;color:#687d92;font-size:.6875rem}.bb-product-cards__card--active{box-shadow:0 .1875rem .3125rem 0 rgba(0,0,0,.05),inset 0 -.25rem 0 0 #0ccca2}.bb-product-cards__card--active .bb-product-cards__card-icon:not(.bb-product-cards__card-icon--colored) path{fill:#2485e5}.bb-product-cards__card--disabled{border-color:#f7f7f8;cursor:not-allowed}.bb-product-cards__card--disabled .bb-product-cards__card-icon,.bb-product-cards__card--disabled .bb-product-cards__card-label,.bb-product-cards__card--disabled .bb-product-cards__card-label-secondary{opacity:.4}.bb-product-cards__card:hover{box-shadow:0 .25rem .5rem 0 rgba(0,0,0,.1)}.bb-product-cards__card:hover:not(.bb-product-cards__card--disabled) .bb-product-cards__card-label,.bb-product-cards__card:hover:not(.bb-product-cards__card--disabled) .bb-product-cards__card-label-secondary{transform:translateY(-2px)}.bb-product-cards__card:hover.bb-product-cards__card--active{box-shadow:0 .25rem .5rem 0 rgba(0,0,0,.1),inset 0 -.25rem 0 0 #0ccca2}.bb-product-cards__card:hover.bb-product-cards__card--disabled{box-shadow:0 .1875rem .3125rem 0 rgba(0,0,0,.05)}.bb-product-cards__card:focus{outline:0}.bb-product-cards__round-button-panel-left,.bb-product-cards__round-button-panel-right{position:absolute;top:0;display:none;height:100%;align-items:center;background:#fff;opacity:0;pointer-events:none;transition:opacity .2s}@media (min-width:768px){.bb-product-cards__round-button-panel-left,.bb-product-cards__round-button-panel-right{display:flex}}.bb-product-cards__round-button-panel-left{left:-3.75rem;padding-right:1.25rem;box-shadow:1.25rem 0 1.25rem 0 rgba(255,255,255,.8)}.bb-product-cards--overflow-left .bb-product-cards__round-button-panel-left{opacity:1;pointer-events:unset}.bb-product-cards__round-button-panel-right{right:-3.75rem;padding-left:1.25rem;box-shadow:-1.25rem 0 1.25rem 0 rgba(255,255,255,.8)}.bb-product-cards--overflow-right .bb-product-cards__round-button-panel-right{opacity:1;pointer-events:unset}.bb-product-cards__button-panel-left,.bb-product-cards__button-panel-right{position:absolute;top:0;display:flex;height:100%;align-items:center;background:#fff;opacity:0;pointer-events:none;transition:opacity .2s}.bb-product-cards__button-panel-left{left:0;box-shadow:.5rem 0 .5rem -.5rem rgba(0,0,0,.05)}.bb-product-cards--overflow-left .bb-product-cards__button-panel-left{opacity:1;pointer-events:unset}.bb-product-cards__button-panel-right{right:0;box-shadow:-.5rem 0 .5rem -.5rem rgba(0,0,0,.05)}.bb-product-cards--overflow-right .bb-product-cards__button-panel-right{opacity:1;pointer-events:unset}.bb-product-cards__button-left,.bb-product-cards__button-right{height:100%}.bb-product-cards__content{padding:1.5rem .625rem .625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$T = undefined;
/* module identifier */
const __vue_module_identifier__$T = undefined;
/* functional template */
const __vue_is_functional_template__$T = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$T = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$T,
  staticRenderFns: __vue_staticRenderFns__$T
}, __vue_inject_styles__$T, __vue_script__$T, __vue_scope_id__$T, __vue_is_functional_template__$T, __vue_module_identifier__$T, false, createInjector, undefined, undefined);

const easeOutQuart = t => 1 - --t * t * t * t;

var script$S = {
  name: 'bb-progress-indicator',
  props: {
    value: {
      type: Number,
      required: true
    },
    max: {
      type: Number,
      required: true
    },
    flavor: {
      type: String,
      default: 'user',
      validator: val => ['user', 'client', 'timer'].includes(val)
    },
    unit: {
      type: String,
      default: '€',
      validator: val => ['€', 'SEK'].includes(val)
    },
    thumb: {
      type: Boolean,
      default: true
    },
    thumbMarked: {
      type: Boolean,
      default: false
    },
    trackColor: {
      type: String,
      default: 'gray-40'
    },
    color: {
      type: String,
      default: 'mint'
    },
    timerRunning: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input'],
  data() {
    return {
      thumbDiameter: 15,
      rendered: false,
      tweeningValue: 0,
      counterDelay: 1500,
      sizeUser: 40,
      sizeClient: 171,
      initialTweenFinished: false,
      timeLeft: this.getClampedValue(),
      timerInterval: null,
      defaultAriaLabel: 'Progress indicator'
    };
  },
  i18n: {
    messages: {
      en: {
        components: {
          progressIndicator: {
            loan: {
              title: 'your loan',
              limit: 'maximum limit'
            }
          }
        }
      }
    }
  },
  computed: {
    isFlavorUser() {
      return this.flavor === 'user';
    },
    isFlavorClient() {
      return this.flavor === 'client';
    },
    isFlavorTimer() {
      return this.flavor === 'timer';
    },
    size() {
      return this.isFlavorUser ? this.sizeUser : this.sizeClient;
    },
    internalValue() {
      return this.value > this.internalMax ? this.internalMax : Math.round(this.value);
    },
    internalMax() {
      return this.isFlavorUser && this.max > 9 ? 9 : Math.round(this.max);
    },
    wrapperClasses() {
      return [`bb-progress-indicator--${this.flavor}`];
    },
    backgroundStrokeWidth() {
      return this.isFlavorUser ? 2 : 3;
    },
    foregroundStrokeWidth() {
      return this.isFlavorUser ? this.backgroundStrokeWidth : 5.5;
    },
    radius() {
      return this.isFlavorUser ? this.size / 2 : (this.size - this.thumbDiameter + this.foregroundStrokeWidth) / 2;
    },
    backgroundCircleRadius() {
      return this.radius - this.backgroundStrokeWidth / 2 - (this.foregroundStrokeWidth - this.backgroundStrokeWidth) / 2;
    },
    backgroundCircleCircumference() {
      return 2 * Math.PI * this.backgroundCircleRadius;
    },
    foregroundCircleRadius() {
      return this.radius - this.foregroundStrokeWidth / 2;
    },
    foregroundCircleCircumference() {
      return 2 * Math.PI * this.foregroundCircleRadius;
    },
    doneRatio() {
      const progress = this.internalValue / this.internalMax;
      return clamp(progress, 0, 1);
    },
    backgroundCircleOffset() {
      return this.backgroundCircleCircumference * 0;
    },
    foregroundCircleOffset() {
      const offsetDirection = this.isFlavorTimer ? -1 : 1;
      return this.foregroundCircleCircumference * (1 - this.doneRatio * offsetDirection);
    },
    formattedTimeLeft() {
      const displayMinutes = Math.floor(this.timeLeft / 60);
      const displaySeconds = this.timeLeft % 60;
      const paddedMinutes = padWithLeadingZero(displayMinutes);
      const paddedSeconds = padWithLeadingZero(displaySeconds);
      return `${paddedMinutes}:${paddedSeconds}`;
    }
  },
  watch: {
    value(newVal, oldVal) {
      if (this.isFlavorTimer) {
        this.timeLeft = this.getClampedValue();
      }
      this.tween(oldVal, newVal, 1400);
    },
    timerRunning() {
      if (this.timerRunning) {
        this.startTimer();
      } else {
        this.stopTimer();
      }
    }
  },
  mounted() {
    if (this.isFlavorTimer && this.timerRunning) {
      this.startTimer();
    }
    this.rendered = true;
    !this.isFlavorTimer && setTimeout(() => {
      const tweenEnd = this.internalValue;
      const tweenStart = Math.round(this.internalValue * 0.3);
      const lengthOfAnimation = 900;
      this.tween(tweenStart, tweenEnd, lengthOfAnimation);
      this.initialTweenFinished = true;
    }, this.counterDelay);
  },
  methods: {
    getClampedValue() {
      return clamp(this.value, 0, this.max);
    },
    tween(startValue, endValue, lengthOfAnimation) {
      const self = this;
      const fps = 24;
      const fpsInterval = 1000 / fps;
      const startTime = Date.now();
      let request, thenFrame, frameElapsed;
      this.tweeningValue = startValue;
      thenFrame = startTime;
      function animateCounter() {
        const now = Date.now();
        const animationElapsed = (now - startTime) / lengthOfAnimation;
        if (animationElapsed < 1) {
          request = window.requestAnimationFrame(animateCounter);
          frameElapsed = now - thenFrame;
          if (frameElapsed > fpsInterval) {
            thenFrame = now - frameElapsed % fpsInterval;
            self.tweeningValue = startValue + Math.floor(easeOutQuart(animationElapsed) * (endValue - startValue));
          }
        } else {
          request && window.cancelAnimationFrame(request);
          self.tweeningValue = endValue;
        }
      }
      animateCounter();
    },
    startTimer() {
      if (!this.timerInterval && this.timeLeft > 0) {
        this.timerInterval = setInterval(() => {
          this.timeLeft -= 1;
          this.emitInput(this.timeLeft);
          if (this.timeLeft === 0) {
            this.stopTimer();
          }
        }, 1000);
      }
    },
    stopTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    },
    emitInput(val) {
      this.$emit('input', val);
    }
  }
};

/* script */
const __vue_script__$S = script$S;

/* template */
var __vue_render__$S = function () {
  var _obj, _obj$1, _obj$2;
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-progress-indicator",
    class: _vm.wrapperClasses,
    attrs: {
      "aria-label": _vm.defaultAriaLabel,
      "role": _vm.isFlavorTimer ? 'timer' : 'group',
      "aria-live": !_vm.isFlavorTimer ? 'polite' : undefined
    }
  }, [_c('svg', {
    attrs: {
      "viewBox": "0 0 " + _vm.size + " " + _vm.size,
      "aria-hidden": _vm.isFlavorUser && !_vm.$slots.default ? undefined : 'true'
    }
  }, [_c('g', {
    attrs: {
      "transform": "rotate(-90 " + _vm.size / 2 + " " + _vm.size / 2 + ")"
    }
  }, [_c('circle', {
    staticClass: "bb-progress-indicator__background-circle",
    class: (_obj = {}, _obj["color-" + _vm.trackColor] = true, _obj),
    attrs: {
      "cx": _vm.size / 2,
      "cy": _vm.size / 2,
      "r": _vm.backgroundCircleRadius,
      "stroke-width": _vm.backgroundStrokeWidth,
      "stroke-dasharray": _vm.backgroundCircleCircumference,
      "stroke-dashoffset": _vm.rendered ? _vm.backgroundCircleOffset : _vm.backgroundCircleCircumference,
      "fill": "none"
    }
  }), _vm._v(" "), _c('circle', {
    staticClass: "bb-progress-indicator__foreground-circle",
    class: (_obj$1 = {
      'bb-progress-indicator__foreground-circle--short-duration': _vm.initialTweenFinished
    }, _obj$1["color-" + _vm.color] = true, _obj$1),
    attrs: {
      "cx": _vm.size / 2,
      "cy": _vm.size / 2,
      "r": _vm.foregroundCircleRadius,
      "stroke-width": _vm.foregroundStrokeWidth,
      "stroke-dasharray": _vm.foregroundCircleCircumference,
      "stroke-dashoffset": _vm.rendered ? _vm.foregroundCircleOffset : _vm.foregroundCircleCircumference,
      "fill": "none",
      "stroke-linecap": "round"
    }
  }), _vm._v(" "), _vm.isFlavorClient && _vm.thumb ? _c('circle', {
    staticClass: "bb-progress-indicator__thumb",
    class: (_obj$2 = {
      'is-rendering': !_vm.rendered
    }, _obj$2["color-" + _vm.color] = true, _obj$2),
    attrs: {
      "cx": _vm.size / 2,
      "cy": _vm.size / 2,
      "r": _vm.foregroundCircleRadius,
      "stroke-width": _vm.thumbDiameter,
      "stroke-dasharray": "0 " + _vm.backgroundCircleCircumference,
      "stroke-dashoffset": _vm.foregroundCircleOffset,
      "fill": "none",
      "stroke-linecap": "round"
    }
  }) : _vm._e(), _vm._v(" "), _vm.isFlavorClient && _vm.thumb && _vm.thumbMarked ? _c('circle', {
    class: {
      'is-rendering': !_vm.rendered
    },
    attrs: {
      "cx": _vm.size / 2,
      "cy": _vm.size / 2,
      "r": _vm.foregroundCircleRadius,
      "stroke-width": _vm.thumbDiameter - 4,
      "stroke-dasharray": "0 " + _vm.backgroundCircleCircumference,
      "stroke-dashoffset": _vm.foregroundCircleOffset,
      "fill": "none",
      "stroke": "black",
      "stroke-linecap": "round"
    }
  }) : _vm._e()]), _vm._v(" "), _vm.isFlavorUser && !_vm.$slots.default ? _c('text', {
    staticClass: "bb-progress-indicator__text-user",
    attrs: {
      "text-anchor": "middle",
      "font-size": "16",
      "x": "21",
      "y": "25"
    }
  }, [_vm._v("\n      " + _vm._s(_vm.internalValue) + "/" + _vm._s(_vm.internalMax) + "\n    ")]) : _vm._e()]), _vm._v(" "), _c('div', {
    staticClass: "bb-progress-indicator__content"
  }, [_vm._t("default", function () {
    var _obj;
    return [_vm.isFlavorTimer ? [_vm._v("\n        " + _vm._s(_vm.formattedTimeLeft) + "\n      ")] : _vm._e(), _vm._v(" "), _vm.isFlavorClient ? _c('div', {
      staticClass: "bb-progress-indicator__content-client"
    }, [_c('div', {
      staticClass: "bb-progress-indicator__your-loan"
    }, [_c('p', {
      staticClass: "bb-progress-indicator__loan-title"
    }, [_vm._t("loan-title", function () {
      return [_vm._v("\n              " + _vm._s(_vm.$t && _vm.$t('components.progressIndicator.loan.title')) + "\n            ")];
    })], 2), _vm._v(" "), _c('p', {
      staticClass: "bb-progress-indicator__loan-amount",
      class: (_obj = {}, _obj["color-" + _vm.color] = true, _obj)
    }, [_vm._v("\n            " + _vm._s(_vm.tweeningValue)), _c('span', {
      staticClass: "bb-progress-indicator__amount-unit"
    }, [_vm._v("\n              " + _vm._s(_vm.unit) + "\n            ")])])]), _vm._v(" "), _c('div', {
      staticClass: "bb-progress-indicator__max-loan"
    }, [_c('p', {
      staticClass: "bb-progress-indicator__loan-limit"
    }, [_vm._t("loan-limit", function () {
      return [_vm._v("\n              " + _vm._s(_vm.$t && _vm.$t('components.progressIndicator.loan.limit')) + "\n            ")];
    })], 2), _vm._v(" "), _c('p', {
      staticClass: "bb-progress-indicator__limit-unit"
    }, [_vm._v("\n            " + _vm._s(_vm.internalMax) + " " + _vm._s(_vm.unit) + "\n          ")])])]) : _vm._e()];
  })], 2)]);
};
var __vue_staticRenderFns__$S = [];

/* style */
const __vue_inject_styles__$S = function (inject) {
  if (!inject) return;
  inject("data-v-7039e594_0", {
    source: ".bb-progress-indicator{display:block;width:100%;height:100%;line-height:0}.bb-progress-indicator__foreground-circle{stroke:currentColor;transition:stroke-dashoffset 1.6s cubic-bezier(.345,.045,.355,1);transition-delay:.7s}.bb-progress-indicator__foreground-circle--short-duration{transition-duration:1.4s}.bb-progress-indicator--timer .bb-progress-indicator__foreground-circle{transition-delay:0s;transition-duration:1s;transition-timing-function:linear}.bb-progress-indicator__background-circle{stroke:currentColor;transition:stroke-dashoffset 2s cubic-bezier(.645,.045,.355,1)}.bb-progress-indicator__thumb{stroke:currentColor;transition:opacity .5s cubic-bezier(.645,.045,.355,1),stroke-width .5s;transition-delay:1.9s}.bb-progress-indicator__thumb.is-rendering{opacity:0;stroke-width:1px}.bb-progress-indicator--timer .bb-progress-indicator__thumb{transition-delay:0s;transition-duration:1s;transition-timing-function:linear}.bb-progress-indicator__content{display:flex;height:100%;align-items:center;justify-content:center;line-height:1}.bb-progress-indicator--user .bb-progress-indicator__content{margin-top:-40px}.bb-progress-indicator--client .bb-progress-indicator__content{margin-top:-171px}.bb-progress-indicator--timer .bb-progress-indicator__content{margin-top:-86px}.bb-progress-indicator__content-client{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-align:center}.bb-progress-indicator__loan-title{color:#57697a;font-size:.5625rem;text-transform:uppercase}.bb-progress-indicator__loan-amount{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:2rem;letter-spacing:-.125rem;line-height:1.4}.bb-progress-indicator__amount-unit{margin-left:.3125rem;font-size:1.5rem}.bb-progress-indicator__loan-limit{color:#57697a;font-size:.5625rem;text-transform:uppercase}.bb-progress-indicator__limit-unit{margin-bottom:.3125rem;color:#9dabb9;font-family:GothamSSm-Black,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;letter-spacing:-.0625rem;line-height:1.4}.bb-progress-indicator__text-user{fill:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;line-height:1}.bb-progress-indicator--user{width:2.5rem;height:2.5rem}.bb-progress-indicator--client{width:10.6875rem;height:10.6875rem}.bb-progress-indicator--timer{width:5.375rem;height:5.375rem}.bb-progress-indicator__caption{font-size:.5625rem}.bb-progress-indicator__your-loan{animation:fade-in .9s ease-in-out;animation-delay:1s;animation-fill-mode:backwards}.bb-progress-indicator__max-loan{animation:slide-down .9s ease-out;animation-delay:.6s;animation-fill-mode:backwards;line-height:1.5}@keyframes slide-down{from{transform:translate(0,-75%)}to{transform:translate(0,0)}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$S = undefined;
/* module identifier */
const __vue_module_identifier__$S = undefined;
/* functional template */
const __vue_is_functional_template__$S = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$S = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$S,
  staticRenderFns: __vue_staticRenderFns__$S
}, __vue_inject_styles__$S, __vue_script__$S, __vue_scope_id__$S, __vue_is_functional_template__$S, __vue_module_identifier__$S, false, createInjector, undefined, undefined);

var script$R = {
  name: 'bb-public-header',
  components: {
    BbIcon: __vue_component__$2C,
    BbSeparator: __vue_component__$2a
  },
  props: {
    icon: String,
    title: String,
    subtitle: String,
    separator: {
      type: Boolean,
      default: true
    },
    baseHeadingLevel: {
      type: Number,
      default: 4,
      validator: val => val > 0 && val < 7
    }
  }
};

/* script */
const __vue_script__$R = script$R;

/* template */
var __vue_render__$R = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-public-header"
  }, [_vm.icon ? _c('bb-icon', {
    staticClass: "bb-public-header__icon",
    attrs: {
      "name": _vm.icon,
      "fill": "original",
      "size": "64"
    }
  }) : _vm._e(), _vm._v(" "), _vm.title ? _c("h" + _vm.baseHeadingLevel, {
    tag: "component",
    staticClass: "bb-public-header__title"
  }, [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]) : _vm._e(), _vm._v(" "), _vm.subtitle ? _c('div', {
    staticClass: "bb-public-header__subtitle-wrapper"
  }, [_c('p', {
    staticClass: "bb-public-header__subtitle"
  }, [_vm._v("\n      " + _vm._s(_vm.subtitle) + "\n    ")]), _vm._v(" "), _vm.separator ? _c('bb-separator', {
    staticClass: "bb-public-header__separator",
    attrs: {
      "dashed": ""
    }
  }) : _vm._e()], 1) : _vm._e()], 1);
};
var __vue_staticRenderFns__$R = [];

/* style */
const __vue_inject_styles__$R = function (inject) {
  if (!inject) return;
  inject("data-v-fe3d72be_0", {
    source: ".bb-public-header{display:flex;flex-direction:column;align-items:center;padding-top:1.875rem;text-align:center}@media (min-width:576px){.bb-public-header{padding-top:2.5rem}}.bb-public-header__icon{margin-bottom:.9375rem}.bb-public-header__title{margin-top:0;margin-bottom:1.875rem;color:#00285a;font-size:1.125rem;line-height:1.5rem}@media (min-width:576px){.bb-public-header__title{margin-bottom:2.5rem}}.bb-public-header__subtitle-wrapper{width:100%;max-width:23.4375rem;text-align:center}.bb-public-header__subtitle{margin-bottom:1.875rem;color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}@media (min-width:576px){.bb-public-header__subtitle{margin-bottom:2.5rem}}.bb-public-header__separator{border-color:#9dabb9;margin-bottom:1.875rem}@media (min-width:576px){.bb-public-header__separator{margin-bottom:2.5rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$R = undefined;
/* module identifier */
const __vue_module_identifier__$R = undefined;
/* functional template */
const __vue_is_functional_template__$R = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$R = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$R,
  staticRenderFns: __vue_staticRenderFns__$R
}, __vue_inject_styles__$R, __vue_script__$R, __vue_scope_id__$R, __vue_is_functional_template__$R, __vue_module_identifier__$R, false, createInjector, undefined, undefined);

var script$Q = {
  name: 'bb-select-range',
  components: {
    BbSelect: __vue_component__$20,
    BbFieldPrefix: __vue_component__$2n
  },
  mixins: [FieldExtensions, Field, FieldRange, Options, veeValidationMixin],
  computed: {
    classes() {
      return {
        'field-range--split': this.splitFieldsView,
        'field-range--focused': this.focus
      };
    }
  }
};

/* script */
const __vue_script__$Q = script$Q;

/* template */
var __vue_render__$Q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    directives: [{
      name: "resize",
      rawName: "v-resize:throttle",
      value: _vm.calculateMiddle,
      expression: "calculateMiddle",
      arg: "throttle"
    }],
    staticClass: "bb-select-range field-group field-range framed-input is-filled",
    class: _vm.classes,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-value-path": "val"
    }
  }, [_c('bb-field-prefix', {
    ref: "fieldPrefix",
    staticClass: "field-range__prefix",
    style: {
      transform: "translateX(" + (_vm.splitFieldsView ? _vm.splitDistance : 0) + "px)"
    },
    attrs: {
      "id": _vm.id + "-prefix",
      "options": _vm.prefixOptions,
      "name": _vm.name + "-prefix",
      "small": ""
    },
    on: {
      "focus": function ($event) {
        _vm.focus = true;
      },
      "blur": function ($event) {
        _vm.focus = false;
      },
      "change": _vm.onChange
    },
    model: {
      value: _vm.val[_vm.prefixModel],
      callback: function ($$v) {
        _vm.$set(_vm.val, _vm.prefixModel, $$v);
      },
      expression: "val[prefixModel]"
    }
  }), _vm._v(" "), _c('bb-select', {
    staticClass: "field-range__primary-field",
    attrs: {
      "id": _vm.id,
      "name": _vm.name,
      "label": _vm.label,
      "options": _vm.options,
      "placeholder": _vm.placeholder,
      "small": "",
      "framed": ""
    },
    on: {
      "focus": function ($event) {
        _vm.focus = true;
      },
      "blur": function ($event) {
        _vm.focus = false;
      },
      "change": _vm.onChange
    },
    model: {
      value: _vm.val[_vm.primaryModel],
      callback: function ($$v) {
        _vm.$set(_vm.val, _vm.primaryModel, $$v);
      },
      expression: "val[primaryModel]"
    }
  }), _vm._v(" "), _vm.splitFieldsView ? _c('bb-select', {
    staticClass: "field-range__secondary-field",
    attrs: {
      "id": _vm.id + "-2",
      "name": _vm.name + "-2",
      "options": _vm.options,
      "placeholder": _vm.placeholder,
      "small": "",
      "framed": ""
    },
    on: {
      "focus": function ($event) {
        _vm.focus = true;
      },
      "blur": function ($event) {
        _vm.focus = false;
      },
      "change": _vm.onChange
    },
    model: {
      value: _vm.val[_vm.secondaryModel],
      callback: function ($$v) {
        _vm.$set(_vm.val, _vm.secondaryModel, $$v);
      },
      expression: "val[secondaryModel]"
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$Q = [];

/* style */
const __vue_inject_styles__$Q = undefined;
/* scoped */
const __vue_scope_id__$Q = undefined;
/* module identifier */
const __vue_module_identifier__$Q = undefined;
/* functional template */
const __vue_is_functional_template__$Q = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$Q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$Q,
  staticRenderFns: __vue_staticRenderFns__$Q
}, __vue_inject_styles__$Q, __vue_script__$Q, __vue_scope_id__$Q, __vue_is_functional_template__$Q, __vue_module_identifier__$Q, false, undefined, undefined, undefined);

var script$P = {
  name: 'bb-sidenav',
  components: {
    BbLogo: __vue_component__$28,
    BbLink: __vue_component__$2p,
    BbIcon: __vue_component__$2C
  },
  props: {
    routes: {
      type: Array,
      validator: a => a.reduce((acc, route) => acc ? route.path && route.icon && route.title || route.iconSize || route.active : false, true)
    },
    logo: {
      type: String
    },
    logoRoute: {
      type: String,
      default: '/'
    }
  },
  data() {
    return {
      activeRoute: this.routes && this.routes.find(item => item.active) ? this.routes.find(item => item.active).path : this.$router ? this.$router.path : ''
    };
  },
  computed: {
    hasRouter() {
      return !!this.$router;
    }
  },
  methods: {
    handleClick(linkPath) {
      this.$router ? this.$route.fullPath === linkPath ? window.location = window.location.href : this.$router.push(linkPath) : '';
      this.activeRoute = linkPath;
    }
  }
};

/* script */
const __vue_script__$P = script$P;

/* template */
var __vue_render__$P = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('aside', {
    staticClass: "bb-sidenav"
  }, [_c('ul', {
    staticClass: "bb-sidenav__list"
  }, [_c('li', {
    staticClass: "bb-sidenav__item bb-sidenav__logo-slot"
  }, [_c('bb-logo', {
    staticClass: "bb-sidenav__logo",
    attrs: {
      "to": _vm.logoRoute,
      "alt": "Bigbank.ee",
      "mark": ""
    }
  })], 1), _vm._v(" "), _vm._l(_vm.routes, function (route) {
    return _c('li', {
      key: route.path,
      staticClass: "bb-sidenav__item"
    }, [_c('bb-link', {
      staticClass: "bb-sidenav__link",
      class: {
        'is-active': _vm.activeRoute === route.path
      },
      attrs: {
        "to": _vm.hasRouter ? null : route.path,
        "no-underline": "",
        "color": "white"
      },
      on: {
        "click": function ($event) {
          return _vm.handleClick(route.path);
        }
      }
    }, [_c('span', {
      staticClass: "bb-sidenav__icon"
    }, [_c('bb-icon', {
      attrs: {
        "name": route.icon,
        "size": route.iconSize || 24,
        "color": "white"
      }
    })], 1), _vm._v(" "), _c('span', {
      staticClass: "bb-sidenav__link-text"
    }, [_vm._v("\n          " + _vm._s(route.title) + "\n        ")])])], 1);
  })], 2)]);
};
var __vue_staticRenderFns__$P = [];

/* style */
const __vue_inject_styles__$P = function (inject) {
  if (!inject) return;
  inject("data-v-badcd00e_0", {
    source: ".bb-sidenav{position:relative;z-index:1200;top:0;bottom:0;display:flex;width:3.75rem;padding:.9375rem 0;background:#00285a}.bb-sidenav .bb-sidenav__list{display:flex;flex-direction:column;color:#fff}.bb-sidenav .bb-sidenav__item{margin-bottom:1.875rem;transition:background-color ease-in-out .1s}.bb-sidenav .bb-sidenav__icon{width:3.75rem;opacity:.3;text-align:center;transition:opacity ease-in-out .1s}.bb-sidenav .bb-sidenav__link{display:flex;min-height:2.8125rem;align-items:center;font-size:14px}.bb-sidenav .bb-sidenav__link::before{width:1.875rem;margin:0 .9375rem;font-size:1.5625rem;text-align:center;transition:color 150ms}.bb-sidenav .bb-sidenav__link:hover{background-color:#003e8c}.bb-sidenav .bb-sidenav__link:hover .bb-sidenav__link-text{display:initial}.bb-sidenav .bb-sidenav__link.is-active .bb-sidenav__icon,.bb-sidenav .bb-sidenav__link:hover .bb-sidenav__icon{opacity:1}.bb-sidenav .bb-sidenav__link-text{display:none;padding:.625rem 1.25rem .625rem .625rem;background-color:#003e8c;white-space:nowrap}.bb-sidenav .bb-sidenav__logo-slot{display:flex;width:3.75rem;justify-content:center;margin-bottom:4.0625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$P = undefined;
/* module identifier */
const __vue_module_identifier__$P = undefined;
/* functional template */
const __vue_is_functional_template__$P = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$P = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$P,
  staticRenderFns: __vue_staticRenderFns__$P
}, __vue_inject_styles__$P, __vue_script__$P, __vue_scope_id__$P, __vue_is_functional_template__$P, __vue_module_identifier__$P, false, createInjector, undefined, undefined);

var script$O = {
  name: 'bb-slider',
  components: {
    BbInput: __vue_component__$2f,
    ClientOnly,
    VueSlider: VueSlider$1
  },
  mixins: [LocalizeNumber],
  props: {
    inputAttrs: {
      type: Object,
      default: () => ({})
    },
    roundInputToStep: Boolean,
    unit: [String, Number],
    unitMin: [String, Number],
    unitMax: [String, Number],
    unitPrefixed: {
      type: Boolean,
      default: false
    },
    unitSpace: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      validator: deprecated('title', 'label', 'Slider')
    },
    label: String,
    name: String,
    min: Number,
    minLabel: String,
    max: Number,
    maxLabel: String,
    value: Number,
    step: {
      type: Number,
      default: 1
    },
    disabled: Boolean,
    avoidOverflow: Boolean,
    editButton: {
      type: Boolean,
      validator: deprecated('`editButton` is deprecated in Slider and will be removed in a future major version.')
    },
    options: {
      type: Array,
      validator: options => options.every(isNumber)
    },
    size: {
      type: String,
      validator: size => ['md', 'lg'].includes(size),
      default: 'md'
    }
  },
  emits: ['blur', 'input'],
  data() {
    return {
      val: null,
      sliderInputHasFocus: false
    };
  },
  computed: {
    classes() {
      return ['bb-slider', `bb-slider--size-${this.size}`, {
        'bb-slider--avoid-overflow': this.avoidOverflow,
        'is-disabled': this.disabled
      }];
    },
    isLarge() {
      return this.size === 'lg';
    },
    computedLabel() {
      return this.label || this.title;
    },
    computedUnit() {
      return notEmpty(this.unitMin) && notEmpty(this.unitMax) ? this.val === this.min ? this.unitMin : this.unitMax : this.unit;
    },
    localizedMin() {
      return this.localizeNumber(this.computedInternalMin);
    },
    localizedMax() {
      return this.localizeNumber(this.computedInternalMax);
    },
    hasOptions() {
      return notEmpty(this.options);
    },
    stepDecimals() {
      return countDecimalPlaces(this.step);
    },
    vueSliderMin() {
      return this.hasOptions ? first(this.sortedOptions) : round(this.minNormalizedToVueSlider, this.stepDecimals);
    },
    vueSliderMax() {
      return this.hasOptions ? last(this.sortedOptions) : round(this.maxNormalizedToVueSlider, this.stepDecimals);
    },
    vueSliderVal() {
      return this.hasOptions ? this.val : this.valNormalizedToVueSlider;
    },
    minNormalizedToVueSlider() {
      return Math.floor(this.computedInternalMin / this.step) * this.step;
    },
    maxNormalizedToVueSlider() {
      return Math.ceil(this.computedInternalMax / this.step) * this.step;
    },
    valNormalizedToVueSlider() {
      return this.val <= this.computedInternalMin ? this.vueSliderMin : this.val >= this.computedInternalMax ? this.vueSliderMax : this.val;
    },
    computedInternalMin() {
      return this.hasOptions ? first(this.sortedOptions) : isNumber(this.min) && (!isNumber(this.max) || this.min <= this.max) ? this.min : this.min > this.max ? this.max : 0;
    },
    computedInternalMax() {
      return this.hasOptions ? last(this.sortedOptions) : isNumber(this.max) ? this.max : isNumber(this.min) ? this.min : 1;
    },
    sortedOptions() {
      return this.hasOptions ? [...this.options].sort((a, b) => a - b) : [];
    },
    unitSpaceChar() {
      return this.unitSpace ? ' ' : '';
    },
    computedMinRange() {
      var _ref, _this$unitMin;
      const unit = (_ref = (_this$unitMin = this.unitMin) !== null && _this$unitMin !== void 0 ? _this$unitMin : this.unit) !== null && _ref !== void 0 ? _ref : '';
      const parts = [this.localizedMin, this.unitSpaceChar, unit];
      return !this.unitPrefixed ? parts.join('') : parts.reverse().join('');
    },
    computedMaxRange() {
      var _ref2, _this$unitMax;
      const unit = (_ref2 = (_this$unitMax = this.unitMax) !== null && _this$unitMax !== void 0 ? _this$unitMax : this.unit) !== null && _ref2 !== void 0 ? _ref2 : '';
      const parts = [this.localizedMax, this.unitSpaceChar, unit];
      return !this.unitPrefixed ? parts.join('') : parts.reverse().join('');
    },
    sanitizedName() {
      return this.name ? slugify(this.name) : uuid();
    }
  },
  watch: {
    value() {
      if (this.val !== this.value) {
        this.updateValEmitValue(this.value);
      }
    }
  },
  mounted() {
    this.updateVal(this.value);
  },
  methods: {
    updateVal(value) {
      this.val = this.normalizeValue(value);
    },
    normalizeValue(value) {
      if (!isNumber(value)) {
        return this.computedInternalMin;
      }
      if (this.hasOptions) {
        return this.sortedOptions.find(option => option >= value);
      }
      const clampedValue = clamp$1(value, this.computedInternalMin, this.computedInternalMax);
      return this.roundInputToStep ? this.roundValueToStep(clampedValue) : clampedValue;
    },
    roundValueToStep(value) {
      return round(value / this.step) * this.step;
    },
    updateValEmitValue(value) {
      this.updateVal(value);
      this.emitNewValue(this.val);
    },
    onVueSliderChange(value) {
      this.updateValEmitValue(value);
    },
    onInputChange(value) {
      this.updateValEmitValue(value);
      this.sliderInputHasFocus = false;
    },
    onInputBlur() {
      this.$emit('blur');
    },
    emitNewValue(value) {
      this.$emit('input', value);
    },
    onInputFocus() {
      this.sliderInputHasFocus = true;
    }
  }
};

/* script */
const __vue_script__$O = script$O;

/* template */
var __vue_render__$O = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_c('div', {
    staticClass: "bb-slider__fields"
  }, [_c('bb-input', {
    staticClass: "bb-slider__input",
    attrs: {
      "input-attrs": _vm.inputAttrs,
      "value": _vm.val,
      "label": _vm.computedLabel,
      "add-on": _vm.computedUnit,
      "disabled": _vm.disabled,
      "name": _vm.sanitizedName,
      "decimals": _vm.decimals,
      "min-decimals": _vm.minDecimals,
      "max-decimals": _vm.maxDecimals,
      "min": _vm.min,
      "max": _vm.max,
      "locale": _vm.locale,
      "currency": "",
      "framed": "",
      "large": _vm.isLarge
    },
    on: {
      "change": _vm.onInputChange,
      "focus": _vm.onInputFocus,
      "blur": _vm.onInputBlur
    }
  }), _vm._v(" "), _c('client-only', [_c('vue-slider', {
    ref: "slider",
    staticClass: "bb-slider__vue-slider",
    attrs: {
      "value": _vm.vueSliderVal,
      "disabled": _vm.disabled,
      "min": _vm.vueSliderMin,
      "max": _vm.vueSliderMax,
      "interval": _vm.step,
      "tooltip": "none",
      "height": "5",
      "use-keyboard": "",
      "silent": "",
      "aria-label": _vm.computedLabel
    },
    on: {
      "change": _vm.onVueSliderChange
    }
  }, [_c('div', {
    staticClass: "vue-slider-background"
  })])], 1)], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-slider__ranges"
  }, [_c('span', [_vm.minLabel ? _c('span', [_vm._v(_vm._s(_vm.minLabel) + " ")]) : _vm._e(), _vm._v(" "), _c('span', [_vm._v(_vm._s(_vm.computedMinRange))])]), _vm._v(" "), _c('span', [_vm.maxLabel ? _c('span', [_vm._v(_vm._s(_vm.maxLabel) + " ")]) : _vm._e(), _vm._v(" "), _c('span', [_vm._v(_vm._s(_vm.computedMaxRange))])])])]);
};
var __vue_staticRenderFns__$O = [];

/* style */
const __vue_inject_styles__$O = function (inject) {
  if (!inject) return;
  inject("data-v-38458c00_0", {
    source: ".bb-slider{line-height:1}.bb-slider__fields{position:relative}.bb-slider--avoid-overflow{padding-right:1.125rem;padding-left:1.125rem}.bb-slider.is-disabled{cursor:not-allowed}.bb-slider--size-lg.bb-slider .bb-input__input{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-slider .bb-slider__ranges{display:flex;justify-content:space-between;margin-top:.625rem;margin-bottom:.625rem;color:#9dabb9;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;text-transform:uppercase;user-select:none}.bb-slider--size-lg.bb-slider .bb-slider__ranges{margin-top:1.125rem}.bb-slider .bb-slider__ranges>span:last-child{text-align:right}.bb-slider .bb-slider__input input:focus{border-color:#00285a}.bb-slider .bb-slider__vue-slider{position:absolute;bottom:.0625rem;left:.0625rem;width:calc(100% - 2px)!important;height:.1875rem}.bb-slider--size-lg.bb-slider .bb-slider__vue-slider{height:.3125rem}.bb-slider .vue-slider{padding:0!important}.bb-slider .vue-slider-disabled .vue-slider-process::before{background-color:#dee3e8}.bb-slider .vue-slider-rail{position:relative!important;height:.1875rem}.bb-slider .vue-slider-background{position:relative;top:-.125rem;display:flex;overflow:hidden;width:100%;height:.3125rem;align-items:flex-end;border-radius:0 0 .3125rem .3125rem}.bb-slider--size-lg.bb-slider .vue-slider-background{top:-.1875rem;height:.5rem;border-radius:0 0 .5rem .5rem}.bb-slider .vue-slider-background::before{display:block;width:100%;height:.1875rem;background-color:#dee3e8;content:\"\"}.bb-slider--size-lg.bb-slider .vue-slider-background::before{height:.3125rem}.bb-slider .vue-slider-process{position:absolute;top:-.125rem!important;display:flex;overflow:hidden;height:.3125rem!important;align-items:flex-end;border-radius:0 0 0 .3125rem}.bb-slider--size-lg.bb-slider .vue-slider-process{top:-.1875rem!important;height:.5rem!important;border-radius:0 0 0 .5rem}.bb-slider .vue-slider-process::before{display:block;height:.1875rem;flex:1;background-color:#0ccca2;content:\"\"}.bb-slider--size-lg.bb-slider .vue-slider-process::before{height:.3125rem}.bb-slider .vue-slider-dot{position:absolute;top:70%!important;width:auto!important;height:auto!important;padding:.3125rem;outline:0}.bb-slider--size-lg.bb-slider .vue-slider-dot{padding:.625rem}.bb-slider .vue-slider-dot:hover{cursor:ew-resize}.bb-slider .vue-slider-dot-handle{width:1rem;height:1rem;box-sizing:border-box;border:.25rem solid #0ccca2;border-radius:50%;background-color:#fff;box-shadow:0 0 0 0 rgba(12,204,162,.2);outline:0;transition:all .2s ease}.bb-slider--size-lg.bb-slider .vue-slider-dot-handle{width:1.75rem;height:1.75rem;border-width:.375rem}.bb-slider .vue-slider-dot-handle-focus:not(:hover){box-shadow:0 0 0 .25rem rgba(12,204,162,.2)}.bb-slider--size-lg.bb-slider .vue-slider-dot-handle-focus:not(:hover){box-shadow:0 0 0 .375rem rgba(12,204,162,.2)}.bb-slider .vue-slider-dot-handle-disabled.vue-slider-dot-handle-disabled.vue-slider-dot-handle-disabled{border-color:#dee3e8;background-color:#fff;cursor:not-allowed}.bb-slider .vue-slider-dot-handle:hover{border-color:#3dd6b5}.bb-slider .vue-slider-dot-handle:active{width:.75rem;height:.75rem;border-color:#099c7c;box-shadow:0 0 0 0 rgba(12,204,162,.2)}.bb-slider--size-lg.bb-slider .vue-slider-dot-handle:active{width:1.375rem;height:1.375rem}.bb-slider .vue-slider-sr-only{display:none!important}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$O = undefined;
/* module identifier */
const __vue_module_identifier__$O = undefined;
/* functional template */
const __vue_is_functional_template__$O = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$O = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$O,
  staticRenderFns: __vue_staticRenderFns__$O
}, __vue_inject_styles__$O, __vue_script__$O, __vue_scope_id__$O, __vue_is_functional_template__$O, __vue_module_identifier__$O, false, createInjector, undefined, undefined);

var script$N = {
  name: 'bb-step-icon',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    done: Boolean,
    inactive: Boolean,
    text: String,
    icon: String
  },
  computed: {
    defaultAriaLabel() {
      return [this.text, this.done ? 'Completed' : 'Not completed'].filter(Boolean).join(', ');
    }
  }
};

/* script */
const __vue_script__$N = script$N;

/* template */
var __vue_render__$N = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-step-icon",
    class: {
      'bb-step-icon--done': _vm.done,
      'bb-step-icon--inactive': _vm.inactive
    },
    attrs: {
      "aria-label": _vm.defaultAriaLabel,
      "aria-disabled": _vm.inactive
    }
  }, [_c('div', {
    staticClass: "bb-step-icon__wrapper"
  }, [_vm.icon ? _c('bb-icon', {
    staticClass: "bb-step-icon__icon",
    attrs: {
      "name": _vm.icon,
      "fill": "original",
      "size": "fluid"
    }
  }) : _vm._e(), _vm._v(" "), _c('transition', {
    attrs: {
      "name": "step-icon__status"
    }
  }, [_c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.done,
      expression: "done"
    }],
    staticClass: "bb-step-icon__status",
    attrs: {
      "name": "ui-check",
      "size": "18",
      "fill": "mint"
    }
  })], 1)], 1), _vm._v(" "), _vm.text ? _c('div', {
    staticClass: "bb-step-icon__title"
  }, [_vm._v("\n    " + _vm._s(_vm.text) + "\n  ")]) : _vm._e()]);
};
var __vue_staticRenderFns__$N = [];

/* style */
const __vue_inject_styles__$N = function (inject) {
  if (!inject) return;
  inject("data-v-c96a0b0a_0", {
    source: ".bb-step-icon{position:relative;display:flex;flex-direction:column;align-items:center;margin:1.25rem 0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-step-icon .bb-step-icon__icon{display:inline-block;width:2.5rem;height:2.5rem;border-radius:50%;margin:0 auto;background-color:#fff}.bb-step-icon__title{display:flex;max-width:5rem;justify-content:center;margin:.9375rem auto 0;color:#fff;font-size:12px;line-height:1.25;text-align:center}.bb-step-icon__icon,.bb-step-icon__status,.bb-step-icon__title{transition:opacity .2s ease-out}.bb-step-icon .bb-step-icon__status{position:absolute;top:.6875rem;left:-.625rem;padding:.0625rem;border-radius:50%;background-color:#fff;color:#0ccca2;vertical-align:middle}.bb-step-icon--done .bb-step-icon__icon,.bb-step-icon--done .bb-step-icon__title,.bb-step-icon--inactive .bb-step-icon__icon,.bb-step-icon--inactive .bb-step-icon__title{opacity:.5}.bb-step-icon__wrapper{position:relative}.bb-step-icon__status-leave-active{transition:opacity .2s ease-out}.bb-step-icon__status-fade-enter{transition:opacity .2s ease-out}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$N = undefined;
/* module identifier */
const __vue_module_identifier__$N = undefined;
/* functional template */
const __vue_is_functional_template__$N = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$N = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$N,
  staticRenderFns: __vue_staticRenderFns__$N
}, __vue_inject_styles__$N, __vue_script__$N, __vue_scope_id__$N, __vue_is_functional_template__$N, __vue_module_identifier__$N, false, createInjector, undefined, undefined);

var Icon$j = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1584274737__a bb-icon__dynamic-fill",attrs:{"d":"M19,13H5V11H19V13Z"}})]) } };

Vue.component('icon-ui-minus', Icon$j);

var Icon$i = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2499900743__a bb-icon__dynamic-fill",attrs:{"d":"M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"}})]) } };

Vue.component('icon-ui-plus', Icon$i);

const MAX_VALUE = 1000000;
const siblingWasClicked = event => event.relatedTarget && event.target.parentElement === event.relatedTarget.parentElement;
var script$M = {
  name: 'bb-step-input',
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C
  },
  mixins: [Field, LocalizeNumber, veeValidationMixin],
  props: {
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 1000,
      validator: target => target < MAX_VALUE
    },
    value: Number,
    step: {
      type: Number,
      default: 10
    },
    disabled: Boolean
  },
  emits: ['input'],
  data() {
    return {
      val: this.value ? this.toFixedDecimals(this.value) : this.min
    };
  },
  computed: {
    computedDisplayVal() {
      return this.localizeNumber(this.val);
    }
  },
  watch: {
    value(newVal) {
      this.val = this.toFixedDecimals(newVal);
    }
  },
  methods: {
    countDecimals(step) {
      const decimalPlaces = countDecimalPlaces(step);
      const maxAllowedDecimals = 3;
      return decimalPlaces > maxAllowedDecimals ? maxAllowedDecimals : decimalPlaces;
    },
    toFixedDecimals(value) {
      return +value.toFixed(this.countDecimals(this.step));
    },
    increment() {
      const newVal = this.val + this.step;
      this.updateVal(newVal);
    },
    reduce() {
      const newVal = this.val - this.step;
      this.updateVal(newVal);
    },
    onInternalInputBlur(event) {
      const unlocalizedValue = this.unlocalizeNumber(event.target.value);
      if (!siblingWasClicked(event)) {
        this.updateVal(unlocalizedValue);
      }
    },
    onInternalInputChange(event) {
      const unlocalizedValue = this.unlocalizeNumber(event.target.value);
      this.updateVal(unlocalizedValue);
    },
    updateVal(newVal) {
      const oldVal = this.val;
      this.val = newVal > this.max ? this.max : newVal < this.min ? this.min : this.toFixedDecimals(newVal);
      if (this.val === oldVal && oldVal !== newVal) {
        this.$refs.input.value = oldVal;
      }
      this.$emit('input', this.val);
    }
  }
};

const __vue_script__$M = script$M;

/* template */
var __vue_render__$M = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-step-input field-group",
    class: _vm.fieldGroupClasses
  }, [_c('label', {
    staticClass: "bb-step-input__label",
    attrs: {
      "for": _vm.id
    }
  }, [_vm._t("default", function () {
    return [_vm._v(_vm._s(_vm.label))];
  })], 2), _vm._v(" "), _c('div', {
    staticClass: "input-group framed-input bb-step-input__input-group"
  }, [_c('bb-button', {
    staticClass: "bb-step-input__button bb-step-input__button--decrement",
    attrs: {
      "disabled": _vm.disabled,
      "display": "circle",
      "size": "sm",
      "inverted": "",
      "color": "blue"
    },
    on: {
      "click": _vm.reduce
    }
  }, [_c('bb-icon', {
    attrs: {
      "fill": _vm.disabled ? 'gray-80' : 'navy',
      "name": "ui-minus",
      "size": "18"
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-step-input__field-wrapper"
  }, [_c('input', {
    ref: "input",
    staticClass: "bb-step-input__field",
    attrs: {
      "id": _vm.id,
      "disabled": _vm.disabled,
      "name": _vm.name,
      "min": _vm.min,
      "max": _vm.max,
      "size": "sm",
      "type": "text",
      "step": "any"
    },
    domProps: {
      "value": _vm.computedDisplayVal
    },
    on: {
      "blur": _vm.onInternalInputBlur,
      "change": _vm.onInternalInputChange
    }
  })]), _vm._v(" "), _c('bb-button', {
    staticClass: "bb-step-input__button bb-step-input__button--increment",
    attrs: {
      "disabled": _vm.disabled,
      "display": "circle",
      "size": "sm",
      "inverted": "",
      "color": "blue"
    },
    on: {
      "click": _vm.increment
    }
  }, [_c('bb-icon', {
    attrs: {
      "fill": _vm.disabled ? 'gray-80' : 'navy',
      "name": "ui-plus",
      "size": "18"
    }
  })], 1)], 1)]);
};
var __vue_staticRenderFns__$M = [];

/* style */
const __vue_inject_styles__$M = function (inject) {
  if (!inject) return;
  inject("data-v-735d8488_0", {
    source: ".bb-step-input__input-group{display:flex;height:2.5rem;flex-direction:row;align-items:center;padding-right:0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-step-input__button.bb-button{display:block;width:1.5rem;height:1.5rem;flex:none}.bb-step-input__button.bb-button.bb-button--inverted:not(.bb-button--disabled){border-width:.125rem}.bb-step-input__button--decrement.bb-button{margin-right:.9375rem}.bb-step-input__button--increment.bb-button{margin-left:.9375rem}.bb-step-input__field-wrapper{flex:1 0 0}.bb-step-input__field{font-size:1.5rem;line-height:inherit;text-align:center}.bb-step-input .bb-step-input__label{padding-right:0;text-align:center}.field-group.bb-step-input .bb-step-input__label{transform:translate(0,0)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$M = undefined;
/* module identifier */
const __vue_module_identifier__$M = undefined;
/* functional template */
const __vue_is_functional_template__$M = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$M = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$M,
  staticRenderFns: __vue_staticRenderFns__$M
}, __vue_inject_styles__$M, __vue_script__$M, __vue_scope_id__$M, __vue_is_functional_template__$M, __vue_module_identifier__$M, false, createInjector, undefined, undefined);

var Icon$h = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2771699895__a bb-icon__dynamic-fill",attrs:{"d":"M16,17H7V10.5C7,8 9,6 11.5,6C14,6 16,8 16,10.5M18,16V10.5C18,7.43 15.86,4.86 13,4.18V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V4.18C7.13,4.86 5,7.43 5,10.5V16L3,18V19H20V18M11.5,22A2,2 0 0,0 13.5,20H9.5A2,2 0 0,0 11.5,22Z"}})]) } };

Vue.component('icon-ui-bell-outline', Icon$h);

const targets = ['_self', '_blank', '_parent', '_top'];

var script$L = {
  name: 'bb-task',
  components: {
    BbLink: __vue_component__$2p,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbIcon: __vue_component__$2C,
    BbLabel: __vue_component__$2e
  },
  mixins: [TaskColor],
  props: {
    title: String,
    status: String,
    assigned: {
      type: Boolean,
      default: false
    },
    assigneeLabel: {
      type: String,
      default: 'Assignee:'
    },
    assignee: String,
    date: String,
    dateColor: Boolean,
    info: String,
    customer: String,
    customerLink: [String, Object],
    subject: String,
    subjectLink: [String, Object],
    source: String,
    sourceLink: [String, Object],
    linkTarget: {
      type: String,
      validator: target => targets.includes(target)
    },
    entity: Boolean,
    reminders: {
      type: [String, Number],
      default: null
    },
    labels: {
      type: [Array],
      validator: value => value.length <= 5,
      default: () => []
    }
  },
  emits: ['click'],
  computed: {
    computedInitials() {
      const initials = this.title.match(/(?:^|\s)\S/g).map(s => s.trim()).join('').toUpperCase();
      return initials.length > 1 ? initials[0] + initials.slice(-1)[0] : initials[0];
    },
    computedReminders() {
      return Number(this.reminders) > 99 ? '99+' : this.reminders;
    },
    computedLabels() {
      const labels = this.labels;
      if (labels.length > 5) labels.length = 5;
      labels.forEach((label, key) => {
        if (label.length > 32) labels[key] = label.substring(0, 32);
      });
      return labels;
    }
  }
};

/* script */
const __vue_script__$L = script$L;

/* template */
var __vue_render__$L = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-task-wrapper",
    class: {
      'bb-task-wrapper--entity': _vm.entity
    }
  }, [_c('div', {
    staticClass: "bb-task",
    class: [_vm.status, {
      'bb-task--assigned': _vm.assigned
    }],
    on: {
      "click": function ($event) {
        return _vm.$emit('click');
      }
    }
  }, [!_vm.entity ? _c('div', {
    staticClass: "bb-task__type-bar",
    class: "bg-" + _vm.taskColor
  }) : _vm._e(), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-task__body"
  }, [_vm.entity ? _c('div', {
    staticClass: "bb-task__initials",
    class: "bg-" + _vm.taskColor
  }, [_vm._v("\n        " + _vm._s(_vm.computedInitials) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.reminders && !_vm.entity ? _c('div', {
    staticClass: "bb-task__reminder"
  }, [_c('bb-icon', {
    attrs: {
      "name": "ui-bell-outline",
      "fill": "gray",
      "size": "24"
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-task__reminder-count"
  }, [_vm._v("\n          " + _vm._s(_vm.computedReminders) + "\n        ")])], 1) : _vm._e(), _vm._v(" "), _c('bb-col', {
    staticClass: "bb-task__title",
    attrs: {
      "col": "24",
      "xl": "8"
    }
  }, [_c('bb-row', [_c('bb-col', {
    staticClass: "bb-task__main",
    attrs: {
      "col": "24"
    }
  }, [_vm._v("\n            " + _vm._s(_vm.title) + "\n          ")]), _vm._v(" "), _vm.labels.length > 0 ? _c('bb-col', {
    staticClass: "bb-task__labels",
    attrs: {
      "col": "24"
    }
  }, _vm._l(_vm.computedLabels, function (label, key) {
    return _c('bb-label', {
      key: key,
      staticClass: "bb-task__label",
      attrs: {
        "size": "18"
      }
    }, [_vm._v("\n              " + _vm._s(label) + "\n            ")]);
  }), 1) : _vm._e(), _vm._v(" "), _c('bb-col', {
    staticClass: "bb-task__info",
    attrs: {
      "col": "24"
    }
  }, [_c('bb-row', [_vm.assignee ? _c('bb-col', [_c('span', [_vm._v("\n                  " + _vm._s(_vm.assigneeLabel) + "\n                ")]), _vm._v(" "), _c('span', {
    staticClass: "bb-task__assignee"
  }, [_vm._v("\n                  " + _vm._s(_vm.assignee) + "\n                ")])]) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-task__info-content"
  }, [_vm._v("\n                " + _vm._s(_vm.info) + "\n              ")])], 1)], 1)], 1)], 1), _vm._v(" "), _c('bb-col', [!_vm.entity ? _c('div', {
    staticClass: "bb-task__info bb-task__info-content--xl-only"
  }, [_vm._v("\n          " + _vm._s(_vm.info) + "\n        ")]) : _vm._e(), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-task__details"
  }, [_c('bb-col', {
    staticClass: "bb-task__customer hidden-max-xl",
    class: {
      'has-bullet': _vm.customer
    },
    attrs: {
      "col": "24",
      "lg": "6"
    }
  }, [_c('bb-link', {
    attrs: {
      "to": _vm.customerLink,
      "target": _vm.linkTarget,
      "color": "blue"
    },
    on: {
      "click": function ($event) {
        $event.stopPropagation();
      }
    }
  }, [_vm._v("\n              " + _vm._s(_vm.customer) + "\n            ")])], 1), _vm._v(" "), _c('bb-col', {
    staticClass: "bb-task__source",
    class: {
      'has-bullet': _vm.source,
      'hidden-max-xl': !_vm.source
    },
    attrs: {
      "col": "12",
      "md": "auto",
      "xl": "5"
    }
  }, [_c('bb-link', {
    attrs: {
      "to": _vm.sourceLink,
      "target": _vm.linkTarget,
      "color": "blue"
    },
    on: {
      "click": function ($event) {
        $event.stopPropagation();
      }
    }
  }, [_vm._v("\n              " + _vm._s(_vm.source) + "\n            ")])], 1), _vm._v(" "), _vm.subject ? _c('bb-col', {
    staticClass: "bb-task__subject",
    class: {
      'has-bullet': _vm.subject
    },
    attrs: {
      "col": "12",
      "md": "auto",
      "xl": "6"
    }
  }, [_c('bb-link', {
    attrs: {
      "to": _vm.subjectLink,
      "target": _vm.linkTarget,
      "color": "blue"
    },
    on: {
      "click": function ($event) {
        $event.stopPropagation();
      }
    }
  }, [_vm._v("\n              " + _vm._s(_vm.subject) + "\n            ")])], 1) : _vm._e(), _vm._v(" "), _c('bb-col', {
    staticClass: "bb-task__date",
    class: [{
      'color-red': _vm.dateColor,
      'has-bullet': _vm.date
    }],
    attrs: {
      "col": "12",
      "md": "auto"
    }
  }, [_vm._v("\n            " + _vm._s(_vm.date) + "\n          ")])], 1)], 1)], 1), _vm._v(" "), _c('bb-icon', {
    staticClass: "bb-task__arrow",
    attrs: {
      "name": "ui-menu-down",
      "size": "24",
      "rotate": "left"
    }
  })], 1)]);
};
var __vue_staticRenderFns__$L = [];

/* style */
const __vue_inject_styles__$L = function (inject) {
  if (!inject) return;
  inject("data-v-73622436_0", {
    source: "@charset \"UTF-8\";.bb-task-group+.bb-task-wrapper,.bb-task-wrapper+.bb-task-wrapper{margin-top:.4375rem}.bb-task{position:relative;display:flex;border:.125rem solid transparent;background:#fff;box-shadow:0 .125rem .25rem 0 rgba(202,202,202,.35);color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;transition:all ease-in .1s}.bb-task--assigned{border-color:#add9ff;background-color:#e6f3ff;box-shadow:none}.bb-task:hover{border-color:#dee3e8;background-color:#eeeff2;box-shadow:none}.bb-task__title{margin-right:auto;color:#00285a}.bb-task-wrapper--entity .bb-task__title{margin-right:0}.bb-task__initials{display:flex;width:2.1875rem;height:2.1875rem;align-items:center;justify-content:center;border-radius:50%;margin-left:-3.125rem;color:#fff}.bb-task__reminder{position:relative;margin-left:-.9375rem}.bb-task__reminder-count{position:absolute;top:-.0625rem;left:.5625rem;display:flex;min-width:14px;height:14px;align-items:center;justify-content:center;padding:0 4px;border-radius:99em;background-color:#2485e5;color:#fff;font-size:9px;line-height:14px}.bb-task__label{margin:4px 8px 0 0;background-color:#38414d}.bb-task__info{padding-right:.3125rem;color:#57697a;font-size:12px}.bb-task-wrapper--entity .bb-task__info{order:1;font-size:12px}@media (min-width:1200px){.bb-task-wrapper--entity .bb-task__info-content{display:block}}.bb-task__info-content{position:relative;width:100%;padding-right:.9375rem;padding-left:.9375rem}@media (min-width:1200px){.bb-task__info-content{display:none}}@media (min-width:768px){.bb-task--assigned .bb-task__info-content{width:inherit;text-align:right}}.bb-task__info-content--xl-only{display:none}@media (min-width:1200px){.bb-task__info-content--xl-only{display:block;text-align:right}}@media (min-width:768px){.bb-task__info{order:-1}}.bb-task__assignee{color:#2485e5}.bb-task__type-bar{width:.5rem;margin:-.125rem}.bb-task__body{display:flex;flex:1;align-items:center;justify-content:flex-end;padding:.9375rem 1.875rem .9375rem 3.125rem}.bb-task-wrapper--entity .bb-task__body{justify-content:flex-start;padding-left:4.6875rem;margin:0}.bb-task-wrapper--entity .bb-task__body .col:last-child{margin-right:auto}.bb-task__body .has-bullet::before{margin-right:.625rem;color:#dee3e8;content:\"•\";font-size:1.5625rem;line-height:.8}@media (max-width:1200px){.bb-task__body{justify-content:flex-start}}.bb-task__arrow{align-self:center;margin-right:1.25rem}.bb-task__customer,.bb-task__date,.bb-task__source,.bb-task__subject{display:flex;min-width:fit-content;align-items:center;padding:0 1.25rem}@media (max-width:1200px){.bb-task__customer,.bb-task__date,.bb-task__source,.bb-task__subject{padding-top:0}}.bb-task-wrapper--entity .bb-task__customer,.bb-task-wrapper--entity .bb-task__date,.bb-task-wrapper--entity .bb-task__source,.bb-task-wrapper--entity .bb-task__subject{min-width:auto;font-size:12px;text-align:left}@media (max-width:1200px){.bb-task-wrapper--entity .bb-task__customer,.bb-task-wrapper--entity .bb-task__date,.bb-task-wrapper--entity .bb-task__source,.bb-task-wrapper--entity .bb-task__subject{padding-top:0}}.bb-task-wrapper--entity .bb-task__source{color:#2485e5}@media (min-width:1200px){.bb-task__date{justify-content:flex-end;margin-left:auto}}.bb-task a{box-shadow:none;color:#2485e5;text-decoration:none}.bb-task a:not([href]){color:inherit}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$L = undefined;
/* module identifier */
const __vue_module_identifier__$L = undefined;
/* functional template */
const __vue_is_functional_template__$L = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$L = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$L,
  staticRenderFns: __vue_staticRenderFns__$L
}, __vue_inject_styles__$L, __vue_script__$L, __vue_scope_id__$L, __vue_is_functional_template__$L, __vue_module_identifier__$L, false, createInjector, undefined, undefined);

var script$K = {
  name: 'bb-task-group',
  components: {
    BbIcon: __vue_component__$2C
  },
  props: {
    maxHeight: {
      type: Number,
      default: 3000
    },
    entities: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle'],
  data() {
    return {
      open: false
    };
  },
  computed: {
    childrenHeight() {
      return {
        maxHeight: this.open ? `${this.maxHeight}px` : '0'
      };
    }
  },
  methods: {
    toggle() {
      this.open = !this.open;
      this.$emit('toggle');
    }
  }
};

/* script */
const __vue_script__$K = script$K;

/* template */
var __vue_render__$K = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-task-group",
    class: [{
      entities: _vm.entities,
      'is-open': _vm.open
    }]
  }, [_c('div', {
    staticClass: "bb-task-group__head"
  }, [_c('div', {
    staticClass: "bb-task-group__toggle",
    on: {
      "click": _vm.toggle
    }
  }, [_c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.open,
      expression: "open"
    }],
    attrs: {
      "name": "ui-minus-box-outline",
      "fill": "blue-70",
      "size": "24"
    }
  }), _vm._v(" "), _c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: !_vm.open,
      expression: "!open"
    }],
    attrs: {
      "name": "ui-plus-box-outline",
      "fill": "blue-70",
      "size": "24"
    }
  })], 1), _vm._v(" "), _vm._t("default")], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-task-group__children",
    style: _vm.childrenHeight
  }, [_vm._t("children")], 2)]);
};
var __vue_staticRenderFns__$K = [];

/* style */
const __vue_inject_styles__$K = function (inject) {
  if (!inject) return;
  inject("data-v-fcd0b3be_0", {
    source: ".bb-task-group+.bb-task-group{margin-top:.4375rem}.bb-task-group__head{position:relative;margin-top:.4375rem}.bb-task-group__toggle{position:absolute;z-index:1100;top:calc(50% - 1rem);left:-.725rem;width:2rem;height:2rem;color:#89a;cursor:pointer;line-height:2rem;text-align:center;user-select:none}.bb-task-group__toggle svg{width:2rem;height:2rem}.bb-task-group__children{overflow:hidden;max-height:0;transition:max-height .3s cubic-bezier(.03,.71,.23,.98)}.bb-task-group__children>.bb-task-wrapper{margin-top:.4375rem}.bb-task-group__children>.bb-task-group,.bb-task-group__children>.bb-task-wrapper{position:relative;margin-left:1.25rem}.bb-task-group__children .bb-task-group:not(:last-child)::before,.bb-task-group__children .bb-task-group__head::before,.bb-task-group__children .bb-task-wrapper::before{position:absolute;width:.0625rem;border-left:.0625rem solid #89a;content:\"\";inset:-.625rem auto -.0625rem -1.25rem}.bb-task-group__children .bb-task-group__head::after,.bb-task-group__children .bb-task-wrapper::after{position:absolute;top:50%;right:auto;left:-1.25rem;width:1.25rem;height:1.25rem;border-top:.0625rem solid #89a;content:\"\"}.bb-task-group__children .bb-task-group:last-child .bb-task-group__head::before,.bb-task-group__children .bb-task-wrapper:last-child::before{height:calc(50% + .625rem)}.bb-task-group__children .bb-task-group__head::before{left:-1.25rem}.is-open>.bb-task-group__children{max-height:187.5rem;transition:max-height .3s cubic-bezier(.7,.02,.99,.32)}.entities .bb-task-group__children .bb-task-wrapper::before{left:-1.25rem}.entities .bb-task-group__children .bb-task-wrapper::after{left:-1.25rem;width:1.25rem}.entities .bb-task-group::before{left:-1.25rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$K = undefined;
/* module identifier */
const __vue_module_identifier__$K = undefined;
/* functional template */
const __vue_is_functional_template__$K = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$K = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$K,
  staticRenderFns: __vue_staticRenderFns__$K
}, __vue_inject_styles__$K, __vue_script__$K, __vue_scope_id__$K, __vue_is_functional_template__$K, __vue_module_identifier__$K, false, createInjector, undefined, undefined);

var Transactions = {
  mixins: [LocalizeNumber],
  props: {
    loading: Boolean,
    currency: {
      type: String,
      default: 'EUR'
    },
    useGrouping: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    formatMoney(number) {
      var _this$decimals, _this$decimals2;
      return this.localizeNumber(number, {
        style: 'currency',
        useGrouping: this.useGrouping,
        currency: this.currency,
        minimumFractionDigits: (_this$decimals = this.decimals) !== null && _this$decimals !== void 0 ? _this$decimals : this.minDecimals,
        maximumFractionDigits: (_this$decimals2 = this.decimals) !== null && _this$decimals2 !== void 0 ? _this$decimals2 : this.maxDecimals
      });
    }
  }
};

var script$J = {
  name: 'bb-transaction-item',
  components: {
    BbButton: __vue_component__$2v,
    BbIconFrame: __vue_component__$2g,
    BbIcon: __vue_component__$2C,
    BbSkeleton: __vue_component__$2d
  },
  mixins: [Transactions],
  props: {
    icon: {
      type: String,
      required: true
    },
    iconColor: {
      type: String,
      default: 'gray-50',
      validator: type => ['navy', 'blue', 'mint', 'green',
      'red', 'gray', 'gray-90', 'gray-80', 'gray-70', 'gray-50', 'gray-40', 'gray-30', 'gray-20', 'gray-10'].includes(type)
    },
    iconFrameFilled: {
      type: Boolean,
      default: true
    },
    iconFrameColored: Boolean,
    debt: Boolean,
    income: Boolean,
    hidePlusMinus: Boolean,
    creditCard: Boolean,
    status: {
      type: String,
      default: 'none',
      validator: type => ['none', 'success', 'error'].includes(type)
    },
    statusColumn: {
      type: Object,
      default() {
        return {};
      }
    },
    lastColumn: {
      type: Object,
      default() {
        return {};
      }
    },
    button: {
      type: Object,
      default() {
        return {};
      }
    },
    middleColumns: {
      type: Array,
      default() {
        return [];
      }
    },
    middleColumnCount: {
      type: [Number, String],
      default: 3,
      validator: type => type >= 0 && type <= 3
    }
  },
  emits: ['itemClick', 'buttonClick'],
  computed: {
    classes() {
      return {
        [`bb-transaction-item--status-${this.status}`]: !this.loading && this.status && this.status !== 'none',
        [`bb-transaction-item--columns-${this.computedMiddleColumnCount}`]: this.computedMiddleColumnCount > 0,
        'bb-transaction-item--loading': this.loading,
        'bb-transaction-item--income': this.income,
        'bb-transaction-item--debt': this.debt,
        'bb-transaction-item--button-visible': !this.loading && this.button.visible,
        'bb-transaction-item--credit-card': this.creditCard,
        'bb-transaction-item--status-missing-lines': this.hasMissingLinesInStatusColumn
      };
    },
    hasMissingLinesInStatusColumn() {
      return isEmpty(this.statusColumn.progress) || isEmpty(this.statusColumn.status);
    },
    computedMiddleColumnCount() {
      return this.creditCard ? 1 : this.middleColumnCount;
    },
    computedMiddleColumns() {
      return this.middleColumns.slice(0, this.middleColumnCount);
    },
    displayedAmount() {
      if (this.lastColumn && isNumber(this.lastColumn.amount)) {
        const formattedAmount = this.formatMoney(this.lastColumn.amount);
        return this.hidePlusMinus || parseFloat(this.lastColumn.amount) === 0 ? formattedAmount : this.income ? `+ ${formattedAmount}` : `- ${formattedAmount}`;
      } else {
        return '';
      }
    }
  },
  methods: {
    onItemClick() {
      this.$emit('itemClick');
    },
    onButtonClick() {
      this.$emit('buttonClick');
    }
  }
};

/* script */
const __vue_script__$J = script$J;

/* template */
var __vue_render__$J = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('li', {
    staticClass: "bb-transaction-item",
    class: _vm.classes,
    attrs: {
      "tabindex": "0",
      "aria-busy": _vm.loading
    },
    on: {
      "keydown": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        $event.preventDefault();
        return _vm.onItemClick.apply(null, arguments);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
          return null;
        }
        $event.preventDefault();
        return _vm.onItemClick.apply(null, arguments);
      }],
      "click": _vm.onItemClick
    }
  }, [_c('div', {
    staticClass: "bb-transaction-item__icon"
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": "40",
      "height": "40",
      "color": "gray-30",
      "radius": "10"
    }
  }) : _c('bb-icon-frame', {
    attrs: {
      "filled": _vm.iconFrameFilled && _vm.iconColor !== 'gray-50',
      "colored-frame": _vm.iconFrameColored,
      "icon": _vm.icon,
      "icon-color": _vm.iconColor,
      "title": _vm.statusColumn && _vm.statusColumn.status
    }
  })], 1), _vm._v(" "), _vm.statusColumn && _vm.statusColumn.progress ? _c('div', {
    staticClass: "bb-transaction-item__progress"
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": "60",
      "height": "10"
    }
  }) : [_vm._v("\n      " + _vm._s(_vm.statusColumn.progress) + "\n    ")]], 2) : _vm._e(), _vm._v(" "), _vm.statusColumn && _vm.statusColumn.status ? _c('div', {
    staticClass: "bb-transaction-item__status",
    attrs: {
      "title": _vm.statusColumn.status
    }
  }, [_vm.loading ? _c('bb-skeleton', {
    style: {
      'max-width': '15rem'
    },
    attrs: {
      "width": "100%",
      "height": "14"
    }
  }) : [_c('span', {
    staticClass: "bb-transaction-item__status-text",
    class: _vm.statusColumn.statusColor && ["color-" + _vm.statusColumn.statusColor],
    attrs: {
      "data-testid": "bb-transaction-item__status-text"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.statusColumn.status) + "\n      ")]), _vm._v(" "), _vm.creditCard && _vm.statusColumn && _vm.statusColumn.statusSuffix ? [_c('span', {
    staticClass: "bb-transaction-item__status-suffix",
    class: _vm.statusColumn.statusSuffixColor && ["color-" + _vm.statusColumn.statusSuffixColor],
    attrs: {
      "data-testid": "bb-transaction-item__status-suffix"
    }
  }, [_vm._v("\n          " + _vm._s(" | " + _vm.statusColumn.statusSuffix) + "\n        ")])] : _vm._e()]], 2) : _vm._e(), _vm._v(" "), _vm.creditCard ? _c('div', {
    staticClass: "bb-transaction-item__middle-column-credit-card",
    attrs: {
      "data-testid": "bb-transaction-item__middle-column-credit-card"
    }
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": "24",
      "height": "24"
    }
  }) : _c('bb-icon', {
    attrs: {
      "name": "ui-information-circle-outline",
      "size": "24",
      "fill": "gray-80",
      "data-testid": "bb-transaction-item__info-icon"
    }
  })], 1) : _vm._e(), _vm._v(" "), !_vm.creditCard ? _vm._l(_vm.computedMiddleColumns, function (column, index) {
    return _c('div', {
      key: index,
      staticClass: "bb-transaction-item__middle-column"
    }, [_vm.loading ? _c('bb-skeleton', {
      attrs: {
        "width": "100%",
        "height": "14"
      }
    }) : [_vm._v("\n        " + _vm._s(_vm.formatMoney(column)) + "\n      ")]], 2);
  }) : _vm._e(), _vm._v(" "), !_vm.debt ? _c('div', {
    staticClass: "bb-transaction-item__date"
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": "60",
      "height": "10"
    }
  }) : [_vm._v("\n      " + _vm._s(_vm.lastColumn && _vm.lastColumn.date) + "\n    ")]], 2) : _vm._e(), _vm._v(" "), !_vm.debt ? _c('div', {
    staticClass: "bb-transaction-item__amount",
    class: _vm.lastColumn.amountColor && ["color-" + _vm.lastColumn.amountColor],
    attrs: {
      "data-testid": "bb-transaction-item__amount"
    }
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": "100",
      "height": "14"
    }
  }) : [_vm._v("\n      " + _vm._s(_vm.displayedAmount) + "\n    ")], _vm._v(" "), _vm.creditCard && _vm.statusColumn && _vm.statusColumn.statusSuffix ? _c('div', {
    staticClass: "bb-transaction-item__status-suffix-mobile",
    attrs: {
      "title": _vm.statusColumn.statusSuffix
    }
  }, [_vm.loading ? _c('bb-skeleton', {
    style: {
      'max-width': '15rem'
    },
    attrs: {
      "width": "100%",
      "height": "14"
    }
  }) : _c('span', {
    staticClass: "bb-transaction-item__status-suffix",
    class: _vm.statusColumn.statusSuffixColor && ["color-" + _vm.statusColumn.statusSuffixColor],
    attrs: {
      "data-testid": "bb-transaction-item__status-suffix"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.statusColumn.statusSuffix) + "\n      ")])], 1) : _vm._e()], 2) : _vm._e(), _vm._v(" "), !_vm.loading && _vm.button.visible ? _c('bb-button', {
    staticClass: "bb-transaction-item__button",
    attrs: {
      "inverted": _vm.status !== 'error',
      "uppercase": _vm.status === 'error',
      "color": _vm.status === 'error' ? 'mint' : 'blue',
      "label": _vm.button.label,
      "size": "sm"
    },
    nativeOn: {
      "click": function ($event) {
        $event.stopPropagation();
        return _vm.onButtonClick.apply(null, arguments);
      }
    }
  }) : _vm._e()], 2);
};
var __vue_staticRenderFns__$J = [];

/* style */
const __vue_inject_styles__$J = function (inject) {
  if (!inject) return;
  inject("data-v-6cacacce_0", {
    source: ".bb-transaction-item{display:grid;align-content:center;padding-right:.9375rem;padding-left:.9375rem;column-gap:.9375rem;grid-column-gap:.9375rem;grid-template-areas:\"progress date\" \"status amount\";grid-template-columns:repeat(2,1fr);position:relative;padding-top:.5rem;padding-bottom:.5rem;cursor:pointer;grid-template-rows:20px;transition:background .3s}@media (min-width:576px){.bb-transaction-item{grid-template-areas:\"icon progress date\" \"icon status amount\";grid-template-columns:2.5rem repeat(2,1fr)}}.bb-transaction-item.bb-transaction-item--credit-card{grid-template-columns:2.5fr 1fr}.bb-transaction-item.bb-transactions__table-footer,.bb-transaction-item.bb-transactions__table-header{grid-template-columns:repeat(2,1fr)}@media (min-width:992px){.bb-transaction-item.bb-transactions__table-footer,.bb-transaction-item.bb-transactions__table-header{grid-template-columns:2.5rem repeat(5,1fr)}}@media (min-width:992px){.bb-transaction-item--columns-3{grid-template-areas:\"icon progress mid-column mid-column mid-column date\" \"icon status mid-column mid-column mid-column amount\";grid-template-columns:2.5rem repeat(5,1fr)}}@media (min-width:992px){.bb-transaction-item--columns-3.bb-transactions__table-footer,.bb-transaction-item--columns-3.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column mid-column mid-column last-column\";grid-template-columns:2.5rem repeat(5,1fr)}}@media (min-width:992px){.bb-transaction-item--columns-2{grid-template-areas:\"icon progress mid-column mid-column date\" \"icon status mid-column mid-column amount\";grid-template-columns:2.5rem repeat(4,1fr)}}@media (min-width:992px){.bb-transaction-item--columns-2.bb-transactions__table-footer,.bb-transaction-item--columns-2.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column mid-column last-column\";grid-template-columns:2.5rem repeat(4,1fr)}}@media (min-width:992px){.bb-transaction-item--columns-1{grid-template-areas:\"icon progress mid-column date\" \"icon status mid-column amount\";grid-template-columns:2.5rem repeat(3,1fr)}}@media (min-width:992px){.bb-transaction-item--columns-1.bb-transactions__table-footer,.bb-transaction-item--columns-1.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column last-column\";grid-template-columns:2.5rem repeat(3,1fr)}}@media (min-width:768px){.bb-transaction-item--columns-1.bb-transaction-item--credit-card{grid-template-columns:2fr .1fr .6fr}}.bb-transaction-item::after{position:absolute;top:0;left:0;width:100%;height:100%;content:\"\";opacity:0;pointer-events:none;transition:opacity .3s}.bb-transaction-item:hover{background:#f7f7f8}.bb-transaction-item:hover.bb-transaction-item--button-visible::after{background:linear-gradient(90deg,rgba(247,247,248,0) 0,#f7f7f8 100%);opacity:1}.bb-transaction-item:focus{outline:0}.bb-transaction-item:focus::after{border:dashed 1px #bdc7d0;opacity:1}.bb-transaction-item.bb-transaction-item--credit-card{grid-template-areas:\"status amount\" \"date amount\";grid-template-rows:auto}@media (min-width:576px){.bb-transaction-item.bb-transaction-item--credit-card{grid-template-areas:\"icon status date\" \"icon progress amount\";grid-template-columns:2.5rem 2fr .6fr}}@media (min-width:768px){.bb-transaction-item.bb-transaction-item--credit-card{grid-template-areas:\"icon status mid-column date\" \"icon progress mid-column amount\";grid-template-columns:2.5rem 2fr .1fr .6fr}}.bb-transaction-item--button-visible .bb-transaction-item__button{display:none;opacity:0;transform:translateX(-.25rem) translateY(-50%)}@media (min-width:992px){.bb-transaction-item--button-visible .bb-transaction-item__button{display:initial}}.bb-transaction-item--button-visible.bb-transaction-item--debt .bb-transaction-item__button{display:initial;opacity:1;transform:translateX(0) translateY(-50%)}.bb-transaction-item--button-visible.bb-transaction-item--status-error .bb-transaction-item__amount,.bb-transaction-item--button-visible.bb-transaction-item--status-error .bb-transaction-item__date,.bb-transaction-item--button-visible.bb-transaction-item--status-success .bb-transaction-item__amount,.bb-transaction-item--button-visible.bb-transaction-item--status-success .bb-transaction-item__date{opacity:0;transform:translateX(-.25rem)}@media (min-width:992px){.bb-transaction-item--button-visible.bb-transaction-item--status-error .bb-transaction-item__amount,.bb-transaction-item--button-visible.bb-transaction-item--status-error .bb-transaction-item__date,.bb-transaction-item--button-visible.bb-transaction-item--status-success .bb-transaction-item__amount,.bb-transaction-item--button-visible.bb-transaction-item--status-success .bb-transaction-item__date{opacity:1;transform:translateX(0)}}.bb-transaction-item--button-visible.bb-transaction-item--status-error .bb-transaction-item__button,.bb-transaction-item--button-visible.bb-transaction-item--status-success .bb-transaction-item__button{display:initial;opacity:1;transform:translateX(0) translateY(-50%)}@media (min-width:992px){.bb-transaction-item--button-visible.bb-transaction-item--status-error .bb-transaction-item__button,.bb-transaction-item--button-visible.bb-transaction-item--status-success .bb-transaction-item__button{opacity:0;transform:translateX(-.25rem) translateY(-50%)}}.bb-transaction-item--button-visible.bb-transaction-item--status-error.bb-transaction-item--debt .bb-transaction-item__button,.bb-transaction-item--button-visible.bb-transaction-item--status-success.bb-transaction-item--debt .bb-transaction-item__button{display:initial;opacity:1;transform:translateX(0) translateY(-50%)}@media (min-width:992px){.bb-transaction-item--button-visible:hover .bb-transaction-item__amount,.bb-transaction-item--button-visible:hover .bb-transaction-item__date{opacity:0;transform:translateX(-.25rem)}}@media (min-width:992px){.bb-transaction-item--button-visible:hover .bb-transaction-item__button{opacity:1;transform:translateX(0) translateY(-50%)}}.bb-transaction-item--debt{background:#f7f7f8}.bb-transaction-item--status-error::before,.bb-transaction-item--status-success::before{position:absolute;top:0;left:0;width:.25rem;height:100%;background:0 0;content:\"\"}@media (min-width:992px){.bb-transaction-item--status-error::before,.bb-transaction-item--status-success::before{top:0;height:100%}}.bb-transaction-item--status-success::before{background:#0ccca2}.bb-transaction-item--status-error::before{background:#ff7155}.bb-transaction-item__middle-column-credit-card{display:none}.bb-transaction-item--credit-card .bb-transaction-item__middle-column-credit-card{align-items:center;justify-content:center;grid-area:mid-column}@media (min-width:768px){.bb-transaction-item--credit-card .bb-transaction-item__middle-column-credit-card{display:flex}}.bb-transaction-item__middle-column{display:none}@media (min-width:992px){.bb-transaction-item__middle-column{display:flex;justify-content:flex-end;grid-row:2}}.bb-transaction-item__amount,.bb-transaction-item__date{justify-content:flex-end}.bb-transaction-item__date,.bb-transaction-item__progress{display:flex;color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}.bb-transaction-item--loading .bb-transaction-item__date,.bb-transaction-item--loading .bb-transaction-item__progress{margin-top:.25rem}.bb-transaction-item--credit-card.bb-transaction-item--loading .bb-transaction-item__progress,.bb-transaction-item--loading .bb-transaction-item__date{margin-bottom:.25rem}.bb-transaction-item__progress{display:inline-block;overflow:hidden;grid-area:progress;text-overflow:ellipsis;white-space:nowrap}.bb-transaction-item--credit-card .bb-transaction-item__progress{display:none}@media (min-width:576px){.bb-transaction-item--status-missing-lines.bb-transaction-item--credit-card .bb-transaction-item__progress{display:inline-flex;align-items:center;grid-row-start:span 2}}@media (min-width:576px){.bb-transaction-item--credit-card .bb-transaction-item__progress{display:inline-block;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;line-height:1.8}}.bb-transaction-item__amount,.bb-transaction-item__middle-column,.bb-transaction-item__status{color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:.875rem}.bb-transaction-item__amount,.bb-transaction-item__status{display:flex;line-height:.875rem}.bb-transaction-item__icon{z-index:1;display:none;align-items:center;justify-content:center;grid-area:icon}@media (min-width:576px){.bb-transaction-item__icon{display:flex}}.bb-transaction-item__status{min-width:0;grid-area:status}.bb-transaction-item--credit-card .bb-transaction-item__status{max-width:13.75rem;align-items:baseline;margin-bottom:2px}@media (min-width:576px){.bb-transaction-item--status-missing-lines.bb-transaction-item--credit-card .bb-transaction-item__status{display:inline-flex;flex-direction:row;align-items:center;grid-row-end:span 2}}@media (min-width:576px){.bb-transaction-item--credit-card .bb-transaction-item__status{max-width:100%;margin-bottom:0}}.bb-transaction-item--credit-card .bb-transaction-item__status-text{display:-webkit-inline-box;overflow:hidden;-webkit-box-orient:vertical;font-size:.75rem;-webkit-line-clamp:2;line-height:1.4;text-overflow:ellipsis}@media (min-width:576px){.bb-transaction-item--credit-card .bb-transaction-item__status-text{display:inline;font-size:1rem;line-height:1.1;white-space:nowrap}}.bb-transaction-item__status-suffix{display:inline;flex-shrink:0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;line-height:1rem;text-transform:uppercase;white-space:nowrap}.bb-transaction-item--credit-card .bb-transaction-item__status-suffix{margin-left:.25rem;font-size:.5625rem}@media (min-width:576px){.bb-transaction-item--credit-card .bb-transaction-item__status-suffix{font-size:.75rem}}.bb-transaction-item--credit-card :not(.bb-transaction-item__status-suffix-mobile)>.bb-transaction-item__status-suffix{display:none}@media (min-width:576px){.bb-transaction-item--credit-card :not(.bb-transaction-item__status-suffix-mobile)>.bb-transaction-item__status-suffix{display:inline}}@media (min-width:576px){.bb-transaction-item__status-suffix-mobile{display:none}}.bb-transaction-item__date{z-index:1;grid-area:date;white-space:nowrap}.bb-transaction-item--credit-card .bb-transaction-item__date{align-self:end;justify-content:flex-start;padding-bottom:0;font-size:.6875rem;line-height:1.6}@media (min-width:576px){.bb-transaction-item--credit-card .bb-transaction-item__date{justify-content:flex-end}}.bb-transaction-item__amount{z-index:1;align-items:baseline;grid-area:amount;white-space:nowrap}.bb-transaction-item--credit-card .bb-transaction-item__amount{flex-direction:column;align-items:flex-end;justify-content:center;font-size:.6875rem;line-height:1.5}@media (min-width:576px){.bb-transaction-item--credit-card .bb-transaction-item__amount{font-size:.875rem}}.bb-transaction-item--status-error .bb-transaction-item__amount{color:#ff7155}.bb-transaction-item--income .bb-transaction-item__amount{color:#0ccca2}.bb-transaction-item__button.bb-button{position:absolute;z-index:2;top:50%;right:.75rem;transition:background-color .2s,border-color .2s,transform .3s,opacity .3s}@media (min-width:992px){.bb-transaction-item__button.bb-button{opacity:0;transform:translateX(-.25rem) translateY(-50%)}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$J = undefined;
/* module identifier */
const __vue_module_identifier__$J = undefined;
/* functional template */
const __vue_is_functional_template__$J = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$J = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$J,
  staticRenderFns: __vue_staticRenderFns__$J
}, __vue_inject_styles__$J, __vue_script__$J, __vue_scope_id__$J, __vue_is_functional_template__$J, __vue_module_identifier__$J, false, createInjector, undefined, undefined);

var Icon$g = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3421315198__a bb-icon__dynamic-fill",attrs:{"d":"M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z"}})]) } };

Vue.component('icon-ui-chevron-up', Icon$g);

var Icon$f = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{staticClass:"svg2683959983__a bb-icon__dynamic-fill",attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{attrs:{"d":"M14,2L20,8V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V4A2,2 0 0,1 6,2H14M18,20V9H13V4H6V20H18M12,19L8,15H10.5V12H13.5V15H16L12,19Z"}})]) } };

Vue.component('icon-ui-file-download-outline', Icon$f);

var Icon$e = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg2469999351__a bb-icon__dynamic-fill",attrs:{"d":"M3,17V19H9V17H3M3,5V7H13V5H3M13,21V19H21V17H13V15H11V21H13M7,9V11H3V13H7V15H9V9H7M21,13V11H11V13H21M15,9H17V7H21V5H17V3H15V9Z"}})]) } };

Vue.component('icon-ui-tune', Icon$e);

var Icon$d = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","width":"83","height":"67","viewBox":"0 0 83 67"}},[_c('g',{attrs:{"fill":"none","fill-rule":"evenodd"}},[_c('path',{attrs:{"fill":"#E3E3E3","d":"M2.268,1.0658141e-13 L80.4057288,1.0658141e-13 C81.6583106,1.0658141e-13 82.6737288,1.01541819 82.6737288,2.268 L82.6737288,28.0709831 C82.6737288,29.3235649 81.6583106,30.3389831 80.4057288,30.3389831 L2.268,30.3389831 C1.01541819,30.3389831 -1.13686838e-13,29.3235649 -1.13686838e-13,28.0709831 L-1.13686838e-13,2.268 C-1.13686838e-13,1.01541819 1.01541819,1.0658141e-13 2.268,1.0658141e-13 Z"}}),_c('path',{attrs:{"fill":"#FFF","d":"M8.51694915,7 L64.6440678,7 C65.4818557,7 66.1610169,7.67916127 66.1610169,8.51694915 C66.1610169,9.35473704 65.4818557,10.0338983 64.6440678,10.0338983 L8.51694915,10.0338983 C7.67916127,10.0338983 7,9.35473704 7,8.51694915 C7,7.67916127 7.67916127,7 8.51694915,7 Z M8.51694915,13.0677966 L48.7161017,13.0677966 C49.5538896,13.0677966 50.2330508,13.7469579 50.2330508,14.5847458 C50.2330508,15.4225336 49.5538896,16.1016949 48.7161017,16.1016949 L8.51694915,16.1016949 C7.67916127,16.1016949 7,15.4225336 7,14.5847458 C7,13.7469579 7.67916127,13.0677966 8.51694915,13.0677966 Z"}}),_c('rect',{attrs:{"width":"12.894","height":"3.034","x":"7","y":"21","fill":"#FFF","rx":"1.517"}}),_c('path',{attrs:{"fill":"#E3E3E3","d":"M2.268,36 L80.4057288,36 C81.6583106,36 82.6737288,37.0154182 82.6737288,38.268 L82.6737288,64.0709831 C82.6737288,65.3235649 81.6583106,66.3389831 80.4057288,66.3389831 L2.268,66.3389831 C1.01541819,66.3389831 -1.0658141e-13,65.3235649 -1.0658141e-13,64.0709831 L-1.0658141e-13,38.268 C-1.0658141e-13,37.0154182 1.01541819,36 2.268,36 Z"}}),_c('path',{attrs:{"fill":"#FFF","d":"M8.51694915,43 C7.67916127,43 7,43.6791613 7,44.5169492 C7,45.354737 7.67916127,46.0338983 8.51694915,46.0338983 L64.6440678,46.0338983 C65.4818557,46.0338983 66.1610169,45.354737 66.1610169,44.5169492 C66.1610169,43.6791613 65.4818557,43 64.6440678,43 L8.51694915,43 Z M8.51694915,49.0677966 C7.67916127,49.0677966 7,49.7469579 7,50.5847458 C7,51.4225336 7.67916127,52.1016949 8.51694915,52.1016949 L48.7161017,52.1016949 C49.5538896,52.1016949 50.2330508,51.4225336 50.2330508,50.5847458 C50.2330508,49.7469579 49.5538896,49.0677966 48.7161017,49.0677966 L8.51694915,49.0677966 Z"}}),_c('rect',{attrs:{"width":"12.894","height":"3.034","x":"7","y":"57","fill":"#FFF","rx":"1.517"}})])]) } };

Vue.component('icon-client-empty-cards', Icon$d);

Vue.use(VueScreen, gridBreakpoints);
var script$I = {
  name: 'bb-transactions',
  components: {
    BbTransactionItem: __vue_component__$J,
    BbBox: __vue_component__$2x,
    BbButton: __vue_component__$2v,
    BbCard: __vue_component__$2t,
    BbIcon: __vue_component__$2C,
    BbLabeledValue: __vue_component__$2c,
    BbSkeleton: __vue_component__$2d,
    BbFunctionalSticky: __vue_component__$2k,
    BbDropdownItem: __vue_component__$2o,
    BbDropdown: __vue_component__$1M,
    BbSeparator: __vue_component__$2a,
    CollapseTransition
  },
  mixins: [Transactions],
  props: {
    showEmptyState: Boolean,
    emptyStateText: String,
    data: {
      type: Array,
      required: true
    },
    header: {
      type: Object
    },
    footer: {
      type: Object
    },
    debt: {
      type: Object
    },
    filters: Boolean,
    isFiltered: Boolean,
    scrollToFiltersLabel: String,
    filtersToggleLabel: {
      type: String,
      default: 'Filters'
    },
    filtersExpandLabel: {
      type: String,
      default: 'Expand filter'
    },
    filtersCollapseLabel: {
      type: String,
      default: 'Hide filter'
    },
    filtersSubmitButtonLabel: {
      type: String,
      default: 'Update statement'
    },
    filtersResetLabel: {
      type: String
    },
    filtersResetButtonLabel: {
      type: String
    },
    filtersSubmitButtonDisabled: Boolean,
    downloadButtonLabel: {
      type: String,
      validator: deprecated('`downloadButtonDisabled` will be removed from Transactions in IC 12.')
    },
    downloadButtonDisabled: {
      type: Boolean,
      validator: deprecated('`downloadButtonDisabled` will be removed from Transactions in IC 12.')
    },
    creditCardBalance: Object,
    title: String,
    titleOptions: Array,
    hidePlusMinus: Boolean,
    iconColor: {
      type: String,
      validator: type => ['navy', 'blue', 'mint', 'green',
      'red', 'gray', 'gray-90', 'gray-80', 'gray-70', 'gray-50', 'gray-40', 'gray-30', 'gray-20', 'gray-10'].includes(type)
    },
    iconFrameFilled: {
      type: Boolean,
      default: true
    },
    iconFrameColored: Boolean,
    creditCard: Boolean,
    stickyHeaderTop: String,
    stickyFooterBottom: String,
    viewport: typeof HTMLElement === 'undefined' ? null : HTMLElement,
    ariaLabels: {
      type: Object,
      default() {
        return {
          tableLabel: 'Table',
          loading: 'Loading',
          footer: 'Footer',
          header: 'Header'
        };
      }
    }
  },
  emits: ['filterSubmit', 'downloadClick', 'transactionItemClick', 'transactionItemButtonClick', 'debtClick', 'debtButtonClick', 'titleClick', 'filterReset', 'headerStuck', 'footerStuck'],
  data() {
    return {
      internalTitles: JSON.parse(JSON.stringify(this.titleOptions) || null),
      inactiveTitles: [],
      filtersExpanded: false,
      headerStuck: false,
      footerStuck: false,
      refs: {
        ...this.$refs
      },
      topStickyTransitionClass: null,
      uuid: uuid()
    };
  },
  computed: {
    renderHeaderMain() {
      return this.titleOptions || this.title || this.creditCard && !this.showFilters && this.showEmptyState && this.$slots.downloadButton || !this.creditCard && this.showFilters;
    },
    computedStartingBalanceLabel() {
      return this.$screen.sm ? this.creditCardBalance.startingBalance.label : null;
    },
    computedFinalBalanceLabel() {
      return this.$screen.sm ? this.creditCardBalance.finalBalance.label : null;
    },
    computedLabeledValueSize() {
      return this.$screen.sm ? '16' : '12';
    },
    computedListRef() {
      var _this$refs$boxEmptySt;
      return this.refs.list || ((_this$refs$boxEmptySt = this.refs.boxEmptyState) === null || _this$refs$boxEmptySt === void 0 ? void 0 : _this$refs$boxEmptySt.$el);
    },
    hasScrollToFilters() {
      return this.showFilters && notEmpty(this.scrollToFiltersLabel);
    },
    hasFinalBalance() {
      var _this$creditCardBalan;
      return (_this$creditCardBalan = this.creditCardBalance) === null || _this$creditCardBalan === void 0 ? void 0 : _this$creditCardBalan.finalBalance;
    },
    hasStartingBalance() {
      var _this$creditCardBalan2;
      return (_this$creditCardBalan2 = this.creditCardBalance) === null || _this$creditCardBalan2 === void 0 ? void 0 : _this$creditCardBalan2.startingBalance;
    },
    showFinalBalance() {
      const filtersEmptyStateUnfiltered = this.showFilters && !this.isFiltered && this.showEmptyState;
      return !filtersEmptyStateUnfiltered && this.hasFinalBalance;
    },
    hasFiltersIsFiltered() {
      return this.showFilters && this.isFiltered;
    },
    hasBalanceRowTop() {
      return this.hasFiltersIsFiltered || this.showFilters && this.showEmptyState && this.$slots.downloadButton || this.creditCard && this.hasFinalBalance && !this.showEmptyState;
    },
    hasBalanceRowBottom() {
      return this.hasStartingBalance && !this.isFiltered && !this.showEmptyState;
    },
    showHeader() {
      return this.filters || this.hasTitle;
    },
    hasTitle() {
      return this.title || this.titleOptions;
    },
    classes() {
      return ['bb-transactions', {
        'bb-transactions--top-sticky-stuck': this.headerStuck,
        'bb-transactions--bottom-sticky-stuck': this.footerStuck,
        'bb-transactions--credit-card': this.creditCard,
        'bb-transactions--empty-state': this.showEmptyState,
        'bb-transactions--is-filtered': this.isFiltered,
        'bb-transactions--has-download-button': this.$slots.downloadButton,
        'bb-transactions--has-balance-row-top': this.hasBalanceRowTop,
        'bb-transactions--has-balance-row-bottom': this.hasBalanceRowBottom,
        'bb-transactions--has-scroll-to-filters': this.hasScrollToFilters
      }];
    },
    sectionsClasses() {
      return ['bb-transactions__sections', {
        [`bb-transactions__sections--columns-${this.middleColumnCount}`]: this.middleColumnCount > 0
      }];
    },
    middleColumnCount() {
      return this.header ? this.header.middleColumns ? this.header.middleColumns.length : 0 : this.data && this.data[0].middleColumns ? this.data[0].middleColumns.length : 0;
    },
    computedFooterMiddleColumns() {
      return this.footer.middleColumns.slice(0, this.middleColumnCount);
    },
    activeTitle() {
      var _this$internalTitles;
      return ((_this$internalTitles = this.internalTitles) === null || _this$internalTitles === void 0 ? void 0 : _this$internalTitles.find(val => val.active)) || null;
    },
    showFilters() {
      var _this$activeTitle;
      return ((_this$activeTitle = this.activeTitle) === null || _this$activeTitle === void 0 ? void 0 : _this$activeTitle.filters) || this.filters;
    },
    computedFiltersExpandLabel() {
      return this.filtersExpanded ? this.filtersCollapseLabel : this.filtersExpandLabel;
    },
    filtersMenuId() {
      return `filters-menu-${this.uuid}`;
    }
  },
  watch: {
    titleOptions: {
      handler(titleOptions) {
        this.internalTitles = JSON.parse(JSON.stringify(titleOptions));
        this.setFirstTitleActive();
      },
      deep: true
    },
    internalTitles: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal) this.inactiveTitles = newVal.filter(val => !val.active);
      }
    },
    headerStuck(isStuck) {
      this.topStickyTransitionClass = isStuck && this.hasScrollToFilters ? 'slide-down-top-sticky-enter-active' : 'slide-up-top-sticky-enter-active';
    }
  },
  mounted() {
    this.updateRefs();
  },
  updated() {
    this.updateRefs();
  },
  created() {
    this.setFirstTitleActive();
  },
  methods: {
    updateRefs() {
      Object.keys({
        ...this.$refs,
        ...this.refs
      }).forEach(key => this.$set(this.refs, key, this.$refs[key]));
    },
    onScrollToFiltersClick() {
      var _this$viewport;
      const scrollTarget = this.$refs.header;
      const scrollTargetYPosition = scrollTarget.getBoundingClientRect().top;
      (_this$viewport = this.viewport) === null || _this$viewport === void 0 ? void 0 : _this$viewport.scrollBy({
        left: 0,
        top: scrollTargetYPosition,
        behavior: 'smooth'
      });
    },
    onTopStickyStuck(isStickyStuck) {
      this.headerStuck = isStickyStuck;
      this.$emit('headerStuck', isStickyStuck);
    },
    onBottomStickyStuck(isStickyStuck) {
      this.footerStuck = isStickyStuck;
      this.$emit('footerStuck', isStickyStuck);
    },
    colorizeNumber(number) {
      return number === 0 ? 'gray' : number > 0 ? 'mint' : 'red';
    },
    prependPlusOrMinus(number) {
      const formattedAmount = this.formatMoney(Math.abs(number));
      return this.hidePlusMinus || number === 0 ? `${formattedAmount}` : number > 0 ? `+ ${formattedAmount}` : `- ${formattedAmount}`;
    },
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },
    onFilterReset() {
      this.$emit('filterReset');
    },
    onFilterSubmitClick() {
      this.$emit('filterSubmit');
    },
    setFirstTitleActive() {
      if (this.internalTitles && !this.internalTitles.some(val => val.active)) {
        this.internalTitles[0].active = true;
      }
    },
    onTransactionItemClick(itemIndex) {
      this.$emit('transactionItemClick', itemIndex);
    },
    onTransactionItemButtonClick(itemIndex) {
      this.$emit('transactionItemButtonClick', itemIndex);
    },
    onDebtClick() {
      this.$emit('debtClick');
    },
    onDebtButtonClick() {
      this.$emit('debtButtonClick');
    },
    onTitleClick(title) {
      const clickedTitleIndex = this.titleOptions.findIndex(obj => obj.label === title.label);
      this.internalTitles.forEach((_internalTitle, internalTitleIndex) => {
        this.$set(this.internalTitles[internalTitleIndex], 'active', internalTitleIndex === clickedTitleIndex);
      });
      this.$emit('titleClick', title);
    }
  }
};

/* script */
const __vue_script__$I = script$I;

/* template */
var __vue_render__$I = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.ariaLabels.tableLabel,
      "aria-busy": _vm.loading
    }
  }, [!_vm.showEmptyState && _vm.debt ? _c('bb-transaction-item', _vm._b({
    staticClass: "bb-transactions__debt",
    attrs: {
      "loading": _vm.debt.loading || _vm.loading,
      "debt": "",
      "icon-frame-filled": "",
      "middle-column-count": "0"
    },
    on: {
      "itemClick": _vm.onDebtClick,
      "buttonClick": _vm.onDebtButtonClick
    }
  }, 'bb-transaction-item', Object.assign({}, {
    locale: _vm.locale,
    decimals: _vm.decimals,
    currency: _vm.currency,
    useGrouping: _vm.useGrouping,
    hidePlusMinus: _vm.hidePlusMinus
  }, _vm.debt), false)) : _vm._e(), _vm._v(" "), _vm.showHeader ? _c('header', {
    ref: "header",
    staticClass: "bb-transactions__header"
  }, [_vm.renderHeaderMain ? _c('div', {
    staticClass: "bb-transactions__header-main"
  }, [_vm.titleOptions ? _c('bb-dropdown', {
    staticClass: "bb-transactions__title-dropdown",
    attrs: {
      "autosize": ""
    },
    scopedSlots: _vm._u([{
      key: "trigger",
      fn: function () {
        return [_c('bb-button', {
          staticClass: "bb-transactions__title",
          attrs: {
            "label": _vm.activeTitle && _vm.activeTitle.label,
            "color": "gray",
            "display": "link",
            "no-underline": "",
            "no-paddings": "",
            "icon-after": "ui-chevron-down",
            "icon-after-color": "gray-70",
            "data-testid": "bb-transactions__title"
          }
        })];
      },
      proxy: true
    }], null, false, 692870597)
  }, [_vm._v(" "), _vm._l(_vm.inactiveTitles, function (inactiveTitle, index) {
    return _c('bb-dropdown-item', {
      key: index,
      attrs: {
        "label": inactiveTitle.label
      },
      on: {
        "click": function ($event) {
          return _vm.onTitleClick(inactiveTitle);
        }
      }
    });
  })], 2) : _vm.title ? _c('div', {
    staticClass: "bb-transactions__title",
    attrs: {
      "data-testid": "bb-transactions__title"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.title) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.creditCard ? [!_vm.showFilters && _vm.showEmptyState && _vm.$slots.downloadButton ? _c('div', {
    staticClass: "bb-transactions__download-button-desktop",
    attrs: {
      "data-testid": "bb-transactions__download-button-desktop"
    }
  }, [_vm._t("downloadButton")], 2) : _vm._e()] : [_vm.showFilters ? _c('bb-button', {
    attrs: {
      "label": _vm.filtersToggleLabel,
      "icon": "ui-tune",
      "color": "gray",
      "display": "narrow",
      "no-background": "",
      "data-testid": "bb-transactions__filters-toggle-button",
      "aria-controls": _vm.filtersMenuId,
      "aria-expanded": _vm.filtersExpanded
    },
    on: {
      "click": _vm.toggleFilters
    }
  }) : _vm._e()]], 2) : _vm._e(), _vm._v(" "), _vm.creditCard ? _c('bb-card', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showFilters,
      expression: "showFilters"
    }],
    staticClass: "bb-transactions__filters-card",
    attrs: {
      "size": "fluid",
      "data-testid": "bb-transactions__filters-card"
    }
  }, [_vm._t("filters"), _vm._v(" "), _c('collapse-transition', [_vm.$slots.filtersExpanded ? _c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.filtersExpanded,
      expression: "filtersExpanded"
    }],
    staticClass: "bb-transactions__filters-expanded",
    attrs: {
      "id": _vm.filtersMenuId,
      "data-testid": "bb-transactions__filters-expanded"
    }
  }, [_vm._t("filtersExpanded")], 2) : _vm._e()]), _vm._v(" "), _c('div', {
    staticClass: "bb-transactions__filters-footer",
    attrs: {
      "aria-label": _vm.ariaLabels.footer
    }
  }, [_c('bb-button', {
    attrs: {
      "label": _vm.filtersSubmitButtonLabel,
      "disabled": _vm.filtersSubmitButtonDisabled,
      "display": "blockMobileOnly",
      "color": "mint",
      "data-testid": "bb-transactions__filters-submit-button"
    },
    on: {
      "click": _vm.onFilterSubmitClick
    }
  }), _vm._v(" "), _vm.$slots.filtersExpanded ? _c('bb-button', {
    staticClass: "bb-transactions__filters-toggle-button",
    attrs: {
      "label": _vm.computedFiltersExpandLabel,
      "icon-after": "ui-chevron-down",
      "icon-after-rotate": _vm.filtersExpanded ? 'down' : 'up',
      "color": "gray",
      "size": "sm",
      "no-background": "",
      "data-testid": "bb-transactions__filters-toggle-button"
    },
    on: {
      "click": _vm.toggleFilters
    }
  }) : _vm._e()], 1)], 2) : _vm._e(), _vm._v(" "), !_vm.creditCard ? [_vm.showFilters ? _c('collapse-transition', [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.filtersExpanded,
      expression: "filtersExpanded"
    }],
    staticClass: "bb-transactions__filters-content"
  }, [_vm._t("filters")], 2)]) : _vm._e(), _vm._v(" "), !_vm.$slots.underFilters ? _c('bb-separator', {
    staticClass: "bb-transactions__filters-separator"
  }) : _vm._e()] : _vm._e()], 2) : _vm._e(), _vm._v(" "), _vm.creditCard && _vm.$slots.downloadButton ? _c('div', {
    staticClass: "bb-transactions__download-button-mobile",
    attrs: {
      "data-testid": "bb-transactions__download-button-mobile"
    }
  }, [_vm._t("downloadButton")], 2) : _vm._e(), _vm._v(" "), _vm.hasBalanceRowTop || _vm.hasScrollToFilters ? _c('bb-functional-sticky', {
    staticClass: "bb-transactions__sticky-header",
    attrs: {
      "position": "top",
      "fit-parent": "",
      "scroll-content": _vm.computedListRef,
      "sticky-area-distance": _vm.stickyHeaderTop,
      "viewport": _vm.viewport
    },
    on: {
      "stuck": _vm.onTopStickyStuck
    },
    scopedSlots: _vm._u([{
      key: "noHeight",
      fn: function () {
        return [_c('transition', {
          attrs: {
            "name": "slide-down-top-sticky"
          }
        }, [_vm.hasScrollToFilters && _vm.headerStuck ? _c('div', {
          staticClass: "bb-transactions__scroll-to-filters-wrapper"
        }, [_c('bb-button', {
          staticClass: "bb-transactions__scroll-to-filters-button",
          attrs: {
            "label": _vm.scrollToFiltersLabel,
            "display": "link",
            "no-underline": "",
            "color": "gray",
            "icon-after": "ui-chevron-up"
          },
          on: {
            "click": _vm.onScrollToFiltersClick
          }
        })], 1) : _vm._e()])];
      },
      proxy: true
    }, {
      key: "keepHeight",
      fn: function () {
        return [_c('div', {
          staticClass: "bb-transactions__balance-row-top-wrapper",
          class: _vm.topStickyTransitionClass
        }, [_vm.hasBalanceRowTop ? _c('div', {
          staticClass: "bb-transactions__balance-row bb-transactions__balance-row-top",
          attrs: {
            "data-testid": "bb-transactions__balance-row"
          }
        }, [_vm.hasFiltersIsFiltered ? [_c('span', {
          staticClass: "bb-transactions__reset-label",
          attrs: {
            "data-testid": "bb-transactions__reset-label"
          }
        }, [_vm._v(_vm._s(_vm.filtersResetLabel))]), _vm._v(" "), _c('bb-button', {
          attrs: {
            "inverted": "",
            "label": _vm.filtersResetButtonLabel,
            "color": "gray",
            "size": "sm",
            "data-testid": "bb-transactions__reset-button"
          },
          on: {
            "click": _vm.onFilterReset
          }
        }), _vm._v(" "), _vm.$slots.downloadButton ? _c('div', {
          staticClass: "bb-transactions__download-button-desktop",
          attrs: {
            "data-testid": "bb-transactions__download-button-desktop"
          }
        }, [_vm._t("downloadButton")], 2) : _vm._e()] : _vm.hasFinalBalance || _vm.$slots.downloadButton ? [_vm.loading ? _c('bb-skeleton', {
          attrs: {
            "width": "200",
            "height": "18"
          }
        }) : _vm.showFinalBalance ? _c('div', {
          staticClass: "bb-transactions__balance-row-label-wrapper"
        }, [_c('div', {
          staticClass: "bb-transactions__balance-row-label",
          attrs: {
            "title": _vm.creditCardBalance.finalBalance.title,
            "data-testid": "bb-transactions__credit-card-final-balance-title"
          }
        }, [_vm._v("\n                " + _vm._s(_vm.creditCardBalance.finalBalance.title) + "\n              ")]), _vm._v(" "), _c('div', {
          staticClass: "bb-transactions__balance-row-date"
        }, [_vm._v("\n                " + _vm._s(_vm.creditCardBalance.finalBalance.label) + "\n              ")])]) : _vm._e(), _vm._v(" "), _vm.$slots.downloadButton ? _c('div', {
          staticClass: "bb-transactions__download-button-desktop",
          attrs: {
            "data-testid": "bb-transactions__download-button-desktop"
          }
        }, [_vm._t("downloadButton")], 2) : _vm._e(), _vm._v(" "), _vm.showFinalBalance ? _c('bb-labeled-value', {
          staticClass: "bb-transactions__credit-card-final-balance",
          attrs: {
            "value": _vm.formatMoney(_vm.creditCardBalance.finalBalance.value),
            "label": _vm.computedFinalBalanceLabel,
            "loading": _vm.loading,
            "size": _vm.computedLabeledValueSize,
            "label-position": "top-right",
            "label-weight": "medium",
            "label-uppercase": "",
            "data-testid": "bb-transactions__credit-card-final-balance"
          }
        }) : _vm._e()] : _vm._e()], 2) : _vm._e()]), _vm._v(" "), _c('div', {
          staticClass: "bb-transactions__top-sticky-gradient"
        })];
      },
      proxy: true
    }], null, true)
  }) : _vm._e(), _vm._v(" "), _vm.showEmptyState ? _c('bb-box', {
    ref: "boxEmptyState",
    staticClass: "bb-transactions__empty",
    attrs: {
      "dashed": ""
    }
  }, [_c('bb-icon', {
    staticClass: "bb-transactions__empty-icon",
    attrs: {
      "name": "client-empty-cards",
      "fill": "original",
      "size": "fluid"
    }
  }), _vm._v(" "), _vm.emptyStateText ? _c('div', {
    staticClass: "bb-transactions__empty-text"
  }, [_vm._v("\n      " + _vm._s(_vm.emptyStateText) + "\n    ")]) : _vm._e()], 1) : [_vm.$slots.underFilters ? _c('div', {
    staticClass: "bb-transactions__under-filters-slot"
  }, [_vm._t("underFilters")], 2) : _vm._e(), _vm._v(" "), _c('ul', {
    ref: "list",
    staticClass: "bb-transactions__list",
    attrs: {
      "data-testid": "bb-transactions__list"
    }
  }, [!_vm.creditCard && _vm.header ? _c('li', {
    staticClass: "bb-transactions__table-header",
    class: _vm.sectionsClasses,
    attrs: {
      "aria-label": _vm.ariaLabels.header
    }
  }, [_c('div', {
    staticClass: "bb-transactions__info-column",
    attrs: {
      "title": _vm.header.info
    }
  }, [_vm._v("\n          " + _vm._s(_vm.header.info) + "\n        ")]), _vm._v(" "), _vm._l(_vm.header.middleColumns, function (section, index) {
    return _c('div', {
      key: index,
      staticClass: "bb-transactions__middle-column",
      attrs: {
        "title": section
      }
    }, [_vm._v("\n          " + _vm._s(section) + "\n        ")]);
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-transactions__last-column",
    attrs: {
      "title": _vm.header.last
    }
  }, [_vm._v("\n          " + _vm._s(_vm.header.last) + "\n        ")])], 2) : _vm._e(), _vm._v(" "), _vm._l(_vm.data, function (item, index) {
    return _c('bb-transaction-item', _vm._b({
      key: index,
      staticClass: "bb-transactions__item",
      attrs: {
        "credit-card": _vm.creditCard,
        "loading": item.loading || _vm.loading
      },
      on: {
        "itemClick": function ($event) {
          return _vm.onTransactionItemClick(index);
        },
        "buttonClick": function ($event) {
          return _vm.onTransactionItemButtonClick(index);
        }
      }
    }, 'bb-transaction-item', Object.assign({}, {
      middleColumnCount: _vm.middleColumnCount,
      locale: _vm.locale,
      decimals: _vm.decimals,
      currency: _vm.currency,
      useGrouping: _vm.useGrouping,
      hidePlusMinus: _vm.hidePlusMinus,
      iconColor: _vm.iconColor,
      iconFrameFilled: _vm.iconFrameFilled,
      iconFrameColored: _vm.iconFrameColored
    }, item), false));
  }), _vm._v(" "), !_vm.creditCard && _vm.footer ? _c('li', {
    staticClass: "bb-transactions__table-footer",
    class: _vm.sectionsClasses,
    attrs: {
      "aria-label": _vm.ariaLabels.footer
    }
  }, [_c('div', {
    staticClass: "bb-transactions__info-column",
    attrs: {
      "title": _vm.footer.info
    }
  }, [_vm._v("\n          " + _vm._s(_vm.footer.info) + "\n        ")]), _vm._v(" "), _vm._l(_vm.computedFooterMiddleColumns, function (section, index) {
    return _c('div', {
      key: index,
      staticClass: "bb-transactions__middle-column"
    }, [_vm.loading ? _c('bb-skeleton', {
      attrs: {
        "width": "100%",
        "height": "14",
        "aria-label": _vm.ariaLabels.loading
      }
    }) : [_vm._v("\n            " + _vm._s(_vm.formatMoney(section)) + "\n          ")]], 2);
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-transactions__last-column"
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "height": "14",
      "aria-label": _vm.ariaLabels.loading
    }
  }) : [_vm._v("\n            " + _vm._s(_vm.footer && _vm.formatMoney(_vm.footer.last)) + "\n          ")]], 2)], 2) : _vm._e()], 2)], _vm._v(" "), _vm.$slots.underTransactionsList ? _c('div', {
    staticClass: "bb-transactions__under-list-slot",
    attrs: {
      "data-testid": "bb-transactions__under-list-slot"
    }
  }, [_vm._t("underTransactionsList")], 2) : _vm._e(), _vm._v(" "), _vm.creditCard && !_vm.showEmptyState ? _c('bb-functional-sticky', {
    staticClass: "bb-transactions__sticky-footer",
    attrs: {
      "position": "bottom",
      "fit-parent": "",
      "scroll-content": _vm.computedListRef,
      "sticky-area-distance": _vm.stickyFooterBottom,
      "viewport": _vm.viewport
    },
    on: {
      "stuck": _vm.onBottomStickyStuck
    },
    scopedSlots: _vm._u([{
      key: "keepHeight",
      fn: function () {
        return [_c('div', {
          staticClass: "bb-transactions__bottom-sticky-gradient"
        }), _vm._v(" "), _vm.hasBalanceRowBottom ? _c('div', {
          staticClass: "bb-transactions__balance-row bb-transactions__balance-row-bottom"
        }, [_vm.loading ? _c('bb-skeleton', {
          attrs: {
            "width": "200",
            "height": "18"
          }
        }) : _c('div', {
          staticClass: "bb-transactions__balance-row-label-wrapper"
        }, [_c('div', {
          staticClass: "bb-transactions__balance-row-label",
          attrs: {
            "title": _vm.creditCardBalance.startingBalance.title,
            "data-testid": "bb-transactions__credit-card-starting-balance-title"
          }
        }, [_vm._v("\n            " + _vm._s(_vm.creditCardBalance.startingBalance.title) + "\n          ")]), _vm._v(" "), _c('div', {
          staticClass: "bb-transactions__balance-row-date"
        }, [_vm._v("\n            " + _vm._s(_vm.creditCardBalance.startingBalance.label) + "\n          ")])]), _vm._v(" "), _c('bb-labeled-value', {
          attrs: {
            "value": _vm.formatMoney(_vm.creditCardBalance.startingBalance.value),
            "label": _vm.computedStartingBalanceLabel,
            "loading": _vm.loading,
            "size": _vm.computedLabeledValueSize,
            "label-position": "top-right",
            "label-weight": "medium",
            "label-uppercase": "",
            "data-testid": "bb-transactions__credit-card-starting-balance"
          }
        })], 1) : _vm._e(), _vm._v(" "), _vm.creditCardBalance ? _c('div', {
          staticClass: "bb-transactions__balance-footer"
        }, [_c('bb-separator', {
          staticClass: "border-gray-30"
        }), _vm._v(" "), _c('div', {
          staticClass: "bb-transactions__balance-under-line"
        }, [_vm.creditCardBalance && !_vm.showEmptyState ? _c('div', {
          staticClass: "bb-transactions__balance-under-line-values"
        }, [_vm.creditCardBalance.reservations ? _c('bb-labeled-value', {
          attrs: {
            "value": _vm.formatMoney(_vm.creditCardBalance.reservations.value),
            "label": _vm.creditCardBalance.reservations.label,
            "loading": _vm.loading,
            "size": _vm.computedLabeledValueSize,
            "label-position": "top-right",
            "label-weight": "medium",
            "label-uppercase": "",
            "data-testid": "bb-transactions__credit-card-reservations"
          }
        }) : _vm._e(), _vm._v(" "), _vm.creditCardBalance.debitTurnover ? _c('bb-labeled-value', {
          attrs: {
            "value": _vm.prependPlusOrMinus(_vm.creditCardBalance.debitTurnover.value),
            "label": _vm.creditCardBalance.debitTurnover.label,
            "color": _vm.colorizeNumber(_vm.creditCardBalance.debitTurnover.value),
            "loading": _vm.loading,
            "size": _vm.computedLabeledValueSize,
            "label-position": "top-right",
            "label-weight": "medium",
            "label-uppercase": "",
            "data-testid": "bb-transactions__credit-card-debit-turnover"
          }
        }) : _vm._e(), _vm._v(" "), _vm.creditCardBalance.creditTurnover ? _c('bb-labeled-value', {
          attrs: {
            "value": _vm.prependPlusOrMinus(_vm.creditCardBalance.creditTurnover.value),
            "label": _vm.creditCardBalance.creditTurnover.label,
            "color": _vm.colorizeNumber(_vm.creditCardBalance.creditTurnover.value),
            "loading": _vm.loading,
            "size": _vm.computedLabeledValueSize,
            "label-position": "top-right",
            "label-weight": "medium",
            "label-uppercase": "",
            "data-testid": "bb-transactions__credit-card-credit-turnover"
          }
        }) : _vm._e()], 1) : _vm._e()])], 1) : _vm._e()];
      },
      proxy: true
    }], null, false, 980639201)
  }) : _vm._e()], 2);
};
var __vue_staticRenderFns__$I = [];

/* style */
const __vue_inject_styles__$I = function (inject) {
  if (!inject) return;
  inject("data-v-3141396e_0", {
    source: ".bb-transactions{display:flex;flex-direction:column}.bb-transactions__scroll-to-filters-wrapper{position:relative;z-index:1;height:0;background:#f7f7f8;text-align:center;transform:translateY(-36px);transition:transform .3s ease-in-out}@media (min-width:576px){.bb-transactions__scroll-to-filters-wrapper{transform:translateY(-56px)}}.bb-transactions--has-download-button .bb-transactions__scroll-to-filters-wrapper{transform:translateY(-36px -40px -24px)}.bb-transactions--top-sticky-stuck .bb-transactions__scroll-to-filters-wrapper{height:auto;transform:translateY(0)}.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button{height:2.25rem;padding:0 1.75rem;border-radius:0 0 .625rem .625rem;background:#fff;box-shadow:0 -.0625rem 1.0625rem rgba(0,0,0,.07);font-size:.75rem}@media (min-width:576px){.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button.bb-transactions__scroll-to-filters-button{height:3.5rem}}.bb-transactions__bottom-sticky-gradient,.bb-transactions__top-sticky-gradient{position:absolute;z-index:-1;left:0;width:100%;height:6.625rem;pointer-events:none;transition:transform .3s ease-in-out}@media (min-width:576px){.bb-transactions__bottom-sticky-gradient,.bb-transactions__top-sticky-gradient{height:8.375rem}}.bb-transactions__top-sticky-gradient{bottom:0;background:linear-gradient(0deg,rgba(247,247,248,0) 0,#f7f7f8 50%);transform:translateY(-53px)}.bb-transactions--top-sticky-stuck .bb-transactions__top-sticky-gradient{transform:translateY(53px)}@media (min-width:576px){.bb-transactions--top-sticky-stuck .bb-transactions__top-sticky-gradient{transform:translateY(67px)}}.bb-transactions__bottom-sticky-gradient{top:0;background:linear-gradient(180deg,rgba(247,247,248,0) 0,#f7f7f8 50%);transform:translateY(0)}.bb-transactions--bottom-sticky-stuck .bb-transactions__bottom-sticky-gradient{transform:translateY(-48px)}@media (min-width:576px){.bb-transactions--bottom-sticky-stuck .bb-transactions__bottom-sticky-gradient{transform:translateY(-67px)}}.bb-transactions__empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2.5rem;border-color:#9dabb9;margin-top:.5rem}.bb-transactions--credit-card .bb-transactions__empty,.bb-transactions--is-filtered .bb-transactions__empty{margin-top:0}.bb-transactions__empty-icon{max-width:5rem;margin-bottom:1.25rem}.bb-transactions__empty-text{max-width:25rem;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-align:center}.bb-transactions__debt,.bb-transactions__list{z-index:0;margin-right:-.9375rem;margin-left:-.9375rem}.bb-transactions__debt{margin-bottom:1.875rem}.bb-transactions__header{position:relative;z-index:2;width:100%;transition:background .2s}.bb-transactions__filters-card{z-index:2;max-width:100%;padding:.9375rem;margin-bottom:1.5rem}@media (min-width:768px){.bb-transactions__filters-card{padding:1.875rem}}.bb-transactions--has-balance-row-top .bb-transactions__filters-card,.bb-transactions--is-filtered .bb-transactions__filters-card{margin-bottom:0}.bb-transactions__header-main{display:flex;height:3.125rem;align-items:center;justify-content:space-between}.bb-transactions--credit-card .bb-transactions__header-main{height:6.25rem}.bb-transactions__title{color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;text-align:left}@media (min-width:768px){.bb-transactions__title{font-size:1.5rem}}.bb-transactions__reset-label{padding-right:1.25rem;color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-decoration:underline}.bb-transactions__filters-separator{margin-top:.3125rem;margin-bottom:1.25rem}.bb-transactions__filters-content{display:flex;justify-content:space-between;padding-top:.9375rem;padding-bottom:1.25rem}.bb-transactions__filters-content>*{flex:1 1 0}.bb-transactions__filters-expanded{padding-top:.625rem;padding-bottom:1.25rem}@media (min-width:768px){.bb-transactions__filters-expanded{padding-top:.9375rem}}.bb-transactions__filters-footer{display:flex;flex-direction:column;align-items:center;justify-content:space-between;padding-top:.3125rem}@media (min-width:768px){.bb-transactions__filters-footer{flex-direction:row;padding-top:1.25rem}}.bb-transactions__filters-toggle-button{margin-top:1.5rem}@media (min-width:768px){.bb-transactions__filters-toggle-button{margin-top:0}}.bb-transactions__under-filters-slot{padding-top:.9375rem;padding-bottom:1.25rem}@media (min-width:768px){.bb-transactions__credit-card-final-balance{margin-left:2rem}}.bb-transactions__item{margin-bottom:.3125rem}.bb-transactions--credit-card .bb-transactions__item{margin-bottom:0}@media (min-width:768px){.bb-transactions--credit-card .bb-transactions__item{margin-bottom:1rem}}.bb-transactions--credit-card .bb-transactions__item:last-child{margin-bottom:1rem}.bb-transactions__under-list-slot{margin-bottom:1rem}.bb-transactions--empty-state .bb-transactions__under-list-slot{display:none}.bb-transactions__balance-row-top-wrapper{position:relative;padding-top:1.5625rem;padding-bottom:.625rem;background:#f7f7f8}@media (min-width:768px){.bb-transactions__balance-row-top-wrapper{padding-top:0;padding-bottom:0}}@media (min-width:576px){.bb-transactions__balance-footer,.bb-transactions__balance-row{padding-left:3.4375rem}}.bb-transactions--empty-state .bb-transactions__balance-footer,.bb-transactions--empty-state .bb-transactions__balance-row{padding-left:0}.bb-transactions__balance-row{display:flex;align-items:center;justify-content:space-between}@media (min-width:768px){.bb-transactions__balance-row{height:6.25rem}}@media (min-width:768px){.bb-transactions--is-filtered .bb-transactions__balance-row{justify-content:flex-start}}.bb-transactions__balance-row-top{min-height:1.875rem}@media (min-width:768px){.bb-transactions__balance-row-top{min-height:unset}}.bb-transactions__sticky-header{z-index:1}.bb-transactions--has-balance-row-bottom .bb-transactions__sticky-footer{margin-top:-1rem}.bb-transactions__balance-row-bottom{padding-top:.625rem;padding-bottom:1rem;background:#f7f7f8}@media (min-width:768px){.bb-transactions__balance-row-bottom{padding-top:0;padding-bottom:0}}.bb-transactions__balance-row-label-wrapper{display:flex;flex-direction:column;align-items:flex-start}.bb-transactions__balance-row-label{margin-bottom:.25rem;color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1}@media (min-width:576px){.bb-transactions__balance-row-label{margin-bottom:0;font-size:1.125rem;line-height:1.5rem}}.bb-transactions__balance-row-date{color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;line-height:1;white-space:nowrap}@media (min-width:576px){.bb-transactions__balance-row-date{display:none}}.bb-transactions__balance-footer{background:#f7f7f8}.bb-transactions--empty-state .bb-transactions__balance-footer{display:none}@media (min-width:576px){.bb-transactions--empty-state .bb-transactions__balance-footer{display:block;margin-top:2rem}}.bb-transactions__balance-under-line{display:flex;flex-wrap:wrap;padding:1rem 0 1.25rem}.bb-transactions--empty-state .bb-transactions__balance-under-line{justify-content:flex-start}.bb-transactions__balance-under-line-values{display:flex;flex:1 1 0%;align-items:flex-end;justify-content:flex-end}.bb-transactions__balance-under-line-values>*{margin-left:1.75rem}@media (min-width:576px){.bb-transactions__balance-under-line-values>*{margin-left:2.75rem}}.bb-transactions__balance-under-line-values>:first-child{margin-left:0}.bb-transactions__download-button-mobile{z-index:2;margin-top:1.5rem}@media (min-width:768px){.bb-transactions__download-button-mobile{display:none}}.bb-transactions__download-button-desktop{display:none;margin-left:auto}@media (min-width:768px){.bb-transactions__download-button-desktop{display:block}}.bb-transactions__sections{display:grid;align-content:center;padding-right:.9375rem;padding-left:.9375rem;column-gap:.9375rem;grid-column-gap:.9375rem;grid-template-areas:\"progress date\" \"status amount\";grid-template-columns:repeat(2,1fr);padding-top:.75rem;padding-bottom:.75rem;color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-transform:uppercase}@media (min-width:576px){.bb-transactions__sections{grid-template-areas:\"icon progress date\" \"icon status amount\";grid-template-columns:2.5rem repeat(2,1fr)}}.bb-transactions__sections.bb-transaction-item--credit-card{grid-template-columns:2.5fr 1fr}.bb-transactions__sections.bb-transactions__table-footer,.bb-transactions__sections.bb-transactions__table-header{grid-template-columns:repeat(2,1fr)}@media (min-width:992px){.bb-transactions__sections.bb-transactions__table-footer,.bb-transactions__sections.bb-transactions__table-header{grid-template-columns:2.5rem repeat(5,1fr)}}@media (min-width:992px){.bb-transactions__sections--columns-3{grid-template-areas:\"icon progress mid-column mid-column mid-column date\" \"icon status mid-column mid-column mid-column amount\";grid-template-columns:2.5rem repeat(5,1fr)}}@media (min-width:992px){.bb-transactions__sections--columns-3.bb-transactions__table-footer,.bb-transactions__sections--columns-3.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column mid-column mid-column last-column\";grid-template-columns:2.5rem repeat(5,1fr)}}@media (min-width:992px){.bb-transactions__sections--columns-2{grid-template-areas:\"icon progress mid-column mid-column date\" \"icon status mid-column mid-column amount\";grid-template-columns:2.5rem repeat(4,1fr)}}@media (min-width:992px){.bb-transactions__sections--columns-2.bb-transactions__table-footer,.bb-transactions__sections--columns-2.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column mid-column last-column\";grid-template-columns:2.5rem repeat(4,1fr)}}@media (min-width:992px){.bb-transactions__sections--columns-1{grid-template-areas:\"icon progress mid-column date\" \"icon status mid-column amount\";grid-template-columns:2.5rem repeat(3,1fr)}}@media (min-width:992px){.bb-transactions__sections--columns-1.bb-transactions__table-footer,.bb-transactions__sections--columns-1.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column last-column\";grid-template-columns:2.5rem repeat(3,1fr)}}@media (min-width:768px){.bb-transactions__sections--columns-1.bb-transaction-item--credit-card{grid-template-columns:2fr .1fr .6fr}}.bb-transactions__table-header{border-bottom:.125rem solid #9dabb9;margin-bottom:.75rem;font-size:.75rem}.bb-transactions__table-footer{border-top:.125rem solid #9dabb9;margin-top:.75rem;font-size:.875rem}.bb-transactions__table-footer,.bb-transactions__table-header{grid-template-areas:\"info-column last-column\"}@media (min-width:992px){.bb-transactions__table-footer,.bb-transactions__table-header{grid-template-areas:\"info-column info-column mid-column mid-column mid-column last-column\"}}.bb-transactions__info-column,.bb-transactions__last-column{display:flex}.bb-transactions__middle-column{display:none}@media (min-width:992px){.bb-transactions__middle-column{display:flex}}.bb-transactions__info-column,.bb-transactions__last-column,.bb-transactions__middle-column{overflow:hidden;align-items:center;text-overflow:ellipsis;white-space:nowrap}.bb-transactions__last-column,.bb-transactions__middle-column{justify-content:flex-end}.bb-transactions__info-column{grid-area:info-column}.bb-transactions__last-column{grid-area:last-column}.slide-down-top-sticky-enter-active,.slide-up-top-sticky-enter-active{animation-duration:.4s;animation-fill-mode:both;animation-timing-function:ease-in-out}.slide-down-top-sticky-enter-active{animation-name:slideDownTopStickyMobile}@media (min-width:576px){.slide-down-top-sticky-enter-active{animation-name:slideDownTopStickyDesktop}}.slide-up-top-sticky-enter-active{animation-name:slideUpTopStickyMobile}@media (min-width:576px){.slide-up-top-sticky-enter-active{animation-name:slideUpTopStickyDesktop}}@keyframes slideUpTopStickyMobile{from{transform:translateY(36px)}}@keyframes slideUpTopStickyDesktop{from{transform:translateY(56px)}}@keyframes slideDownTopStickyMobile{from{transform:translateY(-36px)}}@keyframes slideDownTopStickyDesktop{from{transform:translateY(-56px)}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$I = undefined;
/* module identifier */
const __vue_module_identifier__$I = undefined;
/* functional template */
const __vue_is_functional_template__$I = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$I = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$I,
  staticRenderFns: __vue_staticRenderFns__$I
}, __vue_inject_styles__$I, __vue_script__$I, __vue_scope_id__$I, __vue_is_functional_template__$I, __vue_module_identifier__$I, false, createInjector, undefined, undefined);

dayjs.extend(customParseFormat);
var script$H = {
  name: 'bb-upload-item',
  components: {
    BbLink: __vue_component__$2p,
    BbIcon: __vue_component__$2C,
    BbButton: __vue_component__$2v,
    BbProgressBar: __vue_component__$26
  },
  props: {
    description: String,
    bytesLoaded: Number,
    downloadLink: String,
    clickable: Boolean,
    showDate: Boolean,
    file: {
      validator: val => val instanceof File
    },
    dateModifiedLabel: {
      type: String,
      default: 'Date'
    },
    errorMessage: String,
    errorFileName: String,
    hideButtons: Boolean,
    type: {
      type: String,
      default: 'li'
    },
    border: Boolean,
    docTypes: Array,
    backgroundGray: Boolean,
    removeItemAriaLabel: {
      type: String,
      default: 'Remove item'
    },
    linkAriaLabel: String
  },
  emits: ['remove', 'click'],
  computed: {
    classes() {
      return ['bb-upload-item', {
        'bb-upload-item--error': this.errorMessage,
        'bb-upload-item--border': this.border,
        'bb-upload-item--background-gray': this.backgroundGray
      }];
    },
    dateModified() {
      return dayjs(this.file.lastModified).format('DD.MM.YYYY');
    },
    fileName() {
      return this.file && this.file.name || this.errorFileName;
    },
    descriptionMessage() {
      return this.errorMessage || this.description || this.docTypes && this.docTypes.join(', ');
    }
  },
  methods: {
    downloader() {
      const url = this.downloadLink;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', url);
      link.click();
    },
    remove() {
      this.$emit('remove');
    },
    click() {
      this.$emit('click');
    }
  }
};

/* script */
const __vue_script__$H = script$H;

/* template */
var __vue_render__$H = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(_vm.type, {
    tag: "component",
    class: _vm.classes
  }, [_c('div', {
    staticClass: "bb-upload-item__wrapper"
  }, [_c('div', {
    staticClass: "bb-upload-item__text"
  }, [_vm.errorMessage ? _c('bb-icon', {
    staticClass: "bb-upload-item__error-icon",
    attrs: {
      "name": "ui-alert-circle-outline",
      "size": "24",
      "fill": "red"
    }
  }) : _vm._e(), _vm._v(" "), _vm.downloadLink || _vm.clickable ? _c('bb-link', {
    staticClass: "bb-upload-item__file-name",
    class: {
      'bb-upload-item__file-name--clickable': _vm.clickable
    },
    attrs: {
      "to": _vm.downloadLink || null,
      "color": "mint",
      "target": "_blank",
      "aria-label": _vm.linkAriaLabel,
      "download": _vm.downloadLink ? '' : null
    },
    on: {
      "click": _vm.click
    }
  }, [_vm._v("\n        " + _vm._s(_vm.fileName) + "\n      ")]) : _c('p', {
    staticClass: "bb-upload-item__file-name"
  }, [_vm._v("\n        " + _vm._s(_vm.fileName) + "\n      ")]), _vm._v(" "), _vm.descriptionMessage ? _c('div', {
    staticClass: "bb-upload-item__description",
    attrs: {
      "aria-live": "assertive"
    }
  }, [_vm._v("\n        " + _vm._s(_vm.descriptionMessage) + "\n      ")]) : _vm._e()], 1), _vm._v(" "), _vm.showDate && _vm.dateModified ? _c('div', {
    staticClass: "bb-upload-item__date"
  }, [_vm._v("\n      " + _vm._s(_vm.dateModifiedLabel) + ": "), _c('span', {
    staticClass: "f-nowrap"
  }, [_vm._v("\n        " + _vm._s(_vm.dateModified) + "\n      ")])]) : _vm._e(), _vm._v(" "), !_vm.hideButtons ? _c('div', {
    staticClass: "bb-upload-item__buttons"
  }, [_vm._t("default", function () {
    return [_c('bb-button', {
      attrs: {
        "no-paddings": "",
        "no-background": "",
        "icon": "ui-close",
        "color": "red",
        "aria-label": _vm.removeItemAriaLabel
      },
      nativeOn: {
        "click": function ($event) {
          $event.preventDefault();
          return _vm.remove.apply(null, arguments);
        }
      }
    })];
  }, {
    "downloader": _vm.downloader
  })], 2) : _vm._e()]), _vm._v(" "), _vm.bytesLoaded || _vm.bytesLoaded === 0 ? _c('bb-progress-bar', {
    staticClass: "bb-upload-item__progress-bar",
    attrs: {
      "value": _vm.bytesLoaded,
      "max": _vm.file.size,
      "color": "mint"
    }
  }) : _vm._e(), _vm._v(" "), _vm._t("footer")], 2);
};
var __vue_staticRenderFns__$H = [];

/* style */
const __vue_inject_styles__$H = function (inject) {
  if (!inject) return;
  inject("data-v-b0c707d2_0", {
    source: "@charset \"UTF-8\";.bb-upload-item{display:flex;flex-direction:column;padding:.625rem 1.875rem;list-style:none}.bb-upload-item--border{border-bottom:.0625rem solid #dee3e8}.bb-upload-item--background-gray{background-color:#f7f7f8}.bb-upload-item:not(:last-of-type){border-bottom:.0625rem solid #dee3e8}.bb-upload-item__wrapper{display:flex}.bb-upload-item__text{overflow:hidden;min-width:3rem;flex:1 0 0;text-overflow:ellipsis;white-space:nowrap}.bb-upload-item__error-icon{margin-top:-.125rem;margin-right:.3125rem}.bb-upload-item__file-name{display:inline;color:#0ccca2;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}.bb-upload-item--error .bb-upload-item__file-name{color:#ff7155}.bb-upload-item__file-name--clickable{cursor:pointer}.bb-upload-item__date,.bb-upload-item__description{color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:12px}.bb-upload-item__description{overflow:hidden;margin-bottom:.125rem;text-overflow:ellipsis}.bb-upload-item--error .bb-upload-item__description{color:#89a}.bb-upload-item__date{padding-top:.1875rem;padding-left:.625rem}.bb-upload-item__date::before{position:relative;top:.25rem;right:.3125rem;color:#bdc7d0;content:\"•\";font-size:1.5625rem;line-height:0}.bb-upload-item__buttons{display:flex;margin-left:.9375rem}.bb-upload-item__buttons .bb-button{width:1.5rem;height:1.5rem}.bb-upload-item__buttons .bb-button--square{min-height:1.5rem}.bb-upload-item__buttons .bb-button+.bb-button{margin-left:.1875rem}.bb-upload-item__buttons .bb-button>.bb-icon{margin:0}.bb-upload-item__progress-bar{height:.25rem;margin-top:.9375rem;margin-bottom:.5625rem}.bb-upload-item__progress-bar .bb-progress-bar__progress{height:.25rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$H = undefined;
/* module identifier */
const __vue_module_identifier__$H = undefined;
/* functional template */
const __vue_is_functional_template__$H = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$H = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$H,
  staticRenderFns: __vue_staticRenderFns__$H
}, __vue_inject_styles__$H, __vue_script__$H, __vue_scope_id__$H, __vue_is_functional_template__$H, __vue_module_identifier__$H, false, createInjector, undefined, undefined);

var Icon$c = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{staticClass:"application-upload",attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 40 40"}},[_c('circle',{staticClass:"application-upload__background-fill application-upload__background-scale",attrs:{"cx":"19.9","cy":"19.9","r":"19.9","fill":"#f7f7f7"}}),_c('path',{attrs:{"fill":"#8ac4ff","d":"M29.34 15L23.91 9.07 23.91 15 29.34 15z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M24.06,8.19H13.76A2.85,2.85,0,0,0,10.89,11V29a2.85,2.85,0,0,0,2.87,2.82H27.11A2.85,2.85,0,0,0,30,29V14.69Zm.21,1.38,4.45,4.88H24.27Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M29.2,29a2.08,2.08,0,0,1-2.09,2.06H13.76A2.08,2.08,0,0,1,11.67,29V11A2.08,2.08,0,0,1,13.76,9h9.73v6.27H29.2Z"}}),_c('path',{staticClass:"application-upload__arrow",attrs:{"fill":"#0087ca","d":"M23.32,17.5a4,4,0,0,0-5.73,0,4.06,4.06,0,0,0,2.87,6.92,4,4,0,0,0,2.86-1.19,4,4,0,0,0,0-5.73Zm-2.86,5.82a.63.63,0,0,1-.64-.64V19.6l-.76.76a.67.67,0,0,1-.91,0,.65.65,0,0,1,0-.91L20,17.6a.39.39,0,0,1,.1-.08l0,0,.06,0,.07,0,0,0h.25l.06,0,.06,0,.07,0,0,0a.39.39,0,0,1,.1.08l1.85,1.85a.63.63,0,0,1,0,.9.61.61,0,0,1-.45.19.63.63,0,0,1-.46-.19l-.75-.75v3.08a.64.64,0,0,1-.64.64Z"}}),_c('path',{staticClass:"application-upload__progress-loader",attrs:{"fill":"#3cc88c","d":"M15 28L15 27 26 27 26 28 15 28z"}})]) } };

Vue.component('icon-client-upload', Icon$c);

var Icon$b = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 48 42"}},[_c('g',{attrs:{"fill":"none","fill-rule":"evenodd","transform":"translate(1 4)"}},[_c('path',{attrs:{"fill":"#0087CA","d":"M4.924 6.704c-1.65 0-2.965 1.322-2.965 2.932v20.488a2.948 2.948 0 0 0 2.965 2.937h37.154c1.627 0 2.98-1.324 2.98-2.937V9.636c0-1.61-1.353-2.932-2.98-2.932H31.93c-.35 0-.67-.214-.857-.523l-2.264-4.24H18.193l-2.264 4.24c-.188.31-.507.523-.856.523H4.924zM42.078 35H4.924C2.205 35 0 32.817 0 30.124V9.636c0-2.689 2.205-4.871 4.924-4.871h9.544L16.734.523C16.92.22 17.24 0 17.591 0h11.82c.352 0 .704.22.857.523l2.264 4.242h9.546C44.797 4.765 47 6.947 47 9.636v20.488C47 32.817 44.797 35 42.078 35z"}}),_c('path',{attrs:{"fill":"#0087CA","d":"M23.488 11.99c-4.695 0-8.499 3.804-8.499 8.484 0 4.728 3.804 8.536 8.499 8.536a8.515 8.515 0 0 0 8.523-8.536c0-4.68-3.808-8.484-8.523-8.484m0 19.01C17.724 31 13 26.287 13 20.474 13 14.687 17.724 10 23.488 10 29.31 10 34 14.687 34 20.474 34 26.287 29.311 31 23.488 31"}}),_c('circle',{attrs:{"cx":"23.5","cy":"20.5","r":"5.5","fill":"#8ac4ff"}}),_c('path',{attrs:{"fill":"#3CC88C","d":"M37.528 11c-.826 0-1.528.653-1.528 1.479 0 .82.702 1.521 1.528 1.521.823 0 1.472-.702 1.472-1.521 0-.826-.649-1.479-1.472-1.479"}})])]) } };

Vue.component('icon-client-mob-capture', Icon$b);

var Icon$a = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 48 42"}},[_c('g',{attrs:{"fill":"none","fill-rule":"evenodd"}},[_c('path',{attrs:{"fill":"#0087Ca","d":"M41.563 38H7.437C6.093 38 5 36.861 5 35.462V7.538C5 6.138 6.093 5 7.438 5h34.124C42.907 5 44 6.139 44 7.538v27.924c0 1.4-1.093 2.538-2.438 2.538zm.326-8.245l-7.996-8.14a1.203 1.203 0 0 0-1.624-.091l-5.242 4.269-8.978-9.14a1.203 1.203 0 0 0-1.671-.05l-9.27 8.387V7.129h34.784l-.003 22.626zm0 2.628v3.488H7.108v-8.38l10.03-9.032 8.966 9.29a1.2 1.2 0 0 0 1.623.091l5.213-4.315 8.948 8.858z"}}),_c('path',{attrs:{"fill":"#3CC88C","d":"M32.502 17A2.503 2.503 0 0 1 30 14.502 2.505 2.505 0 0 1 32.502 12a2.5 2.5 0 0 1 0 5z"}})])]) } };

Vue.component('icon-client-mob-upload', Icon$a);

const isKey = key => key || key === 0;
const hasKey = obj => isKey(obj.key);
const defaultFileCountExceededLabel = 'File limit exceeded';
var script$G = {
  name: 'bb-upload',
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbTaskArea: __vue_component__$1T,
    BbUploadItem: __vue_component__$H
  },
  mixins: [vuelidateValidator],
  model: {
    prop: 'values',
    event: 'change'
  },
  props: {
    flavor: {
      type: String,
      default: 'admin',
      validator: f => ['admin', 'user', 'co-borrower', 'user-selfcontained'].includes(f)
    },
    values: {
      type: Array,
      default: () => []
    },
    dateModifiedLabel: {
      type: String,
      default: 'Date'
    },
    heading: {
      type: String
    },
    useCapture: {
      type: Boolean,
      default: true
    },
    captureLabel: {
      type: String,
      default: 'Capture'
    },
    chooseLabel: {
      type: String,
      default: 'Choose'
    },
    publicChooseLabel: {
      type: String,
      default: 'Tap here to choose a file'
    },
    uploadAnotherLabel: {
      type: String,
      default: 'Upload another file'
    },
    emptyDescriptionLabel: {
      type: String,
      default: 'No documents are uploaded.'
    },
    coBorrowerEmptyLabel: {
      type: String,
      default: 'Upload documents'
    },
    showDate: {
      type: Boolean
    },
    downloadOnly: {
      type: Boolean
    },
    fileCountLimit: Number,
    fileCountExceededLabel: {
      type: String,
      default: defaultFileCountExceededLabel
    },
    sizeLimit: {
      type: Number,
      default: 10
    },
    allowedTypes: Array,
    sizeLimitLabel: {
      type: String,
      default: 'File size is over allowed size'
    },
    allowedTypesLabel: {
      type: String,
      default: 'File type is not allowed'
    },
    duplicateFileLabel: {
      type: String,
      default: 'File is already uploaded'
    },
    emptyFileLabel: {
      type: String,
      default: 'The uploaded file is empty'
    },
    sections: {
      type: Array,
      default() {
        return [[]];
      }
    },
    uploadItemClassProvider: {
      type: Function,
      default: (_sectionKey, _rowIndexInSection) => ''
    },
    allowDuplicateFiles: {
      type: Boolean
    },
    allowDuplicateFilesInDifferentSections: {
      type: Boolean,
      default: true
    },
    disableDragAndDrop: Boolean,
    filenameMaxLength: {
      type: Number,
      default: 50
    },
    filenameMaxLengthLabel: {
      type: String,
      default: 'The file name is too long'
    },
    useCustomValidation: Boolean,
    baseHeadingLevel: {
      type: Number,
      default: 3,
      validator: val => val > 0 && val < 6
    }
  },
  emits: ['change', 'add', 'fileClick', 'remove'],
  data() {
    return {
      uuid: uuid(),
      multiple: true,
      dragged: false,
      files: this.values || [],
      uploadedFileMeta: {},
      cachedErrorMessageLabels: {
        sizeLimitLabel: this.sizeLimitLabel,
        allowedTypesLabel: this.allowedTypesLabel,
        duplicateFileLabel: this.duplicateFileLabel,
        emptyFileLabel: this.emptyFileLabel
      },
      val: []
    };
  },
  computed: {
    classes() {
      return {
        'bb-upload--dragged': this.dragAndDropIsEnabledAndDragged,
        'bb-upload--dragged--error': this.dragAndDropIsEnabledAndDragged && this.fileCountLimitReached,
        'bb-upload--user': this.flavor === 'user',
        'bb-upload--client': this.flavor === 'client',
        'bb-upload--co-borrower': this.flavor === 'co-borrower',
        'bb-upload--user-selfcontained': this.flavor === 'user-selfcontained',
        'bb-upload--for-public-use': this.forPublicUse,
        'bb-upload--has-files': this.hasFiles,
        'bb-upload--has-sections': this.hasSections,
        'bb-upload--using-capture': this.useCapture
      };
    },
    fileCountLimitReached() {
      return this.fileCountLimit && this.files.length >= this.fileCountLimit;
    },
    showMethodSelector() {
      return this.files.length === 0;
    },
    clickableLabelOrDiv() {
      return this.downloadOnly || this.hasSections ? 'div' : 'label';
    },
    hasCoBorrowerCTA() {
      return !this.fileCountLimitReached && this.flavor === 'co-borrower' && this.hasFiles;
    },
    hasDesktopFooterCTA() {
      return !this.fileCountLimitReached && !this.downloadOnly && !this.hasSections && this.flavor !== 'co-borrower';
    },
    hasMobileFooterCTA() {
      return !this.fileCountLimitReached && !this.downloadOnly && !this.hasSections;
    },
    hasFiles() {
      return this.files.length > 0;
    },
    hasSections() {
      return this.sections && this.sections[0] && hasKey(this.sections[0]);
    },
    hasFixedSections() {
      return this.sections.some(section => section.fixedFileCount);
    },
    fixedFileCount() {
      return this.sections.reduce((lastFileCount, section) => lastFileCount + (section.fixedFileCount || 0), 0);
    },
    latestKeyInNonFixedSections() {
      const nonFixedSections = this.sectionsWithFiles.filter(section => !section.fixedFileCount);
      const res = nonFixedSections.reduce((acc, section) => {
        const latestKeyInFiles = this.latestKeyInFiles(section.files);
        return latestKeyInFiles > acc ? latestKeyInFiles : acc;
      }, -1);
      return res;
    },
    generateFileKey() {
      const latestFileKey = !this.hasFixedSections ? this.latestKeyInFiles(this.files) : this.latestKeyInNonFixedSections > -1 ? this.latestKeyInNonFixedSections : this.fixedFileCount - 1;
      return latestFileKey + 1;
    },
    sectionsWithFiles() {
      return this.sections.reduce((acc, section) => ({
        value: [...acc.value, this.enrichSectionWithFiles(section, acc.lastFixedFileKey)],
        lastFixedFileKey: acc.lastFixedFileKey + (section.fixedFileCount || 0)
      }), {
        value: [],
        lastFixedFileKey: -1
      }).value;
    },
    wrapperComponent() {
      return this.forPublicUse ? 'div' : 'bb-task-area';
    },
    contentComponent() {
      return this.dragAndDropIsDisabledOrHasFiles ? 'div' : 'label';
    },
    contentComponentForAttribute() {
      return this.dragAndDropIsDisabledOrHasFiles ? null : `desktopUpload-${this.uuid}`;
    },
    dragAndDropIsEnabledAndDragged() {
      return !this.disableDragAndDrop && this.dragged;
    },
    dragAndDropIsDisabledOrHasFiles() {
      return this.disableDragAndDrop || this.hasFiles;
    },
    forPublicUse() {
      return ['user', 'user-selfcontained'].includes(this.flavor);
    },
    rules() {
      return {
        uploadedFilesAreValid: files => !files.some(file => file.errorFileName)
      };
    },
    hasCustomValidation() {
      return this.useCustomValidation;
    },
    erroneousFiles() {
      return this.files.filter(file => {
        var _file$errorMessages;
        return (_file$errorMessages = file.errorMessages) === null || _file$errorMessages === void 0 ? void 0 : _file$errorMessages.length;
      });
    },
    shouldHideInput() {
      return this.downloadOnly || !this.hasCoBorrowerCTA && !this.hasDesktopFooterCTA;
    },
    uploadErrorId() {
      return `bb-upload-error-${this.uuid}`;
    },
    desktopUploadId() {
      return `desktopUpload-${this.uuid}`;
    },
    cameraId() {
      return `camera-${this.uuid}`;
    },
    fileUploadId() {
      return `fileUpload-${this.uuid}`;
    },
    fileUploadNoCaptureId() {
      return `fileUploadNoCapture-${this.uuid}`;
    },
    fileUploadNativeId() {
      return `fileUploadNative-${this.uuid}`;
    }
  },
  watch: {
    fileCountLimit: {
      immediate: true,
      handler() {
        this.validatePropCombos();
      }
    },
    values: {
      deep: true,
      handler() {
        this.files = [...this.values];
      }
    },
    files: {
      handler() {
        this.val = [...this.files];
        this.validate();
      },
      immediate: true
    },
    sizeLimitLabel(newValue) {
      this.cachedErrorMessages(newValue, 'sizeLimitLabel');
    },
    allowedTypesLabel(newValue) {
      this.cachedErrorMessages(newValue, 'allowedTypesLabel');
    },
    duplicateFileLabel(newValue) {
      this.cachedErrorMessages(newValue, 'duplicateFileLabel');
    },
    emptyFileLabel(newValue) {
      this.cachedErrorMessages(newValue, 'emptyFileLabel');
    }
  },
  methods: {
    onChange(event) {
      const fileList = Array.from(event.target.files || event.dataTransfer.files);
      const section = this.hasSections && this.findSection(this.uploadedFileMeta.section);
      if (section && section.fileCountLimit) {
        const remainingFileCount = section.fileCountLimit - section.files.length;
        fileList.splice(remainingFileCount, fileList.length - remainingFileCount);
      }
      const fileCount = this.files.length;
      fileList.forEach((file, index) => this.addFile(file, section, fileCount + index));
      this.uploadedFileMeta = {};
      this.$emit('change', this.files);
      this.dragged = false;
      event.target.value = null;
    },
    addFile(file, section, index) {
      const newFile = Object.assign({
        errorMessages: []
      }, this.uploadedFileMeta);
      if (file.size === 0) {
        newFile.errorMessages.push(this.emptyFileLabel);
      }
      if (file.name.length > this.filenameMaxLength) {
        newFile.errorMessages.push(this.filenameMaxLengthLabel);
      }
      if (file.size > this.formatToBytes(this.sizeLimit)) {
        newFile.errorMessages.push(this.sizeLimitLabel);
      }
      if (this.allowedTypes && !this.allowedTypes.includes(file.type) && !this.allowedTypes.some(type => type === file.name.substring(file.name.length - type.length))) {
        newFile.errorMessages.push(this.allowedTypesLabel);
      }
      const duplicateScope = this.allowDuplicateFiles ? [] : this.hasSections && this.allowDuplicateFilesInDifferentSections ? section.files : this.files;
      const duplicates = duplicateScope.some(existingFile => {
        const existingFileName = existingFile.errorFileName || existingFile.file && existingFile.file.name;
        return existingFileName === file.name;
      });
      if (duplicates) {
        newFile.errorMessages.push(this.duplicateFileLabel);
      }
      if (index >= this.fileCountLimit) {
        newFile.errorMessages.unshift(this.fileCountExceededLabel);
      }
      if (newFile.errorMessages.length > 0) {
        newFile.errorFileName = file.name;
        newFile.errorMessage = newFile.errorMessages[0];
        if (newFile.errorMessage === this.fileCountExceededLabel) {
          newFile.errorFile = file;
        }
      } else {
        if (this.showDate) {
          newFile.showDate = true;
        }
        newFile.file = file;
      }
      if (this.$scopedSlots.itemFooter) {
        newFile.showDocTypes = true;
      }
      if (!hasKey(newFile)) {
        newFile.key = this.generateFileKey;
      }
      this.files.push(newFile);
      this.$emit('add', newFile);
      this.$nextTick(() => {
        this.$refs.root.focus();
      });
    },
    findSection(sectionKey) {
      return this.sectionsWithFiles.find(section => section.key === sectionKey);
    },
    onFileClick(bbFile) {
      this.$emit('fileClick', bbFile);
    },
    latestKeyInFiles(files) {
      return files && files.length ? Math.max.apply(null, files.map(file => file.key)) : -1;
    },
    enrichSectionWithFiles(section, lastFixedFileKey) {
      return Object.assign({}, section, {
        files: this.collectSectionFiles(section, lastFixedFileKey)
      });
    },
    collectSectionFiles(section, lastFixedFileKey) {
      const existingSectionFiles = this.files.filter(file => file.section === section.key);
      return section.fixedFileCount ? this.listFixedFilesByKey(section, lastFixedFileKey, existingSectionFiles) : existingSectionFiles;
    },
    listFixedFilesByKey(section, lastFixedFileKey, existingSectionFiles) {
      return generateRange$1(section.fixedFileCount).map(fileNumberInSection => {
        const key = lastFixedFileKey + fileNumberInSection;
        const existingFileWithKey = existingSectionFiles.find(sectionFile => +sectionFile.key === key);
        const emptyFileWithKey = {
          key
        };
        return existingFileWithKey || emptyFileWithKey;
      });
    },
    openFileDialog(fileMeta) {
      if (this.hasSections) {
        if (fileMeta && fileMeta.section) {
          const targetSection = this.findSection(fileMeta.section);
          if (!targetSection) {
            throw new TypeError('Destination section, where the file should be uploaded to, does not exist.');
          } else if (targetSection.fixedFileCount && !hasKey(fileMeta)) {
            throw new TypeError('Expected to receive the `key` for the file that is uploaded into a section with fixed length.');
          }
        } else {
          throw new TypeError('Expected to receive meta data of the section that an uploaded file belongs to. This only applies when using Upload with sections.');
        }
      }
      if (fileMeta) {
        this.uploadedFileMeta = fileMeta;
        this.multiple = !hasKey(fileMeta);
        this.multiple ? this.$refs.fileInput.setAttribute('multiple', 'multiple') : this.$refs.fileInput.removeAttribute('multiple');
      }
      this.$refs.fileInput.click();
    },
    showDocTypes(file) {
      return file.showDocTypes && !file.errorMessage;
    },
    setDocTypes(docTypes, indexFile) {
      this.$set(this.files[indexFile], 'docTypes', docTypes);
    },
    removeFile(indexInSection, fileKey) {
      if (this.hasSections && !isKey(fileKey)) {
        throw new TypeError('Removing files from section can be done only using fileKey.');
      }
      const originalFileIndex = isKey(fileKey) ? this.files.findIndex(file => file.key === fileKey) : indexInSection;
      const fileName = this.files[originalFileIndex].errorFileName || this.files[originalFileIndex].file.name;
      const section = this.hasSections && this.sectionsWithFiles.find(section => section.files.some(file => file.key === fileKey));
      const scopeOfDuplicates = section ? section.files : this.files;
      const duplicateFile = scopeOfDuplicates.find(file => file.errorFileName === fileName);
      const removedFileIndex = duplicateFile ? this.files.indexOf(duplicateFile) : originalFileIndex;
      this.$emit('remove', this.files[removedFileIndex]);
      this.files.splice(removedFileIndex, 1);
      this.reevaluateErrors();
      this.$emit('change', this.files);
      this.$nextTick(() => {
        this.$refs.root.focus();
      });
    },
    formatToBytes(MB) {
      return +MB * 1024 * 1024;
    },
    cachedErrorMessages(newMessage, key) {
      const oldMessage = this.cachedErrorMessageLabels[key];
      this.cachedErrorMessageLabels[key] = newMessage;
      this.files.forEach(file => {
        if (file.errorMessage === oldMessage) {
          file.errorMessage = newMessage;
        }
      });
    },
    reevaluateErrors() {
      this.files.forEach((file, index) => {
        if (index < this.fileCountLimit && file.errorFile) {
          file.file = file.errorFile;
          file.errorMessages = file.errorMessages.filter(message => message !== this.fileCountExceededLabel);
          delete file.errorFile;
          if (file.errorMessages.length > 0) {
            file.errorMessage = file.errorMessages[0];
          } else {
            delete file.errorMessage;
          }
        }
      });
    },
    validatePropCombos() {
      if (this.fileCountLimit > 0 && this.fileCountExceededLabel === defaultFileCountExceededLabel) {
        console.warn('Upload', 'Define translation for fileCountExceededLabel');
      }
    },
    formatFileErrorForReadability(file) {
      return [file.errorFileName, ...file.errorMessages].join(', ');
    }
  }
};

/* script */
const __vue_script__$G = script$G;

/* template */
var __vue_render__$G = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    staticClass: "bb-upload",
    class: _vm.classes,
    attrs: {
      "tabindex": "0",
      "aria-describedby": _vm.uploadErrorId
    },
    on: {
      "dragover": function ($event) {
        $event.preventDefault();
        _vm.dragged = true;
      }
    }
  }, [_c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.dragAndDropIsEnabledAndDragged,
      expression: "dragAndDropIsEnabledAndDragged"
    }],
    staticClass: "bb-upload__drag-area-overlay",
    on: {
      "dragleave": function ($event) {
        _vm.dragged = false;
      },
      "drop": function ($event) {
        $event.preventDefault();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }), _vm._v(" "), _c(_vm.wrapperComponent, {
    tag: "component",
    staticClass: "bb-upload__wrapper",
    class: {
      'bb-upload__wrapper--task-area': !_vm.forPublicUse
    },
    attrs: {
      "flavor": _vm.flavor === 'co-borrower' ? 'admin' : null,
      "title": _vm.heading
    }
  }, [_c(_vm.contentComponent, {
    tag: "component",
    staticClass: "bb-upload__content",
    attrs: {
      "for": _vm.contentComponentForAttribute
    }
  }, [_vm.forPublicUse ? _c("h" + _vm.baseHeadingLevel, {
    tag: "component",
    staticClass: "bb-upload__heading"
  }, [_vm._v("\n        " + _vm._s(_vm.heading) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.files.length || _vm.hasSections ? _c('ul', {
    staticClass: "bb-upload__files",
    attrs: {
      "aria-live": "assertive"
    }
  }, [_vm._l(_vm.sectionsWithFiles, function (section, indexSection) {
    return [section.title ? _c('li', {
      key: "sectionTitle" + indexSection,
      staticClass: "bb-upload__section-title-item"
    }, [_c("h" + (_vm.baseHeadingLevel + 1), {
      tag: "component",
      staticClass: "bb-upload__section-title"
    }, [_vm._v("\n              " + _vm._s(section.title) + "\n            ")])], 1) : _vm._e(), _vm._v(" "), _vm._l(section.files, function (bbFile, indexFile) {
      return [bbFile.file || bbFile.errorFileName ? _c('bb-upload-item', _vm._b({
        key: "section" + indexSection + "file" + indexFile,
        class: _vm.uploadItemClassProvider(section.key, indexFile),
        attrs: {
          "date-modified-label": _vm.dateModifiedLabel,
          "border": _vm.forPublicUse,
          "hide-buttons": _vm.showDocTypes(bbFile),
          "background-gray": _vm.showDocTypes(bbFile)
        },
        on: {
          "remove": function ($event) {
            return _vm.removeFile(indexFile, bbFile.key);
          },
          "click": function () {
            return _vm.onFileClick(bbFile);
          }
        },
        scopedSlots: _vm._u([{
          key: "default",
          fn: function () {
            return [_vm._t("itemButtons", null, {
              "file": bbFile,
              "indexFile": indexFile,
              "section": section,
              "removeFile": _vm.removeFile
            })];
          },
          proxy: true
        }, {
          key: "footer",
          fn: function () {
            return [_vm._t("itemFooter", null, {
              "docTypes": bbFile.docTypes,
              "showDocTypes": bbFile.showDocTypes,
              "setDocTypes": _vm.setDocTypes,
              "removeFile": _vm.removeFile,
              "file": bbFile,
              "indexFile": indexFile
            })];
          },
          proxy: true
        }], null, true)
      }, 'bb-upload-item', bbFile, false)) : _vm._t("uploadCTA", null, {
        "file": bbFile,
        "indexFile": indexFile,
        "section": section,
        "openFileDialog": _vm.openFileDialog
      })];
    }), _vm._v(" "), _vm._t(section.key + "Footer", function () {
      return [_vm._t("sectionFooter", null, {
        "section": section,
        "openFileDialog": _vm.openFileDialog
      })];
    }, {
      "section": section,
      "openFileDialog": _vm.openFileDialog
    })];
  })], 2) : _vm._e(), _vm._v(" "), _vm._t("footer"), _vm._v(" "), _c(_vm.clickableLabelOrDiv, {
    tag: "component",
    staticClass: "bb-upload__label",
    class: {
      'hidden': _vm.shouldHideInput
    },
    attrs: {
      "for": _vm.desktopUploadId
    }
  }, [!_vm.files.length && !_vm.hasSections ? _c('div', {
    staticClass: "bb-upload__placeholder"
  }, [_vm.forPublicUse ? [!_vm.dragged ? _c('bb-icon', {
    staticClass: "bb-upload__icon",
    attrs: {
      "name": "client-upload",
      "size": "fluid",
      "fill": "default"
    }
  }) : _vm._e(), _vm._v(" "), _vm.dragged ? _c('bb-icon', {
    staticClass: "bb-upload__icon",
    attrs: {
      "animated": "",
      "name": "client-upload",
      "size": "fluid",
      "fill": "default"
    }
  }) : _vm._e()] : _vm.flavor === 'co-borrower' ? _c('div', {
    staticClass: "bb-upload__add-file bb-upload__add-file--no-border"
  }, [_c('bb-icon', {
    attrs: {
      "name": "ui-plus-circle-outline",
      "size": "24",
      "fill": "mint"
    }
  }), _vm._v(" "), _c('span', {
    staticClass: "bb-upload__add-file-text"
  }, [_vm._v("\n              " + _vm._s(_vm.coBorrowerEmptyLabel) + "\n            ")])], 1) : _c('p', [_vm._v("\n            " + _vm._s(_vm.emptyDescriptionLabel) + "\n          ")])], 2) : _vm._e(), _vm._v(" "), _vm.hasDesktopFooterCTA ? _c('div', {
    ref: "browseBar",
    staticClass: "bb-upload__footer-cta"
  }, [_vm._t("browseLabel", function () {
    return [_vm._v("\n            Drag a file here or\n            "), _vm._v(" "), _c('span', {
      staticClass: "bb-upload__highlight"
    }, [_vm._v("browse")]), _vm._v("\n            a file to upload\n          ")];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasCoBorrowerCTA ? _c('div', {
    staticClass: "bb-upload__add-file bb-upload__add-file--center"
  }, [_c('bb-icon', {
    attrs: {
      "name": "ui-plus-circle-outline",
      "size": "24",
      "fill": "mint"
    }
  }), _vm._v(" "), _c('span', {
    staticClass: "bb-upload__add-file-text"
  }, [_vm._v("\n            " + _vm._s(_vm.uploadAnotherLabel) + "\n          ")])], 1) : _vm._e(), _vm._v(" "), !_vm.downloadOnly ? _c('input', {
    ref: "fileInput",
    staticClass: "bb-upload__input",
    attrs: {
      "id": _vm.desktopUploadId,
      "multiple": _vm.multiple,
      "type": "file"
    },
    on: {
      "change": function ($event) {
        $event.preventDefault();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }) : _vm._e()])], 2), _vm._v(" "), _c('div', {
    staticClass: "bb-upload__content bb-upload__content--mobile"
  }, [_vm.flavor === 'user' || _vm.flavor === 'user-selfcontained' && _vm.useCapture ? _c("h" + _vm.baseHeadingLevel, {
    tag: "component",
    staticClass: "bb-upload__heading"
  }, [_vm._v("\n        " + _vm._s(_vm.heading) + "\n      ")]) : _vm._e(), _vm._v(" "), _vm.files.length || _vm.hasSections ? _c('ul', {
    staticClass: "bb-upload__files"
  }, [_vm._l(_vm.sectionsWithFiles, function (section, indexSection) {
    return [section.title ? _c('li', {
      key: "mobileSectionTitle" + indexSection,
      staticClass: "bb-upload__section-title-item"
    }, [_c("h" + (_vm.baseHeadingLevel + 1), {
      tag: "component",
      staticClass: "bb-upload__section-title"
    }, [_vm._v("\n              " + _vm._s(section.title) + "\n            ")])], 1) : _vm._e(), _vm._v(" "), _vm._l(section.files, function (bbFile, indexFile) {
      return [bbFile.file || bbFile.errorFileName ? _c('bb-upload-item', _vm._b({
        key: "mobileSection" + indexSection + "file" + indexFile,
        class: _vm.uploadItemClassProvider(section.key, indexFile),
        attrs: {
          "date-modified-label": _vm.dateModifiedLabel,
          "border": _vm.forPublicUse,
          "hide-buttons": _vm.showDocTypes(bbFile),
          "background-gray": _vm.showDocTypes(bbFile)
        },
        on: {
          "remove": function ($event) {
            return _vm.removeFile(indexFile, bbFile.key);
          },
          "click": function () {
            return _vm.onFileClick(bbFile);
          }
        },
        scopedSlots: _vm._u([{
          key: "default",
          fn: function () {
            return [_vm._t("itemButtons", null, {
              "file": bbFile,
              "indexFile": indexFile,
              "section": section,
              "removeFile": _vm.removeFile
            })];
          },
          proxy: true
        }, {
          key: "footer",
          fn: function () {
            return [_vm._t("itemFooter", null, {
              "docTypes": bbFile.docTypes,
              "showDocTypes": bbFile.showDocTypes,
              "setDocTypes": _vm.setDocTypes,
              "removeFile": _vm.removeFile,
              "file": bbFile,
              "indexFile": indexFile
            })];
          },
          proxy: true
        }], null, true)
      }, 'bb-upload-item', bbFile, false)) : _vm._t("uploadCTA", null, {
        "file": bbFile,
        "indexFile": indexFile,
        "section": section,
        "openFileDialog": _vm.openFileDialog
      })];
    }), _vm._v(" "), _vm._t(section.key + "Footer", function () {
      return [_vm._t("sectionFooter", null, {
        "section": section,
        "openFileDialog": _vm.openFileDialog
      })];
    }, {
      "section": section,
      "openFileDialog": _vm.openFileDialog
    })];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasMobileFooterCTA ? [_vm.showMethodSelector ? [_vm.useCapture ? _c('div', {
    staticClass: "bb-upload__icon-container"
  }, [_c('label', {
    staticClass: "bb-upload__icon",
    attrs: {
      "for": _vm.cameraId
    }
  }, [_c('input', {
    staticClass: "bb-upload__input",
    attrs: {
      "id": _vm.cameraId,
      "type": "file",
      "accept": "image/*",
      "capture": "camera"
    },
    on: {
      "change": function ($event) {
        $event.preventDefault();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "client-mob-capture",
      "size": "64",
      "fill": "blue"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "bb-upload__choose-capture-label"
  }, [_vm._v("\n                " + _vm._s(_vm.captureLabel) + "\n              ")])], 1), _vm._v(" "), _c('label', {
    staticClass: "bb-upload__icon",
    attrs: {
      "for": _vm.fileUploadId
    }
  }, [_c('input', {
    staticClass: "bb-upload__input",
    attrs: {
      "id": _vm.fileUploadId,
      "multiple": _vm.multiple,
      "type": "file"
    },
    on: {
      "change": function ($event) {
        $event.preventDefault();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "client-mob-upload",
      "size": "64",
      "fill": "blue"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "bb-upload__choose-capture-label"
  }, [_vm._v("\n                " + _vm._s(_vm.chooseLabel) + "\n              ")])], 1)]) : [_c('label', {
    staticClass: "bb-upload__upload-no-capture",
    attrs: {
      "for": _vm.fileUploadNoCaptureId
    }
  }, [_c('input', {
    staticClass: "bb-upload__input",
    attrs: {
      "id": _vm.fileUploadNoCaptureId,
      "multiple": _vm.multiple,
      "type": "file"
    },
    on: {
      "change": function ($event) {
        $event.preventDefault();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }), _vm._v(" "), _c('bb-icon', {
    staticClass: "bb-upload__icon",
    attrs: {
      "name": "client-upload",
      "size": "fluid",
      "fill": "default"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "bb-upload__choose-capture-label"
  }, [_vm._v("\n                " + _vm._s(_vm.publicChooseLabel) + "\n              ")])], 1)]] : _vm.hasFiles ? [_c('label', {
    staticClass: "bb-upload__add-file",
    attrs: {
      "for": _vm.fileUploadNativeId
    }
  }, [_c('input', {
    staticClass: "bb-upload__input",
    attrs: {
      "id": _vm.fileUploadNativeId,
      "multiple": _vm.multiple,
      "type": "file"
    },
    on: {
      "change": function ($event) {
        $event.preventDefault();
        return _vm.onChange.apply(null, arguments);
      }
    }
  }), _vm._v(" "), _c('bb-icon', {
    attrs: {
      "name": "ui-plus-circle-outline",
      "size": "24",
      "fill": "mint"
    }
  }), _vm._v(" "), _c('span', {
    staticClass: "bb-upload__add-file-text"
  }, [_vm._v("\n              " + _vm._s(_vm.uploadAnotherLabel) + "\n            ")])], 1)] : _vm._e()] : _vm._e(), _vm._v(" "), _vm._t("footer")], 2)], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-upload__screenreader-errors",
    attrs: {
      "id": _vm.uploadErrorId,
      "aria-live": "assertive",
      "role": "alert"
    }
  }, _vm._l(_vm.erroneousFiles, function (file, index) {
    return _c('p', {
      key: index
    }, [_vm._v("\n      " + _vm._s(_vm.formatFileErrorForReadability(file)) + "\n    ")]);
  }), 0)], 1);
};
var __vue_staticRenderFns__$G = [];

/* style */
const __vue_inject_styles__$G = function (inject) {
  if (!inject) return;
  inject("data-v-56fd55e6_0", {
    source: ".bb-upload__input,.bb-upload__screenreader-errors{position:absolute!important;overflow:hidden!important;width:1px!important;height:1px!important;padding:0!important;border:0!important;margin:-1px!important;clip:rect(0 0 0 0)!important;clip-path:inset(50%)!important;white-space:nowrap!important}.bb-upload{position:relative}.bb-upload+.bb-upload{margin-top:1.25rem}.bb-upload--user{margin:-1.875rem}.bb-upload__drag-area-overlay{position:absolute;z-index:1500;inset:0}.bb-upload__content{display:none;flex-direction:column;align-items:center;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}@media (min-width:768px){.bb-upload__content{display:flex}}label.bb-upload__content{cursor:pointer}.bb-upload__content:not(.bb-upload__content--mobile){margin:-1.25rem -1.875rem}.bb-upload--for-public-use .bb-upload__content:not(.bb-upload__content--mobile){padding-top:1.875rem;margin:0}.bb-upload__content--mobile{display:flex;justify-content:center}@media (min-width:768px){.bb-upload__content--mobile{display:none}}.bb-upload__wrapper--task-area .bb-upload__content--mobile{padding:1.25rem 0}.bb-upload--has-files .bb-upload__wrapper--task-area .bb-upload__content--mobile{padding:0}.bb-upload--for-public-use.bb-upload--using-capture .bb-upload__content--mobile{padding-top:1.5rem}.bb-upload--for-public-use.bb-upload--using-capture:not(.bb-upload--has-sections):not(.bb-upload--has-files) .bb-upload__content--mobile{padding-bottom:1.875rem}.bb-upload:not(.bb-upload--for-public-use) .bb-upload__content--mobile{margin:0 -1.875rem}.bb-upload__label{display:flex;width:100%;flex-direction:column;align-items:center}.bb-upload__highlight{border-bottom:.0625rem solid #687d92;color:#0ccca2}.bb-upload__upload-no-capture{width:100%;padding:1.875rem 0;text-align:center}.bb-upload__icon{width:5rem;height:5rem}@media (max-width:768px){.bb-upload--using-capture .bb-upload__icon{width:50%;height:auto;padding:1.25rem;text-align:center}.bb-upload--using-capture .bb-upload__icon+.bb-upload__icon{border-left:.0625rem solid #dee3e8}}.bb-upload__placeholder{margin:1.875rem}.bb-upload__footer-cta{width:100%;padding:1.25rem;border-top:.0625rem dashed #9dabb9;background-color:#f7f7f8;border-bottom-left-radius:.1875rem;border-bottom-right-radius:.1875rem;font-size:.875rem;text-align:center}.bb-upload--user .bb-upload__footer-cta{border:.0625rem dashed #9dabb9}.bb-upload--user.bb-upload--dragged .bb-upload__footer-cta{border:none;border-top:.125rem dashed #5ea6ed}.bb-upload--dragged .bb-upload__footer-cta{border-top:.125rem dashed #5ea6ed}.bb-upload--dragged--error .bb-upload__footer-cta{border-top:.125rem dashed #ff7155}.bb-upload--for-public-use.bb-upload--has-files .bb-upload__footer-cta{margin-top:1.25rem}.bb-upload--user-selfcontained .bb-upload__wrapper{border:.0625rem dashed #9dabb9;border-radius:.1875rem}.bb-upload--dragged .bb-upload__wrapper{border:.125rem dashed #5ea6ed}.bb-upload--dragged--error .bb-upload__wrapper{border:.125rem dashed #ff7155}.bb-upload--user.bb-upload--dragged .bb-upload__wrapper{border-radius:.1875rem;border-top-left-radius:.625rem;border-top-right-radius:.625rem}.bb-upload__choose-capture-label{color:#2485e5}.bb-upload:not(.bb-upload--using-capture) .bb-upload__choose-capture-label{margin-top:.9375rem}.bb-upload__icon-container{display:flex;width:100%;font-size:.875rem}.bb-upload__heading{padding-right:1.25rem;padding-left:1.25rem;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;line-height:1.5;text-align:center}@media (min-width:768px){.bb-upload--for-public-use.bb-upload--has-sections .bb-upload__heading{margin-bottom:1.875rem}}.bb-upload__content--mobile .bb-upload__heading{margin-bottom:1.25rem}.bb-upload__section-title{padding:.375rem 1.875rem .375rem;border-top:.0625rem solid #dee3e8;margin-bottom:0;background:#f7f7f8;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.6875rem;line-height:1.5rem;text-transform:uppercase}.bb-upload__files{display:flex;width:100%;flex:0 0 auto;flex-direction:column}.bb-upload__add-file{width:100%;padding:.625rem 0;border:none;border-top:.0625rem dashed #9dabb9;background:0 0;cursor:pointer;font-size:.875rem;text-align:center}.bb-upload__add-file--no-border{border:none}.bb-upload__add-file--center{display:flex;align-items:center;justify-content:center}.bb-upload__add-file-text{margin-left:.625rem;color:#0ccca2}.bb-upload--has-files .bb-upload__add-file-text{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$G = undefined;
/* module identifier */
const __vue_module_identifier__$G = undefined;
/* functional template */
const __vue_is_functional_template__$G = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$G = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$G,
  staticRenderFns: __vue_staticRenderFns__$G
}, __vue_inject_styles__$G, __vue_script__$G, __vue_scope_id__$G, __vue_is_functional_template__$G, __vue_module_identifier__$G, false, createInjector, undefined, undefined);

const FLAT_VALUE_SEPARATOR = '___';
const toUpperCase = value => {
  var _value$toUpperCase, _value$toUpperCase2;
  return (_value$toUpperCase = value === null || value === void 0 ? void 0 : (_value$toUpperCase2 = value.toUpperCase) === null || _value$toUpperCase2 === void 0 ? void 0 : _value$toUpperCase2.call(value)) !== null && _value$toUpperCase !== void 0 ? _value$toUpperCase : value;
};
var script$F = {
  name: 'bb-memo-fields-flat-channel-and-direction',
  components: {
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbRadio: __vue_component__$12,
    BbSelect: __vue_component__$20
  },
  mixins: [validator],
  props: {
    props: Object
  },
  emits: ['input'],
  data() {
    var _this$props$flatChann, _this$props$contactCh, _this$props$contactDi, _this$props$isReached;
    return {
      flatChannelAndDirection: (_this$props$flatChann = this.props.flatChannelAndDirectionRadioProps) === null || _this$props$flatChann === void 0 ? void 0 : _this$props$flatChann.value,
      models: {
        contactChannel: (_this$props$contactCh = this.props.contactChannelSelectProps) === null || _this$props$contactCh === void 0 ? void 0 : _this$props$contactCh.value,
        contactDirection: (_this$props$contactDi = this.props.contactDirectionRadioProps) === null || _this$props$contactDi === void 0 ? void 0 : _this$props$contactDi.value,
        isReached: (_this$props$isReached = this.props.isReachedRadioProps) === null || _this$props$isReached === void 0 ? void 0 : _this$props$isReached.value
      }
    };
  },
  computed: {
    showReachedRadio() {
      return ['PHONE', 'CALL']
      .includes(toUpperCase(this.models.contactChannel)) && 'OUT' === toUpperCase(this.models.contactDirection);
    },
    showContactDirection() {
      return 'INTERNAL' !== toUpperCase(this.models.contactChannel);
    },
    flatChannelAndDirectionRadioOptions() {
      var _this$props$contactCh2, _this$props$contactCh3;
      return ((_this$props$contactCh2 = this.props.contactChannelSelectProps) === null || _this$props$contactCh2 === void 0 ? void 0 : (_this$props$contactCh3 = _this$props$contactCh2.options) === null || _this$props$contactCh3 === void 0 ? void 0 : _this$props$contactCh3.reduce((acc, channelOption) => {
        var _this$props$contactDi2, _this$props$contactDi3;
        return [...acc, ...(this.props.channelsWithoutDirection.includes(toUpperCase(channelOption.value)) ? [channelOption] : ((_this$props$contactDi2 = this.props.contactDirectionRadioProps) === null || _this$props$contactDi2 === void 0 ? void 0 : (_this$props$contactDi3 = _this$props$contactDi2.options) === null || _this$props$contactDi3 === void 0 ? void 0 : _this$props$contactDi3.map(directionOption => ({
          text: `${channelOption.text} ${directionOption.text}`,
          value: `${channelOption.value}${FLAT_VALUE_SEPARATOR}${directionOption.value}`
        }))) || [])];
      }, [])) || [];
    }
  },
  methods: {
    syncModelForIsReached() {
      if (!this.showReachedRadio) {
        this.models.isReached = null;
      }
    },
    syncModelForContactDirection() {
      if (!this.showContactDirection) {
        this.models.contactDirection = null;
      }
    },
    onContactDirectionInput() {
      this.syncModelForIsReached();
      this.emitInput();
    },
    onContactChannelInput() {
      this.syncModelForContactDirection();
      this.syncModelForIsReached();
      this.emitInput();
    },
    onFlatChannelAndDirectionRadioInput() {
      const [contactChannel, contactDirection] = this.flatChannelAndDirection.split(FLAT_VALUE_SEPARATOR);
      this.models.contactChannel = contactChannel;
      this.models.contactDirection = contactDirection;
      this.syncModelForIsReached();
      this.emitInput();
    },
    emitInput() {
      this.$emit('input', this.models);
    }
  }
};

/* script */
const __vue_script__$F = script$F;

/* template */
var __vue_render__$F = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-memo-fields-flat-channel-and-direction"
  }, [_vm.props.flatChannelAndDirectionRadioProps ? _c('bb-radio', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.flatChannelAndDirectionRadioProps && _vm.props.flatChannelAndDirectionRadioProps.validate,
      expression: "props.flatChannelAndDirectionRadioProps && props.flatChannelAndDirectionRadioProps.validate"
    }],
    attrs: {
      "options": _vm.flatChannelAndDirectionRadioOptions,
      "name": "flatChannelAndDirection"
    },
    on: {
      "input": _vm.onFlatChannelAndDirectionRadioInput
    },
    model: {
      value: _vm.flatChannelAndDirection,
      callback: function ($$v) {
        _vm.flatChannelAndDirection = $$v;
      },
      expression: "flatChannelAndDirection"
    }
  }, 'bb-radio', _vm.props.flatChannelAndDirectionRadioProps, false)) : _c('bb-row', {
    attrs: {
      "gutter": "20"
    }
  }, [_c('bb-col', {
    attrs: {
      "align-self": "end"
    }
  }, [_c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.contactChannelSelectProps && _vm.props.contactChannelSelectProps.validate,
      expression: "props.contactChannelSelectProps && props.contactChannelSelectProps.validate"
    }],
    staticClass: "bb-memo-fields-flat-channel-and-direction__contact-channel-select",
    attrs: {
      "name": "contactChannel",
      "framed": ""
    },
    on: {
      "input": _vm.onContactChannelInput
    },
    model: {
      value: _vm.models.contactChannel,
      callback: function ($$v) {
        _vm.$set(_vm.models, "contactChannel", $$v);
      },
      expression: "models.contactChannel"
    }
  }, 'bb-select', _vm.props.contactChannelSelectProps, false))], 1), _vm._v(" "), _c('bb-col', [_vm.showContactDirection ? _c('bb-radio', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.contactDirectionRadioProps && _vm.props.contactDirectionRadioProps.validate,
      expression: "props.contactDirectionRadioProps && props.contactDirectionRadioProps.validate"
    }],
    attrs: {
      "name": "contactDirection",
      "equal-width": "",
      "small-label": "",
      "square": ""
    },
    on: {
      "input": _vm.onContactDirectionInput
    },
    model: {
      value: _vm.models.contactDirection,
      callback: function ($$v) {
        _vm.$set(_vm.models, "contactDirection", $$v);
      },
      expression: "models.contactDirection"
    }
  }, 'bb-radio', _vm.props.contactDirectionRadioProps, false)) : _vm._e()], 1)], 1), _vm._v(" "), _vm.showReachedRadio ? _c('bb-radio', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.isReachedRadioProps && _vm.props.isReachedRadioProps.validate,
      expression: "props.isReachedRadioProps && props.isReachedRadioProps.validate"
    }],
    staticClass: "bb-memo-fields-flat-channel-and-direction__reached-radio",
    attrs: {
      "name": "isReached",
      "equal-width": "",
      "square": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.isReached,
      callback: function ($$v) {
        _vm.$set(_vm.models, "isReached", $$v);
      },
      expression: "models.isReached"
    }
  }, 'bb-radio', _vm.props.isReachedRadioProps, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$F = [];

/* style */
const __vue_inject_styles__$F = function (inject) {
  if (!inject) return;
  inject("data-v-737aa3f0_0", {
    source: ".bb-memo-fields-flat-channel-and-direction .bb-memo-fields-flat-channel-and-direction__reached-radio .bb-radio__button:not(.is-disabled):hover:first-child .bb-radio__text{border-color:#0ccca2;background-color:#0ccca2}.bb-memo-fields-flat-channel-and-direction .bb-memo-fields-flat-channel-and-direction__reached-radio .bb-radio__button:not(.is-disabled):hover:last-child .bb-radio__text{border-color:#ff7155;background-color:#ff7155}.bb-memo-fields-flat-channel-and-direction .bb-memo-fields-flat-channel-and-direction__reached-radio .bb-radio__button:not(.is-disabled):first-child .bb-radio__input:checked+.bb-radio__text,.bb-memo-fields-flat-channel-and-direction .bb-memo-fields-flat-channel-and-direction__reached-radio .bb-radio__button:not(.is-disabled):first-child .bb-radio__input:focus+.bb-radio__text{border-color:#0ccca2;background-color:#0ccca2}.bb-memo-fields-flat-channel-and-direction .bb-memo-fields-flat-channel-and-direction__reached-radio .bb-radio__button:not(.is-disabled):last-child .bb-radio__input:checked+.bb-radio__text,.bb-memo-fields-flat-channel-and-direction .bb-memo-fields-flat-channel-and-direction__reached-radio .bb-radio__button:not(.is-disabled):last-child .bb-radio__input:focus+.bb-radio__text{border-color:#ff7155;background-color:#ff7155}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$F = undefined;
/* module identifier */
const __vue_module_identifier__$F = undefined;
/* functional template */
const __vue_is_functional_template__$F = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$F = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$F,
  staticRenderFns: __vue_staticRenderFns__$F
}, __vue_inject_styles__$F, __vue_script__$F, __vue_scope_id__$F, __vue_is_functional_template__$F, __vue_module_identifier__$F, false, createInjector, undefined, undefined);

var script$E = {
  name: 'bb-memo-fieldset-default',
  components: {
    BbDatepicker: __vue_component__$1o,
    BbCheckbox: __vue_component__$2s,
    BbTextarea: __vue_component__$1S,
    BbSelect: __vue_component__$20,
    BbUpload: __vue_component__$G,
    BbMemoFieldsFlatChannelAndDirection: __vue_component__$F
  },
  mixins: [validator],
  props: {
    props: Object
  },
  emits: ['input'],
  data() {
    var _this$props$topicSele, _this$props$taskNameS, _this$props$contactCh, _this$props$contactDi, _this$props$isReached, _this$props$commentTe, _this$props$validToDa, _this$props$uploadPro, _this$props$isUrgentC;
    return {
      models: {
        topic: (_this$props$topicSele = this.props.topicSelectProps) === null || _this$props$topicSele === void 0 ? void 0 : _this$props$topicSele.value,
        taskName: (_this$props$taskNameS = this.props.taskNameSelectProps) === null || _this$props$taskNameS === void 0 ? void 0 : _this$props$taskNameS.value,
        contactChannel: (_this$props$contactCh = this.props.contactChannelSelectProps) === null || _this$props$contactCh === void 0 ? void 0 : _this$props$contactCh.value,
        contactDirection: (_this$props$contactDi = this.props.contactDirectionRadioProps) === null || _this$props$contactDi === void 0 ? void 0 : _this$props$contactDi.value,
        isReached: (_this$props$isReached = this.props.isReachedRadioProps) === null || _this$props$isReached === void 0 ? void 0 : _this$props$isReached.value,
        comment: (_this$props$commentTe = this.props.commentTextareaProps) === null || _this$props$commentTe === void 0 ? void 0 : _this$props$commentTe.value,
        validToDate: (_this$props$validToDa = this.props.validToDatepickerProps) === null || _this$props$validToDa === void 0 ? void 0 : _this$props$validToDa.value,
        upload: (_this$props$uploadPro = this.props.uploadProps) === null || _this$props$uploadPro === void 0 ? void 0 : _this$props$uploadPro.value,
        isUrgent: (_this$props$isUrgentC = this.props.isUrgentCheckboxProps) === null || _this$props$isUrgentC === void 0 ? void 0 : _this$props$isUrgentC.value
      }
    };
  },
  beforeMount() {
    this.emitInput();
  },
  methods: {
    emitInput() {
      this.$emit('input', this.models);
    },
    onFlatChannelAndDirectionInput(models) {
      Object.assign(this.models, models);
      this.emitInput();
    }
  }
};

/* script */
const __vue_script__$E = script$E;

/* template */
var __vue_render__$E = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-memo-fieldset-default"
  }, [_vm.props.topicSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.topicSelectProps && _vm.props.topicSelectProps.validate,
      expression: "props.topicSelectProps && props.topicSelectProps.validate"
    }],
    attrs: {
      "name": "topic",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.topic,
      callback: function ($$v) {
        _vm.$set(_vm.models, "topic", $$v);
      },
      expression: "models.topic"
    }
  }, 'bb-select', _vm.props.topicSelectProps, false)) : _vm._e(), _vm._v(" "), _vm.props.taskNameSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.taskNameSelectProps && _vm.props.taskNameSelectProps.validate,
      expression: "props.taskNameSelectProps && props.taskNameSelectProps.validate"
    }],
    attrs: {
      "name": "taskName",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.taskName,
      callback: function ($$v) {
        _vm.$set(_vm.models, "taskName", $$v);
      },
      expression: "models.taskName"
    }
  }, 'bb-select', _vm.props.taskNameSelectProps, false)) : _vm._e(), _vm._v(" "), _c('bb-memo-fields-flat-channel-and-direction', {
    attrs: {
      "props": _vm.props
    },
    on: {
      "input": _vm.onFlatChannelAndDirectionInput
    }
  }), _vm._v(" "), _c('bb-textarea', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.commentTextareaProps && _vm.props.commentTextareaProps.validate,
      expression: "props.commentTextareaProps && props.commentTextareaProps.validate"
    }],
    staticClass: "bb-memo-fieldset-default__comment",
    attrs: {
      "name": "comment",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.comment,
      callback: function ($$v) {
        _vm.$set(_vm.models, "comment", $$v);
      },
      expression: "models.comment"
    }
  }, 'bb-textarea', _vm.props.commentTextareaProps, false)), _vm._v(" "), _vm.props.validToDatepickerProps ? _c('bb-datepicker', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.validToDatepickerProps && _vm.props.validToDatepickerProps.validate,
      expression: "props.validToDatepickerProps && props.validToDatepickerProps.validate"
    }],
    attrs: {
      "format": "DD/MM/YYYY",
      "name": "validToDatepicker",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.validToDate,
      callback: function ($$v) {
        _vm.$set(_vm.models, "validToDate", $$v);
      },
      expression: "models.validToDate"
    }
  }, 'bb-datepicker', _vm.props.validToDatepickerProps, false)) : _vm._e(), _vm._v(" "), _vm.props.uploadProps ? _c('bb-upload', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.uploadProps && _vm.props.uploadProps.validate,
      expression: "props.uploadProps && props.uploadProps.validate"
    }],
    staticClass: "bb-memo-fieldset-default__upload",
    attrs: {
      "flavor": "co-borrower",
      "name": "upload"
    },
    on: {
      "change": _vm.emitInput
    },
    model: {
      value: _vm.models.upload,
      callback: function ($$v) {
        _vm.$set(_vm.models, "upload", $$v);
      },
      expression: "models.upload"
    }
  }, 'bb-upload', _vm.props.uploadProps, false)) : _vm._e(), _vm._v(" "), _vm.props.isUrgentCheckboxProps ? _c('bb-checkbox', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.isUrgentCheckboxProps && _vm.props.isUrgentCheckboxProps.validate,
      expression: "props.isUrgentCheckboxProps && props.isUrgentCheckboxProps.validate"
    }],
    attrs: {
      "name": "isUrgent"
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.isUrgent,
      callback: function ($$v) {
        _vm.$set(_vm.models, "isUrgent", $$v);
      },
      expression: "models.isUrgent"
    }
  }, 'bb-checkbox', _vm.props.isUrgentCheckboxProps, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$E = [];

/* style */
const __vue_inject_styles__$E = function (inject) {
  if (!inject) return;
  inject("data-v-626d0be6_0", {
    source: ".bb-memo-fieldset-default .bb-memo-fieldset-default__upload{margin-top:1.25rem}.bb-memo-fieldset-default .bb-memo-fieldset-default__comment .bb-textarea__textarea{min-height:6rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$E = undefined;
/* module identifier */
const __vue_module_identifier__$E = undefined;
/* functional template */
const __vue_is_functional_template__$E = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$E = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$E,
  staticRenderFns: __vue_staticRenderFns__$E
}, __vue_inject_styles__$E, __vue_script__$E, __vue_scope_id__$E, __vue_is_functional_template__$E, __vue_module_identifier__$E, false, createInjector, undefined, undefined);

var script$D = {
  name: 'bb-memo-fieldset-reminder',
  components: {
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbDatepicker: __vue_component__$1o,
    BbCheckbox: __vue_component__$2s,
    BbSelect: __vue_component__$20,
    BbUpload: __vue_component__$G,
    BbSeparator: __vue_component__$2a,
    BbMemoFieldsFlatChannelAndDirection: __vue_component__$F
  },
  mixins: [validator],
  props: {
    props: Object
  },
  emits: ['input'],
  data() {
    var _this$props$topicSele, _this$props$taskNameS, _this$props$contactCh, _this$props$contactDi, _this$props$isReached, _this$props$uploadPro, _this$props$isUrgentC, _this$props$assigneeS, _this$props$assigneeG, _this$props$validFrom, _this$props$validToDa, _this$props$dueDateDa;
    return {
      models: {
        topic: (_this$props$topicSele = this.props.topicSelectProps) === null || _this$props$topicSele === void 0 ? void 0 : _this$props$topicSele.value,
        taskName: (_this$props$taskNameS = this.props.taskNameSelectProps) === null || _this$props$taskNameS === void 0 ? void 0 : _this$props$taskNameS.value,
        contactChannel: (_this$props$contactCh = this.props.contactChannelSelectProps) === null || _this$props$contactCh === void 0 ? void 0 : _this$props$contactCh.value,
        contactDirection: (_this$props$contactDi = this.props.contactDirectionRadioProps) === null || _this$props$contactDi === void 0 ? void 0 : _this$props$contactDi.value,
        isReached: (_this$props$isReached = this.props.isReachedRadioProps) === null || _this$props$isReached === void 0 ? void 0 : _this$props$isReached.value,
        upload: (_this$props$uploadPro = this.props.uploadProps) === null || _this$props$uploadPro === void 0 ? void 0 : _this$props$uploadPro.value,
        isUrgent: (_this$props$isUrgentC = this.props.isUrgentCheckboxProps) === null || _this$props$isUrgentC === void 0 ? void 0 : _this$props$isUrgentC.value,
        assignee: (_this$props$assigneeS = this.props.assigneeSelectProps) === null || _this$props$assigneeS === void 0 ? void 0 : _this$props$assigneeS.value,
        assigneeGroup: (_this$props$assigneeG = this.props.assigneeGroupSelectProps) === null || _this$props$assigneeG === void 0 ? void 0 : _this$props$assigneeG.value,
        validFromDate: (_this$props$validFrom = this.props.validFromDatepickerProps) === null || _this$props$validFrom === void 0 ? void 0 : _this$props$validFrom.value,
        validToDate: (_this$props$validToDa = this.props.validToDatepickerProps) === null || _this$props$validToDa === void 0 ? void 0 : _this$props$validToDa.value,
        dueDate: (_this$props$dueDateDa = this.props.dueDateDatepickerProps) === null || _this$props$dueDateDa === void 0 ? void 0 : _this$props$dueDateDa.value
      }
    };
  },
  beforeMount() {
    this.emitInput();
  },
  methods: {
    emitInput() {
      this.$emit('input', this.models);
    },
    onFlatChannelAndDirectionInput(models) {
      Object.assign(this.models, models);
      this.emitInput();
    }
  }
};

/* script */
const __vue_script__$D = script$D;

/* template */
var __vue_render__$D = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-memo-fieldset-reminder"
  }, [_vm.props.topicSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.topicSelectProps && _vm.props.topicSelectProps.validate,
      expression: "props.topicSelectProps && props.topicSelectProps.validate"
    }],
    attrs: {
      "name": "reminder-topic",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.topic,
      callback: function ($$v) {
        _vm.$set(_vm.models, "topic", $$v);
      },
      expression: "models.topic"
    }
  }, 'bb-select', _vm.props.topicSelectProps, false)) : _vm._e(), _vm._v(" "), _vm.props.taskNameSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.taskNameSelectProps && _vm.props.taskNameSelectProps.validate,
      expression: "props.taskNameSelectProps && props.taskNameSelectProps.validate"
    }],
    attrs: {
      "name": "reminder-nameSelect",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.taskName,
      callback: function ($$v) {
        _vm.$set(_vm.models, "taskName", $$v);
      },
      expression: "models.taskName"
    }
  }, 'bb-select', _vm.props.taskNameSelectProps, false)) : _vm._e(), _vm._v(" "), _c('bb-memo-fields-flat-channel-and-direction', {
    attrs: {
      "props": _vm.props
    },
    on: {
      "input": _vm.onFlatChannelAndDirectionInput
    }
  }), _vm._v(" "), _vm.props.uploadProps ? _c('bb-upload', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.uploadProps && _vm.props.uploadProps.validate,
      expression: "props.uploadProps && props.uploadProps.validate"
    }],
    staticClass: "bb-memo-fieldset-reminder__upload",
    attrs: {
      "flavor": "co-borrower",
      "name": "reminder-upload"
    },
    on: {
      "change": _vm.emitInput
    },
    model: {
      value: _vm.models.upload,
      callback: function ($$v) {
        _vm.$set(_vm.models, "upload", $$v);
      },
      expression: "models.upload"
    }
  }, 'bb-upload', _vm.props.uploadProps, false)) : _vm._e(), _vm._v(" "), _c('bb-row', {
    attrs: {
      "gutter": "20"
    }
  }, [_c('bb-col', [_vm.props.assigneeSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.assigneeSelectProps && _vm.props.assigneeSelectProps.validate,
      expression: "props.assigneeSelectProps && props.assigneeSelectProps.validate"
    }],
    attrs: {
      "name": "reminder-assignee",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.assignee,
      callback: function ($$v) {
        _vm.$set(_vm.models, "assignee", $$v);
      },
      expression: "models.assignee"
    }
  }, 'bb-select', _vm.props.assigneeSelectProps, false)) : _vm._e()], 1), _vm._v(" "), _c('bb-col', [_vm.props.assigneeGroupSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.assigneeGroupSelectProps && _vm.props.assigneeGroupSelectProps.validate,
      expression: "props.assigneeGroupSelectProps && props.assigneeGroupSelectProps.validate"
    }],
    attrs: {
      "name": "reminder-assigneeGroup",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.assigneeGroup,
      callback: function ($$v) {
        _vm.$set(_vm.models, "assigneeGroup", $$v);
      },
      expression: "models.assigneeGroup"
    }
  }, 'bb-select', _vm.props.assigneeGroupSelectProps, false)) : _vm._e()], 1)], 1), _vm._v(" "), _c('bb-separator', {
    staticClass: "bb-memo-fieldset-reminder__separator"
  }), _vm._v(" "), _c('bb-row', {
    attrs: {
      "gutter": "20"
    }
  }, [_c('bb-col', [_vm.props.validFromDatepickerProps ? _c('bb-datepicker', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.validFromDatepickerProps && _vm.props.validFromDatepickerProps.validate,
      expression: "props.validFromDatepickerProps && props.validFromDatepickerProps.validate"
    }],
    attrs: {
      "format": "DD/MM/YYYY",
      "name": "reminder-validFromDatepicker",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.validFromDate,
      callback: function ($$v) {
        _vm.$set(_vm.models, "validFromDate", $$v);
      },
      expression: "models.validFromDate"
    }
  }, 'bb-datepicker', _vm.props.validFromDatepickerProps, false)) : _vm._e()], 1), _vm._v(" "), _c('bb-col', [_vm.props.validToDatepickerProps ? _c('bb-datepicker', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.validToDatepickerProps && _vm.props.validToDatepickerProps.validate,
      expression: "props.validToDatepickerProps && props.validToDatepickerProps.validate"
    }],
    attrs: {
      "format": "DD/MM/YYYY",
      "name": "reminder-validToDatepicker",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.validToDate,
      callback: function ($$v) {
        _vm.$set(_vm.models, "validToDate", $$v);
      },
      expression: "models.validToDate"
    }
  }, 'bb-datepicker', _vm.props.validToDatepickerProps, false)) : _vm._e()], 1)], 1), _vm._v(" "), _c('bb-row', {
    attrs: {
      "gutter": "20"
    }
  }, [_c('bb-col', [_vm.props.dueDateDatepickerProps ? _c('bb-datepicker', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.dueDateDatepickerProps && _vm.props.dueDateDatepickerProps.validate,
      expression: "props.dueDateDatepickerProps && props.dueDateDatepickerProps.validate"
    }],
    attrs: {
      "format": "DD/MM/YYYY",
      "name": "reminder-dueDateDatepicker",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.dueDate,
      callback: function ($$v) {
        _vm.$set(_vm.models, "dueDate", $$v);
      },
      expression: "models.dueDate"
    }
  }, 'bb-datepicker', _vm.props.dueDateDatepickerProps, false)) : _vm._e()], 1), _vm._v(" "), _c('bb-col')], 1), _vm._v(" "), _vm.props.isUrgentCheckboxProps ? _c('bb-checkbox', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.isUrgentCheckboxProps && _vm.props.isUrgentCheckboxProps.validate,
      expression: "props.isUrgentCheckboxProps && props.isUrgentCheckboxProps.validate"
    }],
    attrs: {
      "name": "reminder-isUrgent"
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.isUrgent,
      callback: function ($$v) {
        _vm.$set(_vm.models, "isUrgent", $$v);
      },
      expression: "models.isUrgent"
    }
  }, 'bb-checkbox', _vm.props.isUrgentCheckboxProps, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$D = [];

/* style */
const __vue_inject_styles__$D = function (inject) {
  if (!inject) return;
  inject("data-v-deca3b46_0", {
    source: ".bb-memo-fieldset-reminder .bb-memo-fieldset-reminder__upload{margin-top:1.25rem}.bb-memo-fieldset-reminder .bb-memo-fieldset-reminder__separator{margin-top:1.25rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$D = undefined;
/* module identifier */
const __vue_module_identifier__$D = undefined;
/* functional template */
const __vue_is_functional_template__$D = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$D = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$D,
  staticRenderFns: __vue_staticRenderFns__$D
}, __vue_inject_styles__$D, __vue_script__$D, __vue_scope_id__$D, __vue_is_functional_template__$D, __vue_module_identifier__$D, false, createInjector, undefined, undefined);

var script$C = {
  name: 'bb-memo-fieldset-complaint',
  components: {
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbDatepicker: __vue_component__$1o,
    BbCheckbox: __vue_component__$2s,
    BbTextarea: __vue_component__$1S,
    BbSelect: __vue_component__$20,
    BbUpload: __vue_component__$G,
    BbInput: __vue_component__$2f,
    BbSeparator: __vue_component__$2a,
    BbRadio: __vue_component__$12
  },
  mixins: [validator],
  props: {
    props: Object
  },
  emits: ['input'],
  data() {
    var _this$props$summaryIn, _this$props$descripti, _this$props$uploadPro, _this$props$countrySe, _this$props$complaint, _this$props$incomingC, _this$props$personalD, _this$props$replyNeed;
    return {
      models: {
        summary: (_this$props$summaryIn = this.props.summaryInputProps) === null || _this$props$summaryIn === void 0 ? void 0 : _this$props$summaryIn.value,
        description: (_this$props$descripti = this.props.descriptionTextareaProps) === null || _this$props$descripti === void 0 ? void 0 : _this$props$descripti.value,
        upload: (_this$props$uploadPro = this.props.uploadProps) === null || _this$props$uploadPro === void 0 ? void 0 : _this$props$uploadPro.value,
        country: (_this$props$countrySe = this.props.countrySelectProps) === null || _this$props$countrySe === void 0 ? void 0 : _this$props$countrySe.value,
        complaintDate: (_this$props$complaint = this.props.complaintDatepickerProps) === null || _this$props$complaint === void 0 ? void 0 : _this$props$complaint.value,
        incomingComplaintChannel: (_this$props$incomingC = this.props.incomingComplaintChannelSelectProps) === null || _this$props$incomingC === void 0 ? void 0 : _this$props$incomingC.value,
        personalDataInfringement: (_this$props$personalD = this.props.personalDataInfringementRadioProps) === null || _this$props$personalD === void 0 ? void 0 : _this$props$personalD.value,
        replyNeeded: (_this$props$replyNeed = this.props.replyNeededCheckboxProps) === null || _this$props$replyNeed === void 0 ? void 0 : _this$props$replyNeed.value
      }
    };
  },
  beforeMount() {
    this.emitInput();
  },
  methods: {
    emitInput() {
      this.$emit('input', this.models);
    }
  }
};

/* script */
const __vue_script__$C = script$C;

/* template */
var __vue_render__$C = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-memo-fieldset-complaint"
  }, [_vm.props.summaryInputProps ? _c('bb-input', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.summaryInputProps && _vm.props.summaryInputProps.validate,
      expression: "props.summaryInputProps && props.summaryInputProps.validate"
    }],
    attrs: {
      "name": "complaint-summary",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.summary,
      callback: function ($$v) {
        _vm.$set(_vm.models, "summary", $$v);
      },
      expression: "models.summary"
    }
  }, 'bb-input', _vm.props.summaryInputProps, false)) : _vm._e(), _vm._v(" "), _vm.props.descriptionTextareaProps ? _c('bb-textarea', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.descriptionTextareaProps && _vm.props.descriptionTextareaProps.validate,
      expression: "props.descriptionTextareaProps && props.descriptionTextareaProps.validate"
    }],
    staticClass: "bb-memo-fieldset-complaint__description",
    attrs: {
      "framed": "",
      "name": "complaint-description"
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.description,
      callback: function ($$v) {
        _vm.$set(_vm.models, "description", $$v);
      },
      expression: "models.description"
    }
  }, 'bb-textarea', _vm.props.descriptionTextareaProps, false)) : _vm._e(), _vm._v(" "), _vm.props.uploadProps ? _c('bb-upload', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.uploadProps && _vm.props.uploadProps.validate,
      expression: "props.uploadProps && props.uploadProps.validate"
    }],
    staticClass: "bb-memo-fieldset-complaint__upload",
    attrs: {
      "flavor": "co-borrower",
      "name": "complaint-upload"
    },
    on: {
      "change": _vm.emitInput
    },
    model: {
      value: _vm.models.upload,
      callback: function ($$v) {
        _vm.$set(_vm.models, "upload", $$v);
      },
      expression: "models.upload"
    }
  }, 'bb-upload', _vm.props.uploadProps, false)) : _vm._e(), _vm._v(" "), _c('bb-separator', {
    staticClass: "bb-memo-fieldset-complaint__separator"
  }), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-memo-fieldset-complaint__country-select",
    attrs: {
      "gutter": "20"
    }
  }, [_c('bb-col', [_vm.props.countrySelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.countrySelectProps && _vm.props.countrySelectProps.validate,
      expression: "props.countrySelectProps && props.countrySelectProps.validate"
    }],
    attrs: {
      "name": "complaint-countrySelect",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.country,
      callback: function ($$v) {
        _vm.$set(_vm.models, "country", $$v);
      },
      expression: "models.country"
    }
  }, 'bb-select', _vm.props.countrySelectProps, false)) : _vm._e()], 1), _vm._v(" "), _c('bb-col', [_vm.props.complaintDatepickerProps ? _c('bb-datepicker', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.complaintDatepickerProps && _vm.props.complaintDatepickerProps.validate,
      expression: "props.complaintDatepickerProps && props.complaintDatepickerProps.validate"
    }],
    attrs: {
      "format": "DD/MM/YYYY",
      "name": "complaint-complaintDatepicker",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.complaintDate,
      callback: function ($$v) {
        _vm.$set(_vm.models, "complaintDate", $$v);
      },
      expression: "models.complaintDate"
    }
  }, 'bb-datepicker', _vm.props.complaintDatepickerProps, false)) : _vm._e()], 1)], 1), _vm._v(" "), _vm.props.incomingComplaintChannelSelectProps ? _c('bb-select', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.incomingComplaintChannelSelectProps && _vm.props.incomingComplaintChannelSelectProps.validate,
      expression: "props.incomingComplaintChannelSelectProps && props.incomingComplaintChannelSelectProps.validate"
    }],
    staticClass: "bb-memo-fieldset-complaint__channel-select",
    attrs: {
      "name": "complaint-incomingComplaintChannelSelect",
      "framed": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.incomingComplaintChannel,
      callback: function ($$v) {
        _vm.$set(_vm.models, "incomingComplaintChannel", $$v);
      },
      expression: "models.incomingComplaintChannel"
    }
  }, 'bb-select', _vm.props.incomingComplaintChannelSelectProps, false)) : _vm._e(), _vm._v(" "), _vm.props.personalDataInfringementRadioProps ? _c('bb-radio', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.personalDataInfringementRadioProps && _vm.props.personalDataInfringementRadioProps.validate,
      expression: "props.personalDataInfringementRadioProps && props.personalDataInfringementRadioProps.validate"
    }],
    staticClass: "bb-memo-fieldset-complaint__infringement-radio",
    attrs: {
      "name": "complaint-personalDataInfringementRadio",
      "equal-width": ""
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.personalDataInfringement,
      callback: function ($$v) {
        _vm.$set(_vm.models, "personalDataInfringement", $$v);
      },
      expression: "models.personalDataInfringement"
    }
  }, 'bb-radio', _vm.props.personalDataInfringementRadioProps, false)) : _vm._e(), _vm._v(" "), _vm.props.replyNeededCheckboxProps ? _c('bb-checkbox', _vm._b({
    directives: [{
      name: "validate",
      rawName: "v-validate",
      value: _vm.props.replyNeededCheckboxProps && _vm.props.replyNeededCheckboxProps.validate,
      expression: "props.replyNeededCheckboxProps && props.replyNeededCheckboxProps.validate"
    }],
    attrs: {
      "name": "complaint-replyNeededCheckbox"
    },
    on: {
      "input": _vm.emitInput
    },
    model: {
      value: _vm.models.replyNeeded,
      callback: function ($$v) {
        _vm.$set(_vm.models, "replyNeeded", $$v);
      },
      expression: "models.replyNeeded"
    }
  }, 'bb-checkbox', _vm.props.replyNeededCheckboxProps, false)) : _vm._e()], 1);
};
var __vue_staticRenderFns__$C = [];

/* style */
const __vue_inject_styles__$C = function (inject) {
  if (!inject) return;
  inject("data-v-244b1018_0", {
    source: ".bb-memo-fieldset-complaint .bb-memo-fieldset-complaint__description .bb-textarea__textarea{min-height:6rem}.bb-memo-fieldset-complaint .bb-memo-fieldset-complaint__upload{margin-top:.8125rem}.bb-memo-fieldset-complaint .bb-memo-fieldset-complaint__channel-select{margin-top:.3125rem}.bb-memo-fieldset-complaint .bb-memo-fieldset-complaint__infringement-radio{margin-top:.9375rem}.bb-memo-fieldset-complaint .bb-memo-fieldset-complaint__separator{margin-top:1.25rem;margin-bottom:.375rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$C = undefined;
/* module identifier */
const __vue_module_identifier__$C = undefined;
/* functional template */
const __vue_is_functional_template__$C = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$C = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$C,
  staticRenderFns: __vue_staticRenderFns__$C
}, __vue_inject_styles__$C, __vue_script__$C, __vue_scope_id__$C, __vue_is_functional_template__$C, __vue_module_identifier__$C, false, createInjector, undefined, undefined);

/**
 * A form for adding a memo in the back-office.
 *
 * See also [bb-memos](#/Organisms/bb-memos) and [bb-memo-filter](#/Organisms/bb-memo-filter).
 */
var script$B = Vue.extend({
  name: 'bb-add-memo',
  components: {
    BbForm: __vue_component__$1c,
    BbMemoFieldsetDefault: __vue_component__$E,
    BbMemoFieldsetReminder: __vue_component__$D,
    BbMemoFieldsetComplaint: __vue_component__$C,
    BbTabs: __vue_component__$Z
  },
  props: {
    /**
     * Map commentType for fields to be displayed.
     */
    fieldsetByTabs: {
      type: Object,
      default: () => ({
        task: 'TASK',
        complaint: 'COMPLAINT'
      })
    },
    commentTypeTabs: {
      type: Array
    },
    topicSelectProps: {
      type: Object
    },
    taskNameSelectProps: {
      type: Object
    },
    /**
     * Flatten contactChannel & contactDirection into a single Radio.
     * Provide the `options` still separately to `contactChannelSelectProps`
     * and `contactDirectionRadioProps`.
     */
    flatChannelAndDirectionRadioProps: {
      type: Object
    },
    contactChannelSelectProps: {
      type: Object,
      default: () => ({
        label: 'Contact channel',
        placeholder: 'Choose a channel'
      })
    },
    contactDirectionRadioProps: {
      type: Object,
      default: () => ({
        label: 'Direction'
      })
    },
    /**
     * Radio to set the "reached client" status for outgoing phone calls.
     */
    isReachedRadioProps: {
      type: Object,
      default: () => ({
        label: 'Result',
        options: [{
          text: 'Reached',
          value: 'REACHED'
        }, {
          text: 'Not reached',
          value: 'NOT_REACHED'
        }]
      })
    },
    commentTextareaProps: {
      type: Object,
      default: () => ({
        label: 'Comment',
        placeholder: 'Add a comment'
      })
    },
    uploadProps: {
      type: Object
    },
    isUrgentCheckboxProps: {
      type: Object
    },
    assigneeSelectProps: {
      type: Object
    },
    assigneeGroupSelectProps: {
      type: Object
    },
    validFromDatepickerProps: {
      type: Object
    },
    validToDatepickerProps: {
      type: Object
    },
    dueDateDatepickerProps: {
      type: Object
    },
    summaryInputProps: {
      type: Object
    },
    descriptionTextareaProps: {
      type: Object
    },
    countrySelectProps: {
      type: Object
    },
    complaintDatepickerProps: {
      type: Object
    },
    incomingComplaintChannelSelectProps: {
      type: Object
    },
    personalDataInfringementRadioProps: {
      type: Object
    },
    replyNeededCheckboxProps: {
      type: Object
    },
    submitButtonLabel: {
      type: String,
      default: 'Add'
    },
    submitButtonDisabled: Boolean,
    submitButtonLoading: Boolean,
    channelsWithoutDirection: {
      type: Array,
      default: () => ['INTERNAL', 'OFFICE']
    }
  },
  data() {
    var _this$commentTypeTabs, _this$commentTypeTabs2;
    return {
      commentType: (_this$commentTypeTabs = this.commentTypeTabs) === null || _this$commentTypeTabs === void 0 ? void 0 : (_this$commentTypeTabs2 = _this$commentTypeTabs[0]) === null || _this$commentTypeTabs2 === void 0 ? void 0 : _this$commentTypeTabs2.id,
      models: {}
    };
  },
  computed: {
    fieldset() {
      const fieldset = Object.keys(this.fieldsetByTabs).find(fieldset => {
        const tabs = this.fieldsetByTabs[fieldset];
        return castArray(tabs).includes(this.commentType);
      });
      return fieldset || 'default';
    },
    hasTabs() {
      var _this$commentTypeTabs3;
      return ((_this$commentTypeTabs3 = this.commentTypeTabs) === null || _this$commentTypeTabs3 === void 0 ? void 0 : _this$commentTypeTabs3.length) > 1;
    },
    isReachedText() {
      var _this$isReachedRadioP, _this$isReachedRadioP2, _this$isReachedRadioP3;
      return (_this$isReachedRadioP = this.isReachedRadioProps) === null || _this$isReachedRadioP === void 0 ? void 0 : (_this$isReachedRadioP2 = _this$isReachedRadioP.options) === null || _this$isReachedRadioP2 === void 0 ? void 0 : (_this$isReachedRadioP3 = _this$isReachedRadioP2.find(option => option.value === this.models.isReached)) === null || _this$isReachedRadioP3 === void 0 ? void 0 : _this$isReachedRadioP3.text;
    }
  },
  methods: {
    onInput(event) {
      this.models = event;
    },
    onSubmit() {
      this.$validator.validateAll().then(this.emitSubmit);
    },
    emitSubmit() {
      var _this$models$comment;
      const mergeIsReachedIntoComment = {
        comment: this.isReachedText ? `${this.isReachedText}: ${(_this$models$comment = this.models.comment) !== null && _this$models$comment !== void 0 ? _this$models$comment : ''}` : this.models.comment
      };
      const models = {
        commentType: this.commentType,
        ...this.models,
        ...mergeIsReachedIntoComment
      };
      /**
       * Submit event.
       *
       * @event submit
       * @type {object}
       */
      this.$emit('submit', pickBy(models, isDefined));
    }
  }
});

/* script */
const __vue_script__$B = script$B;

/* template */
var __vue_render__$B = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-form', {
    staticClass: "bb-add-memo",
    attrs: {
      "submit-text": _vm.submitButtonLabel,
      "static-submit": !_vm.$scopedSlots.submit,
      "disabled": _vm.submitButtonDisabled,
      "loading": _vm.submitButtonLoading
    },
    on: {
      "submit": _vm.onSubmit
    },
    scopedSlots: _vm._u([{
      key: "submit",
      fn: function (formScope) {
        return [_vm._t("submit", null, null, Object.assign({}, formScope, {
          triggerSubmit: _vm.onSubmit
        }))];
      }
    }], null, true)
  }, [_vm.hasTabs ? _c('bb-tabs', {
    staticClass: "bb-add-memo__tabs",
    attrs: {
      "tabs": _vm.commentTypeTabs,
      "aligned": ""
    },
    model: {
      value: _vm.commentType,
      callback: function ($$v) {
        _vm.commentType = $$v;
      },
      expression: "commentType"
    }
  }) : _vm._e(), _vm._v(" "), 'default' === _vm.fieldset ? _c('bb-memo-fieldset-default', {
    attrs: {
      "props": {
        flatChannelAndDirectionRadioProps: _vm.flatChannelAndDirectionRadioProps,
        topicSelectProps: _vm.topicSelectProps,
        taskNameSelectProps: _vm.taskNameSelectProps,
        contactChannelSelectProps: _vm.contactChannelSelectProps,
        contactDirectionRadioProps: _vm.contactDirectionRadioProps,
        isReachedRadioProps: _vm.isReachedRadioProps,
        commentTextareaProps: _vm.commentTextareaProps,
        validToDatepickerProps: _vm.validToDatepickerProps,
        uploadProps: _vm.uploadProps,
        isUrgentCheckboxProps: _vm.isUrgentCheckboxProps,
        channelsWithoutDirection: _vm.channelsWithoutDirection
      }
    },
    on: {
      "input": _vm.onInput
    }
  }) : _vm._e(), _vm._v(" "), 'task' === _vm.fieldset ? _c('bb-memo-fieldset-reminder', {
    attrs: {
      "props": {
        flatChannelAndDirectionRadioProps: _vm.flatChannelAndDirectionRadioProps,
        topicSelectProps: _vm.topicSelectProps,
        taskNameSelectProps: _vm.taskNameSelectProps,
        contactChannelSelectProps: _vm.contactChannelSelectProps,
        contactDirectionRadioProps: _vm.contactDirectionRadioProps,
        isReachedRadioProps: _vm.isReachedRadioProps,
        uploadProps: _vm.uploadProps,
        isUrgentCheckboxProps: _vm.isUrgentCheckboxProps,
        assigneeSelectProps: _vm.assigneeSelectProps,
        assigneeGroupSelectProps: _vm.assigneeGroupSelectProps,
        validFromDatepickerProps: _vm.validFromDatepickerProps,
        validToDatepickerProps: _vm.validToDatepickerProps,
        dueDateDatepickerProps: _vm.dueDateDatepickerProps,
        channelsWithoutDirection: _vm.channelsWithoutDirection
      }
    },
    on: {
      "input": _vm.onInput
    }
  }) : _vm._e(), _vm._v(" "), 'complaint' === _vm.fieldset ? _c('bb-memo-fieldset-complaint', {
    attrs: {
      "props": {
        summaryInputProps: _vm.summaryInputProps,
        descriptionTextareaProps: _vm.descriptionTextareaProps,
        uploadProps: _vm.uploadProps,
        countrySelectProps: _vm.countrySelectProps,
        complaintDatepickerProps: _vm.complaintDatepickerProps,
        incomingComplaintChannelSelectProps: _vm.incomingComplaintChannelSelectProps,
        personalDataInfringementRadioProps: _vm.personalDataInfringementRadioProps,
        replyNeededCheckboxProps: _vm.replyNeededCheckboxProps
      }
    },
    on: {
      "input": _vm.onInput
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$B = [];

/* style */
const __vue_inject_styles__$B = function (inject) {
  if (!inject) return;
  inject("data-v-2758d70c_0", {
    source: ".bb-add-memo{overflow:hidden}.bb-add-memo .bb-add-memo__tabs{margin:-.625rem -1.875rem .625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$B = undefined;
/* module identifier */
const __vue_module_identifier__$B = undefined;
/* functional template */
const __vue_is_functional_template__$B = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$B = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$B,
  staticRenderFns: __vue_staticRenderFns__$B
}, __vue_inject_styles__$B, __vue_script__$B, __vue_scope_id__$B, __vue_is_functional_template__$B, __vue_module_identifier__$B, false, createInjector, undefined, undefined);

/**
 * Presents [AddMemo](#/Organisms/bb-add-memo) in a Modal. Proxies all its events.
 */
var script$A = Vue.extend({
  name: 'bb-add-memo-modal',
  components: {
    BbAddMemo: __vue_component__$B,
    BbModal: __vue_component__$W
  },
  props: {
    visible: Boolean,
    title: {
      type: String,
      default: 'Add new memo'
    },
    /**
    * @see [AddMemo](#/Organisms/bb-add-memo) for a list of its props and events
    */
    addMemoProps: {
      type: Object
    }
  },
  methods: {
    onClose() {
      /**
       * Close event.
       *
       * @event close
       */
      this.$emit('close');
    }
  }
});

/* script */
const __vue_script__$A = script$A;

/* template */
var __vue_render__$A = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-modal', {
    staticClass: "bb-add-memo-modal",
    attrs: {
      "visible": _vm.visible,
      "size": "x2"
    },
    on: {
      "close": _vm.onClose
    },
    scopedSlots: _vm._u([{
      key: "heading",
      fn: function () {
        return [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")];
      },
      proxy: true
    }])
  }, [_vm._v(" "), _c('bb-add-memo', _vm._g(_vm._b({
    scopedSlots: _vm._u([{
      key: "submit",
      fn: function (formScope) {
        return [_vm._t("submit", null, null, formScope)];
      }
    }], null, true)
  }, 'bb-add-memo', _vm.addMemoProps, false), _vm.$listeners))], 1);
};
var __vue_staticRenderFns__$A = [];

/* style */
const __vue_inject_styles__$A = function (inject) {
  if (!inject) return;
  inject("data-v-a0196320_0", {
    source: "@media (min-width:768px){.bb-add-memo-modal .bb-modal{margin-bottom:9rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$A = undefined;
/* module identifier */
const __vue_module_identifier__$A = undefined;
/* functional template */
const __vue_is_functional_template__$A = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$A = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$A,
  staticRenderFns: __vue_staticRenderFns__$A
}, __vue_inject_styles__$A, __vue_script__$A, __vue_scope_id__$A, __vue_is_functional_template__$A, __vue_module_identifier__$A, false, createInjector, undefined, undefined);

var script$z = {
  name: 'bb-add-schedule-modal',
  components: {
    BbModal: __vue_component__$W,
    BbDatepicker: __vue_component__$1o,
    BbButton: __vue_component__$2v
  },
  props: {
    visible: Boolean,
    title: {
      type: String,
      default: 'Add new schedule'
    },
    datepickerProps: {
      type: Object,
      default: () => ({
        label: 'Indicate when the task shall be activated again',
        placeholder: 'Select a date and time'
      })
    },
    cancelButtonLabel: {
      type: String,
      default: 'Cancel'
    },
    submitButtonLabel: {
      type: String,
      default: 'Add'
    }
  },
  emits: ['close', 'submit'],
  data() {
    return {
      models: {
        scheduleDate: null
      },
      internalDatepickerProps: this.datepickerProps
    };
  },
  watch: {
    datepickerProps(newValue) {
      this.internalDatepickerProps = newValue;
    }
  },
  methods: {
    emitClose() {
      this.$emit('close');
    },
    emitSubmit() {
      this.$emit('submit', this.models);
    },
    onSubmit() {
      this.emitSubmit();
      this.emitClose();
    }
  }
};

/* script */
const __vue_script__$z = script$z;

/* template */
var __vue_render__$z = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-modal', {
    staticClass: "bb-add-schedule-modal",
    attrs: {
      "visible": _vm.visible,
      "size": "x2"
    },
    on: {
      "close": _vm.emitClose
    },
    scopedSlots: _vm._u([{
      key: "heading",
      fn: function () {
        return [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")];
      },
      proxy: true
    }])
  }, [_vm._v(" "), _c('bb-datepicker', _vm._b({
    attrs: {
      "framed": "",
      "type": "datetime",
      "format": "DD/MM/YYYY HH:mm",
      "name": "scheduleDatepicker"
    },
    model: {
      value: _vm.models.scheduleDate,
      callback: function ($$v) {
        _vm.$set(_vm.models, "scheduleDate", $$v);
      },
      expression: "models.scheduleDate"
    }
  }, 'bb-datepicker', _vm.datepickerProps, false)), _vm._v(" "), _c('div', {
    staticClass: "bb-add-schedule-modal__buttons"
  }, [_c('bb-button', {
    attrs: {
      "inverted": "",
      "color": "gray",
      "label": _vm.cancelButtonLabel
    },
    on: {
      "click": _vm.emitClose
    }
  }), _vm._v(" "), _c('bb-button', {
    attrs: {
      "label": _vm.submitButtonLabel
    },
    on: {
      "click": _vm.onSubmit
    }
  })], 1)], 1);
};
var __vue_staticRenderFns__$z = [];

/* style */
const __vue_inject_styles__$z = function (inject) {
  if (!inject) return;
  inject("data-v-adddb53c_0", {
    source: ".bb-add-schedule-modal .bb-add-schedule-modal__buttons{display:flex;justify-content:flex-end;margin:1.875rem 0 .625rem}.bb-add-schedule-modal .bb-add-schedule-modal__buttons>:not(:first-child){margin-left:.625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$z = undefined;
/* module identifier */
const __vue_module_identifier__$z = undefined;
/* functional template */
const __vue_is_functional_template__$z = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$z = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$z,
  staticRenderFns: __vue_staticRenderFns__$z
}, __vue_inject_styles__$z, __vue_script__$z, __vue_scope_id__$z, __vue_is_functional_template__$z, __vue_module_identifier__$z, false, createInjector, undefined, undefined);

Vue.use(Clipboard);
var script$y = {
  name: 'bb-iban-area',
  components: {
    BbButton: __vue_component__$2v,
    BbTooltip: __vue_component__$2B
  },
  props: {
    loading: Boolean,
    iban: String,
    copyButton: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  emits: ['click'],
  data() {
    return {
      showCopyTooltip: false,
      copyFeedbackMsg: null
    };
  },
  computed: {
    hasCopyButton() {
      return notEmpty(this.copyButton);
    }
  },
  methods: {
    onCopy(status) {
      this.showCopyTooltip = true;
      this.copyFeedbackMsg = status ? this.copyButton.successMsg : this.copyButton.errorMsg;
      setTimeout(() => {
        this.showCopyTooltip = false;
      }, 3000);
    },
    onClick() {
      this.$emit('click');
    }
  }
};

/* script */
const __vue_script__$y = script$y;

/* template */
var __vue_render__$y = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-iban-area"
  }, [_c('div', {
    staticClass: "bb-iban-area__separator"
  }, [_vm._t("separator")], 2), _vm._v(" "), !_vm.hasCopyButton ? _c('div', {
    staticClass: "bb-iban-area__iban"
  }, [_vm.loading ? _c('bb-skeleton', {
    staticClass: "m-y-1",
    attrs: {
      "width": "180",
      "height": "22"
    }
  }) : [_vm._v("\n      " + _vm._s(_vm.iban) + "\n    ")]], 2) : [_c('div', {
    staticClass: "bb-iban-area__copy-button"
  }, [_vm.loading ? _c('bb-skeleton', {
    staticClass: "m-y-15",
    attrs: {
      "width": "180",
      "height": "22"
    }
  }) : _c('bb-button', {
    directives: [{
      name: "clipboard",
      rawName: "v-clipboard:copy",
      value: _vm.iban,
      expression: "iban",
      arg: "copy"
    }, {
      name: "clipboard",
      rawName: "v-clipboard:success",
      value: _vm.onCopy,
      expression: "onCopy",
      arg: "success"
    }, {
      name: "clipboard",
      rawName: "v-clipboard:error",
      value: _vm.onCopy,
      expression: "onCopy",
      arg: "error"
    }],
    attrs: {
      "label": _vm.iban,
      "display": "link",
      "color": "gray",
      "icon-after": "ui-content-copy",
      "icon-after-size": "18",
      "no-underline": ""
    },
    on: {
      "click": _vm.onClick
    }
  }, [_vm.showCopyTooltip ? _c('bb-tooltip', {
    attrs: {
      "bubble": ""
    }
  }, [_vm._v("\n          " + _vm._s(_vm.copyFeedbackMsg) + "\n        ")]) : _vm._e()], 1)], 1)]], 2);
};
var __vue_staticRenderFns__$y = [];

/* style */
const __vue_inject_styles__$y = function (inject) {
  if (!inject) return;
  inject("data-v-ffc5af60_0", {
    source: ".bb-iban-area{display:flex;width:100%;flex-direction:column;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif}@media (min-width:992px){.bb-iban-area{width:unset;flex-direction:row;padding:0}}.bb-iban-area__separator{width:100%;margin-top:.5rem;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif}@media (min-width:992px){.bb-iban-area__separator{width:unset;margin-top:0}}.bb-iban-area__iban{padding-top:1rem;padding-bottom:1rem;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}@media (min-width:992px){.bb-iban-area__iban{padding:0}}.bb-iban-area__copy-button .bb-button.bb-button.bb-button{width:100%;padding-top:1rem;padding-bottom:1rem;color:#57697a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}@media (min-width:992px){.bb-iban-area__copy-button .bb-button.bb-button.bb-button{width:unset;padding:0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$y = undefined;
/* module identifier */
const __vue_module_identifier__$y = undefined;
/* functional template */
const __vue_is_functional_template__$y = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$y = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$y,
  staticRenderFns: __vue_staticRenderFns__$y
}, __vue_inject_styles__$y, __vue_script__$y, __vue_scope_id__$y, __vue_is_functional_template__$y, __vue_module_identifier__$y, false, createInjector, undefined, undefined);

var Icon$9 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1839170220__a bb-icon__dynamic-fill",attrs:{"d":"M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"}})]) } };

Vue.component('icon-ui-alert-circle', Icon$9);

Vue.use(VueScreen, gridBreakpoints);
const STATUS_POSITIVE = ['ok', 'closed', 'paidOut'];
const STATUS_NEGATIVE = ['late', 'terminated'];
const STATUS_COLORS = {
  ok: 'blue-50',
  late: 'red-70',
  terminated: 'red-70',
  closed: 'mint-70',
  paidOut: 'mint-70'
};
var script$x = {
  name: 'bb-application-contract-card',
  components: {
    BbIbanArea: __vue_component__$y,
    BbIcon: __vue_component__$2C,
    BbButton: __vue_component__$2v,
    BbTooltip: __vue_component__$2B,
    BbSeparator: __vue_component__$2a,
    BbIconFrame: __vue_component__$2g,
    BbListGroup: __vue_component__$2b,
    BbListItem: __vue_component__$29,
    BbSkeleton: __vue_component__$2d,
    BbProgressBar: __vue_component__$26,
    BbLabeledValue: __vue_component__$2c
  },
  props: {
    showAmountOnMobile: {
      type: Boolean,
      validator: deprecated('`showAmountOnMobile` is deprecated in ApplicationContractCard and will be removed in IC 12.')
    },
    depoStatusText: {
      type: String,
      validator: deprecated('`depoStatusText` is deprecated in ApplicationContractCard and will be removed in IC 12.')
    },
    loanStatusText: {
      type: String,
      validator: deprecated('`loanStatusText` is deprecated in ApplicationContractCard and will be removed in IC 12.')
    },
    detailsList: {
      type: Array,
      validator: deprecated('`detailsList` is deprecated in ApplicationContractCard and will be removed in IC 12.')
    },
    flavorAgnosticProgress: Boolean,
    separator: Boolean,
    type: {
      type: String,
      default: 'application',
      validator: type => ['application', 'depo', 'loan', 'offer'].includes(type)
    },
    depoType: {
      type: String,
      validator: type => ['term', 'savings'].includes(type)
    },
    status: {
      type: String,
      validator: status => ['ok', 'late', 'closed', 'terminated', 'paidOut', 'inactive'].includes(status)
    },
    icon: Object,
    title: String,
    secondaryTitle: String,
    amount: String,
    timeRemaining: String,
    progress: Object,
    subtitle: String,
    description: String,
    sections: Array,
    primaryButton: {
      type: Object,
      default() {
        return {};
      }
    },
    primaryButtonTooltip: String,
    secondaryButton: {
      type: Object | Array,
      default() {
        return {};
      }
    },
    iban: String,
    copyButton: {
      type: Object,
      default() {
        return {};
      }
    },
    loading: Boolean,
    progressBarProps: Object,
    strikeThroughAriaLabelPartial: String
  },
  emits: ['primaryButtonClick', 'secondaryButtonClick', 'copyButtonClick'],
  data() {
    return {
      primaryButtonTooltipVisible: false,
      loadingAriaLabel: 'Content loading'
    };
  },
  computed: {
    classes() {
      return {
        [`bb-application-contract-card--type-${this.type}`]: this.type,
        [`bb-application-contract-card--depo-type-${this.depoType}`]: this.depoType,
        [`bb-application-contract-card--status-${this.status}`]: this.status,
        'bb-application-contract-card--loading': this.loading,
        'bb-application-contract-card--has-icon': this.hasIcon,
        'bb-application-contract-card--primary-button-inlined': this.primaryButtonInlined,
        'bb-application-contract-card--primary-button-inlined-on-mobile': this.primaryButtonInlinedOnMobile,
        'bb-application-contract-card--secondary-mobile-buttons-have-margin': this.secondaryMobileButtonsHaveMargin,
        'bb-application-contract-card--has-iban': this.iban,
        'bb-application-contract-card--has-sections': this.hasSections,
        'bb-application-contract-card--body-has-padding-on-mobile': this.status === 'terminated' && this.description,
        'bb-application-contract-card--has-progress': this.progress,
        'bb-application-contract-card--progress-bar-visible-on-mobile': this.isProgressBarVisibleOnMobile,
        'bb-application-contract-card--depo-awaiting-activation': this.isDepoInactive
      };
    },
    isLargeViewport() {
      return this.$screen.lg;
    },
    isStatusInactive() {
      return this.status === 'inactive';
    },
    isTypeApplication() {
      return this.type === 'application';
    },
    isTypeDepo() {
      return this.type === 'depo';
    },
    isTypeLoan() {
      return this.type === 'loan';
    },
    isTypeDepoOrLoan() {
      return this.isTypeDepo || this.isTypeLoan;
    },
    isDepoInactive() {
      return this.isTypeDepo && this.isStatusInactive;
    },
    hasSections() {
      return notEmpty(this.sections);
    },
    hasPrimaryButton() {
      return notEmpty(this.primaryButton);
    },
    hasPrimaryButtonOrActionButtons() {
      return this.hasPrimaryButton || this.$slots.actionButtons;
    },
    primaryButtonNotInverted() {
      return !this.primaryButton.inverted;
    },
    primaryButtonInlinedOnMobile() {
      return this.hasPrimaryButtonOrActionButtons && !this.isLargeViewport && this.primaryButtonNotInverted && this.hasSections;
    },
    primaryButtonInlined() {
      return this.isLargeViewport && !this.hasSecondaryButtons || this.primaryButtonInlinedOnMobile;
    },
    primaryButtonSize() {
      return this.primaryButtonInlinedOnMobile ? 'sm' : 'md';
    },
    hasSecondaryButtons() {
      return notEmpty(this.secondaryButton);
    },
    secondaryButtons() {
      return castArray(this.secondaryButton);
    },
    secondaryMobileButtonsHaveMargin() {
      return this.subtitle || this.description || this.hasPrimaryButtonOrActionButtons || this.sections && this.hasPrimaryButtonOrActionButtons;
    },
    labeledValueLabelPosition() {
      if (this.isLargeViewport) {
        return 'top-left';
      }
      return this.primaryButtonInlinedOnMobile ? 'bottom-left' : 'bottom-center';
    },
    loanStatusIconName() {
      return STATUS_POSITIVE.includes(this.status) ? 'ui-check-circle' : STATUS_NEGATIVE.includes(this.status) ? 'ui-alert-circle' : null;
    },
    loanStatusIconFill() {
      return STATUS_COLORS[this.status];
    },
    computedDepoLoanMobileAmount() {
      return this.isTypeDepo ? this.sections && this.sections[0].content : this.isTypeLoan ? this.sections && this.sections[2].content : null;
    },
    hasSeparator() {
      return this.flavorAgnosticProgress && this.separator;
    },
    hasProgressBar() {
      if (this.flavorAgnosticProgress) {
        return this.progress;
      }
      return this.hasProgressBarLegacy;
    },
    hasProgressBarLegacy() {
      return this.progress ? this.isTypeDepoOrLoan : 'offer' !== this.type;
    },
    isProgressBarVisibleOnMobile() {
      var _this$progress;
      return ((_this$progress = this.progress) === null || _this$progress === void 0 ? void 0 : _this$progress.value) > 0 || this.isDepoInactive;
    },
    hasIcon() {
      return notEmpty(this.icon);
    }
  },
  methods: {
    setSectionContentColor(index, highlighted, strikeThrough) {
      const disabledConditions = ['inactive', 'closed', 'terminated'];
      return ifElse({
        'gray-40': disabledConditions.includes(this.status),
        'mint': notEmpty(strikeThrough) || highlighted === true,
        'red': index === 0 && this.status === 'late',
        'gray': true
      });
    },
    onPrimaryButtonClick() {
      this.$emit('primaryButtonClick');
    },
    onSecondaryButtonClick(index) {
      this.$emit('secondaryButtonClick', index);
    },
    onCopyButtonClick() {
      this.$emit('copyButtonClick');
    },
    togglePrimaryButtonTooltip() {
      this.primaryButtonTooltipVisible = !this.primaryButtonTooltipVisible;
    }
  }
};

/* script */
const __vue_script__$x = script$x;

/* template */
var __vue_render__$x = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-application-contract-card",
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.loading ? _vm.loadingAriaLabel : _vm.title
    }
  }, [_c('div', {
    staticClass: "bb-application-contract-card__header"
  }, [['application', 'offer', 'loan'].includes(_vm.type) && _vm.icon ? _c('div', {
    staticClass: "bb-application-contract-card__icon"
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": _vm.isTypeApplication ? '36' : '24',
      "height": _vm.isTypeApplication ? '36' : '24'
    }
  }) : _vm.isTypeLoan ? _c('bb-icon-frame', {
    attrs: {
      "shape": "circle",
      "icon": _vm.icon.name,
      "icon-color": _vm.icon.fill,
      "background-color": "gray-10"
    }
  }) : _c('bb-icon', {
    attrs: {
      "name": _vm.icon.name,
      "size": _vm.isTypeApplication ? 36 : 24,
      "fill": _vm.isTypeApplication ? 'original' : _vm.icon.fill
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-application-contract-card__title"
  }, [_vm.loading ? _c('bb-skeleton', {
    class: {
      'm-y-5': !_vm.isLargeViewport
    },
    attrs: {
      "width": "40%",
      "height": _vm.isLargeViewport ? '20' : '11'
    }
  }) : _vm._t("title", function () {
    return [_vm._v("\n        " + _vm._s(_vm.title) + "\n        "), _vm.iban && _vm.isLargeViewport ? _c('bb-iban-area', {
      staticClass: "bb-application-contract-card__iban-desktop",
      attrs: {
        "iban": _vm.iban,
        "loading": _vm.loading,
        "copy-button": _vm.copyButton
      },
      on: {
        "click": _vm.onCopyButtonClick
      },
      scopedSlots: _vm._u([{
        key: "separator",
        fn: function () {
          return [_vm._v("\n             - \n          ")];
        },
        proxy: true
      }], null, false, 4263261069)
    }) : _vm._e()];
  })], 2), _vm._v(" "), _vm.iban && !_vm.isLargeViewport ? _c('bb-iban-area', {
    staticClass: "bb-application-contract-card__iban-mobile",
    attrs: {
      "iban": _vm.iban,
      "loading": _vm.loading,
      "copy-button": _vm.copyButton
    },
    on: {
      "click": _vm.onCopyButtonClick
    },
    scopedSlots: _vm._u([{
      key: "separator",
      fn: function () {
        return [_c('bb-separator', {
          staticClass: "border-gray-50",
          attrs: {
            "width": "1",
            "dashed": ""
          }
        })];
      },
      proxy: true
    }], null, false, 3294039571)
  }) : _vm._e(), _vm._v(" "), _vm.secondaryTitle && _vm.isTypeDepoOrLoan ? _c('div', {
    staticClass: "bb-application-contract-card__secondary-title"
  }, [_vm.loading ? _c('bb-skeleton', {
    class: {
      'm-y-5': !_vm.isLargeViewport
    },
    attrs: {
      "width": "60",
      "height": _vm.isLargeViewport ? '11' : '8'
    }
  }) : [_vm._v("\n        " + _vm._s(_vm.secondaryTitle) + "\n        "), _vm.timeRemaining ? _c('div', {
    staticClass: "bb-application-contract-card__time-remaining"
  }, [_vm._v("\n          " + _vm._s(_vm.timeRemaining) + "\n        ")]) : _vm._e()], _vm._v(" "), _vm.status && !_vm.isStatusInactive ? _c('span', {
    staticClass: "bb-application-contract-card__loan-status-icon"
  }, [_vm.loading ? _c('bb-skeleton', {
    attrs: {
      "width": "24",
      "height": "24"
    }
  }) : _c('bb-icon', {
    attrs: {
      "name": _vm.loanStatusIconName,
      "fill": _vm.loanStatusIconFill,
      "size": "24"
    }
  })], 1) : _vm._e()], 2) : _vm._e(), _vm._v(" "), _vm.amount || _vm.isProgressBarVisibleOnMobile && _vm.computedDepoLoanMobileAmount && !_vm.isLargeViewport ? _c('div', {
    staticClass: "bb-application-contract-card__amount"
  }, [_vm.loading ? _c('bb-skeleton', {
    class: {
      'm-y-5': !_vm.isLargeViewport
    },
    attrs: {
      "width": _vm.isLargeViewport ? '120' : '80',
      "height": _vm.isLargeViewport ? '20' : '11'
    }
  }) : [_vm._v("\n        " + _vm._s(_vm.amount || _vm.computedDepoLoanMobileAmount) + "\n      ")]], 2) : _vm._e(), _vm._v(" "), _vm.hasSeparator && (_vm.hasIcon || !_vm.hasProgressBar) ? _c('bb-separator', {
    staticClass: "bb-application-contract-card__title-separator",
    attrs: {
      "width": "1",
      "dashed": ""
    }
  }) : _vm._e(), _vm._v(" "), _vm.hasProgressBar ? _c('bb-progress-bar', _vm._b({
    staticClass: "bb-application-contract-card__progress-bar",
    attrs: {
      "value": !_vm.loading && _vm.progress ? _vm.progress.value : 0,
      "max": !_vm.loading && _vm.progress ? _vm.progress.max : 0,
      "corner": "round"
    }
  }, 'bb-progress-bar', _vm.progressBarProps, false)) : _vm._e(), _vm._v(" "), _vm.progress && _vm.isTypeDepoOrLoan && !_vm.isStatusInactive ? _c('div', {
    staticClass: "bb-application-contract-card__textual-progress"
  }, [_vm.loading ? _c('bb-skeleton', {
    staticClass: "m-y-5",
    attrs: {
      "width": "60",
      "height": _vm.isLargeViewport ? '11' : '8'
    }
  }) : [_c('span', {
    staticClass: "bb-application-contract-card__textual-progress-value"
  }, [_vm._v("\n          " + _vm._s(_vm.progress.value))]), _vm._v("/" + _vm._s(_vm.progress.max) + "\n        "), _vm.progress.unit ? _c('span', [_vm._v(_vm._s(_vm.progress.unit))]) : _vm._e()]], 2) : _vm._e()], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-application-contract-card__body"
  }, [_vm.subtitle || _vm.description ? [_vm.subtitle ? _c('p', {
    staticClass: "bb-application-contract-card__subtitle"
  }, [_vm.loading ? _c('bb-skeleton', {
    staticClass: "m-y-5",
    attrs: {
      "width": "70%",
      "height": _vm.isLargeViewport ? '14' : '11'
    }
  }) : [_vm._v("\n          " + _vm._s(_vm.subtitle) + "\n        ")]], 2) : _vm._e(), _vm._v(" "), _vm.description ? _c('p', {
    staticClass: "bb-application-contract-card__description"
  }, [_vm.loading ? [_c('bb-skeleton', {
    staticClass: "m-t-5",
    attrs: {
      "width": "80%",
      "height": "8"
    }
  }), _vm._v(" "), _c('bb-skeleton', {
    staticClass: "m-t-10 m-b-5",
    attrs: {
      "width": "60%",
      "height": "8"
    }
  })] : [_vm._v("\n          " + _vm._s(_vm.description) + "\n        ")]], 2) : _vm._e()] : _vm.hasSections ? _c('div', {
    staticClass: "bb-application-contract-card__section-wrapper"
  }, _vm._l(_vm.sections, function (section, sectionIndex) {
    return _c('div', {
      key: section.label + "-" + sectionIndex,
      staticClass: "bb-application-contract-card__section",
      class: "bb-application-contract-card__section-" + sectionIndex
    }, [_c('bb-labeled-value', {
      attrs: {
        "loading": _vm.loading,
        "label": section.label,
        "label-position": _vm.labeledValueLabelPosition,
        "strike-through-value": section.strikeThroughContent,
        "strike-through-aria-label-partial": _vm.strikeThroughAriaLabelPartial,
        "color": _vm.setSectionContentColor(sectionIndex, section.highlighted, section.strikeThroughContent),
        "size": "18",
        "font-weight": "bold",
        "label-uppercase": ""
      },
      scopedSlots: _vm._u([{
        key: "value",
        fn: function () {
          return [_vm._v("\n            " + _vm._s(section.content) + "\n            "), section.tooltip ? _c('bb-tooltip', [_vm._v("\n              " + _vm._s(section.tooltip) + "\n            ")]) : _vm._e()];
        },
        proxy: true
      }], null, true)
    })], 1);
  }), 0) : _vm._e(), _vm._v(" "), _vm.hasPrimaryButtonOrActionButtons ? _c('div', {
    staticClass: "bb-application-contract-card__button-primary"
  }, [_vm._t("actionButtons", function () {
    return [_vm.hasPrimaryButton ? [_vm.loading ? _c('bb-skeleton', {
      attrs: {
        "width": _vm.isLargeViewport ? '150px' : _vm.primaryButtonInlinedOnMobile ? '150px' : '100%',
        "height": _vm.primaryButtonInlinedOnMobile ? '30' : '40',
        "radius": "6"
      }
    }) : _c('bb-button', _vm._b({
      attrs: {
        "color": _vm.primaryButton.inverted ? 'blue' : 'mint',
        "size": _vm.primaryButtonSize,
        "aria-disabled": _vm.primaryButton.disabled
      },
      on: {
        "click": _vm.onPrimaryButtonClick
      }
    }, 'bb-button', _vm.primaryButton, false), [_vm.primaryButton.disabled && _vm.primaryButtonTooltip ? _c('div', {
      staticClass: "bb-application-contract-card__button-primary-hover-area",
      on: {
        "mouseover": _vm.togglePrimaryButtonTooltip,
        "mouseleave": _vm.togglePrimaryButtonTooltip
      }
    }, [_vm.primaryButtonTooltipVisible ? _c('bb-tooltip', {
      attrs: {
        "bubble": ""
      }
    }, [_vm._v("\n                " + _vm._s(_vm.primaryButtonTooltip) + "\n              ")]) : _vm._e()], 1) : _vm._e()])] : _vm._e()];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasSecondaryButtons && _vm.isLargeViewport ? _c('div', {
    staticClass: "bb-application-contract-card__button-secondary-desktop"
  }, [_vm._l(_vm.secondaryButtons, function (button, index) {
    return [_vm.loading ? _c('bb-skeleton', {
      key: JSON.stringify(button),
      staticClass: "m-t-5",
      attrs: {
        "width": "150",
        "height": "16"
      }
    }) : _c('bb-button', _vm._b({
      key: JSON.stringify(button),
      attrs: {
        "display": "link"
      },
      on: {
        "click": function ($event) {
          return _vm.onSecondaryButtonClick(index);
        }
      }
    }, 'bb-button', button, false))];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasSecondaryButtons && !_vm.isLargeViewport ? _c('bb-list-group', {
    staticClass: "bb-application-contract-card__button-secondary-mobile"
  }, [_vm._l(_vm.secondaryButtons, function (button, index) {
    return [_vm.loading ? _c('bb-skeleton', {
      key: JSON.stringify(button),
      class: index >= 1 ? 'nm-t-1' : '',
      attrs: {
        "width": "100%",
        "height": "41",
        "radius": "0"
      }
    }) : _c('bb-list-item', {
      key: JSON.stringify(button),
      attrs: {
        "title": button.label,
        "title-uppercase": false,
        "no-bottom-border": "",
        "type": "button",
        "button-icon": "ui-chevron-right"
      },
      on: {
        "click": function ($event) {
          return _vm.onSecondaryButtonClick(index);
        }
      }
    })];
  })], 2) : _vm._e()], 2)]);
};
var __vue_staticRenderFns__$x = [];

/* style */
const __vue_inject_styles__$x = function (inject) {
  if (!inject) return;
  inject("data-v-1631c0e5_0", {
    source: ".bb-application-contract-card{display:flex;overflow:hidden;flex-direction:column;padding:1rem;border-radius:.625rem;margin:0 auto;background:#fff;box-shadow:0 .0625rem .9375rem 0 rgba(74,74,74,.06);transition:transform .2s ease-out,box-shadow .2s ease-out}.bb-application-contract-card:hover{box-shadow:0 .0625rem 1.25rem 0 rgba(74,74,74,.1);transform:scale(1.01)}@media (min-width:992px){.bb-application-contract-card{padding:2rem}}.bb-application-contract-card__header{display:inline-grid;align-items:center;margin-top:calc(1rem / -2);grid-template:\"icon title secondary-title\" \"separator separator separator\" \"metadata metadata metadata\"/auto 1fr auto}@media (min-width:992px){.bb-application-contract-card__header{margin-top:0}}.bb-application-contract-card--has-icon .bb-application-contract-card__header{margin-top:0}.bb-application-contract-card__icon{margin-right:1.25rem;grid-area:icon}.bb-application-contract-card--type-offer .bb-application-contract-card__icon{padding:.25rem;border-radius:50%;background:#f7f7f8}.bb-application-contract-card__title{display:flex;align-items:center;color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;grid-area:title;text-transform:capitalize}@media (min-width:992px){.bb-application-contract-card__title{height:2.5rem;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem}}@media (min-width:992px){.bb-application-contract-card--type-loan .bb-application-contract-card__title{text-transform:none}}.bb-application-contract-card--type-offer .bb-application-contract-card__title{color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-transform:none}.bb-application-contract-card--has-iban .bb-application-contract-card__title{flex-direction:column;align-items:flex-start}@media (min-width:992px){.bb-application-contract-card--has-iban .bb-application-contract-card__title{flex-direction:row;align-items:center}}.bb-application-contract-card__iban-desktop{grid-area:title}.bb-application-contract-card__iban-mobile{grid-area:metadata}.bb-application-contract-card__secondary-title{display:flex;align-items:center;color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;grid-area:secondary-title;justify-self:end;text-transform:lowercase}@media (min-width:992px){.bb-application-contract-card__secondary-title{font-size:.875rem}}.bb-application-contract-card--progress-bar-visible-on-mobile .bb-application-contract-card__secondary-title{margin-top:.25rem;grid-area:metadata}@media (min-width:992px){.bb-application-contract-card--progress-bar-visible-on-mobile .bb-application-contract-card__secondary-title{margin-top:0;grid-area:secondary-title}}.bb-application-contract-card--status-inactive .bb-application-contract-card__secondary-title{color:#2485e5}.bb-application-contract-card__amount{color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;grid-area:secondary-title;justify-self:end}@media (min-width:992px){.bb-application-contract-card__amount{font-size:1.125rem}}.bb-application-contract-card--depo-awaiting-activation .bb-application-contract-card__amount{color:#9dabb9}.bb-application-contract-card__time-remaining{margin-left:.25rem;color:#57697a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;grid-area:secondary-title}@media (min-width:992px){.bb-application-contract-card__time-remaining{font-size:.875rem}}.bb-application-contract-card__loan-status-icon{margin-left:.5rem;grid-area:secondary-title}.bb-application-contract-card__progress-bar{display:none;height:.25rem;margin-top:.25rem;grid-area:separator}@media (min-width:992px){.bb-application-contract-card__progress-bar{display:block;margin-top:.5rem}}.bb-application-contract-card--progress-bar-visible-on-mobile .bb-application-contract-card__progress-bar{display:block}.bb-application-contract-card__progress-bar .bb-progress-bar__progress{height:.25rem}.bb-application-contract-card__title-separator{border-color:#dee3e8;margin-top:.5rem;grid-area:separator}.bb-application-contract-card--has-iban .bb-application-contract-card__title-separator,.bb-application-contract-card--has-sections .bb-application-contract-card__title-separator{display:none}@media (min-width:992px){.bb-application-contract-card--has-iban .bb-application-contract-card__title-separator,.bb-application-contract-card--has-sections .bb-application-contract-card__title-separator{display:block}}.bb-application-contract-card--type-loan.bb-application-contract-card--status-closed .bb-application-contract-card__title-separator,.bb-application-contract-card--type-loan.bb-application-contract-card--status-terminated .bb-application-contract-card__title-separator{display:none}@media (min-width:992px){.bb-application-contract-card--type-loan.bb-application-contract-card--status-closed .bb-application-contract-card__title-separator,.bb-application-contract-card--type-loan.bb-application-contract-card--status-terminated .bb-application-contract-card__title-separator{display:block}}.bb-application-contract-card__textual-progress{display:none;margin-top:.25rem;color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;grid-area:metadata}@media (min-width:992px){.bb-application-contract-card__textual-progress{display:block;margin-top:.5rem;font-size:.875rem}}.bb-application-contract-card--progress-bar-visible-on-mobile .bb-application-contract-card__textual-progress{display:block}.bb-application-contract-card__textual-progress-value{color:#38414d}.bb-application-contract-card__body{position:relative;display:grid;margin-top:.5rem;grid-template:\"subtitle inlined-button\" \"description inlined-button\" \"actions actions\" \"actions actions\"/1fr auto}@media (min-width:992px){.bb-application-contract-card__body{margin-top:1.5rem;grid-template:\"subtitle inlined-button\" \"description inlined-button\" \"actions actions\"/1fr auto}}@media (min-width:992px){.bb-application-contract-card--primary-button-inlined .bb-application-contract-card__body{gap:0 2rem}}.bb-application-contract-card--depo-type-savings.bb-application-contract-card--loading .bb-application-contract-card__body{margin-top:.5rem}@media (min-width:992px){.bb-application-contract-card--depo-type-savings.bb-application-contract-card--loading .bb-application-contract-card__body{margin-top:1.5rem}}.bb-application-contract-card--depo-type-savings.bb-application-contract-card--has-iban .bb-application-contract-card__body{margin-top:0}@media (min-width:992px){.bb-application-contract-card--depo-type-savings.bb-application-contract-card--has-iban .bb-application-contract-card__body{margin-top:1.5rem}}.bb-application-contract-card--depo-type-savings.bb-application-contract-card--status-closed .bb-application-contract-card__body{margin-top:0}@media (min-width:992px){.bb-application-contract-card--type-loan.bb-application-contract-card--status-closed .bb-application-contract-card__body{margin-top:0}}.bb-application-contract-card--body-has-padding-on-mobile .bb-application-contract-card__body{padding-top:1rem}@media (min-width:992px){.bb-application-contract-card--body-has-padding-on-mobile .bb-application-contract-card__body{padding:0}}.bb-application-contract-card--primary-button-inlined-on-mobile .bb-application-contract-card__body::before,.bb-application-contract-card--status-terminated .bb-application-contract-card__body::before{position:absolute;width:calc(100% + 2 * 1rem);border-top:solid .0625rem #eeeff2;margin-left:-1rem;content:\"\"}@media (min-width:992px){.bb-application-contract-card--primary-button-inlined-on-mobile .bb-application-contract-card__body::before,.bb-application-contract-card--status-terminated .bb-application-contract-card__body::before{content:none}}.bb-application-contract-card__subtitle{margin-bottom:.625rem;color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}@media (min-width:992px){.bb-application-contract-card__subtitle{margin-bottom:.5rem;font-size:1rem;grid-area:subtitle}}.bb-application-contract-card--type-offer .bb-application-contract-card__subtitle{display:none}.bb-application-contract-card__description{color:#57697a;font-size:.75rem;grid-area:description}.bb-application-contract-card__section-wrapper{display:flex;flex-wrap:wrap;margin-right:-1rem;margin-left:-1rem;grid-column:1;grid-row:1/3}@media (min-width:992px){.bb-application-contract-card__section-wrapper{flex-wrap:nowrap;margin:0}}.bb-application-contract-card__section{display:flex;width:50%;align-items:flex-start;justify-content:center;padding:1rem;text-align:center}@media (min-width:992px){.bb-application-contract-card__section{overflow:hidden;width:unset;flex:0 0 10rem;justify-content:flex-start;padding:0;text-align:inherit}}@media (min-width:992px){.bb-application-contract-card__section .bb-labeled-value{overflow:hidden}}@media (min-width:992px){.bb-application-contract-card__section .bb-labeled-value,.bb-application-contract-card__section .bb-labeled-value__value{overflow:hidden;width:100%;text-overflow:ellipsis}}.bb-application-contract-card--secondary-mobile-buttons-have-margin:not(.bb-application-contract-card--primary-button-inlined-on-mobile) .bb-application-contract-card__section{border-bottom:solid .0625rem #eeeff2}.bb-application-contract-card--type-loan .bb-application-contract-card__section-0,.bb-application-contract-card--type-loan .bb-application-contract-card__section-1{border-top:solid .0625rem #eeeff2}.bb-application-contract-card--type-loan .bb-application-contract-card__section-1{border-left:solid .0625rem #eeeff2}.bb-application-contract-card--type-loan .bb-application-contract-card__section-2{display:none}.bb-application-contract-card--type-loan .bb-application-contract-card__section-3{flex:1;order:1}.bb-application-contract-card--type-loan .bb-application-contract-card__section-3 .bb-labeled-value__value{white-space:inherit}@media (min-width:992px){.bb-application-contract-card--type-loan .bb-application-contract-card__section-3 .bb-labeled-value__value{white-space:nowrap}}.bb-application-contract-card--depo-type-term .bb-application-contract-card__section-0{display:none}.bb-application-contract-card--depo-type-term .bb-application-contract-card__section-1,.bb-application-contract-card--depo-type-term .bb-application-contract-card__section-2{border-top:solid .0625rem #eeeff2}.bb-application-contract-card--depo-type-term .bb-application-contract-card__section-2{border-left:solid .0625rem #eeeff2}.bb-application-contract-card--depo-type-term .bb-application-contract-card__section-3{width:100%;order:1;border-top:solid .0625rem #eeeff2}.bb-application-contract-card--depo-type-term.bb-application-contract-card--status-closed .bb-application-contract-card__section-0,.bb-application-contract-card--depo-type-term.bb-application-contract-card--status-closed .bb-application-contract-card__section-1{border-top:solid .0625rem #eeeff2}.bb-application-contract-card--depo-type-term.bb-application-contract-card--status-closed .bb-application-contract-card__section-0{display:flex}.bb-application-contract-card--depo-type-term.bb-application-contract-card--status-closed .bb-application-contract-card__section-1{border-left:solid .0625rem #eeeff2}.bb-application-contract-card--depo-type-term.bb-application-contract-card--status-closed .bb-application-contract-card__section-2{width:100%;order:1;border-top:solid .0625rem #eeeff2;border-left:none}.bb-application-contract-card--depo-type-term.bb-application-contract-card--status-closed .bb-application-contract-card__section-3{display:none}.bb-application-contract-card--depo-type-savings .bb-application-contract-card__section-0,.bb-application-contract-card--depo-type-savings .bb-application-contract-card__section-1,.bb-application-contract-card--depo-type-savings .bb-application-contract-card__section-2{border-top:solid .0625rem #eeeff2}.bb-application-contract-card--depo-type-savings .bb-application-contract-card__section-0{width:100%;order:1}.bb-application-contract-card--depo-type-savings .bb-application-contract-card__section-2{border-left:solid .0625rem #eeeff2}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-loan .bb-application-contract-card__section-0{flex-grow:1;justify-content:flex-start;border-top:none}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-loan .bb-application-contract-card__section-1,.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-loan .bb-application-contract-card__section-2,.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-loan .bb-application-contract-card__section-3{display:none}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo .bb-application-contract-card__section-1{flex-grow:1;justify-content:flex-start;border-top:none}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo .bb-application-contract-card__section-0,.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo .bb-application-contract-card__section-2,.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo .bb-application-contract-card__section-3{display:none}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo.bb-application-contract-card--status-inactive .bb-application-contract-card__section-0{display:none}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo.bb-application-contract-card--status-inactive .bb-application-contract-card__section-1,.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo.bb-application-contract-card--status-inactive .bb-application-contract-card__section-2{display:flex}.bb-application-contract-card--primary-button-inlined-on-mobile.bb-application-contract-card--type-depo.bb-application-contract-card--status-closed .bb-application-contract-card__section-0{display:none}@media (min-width:992px){.bb-application-contract-card--secondary-mobile-buttons-have-margin:not(.bb-application-contract-card--primary-button-inlined-on-mobile) .bb-application-contract-card__section,.bb-application-contract-card--type-depo.bb-application-contract-card--depo-type-savings .bb-application-contract-card__section,.bb-application-contract-card--type-depo.bb-application-contract-card--depo-type-term .bb-application-contract-card__section,.bb-application-contract-card--type-loan .bb-application-contract-card__section{display:flex;width:unset;order:unset;border:none}}.bb-application-contract-card__button-primary{margin-top:1rem;grid-column:1/span 2;grid-row:3}@media (min-width:992px){.bb-application-contract-card__button-primary{margin:0;grid-area:actions;place-self:end end}}.bb-application-contract-card--primary-button-inlined .bb-application-contract-card__button-primary{grid-area:inlined-button}@media (min-width:992px){.bb-application-contract-card--primary-button-inlined .bb-application-contract-card__button-primary{align-self:center;grid-area:inlined-button}}.bb-application-contract-card--primary-button-inlined-on-mobile .bb-application-contract-card__button-primary{align-self:center;margin:0}.bb-application-contract-card__button-primary-hover-area{position:absolute;inset:0}.bb-application-contract-card__button-primary .bb-button{width:100%}@media (min-width:992px){.bb-application-contract-card__button-primary .bb-button{width:unset}}.bb-application-contract-card__button-secondary-desktop{display:flex;align-self:end;margin-top:2.8125rem;gap:2rem;grid-area:actions}.bb-application-contract-card--status-closed:not(.bb-application-contract-card--has-sections) .bb-application-contract-card__button-secondary-desktop{margin-top:2rem}.bb-application-contract-card__button-secondary-mobile{display:flex;flex-direction:column;margin-right:-1rem;margin-bottom:-1rem;margin-left:-1rem;grid-column:1/span 2;grid-row:4}.bb-application-contract-card--secondary-mobile-buttons-have-margin .bb-application-contract-card__button-secondary-mobile{margin-top:1rem}.bb-application-contract-card__button-secondary-mobile .bb-button.bb-button.bb-button{padding-left:1rem;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-application-contract-card--primary-button-inlined-on-mobile .bb-application-contract-card__button-secondary-mobile{margin-top:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$x = undefined;
/* module identifier */
const __vue_module_identifier__$x = undefined;
/* functional template */
const __vue_is_functional_template__$x = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$x = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$x,
  staticRenderFns: __vue_staticRenderFns__$x
}, __vue_inject_styles__$x, __vue_script__$x, __vue_scope_id__$x, __vue_is_functional_template__$x, __vue_module_identifier__$x, false, createInjector, undefined, undefined);

var Icon$8 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 40 40"}},[_c('circle',{attrs:{"cx":"19.9","cy":"19.9","r":"19.9","fill":"#f7f7f7"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M27.47,30.66a1.34,1.34,0,0,0,1.33-1.34V10.55a1.34,1.34,0,0,0-1.33-1.33H14a1.33,1.33,0,0,0-1.33,1.33V29.32A1.33,1.33,0,0,0,14,30.66Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M14,10.11a.45.45,0,0,0-.44.44V29.32a.44.44,0,0,0,.44.44H27.47a.45.45,0,0,0,.45-.44V10.55a.45.45,0,0,0-.45-.44Z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M16.33,15a1.1,1.1,0,1,1,1.08-1.1A1.1,1.1,0,0,1,16.33,15Zm0,4.06a1.11,1.11,0,1,1,1.08-1.1A1.1,1.1,0,0,1,16.33,19Zm0,4.05A1.1,1.1,0,1,1,17.41,22,1.1,1.1,0,0,1,16.33,23.07Zm0,4.06A1.1,1.1,0,1,1,17.41,26,1.1,1.1,0,0,1,16.33,27.13Z"}}),_c('path',{attrs:{"fill":"#3cc88c","d":"M25.71,14.31v.32h-6.3a.77.77,0,0,1,0-1.54h6.3a.77.77,0,0,1,0,1.54Zm0,4.06v.31h-6.3a.77.77,0,0,1,0-1.53h6.3a.77.77,0,0,1,0,1.53Zm0,4.05v.32h-6.3a.77.77,0,0,1,0-1.54h6.3a.77.77,0,0,1,0,1.54Zm0,4.06v.32h-6.3a.77.77,0,0,1,0-1.54h6.3a.77.77,0,0,1,0,1.54Z"}})]) } };

Vue.component('icon-client-offer', Icon$8);

var script$w = {
  name: 'bb-application-phase-card',
  components: {
    BbIcon: __vue_component__$2C,
    BbIconFrame: __vue_component__$2g,
    BbCard: __vue_component__$2t
  },
  props: {
    amount: {
      type: [String, Number],
      required: true
    },
    currency: {
      type: String,
      default: '€'
    },
    productType: {
      type: String,
      required: true
    },
    status: {
      type: String,
      required: true
    },
    statuses: {
      type: Object,
      required: true
    },
    icons: {
      type: Object,
      required: true
    },
    titles: {
      type: Object,
      required: true
    },
    greenStatusStates: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    title() {
      return this.titles[this.productType] || 'Product type not found';
    },
    iconForCurrentStatus() {
      return this.icons[this.status];
    },
    statusText() {
      return this.statuses[this.status] || '';
    },
    isMint() {
      return this.greenStatusStates.includes(this.status);
    }
  }
};

/* script */
const __vue_script__$w = script$w;

/* template */
var __vue_render__$w = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-card', {
    staticClass: "bb-application-phase-card"
  }, [_c('section', {
    staticClass: "bb-application-phase-card__row"
  }, [_c('div', {
    staticClass: "bb-application-phase-card__icon"
  }, [_vm.iconForCurrentStatus ? _c('bb-icon', {
    attrs: {
      "name": _vm.iconForCurrentStatus.icon || _vm.iconForCurrentStatus,
      "fill": _vm.iconForCurrentStatus.fill || 'original',
      "size": "fluid",
      "data-testid": "bb-application-phase-card__icon"
    }
  }) : _c('bb-icon-frame', {
    attrs: {
      "icon": "ui-alert-circle-outline",
      "icon-color": "yellow",
      "background-color": "gray-10",
      "shape": "circle",
      "data-testid": "bb-application-phase-card__icon-frame"
    }
  })], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-application-phase-card__status",
    class: {
      'bb-application-phase-card__status--mint': _vm.isMint
    }
  }, [_c('span', {
    staticClass: "bb-application-phase-card__status-title"
  }, [_vm._v("\n        " + _vm._s(_vm.title) + "\n      ")]), _vm._v(" "), _c('span', {
    staticClass: "bb-application-phase-card__status-text"
  }, [_vm._v("\n        " + _vm._s(_vm.statusText) + "\n      ")])]), _vm._v(" "), _c('div', {
    staticClass: "bb-application-phase-card__amount"
  }, [_vm._v("\n      " + _vm._s(_vm.amount) + " " + _vm._s(_vm.currency) + "\n    ")])]), _vm._v(" "), _vm._t("default")], 2);
};
var __vue_staticRenderFns__$w = [];

/* style */
const __vue_inject_styles__$w = function (inject) {
  if (!inject) return;
  inject("data-v-0ec34bfb_0", {
    source: ".bb-application-phase-card__row{display:flex;justify-content:space-between;padding-bottom:.625rem;border-bottom:.1875rem solid #dee3e8;margin-bottom:.9375rem}.bb-application-phase-card__row>*{flex:1 0 0;margin:0 auto;color:#57697a}.bb-application-phase-card__status{font-size:.875rem}.bb-application-phase-card__status-title{display:block;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-transform:uppercase}.bb-application-phase-card__status-text{color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem}.bb-application-phase-card__status--mint .bb-application-phase-card__status-text{color:#0ccca2}.bb-application-phase-card__icon{max-width:2.625rem;margin-right:.9375rem}.bb-application-phase-card__amount{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-align:right;white-space:nowrap}.bb-application-phase-card.bb-card{padding:.9375rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$w = undefined;
/* module identifier */
const __vue_module_identifier__$w = undefined;
/* functional template */
const __vue_is_functional_template__$w = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$w = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$w,
  staticRenderFns: __vue_staticRenderFns__$w
}, __vue_inject_styles__$w, __vue_script__$w, __vue_scope_id__$w, __vue_is_functional_template__$w, __vue_module_identifier__$w, false, createInjector, undefined, undefined);

var script$v = Vue.extend({
  name: 'bb-back-navigation-modal',
  components: {
    BbButton: __vue_component__$2v,
    BbModal: __vue_component__$W,
    FocusTrap
  },
  props: {
    description: {
      type: String,
      required: true
    },
    buttonLabel: {
      type: String,
      required: true
    },
    /**
     * Array of query parameters to check when deciding if back navigation was used or not.
     * When params not provided, only path check is done.
     */
    queryParamsToCheck: {
      type: Array,
      default: () => [],
      required: false
    }
  },
  data() {
    return {
      showModal: false,
      previousRoute: null
    };
  },
  computed: {
    hasRouter() {
      return !!this.$router;
    }
  },
  beforeMount() {
    if (this.hasRouter) {
      this.$router.beforeEach((toRoute, fromRoute, next) => {
        const previousPath = this.previousRoute && this.previousRoute.path;
        const pathIsSame = toRoute.path === previousPath;
        const queryParamsAreSame = this.queryParamsToCheck.every(key => {
          var _this$previousRoute, _this$previousRoute$q, _toRoute$query;
          return ((_this$previousRoute = this.previousRoute) === null || _this$previousRoute === void 0 ? void 0 : (_this$previousRoute$q = _this$previousRoute.query) === null || _this$previousRoute$q === void 0 ? void 0 : _this$previousRoute$q[key]) === ((_toRoute$query = toRoute.query) === null || _toRoute$query === void 0 ? void 0 : _toRoute$query[key]);
        });
        if (pathIsSame && queryParamsAreSame) {
          this.open();
          next(false);
          return;
        }
        this.previousRoute = fromRoute;
        next();
      });
    }
  },
  methods: {
    open() {
      this.showModal = true;
    },
    close() {
      this.showModal = false;
    }
  }
});

/* script */
const __vue_script__$v = script$v;

/* template */
var __vue_render__$v = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('focus-trap', {
    attrs: {
      "active": _vm.showModal
    }
  }, [_c('bb-modal', {
    staticClass: "bb-back-navigation-modal",
    attrs: {
      "visible": _vm.showModal,
      "flavor": "user"
    },
    on: {
      "close": _vm.close
    }
  }, [_c('p', {
    staticClass: "bb-back-navigation-modal__description"
  }, [_vm._v("\n      " + _vm._s(_vm.description) + "\n    ")]), _vm._v(" "), _c('div', {
    staticClass: "bb-back-navigation-modal__confirmation-button-wrapper"
  }, [_c('bb-button', {
    staticClass: "bb-back-navigation-modal__confirmation-button",
    attrs: {
      "color": "mint",
      "label": _vm.buttonLabel
    },
    on: {
      "click": _vm.close
    }
  })], 1)])], 1);
};
var __vue_staticRenderFns__$v = [];

/* style */
const __vue_inject_styles__$v = function (inject) {
  if (!inject) return;
  inject("data-v-0fa2eeac_0", {
    source: ".bb-back-navigation-modal__description{margin:.625rem;font-size:.875rem}.bb-back-navigation-modal__confirmation-button{flex-grow:1;margin:.9375rem .3125rem 0 .3125rem}.bb-back-navigation-modal__confirmation-button-wrapper{display:flex}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$v = undefined;
/* module identifier */
const __vue_module_identifier__$v = undefined;
/* functional template */
const __vue_is_functional_template__$v = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$v = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$v,
  staticRenderFns: __vue_staticRenderFns__$v
}, __vue_inject_styles__$v, __vue_script__$v, __vue_scope_id__$v, __vue_is_functional_template__$v, __vue_module_identifier__$v, false, createInjector, undefined, undefined);

var script$u = {
  name: 'bb-blog-section',
  components: {
    BbHeading: __vue_component__$2j,
    BbButton: __vue_component__$2v,
    BbBlogFeaturedCard: __vue_component__$1C,
    BbBlogTopicCard: __vue_component__$1z
  },
  props: {
    heading: Object,
    link: Object,
    featuredCards: Array,
    topicCards: Array
  },
  computed: {
    computedFeaturedCards() {
      var _this$featuredCards;
      const maxCards = 2;
      return (_this$featuredCards = this.featuredCards) === null || _this$featuredCards === void 0 ? void 0 : _this$featuredCards.slice(0, maxCards);
    },
    computedTopicCards() {
      var _this$topicCards;
      const maxCards = 3;
      return (_this$topicCards = this.topicCards) === null || _this$topicCards === void 0 ? void 0 : _this$topicCards.slice(0, maxCards);
    }
  }
};

/* script */
const __vue_script__$u = script$u;

/* template */
var __vue_render__$u = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-blog-section"
  }, [_c('div', {
    staticClass: "bb-blog-section__header"
  }, [_c('bb-heading', _vm._b({}, 'bb-heading', _vm.heading, false)), _vm._v(" "), _vm.link ? _c('bb-button', {
    staticClass: "bb-blog-section__header-link",
    attrs: {
      "to": _vm.link.to,
      "label": _vm.link.label,
      "icon-after": "ui-chevron-right",
      "display": "link",
      "no-underline": ""
    }
  }) : _vm._e()], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-blog-section__content"
  }, [_vm._l(_vm.computedFeaturedCards, function (card, featuredIndex) {
    return _c('bb-blog-featured-card', _vm._b({
      key: featuredIndex,
      staticClass: "bb-blog-section__featured-card"
    }, 'bb-blog-featured-card', card, false));
  }), _vm._v(" "), _vm.topicCards ? _c('div', {
    staticClass: "bb-blog-section__content-topic-list"
  }, _vm._l(_vm.computedTopicCards, function (card, topicIndex) {
    return _c('bb-blog-topic-card', _vm._b({
      key: topicIndex,
      staticClass: "bb-blog-section__topic-card"
    }, 'bb-blog-topic-card', card, false));
  }), 1) : _vm._e()], 2), _vm._v(" "), _vm.link ? _c('bb-button', {
    staticClass: "bb-blog-section__header-link-mobile",
    attrs: {
      "to": _vm.link.to,
      "label": _vm.link.label,
      "icon-after": "ui-chevron-right",
      "display": "link",
      "no-underline": ""
    }
  }) : _vm._e()], 1);
};
var __vue_staticRenderFns__$u = [];

/* style */
const __vue_inject_styles__$u = function (inject) {
  if (!inject) return;
  inject("data-v-ced291fa_0", {
    source: ".bb-blog-section .bb-blog-section__header{display:flex;align-items:center;justify-content:space-between;margin-bottom:2rem}@media (min-width:768px){.bb-blog-section .bb-blog-section__header{margin-bottom:3.625rem}}@media (min-width:992px){.bb-blog-section .bb-blog-section__header{margin-bottom:4.5rem}}@media (min-width:1200px){.bb-blog-section .bb-blog-section__header{margin-bottom:3.75rem}}.bb-blog-section .bb-blog-section__header-link,.bb-blog-section .bb-blog-section__header-link-mobile{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-blog-section .bb-blog-section__header-link{display:none}@media (min-width:768px){.bb-blog-section .bb-blog-section__header-link{display:inline-flex}}.bb-blog-section .bb-blog-section__header-link-mobile{margin-top:3.125rem}@media (min-width:768px){.bb-blog-section .bb-blog-section__header-link-mobile{display:none}}.bb-blog-section .bb-blog-section__content{display:flex;flex-direction:column}@media (min-width:992px){.bb-blog-section .bb-blog-section__content{flex-direction:row}}.bb-blog-section .bb-blog-section__featured-card:not(:first-child){margin-top:1.25rem}@media (min-width:992px){.bb-blog-section .bb-blog-section__featured-card:not(:first-child){margin-top:0;margin-left:1.5625rem}}.bb-blog-section .bb-blog-section__content-topic-list{display:flex;min-width:17.1875rem;flex-direction:column;justify-content:space-between;margin-top:2rem}@media (min-width:768px){.bb-blog-section .bb-blog-section__content-topic-list{flex-direction:row;margin-top:3.625rem}}@media (min-width:992px){.bb-blog-section .bb-blog-section__content-topic-list{flex-direction:column;margin-top:0;margin-left:3.5625rem}}@media (min-width:1200px){.bb-blog-section .bb-blog-section__content-topic-list{margin-left:4.1875rem}}.bb-blog-section .bb-blog-section__topic-card:not(:first-child){margin-top:1.5625rem}@media (min-width:768px){.bb-blog-section .bb-blog-section__topic-card:not(:first-child){margin-top:0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$u = undefined;
/* module identifier */
const __vue_module_identifier__$u = undefined;
/* functional template */
const __vue_is_functional_template__$u = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$u = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$u,
  staticRenderFns: __vue_staticRenderFns__$u
}, __vue_inject_styles__$u, __vue_script__$u, __vue_scope_id__$u, __vue_is_functional_template__$u, __vue_module_identifier__$u, false, createInjector, undefined, undefined);

const calculateAmortizationScheduleFixedPayment = (amount, period, interest) => {
  const compoundInterest = (1 + interest) ** period;
  return interest * amount * compoundInterest / (compoundInterest - 1);
};
const calculateFixedMonthlyPayment = (amount, period, interestRate = 0, additionalPeriodicCost = 0) => {
  const monthlyInterest = interestRate / 1200;
  const fixedMonthlyPayment = monthlyInterest === 0 ? amount / period : calculateAmortizationScheduleFixedPayment(amount, period, monthlyInterest);
  return additionalPeriodicCost + fixedMonthlyPayment;
};
const amountMinForPeriod = (amountMin, amountMax, interestRate, loanPeriod, conclusionFeePercent = 0, conclusionFeeFixed = 0, administrationFee = 0, dailyAprcLimit) => {
  const calculatedMinAmount = generateRange(amountMin, amountMax).find(loanAmount => {
    const monthlyPayment = calculateFixedMonthlyPayment(loanAmount, loanPeriod, interestRate, administrationFee);
    const dailyRate = dailyAprcRate(monthlyPayment, loanAmount, loanPeriod, conclusionFeePercent, conclusionFeeFixed);
    return dailyRate < dailyAprcLimit;
  });
  return Math.max(amountMin, Math.ceil(calculatedMinAmount !== null && calculatedMinAmount !== void 0 ? calculatedMinAmount : 0));
};
const amountMaxForPeriod = (amount, period, interestRate, paymentMax, additionalPeriodicCost = 0, maxAmount) => {
  const monthlyInterestRate = interestRate / 100 / 12;
  const compoundInterest = (1 + monthlyInterestRate) ** period;
  const amountMaxForPeriod = (paymentMax - additionalPeriodicCost) * (monthlyInterestRate / (1 - 1 / compoundInterest)) ** -1;
  return Math.min(maxAmount, Math.floor(amountMaxForPeriod));
};
const periodMinForAmount = (periodMin, periodMax, interestRate, loanAmount, conclusionFeePercent = 0, conclusionFeeFixed = 0, administrationFee = 0, paymentMax, dailyAprcLimit) => {
  const calculatedMinPeriod = generateRange(periodMin, periodMax).find(loanPeriod => {
    const monthlyPayment = calculateFixedMonthlyPayment(loanAmount, loanPeriod, interestRate, administrationFee);
    const dailyRate = dailyAprcRate(monthlyPayment, loanAmount, loanPeriod, conclusionFeePercent, conclusionFeeFixed);
    return monthlyPayment < paymentMax && dailyRate < dailyAprcLimit;
  });
  return Math.max(periodMin, calculatedMinPeriod !== null && calculatedMinPeriod !== void 0 ? calculatedMinPeriod : 0);
};
const generateRange = (start, end, step = 1) => {
  if (start > end) {
    const ascendingRange = generateRange(end, start, step);
    return ascendingRange.reverse();
  }
  return Array(Math.round((end - start) / step) + 1).fill().map((item, index) => start + index * step);
};
const dailyAprcRate = (monthlyPayment, loanAmount, loanPeriod = 1, conclusionFeePercent = 0, conclusionFeeFixed = 0) => {
  const totalConclusionFee = conclusionFeePercent ? loanAmount * conclusionFeePercent / 100 : conclusionFeeFixed;
  const interestAndFees = monthlyPayment * loanPeriod + totalConclusionFee - loanAmount;
  return interestAndFees / loanAmount / (loanPeriod * 30);
};

var script$t = {
  name: 'bb-calculator',
  components: {
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbSeparator: __vue_component__$2a,
    BbBox: __vue_component__$2x,
    BbLabeledValue: __vue_component__$2c,
    BbInput: __vue_component__$2f,
    BbStepInput: __vue_component__$M,
    BbSlider: __vue_component__$O
  },
  mixins: [validator, LocalizeNumber],
  props: {
    bigSliders: Boolean,
    showLoanAmount: Boolean,
    flavorSelfFinancing: Boolean,
    editButtons: {
      type: Boolean,
      validator: deprecated('`editButtons` is deprecated in Calculator and will be removed in a future major version.')
    },
    price: {
      type: Number,
      default: 1000
    },
    minPrice: {
      type: Number,
      default: 500
    },
    maxPrice: {
      type: Number,
      default: 25000
    },
    priceStep: {
      type: Number,
      default: 10
    },
    priceDisabled: Boolean,
    selfFinancingAmount: {
      type: Number,
      default: 0
    },
    minSelfFinancingAmount: {
      type: Number,
      default: 0
    },
    maxSelfFinancingAmount: {
      type: Number,
      default: 500
    },
    selfFinancingAmountStep: {
      type: Number,
      default: 10
    },
    selfFinancingAmountDisabled: Boolean,
    selfFinancingPercentStep: {
      type: Number,
      default: 0.1
    },
    selfFinancingPercentDisabled: Boolean,
    amount: {
      type: Number,
      default: 1000
    },
    minAmount: {
      type: Number,
      default: 100
    },
    maxAmount: {
      type: Number,
      default: 10000
    },
    amountStep: {
      type: Number,
      default: 10
    },
    amountDisabled: Boolean,
    period: {
      type: Number,
      default: 36
    },
    minPeriod: {
      type: Number,
      default: 2
    },
    maxPeriod: {
      type: Number,
      default: 72
    },
    periodStep: {
      type: Number,
      default: 1
    },
    periodDisabled: Boolean,
    withInterestInput: {
      type: Boolean,
      default: false
    },
    interestRate: {
      type: Number,
      default: 15.9
    },
    minInterestRate: {
      type: Number,
      default: 6.9
    },
    maxInterestRate: {
      type: Number,
      default: 19
    },
    maxMonthlyPayment: {
      type: Number
    },
    dailyAprcLimit: {
      type: Number
    },
    interestRateStep: {
      type: Number,
      default: 0.1
    },
    interestDisabled: Boolean,
    additionalPeriodicCost: {
      type: Number,
      default: 0
    },
    conclusionFeePercent: {
      type: Number,
      default: 0
    },
    conclusionFeeFixed: {
      type: Number,
      default: 0
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    currencySymbol: {
      type: String,
      default: '\u20ac'
    },
    loader: {
      type: Boolean,
      default: false
    },
    id: String,
    name: {
      type: String,
      required: true
    },
    hidePeriodInput: {
      type: Boolean,
      default: false
    },
    hideResult: {
      type: Boolean,
      default: false
    },
    payment: {
      type: Number,
      default: null
    },
    sliders: {
      type: Boolean,
      default: false
    },
    decimals: {
      type: [Number, String],
      default: 2
    },
    labels: Object,
    previousResult: String
  },
  emits: ['input'],
  data() {
    return {
      defaultLocale: 'et',
      inputAmount: this.amount,
      inputPrice: this.price,
      inputPeriod: this.period,
      inputInterestRate: this.interestRate,
      inputSelfFinancingAmount: clamp(this.selfFinancingAmount, this.minSelfFinancingAmount, this.maxSelfFinancingAmount)
    };
  },
  computed: {
    classes() {
      return {
        'bb-calculator--has-sliders': this.computedHasSliders,
        'bb-calculator--has-big-sliders': this.bigSliders,
        'bb-calculator--has-previous-result': this.hasPreviousResult
      };
    },
    hasPreviousResult() {
      return notEmpty(this.previousResult);
    },
    computedHasSliders() {
      return this.sliders || this.bigSliders || this.flavorSelfFinancing;
    },
    selfFinancingPercentStepDecimalPlaces() {
      return countDecimalPlaces(this.selfFinancingPercentStep);
    },
    selfFinancingAmountStepDecimalPlaces() {
      return countDecimalPlaces(this.selfFinancingAmountStep);
    },
    computedSelfFinancingPercent() {
      return this.roundAsSelfFinancingPercent(this.inputSelfFinancingAmount / this.inputPrice * 100);
    },
    minSelfFinancingPercent() {
      return this.roundAsSelfFinancingPercent(this.minSelfFinancingAmount / this.inputPrice * 100);
    },
    maxSelfFinancingPercent() {
      return this.roundAsSelfFinancingPercent(this.maxSelfFinancingAmount / this.inputPrice * 100);
    },
    computedPayment() {
      if (this.payment !== null) {
        return this.payment;
      }
      const amount = this.flavorSelfFinancing ? this.inputPrice - this.inputSelfFinancingAmount : this.inputAmount;
      return calculateFixedMonthlyPayment(amount, this.inputPeriod, this.inputInterestRate, this.additionalPeriodicCost);
    },
    computedResult() {
      return !this.loader ? this.formatMoney(this.computedPayment) : '';
    },
    monthlyPaymentUnit() {
      var _this$labels, _this$labels$monthlyP;
      if (this.loader) {
        return '';
      }
      return ((_this$labels = this.labels) === null || _this$labels === void 0 ? void 0 : (_this$labels$monthlyP = _this$labels.monthlyPayment) === null || _this$labels$monthlyP === void 0 ? void 0 : _this$labels$monthlyP.unit) || this.$t && this.tOrNull('components.calculator.monthlyPayment.unit') || '/mo';
    },
    amountTitle() {
      var _this$labels2, _this$labels2$amount;
      return ((_this$labels2 = this.labels) === null || _this$labels2 === void 0 ? void 0 : (_this$labels2$amount = _this$labels2.amount) === null || _this$labels2$amount === void 0 ? void 0 : _this$labels2$amount.title) || this.$t && this.tOrNull('components.calculator.amount.title') || 'Loan sum';
    },
    priceTitle() {
      var _this$labels3, _this$labels3$price;
      return ((_this$labels3 = this.labels) === null || _this$labels3 === void 0 ? void 0 : (_this$labels3$price = _this$labels3.price) === null || _this$labels3$price === void 0 ? void 0 : _this$labels3$price.title) || this.$t && this.tOrNull('components.calculator.price.title') || 'Vehicle price';
    },
    selfFinancingAmountTitle() {
      var _this$labels4, _this$labels4$selfFin;
      const hardDefault = this.bigSliders ? 'Downpayment' : 'Self financing amount';
      return ((_this$labels4 = this.labels) === null || _this$labels4 === void 0 ? void 0 : (_this$labels4$selfFin = _this$labels4.selfFinancingAmount) === null || _this$labels4$selfFin === void 0 ? void 0 : _this$labels4$selfFin.title) || this.$t && this.tOrNull('components.calculator.selfFinancingAmount.title') || hardDefault;
    },
    selfFinancingPercentTitle() {
      var _this$labels5, _this$labels5$selfFin;
      const hardDefault = this.bigSliders ? '' : 'Self financing amount';
      return ((_this$labels5 = this.labels) === null || _this$labels5 === void 0 ? void 0 : (_this$labels5$selfFin = _this$labels5.selfFinancingPercent) === null || _this$labels5$selfFin === void 0 ? void 0 : _this$labels5$selfFin.title) || this.$t && this.tOrNull('components.calculator.selfFinancingPercent.title') || hardDefault;
    },
    loanAmountTitle() {
      var _this$labels6, _this$labels6$loanAmo;
      return ((_this$labels6 = this.labels) === null || _this$labels6 === void 0 ? void 0 : (_this$labels6$loanAmo = _this$labels6.loanAmount) === null || _this$labels6$loanAmo === void 0 ? void 0 : _this$labels6$loanAmo.title) || this.$t && this.tOrNull('components.calculator.loanAmount.title') || 'Your loan amount';
    },
    amountUnit() {
      var _this$labels7, _this$labels7$amount;
      return ((_this$labels7 = this.labels) === null || _this$labels7 === void 0 ? void 0 : (_this$labels7$amount = _this$labels7.amount) === null || _this$labels7$amount === void 0 ? void 0 : _this$labels7$amount.unit) || this.$t && this.tOrNull('components.calculator.amount.unit', {
        currencySymbol: this.currencySymbol
      }) || '€';
    },
    priceUnit() {
      var _this$labels8, _this$labels8$price;
      return ((_this$labels8 = this.labels) === null || _this$labels8 === void 0 ? void 0 : (_this$labels8$price = _this$labels8.price) === null || _this$labels8$price === void 0 ? void 0 : _this$labels8$price.unit) || this.$t && this.tOrNull('components.calculator.price.unit', {
        currencySymbol: this.currencySymbol
      }) || '€';
    },
    selfFinancingAmountUnit() {
      var _this$labels9, _this$labels9$selfFin;
      return ((_this$labels9 = this.labels) === null || _this$labels9 === void 0 ? void 0 : (_this$labels9$selfFin = _this$labels9.selfFinancingAmount) === null || _this$labels9$selfFin === void 0 ? void 0 : _this$labels9$selfFin.unit) || this.$t && this.tOrNull('components.calculator.selfFinancingAmount.unit', {
        currencySymbol: this.currencySymbol
      }) || '€';
    },
    selfFinancingPercentUnit() {
      var _this$labels10, _this$labels10$selfFi;
      return ((_this$labels10 = this.labels) === null || _this$labels10 === void 0 ? void 0 : (_this$labels10$selfFi = _this$labels10.selfFinancingPercent) === null || _this$labels10$selfFi === void 0 ? void 0 : _this$labels10$selfFi.unit) || this.$t && this.tOrNull('components.calculator.selfFinancingPercent.unit') || '%';
    },
    amountHint() {
      var _this$labels11, _this$labels11$amount;
      return ((_this$labels11 = this.labels) === null || _this$labels11 === void 0 ? void 0 : (_this$labels11$amount = _this$labels11.amount) === null || _this$labels11$amount === void 0 ? void 0 : _this$labels11$amount.hint) || this.$t && this.tOrNull('components.calculator.amount.hint', {
        minAmount: this.localizeNumber(this.minAmount),
        maxAmount: this.localizeNumber(this.maxAmount),
        currencySymbol: this.currencySymbol
      }) || `Enter ${this.minAmount} - ${this.maxAmount}`;
    },
    periodTitle() {
      var _this$labels12, _this$labels12$period;
      return ((_this$labels12 = this.labels) === null || _this$labels12 === void 0 ? void 0 : (_this$labels12$period = _this$labels12.period) === null || _this$labels12$period === void 0 ? void 0 : _this$labels12$period.title) || this.$t && this.tOrNull('components.calculator.period.title') || 'Period';
    },
    periodUnit() {
      var _this$labels13, _this$labels13$period;
      return ((_this$labels13 = this.labels) === null || _this$labels13 === void 0 ? void 0 : (_this$labels13$period = _this$labels13.period) === null || _this$labels13$period === void 0 ? void 0 : _this$labels13$period.unit) || this.$t && this.tOrNull('components.calculator.period.unit') || 'months';
    },
    periodHint() {
      var _this$labels14, _this$labels14$period;
      return ((_this$labels14 = this.labels) === null || _this$labels14 === void 0 ? void 0 : (_this$labels14$period = _this$labels14.period) === null || _this$labels14$period === void 0 ? void 0 : _this$labels14$period.hint) || this.$t && this.tOrNull('components.calculator.period.hint', {
        minPeriod: this.minPeriod,
        maxPeriod: this.maxPeriod
      }) || `Enter ${this.minPeriod} - ${this.maxPeriod}`;
    },
    interestRateTitle() {
      var _this$labels15, _this$labels15$intere;
      return ((_this$labels15 = this.labels) === null || _this$labels15 === void 0 ? void 0 : (_this$labels15$intere = _this$labels15.interestRate) === null || _this$labels15$intere === void 0 ? void 0 : _this$labels15$intere.title) || this.$t && this.tOrNull('components.calculator.interestRate.title') || 'Interest rate';
    },
    interestRateUnit() {
      var _this$labels16, _this$labels16$intere;
      return ((_this$labels16 = this.labels) === null || _this$labels16 === void 0 ? void 0 : (_this$labels16$intere = _this$labels16.interestRate) === null || _this$labels16$intere === void 0 ? void 0 : _this$labels16$intere.unit) || this.$t && this.tOrNull('components.calculator.interestRate.unit') || '%';
    },
    interestRateHint() {
      var _this$labels17, _this$labels17$intere;
      return ((_this$labels17 = this.labels) === null || _this$labels17 === void 0 ? void 0 : (_this$labels17$intere = _this$labels17.interestRate) === null || _this$labels17$intere === void 0 ? void 0 : _this$labels17$intere.hint) || this.$t && this.tOrNull('components.calculator.interestRate.hint', {
        minInterestRate: this.minInterestRate,
        maxInterestRate: this.maxInterestRate
      }) || `Enter ${this.minInterestRate} - ${this.maxInterestRate}`;
    },
    previousResultTitle() {
      var _this$labels18, _this$labels18$previo;
      return ((_this$labels18 = this.labels) === null || _this$labels18 === void 0 ? void 0 : (_this$labels18$previo = _this$labels18.previousResult) === null || _this$labels18$previo === void 0 ? void 0 : _this$labels18$previo.title) || this.$t && this.tOrNull('components.calculator.previousResult.title', {
        currencySymbol: this.currencySymbol
      }) || `Previous monthly payment ${this.currencySymbol}`;
    },
    resultTitle() {
      var _this$labels19, _this$labels19$result;
      const hardDefault = this.bigSliders ? 'Your repayments would be' : `Monthly payment ${this.currencySymbol}`;
      return ((_this$labels19 = this.labels) === null || _this$labels19 === void 0 ? void 0 : (_this$labels19$result = _this$labels19.result) === null || _this$labels19$result === void 0 ? void 0 : _this$labels19$result.title) || this.$t && this.tOrNull('components.calculator.result.title', {
        currencySymbol: this.currencySymbol
      }) || hardDefault;
    },
    minAmountAdjusted() {
      if (this.maxMonthlyPayment && this.dailyAprcLimit) {
        return amountMinForPeriod(this.minAmount, this.maxAmount, this.inputInterestRate, this.inputPeriod, this.conclusionFeePercent, this.conclusionFeeFixed, this.additionalPeriodicCost, this.dailyAprcLimit);
      }
      return this.minAmount;
    },
    maxAmountAdjusted() {
      return this.maxMonthlyPayment && this.dailyAprcLimit ? amountMaxForPeriod(this.inputAmount, this.inputPeriod, this.inputInterestRate, this.maxMonthlyPayment, this.additionalPeriodicCost, this.maxAmount) : this.maxAmount;
    },
    minPeriodAdjusted() {
      return this.maxMonthlyPayment && this.dailyAprcLimit ? periodMinForAmount(this.minPeriod, this.maxPeriod, this.inputInterestRate, this.inputAmount, this.conclusionFeePercent, this.conclusionFeeFixed, this.additionalPeriodicCost, this.maxMonthlyPayment, this.dailyAprcLimit) : this.minPeriod;
    },
    loanAmount() {
      const loanAmount = this.inputPrice - this.inputSelfFinancingAmount;
      return this.formatMoney(loanAmount);
    }
  },
  watch: {
    amount(newValue) {
      this.inputAmount = +newValue;
    },
    price(newValue) {
      this.inputPrice = +newValue;
    },
    selfFinancingAmount(newValue) {
      this.inputSelfFinancingAmount = clamp(newValue, this.minSelfFinancingAmount, this.maxSelfFinancingAmount);
      if (this.inputSelfFinancingAmount !== this.selfFinancingAmount) {
        this.onInput();
      }
    },
    minSelfFinancingAmount() {
      if (this.bigSliders && this.inputSelfFinancingAmount < this.minSelfFinancingAmount) {
        this.inputSelfFinancingAmount = this.minSelfFinancingAmount;
        this.onInput();
      }
    },
    maxSelfFinancingAmount() {
      if (this.bigSliders && this.inputSelfFinancingAmount > this.maxSelfFinancingAmount) {
        this.inputSelfFinancingAmount = this.maxSelfFinancingAmount;
        this.onInput();
      }
    },
    period(newValue) {
      this.inputPeriod = +newValue;
    },
    interestRate(newValue) {
      this.inputInterestRate = +newValue;
    }
  },
  mounted() {
    if (this.inputSelfFinancingAmount !== this.selfFinancingAmount) {
      this.onInput();
    }
  },
  methods: {
    roundAsSelfFinancingPercent(val) {
      const rounded = Math.round(val / this.selfFinancingPercentStep) * this.selfFinancingPercentStep;
      return +rounded.toFixed(this.selfFinancingPercentStepDecimalPlaces);
    },
    roundAsSelfFinancingAmount(val) {
      const rounded = Math.round(val / this.selfFinancingAmountStep) * this.selfFinancingAmountStep;
      return +rounded.toFixed(this.selfFinancingAmountStepDecimalPlaces);
    },
    tOrNull(path) {
      const t = this.$t(path);
      return t === path ? null : t;
    },
    formatMoney(number) {
      return number.toLocaleString(this.locale || this.defaultLocale, {
        style: 'currency',
        useGrouping: true,
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    onSelfFinancingPriceBlur() {
      if (!this.inputPrice) this.inputPrice = this.minPrice;
    },
    onSelfFinancingPeriodBlur() {
      if (!this.inputPeriod) this.inputPeriod = this.minPeriodAdjusted;
    },
    onInterestInputBlur() {
      if (!this.inputInterestRate) this.inputInterestRate = this.minInterestRate;
    },
    onSelfFinancingAmountBlur() {
      if (!this.$refs.selfFinancingAmount || !this.$refs.selfFinancingAmount.$refs.input) return;
      if (!this.$refs.selfFinancingAmount.$refs.input.$ci.numberValue) {
        const amount = clamp(this.inputSelfFinancingAmount, this.minSelfFinancingAmount, this.maxSelfFinancingAmount);
        this.$refs.selfFinancingAmount.$refs.input.$ci.numberValue = amount;
      }
      this.validateSelfFinancingAmount();
    },
    onSelfFinancingPercentBlur() {
      if (!this.$refs.selfFinancingPercent || !this.$refs.selfFinancingPercent.$refs.input) return;
      if (!this.$refs.selfFinancingPercent.$refs.input.$ci.numberValue) {
        this.$refs.selfFinancingPercent.$refs.input.$ci.numberValue = this.computedSelfFinancingPercent;
      }
    },
    onInput() {
      const payload = this.flavorSelfFinancing ? {
        price: this.inputPrice === null ? this.price : this.inputPrice,
        selfFinancingAmount: +this.inputSelfFinancingAmount,
        period: this.inputPeriod === null ? this.period : this.inputPeriod,
        interestRate: this.inputInterestRate === null ? this.interestRate : this.inputInterestRate
      } : {
        amount: this.inputAmount,
        ...(!this.hidePeriodInput && {
          period: this.inputPeriod
        }),
        ...(this.withInterestInput && {
          interestRate: this.inputInterestRate
        })
      };
      this.$emit('input', payload);
    },
    validateSelfFinancingAmount() {
      if (this.bigSliders) {
        const amount = clamp(this.inputSelfFinancingAmount, this.minSelfFinancingAmount, this.maxSelfFinancingAmount);
        this.onSelfFinancingAmountChange(amount);
      }
    },
    formatSelfFinancingAmount(selfFinancingAmount) {
      const clampedSelfFinancingAmount = this.bigSliders ? clamp(selfFinancingAmount, this.minSelfFinancingAmount, this.maxSelfFinancingAmount) : selfFinancingAmount;
      return this.roundAsSelfFinancingAmount(clampedSelfFinancingAmount);
    },
    onSelfFinancingAmountChange(selfFinancingAmount) {
      this.inputSelfFinancingAmount = this.formatSelfFinancingAmount(selfFinancingAmount);
      this.onInput();
    },
    onSelfFinancingPercentInput(selfFinancingPercent) {
      const inputSelfFinancingAmount = selfFinancingPercent / 100 * this.inputPrice;
      this.inputSelfFinancingAmount = this.roundAsSelfFinancingAmount(inputSelfFinancingAmount);
      this.onInput();
    },
    onSelfFinancingPercentChange(selfFinancingPercent) {
      const percent = selfFinancingPercent !== null && selfFinancingPercent !== void 0 ? selfFinancingPercent : this.inputSelfFinancingPercent;
      const inputSelfFinancingAmount = percent / 100 * this.inputPrice;
      this.inputSelfFinancingAmount = this.formatSelfFinancingAmount(inputSelfFinancingAmount);
      this.onInput();
    }
  }
};

/* script */
const __vue_script__$t = script$t;

/* template */
var __vue_render__$t = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-calculator",
    class: _vm.classes,
    attrs: {
      "id": _vm.id
    }
  }, [_vm.computedHasSliders ? [_vm.flavorSelfFinancing ? [_c('bb-slider', {
    staticClass: "bb-calculator__slider bb-calculator__price-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-price",
      "label": _vm.priceTitle,
      "unit": _vm.priceUnit,
      "min": _vm.minPrice,
      "max": _vm.maxPrice,
      "step": _vm.priceStep,
      "disabled": _vm.priceDisabled,
      "locale": _vm.locale,
      "min-decimals": 0,
      "max-decimals": 2,
      "name": _vm.name + "-price",
      "type": "number",
      "edit-button": _vm.editButtons,
      "size": _vm.bigSliders ? 'lg' : 'md'
    },
    on: {
      "input": _vm.onInput,
      "blur": _vm.onSelfFinancingPriceBlur
    },
    model: {
      value: _vm.inputPrice,
      callback: function ($$v) {
        _vm.inputPrice = $$v;
      },
      expression: "inputPrice"
    }
  }), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-calculator__self-financing-row",
    attrs: {
      "align": "end",
      "gutter": "20"
    }
  }, [_c('bb-col', [_vm.bigSliders ? _c('bb-input', {
    ref: "selfFinancingAmount",
    staticClass: "bb-calculator__slider bb-calculator__self-financing-amount-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-self-financing-amount",
      "value": _vm.inputSelfFinancingAmount,
      "currency": "",
      "framed": _vm.bigSliders,
      "label": _vm.bigSliders ? _vm.selfFinancingAmountTitle : null,
      "add-on": _vm.bigSliders ? _vm.selfFinancingAmountUnit : null,
      "unit": _vm.selfFinancingAmountUnit,
      "min": _vm.minSelfFinancingAmount,
      "max": _vm.maxSelfFinancingAmount,
      "step": _vm.selfFinancingAmountStep,
      "locale": _vm.locale,
      "name": _vm.name + "-selfFinancingAmount",
      "disabled": _vm.selfFinancingAmountDisabled,
      "edit-button": _vm.editButtons
    },
    on: {
      "change": _vm.onSelfFinancingAmountChange,
      "blur": _vm.onSelfFinancingAmountBlur
    }
  }) : _c('bb-slider', {
    staticClass: "bb-calculator__slider bb-calculator__self-financing-amount-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-self-financing-amount",
      "value": _vm.inputSelfFinancingAmount,
      "framed": _vm.bigSliders,
      "label": _vm.selfFinancingAmountTitle,
      "add-on": _vm.bigSliders ? _vm.selfFinancingAmountUnit : null,
      "unit": _vm.selfFinancingAmountUnit,
      "min": _vm.minSelfFinancingAmount,
      "max": _vm.maxSelfFinancingAmount,
      "step": _vm.selfFinancingAmountStep,
      "locale": _vm.locale,
      "name": _vm.name + "-selfFinancingAmount",
      "type": "number",
      "disabled": _vm.selfFinancingAmountDisabled,
      "edit-button": _vm.editButtons
    },
    on: {
      "input": _vm.onInput,
      "change": _vm.validateSelfFinancingAmount
    },
    model: {
      value: _vm.inputSelfFinancingAmount,
      callback: function ($$v) {
        _vm.inputSelfFinancingAmount = $$v;
      },
      expression: "inputSelfFinancingAmount"
    }
  })], 1), _vm._v(" "), _c('bb-col', [_vm.bigSliders ? _c('bb-input', {
    ref: "selfFinancingPercent",
    staticClass: "bb-calculator__slider bb-calculator__self-financing-percent-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-self-financing-percent",
      "currency": "",
      "framed": _vm.bigSliders,
      "label": _vm.bigSliders ? _vm.selfFinancingPercentTitle : null,
      "add-on": _vm.bigSliders ? _vm.selfFinancingPercentUnit : null,
      "unit": _vm.selfFinancingPercentUnit,
      "min": _vm.minSelfFinancingPercent,
      "max": _vm.maxSelfFinancingPercent,
      "step": _vm.selfFinancingPercentStep,
      "locale": _vm.locale,
      "min-decimals": 0,
      "max-decimals": 2,
      "name": _vm.name + "-selfFinancingPercent",
      "disabled": _vm.selfFinancingPercentDisabled,
      "edit-button": _vm.editButtons,
      "value": _vm.computedSelfFinancingPercent
    },
    on: {
      "change": _vm.onSelfFinancingPercentChange,
      "blur": _vm.onSelfFinancingPercentBlur
    }
  }) : _c('bb-slider', {
    staticClass: "bb-calculator__slider bb-calculator__self-financing-percent-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-self-financing-percent",
      "framed": _vm.bigSliders,
      "label": _vm.selfFinancingAmountTitle,
      "add-on": _vm.bigSliders ? _vm.selfFinancingPercentUnit : null,
      "unit": _vm.selfFinancingPercentUnit,
      "min": _vm.minSelfFinancingPercent,
      "max": _vm.maxSelfFinancingPercent,
      "step": _vm.selfFinancingPercentStep,
      "locale": _vm.locale,
      "min-decimals": 0,
      "max-decimals": 2,
      "name": _vm.name + "-selfFinancingPercent",
      "type": "number",
      "disabled": _vm.selfFinancingPercentDisabled,
      "edit-button": _vm.editButtons,
      "value": _vm.computedSelfFinancingPercent
    },
    on: {
      "input": _vm.onSelfFinancingPercentInput,
      "change": _vm.validateSelfFinancingAmount
    }
  })], 1)], 1)] : _c('bb-slider', {
    staticClass: "bb-calculator__slider bb-calculator__amount-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-amount",
      "label": _vm.amountTitle,
      "unit": _vm.amountUnit,
      "min": _vm.minAmountAdjusted,
      "max": _vm.maxAmountAdjusted,
      "step": _vm.amountStep,
      "locale": _vm.locale,
      "name": _vm.name + "-amount",
      "disabled": _vm.amountDisabled,
      "edit-button": _vm.editButtons,
      "size": _vm.bigSliders ? 'lg' : 'md'
    },
    on: {
      "input": _vm.onInput
    },
    model: {
      value: _vm.inputAmount,
      callback: function ($$v) {
        _vm.inputAmount = $$v;
      },
      expression: "inputAmount"
    }
  }), _vm._v(" "), _vm._t("amount-alert"), _vm._v(" "), !_vm.hidePeriodInput ? _c('bb-slider', {
    staticClass: "bb-calculator__slider bb-calculator__period-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-period",
      "label": _vm.periodTitle,
      "unit": _vm.periodUnit,
      "min": _vm.minPeriodAdjusted,
      "max": _vm.maxPeriod,
      "decimals": 0,
      "step": _vm.periodStep,
      "name": _vm.name + "-period",
      "type": "number",
      "disabled": _vm.periodDisabled,
      "edit-button": _vm.editButtons,
      "size": _vm.bigSliders ? 'lg' : 'md'
    },
    on: {
      "input": _vm.onInput,
      "blur": _vm.onSelfFinancingPeriodBlur
    },
    model: {
      value: _vm.inputPeriod,
      callback: function ($$v) {
        _vm.inputPeriod = $$v;
      },
      expression: "inputPeriod"
    }
  }) : _vm._e(), _vm._v(" "), _vm.withInterestInput ? _c('bb-slider', {
    staticClass: "bb-calculator__slider bb-calculator__interest-slider",
    attrs: {
      "id": _vm.id && _vm.id + "-interest",
      "label": _vm.interestRateTitle,
      "unit": _vm.interestRateUnit,
      "min": _vm.minInterestRate,
      "max": _vm.maxInterestRate,
      "step": _vm.interestRateStep,
      "locale": _vm.locale,
      "min-decimals": 0,
      "max-decimals": 2,
      "name": _vm.name + "-interest",
      "disabled": _vm.interestDisabled,
      "edit-button": _vm.editButtons,
      "size": _vm.bigSliders ? 'lg' : 'md'
    },
    on: {
      "input": _vm.onInput,
      "blur": _vm.onInterestInputBlur
    },
    model: {
      value: _vm.inputInterestRate,
      callback: function ($$v) {
        _vm.inputInterestRate = $$v;
      },
      expression: "inputInterestRate"
    }
  }) : _vm._e(), _vm._v(" "), !_vm.hideResult && _vm.hasPreviousResult ? _c('bb-box', {
    staticClass: "bb-calculator__previous-result-for-sliders"
  }, [_c('bb-labeled-value', {
    attrs: {
      "label": _vm.previousResultTitle,
      "value": _vm.previousResult,
      "font-weight": "bold",
      "color": "gray-40",
      "label-position": "top-center",
      "label-uppercase": ""
    }
  })], 1) : _vm._e(), _vm._v(" "), !_vm.hideResult ? _c('bb-box', {
    staticClass: "bb-calculator__result-for-sliders"
  }, [_vm.bigSliders ? [_c('p', {
    staticClass: "bb-calculator__label-title"
  }, [_vm._v("\n          " + _vm._s(_vm.resultTitle) + "\n        ")]), _vm._v(" "), _c('p', {
    staticClass: "bb-calculator__result-value",
    class: {
      'bb-calculator__result-value--loader': _vm.loader
    }
  }, [_vm._v("\n          " + _vm._s(_vm.computedResult) + "\n          "), _c('span', {
    staticClass: "bb-calculator__result-value-unit"
  }, [_vm._v("\n            " + _vm._s(_vm.monthlyPaymentUnit) + "\n          ")])])] : [_c('bb-labeled-value', {
    attrs: {
      "label": _vm.resultTitle,
      "value": _vm.computedResult,
      "font-weight": "bold",
      "color": "blue",
      "label-position": "top-center",
      "label-uppercase": ""
    }
  }), _vm._v(" "), _vm.loader ? _c('div', {
    staticClass: "bb-calculator__result-value",
    class: {
      'bb-calculator__result-value--loader': _vm.loader
    }
  }) : _vm._e()], _vm._v(" "), _vm.showLoanAmount ? [_c('bb-separator', {
    staticClass: "bb-calculator__result-separator",
    attrs: {
      "dashed": ""
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-calculator__result-amount"
  }, [_c('span', {
    staticClass: "bb-calculator__result-amount-label"
  }, [_vm._v(_vm._s(_vm.loanAmountTitle))]), _vm._v(" "), _c('span', {
    staticClass: "bb-calculator__result-amount-value"
  }, [_vm._v(_vm._s(_vm.loanAmount))])])] : _vm._e()], 2) : _vm._e()] : [_c('bb-step-input', {
    attrs: {
      "id": _vm.id && _vm.id + "-amount",
      "min": _vm.minAmount,
      "max": _vm.maxAmount,
      "step": _vm.amountStep,
      "value": _vm.amount,
      "locale": _vm.locale,
      "decimals": _vm.decimals,
      "name": _vm.name + "-amount",
      "disabled": _vm.amountDisabled
    },
    on: {
      "input": _vm.onInput
    },
    model: {
      value: _vm.inputAmount,
      callback: function ($$v) {
        _vm.inputAmount = $$v;
      },
      expression: "inputAmount"
    }
  }, [_vm._t("amount-label", function () {
    return [_c('p', {
      staticClass: "bb-calculator__label-title"
    }, [_vm._v("\n          " + _vm._s(_vm.amountTitle) + "\n        ")]), _vm._v(" "), _c('p', {
      staticClass: "bb-calculator__label-hint"
    }, [_vm._v("\n          " + _vm._s(_vm.amountHint) + "\n        ")])];
  })], 2), _vm._v(" "), _c('bb-step-input', {
    attrs: {
      "id": _vm.id && _vm.id + "-period",
      "min": _vm.minPeriod,
      "max": _vm.maxPeriod,
      "step": _vm.periodStep,
      "value": _vm.period,
      "name": _vm.name + "-period",
      "disabled": _vm.periodDisabled
    },
    on: {
      "input": _vm.onInput
    },
    model: {
      value: _vm.inputPeriod,
      callback: function ($$v) {
        _vm.inputPeriod = $$v;
      },
      expression: "inputPeriod"
    }
  }, [_vm._t("period-label", function () {
    return [_c('p', {
      staticClass: "bb-calculator__label-title"
    }, [_vm._v("\n          " + _vm._s(_vm.periodTitle) + "\n        ")]), _vm._v(" "), _c('p', {
      staticClass: "bb-calculator__label-hint"
    }, [_vm._v("\n          " + _vm._s(_vm.periodHint) + "\n        ")])];
  })], 2), _vm._v(" "), _vm.withInterestInput ? _c('bb-step-input', {
    attrs: {
      "id": _vm.id && _vm.id + "-interest",
      "min": _vm.minInterestRate,
      "max": _vm.maxInterestRate,
      "step": _vm.interestRateStep,
      "value": _vm.interestRate,
      "locale": _vm.locale,
      "decimals": _vm.decimals,
      "name": _vm.name + "-interest",
      "disabled": _vm.interestDisabled
    },
    on: {
      "input": _vm.onInput
    },
    model: {
      value: _vm.inputInterestRate,
      callback: function ($$v) {
        _vm.inputInterestRate = $$v;
      },
      expression: "inputInterestRate"
    }
  }, [_vm._t("interest-label", function () {
    return [_c('p', {
      staticClass: "bb-calculator__label-title"
    }, [_vm._v("\n          " + _vm._s(_vm.interestRateTitle) + "\n        ")]), _vm._v(" "), _c('p', {
      staticClass: "bb-calculator__label-hint"
    }, [_vm._v("\n          " + _vm._s(_vm.interestRateHint) + "\n        ")])];
  })], 2) : _vm._e(), _vm._v(" "), !_vm.hideResult ? _c('div', {
    staticClass: "bb-calculator__result"
  }, [_vm._t("result-label", function () {
    return [_c('p', {
      staticClass: "bb-calculator__label-title"
    }, [_vm._v("\n          " + _vm._s(_vm.resultTitle) + "\n        ")])];
  }), _vm._v(" "), _c('p', {
    staticClass: "bb-calculator__result-value",
    class: {
      'bb-calculator__result-value--loader': _vm.loader
    }
  }, [_vm._v("\n        " + _vm._s(_vm.computedResult) + "\n      ")])], 2) : _vm._e()]], 2);
};
var __vue_staticRenderFns__$t = [];

/* style */
const __vue_inject_styles__$t = function (inject) {
  if (!inject) return;
  inject("data-v-55a424ae_0", {
    source: ".bb-calculator{color:#00285a;text-align:center}.bb-calculator--has-big-sliders{display:flex;flex-direction:column}.bb-calculator--has-sliders{text-align:left}.bb-calculator .bb-step-input__field{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:24px}.bb-calculator__price-slider{order:1}.bb-calculator__self-financing-row{order:2}.bb-calculator--has-big-sliders .bb-calculator__self-financing-row{order:3;margin-bottom:1.4375rem}.bb-calculator__period-slider{order:3}.bb-calculator--has-big-sliders .bb-calculator__period-slider{order:2}.bb-calculator__previous-result-for-sliders,.bb-calculator__result-for-sliders{background:#fff;text-align:center}.bb-calculator__previous-result-for-sliders{margin-top:1.25rem}.bb-calculator__result-for-sliders{order:4;margin-top:1.25rem}.bb-calculator--has-previous-result .bb-calculator__result-for-sliders{border-color:#2485e5}.bb-calculator--has-big-sliders .bb-calculator__result-for-sliders{margin-top:.625rem}.bb-calculator__label-title{margin-top:.3125rem;color:#00285a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;line-height:inherit}.bb-calculator--has-sliders .bb-calculator__label-title{margin-top:0;color:#57697a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:9px;text-transform:uppercase}.bb-calculator--has-big-sliders .bb-calculator__label-title{color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:11px;text-transform:none}.bb-calculator__label-hint{margin-bottom:.3125rem;color:#00285a;font-size:12px;line-height:1em}.bb-calculator__result{margin-top:.9375rem}.bb-calculator__result-value{position:relative;min-height:24px;margin:.3125rem 0;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:24px}.bb-calculator--has-sliders .bb-calculator__result-value{margin-bottom:0;color:#2485e5}.bb-calculator--has-big-sliders .bb-calculator__result-value{margin-top:0;margin-bottom:0;color:#00285a;font-size:32px;line-height:1.45}.bb-calculator__result-value--loader::after{position:absolute;top:calc(50% - .75rem);left:calc(50% - .75rem);width:1.5rem;height:1.5rem;border:.1875rem solid rgba(255,255,255,.2);border-radius:50%;border-top-color:#00285a;animation:rotate-full .7s linear infinite;content:\"\"}.bb-calculator__result-value-unit{margin-left:.3125rem;font-family:GothamSSm-Book,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}.bb-calculator__result-separator{width:auto;margin-top:.25rem;margin-right:-.9375rem;margin-left:-.9375rem}.bb-calculator__result-amount{display:flex;align-items:center;justify-content:space-between;margin-top:.625rem;margin-bottom:-.4375rem}.bb-calculator__result-amount-label{color:#38414d;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:11px}.bb-calculator__result-amount-value{color:#2485e5;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$t = undefined;
/* module identifier */
const __vue_module_identifier__$t = undefined;
/* functional template */
const __vue_is_functional_template__$t = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$t = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$t,
  staticRenderFns: __vue_staticRenderFns__$t
}, __vue_inject_styles__$t, __vue_script__$t, __vue_scope_id__$t, __vue_is_functional_template__$t, __vue_module_identifier__$t, false, createInjector, undefined, undefined);

var script$s = {
  name: 'bb-calculator-modal',
  components: {
    BbButton: __vue_component__$2v,
    BbListItem: __vue_component__$29,
    BbListGroup: __vue_component__$2b,
    BbModal: __vue_component__$W,
    BbTooltip: __vue_component__$2B,
    BbCalculator: __vue_component__$t
  },
  props: {
    visible: Boolean,
    heading: String,
    submitLabel: String,
    submitLabelUppercase: Boolean,
    value: Object,
    listItems: Array
  },
  emits: ['close', 'submit', 'input'],
  methods: {
    onClose(eventSource) {
      this.$emit('close', {
        eventSource
      });
    },
    onSubmit(eventSource) {
      this.$emit('submit', {
        eventSource
      });
    },
    onCalculatorInput(payload) {
      this.$emit('input', payload);
    }
  }
};

/* script */
const __vue_script__$s = script$s;

/* template */
var __vue_render__$s = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('bb-modal', {
    staticClass: "bb-calculator-modal",
    attrs: {
      "visible": _vm.visible,
      "flavor": "card",
      "public-size": "m",
      "full-mobile": ""
    },
    on: {
      "close": _vm.onClose
    },
    scopedSlots: _vm._u([_vm.heading ? {
      key: "heading",
      fn: function () {
        return [_c('span', {
          staticClass: "bb-calculator-modal__heading"
        }, [_vm._v("\n      " + _vm._s(_vm.heading) + "\n    ")])];
      },
      proxy: true
    } : null, {
      key: "footer",
      fn: function () {
        return [_c('bb-button', {
          staticClass: "bb-calculator-modal__submit-button",
          attrs: {
            "label": _vm.submitLabel,
            "display": "block",
            "uppercase": _vm.submitLabelUppercase
          },
          on: {
            "click": _vm.onSubmit
          }
        })];
      },
      proxy: true
    }, {
      key: "disclaimer",
      fn: function () {
        return [_vm._t("disclaimer")];
      },
      proxy: true
    }], null, true)
  }, [_vm._v(" "), _c('bb-calculator', _vm._b({
    staticClass: "bb-calculator-modal__calculator",
    attrs: {
      "sliders": ""
    },
    on: {
      "input": _vm.onCalculatorInput
    },
    nativeOn: {
      "keydown": function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        $event.preventDefault();
        return _vm.onSubmit.apply(null, arguments);
      }
    },
    scopedSlots: _vm._u([{
      key: "amount-alert",
      fn: function () {
        return [_vm._t("amount-alert")];
      },
      proxy: true
    }], null, true)
  }, 'bb-calculator', _vm.value, false)), _vm._v(" "), _vm.listItems ? _c('bb-list-group', {
    staticClass: "bb-calculator-modal__list"
  }, _vm._l(_vm.listItems, function (item) {
    return _c('bb-list-item', {
      key: item.title,
      attrs: {
        "title": item.title,
        "condensed": ""
      }
    }, [_vm._v("\n      " + _vm._s(item.content) + "\n      "), item.tooltip ? _c('bb-tooltip', {
      staticClass: "bb-calculator-modal__list-tooltip",
      attrs: {
        "icon-size": "24"
      }
    }, [_vm._v("\n        " + _vm._s(item.tooltip) + "\n      ")]) : _vm._e()], 1);
  }), 1) : _vm._e()], 1);
};
var __vue_staticRenderFns__$s = [];

/* style */
const __vue_inject_styles__$s = function (inject) {
  if (!inject) return;
  inject("data-v-3f1b355e_0", {
    source: ".bb-calculator-modal__heading{color:#2485e5}.bb-calculator-modal__list{margin-top:1.875rem}.bb-calculator-modal__list-tooltip{margin-left:.3125rem}@media (max-width:768px){.bb-calculator-modal__submit-button.bb-button{border-radius:0}}@media (min-width:768px){.bb-calculator-modal .bb-modal__footer{padding:.625rem 1.875rem 1.875rem}}@media (min-width:768px){.bb-calculator-modal .bb-overlay__content{margin-top:2.5rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$s = undefined;
/* module identifier */
const __vue_module_identifier__$s = undefined;
/* functional template */
const __vue_is_functional_template__$s = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$s = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$s,
  staticRenderFns: __vue_staticRenderFns__$s
}, __vue_inject_styles__$s, __vue_script__$s, __vue_scope_id__$s, __vue_is_functional_template__$s, __vue_module_identifier__$s, false, createInjector, undefined, undefined);

var script$r = defineComponent({
  name: 'bb-error-page',
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbLink: __vue_component__$2p
  },
  props: {
    /**
     * Base heading level for page title
     * - '3' for <h3>, '4' for <h4> etc.
     */
    baseHeadingLevel: {
      type: Number,
      default: 4
    },
    /** Name of an IC icon. */
    icon: {
      type: String
    },
    /** Longer description of the error. */
    title: {
      type: String,
      required: true
    },
    /** Text displayed inside the button. */
    description: {
      type: String,
      required: true
    },
    /** Text displayed on the link below button. */
    buttonText: {
      type: String
    },
    /** Icon displayed in button. */
    buttonIcon: {
      type: String
    },
    /** URL where button will redirect. */
    buttonPath: {
      type: String,
      default: '/'
    },
    /**
     * The target attribute of the button | [`_self`, `_blank`, `_parent`, `_top`]
     */
    buttonTarget: {
      type: String,
      default: '_self',
      validator: target => ['_self', '_blank', '_parent', '_top'].includes(target)
    },
    /** Button will receive 'loading' flag. */
    buttonLoading: {
      type: Boolean
    },
    /** Button will be disabled. */
    buttonDisabled: {
      type: Boolean
    },
    /** Link below the button. */
    linkText: {
      type: String
    },
    /** URL where link will redirect. */
    linkPath: {
      type: String,
      default: '/'
    },
    /**
     * The target attribute of the link  | [`_self`, `_blank`, `_parent`, `_top`]
     */
    linkTarget: {
      type: String,
      default: '_self',
      validator: target => ['_self', '_blank', '_parent', '_top'].includes(target)
    },
    /** Disable icon animations */
    disableAnimations: {
      type: Boolean,
      default: false
    }
  },
  emits: ['clickButton', 'clickLink'],
  data() {
    return {
      defaultAriaLabel: 'Error page'
    };
  },
  methods: {
    onButtonClick() {
      /**
       * Button click event.
       *
       * @event clickButton
       * @type {boolean}
       */
      this.$emit('clickButton');
    },
    onLinkClick() {
      /**
       * Link click event.
       *
       * @event clickLink
       * @type {boolean}
       */
      this.$emit('clickLink');
    }
  }
});

/* script */
const __vue_script__$r = script$r;

/* template */
var __vue_render__$r = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-error-page",
    attrs: {
      "aria-label": _vm.defaultAriaLabel
    }
  }, [_c('div', {
    staticClass: "bb-error-page__icon-area"
  }, [_vm.icon ? _c('bb-icon', {
    attrs: {
      "name": _vm.icon,
      "animated": !_vm.disableAnimations,
      "size": "fluid",
      "fill": "original"
    }
  }) : _vm._e()], 1), _vm._v(" "), _c("h" + _vm.baseHeadingLevel, {
    tag: "component",
    staticClass: "bb-error-page__title"
  }, [_vm._v("\n    " + _vm._s(_vm.title) + "\n  ")]), _vm._v(" "), _c('p', {
    staticClass: "bb-error-page__description"
  }, [_vm._v("\n    " + _vm._s(_vm.description) + "\n  ")]), _vm._v(" "), _vm.buttonText && _vm.buttonPath ? _c('div', {
    staticClass: "bb-error-page__button"
  }, [_c('bb-button', {
    attrs: {
      "to": _vm.buttonPath,
      "target": _vm.buttonTarget,
      "icon": _vm.buttonIcon,
      "loading": _vm.buttonLoading,
      "disabled": _vm.buttonDisabled,
      "label": _vm.buttonText,
      "size": "lg",
      "uppercase": ""
    },
    on: {
      "click": _vm.onButtonClick
    }
  })], 1) : _vm._e(), _vm._v(" "), _vm.linkText && _vm.linkPath ? _c('div', {
    staticClass: "bb-error-page__info-link"
  }, [_c('bb-link', {
    staticClass: "bb-error-page__link-path",
    attrs: {
      "to": _vm.linkPath,
      "target": _vm.linkTarget,
      "color": "mint"
    },
    on: {
      "click": _vm.onLinkClick
    }
  }, [_vm._v("\n      " + _vm._s(_vm.linkText) + "\n    ")])], 1) : _vm._e()], 1);
};
var __vue_staticRenderFns__$r = [];

/* style */
const __vue_inject_styles__$r = function (inject) {
  if (!inject) return;
  inject("data-v-3087f89a_0", {
    source: ".bb-error-page{height:100%;padding-top:6.25rem;text-align:center}@media (min-width:768px){.bb-error-page{padding-top:9.375rem}}.bb-error-page__button{margin-top:2.1875rem}@media (min-width:768px){.bb-error-page__button{margin-top:4.375rem}}.bb-error-page__button .bb-icon{vertical-align:top}.bb-error-page__title{margin-top:2.1875rem;margin-bottom:0;color:#2485e5;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;line-height:1.5rem}.bb-error-page__description{margin-top:2.1875rem;color:#687d92;font-size:.875rem}.bb-error-page__info-link{margin-top:2.8125rem}@media (min-width:768px){.bb-error-page__info-link{margin-top:3.125rem}}.bb-error-page__link-path{font-size:.875rem}.bb-error-page__icon-area{width:100%;max-width:15.625rem;margin:auto}@media (min-width:768px){.bb-error-page__icon-area{max-width:17.9375rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$r = undefined;
/* module identifier */
const __vue_module_identifier__$r = undefined;
/* functional template */
const __vue_is_functional_template__$r = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$r = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$r,
  staticRenderFns: __vue_staticRenderFns__$r
}, __vue_inject_styles__$r, __vue_script__$r, __vue_scope_id__$r, __vue_is_functional_template__$r, __vue_module_identifier__$r, false, createInjector, undefined, undefined);

function flatten(components, component) {
  return [...components, component, ...flattenChildren$1(component.$children)];
}
function flattenChildren$1(children) {
  return children.reduce(flatten, []);
}

var Repeater = {
  mixins: [validator],
  data() {
    return {
      val: [],
      fieldsWithCustomValidation: []
    };
  },
  components: {
    BbButton: __vue_component__$2v,
    BbIcon: __vue_component__$2C,
    BbRow: __vue_component__$22
  },
  props: {
    id: {
      type: String
    },
    value: {
      type: Array,
      default: () => []
    },
    addButtonLabel: {
      type: String,
      default: 'I have another income'
    },
    rowLimit: Number,
    rowLimitText: String,
    hideInitialRow: Boolean,
    includeEmptyRowsInModel: {
      type: Boolean
    },
    emptyRowModel: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['input'],
  created() {
    this.addEmptyRowIfNeeded();
  },
  mounted() {
    this.scrapeFieldsWithCustomValidation();
  },
  updated() {
    this.scrapeFieldsWithCustomValidation();
  },
  computed: {
    templateRows() {
      return this.rowLimit ? this.val.slice(0, this.rowLimit) : this.val;
    },
    fieldNamesOfRepeater() {
      return this.fields ? Object.keys(this.fields).filter(fieldName => this.$el.querySelector(`[name='${fieldName}']`)) : [];
    },
    totalFieldCount() {
      return this.fieldNamesOfRepeater.length;
    },
    validFieldCount() {
      return this.fieldNamesOfRepeater.filter(fieldName => this.fields[fieldName].valid).length;
    },
    valid() {
      if (this.invalidFieldsWithCustomValidation.length > 0) {
        return false;
      }
      return isEmpty(this.fields) || this.validFieldCount / this.totalFieldCount === 1;
    },
    rowCount() {
      return this.val.length - 1;
    },
    rowLimitExceeded() {
      return this.rowLimit && this.val.length > this.rowLimit;
    },
    showAddButton() {
      return !this.rowLimitExceeded;
    },
    showRowLimitText() {
      return this.rowLimitText && this.rowLimitExceeded;
    },
    fieldsWithErrors() {
      return this.errors ? this.errors.items.map(error => error.field) : [];
    },
    hasRemoveButton() {
      return this.rowCount > 0 || this.notRequired;
    },
    filterNonEmptyIfNeeded() {
      return this.includeEmptyRowsInModel ? Boolean : this.notEmptyModel;
    },
    valModels() {
      return this.val.map(row => row.model);
    },
    filteredValModels() {
      return this.valModels.filter(this.filterNonEmptyIfNeeded);
    },
    lastRowModelNotEmpty() {
      const lastRowModel = last$1(this.valModels);
      return this.notEmptyModel(lastRowModel);
    },
    repeaterHasCustomValidation() {
      return this.fieldsWithCustomValidation.length > 0;
    },
    invalidFieldsWithCustomValidation() {
      return this.fieldsWithCustomValidation.filter(field => field.isInvalid);
    }
  },
  methods: {
    scrapeFieldsWithCustomValidation() {
      const customValidationFields = flattenChildren$1(this.$children).filter(child => child.hasCustomValidation).map(field => pick(field, ['name', 'isInvalid', 'validate']));
      const hasChanged = !isEqual(this.fieldsWithCustomValidation, customValidationFields);
      if (hasChanged) {
        this.fieldsWithCustomValidation = customValidationFields;
      }
    },
    isOpen(row) {
      return row.open || this.expandAll;
    },
    rowHasInvalidField(rowId) {
      if (this.repeaterHasCustomValidation) {
        return this.invalidFieldsWithCustomValidation.some(field => field.name.includes(rowId));
      }
      return this.fieldsWithErrors.some(field => field.includes(rowId));
    },
    rowClasses(row, rowIndex) {
      return [`${this.$options.name}__row`, `${this.$options.name}__row--${rowIndex}`, {
        [`${this.$options.name}__row--error`]: this.rowHasInvalidField(row.id),
        [`${this.$options.name}__row--open`]: this.isOpen(row)
      }];
    },
    edit(editedRowIndex) {
      this.val.forEach((row, rowIndex) => {
        row.open = editedRowIndex === rowIndex;
      });
      this.removeAllEmptyRows();
    },
    addEmptyRow() {
      this.val = [...this.val, this.generateRow(clone(this.emptyRowModel), this.val.length)];
      this.openRow(this.val.length - 1);
    },
    onAdd() {
      if (this.repeaterHasCustomValidation) {
        this.fieldsWithCustomValidation.forEach(field => field.validate());
        const isValidForm = this.invalidFieldsWithCustomValidation.length === 0;
        if (isValidForm) {
          this.cleanAndAddEmptyRow();
        }
        return;
      }
      this.$validator.validateAll(this.fieldNamesOfRepeater).then(isValidForm => {
        if (isValidForm) {
          this.cleanAndAddEmptyRow();
        }
      }).catch(() => {});
    },
    cleanAndAddEmptyRow() {
      this.closeAllRows();
      this.removeAllEmptyRows();
      this.addEmptyRow();
    },
    onRemove(rowIndex) {
      this.removeRow(rowIndex);
      if (this.lastRowModelNotEmpty) {
        this.removeAllEmptyRows();
      }
      this.openRow(this.getIndexForRowToBeOpened(rowIndex));
    },
    getIndexForRowToBeOpened(rowIndex) {
      return rowIndex >= this.val.length ? this.val.length - 1 : rowIndex;
    },
    removeRow(index) {
      this.val = this.val.filter((_, i) => i !== index);
    },
    removeAllEmptyRows() {
      this.val = this.val.filter(row => this.notEmptyModel(row.model));
    },
    openRow(rowIndex) {
      this.closeAllRows();
      if (this.val[rowIndex]) {
        this.val[rowIndex].open = true;
      }
    },
    closeAllRows() {
      this.val.forEach(row => {
        row.open = false;
      });
    },
    emitInput() {
      this.$emit('input', this.filteredValModels);
    },
    diffValueModel() {
      return !isEqual(this.value, this.filteredValModels);
    },
    generateRow(model, rowIndex) {
      return {
        open: false,
        id: this.id ? `${this.id}-${rowIndex}` : uuid(),
        model
      };
    },
    addEmptyRowIfNeeded() {
      const showInitialRow = !this.hideInitialRow;
      if (isEmpty(this.val) || showInitialRow && this.lastRowModelNotEmpty) {
        this.addEmptyRow();
      }
    },
    notEmptyModel(model) {
      return !isEqual(model, this.emptyRowModel);
    }
  },
  watch: {
    val: {
      deep: true,
      handler() {
        if (this.diffValueModel()) {
          this.emitInput();
        }
      }
    },
    value: {
      deep: true,
      immediate: true,
      handler() {
        if (this.diffValueModel()) {
          this.val = this.value.map(this.generateRow);
          this.addEmptyRowIfNeeded();
        }
      }
    }
  }
};

var Icon$7 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3823388228__a bb-icon__dynamic-fill",attrs:{"d":"M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"}})]) } };

Vue.component('icon-ui-delete', Icon$7);

var script$q = Vue.extend({
  name: 'bb-fieldset-repeater',
  components: {
    BbButton: __vue_component__$2v
  },
  mixins: [Repeater],
  props: {
    /**
     * Label for the component.
     */
    label: String,
    /**
     * Bleed outside containing elements
     */
    bleed: {
      type: Boolean,
      default: true
    },
    /**
     * Reduce the space between elements
     */
    condensed: Boolean,
    /**
     * Don't render the highlights (header) part of each row
     */
    headerless: Boolean,
    /**
     * Disable "Add" button
     */
    disableAdding: Boolean
  },
  computed: {
    classes() {
      return ['bb-fieldset-repeater', {
        'bb-fieldset-repeater--constrained': !this.bleed,
        'bb-fieldset-repeater--condensed': this.condensed,
        'bb-fieldset-repeater--headerless': this.headerless
      }];
    },
    hasLabel() {
      return notEmpty(this.label);
    }
  },
  methods: {
    isOpen(row) {
      return row.open;
    }
  }
});

/* script */
const __vue_script__$q = script$q;

/* template */
var __vue_render__$q = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.hasLabel ? _c('div', {
    staticClass: "bb-fieldset-repeater__repeater-label",
    attrs: {
      "data-testid": "bb-fieldset-repeater__label"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]) : _vm._e(), _vm._v(" "), _c('ol', _vm._b({}, 'ol', {
    id: _vm.id
  }, false), _vm._l(_vm.templateRows, function (row, rowIndex) {
    return _c('li', {
      key: row.id,
      class: _vm.rowClasses(row, rowIndex)
    }, [!_vm.headerless ? _c('div', {
      staticClass: "repeater__header bb-fieldset-repeater__header",
      class: {
        'repeater__header--visible': !_vm.isOpen(row)
      },
      attrs: {
        "data-testid": "bb-fieldset-repeater__header"
      },
      on: {
        "click": function ($event) {
          return _vm.edit(rowIndex);
        }
      }
    }, [_c('div', {
      staticClass: "repeater__highlights"
    }, [_c('div', {
      staticClass: "repeater__counter"
    }, [_vm._v("\n            " + _vm._s(rowIndex + 1) + "\n          ")]), _vm._v(" "), _vm._t("highlights", null, {
      "model": row.model
    })], 2), _vm._v(" "), _c('bb-button', {
      attrs: {
        "display": "square",
        "color": "gray",
        "icon": "ui-menu-down",
        "no-background": ""
      }
    })], 1) : _vm._e(), _vm._v(" "), _c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.headerless || _vm.isOpen(row),
        expression: "headerless || isOpen(row)"
      }],
      staticClass: "bb-fieldset-repeater__fields",
      attrs: {
        "data-testid": "bb-fieldset-repeater__fields"
      }
    }, [_vm._t("fields", null, {
      "id": row.id,
      "index": rowIndex,
      "model": row.model
    })], 2), _vm._v(" "), _c('bb-button', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.rowCount > 0,
        expression: "rowCount > 0"
      }],
      staticClass: "bb-fieldset-repeater__remove",
      attrs: {
        "display": "square",
        "color": "gray",
        "icon": "ui-delete",
        "icon-color": "gray-40",
        "no-background": "",
        "data-testid": "bb-fieldset-repeater__remove"
      },
      on: {
        "click": function ($event) {
          $event.stopPropagation();
          return _vm.onRemove(rowIndex);
        }
      }
    })], 1);
  }), 0), _vm._v(" "), _c('bb-button', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showAddButton,
      expression: "showAddButton"
    }],
    staticClass: "bb-fieldset-repeater__add",
    attrs: {
      "label": _vm.addButtonLabel,
      "disabled": _vm.disableAdding,
      "display": "blockMobileOnly",
      "icon": "ui-plus-circle-outline",
      "no-background": "",
      "data-testid": "bb-fieldset-repeater__add"
    },
    on: {
      "click": _vm.onAdd
    }
  }), _vm._v(" "), _c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showRowLimitText,
      expression: "showRowLimitText"
    }],
    staticClass: "bb-fieldset-repeater__info"
  }, [_vm._v("\n    " + _vm._s(_vm.rowLimitText) + "\n  ")])], 1);
};
var __vue_staticRenderFns__$q = [];

/* style */
const __vue_inject_styles__$q = function (inject) {
  if (!inject) return;
  inject("data-v-2969a829_0", {
    source: ".repeater__highlights>:nth-child(2){overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.repeater__highlights>:nth-child(2){overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.repeater__header{position:fixed;display:flex;height:3.125rem;align-items:center;justify-content:space-between;padding-left:.75rem;border-radius:.3125rem;cursor:pointer;pointer-events:none;visibility:hidden}.repeater__header--visible{position:relative;pointer-events:all;visibility:visible}.repeater__header:hover{background-color:#eeeff2}.repeater__highlights{display:flex;min-width:0;flex:1;align-items:center;padding-right:6.875rem;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;white-space:nowrap}@media (min-width:768px){.repeater__highlights{padding-right:3.125rem}}.repeater__highlights>:nth-child(2){padding-right:1.25rem;margin-right:auto}.repeater__counter{display:inline-block;width:2rem;flex-shrink:0;margin-right:.625rem;color:#0ccca2;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;text-align:center}.bb-fieldset-repeater{counter-reset:repeater}.bb-fieldset-repeater__repeater-label{margin-bottom:1rem;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;line-height:1.3125rem}.bb-fieldset-repeater__row{position:relative;padding-right:1.875rem}.bb-fieldset-repeater__row+.bb-fieldset-repeater__row{margin-top:.9375rem}.bb-fieldset-repeater--condensed .bb-fieldset-repeater__row+.bb-fieldset-repeater__row{margin-top:.5rem}.bb-fieldset-repeater--constrained .bb-fieldset-repeater__row{padding-right:3rem}.bb-fieldset-repeater--constrained .bb-fieldset-repeater__row:only-child{padding-right:0}.bb-fieldset-repeater__row--error:not(.bb-fieldset-repeater__row--open) .bb-fieldset-repeater__header{box-shadow:0 0 0 .0625rem #ff7155}.bb-fieldset-repeater:not(.bb-fieldset-repeater--headerless) .bb-fieldset-repeater__fields{padding:.3125rem 1.25rem 1.25rem 1.25rem;border:.0625rem solid #eeeff2;border-radius:.25rem}.bb-fieldset-repeater__add{margin-top:1.875rem}.bb-fieldset-repeater--condensed .bb-fieldset-repeater__add{margin-top:.75rem}.bb-fieldset-repeater__remove{position:absolute;top:0;right:-.625rem}.bb-fieldset-repeater--constrained .bb-fieldset-repeater__remove{right:0}.bb-fieldset-repeater__info{padding:.625rem .625rem .3125rem;color:#89a;font-size:1rem;text-align:center;white-space:nowrap}.bb-fieldset-repeater .repeater__header{height:2.5rem;background:#f7f7f8}.bb-fieldset-repeater+.bb-form__button{position:absolute;right:0;display:inline-block;width:auto;transform:translate(-1.25rem,-140%)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$q = undefined;
/* module identifier */
const __vue_module_identifier__$q = undefined;
/* functional template */
const __vue_is_functional_template__$q = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$q = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$q,
  staticRenderFns: __vue_staticRenderFns__$q
}, __vue_inject_styles__$q, __vue_script__$q, __vue_scope_id__$q, __vue_is_functional_template__$q, __vue_module_identifier__$q, false, createInjector, undefined, undefined);

var script$p = {
  name: 'bb-filter-progress-item',
  mixins: [TaskColor],
  props: {
    id: String,
    name: String,
    state: {
      type: String,
      validator: state => ['active', 'inactive'].includes(state)
    },
    completedTasks: Number,
    totalTasks: Number,
    partiallySelected: Boolean
  },
  emits: ['click'],
  computed: {
    calculateWidth() {
      return `${Math.floor((this.totalTasks - this.completedTasks) / this.totalTasks * 100)}%`;
    },
    classObject() {
      return {
        [`is-${this.state}`]: this.state,
        'is-partially-selected': this.partiallySelected
      };
    },
    unfinishedTasks() {
      return this.totalTasks !== undefined && this.completedTasks !== undefined ? this.totalTasks - this.completedTasks : '';
    },
    idOrName() {
      return this.id || slugify(this.name);
    }
  }
};

/* script */
const __vue_script__$p = script$p;

/* template */
var __vue_render__$p = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-filter-progress-item",
    class: _vm.classObject,
    attrs: {
      "id": _vm.idOrName
    },
    on: {
      "click": function ($event) {
        return _vm.$emit('click');
      }
    }
  }, [_c('div', {
    staticClass: "bb-filter-progress-item__content"
  }, [_c('div', {
    staticClass: "bb-filter-progress-item__title"
  }, [_c('span', {
    staticClass: "bb-filter-progress-item__title-name"
  }, [_vm._v("\n        " + _vm._s(_vm.name) + "\n      ")]), _vm._v(" "), _c('span', {
    staticClass: "bb-filter-progress-item__title-uncompleted"
  }, [_vm._v("\n        " + _vm._s(_vm.unfinishedTasks) + "\n      ")])]), _vm._v(" "), _c('div', {
    staticClass: "bb-filter-progress-item__type-bar",
    class: "bg-" + _vm.taskColor,
    style: {
      width: _vm.calculateWidth
    }
  }), _vm._v(" "), _c('span', {
    staticClass: "bb-filter-progress-item__completed"
  }, [_vm._t("completed-label", function () {
    return [_vm._v("\n        " + _vm._s(_vm.completedTasks && _vm.completedTasks + " completed") + "\n      ")];
  }, {
    "completedTasks": _vm.completedTasks
  })], 2)])]);
};
var __vue_staticRenderFns__$p = [];

/* style */
const __vue_inject_styles__$p = function (inject) {
  if (!inject) return;
  inject("data-v-563a75dc_0", {
    source: ".bb-filter-progress-item{overflow:auto;min-height:3.9375rem;padding:.3125rem 1.875rem .3125rem 1.25rem;border:.0625rem solid transparent;color:#00285a;cursor:pointer;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;list-style:none;text-align:left}.bb-filter-progress-item.is-active{border-color:#0ccca2}.bb-filter-progress-item.is-partially-selected{border-style:dotted;border-color:#0ccca2}.bb-filter-progress-item:hover{border-color:#0ccca2;opacity:1}.is-inactive>.bb-filter-progress-item__content{opacity:.4}.bb-filter-progress-item__title{display:flex;justify-content:space-between}.bb-filter-progress-item__title-name{padding-bottom:.1875rem;line-height:1.3}.bb-filter-progress-item__title-uncompleted{padding-left:.3125rem;font-size:1.125rem;line-height:1.2;text-align:right}.bb-filter-progress-item__type-bar{height:.25rem;border-radius:.25rem;margin:.1875rem 0}.bb-filter-progress-item__completed{color:#89a;font-size:.6875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$p = undefined;
/* module identifier */
const __vue_module_identifier__$p = undefined;
/* functional template */
const __vue_is_functional_template__$p = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$p = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$p,
  staticRenderFns: __vue_staticRenderFns__$p
}, __vue_inject_styles__$p, __vue_script__$p, __vue_scope_id__$p, __vue_is_functional_template__$p, __vue_module_identifier__$p, false, createInjector, undefined, undefined);

const toFlatChildren = (acc, node) => {
  var _node$children;
  return [...acc, node, ...((_node$children = node.children) !== null && _node$children !== void 0 && _node$children.length ? flattenChildren(node.children) : [])];
};
const flattenChildren = tree => tree.reduce(toFlatChildren, []);

var script$o = {
  name: 'bb-filter-progress-group',
  components: {
    BbFilterProgressItem: __vue_component__$p,
    BbIcon: __vue_component__$2C
  },
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    getChildren: {
      type: Function
    },
    value: {
      type: Object,
      default() {
        return {};
      }
    },
    searchTerm: String
  },
  emits: ['filter', 'reset', 'change'],
  data() {
    return {
      open: false,
      val: {
        ...this.value
      }
    };
  },
  computed: {
    flatChildren() {
      return flattenChildren(this.val.children || []);
    },
    hasOrCanGetChildren() {
      return notEmpty(this.val.children) || this.getChildren;
    },
    canBeOpened() {
      return !this.val.noChildren && this.hasOrCanGetChildren;
    },
    looksOpen() {
      return this.open || this.hasFilteredChildren;
    },
    isActive() {
      return this.val.state === 'active';
    },
    isPartiallySelected() {
      const hasChildren = notEmpty(this.val.children);
      if (this.isActive || !hasChildren) {
        return false;
      }
      return this.flatChildren.some(child => child.state === 'active');
    },
    childrenNames() {
      return this.flatChildren.map(child => child.name);
    },
    looksFiltered() {
      return !this.hasSearchTerm || this.hasFilteredChildren || containsCaseInsensitive(this.val.name, this.searchTerm);
    },
    hasFilteredChildren() {
      return this.hasSearchTerm && this.childrenNames.some(childName => containsCaseInsensitive(childName, this.searchTerm));
    },
    hasSearchTerm() {
      return notEmpty(this.searchTerm);
    }
  },
  watch: {
    searchTerm() {
      this.open = this.hasSearchTerm ? this.hasFilteredChildren : this.isPartiallySelected;
    },
    value: {
      deep: true,
      immediate: true,
      handler() {
        this.val = {
          ...this.value
        };
        if (this.canBeOpened && this.isPartiallySelected) {
          this.open = true;
        }
      }
    }
  },
  methods: {
    emitFilter(activeFilter) {
      this.$emit('filter', activeFilter);
    },
    emitReset() {
      this.$emit('reset');
    },
    emitChange() {
      this.$emit('change', this.val);
    },
    onChildChange(indexChild, childValue) {
      this.val.children[indexChild] = childValue;
      this.emitChange();
    },
    toggleFilterState() {
      if (this.isActive) {
        this.val.state = null;
        this.emitReset();
      } else {
        this.$set(this.val, 'state', 'active');
        this.emitChange();
        this.emitFilter(this.val);
      }
    },
    toggleOpen() {
      this.open = !this.open;
      if (!this.val.children && this.getChildren) {
        this.getChildren(this.val, this.setChildren);
      }
    },
    setChildren(children) {
      if (children === null) {
        this.$set(this.val, 'noChildren', true);
      } else {
        this.val.children = children;
      }
      this.emitChange();
    }
  }
};

/* script */
const __vue_script__$o = script$o;

/* template */
var __vue_render__$o = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _vm.looksFiltered ? _c('li', {
    staticClass: "bb-filter-progress-group",
    class: {
      'is-open': _vm.looksOpen
    }
  }, [_c('div', {
    staticClass: "bb-filter-progress-group__head"
  }, [_c('bb-filter-progress-item', {
    staticClass: "bb-filter-progress-group__filter-progress-item",
    attrs: {
      "id": _vm.val.id,
      "state": _vm.val.state,
      "task-color": _vm.val.taskColor,
      "name": _vm.val.name,
      "completed-tasks": _vm.val.completedTasks,
      "total-tasks": _vm.val.totalTasks,
      "partially-selected": _vm.isPartiallySelected
    },
    on: {
      "click": _vm.toggleFilterState
    }
  }), _vm._v(" "), _vm.canBeOpened ? _c('div', {
    staticClass: "bb-filter-progress-group__toggle-vertical-web"
  }) : _vm._e(), _vm._v(" "), _vm.canBeOpened ? _c('div', {
    staticClass: "bb-filter-progress-group__toggle",
    on: {
      "click": _vm.toggleOpen
    }
  }, [_c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.open,
      expression: "open"
    }],
    attrs: {
      "name": "ui-minus-box-outline",
      "size": "24",
      "fill": "gray-70"
    }
  }), _vm._v(" "), _c('bb-icon', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: !_vm.open,
      expression: "!open"
    }],
    attrs: {
      "name": "ui-plus-box-outline",
      "size": "24",
      "fill": "gray-70"
    }
  })], 1) : _vm._e()], 1), _vm._v(" "), _vm.looksOpen ? _c('ul', {
    staticClass: "bb-filter-progress-group__children"
  }, _vm._l(_vm.val.children, function (child, indexChild) {
    return _c('bb-filter-progress-group', _vm._b({
      key: indexChild,
      staticClass: "bb-filter-progress-group__child",
      on: {
        "change": function (childValue) {
          return _vm.onChildChange(indexChild, childValue);
        },
        "click": function ($event) {
          return _vm.toggleFilterState(child);
        },
        "reset": _vm.emitReset,
        "filter": _vm.emitFilter
      },
      model: {
        value: _vm.val.children[indexChild],
        callback: function ($$v) {
          _vm.$set(_vm.val.children, indexChild, $$v);
        },
        expression: "val.children[indexChild]"
      }
    }, 'bb-filter-progress-group', {
      getChildren: _vm.getChildren,
      searchTerm: _vm.searchTerm
    }, false));
  }), 1) : _vm._e()]) : _vm._e();
};
var __vue_staticRenderFns__$o = [];

/* style */
const __vue_inject_styles__$o = function (inject) {
  if (!inject) return;
  inject("data-v-3ffb294a_0", {
    source: ".bb-filter-progress-group{list-style:none}.bb-filter-progress-group+.bb-filter-progress-group{margin-top:-.0625rem}.bb-filter-progress-group__head{position:relative}.bb-filter-progress-group__toggle{position:absolute;z-index:1100;top:50%;left:-.5625rem;cursor:pointer;transform:translateY(-50%);user-select:none}.bb-filter-progress-group__toggle svg{width:1.25rem;height:1.25rem}.is-open>*>.bb-filter-progress-group__toggle-vertical-web{position:absolute;top:50%;left:0;width:.0625rem;height:50%;border-left:.0625rem solid #bdc7d0;content:\"\"}.bb-filter-progress-group__filter-progress-item.is-active+.bb-filter-progress-group__toggle-vertical-web,.bb-filter-progress-group__filter-progress-item:hover+.bb-filter-progress-group__toggle-vertical-web{display:none}.bb-filter-progress-group__child>*>.bb-filter-progress-group__filter-progress-item.is-active::before,.bb-filter-progress-group__child>*>.bb-filter-progress-group__filter-progress-item:hover::before{width:1.875rem}.bb-filter-progress-group__child>*>.bb-filter-progress-group__filter-progress-item::before{position:absolute;top:50%;left:-1.875rem;width:2.25rem;height:.0625rem;border-top:.0625rem solid #bdc7d0;content:\"\";transform:translateY(-50%)}.bb-filter-progress-group__child>*>.bb-filter-progress-group__filter-progress-item::after{position:absolute;top:0;left:-1.875rem;width:.0625rem;height:calc(100% - .0625rem);border:.0625rem solid #bdc7d0;border-top:0;border-right:0;content:\"\"}.bb-filter-progress-group__child:last-child>*>.bb-filter-progress-group__filter-progress-item::after{height:50%}.bb-filter-progress-group__children{position:relative;padding-left:1.875rem;margin-top:-.0625rem}.bb-filter-progress-group__child:not(:last-child).is-open>.bb-filter-progress-group__children::after{position:absolute;top:0;left:-1.875rem;width:.0625rem;height:100%;border-left:.0625rem solid #bdc7d0;content:\"\"}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$o = undefined;
/* module identifier */
const __vue_module_identifier__$o = undefined;
/* functional template */
const __vue_is_functional_template__$o = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$o = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$o,
  staticRenderFns: __vue_staticRenderFns__$o
}, __vue_inject_styles__$o, __vue_script__$o, __vue_scope_id__$o, __vue_is_functional_template__$o, __vue_module_identifier__$o, false, createInjector, undefined, undefined);

var script$n = {
  name: 'bb-filter-progress-deprecated',
  components: {
    BbFilterProgressGroup: __vue_component__$o
  },
  props: {
    filters: {
      type: Array,
      default() {
        return [];
      }
    },
    getChildren: Function
  },
  emits: ['input'],
  data() {
    return {
      activeFilter: null,
      val: [...this.filters.map(filter => this.adjustFilterProps(filter))]
    };
  },
  watch: {
    filters() {
      this.val = [...this.filters.map(filter => this.adjustFilterProps(filter))];
    }
  },
  methods: {
    setFilterAndChildrenOfSameColor(filter, color) {
      return Object.assign(filter, {
        taskColor: color
      }, filter.children && {
        children: filter.children.map(childFilter => this.setFilterAndChildrenOfSameColor(childFilter, color))
      });
    },
    adjustFilterProps(filter) {
      this.initState(filter);
      return this.adjustChildrenColor(filter);
    },
    initState(filter) {
      if (!filter.hasOwnProperty('state')) {
        this.$set(filter, 'state', this.activeFilter == null ? null : 'inactive');
      }
      filter.children && filter.children.forEach(childFilter => this.initState(childFilter));
    },
    adjustChildrenColor(filter) {
      const colorShades = [90, 70, 50, 30, 10];
      return Object.assign(filter, filter.children && {
        children: filter.children.map((childFilter, index) => {
          const newColor = `${filter.taskColor}-${colorShades[index % colorShades.length]}`;
          return this.setFilterAndChildrenOfSameColor(childFilter, newColor);
        })
      });
    },
    onReset() {
      this.activeFilter = null;
      this.setStateToAll(this.val, null);
      this.$emit('input', null);
    },
    onFilter(activeFilter) {
      this.activeFilter = activeFilter;
      this.setStateToAll(this.val, 'inactive');
      this.$set(activeFilter, 'state', 'active');
      this.$emit('input', activeFilter);
    },
    setStateToAll(filters, state) {
      filters.forEach(filter => {
        this.$set(filter, 'state', state);
        filter.children && this.setStateToAll(filter.children, state);
      });
    }
  }
};

/* script */
const __vue_script__$n = script$n;

/* template */
var __vue_render__$n = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ul', {
    staticClass: "bb-filter-progress"
  }, _vm._l(_vm.val, function (filter, indexFilter) {
    return _c('bb-filter-progress-group', {
      key: indexFilter,
      attrs: {
        "value": _vm.val[indexFilter],
        "get-children": _vm.getChildren
      },
      on: {
        "change": function ($event) {
          _vm.$set(_vm.val, indexFilter, _vm.adjustFilterProps($event));
        },
        "reset": _vm.onReset,
        "filter": _vm.onFilter
      }
    });
  }), 1);
};
var __vue_staticRenderFns__$n = [];

/* style */
const __vue_inject_styles__$n = undefined;
/* scoped */
const __vue_scope_id__$n = undefined;
/* module identifier */
const __vue_module_identifier__$n = undefined;
/* functional template */
const __vue_is_functional_template__$n = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$n = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$n,
  staticRenderFns: __vue_staticRenderFns__$n
}, __vue_inject_styles__$n, __vue_script__$n, __vue_scope_id__$n, __vue_is_functional_template__$n, __vue_module_identifier__$n, false, undefined, undefined, undefined);

var script$m = Vue.extend({
  name: 'bb-filter-progress-multiple',
  components: {
    BbFilterProgressGroup: __vue_component__$o
  },
  props: {
    /**
     * Array of filters to render in the list
     */
    filters: {
      type: Array,
      required: true,
      default() {
        return [];
      }
    },
    /**
     * Selection of multiple filters
     */
    multiple: Boolean,
    /**
     * Custom method to retrieve the filters from backend
     */
    getChildren: Function,
    /**
     * Filter the filters with a search term, only works with `multiple`
     */
    searchTerm: String
  },
  data() {
    return {
      val: this.getAdjustedFilters()
    };
  },
  computed: {
    flatVal() {
      return flattenChildren(this.val);
    },
    activeKeys() {
      const active = [];
      const getActive = filters => filters === null || filters === void 0 ? void 0 : filters.forEach(f => {
        if (f.state === 'active') {
          active.push(f.key);
        } else {
          getActive(f.children);
        }
      });
      getActive(this.val);
      return active;
    }
  },
  watch: {
    filters() {
      this.val = this.getAdjustedFilters();
    }
  },
  methods: {
    getAdjustedFilters() {
      return [...this.filters.map(this.adjustFilterProps)];
    },
    setFilterAndChildrenOfSameColor(filter, color) {
      const updatedFilter = Object.assign(filter, {
        taskColor: color
      }, filter.children && {
        children: filter.children.map(childFilter => this.setFilterAndChildrenOfSameColor(childFilter, color))
      });
      return updatedFilter;
    },
    adjustFilterProps(filter) {
      this.initState(filter);
      return this.adjustChildrenColor(filter);
    },
    initState(filter) {
      var _filter$children;
      if (!filter.hasOwnProperty('state')) {
        var _this$activeKeys;
        this.$set(filter, 'state', (_this$activeKeys = this.activeKeys) !== null && _this$activeKeys !== void 0 && _this$activeKeys.length ? 'inactive' : null);
      }
      filter === null || filter === void 0 ? void 0 : (_filter$children = filter.children) === null || _filter$children === void 0 ? void 0 : _filter$children.forEach(this.initState);
    },
    adjustChildrenColor(filter) {
      const colorShades = [90, 70, 50, 30, 10];
      const updatedFilter = Object.assign(filter, filter.children && {
        children: filter.children.map((childFilter, index) => {
          const newColor = `${filter.taskColor}-${colorShades[index % colorShades.length]}`;
          return this.setFilterAndChildrenOfSameColor(childFilter, newColor);
        })
      });
      return updatedFilter;
    },
    onChange(filter, index) {
      this.$set(this.val, index, this.adjustFilterProps(filter));
    },
    onReset() {
      this.updateStateForFiltersAndChildren(this.flatVal, null);
      this.$emit('input', null);
    },
    onFilter(filter) {
      this.updateStateForFiltersAndChildren(this.flatVal, 'inactive', null);
      this.updateStateForFiltersAndChildren(flattenChildren(filter.children), filter.state);
      this.updateStateForParentFilter(filter);
      /**
       * Emitted when Filter Progress is clicked.
       *
       * @event input
       * @type {string[]}
       */
      this.$emit('input', this.activeKeys);
    },
    updateStateForFiltersAndChildren(flatFilters, newState, oldState) {
      for (const filter of flatFilters) {
        if (oldState === undefined || oldState === filter.state) {
          this.$set(filter, 'state', newState);
        }
      }
    },
    updateStateForParentFilter(filter) {
      const parent = this.flatVal.find(({
        children
      }) => children === null || children === void 0 ? void 0 : children.some(({
        key
      }) => key === filter.key));
      const allSiblingsHaveSameState = parent === null || parent === void 0 ? void 0 : parent.children.every(({
        state
      }) => state === filter.state);
      if (allSiblingsHaveSameState) {
        this.$set(parent, 'state', filter.state);
      }
    }
  }
});

/* script */
const __vue_script__$m = script$m;

/* template */
var __vue_render__$m = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ul', {
    staticClass: "bb-filter-progress"
  }, _vm._l(_vm.val, function (_, indexFilter) {
    return _c('bb-filter-progress-group', {
      key: indexFilter,
      attrs: {
        "value": _vm.val[indexFilter],
        "get-children": _vm.getChildren,
        "search-term": _vm.searchTerm
      },
      on: {
        "change": function ($event) {
          return _vm.onChange($event, indexFilter);
        },
        "reset": _vm.onReset,
        "filter": _vm.onFilter
      }
    });
  }), 1);
};
var __vue_staticRenderFns__$m = [];

/* style */
const __vue_inject_styles__$m = undefined;
/* scoped */
const __vue_scope_id__$m = undefined;
/* module identifier */
const __vue_module_identifier__$m = undefined;
/* functional template */
const __vue_is_functional_template__$m = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$m = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$m,
  staticRenderFns: __vue_staticRenderFns__$m
}, __vue_inject_styles__$m, __vue_script__$m, __vue_scope_id__$m, __vue_is_functional_template__$m, __vue_module_identifier__$m, false, undefined, undefined, undefined);

var script$l = Vue.extend({
  name: 'bb-filter-progress',
  components: {
    BbFilterProgressDeprecated: __vue_component__$n,
    BbFilterProgressMultiple: __vue_component__$m
  },
  props: {
    /**
     * Array of filters to render in the list
     */
    filters: {
      type: Array,
      required: true,
      default() {
        return [];
      }
    },
    /**
     * Selection of multiple filters
     */
    multiple: Boolean,
    /**
     * Custom method to retrieve the filters from backend
     */
    getChildren: Function,
    /**
     * Filter the filters with a search term, only works with `multiple`
     */
    searchTerm: String
  },
  computed: {
    componentName() {
      return this.multiple ? 'bb-filter-progress-multiple' : 'bb-filter-progress-deprecated';
    }
  }
});

/* script */
const __vue_script__$l = script$l;

/* template */
var __vue_render__$l = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c(_vm.componentName, _vm._g(_vm._b({
    tag: "component"
  }, 'component', _vm.$props, false), _vm.$listeners));
};
var __vue_staticRenderFns__$l = [];

/* style */
const __vue_inject_styles__$l = undefined;
/* scoped */
const __vue_scope_id__$l = undefined;
/* module identifier */
const __vue_module_identifier__$l = undefined;
/* functional template */
const __vue_is_functional_template__$l = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$l = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$l,
  staticRenderFns: __vue_staticRenderFns__$l
}, __vue_inject_styles__$l, __vue_script__$l, __vue_scope_id__$l, __vue_is_functional_template__$l, __vue_module_identifier__$l, false, undefined, undefined, undefined);

var script$k = {
  name: 'bb-filter-status',
  props: {
    filters: {
      type: Array,
      required: true,
      validator: val => val.every(filter => filter.name && !isNaN(parseFloat(filter.value, 10)))
    },
    value: Object
  },
  emits: ['input'],
  data() {
    return {
      val: this.value || null,
      lastActive: this.value ? this.filters.findIndex(filter => this.compareItems(this.value, filter)) : null
    };
  },
  computed: {
    hasValue() {
      return !!Object.keys(this.val || {}).length;
    }
  },
  watch: {
    val: {
      deep: true,
      handler(filter) {
        this.$emit('input', filter);
      }
    },
    value: {
      deep: true,
      handler(v) {
        this.val = v;
      }
    }
  },
  methods: {
    getColor(i) {
      return allowedColors[i % allowedColors.length];
    },
    compareItems(obj1, obj2) {
      return Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every(key => obj1[key] === obj2[key]);
    },
    deselectIfSelected(filterIndex) {
      const sameFilterClicked = this.compareItems(this.filters[filterIndex], this.filters[this.lastActive] || {});
      if (sameFilterClicked) {
        this.val = null;
        this.lastActive = null;
      } else {
        this.lastActive = filterIndex;
      }
    }
  }
};

/* script */
const __vue_script__$k = script$k;

/* template */
var __vue_render__$k = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-filter-status",
    attrs: {
      "data-last-active": _vm.lastActive
    }
  }, _vm._l(_vm.filters, function (pair, filterIndex) {
    return _c('label', {
      key: filterIndex,
      staticClass: "bb-filter-status__item",
      class: {
        'bb-filter-status__item--active': _vm.hasValue ? _vm.compareItems(pair, _vm.val) : null,
        'bb-filter-status__item--bright': !_vm.hasValue || _vm.compareItems(pair, _vm.val)
      }
    }, [_c('input', {
      directives: [{
        name: "model",
        rawName: "v-model",
        value: _vm.val,
        expression: "val"
      }],
      staticClass: "visually-hidden",
      attrs: {
        "type": "radio"
      },
      domProps: {
        "value": pair,
        "checked": _vm._q(_vm.val, pair)
      },
      on: {
        "click": function ($event) {
          return _vm.deselectIfSelected(filterIndex);
        },
        "change": function ($event) {
          _vm.val = pair;
        }
      }
    }), _vm._v(" "), _c('div', {
      staticClass: "bb-filter-status__color",
      class: "bg-" + (pair.color || _vm.getColor(filterIndex))
    }), _vm._v(" "), _c('span', {
      staticClass: "bb-filter-status__name"
    }, [_vm._v("\n      " + _vm._s(pair.name) + "\n    ")]), _vm._v(" "), _c('span', {
      staticClass: "bb-filter-status__value"
    }, [_vm._v("\n      " + _vm._s(pair.value) + "\n    ")])]);
  }), 0);
};
var __vue_staticRenderFns__$k = [];

/* style */
const __vue_inject_styles__$k = function (inject) {
  if (!inject) return;
  inject("data-v-43f7cd46_0", {
    source: ".bb-filter-status{background:#fff}.bb-filter-status__item{display:flex;height:100%;align-items:center;padding:.625rem 1.875rem;border:.0625rem solid transparent;color:#00285a;cursor:pointer;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;opacity:.4;transition:opacity .2s ease-out}.bb-filter-status__item+.bb-filter-status__item{margin-top:-.0625rem}.bb-filter-status__item--active,.bb-filter-status__item:hover{border-color:#0ccca2;opacity:1}.bb-filter-status__item--bright{opacity:1}.bb-filter-status__color{width:.1875rem;height:2.1875rem;margin-right:1.25rem}.bb-filter-status__value{margin-left:auto;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$k = undefined;
/* module identifier */
const __vue_module_identifier__$k = undefined;
/* functional template */
const __vue_is_functional_template__$k = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$k = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$k,
  staticRenderFns: __vue_staticRenderFns__$k
}, __vue_inject_styles__$k, __vue_script__$k, __vue_scope_id__$k, __vue_is_functional_template__$k, __vue_module_identifier__$k, false, createInjector, undefined, undefined);

var script$j = Vue.extend({
  name: 'bb-form-repeater',
  components: {
    BbButton: __vue_component__$2v
  },
  mixins: [Repeater],
  props: {
    /**
     * Always expand all elements
     */
    expandAll: Boolean,
    /**
     * Label for the component.
     */
    label: String,
    /**
     * Allows to remove the row even if it's the only one
     */
    notRequired: Boolean
  },
  computed: {
    hasLabel() {
      return notEmpty(this.label);
    }
  },
  methods: {
    notEmpty
  }
});

/* script */
const __vue_script__$j = script$j;

/* template */
var __vue_render__$j = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.$options.name
  }, [_vm.hasLabel ? _c('div', {
    staticClass: "bb-form-repeater__repeater-label",
    attrs: {
      "data-testid": "bb-form-repeater__label"
    }
  }, [_vm._v("\n    " + _vm._s(_vm.label) + "\n  ")]) : _vm._e(), _vm._v(" "), _c('ol', _vm._b({}, 'ol', {
    id: _vm.id
  }, false), [_vm._l(_vm.templateRows, function (row, rowIndex) {
    return _c('li', {
      key: row.id,
      class: _vm.rowClasses(row, rowIndex)
    }, [_c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: !_vm.isOpen(row),
        expression: "!isOpen(row)"
      }],
      staticClass: "repeater__header",
      class: {
        'repeater__header--visible': !_vm.isOpen(row)
      },
      on: {
        "click": function ($event) {
          return _vm.edit(rowIndex);
        }
      }
    }, [_c('div', {
      staticClass: "repeater__highlights"
    }, [_c('div', {
      staticClass: "repeater__counter"
    }, [_vm._v("\n            " + _vm._s(rowIndex + 1) + "\n          ")]), _vm._v(" "), _vm._t("highlights", null, {
      "model": row.model
    })], 2), _vm._v(" "), _c('div', {
      staticClass: "bb-form-repeater__buttons"
    }, [_c('bb-button', {
      staticClass: "bb-form-repeater__edit",
      attrs: {
        "no-background": "",
        "display": "square",
        "icon": "ui-pencil"
      }
    }), _vm._v(" "), _c('bb-button', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.hasRemoveButton,
        expression: "hasRemoveButton"
      }],
      staticClass: "bb-form-repeater__remove",
      attrs: {
        "no-background": "",
        "display": "square",
        "icon": "ui-close",
        "color": "red"
      },
      on: {
        "click": function ($event) {
          $event.stopPropagation();
          return _vm.onRemove(rowIndex);
        }
      }
    })], 1)]), _vm._v(" "), _c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.isOpen(row),
        expression: "isOpen(row)"
      }],
      staticClass: "bb-form-repeater__fields",
      attrs: {
        "aria-labelledby": row.id
      }
    }, [_vm.notEmpty(row.model) || _vm.expandAll ? _c('p', {
      staticClass: "bb-form-repeater__label",
      attrs: {
        "id": row.id
      }
    }, [_c('span', {
      staticClass: "repeater__counter bb-form-repeater__counter"
    }, [_vm._v("\n            " + _vm._s(rowIndex + 1) + "\n          ")]), _vm._v(" "), _vm._t("editLabel", null, {
      "model": row.model
    })], 2) : _vm._e(), _vm._v(" "), _vm._t("fields", null, {
      "id": row.id,
      "index": rowIndex,
      "model": row.model
    }), _vm._v(" "), _c('div', {
      staticClass: "bb-form-repeater__buttons bb-form-repeater__buttons-fields"
    }, [_c('bb-button', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.hasRemoveButton,
        expression: "hasRemoveButton"
      }],
      staticClass: "bb-form-repeater__remove",
      attrs: {
        "no-background": "",
        "display": "square",
        "icon": "ui-close",
        "color": "red"
      },
      on: {
        "click": function ($event) {
          $event.stopPropagation();
          return _vm.onRemove(rowIndex);
        }
      }
    })], 1)], 2)]);
  }), _vm._v(" "), _c('li', {
    staticClass: "bb-form-repeater__controls"
  }, [_c('bb-button', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showAddButton,
      expression: "showAddButton"
    }],
    staticClass: "bb-form-repeater__add",
    attrs: {
      "label": _vm.addButtonLabel,
      "no-background": "",
      "icon": "ui-plus-circle-outline"
    },
    on: {
      "click": _vm.onAdd
    }
  }), _vm._v(" "), _c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showRowLimitText,
      expression: "showRowLimitText"
    }],
    staticClass: "bb-form-repeater__info"
  }, [_vm._v("\n        " + _vm._s(_vm.rowLimitText) + "\n      ")])], 1)], 2)]);
};
var __vue_staticRenderFns__$j = [];

/* style */
const __vue_inject_styles__$j = function (inject) {
  if (!inject) return;
  inject("data-v-e173dc70_0", {
    source: ".repeater__highlights>:nth-child(2){overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.repeater__highlights>:nth-child(2){overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.repeater__header{position:fixed;display:flex;height:3.125rem;align-items:center;justify-content:space-between;padding-left:.75rem;border-radius:.3125rem;cursor:pointer;pointer-events:none;visibility:hidden}.repeater__header--visible{position:relative;pointer-events:all;visibility:visible}.repeater__header:hover{background-color:#eeeff2}.repeater__highlights{display:flex;min-width:0;flex:1;align-items:center;padding-right:6.875rem;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;white-space:nowrap}@media (min-width:768px){.repeater__highlights{padding-right:3.125rem}}.repeater__highlights>:nth-child(2){padding-right:1.25rem;margin-right:auto}.repeater__counter{display:inline-block;width:2rem;flex-shrink:0;margin-right:.625rem;color:#0ccca2;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;text-align:center}.bb-form-repeater{counter-reset:repeater}.bb-form-repeater .bb-form-repeater{margin-right:-1.25rem;margin-left:-1.25rem}.bb-form-repeater__repeater-label{margin-bottom:1rem;color:#687d92;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:14px;line-height:1.3125rem}.bb-form-repeater__row{position:relative;border-radius:.3125rem;margin-bottom:.625rem;background:#f7f7f8}.bb-form-repeater__row--error:not(.bb-form-repeater__row--open){box-shadow:0 0 0 .0625rem #ff7155}.bb-form-repeater__controls{text-align:center}.bb-form-repeater__buttons{position:absolute;top:0;right:0;display:flex;width:6.25rem;justify-content:flex-end}@media (min-width:768px){.bb-form-repeater__buttons{top:.3125rem;right:.3125rem;width:5rem;justify-content:flex-start;transform:translateX(2.5rem)}}@media (min-width:768px){.bb-form-repeater .bb-form-repeater .bb-form-repeater__buttons{right:1.125rem;transform:none}}.bb-form-repeater .bb-form-repeater__edit.bb-button,.bb-form-repeater .bb-form-repeater__remove.bb-button{position:relative;width:3.125rem;height:3.125rem}@media (min-width:768px){.bb-form-repeater .bb-form-repeater__edit.bb-button,.bb-form-repeater .bb-form-repeater__remove.bb-button{width:2.5rem;height:2.5rem}}.bb-form-repeater .bb-form-repeater__edit{margin-right:.0625rem}.bb-form-repeater .bb-form-repeater__edit::after{position:absolute;right:-.0625rem;display:block;width:.0625rem;height:100%;background:#dee3e8;content:\"\"}@media (min-width:768px){.bb-form-repeater .bb-form-repeater__edit{margin-right:0}.bb-form-repeater .bb-form-repeater__edit::after{display:none}}@media (min-width:768px){.bb-form-repeater .bb-form-repeater__remove{left:.625rem}}.bb-form-repeater__buttons-fields{justify-content:flex-end}.bb-form-repeater__buttons-fields .bb-form-repeater__remove{border-left:none}.bb-form-repeater__label{display:flex;align-items:center;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-form-repeater__fields{padding:.71875rem 1.25rem 1.25rem}.bb-form-repeater__counter{margin-right:.125rem;transform:translateX(-.5rem)}.bb-form-repeater__footer{height:2.5rem;border-top:.0625rem dashed #9dabb9;margin:2.5rem -1.25rem -1.25rem}.bb-form-repeater__footer .bb-button+.bb-button{border-left:.0625rem dashed #9dabb9}.bb-form-repeater__info{padding:.625rem .625rem .3125rem;color:#89a;font-size:1rem;text-align:center;white-space:nowrap}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$j = undefined;
/* module identifier */
const __vue_module_identifier__$j = undefined;
/* functional template */
const __vue_is_functional_template__$j = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$j = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$j,
  staticRenderFns: __vue_staticRenderFns__$j
}, __vue_inject_styles__$j, __vue_script__$j, __vue_scope_id__$j, __vue_is_functional_template__$j, __vue_module_identifier__$j, false, createInjector, undefined, undefined);

var script$i = Vue.extend({
  name: 'bb-form-repeater-toggleable',
  components: {
    BbButton: __vue_component__$2v,
    BbToggleSwitch: __vue_component__$1Q
  },
  mixins: [Repeater],
  props: {
    /**
     * Always expand all elements
     */
    expandAll: {
      type: Boolean
    },
    removeButtonLabel: {
      type: String,
      default: 'Remove'
    },
    openButtonLabel: {
      type: String,
      default: 'Check details'
    },
    closeButtonLabel: {
      type: String,
      default: 'Close details'
    },
    /**
     * Allows to remove the row even if it's the only one'
     */
    notRequired: Boolean
  },
  methods: {
    notEmpty
  }
});

/* script */
const __vue_script__$i = script$i;

/* template */
var __vue_render__$i = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('ol', _vm._b({
    class: _vm.$options.name
  }, 'ol', {
    id: _vm.id
  }, false), [_vm._l(_vm.templateRows, function (row, rowIndex) {
    return _c('li', {
      key: row.id,
      class: _vm.rowClasses(row, rowIndex)
    }, [_vm.notEmpty(row.model) ? _c('div', {
      staticClass: "bb-form-repeater-toggleable__row-header"
    }, [_vm._t("header", null, {
      "model": row.model
    }), _vm._v(" "), !row.model.hideToggle ? _c('bb-toggle-switch', {
      staticClass: "bb-form-repeater-toggleable__toggle",
      attrs: {
        "name": row.id + "-toggle"
      },
      model: {
        value: row.model.isToggled,
        callback: function ($$v) {
          _vm.$set(row.model, "isToggled", $$v);
        },
        expression: "row.model.isToggled"
      }
    }) : _vm._e()], 2) : _vm._e(), _vm._v(" "), _c('div', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.isOpen(row),
        expression: "isOpen(row)"
      }],
      staticClass: "bb-form-repeater-toggleable__fields",
      attrs: {
        "aria-labelledby": row.id
      }
    }, [_vm._t("fields", null, {
      "id": row.id,
      "index": rowIndex,
      "model": row.model
    })], 2), _vm._v(" "), _c('div', {
      staticClass: "bb-form-repeater-toggleable__row-footer",
      attrs: {
        "aria-labelledby": row.id
      }
    }, [_c('bb-button', {
      directives: [{
        name: "show",
        rawName: "v-show",
        value: _vm.hasRemoveButton,
        expression: "hasRemoveButton"
      }],
      staticClass: "bb-form-repeater-toggleable__remove",
      attrs: {
        "size": "sm",
        "color": "gray",
        "no-side-paddings": "",
        "no-background": ""
      },
      on: {
        "click": function ($event) {
          return _vm.onRemove(rowIndex);
        }
      }
    }, [_c('bb-icon', {
      staticClass: "bb-form-repeater-toggleable__remove-icon",
      attrs: {
        "size": "24",
        "name": "ui-close-circle-outline"
      }
    }), _vm._v("\n        " + _vm._s(_vm.removeButtonLabel) + "\n      ")], 1), _vm._v(" "), _c('bb-button', {
      staticClass: "bb-form-repeater-toggleable__collapse",
      attrs: {
        "no-background": "",
        "size": "sm",
        "data-testid": "bb-form-repeater-toggleable__collapse",
        "no-side-paddings": ""
      },
      on: {
        "click": function () {
          return row.open = !row.open;
        }
      }
    }, [_vm._v("\n        " + _vm._s(row.open ? _vm.closeButtonLabel : _vm.openButtonLabel) + "\n        "), _c('bb-icon', {
      staticClass: "bb-form-repeater-toggleable__collapse-icon",
      attrs: {
        "size": "24",
        "name": "ui-chevron-" + (row.open ? 'up' : 'down')
      }
    })], 1)], 1)]);
  }), _vm._v(" "), _c('li', {
    staticClass: "bb-form-repeater-toggleable__controls"
  }, [_c('bb-button', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showAddButton,
      expression: "showAddButton"
    }],
    staticClass: "bb-form-repeater-toggleable__add",
    attrs: {
      "label": _vm.addButtonLabel,
      "no-background": "",
      "icon": "ui-plus-circle-outline",
      "data-testid": "bb-form-repeater-toggleable__add"
    },
    on: {
      "click": _vm.onAdd
    }
  }), _vm._v(" "), _c('div', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.showRowLimitText,
      expression: "showRowLimitText"
    }],
    staticClass: "bb-form-repeater-toggleable__info"
  }, [_vm._v("\n      " + _vm._s(_vm.rowLimitText) + "\n    ")])], 1)], 2);
};
var __vue_staticRenderFns__$i = [];

/* style */
const __vue_inject_styles__$i = function (inject) {
  if (!inject) return;
  inject("data-v-302f3e74_0", {
    source: ".repeater__highlights>:nth-child(2){overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.repeater__highlights>:nth-child(2){overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important}.repeater__header{position:fixed;display:flex;height:3.125rem;align-items:center;justify-content:space-between;padding-left:.75rem;border-radius:.3125rem;cursor:pointer;pointer-events:none;visibility:hidden}.repeater__header--visible{position:relative;pointer-events:all;visibility:visible}.repeater__header:hover{background-color:#eeeff2}.repeater__highlights{display:flex;min-width:0;flex:1;align-items:center;padding-right:6.875rem;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;white-space:nowrap}@media (min-width:768px){.repeater__highlights{padding-right:3.125rem}}.repeater__highlights>:nth-child(2){padding-right:1.25rem;margin-right:auto}.repeater__counter{display:inline-block;width:2rem;flex-shrink:0;margin-right:.625rem;color:#0ccca2;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.125rem;text-align:center}.bb-form-repeater-toggleable__row{position:relative;border-radius:.3125rem;margin-bottom:1.25rem}.bb-form-repeater-toggleable__row--error:not(.bb-form-repeater-toggleable__row--open){box-shadow:0 0 0 .0625rem #ff7155}.bb-form-repeater-toggleable__row-header{display:flex;align-items:flex-start;justify-content:space-between;padding:.625rem;background:#f7f7f8;cursor:pointer}.bb-form-repeater-toggleable__row--open .bb-form-repeater-toggleable__row-header{padding-bottom:.375rem}.bb-form-repeater-toggleable__label{margin-bottom:.4375rem;color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.5625rem}.bb-form-repeater-toggleable__value{color:#00285a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-form-repeater-toggleable__toggle{align-self:center}.bb-form-repeater-toggleable__fields{padding:0 .625rem 1.875rem;background:#f7f7f8}.bb-form-repeater-toggleable__row-footer{display:flex;align-items:center;justify-content:space-between;margin-top:.1875rem;background:#f7f7f8}.bb-form-repeater-toggleable__remove{padding-right:.75rem;padding-left:.4375rem;margin-right:auto}.bb-form-repeater-toggleable__remove-icon{margin-right:.4375rem}.bb-form-repeater-toggleable__collapse{padding-right:.4375rem;padding-left:.75rem;margin-left:auto}.bb-form-repeater-toggleable__collapse-icon{margin-left:.4375rem}.bb-form-repeater-toggleable__controls{text-align:center}.bb-form-repeater-toggleable__info{padding:.625rem .625rem .3125rem;color:#89a;font-size:1rem;text-align:center;white-space:nowrap}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$i = undefined;
/* module identifier */
const __vue_module_identifier__$i = undefined;
/* functional template */
const __vue_is_functional_template__$i = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$i = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$i,
  staticRenderFns: __vue_staticRenderFns__$i
}, __vue_inject_styles__$i, __vue_script__$i, __vue_scope_id__$i, __vue_is_functional_template__$i, __vue_module_identifier__$i, false, createInjector, undefined, undefined);

var Icon$6 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg1971026717__a bb-icon__dynamic-fill",attrs:{"d":"M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z"}})]) } };

Vue.component('icon-ui-filter', Icon$6);

dayjs.extend(isBetween);
const templateFields = {
  topic: 'select',
  taskName: 'select',
  commentType: 'select',
  contactChannel: 'typeahead',
  registrationDate: 'datepicker',
  contactDirection: 'select',
  user: 'typeahead',
  isValid: 'select',
  isUrgent: 'select'
};
const componentsDefaultProps = {
  select: {
    framed: true,
    small: true,
    noempty: true
  },
  typeahead: {
    labelPropName: 'text',
    trackBy: 'value',
    small: true,
    multiple: true
  },
  datepicker: {
    range: true,
    framed: true,
    small: true
  }
};
/**
 * A filter for memos.
 */
var script$h = Vue.extend({
  name: 'bb-memo-filter',
  components: {
    BbIcon: __vue_component__$2C,
    BbButton: __vue_component__$2v,
    BbSelect: __vue_component__$20,
    BbTypeahead: __vue_component__$14,
    BbDatepicker: __vue_component__$1o
  },
  props: {
    /**
     * Memos to be filtered.
     * Field names: `topic`, `taskName`, `commentType`, `contactChannel`,
     * `registrationDate`, `contactDirection`, `user`, `isUrgent`, `isValid`.
     */
    memos: {
      type: Array,
      required: true
    },
    /**
     * Title for the filters.
     */
    title: {
      type: String,
      default: 'Filters'
    },
    /**
     * Props for all filter fields.
     * Field names: `topic`, `taskName`, `commentType`, `contactChannel`,
     * `registrationDate`, `contactDirection`, `user`, `isUrgent`, `isValid`.
     */
    fieldsProps: {
      type: Object,
      default: () => ({
        topic: {
          label: 'Topic',
          placeholder: 'Select topic'
        },
        taskName: {
          label: 'Task name',
          placeholder: 'Select task'
        },
        commentType: {
          label: 'Type',
          placeholder: 'Select comment type'
        },
        contactChannel: {
          label: 'Channel',
          placeholder: 'Select contact channel'
        },
        registrationDate: {
          label: 'Registration date',
          placeholder: 'Select registration date',
          format: 'DD.MM.YYYY'
        },
        contactDirection: {
          label: 'Direction',
          placeholder: 'Select contact direction'
        },
        user: {
          label: 'User',
          placeholder: 'Select creator'
        },
        isValid: {
          label: 'Valid',
          placeholder: 'Select validity'
        },
        isUrgent: {
          label: 'Is urgent priority',
          placeholder: 'Select urgency'
        }
      })
    },
    /**
     * Clear filters button label.
     */
    clearFiltersLabel: {
      type: String,
      default: 'Clear filters'
    }
  },
  data() {
    return {
      templateFields,
      componentsDefaultProps,
      fieldsModels: mapValues(templateFields, () => null)
    };
  },
  computed: {
    fieldsOptions() {
      return mapValues(this.templateFields, (_, fieldName) => uniqueArrayBy(this.memos, memo => memo[fieldName]).map(uniqueMemoValue => ({
        text: `${uniqueMemoValue}`,
        value: uniqueMemoValue
      })));
    },
    filterObject() {
      const filterEntries = Object.keys(this.templateFields).filter(fieldName => notEmpty(this.fieldsModels[fieldName])).map(fieldName => [fieldName, this.castFilterValue(fieldName)]);
      return Object.fromEntries(filterEntries);
    },
    filteredMemos() {
      return this.memos.filter(memo =>
      // eslint-disable-next-line
      Object.entries(this.filterObject).every(([fieldName, filterValue]) => this.isValueInFilter(memo[fieldName], filterValue)));
    }
  },
  watch: {
    filteredMemos: {
      deep: true,
      handler() {
        /**
         * Emitted whenever memos have been filtered.
         * @event input
         * @type {array}
         */
        this.$emit('input', this.filteredMemos);
      }
    }
  },
  methods: {
    onClear() {
      Object.keys(this.fieldsModels).forEach(fieldName => this.$set(this.fieldsModels, fieldName, null));
    },
    isValueInFilter(memoValue, filterValue) {
      if (filterValue.some(isDate)) {
        const [notBefore, notAfter] = filterValue; // eslint-disable-line
        return dayjs(memoValue).isBetween(notBefore, notAfter, 'day', '[]');
      }
      return filterValue.includes(memoValue);
    },
    fieldOptions(fieldName) {
      if ('datepicker' !== this.templateFields[fieldName]) {
        return this.fieldsOptions[fieldName];
      }
    },
    castFilterValue(fieldName) {
      return castArray(this.fieldsModels[fieldName]).map(valueItem => {
        var _valueItem$value;
        return (_valueItem$value = valueItem === null || valueItem === void 0 ? void 0 : valueItem.value) !== null && _valueItem$value !== void 0 ? _valueItem$value : valueItem;
      });
    }
  }
});

/* script */
const __vue_script__$h = script$h;

/* template */
var __vue_render__$h = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-memo-filter"
  }, [_c('div', {
    staticClass: "bb-memo-filter__header"
  }, [_c('bb-icon', {
    attrs: {
      "name": "ui-filter",
      "size": "24",
      "fill": "white"
    }
  }), _vm._v(" "), _c('span', {
    staticClass: "bb-memo-filter__title"
  }, [_vm._v(_vm._s(_vm.title))])], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-memo-filter__content"
  }, [_vm._l(_vm.templateFields, function (fieldComponent, fieldName) {
    return _c("bb-" + fieldComponent, _vm._b({
      key: fieldName,
      tag: "component",
      attrs: {
        "options": _vm.fieldOptions(fieldName),
        "name": fieldName
      },
      model: {
        value: _vm.fieldsModels[fieldName],
        callback: function ($$v) {
          _vm.$set(_vm.fieldsModels, fieldName, $$v);
        },
        expression: "fieldsModels[fieldName]"
      }
    }, 'component', Object.assign({}, _vm.componentsDefaultProps[fieldComponent] || {}, _vm.fieldsProps[fieldName] || {}), false));
  }), _vm._v(" "), _c('bb-button', {
    staticClass: "m-t-20",
    attrs: {
      "label": _vm.clearFiltersLabel,
      "icon": "ui-close",
      "size": "sm",
      "color": "red",
      "display": "block",
      "no-background": ""
    },
    on: {
      "click": _vm.onClear
    }
  })], 2)]);
};
var __vue_staticRenderFns__$h = [];

/* style */
const __vue_inject_styles__$h = function (inject) {
  if (!inject) return;
  inject("data-v-6e163a32_0", {
    source: ".bb-memo-filter{background:#fff}.bb-memo-filter .bb-memo-filter__header{display:flex;height:2.5rem;align-items:center;justify-content:center;background:#6eb3f7}.bb-memo-filter .bb-memo-filter__title{margin-left:.6875rem;color:#fff;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;text-transform:uppercase}.bb-memo-filter .bb-memo-filter__content{padding:.3125rem 1.25rem 1.25rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$h = undefined;
/* module identifier */
const __vue_module_identifier__$h = undefined;
/* functional template */
const __vue_is_functional_template__$h = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$h = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$h,
  staticRenderFns: __vue_staticRenderFns__$h
}, __vue_inject_styles__$h, __vue_script__$h, __vue_scope_id__$h, __vue_is_functional_template__$h, __vue_module_identifier__$h, false, createInjector, undefined, undefined);

var script$g = {
  name: 'bb-mobile-menu-landing-accordion-item',
  components: {
    BbFunctionalAccordion: __vue_component__$2m,
    BbLink: __vue_component__$2p,
    BbIcon: __vue_component__$2C,
    BbLabel: __vue_component__$2e
  },
  props: {
    type: {
      type: String,
      default: 'dl'
    },
    active: Boolean,
    link: {
      type: Object,
      default: null
    },
    level: {
      type: Number,
      default: 1
    },
    title: String
  },
  emits: ['open', 'close', 'linkClick'],
  data() {
    return {
      expanded: this.active
    };
  },
  computed: {
    classes() {
      return ['bb-mobile-menu-landing-accordion-item', `bb-mobile-menu-landing-accordion-item--level-${this.level}`, {
        'is-open': this.expanded
      }];
    }
  },
  watch: {
    expanded() {
      if (this.expanded) {
        this.$emit('open');
      } else {
        this.$emit('close');
      }
    },
    active(newVal) {
      this.expanded = newVal;
    }
  },
  methods: {
    onOpen() {
      this.expanded = true;
    },
    onClose() {
      this.expanded = false;
    },
    onClick() {
      this.$emit('linkClick');
    },
    notEmpty
  }
};

/* script */
const __vue_script__$g = script$g;

/* template */
var __vue_render__$g = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm.link ? _c('bb-link', {
    staticClass: "bb-mobile-menu-landing-accordion-item__link",
    attrs: {
      "to": _vm.link.to,
      "color": _vm.expanded ? 'mint' : 'gray',
      "title": _vm.link.label
    },
    on: {
      "click": _vm.onClick
    }
  }, [_c('span', {
    staticClass: "bb-mobile-menu-landing-accordion-item__link-label"
  }, [_vm._v(_vm._s(_vm.link.label))]), _vm._v(" "), _vm.link.tag ? _c('bb-label', {
    staticClass: "bb-mobile-menu-landing-accordion-item__link-tag",
    attrs: {
      "size": "18",
      "value": _vm.link.tag
    }
  }) : _vm._e()], 1) : [_c('bb-functional-accordion', {
    class: _vm.classes,
    attrs: {
      "open": _vm.expanded
    },
    on: {
      "open": _vm.onOpen,
      "close": _vm.onClose
    },
    scopedSlots: _vm._u([{
      key: "header",
      fn: function (ref) {
        var accordionExpanded = ref.expanded;
        return [_c('div', {
          staticClass: "bb-mobile-menu-landing-accordion-item__header"
        }, [_vm.notEmpty(_vm.title) ? _c('span', {
          staticClass: "bb-mobile-menu-landing-accordion-item__header-title",
          attrs: {
            "title": _vm.title
          }
        }, [_vm._v("\n            " + _vm._s(_vm.title) + "\n          ")]) : _vm._e(), _vm._v(" "), _c('bb-icon', {
          attrs: {
            "rotate": accordionExpanded ? 'down' : 'up',
            "name": "ui-chevron-down",
            "size": "24"
          }
        })], 1)];
      }
    }])
  }, [_vm._v(" "), _c('div', {
    staticClass: "bb-mobile-menu-landing-accordion-item__content"
  }, [_vm._t("default")], 2)])]], 2);
};
var __vue_staticRenderFns__$g = [];

/* style */
const __vue_inject_styles__$g = function (inject) {
  if (!inject) return;
  inject("data-v-96c2d7a8_0", {
    source: ".bb-mobile-menu-landing-accordion-item{width:100%}.bb-mobile-menu-landing-accordion-item__header{position:relative;display:flex;height:2.8125rem;align-items:center;justify-content:space-between;border:none;-moz-appearance:none;-webkit-appearance:none;background:0 0;color:#687d92;cursor:pointer;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:inherit;outline:0;transition:background .2s,color .2s;user-select:none}.bb-mobile-menu-landing-accordion-item--level-1.is-open>.bb-functional-accordion__header .bb-mobile-menu-landing-accordion-item__header{background:#3dd6b5;color:#fff}.bb-mobile-menu-landing-accordion-item--level-1.is-open>.bb-functional-accordion__header .bb-mobile-menu-landing-accordion-item__header .bb-mobile-menu-landing-accordion-item__header-title{color:#fff}.bb-mobile-menu-landing-accordion-item--level-2.is-open>.bb-functional-accordion__header .bb-mobile-menu-landing-accordion-item__header{background:#f7f7f8}.bb-mobile-menu-landing-accordion-item--level-2.is-open>.bb-functional-accordion__header .bb-mobile-menu-landing-accordion-item__header .bb-mobile-menu-landing-accordion-item__header-title{color:#0ccca2}.bb-mobile-menu-landing-accordion-item--level-1 .bb-mobile-menu-landing-accordion-item__header{padding:0 1.5625rem 0 2.125rem;text-transform:uppercase}.bb-mobile-menu-landing-accordion-item--level-2 .bb-mobile-menu-landing-accordion-item__header{padding:0 1.5625rem 0 2.875rem;background-color:#f7f7f8;text-transform:uppercase}.bb-mobile-menu-landing-accordion-item--level-2:not(:last-of-type) .bb-mobile-menu-landing-accordion-item__header{border-bottom:solid .0625rem #eeeff2}.bb-mobile-menu-landing-accordion-item__header-title{display:-webkit-box;overflow:hidden;margin-bottom:0;-webkit-box-orient:vertical;box-orient:vertical;color:#687d92;font-size:.875rem;-webkit-line-clamp:2;line-clamp:2;line-height:1.1875rem;transition:color .2s;user-select:none}.bb-mobile-menu-landing-accordion-item__header:focus .bb-mobile-menu-landing-accordion-item__header-content::after{position:absolute;top:0;left:0;width:100%;height:100%;border:.0625rem dashed rgba(136,153,170,.5);border-radius:.1875rem;content:\"\"}.bb-mobile-menu-landing-accordion-item__header-content:focus,.bb-mobile-menu-landing-accordion-item__header:focus{outline:0}.bb-mobile-menu-landing-accordion-item__content{overflow:hidden;height:auto;color:#38414d;font-size:.875rem}.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link{display:flex;height:2.8125rem;align-items:center;justify-content:space-between;padding-right:1.5625rem}.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link.bb-link{box-shadow:none}.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link.bb-link:active,.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link.bb-link:focus,.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link.bb-link:hover{box-shadow:none}.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link.bb-link::before{content:none}.bb-mobile-menu-landing-accordion-item--level-1.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link{padding-left:2.125rem;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-transform:uppercase}.bb-mobile-menu-landing-accordion-item--level-2.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link .bb-mobile-menu-landing-accordion-item__link-label,.bb-mobile-menu-landing-accordion-item--level-3.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link .bb-mobile-menu-landing-accordion-item__link-label{display:-webkit-box;overflow:hidden;-webkit-box-orient:vertical;box-orient:vertical;-webkit-line-clamp:2;line-clamp:2;line-height:1.1875rem}.bb-mobile-menu-landing-accordion-item--level-2.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link{padding-left:2.875rem;background-color:#f7f7f8;text-transform:uppercase}.bb-mobile-menu-landing-accordion-item--level-3.bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link{height:3.4375rem;padding-left:3.375rem}.bb-mobile-menu-landing-accordion-item--level-3:not(:last-of-type).bb-mobile-menu-landing-accordion-item>.bb-mobile-menu-landing-accordion-item__link::after{position:absolute;right:1.5625rem;bottom:0;left:1.5625rem;max-width:100%;height:.0625rem;background:#dee3e8;content:\"\"}.bb-mobile-menu-landing-accordion-item__link-tag{flex-shrink:0;margin-right:.375rem;margin-left:.9375rem;background:#0ccca2;font-size:.5625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$g = undefined;
/* module identifier */
const __vue_module_identifier__$g = undefined;
/* functional template */
const __vue_is_functional_template__$g = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$g = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$g,
  staticRenderFns: __vue_staticRenderFns__$g
}, __vue_inject_styles__$g, __vue_script__$g, __vue_scope_id__$g, __vue_is_functional_template__$g, __vue_module_identifier__$g, false, createInjector, undefined, undefined);

const PRISTINE_SELECTION = Symbol();
const childrenPropertyNames = ['sections', 'products', 'children'];
var script$f = {
  name: 'bb-mobile-menu-landing-accordion',
  components: {
    BbMobileMenuLandingAccordionItem: __vue_component__$g
  },
  props: {
    value: {
      type: Array,
      default() {
        return [];
      }
    },
    level: {
      type: Number,
      default: 1
    }
  },
  emits: ['linkClick'],
  data() {
    return {
      activeItemId: PRISTINE_SELECTION
    };
  },
  methods: {
    skipLevel2IfNoChildren(item) {
      return this.level === 2 && !this.hasChildren(item) ? 3 : this.level;
    },
    getChildren(item) {
      const childrenPropertyName = childrenPropertyNames.find(key => item.hasOwnProperty(key));
      return childrenPropertyName ? item[childrenPropertyName] : [];
    },
    hasChildren(item) {
      return notEmpty(this.getChildren(item));
    },
    isTheOnlySiblingAndHasChildren(item) {
      return this.level > 1 && this.isTheOnlySibling() && this.hasChildren(item);
    },
    isActive(item) {
      if (this.activeItemId === PRISTINE_SELECTION) {
        return this.containsActive(item) || this.isTheOnlySiblingAndHasChildren(item);
      }
      return this.activeItemId === item.id;
    },
    containsActive(item) {
      return item.active || this.getChildren(item).some(this.containsActive);
    },
    isTheOnlySibling() {
      var _this$value;
      return ((_this$value = this.value) === null || _this$value === void 0 ? void 0 : _this$value.length) === 1;
    },
    getLink(item) {
      return this.hasChildren(item) ? undefined : {
        to: item.to,
        label: item.label,
        tag: item.tag
      };
    },
    onOpen(item) {
      this.activeItemId = item.id;
    },
    onClick() {
      this.$emit('linkClick');
    }
  }
};

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$f = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-mobile-menu-landing-accordion"
  }, _vm._l(_vm.value, function (item, itemIndex) {
    return _c('bb-mobile-menu-landing-accordion-item', {
      key: item.id,
      class: ['bb-mobile-menu-landing-accordion__item', "bb-mobile-menu-landing-accordion__item-" + itemIndex],
      attrs: {
        "type": _vm.hasChildren(item) ? 'dl' : 'div',
        "active": _vm.isActive(item),
        "link": _vm.getLink(item),
        "title": item.label,
        "level": _vm.skipLevel2IfNoChildren(item)
      },
      on: {
        "open": function ($event) {
          return _vm.onOpen(item);
        },
        "linkClick": _vm.onClick
      }
    }, [_vm.hasChildren(item) ? _c('bb-mobile-menu-landing-accordion', {
      attrs: {
        "value": _vm.getChildren(item),
        "level": _vm.level + 1
      },
      on: {
        "linkClick": _vm.onClick
      }
    }) : _vm._e()], 1);
  }), 1);
};
var __vue_staticRenderFns__$f = [];

/* style */
const __vue_inject_styles__$f = undefined;
/* scoped */
const __vue_scope_id__$f = undefined;
/* module identifier */
const __vue_module_identifier__$f = undefined;
/* functional template */
const __vue_is_functional_template__$f = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$f = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$f,
  staticRenderFns: __vue_staticRenderFns__$f
}, __vue_inject_styles__$f, __vue_script__$f, __vue_scope_id__$f, __vue_is_functional_template__$f, __vue_module_identifier__$f, false, undefined, undefined, undefined);

var script$e = {
  name: 'bb-contact-menu',
  components: {
    BbContactMenuItem: __vue_component__$1g,
    BbSocialLinks: __vue_component__$1Z,
    BbSeparator: __vue_component__$2a
  },
  props: {
    title: String,
    subTitle: String,
    links: Array,
    socialLinks: Array,
    contained: Boolean
  },
  computed: {
    classes() {
      return ['bb-contact-menu', {
        'bb-contact-menu--contained': this.contained,
        'bb-contact-menu--ratio-compensation': this.links.length < 3
      }];
    }
  }
};

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$e = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [!_vm.contained ? [_c('h4', {
    staticClass: "bb-contact-menu__title"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]), _vm._v(" "), _c('p', {
    staticClass: "bb-contact-menu__subtitle"
  }, [_vm._v("\n      " + _vm._s(_vm.subTitle) + "\n    ")]), _vm._v(" "), _c('bb-separator', {
    attrs: {
      "dashed": ""
    }
  })] : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-contact-menu__items"
  }, _vm._l(_vm.links, function (item, index) {
    return _c('bb-contact-menu-item', {
      key: index,
      attrs: {
        "icon": item.icon,
        "icon-color": item.iconColor,
        "label": item.label,
        "link": item.link,
        "background-color": "gray-10"
      }
    });
  }), 1), _vm._v(" "), _c('div', {
    staticClass: "bb-contact-menu__social"
  }, [_c('bb-social-links', {
    attrs: {
      "links": _vm.socialLinks
    }
  })], 1)], 2);
};
var __vue_staticRenderFns__$e = [];

/* style */
const __vue_inject_styles__$e = function (inject) {
  if (!inject) return;
  inject("data-v-7a44634a_0", {
    source: ".bb-contact-menu{display:flex;width:18.625rem;flex-direction:column;align-items:center;justify-content:center;padding-top:1.5rem;border-radius:0 0 .625rem .625rem;background:#fff;box-shadow:0 1.5rem 3.125rem rgba(0,0,0,.08)}.bb-contact-menu--contained{width:100%;padding-top:0;border-radius:0;background:0 0;box-shadow:none}.bb-contact-menu .bb-contact-menu__subtitle,.bb-contact-menu .bb-contact-menu__title{padding:0 1.5rem;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;text-align:center}.bb-contact-menu .bb-contact-menu__title{margin-bottom:1.25rem;color:#38414d;font-size:1rem;line-height:1.5}.bb-contact-menu .bb-contact-menu__subtitle{padding-bottom:1.5rem;color:#57697a;font-size:.875rem;line-height:1.125rem}.bb-contact-menu .bb-contact-menu__items{display:flex;width:100%;flex-direction:column;align-content:flex-start;padding:1.5rem 1.5rem 0}.bb-contact-menu .bb-contact-menu__social{display:flex;width:100%;height:3.5rem;align-items:center;justify-content:center;padding:0 1.5rem;border-radius:0 0 .625rem .625rem;background:#f7f7f8}.bb-contact-menu--ratio-compensation.bb-contact-menu .bb-contact-menu__social{height:4.5rem}.bb-contact-menu--contained.bb-contact-menu .bb-contact-menu__social{justify-content:flex-start;border-radius:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$e = undefined;
/* module identifier */
const __vue_module_identifier__$e = undefined;
/* functional template */
const __vue_is_functional_template__$e = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$e,
  staticRenderFns: __vue_staticRenderFns__$e
}, __vue_inject_styles__$e, __vue_script__$e, __vue_scope_id__$e, __vue_is_functional_template__$e, __vue_module_identifier__$e, false, createInjector, undefined, undefined);

var script$d = {
  name: 'bb-mobile-menu-landing',
  components: {
    BbMobileMenuLandingAccordion: __vue_component__$f,
    BbContactMenu: __vue_component__$e,
    BbDrawer: __vue_component__$1l,
    FocusTrap
  },
  props: {
    visible: Boolean,
    links: Array,
    contactMenu: Object,
    drawerLabel: String
  },
  emits: ['close'],
  methods: {
    onClose() {
      this.$emit('close');
    },
    notEmpty
  }
};

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$d = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('focus-trap', {
    staticClass: "bb-mobile-menu-landing",
    attrs: {
      "active": _vm.visible
    }
  }, [_c('bb-drawer', _vm._g({
    staticClass: "bb-mobile-menu-landing__drawer",
    attrs: {
      "navigation": "",
      "left": "",
      "visible": _vm.visible,
      "role": "navigation",
      "aria-label": _vm.drawerLabel,
      "aria-hidden": !_vm.visible
    },
    on: {
      "close": _vm.onClose
    }
  }, _vm.$listeners), [_c('div', {
    staticClass: "bb-mobile-menu-landing__content",
    attrs: {
      "role": "menu"
    }
  }, [_c('bb-mobile-menu-landing-accordion', {
    staticClass: "bb-mobile-menu-landing__accordion",
    attrs: {
      "value": _vm.links
    },
    on: {
      "linkClick": _vm.onClose
    }
  }), _vm._v(" "), _vm._t("menuItemsAfter"), _vm._v(" "), _vm.notEmpty(_vm.contactMenu) ? _c('bb-contact-menu', _vm._b({
    staticClass: "bb-mobile-menu-landing__contact-menu",
    attrs: {
      "contained": ""
    }
  }, 'bb-contact-menu', _vm.contactMenu, false)) : _vm._e()], 2)])], 1);
};
var __vue_staticRenderFns__$d = [];

/* style */
const __vue_inject_styles__$d = function (inject) {
  if (!inject) return;
  inject("data-v-6df6936d_0", {
    source: ".bb-mobile-menu-landing{background:#fff}.bb-mobile-menu-landing__drawer .bb-drawer__dialog{max-height:100vh;padding-top:3.125rem}@media (min-width:992px){.bb-mobile-menu-landing__drawer .bb-drawer__dialog{padding-top:4rem}}.bb-mobile-menu-landing__drawer .bb-drawer__header{min-height:unset}.bb-mobile-menu-landing__content{display:flex;flex:1 1 0;flex-direction:column}.bb-mobile-menu-landing__accordion{margin-top:1.5rem}.bb-mobile-menu-landing__contact-menu{margin-top:auto}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$d = undefined;
/* module identifier */
const __vue_module_identifier__$d = undefined;
/* functional template */
const __vue_is_functional_template__$d = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$d = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$d,
  staticRenderFns: __vue_staticRenderFns__$d
}, __vue_inject_styles__$d, __vue_script__$d, __vue_scope_id__$d, __vue_is_functional_template__$d, __vue_module_identifier__$d, false, createInjector, undefined, undefined);

var script$c = {
  name: 'bb-smart-menu-product',
  components: {
    BbListGroup: __vue_component__$2b,
    BbListItem: __vue_component__$29
  },
  props: {
    imageUrl: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      required: true
    }
  },
  methods: {
    listItemTitle(item) {
      return isObject$1(item) ? `${item.name} ${item.value}` : item;
    }
  }
};

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-smart-menu-product"
  }, [_c('div', {
    staticClass: "bb-smart-menu-product__image",
    style: {
      backgroundImage: "url(" + _vm.imageUrl + ")"
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "bb-smart-menu-product__text-wrapper"
  }, [_c('div', {
    staticClass: "bb-smart-menu-product__title"
  }, [_vm._v("\n      " + _vm._s(_vm.title) + "\n    ")]), _vm._v(" "), _c('bb-list-group', _vm._l(_vm.items, function (item, index) {
    return _c('bb-list-item', {
      key: index,
      attrs: {
        "title": _vm.listItemTitle(item),
        "title-uppercase": false,
        "bullet": "",
        "condensed": ""
      }
    });
  }), 1)], 1)]);
};
var __vue_staticRenderFns__$c = [];

/* style */
const __vue_inject_styles__$c = function (inject) {
  if (!inject) return;
  inject("data-v-4c481c53_0", {
    source: ".bb-smart-menu-product{display:flex;flex-direction:column}.bb-smart-menu-product__image{height:12.75rem;background-size:cover;border-top-left-radius:.625rem;border-top-right-radius:.625rem}.bb-smart-menu-product__text-wrapper{flex-grow:1;padding:2rem;background-color:#e7faf6;border-bottom-left-radius:.625rem;border-bottom-right-radius:.625rem}.bb-smart-menu-product__title{padding-bottom:.75rem;color:#00285a;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1.5rem;line-height:1.1}.bb-smart-menu-product .bb-list-item__title{font-size:.875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$c = undefined;
/* module identifier */
const __vue_module_identifier__$c = undefined;
/* functional template */
const __vue_is_functional_template__$c = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$c,
  staticRenderFns: __vue_staticRenderFns__$c
}, __vue_inject_styles__$c, __vue_script__$c, __vue_scope_id__$c, __vue_is_functional_template__$c, __vue_module_identifier__$c, false, createInjector, undefined, undefined);

var script$b = Vue.extend({
  name: 'bb-smart-menu',
  components: {
    BbMenuItem: __vue_component__$X,
    BbSmartMenuProduct: __vue_component__$c,
    BbButton: __vue_component__$2v,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r
  },
  props: {
    /** Array of tabs that contain products and their content */
    value: Array,
    /** Initial active tab and product id */
    active: Object
  },
  data() {
    return {
      activeTabId: this.active.tabId,
      activeProductId: this.active.productId
    };
  },
  computed: {
    tabs() {
      return this.value.map(({
        id,
        label
      }) => ({
        id,
        label
      }));
    },
    products() {
      var _this$value;
      const activeTab = (_this$value = this.value) === null || _this$value === void 0 ? void 0 : _this$value.find(tab => tab.id === this.activeTabId);
      return activeTab === null || activeTab === void 0 ? void 0 : activeTab.products;
    },
    product() {
      var _this$products;
      const product = (_this$products = this.products) === null || _this$products === void 0 ? void 0 : _this$products.find(item => item.id === this.activeProductId);
      return product || [{
        overview: [],
        imageUrl: '',
        description: '',
        ctaLabel: ''
      }];
    }
  },
  methods: {
    arrow(ctaLabel) {
      return isDefined(ctaLabel) ? 'visible' : 'hover';
    },
    uppercaseLabel(label) {
      return label.toUpperCase();
    },
    isDefined,
    activateFirstProduct() {
      this.activeProductId = this.products[0].id;
    },
    onProductHover(product) {
      if (isDefined(product.ctaLabel)) {
        this.activeProductId = product.id;
      }
    },
    onTabClick(tab) {
      this.activeTabId = tab.id;
      this.activateFirstProduct();
    },
    onProductButtonClick() {
      this.$emit('redirect');
    }
  }
});

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-smart-menu"
  }, [_c('div', {
    staticClass: "bb-smart-menu__main"
  }, [_vm.tabs.length > 1 ? _c('ul', {
    staticClass: "bb-smart-menu__main-menu-items"
  }, _vm._l(_vm.tabs, function (ref) {
    var label = ref.label;
    var id = ref.id;
    return _c('li', {
      key: label,
      staticClass: "bb-smart-menu__main-menu-item-wrapper"
    }, [_c('bb-menu-item', {
      attrs: {
        "id": id,
        "label": _vm.uppercaseLabel(label),
        "active": _vm.activeTabId === id,
        "arrow": "visible",
        "aria-controls": "panel-" + id,
        "aria-selected": _vm.activeTabId === id
      },
      on: {
        "click": function ($event) {
          return _vm.onTabClick({
            id: id
          });
        }
      },
      nativeOn: {
        "keydown": [function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
            return null;
          }
          $event.stopPropagation();
          return _vm.onTabClick({
            id: id
          });
        }, function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
            return null;
          }
          $event.stopPropagation();
          $event.preventDefault();
          return _vm.onTabClick({
            id: id
          });
        }]
      }
    })], 1);
  }), 0) : _vm._e(), _vm._v(" "), _c('ul', {
    staticClass: "bb-smart-menu__submenu-items"
  }, _vm._l(_vm.products, function (ref, index) {
    var icon = ref.icon;
    var label = ref.label;
    var id = ref.id;
    var to = ref.to;
    var ctaLabel = ref.ctaLabel;
    return _c('li', {
      key: label,
      staticClass: "bb-smart-menu__submenu-item-wrapper"
    }, [_c('bb-menu-item', _vm._b({
      attrs: {
        "active": _vm.activeProductId === id,
        "arrow": _vm.arrow(ctaLabel),
        "aria-current": _vm.activeProductId === id
      },
      on: {
        "mouseover": function ($event) {
          return _vm.onProductHover(_vm.products[index]);
        },
        "click": _vm.onProductButtonClick
      },
      nativeOn: {
        "focus": function ($event) {
          return _vm.onProductHover(_vm.products[index]);
        }
      }
    }, 'bb-menu-item', {
      icon: icon,
      label: label,
      to: to,
      id: id
    }, false))], 1);
  }), 0), _vm._v(" "), _c('div', {
    staticClass: "bb-smart-menu__content",
    attrs: {
      "id": "panel-" + _vm.activeTabId,
      "aria-labelledby": _vm.activeTabId
    }
  }, [_c('bb-smart-menu-product', {
    staticClass: "bb-smart-menu__product",
    attrs: {
      "image-url": _vm.product.imageUrl,
      "title": _vm.product.description,
      "items": _vm.product.overview
    }
  }), _vm._v(" "), _c('bb-row', {
    staticClass: "bb-smart-menu__cta-buttons"
  }, [_c('bb-col', [_c('bb-button', {
    staticClass: "bb-smart-menu__cta--primary",
    attrs: {
      "label": _vm.product.ctaLabel,
      "to": _vm.product.to,
      "size": "md"
    },
    on: {
      "click": _vm.onProductButtonClick
    }
  })], 1), _vm._v(" "), _vm.product.ctaLabelSecondary ? _c('bb-col', [_c('bb-button', {
    staticClass: "bb-smart-menu__cta--secondary",
    attrs: {
      "label": _vm.product.ctaLabelSecondary,
      "to": _vm.product.toSecondary,
      "size": "md",
      "inverted": ""
    },
    on: {
      "click": _vm.onProductButtonClick
    }
  })], 1) : _vm._e()], 1)], 1)])]);
};
var __vue_staticRenderFns__$b = [];

/* style */
const __vue_inject_styles__$b = function (inject) {
  if (!inject) return;
  inject("data-v-33c9543b_0", {
    source: ".bb-smart-menu{display:flex;width:100%;min-height:37rem;border-radius:0 0 .625rem .625rem;background:#fff;box-shadow:0 1.5rem 3.125rem rgba(0,0,0,.08)}@media (min-width:1200px){.bb-smart-menu{max-width:69.375rem}}.bb-smart-menu .bb-smart-menu__main{display:flex;width:100%;padding:1.5rem}.bb-smart-menu .bb-smart-menu__main-menu-items,.bb-smart-menu .bb-smart-menu__submenu-items{overflow:auto;flex-grow:1;padding-right:1.25rem}.bb-smart-menu .bb-smart-menu__main-menu-items{flex-basis:20%}.bb-smart-menu .bb-smart-menu__submenu-items{flex-basis:30%}.bb-smart-menu .bb-smart-menu__main-menu-item-wrapper:last-child,.bb-smart-menu .bb-smart-menu__submenu-item-wrapper:last-child{margin-bottom:1.875rem}.bb-smart-menu .bb-smart-menu__content{display:flex;flex-basis:50%;flex-direction:column}.bb-smart-menu .bb-smart-menu__product{flex-grow:1;margin-bottom:1.5rem}.bb-smart-menu .bb-smart-menu__cta--primary,.bb-smart-menu .bb-smart-menu__cta--secondary{width:100%}.bb-smart-menu .bb-smart-menu__cta--primary{align-self:flex-end}.bb-smart-menu .bb-smart-menu__cta-buttons{display:flex}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$b = undefined;
/* module identifier */
const __vue_module_identifier__$b = undefined;
/* functional template */
const __vue_is_functional_template__$b = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$b = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$b,
  staticRenderFns: __vue_staticRenderFns__$b
}, __vue_inject_styles__$b, __vue_script__$b, __vue_scope_id__$b, __vue_is_functional_template__$b, __vue_module_identifier__$b, false, createInjector, undefined, undefined);

var script$a = {
  name: 'bb-navbar-dropdown',
  components: {
    BbMenuItem: __vue_component__$X
  },
  props: {
    links: Array
  },
  emits: ['redirect'],
  methods: {
    linkIsActive(link) {
      return link.active || isDefined(this.$nuxt) && this.$nuxt.$route.path === link.to;
    },
    onMenuItemClick(link) {
      this.$emit('redirect', link);
    }
  }
};

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-navbar-dropdown"
  }, [_vm._l(_vm.links, function (link, index) {
    return [_c('bb-menu-item', _vm._b({
      key: index,
      staticClass: "bb-navbar-dropdown__link",
      attrs: {
        "active": _vm.linkIsActive(link)
      },
      on: {
        "click": function ($event) {
          return _vm.onMenuItemClick(link);
        }
      }
    }, 'bb-menu-item', link, false))];
  })], 2);
};
var __vue_staticRenderFns__$a = [];

/* style */
const __vue_inject_styles__$a = function (inject) {
  if (!inject) return;
  inject("data-v-9dcef8b2_0", {
    source: ".bb-navbar-dropdown{z-index:1400;width:min-content;padding:.9375rem;border-radius:0 0 .625rem .625rem;background:#fff;box-shadow:0 1.5rem 3.125rem rgba(0,0,0,.08)}.bb-navbar-dropdown__link{white-space:nowrap}.bb-navbar-dropdown__link:not(:last-of-type){margin-bottom:.625rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$a = undefined;
/* module identifier */
const __vue_module_identifier__$a = undefined;
/* functional template */
const __vue_is_functional_template__$a = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$a,
  staticRenderFns: __vue_staticRenderFns__$a
}, __vue_inject_styles__$a, __vue_script__$a, __vue_scope_id__$a, __vue_is_functional_template__$a, __vue_module_identifier__$a, false, createInjector, undefined, undefined);

/**
 * Navbar for Admin flavor
 */
var script$9 = defineComponent({
  name: 'bb-navbar-admin',
  components: {
    BbDropdownItem: __vue_component__$2o,
    BbButton: __vue_component__$2v,
    BbDropdown: __vue_component__$1M
  },
  props: {
    /**
     * Array of link objects | `[{ label: String, to: String/route, target: String }, …]`
     */
    links: Array
  },
  methods: {
    linkHasItems(link) {
      return notEmpty(link.items);
    }
  }
});

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-navbar-admin"
  }, [_vm._l(_vm.links, function (link, linkIndex) {
    return [!link.hidden ? _c('bb-dropdown', {
      key: "dropdown-link-" + linkIndex,
      attrs: {
        "hoverable": ""
      },
      scopedSlots: _vm._u([{
        key: "trigger",
        fn: function () {
          return [_c('bb-button', {
            class: {
              'bb-navbar__dropdown-toggle--active': link.active
            },
            attrs: {
              "label": link.label,
              "to": link.to,
              "icon-after": _vm.linkHasItems(link) ? 'ui-menu-down' : null,
              "color": "blue",
              "icon-after-color": "white",
              "corner": "sharp"
            }
          })];
        },
        proxy: true
      }], null, true)
    }, [_vm._v(" "), _vm._l(link.items, function (item, itemIndex) {
      return [!item.hidden ? _c('bb-dropdown-item', {
        key: "dropdown-item-" + itemIndex,
        attrs: {
          "id": item.id,
          "label": item.label,
          "to": item.to
        }
      }) : _vm._e()];
    })], 2) : _vm._e()];
  }), _vm._v(" "), _vm._t("default")], 2);
};
var __vue_staticRenderFns__$9 = [];

/* style */
const __vue_inject_styles__$9 = function (inject) {
  if (!inject) return;
  inject("data-v-773522b3_0", {
    source: ".bb-navbar-admin{position:relative;z-index:1100;top:0;left:0;display:flex;width:100%;height:2.5rem;align-items:center;background:#5ea6ed;outline:0}.bb-navbar-admin .bb-dropdown{margin-left:.625rem}.bb-navbar-admin .bb-dropdown__toggle{min-width:0}.bb-navbar-admin .bb-dropdown__menu{padding-top:0}.bb-navbar-admin .bb-dropdown .bb-button{padding:0 1.25rem;background:#5ea6ed;opacity:.8}.bb-navbar-admin .bb-dropdown .bb-button--icon-after{padding-right:.625rem}.bb-navbar-admin .bb-dropdown .bb-button__label{font-size:.75rem}.bb-navbar-admin .bb-dropdown .bb-button__label::after{position:absolute;right:.9375rem;bottom:.5rem;left:1.25rem;height:.125rem;background:#5ea6ed;content:\"\"}.bb-navbar-admin .bb-dropdown .bb-button.bb-navbar__dropdown-toggle--active{opacity:1}.bb-navbar-admin .bb-dropdown .bb-button.bb-navbar__dropdown-toggle--active .bb-button__label::after{background:#fff}.bb-navbar-admin .bb-dropdown.is-shown .bb-button,.bb-navbar-admin .bb-dropdown:hover .bb-button{background-color:#fff;color:#5ea6ed;opacity:1}.bb-navbar-admin .bb-dropdown.is-shown .bb-button.bb-navbar__dropdown-toggle--active .bb-button__label::after,.bb-navbar-admin .bb-dropdown:hover .bb-button.bb-navbar__dropdown-toggle--active .bb-button__label::after{background:#5ea6ed}.bb-navbar-admin .bb-dropdown.is-shown .bb-button .bb-icon__dynamic-fill,.bb-navbar-admin .bb-dropdown:hover .bb-button .bb-icon__dynamic-fill{fill:#2485e5}.bb-navbar-admin .bb-dropdown-item{font-size:.75rem}.bb-navbar--admin .bb-navbar__nav>.bb-container{min-height:2.5rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$9 = undefined;
/* module identifier */
const __vue_module_identifier__$9 = undefined;
/* functional template */
const __vue_is_functional_template__$9 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$9 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$9,
  staticRenderFns: __vue_staticRenderFns__$9
}, __vue_inject_styles__$9, __vue_script__$9, __vue_scope_id__$9, __vue_is_functional_template__$9, __vue_module_identifier__$9, false, createInjector, undefined, undefined);

var Icon$5 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg631576995__a bb-icon__dynamic-fill",attrs:{"d":"M12,19.2C9.5,19.2 7.29,17.92 6,16C6.03,14 10,12.9 12,12.9C14,12.9 17.97,14 18,16C16.71,17.92 14.5,19.2 12,19.2M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2Z"}})]) } };

Vue.component('icon-ui-account-circle', Icon$5);

var Icon$4 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg4032940187__a bb-icon__dynamic-fill",attrs:{"d":"M12,17C10.89,17 10,16.1 10,15C10,13.89 10.89,13 12,13A2,2 0 0,1 14,15A2,2 0 0,1 12,17M18,20V10H6V20H18M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6C4.89,22 4,21.1 4,20V10C4,8.89 4.89,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z"}})]) } };

Vue.component('icon-ui-lock-outline', Icon$4);

var Icon$3 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3688047406__a bb-icon__dynamic-fill",attrs:{"d":"M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z"}})]) } };

Vue.component('icon-ui-menu', Icon$3);

var Icon$2 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3645601243__a bb-icon__dynamic-fill",attrs:{"d":"M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z"}})]) } };

Vue.component('icon-ui-printer', Icon$2);

var Icon$1 = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 24 24"}},[_c('path',{staticClass:"svg3439224417__a bb-icon__dynamic-fill",attrs:{"d":"M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"}})]) } };

Vue.component('icon-ui-phone', Icon$1);

const addTypeButton = language => ({
  ...language,
  type: 'button'
});
Vue.use(VueScreen, gridBreakpoints);
var script$8 = {
  name: 'bb-navbar',
  components: {
    BbNavbarAdmin: __vue_component__$9,
    BbSmartMenu: __vue_component__$b,
    BbMobileMenuLanding: __vue_component__$d,
    BbNavbarDropdown: __vue_component__$a,
    BbContactMenu: __vue_component__$e,
    BbAccordion: __vue_component__$1N,
    BbButton: __vue_component__$2v,
    BbContainer: __vue_component__$2q,
    BbIcon: __vue_component__$2C,
    BbLink: __vue_component__$2p,
    BbLogo: __vue_component__$28,
    BbProgressBar: __vue_component__$26,
    BbSeparator: __vue_component__$2a,
    BbDropdown: __vue_component__$1M,
    BbEditAmount: __vue_component__$1k,
    BbDrawer: __vue_component__$1l,
    BbTooltip: __vue_component__$2B
  },
  directives: {
    clickOutside
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  props: {
    primaryBarClasses: [Object, String],
    secondaryBarClasses: [Object, String],
    logo: {
      type: String
    },
    logoTo: String,
    links: Array,
    contactMenu: Object,
    initialMenuLabel: {
      type: String,
      validator: deprecated('`initialMenuLabel` will be removed from Navbar in IC 12.')
    },
    languages: Array,
    actions: Array,
    editAmount: Object,
    login: Object,
    user: Object,
    closeButton: {
      type: Boolean,
      default: false
    },
    addButton: {
      type: Boolean,
      default: false
    },
    sidebar: {
      type: Boolean,
      default: false
    },
    progressBar: {
      type: Object
    },
    sticky: {
      type: Boolean,
      default: false
    },
    inverted: Boolean,
    flavor: {
      type: String,
      default: 'admin',
      validator: f => ['admin', 'public', 'landing'].includes(f)
    },
    secondary: Boolean,
    overview: Object,
    theme: {
      type: String,
      default: 'private',
      validator: theme => ['private', 'corporate'].includes(theme)
    },
    ariaLabels: {
      type: Object,
      default: () => ({
        language: 'Language picker',
        toggleMenu: 'Toggle menu',
        close: 'Close',
        add: 'Add item',
        logo: 'Bigbank logo',
        navigationMenu: 'Navigation menu',
        contactMenu: 'Contact info'
      })
    }
  },
  emits: ['languageClick', 'editAmountClick', 'loginClick', 'logoutClick', 'usernameClick', 'avatarClick',
  'closeClick', 'addClick', 'linkClick'],
  data() {
    var _this$languages;
    return {
      activeLink: null,
      isScrolled: false,
      internalLanguages: ((_this$languages = this.languages) === null || _this$languages === void 0 ? void 0 : _this$languages.map(addTypeButton)) || [],
      inactiveLanguages: [],
      languagesVisible: false,
      languagesVisibleSidebar: false,
      mobileMenuVisible: false,
      sidebarVisible: false,
      showTooltipByIndex: null,
      fromRoute: null,
      hasOverflow: {
        top: false,
        bottom: false
      },
      intersectionObservers: [],
      defaultAriaLabel: 'Navigation bar',
      uuid: uuid()
    };
  },
  computed: {
    classes() {
      return ['bb-navbar', `bb-navbar--theme-${this.theme}`, {
        'bb-navbar--actions': this.actions,
        'bb-navbar--links': this.links,
        'bb-navbar--userinfo': this.user,
        'bb-navbar--sidebar': this.sidebar,
        'bb-navbar--inverted': this.inverted,
        'bb-navbar--overview': this.overview,
        'bb-navbar--secondary': this.secondary,
        'bb-navbar--mobile-menu-visible': this.mobileMenuVisible,
        'is-sticky': this.sticky && this.isScrolled,
        'has-progressbar': this.progressBar
      }];
    },
    internalClasses() {
      return ['bb-navbar__nav', `bb-navbar__nav--${this.flavor}`];
    },
    isCorporateTheme() {
      return this.theme === 'corporate';
    },
    activeLanguage() {
      return this.internalLanguages && this.internalLanguages.find(val => val.active) || null;
    },
    isFlavorLanding() {
      return this.flavor === 'landing';
    },
    textColor() {
      return this.inverted || this.isCorporateTheme ? 'white' : 'gray';
    },
    iconColor() {
      return this.inverted || this.isCorporateTheme ? 'white' : 'gray-80';
    },
    toggleButtonColor() {
      return this.inverted && !this.mobileMenuVisible || this.overview || this.isCorporateTheme ? 'white' : 'gray';
    },
    computedButtonSize() {
      return this.$screen.sm ? 'md' : 'sm';
    },
    isAuthenticated() {
      return notEmpty(this.user) && notEmpty(this.user.name);
    },
    hasLogin() {
      return notEmpty(this.login);
    },
    observables() {
      return [{
        element: this.$refs.scrollTopMarker,
        handler: this.onTopIntersection
      }, {
        element: this.$refs.scrollBottomMarker,
        handler: this.onBottomIntersection
      }];
    },
    mobileMenuId() {
      return `mobile-menu-${this.uuid}`;
    }
  },
  watch: {
    languages: {
      handler(e) {
        this.internalLanguages = e.map(addTypeButton);
        this.setFirstLanguageActive();
      },
      deep: true
    },
    internalLanguages: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal) this.inactiveLanguages = newVal.filter(val => !val.active);
      }
    }
  },
  beforeMount() {
    window.addEventListener('scroll', this.listenScroll);
    this.setFirstLanguageActive();
  },
  mounted() {
    if (this.sidebar) {
      this.initializeIntersectionObservers();
    }
  },
  destroyed() {
    window.removeEventListener('scroll', this.listenScroll);
    this.removeIntersectionObservers();
  },
  methods: {
    linkHasChildren(link) {
      return notEmpty(link.children);
    },
    linkHasSections(link) {
      return notEmpty(link.sections);
    },
    linkHasItems(link) {
      return notEmpty(link.items);
    },
    linkHasChildrenOrSections(link) {
      return this.linkHasChildren(link) || this.linkHasSections(link);
    },
    listenScroll: throttle(function () {
      this.isScrolled = this.$refs.navbar && this.$refs.navbar.getBoundingClientRect().top < 0;
    }, 100),
    setFirstLanguageActive() {
      if (this.internalLanguages.length && !this.internalLanguages.find(val => val.active)) {
        this.internalLanguages[0].active = true;
      }
    },
    navItemIsActive(navItem) {
      return navItem.active || isDefined(this.$nuxt) && this.$nuxt.$route.path === navItem.to || this.linkHasItems(navItem) && navItem.items.some(item => item.active) || this.linkHasSections(navItem) && navItem.sections.some(this.navItemIsActive) || this.linkHasChildren(navItem) && navItem.children.some(this.navItemIsActive);
    },
    getSmartMenuActiveIds(sections) {
      if (this.fromRoute) {
        for (let i = 0; i < sections.length; i++) {
          const product = sections[i].products.find(product.to === this.fromRoute.path);
          if (product) {
            return {
              tabId: sections[i].id,
              productId: product.id
            };
          }
        }
      }
      return {
        tabId: sections[0].id,
        productId: sections[0].products[0].id
      };
    },
    onDrawerToggle() {
      if (this.sidebar) {
        this.sidebarVisible = true;
      } else {
        this.mobileMenuVisible = !this.mobileMenuVisible;
      }
    },
    closeMobileMenu() {
      if (this.sidebar) {
        this.sidebarVisible = false;
      } else {
        this.mobileMenuVisible = false;
      }
    },
    onLanguageClick(language) {
      const clickedLanguageIndex = this.languages.findIndex(obj => obj.label === language.label);
      this.internalLanguages.forEach((_internalLanguage, internalLanguageIndex) => {
        if (internalLanguageIndex === clickedLanguageIndex) {
          this.$set(this.internalLanguages[internalLanguageIndex], 'active', true);
        } else {
          this.$set(this.internalLanguages[internalLanguageIndex], 'active', false);
        }
      });
      this.closeLanguageDropdown();
      this.closeLanguageDropdownSidebar();
      this.$emit('languageClick', language);
    },
    onEditAmountClick() {
      this.$emit('editAmountClick');
    },
    onLoginClick() {
      this.$emit('loginClick');
    },
    onLogoutClick() {
      this.$emit('logoutClick');
    },
    onUsernameClick() {
      this.$emit('usernameClick');
      this.onAvatarClick();
    },
    onAvatarClick() {
      this.$emit('avatarClick');
    },
    onCloseClick() {
      this.$emit('closeClick');
    },
    onAddClick() {
      this.$emit('addClick');
    },
    onActionButtonClick(eventName) {
      this.$emit(eventName);
    },
    onActionTooltipButtonClick(eventName) {
      this.showTooltipByIndex = null;
      this.$emit(eventName);
    },
    setActiveLink(link) {
      this.activeLink = link;
    },
    unsetActiveLink() {
      this.activeLink = null;
    },
    onLinkClick(link) {
      this.$emit('linkClick', link.id);
      this.toggleActiveLink(link);
    },
    toggleActiveLink(link) {
      this.activeLink === link ? this.unsetActiveLink() : this.setActiveLink(link);
    },
    closeLanguageDropdown() {
      this.languagesVisible = false;
    },
    toggleLanguageDropdown() {
      this.languagesVisible = !this.languagesVisible;
    },
    closeLanguageDropdownSidebar() {
      this.languagesVisibleSidebar = false;
    },
    toggleLanguageDropdownSidebar() {
      this.languagesVisibleSidebar = !this.languagesVisibleSidebar;
    },
    initializeIntersectionObservers() {
      this.removeIntersectionObservers();
      this.addIntersectionObservers();
    },
    addIntersectionObservers() {
      this.intersectionObservers = this.observables.map(this.addIntersectionObserver);
    },
    removeIntersectionObservers() {
      mutateValues(this.hasOverflow, false);
      this.intersectionObservers.forEach(stop => stop());
      this.intersectionObservers.length = 0;
    },
    addIntersectionObserver({
      element,
      handler
    }) {
      const options = {
        threshold: 1,
        root: this.$refs.mobileNavbarBody
      };
      const {
        stop
      } = useIntersectionObserver(element, handler, options);
      return stop;
    },
    onTopIntersection([{
      isIntersecting
    }]) {
      this.hasOverflow.top = !isIntersecting;
    },
    onBottomIntersection([{
      isIntersecting
    }]) {
      this.hasOverflow.bottom = !isIntersecting;
    },
    isEmpty,
    notEmpty
  }
};

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('nav', {
    ref: "navbar",
    class: _vm.classes,
    attrs: {
      "aria-label": _vm.defaultAriaLabel
    }
  }, [_vm.flavor === 'admin' ? _c('bb-navbar-admin', {
    attrs: {
      "links": _vm.links
    }
  }, [_vm._t("default")], 2) : [_c('div', {
    staticClass: "bb-navbar__primary-bar",
    class: [_vm.internalClasses, _vm.primaryBarClasses]
  }, [_c('bb-container', {
    staticClass: "bb-navbar__container"
  }, [!_vm.secondary ? _c('div', {
    staticClass: "bb-navbar__burger-logo-wrapper"
  }, [_vm.links ? _c('bb-button', {
    staticClass: "bb-navbar__burger-trigger",
    attrs: {
      "color": _vm.toggleButtonColor,
      "icon": _vm.mobileMenuVisible ? 'ui-close' : 'ui-menu',
      "display": "square",
      "no-background": "",
      "aria-expanded": _vm.mobileMenuVisible,
      "aria-controls": _vm.mobileMenuId,
      "aria-label": _vm.mobileMenuVisible ? _vm.ariaLabels.close : _vm.ariaLabels.toggleMenu
    },
    on: {
      "click": _vm.onDrawerToggle
    }
  }) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "bb-navbar__logo-slot"
  }, [_vm._t("default"), _vm._v(" "), !_vm.$slots.default && !_vm.overview ? _c('bb-logo', {
    staticClass: "bb-navbar__logo",
    attrs: {
      "logo": _vm.logo,
      "to": _vm.logoTo,
      "inverted": _vm.inverted && !_vm.mobileMenuVisible || _vm.isCorporateTheme,
      "aria-label": _vm.ariaLabels.logo,
      "mark-before-breakpoint": "sm"
    }
  }) : _vm._e()], 2)], 1) : _vm._e(), _vm._v(" "), _vm.links && !_vm.sidebar ? _c('ul', {
    directives: [{
      name: "click-outside",
      rawName: "v-click-outside:navbar",
      value: _vm.unsetActiveLink,
      expression: "unsetActiveLink",
      arg: "navbar"
    }],
    staticClass: "bb-navbar__links"
  }, _vm._l(_vm.links, function (link, index) {
    return _c('li', {
      key: "navbar-link-wrapper-" + index,
      staticClass: "bb-navbar__link-wrapper",
      class: {
        'is-expanded': _vm.activeLink === link
      }
    }, [_vm.secondary && index > 0 ? _c('bb-separator', {
      key: "separator-" + index,
      staticClass: "bb-navbar__link-separator",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e(), _vm._v(" "), _c('bb-link', {
      key: "navbar-link-" + index,
      class: ['bb-navbar__link', {
        'is-active': _vm.navItemIsActive(link)
      }],
      attrs: {
        "id": link.id,
        "to": _vm.linkHasChildrenOrSections(link) ? null : link.to,
        "target": link.target,
        "aria-label": link.label,
        "aria-current": _vm.navItemIsActive(link) ? 'page' : undefined,
        "aria-haspopup": _vm.linkHasChildrenOrSections(link) ? 'true' : undefined,
        "aria-expanded": _vm.activeLink === link,
        "role": _vm.linkHasChildrenOrSections(link) ? 'button' : undefined,
        "color": _vm.inverted ? 'white' : 'gray',
        "no-underline": "",
        "tabindex": "0"
      },
      on: {
        "click": function ($event) {
          return _vm.onLinkClick(link);
        }
      },
      nativeOn: {
        "keydown": [function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
            return null;
          }
          return _vm.onLinkClick(link);
        }, function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
            return null;
          }
          $event.preventDefault();
          return _vm.onLinkClick(link);
        }]
      }
    }, [_c('span', {
      staticClass: "bb-navbar__link-content",
      attrs: {
        "tabindex": "-1"
      }
    }, [_c('span', {
      staticClass: "bb-navbar__link-label",
      attrs: {
        "data-label": link.label
      }
    }, [_vm._v("\n                  " + _vm._s(link.label) + "\n                ")]), _vm._v(" "), _vm.isFlavorLanding && _vm.linkHasChildrenOrSections(link) ? _c('bb-icon', {
      staticClass: "bb-navbar__dropdown-arrow",
      attrs: {
        "fill": _vm.inverted ? 'white' : 'gray-80',
        "name": "ui-menu-down",
        "size": "18"
      }
    }) : _vm._e()], 1)]), _vm._v(" "), _vm.isFlavorLanding && !_vm.sidebar && _vm.linkHasSections(link) ? _c('bb-smart-menu', {
      key: "smart-menu-" + index,
      staticClass: "bb-navbar__smart-menu",
      attrs: {
        "value": link.sections,
        "active": _vm.getSmartMenuActiveIds(link.sections),
        "aria-label": link.label + " submenu"
      },
      on: {
        "redirect": _vm.unsetActiveLink
      }
    }) : _vm._e(), _vm._v(" "), _vm.isFlavorLanding && !_vm.sidebar && _vm.linkHasChildren(link) ? _c('bb-navbar-dropdown', {
      staticClass: "bb-navbar__dropdown-menu",
      attrs: {
        "links": link.children
      },
      on: {
        "redirect": _vm.unsetActiveLink
      }
    }) : _vm._e()], 1);
  }), 0) : _vm._e(), _vm._v(" "), _vm.overview ? _c('div', {
    staticClass: "bb-navbar__center"
  }, [_c('p', {
    staticClass: "bb-navbar__center-label"
  }, [_vm._v("\n            " + _vm._s(_vm.overview.label) + "\n          ")]), _vm._v(" "), _c('p', {
    staticClass: "bb-navbar__center-amount"
  }, [_vm._v("\n            " + _vm._s(_vm.overview.amount) + "\n          ")])]) : _vm._e(), _vm._v(" "), _vm.notEmpty(_vm.contactMenu) || _vm.languages || _vm.editAmount || _vm.login || _vm.closeButton || _vm.addButton || _vm.actions || _vm.$slots.primaryBarRightSectionAfter ? _c('div', {
    staticClass: "bb-navbar__right-side"
  }, [_vm.isFlavorLanding && !_vm.sidebar && _vm.notEmpty(_vm.contactMenu) ? _c('div', {
    staticClass: "bb-navbar__contact-menu-container"
  }, [_c('bb-link', {
    staticClass: "bb-navbar__contact-menu-trigger",
    attrs: {
      "color": _vm.inverted ? 'white' : 'gray',
      "no-underline": "",
      "tabindex": "0"
    }
  }, [_c('bb-icon', {
    attrs: {
      "fill": _vm.inverted ? 'white' : 'gray-80',
      "name": "ui-phone",
      "size": "24"
    }
  })], 1), _vm._v(" "), _c('bb-contact-menu', _vm._b({
    staticClass: "bb-navbar__contact-menu",
    attrs: {
      "role": "complementary",
      "aria-label": _vm.ariaLabels.contactMenu
    }
  }, 'bb-contact-menu', _vm.contactMenu, false))], 1) : _vm._e(), _vm._v(" "), _vm.isFlavorLanding && _vm.notEmpty(_vm.contactMenu) && _vm.isEmpty(_vm.languages) ? _c('bb-separator', {
    staticClass: "bb-navbar__separator bb-navbar__separator-contact-menu",
    attrs: {
      "direction": "vertical"
    }
  }) : _vm._e(), _vm._v(" "), _vm.editAmount ? _c('bb-edit-amount', {
    staticClass: "bb-navbar__edit-amount",
    attrs: {
      "title": _vm.editAmount.title,
      "amount": _vm.editAmount.amount
    },
    on: {
      "click": _vm.onEditAmountClick
    }
  }) : _vm._e(), _vm._v(" "), _vm.actions ? _c('div', {
    staticClass: "bb-navbar__actions"
  }, _vm._l(_vm.actions, function (action, index) {
    return _c('bb-button', {
      key: "action-button-" + index,
      staticClass: "bb-navbar__actions-button",
      attrs: {
        "icon": action.icon,
        "color": _vm.textColor,
        "display": "square",
        "icon-size": "24",
        "no-background": "",
        "aria-label": action.ariaLabel
      },
      on: {
        "click": function ($event) {
          action.event ? _vm.onActionButtonClick(action.event) : null;
        }
      },
      nativeOn: {
        "mouseover": function ($event) {
          _vm.showTooltipByIndex = index;
        },
        "mouseout": function ($event) {
          _vm.showTooltipByIndex = Array.isArray(action.tooltip) ? index : null;
        }
      }
    }, [_vm.showTooltipByIndex === index ? _c('bb-tooltip', {
      staticClass: "bb-navbar__actions-tooltip",
      attrs: {
        "bubble": ""
      }
    }, [Array.isArray(action.tooltip) ? _vm._l(action.tooltip, function (button, tooltipIndex) {
      return _c('bb-button', {
        key: "action-tooltip-" + tooltipIndex,
        staticClass: "bb-navbar__actions-tooltip-button",
        attrs: {
          "label": button.label,
          "color": button.color,
          "display": "block",
          "inverted": ""
        },
        on: {
          "click": function ($event) {
            button.event ? _vm.onActionTooltipButtonClick(button.event) : null;
          }
        }
      });
    }) : [_vm._v("\n                  " + _vm._s(action.tooltip) + "\n                ")]], 2) : _vm._e()], 1);
  }), 1) : _vm._e(), _vm._v(" "), _vm.languages && _vm.editAmount ? _c('bb-separator', {
    staticClass: "bb-navbar__separator",
    attrs: {
      "direction": "vertical"
    }
  }) : _vm._e(), _vm._v(" "), _vm.languages && !_vm.overview ? _c('ul', {
    directives: [{
      name: "click-outside",
      rawName: "v-click-outside:languageDropdown",
      value: _vm.closeLanguageDropdown,
      expression: "closeLanguageDropdown",
      arg: "languageDropdown"
    }],
    staticClass: "bb-navbar__languages",
    attrs: {
      "aria-label": _vm.ariaLabels.language
    }
  }, [_c('li', {
    staticClass: "bb-navbar__link-wrapper",
    class: {
      'is-expanded': _vm.languagesVisible
    }
  }, [_c('bb-link', {
    class: ['bb-navbar__link', {
      'is-active': _vm.languagesVisible
    }],
    attrs: {
      "aria-label": _vm.activeLanguage && _vm.activeLanguage.label,
      "aria-haspopup": true,
      "aria-expanded": _vm.languagesVisible,
      "role": "button",
      "color": _vm.textColor,
      "no-underline": "",
      "tabindex": "0"
    },
    on: {
      "click": _vm.toggleLanguageDropdown
    },
    nativeOn: {
      "keydown": [function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
          return null;
        }
        return _vm.toggleLanguageDropdown.apply(null, arguments);
      }, function ($event) {
        if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
          return null;
        }
        $event.preventDefault();
        return _vm.toggleLanguageDropdown.apply(null, arguments);
      }]
    }
  }, [_c('span', {
    staticClass: "bb-navbar__link-content",
    attrs: {
      "tabindex": "-1"
    }
  }, [_c('span', {
    staticClass: "bb-navbar__link-label",
    attrs: {
      "data-label": _vm.activeLanguage && _vm.activeLanguage.label
    }
  }, [_vm._v("\n                    " + _vm._s(_vm.activeLanguage && _vm.activeLanguage.label) + "\n                  ")]), _vm._v(" "), _c('bb-icon', {
    staticClass: "bb-navbar__dropdown-arrow",
    attrs: {
      "fill": _vm.iconColor,
      "name": "ui-menu-down",
      "size": "18"
    }
  })], 1)]), _vm._v(" "), _c('bb-navbar-dropdown', {
    ref: "languageDropdown",
    staticClass: "bb-navbar__dropdown-menu",
    attrs: {
      "links": _vm.inactiveLanguages
    },
    on: {
      "redirect": _vm.onLanguageClick
    }
  })], 1)]) : _vm._e(), _vm._v(" "), _vm._t("primaryBarRightSectionAfter", function () {
    return [(_vm.languages || _vm.editAmount) && _vm.login ? _c('bb-separator', {
      staticClass: "bb-navbar__separator",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e(), _vm._v(" "), _vm.hasLogin && !_vm.isAuthenticated ? _c('bb-button', {
      staticClass: "bb-navbar__login-button",
      attrs: {
        "label": _vm.login.label,
        "to": _vm.login.to,
        "color": _vm.inverted ? 'white' : 'gray',
        "icon": "ui-lock-outline",
        "inverted": "",
        "size": _vm.computedButtonSize
      },
      on: {
        "click": _vm.onLoginClick
      }
    }) : _vm.isAuthenticated && _vm.isFlavorLanding ? _c('bb-dropdown', {
      attrs: {
        "autosize": "",
        "right": "",
        "content-classes": "bb-navbar__profile-content"
      },
      scopedSlots: _vm._u([{
        key: "trigger",
        fn: function () {
          return [_c('bb-button', {
            attrs: {
              "label": _vm.user.ctaLabel,
              "size": _vm.computedButtonSize,
              "color": "mint",
              "inverted": "",
              "icon": "ui-account"
            }
          })];
        },
        proxy: true
      }], null, false, 1751378978)
    }, [_vm._v(" "), _vm._t("profile")], 2) : _vm._e(), _vm._v(" "), (_vm.languages || _vm.editAmount || _vm.login || _vm.actions) && _vm.closeButton ? _c('bb-separator', {
      staticClass: "bb-navbar__separator bb-navbar__separator-close-button",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e(), _vm._v(" "), _vm.closeButton ? _c('bb-button', {
      staticClass: "bb-navbar__close-button",
      attrs: {
        "color": _vm.textColor,
        "icon": "ui-close",
        "display": "square",
        "no-background": "",
        "aria-label": _vm.ariaLabels.close
      },
      on: {
        "click": _vm.onCloseClick
      }
    }) : _vm._e(), _vm._v(" "), (_vm.languages || _vm.editAmount || _vm.login || _vm.actions || _vm.closeButton) && _vm.addButton && !_vm.overview ? _c('bb-separator', {
      staticClass: "bb-navbar__separator bb-navbar__separator-add-button",
      attrs: {
        "direction": "vertical"
      }
    }) : _vm._e(), _vm._v(" "), _vm.addButton ? _c('bb-button', {
      staticClass: "bb-navbar__add-button",
      attrs: {
        "color": _vm.overview ? 'white' : 'mint',
        "icon": "ui-plus",
        "icon-size": "24",
        "display": "circle",
        "size": "sm",
        "aria-label": _vm.ariaLabels.add
      },
      on: {
        "click": _vm.onAddClick
      }
    }) : _vm._e()];
  })], 2) : _vm._e()]), _vm._v(" "), _c('transition', {
    attrs: {
      "name": "fade"
    }
  }, [_vm.progressBar ? _c('bb-progress-bar', _vm._b({
    staticClass: "bb-navbar__progress-bar",
    attrs: {
      "corner": "sharp"
    }
  }, 'bb-progress-bar', _vm.progressBar, false)) : _vm._e()], 1)], 1), _vm._v(" "), _vm.$slots.secondaryBar ? _c('div', {
    class: ['bb-navbar__secondary-bar', _vm.internalClasses, _vm.secondaryBarClasses]
  }, [_c('bb-container', {
    staticClass: "bb-navbar__container"
  }, [_vm._t("secondaryBar")], 2)], 1) : _vm._e(), _vm._v(" "), _vm._t("after", function () {
    return [_vm.links && !_vm.sidebar && _vm.isFlavorLanding ? _c('bb-mobile-menu-landing', {
      attrs: {
        "id": _vm.mobileMenuId,
        "visible": _vm.mobileMenuVisible,
        "links": _vm.links,
        "contact-menu": _vm.contactMenu,
        "drawer-label": _vm.ariaLabels.navigationMenu
      },
      on: {
        "close": _vm.closeMobileMenu
      }
    }) : _vm._e(), _vm._v(" "), _vm.links || _vm.sidebar && !_vm.isFlavorLanding ? _c('div', {
      staticClass: "bb-navbar__mobile",
      class: _vm.sidebar && _vm.internalClasses,
      attrs: {
        "tabindex": "-1"
      },
      on: {
        "keyup": function ($event) {
          if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "esc", 27, $event.key, ["Esc", "Escape"])) {
            return null;
          }
          return _vm.closeMobileMenu.apply(null, arguments);
        }
      }
    }, [_c('bb-drawer', {
      staticClass: "bb-navbar__drawer",
      attrs: {
        "visible": _vm.sidebarVisible,
        "static": _vm.sidebar,
        "left": "",
        "navigation": ""
      },
      on: {
        "close": _vm.closeMobileMenu
      },
      scopedSlots: _vm._u([{
        key: "header",
        fn: function () {
          return [_c('header', {
            class: ['bb-navbar__mobile-header', {
              'has-shadow': _vm.hasOverflow.top
            }]
          }, [_c('bb-button', {
            staticClass: "bb-navbar__mobile-close-button",
            attrs: {
              "no-background": "",
              "color": _vm.isCorporateTheme ? 'white' : 'gray',
              "icon": "ui-close",
              "display": "square",
              "aria-label": _vm.ariaLabels.close
            },
            on: {
              "click": _vm.closeMobileMenu
            }
          }), _vm._v(" "), _vm.sidebar ? _c('div', {
            staticClass: "bb-navbar__mobile-logo-slot"
          }, [_vm._t("default"), _vm._v(" "), !_vm.$slots.default ? _c('bb-logo', {
            staticClass: "bb-navbar__logo",
            attrs: {
              "logo": _vm.logo,
              "to": _vm.logoTo,
              "inverted": _vm.isCorporateTheme
            }
          }) : _vm._e()], 2) : _vm._e(), _vm._v(" "), _vm.languages ? _c('ul', {
            directives: [{
              name: "click-outside",
              rawName: "v-click-outside:languageDropdownSidebar",
              value: _vm.closeLanguageDropdownSidebar,
              expression: "closeLanguageDropdownSidebar",
              arg: "languageDropdownSidebar"
            }],
            staticClass: "bb-navbar__languages-sidebar",
            attrs: {
              "tabindex": "-1"
            }
          }, [_c('li', {
            staticClass: "bb-navbar__link-wrapper",
            class: {
              'is-expanded': _vm.languagesVisibleSidebar
            }
          }, [_c('bb-link', {
            class: ['bb-navbar__link', {
              'is-active': _vm.languagesVisibleSidebar
            }],
            attrs: {
              "aria-label": _vm.activeLanguage && _vm.activeLanguage.label,
              "role": "button",
              "color": _vm.textColor,
              "no-underline": "",
              "tabindex": "0"
            },
            on: {
              "click": _vm.toggleLanguageDropdownSidebar
            },
            nativeOn: {
              "keydown": [function ($event) {
                if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "enter", 13, $event.key, "Enter")) {
                  return null;
                }
                return _vm.toggleLanguageDropdownSidebar.apply(null, arguments);
              }, function ($event) {
                if (!$event.type.indexOf('key') && _vm._k($event.keyCode, "space", 32, $event.key, [" ", "Spacebar"])) {
                  return null;
                }
                $event.preventDefault();
                return _vm.toggleLanguageDropdownSidebar.apply(null, arguments);
              }]
            }
          }, [_c('span', {
            staticClass: "bb-navbar__link-content",
            attrs: {
              "tabindex": "-1"
            }
          }, [_c('span', {
            staticClass: "bb-navbar__link-label",
            attrs: {
              "data-label": _vm.activeLanguage && _vm.activeLanguage.label
            }
          }, [_vm._v("\n                        " + _vm._s(_vm.activeLanguage && _vm.activeLanguage.label) + "\n                      ")]), _vm._v(" "), _c('bb-icon', {
            staticClass: "bb-navbar__dropdown-arrow",
            attrs: {
              "fill": _vm.iconColor,
              "name": "ui-menu-down",
              "size": "18"
            }
          })], 1)]), _vm._v(" "), _c('bb-navbar-dropdown', {
            ref: "languageDropdownSidebar",
            staticClass: "bb-navbar__dropdown-menu",
            attrs: {
              "links": _vm.inactiveLanguages
            },
            on: {
              "redirect": _vm.onLanguageClick
            }
          })], 1)]) : _vm._e()], 1)];
        },
        proxy: true
      }], null, true)
    }, [_vm._v(" "), _c('div', {
      ref: "mobileNavbarBody",
      staticClass: "bb-navbar__mobile-body"
    }, [_c('div', {
      ref: "scrollTopMarker"
    }), _vm._v(" "), _vm.$slots.accountSwitcher ? _c('div', {
      staticClass: "bb-navbar__mobile-account-switcher"
    }, [_vm._t("accountSwitcher")], 2) : _vm._e(), _vm._v(" "), _c('div', {
      staticClass: "bb-navbar__mobile-scroll"
    }, [_vm._l(_vm.links, function (link, linkIndex) {
      return [!_vm.linkHasSections(link) ? _c('bb-link', {
        key: "mobile-nav-link-" + linkIndex,
        class: ['bb-navbar__link bb-navbar__link--mobile bb-navbar__link--mobile-top-level', {
          'is-active': _vm.navItemIsActive(link)
        }],
        attrs: {
          "id": link.id,
          "to": link.to,
          "target": link.target,
          "color": _vm.textColor,
          "no-underline": "",
          "tabindex": "0"
        },
        on: {
          "click": _vm.closeMobileMenu
        }
      }, [_c('span', {
        staticClass: "bb-navbar__link-content",
        attrs: {
          "tabindex": "-1"
        }
      }, [_vm._v("\n                    " + _vm._s(link.label) + "\n                    "), _vm._v(" "), _vm._t("itemAfter", function () {
        return [link.iconAfter ? _c('bb-icon', _vm._b({
          attrs: {
            "data-testid": "bb-navbar__link-icon-after"
          }
        }, 'bb-icon', Object.assign({}, {
          size: 24
        }, link.iconAfter), false)) : _vm._e()];
      }, null, link)], 2)]) : _vm._e(), _vm._v(" "), _vm.linkHasSections(link) ? _c('bb-accordion', {
        key: "mobile-nav-accordion-" + linkIndex,
        class: ['bb-navbar__accordion', {
          'is-active': _vm.navItemIsActive(link)
        }],
        attrs: {
          "id": link.id,
          "value": [{
            id: 'accordion',
            title: link.label,
            open: _vm.linkHasSections(link) && link.sections.some(_vm.navItemIsActive)
          }]
        },
        scopedSlots: _vm._u([{
          key: "accordion-after",
          fn: function () {
            return [_vm._t("itemAfter", function () {
              return [link.iconAfter ? _c('bb-icon', _vm._b({
                staticClass: "bb-navbar__accordion-icon-after",
                attrs: {
                  "data-testid": "bb-navbar__accordion-icon-after"
                }
              }, 'bb-icon', Object.assign({}, {
                size: 24
              }, link.iconAfter), false)) : _vm._e()];
            }, null, link)];
          },
          proxy: true
        }, {
          key: "accordion",
          fn: function () {
            return [_c('bb-separator', {
              key: "mobile-nav-separator-top-" + linkIndex,
              staticClass: "bb-navbar__separator--mobile"
            }), _vm._v(" "), _vm._l(link.sections, function (section, sectionIndex) {
              return [_c('bb-link', {
                key: "mobile-nav-link-" + linkIndex + "-" + sectionIndex,
                class: ['bb-navbar__link bb-navbar__link--mobile bb-navbar__link--mobile-sub-level', {
                  'is-active': _vm.navItemIsActive(section)
                }],
                attrs: {
                  "id": section.id,
                  "to": section.to,
                  "target": section.target,
                  "color": _vm.textColor,
                  "no-underline": "",
                  "tabindex": "0"
                },
                on: {
                  "click": _vm.closeMobileMenu
                }
              }, [_c('span', {
                staticClass: "bb-navbar__link-content",
                attrs: {
                  "tabindex": "-1"
                }
              }, [_vm._v("\n                          " + _vm._s(section.label) + "\n                          "), _vm._v(" "), _vm._t("itemAfter", function () {
                return [section.iconAfter ? _c('bb-icon', _vm._b({
                  attrs: {
                    "data-testid": "bb-navbar__link-icon-after"
                  }
                }, 'bb-icon', Object.assign({}, {
                  size: 24
                }, section.iconAfter), false)) : _vm._e()];
              }, null, section)], 2)]), _vm._v(" "), _c('bb-separator', {
                key: "mobile-nav-separator-bottom-" + sectionIndex,
                staticClass: "bb-navbar__separator--mobile"
              })];
            })];
          },
          proxy: true
        }], null, true)
      }) : _vm._e()];
    })], 2), _vm._v(" "), _vm._t("notification"), _vm._v(" "), _c('div', {
      ref: "scrollBottomMarker"
    })], 2), _vm._v(" "), _vm.user ? _c('div', {
      class: ['bb-navbar__mobile-user', {
        'has-shadow': _vm.hasOverflow.bottom
      }]
    }, [_c('div', {
      staticClass: "bb-navbar__mobile-user-name-role-wrapper"
    }, [_c('bb-button', {
      staticClass: "bb-navbar__mobile-user-name",
      attrs: {
        "label": _vm.user.name,
        "to": _vm.user.to,
        "display": "link",
        "color": _vm.isCorporateTheme ? 'white' : 'black',
        "no-underline": "",
        "no-paddings": "",
        "data-hj-suppress": "true"
      },
      on: {
        "click": _vm.onUsernameClick
      }
    }), _vm._v(" "), _vm.user.role ? _c('div', {
      staticClass: "bb-navbar__mobile-user-role",
      attrs: {
        "data-hj-suppress": "true"
      }
    }, [_vm._v("\n                " + _vm._s(_vm.user.role) + "\n              ")]) : _vm._e()], 1), _vm._v(" "), _c('bb-button', _vm._b({
      staticClass: "bb-navbar__mobile-logout",
      attrs: {
        "inverted": _vm.isCorporateTheme ? 'filled' : false,
        "color": "black",
        "size": "sm",
        "display": "block"
      },
      on: {
        "click": _vm.onLogoutClick
      }
    }, 'bb-button', _vm.user.link, false))], 1) : _vm._e()])], 1) : _vm._e()];
  }, null, {
    mobileMenuVisible: _vm.mobileMenuVisible,
    closeMobileMenu: _vm.closeMobileMenu
  })]], 2);
};
var __vue_staticRenderFns__$8 = [];

/* style */
const __vue_inject_styles__$8 = function (inject) {
  if (!inject) return;
  inject("data-v-f2d51bc0_0", {
    source: ".bb-navbar{position:relative}.bb-navbar:not(.bb-navbar--links){height:3.125rem}@media (min-width:992px){.bb-navbar:not(.bb-navbar--links){height:4rem}}@media (min-width:1200px){.bb-navbar--sidebar{width:18.125rem;height:100%}}.bb-navbar--sidebar.bb-navbar--links .bb-navbar__burger-logo-wrapper{left:-.75rem}@media (min-width:1200px){.bb-navbar--sidebar.bb-navbar--links .bb-navbar__burger-logo-wrapper{left:0}}@media (min-width:1200px){.bb-navbar--sidebar .bb-navbar__mobile-close-button{display:none}}.bb-navbar--sidebar .bb-navbar__links{display:none}@media (min-width:1200px){.bb-navbar--sidebar .bb-navbar__links{display:flex}}@media (min-width:1200px){.bb-navbar--sidebar .bb-navbar__link-content::after{left:-1.8125rem;width:.25rem;height:3.25rem}}.bb-navbar--sidebar .bb-accordion-item__header-after{margin-right:auto;margin-left:.75rem}@media (min-width:1200px){.bb-navbar--sidebar .bb-navbar__nav:not(.bb-navbar__mobile){position:absolute;pointer-events:none;visibility:hidden}}.bb-navbar--sidebar.bb-navbar--theme-corporate .bb-drawer__dialog{background:#00285a}.bb-navbar .bb-navbar__burger-logo-wrapper{position:relative;display:flex;align-items:center}.bb-navbar--links.bb-navbar .bb-navbar__burger-logo-wrapper{left:-.75rem}@media (min-width:992px){.bb-navbar--links.bb-navbar .bb-navbar__burger-logo-wrapper{left:0}}.bb-navbar .bb-navbar__burger-trigger{display:block}@media (min-width:992px){.bb-navbar .bb-navbar__burger-trigger{display:none}}.bb-navbar--sidebar.bb-navbar .bb-navbar__burger-trigger{display:block}@media (min-width:1200px){.bb-navbar--sidebar.bb-navbar .bb-navbar__burger-trigger{display:none}}.bb-navbar .bb-navbar__logo-slot,.bb-navbar .bb-navbar__mobile-logo-slot{display:flex;align-items:center;justify-content:center}@media (min-width:1200px){.bb-navbar .bb-navbar__mobile-logo-slot,.bb-navbar--sidebar.bb-navbar .bb-navbar__logo-slot{visibility:visible}}.bb-navbar .bb-navbar__logo-slot img,.bb-navbar .bb-navbar__mobile-logo-slot img{max-width:unset}.bb-navbar .bb-navbar__logo-slot{margin-left:.375rem}.bb-navbar .bb-navbar__logo{height:1.875rem}@media (min-width:992px){.bb-navbar .bb-navbar__logo{height:2.1875rem}}.bb-navbar .bb-navbar__drawer{height:100%}.bb-navbar .bb-navbar__container{max-width:100%}@media (min-width:1200px){.bb-navbar .bb-navbar__container{max-width:1140px}}.bb-navbar .bb-navbar__edit-amount{white-space:nowrap}.bb-navbar .bb-navbar__login-button{padding-right:.9375rem;padding-left:.625rem;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;white-space:nowrap}@media (min-width:576px){.bb-navbar .bb-navbar__login-button{padding-right:1.25rem;padding-left:.9375rem}}.bb-navbar .bb-navbar__mobile{outline:0}.bb-navbar--sidebar.bb-navbar .bb-navbar__mobile{height:0%;min-height:unset}@media (min-width:1200px){.bb-navbar--sidebar.bb-navbar .bb-navbar__mobile{display:flex;height:100%}}.bb-navbar .bb-navbar__mobile:focus{outline:0}.bb-navbar .bb-navbar__mobile-header{display:flex;height:3.125rem;align-items:center;justify-content:flex-start;padding:0 .1875rem;transition:box-shadow .2s}@media (min-width:992px){.bb-navbar .bb-navbar__mobile-header{height:4rem}}.bb-navbar .bb-navbar__mobile-header.has-shadow{box-shadow:0 1.25rem 1.25rem -1rem rgba(0,0,0,.05)}.bb-navbar .bb-navbar__mobile-account-switcher{padding:1.75rem}.bb-navbar .bb-navbar__mobile-logo-slot{margin-left:.375rem}@media (min-width:1200px){.bb-navbar .bb-navbar__mobile-logo-slot{margin-left:1.75rem}}.bb-navbar .bb-navbar__mobile .bb-navbar__languages-sidebar{margin-left:auto}.bb-navbar .bb-navbar__mobile-body{overflow:auto;flex:1 1 0%}.bb-navbar--sidebar.bb-navbar .bb-navbar__mobile-body{display:flex;flex-direction:column}@media (min-width:1200px){.bb-navbar--sidebar.bb-navbar .bb-navbar__mobile-body{justify-content:space-between}}.bb-navbar .bb-navbar__mobile-scroll{padding:3.125rem 1.75rem;margin:auto 0}@media (min-width:1200px){.bb-navbar .bb-navbar__mobile-scroll{flex:unset;padding:1.25rem 1.75rem}}.bb-navbar .bb-navbar__mobile-user{display:flex;flex-direction:column;align-items:center;padding:2.125rem;background:#fff;box-shadow:none;transition:box-shadow .2s}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__mobile-user{background:#00285a}.bb-navbar .bb-navbar__mobile-user.has-shadow{box-shadow:0 -20px 20px -16px rgba(0,0,0,.05)}.bb-navbar .bb-navbar__mobile-user-name-role-wrapper{width:100%;margin-bottom:1.25rem}.bb-navbar .bb-navbar__mobile-user-name,.bb-navbar .bb-navbar__mobile-user-role{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem}.bb-navbar .bb-navbar__mobile-user-name{margin-bottom:.25rem}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__mobile-user-name:hover .bb-button__label{color:#0ccca2}.bb-navbar .bb-navbar__mobile-user-role{color:#89a}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__mobile-user-role{color:#bdc7d0}.bb-navbar .bb-navbar__mobile-enter-active,.bb-navbar .bb-navbar__mobile-leave-active{transition:transform .4s}.bb-navbar .bb-navbar__mobile-enter,.bb-navbar .bb-navbar__mobile-leave-to{transform:translateX(-100%)}.bb-navbar .bb-navbar__accordion{position:relative}.bb-navbar .bb-navbar__accordion.is-active.is-active.is-active::before{position:absolute;top:0;left:-1.75rem;width:.25rem;height:3.25rem;background:#0ccca2;content:\"\"}.bb-navbar .bb-navbar__accordion.is-active.is-active.is-active .bb-accordion-item__header-title{color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__accordion.is-active.is-active.is-active .bb-accordion-item__chevron path{fill:#0ccca2}.bb-navbar .bb-navbar__accordion .bb-accordion-item__header-title{color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.875rem;text-transform:uppercase}.bb-navbar--inverted.bb-navbar .bb-navbar__accordion .bb-accordion-item__header-title{color:#fff}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__accordion .bb-accordion-item__header-title{color:#fff}.bb-navbar .bb-navbar__accordion .bb-accordion-item__chevron path{fill:#89a;transition:fill .2s}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__accordion .bb-accordion-item__chevron path{fill:#fff}.bb-navbar .bb-navbar__accordion .bb-accordion-item__header:hover .bb-accordion-item__header-title{color:#0ccca2}.bb-navbar .bb-navbar__accordion .bb-accordion-item__header:hover .bb-accordion-item__chevron path{fill:#0ccca2}.bb-navbar .bb-navbar__accordion .bb-accordion-item.is-open .bb-accordion-item__header-title{color:#0ccca2}.bb-navbar .bb-navbar__accordion .bb-accordion-item.is-open .bb-accordion-item__chevron path{fill:#0ccca2}.bb-navbar .bb-navbar__separator{height:2.5rem;align-self:auto;border-color:#dee3e8;margin:0 .625rem;transition:border-color .2s}@media (min-width:576px){.bb-navbar .bb-navbar__separator{margin:0 1.5625rem}}.bb-navbar--inverted.bb-navbar .bb-navbar__separator{border-color:#fff}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__separator{border-color:#9dabb9}.bb-navbar .bb-navbar__separator-add-button,.bb-navbar .bb-navbar__separator-close-button{margin:0 .625rem}.bb-navbar .bb-navbar__separator-contact-menu{display:none;margin-right:1.5625rem;margin-left:.625rem}@media (min-width:992px){.bb-navbar .bb-navbar__separator-contact-menu{display:block}}.bb-navbar .bb-navbar__separator--mobile{border-color:#eeeff2;margin:0}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__separator--mobile{border-color:#9dabb9}.bb-navbar .bb-navbar__links{display:none}@media (min-width:992px){.bb-navbar .bb-navbar__links{display:flex}}.bb-navbar .bb-navbar__languages,.bb-navbar .bb-navbar__languages-sidebar,.bb-navbar .bb-navbar__links{align-items:center;justify-content:center}.bb-navbar .bb-navbar__languages .bb-navbar__link-label,.bb-navbar .bb-navbar__languages-sidebar .bb-navbar__link-label,.bb-navbar .bb-navbar__links .bb-navbar__link-label{display:flex;flex-direction:column;justify-content:center}.bb-navbar .bb-navbar__languages .bb-navbar__link-label::before,.bb-navbar .bb-navbar__languages-sidebar .bb-navbar__link-label::before,.bb-navbar .bb-navbar__links .bb-navbar__link-label::before{display:block;overflow:hidden;height:0;color:transparent;content:attr(data-label);font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;visibility:hidden}@media speech{.bb-navbar .bb-navbar__languages .bb-navbar__link-label::before,.bb-navbar .bb-navbar__languages-sidebar .bb-navbar__link-label::before,.bb-navbar .bb-navbar__links .bb-navbar__link-label::before{display:none}}.bb-navbar .bb-navbar__languages-sidebar:hover .bb-navbar__link-content::after,.bb-navbar .bb-navbar__languages:hover .bb-navbar__link-content::after,.bb-navbar .bb-navbar__links:hover .bb-navbar__link-content::after{background:0 0}.bb-navbar .bb-navbar__languages .bb-navbar__link-wrapper,.bb-navbar .bb-navbar__languages-sidebar .bb-navbar__link-wrapper{justify-content:end}.bb-navbar .bb-navbar__right-side{display:flex;align-items:center;justify-content:center}.bb-navbar .bb-navbar__center{color:#fff;text-align:center;text-transform:uppercase}.bb-navbar .bb-navbar__center-label{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.5625rem}.bb-navbar .bb-navbar__center-amount{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__contact-menu-container{position:relative;display:none;height:4.25rem;height:3.125rem;align-items:center;justify-content:center}@media (min-width:992px){.bb-navbar .bb-navbar__contact-menu-container{display:flex;height:4rem}}.bb-navbar .bb-navbar__contact-menu-container:focus-within .bb-navbar__contact-menu-trigger path,.bb-navbar .bb-navbar__contact-menu-container:hover .bb-navbar__contact-menu-trigger path{fill:#0ccca2}.bb-navbar .bb-navbar__contact-menu-container:focus-within .bb-navbar__contact-menu,.bb-navbar .bb-navbar__contact-menu-container:hover .bb-navbar__contact-menu{visibility:visible}.bb-navbar .bb-navbar__contact-menu-trigger{display:flex;height:100%;align-items:center;justify-content:center;padding:.75rem;cursor:pointer}.bb-navbar .bb-navbar__contact-menu-trigger path{transition:fill .2s}.bb-navbar .bb-navbar__contact-menu-trigger:focus path,.bb-navbar .bb-navbar__contact-menu-trigger:hover path{fill:#0ccca2}.bb-navbar .bb-navbar__contact-menu-trigger:focus+.bb-navbar__contact-menu,.bb-navbar .bb-navbar__contact-menu-trigger:hover+.bb-navbar__contact-menu{visibility:visible}.bb-navbar .bb-navbar__actions{display:flex;align-items:center}.bb-navbar .bb-navbar__actions-button:not(:last-of-type){margin-right:.625rem}.bb-navbar .bb-navbar__actions-tooltip{display:flex;flex-direction:column;justify-content:center}.bb-navbar .bb-navbar__actions-tooltip-button{padding:0 1.875rem}.bb-navbar .bb-navbar__actions-tooltip-button:not(:last-of-type){margin-bottom:.625rem}.bb-navbar .bb-navbar__link-wrapper{display:flex;justify-content:center}.bb-navbar .bb-navbar__link-wrapper.is-expanded .bb-navbar__dropdown-arrow{transform:rotate(180deg)}.bb-navbar .bb-navbar__link-wrapper.is-expanded .bb-navbar__link{color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__link-wrapper.is-expanded .bb-navbar__dropdown-menu,.bb-navbar .bb-navbar__link-wrapper.is-expanded .bb-navbar__smart-menu{visibility:visible}.bb-navbar .bb-navbar__dropdown-arrow{margin-left:.3125rem;transition:transform .1s}.bb-navbar .bb-navbar__link{position:relative;cursor:pointer;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:.75rem;line-height:.75rem;text-align:center;transition:color .2s}.bb-navbar--secondary.bb-navbar .bb-navbar__link{color:#89a;font-size:.875rem;font-weight:500;line-height:1.2}.bb-navbar--theme-corporate.bb-navbar .bb-navbar__link:hover{color:#0ccca2}.bb-navbar .bb-navbar__link-separator{border-color:#dee3e8}.bb-navbar .bb-navbar__link-content{position:relative;display:flex;min-height:3.125rem;align-items:center;padding:0 .9375rem;font-size:.875rem}@media (min-width:992px){.bb-navbar .bb-navbar__link-content{height:4rem}}.bb-navbar--secondary.bb-navbar .bb-navbar__link-content{height:auto;min-height:auto}.bb-navbar .bb-navbar__link.is-active.is-active.is-active,.bb-navbar .bb-navbar__link:focus,.bb-navbar .bb-navbar__link:hover{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__link.is-active.is-active.is-active,.bb-navbar .bb-navbar__link.is-active.is-active.is-active:focus,.bb-navbar .bb-navbar__link.is-active.is-active.is-active:hover{color:#0ccca2}.bb-navbar .bb-navbar__link.is-active.is-active.is-active .bb-navbar__dropdown-arrow path,.bb-navbar .bb-navbar__link.is-active.is-active.is-active:focus .bb-navbar__dropdown-arrow path,.bb-navbar .bb-navbar__link.is-active.is-active.is-active:hover .bb-navbar__dropdown-arrow path{fill:#0ccca2}.bb-navbar .bb-navbar__link-content:focus,.bb-navbar .bb-navbar__link:active,.bb-navbar .bb-navbar__link:focus{outline:0}.bb-navbar .bb-navbar__link--mobile{display:block;padding:0;font-size:.875rem;line-height:unset;text-align:left}.bb-navbar .bb-navbar__link--mobile .bb-navbar__link-content{height:unset}.bb-navbar .bb-navbar__link--mobile-top-level{color:#89a;text-transform:uppercase}.bb-navbar .bb-navbar__link--mobile-top-level.is-active.is-active.is-active{color:#0ccca2;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__link--mobile-top-level.is-active.is-active.is-active:hover{color:#0ccca2}.bb-navbar .bb-navbar__link--mobile-top-level .bb-navbar__link-content{display:flex;padding:.9375rem .5rem 1rem;margin:.625rem 0;gap:.75rem}.bb-navbar .bb-navbar__link--mobile-top-level .bb-navbar__link-content::after{top:0;left:-1.8125rem;width:.25rem;height:3.25rem}.bb-navbar .bb-navbar__link--mobile-sub-level{color:#89a;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__link--mobile-sub-level.is-active.is-active.is-active{color:#0ccca2;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-navbar .bb-navbar__link--mobile-sub-level .bb-navbar__link-content{display:flex;padding:.9375rem 0 1rem 1.5625rem;gap:.75rem}.bb-navbar .bb-navbar__link--mobile-sub-level .bb-navbar__link-content::after{display:none}.bb-navbar .bb-navbar__contact-menu,.bb-navbar .bb-navbar__dropdown-menu,.bb-navbar .bb-navbar__smart-menu{position:absolute;top:3.125rem;visibility:hidden}@media (min-width:992px){.bb-navbar .bb-navbar__contact-menu,.bb-navbar .bb-navbar__dropdown-menu,.bb-navbar .bb-navbar__smart-menu{top:4rem}}.bb-navbar .bb-navbar__smart-menu{left:50%;transform:translateX(-50%)}.bb-navbar__nav{position:relative;z-index:1500;top:0;left:0;display:flex;width:100%;height:3.125rem;align-items:center;outline:0}@media (min-width:992px){.bb-navbar__nav{height:4rem}}.bb-navbar__nav--landing,.bb-navbar__nav--public{background:#fff}.bb-navbar--theme-corporate .bb-navbar__nav{background:#00285a}.bb-navbar--overview .bb-navbar__nav--public{background:#0ccca2}.bb-navbar--inverted .bb-navbar__nav{background:0 0}.bb-navbar--mobile-menu-visible .bb-navbar__nav,.is-sticky.bb-navbar--mobile-menu-visible .bb-navbar__nav{z-index:1501}.is-sticky .bb-navbar__nav{position:fixed;box-shadow:0 .125rem 1.25rem 0 rgba(0,0,0,.1)}.is-sticky.bb-navbar--inverted .bb-navbar__nav,.is-sticky.bb-navbar--mobile-menu-visible .bb-navbar__nav{box-shadow:none}.bb-navbar__nav>.bb-progress-bar{position:absolute;bottom:-.25rem}@media (min-width:992px){.bb-navbar__nav>.bb-progress-bar{bottom:-.375rem}}.bb-navbar__nav>.bb-container{display:flex;align-items:center;justify-content:space-between}.bb-navbar__secondary-bar{z-index:auto}.bb-navbar__secondary-bar>.bb-container{height:100%}.bb-navbar__profile-content{width:max-content;border-radius:0 0 .625rem .625rem;margin-top:.625rem;box-shadow:0 1.5rem 3.125rem rgba(0,0,0,.08)}@media (min-width:992px){.bb-navbar__profile-content{margin-top:.75rem}}.bb-navbar.has-progressbar .bb-dropdown__menu{padding-top:.875rem}@media (min-width:992px){.bb-navbar.has-progressbar .bb-dropdown__menu{padding-top:1.5625rem}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$8 = undefined;
/* module identifier */
const __vue_module_identifier__$8 = undefined;
/* functional template */
const __vue_is_functional_template__$8 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$8 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$8,
  staticRenderFns: __vue_staticRenderFns__$8
}, __vue_inject_styles__$8, __vue_script__$8, __vue_scope_id__$8, __vue_is_functional_template__$8, __vue_module_identifier__$8, false, createInjector, undefined, undefined);

const neutralFunction = v => v;
var script$7 = {
  name: 'bb-prefixed-input',
  components: {
    BbInput: __vue_component__$2f,
    BbMaskedInput: __vue_component__$27,
    BbTypeahead: __vue_component__$14,
    BbIcon: __vue_component__$2C,
    BbDropdownItem: __vue_component__$2o
  },
  mixins: [FieldExtensions, Field, ErrorBubble, LocalizeNumber, Options, veeValidationMixin, vuelidateValidator],
  props: {
    addOn: String,
    separator: {
      type: String,
      default() {
        return ' ';
      }
    },
    helpText: String,
    helpActive: Boolean,
    prefix: [String, Object],
    suffix: String,
    mask: {
      type: String,
      default: ''
    },
    prefixes: {
      type: Array,
      default() {
        return [];
      }
    },
    disabled: Boolean,
    disableSearch: Boolean,
    disablePrefix: Boolean,
    searchLabel: {
      type: Function,
      default: undefined
    },
    trackBy: {
      type: String,
      default: 'value'
    },
    formatter: {
      type: Function,
      default: neutralFunction
    },
    currency: Boolean,
    rules: Object,
    mandatory: Boolean,
    buttonAriaLabel: {
      type: String,
      default: 'Prefix selector'
    }
  },
  data() {
    var _this$prefix, _this$suffix;
    const internalPrefix = (_this$prefix = this.prefix) !== null && _this$prefix !== void 0 ? _this$prefix : this.getPrefixFromVal();
    const internalSuffix = (_this$suffix = this.suffix) !== null && _this$suffix !== void 0 ? _this$suffix : this.getSuffixFromVal();
    return {
      internalPrefix,
      internalSuffix,
      inputValue: internalSuffix !== '' ? internalSuffix : null,
      typeaheadOpen: false
    };
  },
  computed: {
    prefixListId() {
      return `${this.id}-prefix-list`;
    },
    prefixButtonId() {
      return `${this.id}-prefix-button`;
    },
    inputId() {
      return `${this.id}-input`;
    },
    filled() {
      const valuePartials = [this.internalPrefix, this.internalSuffix];
      return valuePartials.every(valueIsFilled);
    },
    classes() {
      return ['bb-prefixed-input', {
        'bb-prefixed-input--framed': this.isFramed,
        'bb-prefixed-input--framed-small': this.small,
        'bb-prefixed-input--disable-search': this.disableSearch,
        'bb-prefixed-input--disable-prefix': this.disablePrefix
      }];
    },
    valFromPrefixSuffix() {
      const preffixSuffix = [this.getValue(this.internalPrefix), this.internalSuffix];
      return preffixSuffix.join('') ? preffixSuffix.join(this.separator) : '';
    },
    prefixFromVal() {
      return this.getPrefixFromVal();
    },
    suffixFromVal() {
      return this.getSuffixFromVal();
    },
    simplePrefixes() {
      return !this.prefixes.length || !isObject(this.prefixes[0]);
    },
    disabledPrefix() {
      return this.disabled || this.disablePrefix;
    }
  },
  watch: {
    prefix: {
      deep: true,
      handler(newVal) {
        this.updatePrefix(newVal);
      }
    },
    suffix(newVal) {
      this.updateSuffix(newVal);
      this.updateInputValue();
    },
    value(newVal) {
      this.updateValue(newVal);
      this.updateInputValue();
    },
    prefixes: {
      deep: true,
      handler() {
        this.checkSeparatorConflict();
      }
    }
  },
  mounted() {
    this.checkSeparatorConflict();
    this.internalPrefix = this.prefix || this.prefixFromVal;
    this.internalSuffix = this.suffix || this.suffixFromVal;
    this.updateInputValue();
    if (this.value !== this.valFromPrefixSuffix) {
      this.$emit('input', this.valFromPrefixSuffix);
    }
  },
  methods: {
    onBlurAndValidate() {
      this.onBlur();
      this.validate();
    },
    getPrefixFromVal() {
      var _this$value$indexOf, _this$value;
      if (this.checkSeparatorConflict()) {
        return null;
      }
      if (!this.value || this.value === this.separator) {
        return this.simplePrefixes ? '' : null;
      }
      const separatorIndex = (_this$value$indexOf = (_this$value = this.value) === null || _this$value === void 0 ? void 0 : _this$value.indexOf(this.separator)) !== null && _this$value$indexOf !== void 0 ? _this$value$indexOf : -1;
      if (separatorIndex === -1) {
        warning(this, 'Unable to infer prefix from the value (separator not present in value)');
        return null;
      }
      const prefixString = this.value.substring(0, separatorIndex);
      if (this.simplePrefixes) {
        return prefixString;
      }
      const prefixObject = this.prefixes.find(item => item.value === prefixString);
      if (!prefixObject) {
        warning(this, 'Unable to infer prefix from the value (prefixes don\'t contain the value)');
        return null;
      }
      return prefixObject;
    },
    getSuffixFromVal() {
      var _this$value$indexOf2, _this$value2;
      const separatorIndex = (_this$value$indexOf2 = (_this$value2 = this.value) === null || _this$value2 === void 0 ? void 0 : _this$value2.indexOf(this.separator)) !== null && _this$value$indexOf2 !== void 0 ? _this$value$indexOf2 : -1;
      const suffixIndex = separatorIndex + this.separator.length;
      return separatorIndex > -1 ? this.value.substring(suffixIndex, this.value.length) : this.value;
    },
    updateInputValue() {
      this.inputValue = this.suffixFromVal === '' ? null : this.suffixFromVal;
    },
    prefixesContainSeparator() {
      return this.prefixes.some(prefix => this.getValue(prefix) && this.getValue(prefix).includes(this.separator));
    },
    checkSeparatorConflict() {
      if (this.prefixesContainSeparator()) {
        warning(this, `Prefix should not contain the separator ("${this.separator}")`);
        return true;
      }
    },
    getValue(item) {
      return this.simplePrefixes || !item ? item : item.value;
    },
    updatePrefix(newVal) {
      if (this.getValue(newVal) && this.getValue(newVal).includes(this.separator)) {
        warning(this, `Prefix should not contain the separator ("${this.separator}")`);
      } else {
        this.internalPrefix = newVal;
        this.val = this.valFromPrefixSuffix;
        this.$emit('prefix', this.internalPrefix);
        this.$emit('input', this.val);
      }
    },
    updateSuffix(newVal) {
      this.internalSuffix = newVal;
      this.val = this.valFromPrefixSuffix;
      this.$emit('suffix', this.internalSuffix);
      this.$emit('input', this.val);
    },
    updateValue(newVal) {
      this.val = newVal;
      this.internalPrefix = this.prefixFromVal;
      this.internalSuffix = this.suffixFromVal;
      this.$emit('prefix', this.internalPrefix);
      this.$emit('suffix', this.internalSuffix);
      this.$emit('input', this.val);
    },
    toggleTypeahead() {
      if (!this.disabled) {
        this.typeaheadOpen = !this.typeaheadOpen;
      }
      if (this.typeaheadOpen) {
        this.$nextTick(function () {
          this.$refs.typeahead.activate();
        });
      }
    }
  }
};

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    ref: "root",
    class: _vm.classes,
    attrs: {
      "data-valid": _vm.validity,
      "data-vv-name": _vm.name,
      "data-vv-value-path": "val"
    },
    on: {
      "mouseenter": _vm.showErrorBubble,
      "mouseleave": _vm.hideErrorBubble
    }
  }, [_c('div', {
    ref: "inputWrapper",
    staticClass: "bb-prefixed-input__input-wrapper"
  }, [_vm.mask ? _c('bb-masked-input', {
    staticClass: "bb-prefixed-input__masked-input",
    attrs: {
      "id": _vm.inputId,
      "input-attrs": _vm.inputAttrs,
      "type": _vm.type,
      "label": _vm.label,
      "mandatory": _vm.mandatory,
      "help-text": _vm.helpText,
      "help-active": _vm.helpActive,
      "add-on": _vm.addOn,
      "framed": _vm.isFramed,
      "small": _vm.small,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "mask": _vm.mask,
      "disabled": _vm.disabled,
      "name": _vm.name
    },
    on: {
      "input": _vm.updateSuffix,
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus
    },
    model: {
      value: _vm.internalSuffix,
      callback: function ($$v) {
        _vm.internalSuffix = $$v;
      },
      expression: "internalSuffix"
    }
  }) : _c('bb-input', {
    staticClass: "bb-prefixed-input__input",
    attrs: {
      "id": _vm.inputId,
      "input-attrs": _vm.inputAttrs,
      "currency": _vm.currency,
      "type": _vm.type,
      "label": _vm.label,
      "mandatory": _vm.mandatory,
      "help-text": _vm.helpText,
      "help-active": _vm.helpActive,
      "add-on": _vm.addOn,
      "framed": _vm.isFramed,
      "small": _vm.small,
      "tabindex": _vm.tabindex,
      "placeholder": _vm.placeholder,
      "disabled": _vm.disabled,
      "name": _vm.name,
      "min": _vm.min,
      "max": _vm.max,
      "formatter": _vm.formatter,
      "min-decimals": _vm.minDecimals,
      "max-decimals": _vm.maxDecimals
    },
    on: {
      "input": _vm.updateSuffix,
      "blur": _vm.onBlurAndValidate,
      "focus": _vm.onFocus
    },
    model: {
      value: _vm.inputValue,
      callback: function ($$v) {
        _vm.inputValue = $$v;
      },
      expression: "inputValue"
    }
  }), _vm._v(" "), _c('button', {
    class: ['bb-prefixed-input__prefix', {
      disabled: _vm.disabled
    }],
    attrs: {
      "id": _vm.prefixButtonId,
      "aria-expanded": String(_vm.typeaheadOpen),
      "aria-controls": _vm.prefixListId,
      "disabled": _vm.disabledPrefix,
      "aria-haspopup": "listbox",
      "type": "button",
      "aria-label": _vm.buttonAriaLabel
    },
    on: {
      "click": _vm.toggleTypeahead
    }
  }, [_vm._v("\n      " + _vm._s(_vm.getValue(_vm.internalPrefix)) + "\n      "), _c('bb-icon', {
    class: ['bb-prefixed-input__prefix-caret', {
      'is-active': _vm.typeaheadOpen
    }],
    attrs: {
      "name": "ui-menu-down",
      "size": "24",
      "fill": "gray-70"
    }
  })], 1)], 1), _vm._v(" "), _c('bb-typeahead', {
    directives: [{
      name: "show",
      rawName: "v-show",
      value: _vm.typeaheadOpen,
      expression: "typeaheadOpen"
    }],
    ref: "typeahead",
    staticClass: "bb-prefixed-input__typeahead",
    attrs: {
      "id": _vm.prefixListId,
      "options": _vm.prefixes,
      "allow-empty": false,
      "track-by": _vm.simplePrefixes ? null : _vm.trackBy,
      "custom-label": _vm.searchLabel,
      "disabled": _vm.disabledPrefix,
      "disable-search": _vm.disableSearch,
      "name": _vm.name + "-prefix",
      "aria-labelledby": _vm.prefixButtonId,
      "close-on-select": "",
      "label-prop-name": "label"
    },
    on: {
      "input": _vm.updatePrefix,
      "open": _vm.onFocus,
      "blur": function ($event) {
        _vm.typeaheadOpen = false;
        _vm.onBlurAndValidate();
      }
    },
    scopedSlots: _vm._u([{
      key: "option",
      fn: function (ref) {
        var option = ref.option;
        return [_c('bb-dropdown-item', {
          attrs: {
            "label": option.label || option.isTag ? option.label : option
          }
        })];
      }
    }]),
    model: {
      value: _vm.internalPrefix,
      callback: function ($$v) {
        _vm.internalPrefix = $$v;
      },
      expression: "internalPrefix"
    }
  }), _vm._v(" "), _vm.veeValidateErrors || _vm.rules && _vm.hasError ? [_vm.validationTooltipCondition ? _c('bb-tooltip', {
    attrs: {
      "reference": _vm.$refs.inputWrapper,
      "is-in-input": "",
      "bubble": "error"
    }
  }, [_c('p', [_vm._v(_vm._s(_vm.firstError || _vm.getFirstError()))])]) : _vm._e(), _vm._v(" "), !_vm.small && !_vm.errorMessageAsBubble ? _c('p', {
    staticClass: "error-message"
  }, [_vm._v("\n      " + _vm._s(_vm.firstError || _vm.getFirstError()) + "\n    ")]) : _vm._e()] : _vm._e()], 2);
};
var __vue_staticRenderFns__$7 = [];

/* style */
const __vue_inject_styles__$7 = function (inject) {
  if (!inject) return;
  inject("data-v-748c6bfd_0", {
    source: ".bb-prefixed-input{position:relative}.bb-prefixed-input__input-wrapper{position:relative}.bb-prefixed-input__input label,.bb-prefixed-input__masked-input label{padding-left:5.625rem}.bb-prefixed-input--framed .bb-prefixed-input__input label,.bb-prefixed-input--framed .bb-prefixed-input__masked-input label{padding-left:0}.bb-prefixed-input__input.is-filled label,.bb-prefixed-input__input.is-focused label,.bb-prefixed-input__masked-input.is-filled label,.bb-prefixed-input__masked-input.is-focused label{padding-right:0;transform:translate(-4.921875rem,.25rem) scale(.875)}.bb-prefixed-input--framed .bb-prefixed-input__input.is-filled label,.bb-prefixed-input--framed .bb-prefixed-input__input.is-focused label,.bb-prefixed-input--framed .bb-prefixed-input__masked-input.is-filled label,.bb-prefixed-input--framed .bb-prefixed-input__masked-input.is-focused label{transform:none}.bb-prefixed-input__input input,.bb-prefixed-input__masked-input input{padding-left:5.625rem}.bb-prefixed-input--framed .bb-prefixed-input__input input,.bb-prefixed-input--framed .bb-prefixed-input__masked-input input{padding-left:5rem}.bb-prefixed-input--framed-small .bb-prefixed-input__input input,.bb-prefixed-input--framed-small .bb-prefixed-input__masked-input input{padding-left:4.4375rem}.bb-prefixed-input[data-valid=false] .bb-prefixed-input__input input,.bb-prefixed-input[data-valid=false] .bb-prefixed-input__masked-input input{border-color:#ff7155}.bb-prefixed-input[data-valid=true] .bb-prefixed-input__input input,.bb-prefixed-input[data-valid=true] .bb-prefixed-input__masked-input input{border-color:#0ccca2}.bb-prefixed-input__input .validation-error-icon,.bb-prefixed-input__masked-input .validation-error-icon{color:#ff7155;font-size:1.4375rem}.bb-prefixed-input[data-valid=false] .bb-prefixed-input__input .validation-error-icon,.bb-prefixed-input[data-valid=false] .bb-prefixed-input__masked-input .validation-error-icon{display:block}.bb-prefixed-input__input .error-message,.bb-prefixed-input__masked-input .error-message{display:none}.bb-prefixed-input__prefix{position:absolute;bottom:0;left:0;display:flex;min-width:5.125rem;height:1.875rem;padding:0 0 .5rem 0;border-width:initial;border-style:initial;border-color:initial;background:initial;color:#00285a;cursor:pointer;font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:inherit;outline:0;text-align:initial;white-space:nowrap}.bb-prefixed-input__prefix.disabled{opacity:.6;pointer-events:none}.bb-prefixed-input--framed .bb-prefixed-input__prefix{bottom:.0625rem;left:.9375rem;width:3.125rem;font-size:.875rem}.bb-prefixed-input--framed-small .bb-prefixed-input__prefix{bottom:-.25rem;width:2.8125rem;font-size:.75rem}.bb-prefixed-input--disable-prefix .bb-prefixed-input__prefix{cursor:none;opacity:.6;pointer-events:none}.bb-prefixed-input__prefix-caret{margin-right:-.5rem;margin-left:auto;cursor:pointer;transition:transform .2s ease}.bb-prefixed-input__prefix-caret.is-active{transform:rotate(180deg)}.bb-prefixed-input__typeahead{position:absolute;z-index:1;width:100%}.bb-prefixed-input__typeahead .bb-typeahead__caret{top:.8125rem;right:.4375rem}.bb-prefixed-input__typeahead input{position:absolute;display:block!important;padding-top:0}.bb-prefixed-input__typeahead .multiselect__caret{display:none}.bb-prefixed-input__typeahead .multiselect__label{display:none}.bb-prefixed-input__typeahead .multiselect__content{max-height:16.875rem;overflow-y:scroll}.bb-multiselect--above .bb-prefixed-input__typeahead .multiselect__content{bottom:150%}.bb-prefixed-input__typeahead .multiselect__tags{min-height:3.125rem;padding-top:.9375rem;padding-left:.75rem;border:none;margin-top:-.625rem;background:#fff;box-shadow:0 .0625rem .25rem 0 rgba(0,0,0,.2)}.bb-prefixed-input--disable-search .bb-prefixed-input__typeahead .multiselect__tags{position:absolute;opacity:0!important;pointer-events:none}.bb-prefixed-input--disable-search .bb-prefixed-input__typeahead{padding-top:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$7 = undefined;
/* module identifier */
const __vue_module_identifier__$7 = undefined;
/* functional template */
const __vue_is_functional_template__$7 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$7 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$7,
  staticRenderFns: __vue_staticRenderFns__$7
}, __vue_inject_styles__$7, __vue_script__$7, __vue_scope_id__$7, __vue_is_functional_template__$7, __vue_module_identifier__$7, false, createInjector, undefined, undefined);

const CarouselDirection = Object.freeze({
  forward: 1,
  backward: 2
});
var script$6 = {
  name: 'bb-testimonial-section',
  components: {
    BbContainer: __vue_component__$2q,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbCard: __vue_component__$2t,
    BbIcon: __vue_component__$2C,
    BbHeading: __vue_component__$2j,
    BbProgressDots: __vue_component__$25
  },
  props: {
    ratings: Boolean,
    maximumRating: {
      type: Number,
      default: 5
    },
    heading: Object,
    carouselItems: Array,
    verifiedText: {
      type: String,
      default: 'Verified customer'
    },
    progressDotsProps: Object
  },
  data() {
    return {
      activeIndex: 0,
      carouselDirection: CarouselDirection.forward
    };
  },
  computed: {
    classes() {
      return ['bb-testimonial-section', `bb-testimonial-section--carousel-direction-${this.carouselDirectionName}`, {
        'bb-testimonial-section--ratings': this.ratings
      }];
    },
    carouselDirectionName() {
      return Object.keys(CarouselDirection).find(key => CarouselDirection[key] === this.carouselDirection);
    }
  },
  methods: {
    setActive(index) {
      this.carouselDirection = this.activeIndex < index ? CarouselDirection.forward : CarouselDirection.backward;
      this.activeIndex = index;
    },
    clampedRatingFullCount(rating) {
      const ratingFullCount = this.ratingWithoutDecimals(rating);
      const clampedRatingFullCount = ratingFullCount > this.maximumRating ? this.maximumRating : ratingFullCount;
      return clampedRatingFullCount;
    },
    ratingIncludesDecimals(rating) {
      const ratingIncludesDecimals = String(rating).includes('.');
      return ratingIncludesDecimals;
    },
    ratingWithoutDecimals(rating) {
      const ratingWithoutDecimals = +String(rating).split('.', 1);
      return ratingWithoutDecimals;
    },
    ratingContainsEmpties(rating) {
      const ratingContainsEmpties = this.maximumRating - this.ratingWithoutDecimals(rating) <= 0;
      return ratingContainsEmpties;
    },
    ratingEmptyCount(rating) {
      const ratingFullCount = this.clampedRatingFullCount(rating);
      const emptyCount = this.maximumRating - ratingFullCount;
      const ratingEmptyCount = this.ratingIncludesDecimals(rating) ? emptyCount - 1 : emptyCount;
      return ratingEmptyCount;
    }
  }
};

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_c('div', {
    staticClass: "bb-testimonial-section__header"
  }, [_c('bb-container', [_c('bb-row', {
    staticClass: "bb-testimonial-section__heading-wrapper"
  }, [_c('bb-col', {
    attrs: {
      "lg": "10",
      "offset-lg": "1",
      "offset-xl": "3",
      "order-lg": "2"
    }
  }, [_c('bb-heading', _vm._b({
    staticClass: "bb-testimonial-section__heading",
    attrs: {
      "white": ""
    }
  }, 'bb-heading', _vm.heading, false))], 1), _vm._v(" "), _c('bb-col', {
    attrs: {
      "lg": "13",
      "xl": "11",
      "order-lg": "1"
    }
  }, [_c('div', {
    staticClass: "bb-testimonial-section__carousel"
  }, [_c('transition', {
    attrs: {
      "name": "testimonial-carousel"
    }
  }, [_vm._l(_vm.carouselItems, function (item, index) {
    return [index === _vm.activeIndex ? _c('bb-card', {
      key: "card-" + index,
      staticClass: "bb-testimonial-section__carousel-card"
    }, [_c('div', {
      staticClass: "bb-testimonial-section__carousel-card-review"
    }, [_c('bb-icon', {
      staticClass: "bb-testimonial-section__carousel-card-icon",
      attrs: {
        "name": "ui-format-quote-open",
        "size": "48",
        "fill": "gray-40",
        "rotate": "down"
      }
    }), _vm._v(" "), _vm.ratings ? _c('div', {
      staticClass: "bb-testimonial-section__carousel-card-rating"
    }, [_vm._l(_vm.clampedRatingFullCount(item.rating), function (star) {
      return _c('svg', {
        key: "star-" + star,
        staticClass: "bb-testimonial-section__carousel-card-rating-star",
        attrs: {
          "width": "20",
          "height": "20",
          "viewBox": "0 0 20 20",
          "fill": "none",
          "xmlns": "http://www.w3.org/2000/svg"
        }
      }, [_c('path', {
        attrs: {
          "d": "M0 0H19.925V19.925H0V0Z",
          "fill": "#0CCCA2"
        }
      }), _vm._v(" "), _c('path', {
        attrs: {
          "fill-rule": "evenodd",
          "clip-rule": "evenodd",
          "d": "M11.6 8.4002H16.95L12.625 11.5502L9.98954 13.4713L9.97495 13.4752L9.97961 13.4786L9.94995 13.5002L5.62495 16.6252L7.27495 11.5502L2.94995 8.4002H8.29995L9.94995 3.3252L11.6 8.4002ZM9.97961 13.4786L14.275 16.6002L13 12.6752L9.98954 13.4713L9.97961 13.4786Z",
          "fill": "white"
        }
      })]);
    }), _vm._v(" "), _vm.ratingIncludesDecimals(item.rating) ? _c('svg', {
      staticClass: "bb-testimonial-section__carousel-card-rating-star",
      attrs: {
        "width": "21",
        "height": "20",
        "viewBox": "0 0 21 20",
        "fill": "none",
        "xmlns": "http://www.w3.org/2000/svg"
      }
    }, [_c('path', {
      attrs: {
        "d": "M20.5 0H10.5V20H20.5V0Z",
        "fill": "#DEE3E8"
      }
    }), _vm._v(" "), _c('path', {
      attrs: {
        "d": "M10.5 0H0.5V20H10.5V0Z",
        "fill": "#0CCCA2"
      }
    }), _vm._v(" "), _c('path', {
      attrs: {
        "d": "M10.5 13.4792L13.5417 12.7083L14.8125 16.625L10.5 13.4792ZM17.5 8.41667H12.1458L10.5 3.375L8.85417 8.41667H3.5L7.83334 11.5417L6.1875 16.5833L10.5208 13.4583L13.1875 11.5417L17.5 8.41667Z",
        "fill": "white"
      }
    })]) : _vm._e(), _vm._v(" "), _vm._l(_vm.ratingEmptyCount(item.rating), function (empty) {
      return _c('svg', {
        key: "star-empty-" + empty,
        staticClass: "bb-testimonial-section__carousel-card-rating-star",
        attrs: {
          "width": "20",
          "height": "20",
          "viewBox": "0 0 20 20",
          "fill": "none",
          "xmlns": "http://www.w3.org/2000/svg"
        }
      }, [_c('path', {
        attrs: {
          "d": "M20 0H0V20H20V0Z",
          "fill": "#DEE3E8"
        }
      }), _vm._v(" "), _c('path', {
        attrs: {
          "d": "M10 13.4792L13.0417 12.7083L14.3125 16.625L10 13.4792ZM17 8.41667H11.6458L10 3.375L8.35417 8.41667H3L7.33334 11.5417L5.6875 16.5833L10.0208 13.4583L12.6875 11.5417L17 8.41667Z",
          "fill": "white"
        }
      })]);
    })], 2) : _vm._e(), _vm._v("\n                    " + _vm._s(item.review) + "\n                  ")], 1), _vm._v(" "), item.name ? _c('div', {
      staticClass: "bb-testimonial-section__carousel-card-footer"
    }, [_c('div', {
      staticClass: "bb-testimonial-section__carousel-card-reviewer"
    }, [_c('div', {
      staticClass: "bb-testimonial-section__reviewer-avatar",
      style: {
        backgroundImage: "url(" + item.avatar + ")"
      }
    }), _vm._v(" "), _c('div', {
      staticClass: "bb-testimonial-section__reviewer-text"
    }, [_c('div', {
      staticClass: "bb-testimonial-section__reviewer-name"
    }, [_vm._v("\n                          " + _vm._s(item.name) + "\n                        ")]), _vm._v(" "), item.description || _vm.verifiedText ? _c('div', {
      staticClass: "bb-testimonial-section__reviewer-verified"
    }, [item.description ? [_c('bb-icon', {
      staticClass: "bb-testimonial-section__reviewer-verified-icon",
      attrs: {
        "name": "ui-check-circle",
        "size": "18",
        "fill": "mint"
      }
    }), _vm._v(" "), _c('span', [_vm._v("\n                              " + _vm._s(item.description) + "\n                            ")])] : _vm.verifiedText ? _c('span', [_vm._v("\n                            " + _vm._s(_vm.verifiedText) + "\n                          ")]) : _vm._e()], 2) : _vm._e()])])]) : _vm._e(), _vm._v(" "), _c('bb-progress-dots', _vm._b({
      staticClass: "bb-testimonial-section__carousel-card-progress",
      attrs: {
        "active-index": _vm.activeIndex,
        "count": _vm.carouselItems.length,
        "shape": "rectangle",
        "is-clickable": ""
      },
      on: {
        "activeIndex": _vm.setActive
      }
    }, 'bb-progress-dots', _vm.progressDotsProps, false))], 1) : _vm._e()];
  })], 2)], 1)])], 1)], 1)], 1), _vm._v(" "), _c('div', {
    staticClass: "bb-testimonial-section__points"
  }, [_c('bb-container', [_c('div', {
    staticClass: "bb-testimonial-section__points-wrapper"
  }, [_vm._l(3, function (i) {
    return [i > 1 ? _c('div', {
      key: "sep-" + i,
      staticClass: "bb-testimonial-section__points-separator"
    }) : _vm._e(), _vm._v(" "), _c('div', {
      key: i,
      staticClass: "bb-testimonial-section__points-item"
    }, [_c('div', {
      staticClass: "bb-testimonial-section__points-item-image"
    }, [_vm._t("item-image-" + i)], 2), _vm._v(" "), _c('div', {
      staticClass: "bb-testimonial-section__points-item-text"
    }, [_vm._t("item-text-" + i)], 2)])];
  })], 2)])], 1)]);
};
var __vue_staticRenderFns__$6 = [];

/* style */
const __vue_inject_styles__$6 = function (inject) {
  if (!inject) return;
  inject("data-v-705af0b0_0", {
    source: ".bb-testimonial-section .bb-testimonial-section__header{padding:1.875rem 0;background:#2485e5}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__header{padding:1.875rem 0 3.75rem}}@media (min-width:992px){.bb-testimonial-section .bb-testimonial-section__header{padding:4.375rem 0 5.3125rem}}.bb-testimonial-section .bb-testimonial-section__heading-wrapper{display:flex;align-items:center}@media (min-width:576px){.bb-testimonial-section .bb-testimonial-section__heading{margin-bottom:1.875rem}}@media (min-width:992px){.bb-testimonial-section .bb-testimonial-section__heading{padding-right:0}}.bb-testimonial-section .bb-testimonial-section__carousel{position:relative;display:flex;width:100%;max-width:100%;height:28.125rem;align-items:center;justify-content:center;padding-bottom:1.875rem;margin:1.25rem 0 0}@media (min-width:375px){.bb-testimonial-section .bb-testimonial-section__carousel{height:25rem}}@media (min-width:414px){.bb-testimonial-section .bb-testimonial-section__carousel{height:21.875rem}}@media (min-width:576px){.bb-testimonial-section .bb-testimonial-section__carousel{height:18.75rem}}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__carousel{margin:0}}.bb-testimonial-section .bb-testimonial-section__carousel-card{position:absolute;display:flex;width:100%;max-width:100%;flex-direction:column;padding-bottom:1.5625rem;color:#57697a}@media (min-width:576px){.bb-testimonial-section .bb-testimonial-section__carousel-card{padding-bottom:1.875rem}}.bb-testimonial-section .bb-testimonial-section__carousel-card>*{position:relative;z-index:1}.bb-testimonial-section .bb-testimonial-section__carousel-card::after{position:absolute;bottom:0;left:3%;display:block;width:94%;height:2.5rem;border-radius:10px;background:#fff;content:\"\";opacity:.5;transform:translateY(.9375rem)}.bb-testimonial-section .bb-testimonial-section__carousel-card-rating{min-height:1.5rem;padding-bottom:2.1875rem}.bb-testimonial-section .bb-testimonial-section__carousel-card-rating-star{margin-right:.25rem}.bb-testimonial-section .bb-testimonial-section__carousel-card-review{position:relative}.bb-testimonial-section .bb-testimonial-section__carousel-card-footer{display:flex;flex-direction:column;padding-top:2.1875rem}.bb-testimonial-section .bb-testimonial-section__carousel-card-reviewer{display:flex}.bb-testimonial-section .bb-testimonial-section__reviewer-avatar{width:2.5rem;height:2.5rem;flex-shrink:0;border-radius:50%;margin-right:.75rem;background-color:#eeeff2;background-position:center;background-repeat:no-repeat;background-size:cover}.bb-testimonial-section .bb-testimonial-section__reviewer-text{display:flex;flex-direction:column;flex-grow:1;justify-content:center}.bb-testimonial-section .bb-testimonial-section__reviewer-name{color:#38414d;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif;font-size:1rem}@media (min-width:576px){.bb-testimonial-section .bb-testimonial-section__reviewer-name{width:0;white-space:nowrap}}.bb-testimonial-section .bb-testimonial-section__reviewer-verified{display:flex;font-size:12px;font-style:italic}.bb-testimonial-section .bb-testimonial-section__reviewer-verified-icon{margin-right:.3125rem}.bb-testimonial-section .bb-testimonial-section__carousel-card-icon{position:relative;top:-15px;right:-5px;float:right}.bb-testimonial-section--ratings.bb-testimonial-section .bb-testimonial-section__carousel-card-icon{position:absolute}.bb-testimonial-section .bb-testimonial-section__carousel-card-progress{margin-top:.9375rem}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__carousel-card-progress{align-self:flex-end}}.bb-testimonial-section .bb-testimonial-section__points{padding:0 0 1.875rem;background:#f7f7f8}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__points{padding:1.875rem 0 4rem}}.bb-testimonial-section .bb-testimonial-section__points-wrapper{display:flex;flex-direction:column;align-items:flex-start;justify-content:space-between}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__points-wrapper{flex-direction:row;align-items:center}}.bb-testimonial-section .bb-testimonial-section__points-item{display:flex;width:100%;height:5rem;align-items:center;justify-content:space-between;padding-top:1.5625rem}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__points-item{width:auto;height:8.125rem;flex-direction:column;justify-content:flex-end}}.bb-testimonial-section .bb-testimonial-section__points-item-image{width:7.5rem;flex-shrink:0}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__points-item-image{width:unset;margin-bottom:1.25rem;text-align:center}}.bb-testimonial-section .bb-testimonial-section__points-item-text{max-width:12.5rem;padding-left:1.875rem;color:#38414d;font-size:.875rem}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__points-item-text{padding-left:0;text-align:center}}@media (min-width:992px){.bb-testimonial-section .bb-testimonial-section__points-item-text{max-width:14.375rem;font-size:1rem}}.bb-testimonial-section .bb-testimonial-section__points-separator{display:none;width:.125rem;height:2.6875rem;background:#dee3e8;transform:rotate(20deg)}@media (min-width:768px){.bb-testimonial-section .bb-testimonial-section__points-separator{display:block}}.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-enter,.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-leave-active{opacity:0}.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-enter::after,.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-leave-active::after{opacity:0;transform:scale(.8)}.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-enter{transform:translateY(2.5rem) scale(.8)}.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-enter::after{opacity:1;transform:translateY(2.5rem) scale(1)}.bb-testimonial-section--carousel-direction-forward .testimonial-carousel-leave-active{transform:translateY(-2.5rem) scale(.7)}.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-enter,.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-leave-active{opacity:0}.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-enter::after,.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-leave-active::after{opacity:0;transform:scale(0)}.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-enter{transform:translateY(-2.5rem) scale(.8)}.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-enter::after{opacity:1;transform:translateY(-2.5rem) scale(.8)}.bb-testimonial-section--carousel-direction-backward .testimonial-carousel-leave-active{transform:translateY(2.5rem) scale(.7)}.testimonial-carousel-enter-active,.testimonial-carousel-leave-active{transition:transform 1s,opacity .5s}.testimonial-carousel-enter-active::after,.testimonial-carousel-leave-active::after{transition:transform 1s,opacity .5s}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$6 = undefined;
/* module identifier */
const __vue_module_identifier__$6 = undefined;
/* functional template */
const __vue_is_functional_template__$6 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$6 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$6,
  staticRenderFns: __vue_staticRenderFns__$6
}, __vue_inject_styles__$6, __vue_script__$6, __vue_scope_id__$6, __vue_is_functional_template__$6, __vue_module_identifier__$6, false, createInjector, undefined, undefined);

var Icon = { render: function () { var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"xmlns":"http://www.w3.org/2000/svg","viewBox":"0 0 40 40"}},[_c('circle',{attrs:{"cx":"19.9","cy":"19.9","r":"19.9","fill":"#f7f7f7"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M17.49 27.48L17.49 23.24 22.05 23.24 22.05 27.48 17.49 27.48z"}}),_c('path',{attrs:{"fill":"#fff","d":"M20.97 26.4L20.97 24.32 18.57 24.32 18.57 26.4 20.97 26.4z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M27.74,30.74l-.12.39H11.92l-.11-.39a3.14,3.14,0,0,1-.15-.95c0-2.82,3.67-5,8.11-5s8.12,2.15,8.12,5A3.14,3.14,0,0,1,27.74,30.74Z"}}),_c('path',{attrs:{"fill":"#8ac4ff","d":"M26.81,29.79c0-2.07-3.11-3.89-7-3.89s-7,1.82-7,3.89a2.26,2.26,0,0,0,0,.26h14A2.28,2.28,0,0,0,26.81,29.79Z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M16.76,18.89a5.55,5.55,0,0,0-1.42.21l-.4.1-.38.09a1.66,1.66,0,0,1-.8,0c-.6-.17-.83-.73-.83-1.63l-.51-5.1a4.39,4.39,0,0,1,4.34-4.44h6.73A3.5,3.5,0,0,1,27,10.45a5.31,5.31,0,0,1,.28,2,1.21,1.21,0,0,1,0,.18l-.56,4.74a1.5,1.5,0,0,1-1.49,1.51Z"}}),_c('path',{attrs:{"fill":"#8ac4ff","d":"M25.26,17.79a.44.44,0,0,0,.41-.49l.57-4.74v-.14A4.43,4.43,0,0,0,26,10.81a2.44,2.44,0,0,0-2.55-1.62H16.76a3.31,3.31,0,0,0-3.27,3.3L14,17.61a1.61,1.61,0,0,0,.06.65c.08,0,.13,0,1-.21a6.55,6.55,0,0,1,1.69-.24Z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M25.47,19.46c0,2.72-2.52,4.89-5.6,4.89s-5.61-2.17-5.61-4.89v-.13s0-1.54,0-4.55a2.12,2.12,0,0,1,2.1-2.13l.22,0,.3.12a8.68,8.68,0,0,0,.88.26,12.24,12.24,0,0,0,3.68.38,3.2,3.2,0,0,0,.66-.23l.25-.12c.3-.14.57-.29.74-.38l.12-.07h.15a2.12,2.12,0,0,1,2.1,2.13v4.68Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M24.39,14.78a1,1,0,0,0-.9-1l-.1,0c-.18.1-.37.19-.56.28l-.28.13a3.33,3.33,0,0,1-1,.33,13.4,13.4,0,0,1-4-.41,10,10,0,0,1-1.22-.39,1,1,0,0,0-.93,1v4.68c0,2.09,2,3.81,4.53,3.81s4.52-1.72,4.52-3.8V14.78Z"}}),_c('path',{attrs:{"fill":"#3cc88c","d":"M17.09,20.43a.54.54,0,0,1,.33-.69.53.53,0,0,1,.69.32,1.7,1.7,0,0,0,3.22,0,.53.53,0,0,1,.69-.32.54.54,0,0,1,.33.69,2.79,2.79,0,0,1-5.26,0Z"}}),_c('path',{attrs:{"fill":"#0087ca","d":"M25.94,18.05h0a.35.35,0,0,0-.34.36c0,.1,0,.2,0,.3Zm0-1.1A1.09,1.09,0,0,1,27,18a1.14,1.14,0,0,1-.14.54c-.57,1-1,1.73-1.24,2.11s-.31.42-.51.49a.55.55,0,0,1-.77-.48,1,1,0,0,1,0-.33,7.66,7.66,0,0,0,.1-1.94A1.44,1.44,0,0,1,25.87,17Zm-12.3-.15h.05A1.41,1.41,0,0,1,15,18.25a8,8,0,0,0,.1,2,1.35,1.35,0,0,1,0,.33.56.56,0,0,1-.79.47c-.19-.08-.29-.21-.49-.5s-.65-1.09-1.21-2.14a1.1,1.1,0,0,1-.13-.51A1.08,1.08,0,0,1,13.62,16.8Z"}}),_c('path',{attrs:{"fill":"#fff","d":"M14,18.74,13.54,18a.41.41,0,0,1,.41.18A.76.76,0,0,1,14,18.74Zm11.6,0a.76.76,0,0,1,0-.56A.41.41,0,0,1,26,18Z"}})]) } };

Vue.component('icon-client-personal', Icon);

var script$5 = {
  name: 'bb-application-page',
  components: {
    BbContainer: __vue_component__$2q,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbForm: __vue_component__$1c
  },
  mixins: [validator],
  props: {
    formProps: Object
  },
  emits: ['submit', 'progress', 'cancel'],
  methods: {
    onFormSubmit(event) {
      this.$emit('submit', event);
    },
    onFormProgress(event) {
      this.$emit('progress', event);
    },
    onFormCancel(event) {
      this.$emit('cancel', event);
    }
  }
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-application-page"
  }, [_vm._t("header"), _vm._v(" "), _c('bb-container', [_c('bb-row', {
    attrs: {
      "justify": "center"
    }
  }, [_c('bb-col', {
    attrs: {
      "sm": "20",
      "md": "16",
      "lg": "12",
      "xl": "10"
    }
  }, [_c('bb-form', _vm._b({
    on: {
      "submit": _vm.onFormSubmit,
      "progress": _vm.onFormProgress,
      "cancel": _vm.onFormCancel
    }
  }, 'bb-form', _vm.formProps, false), [_vm._t("default")], 2)], 1)], 1)], 1)], 2);
};
var __vue_staticRenderFns__$5 = [];

/* style */
const __vue_inject_styles__$5 = function (inject) {
  if (!inject) return;
  inject("data-v-019e8c7c_0", {
    source: ".bb-application-page{width:100%;padding-bottom:1.875rem;background:#fff}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$5 = undefined;
/* module identifier */
const __vue_module_identifier__$5 = undefined;
/* functional template */
const __vue_is_functional_template__$5 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$5 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$5,
  staticRenderFns: __vue_staticRenderFns__$5
}, __vue_inject_styles__$5, __vue_script__$5, __vue_scope_id__$5, __vue_is_functional_template__$5, __vue_module_identifier__$5, false, createInjector, undefined, undefined);

var script$4 = Vue.extend({
  name: 'bb-overview',
  components: {
    BbButton: __vue_component__$2v,
    BbBox: __vue_component__$2x
  },
  props: {
    contracts: Array,
    boxHighlighted: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    onDownload() {
      /**
       * Download event.
       *
       * @event download
       */
      this.$emit('download');
    }
  }
});

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', _vm._l(_vm.contracts, function (contract) {
    return _c('div', {
      key: "overview-" + contract.heading,
      staticClass: "bb-overview-list__contract"
    }, [_vm._t("contractOverview", function () {
      return [_vm._l(contract.overview, function (overview, boxIndex) {
        return _c('bb-box', {
          key: overview.title,
          staticClass: "bb-overview-list__box",
          class: {
            'bb-overview-list__box--highlight': _vm.boxHighlighted && boxIndex === 0
          }
        }, [_c('p', {
          staticClass: "bb-overview-list__box-title"
        }, [_vm._v("\n          " + _vm._s(overview.title) + "\n        ")]), _vm._v(" "), _c('p', {
          staticClass: "bb-overview-list__box-content"
        }, [_vm._v("\n          " + _vm._s(overview.content) + "\n        ")])]);
      }), _vm._v(" "), _vm._l(contract.footnotes, function (footnote) {
        return _c('p', {
          key: footnote,
          staticClass: "bb-overview-list__footnote"
        }, [_vm._v("\n        " + _vm._s(footnote) + "\n      ")]);
      }), _vm._v(" "), contract.download ? _c('section', {
        staticClass: "bb-overview-list__download-section"
      }, [contract.download.title ? _c('p', {
        staticClass: "bb-overview-list__download-title"
      }, [_vm._v("\n          " + _vm._s(contract.download.title) + "\n        ")]) : _vm._e(), _vm._v(" "), _vm._l(contract.download.paragraphs, function (paragraph, index) {
        return _c('p', {
          key: index,
          staticClass: "bb-overview-list__download-paragraph"
        }, [_vm._v("\n          " + _vm._s(paragraph) + "\n        ")]);
      }), _vm._v(" "), contract.download.button ? _c('bb-button', _vm._b({
        staticClass: "bb-overview-list__download-button",
        attrs: {
          "color": "blue",
          "size": "sm"
        },
        on: {
          "click": _vm.onDownload
        }
      }, 'bb-button', contract.download.button, false)) : _vm._e()], 2) : _vm._e()];
    }, null, Object.assign({}, contract))], 2);
  }), 0);
};
var __vue_staticRenderFns__$4 = [];

/* style */
const __vue_inject_styles__$4 = function (inject) {
  if (!inject) return;
  inject("data-v-12551a76_0", {
    source: ".bb-overview-list__contract{padding-bottom:1.875rem}.bb-overview-list__box{display:none;padding-top:1.875rem;padding-bottom:1.875rem;margin-bottom:.9375rem;background:#fff;text-align:center}.bb-overview-list__box:last-of-type{margin-bottom:1.25rem}@media (min-width:768px){.bb-overview-list__box{display:block}}.bb-overview-list__box--highlight{border-color:#2485e5;background:#2485e5}.bb-overview-list__box--highlight .bb-overview-list__box-content,.bb-overview-list__box--highlight .bb-overview-list__box-title{color:#fff}.bb-overview-list__box-content,.bb-overview-list__box-title{font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-overview-list__box-title{color:#89a;font-size:.6875rem;text-transform:uppercase}.bb-overview-list__box-content{color:#2485e5;font-size:1.5rem;line-height:28px}.bb-overview-list__footnote{display:none}@media (min-width:768px){.bb-overview-list__footnote{display:block}}.bb-overview-list__download-paragraph,.bb-overview-list__footnote{margin-bottom:.9375rem;color:#38414d;font-size:.875rem}@media (min-width:768px){.bb-overview-list__download-section{margin-top:3.125rem}}.bb-overview-list__download-title{margin-bottom:1.875rem;color:#9dabb9;font-family:GothamSSm-Bold,\"Helvetica Neue\",Arial,Helvetica,sans-serif}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$4 = undefined;
/* module identifier */
const __vue_module_identifier__$4 = undefined;
/* functional template */
const __vue_is_functional_template__$4 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$4 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$4,
  staticRenderFns: __vue_staticRenderFns__$4
}, __vue_inject_styles__$4, __vue_script__$4, __vue_scope_id__$4, __vue_is_functional_template__$4, __vue_module_identifier__$4, false, createInjector, undefined, undefined);

var script$3 = Vue.extend({
  name: 'bb-mobile-overview-list',
  components: {
    BbSeparator: __vue_component__$2a
  },
  props: {
    value: Array
  }
});

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', [_vm._l(_vm.value, function (item) {
    return [_c('bb-separator', {
      key: "mobile-overview-" + item.title + "-1",
      staticClass: "bb-mobile-overview-list__separator"
    }), _vm._v(" "), _c('p', {
      key: "mobile-overview-" + item.title + "-2",
      staticClass: "bb-mobile-overview-list__title"
    }, [_vm._v("\n      " + _vm._s(item.title) + "\n    ")]), _vm._v(" "), _c('p', {
      key: "mobile-overview-" + item.title + "-3",
      staticClass: "bb-mobile-overview-list__content"
    }, [_vm._v("\n      " + _vm._s(item.content) + "\n    ")])];
  }), _vm._v(" "), _vm.value.length ? _c('bb-separator', {
    staticClass: "bb-mobile-overview-list__separator"
  }) : _vm._e()], 2);
};
var __vue_staticRenderFns__$3 = [];

/* style */
const __vue_inject_styles__$3 = function (inject) {
  if (!inject) return;
  inject("data-v-488c9a4f_0", {
    source: ".bb-mobile-overview-list__separator{margin:.3125rem 0}.bb-mobile-overview-list__content,.bb-mobile-overview-list__title{font-family:GothamSSm-Medium,\"Helvetica Neue\",Arial,Helvetica,sans-serif}.bb-mobile-overview-list__title{color:#89a;font-size:.6875rem;text-transform:uppercase}.bb-mobile-overview-list__content{color:#38414d;font-size:.875rem}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$3 = undefined;
/* module identifier */
const __vue_module_identifier__$3 = undefined;
/* functional template */
const __vue_is_functional_template__$3 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$3 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$3,
  staticRenderFns: __vue_staticRenderFns__$3
}, __vue_inject_styles__$3, __vue_script__$3, __vue_scope_id__$3, __vue_is_functional_template__$3, __vue_module_identifier__$3, false, createInjector, undefined, undefined);

var script$2 = Vue.extend({
  name: 'bb-contracts',
  components: {
    BbListGroup: __vue_component__$2b,
    BbListItem: __vue_component__$29,
    BbTooltip: __vue_component__$2B,
    BbButton: __vue_component__$2v,
    BbMobileOverviewList: __vue_component__$3
  },
  props: {
    contracts: Array
  }
});

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', _vm._l(_vm.contracts, function (contract, contractIndex) {
    return _c('div', {
      key: "heading-" + contractIndex,
      staticClass: "bb-contracts__contract"
    }, [_vm._t("contractOverview", null, null, Object.assign({}, contract)), _vm._v(" "), _vm._t("contract", function () {
      return [contract.heading ? _c('h4', {
        staticClass: "bb-contracts__heading"
      }, [_vm._v("\n        " + _vm._s(contract.heading) + "\n      ")]) : _vm._e(), _vm._v(" "), contract.paragraph ? _c('p', {
        staticClass: "bb-contracts__paragraph"
      }, [_vm._v("\n        " + _vm._s(contract.paragraph) + "\n      ")]) : _vm._e(), _vm._v(" "), contract.overview ? _c('bb-mobile-overview-list', {
        staticClass: "bb-contracts__mobile-overview",
        attrs: {
          "value": contract.overview
        }
      }) : _vm._e(), _vm._v(" "), _c('bb-list-group', {
        staticClass: "bb-contracts__list"
      }, _vm._l(contract.list, function (item, itemIndex) {
        return _c('bb-list-item', {
          key: contractIndex + "-" + itemIndex,
          attrs: {
            "id": item.id,
            "title": item.title,
            "title-uppercase": false,
            "slot-nowrap": false,
            "condensed": ""
          }
        }, [item.button ? _c('bb-button', {
          staticClass: "bb-contracts__list-button",
          attrs: {
            "label": item.button.label,
            "to": item.button.to,
            "target": item.button.target,
            "color": "blue",
            "size": "sm"
          },
          on: {
            "click": function ($event) {
              return _vm.$emit(item.button.clickEventName);
            }
          }
        }) : [_vm._v("\n            " + _vm._s(item.content) + "\n          ")], _vm._v(" "), item.tooltip ? _c('bb-tooltip', [_vm._v("\n            " + _vm._s(item.tooltip) + "\n          ")]) : _vm._e()], 2);
      }), 1), _vm._v(" "), _vm._l(contract.footnotes, function (footnote) {
        return _c('p', {
          key: footnote,
          staticClass: "bb-contracts__footnote"
        }, [_vm._v("\n        " + _vm._s(footnote) + "\n      ")]);
      })];
    }, null, Object.assign({}, contract))], 2);
  }), 0);
};
var __vue_staticRenderFns__$2 = [];

/* style */
const __vue_inject_styles__$2 = function (inject) {
  if (!inject) return;
  inject("data-v-88c0ef3c_0", {
    source: ".bb-contracts__contract:not(:nth-of-type(1)){margin-top:2.5rem}.bb-contracts__heading{margin-bottom:1.25rem;color:#00285a}.bb-contracts__paragraph{color:#38414d;font-size:.875rem}.bb-contracts__mobile-overview{margin-top:1.5625rem}@media (min-width:768px){.bb-contracts__mobile-overview{display:none}}.bb-contracts__list{margin-top:2.5rem}.bb-contracts__list:not(:last-child){margin-bottom:2.5rem}@media (min-width:768px){.bb-contracts__list{margin-top:1.875rem}}.bb-contracts__footnote{margin-bottom:.9375rem;color:#38414d;font-size:.875rem}@media (min-width:768px){.bb-contracts__footnote{display:none}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$2 = undefined;
/* module identifier */
const __vue_module_identifier__$2 = undefined;
/* functional template */
const __vue_is_functional_template__$2 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, createInjector, undefined, undefined);

Vue.use(VueScreen, gridBreakpoints);
var script$1 = Vue.extend({
  name: 'bb-contract-page',
  components: {
    BbContainer: __vue_component__$2q,
    BbContracts: __vue_component__$2,
    BbRow: __vue_component__$22,
    BbCol: __vue_component__$2r,
    BbButton: __vue_component__$2v,
    BbContractOverview: __vue_component__$4,
    BbSticky: __vue_component__$1W
  },
  props: {
    /** Contracts. Replaces the `conditionLists` prop */
    contracts: {
      type: Array,
      default: () => []
    },
    conditionsParagraph: String,
    /** Heading for the extra slot */
    extraHeading: String,
    /** An array of objects to populate the list group | `[ [{ heading: String, list: [ { title: String, content: String, button: Object } ] }], … ]` */
    conditionLists: {
      type: Array,
      default: () => []
    },
    /** Object of sign button props */
    signButton: {
      type: Object
    },
    /** Object of download button props */
    downloadButton: {
      type: Object
    },
    /** Loan amount section | `{ title: String, content: String }` */
    loanAmount: {
      type: Object
    },
    /** Monthly payment section | `{ title: String, content: String }` */
    monthlyPayment: {
      type: Object
    },
    /** Loan period section | `{ title: String, content: String }` */
    period: {
      type: Object
    },
    /** Heading for the download section */
    downloadTitle: String,
    /** Paragraphs for the download section | `[ String ]`*/
    downloadParagraphs: {
      type: Array
    },
    /** Highlight the first overview box of a contract. */
    boxHighlighted: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    downloadSection() {
      const download = {
        title: this.downloadTitle,
        paragraphs: this.downloadParagraphs,
        button: this.downloadButton
      };
      return Object.values(download).some(notEmpty) ? {
        download
      } : {};
    },
    computedContracts() {
      return notEmpty(this.contracts) ? this.contracts : this.conditionLists.map((conditionList, index) => ({
        ...conditionList,
        ...(index === 0 && this.contractFromDeprecatedProps)
      }));
    },
    contractFromDeprecatedProps() {
      return {
        paragraph: this.conditionsParagraph,
        overview: this.overviewBoxesFromDeprecatedProps,
        ...this.downloadSection
      };
    },
    overviewBoxesFromDeprecatedProps() {
      return [this.loanAmount, this.monthlyPayment, this.period].filter(notEmpty);
    }
  },
  methods: {
    onSign() {
      /**
       * Sign event.
       *
       * @event sign
       */
      this.$emit('sign');
    },
    onDownload() {
      /**
       * Download event.
       *
       * @event download
       */
      this.$emit('download');
    }
  }
});

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "bb-contract-page"
  }, [_vm._t("steps"), _vm._v(" "), _c('bb-container', [_c('bb-row', [_c('bb-col', {
    staticClass: "bb-contract-page__main",
    attrs: {
      "md": "14"
    }
  }, [_c('bb-contracts', _vm._g({
    attrs: {
      "contracts": _vm.computedContracts
    },
    scopedSlots: _vm._u([!_vm.$screen.md ? {
      key: "contractOverview",
      fn: function (contract) {
        return [_vm._t("contractOverview", null, null, Object.assign({}, contract))];
      }
    } : null, {
      key: "contract",
      fn: function (contract) {
        return [_vm._t("contract", null, null, Object.assign({}, contract))];
      }
    }], null, true)
  }, _vm.$listeners)), _vm._v(" "), _vm.extraHeading ? _c('h4', {
    staticClass: "bb-contract-page__extra-heading"
  }, [_vm._v("\n          " + _vm._s(_vm.extraHeading) + "\n        ")]) : _vm._e(), _vm._v(" "), _vm._t("extraContent"), _vm._v(" "), _vm.signButton ? _c('bb-sticky', {
    staticClass: "bb-contract-page__sticky",
    attrs: {
      "bleed-mobile-only": ""
    }
  }, [_c('bb-container', [_c('bb-button', _vm._b({
    staticClass: "bb-contract-page__sign-button",
    on: {
      "click": _vm.onSign
    }
  }, 'bb-button', _vm.signButton, false))], 1)], 1) : _vm._e()], 2), _vm._v(" "), !_vm.$scopedSlots.contractOverview || _vm.$screen.md ? _c('bb-col', {
    staticClass: "bb-contract-page__overview",
    attrs: {
      "md": "8",
      "offset-md": "2"
    }
  }, [_c('bb-contract-overview', {
    attrs: {
      "contracts": _vm.computedContracts,
      "box-highlighted": _vm.boxHighlighted
    },
    on: {
      "download": _vm.onDownload
    },
    scopedSlots: _vm._u([{
      key: "contractOverview",
      fn: function (contract) {
        return [_vm._t("contractOverview", null, null, Object.assign({}, contract))];
      }
    }], null, true)
  })], 1) : _vm._e()], 1)], 1)], 2);
};
var __vue_staticRenderFns__$1 = [];

/* style */
const __vue_inject_styles__$1 = function (inject) {
  if (!inject) return;
  inject("data-v-b662dad4_0", {
    source: ".bb-contract-page{width:100%}.bb-contract-page__main,.bb-contract-page__overview{padding-top:1.875rem;padding-bottom:.625rem}@media (min-width:768px){.bb-contract-page__main,.bb-contract-page__overview{padding-top:2.5rem;padding-bottom:3.75rem}}.bb-contract-page__overview{padding-bottom:3.75rem}@media (min-width:768px){.bb-contract-page__overview{background:#f7f7f8}.bb-contract-page__overview.container{padding-right:2.5rem;padding-left:2.5rem}}.bb-contract-page__extra-heading{margin-top:2.5rem;margin-bottom:-1.5rem;color:#00285a}@media (min-width:768px){.bb-contract-page__extra-heading{margin-bottom:1.25rem}}.bb-contract-page__mobile-overview{margin-top:1.5625rem}@media (min-width:768px){.bb-contract-page__mobile-overview{display:none}}.bb-contract-page__sticky.bb-sticky{margin-top:3.125rem;text-align:right}@media (min-width:768px){.bb-contract-page__sticky.bb-sticky{margin-top:5.625rem}}.bb-contract-page__sticky.bb-sticky.bb-sticky--sticky{text-align:center}.bb-contract-page__sticky.bb-sticky:not(.bb-sticky--sticky) .bb-sticky__container{padding:0}@media (max-width:768px){.bb-contract-page__sign-button{width:100%}}@media (max-width:768px){.bb-sticky--sticky .bb-contract-page__sign-button{border-radius:0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__$1 = undefined;
/* module identifier */
const __vue_module_identifier__$1 = undefined;
/* functional template */
const __vue_is_functional_template__$1 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, createInjector, undefined, undefined);

var script = {
  name: 'bb-offer-thank-you-page',
  components: {
    BbCol: __vue_component__$2r,
    BbRow: __vue_component__$22,
    BbContainer: __vue_component__$2q,
    BbCard: __vue_component__$2t,
    BbForm: __vue_component__$1c,
    BbButton: __vue_component__$2v,
    BbSticky: __vue_component__$1W,
    BbModal: __vue_component__$W
  },
  mixins: [validator],
  props: {
    formProps: Object,
    cardProps: Object,
    editButtonLabel: String,
    editButtonDisabled: Boolean,
    cardBacksideWide: Boolean,
    useModal: Boolean,
    modalProps: Object,
    formExtrasAriaLabel: {
      type: String,
      default: 'Additional info'
    }
  },
  emits: ['submit', 'progress', 'edit', 'cancel', 'cardFlip', 'cardClose', 'modalOpen', 'modalClose'],
  data() {
    return {
      stuck: null
    };
  },
  computed: {
    classes() {
      return ['bb-offer-thank-you-page', {
        'bb-offer-thank-you-page--card-backside-wide': this.cardBacksideWide
      }];
    },
    editButtonVisible() {
      return this.$slots['card-back-side'] || this.useModal && this.$slots['modal-content'];
    }
  },
  methods: {
    onEditButtonClick() {
      this.useModal ? this.emitModalOpen() : this.$refs.card.flip();
    },
    onFormSubmit(event) {
      this.$emit('submit', event);
    },
    onFormProgress(event) {
      this.$emit('progress', event);
    },
    onFormEdit(event) {
      this.$emit('edit', event);
    },
    onFormCancel(event) {
      this.$emit('cancel', event);
    },
    onCardFlip(event) {
      this.$emit('cardFlip', event);
    },
    onCardClose(event) {
      this.$emit('cardClose', event);
    },
    emitModalOpen(event) {
      this.$emit('modalOpen', event);
    },
    emitModalClose(event) {
      this.$emit('modalClose', event);
    }
  }
};

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    class: _vm.classes
  }, [_vm._t("header"), _vm._v(" "), _c('bb-container', [_c('bb-row', {
    attrs: {
      "justify": "center"
    }
  }, [_c('bb-col', {
    attrs: {
      "sm": "20",
      "md": "16",
      "lg": "12",
      "xl": "10"
    }
  }, [_c('bb-form', _vm._b({
    on: {
      "submit": _vm.onFormSubmit,
      "progress": _vm.onFormProgress,
      "edit": _vm.onFormEdit,
      "cancel": _vm.onFormCancel
    },
    scopedSlots: _vm._u([{
      key: "submit",
      fn: function (form) {
        return [_vm.$scopedSlots.submit ? _c('bb-sticky', {
          on: {
            "stuck": function ($event) {
              _vm.stuck = $event;
            }
          }
        }, [!_vm.stuck ? _vm._t("submit", null, {
          "progress": form.progress
        }) : _vm._e(), _vm._v(" "), _vm.stuck ? _c('bb-container', [_c('bb-row', {
          attrs: {
            "justify": "center"
          }
        }, [_c('bb-col', {
          attrs: {
            "sm": "20",
            "md": "16",
            "lg": "12",
            "xl": "10"
          }
        }, [_vm._t("submit", null, {
          "progress": form.progress
        })], 2)], 1)], 1) : _vm._e()], 2) : _vm._e()];
      }
    }], null, true)
  }, 'bb-form', _vm.formProps, false), [_c('bb-card', _vm._b({
    ref: "card",
    staticClass: "bb-offer-thank-you-page__card",
    attrs: {
      "size": "fluid"
    },
    on: {
      "flip": _vm.onCardFlip,
      "close": _vm.onCardClose
    },
    scopedSlots: _vm._u([_vm.editButtonLabel ? {
      key: "flip-icon",
      fn: function () {
        return [_c('div')];
      },
      proxy: true
    } : null, !_vm.useModal ? {
      key: "back-side",
      fn: function () {
        return [_vm._t("card-back-side")];
      },
      proxy: true
    } : null], null, true)
  }, 'bb-card', _vm.cardProps, false), [_vm._v(" "), _vm._t("default"), _vm._v(" "), _vm.editButtonVisible ? _c('div', {
    staticClass: "bb-offer-thank-you-page__card--button"
  }, [_vm.editButtonLabel ? _c('bb-button', {
    attrs: {
      "label": _vm.editButtonLabel,
      "disabled": _vm.editButtonDisabled,
      "no-background": "",
      "color": "blue",
      "icon": "ui-pencil-circle-outline"
    },
    on: {
      "click": _vm.onEditButtonClick
    }
  }) : _vm._e()], 1) : _vm._e()], 2), _vm._v(" "), _vm.$slots['form-extras'] ? _c('div', {
    staticClass: "bb-offer-thank-you-page__form-extras",
    attrs: {
      "aria-label": _vm.formExtrasAriaLabel
    }
  }, [_vm._t("form-extras")], 2) : _vm._e()], 1)], 1)], 1)], 1), _vm._v(" "), _vm.useModal ? _c('bb-modal', _vm._b({
    attrs: {
      "flavor": "card"
    },
    on: {
      "close": _vm.emitModalClose
    }
  }, 'bb-modal', _vm.modalProps, false), [_vm._t("modal-content")], 2) : _vm._e()], 2);
};
var __vue_staticRenderFns__ = [];

/* style */
const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-379b8098_0", {
    source: ".bb-offer-thank-you-page{display:block;width:100%;padding-bottom:30px;background:#f7f7f8}.bb-offer-thank-you-page__form-extras{max-width:90%;margin:1.875rem auto 0}.bb-offer-thank-you-page__card{max-width:none}.bb-offer-thank-you-page__card--button{display:flex;flex-direction:column;justify-content:center}.bb-offer-thank-you-page--card-backside-wide .bb-card__front-side{width:auto}.bb-offer-thank-you-page--card-backside-wide .bb-card__back-side{width:40rem}.bb-offer-thank-you-page--card-backside-wide .bb-card__flip-stage{perspective:40rem}@media (min-width:768px){.bb-offer-thank-you-page{padding-bottom:0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */
const __vue_scope_id__ = undefined;
/* module identifier */
const __vue_module_identifier__ = undefined;
/* functional template */
const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);

const flagFromCountryCode = function (countryCode) {
  const base = 127397;
  return String.fromCodePoint(countryCode.charCodeAt(0) + base) + String.fromCodePoint(countryCode.charCodeAt(1) + base);
};
const countryCodes = [{
  'alpha2': 'AC',
  'callingCode': '247',
  'name': 'Ascension Island'
}, {
  'alpha2': 'AD',
  'callingCode': '376',
  'name': 'Andorra'
}, {
  'alpha2': 'AE',
  'callingCode': '971',
  'name': 'United Arab Emirates'
}, {
  'alpha2': 'AF',
  'callingCode': '93',
  'name': 'Afghanistan'
}, {
  'alpha2': 'AG',
  'callingCode': '1 268',
  'name': 'Antigua And Barbuda'
}, {
  'alpha2': 'AI',
  'callingCode': '1 264',
  'name': 'Anguilla'
}, {
  'alpha2': 'AL',
  'callingCode': '355',
  'name': 'Albania'
}, {
  'alpha2': 'AM',
  'callingCode': '374',
  'name': 'Armenia'
}, {
  'alpha2': 'AO',
  'callingCode': '244',
  'name': 'Angola'
}, {
  'alpha2': 'AQ',
  'callingCode': '672',
  'name': 'Antarctica'
}, {
  'alpha2': 'AR',
  'callingCode': '54',
  'name': 'Argentina'
}, {
  'alpha2': 'AS',
  'callingCode': '1 684',
  'name': 'American Samoa'
}, {
  'alpha2': 'AT',
  'callingCode': '43',
  'name': 'Austria'
}, {
  'alpha2': 'AU',
  'callingCode': '61',
  'name': 'Australia'
}, {
  'alpha2': 'AW',
  'callingCode': '297',
  'name': 'Aruba'
}, {
  'alpha2': 'AX',
  'callingCode': '358',
  'name': 'Åland Islands'
}, {
  'alpha2': 'AZ',
  'callingCode': '994',
  'name': 'Azerbaijan'
}, {
  'alpha2': 'BA',
  'callingCode': '387',
  'name': 'Bosnia & Herzegovina'
}, {
  'alpha2': 'BB',
  'callingCode': '1 246',
  'name': 'Barbados'
}, {
  'alpha2': 'BD',
  'callingCode': '880',
  'name': 'Bangladesh'
}, {
  'alpha2': 'BE',
  'callingCode': '32',
  'name': 'Belgium'
}, {
  'alpha2': 'BF',
  'callingCode': '226',
  'name': 'Burkina Faso'
}, {
  'alpha2': 'BG',
  'callingCode': '359',
  'name': 'Bulgaria'
}, {
  'alpha2': 'BH',
  'callingCode': '973',
  'name': 'Bahrain'
}, {
  'alpha2': 'BI',
  'callingCode': '257',
  'name': 'Burundi'
}, {
  'alpha2': 'BJ',
  'callingCode': '229',
  'name': 'Benin'
}, {
  'alpha2': 'BL',
  'callingCode': '590',
  'name': 'Saint Barthélemy'
}, {
  'alpha2': 'BM',
  'callingCode': '1 441',
  'name': 'Bermuda'
}, {
  'alpha2': 'BN',
  'callingCode': '673',
  'name': 'Brunei Darussalam'
}, {
  'alpha2': 'BO',
  'callingCode': '591',
  'name': 'Bolivia, Plurinational State Of'
}, {
  'alpha2': 'BQ',
  'callingCode': '599',
  'name': 'Bonaire, Saint Eustatius And Saba'
}, {
  'alpha2': 'BR',
  'callingCode': '55',
  'name': 'Brazil'
}, {
  'alpha2': 'BS',
  'callingCode': '1 242',
  'name': 'Bahamas'
}, {
  'alpha2': 'BT',
  'callingCode': '975',
  'name': 'Bhutan'
}, {
  'alpha2': 'BW',
  'callingCode': '267',
  'name': 'Botswana'
}, {
  'alpha2': 'BY',
  'callingCode': '375',
  'name': 'Belarus'
}, {
  'alpha2': 'BZ',
  'callingCode': '501',
  'name': 'Belize'
}, {
  'alpha2': 'CA',
  'callingCode': '1',
  'name': 'Canada'
}, {
  'alpha2': 'CC',
  'callingCode': '61',
  'name': 'Cocos (Keeling) Islands'
}, {
  'alpha2': 'CD',
  'callingCode': '243',
  'name': 'Democratic Republic Of Congo'
}, {
  'alpha2': 'CF',
  'callingCode': '236',
  'name': 'Central African Republic'
}, {
  'alpha2': 'CG',
  'callingCode': '242',
  'name': 'Republic Of Congo'
}, {
  'alpha2': 'CH',
  'callingCode': '41',
  'name': 'Switzerland'
}, {
  'alpha2': 'CI',
  'callingCode': '225',
  'name': 'Côte d\'Ivoire'
}, {
  'alpha2': 'CK',
  'callingCode': '682',
  'name': 'Cook Islands'
}, {
  'alpha2': 'CL',
  'callingCode': '56',
  'name': 'Chile'
}, {
  'alpha2': 'CM',
  'callingCode': '237',
  'name': 'Cameroon'
}, {
  'alpha2': 'CN',
  'callingCode': '86',
  'name': 'China'
}, {
  'alpha2': 'CO',
  'callingCode': '57',
  'name': 'Colombia'
}, {
  'alpha2': 'CR',
  'callingCode': '506',
  'name': 'Costa Rica'
}, {
  'alpha2': 'CU',
  'callingCode': '53',
  'name': 'Cuba'
}, {
  'alpha2': 'CV',
  'callingCode': '238',
  'name': 'Cabo Verde'
}, {
  'alpha2': 'CW',
  'callingCode': '599',
  'name': 'Curacao'
}, {
  'alpha2': 'CX',
  'callingCode': '61',
  'name': 'Christmas Island'
}, {
  'alpha2': 'CY',
  'callingCode': '357',
  'name': 'Cyprus'
}, {
  'alpha2': 'CZ',
  'callingCode': '420',
  'name': 'Czech Republic'
}, {
  'alpha2': 'DE',
  'callingCode': '49',
  'name': 'Germany'
}, {
  'alpha2': 'DJ',
  'callingCode': '253',
  'name': 'Djibouti'
}, {
  'alpha2': 'DK',
  'callingCode': '45',
  'name': 'Denmark'
}, {
  'alpha2': 'DM',
  'callingCode': '1 767',
  'name': 'Dominica'
}, {
  'alpha2': 'DO',
  'callingCode': '1 809',
  'name': 'Dominican Republic'
}, {
  'alpha2': 'DO',
  'callingCode': '1 829',
  'name': 'Dominican Republic'
}, {
  'alpha2': 'DO',
  'callingCode': '1 849',
  'name': 'Dominican Republic'
}, {
  'alpha2': 'DZ',
  'callingCode': '213',
  'name': 'Algeria'
}, {
  'alpha2': 'EC',
  'callingCode': '593',
  'name': 'Ecuador'
}, {
  'alpha2': 'EE',
  'callingCode': '372',
  'name': 'Estonia'
}, {
  'alpha2': 'EG',
  'callingCode': '20',
  'name': 'Egypt'
}, {
  'alpha2': 'EH',
  'callingCode': '212',
  'name': 'Western Sahara'
}, {
  'alpha2': 'ER',
  'callingCode': '291',
  'name': 'Eritrea'
}, {
  'alpha2': 'ES',
  'callingCode': '34',
  'name': 'Spain'
}, {
  'alpha2': 'ET',
  'callingCode': '251',
  'name': 'Ethiopia'
}, {
  'alpha2': 'EU',
  'callingCode': '388',
  'name': 'European Union'
}, {
  'alpha2': 'FI',
  'callingCode': '358',
  'name': 'Finland'
}, {
  'alpha2': 'FJ',
  'callingCode': '679',
  'name': 'Fiji'
}, {
  'alpha2': 'FK',
  'callingCode': '500',
  'name': 'Falkland Islands'
}, {
  'alpha2': 'FM',
  'callingCode': '691',
  'name': 'Micronesia, Federated States Of'
}, {
  'alpha2': 'FO',
  'callingCode': '298',
  'name': 'Faroe Islands'
}, {
  'alpha2': 'FR',
  'callingCode': '33',
  'name': 'France'
}, {
  'alpha2': 'FX',
  'callingCode': '241',
  'name': 'France, Metropolitan'
}, {
  'alpha2': 'GA',
  'callingCode': '241',
  'name': 'Gabon'
}, {
  'alpha2': 'GB',
  'callingCode': '44',
  'name': 'United Kingdom'
}, {
  'alpha2': 'GD',
  'callingCode': '473',
  'name': 'Grenada'
}, {
  'alpha2': 'GE',
  'callingCode': '995',
  'name': 'Georgia'
}, {
  'alpha2': 'GF',
  'callingCode': '594',
  'name': 'French Guiana'
}, {
  'alpha2': 'GG',
  'callingCode': '44',
  'name': 'Guernsey'
}, {
  'alpha2': 'GH',
  'callingCode': '233',
  'name': 'Ghana'
}, {
  'alpha2': 'GI',
  'callingCode': '350',
  'name': 'Gibraltar'
}, {
  'alpha2': 'GL',
  'callingCode': '299',
  'name': 'Greenland'
}, {
  'alpha2': 'GM',
  'callingCode': '220',
  'name': 'Gambia'
}, {
  'alpha2': 'GN',
  'callingCode': '224',
  'name': 'Guinea'
}, {
  'alpha2': 'GP',
  'callingCode': '590',
  'name': 'Guadeloupe'
}, {
  'alpha2': 'GQ',
  'callingCode': '240',
  'name': 'Equatorial Guinea'
}, {
  'alpha2': 'GR',
  'callingCode': '30',
  'name': 'Greece'
}, {
  'alpha2': 'GT',
  'callingCode': '502',
  'name': 'Guatemala'
}, {
  'alpha2': 'GU',
  'callingCode': '1 671',
  'name': 'Guam'
}, {
  'alpha2': 'GW',
  'callingCode': '245',
  'name': 'Guinea-bissau'
}, {
  'alpha2': 'GY',
  'callingCode': '592',
  'name': 'Guyana'
}, {
  'alpha2': 'HK',
  'callingCode': '852',
  'name': 'Hong Kong'
}, {
  'alpha2': 'HN',
  'callingCode': '504',
  'name': 'Honduras'
}, {
  'alpha2': 'HR',
  'callingCode': '385',
  'name': 'Croatia'
}, {
  'alpha2': 'HT',
  'callingCode': '509',
  'name': 'Haiti'
}, {
  'alpha2': 'HU',
  'callingCode': '36',
  'name': 'Hungary'
}, {
  'alpha2': 'ID',
  'callingCode': '62',
  'name': 'Indonesia'
}, {
  'alpha2': 'IE',
  'callingCode': '353',
  'name': 'Ireland'
}, {
  'alpha2': 'IL',
  'callingCode': '972',
  'name': 'Israel'
}, {
  'alpha2': 'IM',
  'callingCode': '44',
  'name': 'Isle Of Man'
}, {
  'alpha2': 'IN',
  'callingCode': '91',
  'name': 'India'
}, {
  'alpha2': 'IO',
  'callingCode': '246',
  'name': 'British Indian Ocean Territory'
}, {
  'alpha2': 'IQ',
  'callingCode': '964',
  'name': 'Iraq'
}, {
  'alpha2': 'IR',
  'callingCode': '98',
  'name': 'Iran, Islamic Republic Of'
}, {
  'alpha2': 'IS',
  'callingCode': '354',
  'name': 'Iceland'
}, {
  'alpha2': 'IT',
  'callingCode': '39',
  'name': 'Italy'
}, {
  'alpha2': 'JE',
  'callingCode': '44',
  'name': 'Jersey'
}, {
  'alpha2': 'JM',
  'callingCode': '1 876',
  'name': 'Jamaica'
}, {
  'alpha2': 'JO',
  'callingCode': '962',
  'name': 'Jordan'
}, {
  'alpha2': 'JP',
  'callingCode': '81',
  'name': 'Japan'
}, {
  'alpha2': 'KE',
  'callingCode': '254',
  'name': 'Kenya'
}, {
  'alpha2': 'KG',
  'callingCode': '996',
  'name': 'Kyrgyzstan'
}, {
  'alpha2': 'KH',
  'callingCode': '855',
  'name': 'Cambodia'
}, {
  'alpha2': 'KI',
  'callingCode': '686',
  'name': 'Kiribati'
}, {
  'alpha2': 'KM',
  'callingCode': '269',
  'name': 'Comoros'
}, {
  'alpha2': 'KN',
  'callingCode': '1 869',
  'name': 'Saint Kitts And Nevis'
}, {
  'alpha2': 'KP',
  'callingCode': '850',
  'name': 'Korea, Democratic People\'s Republic Of'
}, {
  'alpha2': 'KR',
  'callingCode': '82',
  'name': 'Korea, Republic Of'
}, {
  'alpha2': 'KW',
  'callingCode': '965',
  'name': 'Kuwait'
}, {
  'alpha2': 'KY',
  'callingCode': '1 345',
  'name': 'Cayman Islands'
}, {
  'alpha2': 'KZ',
  'callingCode': '7',
  'name': 'Kazakhstan'
}, {
  'alpha2': 'KZ',
  'callingCode': '7 6',
  'name': 'Kazakhstan'
}, {
  'alpha2': 'KZ',
  'callingCode': '7 7',
  'name': 'Kazakhstan'
}, {
  'alpha2': 'LA',
  'callingCode': '856',
  'name': 'Lao People\'s Democratic Republic'
}, {
  'alpha2': 'LB',
  'callingCode': '961',
  'name': 'Lebanon'
}, {
  'alpha2': 'LC',
  'callingCode': '1 758',
  'name': 'Saint Lucia'
}, {
  'alpha2': 'LI',
  'callingCode': '423',
  'name': 'Liechtenstein'
}, {
  'alpha2': 'LK',
  'callingCode': '94',
  'name': 'Sri Lanka'
}, {
  'alpha2': 'LR',
  'callingCode': '231',
  'name': 'Liberia'
}, {
  'alpha2': 'LS',
  'callingCode': '266',
  'name': 'Lesotho'
}, {
  'alpha2': 'LT',
  'callingCode': '370',
  'name': 'Lithuania'
}, {
  'alpha2': 'LU',
  'callingCode': '352',
  'name': 'Luxembourg'
}, {
  'alpha2': 'LV',
  'callingCode': '371',
  'name': 'Latvia'
}, {
  'alpha2': 'LY',
  'callingCode': '218',
  'name': 'Libya'
}, {
  'alpha2': 'MA',
  'callingCode': '212',
  'name': 'Morocco'
}, {
  'alpha2': 'MC',
  'callingCode': '377',
  'name': 'Monaco'
}, {
  'alpha2': 'MD',
  'callingCode': '373',
  'name': 'Moldova'
}, {
  'alpha2': 'ME',
  'callingCode': '382',
  'name': 'Montenegro'
}, {
  'alpha2': 'MF',
  'callingCode': '590',
  'name': 'Saint Martin'
}, {
  'alpha2': 'MG',
  'callingCode': '261',
  'name': 'Madagascar'
}, {
  'alpha2': 'MH',
  'callingCode': '692',
  'name': 'Marshall Islands'
}, {
  'alpha2': 'MK',
  'callingCode': '389',
  'name': 'Macedonia, The Former Yugoslav Republic Of'
}, {
  'alpha2': 'ML',
  'callingCode': '223',
  'name': 'Mali'
}, {
  'alpha2': 'MM',
  'callingCode': '95',
  'name': 'Myanmar'
}, {
  'alpha2': 'MN',
  'callingCode': '976',
  'name': 'Mongolia'
}, {
  'alpha2': 'MO',
  'callingCode': '853',
  'name': 'Macao'
}, {
  'alpha2': 'MP',
  'callingCode': '1 670',
  'name': 'Northern Mariana Islands'
}, {
  'alpha2': 'MQ',
  'callingCode': '596',
  'name': 'Martinique'
}, {
  'alpha2': 'MR',
  'callingCode': '222',
  'name': 'Mauritania'
}, {
  'alpha2': 'MS',
  'callingCode': '1 664',
  'name': 'Montserrat'
}, {
  'alpha2': 'MT',
  'callingCode': '356',
  'name': 'Malta'
}, {
  'alpha2': 'MU',
  'callingCode': '230',
  'name': 'Mauritius'
}, {
  'alpha2': 'MV',
  'callingCode': '960',
  'name': 'Maldives'
}, {
  'alpha2': 'MW',
  'callingCode': '265',
  'name': 'Malawi'
}, {
  'alpha2': 'MX',
  'callingCode': '52',
  'name': 'Mexico'
}, {
  'alpha2': 'MY',
  'callingCode': '60',
  'name': 'Malaysia'
}, {
  'alpha2': 'MZ',
  'callingCode': '258',
  'name': 'Mozambique'
}, {
  'alpha2': 'NA',
  'callingCode': '264',
  'name': 'Namibia'
}, {
  'alpha2': 'NC',
  'callingCode': '687',
  'name': 'New Caledonia'
}, {
  'alpha2': 'NE',
  'callingCode': '227',
  'name': 'Niger'
}, {
  'alpha2': 'NF',
  'callingCode': '672',
  'name': 'Norfolk Island'
}, {
  'alpha2': 'NG',
  'callingCode': '234',
  'name': 'Nigeria'
}, {
  'alpha2': 'NI',
  'callingCode': '505',
  'name': 'Nicaragua'
}, {
  'alpha2': 'NL',
  'callingCode': '31',
  'name': 'Netherlands'
}, {
  'alpha2': 'NO',
  'callingCode': '47',
  'name': 'Norway'
}, {
  'alpha2': 'NP',
  'callingCode': '977',
  'name': 'Nepal'
}, {
  'alpha2': 'NR',
  'callingCode': '674',
  'name': 'Nauru'
}, {
  'alpha2': 'NU',
  'callingCode': '683',
  'name': 'Niue'
}, {
  'alpha2': 'NZ',
  'callingCode': '64',
  'name': 'New Zealand'
}, {
  'alpha2': 'OM',
  'callingCode': '968',
  'name': 'Oman'
}, {
  'alpha2': 'PA',
  'callingCode': '507',
  'name': 'Panama'
}, {
  'alpha2': 'PE',
  'callingCode': '51',
  'name': 'Peru'
}, {
  'alpha2': 'PF',
  'callingCode': '689',
  'name': 'French Polynesia'
}, {
  'alpha2': 'PG',
  'callingCode': '675',
  'name': 'Papua New Guinea'
}, {
  'alpha2': 'PH',
  'callingCode': '63',
  'name': 'Philippines'
}, {
  'alpha2': 'PK',
  'callingCode': '92',
  'name': 'Pakistan'
}, {
  'alpha2': 'PL',
  'callingCode': '48',
  'name': 'Poland'
}, {
  'alpha2': 'PM',
  'callingCode': '508',
  'name': 'Saint Pierre And Miquelon'
}, {
  'alpha2': 'PN',
  'callingCode': '872',
  'name': 'Pitcairn'
}, {
  'alpha2': 'PR',
  'callingCode': '1 787',
  'name': 'Puerto Rico'
}, {
  'alpha2': 'PR',
  'callingCode': '1 939',
  'name': 'Puerto Rico'
}, {
  'alpha2': 'PS',
  'callingCode': '970',
  'name': 'Palestinian Territory, Occupied'
}, {
  'alpha2': 'PT',
  'callingCode': '351',
  'name': 'Portugal'
}, {
  'alpha2': 'PW',
  'callingCode': '680',
  'name': 'Palau'
}, {
  'alpha2': 'PY',
  'callingCode': '595',
  'name': 'Paraguay'
}, {
  'alpha2': 'QA',
  'callingCode': '974',
  'name': 'Qatar'
}, {
  'alpha2': 'RE',
  'callingCode': '262',
  'name': 'Reunion'
}, {
  'alpha2': 'RO',
  'callingCode': '40',
  'name': 'Romania'
}, {
  'alpha2': 'RS',
  'callingCode': '381',
  'name': 'Serbia'
}, {
  'alpha2': 'RU',
  'callingCode': '7',
  'name': 'Russian Federation'
}, {
  'alpha2': 'RU',
  'callingCode': '7 3',
  'name': 'Russian Federation'
}, {
  'alpha2': 'RU',
  'callingCode': '7 4',
  'name': 'Russian Federation'
}, {
  'alpha2': 'RU',
  'callingCode': '7 8',
  'name': 'Russian Federation'
}, {
  'alpha2': 'RW',
  'callingCode': '250',
  'name': 'Rwanda'
}, {
  'alpha2': 'SA',
  'callingCode': '966',
  'name': 'Saudi Arabia'
}, {
  'alpha2': 'SB',
  'callingCode': '677',
  'name': 'Solomon Islands'
}, {
  'alpha2': 'SC',
  'callingCode': '248',
  'name': 'Seychelles'
}, {
  'alpha2': 'SD',
  'callingCode': '249',
  'name': 'Sudan'
}, {
  'alpha2': 'SE',
  'callingCode': '46',
  'name': 'Sweden'
}, {
  'alpha2': 'SG',
  'callingCode': '65',
  'name': 'Singapore'
}, {
  'alpha2': 'SH',
  'callingCode': '290',
  'name': 'Saint Helena, Ascension And Tristan Da Cunha'
}, {
  'alpha2': 'SI',
  'callingCode': '386',
  'name': 'Slovenia'
}, {
  'alpha2': 'SJ',
  'callingCode': '47',
  'name': 'Svalbard And Jan Mayen'
}, {
  'alpha2': 'SK',
  'callingCode': '421',
  'name': 'Slovakia'
}, {
  'alpha2': 'SL',
  'callingCode': '232',
  'name': 'Sierra Leone'
}, {
  'alpha2': 'SM',
  'callingCode': '378',
  'name': 'San Marino'
}, {
  'alpha2': 'SN',
  'callingCode': '221',
  'name': 'Senegal'
}, {
  'alpha2': 'SO',
  'callingCode': '252',
  'name': 'Somalia'
}, {
  'alpha2': 'SR',
  'callingCode': '597',
  'name': 'Suriname'
}, {
  'alpha2': 'SS',
  'callingCode': '211',
  'name': 'South Sudan'
}, {
  'alpha2': 'ST',
  'callingCode': '239',
  'name': 'Sao Tome and Principe'
}, {
  'alpha2': 'SV',
  'callingCode': '503',
  'name': 'El Salvador'
}, {
  'alpha2': 'SX',
  'callingCode': '1 721',
  'name': 'Sint Maarten'
}, {
  'alpha2': 'SY',
  'callingCode': '963',
  'name': 'Syrian Arab Republic'
}, {
  'alpha2': 'SZ',
  'callingCode': '268',
  'name': 'Swaziland'
}, {
  'alpha2': 'TA',
  'callingCode': '290',
  'name': 'Tristan de Cunha'
}, {
  'alpha2': 'TC',
  'callingCode': '1 649',
  'name': 'Turks And Caicos Islands'
}, {
  'alpha2': 'TD',
  'callingCode': '235',
  'name': 'Chad'
}, {
  'alpha2': 'TG',
  'callingCode': '228',
  'name': 'Togo'
}, {
  'alpha2': 'TH',
  'callingCode': '66',
  'name': 'Thailand'
}, {
  'alpha2': 'TJ',
  'callingCode': '992',
  'name': 'Tajikistan'
}, {
  'alpha2': 'TK',
  'callingCode': '690',
  'name': 'Tokelau'
}, {
  'alpha2': 'TL',
  'callingCode': '670',
  'name': 'Timor-Leste, Democratic Republic of'
}, {
  'alpha2': 'TM',
  'callingCode': '993',
  'name': 'Turkmenistan'
}, {
  'alpha2': 'TN',
  'callingCode': '216',
  'name': 'Tunisia'
}, {
  'alpha2': 'TO',
  'callingCode': '676',
  'name': 'Tonga'
}, {
  'alpha2': 'TR',
  'callingCode': '90',
  'name': 'Turkey'
}, {
  'alpha2': 'TT',
  'callingCode': '1 868',
  'name': 'Trinidad And Tobago'
}, {
  'alpha2': 'TV',
  'callingCode': '688',
  'name': 'Tuvalu'
}, {
  'alpha2': 'TW',
  'callingCode': '886',
  'name': 'Taiwan'
}, {
  'alpha2': 'TZ',
  'callingCode': '255',
  'name': 'Tanzania, United Republic Of'
}, {
  'alpha2': 'UA',
  'callingCode': '380',
  'name': 'Ukraine'
}, {
  'alpha2': 'UG',
  'callingCode': '256',
  'name': 'Uganda'
}, {
  'alpha2': 'UM',
  'callingCode': '1',
  'name': 'United States Minor Outlying Islands'
}, {
  'alpha2': 'US',
  'callingCode': '1',
  'name': 'United States'
}, {
  'alpha2': 'UY',
  'callingCode': '598',
  'name': 'Uruguay'
}, {
  'alpha2': 'UZ',
  'callingCode': '998',
  'name': 'Uzbekistan'
}, {
  'alpha2': 'VA',
  'callingCode': '379',
  'name': 'Vatican City State'
}, {
  'alpha2': 'VA',
  'callingCode': '39',
  'name': 'Vatican City State'
}, {
  'alpha2': 'VC',
  'callingCode': '1 784',
  'name': 'Saint Vincent And The Grenadines'
}, {
  'alpha2': 'VE',
  'callingCode': '58',
  'name': 'Venezuela, Bolivarian Republic Of'
}, {
  'alpha2': 'VG',
  'callingCode': '1 284',
  'name': 'Virgin Islands (British)'
}, {
  'alpha2': 'VI',
  'callingCode': '1 340',
  'name': 'Virgin Islands (US)'
}, {
  'alpha2': 'VN',
  'callingCode': '84',
  'name': 'Viet Nam'
}, {
  'alpha2': 'VU',
  'callingCode': '678',
  'name': 'Vanuatu'
}, {
  'alpha2': 'WF',
  'callingCode': '681',
  'name': 'Wallis And Futuna'
}, {
  'alpha2': 'WS',
  'callingCode': '685',
  'name': 'Samoa'
}, {
  'alpha2': 'XK',
  'callingCode': '383',
  'name': 'Kosovo'
}, {
  'alpha2': 'YE',
  'callingCode': '967',
  'name': 'Yemen'
}, {
  'alpha2': 'YT',
  'callingCode': '262',
  'name': 'Mayotte'
}, {
  'alpha2': 'ZA',
  'callingCode': '27',
  'name': 'South Africa'
}, {
  'alpha2': 'ZM',
  'callingCode': '260',
  'name': 'Zambia'
}, {
  'alpha2': 'ZW',
  'callingCode': '263',
  'name': 'Zimbabwe'
}];

// Styles, except helpers

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  countryCodes: countryCodes,
  flagFromCountryCode: flagFromCountryCode,
  gridBreakpoints: gridBreakpoints,
  ICON_CDN_PATH: ICON_CDN_PATH,
  jump: jump,
  calculatePaymentValue: calculateFixedMonthlyPayment,
  calculateFixedMonthlyPayment: calculateFixedMonthlyPayment,
  thousandsSeparator: thousandsSeparator,
  decimalSeparator: decimalSeparator,
  localizeNumber: localizeNumber,
  unlocalizeNumber: unlocalizeNumber,
  BbBigInput: __vue_component__$2A,
  BbBigInputCurrency: __vue_component__$2y,
  BbBox: __vue_component__$2x,
  BbButton: __vue_component__$2v,
  BbCard: __vue_component__$2t,
  BbCheckbox: __vue_component__$2s,
  BbCol: __vue_component__$2r,
  BbContainer: __vue_component__$2q,
  BbDropdownItem: __vue_component__$2o,
  BbFieldPrefix: __vue_component__$2n,
  BbFunctionalAccordion: __vue_component__$2m,
  BbFunctionalDropdown: __vue_component__$2l,
  BbFunctionalInputCurrency: __vue_component__$2z,
  BbFunctionalSticky: __vue_component__$2k,
  BbHeading: __vue_component__$2j,
  BbHero: __vue_component__$2i,
  BbHighlight: __vue_component__$2h,
  BbIcon: __vue_component__$2C,
  BbIconFrame: __vue_component__$2g,
  BbInput: __vue_component__$2f,
  BbLabel: __vue_component__$2e,
  BbLabeledValue: __vue_component__$2c,
  BbLink: __vue_component__$2p,
  BbListGroup: __vue_component__$2b,
  BbListItem: __vue_component__$29,
  BbLogo: __vue_component__$28,
  BbMaskedInput: __vue_component__$27,
  BbOverlay: __vue_component__$2u,
  BbProgressBar: __vue_component__$26,
  BbProgressDots: __vue_component__$25,
  BbPublicTable: __vue_component__$24,
  BbRibbon: __vue_component__$23,
  BbRow: __vue_component__$22,
  BbScrollWrapper: __vue_component__$21,
  BbSelect: __vue_component__$20,
  BbSeparator: __vue_component__$2a,
  BbSideTabs: __vue_component__$1$,
  BbSkeleton: __vue_component__$2d,
  BbSlottedParagraph: __vue_component__$1_,
  BbSocialLinks: __vue_component__$1Z,
  BbSpinner: __vue_component__$2w,
  BbStepList: __vue_component__$1Y,
  BbStepsOverview: __vue_component__$1X,
  BbSticky: __vue_component__$1W,
  BbTable: __vue_component__$1V,
  BbTagGroup: __vue_component__$1U,
  BbTaskArea: __vue_component__$1T,
  BbTextarea: __vue_component__$1S,
  BbTicketStub: __vue_component__$1R,
  BbToggleSwitch: __vue_component__$1Q,
  BbToolbar: __vue_component__$1P,
  BbTooltip: __vue_component__$2B,
  BbAccordion: __vue_component__$1N,
  BbAccountCard: __vue_component__$1L,
  BbArticleFooter: __vue_component__$1K,
  BbArticleMeta: __vue_component__$1J,
  BbArticlePhoto: __vue_component__$1I,
  BbArticlePullquote: __vue_component__$1H,
  BbBanner: __vue_component__$1G,
  BbBigSlider: __vue_component__$1F,
  BbBlogFeaturedCard: __vue_component__$1C,
  BbBlogFeaturedListCard: __vue_component__$1B,
  BbBlogListCard: __vue_component__$1A,
  BbBlogTopicCard: __vue_component__$1z,
  BbCampaignCard: __vue_component__$1y,
  BbCampaignCards: __vue_component__$1x,
  BbCardButtonGroup: __vue_component__$1v,
  BbCardSlider: __vue_component__$1u,
  BbCheckboxGroup: __vue_component__$1t,
  BbCreditCard: __vue_component__$1s,
  BbDatepicker: __vue_component__$1o,
  BbDetailedRadio: __vue_component__$1m,
  BbDrawer: __vue_component__$1l,
  BbDropdown: __vue_component__$1M,
  BbEditAmount: __vue_component__$1k,
  BbFeedItem: __vue_component__$1j,
  BbFeedTask: __vue_component__$1i,
  BbFilterBar: __vue_component__$1h,
  BbFooter: __vue_component__$1e,
  BbForm: __vue_component__$1c,
  BbGraphCard: __vue_component__$18,
  BbHeader: __vue_component__$17,
  BbHorizontalMenu: __vue_component__$16,
  BbInputRange: __vue_component__$13,
  BbItemsPerPage: __vue_component__$11,
  BbLoadingOverlay: __vue_component__$10,
  BbMemos: __vue_component__$Y,
  BbMenuItem: __vue_component__$X,
  BbModal: __vue_component__$W,
  BbPagination: __vue_component__$V,
  BbPlaceholder: __vue_component__$$,
  BbProductCard: __vue_component__$U,
  BbProductCards: __vue_component__$T,
  BbProgressIndicator: __vue_component__$S,
  BbPublicHeader: __vue_component__$R,
  BbRadio: __vue_component__$12,
  BbRouterLink: __vue_component__$1D,
  BbSelectRange: __vue_component__$Q,
  BbSidenav: __vue_component__$P,
  BbSlider: __vue_component__$O,
  BbStepIcon: __vue_component__$N,
  BbStepInput: __vue_component__$M,
  BbSubmitButton: __vue_component__$1d,
  BbTabs: __vue_component__$Z,
  BbTag: __vue_component__$15,
  BbTask: __vue_component__$L,
  BbTaskGroup: __vue_component__$K,
  BbTransactions: __vue_component__$I,
  BbTypeahead: __vue_component__$14,
  BbUploadItem: __vue_component__$H,
  BbAddMemo: __vue_component__$B,
  BbAddMemoModal: __vue_component__$A,
  BbAddScheduleModal: __vue_component__$z,
  BbApplicationContractCard: __vue_component__$x,
  BbApplicationPhaseCard: __vue_component__$w,
  BbBackNavigationModal: __vue_component__$v,
  BbBlogSection: __vue_component__$u,
  BbCalculator: __vue_component__$t,
  BbCalculatorModal: __vue_component__$s,
  BbErrorPage: __vue_component__$r,
  BbFieldsetRepeater: __vue_component__$q,
  BbFilterProgress: __vue_component__$l,
  BbFilterStatus: __vue_component__$k,
  BbFormRepeater: __vue_component__$j,
  BbFormRepeaterToggleable: __vue_component__$i,
  BbMemoFilter: __vue_component__$h,
  BbMobileMenuLanding: __vue_component__$d,
  BbNavbar: __vue_component__$8,
  BbPrefixedInput: __vue_component__$7,
  BbSmartMenu: __vue_component__$b,
  BbTestimonialSection: __vue_component__$6,
  BbUpload: __vue_component__$G,
  BbApplicationPage: __vue_component__$5,
  BbContractPage: __vue_component__$1,
  BbOfferThankYouPage: __vue_component__
});

const install = function installComponent(Vue) {
  if (install.installed) return;
  install.installed = true;
  Object.entries(components).forEach(([componentName, component]) => {
    const name = componentName === 'VueComponent' ? component.options.name : componentName;
    Vue.component(name, component);
  });
};
const plugin = {
  install
};

export { __vue_component__$1N as BbAccordion, __vue_component__$1L as BbAccountCard, __vue_component__$B as BbAddMemo, __vue_component__$A as BbAddMemoModal, __vue_component__$z as BbAddScheduleModal, __vue_component__$x as BbApplicationContractCard, __vue_component__$5 as BbApplicationPage, __vue_component__$w as BbApplicationPhaseCard, __vue_component__$1K as BbArticleFooter, __vue_component__$1J as BbArticleMeta, __vue_component__$1I as BbArticlePhoto, __vue_component__$1H as BbArticlePullquote, __vue_component__$v as BbBackNavigationModal, __vue_component__$1G as BbBanner, __vue_component__$2A as BbBigInput, __vue_component__$2y as BbBigInputCurrency, __vue_component__$1F as BbBigSlider, __vue_component__$1C as BbBlogFeaturedCard, __vue_component__$1B as BbBlogFeaturedListCard, __vue_component__$1A as BbBlogListCard, __vue_component__$u as BbBlogSection, __vue_component__$1z as BbBlogTopicCard, __vue_component__$2x as BbBox, __vue_component__$2v as BbButton, __vue_component__$t as BbCalculator, __vue_component__$s as BbCalculatorModal, __vue_component__$1y as BbCampaignCard, __vue_component__$1x as BbCampaignCards, __vue_component__$2t as BbCard, __vue_component__$1v as BbCardButtonGroup, __vue_component__$1u as BbCardSlider, __vue_component__$2s as BbCheckbox, __vue_component__$1t as BbCheckboxGroup, __vue_component__$2r as BbCol, __vue_component__$2q as BbContainer, __vue_component__$1 as BbContractPage, __vue_component__$1s as BbCreditCard, __vue_component__$1o as BbDatepicker, __vue_component__$1m as BbDetailedRadio, __vue_component__$1l as BbDrawer, __vue_component__$1M as BbDropdown, __vue_component__$2o as BbDropdownItem, __vue_component__$1k as BbEditAmount, __vue_component__$r as BbErrorPage, __vue_component__$1j as BbFeedItem, __vue_component__$1i as BbFeedTask, __vue_component__$2n as BbFieldPrefix, __vue_component__$q as BbFieldsetRepeater, __vue_component__$1h as BbFilterBar, __vue_component__$l as BbFilterProgress, __vue_component__$k as BbFilterStatus, __vue_component__$1e as BbFooter, __vue_component__$1c as BbForm, __vue_component__$j as BbFormRepeater, __vue_component__$i as BbFormRepeaterToggleable, __vue_component__$2m as BbFunctionalAccordion, __vue_component__$2l as BbFunctionalDropdown, __vue_component__$2z as BbFunctionalInputCurrency, __vue_component__$2k as BbFunctionalSticky, __vue_component__$18 as BbGraphCard, __vue_component__$17 as BbHeader, __vue_component__$2j as BbHeading, __vue_component__$2i as BbHero, __vue_component__$2h as BbHighlight, __vue_component__$16 as BbHorizontalMenu, __vue_component__$2C as BbIcon, __vue_component__$2g as BbIconFrame, __vue_component__$2f as BbInput, __vue_component__$13 as BbInputRange, __vue_component__$11 as BbItemsPerPage, __vue_component__$2e as BbLabel, __vue_component__$2c as BbLabeledValue, __vue_component__$2p as BbLink, __vue_component__$2b as BbListGroup, __vue_component__$29 as BbListItem, __vue_component__$10 as BbLoadingOverlay, __vue_component__$28 as BbLogo, __vue_component__$27 as BbMaskedInput, __vue_component__$h as BbMemoFilter, __vue_component__$Y as BbMemos, __vue_component__$X as BbMenuItem, __vue_component__$d as BbMobileMenuLanding, __vue_component__$W as BbModal, __vue_component__$8 as BbNavbar, __vue_component__ as BbOfferThankYouPage, __vue_component__$2u as BbOverlay, __vue_component__$V as BbPagination, __vue_component__$$ as BbPlaceholder, __vue_component__$7 as BbPrefixedInput, __vue_component__$U as BbProductCard, __vue_component__$T as BbProductCards, __vue_component__$26 as BbProgressBar, __vue_component__$25 as BbProgressDots, __vue_component__$S as BbProgressIndicator, __vue_component__$R as BbPublicHeader, __vue_component__$24 as BbPublicTable, __vue_component__$12 as BbRadio, __vue_component__$23 as BbRibbon, __vue_component__$1D as BbRouterLink, __vue_component__$22 as BbRow, __vue_component__$21 as BbScrollWrapper, __vue_component__$20 as BbSelect, __vue_component__$Q as BbSelectRange, __vue_component__$2a as BbSeparator, __vue_component__$1$ as BbSideTabs, __vue_component__$P as BbSidenav, __vue_component__$2d as BbSkeleton, __vue_component__$O as BbSlider, __vue_component__$1_ as BbSlottedParagraph, __vue_component__$b as BbSmartMenu, __vue_component__$1Z as BbSocialLinks, __vue_component__$2w as BbSpinner, __vue_component__$N as BbStepIcon, __vue_component__$M as BbStepInput, __vue_component__$1Y as BbStepList, __vue_component__$1X as BbStepsOverview, __vue_component__$1W as BbSticky, __vue_component__$1d as BbSubmitButton, __vue_component__$1V as BbTable, __vue_component__$Z as BbTabs, __vue_component__$15 as BbTag, __vue_component__$1U as BbTagGroup, __vue_component__$L as BbTask, __vue_component__$1T as BbTaskArea, __vue_component__$K as BbTaskGroup, __vue_component__$6 as BbTestimonialSection, __vue_component__$1S as BbTextarea, __vue_component__$1R as BbTicketStub, __vue_component__$1Q as BbToggleSwitch, __vue_component__$1P as BbToolbar, __vue_component__$2B as BbTooltip, __vue_component__$I as BbTransactions, __vue_component__$14 as BbTypeahead, __vue_component__$G as BbUpload, __vue_component__$H as BbUploadItem, ICON_CDN_PATH, calculateFixedMonthlyPayment, calculateFixedMonthlyPayment as calculatePaymentValue, countryCodes, decimalSeparator, plugin as default, flagFromCountryCode, gridBreakpoints, jump, localizeNumber, thousandsSeparator, unlocalizeNumber };
